// ── BEGIN SHEETPOOL BINDINGS — R-S4PP-2 (ruling `shipyard-s4-ship-pool-path`) ────────────────────────────────────────
// The sheet's dice path lives in lib/shared/sheetPool.js, a `defer` script before this one, moved VERBATIM from this file
// (R-S4PP-1). Each name is bound here by the same name, so the 64 call sites, the page globals the smokes read and each
// function's own memo (poolModSkillIndex._idx) are unchanged. They open the file, so no top-level code can read a binding
// before it exists. test/sheet_pool_one_path.cjs pins one definition per name; scripts/check_sheet_pool_verbatim.cjs the move.
var loadCanonicalCharacterBuild = window.SWRPGSheetPool.loadCanonicalCharacterBuild;
var deriveItemState = window.SWRPGSheetPool.deriveItemState;
var sheetItemRegistries = window.SWRPGSheetPool.sheetItemRegistries;
var resolveSheetEquipment = window.SWRPGSheetPool.resolveSheetEquipment;
const OGG_TO_SHEET_SKILL = window.SWRPGSheetPool.OGG_TO_SHEET_SKILL;
var sheetSkillCharacteristic = window.SWRPGSheetPool.sheetSkillCharacteristic;   // F-100 M7 C2: SKILL_CHARS is deleted (R-M7K-9)
// A skill's BASE characteristic as its abbreviation (BR, AG, …), read from the vocabulary through the charbuild map —
// the creator pickers' one read (R-M7K-9: base-only readers). '' before the vocabulary has loaded.
function skillBaseAbbr(sheetKeyOrId, cb) {
  var c = CharResolve.skillBaseCharacteristic(sheetKeyOrId, cb || window._canonicalCharbuild);
  return (c && CharResolve.CHAR_ABBR[c]) || '';
}
var CHAR_KEY_TO_PROP = window.SWRPGSheetPool.CHAR_KEY_TO_PROP;
var poolModSkillIndex = window.SWRPGSheetPool.poolModSkillIndex;
var collectPoolMods = window.SWRPGSheetPool.collectPoolMods;
var withPoolMods = window.SWRPGSheetPool.withPoolMods;
var collectRollNumbers = window.SWRPGSheetPool.collectRollNumbers;   // F-100 M8 C11: the owner's roll-number talents
var SHEET_KEY_TO_OGG = window.SWRPGSheetPool.SHEET_KEY_TO_OGG;
var skillEntryRank = window.SWRPGSheetPool.skillEntryRank;
var sheetSkillRank = window.SWRPGSheetPool.sheetSkillRank;
var sheetPoolOf = window.SWRPGSheetPool.sheetPoolOf;
var sheetPoolFor = window.SWRPGSheetPool.sheetPoolFor;
var yardCharPool = window.SWRPGSheetPool.yardCharPool;
// ── END SHEETPOOL BINDINGS ──────────────────────────────────────────────────────────────────────────────────────────


const { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } = React;

// Phase 1b — shared UI primitives from lib/shared/ui.jsx (window.SWRPGUI, in /vendor.js).
// Destructured here so the ~existing call sites stay untouched. PlayerBehaviorRow stays local
// (gm's copy pipes text through ITS renderDescription → Phase 2, R-UI-B3; this app's twin of that
// function is RETIRED — see "THE DECODER FAMILY" below). PlayerDicePool went with the old sheet (paper-sheet-delete).
const { PrimaryButton, HubCard, SuiteBar, BottomNav, SubTabs, navIcon, useSwipeBack, useEdgeChrome, usePrefersReducedMotion, ffgDicePool, WIPE_DURATIONS, SWIPE_BACK_EDGE_PX, CritTableRef, QualityChips, openRecoveryCard, VersionBeacon, SHEET_PANEL } = window.SWRPGUI;
const _VersionBeacon = VersionBeacon || function () { return null; };  // F-48 G13 — honest update notice (shim if vendor lags)

// ── HOLOTABLE Stage B wiring — the player's join screen ──────────────────────────────────────
//
// ITS OWN COMPONENT, and that is the point: `useRoomSession` is a hook, and putting it inside the
// App component would mean threading one more unconditional hook through a 22,000-line render
// body where a conditional return above it would be silent and fatal. Here the hook has exactly
// one component to be consistent within, and the mount below is one line.
//
// PLACEMENT IS AN IMPLEMENTER'S CHOICE AND IS DISCLOSED AS ONE. `holotable-substrate` bound the
// GM's door to the bench (R-HT-2, the spine) and left the player's home to LAUNCH — "hero front
// door binds at LAUNCH, dark until then". The sheet is the at-table surface and where a player's
// rolls come from, so the join card sits there for now; R-MPA-4's arrangement ruling can move it
// and this is the single JSX site it would move.
//
// DARK: every composition returns null while the flag is off, so this renders nothing at all.
const _RoomSession = (window.SWRPGRoomSession)
  || { useRoomSession: function () { return null; } };

function HolotableJoinPanel({ name }) {
  const room = _RoomSession.useRoomSession({ side: 'pc', name: name || '' });
  const C = window.SWRPGHolotableCompose;
  if (!C || !room) return null;
  return (
    <React.Fragment>
      <C.JoinTable
        value={room.typed}
        onChange={room.onTyped}
        onJoin={function () { room.joinTable(); }}
        busy={room.busy}
        refusal={room.refusal}
        onRetry={room.retry}
        onReload={function () { window.location.reload(); }}
      />
      <C.Feed entries={room.entries} />
    </React.Fragment>
  );
}
// F-46 Desktop Curation — layout primitives from lib/shared/desktopLayout.js (window.SWRPGDesktop,
// loaded after /vendor.js). Fallback to pass-through shims so a load failure degrades to mobile
// markup rather than white-screening (the tokens.css degrade-not-crash posture).
const { useLayoutMode, CenterColumn, SplitPane, WorkspaceGrid } = (window.SWRPGDesktop || {
  useLayoutMode: function () { return 'mobile'; },
  CenterColumn: function (p) { return p.children; },
  SplitPane: function (p) { return p.detail; },
  WorkspaceGrid: function (p) { return [p.left, p.main, p.right]; },
});

/* B-30 (rider R-F9-5) — reSpec/overlay pack load-failure is now HONEST, never silent. When a
   GAME-VARIANTS pack fails to fetch (flaky mobile network), mods.js records it + fires
   'swrpg-mods-loaderror'; useModsLoadError() surfaces the failed stamps so the creator + the stamped
   sheet render a notice instead of silently falling back to base trees with the toggle still on. */
function useModsLoadError() {
  var _u = React.useState(function () { return (window.SWRPGMods && window.SWRPGMods.failed) ? window.SWRPGMods.failed() : []; });
  var failed = _u[0], setFailed = _u[1];
  React.useEffect(function () {
    if (!window.SWRPGMods || !window.SWRPGMods.failed) return;
    var on = function () { setFailed(window.SWRPGMods.failed()); };
    window.addEventListener('swrpg-mods-loaderror', on);
    window.addEventListener('swrpg-mods-changed', on);
    return function () { window.removeEventListener('swrpg-mods-loaderror', on); window.removeEventListener('swrpg-mods-changed', on); };
  }, []);
  return failed;  // array of stamps whose last fetch failed
}
// THE inline notice — B-27 amber pattern. role=status for SR. Extracted from ModsLoadNotice when
// B-250 needed a second one (R-B250-2 requires an EXISTING shape; inventing chrome would be a new
// surface and R-MU1's mockup lane). One shape, two messages — not a second implementation of the
// same job. `testid` lets a smoke name the specific notice rather than "some amber box".
// B-1022 (D-400, ADDENDUM 36): the COLOURS live in CSS by ground (decisions/0049), never inline — the dark-chrome
// default in lib/shared/tokens.css (`.sheet-notice*`, today's values), the paper skin in lib/shared/paper.css
// under [data-doc-ground="creator"] (frame B's tokens). Layout stays inline: it is the same on every ground.
// B-1257 (D-411 items 13–14, ADDENDUM 295 Q1 → A): `paper` opts a notice on another creator ground into the same paper
// skin (`.sheet-notice-paper`); `standalone` is a notice that IS the screen's content (a failed download) — no bottom
// margin, its measure 520px, left-aligned, as the 247 page drew both.
function SheetNotice({ children, actionLabel, onAction, testid, paper, standalone, actTestid }) {
  return (
    <div role="status" aria-live="polite" data-testid={testid} className={paper ? 'sheet-notice sheet-notice-paper' : 'sheet-notice'} style={Object.assign({
      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
      margin: '0 0 12px', padding: '10px 12px', borderRadius: '6px',
    }, standalone ? { margin: 0, maxWidth: '520px', width: '100%', boxSizing: 'border-box', textAlign: 'left', border: '1px solid' } : null)}>
      <span className="sheet-notice-text" style={{ flex: 1, minWidth: '180px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45 }}>
        {children}
      </span>
      {onAction ? (
        <button type="button" className="sheet-notice-act" data-testid={actTestid} onClick={onAction} style={{
          flexShrink: 0, minHeight: '44px', padding: '7px 16px', cursor: 'pointer',
          borderRadius: '5px',
          fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', fontWeight: 700,
        }}>{actionLabel}</button>
      ) : null}
    </div>
  );
}

function ModsLoadNotice({ onRetry }) {
  return (
    <SheetNotice actionLabel="RETRY" onAction={onRetry} testid="notice-mods-load">
      reSpecialized data didn&rsquo;t load — showing the standard talent trees.
    </SheetNotice>
  );
}

// B-250 (R-B250-2). MECHANICS VOICE — says what happened, what is safe, and what to do, with no
// metaphor and no apology. The three facts are the ruling's; the wording is mine within them.
// DISMISS deletes the marker field, so a player who has read it never sees it again — the notice
// cannot become furniture on a character whose owner already acted on it.
function XpRepairedNotice({ onDismiss }) {
  return (
    <SheetNotice actionLabel="DISMISS" onAction={onDismiss} testid="notice-xp-repaired">
      This character&rsquo;s XP record was damaged by an old bug and has been reset to 0.
      Everything you bought is still here — only the XP totals were cleared.
      Ask your GM to re-grant your XP.
    </SheetNotice>
  );
}

/* ══════════════════════════════════════════════════════
   SLIDE DATA
   Replace `url: ''` with real image paths when assets are ready.
   Images should be portrait-oriented, high contrast subjects.
   Ken Burns keyframe: 'kb1' | 'kb2' | 'kb3' | 'kb4'
══════════════════════════════════════════════════════ */
const WIPE_TYPES = ['wipeHorizontal', 'wipeDiagonal', 'wipeIris'];
// WIPE_DURATIONS → window.SWRPGUI (lib/shared/ui.jsx), destructured at top. [Phase 1b]

/* ══════════════════════════════════════════════════════
   SLIDESHOW
══════════════════════════════════════════════════════ */
function Slideshow({ slides, active }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), 7000);
    return () => clearInterval(t);
  }, [active, slides.length]);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {slides.map((slide, i) => (
        <div
          key={i}
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: slide.url
              ? `url(${slide.url})`
              : slide.fallback,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: i === idx ? 1 : 0,
            transition: 'opacity 1.6s ease',
            animation: i === idx
              ? `${slide.ken} 12s ease-in-out infinite alternate`
              : 'none',
            willChange: 'transform, opacity',
          }}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   STATIC BG (Quick Build)
══════════════════════════════════════════════════════ */
function StaticBg({ bg }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: bg.url ? `url(${bg.url})` : bg.fallback,
      backgroundSize: 'cover',
      backgroundPosition: 'center top',
    }} />
  );
}

/* ══════════════════════════════════════════════════════
   SILHOUETTE OVERLAY (Custom card)
══════════════════════════════════════════════════════ */
function SilhouetteLayer({ active }) {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* Center figure */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: '50%',
        transform: 'translateX(-50%)',
        width: '38%', height: '85%',
        background: 'linear-gradient(180deg, transparent 0%, #000 15%)',
        clipPath: 'polygon(30% 0%, 70% 0%, 85% 30%, 90% 70%, 80% 100%, 20% 100%, 10% 70%, 15% 30%)',
        opacity: active ? 0.85 : 0.5,
        transition: 'opacity 0.8s ease',
      }} />
      {/* Left figure */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: '12%',
        width: '28%', height: '72%',
        background: '#000',
        clipPath: 'polygon(28% 0%, 72% 0%, 88% 35%, 90% 100%, 10% 100%, 12% 35%)',
        opacity: active ? 0.65 : 0.35,
        transition: 'opacity 0.8s ease',
      }} />
      {/* Right figure */}
      <div style={{
        position: 'absolute',
        bottom: 0, right: '12%',
        width: '28%', height: '68%',
        background: '#000',
        clipPath: 'polygon(25% 0%, 75% 0%, 85% 30%, 88% 100%, 12% 100%, 15% 30%)',
        opacity: active ? 0.6 : 0.3,
        transition: 'opacity 0.8s ease',
      }} />
    </div>
  );
}

// (AccordionCard REMOVED — player-hubcard: the landing now uses the shared SWRPGUI.HubCard in its
// expandable+art variant; one card system across the app. The bespoke AccordionCard is deleted.)

/* ══════════════════════════════════════════════════════
   CARD DEFINITIONS
══════════════════════════════════════════════════════ */
// Prominence swap (landing-onboarding-streamline): the mode name is now the
// PROMINENT title; the old tagline-y title becomes the small eyebrow. Descriptions
// migrated verbatim from the retired first-visit landing overlays (Jason-approved).
const CARDS = [
  {
    id: 'pregen',
    tourId: 'p-landing-card-pregen',
    label: 'PICK A CHARACTER',
    title: 'Quick Start',
    accent: '#4EAAFF',
    glow: 'rgba(78,170,255,1)',
  },
  {
    id: 'quickbuild',
    tourId: 'p-landing-card-quickbuild',
    label: 'BUILD YOUR LEGEND',
    title: 'Guided Build',
    accent: '#4EAAFF',
    glow: 'rgba(78,170,255,1)',
  },
  {
    id: 'custom',
    tourId: 'p-landing-card-custom',
    label: 'FORGE YOUR IDENTITY',
    title: 'Create Your Character',
    accent: '#4EAAFF',
    glow: 'rgba(78,170,255,1)',
  },
];

// THE YARD'S ACCENT, ITS ONE HOME. The old landing's Ship Yard card was deleted in N-post (card `paper-roster-delete`
// JOB 2) with the landing it lived on; SHIP YARD S4 (carry 766f215c) reads the player Yard's accent from here
// (`YARD_CARD.accent`, four sites), so the one field it reads is kept under its name. Nothing else of the card is.
const YARD_CARD = {
  accent: '#4EAAFF',
};

/* ══════════════════════════════════════════════════════
   SWIPE BACK HOOK
══════════════════════════════════════════════════════ */
// useSwipeBack + SWIPE_BACK_EDGE_PX → window.SWRPGUI (lib/shared/ui.jsx), destructured at top. [Phase 1b]

// NPC library view loaded on demand at first use, not at boot.
var _npcLibPromise = null;
function ensureNpcLibrary() {
  if (window.SWRPGLibrary) return Promise.resolve(window.SWRPGLibrary);
  if (_npcLibPromise) return _npcLibPromise;
  _npcLibPromise = new Promise(function(resolve, reject) {
    var s = document.createElement('script');
    s.src = '/lib/library/npc/view.js';
    s.onload = function() { resolve(window.SWRPGLibrary); };
    s.onerror = function() { _npcLibPromise = null; reject(new Error('Failed to load NPC library')); };
    document.head.appendChild(s);
  });
  return _npcLibPromise;
}

/* ══════════════════════════════════════════════════════
   NPC LIBRARY (player host — Step 2)
   Mounts the shared window.SWRPGLibrary.NPC.Library (the headless core) as a
   FULL MIRROR: same browse/drill/detail/history screens as the GM. Since the
   flip's deletion ledger (B-871) the library has ONE face, paper, and draws its
   own head, tabs and rail; the host passes no chrome.
   Browse-only: actions=null, capabilities={} — the player declares neither capability (B-863 deleted the inert seed flag).
══════════════════════════════════════════════════════ */
const NPC_LIB_ACCENT = '#4EAAFF';  // the NPC library's identity accent (matches the module's NPC_ACCENT)

// Thin player host — mounts the shared core, browse-only (no capabilities).
function PlayerNpcLibrary({ onBack, location, onLocation }) {
  const [Lib, setLib] = useState(function() { return window.SWRPGLibrary ? window.SWRPGLibrary.NPC.Library : null; });
  const [err, setErr] = useState(null);

  useEffect(function() {
    if (Lib) return;
    ensureNpcLibrary().then(function(mod) {
      setLib(function() { return mod.NPC.Library; });
    }).catch(function(e) { setErr(e.message); });
  }, []);

  // B-1257 (DECISIONS D-411 item 14, the 247 page): D-400's shared notice with BACK on the creator's paper, the new words
  if (err) return (
    <div data-testid="npc-load-error" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100%', padding: '24px', textAlign: 'center', gap: '16px' }}>
      <div data-doc-ground="creator" style={{ width: '100%', maxWidth: '520px', display: 'flex', justifyContent: 'center' }}>
        <SheetNotice testid="npc-load-notice" paper standalone actionLabel="BACK" onAction={onBack}>
          The NPC library didn&rsquo;t finish downloading. Check your connection, then go back and open it again.
        </SheetNotice>
      </div>
    </div>
  );

  if (!Lib) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: NPC_LIB_ACCENT, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
      letterSpacing: '0.14em' }}>
      LOADING…
    </div>
  );

  return (
    <Lib
      onBack={onBack}
      accent={NPC_LIB_ACCENT}
      actions={null}
      capabilities={{}}
      location={location}
      onLocation={onLocation}
    />
  );
}

/* F-46 Desktop Curation (G10): useLayoutMode() is the shared hook (window.SWRPGDesktop, destructured
   at top) — one home for app.jsx AND the store/NPC kernels. Re-renders on a mode flip; per A4 the
   flip is a pure restyle (the consumer keeps its tree, only geometry changes). */

/* ══════════════════════════════════════════════════════
   LANDING
══════════════════════════════════════════════════════ */
// THE FAN DISCLAIMER, ONE STRING (B-646) — the paper colophon in `LandingPaper` prints it. The rendered
// text is unchanged: JSX trims the whitespace around a text node, which is what this const holds.
const LANDING_DISCLAIMER = 'A fan-made tool for FFG Star Wars Roleplaying. Not affiliated with or endorsed by Fantasy Flight Games or Lucasfilm Ltd.';
// THE DRAFT'S DISPLAY STRINGS, ONE DERIVATION (B-647): UXQP-6's fallback chain for the paper landing's
// draft row. Field names differ per flow (custom stores registry records as speciesData/careerData;
// quickbuild stores species/role), which is the whole reason for the chain.
function landingDraftStrings(draft) {
  if (!draft) return { accent: null, title: '', meta: '' };
  var draftName = draft.character && draft.character.identity && draft.character.identity.name;
  var draftSpecies = draft.flow === 'quickbuild'
    ? (draft.species && draft.species.name)
    : (draft.speciesData && draft.speciesData.name);
  var draftCareer = draft.flow === 'quickbuild'
    ? (draft.role && draft.role.career)
    : (draft.careerData && draft.careerData.name);
  var GL = { eote: 'EDGE OF THE EMPIRE', aor: 'AGE OF REBELLION', fad: 'FORCE AND DESTINY' };
  return {
    accent: draft.flow === 'quickbuild' ? '#00C8FF' : '#F5A623',
    title: draftName || [draftSpecies, draftCareer].filter(Boolean).join(' ') || 'Unfinished character',
    meta: (draft.flow === 'quickbuild' ? 'GUIDED BUILD' : 'CREATE YOUR CHARACTER')
      + (GL[draft.gameLine] ? ' • ' + GL[draft.gameLine] : ''),
  };
}

/* ══════════════════════════════════════════════════════
   THE FRONT MATTER — the paper landing
   (B-646…B-652; ruling `landing-front-matter-concept` R-LFM-1..15 + Addenda 1–15;
   mockup `lab/holocron/landing.html` @ lab `1775bc79`, Jason 2026-09-17 "Approved" (Addendum 13))

   THE SHEET'S PRECEDENT, TAKEN: `SheetPaper` lives in this file rather than a `lib/shared` module,
   and so does this — a per-surface component of the Player app, not a shared core. What IS shared is
   the layer it sits on: `lib/shared/paper.css`'s plate kit and `lib/shared/landing.css`, the board's
   own stylesheet ported at that pin.

   ONE TREE, BOTH BANDS. The board drew the phone and the desk from two branches of `landing()`; this
   renders the bounded-desk structure once — `.ld-head` (title + lead) · `.ld-body` → `.ld-colL`
   (stage · draft · index · reading) + `.ld-colR` (CREATE · the doors) · the colophon — and
   `landing.css` lays it out as the approved phone frame below 1024 and as the desk from 1024 up.
══════════════════════════════════════════════════════ */

// THE ROLL CALL FILE, FETCHED ONCE PER PAGE AND CACHED AT MODULE LEVEL (R-LFM-3). The three door
// populations and the register's extent are its `populations`; the sampled names are its `doors`.
// `public/data/canonical/rollcall.json` is 24 names a door — under 4 KB — against 5.8 MB of registry,
// which is why the front page reads the derived file and never the registries.
var _ldRollcall = null;
var _ldRollcallPromise = null;
function loadLandingRollcall() {
  if (_ldRollcall) return Promise.resolve(_ldRollcall);
  if (_ldRollcallPromise) return _ldRollcallPromise;
  _ldRollcallPromise = fetch('public/data/canonical/rollcall.json')
    .then(function (r) { return r.json(); })
    .then(function (j) { _ldRollcall = j; return j; })
    .catch(function () { _ldRollcallPromise = null; return null; });
  return _ldRollcallPromise;
}
// AT IDLE AFTER FIRST PAINT, not during it: the landing is the first screen a cold load draws, and a
// figure is worth nothing if fetching it delays the page the figure is on. `requestIdleCallback` where
// it exists (Safari does not have it), a short timeout where it does not.
function useLandingRollcall() {
  var _s = React.useState(_ldRollcall), rc = _s[0], setRc = _s[1];
  React.useEffect(function () {
    if (rc) return undefined;
    var live = true;
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    var drop = window.cancelIdleCallback || clearTimeout;
    var handle = idle(function () {
      loadLandingRollcall().then(function (j) { if (live && j) setRc(j); });
    });
    return function () { live = false; try { drop(handle); } catch (e) {} };
  }, [rc]);
  return rc;
}

// A FIGURE IS DERIVED OR IT IS NOT DRAWN (R-LFM-1). `n == null` means the registry or the roll call
// has not landed: the slot is RESERVED AND EMPTY — never `0`, never a placeholder, never a dash — and
// `data-tally` is absent, so row 6's count-up has nothing to animate to until the number is real.
// F-100 C2 · F10a — the Homebrew tile, in the reference tiles' own markup (the peek's duplicate run included, so
// `.ld-tilebody`'s rules apply unchanged). Its figure is the records to start from; its line is the collections.
function LandingHomebrewTile(p) {
  var V = window.SWRPGEditorView, D = V.DOOR, f = V.useDoorFigures(), roll = V.doorRoll(f);
  return (
    <button type="button" className="hp-plate ld-tile" data-pad="read" data-landing-door="homebrew" onClick={p.onEnter('homebrew')}>
      <span className="ld-tilehead">
        <span className="hp-row-desig">{D.title}</span>
        {ldFigure('homebrew', f.total, D.noun)}
      </span>
      <span className="ld-tilebody">
        <span className="ld-chrun ld-roll">{roll}</span>
        <span className="ld-peek-text"></span>
        <span className="ld-chrun ld-roll ld-peek-dup" data-peek-copy="2" aria-hidden="true">{roll}</span>
        <span className="ld-peek-text ld-peek-dup" aria-hidden="true"></span>
      </span>
      <span className="ld-chev">&rsaquo;</span>
    </button>);
}
function ldFigure(kind, n, noun) {
  return (
    <span className="ld-fig" data-landing-fig={kind} key={kind}>
      <b data-tally={n == null ? undefined : n}>{n == null ? '' : n.toLocaleString('en-US')}</b>
      <i>{noun}</i>
    </span>
  );
}
// The same number set inline, in the lead's own sentence — no `.ld-fig` column, the hook and the
// tally only, so row 6 animates the lead's figures with the contents'.
function ldInline(kind, n) {
  return (
    <span data-landing-fig={kind} data-tally={n == null ? undefined : n} key={kind}>
      {n == null ? '' : n.toLocaleString('en-US')}
    </span>
  );
}

// THE ROLLED CHARACTER (R-LFM-2 as Addendum 1 replaced it — Jason 2026-09-16: "a system that generates
// a new character every visit to the landing screen"). ONE PICK PER MOUNT, `Math.random()` the seed, so
// a fresh visit is a fresh character; the board rolls the same populations by digest so a frame stays an
// address. THE POPULATIONS ARE STATED, not filtered by hand: careers whose specializations the register
// actually carries, one specialization OF that career, and a species that resolves WITHOUT A PLAYER'S
// DECISION — the ones carrying `option_choices` or `skill_choice_grants` need a choice the roll cannot
// make for them, so they wait for the creator. The character is FRESH: nothing raised, nothing bought,
// so every number on the stage is the species' own.
function ldRollCharacter(cb) {
  if (!cb || !cb.species || !cb.careers || !cb.specializations) return null;
  var pick = function (keys) { return keys[Math.floor(Math.random() * keys.length)]; };
  var careerKeys = Object.keys(cb.careers).filter(function (k) {
    return (cb.careers[k].specializations || []).some(function (s) { return cb.specializations[s]; });
  });
  var speciesKeys = Object.keys(cb.species).filter(function (k) {
    var v = cb.species[k];
    return v && v.characteristics && !v.option_choices && !v.skill_choice_grants;
  });
  if (!careerKeys.length || !speciesKeys.length) return null;
  var career = cb.careers[pick(careerKeys)];
  var specIds = (career.specializations || []).filter(function (s) { return cb.specializations[s]; });
  var spec = cb.specializations[pick(specIds)];
  var species = cb.species[pick(speciesKeys)];
  return { species: species, career: career, spec: spec };
}

// THE RAIL — `SheetSpine`'s own markup (`data-testid="sheet-rail"`), one kit, one drawing. FULL WORDS,
// never abbreviations (the ban's item 8): a frontispiece has one rail and all the width it needs.
function ldRail(chars) {
  return (
    <div className="hp-railwrap"><div className="hp-plate" data-pad="snug">
      <div className="hp-rail" data-landing-rail="1">
        {FC_CHAR_ORDER.map(function (a) {
          return (
            <div className="hp-char" key={a} data-char={a}>
              <span className="hp-char-val"><span className="hp-char-bead">{(chars && chars[a]) || 0}</span></span>
              <i className="hp-char-lab">{FC_CHAR_FULL[a]}</i>
            </div>);
        })}
      </div>
    </div></div>
  );
}

// THE CLOCK (R-LFM-4) — the lab's `ago()`, in the lab's words: minutes, hours, days, then the date.
// Nothing here rounds a number into a mood. Production had no such helper; this is the landing's.
function ldAgo(ms) {
  var d = Math.max(0, Date.now() - (ms || 0));
  var mins = Math.floor(d / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + ' minute' + (mins !== 1 ? 's' : '') + ' ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + ' hour' + (hrs !== 1 ? 's' : '') + ' ago';
  var days = Math.floor(hrs / 24);
  if (days < 30) return days + ' day' + (days !== 1 ? 's' : '') + ' ago';
  try {
    return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) { return ''; }
}
// The clock re-renders each minute while the landing is mounted — the one idle value on the page that
// really moves, and it carries no animation (data does not fidget).
function useLandingMinute() {
  var _t = React.useState(0), tick = _t[0], setTick = _t[1];
  React.useEffect(function () {
    var id = setInterval(function () { setTick(function (n) { return n + 1; }); }, 60000);
    return function () { clearInterval(id); };
  }, []);
  return tick;
}

// A RECORD'S THREE-FIELD SORT, PRODUCTION'S OWN (`RosterScreen`'s, quoted): last-opened first is true
// of the common case and not of the field, so all three are read in order.
function ldRecency(r) { return (r.lastOpenedAt || r.updatedAt || r.savedAt || 0); }

// THE ARRANGED ORDER (B-649 · R-LFM-12, concept A4 — the durable-contract change surfaced on paper
// before this code). A roster record MAY carry `order`, an integer the landing's ARRANGE writes on
// every record (0..n−1). The sort honours it the moment ANY record carries one; a record without one
// sorts AFTER the arranged ones, among themselves by the three-field sort — so a roster arranged on
// one device and grown on another (`order` rides the record, LIST-merge by id) reads in a stated
// order rather than an accident. A roster that has NEVER been arranged carries no `order` at all and
// keeps today's last-opened sort exactly (`ldOrdered` returns the same list `sort(ldRecency)` did).
function ldHasOrder(r) { return !!r && typeof r.order === 'number' && isFinite(r.order); }
function ldOrdered(list) {
  var any = (list || []).some(ldHasOrder);
  return (list || []).slice().sort(function (a, b) {
    if (any) {
      var ao = ldHasOrder(a), bo = ldHasOrder(b);
      if (ao && bo && a.order !== b.order) return a.order - b.order;
      if (ao !== bo) return ao ? -1 : 1;
    }
    return ldRecency(b) - ldRecency(a);
  });
}

// THE SPECIALIZATION'S NAME off a saved record: the registry's name for `sheetData.specializations[0].id`
// when the id resolves, else the record's own display string. Old saves key specializations by a tree key
// the registry never held (`guardiansoresudefender`), which is exactly why the fallback exists.
function ldSpecName(sheet, cb) {
  var first = sheet && sheet.specializations && sheet.specializations[0];
  var id = first && (typeof first === 'string' ? first : first.id);
  var reg = cb && cb.specializations && id ? cb.specializations[id] : null;
  return (reg && reg.name) || (sheet && sheet.specialization) || '';
}

// THE DESCRIPTORS (R-LFM-9/13, A5, A12) — the registry's OWN `description`, authored on all 21 careers
// and all 137 specializations. An empty one omits its plate rather than printing a placeholder.
function ldDescriptors(career, spec) {
  var out = [];
  if (career && (career.description || '').trim()) {
    out.push({ key: 'career', kind: 'Career', name: career.name, text: career.description.trim() });
  }
  if (spec && (spec.description || '').trim()) {
    out.push({ key: 'spec', kind: 'Specialization', name: spec.name, text: spec.description.trim() });
  }
  return out;
}

// THE THREE CREATE DOORS' OWN TITLES, ONE SOURCE (B-719). The approved landing draws them (its
// `ways` below reads this) and the on-ramp PRINTS ITS TITLE BACK as its designation; the doors'
// sentences are cut (copy-voice-sweep player/app.jsx:627:45..629:44, D-289). The lab board derives them from the landing
// generator's `plate()` calls for exactly this reason (`LANDING_DOORS`, `qsg_build.py`) — a second
// copy would drift the first time Jason rewords one, and copy that drifts between the door and the
// room is the class of defect B-717 just spent a row on. The landing's figures stay in `ways`, where
// they are computed; only the COPY is here.
var LANDING_DOORS = {
  custom:     { title: 'Custom Build' },
  quickbuild: { title: 'Guided Build' },
  pregen:     { title: 'Quick Start' },
};
// THE APP'S OWN TWO WORDS, read rather than retyped for the same reason: the runhead on either
// on-ramp says where BACK goes (the app), and the kicker over the designation is the bar's own label
// for the chapter you are inside. Both are the `BottomNav` props below, and the board reads them out
// of this file (`BAR_LABEL`, `BAR["create"]`).
var PLAYER_BAR_COPY = { app: 'Player Datapad', create: 'Create' };

// I6 · THE ROLL CALL'S HOLD: every name stands this long, then all of them change together (R-PLF-2, D-390).
var LD_ROLL_HOLD_MS = 3000;

// A10 · THE PEEK'S CLOCK (B-790, Jason's walk 2026-09-22: "too fast a reading speed"). THE RATE IS
// STATED IN SECONDS PER LINE and the pixels are DERIVED at run time from the peek text's own computed
// `line-height`, so the only typed figure is a dwell in seconds and the motion tracks the type if the
// type is ever re-cut. Measured 2026-09-22 at 1440x900: one line of `.ld-peek-text` is 20.8px
// (16px x 1.3), so 2.0s/line is 10.4px/s, and the Store tile's 393px run is a 38s loop.
// THE BASIS FOR 2.0: the page's own roll call (I6, below) holds each name 3s with a 600ms crossfade,
// and a sentence line read continuously wants less dwell than a name read as a glance. THE NUMBER IS
// JASON'S to settle; what this replaced was wrong at any number, because it was counted in FRAMES —
// `pos += 0.28` once per `requestAnimationFrame` is 16.8px/s at 60Hz and 33.6px/s on a 120Hz display,
// which makes the reading speed the DISPLAY'S and not the page's.
var LD_PEEK_SECONDS_PER_LINE = 2.0;
// WHAT PEEKS: the three reference tiles and, since D-390 item 4 (R-PLF-5), the Career and Specialization
// teasers — one clock, one rate, one hover trigger. A host's body is the scroller the clock moves.
var LD_PEEK_HOSTS = '.ld-tile, .ld-desc';
var LD_PEEK_BODIES = '.ld-tilebody, .ld-readwin';
var LD_PEEK_ENTER_HOLD_MS = 500;      // the beat before the body starts moving (was 30 frames)
var LD_PEEK_WRAP_HOLD_MS = 1500;      // the beat at the top of the run, so the loop point reads (was 90 frames)
// A BACKGROUNDED TAB OR A BLOCKED MAIN THREAD hands the next callback a multi-second delta, which
// would jump the box the whole width of the run. Clamped to two 60Hz frames' worth: a stall costs
// MOTION, never a JUMP.
var LD_PEEK_MAX_STEP_MS = 34;

function LandingPaper(p) {
  var mode = useLayoutMode();              // the app's 1024 seam; the page's BANDS are container queries in landing.css
  var canonReady = useCanonicalReady();    // species/careers/specializations — the creator waits for it, so does this
  var rollcall = useLandingRollcall();
  var cb = canonReady ? (window._canonicalCharbuild || window._canonicalCharbuildBase) : null;
  var pops = (rollcall && rollcall.populations) || {};
  var count = function (table) { return table ? Object.keys(table).length : null; };
  var figs = {
    species: count(cb && cb.species),
    careers: count(cb && cb.careers),
    specs: count(cb && cb.specializations),
    npcs: pops.npcs != null ? pops.npcs : null,
    yard: pops.yard != null ? pops.yard : null,
    store: pops.store != null ? pops.store : null,
    books: pops.books != null ? pops.books : null,
    pregen: PREGEN_CHARACTERS.length,      // the pre-built count is in the build, so it is never absent
  };
  var state = (p.savedChars && p.savedChars.length) ? 'roster' : 'first';
  useLandingMinute();                      // the index's clock, re-read each minute

  // ── THE ROSTER, AS THE FRONT MATTER READS IT (R-LFM-6) ────────────────────────────────────────
  // The current character is the one `swrpg_last_sheet_id` names (UXQP-5: which character), then the
  // app's own open record, then the first of the three-field sort — R-LFM-4's dangling-id fallback,
  // stated as the chain rather than as a special case.
  var saved = p.savedChars || [];
  // B-649: `ldOrdered` IS the three-field sort until a record carries `order` (R-LFM-12) — one sort,
  // not a branch the reader has to keep two versions of in their head.
  var sorted = React.useMemo(function () { return ldOrdered(saved); }, [saved]);
  var lastId = null;
  try { lastId = window.localStorage.getItem('swrpg_last_sheet_id'); } catch (e) {}
  var byId = function (id) { return id ? sorted.filter(function (r) { return r.id === id; })[0] : null; };
  var current = byId(lastId) || byId(p.activeId) || sorted[0] || null;
  var rest = sorted.filter(function (r) { return r !== current; });

  // ── THE STAGE ─────────────────────────────────────────────────────────────────────────────────
  // FIRST VISIT: the rolled character, ONE PICK PER MOUNT. The memo's dependency is the registry
  // landing, not the render — a character that re-rolled on every keystroke would be a slot machine.
  var roll = React.useMemo(function () { return cb ? ldRollCharacter(cb) : null; }, [cb]);
  var mine = !!current;
  var sheet = (current && current.sheetData && typeof current.sheetData === 'object') ? current.sheetData : null;
  // A SAVED RECORD KEEPS DISPLAY STRINGS, NOT IDS ("Mystic", and older saves "guardian"), so the career
  // and the specialization are resolved back through the registry BY NAME: the register's own spelling
  // is what the page prints, and it is also what carries the description the reading needs. Unresolved
  // → the record's own string, verbatim. One lookup, two consumers (the sub line and the descriptors).
  var regByName = function (table, name) {
    if (!table || !name) return null;
    var keys = Object.keys(table);
    for (var i = 0; i < keys.length; i++) {
      if ((table[keys[i]].name || '').toLowerCase() === String(name).toLowerCase()) return table[keys[i]];
    }
    return null;
  };
  var savedCareer = mine ? regByName(cb && cb.careers, (sheet && sheet.career) || current.career) : null;
  var savedSpec = mine ? regByName(cb && cb.specializations, ldSpecName(sheet, cb)) : null;
  var stage = mine
    // A RECORD WHOSE `sheetData` IS NOT AN OBJECT still draws (B-405's class): the record's own
    // name/species/career and an EMPTY rail. Never a throw on the front page.
    ? {
      name: (sheet && sheet.name) || current.name || '',
      species: (sheet && sheet.species) || current.species || '',
      career: (savedCareer && savedCareer.name) || (sheet && sheet.career) || current.career || '',
      spec: (savedSpec && savedSpec.name) || ldSpecName(sheet, cb),
      chars: sheet && sheet.characteristics,
      career_reg: savedCareer, spec_reg: savedSpec,
    }
    : (roll ? {
      name: roll.species.name + ' ' + roll.career.name,
      species: roll.species.name, career: roll.career.name, spec: roll.spec.name,
      chars: roll.species.characteristics,
      career_reg: roll.career, spec_reg: roll.spec,
    } : null);

  // THE DESCRIPTORS' SOURCE RECORDS. On a first visit they are the rolled character's own; for a saved
  // character they are resolved out of the registry by NAME, because a record keeps display strings
  // ("Mystic") and not the registry id the description hangs off.
  var descRecs = React.useMemo(function () {
    if (!cb || !stage) return [];
    return ldDescriptors(stage.career_reg, stage.spec_reg);
  }, [cb, stage && stage.career_reg, stage && stage.spec_reg]);
  var teaser = mine;                       // §171: with no roster the reading is WHOLE; with one, teasers

  // A9 · THE READING OPENS, AND ITS FORM IS THE BAND'S (R-LFM-13 as A12 amended it). The desk opens a
  // pane over the stage column (the page cannot grow); the phone UNFOLDS the teaser where it stands.
  // The band is measured on the SURFACE at press time — the lab's runtime branch, as React state —
  // rather than read off `mode`, because the page's bands are its container's and not the viewport's.
  var surfRef = React.useRef(null);
  var _read = React.useState(null), read = _read[0], setRead = _read[1];
  var pressRead = function (key) {
    var w = surfRef.current ? surfRef.current.getBoundingClientRect().width : 0;
    var inline = w > 0 && w < 1024;
    setRead(function (prev) {
      if (prev && prev.key === key) return null;
      return { key: key, inline: inline };
    });
  };
  // A11 · EXPLORE THIS CHARACTER — the phone's callout (Jason 2026-09-17: "For mobile only"). The
  // callout is drawn on every band and the stylesheet hides it from 1024 up, where the descriptors are
  // already in view; `data-explore` on the stage column is what the phone's rule reads.
  var _ex = React.useState(false), explore = _ex[0], setExplore = _ex[1];

  var ds = landingDraftStrings(p.draft);

  /* ══ B-651 · THE MOTION (LANDING_SHAPE §3.7's catalog, the lab's runtime as React) ═════════════
     FOUR LAWS, and they are the board's: every animated property is `transform` or `opacity`; every
     event has a reduced-motion still form; at most three idle loops run ON THE PAGE; data does not
     fidget. I1 (the sweep), I7 (the reading's drift), A7 (the lift), A8 (the marks), A9 (the pane's
     open) and EVERY still form are CSS and already in `landing.css` — none of them is repeated here.
     What is here is the five events a stylesheet cannot express: a count, a clock, a scroll, and the
     one-shot classes a press adds.

     THE STILL FORM OF A ONE-SHOT IS THE STATE IT LANDS IN, so under reduced motion the class is never
     added and the act happens at once — `play()` and `fold()` below both read that way, and neither
     carries a second code path for it. */
  var reduced = usePrefersReducedMotion();
  // AN IDLE LOOP RUNS ONLY WHERE SOMEONE IS LOOKING. The landing is MOUNTED behind every other screen
  // (the wrapper's visibility rule is what hides it), so a clock started at mount would tick through
  // the whole of a creator session at one name a second. `p.visible` is the host's screen, the tab's
  // own visibility is the browser's, and the CSS loops stop with them too (`data-live`, landing.css).
  var _tv = React.useState(true), tabVisible = _tv[0], setTabVisible = _tv[1];
  React.useEffect(function () {
    var on = function () { setTabVisible(!document.hidden); };
    document.addEventListener('visibilitychange', on);
    on();
    return function () { document.removeEventListener('visibilitychange', on); };
  }, []);
  var live = (p.visible !== false) && tabVisible;
  var running = live && !reduced;

  // A1 · THE TALLY — each figure counts up from zero to the value ALREADY IN THE MARKUP, over 900ms,
  // easing out, landing exactly on it. The markup carrying the final value is what makes this safe:
  // a page with no script, a page under reduced motion and a page mid-count are all correct at rest,
  // and the count is decoration over a number that is already right. Each element is counted ONCE —
  // arrival, not re-render — which the WeakSet remembers without holding a node alive.
  var seenTally = React.useRef(null);
  if (!seenTally.current) seenTally.current = new WeakSet();
  React.useEffect(function () {
    var root = surfRef.current;
    if (!root || reduced || !live) return undefined;
    var seen = seenTally.current, cancels = [];
    [].slice.call(root.querySelectorAll('[data-tally]')).forEach(function (el) {
      if (seen.has(el)) return;
      var target = parseInt(el.getAttribute('data-tally'), 10);
      if (!isFinite(target)) return;
      seen.add(el);
      var t0 = null, handle = null;
      var step = function (ts) {
        if (t0 === null) t0 = ts;
        var k = Math.min(1, (ts - t0) / 900);
        el.textContent = Math.round(target * (1 - Math.pow(1 - k, 3))).toLocaleString('en-US');
        if (k < 1) handle = requestAnimationFrame(step);
        else { handle = null; el.textContent = target.toLocaleString('en-US'); }
      };
      handle = requestAnimationFrame(step);
      // the cleanup LANDS THE NUMBER rather than leaving it mid-count: an unmount in the middle of a
      // tally must not be the reason a figure reads 812 of 1,430.
      cancels.push(function () {
        if (handle) cancelAnimationFrame(handle);
        el.textContent = target.toLocaleString('en-US');
      });
    });
    return function () { cancels.forEach(function (c) { c(); }); };
  }, [reduced, live, figs.species, figs.careers, figs.specs, figs.npcs, figs.yard, figs.store,
      figs.books, figs.pregen, mine]);

  // I6 · THE ROLL CALL — ONE CLOCK, every line changing TOGETHER (D-390 item 1, card `player-landing-fixes`
  // R-PLF-2, overruling R-LFM-3's "one change at a time"). Each tick is one HOLD (3s, the hold each line had
  // when three doors took turns on a 1s stagger) and moves every un-hovered door at once, in one crossfade.
  // The crossfade is CSS (`.ld-roll{transition:opacity .6s}`); this only sets `data-fade` and swaps the
  // names at the fade's own end — in ONE state write, so the lines land in the same frame.
  // A HOVERED TILE HOLDS (Jason 2026-09-16: "when in the hover state, the roll call stops") and rejoins at
  // the next shared change.
  var _rc = React.useState({}), rollAt = _rc[0], setRollAt = _rc[1];
  var _rf = React.useState(null), rollFade = _rf[0], setRollFade = _rf[1];
  React.useEffect(function () {
    var doors = rollcall && rollcall.doors;
    if (!running || !doors) return undefined;
    var keys = ['npcs', 'yard', 'store'].filter(function (k) { return (doors[k] || []).length > 1; });
    if (!keys.length) return undefined;
    var fades = [];
    var id = setInterval(function () {
      var turn = keys.filter(function (k) {
        var line = surfRef.current && surfRef.current.querySelector('.ld-roll[data-roll="' + k + '"]');
        var tile = line && line.closest ? line.closest('.ld-tile') : null;
        return !(tile && tile.matches(':hover'));
      });
      if (!turn.length) return;
      var fading = {};
      turn.forEach(function (k) { fading[k] = true; });
      setRollFade(fading);
      fades.push(setTimeout(function () {
        setRollFade(function (cur) { return cur === fading ? null : cur; });
        setRollAt(function (m) {
          var n = {}; Object.keys(m).forEach(function (q) { n[q] = m[q]; });
          turn.forEach(function (k) { n[k] = (m[k] || 0) + 1; });
          return n;
        });
      }, 600));
    }, LD_ROLL_HOLD_MS);
    return function () { clearInterval(id); fades.forEach(clearTimeout); };
  }, [running, rollcall]);

  // A10 · THE PREVIEW READS — on hover the tile's BODY scrolls (a real `scrollTop`, not a transform:
  // the body is a scroll box the reader can also move with a wheel or a thumb), ONE LINE OF THE
  // DESCRIPTION EVERY `LD_PEEK_SECONDS_PER_LINE`, in ONE DIRECTION, wrapping at the end of the run and
  // reading it again; the pointer leaving returns it to the top. The position is kept as a FLOAT and
  // written rounded, which is the board's own hard-won line: a scrollTop grown by a fraction of a pixel
  // a frame is rounded back to where it was, and the first cut never moved at all.
  //
  // THE CLOCK IS TIME, NOT FRAMES (B-790). `pos` is advanced by the delta between the timestamps the
  // rAF callback is handed — the idiom A1's count-up above already uses (`(ts - t0) / 900`) — so the
  // rate is the page's at every refresh rate. The holds are in ms for the same reason.
  //
  // IT LOOPS RATHER THAN REWINDS (B-791, Jason's walk: "keep scrolling in the same direction but loop
  // the information"). A scroll box cannot wrap, so THE RUN EXISTS TWICE inside the body — the second
  // copy is `aria-hidden` and carries no `data-roll` (I6's effect takes the FIRST `.ld-roll[data-roll]`
  // it finds, so a copy carrying the attribute would steal the roll call). At `pos >= period` the
  // position drops back one run and the box is already showing the same pixels, because the second copy
  // is underneath. There is no `dir` at all: it is not pinned to 1, it is GONE, so nothing can
  // reintroduce a reversal.
  //
  // `mouseover`/`mouseout` rather than `mouseenter`/`mouseleave`: these bubble, so ONE pair of
  // listeners on the surface covers every tile and survives React re-rendering the tiles under them.
  React.useEffect(function () {
    var root = surfRef.current;
    if (!root) return undefined;
    var raf = null, body = null, hold = 0, pos = 0, last = null, rate = 0, wrote = null;
    // THE RUN'S PERIOD is the distance from the first copy's first line to the SECOND copy's — the two
    // boxes' OWN `offsetTop`s, which is the wrap distance with the gap between the copies inside it.
    // NEVER `scrollHeight`: that is `max(content, clientHeight)`-shaped, so on a tile whose content does
    // not fill it it reads the BOX and not the RUN — the trap B-773 hit, a 900px plant reading +329.
    // Re-read every frame: the roll call can land a new description (and so a new height) under the
    // pointer when a crossfade was already in flight as the hover began.
    // The run's first line is the body's first child: a tile whose sentence is cut (copy-voice-sweep, D-289) starts at its roll call.
    function periodOf(b) {
      var a = b.firstElementChild, c = b.querySelector('[data-peek-copy="2"]');
      return (a && c) ? (c.offsetTop - a.offsetTop) : 0;
    }
    // ONE LINE PER `LD_PEEK_SECONDS_PER_LINE`, the line READ OFF THE ELEMENT rather than typed.
    function rateOf(b) {
      var t = b.querySelector('.ld-peek-text, .ld-readtext');
      var lh = t ? parseFloat(getComputedStyle(t).lineHeight) : NaN;
      return (isFinite(lh) && lh > 0) ? (lh / LD_PEEK_SECONDS_PER_LINE) : 0;
    }
    function step(ts) {
      raf = requestAnimationFrame(step);
      if (!body) return;
      if (last === null) { last = ts; return; }   // the first callback only starts the clock
      var dt = ts - last;
      last = ts;
      if (!(dt > 0)) return;
      if (dt > LD_PEEK_MAX_STEP_MS) dt = LD_PEEK_MAX_STEP_MS;
      // THE READER'S OWN HAND WINS. This clock writes `scrollTop` every frame, so a wheel or a thumb
      // that moved the box between two frames would be written straight back and the text would be
      // immovable under the very pointer that is making it drift. If the box is not where the last
      // frame left it, the READER moved it: adopt the position and read on from there — which is what
      // makes "the run is in there twice" something you can also scroll by hand (B-791). Compared
      // against what was READ BACK after the write, not against `pos`, so the browser's own clamping
      // is never mistaken for a reader.
      if (wrote !== null && Math.abs(body.scrollTop - wrote) >= 1) { pos = body.scrollTop; }
      if (hold > 0) { hold -= dt; return; }
      var period = periodOf(body);
      // THE WRAP IS SEAMLESS ONLY WHILE THE SECOND COPY CAN COVER THE BOX at the wrap point
      // (`period + clientHeight <= scrollHeight`). A copy shorter than the box would clamp mid-run and
      // read as a stall at a random place, so the peek holds still instead — the wheel and the thumb
      // still reach the text, which is the same floor reduced motion is held to.
      if (!(rate > 0) || !(period > 0) || period + body.clientHeight > body.scrollHeight) return;
      pos += rate * (dt / 1000);
      if (pos >= period) {                        // THE LOOP: one run further down, the same pixels
        pos -= period;
        if (!(pos >= 0 && pos < period)) pos = 0; // a period that shrank under the pointer
        hold = LD_PEEK_WRAP_HOLD_MS;
      }
      body.scrollTop = Math.round(pos);
      wrote = body.scrollTop;
    }
    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      if (body) body.scrollTop = 0;              // a tile that is left rests at the top of ONE copy
      body = null; pos = 0; hold = 0; last = null; rate = 0; wrote = null;
    }
    function onOver(ev) {
      var tile = ev.target && ev.target.closest ? ev.target.closest(LD_PEEK_HOSTS) : null;
      if (!tile) return;
      var b = tile.querySelector(LD_PEEK_BODIES);
      if (!b || b === body) return;
      stop();
      if (reduced) return;                  // no drift; the wheel and the swipe still reach the text
      body = b; pos = b.scrollTop; hold = LD_PEEK_ENTER_HOLD_MS; last = null; rate = rateOf(b);
      wrote = b.scrollTop;   // …and the entry hold is INSIDE the reader's reach too: seeded, not null
      raf = requestAnimationFrame(step);
    }
    function onOut(ev) {
      var tile = ev.target && ev.target.closest ? ev.target.closest(LD_PEEK_HOSTS) : null;
      if (!tile) return;
      if (ev.relatedTarget && tile.contains(ev.relatedTarget)) return;
      stop();
    }
    root.addEventListener('mouseover', onOver);
    root.addEventListener('mouseout', onOut);
    return function () {
      root.removeEventListener('mouseover', onOver);
      root.removeEventListener('mouseout', onOut);
      stop();
    };
  }, [reduced]);

  // THE ONE-SHOTS. `play` adds a class and takes it off on its own clock; `fold` is the one that has
  // an act on the other end of it, so it holds the act until the row has folded (200ms) and does it
  // at once when motion is reduced — A6's still form is a cut, not a slower fold.
  var play = function (el, cls, ms) {
    if (!el || reduced) return;
    el.classList.add(cls);
    setTimeout(function () { try { el.classList.remove(cls); } catch (e) {} }, ms);
  };
  var pageEl = function () { return surfRef.current ? surfRef.current.querySelector('.hp-sheet.ld') : null; };
  var fold = function (el, after) {
    if (!el || reduced) { after(); return; }
    el.classList.add('is-folding');
    setTimeout(function () { try { el.classList.remove('is-folding'); } catch (e) {} after(); }, 200);
  };
  // A3 · THE STAMP on the press · A4 · THE PAGE TURNS, and THEN the navigation happens — the turn is
  // the transition between two surfaces, so a navigation fired first would play it behind the screen
  // that had already arrived. 260ms is A4's own figure; the class comes off at 420 (the animation
  // fills forwards, so it must outlive the frame the navigation takes).
  var enter = function (id) {
    return function (ev) {
      if (!p.onEnter) return;
      play(ev.currentTarget, 'is-stamped', 200);
      if (reduced) { p.onEnter(id); return; }
      play(pageEl(), 'is-turningout', 420);
      setTimeout(function () { p.onEnter(id); }, 260);
    };
  };
  // A5 · OPEN LIFTS THE SHEET — the lift is the press's own answer, so the record opens on its heel.
  var pressOpen = function (ev) {
    if (!p.onOpenRecord || !current) return;
    if (reduced) { p.onOpenRecord(current); return; }
    play(ev.currentTarget, 'is-lifting', 260);
    setTimeout(function () { p.onOpenRecord(current); }, 260);
  };

  // ── WHERE THE PAGE LANDS (B-650 · R-LFM-6) ────────────────────────────────────────────────────
  // `/player/roster` lands on the index; the bar's CHARACTERS on the page's top; CREATE on the CREATE
  // chapter. The host states the zone (`p.focus = {zone, at}`) and this brings it into view — and
  // MOVES THE FOCUS RING there too, because a page that scrolls without moving focus has moved the
  // sighted reader and left everyone else where they were. The zones the effect reaches carry
  // `tabIndex={-1}`: reachable to be focused, never a stop in the tab order.
  // The frame the timeout buys is the render the zone arrives in — the index appears with the roster
  // and the roster arrives with the app's state, not with the mount.
  var focus = p.focus;
  var focusZone = focus && focus.zone, focusAt = focus && focus.at;
  React.useEffect(function () {
    if (!focusZone) return undefined;
    var id = setTimeout(function () {
      var root = surfRef.current;
      if (!root) return;
      var sheet = root.querySelector('.hp-sheet.ld');
      if (focusZone === 'top') {
        if (sheet) sheet.scrollTop = 0;
        var head = root.querySelector('[data-landing-zone="stage"]');
        if (head) { try { head.focus({ preventScroll: true }); } catch (e) {} }
        return;
      }
      var zone = root.querySelector('[data-landing-zone="' + focusZone + '"]');
      if (!zone) return;
      try { zone.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { zone.scrollIntoView(); }
      try { zone.focus({ preventScroll: true }); } catch (e) {}
    }, 60);
    return function () { clearTimeout(id); };
  }, [focusZone, focusAt, mine]);

  // ── ARRANGE, ITS DELETE-WITH-UNDO, AND THE DRAFT'S ARMED MARK (B-649 · R-LFM-12, A4; B-970; B-403) ──
  // EDIT is ARRANGE (Jason 2026-09-16: "Could we have it function so users can arrange the order of
  // their characters?"). Pressed, every index row grows a grab handle, ▲ ▼ and the delete mark, and
  // the head stops counting and says what the state is for. The state here is LOCAL PRESENTATION —
  // nothing about arranging is persisted until a move lands.
  var _ed = React.useState(false), edit = _ed[0], setEdit = _ed[1];
  var _dx = React.useState(false), draftArmed = _dx[0], setDraftArmed = _dx[1];
  // B-970 · D-234/D-237 (approved frame Docs/mockups/landing-arrange-undo.html @ c9df491cd) — ✕ DELETES AT ONCE, AND
  // EVERY DELETED ROW KEEPS ITS UNDO UNTIL THE PLAYER LEAVES ARRANGE. The old two-tap confirm grew 60px left, so a fast
  // second tap meant for ▼ landed on "Delete?"; there is no confirm now, and a deleted row keeps its place.
  // WHEN THE DELETE IS WRITTEN: on LEAVING, not on the tap. Until then the ids live HERE, in memory only — no save field,
  // no sync shape (contract C-3 iv) — so UNDO is taking an id back out of this list and the record was never touched
  // (iii), and another device can see nothing until the delete is real. Leaving (as approved): Done, opening a character
  // or any other screen (the landing stays mounted and is hidden, so that is `p.visible` turning false), and the page
  // closing or reloading (`pagehide`). Each then runs the host's own delete, once per id — the writer and its tombstone
  // as before, so a delete is final everywhere exactly as one was (ii). A tab killed with no `pagehide` writes
  // nothing: the characters are kept, never lost.
  var _gone = React.useState([]), goneIds = _gone[0], setGoneIds = _gone[1];
  var goneRef = React.useRef(goneIds);
  goneRef.current = goneIds;
  var onDeleteRef = React.useRef(p.onDelete);
  onDeleteRef.current = p.onDelete;
  var finalizeGone = function () {
    var ids = goneRef.current;
    if (!ids.length) return;
    goneRef.current = [];
    setGoneIds([]);
    ids.forEach(function (id) { if (onDeleteRef.current) onDeleteRef.current(id); });
  };
  React.useEffect(function () {
    if (p.visible === false) finalizeGone();
  }, [p.visible]);
  React.useEffect(function () {
    window.addEventListener('pagehide', finalizeGone);
    return function () { window.removeEventListener('pagehide', finalizeGone); finalizeGone(); };
  }, []);
  // ✕ and UNDO trade places in one spot, so the focus follows the press to the control that replaced it.
  var undoFocus = React.useRef(null);
  React.useEffect(function () {
    var want = undoFocus.current;
    if (!want) return;
    undoFocus.current = null;
    var b = surfRef.current && surfRef.current.querySelector(
      '.ld-idx .hp-row[data-landing-id="' + want.id + '"] [data-landing-roster="' + want.hook + '"]');
    if (b) try { b.focus({ preventScroll: true }); } catch (e) {}
  }, [goneIds]);
  React.useEffect(function () {
    if (!draftArmed) return undefined;
    var t = setTimeout(function () { setDraftArmed(false); }, 3000);
    return function () { clearTimeout(t); };
  }, [draftArmed]);

  // THE WIRE (R-LFM-12): a move writes `order` on EVERY record, 0..n−1, and it writes it through the
  // HOST's one roster writer — `LandingPaper` owns no storage (R-LFM-6), so the ids go up and
  // `writeSavedCharacters` stays where every other roster write already is.
  // THE STAGE'S CHARACTER LEADS THE LIST: the stage IS the top of the page (§169 — the index is the
  // REST), so the arrangement the player leaves is [the current character, then the rows as drawn].
  // A record the index is not drawing (there is none today) would keep its place at the end.
  // R-LFM-12 AS ADDENDUM 18 AMENDED IT — IN ARRANGE THE INDEX IS THE WHOLE ROSTER.
  // §169's index is THE REST, and that held until a roster of ONE: the stage carried the only
  // character, the index had no rows at all, and B-650 closed the last route to the old roster
  // screen — so the only character could be neither arranged nor deleted. The board's own
  // `roster_index()` draws a `Current` chip on the first row whenever nothing is skipped, and that
  // drawing IS this state. At REST the index stays the rest; in ARRANGE it is the whole roster, the
  // current row first with its chip, so the character on the stage moves and deletes like any other.
  // The failure line takes the same shape for the same reason: it says "it is listed below", and a
  // line that names a record the index is not drawing would be saying something untrue.
  // THE INDEX DRAWS THE ROSTER'S OWN ORDER and the chip follows the current character into it —
  // which IS "the current row first" in every state the player can arrive at: the openers stamp
  // `lastOpenedAt`, so the character on the stage is the first of the three-field sort, and once a
  // roster is arranged `order` decides for both the stage and the index. PINNING the current row to
  // the top was measured and rejected: ▼ on it wrote a new `order` that the pin then drew over, so
  // the one row the amendment exists to make movable was the one row that could not move.
  var whole = edit || !!p.statusLine;
  var viewList = whole ? sorted : rest;
  var viewIds = viewList.map(function (r) { return r.id; });
  var commitOrder = function (ids) {
    // ARRANGE draws the whole roster, so the order the player leaves IS the roster's order. At rest
    // no move is reachable (the marks are out of the tab order and untappable), but if one ever were,
    // the stage's character still leads — §169's own arrangement.
    if (p.onReorder) p.onReorder(whole ? ids : (current ? [current.id] : []).concat(ids));
  };
  // ▲ ▼ — the lab's runtime, as state: a row with no sibling in that direction does not move.
  var moveBy = function (id, delta) {
    var ids = viewIds.slice();
    var i = ids.indexOf(id), j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    ids.splice(j, 0, ids.splice(i, 1)[0]);
    commitOrder(ids);
  };
  // THE GRAB HANDLE'S DRAG. The frame draws a handle AND the arrows; the lab's runtime moved rows by
  // the arrows alone, so this is the one piece of ARRANGE with no runtime to port. Pointer events
  // (one code path for mouse, pen and touch), the rows' rects measured ONCE at grab time — the list
  // keeps its height while a row travels through it, so re-measuring per frame would only add jitter
  // — and the preview is LOCAL: nothing is persisted until the pointer is released.
  var _drag = React.useState(null), drag = _drag[0], setDrag = _drag[1];
  var grabDown = function (ev, id) {
    if (!edit) return;
    var host = ev.currentTarget.closest ? ev.currentTarget.closest('.ld-idx') : null;
    if (!host) return;
    var rects = [].slice.call(host.querySelectorAll('.ld-rrow')).map(function (r) {
      var b = r.getBoundingClientRect();
      return { id: r.getAttribute('data-landing-id'), top: b.top, bottom: b.bottom };
    });
    if (ev.preventDefault) ev.preventDefault();       // no text selection, no native drag image
    setDrag({ id: id, ids: viewIds.slice(), rects: rects });
  };
  var grabMove = function (ev) {
    if (!drag) return;
    var y = ev.clientY, over = -1;
    for (var i = 0; i < drag.rects.length; i++) {
      if (y >= drag.rects[i].top && y <= drag.rects[i].bottom) { over = i; break; }
    }
    if (over < 0) return;
    var ids = drag.ids.slice(), from = ids.indexOf(drag.id);
    if (from < 0 || from === over) return;
    ids.splice(over, 0, ids.splice(from, 1)[0]);
    setDrag({ id: drag.id, ids: ids, rects: drag.rects });
  };
  var grabUp = function () {
    if (!drag) return;
    var ids = drag.ids;
    setDrag(null);
    commitOrder(ids);
  };
  // THE MOVE AND THE DROP ARE LISTENED FOR ON THE WINDOW while a row is in the air, and that is not
  // a preference: the preview REORDERS the list, so React MOVES the dragged row's DOM node — and a
  // moved node LOSES ITS POINTER CAPTURE, after which the release lands on whatever row is now under
  // the pointer. Measured before this: the preview followed the pointer and the drop never
  // committed, so the rows sprang back on release. On the window there is no node to lose.
  React.useEffect(function () {
    if (!drag) return undefined;
    var onMove = function (ev) { grabMove(ev); };
    var onUp = function () { grabUp(); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return function () {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag]);
  // The rows the index DRAWS: the live drag's preview while a handle is held, else the sort's own order.
  var viewRows = drag
    ? drag.ids.map(function (id) { return viewList.filter(function (r) { return r.id === id; })[0]; }).filter(Boolean)
    : viewList;

  // ── THE CONTENTS (B-648) ──────────────────────────────────────────────────────────────────────
  // THE CREATE HEAD IS NOT A PRESS, and that is a decision the board makes rather than inherits: the
  // CREATE chapter has three presses under it and no fourth destination for its title to go to — the
  // `landing` route IS this page. A head that navigated to where you already are is a control with
  // nothing behind it, and two primary actions on one block is what the commit rule forbids.
  // B-719 — the three titles and the three sentences come from `LANDING_DOORS` above; the FIGURES
  // are computed here and stay here. Rendered strings are unchanged.
  var ways = [
    { id: 'custom', title: LANDING_DOORS.custom.title, first: true,
      figs: [['species', figs.species, 'species'], ['careers', figs.careers, 'careers'],
             ['specs', figs.specs, 'specializations']] },
    { id: 'quickbuild', title: LANDING_DOORS.quickbuild.title, figs: [] },
    { id: 'pregen', title: LANDING_DOORS.pregen.title,
      figs: [['pregen', figs.pregen, 'pre-built']] },
  ];
  // THE RUN LINE AND THE NUMERALS ARE ONE ADDRESS, drawn together: the stylesheet shows the run line
  // at the mid band (a ~150px plate has no room for three stacked numerals) and the figures at wide.
  var run3 = (figs.species != null && figs.careers != null && figs.specs != null)
    ? figs.species.toLocaleString('en-US') + ' species · ' + figs.careers.toLocaleString('en-US')
      + ' careers · ' + figs.specs.toLocaleString('en-US') + ' specializations'
    : '';

  // THE REFERENCE DOORS. The titles are the BOTTOM BAR's own words (`NPCs` · `Shipyard` · `Store` — the
  // bar's `items` literal in `PlayerApp`), so the landing and the bar cannot name the same door two
  // ways; the landing's smoke reads both and compares them rather than trusting this comment.
  var doorNames = (rollcall && rollcall.doors) || {};
  // I6 — THE NAME THE LINE IS SHOWING. `rollAt` is the clock's count for that door; the first name is
  // index 0, which is what the markup holds before the clock has ticked (and what it holds for good
  // under reduced motion). The tile's `.ld-peek-text` reads off the SAME entry, so the description
  // below the fold is always the description OF the name above it.
  var entryOf = function (k) {
    var names = doorNames[k] || [];
    if (!names.length) return null;
    return names[(rollAt[k] || 0) % names.length];
  };
  // THE PREVIEW TEXT IS READ AT RENDER through the Shipyard's own rule (`vehicleParagraphs`, lib/shared/yardView.jsx
  // — loaded before this bundle), never a second one: the yard's OggDude prose carries `[H3]…[h3]`, `[P]` and `[I]`
  // (D-390 item 3, R-PLF-4, B-1534); on the SHIPYARD tile a first paragraph that only repeats the ship's name is
  // dropped as the catalogue drops it (R-PLF-10). The yard's rule, so the yard's door only: an NPC's or an item's
  // one paragraph names its subject, and the dedupe would empty it (7 npcs and 5 store entries, measured).
  // rollcall.json stays as built.
  var peekText = function (e, roll) {
    var Y = window.SWRPGYard;
    if (!(e && e.description && Y)) return '';
    return (roll === 'yard' ? Y.vehicleParagraphs(e.description, e.name) : Y.cleanVehicleDescription(e.description)).join(' ');
  };
  var tiles = [
    { id: 'npc', roll: 'npcs', title: 'NPCs',
      fig: ['npcs', figs.npcs, 'entries'] },
    { id: 'yard', roll: 'yard', title: 'Shipyard',
      fig: ['yard', figs.yard, 'vessels'] },
    { id: 'store', roll: 'store', title: 'Store',
      fig: ['store', figs.store, 'items'] },
  ];
  // F-100 C2 · the HOMEBREW door is drawn only while swrpg_homebrew_editor is on. ONE test, read by the door and by the
  // tile group's F10a class, so the layout and the door cannot disagree.
  var homebrewDoor = !!(window.SWRPGEditor && window.SWRPGEditor.on() && window.SWRPGEditorView);

  var contents = (
    <React.Fragment>
      <div className="ld-chhead2" data-landing-zone="create" tabIndex={-1}>
        <h3 className="ld-cht">Create</h3>
      </div>
      <div className="ld-wayplates ld-wayband ld-waydesk">
        {ways.map(function (w) {
          return (
            <button type="button" className="hp-plate ld-wayplate" data-pad="read" key={w.id}
                    data-first={w.first ? '1' : undefined} data-landing-door={w.id}
                    onClick={enter(w.id)}>
              <span className="ld-wpb">
                <span className="hp-row-desig ld-wpt">{w.title}</span>
              </span>
              <span className="ld-wptab">
                {w.figs.length ? (
                  <span className="ld-figs">{w.figs.map(function (f) { return ldFigure(f[0], f[1], f[2]); })}</span>
                ) : null}
                {w.first ? <span className="ld-wprun ld-chrun">{run3}</span> : null}
                <span className="ld-chev">&rsaquo;</span>
              </span>
            </button>);
        })}
      </div>
      {/* F10a (R-FEM-A2-1; Jason's D-PV1 (a), ruling `paper-verify`): with the HOMEBREW door the reference doors are FOUR,
          and the approved frame draws them 2×2 at every band with CHARACTER FILES beneath. `ld-tiles-f10a` names that
          state for landing.css; without the door (the editor's '0' rollback) the three-tile landing is unchanged. */}
      <div className={'ld-tiles' + (homebrewDoor ? ' ld-tiles-f10a' : '')} data-landing-zone="tiles">
        {tiles.map(function (t) {
          // THE FIRST NAME IS IN THE MARKUP (I6's own requirement, not a nicety): with the roll call's
          // clock absent or motion reduced, the line must read correctly rather than empty. Until
          // rollcall.json lands the line is RESERVED at two line-heights and empty (landing.css).
          var first = entryOf(t.roll);
          return (
            <button type="button" className="hp-plate ld-tile" data-pad="read" key={t.id}
                    data-landing-door={t.id} onClick={enter(t.id)}>
              <span className="ld-tilehead">
                <span className="hp-row-desig">{t.title}</span>
                {ldFigure(t.fig[0], t.fig[1], t.fig[2])}
              </span>
              <span className="ld-tilebody">
                <span className="ld-chrun ld-roll" data-roll={t.roll}
                      data-fade={rollFade && rollFade[t.roll] ? '1' : undefined}>{first ? first.name : ''}</span>
                <span className="ld-peek-text">{peekText(first, t.roll)}</span>
                {/* A10's SECOND COPY OF THE RUN (B-791) — what makes the peek a LOOP instead of a
                    rewind: the scroll box drops back one run at the end and the same pixels are
                    already under the box. Two siblings (the sentence is cut, D-289) and not a wrapper, so every rule the first
                    copy answers to (`.ld-roll`'s two reserved
                    lines) applies to this one UNCHANGED and the copies are the same height by
                    construction — a wrapper would have had to restate the body's per-band gaps.
                    `aria-hidden`, so the register is read once; NO `data-roll`, because I6's effect
                    takes the first `.ld-roll[data-roll]` it finds (:1168) and a copy carrying it would
                    steal the roll call; `data-peek-copy` is what the clock measures the period from.
                    Hidden on the phone band (landing.css §174), where the body is not a scroller. */}
                <span className="ld-chrun ld-roll ld-peek-dup" data-peek-copy="2" aria-hidden="true"
                      data-fade={rollFade && rollFade[t.roll] ? '1' : undefined}>{first ? first.name : ''}</span>
                <span className="ld-peek-text ld-peek-dup" aria-hidden="true">{peekText(first, t.roll)}</span>
              </span>
              <span className="ld-chev">&rsaquo;</span>
            </button>);
        })}
        {/* F-100 C2 · THE HOMEBREW DOOR (F10a, ruled R-FEM-A2-1: a fourth reference tile; the bar keeps its five
            words). Drawn only while swrpg_homebrew_editor is on — off, this line renders nothing. */}
        {homebrewDoor ? <LandingHomebrewTile onEnter={enter} /> : null}
        {/* THE QUIET ROW, R-OGG1-1's rule kept: no stock, no figure, no eyebrow. B-712: it carries the legacy row's
            tour-id, which R-OEC-1 pins UNCHANGED — three suites select the import row by it. */}
        <button type="button" className="hp-quiet ld-files" data-tour-id="p-landing-import-row" data-landing-door="import"
                onClick={enter('import')}>
          <span className="ld-files-t">Character files</span>
          <span className="ld-chev ld-files-chev" aria-hidden="true">&rsaquo;</span>
        </button>
      </div>
    </React.Fragment>
  );

  var stagePlate = stage ? (
    <div className={'hp-plate ld-front'} data-pad="read" data-mine={mine ? '1' : undefined}
         data-landing-zone="stage" tabIndex={-1}>
      {mine ? (
        <div className="ld-fhead">
          <div className="ld-fid">
            <h2 className="ld-name">{stage.name}</h2>
            <p className="ld-sub"><b>{stage.species}</b>{' · '}{stage.career}{stage.spec ? <React.Fragment>{' · '}<b>{stage.spec}</b></React.Fragment> : null}</p>
          </div>
          {/* §169 — THE STAGE IS THE PRESS: OPEN sits in the plate's head beside the name, and it goes
              through B-645's ONE opener, so `swrpg_last_sheet_id` and `lastOpenedAt` are stamped here
              exactly as they are from the roster's row. */}
          <button type="button" className="hp-commit ld-open ld-open-in" data-landing-open="1"
                  onClick={pressOpen}>
            Open<span className="ld-chev">&rsaquo;</span>
          </button>
        </div>
      ) : (
        <React.Fragment>
          <h2 className="ld-name">{stage.name}</h2>
          <p className="ld-sub"><b>{stage.species}</b>{' · '}{stage.career}{' · '}<b>{stage.spec}</b></p>
        </React.Fragment>
      )}
      {ldRail(stage.chars)}
      {/* §169: the vitals and the skill row are NOT drawn — the desk stage IS the phone's strip, on
          every band, and both are `display:none` in the approved frames at both. */}
      <button type="button" className="ld-explore" data-landing-explore="1"
              aria-expanded={explore ? 'true' : 'false'}
              onClick={function () { setExplore(!explore); }}>
        Explore this character<span className="ld-chev">&rsaquo;</span>
      </button>
    </div>
  ) : null;

  var descsBlock = descRecs.length ? (
    <div className="ld-descs" data-landing-zone="reading">
      {descRecs.map(function (d) {
        var open = !!(read && read.key === d.key);
        if (!teaser) {
          // §171 (Jason 2026-09-16): "When there isn't any created characters, the prose for career and
          // specialization can be the full view." No index competing for the column — the whole text, no
          // window, no press.
          return (
            <div className="hp-plate ld-desc" data-pad="read" data-key={d.key} key={d.key}>
              <p className="ld-desck">{d.kind}{' · '}<b>{d.name}</b></p>
              <p className="ld-desct">{d.text}</p>
            </div>);
        }
        var inlineOpen = open && read.inline;
        return (
          <div className="hp-plate ld-desc" data-pad="read" data-key={d.key} key={d.key}
               data-read={inlineOpen ? '1' : undefined}>
            <button type="button" className="ld-desc-open" data-landing-read={d.key}
                    aria-expanded={open ? 'true' : 'false'}
                    onClick={function () { pressRead(d.key); }}>
              <span className="ld-desck">
                <span>{d.kind}{' · '}<b>{d.name}</b></span>
                <span className="ld-more">{inlineOpen ? 'Close' : 'Read'}</span>
              </span>
              {/* THE WINDOW IS A10's SCROLLER (D-390 item 4, R-PLF-5, retiring I7's always-on drift): still at
                  rest, read on hover by the tiles' own clock. The run is in it TWICE, as in a tile, so the
                  peek loops instead of rewinding; the copy is `aria-hidden` and folds away when opened. */}
              <span className="ld-readwin">
                <span className="ld-desct ld-readtext">{d.text}</span>
                <span className="ld-desct ld-readtext ld-peek-dup" data-peek-copy="2" aria-hidden="true">{d.text}</span>
              </span>
            </button>
          </div>);
      })}
    </div>
  ) : null;

  // THE PANE IS THE COLUMN'S CHILD, not the plate's: `hp-plate` is positioned for its corner cut, and a
  // pane inside it anchors to the plate and opens low over the colophon (§172, the fit check's catch).
  var paneRec = (read && !read.inline) ? descRecs.filter(function (d) { return d.key === read.key; })[0] : null;
  var readPane = paneRec ? (
    <div className="hp-plate ld-readpane" data-pad="read" role="dialog"
         aria-label={paneRec.kind + ': ' + paneRec.name}>
      <p className="ld-desck">{paneRec.kind}{' · '}<b>{paneRec.name}</b></p>
      <p className="ld-desct">{paneRec.text}</p>
      <button type="button" className="hp-quiet ld-readclose"
              onClick={function () { setRead(null); }}>Close</button>
    </div>
  ) : null;

  // THE BLOCK IS DRAWN WHENEVER ONE CHARACTER IS SAVED (A18): at rest with a roster of one it holds
  // the head and EDIT and no rows — which is the only place ARRANGE can be reached from, and the
  // reason a roster of one stopped being a dead end. It is drawn for the failure line too, so the
  // line has a head to stand at even if the roster is somehow empty behind it.
  var indexBlock = (sorted.length || p.statusLine) ? (
    <div className="ld-idxblock" data-landing-zone="index" tabIndex={-1}>
      {/* R-LFM-4 (A18) — THE BOUNDARY'S LINE, IN MECHANICS VOICE, AT THE INDEX'S HEAD. `role="status"`
          is what the old roster screen gave it and the words are byte-identical to that screen's, so
          the two shapes say the same thing about the same event. */}
      {p.statusLine ? (
        <p className="ld-status" data-landing-status="line" role="status">{p.statusLine}</p>
      ) : null}
      {/* §170 — AT REST the head counts the rows it holds (the stage IS the current character and its
          OPEN, so the index is THE REST, §3.2); IN ARRANGE it says what the state is for. Both lines
          are the drawing's, character for character. */}
      <div className="ld-idxhead">
        {/* IN ARRANGE the head's line is cut (copy-voice-sweep player/app.jsx:1406:21, D-289) and its h3 goes with it */}
        {/* D-406 item 26 (B-972; landing-roster-looks @ 727101906, pick B): with no other character the head drops its count —
            "Your characters", never "· 0" */}
        {edit ? null : <h3>{'Your characters' + (viewList.length ? ' · ' + viewList.length : '')}</h3>}
        {/* NO `aria-label` HERE, deliberately: the press's own word IS its name (`Edit` / `Done`,
            the drawing's), and an aria-label would REPLACE that word for a screen reader — the
            accessible name would stop containing the visible one. `aria-pressed` carries the state. */}
        {sorted.length ? (
        <button type="button" className="ld-edit" aria-pressed={edit ? 'true' : 'false'}
                data-landing-roster={edit ? 'done' : 'edit'}
                onClick={function () { if (edit) finalizeGone(); setEdit(!edit); }}>
          {edit ? 'Done' : 'Edit'}
        </button>
        ) : null}
      </div>
      <div className="hp-index ld-idx" data-edit={edit ? '1' : '0'}>
        {viewRows.map(function (r) {
          var rs = (r.sheetData && typeof r.sheetData === 'object') ? r.sheetData : null;
          var line = gameLineTitle(r.book || (rs && rs.book));
          // the register's spelling here too — an index row and the stage may not disagree about the
          // same character's career
          var rc = regByName(cb && cb.careers, (rs && rs.career) || r.career);
          var rspec = ldSpecName(rs, cb);
          var rsp = regByName(cb && cb.specializations, rspec);
          var rname = (rs && rs.name) || r.name || '';
          var isGone = goneIds.indexOf(r.id) >= 0;
          return (
            <div className="hp-row ld-rrow" data-noplate="1" data-landing-roster="row"
                 data-landing-id={r.id} data-gone={isGone ? '1' : '0'} key={r.id}>
              {/* THE GRAB HANDLE is the drawing's: a mark, not a control (`aria-hidden`, no tab stop),
                  because the KEYBOARD's way to move a row is ▲ ▼ beside it. It carries the pointer
                  drag; the stylesheet reveals it in ARRANGE only. */}
              <span className="ld-grab" aria-hidden="true" data-landing-roster="grab"
                    onPointerDown={function (ev) { if (!isGone) grabDown(ev, r.id); }}>
                {'⠿'}
              </span>
              <button type="button" className="ld-rowb" tabIndex={isGone ? -1 : undefined}
                      aria-disabled={isGone ? 'true' : undefined}
                      onClick={function () { if (!isGone && p.onSelect) p.onSelect(r); }}>
                <span className="hp-row-desig">{rname}</span>
                <span className="ld-rowsub">
                  <b>{(rs && rs.species) || r.species || ''}</b>
                  {' · '}{(rc && rc.name) || (rs && rs.career) || r.career || ''}
                  {rspec ? <React.Fragment>{' · '}{(rsp && rsp.name) || rspec}</React.Fragment> : null}
                </span>
                <span className="ld-rowmeta">
                  {/* THE BOARD'S OWN CHIP (`roster_index()`, the `cur` span): the row the stage is
                      drawing, named where the index draws the whole roster. It is not a state the
                      REST can be in — it appears only where the current character has a row. */}
                  {r === current ? <span className="ld-cur">Current</span> : null}
                  <span className="ld-when">{line ? line + ' · ' : ''}{ldAgo(ldRecency(r))}</span>
                </span>
              </button>
              {/* ▲ ▼ · 44×44 each, as drawn. The marks are DRAWN AT REST TOO — A8 slides them in from
                  the row's edge, which needs the element present — so outside ARRANGE they are taken
                  out of the tab order and the a11y tree: an invisible control is not a control (the
                  stylesheet says the same thing about taps with `pointer-events:none`). */}
              <span className="ld-move" data-landing-roster="move">
                <button type="button" aria-label={'Move ' + rname + ' up'} tabIndex={edit ? 0 : -1}
                        aria-hidden={edit ? undefined : 'true'}
                        onClick={function () { moveBy(r.id, -1); }}>{'▲'}</button>
                <button type="button" aria-label={'Move ' + rname + ' down'} tabIndex={edit ? 0 : -1}
                        aria-hidden={edit ? undefined : 'true'}
                        onClick={function () { moveBy(r.id, 1); }}>{'▼'}</button>
              </span>
              {/* THE DELETE MARK (B-970) — ✕ deletes at ONCE and becomes UNDO in its place; UNDO takes the delete back.
                  Neither writes: the delete is written when the player leaves Arrange (`finalizeGone`, above). */}
              <span className="ld-del">
                {isGone ? (
                <button type="button" tabIndex={edit ? 0 : -1} aria-hidden={edit ? undefined : 'true'}
                        data-landing-roster="undo" aria-label={'Undo deleting ' + rname}
                        onClick={function (ev) {
                          ev.stopPropagation();
                          undoFocus.current = { id: r.id, hook: 'delete' };
                          setGoneIds(function (g) { return g.filter(function (x) { return x !== r.id; }); });
                        }}>{'Undo'}</button>
                ) : (
                <button type="button" tabIndex={edit ? 0 : -1} aria-hidden={edit ? undefined : 'true'}
                        data-landing-roster="delete" aria-label={'Delete ' + rname}
                        onClick={function (ev) {
                          ev.stopPropagation();
                          undoFocus.current = { id: r.id, hook: 'undo' };
                          setGoneIds(function (g) { return g.indexOf(r.id) >= 0 ? g : g.concat([r.id]); });
                        }}>{'✕'}</button>
                )}
              </span>
            </div>);
        })}
      </div>
    </div>
  ) : null;

  var draftRow = p.draft ? (
    <div className="ld-draft" data-armed={draftArmed ? '1' : '0'} data-landing-zone="draft">
      <button type="button" className="ld-draft-b" data-landing-draft="resume"
              onClick={p.onResumeDraft}>
        <span className="ld-draft-k">Continue where you left off</span>
        <span className="ld-draft-t">{ds.title}</span>
        <span className="ld-draft-m">{ds.meta}</span>
      </button>
      {/* THE ✕ ASKS FIRST (B-649 closes B-403). Production discards a half-built character on ONE
          tap; the shape's own frame draws the mark ARMED, reading `Discard?`, and the confirming tap
          calls the same `onDiscardDraft`. The arm is the roster's idiom to the millisecond — the same
          3-second disarm above — so the page has ONE way of asking "are you sure", not two. No
          plumbing is added: the arm is local presentation state. */}
      <button type="button" className="ld-x" data-landing-draft="discard"
              aria-label={draftArmed ? 'Tap again to discard the unfinished character'
                                     : 'Discard unfinished character'}
              onClick={function (ev) {
                if (draftArmed) {
                  var row = ev.currentTarget.closest ? ev.currentTarget.closest('.ld-draft') : null;
                  setDraftArmed(false);
                  fold(row, function () { if (p.onDiscardDraft) p.onDiscardDraft(); });   // A6
                } else setDraftArmed(true);
              }}>{draftArmed ? 'Discard?' : '✕'}</button>
    </div>
  ) : null;

  return (
    <div className="ld-surface" ref={surfRef} data-doc-ground="creator" data-landing-shell="1" data-band={mode}>
      {/* THE GROUND IS DECLARED TWICE, AND BOTH ARE LOAD-BEARING. On the OUTER element because
          paper.css's kit is written as DESCENDANTS (`[data-doc-ground="creator"] .hp-*`), so the
          bezel and everything in it resolve the creator's tokens; and on the SHEET because the
          page's own paper — `background-color:var(--doc-paper)`, the ink, the wear wash and the
          grain — is written as a COMPOUND (`[data-doc-ground="creator"].hp-sheet`), which no
          ancestor can satisfy. With it only on the bezel the front matter rendered as light plates
          floating on the app's near-black: measured at 1440×900 before this. Every other
          transplanted surface declares it on its own `.hp-sheet` for the same reason. */}
      {/* `data-live` — THE PAGE'S IDLE LOOPS STOP WHEN NOBODY IS LOOKING. The landing stays mounted
          behind every other screen, so without this the sweep would run (the reading windows' I7 drift is
          retired, R-PLF-5)
          for the whole of a creator session on a page nothing is showing. The JS clocks stop on the
          same signal (the roll call's effect); this is the CSS half of it (landing.css §D). */}
      <div className="hp-sheet ld" data-doc-ground="creator" data-doc-surface="landing"
           data-fit="1" data-state={state} data-live={live ? '1' : '0'}>
        <div className="hp-runhead ld-runhead">
          {/* B-652 / A17 — THE CONTEXTUAL BUTTON THAT CALLS THE APP'S BARS, the sheet's own (B-629):
              same word, same hooks, same labels, FIRST ON THE LEFT (Jason 2026-09-16, of the sheet's:
              "change 'Nav' to 'Menu' and put it in front of the 'Back' on the left side"). The screen's
              top and bottom edges call them too; a second press, Escape or a tap on the page lets them
              go. Absent when the host hands no summoner — the landing renders identically without it. */}
          {p.onSummonChrome ? (
            <button type="button" className="hp-quiet ld-menu" data-testid="chrome-summon"
                    data-chrome-summon="1" aria-pressed={p.chromePinned ? 'true' : 'false'}
                    aria-label={p.chromePinned ? 'Hide the app menu bars'
                      : 'Show the app menu bars (or move the pointer to the top or bottom edge of the screen)'}
                    onClick={p.onSummonChrome}>Menu</button>
          ) : null}
          <span>Player Datapad</span>
          <span className="ld-lamp" aria-hidden="true"></span>
          {/* no folio: "Front matter" is removed (D-390 item 7, R-PLF-6) */}
        </div>
        <div className="ld-head">
          <div className="ld-title" data-landing-zone="title">
            {/* I1's sweep is a span inside the type's own box — the pass never leaves it (landing.css) */}
            <h1 className="hp-desig ld-desig">Player Datapad<span className="ld-sweep" aria-hidden="true"></span></h1>
            <div className="hp-desig-rule"></div>
          </div>
        </div>
        <div className="ld-body">
          <div className="ld-colL" data-explore={explore ? '1' : '0'}>
            {stagePlate}
            {draftRow}
            {indexBlock}
            {descsBlock}
            {readPane}
          </div>
          <div className="ld-colR">{contents}</div>
        </div>
        <div className="ld-col" data-landing-zone="colophon">
          <p className="ld-disc">{LANDING_DISCLAIMER}</p>
          <p className="ld-extent">{ldInline('books', figs.books)}{' titles in the register'}</p>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   PRE-GEN CHARACTER DATA
══════════════════════════════════════════════════════ */
const PREGEN_CHARACTERS = [
  // ── Edge of the Empire ──
  {
    id: 'smuggler', name: 'Kael Voss', career: 'Smuggler', species: 'Weequay', book: 'eote', playstyle: 'Face / pilot',
    portrait: '', accent: '#F5C842',
    tagline: 'Fast ship. Faster mouth.',
    description: 'Owes money to the wrong people, with a gift for making bad situations worse before somehow walking away clean. Tough as leather. Fights only when the exit is already blocked.',
  },
  {
    id: 'bounty_hunter', name: 'Ryn Kessig', career: 'Bounty Hunter', species: 'Human', book: 'eote', playstyle: 'Ranged striker',
    portrait: '', accent: '#F5C842',
    tagline: 'The hunt is everything.',
    description: 'Follows the Guild code, mostly. Brings targets in alive because dead ones pay less. The armor is ancestral, the patience is earned, and violence is the last resort.',
  },
  {
    id: 'colonist', name: 'Maro Jinx', career: 'Colonist', species: 'Human', book: 'eote', playstyle: 'Face / support',
    portrait: '', accent: '#F5C842',
    tagline: 'Information is currency.',
    description: 'A frontier drifter with a cybernetic arm and a knack for being in the wrong place at the right time. Works the gaps between factions, brokering deals and running odd jobs. His real talent is surviving what should have killed him.',
  },
  {
    id: 'explorer', name: 'Tarak Nol', career: 'Explorer', species: 'Duros', book: 'eote', playstyle: 'Tracker / scout',
    portrait: '', accent: '#F5C842',
    tagline: 'The map is never finished.',
    description: 'Charts routes through uncharted space and forgotten worlds. Carries a blaster and a medkit, because the frontier is as likely to shoot you as collapse under you. Quiet, methodical, and impossible to lose in a crowd.',
  },
  {
    id: 'hired_gun', name: 'Gron Torrek', career: 'Hired Gun', species: 'Weequay', book: 'eote', playstyle: 'Tank / gunner',
    portrait: '', accent: '#F5C842',
    tagline: 'Point. Shoot. Get paid.',
    description: 'Has fought for every faction with credits to spend. Carries more firepower than most squads, with the armor scars to prove every paycheck. Loyalty lasts exactly as long as the contract.',
  },
  {
    id: 'technician', name: 'Patch', career: 'Technician', species: 'Human', book: 'eote', playstyle: 'Tech / slicer',
    portrait: '', accent: '#F5C842',
    tagline: 'Give me ten minutes.',
    description: 'Grew up in the underbelly of a shipyard and speaks more fluently to droids than to people. Can repair anything, build devices from scrap, and slice systems that are supposed to be unsliceable. Underestimated by everyone who has not watched him work.',
  },
  // ── Age of Rebellion ──
  {
    id: 'ace', name: 'Vex Rin', career: 'Ace', species: 'Rodian', book: 'aor', playstyle: 'Starfighter ace',
    portrait: '', accent: '#00C8FF',
    tagline: 'Anything with an engine.',
    description: 'A Rodian with an instinct for machines that borders on the supernatural. Coaxes performance out of ships that should be scrap and flies routes the computers call impossible. On the ground, restless. In the cockpit, home.',
  },
  {
    id: 'commander', name: 'Kaelen Dray', career: 'Commander', species: 'Kel Dor', book: 'aor', playstyle: 'Leader / support',
    portrait: '', accent: '#00C8FF',
    tagline: 'Everyone comes home.',
    description: 'Earned his command by keeping people alive when the odds said otherwise. Reads a battlefield the way others read a datapad: patterns, gaps, openings. Speaks rarely. When he does, his squad listens.',
  },
  {
    id: 'diplomat', name: 'Silas Venn', career: 'Diplomat', species: 'Falleen', book: 'aor', playstyle: 'Face / negotiator',
    portrait: '', accent: '#00C8FF',
    tagline: 'Words sharper than vibroblades.',
    description: 'A Falleen political operator whose pheromones are the least dangerous weapon he carries. Moves through Senate corridors and cantina back rooms with equal ease. Every conversation is a negotiation, and he has never lost one that mattered.',
  },
  {
    id: 'engineer', name: 'Wicket', career: 'Engineer', species: 'Ewok', book: 'aor', playstyle: 'Tech / support',
    portrait: '', accent: '#00C8FF',
    tagline: 'Improvise. Adapt. Overcome.',
    description: 'An Ewok prodigy who reverse-engineered Imperial tech from battlefield salvage. Pilots a walker cobbled together from AT-ST parts and Rebellion surplus. Small, and fearless out of all proportion to his size.',
  },
  {
    id: 'soldier', name: 'Dorn Reva', career: 'Soldier', species: 'Human', book: 'aor', playstyle: 'Heavy gunner',
    portrait: '', accent: '#00C8FF',
    tagline: 'Hold the line.',
    description: 'A former infantry sergeant who has watched too many good people die for a cause that keeps asking for more. Still fights, because the alternative is worse. Steady under fire, and the one everyone looks to when the plan falls apart.',
  },
  {
    id: 'spy', name: 'Lyra Kess', career: 'Spy', species: 'Bothan', book: 'aor', playstyle: 'Slicer / infiltrator',
    portrait: '', accent: '#00C8FF',
    tagline: 'Everyone talks eventually.',
    description: 'Moves through Imperial space under a dozen identities. Patient and observant, and utterly ruthless when the mission demands it. The intelligence she gathers saves more lives than any blaster.',
  },
  // ── Force and Destiny ──
  {
    id: 'consular', name: 'Kora Sul', career: 'Consular', species: 'Mon Calamari', book: 'fad', playstyle: 'Support / Force healer',
    portrait: '', accent: '#B080FF',
    tagline: 'Peace is a choice.',
    description: 'A Mon Calamari diplomat and healer who carries a lightsaber she has never ignited in anger. Uses the Force to read emotions, settle disputes, and mend wounds bacta cannot reach. In a galaxy at war, she chose a different kind of fight.',
  },
  {
    id: 'guardian', name: 'Kiran Sol', career: 'Guardian', species: 'Human', book: 'fad', playstyle: 'Lightsaber tank',
    portrait: '', accent: '#B080FF',
    tagline: 'Shield of the innocent.',
    description: 'Carries a lightsaber the way a soldier carries a shield: to protect. Trained in the old ways by a master who did not survive the Purge. Fights with discipline, defends without hesitation.',
  },
  {
    id: 'mystic', name: 'Zara Koth', career: 'Mystic', species: 'Zabrak', book: 'fad', playstyle: 'Force generalist',
    portrait: '', accent: '#B080FF',
    tagline: 'The Force reveals all paths.',
    description: 'A Zabrak seer who works the Force through ritual and meditation. Carries a staff of office and speaks with an authority that outranks her station. Sees the connections others miss: between events, people, and places that shape what is coming.',
  },
  {
    id: 'seeker', name: 'Renna Tide', career: 'Seeker', species: 'Selkath', book: 'fad', playstyle: 'Tracker / scout',
    portrait: '', accent: '#B080FF',
    tagline: 'The wild calls.',
    description: 'Learned to fight in the wild before she learned Basic. Carries twin lightsabers and moves with the grace of a deep-ocean predator. To her the Force is not a philosophy. It is the current she swims in.',
  },
  {
    id: 'sentinel', name: 'Orin Vael', career: 'Sentinel', species: 'Muun', book: 'fad', playstyle: 'Infiltrator / duelist',
    portrait: '', accent: '#B080FF',
    tagline: 'Precision is its own weapon.',
    description: 'A Muun who survived the Purge by being useful to the wrong people. Trained in the shadows between the Jedi and the underworld, he uses the Force with surgical precision, never flashy and always effective. The scars are from the lessons that stuck.',
  },
  {
    id: 'warrior', name: 'Tovan Kade', career: 'Warrior', species: 'Human', book: 'fad', playstyle: 'Lightsaber duelist',
    portrait: '', accent: '#B080FF',
    tagline: 'The blade remembers.',
    description: 'Learned his craft from holocrons and hard experience. Fights with a green blade and the aggressive forms of a bygone era. The Force runs through his strikes like water through a canyon, relentless and unstoppable.',
  },
];

// RETIRED 2026-07-30 (ruling `quick-start-roster-scope` Q2). This carried the twelve FFG Beginner
// Game pregens — Pash, Oskara, Lowhhrick, 41-VEX, Cael, Tendaar, Vendri, Zal, Dao Jodh, Kaveri Ra,
// Sarenda, Tarast Voon — plus ~7,000 characters of their published backstories.
//
// WHY THEY WENT: they were balanced for the Beginner Game boxed adventures, which this project does
// not ship, and they are not creation-legal (Pash spends 150 XP of characteristics against a
// 110-point pool). Quick Start's promise is "pick an archetype and play" — a character that cannot
// legally grow fails that promise, and rebuilding them legal would have made them no longer the
// characters the box printed while this screen called them canon. Mirror what is printed, or do not
// carry it (the Dao Jodh precedent, one lap earlier).
//
// The empty array is DELIBERATE, not a stub: the picker below already branches on
// `FFG_PREGEN_CHARACTERS.length > 0`, so the roster collapses to a single honest row by itself.
// Existing players keep their copies — picking a pregen persists the whole sheet into the roster.
const FFG_PREGEN_CHARACTERS = [];


const BOOK_LABELS = { eote: 'EDGE OF THE EMPIRE', aor: 'AGE OF REBELLION', fad: 'FORCE AND DESTINY' };
// UXQP-13: one-line game-line orientation for the pregen cards, keyed by book.
const PREGEN_BOOK_LINE = {
  eote: 'Outlaws and survivors on the lawless fringe.',
  aor: 'Soldiers and heroes in the war against the Empire.',
  fad: 'Wielders of the living Force in a galaxy that hunts them.',
};
// One row now — the second was the Beginner Game shelf (retired, see FFG_PREGEN_CHARACTERS).
const ROW_LABELS = ['ORIGINAL CHARACTERS'];

/* ══════════════════════════════════════════════════════
   CAROUSEL PHYSICS HOOK
══════════════════════════════════════════════════════ */
function useCarouselPhysics(itemCount, cardWidth, cardGap) {
  const [offset, setOffset] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const velocity = useRef(0);
  const isDragging = useRef(false);
  const skipLerp = useRef(false);
  const dragStart = useRef({ x: 0, offset: 0, time: 0 });
  const lastTouch = useRef({ x: 0, time: 0 });
  const animFrame = useRef(null);
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 393;

  const getSnapOffset = useCallback((idx) => {
    return -(idx * (cardWidth + cardGap)) + (viewportW - cardWidth) / 2;
  }, [cardWidth, cardGap, viewportW]);

  const snapToNearest = useCallback((currentOffset, vel, currentIdx) => {
    var absVel = Math.abs(vel);
    // Slow swipe — just move one card in swipe direction
    if (absVel < 2) {
      var dir = vel > 0.3 ? -1 : vel < -0.3 ? 1 : 0;
      var targetIdx = Math.max(0, Math.min(itemCount - 1, currentIdx + dir));
      return targetIdx;
    }
    // Fast flick — project momentum
    var projected = currentOffset + vel * 10;
    var stepSize = cardWidth + cardGap;
    var targetIdx = Math.round(-projected / stepSize + (viewportW - cardWidth) / (2 * stepSize));
    targetIdx = Math.max(0, Math.min(itemCount - 1, targetIdx));
    return targetIdx;
  }, [cardWidth, cardGap, itemCount, viewportW]);

  // Spring physics animation loop
  useEffect(() => {
    let running = true;
    const animate = () => {
      if (!running || isDragging.current || skipLerp.current) {
        skipLerp.current = false;
        return;
      }

      const target = getSnapOffset(activeIdx);
      const dx = target - offset;

      // Lerp toward target — no spring, no bounce, just smooth glide
      var newOffset = offset + dx * 0.12;

      if (Math.abs(dx) > 0.5) {
        setOffset(newOffset);
        animFrame.current = requestAnimationFrame(animate);
      } else {
        setOffset(target);
        velocity.current = 0;
      }
    };

    if (!isDragging.current) {
      animFrame.current = requestAnimationFrame(animate);
    }

    return () => { running = false; cancelAnimationFrame(animFrame.current); };
  }, [activeIdx, offset, getSnapOffset]);

  const onTouchStart = useCallback((e) => {
    cancelAnimationFrame(animFrame.current);
    isDragging.current = true;
    velocity.current = 0;
    const x = e.touches[0].clientX;
    dragStart.current = { x, offset, time: Date.now() };
    lastTouch.current = { x, time: Date.now() };
  }, [offset]);

  const onTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    const x = e.touches[0].clientX;
    const dx = x - dragStart.current.x;

    let newOffset = dragStart.current.offset + dx;

    // Rubber-band at edges
    const minOffset = getSnapOffset(itemCount - 1);
    const maxOffset = getSnapOffset(0);
    if (newOffset > maxOffset) newOffset = maxOffset + (newOffset - maxOffset) * 0.15;
    if (newOffset < minOffset) newOffset = minOffset + (newOffset - minOffset) * 0.15;

    // Track velocity
    const now = Date.now();
    const dt = now - lastTouch.current.time;
    if (dt > 0) velocity.current = (x - lastTouch.current.x) / dt * 16;
    lastTouch.current = { x, time: now };

    setOffset(newOffset);
  }, [itemCount, getSnapOffset]);

  const onTouchEnd = useCallback(() => {
    isDragging.current = false;
    const idx = snapToNearest(offset, velocity.current, activeIdx);
    setActiveIdx(idx);
  }, [offset, snapToNearest]);

  // Mouse support for desktop testing
  const isMouseDown = useRef(false);

  const onMouseDown = useCallback((e) => {
    cancelAnimationFrame(animFrame.current);
    isDragging.current = true;
    isMouseDown.current = true;
    velocity.current = 0;
    const x = e.clientX;
    dragStart.current = { x, offset, time: Date.now() };
    lastTouch.current = { x, time: Date.now() };
  }, [offset]);

  const onMouseMove = useCallback((e) => {
    if (!isMouseDown.current || !isDragging.current) return;
    const x = e.clientX;
    const dx = x - dragStart.current.x;

    let newOffset = dragStart.current.offset + dx;

    const minOffset = getSnapOffset(itemCount - 1);
    const maxOffset = getSnapOffset(0);
    if (newOffset > maxOffset) newOffset = maxOffset + (newOffset - maxOffset) * 0.15;
    if (newOffset < minOffset) newOffset = minOffset + (newOffset - minOffset) * 0.15;

    const now = Date.now();
    const dt = now - lastTouch.current.time;
    if (dt > 0) velocity.current = (x - lastTouch.current.x) / dt * 16;
    lastTouch.current = { x, time: now };

    setOffset(newOffset);
  }, [itemCount, getSnapOffset]);

  const onMouseUp = useCallback(() => {
    if (!isMouseDown.current) return;
    isMouseDown.current = false;
    isDragging.current = false;
    const idx = snapToNearest(offset, velocity.current, activeIdx);
    setActiveIdx(idx);
  }, [offset, snapToNearest]);

  return {
    offset, setOffset, activeIdx, setActiveIdx, getSnapOffset,
    onTouchStart, onTouchMove, onTouchEnd,
    onMouseDown, onMouseMove, onMouseUp,
    isDragging, velocity, skipLerp,
  };
}

/* ══════════════════════════════════════════════════════
   QUICK START'S SHELF — the eighteen frontispieces (B-720)
══════════════════════════════════════════════════════ */
/* R-QSG-6 row 2. The drawing is frames A · B · C · D of `lab/holocron/qsg.html` @ lab `284f9929`
   (Jason 2026-09-19 "Approved" + "B"), and the markup below is `qsg_build.py`'s `shelf()`,
   `registers()`, `shelf_card()`, `rail()`, `vitals()` and `record_skill_row()` class for class.

   THE RECORD IS THE AUTHORITY (R-QSG-4). Every name, species, career, specialization, game line,
   characteristic, threshold, soak, skill, rank and count on this shelf is read out of
   `public/data/pregen_sheets.json`; `PREGEN_CHARACTERS` is not consulted for one of them, and the
   folio's figure — which row 1 took from the carousel's own length and marked as owed here
   (B-730 (2)) — is now the record count.

   THE TWO SENTENCES THAT ARE THE CARD'S, AND WHY. A record carries no editorial line: `tagline`
   and `playstyle` exist only in `PREGEN_CHARACTERS`, keyed by the same id. The approved board draws
   both from the card and says why in `shelf_card`'s docstring and on frame G — that line exists
   nowhere else and a shelf without it is a spreadsheet — and B-717's guard is what keeps the two
   files describing one character. So: every FIGURE from the record, the two EDITORIAL strings from
   the card, and the split named here rather than left to be noticed.

   ABSENCES STAY ABSENCES (§194). No book page on a card: the records carry no page field and all
   eighteen read `source: "Original"`, so the card prints the LINE and nothing else. An absence
   printed as an absence cannot become the wrong book page. */

// THE EIGHTEEN RECORDS, FETCHED ONCE PER PAGE AND CACHED AT MODULE LEVEL — the landing's
// `loadLandingRollcall` device (R-LFM-3), over the same file `handleSelectCharacter` fetches when a
// card is taken, so reading the shelf warms the pick rather than costing it.
var _qsRecords = null;
var _qsRecordsPromise = null;
function loadPregenRecords() {
  if (_qsRecords) return Promise.resolve(_qsRecords);
  if (_qsRecordsPromise) return _qsRecordsPromise;
  _qsRecordsPromise = fetch('public/data/pregen_sheets.json')
    .then(function (r) { return r.json(); })
    .then(function (j) { _qsRecords = (j && j.characters) || null; return _qsRecords; })
    .catch(function () { _qsRecordsPromise = null; return null; });
  return _qsRecordsPromise;
}
// `enabled` is the SWITCH, not a convenience: with the paper face off this component is the shipped
// carousel and must not grow a fetch it never had. R-LFM-1 governs the wait — until the records
// land the shelf and the folio are RESERVED AND EMPTY, never a placeholder count and never a card
// drawn from the other file.
function usePregenRecords(enabled) {
  var _s = React.useState(_qsRecords), recs = _s[0], setRecs = _s[1];
  React.useEffect(function () {
    if (!enabled || recs) return undefined;
    var live = true;
    loadPregenRecords().then(function (j) { if (live && j) setRecs(j); });
    return function () { live = false; };
  }, [enabled, recs]);
  return enabled ? recs : null;
}

// THE CARD'S EDITORIAL LINE, by id. Built once from the shipped array so the shelf's lookup is a
// hash rather than a scan per card, and `id` is the join because it is the key both files agree on
// (`test/pregen_card_record_parity.cjs` asserts the same ids in the same order from the other side).
var QS_CARD_BY_ID = {};
PREGEN_CHARACTERS.forEach(function (c) { QS_CARD_BY_ID[c.id] = c; });

// THE WORD FOR THE ACT THAT TAKES A PREGEN (B-721). The paper act must print the SHIPPED string and
// not a second opinion of it, and a button's label is copy.
// AND THE SHIPPED LITERALS ARE DELIBERATELY NOT REPLACED WITH THIS NAME, which is the opposite of what
// row 1 did with `LANDING_DOORS` — because something outside this file reads them: the lab board's
// generator resolves its own caption with `next(l for l in APP_LINES if l.strip() == "SELECT
// CHARACTER")` (`qsg_build.py`, `SELECT_LABEL`), so folding the two JSX texts into `{QS_SELECT_LABEL}`
// would leave no line for that parse and break the APPROVED board's next rebuild — a read-only tree
// this row may not edit. The legacy body also stays byte-identical to HEAD, which is row 1's evidence.
// SO THE DUPLICATION IS GUARDED RATHER THAN REMOVED: `smoke_qsg_transplant` parses the legacy body's
// own JSX text out of the source and asserts the paper button prints exactly that, so the two cannot
// drift in silence. B-726 deletes the legacy body and the copy becomes single by subtraction.
var QS_SELECT_LABEL = 'SELECT CHARACTER';

// THE REGISTER IS THE GAME LINE (R-QSG-5 (2)), and it is a field the DATA carries rather than a
// taxonomy it lacks. The app's own tab row (`SubTabs` over `ROW_LABELS_MERGED`) has exactly ONE
// member today — `FFG_PREGEN_CHARACTERS` is empty and the Beginner Game shelf retired — and a tab
// row of one is not a taxonomy. What every record carries is `book`: six per line, and the same
// field that decides which campaign mechanic each of those six was built with. Every count below is
// its own slice's length and every name is `gameLineTitle`'s, which resolves off `books.vocab.json`.
var QS_REGISTER_IDS = ['eote', 'aor', 'fad'];
function qsRegisters(records) {
  return QS_REGISTER_IDS.map(function (k) {
    return {
      id: k,
      name: gameLineTitle(k),
      rows: (records || []).filter(function (c) { return c.book === k; }),
    };
  });
}

// A RECORD KEYS ITS SKILLS BY THE SHEET KEY (`PILSP`); the canonical vocabulary keys by id
// (`piloting_space`) and carries the label and the governing characteristic. `loadCanonicalCharacterBuild`
// already builds the three maps, so this INVERTS them rather than re-reading the registry — and it
// is deliberately not `SKILL_NAMES` / `SKILL_CHARS`, the file's legacy hand-typed pair (SKILL_CHARS since deleted, F-100 M7; the creator's
// own bridge comment at the skills step says as much). A shelf that prints a registry's numbers
// beside a typed label is half derived.
function qsSkillIndex(cb) {
  var out = {};
  if (!cb || !cb.skillSheetKeys) return out;
  Object.keys(cb.skillSheetKeys).forEach(function (id) {
    var key = cb.skillSheetKeys[id];
    if (!key || out[key]) return;
    out[key] = {
      label: (cb.skillNames && cb.skillNames[id]) || id,
      characteristic: (cb.skillChars && cb.skillChars[id]) || null,
    };
  });
  return out;
}

// THE ONE POOL A CARD HAS ROOM FOR, chosen by the RULE rather than by taste: the trained skill that
// throws the most dice. `SWRPGDice.buildPool` is the arithmetic and it is not restated here — the
// characteristic sets how many dice you throw and each rank upgrades one of them (EotE p.21), so a
// pool's SIZE is proficiency + ability. Ties break on the upgraded count and then on the label, so
// the choice is stable across renders and cannot become a property of key order.
// A skill key the vocabulary does not answer to is SKIPPED here and REFUSED by
// `test/pregen_shelf_reconciliation.cjs`, so the skip is unreachable in shipped data and a record
// that grew an unresolvable key turns a guard red instead of quietly losing its best roll.
// EVERY TRAINED SKILL AS A POOL ROW, in the BOARD's own order — by the skill's own LABEL, which is
// the registry's and not the record's key order (`opened_card`'s `sorted(c["skills"], key=label)`).
// B-721 needs all of them and B-720 needs the best ONE; one computer serves both, so the shelf card's
// pool and the opened card's sixth row cannot be produced by two rules that drift apart.
function qsSkillRowsOf(rec, idx) {
  var D = (typeof window !== 'undefined') && window.SWRPGDice;
  if (!D || !D.buildPool) return [];
  var rows = [];
  Object.keys((rec && rec.skills) || {}).forEach(function (k) {
    var s = idx[k];
    if (!s || !s.characteristic) return;
    var cv = ((rec.characteristics || {})[s.characteristic]) || 0;
    var pool = D.buildPool({ characteristic: cv, skillRank: rec.skills[k] });
    rows.push({
      key: k, label: s.label, charKey: s.characteristic, charVal: cv, rank: rec.skills[k],
      pool: pool, size: (pool.proficiency || 0) + (pool.ability || 0), upg: pool.proficiency || 0,
    });
  });
  rows.sort(function (a, b) { return a.label < b.label ? -1 : (a.label > b.label ? 1 : 0); });
  return rows;
}
function qsBestSkill(rec, idx) {
  var best = null;
  qsSkillRowsOf(rec, idx).forEach(function (cand) {
    if (!best
        || cand.size > best.size
        || (cand.size === best.size && cand.upg > best.upg)
        || (cand.size === best.size && cand.upg === best.upg && cand.label > best.label)) best = cand;
  });
  return best;
}

// WHICH CHARACTERISTICS WERE PAID FOR — the record's value above the SPECIES REGISTRY's own base.
// `.hp-char[data-raised="1"]` is the kit's gold "you bought this" bead (paper.css), and on a pregen
// the thing that earns it is derivable rather than decorative.
// THE BASE IS THE PRISTINE VIEW, not the source-toggle overlay: a shipped record is an OWNED
// character, and the app resolves owned things against `_canonicalCharbuildBase` while discovery
// reads the toggle (`sheetTalentsForDisplay`'s seam, and the merge point's own comment: "Discovery
// (toggle) vs ownership (stamp)"). It is also the view `test/pregen_shelf_reconciliation.cjs` reads
// off disk, so the mark on the page and the number in the guard are the same base.
// A base missing any of the six is NOT guessed at — the marks are withheld whole, because a mark
// drawn against an absent base is a claim the data cannot contradict.
function qsRaisedOf(rec, speciesRec) {
  var base = speciesRec && speciesRec.characteristics;
  if (!base) return null;
  for (var i = 0; i < FC_CHAR_ORDER.length; i++) if (base[FC_CHAR_ORDER[i]] == null) return null;
  var up = {};
  FC_CHAR_ORDER.forEach(function (a) {
    if ((((rec && rec.characteristics) || {})[a] || 0) > base[a]) up[a] = true;
  });
  return up;
}

/* ── B-721 · THE OPENED CHARACTER'S FOUR RESOLVERS (R-QSG-6 row 3) ─────────────────────────────────
   The whole record on paper means four things the shelf card does not print, and every one of them
   is a REGISTRY JOIN rather than a string the record stores: the talents' names, the Force power's
   name, the loadout's names/stats/prices, and the campaign mechanic. The record holds id-refs and
   quantities and nothing else, which is ADR 0003's whole point — so the names and the credits below
   are looked up at render or they are invented. */

// THE ONE-LINE STAT FOR A LOADOUT ROW, WITH ONE HOME. This was `QBLoadoutStep.resolveItem`'s own
// three-branch expression (:34497), and the opened card's ledger needs exactly the same sentence —
// so it moved OUT rather than being copied, and Guided's loadout step now calls it too. A second
// copy of "how a weapon reads on one line" is the class PRAXIS §5 names, and the two surfaces are
// the two CREATE doors of one programme. `crit` is read with `!= null` and NOT with `||`, because
// the training lightsaber's crit is **0** and a falsy-guard would print `-` for a real number.
function qsgItemStat(section, rec) {
  var rangeLabels = { wrEngaged: 'Engaged', wrShort: 'Short', wrMedium: 'Medium', wrLong: 'Long', wrExtreme: 'Extreme' };
  if (section === 'weapons') {
    var dmg = rec.damage ? (rec.damage.base === 'brawn' ? 'Br' + window.ItemResolve.brawnDamageText(rec.damage.value) : String(rec.damage.value != null ? rec.damage.value : '?')) : '?';
    return dmg + ' dmg · Crit ' + critText(rec.crit) + ' · '
      + ((rangeLabels['wr' + (rec.range || '').charAt(0).toUpperCase() + (rec.range || '').slice(1)]) || rec.range || '?');
  }
  if (section === 'armor') {
    return '+' + (rec.soak || 0) + ' Soak' + ((rec.defense || 0) > 0 ? ' · ' + rec.defense + ' Defense' : '');
  }
  return rec.type || '';
}

// ONE CURATED LOADOUT KEY, RESOLVED — the second half of `QBLoadoutStep.resolveItem`, moved out at
// B-722 for the same reason B-721 moved the first half: Guided's PAPER loadout page prices the same
// three bundles off the same registries, and "how a loadout key becomes a row" existing twice is the
// class PRAXIS §5 names. The dark step now calls this; no rendered string changed. A key the canonical
// catalogue cannot answer degrades to the key itself at zero credits, exactly as it did inside the
// component — `handleLoadout` skips such an item outright, so a row that prices at 0 and a pack that
// is one item short are the two halves of one already-shipped behaviour and not a new one.
function qsgLoadoutItem(key) {
  var ref = canonicalRefForSourceKey(key);
  var rec = ref && window._canonicalRegistries ? window._canonicalRegistries[ref.section][ref.id] : null;
  if (!rec) return { name: key, price: 0, enc: 0, section: 'gear', stat: '' };
  return { name: rec.name, price: rec.price || 0, enc: rec.encumbrance || 0, section: ref.section,
           stat: qsgItemStat(ref.section, rec) };
}

// THE PACK, PRICED FROM THE REGISTRIES AT RENDER. Weapons in the record's order, then the armour
// with the record's own `state` beside its name, then the gear with its quantity — the board's own
// order (`kit_ledger`). An armour row prints its soak because that is the number the SOAK in the
// vitals above was derived from. A missing registry record is DROPPED here and the row is reported
// by the smoke's count leg rather than printed as a dangling id: a ledger with a line reading
// `undefined` is worse than a ledger one line short, and the count is what makes the short one
// visible. `qty` multiplies the PRICE and not the soak.
function qsKitRows(rec) {
  var regs = (typeof window !== 'undefined') && window._canonicalRegistries;
  if (!regs) return null;
  var eq = (rec && rec.equipment) || {};
  var rows = [];
  ((eq.weapons) || []).forEach(function (w) {
    var r = regs.weapons && regs.weapons[w.id];
    if (r) rows.push({ name: r.name, stat: qsgItemStat('weapons', r), price: r.price || 0 });
  });
  var ar = eq.armor || {};
  if (ar.id) {
    var a = regs.armor && regs.armor[ar.id];
    if (a) rows.push({ name: a.name + (ar.state ? ' (' + ar.state + ')' : ''), stat: qsgItemStat('armor', a), price: a.price || 0 });
  }
  ((eq.gear) || []).forEach(function (g) {
    var r = regs.gear && regs.gear[g.id];
    if (!r) return;
    var q = g.qty || 1;
    rows.push({ name: r.name + (q > 1 ? ' ×' + q : ''), stat: qsgItemStat('gear', r), price: (r.price || 0) * q });
  });
  return rows;
}

// THE TALENTS AND THE FORCE POWER, BY NAME. `talents.json` and `force_powers.json` arrive on the
// SAME charbuild view the shelf's skill index comes off, so this adds no fetch. The rank is printed
// only when it is above one, which is the board's rule and also the record's shape: every one of the
// eighteen carries a single rank-1 talent today, so a printed `1` would be noise on all eighteen.
// An id the registry cannot answer is DROPPED rather than printed as `(unknown talent)`: the shelf is
// a reading surface, and `test/pregen_shelf_reconciliation.cjs` plus the smoke's count legs are where
// a dangling id-ref belongs.
function qsTalentNames(rec, cb) {
  var reg = (cb && cb.talents) || null;
  if (!reg) return null;
  return ((rec && rec.talents) || []).map(function (t) {
    var r = reg[t.id];
    if (!r) return null;
    return r.name + ((t.rank || 1) > 1 ? ' ' + t.rank : '');
  }).filter(Boolean);
}
function qsPowerNames(rec, cb) {
  var reg = (cb && cb.forcePowers) || null;
  if (!reg) return null;
  return ((rec && rec.forcePowers) || []).map(function (p) {
    var r = reg[p.id];
    return r ? r.name : null;
  }).filter(Boolean);
}

// WHICH MECHANIC A RECORD CARRIES IS DECIDED BY ITS LINE AND NOTHING ELSE — `mechanicForLine` is the
// app's ONE reader of that mapping (it replaced four ternaries), so the field name, the track's scope
// word and the section heading all come off `FC_GAME_LINES` rather than off a table this block types.
// The record's `value` is the POST-TRADE figure (B-740: `mechanicTradeIds` names the trade and
// `charResolve.js` :2055 persists only the final value), so the number below is the one a player
// receives and the trade is not re-derived here.
function qsMechanicOf(rec) {
  var field = mechanicForLine(rec && rec.book);
  var m = rec && rec[field];
  if (!m) return null;
  return {
    field: field,
    title: field.charAt(0).toUpperCase() + field.slice(1),
    value: m.value, type: m.type, description: m.description,
  };
}

// THE CREDIT FIGURES CARRY THEIR THOUSANDS SEPARATOR, which is the board's `{p:,}` and the only
// formatting in this row. Fixed to `en-US` rather than the viewer's locale: the ledger's three
// columns are tabular-nums and a locale that groups with a space or a full stop would make the
// carried total and the purse disagree typographically on the same line.
function qsCredits(n) {
  return Number(n || 0).toLocaleString('en-US');
}

// THE SIX-CAPSULE STRIP, in the board's own markup rather than through `FCbRail`. Two reasons and
// both are load-bearing: that component stamps `data-tour-id="p-custom-chars-box"` on its first
// tile, so eighteen cards would put eighteen copies of the CREATOR's anchor on this screen — and
// the anchors are what row 1 proved set-identical to HEAD; and it wraps the rail in `.hp-railwrap`,
// which the board does not draw inside a card.
// SPANS WHERE THE BOARD DRAWS DIVS, and that is the one element change on this shelf: the card is a
// `<button>` and a button's content model is phrasing content, so the strip, the vitals and the
// skill row are spans. Every one of those classes declares its own `display` in `paper.css`
// (`grid`, `block`, `inline-flex`), so the layout is the board's to the pixel; the classes, which
// are the contract, are unchanged. Reported both ways with the runhead's button (B-730 (1)).
// `short` IS GUIDED'S SPECIES CARD (B-722), the strip's THIRD consumer after the shelf card and the
// opened card — and it is two of the board's own values rather than a second component. `body_species`
// draws the same `hp-char` contract at card-in-a-grid size: the label is the key's FIRST TWO LETTERS
// uppercased (BR AG IN CU WI PR — which is also what the shipped dark species step prints, so the
// short form is the app's own vocabulary for this object and not a lab invention), the rail's gap is
// 2px and the bead 13px, both inline on the frame. No `raised` is passed there: the gold bead is
// Quick Start's "the record paid for this" mark and a species' own base has nothing to have paid.
function QSRail(p) {
  return (
    <span className="hp-rail" style={p.short ? { gap: '2px' } : undefined}>
      {FC_CHAR_ORDER.map(function (a) {
        return (
          <span className="hp-char" key={a} data-char={a}
            data-raised={(p.raised && p.raised[a]) ? '1' : undefined}>
            <span className="hp-char-val"><span className="hp-char-bead"
              style={p.short ? { fontSize: '13px' } : undefined}>{p.chars[a]}</span></span>
            <i className="hp-char-lab">{p.short ? a.slice(0, 2).toUpperCase() : FC_CHAR_WORDS[a]}</i>
          </span>);
      })}
    </span>
  );
}

// THE THREE NUMBERS A READER CHOOSES ON, plus a fourth that exists for six of the eighteen. They
// are the record's `derived` block — the only computed block it carries — and all four of them are
// recomputed from the record's own characteristics, the species registry's base, the armour
// registry's soak and the record's `derivedBonuses` by `test/pregen_shelf_reconciliation.cjs`,
// which refuses a mismatch. Force rating is drawn ONLY when it is non-zero: a heading that appears
// only when its data is non-empty cannot promise a choice the character does not have.
function QSVitals(p) {
  // EXEMPT FROM THE n/threshold DISPLAY LAW, on Jason's word 2026-09-20 (B-747, option (a)). This is an
  // APPROVED qsg frame and a pregen has no current wounds — the card is the character's ceiling as
  // authored, not a state readout, and `smoke_qsg_transplant` asserts these against the record's own
  // `derived` block. Do not "fix" it to `0/13`: the law reaches STATE, and a derivation or an as-authored
  // ceiling is the subject rather than a position within one.
  var d = p.derived || {};
  var cells = [['Wounds', d.woundThreshold], ['Strain', d.strainThreshold], ['Soak', d.soak]];
  if (d.forceRating) cells.push(['Force rating', d.forceRating]);
  return (
    <span className="hp-status">
      {cells.map(function (c) {
        return (
          <span key={c[0]}>
            <span className="hp-status-k">{c[0]}</span>
            <span className="hp-status-v">{c[1]}</span>
          </span>);
      })}
    </span>
  );
}

// ONE SKILL AND THE POOL IT THROWS. The arrangement is the board's MEASURED fix rather than a
// taste: the kit's `.hp-skill` is three columns with the NAME column flexible, and under a 350px
// card the flexible element is the one that yields — `Piloting (Space)` measured 103px of text in a
// 63px box on the board's first pass. `.qs-sk` (already in `lib/shared/qsg.css` from row 1) is two
// columns with the annotation under the name; the class stays, so reuse carries its floors with it.
function QSSkillRow(p) {
  var b = p.best, D = window.SWRPGDice;
  return (
    <span className="hp-skill qs-sk">
      <span className="qs-skn">
        <span className="hp-skill-name">{b.label}</span>
        <span className="hp-skill-char">{b.charKey.toUpperCase() + ' ' + b.charVal + ' · Rank ' + b.rank}</span>
      </span>
      <span className="hp-pool" data-pool={(b.pool.proficiency || 0) + 'p' + (b.pool.ability || 0) + 'a'}>
        {D && D.renderPool ? D.renderPool(b.pool, { size: 1, cssSized: true }) : null}
      </span>
    </span>
  );
}

// ONE PREGEN AS A FRONTISPIECE — the landing's frontispiece is a character on paper and a pregen IS
// a whole character, so a shelf card is that drawing at card size. Nothing here is a new concept:
// the strip, the vitals and the roll are the same three objects the landing's stage carries.
function QSShelfCard(p) {
  var rec = p.rec;
  var card = QS_CARD_BY_ID[rec.id] || {};
  var nSkills = Object.keys(rec.skills || {}).length;
  var nTalents = (rec.talents || []).length;
  var spent = rec.xp && rec.xp.spent;
  return (
    <button type="button" className={'hp-plate qs-card' + (p.dim ? ' qs-dim' : '')} data-pad="data"
      data-qs-card={rec.id} onClick={function () { p.onOpen(rec.id); }}>
      <span className="qs-chead">
        <span>
          <span className="qs-name">{rec.name}</span>
          <span className="hp-run qs-sub">
            <b>{rec.species}</b>{' · ' + rec.career + ' · '}<b>{rec.specialization}</b>
          </span>
        </span>
        <span className="hp-cite">{gameLineTitle(rec.book)}</span>
      </span>
      {card.tagline ? <span className="qs-tag">{card.tagline}</span> : null}
      <QSRail chars={rec.characteristics || {}} raised={p.raised} />
      <QSVitals derived={rec.derived} />
      {p.best ? <QSSkillRow best={p.best} /> : null}
      <span className="hp-none qs-prov">
        {(card.playstyle ? card.playstyle + ' · ' : '')
          + (spent != null ? spent + ' XP spent · ' : '')
          + nSkills + ' skills · ' + nTalents + ' talent' + (nTalents === 1 ? '' : 's')}
      </span>
    </button>
  );
}

/* THE OPENED CHARACTER — THE WHOLE RECORD ON PAPER (B-721; frames E and F at lab `284f9929`,
   `qsg_build.py`'s `opened_card`, class for class) ─────────────────────────────────────────────────
   IT EXPANDS WHERE IT STANDS. The board's shelf swaps this plate in for the card in the grid cell the
   card had and dims the rest of the register, which is what the shipped carousel does with opacity —
   so the reader never loses their place and the shelf keeps reading. There is no CLOSE control on the
   frame and none is added: pressing another card moves the opening, which is the carousel's own
   behaviour and the only one the drawing supports.

   THE ORDER IS CONSEQUENCE, NOT SCHEMA (the frame's own caption): who she is · what she can take ·
   what she will roll · what she owes · what is in the pack. Every FIGURE is the record's (R-QSG-4).

   THE THREE STRINGS THAT ARE NOT FIGURES, and the split is B-730's settled one: the TAGLINE and the
   PLAYSTYLE are the card's, because that editorial line exists nowhere else and is joined by id. The
   READING PARAGRAPH is the RECORD's `backstory` and NOT the card's `description` — which is where this
   build departs from the drawn frame, reported both ways in the row's report: the board printed
   `card["description"]` on the reasoning that a record carries no editorial prose, and the record DOES
   carry a paragraph of exactly that kind. R-QSG-4 ("the record is what a player receives") and the
   row's own instruction both name the record, `pregen_card_record_parity` does not compare the pair
   (the two keys are not shared, so it is outside its derived intersection), and the two strings are
   near-paraphrases rather than different claims. The SLOT, the class and the position are the board's
   to the pixel; only the source of the sentence moved. Jason's to settle (`mockup-is-law`).

   THE VITALS ARE THE SAME EXEMPT OBJECT as the shelf card's — see `QSVitals`'s own marker above:
   B-747 option (a), Jason 2026-09-20. A pregen record carries no `currentWounds`/`currentStrain` key
   at all (checked: zero occurrences across the eighteen), so there is no `n` to put before the slash;
   this is the character's ceiling AS AUTHORED and the ceiling is the subject. The opened card reads
   the identical `derived` block through the identical component, so it inherits the exemption rather
   than claiming a second one. */
function QSOpenedCard(p) {
  var rec = p.rec;
  var card = QS_CARD_BY_ID[rec.id] || {};
  var xp = rec.xp || {};
  var rows = p.skillRows || [];
  var talents = p.talents;
  var powers = p.powers;
  var mech = qsMechanicOf(rec);
  var kit = p.kit;
  var carried = kit ? kit.reduce(function (s, r) { return s + r.price; }, 0) : 0;
  return (
    <div className="hp-plate qs-open" data-pad="read" data-qs-open={rec.id}>
      <div className="qs-chead">
        <div>
          <h2 className="qs-name" style={{ fontSize: 22 }}>{rec.name}</h2>
          <p className="hp-run qs-sub">
            <b>{rec.species}</b>{' · ' + rec.career + ' · '}<b>{rec.specialization}</b>
          </p>
        </div>
        <span className="hp-cite">{gameLineTitle(rec.book)}</span>
      </div>
      {card.tagline ? <p className="qs-tag" style={{ marginTop: 8 }}>{card.tagline}</p> : null}
      {rec.backstory ? <p className="qs-desc" style={{ marginTop: 8 }}>{rec.backstory}</p> : null}

      <div className="hp-sechead">
        <h2>Characteristics</h2>
        <span className="hp-cite">{xp.spent + ' of ' + xp.total + ' XP spent'}</span>
      </div>
      <QSRail chars={rec.characteristics || {}} raised={p.raised} />
      <QSVitals derived={rec.derived} />

      <div className="hp-sechead">
        <h2>What you will roll</h2>
        <span className="hp-cite">{Object.keys(rec.skills || {}).length + ' trained'}</span>
      </div>
      {rows.map(function (r) { return <QSSkillRow key={r.key} best={r} />; })}

      {/* THE HEADING IS THE BOARD'S OWN SINGULAR. All eighteen records carry exactly ONE talent
          (checked), so the word is right on every character this surface draws today; a pluralising
          ternary here would be copy the frame does not have, over data that cannot reach it. */}
      <div className="hp-sechead"><h2>Talent</h2></div>
      <p className="hp-prose qs-talents" style={{ margin: 0 }}>{talents ? talents.join(', ') : null}</p>

      {powers && powers.length ? (
        <React.Fragment>
          <div className="hp-sechead"><h2>Force power</h2></div>
          <p className="hp-prose qs-powers" style={{ margin: 0 }}>{powers.join(', ')}</p>
        </React.Fragment>
      ) : null}

      {mech ? (
        <React.Fragment>
          <div className="hp-sechead"><h2>{mech.title}</h2></div>
          <div className="hp-track" data-primary="1" data-qs-mech={mech.field}>
            <div className="hp-track-top">
              <span className="hp-track-scope">{mech.title}</span>
              <span className="hp-track-val">{mech.value}</span>
            </div>
            <div className="hp-track-name">{mech.type}</div>
            <div className="hp-track-foot">
              <p className="hp-prose" style={{ margin: 0 }}>{mech.description}</p>
            </div>
          </div>
        </React.Fragment>
      ) : null}

      <div className="hp-sechead">
        <h2>The pack</h2>
      </div>
      {/* `<tbody>` IS EXPLICIT where the board's HTML string leaves it to the parser — the browser
          inserts one for the board's table too, so the rendered DOM is the same; React will not. */}
      <table className="hp-ledger">
        <tbody>
          {(kit || []).map(function (r, i) {
            return (
              <tr key={r.name + '#' + i} data-qs-kit="row">
                <td className="hp-lkey" style={{ width: 'auto', whiteSpace: 'normal', paddingRight: 10 }}>{r.name}</td>
                <td className="hp-lval" style={{ textAlign: 'left', fontWeight: 400 }}>{r.stat}</td>
                <td className="hp-lval">{qsCredits(r.price)}</td>
              </tr>);
          })}
          <tr data-qs-kit="total">
            <td className="hp-lkey">Carried · credits in hand</td>
            <td className="hp-lval" style={{ textAlign: 'left', fontWeight: 400 }}>
              {(kit ? kit.length : 0) + ' things'}
            </td>
            <td className="hp-lval">
              {qsCredits(carried) + ' · ' + qsCredits((rec.equipment || {}).credits)}
            </td>
          </tr>
        </tbody>
      </table>

      <p className="hp-none qs-openfoot" style={{ display: 'block', marginTop: 12 }}>
        {(card.playstyle ? card.playstyle + ' · ' : '')
          + String(rec.source || '').toUpperCase() + ' · ' + xp.remaining + ' XP left'}
      </p>
    </div>
  );
}

// THE REGISTER TABS, and the count on each is read off its own slice. State is a MARK and not a
// hue (qsg.css gives the standing tab the selection ink AND a rule under it), and it is announced
// as well as painted: `aria-pressed` is the a11y floor over a board that draws no interaction —
// C-MU-4, the standard outranks the mockup, and no drawn pixel moves.
function QSRegisters(p) {
  var tabs = [{ id: null, name: 'All', n: p.total }].concat(p.registers.map(function (r) {
    return { id: r.id, name: r.name, n: r.rows.length };
  }));
  return (
    <div className="qs-regs">
      {tabs.map(function (t) {
        var now = (p.reg || null) === t.id;
        return (
          <button type="button" key={t.id || 'all'} className="qs-reg" data-qs-reg={t.id || 'all'}
            data-now={now ? '1' : undefined} aria-pressed={now ? 'true' : 'false'}
            onClick={function () { p.onPick(t.id); }}>
            <b>{t.name}</b><i>{t.n}</i>
          </button>);
      })}
    </div>
  );
}

// THE SHELF. With no register standing it is all eighteen in the record's own order, under the LINE
// heading at the book boundary the carousel already marks — its dot rail inserts a gap where
// `book` changes, so the boundary exists in the product and this draws it as a heading, with the
// line's own orientation sentence (`PREGEN_BOOK_LINE`) under it. With a register standing it is
// that line's six.
// B-721 — `openId` EXPANDS ONE CARD IN PLACE: the opened character takes the grid cell its card had
// and every other card on the shelf dims (`qs-dim`, the carousel's own 0.15 neighbours as paper),
// which is the board's `shelf(opened=…)` exactly. The dim is driven by "something is open" and not by
// "this card is not the open one", so a register the open character does not belong to dims nothing —
// and it cannot, because the host only passes an id the standing register actually holds.
function QSShelf(p) {
  var groups = p.reg
    ? p.registers.filter(function (r) { return r.id === p.reg; })
    : p.registers;
  return (
    <React.Fragment>
      {groups.map(function (r) {
        return (
          <React.Fragment key={r.id}>
            {p.reg ? null : (
              <React.Fragment>
                <div className="hp-sechead">
                  <h2>{r.name}</h2>
                  <span className="hp-cite">{r.rows.length + ' characters'}</span>
                </div>
                <p className="hp-lead" style={{ margin: 0 }}>{PREGEN_BOOK_LINE[r.id]}</p>
              </React.Fragment>
            )}
            <div className="qs-cards">
              {r.rows.map(function (rec) {
                if (p.openId && rec.id === p.openId) {
                  return (
                    <QSOpenedCard key={rec.id} rec={rec} raised={p.raisedOf(rec)}
                      skillRows={p.skillRowsOf(rec)} talents={p.talentsOf(rec)}
                      powers={p.powersOf(rec)} kit={p.kitOf(rec)} />);
                }
                return (
                  <QSShelfCard key={rec.id} rec={rec} onOpen={p.onOpen} dim={!!p.openId}
                    raised={p.raisedOf(rec)} best={p.bestOf(rec)} />);
              })}
            </div>
          </React.Fragment>);
      })}
    </React.Fragment>
  );
}

/* ══════════════════════════════════════════════════════
   PRE-GEN CAROUSEL
══════════════════════════════════════════════════════ */
// `paper` (B-719, R-QSG-3) — the SAME component takes the ground as a prop; the host passes
// `qsgTransplantSwitch()`. Absent or false is today's carousel, unchanged.
function PreGenCarousel({ onBack, onSelect, paper }) {
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 393;
  const mode = useLayoutMode();   // B-719: the app's 1024 seam, as `data-band` on the paper shell.
                                  // The shelf's own rungs are CONTAINER queries (qsg.css `.qs`).
  // B-720 — THE SHELF'S THREE INPUTS. `records` is the authority (R-QSG-4) and is fetched only with
  // the paper face on, so the shipped carousel grows no request it never had. `useCanonicalReady`
  // is called unconditionally because it is free where it is not used: both loaders are idempotent
  // and `PlayerApp` already warms them at boot, so this resolves against a cached promise and adds
  // no fetch and no node to the dark tree.
  const records = usePregenRecords(!!paper);
  const canonReady = useCanonicalReady();
  const [reg, setReg] = useState(null);   // the standing register, or null for ALL
  // B-721 — the OPENED record's id on the paper shelf. Its own state and not the carousel's
  // `activeIdx`/`isExpanded` pair: the two faces open a character in two different places and sharing
  // one flag between them would make the dark carousel's physics a dependency of a paper reading.
  const [openId, setOpenId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const collapsedW = 260;
  const expandedW = viewportW - 32;
  const cardGap = 16;
  const wasDragging = useRef(false);

  // Built-in pregens only (the LAN GM-overlay was removed — local-first posture).
  const ROWS = COMMERCIAL_MODE
    ? [PREGEN_CHARACTERS]
    : (FFG_PREGEN_CHARACTERS.length > 0
        ? [PREGEN_CHARACTERS, FFG_PREGEN_CHARACTERS]
        : [PREGEN_CHARACTERS]);
  // ROWS.length is now always 1 (the Beginner Game shelf retired), but the branch stays: it is the
  // same shape that made emptying the list a one-line change, and a future second shelf inherits it.
  const ROW_LABELS_MERGED = ROWS.length > 1
    ? ['CHARACTERS', 'BEGINNER GAME CHARACTERS']
    : ['CHARACTERS'];

  const [activeRow, setActiveRow] = useState(0);
  const verticalStart = useRef(null);
  const characters = ROWS[activeRow] || ROWS[0];

  // Row physics — named row0Physics/row1Physics to avoid shadowing the
  // row0 useMemo above (duplicate const name crashes Babel).
  const row0Physics = useCarouselPhysics(ROWS[0].length, collapsedW, cardGap);
  const row1Physics = useCarouselPhysics((ROWS[1] || []).length || 1, collapsedW, cardGap);

  const activePhysics = activeRow === 0 ? row0Physics : row1Physics;
  const { offset, setOffset, activeIdx, setActiveIdx, getSnapOffset,
    onTouchStart: hTouchStart, onTouchMove: hTouchMove, onTouchEnd: hTouchEnd,
    onMouseDown, onMouseMove, onMouseUp,
    isDragging, velocity, skipLerp,
  } = activePhysics;

  // Vertical swipe detection — wraps horizontal touch handlers
  const onTouchStart = useCallback((e) => {
    verticalStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    hTouchStart(e);
  }, [hTouchStart]);

  const verticalLocked = useRef(false);
  const onTouchMove = useCallback((e) => {
    if (verticalLocked.current) return;
    hTouchMove(e);
  }, [hTouchMove]);

  const onTouchEnd = useCallback((e) => {
    if (verticalStart.current && !verticalLocked.current) {
      const dy = e.changedTouches[0].clientY - verticalStart.current.y;
      const dx = e.changedTouches[0].clientX - verticalStart.current.x;
      if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        // Vertical swipe detected
        if (dy < 0 && activeRow < ROWS.length - 1) {
          setActiveRow(r => r + 1);
          setIsExpanded(false);
          verticalStart.current = null;
          verticalLocked.current = false;
          return;
        }
        if (dy > 0 && activeRow > 0) {
          setActiveRow(r => r - 1);
          setIsExpanded(false);
          verticalStart.current = null;
          verticalLocked.current = false;
          return;
        }
      }
    }
    verticalLocked.current = false;
    hTouchEnd(e);
  }, [hTouchEnd, activeRow]);

  // Track drag state for tap detection
  const handleTouchStartWrap = useCallback((e) => {
    wasDragging.current = false;
    onTouchStart(e);
  }, [onTouchStart]);

  const handleTouchMoveWrap = useCallback((e) => {
    onTouchMove(e);
    if (isDragging.current) wasDragging.current = true;
  }, [onTouchMove]);

  const handleMouseDownWrap = useCallback((e) => {
    wasDragging.current = false;
    onMouseDown(e);
  }, [onMouseDown]);

  const handleMouseMoveWrap = useCallback((e) => {
    onMouseMove(e);
    if (isDragging.current) wasDragging.current = true;
  }, [onMouseMove]);

  const handleCardTap = useCallback((charId, idx) => {
    if (wasDragging.current && Math.abs(velocity.current) > 0.5) return;
    if (idx !== activeIdx) {
      setActiveIdx(idx);
      return;
    }
    setIsExpanded(prev => !prev);
  }, [activeIdx, setActiveIdx, velocity]);

  const activeChar = characters[activeIdx];
  const bookLabel = activeChar ? BOOK_LABELS[activeChar.book] : '';
  const activeBook = activeChar ? activeChar.book : 'eote';

  // Atmosphere background images per game line
  // ATMOSPHERE RETIRED (Nunes retirement, Addendum 1): the game-line backgrounds are deleted; the
  // consumer below already renders 'none' when the map has no entry.
  const ATMO_IMAGES = {};

  // Compute parallax: map carousel offset to background-position-x
  // Total scroll range = (itemCount - 1) * (cardW + gap)
  const totalScrollRange = (characters.length - 1) * (collapsedW + cardGap);
  const scrollPct = totalScrollRange > 0 ? Math.max(0, Math.min(1, -offset / totalScrollRange)) : 0;
  // Map 0→100% scroll to 20%→80% background-position (don't show edges)
  const bgPosX = 20 + scrollPct * 60;

  // Global mouseup listener for desktop drag release
  useEffect(() => {
    const handler = () => onMouseUp();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, [onMouseUp]);

  // B-719 (R-QSG-3) — THE SHIPPED BODY, NAMED SO THE PAPER FACE CAN STAND OVER IT. Not one line of
  // it changed: the same tree, the same two `data-tour-id` anchors, the same physics, the same
  // `SELECT CHARACTER` press. With the switch OFF it is still what this component returns; with it
  // ON it mounts inside the shelf's interim island until B-720 and B-721 take it. Assigning it to a
  // name is the whole edit — `paper` decides which of two returns runs, exactly as `SellSheet` does it (and the
  // old `SheetVessels` did, until the S5 flip deleted it) (R-QSG-3: one kernel per surface, never a second tree).
  var legacyBody = (
    <div data-tour-id="p-pregen-screen" style={{
      position: 'absolute', inset: 0, background: '#000',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Atmosphere backgrounds — all 3 pre-decoded, only active visible with parallax */}
      {['eote', 'aor', 'fad'].map(function(book) {
        var isActive = activeBook === book;
        return (
          <div key={book} style={{
            position: 'absolute', top: 0, left: '-25%', right: '-25%', bottom: 0, zIndex: 0,
            backgroundImage: ATMO_IMAGES[book] ? 'url(' + ATMO_IMAGES[book] + ')' : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            transform: isActive ? 'translateX(' + (16.5 - scrollPct * 33) + '%)' : 'translateX(0)',
            willChange: isActive ? 'transform, opacity' : 'auto',
            opacity: isActive ? 0.35 : 0,
            transition: 'opacity 0.8s ease',
            pointerEvents: 'none',
          }} />
        );
      })}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0.95) 100%)' }} />
      {/* Header: back + row tabs */}
      <div style={{
        position: 'relative', zIndex: 30, flexShrink: 0,
        display: 'flex', alignItems: 'center', padding: '12px 12px 0',
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#F5C842', display: 'flex', alignItems: 'center',
            gap: '4px', minWidth: '44px', minHeight: '44px', padding: '8px',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <polyline points="13,4 7,10 13,16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {/* PreGen category tabs → the shared SubTabs (nav 3c — the last forked tab idiom; makes SubTabs
            cross-app GM + Player). Row index ↔ string id. */}
        <div data-tour-id="p-pregen-rows" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <SubTabs
            label="Pregen categories"
            accent="#F5C842"
            items={ROW_LABELS_MERGED.map((label, ri) => ({ id: String(ri), label }))}
            activeId={String(activeRow)}
            onSelect={(id) => { setActiveRow(Number(id)); setIsExpanded(false); }}
          />
        </div>
        <div style={{ minWidth: '44px' }} />
      </div>

      {/* Carousel area */}
      <div
        style={{
          flex: 1, display: 'flex', alignItems: 'center',
          position: 'relative', overflow: 'hidden', zIndex: 2,
          cursor: isDragging.current ? 'grabbing' : 'grab',
        }}
        onTouchStart={handleTouchStartWrap}
        onTouchMove={handleTouchMoveWrap}
        onTouchEnd={onTouchEnd}
        onMouseDown={handleMouseDownWrap}
        onMouseMove={handleMouseMoveWrap}
      >
        <div
          style={{
            display: 'flex', gap: `${cardGap}px`,
            transform: `translateX(${offset}px)`,
            willChange: 'transform',
          }}
        >
          {characters.map((char, i) => {
            const isActive = i === activeIdx;
            const showExpanded = isExpanded && isActive;
            const isFfgCard = !COMMERCIAL_MODE && activeRow === 1;
            const textOnly = COMMERCIAL_MODE;

            return (
              <div
                key={char.id}
                onClick={() => handleCardTap(char.id, i)}
                style={{
                  flex: `0 0 ${collapsedW}px`,
                  height: '380px',
                  position: 'relative',
                  cursor: 'pointer',
                  transform: isActive ? 'scale(1)' : 'scale(0.88)',
                  opacity: isActive ? 1 : (showExpanded ? 0.15 : 0.5),
                  transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
                  zIndex: showExpanded ? 10 : 1,
                }}
              >
                {/* Inner card — expands from center without affecting flex layout */}
                <div style={{
                  position: 'absolute',
                  top: showExpanded ? (isFfgCard ? '-100px' : '-20px') : '0',
                  left: showExpanded ? `${-(expandedW - collapsedW) / 2}px` : '0',
                  width: showExpanded ? `${expandedW}px` : `${collapsedW}px`,
                  height: showExpanded ? (isFfgCard ? '580px' : '420px') : '380px',
                  borderRadius: 8,
                  overflow: 'hidden',
                  transition: 'top 0.3s ease-out, left 0.3s ease-out, width 0.3s ease-out, height 0.3s ease-out',
                  border: `1px solid ${isActive ? char.accent + '40' : 'rgba(255,255,255,0.06)'}`,
                }}>

                {/* Portrait background */}
                <div style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: (!COMMERCIAL_MODE && char.portrait)
                    ? `url('${char.portrait}')`
                    : `linear-gradient(160deg, ${char.accent}15 0%, #080808 50%, #000 100%)`,
                  backgroundSize: (isFfgCard && showExpanded) ? '100% auto' : 'cover',
                  backgroundPosition: 'center top',
                  backgroundRepeat: 'no-repeat',
                }} />
                {/* Nunes retirement: no portrait -> the shared Monogram placeholder, centred in the
                    upper portrait region (the card's own text identity sits at the bottom). */}
                {!char.portrait && (
                  <div style={{ position: 'absolute', top: '18%', left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                    {window.SWRPGUI.Monogram({ name: char.name || char.species || '?', accent: char.accent, size: showExpanded ? 96 : 64 })}
                  </div>
                )}

                {/* Gradient overlay — lighter for FFG cards */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: isFfgCard
                    ? (showExpanded
                      ? 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.95) 100%)'
                      : 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0.6) 100%)')
                    : (showExpanded
                      ? 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.95) 100%)'
                      : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.92) 100%)'),
                  transition: 'background 0.35s ease',
                }} />

                {/* Color treatment layer */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(180deg, ${char.accent}08 0%, transparent 40%)`,
                  mixBlendMode: 'multiply',
                }} />

                {/* Card content */}
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 2,
                  display: 'flex', flexDirection: 'column', justifyContent: textOnly ? 'center' : 'flex-end',
                  padding: textOnly ? '20px' : (showExpanded ? '16px 20px 20px' : '16px 18px 20px'),
                }}>
                  {/* All text */}
                  <div style={{
                    opacity: textOnly ? 1 : (showExpanded ? 0 : 1),
                    animation: (!textOnly && showExpanded) ? 'fadeIn 0.3s ease 0.3s forwards' : 'none',
                  }}>
                    {!isFfgCard && <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)',
                      letterSpacing: '0.2em', color: char.accent, opacity: 0.7,
                      marginBottom: '4px',
                    }}>
                      {char.career.toUpperCase()} {'\u2022'} {char.species.toUpperCase()}
                    </div>}

                    {!isFfgCard && <div style={{
                      fontFamily: 'var(--font-display)', fontWeight: 900,
                      fontSize: textOnly ? '16px' : '20px',
                      lineHeight: 1.1,
                      color: '#fff', letterSpacing: '0.02em',
                      marginBottom: '6px',
                      textShadow: `0 0 30px ${char.accent}44`,
                    }}>
                      {char.name}
                    </div>}

                    {/* UXQP-12: explicit playstyle signal — what you'll DO at the table */}
                    {char.playstyle && <div style={{
                      display: 'inline-block', alignSelf: 'flex-start',
                      padding: '2px 8px', marginBottom: '6px',
                      border: `1px solid ${char.accent}55`, borderRadius: '9px',
                      background: `${char.accent}12`,
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)',
                      letterSpacing: '0.14em', color: char.accent,
                      textTransform: 'uppercase',
                    }}>
                      {char.playstyle}
                    </div>}

                    {/* UXQP-13: game-line orientation line (keyed by book) */}
                    {!isFfgCard && PREGEN_BOOK_LINE[char.book] && <div style={{
                      fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
                      color: 'rgba(255,255,255,0.62)', lineHeight: 1.4,
                      marginBottom: '8px',
                    }}>
                      {PREGEN_BOOK_LINE[char.book]}
                    </div>}

                    {!isFfgCard && <div style={{
                      fontFamily: 'var(--font-body)', fontWeight: 300,
                      fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.5)',
                      fontStyle: 'italic',
                      marginBottom: (textOnly || showExpanded) ? '12px' : '0',
                    }}>
                      {char.tagline}
                    </div>}

                    {/* In text-only mode, show description + select inline without expand */}
                    {textOnly && isActive && (
                      <>
                        <div style={{
                          height: '1px', width: '40px',
                          background: char.accent, opacity: 0.4,
                          marginBottom: '8px',
                        }} />
                        <div style={{
                          fontFamily: 'var(--font-body)', fontWeight: 400,
                          fontSize: 'var(--fs-sm)', lineHeight: 1.5,
                          color: 'rgba(255,255,255,0.6)',
                          marginBottom: '12px',
                          maxHeight: '100px', overflow: 'hidden',
                        }}>
                          {char.cardDescription || char.description}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); onSelect(char); }}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            width: '100%', padding: '10px 0',
                            background: `${char.accent}20`,
                            border: `1px solid ${char.accent}50`,
                            borderRadius: 4, cursor: 'pointer',
                            fontFamily: 'var(--font-display)', fontWeight: 700,
                            fontSize: 'var(--fs-xs)', letterSpacing: '0.14em',
                            color: char.accent,
                          }}
                        >
                          SELECT CHARACTER
                        </button>
                      </>
                    )}
                  </div>

                  {/* Expanded description + select button (non-commercial only) */}
                  {!textOnly && showExpanded && (
                    <div style={{
                      opacity: 0,
                      animation: 'fadeUp 0.35s ease 0.32s forwards',
                    }}>
                      <div style={{
                        height: '1px', width: '40px',
                        background: char.accent, opacity: 0.4,
                        marginBottom: '10px',
                      }} />
                      <div style={{
                        fontFamily: 'var(--font-body)', fontWeight: 400,
                        fontSize: 'var(--fs-sm)', lineHeight: 1.55,
                        color: 'rgba(255,255,255,0.65)',
                        marginBottom: '16px',
                      }}>
                        {char.cardDescription || char.description}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelect(char); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          width: '100%', padding: '12px 0',
                          background: `${char.accent}20`,
                          border: `1px solid ${char.accent}50`,
                          borderRadius: 4, cursor: 'pointer',
                          fontFamily: 'var(--font-display)', fontWeight: 700,
                          fontSize: 'var(--fs-xs)', letterSpacing: '0.14em',
                          color: char.accent,
                        }}
                      >
                        SELECT CHARACTER
                      </button>
                    </div>
                  )}
                </div>
                </div>{/* close inner card */}
              </div>
            );
          })}
        </div>
      </div>

      {/* Book label + dot indicators */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '8px 0 28px', gap: '8px', position: 'relative', zIndex: 2,
      }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)',
          letterSpacing: '0.18em',
          color: activeChar ? activeChar.accent : '#F5C842',
          opacity: 0.5,
          transition: 'color 0.3s ease',
        }}>
          {bookLabel}
        </div>
        <div style={{
          fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'var(--fs-sm)',
          fontStyle: 'italic',
          color: 'rgba(255,255,255,0.5)',
          transition: 'color 0.3s ease',
          marginTop: '-4px',
        }}>
          {activeChar && activeChar.book === 'eote' ? 'Survive the underworld.'
           : activeChar && activeChar.book === 'aor' ? 'Fight for the Rebellion.'
           : activeChar && activeChar.book === 'fad' ? 'Answer the call of the Force.'
           : ''}
        </div>
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '6px',
        }}>
          {characters.map((char, i) => {
            const isBookBoundary = i > 0 && characters[i].book !== characters[i - 1].book;
            return (
              <React.Fragment key={char.id}>
                {isBookBoundary && (
                  <div style={{ width: '8px' }} />
                )}
                <div
                  onClick={() => { setActiveIdx(i); setIsExpanded(false); }}
                  style={{
                    width: i === activeIdx ? '18px' : '6px',
                    height: '6px', borderRadius: '3px',
                    background: i === activeIdx ? char.accent : 'rgba(255,255,255,0.15)',
                    transition: 'width 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.3s ease',
                    cursor: 'pointer',
                  }}
                />
              </React.Fragment>
            );
          })}
        </div>

      </div>
    </div>
  );

  if (!paper) return legacyBody;

  /* ── THE PAPER SHELF: THE GROUND, THE SHELL AND THE RUNHEAD (B-719; frames P–Q of lab `284f9929`,
     Jason 2026-09-19 "Approved" + "B") ──────────────────────────────────────────────────────────
     THE GROUND IS DECLARED TWICE and both are load-bearing — the landing's own sentence (B-646),
     which applies here unchanged: on the OUTER element because paper.css's kit is written as
     DESCENDANTS (`[data-doc-ground="creator"] .hp-*`), and on the SHEET because the page's own paper
     — `background-color:var(--doc-paper)`, the ink, the wear wash and the grain — is written as a
     COMPOUND (`[data-doc-ground="creator"].hp-sheet`), which no ancestor can satisfy.

     THE RUNHEAD'S LEFT CELL IS A BUTTON, and the board draws a span. Reported both ways rather than
     decided here: the board's frames are drawings and draw no interaction, the APP's own transplanted
     pages resolved this once already (`FCGearStep`'s runhead, :9616 — a `data-creator-back` button
     carrying the same '‹ <where back goes>' text), and paper.css already carries the AS-6 rule that
     buys that button its 44px with hit area instead of with the bar's geometry (:491). So the app's
     established shape wins on C-MU-4 and nothing about the drawn pixels moves.

     B-720 TAKES THE SHELF. The register and the eighteen frontispieces are drawn from the records,
     so the `shelf` zone is GONE from `[data-qsg-interim]` and out of `QSG_INTERIM_ZONES` with it —
     the enumeration shrinks as the rows land, which is what makes it a ledger rather than a label.

     B-721 TAKES THE LAST ONE. `opened-card` is drawn — the whole record expands where its card stands
     and the act pins to the foot — so this surface's island is GONE and `[data-qsg-interim]` answers
     with NOTHING on the Quick Start face at every band. Two rows in and the ledger is empty on one of
     the two doors, which is the only claim the enumeration was ever making.

     THE FOLIO IS THE RECORD'S COUNT NOW (B-730 (2), closed here): row 1 took it from the shipped
     carousel's own length and marked the debt; the shelf has the records, so it prints theirs. */
  var registers = qsRegisters(records);
  var cb = canonReady ? (window._canonicalCharbuildBase || window._canonicalCharbuild) : null;
  var skillIdx = qsSkillIndex(cb);
  var speciesReg = (cb && cb.species) || null;
  var raisedOf = function (rec) { return speciesReg ? qsRaisedOf(rec, speciesReg[rec.speciesId]) : null; };
  var bestOf = function (rec) { return cb ? qsBestSkill(rec, skillIdx) : null; };
  var skillRowsOf = function (rec) { return cb ? qsSkillRowsOf(rec, skillIdx) : []; };
  var talentsOf = function (rec) { return cb ? qsTalentNames(rec, cb) : null; };
  var powersOf = function (rec) { return cb ? qsPowerNames(rec, cb) : null; };
  var kitOf = function (rec) { return canonReady ? qsKitRows(rec) : null; };
  // PRESSING A CARD OPENS THAT CHARACTER IN PLACE (B-721). The opened id is this surface's OWN state
  // and no longer the carousel's `activeIdx`/`isExpanded` pair — the legacy island is gone, so walking
  // the shipped physics would be moving a tree nobody can see.
  // THE OPENING IS DERIVED AGAINST THE STANDING REGISTER rather than reset by a handler: an open
  // character the register does not hold is NOT open, so a register press can never leave an act
  // pinned to the foot for a character who is not on the screen. Written as a computation because a
  // handler that has to remember to clear a second piece of state is the shape that rots.
  var openRecord = function (id) { setOpenId(function (cur) { return cur === id ? cur : id; }); };
  var openRec = (records && openId) ? records.find(function (c) { return c.id === openId; }) : null;
  if (openRec && reg && openRec.book !== reg) openRec = null;
  var openCard = openRec ? (QS_CARD_BY_ID[openRec.id] || null) : null;
  return (
    <div className="qsg-surface" data-doc-ground="creator" data-qsg-shell="1"
         data-band={mode} data-qsg-face="pregen">
      <div className="hp-sheet qs" data-doc-ground="creator">
        <div className="qs-top">
          <div className="hp-runhead">
            <button type="button" onClick={onBack} data-creator-back="1"
              aria-label={'Back to ' + PLAYER_BAR_COPY.app}
              style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
              <span>{'‹  ' + PLAYER_BAR_COPY.app}</span>
            </button>
            {/* THE FOLIO IS THE RECORD'S COUNT (R-QSG-4). RESERVED AND EMPTY until the records
                land — never the card array's length, never a placeholder, never a dash: R-LFM-1's
                own rule, and the reason B-730 (2) could be closed rather than argued. */}
            <span className="hp-folio">{records ? records.length + ' ready characters' : null}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <span className="hp-slash"><i /><i /><i /></span>
            <div className="hp-maker">{PLAYER_BAR_COPY.create}</div>
          </div>
          <h1 className="hp-desig">{LANDING_DOORS.pregen.title}</h1>
          <div className="hp-desig-rule" />
          {records ? (
            <QSRegisters registers={registers} total={records.length} reg={reg}
              onPick={function (id) { setReg(id); }} />
          ) : null}
        </div>
        <div className="qs-shelf">
          {records ? (
            <QSShelf registers={registers} reg={reg} onOpen={openRecord}
              openId={openRec ? openRec.id : null}
              raisedOf={raisedOf} bestOf={bestOf} skillRowsOf={skillRowsOf}
              talentsOf={talentsOf} powersOf={powersOf} kitOf={kitOf} />
          ) : null}
        </div>
        {/* THE ONE ACT, AND IT IS THE SHIPPED HANDLER'S. `onSelect` IS `handleSelectCharacter`
            (:37460) — the paper press calls it with the same CARD object the dark face passes, so
            there is one path to the sheet and not a second: the fetch of `pregen_sheets.json`, the
            roster autosave and `openSheetRecord`'s two stamps all happen exactly as they do today.
            The label is the app's own word (`QS_SELECT_LABEL`, and the smoke pins it to the shipped
            body's own literal — see that constant's note for why the literal was not folded into it);
            `.hp-commit` is `text-transform:uppercase` in paper.css, so the frame's title-cased drawing
            and the shipped SHOUT are the same pixels.
            It stands only while a character is open — the board draws no act on the closed shelf —
            and `.hp-phoneact` is `position:sticky;bottom:0`, which is why the frame's caption can say
            the act pins to the foot however long the character runs. */}
        {openRec && openCard ? (
          <div className="hp-phoneact" data-qs-act="1">
            <button type="button" className="hp-commit" data-qs-take={openRec.id}
              onClick={function () { onSelect(openCard); }}>
              {QS_SELECT_LABEL} <span>›</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   SECTION SHELL (placeholder for quickbuild + custom)
══════════════════════════════════════════════════════ */
function PlayerSectionShell({ sectionId, wipeType, onBack }) {
  const card = CARDS.find(c => c.id === sectionId);
  const swipe = useSwipeBack(onBack);
  const dur = WIPE_DURATIONS[wipeType] ?? 1.3;

  return (
    <div
      style={{
        position: 'absolute', inset: 0,
        background: sectionId === 'pregen'
          ? 'linear-gradient(180deg, #1A1000 0%, #080500 100%)'
          : sectionId === 'quickbuild'
          ? 'linear-gradient(180deg, #000D1A 0%, #000205 100%)'
          : 'linear-gradient(180deg, #07080E 0%, #020005 100%)',
        animation: `${wipeType} ${dur}s cubic-bezier(0.42,0,0.18,1) forwards`,
        zIndex: 20,
      }}
      {...swipe}
    >
      {/* Nav */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '16px 16px 12px',
        borderBottom: `1px solid ${card.accent}15`,
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: card.accent, display: 'flex', alignItems: 'center',
            gap: '4px', minWidth: '44px', minHeight: '44px', padding: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <polyline points="13,4 7,10 13,16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)',
            letterSpacing: '0.18em', color: card.accent, opacity: 0.6,
          }}>BACK</span>
        </button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: card.accent,
          }}>
            {card.title.toUpperCase()}
          </div>
        </div>
        <div style={{ minWidth: '44px' }} />
      </div>

      {/* Placeholder body */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 24px', gap: '14px',
        animation: 'fadeIn 0.5s ease forwards 0.6s',
        opacity: 0, animationFillMode: 'forwards',
      }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)',
          letterSpacing: '0.25em', color: card.accent, opacity: 0.35,
        }}>SECTION INCOMING</div>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 'var(--fs-lg)', lineHeight: 1.6,
          color: 'rgba(255,255,255,0.3)', textAlign: 'center', maxWidth: '240px',
        }}>
          This section is under construction. Swipe right or tap Back to return.
        </div>
      </div>

    </div>
  );
}

/* ══════════════════════════════════════════════════════
   FULL CREATOR — "Forge Your Identity"
   10-step character creation with full FFG depth
══════════════════════════════════════════════════════ */

// Veteran review fix: 'forcepowers' step inserted after talents for F&D characters.
// useFullCreatorController skips it when gameLine !== 'fad'.
// CHAR-FIRST (ruling char-first-order): the wizard walks DOWN the permanence ladder — the
// mechanic's trades finalize the XP budget (4), then characteristics (5, the near-permanent
// first spend, before any refundable buy), then skills (6). RAW has one unordered "invest
// experience" step; the order is ours, and trades-before-spending is the only logical constraint.
// ⚠ B-72: THESE IDS ARE LOAD-BEARING IN PERSISTENCE. The creation draft stores the step BY NAME
// (`v: 2`), so a RENAME here is an R-SY4 durable-contract event — it silently strands every
// in-flight draft on `review`. Adding, removing or REORDERING steps is safe by construction;
// renaming one is not. See `creationDraftStepName` + the frozen `FC_STEPS_V1`.
/* ── THE GAME LINES — ONE HOME (B-107 / B-108, lap C6) ──────────────────────────────────
   The campaign a player picks decides three things, and until this lap each of them was written
   out wherever it was needed.

   B-107: the mechanic mapping — `gameLine === 'eote' ? 'obligation' : ... 'duty' : 'morality'` —
   existed as FOUR separate ternaries (app.jsx:3770, :5250, :12703, :13145). One per-line rule with
   four copies is the class this pass met four times over: per-line truth hiding behind a hardcoded
   default (the mechanic step's heading B-67(c); the motivation trios B-93). It is now ONE table and
   `mechanicForLine()` is the only reader.

   B-108: the card copy was string literals inside `QBGameLineStep` while species, careers and
   specializations all read canonical registries. The TITLE now comes from the books vocabulary
   (`books.vocab.json` carries the line ids and labels), so the name a player sees is registry-driven
   like every other creator subject. **The prose, accent and gradient stay here and are declared as
   PRESENTATION** — no canonical registry carries them today, and inventing one is a data-layer
   addition outside a UI lap's contract. Flagged in the gate rather than smuggled in.

   `banner` is GONE — it was `null` on all three cards, an image slot dead since it was written.

   B-184: each line now carries `sources: [<core book id>]` — the SAME shape every canonical record
   carries, so the line list filters through the SHARED `fcFilterActive` predicate rather than a
   QuickBuild-local variant (R-RG-3). The three ids are exactly the three Core-group books in
   `books.vocab.json` (`eote`/`aor`/`fad`, each an alias of itself), so a line is offered iff its core
   rulebook is active. This is the whole mechanism: no line-specific source logic exists anywhere. */
var FC_GAME_LINES = [
  { id: 'eote', mechanic: 'obligation', accent: '#F5C842', sources: ['eote'],
    grad: 'linear-gradient(135deg, #2a1800 0%, #1a0e00 40%, #0d0600 100%)',
    fallbackTitle: 'Edge of the Empire',
    desc: 'Crime, survival, and the criminal underworld. You live on the fringes where the Empire\'s reach is long but its grip is loose.',
  },
  { id: 'aor', mechanic: 'duty', accent: '#00C8FF', sources: ['aor'],
    grad: 'linear-gradient(135deg, #001a2e 0%, #000f1a 40%, #00080e 100%)',
    fallbackTitle: 'Age of Rebellion',
    desc: 'War, sacrifice, and the fight against tyranny. The Rebel Alliance needs every soul willing to stand against the Empire.',
  },
  { id: 'fad', mechanic: 'morality', accent: '#B080FF', sources: ['fad'],
    grad: 'linear-gradient(135deg, #1a0830 0%, #0f041a 40%, #08020e 100%)',
    fallbackTitle: 'Force and Destiny',
    desc: 'Destiny, legacy, and the living Force. The Jedi are gone, but the Force endures — and it has chosen you.',
  },
];

/* The ONE reader. Defaults to morality for an unknown line exactly as all four ternaries did — the
   behaviour is preserved verbatim, only its number of homes changed. */
function mechanicForLine(gameLine) {
  for (var i = 0; i < FC_GAME_LINES.length; i++) {
    if (FC_GAME_LINES[i].id === gameLine) return FC_GAME_LINES[i].mechanic;
  }
  return 'morality';
}

/* B-1668 · D-437 — THE MECHANIC STEP'S WORD, ONE HOME. The step's folio ("Step 5 of 12 · Morality")
   and the step list's fifth entry both print the line's own word, read from here (ruling
   release-r7-bug-burndown ADDENDUM 413: one derivation, not a second table). */
var FC_MECH_STEP_NAME = { obligation: 'Obligation', duty: 'Duty', morality: 'Morality' };

/* The line's display name, REGISTRY-FIRST: `books.vocab.json` carries "<Name> Core Rulebook" for
   each line id, so the title is the label with that suffix removed. The literal is a fallback for a
   cold load, never the source of record. */
function gameLineTitle(id) {
  // R-NLS-3 — THE REGISTRY LOOP DISSOLVES INTO THE SHARED RESOLVER. This walked
  // `_canonicalRegistries.books.entries` itself looking for a row whose `id` matched, which is a
  // second resolution path for a name `lib/shared/sources.js` already owns — and a narrower one:
  // it could only ever match an ID, so an ALIAS or a display TITLE (the form most registries still
  // hold before B-336's conversion) walked straight past it and fell through to the fallback.
  // `sourceLabel` answers all three forms off ONE index. What stays here is the only part that was
  // ever this function's own: stripping the shelf's " Core Rulebook" suffix, which is presentation
  // OVER the shared label, not a lookup beside it.
  //
  // READY-GATED per B-180, and the gate is `current()` rather than `ready()` — `ready()` returns a
  // PROMISE and this is a synchronous display call. The creator awaits it at data load, so the
  // index is present by the time any picker renders; before that, the literal below is the
  // cold-load fallback rather than the source of record, exactly as it was.
  var _S = (typeof window !== 'undefined') && window.SWRPGSources;
  var _cur = _S && _S.current && _S.current();
  var _idx = _cur && _cur.index;
  if (_idx && _S.sourceLabel) {
    var _label = _S.sourceLabel(_idx, id);
    // `sourceLabel` returns the INPUT unchanged when it cannot resolve it, so an unmapped id must
    // fall through to the fallback rather than be printed back at the player as a title.
    if (_label && _label !== id) {
      return String(_label).replace(/\s*Core Rulebook\s*$/i, '').trim();
    }
  }
  for (var j = 0; j < FC_GAME_LINES.length; j++) {
    if (FC_GAME_LINES[j].id === id) return FC_GAME_LINES[j].fallbackTitle;
  }
  return id;
}

/* F-100 M12 (R-M12B-5; D-157 "the desktop source label is the pack's own label"; frames F5 @
   Docs/mockups/f100-m12-careers.html): THE FOUR DESKTOP SOURCE SITES' ONE LABEL — the career rail row, the career
   identity head, the specialization identity head and the talents sheet. A record that names a BOOK prints its book
   through the site's own formatter (`bookLabel`), as before. A pack-ADDED record names none (`sources: []`), so it prints
   the placing pack's label through SWRPGMods.variantOfRecord, the one label path the tree marker reads too (a record
   more than one active pack places reads plain "reSpecialized"). Otherwise the site prints what it always printed. */
function fcRecordSourceLabel(section, rec, bookLabel) {
  var src = ((rec && rec.sources) || [])[0];
  var SS = window.SWRPGSources, cur = SS && SS.current && SS.current(), idx = cur && cur.index;
  if (src && (!idx || (idx.aliasToId && idx.aliasToId[src]))) return bookLabel(src);
  var MODS = window.SWRPGMods, v = MODS && MODS.variantOfRecord && MODS.variantOfRecord(MODS.active(), section, rec);
  if (v && v.label) return v.label;
  return bookLabel(src);
}

/* THE SAME LABEL, BEFORE THE STRIP (B-722). Guided's paper game-line card cites the book, and the
   board's chip is the books vocabulary's WHOLE label — "Edge of the Empire Core Rulebook" — which is
   exactly the suffix `gameLineTitle` removes for display. Same index, same shared resolver, one call:
   this is that function's own lookup without its presentation step, not a second path to a name
   (R-NLS-3's whole point). Falls through to `gameLineTitle` on a cold load, so the chip degrades to
   the short name rather than to an id. */
function gameLineBookLabel(id) {
  var _S = (typeof window !== 'undefined') && window.SWRPGSources;
  var _cur = _S && _S.current && _S.current();
  var _idx = _cur && _cur.index;
  var _label = (_idx && _S.sourceLabel) ? _S.sourceLabel(_idx, id) : null;
  return (_label && _label !== id) ? String(_label).trim() : gameLineTitle(id);
}

var FC_STEPS = ['gameline','species','career','specialization','mechanic','characteristics','skills','talents','forcepowers','motivation','gear','review'];

// (FC_SPEC_SPECIES_REQUIREMENT retired — the RAW species restriction is registry data now:
//  specializations.<id>.species_requirement, read at the spec detail. Ruling
//  `f-eds-6-specializations-full-read` §7 / C5.)

// The authored species lore sections, in reading order (ruling `f-eds-6-species-classification`
// §4). Order is deliberate: what it IS, how it lives, where it is from, what it speaks.
var FC_SPECIES_SECTIONS = [
  { key: 'physiology', label: 'PHYSIOLOGY' },
  { key: 'society',    label: 'SOCIETY' },
  { key: 'homeworld',  label: 'HOMEWORLD' },
  { key: 'language',   label: 'LANGUAGE' },
];

var FC_STEP_INFO = {
  gameline:        { title: 'Choose Your Campaign',           subtitle: 'Each campaign tells a different kind of Star Wars story.' },
  species:         { title: 'Who Are You?',                   subtitle: 'Your species determines your starting abilities and how many experience points you have to build with.' },
  career:          { title: 'What Do You Do?',                subtitle: 'Your career is your profession. It determines which skills come naturally to you.' },
  specialization:  { title: 'What Are You Best At?',           subtitle: 'Your specialization is the focus inside your career. It adds four career skills and opens a talent tree you will spend the rest of the character\'s life filling in.' },
  skills:          { title: 'What Have You Trained?',         subtitle: 'Choose the skills your character has practiced. These free ranks give you a head start.' },
  mechanic:        { title: 'What\'s Your Baggage?',          subtitle: null }, // set dynamically per game line
  // B-78: the old subtitle urged "take the opportunity now to maximize your abilities" —
  // maximizing (any characteristic to 5) is unreachable for 120 of 124 species and spends
  // everything for the other four, so it advised a strategy the economy forbids and gave it
  // without its price. "Very unique circumstances" died with it (ruling
  // `creator-characteristics-mockup`). The lock-in warning is NOT repeated here — it is stated
  // once, in the step body; saying it twice was half of the two-warnings problem. This says
  // what the step IS and points at the prices, which now carry the strategy themselves.
  characteristics: { title: 'How Strong Is Your Foundation?', subtitle: 'These are your <b>core</b> stats. You <b>cannot</b> upgrade them beyond character creation, except through rare talents like Dedication. Each point costs more than the last, and the prices below count from where you are now.' },
  talents:         { title: 'What Sets You Apart?',           subtitle: 'Talents are special abilities unique to your specialization. You can only buy a talent connected by a line to one you already own.' },
  forcepowers:     { title: 'What Force Gifts Will You Learn?', subtitle: 'Each power lists its printed XP cost. Upgrades can be purchased later in play.' },
  motivation:      { title: 'What Drives You?',              subtitle: 'Your Motivations drive your actions. Stick to them during play for potential bonuses.' },
  gear:            { title: 'What Do You Carry?',             subtitle: 'Equip your character with weapons, armor, and gear. Spend wisely.' },
  review:          { title: 'Your Story Begins',              subtitle: 'Review everything and give your character a name.' },
};

// B-67(c) — THE HEADING IS PER LINE, because "What's Your Baggage?" was wrong for two of the three.
// Obligation genuinely IS baggage (Addiction, Blackmail, Bounty). DUTY IS NOT: Combat Victory,
// Recruiting, Intelligence and Personnel are contributions a Rebel is PROUD of, so the old heading
// inverted the mechanic's meaning for every AoR character. Morality is virtues paired with shadows —
// also not baggage. The step already switched its SUBTITLE per line (below); only the title stayed
// fixed, which is how the mismatch survived.
// Jason approved these three verbatim 2026-07-30, each matching that line's existing subtitle and
// the creator's one-plain-question-per-step idiom. ("What Do You Fight For?" was REJECTED for aor —
// too close to the motivation step's "What Drives You?", a different question.)
var FC_MECHANIC_TITLES = {
  eote: 'What Do You Owe?',
  aor:  'How Do You Serve?',
  fad:  'What Pulls At You?',
};
var FC_MECHANIC_SUBTITLES = {
  eote: 'Everyone carries a debt. Choose your obligation and decide if you want to trade it for extra resources.',
  aor:  'The Rebellion demands sacrifice. Choose your duty and decide what you\'re willing to give.',
  fad:  'The Force pulls in two directions. Choose your moral anchor and the trade-offs you\'ll accept.',
};

function useFullCreatorController(resumeDraft, charbuild) {
  // UXQP-6: when the landing's CONTINUE affordance launched this mount, seed
  // every piece of state from the stored draft. All controller state is plain
  // JSON (registry records + primitives + the reducer character), so the
  // round-trip is lossless. subView/returnToStep intentionally reset — the
  // resume lands on the saved step's main view, never a detail pane.
  var _draft = (resumeDraft && resumeDraft.flow === 'custom' && resumeDraft.character) ? resumeDraft : null;
  var _useState = useState(_draft ? creationDraftStepIndex(_draft, FC_STEPS) : 0), step = _useState[0], setStep = _useState[1];
  var _useSV = useState(null), subView = _useSV[0], setSubView = _useSV[1];
  var _useGL = useState(_draft ? _draft.gameLine : null), gameLine = _useGL[0], setGameLine = _useGL[1];
  var _useSp = useState(_draft ? _draft.speciesData : null), selectedSpeciesData = _useSp[0], setSelectedSpeciesData = _useSp[1];
  var _useCr = useState(_draft ? _draft.careerData : null), selectedCareerData = _useCr[0], setSelectedCareerData = _useCr[1];
  var _useSc = useState(_draft ? _draft.specData : null), selectedSpecData = _useSc[0], setSelectedSpecData = _useSc[1];
  var _usePS = useState(_draft ? (_draft.partySize || 4) : 4), partySize = _usePS[0], setPartySize = _usePS[1];

  var charInit = useCallback(function() { return _draft ? _draft.character : createEmptyCharacter('eote'); }, []);
  var _useR = React.useReducer(charReducer, null, charInit);
  var character = _useR[0], dispatch = _useR[1];
  // F-100 M6 R5 (ruling f100-m6-pack R-M6G-2): a RE-ENTERED draft's marker, carried unchanged through every autosave —
  // it names the character's own packs, and those (never the toggles) are the view it resolves through and the stamps
  // finalize writes. The autosave below used to drop it, so a resumed re-entry fell back to the toggle view.
  var _reentry = (_draft && _draft.reentry) ? _draft.reentry : null;

  var updateCharacter = useCallback(function(field, value) { dispatchBinding(field, value, dispatch); }, [dispatch]);

  // UXQP-6: persist the in-progress build so a closed tab or a wandered-off
  // player can pick up where they left off. Nothing worth resuming exists
  // before a game line is chosen. Cleared by the PlayerApp on completion/discard.
  useEffect(function() {
    if (!gameLine) return;
    saveCreationDraft({
      v: 2, flow: 'custom', gameLine: gameLine, step: FC_STEPS[step], partySize: partySize,   // B-72: by NAME
      speciesData: selectedSpeciesData, careerData: selectedCareerData, specData: selectedSpecData,
      character: character, savedAt: Date.now(),
      reentry: _reentry || undefined,   // F-100 M6 R5: absent for a new character (JSON drops undefined)
    });
  }, [gameLine, step, partySize, selectedSpeciesData, selectedCareerData, selectedSpecData, character]);

  // XP computations
  // F-100 B-1264 (R-SCC-4): THE ONE CREATION XP POOL — the save's own function (CharResolve.creationXpPool): species
  // starting XP under the save's picks, the trade XP, the species option's XP (B-22, Gand lunged +10), the career's
  // `xp_adjustment` (dark). Before a species is chosen the bar shows no pool (0) — a display rule, not a term.
  var xpPool = useMemo(function() {
    if (!character || !selectedSpeciesData) return 0;
    return CharResolve.creationXpPool(character, selectedSpeciesData, charbuild || null);
  }, [selectedSpeciesData, charbuild, character && character.career, character && character.mechanic, character && character.speciesOption]);
  // B-1379 / B-1492 (release-r7-bug-burndown ADDENDUM 76): the SAME pool with a mechanic the Obligation step holds but has
  // not yet committed — the trade is the step's local state until CONTINUE, so its header read the saved mechanic and
  // lagged one step. One sum (`creationXpPool`), asked with the live mechanic in place of the saved one.
  var xpPoolWithMechanic = useCallback(function(mech) {
    if (!character || !selectedSpeciesData) return 0;
    return CharResolve.creationXpPool(Object.assign({}, character, { mechanic: mech || null }), selectedSpeciesData, charbuild || null);
  }, [selectedSpeciesData, charbuild, character]);

  var xpSpentOnChars = useMemo(function() {
    if (!character || !selectedSpeciesData) return 0;
    var total = 0;
    // FORK B: the purchase ladder prices off the CHOSEN variant's numbers, not the record's.
    var base = speciesEff(selectedSpeciesData, character.speciesOptionPicks).characteristics || {};
    ['brawn','agility','intellect','cunning','willpower','presence'].forEach(function(attr) {
      var cur = character.characteristics[attr] || 2;
      var b = base[attr] || 2;
      for (var r = b + 1; r <= cur; r++) total += r * 10;
    });
    return total;
  }, [character && character.characteristics, selectedSpeciesData]);

  var xpSpentOnTalents = useMemo(function() {
    if (!character) return 0;
    var total = 0;
    // Sum the PER-ENTRY stored cost — the SAME source the saved sheet uses (normalizeCreated). With no
    // free talents (RAW), the live budget and the final sheet agree by construction; the old divergence
    // (live bar charged rowCosts[row] while the sheet charged entry.cost) was the D-CS4-2 root.
    Object.keys(character.talents).forEach(function(posKey) {
      total += character.talents[posKey].cost || 0;
    });
    return total;
  }, [character && character.talents]);

  // Veteran review fix: Force powers cost XP too (10 per basic power per CRB p.282).
  // F-100 M10 B-1222: the SAVE's own summer (CharResolve.forcePowersXp) — each entry's printed basic cost (5-20 XP,
  // fad p.285, stored on the purchase entry) plus a re-entered character's bought upgrades, priced from the creator's
  // view (`charbuild`, FullCreator's gameData: the draft's own stamps on re-entry). A fresh creation holds no upgrades.
  var xpSpentOnForcePowers = useMemo(function() {
    if (!character) return 0;
    return CharResolve.forcePowersXp(character.forcePowers || {}, charbuild || null);
  }, [character && character.forcePowers, charbuild]);

  // CR-RAW #2: creation skill purchases — sum the PER-ENTRY stored costs (bought[]), the SAME — Docs/CREATOR_COMMENTARY.md § "useFullCreatorController › xpSpentOnSkills"
  var xpSpentOnSkills = useMemo(function() {
    if (!character) return 0;
    var sk = character.skills || {};
    return Object.keys(sk).reduce(function(t, k) {
      var b = sk[k].bought;
      return t + (b ? b.reduce(function(a, c) { return a + (c || 0); }, 0) : 0);
    }, 0);
  }, [character && character.skills]);

  // R-SFC-2: the species-penalty buyouts (`character.creationBuyouts`, keyed by ability name-slug).
  // Summed through the SHARED summer the saved sheet uses, not a second local reduce — the
  // D-CS4-2 one-source rule, and the reason the live bar and the finished sheet cannot disagree.
  var xpSpentOnBuyouts = useMemo(function() {
    return CharResolve.creationBuyoutSpend(character && character.creationBuyouts);
  }, [character && character.creationBuyouts]);

  // F-100 creator concept ADDENDUM 18 C1 (a): the signature abilities bought on the sheet (handed back at re-entry) —
  // the SHARED summer normalizeCreated reads, so the bar and the finished sheet agree; 0 with M12's key OFF.
  var xpSpentOnSignature = useMemo(function() {
    return CharResolve.sigAbilitiesSpend(character || {}, charbuild || null);
  }, [character && character.signatureAbilities, character && character.mods, charbuild]);

  // B-1038 / B-1537: the specializations after the first (a re-entered build carries them) — the save's own summer.
  var xpSpentOnSpecs = useMemo(function() {
    return CharResolve.specializationsSpend(character || {}, charbuild || null);
  }, [character && character.specializations, character && character.career, charbuild]);

  var xpRemaining = xpPool - xpSpentOnChars - xpSpentOnTalents - xpSpentOnForcePowers - xpSpentOnSkills - xpSpentOnBuyouts - xpSpentOnSignature - xpSpentOnSpecs;

  // Credit computations
  // THE ONE PURSE READER (R2; ADDENDUM 4 FLAG 1 (ii)): the gear step's purse is CharResolve.startingCredits' `purse` — 500 +
  // the trade adjustment + the species' spendable deltas; `after` (timed after creation: the Muun's 1,000) is NOT in it,
  // and `rows` name each delta for the Starting line's because (R-SCC-6). Before this the memo summed its own 500 + trade.
  var credits = useMemo(function() {
    var start = CharResolve.startingCredits(character && character.mechanic ? (character.mechanic.creditAdjustment || 0) : 0,
      selectedSpeciesData, character && character.speciesOptionPicks, character && character.creationBuyouts, character);   // R6 lane D: a member's group (Q6 c)
    if (!character) return { total: start.purse, spent: 0, remaining: start.purse, after: start.after, rows: start.rows };
    var spent = character.gearLedger.inventory.reduce(function(sum, item) { return sum + (item.price || 0) * (item.qty || 1); }, 0);
    return { total: start.purse, spent: spent, remaining: start.purse - spent, after: start.after, rows: start.rows };
  }, [selectedSpeciesData, character && character.mechanic, character && character.gearLedger, character && character.speciesOptionPicks, character && character.creationBuyouts, character && character.id]);

  // Navigation. Veteran review fix: skip 'forcepowers' step when gameLine !== 'fad'. — Docs/CREATOR_COMMENTARY.md § "useFullCreatorController › _hasForceAccess"
  var _hasForceAccess = !!(window.CharResolve && window.CharResolve.forceAccessAtCreation(gameLine, selectedSpeciesData, selectedCareerData, selectedSpecData));
  // THE HONEST STEP COUNTER (lap C1; ruling `creator-ui-phase-b-concept` §"two honest fixes"). — Docs/CREATOR_COMMENTARY.md § "useFullCreatorController › reachableSteps"
  var reachableSteps = useMemo(function() {
    return FC_STEPS.filter(function(s) { return s !== 'forcepowers' || _hasForceAccess; });
  }, [_hasForceAccess]);
  // THE CLAMP IS GONE (B-702; `creator-pre-deploy-batch` A5-2). `Math.max(0, indexOf(...))` turned
  // "this step is NOT in the reachable list" into a confident "Step 1" — measured on a character
  // with no Force access seeded ON `forcepowers`, whose folio read 'Step 1 of 11 · Force Powers'.
  // The branch is explicit now: a step the list HOLDS prints its position, unchanged; a step it does
  // not hold prints NO position, because `fcFolio` and `fcMakerLine` already fall back to the step's
  // NAME and 'Character Creation' for a null index — both true without a number. ONE HOME: every
  // folio and maker line in the creator goes through those two builders, so this one line reaches
  // all of them and no site grows a copy of the conditional.
  var _reachIdx = reachableSteps.indexOf(FC_STEPS[step]);
  var stepDisplayIndex = _reachIdx >= 0 ? _reachIdx : null;

  var _skipNonFad = useCallback(function(target, direction) {
    if (FC_STEPS[target] === 'forcepowers' && !_hasForceAccess) {
      return target + direction;  // jump over
    }
    return target;
  }, [_hasForceAccess]);
  // Track a "return to" step so Review-EDIT jumps don't trap players on a linear walk
  // (vet-r2 P1 + play-test 2026-06-15): tap EDIT <section> from Review → both CONTINUE
  // (nextStep) and BACK (prevStepWithReturn) return to Review, instead of advancing/
  // backing through the whole remaining flow. Declared before nextStep so nextStep can
  // consume it. Fires once, then clears.
  var _useRT = useState(null), returnToStep = _useRT[0], setReturnToStep = _useRT[1];
  var nextStep = useCallback(function() {
    if (returnToStep != null) {
      var rt = returnToStep;
      setReturnToStep(null);
      setStep(rt);
      setSubView(null);
      return;
    }
    setStep(function(s) {
      var next = Math.min(FC_STEPS.length - 1, s + 1);
      next = _skipNonFad(next, 1);
      return next;
    });
    setSubView(null);
  }, [_skipNonFad, returnToStep]);
  var prevStep = useCallback(function() {
    setStep(function(s) {
      var prev = Math.max(0, s - 1);
      prev = _skipNonFad(prev, -1);
      return Math.max(0, prev);
    });
    setSubView(null);
  }, [_skipNonFad]);
  var goToStep = useCallback(function(n, opts) {
    var fromReview = opts && opts.fromReview;
    if (fromReview) {
      setReturnToStep(FC_STEPS.indexOf('review'));
    }
    setStep(Math.max(0, Math.min(FC_STEPS.length - 1, n)));
    setSubView(null);
  }, []);
  var prevStepWithReturn = useCallback(function() {
    if (returnToStep != null) {
      setStep(returnToStep);
      setReturnToStep(null);
      setSubView(null);
      return;
    }
    prevStep();
  }, [returnToStep, prevStep]);

  return {
    character: character, dispatch: dispatch, updateCharacter: updateCharacter,
    step: step, setStep: setStep, currentStep: FC_STEPS[step], totalSteps: FC_STEPS.length,
    // display-only, honest counter (lap C1) — see reachableSteps above
    reachableTotal: reachableSteps.length, stepDisplayIndex: stepDisplayIndex,
    reachableSteps: reachableSteps,   // C7: the build rail's step map walks the SAME reachable list
                                      // the counter counts, so the map cannot disagree with "3 of 11".
    subView: subView, setSubView: setSubView,
    gameLine: gameLine, setGameLine: setGameLine,
    selectedSpeciesData: selectedSpeciesData, setSelectedSpeciesData: setSelectedSpeciesData,
    selectedCareerData: selectedCareerData, setSelectedCareerData: setSelectedCareerData,
    selectedSpecData: selectedSpecData, setSelectedSpecData: setSelectedSpecData,
    hasForceAccess: _hasForceAccess,
    partySize: partySize, setPartySize: setPartySize,
    xpPool: xpPool, xpPoolWithMechanic: xpPoolWithMechanic, xpSpentOnChars: xpSpentOnChars, xpSpentOnTalents: xpSpentOnTalents, xpSpentOnForcePowers: xpSpentOnForcePowers, xpSpentOnSkills: xpSpentOnSkills, xpSpentOnBuyouts: xpSpentOnBuyouts, xpSpentOnSignature: xpSpentOnSignature, xpRemaining: xpRemaining,
    credits: credits,
    nextStep: nextStep, prevStep: prevStepWithReturn, goToStep: goToStep,
    returnToStep: returnToStep,
  };
}

/* ── FC Shared Components ────────────────────────────── */

function FCSearchBar({ value, onChange, accent, placeholder }) {
  return (
    <div style={{ padding: '0 16px 8px' }}>
      <input
        type="text" value={value} onChange={function(e) { onChange(e.target.value); }}
        placeholder={placeholder || 'Search…'}
        style={{
          width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)',
          border: 'none', borderBottom: '1px solid ' + accent + '30',
          padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
          color: '#fff', letterSpacing: '0.04em', outline: 'none',
          borderRadius: '4px 4px 0 0',
        }}
      />
    </div>
  );
}

function FCBottomAction({ label, onClick, disabled, accent, revealed }) {
  // SK-3 fix: disabled CTA previously used rgba(255,255,255,0.08) bg — visually
  // similar to the full-saturation active state. Users read "button I haven't
  // tapped" instead of "button waiting for selections." Now uses a dashed
  // outline + transparent bg so the disabled state is unmistakably distinct.
  //
  // Play-test 2026-06-15: on long steps the CTA scrolled below the fold. The bar is now
  // position:sticky bottom:0 so it pins to the viewport bottom while content scrolls under
  // it (it returns to its natural slot at the very end), and it slides up/away on
  // `revealed` (default true). Selection-gated steps pass revealed=<selection complete> so
  // the bar stays out of the way until the user is ready, then slides in — and slides back
  // down on deselect / back, since callers derive `revealed` reactively. The gradient
  // backdrop matches the dark creator bg so scrolled content fades cleanly beneath it.
  var show = revealed !== false;
  return (
    /* MEASUREMENT HOOKS ONLY (`data-creator-commit`, and `data-creator-back` on the step
       header). A data- attribute changes no geometry and exists so an instrument can address the
       control BY ROLE rather than by matching its label — which is exactly the text a census is
       there to question. Defined by ROLE across idioms, as A23 defined the step marker's anchor:
       this is the control that ADVANCES the step, whatever it reads. R-CPR-71; the branch has
       carried the same hook since the composition census. */
    <div data-creator-commit="1" style={{
      position: 'sticky', bottom: 0, zIndex: 5, flexShrink: 0,
      padding: '16px 16px 20px',
      background: 'linear-gradient(to top, rgba(6,7,11,0.97) 52%, rgba(6,7,11,0))',
      transform: show ? 'translateY(0)' : 'translateY(115%)',
      opacity: show ? 1 : 0,
      pointerEvents: show ? 'auto' : 'none',
      transition: 'transform 0.34s cubic-bezier(0.22,1,0.36,1), opacity 0.22s ease',
    }}>
      <button onClick={disabled ? undefined : onClick} disabled={disabled} style={{
        width: '100%', minHeight: '52px', padding: '16px',
        border: disabled ? '1px dashed rgba(255,255,255,0.25)' : 'none',
        borderRadius: '8px',
        background: disabled ? 'transparent' : accent,
        color: disabled ? 'rgba(255,255,255,0.55)' : '#000',
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.12em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s, transform 0.1s',
        transform: 'scale(1)',
      }}
      onMouseDown={function(e) { if (!disabled) e.currentTarget.style.transform = 'scale(0.98)'; }}
      onMouseUp={function(e) { e.currentTarget.style.transform = 'scale(1)'; }}
      >{label}</button>
    </div>
  );
}

/* ── FC Species Browser ──────────────────────────────── */

var FC_CHAR_LABELS = { brawn: 'BR', agility: 'AG', intellect: 'INT', cunning: 'CUN', willpower: 'WIL', presence: 'PR' };

// Career descriptions — Reference Voice (factual, encyclopedic, no game mechanics)
// Bystander-friendly skill descriptions for the skill picker
// FC_SKILL_DESC DELETED (F-58). The 35 skill explanations are REGISTRY data now —
// `skills.vocab.json` `description`, resolved at render via skillDescFor() below. They were moved
// VERBATIM by scripts/canonical/apply_skill_descriptions.py (parsed out of this file, never
// retyped), so no wording changed. The 36th key, CYBERNETICS, had no canonical skill behind it and
// was therefore never reachable; it is declared in that script and belongs to the F-60 lap.
//
// A skill's plain-language explanation, resolved from the registry at render — the same posture as
// speciesAbilitiesForDisplay. Keyed by the legacy SHEET key both the creator and the sheet hold, so
// the three call sites read exactly as they did. Uses the mod-overlaid view when one is active, so a
// pack that adds a skill brings its description with it.
// R-F58-4 (ruling `f58-skill-descriptions`) — LATENT SEAM, marked before it bites: this reads the
// GLOBAL overlaid view (whatever the mods TOGGLE has active), not the sheet's OWN stamp the way
// sheetTalentsForDisplay does via SWRPGMods.resolveForStamp. Moot today because no pack overlays
// skills, so base and overlay agree for all 35. The day a pack adds or reworded one, a stamped sheet
// would resolve its skill text against the *reader's* toggle instead of what the character was built
// under — the discovery-vs-ownership distinction (F-9 B2). Fix by resolving against the stamp then,
// not by widening this function's callers now.
function skillDescFor(sheetKey) {
  // C-SDS-1: ONE implementation. This is now a thin wrapper that supplies the loaded registry to the
  // shared core's `CharResolve.skillDescription` — the same function the DICE MODAL calls, so the
  // sheet, the creator and the modal cannot show different text for the same skill.
  var cb = window._canonicalCharbuild || window._canonicalCharbuildBase;
  return (window.CharResolve && window.CharResolve.skillDescription)
    ? window.CharResolve.skillDescription(sheetKey, cb)
    : '';
}
var FC_CHAR_COLORS = { brawn: '#FF6B6B', agility: '#4ECB71', intellect: '#4EAAFF', cunning: '#FFB84E', willpower: '#B07EFF', presence: '#FF7EB3' };
// The six characteristics' ORDER and their two label forms, defined ONCE. The species step's sort
// bar prints the short form as its own key and every species row prints it under each value — the
// same vocabulary in two places on one screen, which is exactly the shape that drifts if it is
// typed twice.
// SCOPE, stated honestly: this file carries the inline ['brawn','agility',…] literal in TEN more
// places. Only the species card was converted here, because a mechanical sweep of the other ten
// would make this lap's diff unreadable and its regression ambiguous — the same reason B-106 was
// kept out of the gameline lap. The sweep is boarded, not silently skipped.
// The review's own measure (D2 correction). Wide enough to spell every label out at desktop,
// bounded so a row of numbers stays a row rather than a scatter.
var FC_REVIEW_COLUMN_MAX = '920px';
// ── THE CREATOR'S LAYOUT POLICY — one table, one pure function (audit item F) ─────────────────
// Read by FullCreator's render and by scripts/creator_map.mjs. Every rule below used to be a
// branch of an if-chain inside the render with an essay per branch; the essays are in
// Docs/CREATOR_COMMENTARY.md § "FullCreator › layout policy". The facts:
//   · master-detail steps render SplitPane list‖detail at desktop and swap on phone (ADR 0024); while
//     a step is master-detail its subView leaves the scroll key so a pick never dumps the list's
//     scroll position (the scroll-key exception, ruling creator-species-split-composition Q1/Q2).
//   · the build rail earns its width only at the WIDE band (the sheet's own seam).
//   · the column cap at desktop is --desk-content-max for every step but REVIEW, which is a summary
//     and takes its own 920px (creator-d2-width-alignment); on mobile the cap is --desk-read-max
//     (CenterColumn ignores it there). Each step still owns its own measure internally — the page
//     cap must never be the thing that holds a measure (the C10/C11 standing pattern).
//   · characteristics' control group keeps a 620px measure at every band (the drawing's own number).
// The transplant's dark switch (B-563). Retired when all twelve steps are lit.
// B-586 — THE SCROLL CUE. The desk shell's panes scroll independently and iOS draws no scrollbar,
// so on an iPad the read pane ended at "THE REGISTER ENTRY" and read as the end of the dossier
// (Jason 2026-09-14). A pane with more below it carries `data-more="1"`; paper.css fades its
// last 48px. Recomputed on every scroll (capture, so a pane's own scroll reaches it), on resize,
// and after each creator render. Nothing moves; a fade appears where there is more.
// R-PN3-4 (ruling `paper-n3-bench`, B-908): the cue's DRIVER is the one shared module, lib/shared/scrollCue.js
// (window.SWRPGScrollCue: the attribute, the 8px threshold, the scroll/resize listeners). This helper keeps its
// name, its call sites (after each creator and sheet render) and its selector list, which is the creator's own:
// …and the species sidebar's own scroller at the mid band (Jason 2026-09-14: "the same fix").
// …and every list that scrolls on its own — the rail registers, the pick lists, Buy Ranks
// (Jason 2026-09-15, on the motivation snapshot: "bring back the fade scroll like the other
// pages have"): once a pane is a snapshot, its lists are the only thing left to cue.
// …and the PAPER SHEET's bounded scrollers (B-617, the walk's "every list · mid → cue"): the grouped
// stage, the units' bodies, the growth room's spine units and the new-specialization WALK's shelf
// plate — the sheet on an iPad has the same no-scrollbar problem the creator had, and the same fix.
// B-657 re-pointed the last of those: the cue named the journey ISLAND
// (`[data-sheet-interim="growth-journey"]`), which the walk retired (R-R0-4 — the lap that fixes a
// shim's upstream cause retires the shim), and its successor is the walk's own `.sb-jshelf`.
var FC_SCROLL_CUE_PANES = '.hpw-shell .hp-pane, .hpw-shell .hp-linescroll, .hpw-shell .hpw-foldnote .hp-note p, .hpw-shell .hpw-scroll, .hpw-shell [data-skills-buy-scroll], [data-sheet-shell] .sb-stage > .hp-plate, [data-sheet-shell] .sb-unit-body, [data-sheet-shell] .sb-growth .sb-stage .hp-plate, [data-sheet-shell] .sb-jwalk > .sb-jshelf, [data-sheet-shell] .sb-pk-list > .hp-plate';
try { if (window.SWRPGScrollCue) window.SWRPGScrollCue.register(FC_SCROLL_CUE_PANES); } catch (e) {}
function fcUpdateScrollCues() {
  try { if (window.SWRPGScrollCue) window.SWRPGScrollCue.update(); } catch (e) {}
}
var FC_TRANSPLANT_KEY = 'swrpg_creator_transplant';
// The URL flag PERSISTS AT BOOT: the landing's SPA navigation drops the query before the creator
// ever renders, so a walker on a phone who opened `?transplant=1` saw the legacy creator (Jason,
// 2026-09-13) — reading `location.search` inside the creator's render was too late. Read ONCE here,
// while the script loads with the URL the person typed; `?transplant=0` clears it. The instruments
// set the key directly and are unaffected.
(function () {
  try {
    var m = /[?&]transplant=([01])\b/.exec(window.location.search);
    if (m && window.localStorage) window.localStorage.setItem(FC_TRANSPLANT_KEY, m[1]);
  } catch (e) {}
})();
function fcTransplantSwitch() {
  try {
    return !(window.localStorage && window.localStorage.getItem(FC_TRANSPLANT_KEY) === '0');
  } catch (e) { return true; }
}
// K9, the Ship Yard's switch (SHIP YARD S3–S5), is RETIRED: at the S5 flip (ruling `shipyard-s5-ready` R-S5R-18, R-S5R-20)
// the S4 Yard is the only Yard, its old faces are deleted, and nothing reads or writes the switch's key (the persisted
// value is left alone, R-S5R-20.3; test/yard_switch_retired.cjs pins zero readers).
// The resolver's context (lib/shared/vesselResolve.js `ctx`): who might be bonded to the ship. B-1067 · F2 (ruling
// `shipyard-s5-ready` R-S5R-2): every character saved on this device plus the face's scoped one(s), through the ONE builder
// both hosts call (vesselView.jsx `deviceResolveCtx`), so the Yard and the sheet's VESSELS print one number for one ship.
// release-r5-f1 D: the Signature Vehicle's name on the import receipt — the vessel's own name, else its library row's, else its id
function importShipName(id, lib) {
  var V = window.SWRPGVessels, rec = V ? ((V.load() || {}).vessels || []).filter(function (r) { return r.id === id; })[0] : null;
  var row = rec && (Array.isArray(lib) ? lib : []).filter(function (e) { return e && e.key === rec.vehicleKey; })[0];
  return (rec && rec.name) || (row && row.name) || id;
}
function yardResolveCtx(chars) {
  var VV = window.SWRPGVesselView;
  return VV && VV.deviceResolveCtx ? VV.deviceResolveCtx((chars || []).filter(Boolean)) : { characters: [] };
}
// B-953 — WHERE AN APP-LEVEL BACK GOES HOME: the ONE resolution for a BACK that has no screen of its own
// to return to (the store's exit when it was not entered from a sheet or the NPC library, the Yard's
// CLOSE and its VESSELS fallback; the Ship Yard's S4 faces call it too, R-PHX-2). Home is the landing
// with or without saved characters — its index lists the roster (R-LFM-6). `savedChars` stays in the
// signature for those callers.
function homeScreenFor(savedChars) {
  return 'landing';
}
// THE STORE'S SWITCH (B-665; ruling `store-paper-concept` R-SPC-1) — the same mechanism a FOURTH
// time, its own key: `?store=1` persists at boot (the SPA drops the query before the store renders,
// exactly as it drops `?transplant=1`), `?store=0` clears it, the ⚙ "Rebuilt store (preview)" toggle
// writes the same key on local and LAN builds. ON by default since the one-pass flip (R-PRS-8 step 4); a
// stored '0' is the OFF override (the rollback lever until N-post) — OFF renders today's dark store
// byte-for-byte at all four doors. The GM's door (`gm/app.jsx` :684) passes no `paper` at ALL, on or
// off: its face waits for the GM's own programme (R-SPC-2), which is why `gm/index.html` does not
// link `lib/shared/store.css`. The four switches flip together at B-674 (R-SPC-7 row 10).
var STORE_TRANSPLANT_KEY = 'swrpg_store_transplant';
(function () {
  try {
    var m = /[?&]store=([01])\b/.exec(window.location.search);
    if (m && window.localStorage) window.localStorage.setItem(STORE_TRANSPLANT_KEY, m[1]);
  } catch (e) {}
})();
function storeTransplantSwitch() {
  try {
    return !(window.localStorage && window.localStorage.getItem(STORE_TRANSPLANT_KEY) === '0');
  } catch (e) { return true; }
}
// QUICK START + GUIDED BUILD'S SWITCH (B-719; ruling `quickstart-guided-paper-concept` R-QSG-4) —
// the same mechanism a FIFTH time, its own key: `?qsg=1` persists at boot (the SPA drops the query
// before either door renders, exactly as it drops `?transplant=1`), `?qsg=0` clears it, the ⚙
// "Rebuilt Quick Start + Guided (preview)" toggle writes the same key on local and LAN builds.
// ONE KEY FOR TWO SURFACES, and that is R-QSG-1's own reading: they are two doors into the same act,
// so they take the ground together or they would be guaranteed to disagree. ON by default since the
// one-pass flip (R-PRS-8 step 4); a stored '0' is the OFF override (the rollback lever until N-post) — OFF
// renders today's `PreGenCarousel` and `QuickBuild` unchanged, their props, routes and
// `data-tour-id` anchors untouched. The five switches flip together at B-726 (R-QSG-6 row 8).
var QSG_TRANSPLANT_KEY = 'swrpg_qsg_transplant';
(function () {
  try {
    var m = /[?&]qsg=([01])\b/.exec(window.location.search);
    if (m && window.localStorage) window.localStorage.setItem(QSG_TRANSPLANT_KEY, m[1]);
  } catch (e) {}
})();
function qsgTransplantSwitch() {
  try {
    return !(window.localStorage && window.localStorage.getItem(QSG_TRANSPLANT_KEY) === '0');
  } catch (e) { return true; }
}
// THE CHARACTER FILES RECEIPT'S SWITCH (B-770; ruling `character-files-paper-concept` R-CFP-2) —
// the same mechanism a SIXTH time, its own key: `?files=1` persists at boot (the SPA drops the query
// before the room renders, exactly as it drops `?transplant=1`), `?files=0` clears it, the ⚙
// "Rebuilt character files (preview)" toggle writes the same key on local and LAN builds. ON by
// default since the one-pass flip (R-PRS-8 step 4); a stored '0' is the OFF override (the rollback
// lever until N-post) — OFF renders today's dark `ImportOgg` unchanged, every `data-tour-id` anchor and every
// handler where it is.
//
// ITS OWN SWITCH AND NOT A SHARED ONE, and R-CFP-2 states the reason so a later lap cannot
// "simplify" it away: Jason has ruled ONE flip (R-PRS-4), but the flip is simultaneous and the BUILD
// is not. Every suite in this family asserts the legacy surface is byte-identical with its switch
// OFF (since the flip, by a stored '0'), and one asserted the switch was off by default on a clean profile
// (since the flip it asserts ON). A sixth flag flipped in the
// same pass costs nothing; a COUPLED flag costs the OFF-path positive control that proves we broke
// nothing. The six switches flip together at the one-pass flip (R-PRS-5), which B-773's completion
// triggers.
//
// AND THIS SURFACE PERSISTS NOTHING OF ITS OWN (R-CFP-2's durable-contract clause, a POSITIVE claim
// rather than an omission). The room hands `oggImport` / `oggExport` a file and renders what they
// return; the only write it ever makes is the roster write "Add to my roster" already made, in the
// roster's existing shape. This key is the ONE new thing in localStorage and it is a preview lever,
// not save data. A row that finds itself wanting to store anything else STOPS and comes back for
// paper, because that would be a save-shape change no ruling has authorised.
var FILES_TRANSPLANT_KEY = 'swrpg_files_transplant';
(function () {
  try {
    var m = /[?&]files=([01])\b/.exec(window.location.search);
    if (m && window.localStorage) window.localStorage.setItem(FILES_TRANSPLANT_KEY, m[1]);
  } catch (e) {}
})();
function filesTransplantSwitch() {
  try {
    return !(window.localStorage && window.localStorage.getItem(FILES_TRANSPLANT_KEY) === '0');
  } catch (e) { return true; }
}
// THE CHARACTER FILES INTERIM ZONES, DECLARED ONCE (B-770) — the three phases whose paper drawing is
// not built yet, each standing on a dark island inside the paper shell. `QSG_INTERIM_ZONES` below is
// the precedent and its whole reason applies here: the island's attribute is
// `data-files-interim="<zone>"`, the DOM query `[data-files-interim]` is what "what is left to
// transplant" MEANS from here on, and this list is the vocabulary that query may answer with — so a
// name cannot be invented at a call site and a zone cannot be silently renamed out of the
// enumeration. Each entry names the ROW that retires it, and the list SHRINKS as the rows land
// rather than a status line being edited to say so.
//
// B-770 DRAWS FRAMES A · B · C · D · E · F · M · N · U · Y — entry, loading, drop (at rest and with
// a file over it), both import error reasons, the export error, at phone, mid and desk. Everything
// else is below.
// `match-report` LEFT THIS LIST AT B-771 — frames G–L are drawn paper, so the zone is gone from the
// vocabulary rather than a line being edited to say it is done. 3 → 2, and the query
// `[data-files-interim]` now answers with `pick` and `export-report` and nothing else.
// AND B-772 TOOK THE LAST TWO. `pick` (frames O · P · Q — the roster index, its rows and the drawn
// EMPTY state) and `export-report` (frames R · S · T — the export receipt's four heads, its defaults
// ledger, its two SENTENCE lanes and the download act) both LEFT this list, so the map is EMPTY and
// that is the END STATE rather than a gap: `[data-files-interim]` answers with NOTHING on this
// surface, at every phase and every band, and this declaration says so out loud instead of the query
// going quiet because a selector broke. 3 → 2 → 0 across three rows, and nobody edited a status line.
// THE MACHINERY STAYS AND THAT IS DELIBERATE, exactly as `QSG_INTERIM_ZONES` was left at zero: this
// list, `paperZone()` and the `[data-files-interim]` island are B-773's and the flip's deletion lap's
// to remove (the switch is still OFF). An empty vocabulary with its comment intact is the honest
// shape for the window between the last island and the flip — a reader can see the ledger CLOSED
// rather than inferring it from an absence.
var FILES_INTERIM_ZONES = {
  // EMPTY (B-772, 2026-09-22) — no zone is left. Do not re-add a name here without a drawn, approved
  // frame for what stands on it; that is the contract this column has kept for three rows.
};
// THE INTERIM ZONES, DECLARED ONCE (R-QSG-6 row 1) — the eight places the paper shell stands over a
// shipped dark body until its row lands. The island's attribute is `data-qsg-interim="<zone>"` and
// the DOM query `[data-qsg-interim]` is what "what is left to transplant" means from here on; this
// list is the vocabulary that query can answer with, so a name cannot be invented at a call site and
// a zone cannot be silently renamed out of the enumeration. Each entry names the ROW that retires
// it. B-726 deletes the list with the last island.
// `shelf` LEFT THIS LIST AT B-720 and `opened-card` LEFT IT AT B-721 — that is the ledger working:
// the eighteen frontispieces, the register, the whole record on paper and the one act that takes it
// are all drawn from `pregen_sheets.json` and the registries, so QUICK START HAS NO ZONE LEFT and the
// vocabulary the query can answer with is SIX names, all of them Guided's. The list shrank 8 → 7 → 6
// without anyone editing a status line, which is the whole reason it is a list and not a memory.
// AND B-722 TOOK THE FIVE STEP ZONES — `step-gameline` · `step-role` · `step-species` ·
// `step-background` · `step-loadout` all LEFT the list, because Guided's five step pages are drawn
// from `QB_ROLES`, `QB_SPECIES_DESC`, the species registry, the three narrative-mechanic files and
// `QB_LOADOUT_OPTIONS` priced through the item registries. 8 → 7 → 6 → 2, and nobody edited a status
// line to make it so.
// `species-pick` IS THE ONE NAME THIS ROW ADDS, and it is a FINDING rather than a zone B-722 retires:
// the species grant picker (`FCSkillPicker`) is the ONE component Guided and the creator share, and
// NO FRAME DRAWS IT ON EITHER FORK — LAB_NOTES §194 names it and leaves it undrawn ("that sub-view is
// named here and not drawn — it is the same drawing in both forks"). It is dark-styled throughout, so
// it stands on an island rather than being invented in paper on a build row's authority. The row that
// draws it NOW EXISTS — B-750, cut when this row landed — so this column says the thing it promises to
// say, the row that RETIRES the zone, and B-722's disclosed exception to that contract is CLOSED.
// AND B-723 TOOK THE REVIEW — the XP ledger (R-QSG-5 (3)), the deduped grants at their RESOLVED rank
// (B-727/B-718) and the credits the shipped review already computes, all read off the one assembly
// the finalize persists through (`guidedAssembledModel`, B-738). 8 → 7 → 6 → 2 → 1, and the ONE name
// left is the sub-view NO FRAME DRAWS on either fork. When B-750 lands this list is empty and B-726
// deletes it.
// AND B-750 TOOK THE LAST ONE. `species-pick` LEFT THIS LIST — the species grant picker is `FCSkillPicker`
// itself with a `paper` prop (R-QSG-3's device, the SAME component Guided and the creator's three mounts
// share), drawing frames AC/AD/AE of lab `18301f1f` §D, which Jason approved 2026-09-21. So the map is
// EMPTY, and that is the END STATE rather than a gap: `[data-qsg-interim]` answers with NOTHING on either
// door, at every band, on every step, and this declaration says so out loud instead of the query going
// quiet because a selector broke. 8 → 7 → 6 → 2 → 1 → 0 across six rows, and nobody edited a status line.
// THE MACHINERY STAYS AND THAT IS DELIBERATE: this list, `GB_PAPER_STEPS`, the `[data-qsg-interim]` query
// and the `step-` fallback in `QuickBuild` are B-726's to delete with the flip (the switch is still OFF).
// An empty vocabulary with its comment intact is the honest shape for the window between the last island
// and the flip — a reader can see the ledger closed rather than inferring it from an absence.
var QSG_INTERIM_ZONES = {
  // EMPTY (B-750, 2026-09-21) — no zone is left. Do not re-add a name here without a drawn, approved
  // frame for what stands on it; that is the contract this column has kept for six rows.
};
// THE COMPLEMENT OF THAT LIST, for Guided: which of the six steps stand on PAPER at this row. Declared
// so the island's presence is a read of a list rather than a consequence of which component a `&&`
// happened to reach, and so a step added to `STEPS` — which R-QSG-4 forbids, but the check is free —
// cannot silently acquire a paper page it has no frame for.
var GB_PAPER_STEPS = {
  'gameline': 'B-722', 'role': 'B-722', 'species': 'B-722',
  'background': 'B-722', 'loadout': 'B-722', 'review': 'B-723',
};
var FC_MASTER_DETAIL_STEPS = { species: true, career: true };
var FC_STEP_COLUMN_MAX = {
  gameline: 'var(--desk-content-max)', species: 'var(--desk-content-max)', career: 'var(--desk-content-max)',
  specialization: 'var(--desk-content-max)', mechanic: 'var(--desk-content-max)', characteristics: 'var(--desk-content-max)',
  skills: 'var(--desk-content-max)', talents: 'var(--desk-content-max)', forcepowers: 'var(--desk-content-max)',
  motivation: 'var(--desk-content-max)', gear: 'var(--desk-content-max)', review: FC_REVIEW_COLUMN_MAX,
};
var FC_CHAR_MEASURE_PX = '620px';
function creatorLayout(step, subView, band, mode) {
  var masterDetail = mode === 'desktop' && !!FC_MASTER_DETAIL_STEPS[step];
  return {
    masterDetail: masterDetail,
    scrollKey: step + (masterDetail ? '' : (subView || '')),
    showBuildRail: band === 'wide',
    columnMax: band !== 'mobile' ? (FC_STEP_COLUMN_MAX[step] || 'var(--desk-read-max)') : 'var(--desk-read-max)',
  };
}

var FC_CHAR_ORDER = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
var FC_CHAR_ABBR = { brawn: 'BR', agility: 'AG', intellect: 'INT', cunning: 'CUN', willpower: 'WIL', presence: 'PR' };
var FC_CHAR_FULL = { brawn: 'Brawn', agility: 'Agility', intellect: 'Intellect', cunning: 'Cunning', willpower: 'Willpower', presence: 'Presence' };

/* ── Category-stripe card pattern (canonical) ────────────
   Per holocron-design-system §1 "eyes parse by hue 2× faster than by label":
   list surfaces carrying 3+ categories should use a 3px left-stripe of the
   category's hue, a 1px border at ~20% alpha, and an ambient tint at 6–14%
   alpha. `tone='default'` for resting lists (6%), 'active' for engaged
   states (10%), 'danger' for warnings (14%). Used by weapons, skills, gear
   categories, careers — anywhere the list is mixed-category. */
// WEAPON_COLORS + weaponAccentFor DELETED here (ruling `gear-consolidation-build` ADDENDUM 4,
// answering the fork (b)): the GEAR card takes Mock 2's uniform card treatment, and that card was
// their only consumer. Delete, don't stub — the six-colour palette and the per-skill idea are
// preserved on board row **F-90** for a deliberate concept, not stranded in dead code here.
var CATEGORY_CARD_ALPHA = { default: '0E', active: '1A', danger: '22' };
function categoryCardStyle(accent, tone) {
  var alpha = CATEGORY_CARD_ALPHA[tone || 'default'] || CATEGORY_CARD_ALPHA.default;
  return {
    background: accent + alpha,
    border: '1px solid ' + accent + '33',
    borderLeft: '3px solid ' + accent,
    borderRadius: '6px',
  };
}

function fcSectionHeaderStyle(accent) {
  return {
    // Per font-overhaul 2026-04-22: Share Tech Mono 10px read as thin at the
    // scale of a section header. Rajdhani 700 13px with the same 0.18em
    // letter-spacing preserves the tech-tight feel while carrying enough
    // weight to anchor the section below it. Left stripe bumped 2px → 3px
    // to match the canonical category-card stripe.
    fontFamily: 'var(--font-body)',
    fontWeight: 700,
    fontSize: 'var(--fs-sm)',
    letterSpacing: '0.18em',
    color: accent,
    textShadow: '0 0 14px ' + accent + '44',
    padding: '8px 0 8px 12px',
    marginBottom: '10px',
    borderLeft: '3px solid ' + accent,
    background: 'linear-gradient(90deg, ' + accent + '14, transparent 72%)',
  };
}

// ── FCSpeciesChangeReview — C-SVRA-3 / frame H of the approved mockup ─────────────────────────
// Jason 2026-08-11: changing a pick and pressing SELECT again shows what the change costs, then
// Confirm or Go Back. Deliberately TWO FACTS and no more (his second pass): the signed XP swing and
// the change that caused it. The six characteristics are on the card behind this sheet and update on
// confirm, so listing them here was showing work rather than answering the question.
//
// The skills-reset warning the first cut carried is GONE because B-147 removed the behaviour it
// described: a re-selection no longer discards purchased ranks or the career and spec free ranks.
function FCSpeciesChangeReview({ review, accent, onBack, onConfirm }) {
  var up = review.xpSwing >= 0;
  var hue = 'var(--doc-ink)';   // F-101 R-CSC-1(b): the arrow says up or down; the ink does not
  return (
    <div data-testid="fc-species-change-review" role="dialog" aria-modal="true"
      aria-label="Review this change"
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(4,6,10,0.78)',
               display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: '520px', background: 'var(--doc-paper-raised)',
                    borderTop: '2px solid var(--doc-rule)', borderRadius: '14px 14px 0 0',
                    padding: '16px 14px 14px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-sm)', letterSpacing: '0.08em', color: 'var(--doc-accent)', marginBottom: '4px' }}>
          REVIEW THIS CHANGE
        </div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--doc-ink-muted)', marginBottom: '13px' }}>
          {review.changed.map(function(c, i) {
            return <span key={i}>You changed <strong style={{ color: 'var(--doc-ink)' }}>{c.group}</strong> from {c.from} to {c.to}.{' '}</span>;
          })}
        </div>
        <div data-testid="fc-species-change-xp" style={{
          display: 'flex', alignItems: 'baseline', gap: '10px', padding: '14px 12px', borderRadius: '9px',
          background: hue + '12', border: '1px solid ' + hue + '55',
        }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-2xl)', color: hue, lineHeight: 1 }}>
            {(up ? '+' : '') + review.xpSwing}
          </span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)', lineHeight: 1.35 }}>
            XP to spend, once the change is applied.
          </span>
        </div>
        <div style={{ display: 'flex', gap: '9px', marginTop: '14px' }}>
          <button type="button" onClick={onBack} style={{
            flex: 1, minHeight: '48px', borderRadius: '8px', cursor: 'pointer',
            fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', letterSpacing: '0.08em',
            background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)', color: 'var(--doc-ink-muted)',
          }}>GO BACK</button>
          <button type="button" onClick={onConfirm} style={{
            flex: 1, minHeight: '48px', borderRadius: '8px', cursor: 'pointer',
            fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', letterSpacing: '0.08em',
            background: 'var(--doc-paper-raised)',
            border: '1px solid var(--doc-rule)', color: 'var(--doc-accent)',
          }}>CONFIRM CHANGE</button>
        </div>
      </div>
    </div>
  );
}

// R-MSP-A — THE MARKER IS A COUNT CHIP, and it is NEVER grey. Grey is the universal signal for
// *disabled*, and this row is not disabled: it is the one row with MORE behind it. Greying would
// also drop the row's text under the contrast floor, and ADAPTIVE_STANDARD / UI_RULES / the a11y
// floor are LAW where a mockup is a proposal (C-MU-4) — so this is not a preference.
// Quiet on purpose: it sits on 117 of 413 rows, and a marker that reads as an alert on 28% of a
// list is furniture. Mono + letterspaced at the row's existing secondary alpha, the treatment the
// list already uses for its own metadata.

function FCSourceChip({ count, accent }) {
  if (!count || count < 2) return null;
  return (
    <span data-source-chip={count} style={{
      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em',
      color: accent, opacity: 0.75, border: '1px solid ' + accent + '55',
      borderRadius: '2px', padding: '1px 5px', flexShrink: 0, whiteSpace: 'nowrap',
    }}>{count} SOURCES</span>
  );
}

function FCSpeciesCard({ sp, accent, selected, isExpanded, onTap, isFirst, picks, sourceCount }) {
  var isSelected = selected && selected.id === sp.id;
  // FORK B: the row prints the RESOLVED species. For the species the player has already chosen that
  // means their recorded picks, so the row follows the choice (the approved mockup: "the list row
  // and the XP rail follow the choice"). For every other row nothing is recorded, and a `preselect`
  // group resolves to its first option, so a browsing visitor sees a complete stat block rather than
  // a base record no variant actually has.
  var effRow = speciesEff(sp, isSelected ? picks : null);
  var FULL_LABELS = FC_CHAR_FULL;                       // one home (was a private copy of the same six)
  var img = speciesImgPath(sp);
  var abilityCount = (sp.starting_skills || []).length + (sp.starting_talents || []).length +
    (sp.special_abilities || []).length + (sp.option_choices || []).length + (sp.force_rating ? 1 : 0);
  return (
    <button onClick={function() { onTap(sp); }} className="hp-plate"
      data-accent={(isExpanded || isSelected) ? '1' : undefined} style={{
      width: '100%', padding: '0', cursor: 'pointer',
      display: 'flex', alignItems: 'stretch', overflow: 'hidden',
      transition: 'background 0.25s, box-shadow 0.25s',
    }}>
      {/* Portrait thumbnail — hidden in commercial mode */}
      {!COMMERCIAL_MODE && <div style={{
        width: '60px', flexShrink: 0,
        backgroundImage: img ? 'url(' + img + ')' : 'none',
        backgroundSize: '140%', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat',
        backgroundColor: 'var(--doc-paper-raised)',
        display: img ? undefined : 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* A removed portrait gets the DESIGNED placeholder, not an empty grey box (the same
            primitive the sheet uses for a portrait-less character). See speciesImgPath. */}
        {!img && window.SWRPGUI.Monogram({ name: sp.name, accent: accent, size: 44 })}
      </div>}
      {/* Info */}
      {/* `minWidth: 0` — the flex default is `min-width:auto`, which refuses to shrink a flex item
          below its content's intrinsic width. This column would not yield, so the XP badge beside
          it (a `flexShrink:0` sibling) was pushed PAST the card, and once the card became an
          `hp-plate` its `overflow:hidden` clipped the badge away entirely: measured 41.8px outside
          a 255px card, i.e. not merely tight but invisible. B-210's own premise — a rail
          calibrated to 336px "so the badge fits" — is the statement of intent this restores; the
          guard had gone quietly vacuous, reporting the SAME headroom at 336 and at 320. */}
      <div style={{ flex: 1, minWidth: 0, padding: isExpanded ? '10px 10px' : '8px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: isExpanded ? '6px' : '4px', textAlign: 'left' }}>
        {/* The chip sits on the row's NAME line — the metadata line the row already has, so the
            marker is local to the thing it is about (R-MSP-B refused a global top bar for exactly
            that: a bar cannot say WHICH row). The row remains ONE button; the chip is not
            separately tappable in V1. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: isExpanded ? '14px' : '13px', fontWeight: 700, color: 'var(--doc-ink)', letterSpacing: '0.06em' }}>{sp.name}</div>
          <FCSourceChip count={sourceCount} accent={accent} />
        </div>
        {/* Stat bar. The six values now carry their characteristic's SHORT LABEL underneath —
            previously they were six bare numbers whose meaning lived entirely in colour and
            position, which only a player who already knew the order could read. The label takes
            the characteristic's own colour when the value is 3+, matching both the value above it
            and the sort chip that names it, so the sort bar reads as this list's key. Costs ~9px
            of row height, spent deliberately. */}
        <div data-tour-id={isFirst ? 'p-custom-species-stats' : undefined} style={{ display: 'flex', gap: '4px' }}>
          {FC_CHAR_ORDER.map(function(attr) {
            var val = effRow.characteristics[attr];
            var isHigh = val >= 3;
            return (
              <div key={attr} style={{
                minWidth: '26px', textAlign: 'center', padding: isExpanded ? '3px 2px 2px' : '2px 0 3px', borderRadius: '3px',
                background: 'var(--doc-paper-raised)',
                border: '1px solid var(--doc-rule)',
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: isExpanded ? '13px' : '12px', color: 'var(--doc-ink)' }}>{val}</div>
                {isExpanded
                  ? <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'var(--doc-ink-muted)', marginTop: '1px' }}>{FULL_LABELS[attr]}</div>
                  : <div style={{ fontFamily: 'var(--font-mono)', fontSize: '8px', letterSpacing: '0.08em', marginTop: '1px', color: 'var(--doc-ink-muted)' }}>{FC_CHAR_ABBR[attr]}</div>}
              </div>
            );
          })}
        </div>
        {/* Expanded details */}
        {isExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '2px' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)', letterSpacing: '0.04em' }}>
              {effRow.starting_xp} Experience Points
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)', letterSpacing: '0.04em' }}>
              Wound Threshold {effRow.wound_threshold_base} · Strain Threshold {effRow.strain_threshold_base}
            </div>
            {abilityCount > 0 && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--doc-accent)', letterSpacing: '0.04em' }}>
                {abilityCount} Special {abilityCount === 1 ? 'Ability' : 'Abilities'}
              </div>
            )}
          </div>
        )}
      </div>
      {/* XP badge */}
      <div data-tour-id={isFirst ? 'p-custom-species-xp' : undefined} style={{ flexShrink: 0, display: 'flex', alignItems: isExpanded ? 'flex-start' : 'center', paddingRight: '10px', paddingTop: isExpanded ? '12px' : '0' }}>
        <div style={{
          padding: '3px 8px', borderRadius: '10px',
          background: 'var(--doc-paper-raised)',
          border: '1px solid var(--doc-rule)',
        }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--doc-ink)' }}>{effRow.starting_xp}</span>
        </div>
      </div>
    </button>
  );
}

/* The sort bar, which is ALSO the key.
   124 species is a wall, and the honest question a player arrives with is not "which letter" but
   "strong at what?" — so the bar sorts by characteristic. Each chip carries its characteristic's
   abbreviation in that characteristic's own colour PERMANENTLY, not only when active, which is
   what lets it double as the legend for the six labelled numbers in every row beneath it. That is
   the whole reason there is no separate key block taking up space: the control the player is
   already looking at explains the columns directly below it, and pressing one tints the bar the
   same colour as the values that just moved.
   This replaces the flat-versus-sectioned fork rather than answering it — the sectioned variant
   assumed a species "family" grouping that our data does not have (ruling
   `creator-ui-phase-b-mockups`: the fork's premise was false, and Jason's redirect was
   "sort, don't section"). */

function FCSpeciesSortBar({ sortKey, onSort, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', padding: '0 16px 6px' }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.18em', color: 'var(--doc-ink-muted)' }}>SORT</span>
      {/* 44px, not the mockup's 34. The drawing specified `min-height:34px` for these chips and
          AS-6's guard caught it on the first run — the adaptive standard's touch floor is 44px and
          a mockup does not outrank it (ADAPTIVE_STANDARD AS-6). The drawn look survives: the chips
          keep their pill shape and type sizes, and only the vertical padding grows. */}
      <button onClick={function() { onSort('az'); }} aria-pressed={sortKey === 'az'} style={{
        minHeight: '44px', padding: '6px 14px', borderRadius: '999px', cursor: 'pointer',
        fontFamily: 'var(--font-body)', fontSize: '13px',
        background: 'var(--doc-paper-raised)',
        border: '1px solid ' + (sortKey === 'az' ? 'var(--doc-accent)' : 'var(--doc-rule)'),
        color: sortKey === 'az' ? 'var(--doc-accent)' : 'var(--doc-ink-muted)',
      }}>{'A–Z'}</button>
      {FC_CHAR_ORDER.map(function(attr) {
        // R-CSC-3(a): one treatment for all six. Selection reads from STOCK and INK — a raised
        // chip in full ink versus a flat one in muted — never from a per-characteristic hue.
        var hue = 'var(--doc-accent)';
        var on = sortKey === attr;
        return (
          <button key={attr} onClick={function() { onSort(attr); }} aria-pressed={on}
            aria-label={'Sort by highest ' + FC_CHAR_FULL[attr]} style={{
              minHeight: '44px', padding: '5px 13px', borderRadius: '999px', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'baseline', gap: '6px',
              background: 'var(--doc-paper-raised)',
              border: '1px solid var(--doc-rule)',
            }}>
            {/* the abbreviation is ALWAYS in its characteristic's colour — this is the key half */}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.1em', color: hue }}>{FC_CHAR_ABBR[attr]}</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: on ? 'var(--doc-ink)' : 'var(--doc-ink-muted)' }}>{FC_CHAR_FULL[attr]}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── THE CREATOR'S BUILD RAIL (C7; ruling `creator-species-split-composition` Q1) ─────────────
   ≥1367 only, and it belongs to the SHELL, not to any step. That is the whole ruling: the running
   tally of a character — the budget, what has been chosen, where you are — is step-agnostic, so
   building it inside the species step would have meant re-inventing it in each of the eleven step
   laps that follow. Hoisting it also removed the need for a third pane, which is why the reserved
   `WorkspaceGrid` was never touched.

   Three live sections, exactly as the Phase B concept §4 named them: the XP budget, the
   character-so-far, and the step map with jump-to-step — "the review step's ✎ EDIT power made
   ambient". Everything it prints is derived from `ctrl`; the rail owns no state of its own, so it
   cannot drift from the step body beside it.

   Copy is plain (mechanics voice): it says what these are, not what they mean. */
function FCBuildRail({ ctrl, accent }) {
  var chosen = [
    { label: 'SPECIES', value: ctrl.selectedSpeciesData ? ctrl.selectedSpeciesData.name : null },
    { label: 'CAREER', value: ctrl.selectedCareerData ? ctrl.selectedCareerData.name : null },
    { label: 'SPECIALIZATION', value: ctrl.selectedSpecData ? ctrl.selectedSpecData.name : null },
  ];
  var stepLabels = fcStepLabels(ctrl.gameLine);   // B-1668: the wave's own words, one builder
  var steps = ctrl.reachableSteps || [];
  var hereAt = steps.indexOf(ctrl.currentStep);
  return (
    /* D2b — THE SECTIONS DISTRIBUTE OVER THE STRETCHED WRAPPER instead of bunching at the top
       (§4 D2's last bullet). `minHeight: 100%` makes the column as tall as the rail it lives in and
       `space-between` spreads the three sections down it: EXPERIENCE at the top, STEPS at the foot,
       SO FAR between them.
       DISTRIBUTE THE SECTIONS, DO NOT INFLATE THE ROWS — the ruling says this in as many words, and
       it is why the spreading is done with justification rather than by growing anything. Every row
       keeps the height it had, so the 44px floor holds and AS-6's <=72px ledger budget is untouched.
       IT DEGRADES CORRECTLY: `space-between` only has free space to distribute when there IS free
       space. On a short rail the sections spread; once the step map grows past the wrapper the
       justification has nothing to give and the rail simply scrolls, exactly as before. No content
       was added — R-CDW-4 and the ratified-shut R1 both forbid it, and if distribution alone still
       reads empty at 1920x1080 that is the ACCEPTED outcome, reported in the gate rather than
       filled. */
    <div style={{ padding: '14px 14px 24px', display: 'flex', flexDirection: 'column', gap: '18px',
      minHeight: '100%', boxSizing: 'border-box', justifyContent: 'space-between' }}>
      {/* (a) the budget — the SAME bar the spend steps use, not a second one that looks like it */}
      <div data-rail-section="experience">
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.38)', marginBottom: '7px' }}>EXPERIENCE</div>
        <FCXpBudgetBar
          xpPool={ctrl.xpPool} xpSpentOnChars={ctrl.xpSpentOnChars}
          xpSpentOnTalents={ctrl.xpSpentOnTalents + (ctrl.xpSpentOnForcePowers || 0)}
          xpSpentOnSkills={ctrl.xpSpentOnSkills} xpRemaining={ctrl.xpRemaining} accent={accent} />
      </div>

      {/* (b) the character so far */}
      <div data-rail-section="sofar">
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.38)', marginBottom: '7px' }}>SO FAR</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {chosen.map(function(row) {
            return (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.35)' }}>{row.label}</span>
                {/* An unmade choice reads as a dash, not as blank space — the rail says what is
                    still open as plainly as what is settled. */}
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', textAlign: 'right',
                  color: row.value ? '#fff' : 'rgba(255,255,255,0.3)' }}>{row.value || '—'}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* (c) the step map — jump-to-step, the review step's ✎ EDIT made ambient */}
      <div data-rail-section="steps">
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.38)', marginBottom: '7px' }}>STEPS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {steps.map(function(s, i) {
            var isHere = s === ctrl.currentStep;
            var isDone = hereAt >= 0 && i < hereAt;
            return (
              <button key={s} onClick={function() { ctrl.goToStep(FC_STEPS.indexOf(s)); }}
                aria-current={isHere ? 'step' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                  minHeight: '44px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer',
                  textAlign: 'left', border: '1px solid ' + (isHere ? accent + '55' : 'transparent'),
                  background: isHere ? accent + '12' : 'transparent',
                }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', minWidth: '16px',
                  color: isHere ? accent : isDone ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.25)' }}>{i + 1}</span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
                  color: isHere ? '#fff' : isDone ? 'rgba(255,255,255,0.62)' : 'rgba(255,255,255,0.32)' }}>
                  {stepLabels[s] || s}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FCSpeciesBrowser({ speciesList, selected, onSelect, accent, picks, groups }) {
  var _useS = useState(''), search = _useS[0], setSearch = _useS[1];
  var _useK = useState('az'), sortKey = _useK[0], setSortKey = _useK[1];
  var filtered = useMemo(function() {
    if (!search.trim()) return speciesList;
    var q = search.toLowerCase().trim();
    return speciesList.filter(function(sp) { return sp.name.toLowerCase().indexOf(q) >= 0; });
  }, [speciesList, search]);
  // A–Z is the default. The registry is very nearly alphabetical already, but species added after
  // the first import (the A&A set, the community tier) were APPENDED, so they sorted to the bottom
  // of a list that otherwise reads alphabetically — Ewok after Zabrak. Sorting by name puts them
  // where a player looking for them would look.
  // By characteristic: highest first, ties broken alphabetically so the order is total and stable
  // (a tie broken by registry position would reshuffle whenever a species is added).
  // How many ACTIVE records supply this row's name. Read from the groups the pipeline captured
  // before the dedupe, keyed the same way, so a row is marked if and only if the list actually
  // collapsed something into it.
  function countFor(sp) {
    var S = window.SWRPGSources;
    var key = (S && S.dedupeName) ? S.dedupeName(sp && sp.name) : null;
    var g = key && groups ? groups[key] : null;
    return g ? g.length : 1;
  }
  var markedCount = useMemo(function() {
    return (filtered || []).filter(function(sp) { return countFor(sp) > 1; }).length;
  }, [filtered, groups]);
  var ordered = useMemo(function() {
    var list = filtered.slice();
    if (sortKey === 'az') {
      list.sort(function(a, b) { return a.name.localeCompare(b.name); });
    } else {
      list.sort(function(a, b) {
        var d = (b.characteristics[sortKey] || 0) - (a.characteristics[sortKey] || 0);
        return d !== 0 ? d : a.name.localeCompare(b.name);
      });
    }
    return list;
  }, [filtered, sortKey]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', animation: 'slideInRight 0.35s ease-out' }}>
      <FCSearchBar value={search} onChange={setSearch} accent={accent} placeholder="Search species…" />
      <FCSpeciesSortBar sortKey={sortKey} onSort={setSortKey} accent={accent} />
      {/* R-MSP-B — THE COUNT LINE SAYS WHAT IT IS (mechanics voice: plain and gamey, never
          detector-speak like "Multiple Sources Identified"). This line is the list's own status
          line and is the honest home for the fact; a separate notification bar was refused because
          it cannot say WHICH row. The marked count is DERIVED from the same groups the chips read,
          so the sentence and the chips cannot disagree, and it disappears entirely when nothing is
          marked — a single-source shelf reads exactly as it does today. */}
      <div data-species-count="1" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em', color: 'var(--doc-ink-muted)', padding: '0 16px 8px' }}>
        {markedCount > 0
          ? ordered.length + ' SPECIES · ' + markedCount + ' FROM MORE THAN ONE SOURCE'
          : ordered.length + ' SPECIES AVAILABLE'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 12px 24px' }}>
        {ordered.map(function(sp, idx) {
          return <FCSpeciesCard picks={picks} key={sp.id} sp={sp} accent={accent} selected={selected} isExpanded={false} onTap={onSelect} isFirst={idx === 0} sourceCount={countFor(sp)} />;
        })}
      </div>
    </div>
  );
}

/* ── FC Multi-source picker ──────────────────────────────
   Ruling `multi-source-picker` §3/§5, amended by R-MSP-D2 (`species-followup-mockups` §4).
   Approved page: Docs/mockups/multi-source-picker.html.

   THE DEFECT IT FIXES: 117 of 413 species names are supplied by more than one active source, and
   the app has been picking one silently by precedence since F-67. A player who switches a book ON
   cannot see what it changed, and is handed one publisher's answer as if it were the only one.
   This is the moment of choosing, so the choice is asked HERE — a front door, not a settings drawer.

   R-MSP-D2 — IT OPENS WITH NOTHING SELECTED, and that is Jason's call overruling the earlier
   pre-selected shape: "the defect this feature fixes is the app choosing silently, and a
   highlighted default is still the app choosing." The one-tap common path is knowingly given up.
   The primary action is GATED until a pick, and CANCEL PICKS NOTHING — a dismissal returns to the
   list with no species selected, never quietly falling back to the precedence winner. */

/* B-587 (Jason 2026-09-14: "Use this version for the species picker uses the old UI"): the revision
   styled this overlay in `--doc-*` tokens, but it mounts at the creator's overlay layer, OUTSIDE any
   sheet's `data-doc-ground` — so the tokens resolved to tokens.css's dark defaults on the
   transplant too. `ground` (the band, when the transplant is lit) puts the creator ground on the
   overlay's own root; the legacy path passes nothing and keeps its dark defaults. */
function FCSourcePicker({ candidates, accent, onUse, onCancel, ground }) {
  var _useC = useState(null), chosenId = _useC[0], setChosenId = _useC[1];
  if (!candidates || candidates.length < 2) return null;
  var name = candidates[0].name;
  var chosen = candidates.filter(function(c) { return c.id === chosenId; })[0] || null;

  return (
    <div data-source-picker={candidates.length} role="dialog" aria-modal="true"
      data-doc-ground={ground ? 'creator' : undefined} data-band={ground ? ground : undefined}
      aria-label={name + ' appears in ' + candidates.length + ' of your sources'}
      style={{
        // FIXED on the transplant (B-587): the phone's rebuilt species step scrolls the page body to
        // reach a row, and an absolute overlay scrolls away with it (measured top −538 after a
        // press on Droid). The legacy step scrolls its own list, so absolute holds there.
        position: ground ? 'fixed' : 'absolute', inset: 0, zIndex: 40, display: 'flex', flexDirection: 'column',
        background: 'var(--doc-paper)', animation: 'fadeIn 0.2s ease',
      }}>
      <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid var(--doc-rule)' }}>
        {/* R-MSP-B: the title says what happened, in the player's terms — no detector-speak. */}
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--doc-ink)', letterSpacing: '0.02em' }}>
          {name} appears in {candidates.length} of your sources
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px' }}>
        {candidates.map(function(c) {
          // R-MSP-C — each version is THE EXISTING CARD, one per source, plus its source name.
          // This is not a diff and nothing is compared, highlighted or annotated (Jason's V1
          // constraint); it is the row the list already draws, rendered once per candidate. Without
          // it the overlay asks a question the player has no way to answer.
          return (
            <div key={c.id} style={{ marginBottom: '10px' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.14em', color: 'var(--doc-ink-muted)', padding: '0 2px 4px', wordBreak: 'break-word' }}>
                {/* B-336 species wave: a key prints its title */}
                {(((c.sources || [])[0] && gameLineBookLabel(c.sources[0])) || 'UNKNOWN SOURCE').toUpperCase()}
              </div>
              <div style={{ outline: c.id === chosenId ? '2px solid var(--doc-accent)' : 'none', outlineOffset: '2px', borderRadius: '8px' }}>
                <FCSpeciesCard sp={c} accent={accent} selected={chosen} isExpanded={false}
                  onTap={function() { setChosenId(c.id); }} isFirst={false} picks={null} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', gap: '10px', padding: '10px 16px 16px', borderTop: '1px solid var(--doc-rule)' }}>
        <button onClick={onCancel} aria-label="Cancel and pick no species"
          style={{
            minHeight: '44px', padding: '0 16px', borderRadius: '6px', cursor: 'pointer',
            background: 'transparent', border: '1px solid var(--doc-rule)',
            fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em',
            color: 'var(--doc-ink-muted)',
          }}>CANCEL</button>
        <button data-source-confirm={chosenId ? '1' : '0'} disabled={!chosenId}
          onClick={function() { if (chosenId) onUse(chosen); }}
          aria-label={chosenId ? ('Use the ' + (((chosen.sources || [])[0] && gameLineBookLabel(chosen.sources[0])) || 'chosen') + ' version of ' + name)
                               : 'Choose a version first'}
          style={{
            flex: 1, minHeight: '44px', padding: '0 16px', borderRadius: '6px',
            cursor: chosenId ? 'pointer' : 'default',
            background: 'var(--doc-paper-raised)',
            border: '1px solid ' + (chosenId ? 'var(--doc-accent)' : 'var(--doc-rule)'),
            fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700,
            letterSpacing: '0.08em', color: chosenId ? 'var(--doc-accent)' : 'var(--doc-ink-muted)',
          }}>{chosenId ? 'USE THIS VERSION' : 'CHOOSE A VERSION'}</button>
      </div>
    </div>
  );
}

/* ── FC Species Detail ───────────────────────────────── */

// Reveal-on-scroll-bottom latch (play-test 2026-06-15): returns false until the reader has
// scrolled within `pad` px of the bottom of the nearest scrollable ancestor of `anchorRef`
// (or immediately if the content already fits). Latches true, so expanding a "read more"
// block below does NOT drop it again — the reveal point stays put across the expanded state.
// Used to slide the detail-page CONTINUE bar up only once the user reaches the end of the page.
function useRevealNearBottom(anchorRef, pad) {
  var _u = useState(false), revealed = _u[0], setRevealed = _u[1];
  useEffect(function() {
    if (revealed) return undefined;
    var el = anchorRef.current;
    if (!el) return undefined;
    var sc = el.parentElement;
    while (sc && sc !== document.body) {
      var oy = getComputedStyle(sc).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      sc = sc.parentElement;
    }
    if (!sc || sc === document.body) { setRevealed(true); return undefined; }
    var check = function() {
      if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight - (pad || 80)) setRevealed(true);
    };
    check();
    sc.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return function() {
      sc.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [revealed, anchorRef, pad]);
  return revealed;
}

// B-23 (G8): the stable pick key for an option group — slug of its name (reorder-proof; group names
// are unique within a species). MUST match CharResolve._groupId, which READS it off the sheet.
//
// B-124 — HOISTED TO MODULE SCOPE. It lived inside `FullCreator`, which meant the species PICKER
// (13k lines away) could not reach it and keyed its picks by ARRAY INDEX instead. Two vocabularies
// for one fact is how the pick failed to survive a band flip: the draft could not speak the same
// language as the sheet. One implementation, three readers (picker, confirm, CharResolve).
function groupIdSlug(name) {
  return String(name || 'group').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// FORK B — the creator's door to the ONE species-vs-picks resolution (charResolve.speciesEffective).
// Not a second implementation: this is a null-safe wrapper so a call site can read
// `speciesEff(sp, picks).characteristics[attr]` without repeating the shared-core lookup, and so the
// no-pick fallback rule (a `preselect` group resolves to its first option; anything else resolves to
// nothing) lives in exactly one place. B-124's lesson, applied before it is paid for again: two
// vocabularies for one fact is how the picker keyed by index while the sheet keyed by slug.
// C-SVR-1: the re-seed DECISION, from the same shared module as the resolution it compares. The
// creator asks the question rather than answering it, so `test/species_variant_resolver.cjs` pins the
// rule the app actually uses instead of a lookalike.
function speciesNeedsReseed(sp, prevPicks, nextPicks) {
  if (window.CharResolve && window.CharResolve.speciesReseedNeeded) {
    return window.CharResolve.speciesReseedNeeded(sp, prevPicks, nextPicks);
  }
  return true;
}

// LAP B — the creator's door to the ONE pick-normalizer (charResolve `_chosenOptions`). Same
// null-safe idiom as `speciesEff` directly below, and for the same reason: a group may now yield
// more than one option, and the app must not grow its own scalar-or-array branch. Two vocabularies
// for one fact is B-124.
// THE FALLBACK IS DELIBERATELY THE OLD SINGLE-PICK BEHAVIOUR, not a reimplementation of the new
// rule: if the shared core failed to load the app is already broken, and a degraded path that
// quietly invents multi-pick semantics would be worse than one that behaves like last release.
function speciesChosen(oc, picks) {
  if (window.CharResolve && window.CharResolve.speciesChosenOptions) {
    return window.CharResolve.speciesChosenOptions(oc, picks);
  }
  var id = picks ? picks[groupIdSlug(oc && oc.name)] : null;
  if (id == null || Array.isArray(id)) return [];
  return ((oc && oc.options) || []).filter(function (o) { return o && o.id === id; });
}

function speciesEff(sp, picks) {
  if (window.CharResolve && window.CharResolve.speciesEffective) {
    return window.CharResolve.speciesEffective(sp, picks);
  }
  return {
    characteristics: (sp && sp.characteristics) || {},
    wound_threshold_base: sp ? sp.wound_threshold_base : 10,
    strain_threshold_base: sp ? sp.strain_threshold_base : 10,
    starting_xp: sp ? sp.starting_xp : 100,
  };
}

// D2b — THE SPECIES DOSSIER'S COMPOSED MEASURE.
//
// D2 gave this screen the ROOM (the detail pane went 400 → 936 at 1920) and left the arrangement
// for this lap: a 620px column standing in a 936px pane, better than starved but not designed.
//
// TWO MEASURES SIDE BY SIDE, and the number is derived rather than picked. `--desk-prose-max` is the
// reading law (373px, calibrated over 724,066 characters) and D2 did not touch it; the dossier
// pattern says keep the prose at its measure and give the gutter a structured companion. So the
// composition is prose + gutter + a companion ONE MEASURE WIDE, plus the block's own 32px of
// horizontal padding. This is C11's shipped idiom (`FC_SKILL_COMPOSED_MAX`) applied to a second
// screen rather than a new expression invented for it.
//
// AND IT IS THE ELEMENT'S OWN CAP, NOT THE PAGE'S — the C10 lesson, which this programme has now
// re-learned twice at gates. A page cap sized to the composition makes the element's cap inert, and
// an inert cap is a lying instrument no mutant can redden.
var FC_SPECIES_DOSSIER_MAX = 'calc(2 * var(--desk-prose-max) + var(--desk-gutter) + 32px)';

// ── LAP B — THE OPTION READOUT ───────────────────────────────────────────────────────────────
// AT MODULE SCOPE, and that placement was the guard's doing. Written inside FCSpeciesDetail it
// pushed the component past `species_detail_skill_legibility`'s 40,000-char bound — a fixture
// sanity limit whose stated purpose is keeping the slice honest. The cheap fix was to raise the
// number; the right one was to hear it. The readout needs nothing from the component's closure but
// `accent` and `skillNames`, both now parameters, so it belongs out here.
// The approved mockup moved every consequence OFF the chip face and into a readout beneath the
// row (ruling `species-surface-concept` Amendment 1, R-SSC-A1): at 400px an effect string wrapped
// inside a thumb-width control and was present-but-unreadable. Chips carry the NAME; this carries
// the meaning, with room for the registry's own sentence.
function optionNumbers(o, skillNames) {
  var out = [];
  if (o.wound_threshold_adjustment) out.push((o.wound_threshold_adjustment > 0 ? '+' : '\u2212')
    + Math.abs(o.wound_threshold_adjustment) + ' WOUND');
  if (o.strain_threshold_adjustment) out.push((o.strain_threshold_adjustment > 0 ? '+' : '\u2212')
    + Math.abs(o.strain_threshold_adjustment) + ' STRAIN');
  if (o.xp_adjustment) out.push((o.xp_adjustment > 0 ? '+' : '\u2212')
    + Math.abs(o.xp_adjustment) + ' XP');
  (o.skill_grants || []).forEach(function(g) {
    var nm = (skillNames && skillNames[g.skill]) || g.skill;
    out.push(String(nm).toUpperCase() + ' ' + (g.rank_start || 1));
  });
  return out.join(' \u00b7 ');
}
function renderReadout(group, chosenIds, bounds, accent, skillNames) {
  var opts = (group.options || []).filter(function(o) { return chosenIds.indexOf(o.id) !== -1; });
  var label = bounds.max > 1
    ? (chosenIds.length + ' OF ' + bounds.max + ' CHOSEN')
    : (chosenIds.length ? 'CHOSEN' : 'PICK ONE');
  return (
    <div data-fc-readout={groupIdSlug(group.name)} style={{
      border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
      background: 'rgba(255,255,255,0.02)', overflow: 'hidden', marginTop: '8px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px',
        padding: '8px 11px', borderBottom: '1px solid rgba(255,255,255,0.07)',
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em',
        color: 'rgba(255,255,255,0.45)',
      }}>
        <span>WHAT YOU GAIN</span><span style={{ color: accent }}>{label}</span>
      </div>
      {opts.length === 0 ? (
        <div style={{ padding: '11px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
          lineHeight: 1.45, color: 'rgba(255,255,255,0.5)' }}>
          {bounds.max > 1
            ? 'Nothing chosen yet \u2014 pick above and what each one does appears here.'
            : 'Choose one above to see what it gives you.'}
        </div>
      ) : opts.map(function(o, i) {
        var nums = optionNumbers(o, skillNames);
        return (
          <div key={i} style={{ padding: '10px 11px',
            borderTop: i ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)',
              fontWeight: 700, color: accent }}>{o.name}</span>
            {nums && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
              letterSpacing: '0.08em', color: accent, opacity: 0.8, marginLeft: '9px' }}>{nums}</span>}
            {(o.special_abilities || []).map(function(a, ai) {
              return (
                <div key={ai} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
                  lineHeight: 1.45, color: 'rgba(255,255,255,0.72)', marginTop: '5px' }}>
                  <b style={{ color: 'rgba(255,255,255,0.92)' }}>{a.name}</b>
                  {/* A registry entry with NO description renders as the bare stated rule.
                      Writing a gloss would be inventing content the book does not carry
                      (Amendment 1, endorsed) \u2014 and silhouette is unsimulated anyway. */}
                  {a.description ? ' \u2014 ' + a.description : ''}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}


// ── LAP 2a — species pickers lifted to MODULE SCOPE (ruling `species-ask-build-premise` §5).
// Extracted from FCSpeciesDetail so Lap 2b's sheet ask consumes the SAME chip row the creator
// does (species-surface-concept §5, one-component law). Behaviour identical; closure reads
// (optionPicks/species/skillNames) become params or props. skillSentence stays module scope
// because FCSpeciesDetail's ability rows also call it; optionDelta/optionSentence NEST inside
// the component to keep their (group, o) signatures (R-SDL-2/3 pin them).
var DELTA_CHARS = [['brawn', 'Brawn'], ['agility', 'Agility'], ['intellect', 'Intellect'],
                   ['cunning', 'Cunning'], ['willpower', 'Willpower'], ['presence', 'Presence']];

function skillSentence(skillId) {
  var cb = window._canonicalCharbuild || window._canonicalCharbuildBase;
  var key = (cb && cb.skillSheetKeys && cb.skillSheetKeys[skillId]) || skillId;
  return skillDescFor(key);
}

function pickedIdFor(group, optionPicks) {
  var recorded = optionPicks[groupIdSlug(group.name)];
  if (recorded != null && !Array.isArray(recorded)) return recorded;
  if (Array.isArray(recorded)) return recorded[0] != null ? recorded[0] : null;
  return group.preselect ? ((group.options || [])[0] || {}).id : null;
}

function boundsOf(group) {
  return {
    min: (group && group.min_picks != null) ? group.min_picks : 1,
    max: (group && group.max_picks != null) ? group.max_picks : 1,
  };
}

function pickedIdsFor(group, optionPicks) {
  return speciesChosen(group, optionPicks).map(function(o) { return o.id; });
}

// B-656 — THE TWO SENTENCE-BUILDERS, LIFTED INTO A FACTORY, so the paper species ask (`SheetSpine`)
// draws the SAME sentence the creator's chip does instead of a third copy of the rule. The bodies below
// are the shipped bodies MOVED, not rewritten — R-SDL-2(1) pins `function optionSentence(group, o)` and
// its first arm verbatim, and R-SDL-2/3 pin the `(group, o)` signatures, all of which survive. The two
// closure reads become the factory's parameters (`species`, `skillNames`); `FCSpeciesPickGroup` binds it
// with its own props below, so nothing about the creator's row changed.
function speciesOptionSentencers(species, skillNames) {
  function optionDelta(group, o) {
    var opts = group.options || [];
    if (opts.length < 2) return '';
    // An option that states no characteristics inherits the record's, exactly as `speciesEffective`
    // resolves it — so "varies" is measured on RESOLVED values, never on which keys happen to be
    // present in the JSON.
    // C-VCO-1 (`vaathkree-characteristic-overrides`) — mirror `speciesEffective` exactly: resolve the
    // base (the option's absolute block or the record's), THEN apply the partial `characteristic_overrides`
    // over it. Without this, a form that changes ONE characteristic via an override (Vaathkree Humanoid =
    // Presence 3) reads as the base, presence stops "varying", and the row renders blank — the B-174 defect.
    function charOf(x, attr) {
      var base = (x.characteristics || species.characteristics) || {};
      return Object.assign({}, base, x.characteristic_overrides || {})[attr];
    }
    function fieldOf(x, k) { return x[k] != null ? x[k] : species[k]; }
    function varies(read) {
      var first = read(opts[0]);
      return opts.some(function(x) { return read(x) !== first; });
    }
    // ONLY WHAT MOVES. The four unchanged characteristics are already on the card above, and
    // reprinting them turns a trade into a stat dump — the row must read as the difference.
    var chars = DELTA_CHARS.filter(function(p) {
      return varies(function(x) { return charOf(x, p[0]); });
    }).map(function(p) { return p[1] + ' ' + charOf(o, p[0]); });
    var tail = [];
    if (varies(function(x) { return fieldOf(x, 'wound_threshold_base'); })) tail.push('wound ' + fieldOf(o, 'wound_threshold_base'));
    if (varies(function(x) { return fieldOf(x, 'strain_threshold_base'); })) tail.push('strain ' + fieldOf(o, 'strain_threshold_base'));
    if (varies(function(x) { return fieldOf(x, 'starting_xp'); })) tail.push('XP ' + fieldOf(o, 'starting_xp'));
    var out = chars.join(' · ');
    if (tail.length) out += (out ? ' — ' : '') + tail.join(', ');
    if (out) out += '.';
    // An option that grants an ability names it, under the same only-what-moves rule.
    var abilities = (o.special_abilities || []).map(function(a) { return a && a.name; }).filter(Boolean);
    if (abilities.length) out += (out ? ' ' : '') + 'Grants ' + abilities.join(', ') + '.';
    return out;
  }

  function optionSentence(group, o) {
    if (o.description) return o.description;
    var grants = o.skill_grants || [];
    if (grants.length) {
      // 16 of the 293 grant TWO skills, and their option is named for a subspecies rather than for
      // a skill (Aqualish "Ualaq" → Survival + Perception), so each sentence is led by its skill's
      // name. A single-grant option is named for the skill itself, so a bare sentence is unambiguous
      // there and matches the approved page exactly.
      var sentence = grants.map(function(sg) {
        var s = skillSentence(sg.skill);
        if (!s) return '';
        return grants.length > 1 ? ((skillNames && skillNames[sg.skill] || sg.skill) + ' — ' + s) : s;
      }).filter(Boolean).join(' ');
      // COMPOSE, NEVER SUPPRESS (ruling `b294-xidec-castes-gate` R-XCG-3). This `return` used to be
      // the skill sentence ALONE, so an option that both moved stats and granted a skill printed
      // only the grant. Xi'Dec's `messenger` is how it surfaced: the gender whose stats move most
      // (Brawn 1 · Agility 2 · Cunning 1 — wound 9) was the single one in its group not showing
      // them, because it is also the only one granting a rank. The delta leads and the sentence
      // follows — a prose sentence never suppresses a stat delta, and a row that hides half its
      // truth teaches wrong. Where an option moves nothing and states nothing, `optionDelta`
      // returns '' and the row is byte-identical to what shipped.
      var delta = optionDelta(group, o);
      return delta ? (sentence ? delta + ' ' + sentence : delta) : sentence;
    }
    return optionDelta(group, o);
  }
  return { optionDelta: optionDelta, optionSentence: optionSentence };
}

// `bare` (species-ask-mount): drop the group header + teach line + bounds line, rendering ONLY the
// chips (+ multi-pick readout). The sheet ask surface carries the group's name and its "choose N"
// teaching on its own line above, so repeating them here would double the label. Default false — the
// creator renders unchanged. §5's one-component rule holds: the ask surface reuses THIS control, never
// a second chip.
function FCSpeciesPickGroup({ group, gi, teach, species, optionPicks, skillNames, accent, onPickChange, bare }) {
  var optionSentence = speciesOptionSentencers(species, skillNames).optionSentence;
  var picked = pickedIdFor(group, optionPicks);
  var bounds = boundsOf(group);
  var many = bounds.max > 1;
  var chosenIds = many ? pickedIdsFor(group, optionPicks) : (picked != null ? [picked] : []);
  var atMax = many && chosenIds.length >= bounds.max;
  // The bounds line is the group's arity said in words; the chip's square pip says it before any
  // copy is read. Single-pick groups get NEITHER and are pixel-identical to what shipped.
  var boundsLine = !many ? null
    : (bounds.min > 0 ? 'CHOOSE ' + bounds.min + '\u2013' + bounds.max
                      : 'CHOOSE UP TO ' + bounds.max);
  return (
    <div key={'og' + gi} style={{ marginBottom: bare ? '0' : '10px' }}>
      {/* B-145: `name` is the persistence key and stays internal; `display_name` is what a player
          reads. Renderers read `display_name || name` — one fallback, one place. */}
      {!bare && (many ? (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
          <div style={fcSectionHeaderStyle(accent)}>{(group.display_name || group.name || 'OPTION').toUpperCase()}</div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: 'var(--doc-ink-muted)' }}>{boundsLine}</span>
        </div>
      ) : (
        <div style={fcSectionHeaderStyle(accent)}>{(group.display_name || group.name || 'OPTION').toUpperCase()}</div>
      ))}
      {!bare && teach && <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'var(--doc-ink-muted)', margin: '0 0 8px' }}>{teach}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {(group.options || []).map(function(o, oi) {
          var chosen = many ? (chosenIds.indexOf(o.id) !== -1) : (picked === o.id);
          var blocked = many && !chosen && atMax;
          return (
            <button key={oi} type="button" aria-pressed={chosen}
              role={many ? 'checkbox' : undefined} aria-checked={many ? chosen : undefined}
              aria-disabled={blocked ? 'true' : undefined}
              onClick={function() {
                if (!many) { onPickChange(groupIdSlug(group.name), o.id); return; }
                // LAP B: toggle within the bounds. At maximum an unchosen chip does NOTHING and
                // says so visually BEFORE the tap (opacity) — the refusal of a fourth is visible,
                // never an error after the fact.
                if (blocked) return;
                var next = chosen
                  ? chosenIds.filter(function(id) { return id !== o.id; })
                  : chosenIds.concat([o.id]);
                onPickChange(groupIdSlug(group.name), next);
              }}
              style={{
                textAlign: 'left', padding: '10px 12px', borderRadius: '6px',
                cursor: blocked ? 'not-allowed' : 'pointer', width: '100%',
                minHeight: '44px', opacity: blocked ? 0.38 : 1,
                background: 'var(--doc-paper-raised)',
                border: '1px solid ' + (chosen ? 'var(--doc-accent)' : 'var(--doc-rule)'),
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* ROUND = choose one, SQUARE = choose several. The affordance carries the
                    arity before the bounds line is read. */}
                <span style={{ width: 14, height: 14, borderRadius: many ? '3px' : '50%', flexShrink: 0, border: '2px solid ' + (chosen ? 'var(--doc-accent)' : 'var(--doc-rule)'), background: chosen ? 'var(--doc-accent)' : 'transparent' }} />
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700, color: chosen ? 'var(--doc-accent)' : 'var(--doc-ink-muted)' }}>{o.name}</span>
              </div>
              {(function() {
                // R-SDL-2, rendered in the slot this row ALREADY reserved for `o.description`
                // (`fs-xs / 0.5 / marginLeft 22px`) and with the same raw-string treatment it
                // always used — so an official option's page is pixel-identical before and after,
                // and the derived text joins an existing look rather than inventing one.
                if (many) return null;   // multi: the consequence lives in the readout below
                var sentence = optionSentence(group, o);
                return sentence ? (
                  <div data-opt-desc={o.id || ''} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', lineHeight: 1.4, color: 'var(--doc-ink-muted)', marginTop: '4px', marginLeft: '22px' }}>{sentence}</div>
                ) : null;
              })()}
            </button>
          );
        })}
      </div>
      {many && renderReadout(group, chosenIds, bounds, accent, skillNames)}
    </div>
  );
}

// ── SPECIES ASKS (species-ask-mount; ruling `species-surface-concept` §5 + `species-ask-mount-apply-path`
//    + `species-ask-variant-reseed`) ──────────────────────────────────────────────────────────────
// A loaded character can carry an UNANSWERED species option group — a legacy save from before the
// group existed, or an import that added one. The book is offering that character something the app
// never asked. This is the ask: one slim line per unanswered group, mounted under the sheet header,
// above page content, on EVERY page (the offer belongs to the character, not a tab), ABSENT in table
// mode (frame E — an offer to spend a creation choice is not a table-facing control).
//
// It is an OFFER, never a debt: the character is already legal and playable, the line never turns red,
// NOT NOW costs nothing — but it never goes silent (Jason's never-invisible law).
//
// Two classes, split by the shared predicate `speciesUnansweredGroups` (R-SAM-8) so the surface and the
// C-SAVR-1 guard read ONE truth:
//   · ANSWERABLE (183 on the shelf, all pick-one) — expands to the creator's OWN chip control
//     (FCSpeciesPickGroup, §5, `bare`) + CONFIRM/NOT NOW. CONFIRM persists through
//     `CharResolve.speciesAskPatch`: the pick + the merged skill grant + the XP delta, and NOTHING
//     baked-at-creation (C-SAVR-2). Abilities/talents/characteristics then re-resolve LIVE — the one
//     place in this product where watching the effect land is the whole point (frame B).
//   · DEFERRED (24 on the shelf) — a second-question grant or a core-stat rewrite the sheet cannot
//     safely answer; the line stays present + named, carries an IN CREATOR pill, and ROUTES to the
//     creator (frame H). Never hidden, never dismissible.
function speciesAskTeaching(oc) {
  var max = (oc && oc.max_picks != null) ? oc.max_picks : 1;
  var min = (oc && oc.min_picks != null) ? oc.min_picks : 1;
  if (max > 1) return min > 0 ? ('choose ' + min + '–' + max) : ('choose up to ' + max);
  return 'choose one';
}
function speciesAskDeferReason(oc) {
  // The reason is read from the SAME predicate cause the deferral used — never a fixed string, so it
  // is honest for whichever class routed the group here.
  // The species' career pick (the Cerean's Intellectuals, card `f100-cerean-intellectuals` ADDENDUM 4 C1 / D-259): the ONE
  // rule sentence, the creator's own teach line — the route under it already says where the pick is made.
  if (oc && oc.career_pick) return fcCareerPickTeach(oc) + '.';
  var opts = (oc && oc.options) || [];
  if (opts.some(function (o) { return o && o.skill_choice_grants; })) {
    return 'One option lets you pick any skill of your own — a choice answered in the character creator, where that skill-picker lives.';
  }
  return 'This choice sets your core characteristics or thresholds, which the character creator applies so any experience you have already spent stays accounted for.';
}
function SpeciesAskSurface({ char, sheet, onSheetChange, onEditInCreator }) {
  var _open = useState(null), openSlug = _open[0], setOpenSlug = _open[1];
  var _staged = useState({}), staged = _staged[0], setStaged = _staged[1];
  var _dismissed = useState({}), dismissed = _dismissed[0], setDismissed = _dismissed[1];
  // This surface reads the canonical charbuild GLOBAL (`_canonicalCharbuild`), not props — and that
  // view lands a beat AFTER the registries on a cold load. A first paint before it arrives returns
  // null and would never recover, because no prop change follows to re-run the component. So poll the
  // actual dependency and force one re-render the moment it is ready; on a warm sheet this is a no-op.
  var _cb = useState(!!(typeof window !== 'undefined' && (window._canonicalCharbuild || window._canonicalCharbuildBase)));
  var cbReady = _cb[0], setCbReady = _cb[1];
  useEffect(function() {
    if (cbReady) return undefined;
    if (window._canonicalCharbuild || window._canonicalCharbuildBase) { setCbReady(true); return undefined; }
    var iv = setInterval(function() {
      if (window._canonicalCharbuild || window._canonicalCharbuildBase) { setCbReady(true); clearInterval(iv); }
    }, 150);
    return function() { clearInterval(iv); };
  }, [cbReady]);

  var CR = (typeof window !== 'undefined') ? window.CharResolve : null;
  var cb = (typeof window !== 'undefined') ? (window._canonicalCharbuild || window._canonicalCharbuildBase) : null;
  if (!CR || !CR.speciesUnansweredGroups || !CR.speciesAskPatch || !cb || !cb.species || !sheet) return null;

  // Resolve the species record: id first (every creator-built character), then by name for a legacy
  // save that recorded only the species string. Unresolvable → render nothing (graceful).
  var rec = null;
  if (sheet.speciesId && cb.species[sheet.speciesId]) rec = cb.species[sheet.speciesId];
  else if (sheet.species) {
    var ids = Object.keys(cb.species);
    for (var i = 0; i < ids.length && !rec; i++) {
      var r = cb.species[ids[i]];
      if (r && String(r.name || '').toLowerCase() === String(sheet.species).toLowerCase()) rec = r;
    }
  }
  if (!rec) return null;

  // the saved career pick and the charbuild let the predicate defer the species' career pick until it is made (Q3 (b-ii))
  var split = CR.speciesUnansweredGroups(rec, sheet.speciesOptionPicks, sheet.speciesSkillChoices, cb);
  var answerable = split.answerable.filter(function (oc) { return !dismissed[groupIdSlug(oc.name)]; });
  var deferred = split.deferred;   // deferred lines are never dismissible — the entitlement stays owed
  if (!answerable.length && !deferred.length) return null;

  var spName = String(rec.name || sheet.species || 'SPECIES').toUpperCase();
  var skillNames = cb.skillNames || {};

  // ── B-656 · THE ASK ON PAPER — the lab frame `species_ask()` (lab/holocron/sheet_core.py :1865 @ lab
  //    83bf1f2d, Jason 2026-09-17 "Approved."; SHEET_SHAPE §11) ──────────────────────────────────────
  // THE SAME COMPONENT, a second FACE: every line above — the state, the deferral predicate, the writer,
  // the testids — is untouched, and the dark seam below is byte-identical for the legacy sheet. What the
  // frame changes is WHERE and IN WHAT CLASSES: the ask sits WITH THE IDENTITY inside `SheetSpine`'s
  // `.sb-instr` (an unanswered option is a fact about who the character is, and a notice goes where its
  // consequence lands), drawn in the board's `.sb-ask*` / `.sb-opt*` vocabulary, red because red is the
  // paper's PRESS-THIS ink. The dark face's `N UNANSWERED` pill is NOT on the frame: on paper each group
  // is its own block, so the count IS the blocks.
  // ONE sentence rule, not a third copy — the creator's own `optionSentence`, bound to this species
  // (B-656 lifted it out of `FCSpeciesPickGroup` into `speciesOptionSentencers` for exactly this).
  var sentenceOf = speciesOptionSentencers(rec, skillNames).optionSentence;
  var paperAnswerable = function (oc) {
    var slug = groupIdSlug(oc.name);
    var isOpen = openSlug === slug;
    var label = oc.display_name || oc.name || 'OPTION';
    var val = staged[slug];
    var many = (oc.max_picks != null ? oc.max_picks : 1) > 1;
    var chosen = Array.isArray(val) ? val : (val != null ? [val] : []);
    var canConfirm = chosen.length > 0;
    return h('div', { key: slug, className: 'sb-ask', 'data-state': isOpen ? 'open' : 'closed' },
      h('button', { type: 'button', className: 'sb-ask-h', 'aria-expanded': isOpen ? 'true' : 'false',
        'data-testid': 'species-ask-line', 'data-ask-slug': slug,
        onClick: function () { setOpenSlug(isOpen ? null : slug); } },
        h('span', { className: 'sb-ask-c' }, isOpen ? '▾' : '▸'),
        h('span', { className: 'sb-ask-t' }, h('b', null, spName + ' ASKS'), ' · ' + label + ' · ' + speciesAskTeaching(oc))
      ),
      isOpen ? h('div', { className: 'sb-ask-opts' },
        (oc.options || []).map(function (o, oi) {
          var on = chosen.indexOf(o.id) !== -1;
          var sentence = sentenceOf(oc, o);
          return h('button', { key: o.id || oi, type: 'button', className: 'sb-opt',
            role: many ? 'checkbox' : 'radio', 'aria-checked': on ? 'true' : 'false',
            'data-testid': 'species-ask-option', 'data-option-id': o.id || '',
            onClick: function () {
              var ns = Object.assign({}, staged);
              ns[slug] = many ? (on ? chosen.filter(function (x) { return x !== o.id; }) : chosen.concat([o.id])) : o.id;
              setStaged(ns);
            } },
            h('span', { className: 'sb-opt-pip', 'data-many': many ? '1' : '0' }),
            h('span', { className: 'sb-opt-b' },
              h('span', { className: 'sb-opt-n' }, o.name || o.id),
              sentence ? h('span', { className: 'sb-opt-t' }, sentence) : null)
          );
        })
      ) : null,
      isOpen ? h('div', { className: 'sb-pk-verbs', style: { marginTop: 10 } },
        h('button', { type: 'button', className: 'hp-commit', disabled: !canConfirm, 'data-testid': 'species-ask-confirm',
          onClick: function () {
            if (!canConfirm) return;
            var patch = CR.speciesAskPatch(sheet, rec, slug, val, cb.skillSheetKeys);
            onSheetChange(patch);
            var ns = Object.assign({}, staged); delete ns[slug]; setStaged(ns);
            setOpenSlug(null);
          } }, 'Confirm'),
        h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'species-ask-notnow',
          onClick: function () { var nd = Object.assign({}, dismissed); nd[slug] = true; setDismissed(nd); setOpenSlug(null); } }, 'Not now')
      ) : null
    );
  };
  var paperDeferred = function (oc) {
    var slug = groupIdSlug(oc.name);
    var label = oc.display_name || oc.name || 'OPTION';
    // never dismissible — the entitlement stays owed; the head is a DIV, not a button (nothing opens)
    return h('div', { key: 'd_' + slug, className: 'sb-ask', 'data-state': 'deferred' },
      h('div', { className: 'sb-ask-h', 'data-testid': 'species-ask-deferred', 'data-ask-slug': slug },
        h('span', { className: 'sb-ask-c' }, '↗'),
        h('span', { className: 'sb-ask-t' }, h('b', null, spName + ' ASKS'), ' · ' + label),
        h('span', { className: 'sb-refuse' }, 'IN CREATOR')
      ),
      h('p', { className: 'sb-e-t' }, speciesAskDeferReason(oc)),
      h('div', { className: 'sb-pk-verbs', style: { marginTop: 8 } },
        h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'species-ask-route',
          // the career pick is made on the SPECIES step: the route lands there (every other class keeps Review, B-1384)
          onClick: function () { if (onEditInCreator) onEditInCreator(char, sheet, oc.career_pick ? { step: 'species' } : undefined); } }, 'Open character creator →'))
    );
  };
  // The frame composes the blocks as DIRECT children of `.sb-instr` and has no wrapper; the testid
  // contract (and `smoke_species_ask`'s container read on the dark face) needs one element to BE the
  // surface. `.sb-asks` is `display:contents` in sheet.css, so the blocks are still `.sb-instr`'s own
  // flex children at the frame's 14px gap — the geometry is the frame's, the testid survives.
  return h('div', { 'data-testid': 'species-ask-surface', className: 'sb-asks' },
    answerable.map(paperAnswerable), deferred.map(paperDeferred));
}


function FCSpeciesDetail({ species, accent, skillNames, talentDb, onConfirm, onBack, picks, onPickChange }) {
  if (!species) return null;
  var img = speciesImgPath(species);
  // D2b: the band drives ARRANGEMENT only. Same tree, same children, same order at both bands —
  // AS-8/A4 forbid a mode flip from remounting a subtree, because a resize that drops a half-typed
  // note or a made pick-one choice is a data-loss bug wearing a layout costume. The reflow below is
  // CSS Grid placement, so it reorders NOTHING and remounts NOTHING.
  //
  // BUT THAT IS A CLAIM ABOUT THE REFLOW, NOT ABOUT THE STATE, and the two must not be blurred.
  // A band flip changes the MOUNT PATH on the phone side, so this component does not survive it —
  // no amount of care in the grid can preserve a `useState` through that.
  //
  // B-124 — CLOSED. `optionPicks` no longer lives here: it lives in the creator's DRAFT, keyed by
  // G8's stable group slug, so it outlives this component exactly as every other step choice does.
  // `descExpanded` REMAINS component state and still dies on a flip — deliberately: re-expanding a
  // read-more is a tap, not a lost decision, and it gates nothing. The distinction is the point of
  // the fix; the pick gated SELECT THIS SPECIES and stranded the player, the disclosure does not.
  // Guarded by `smoke_d2b_species_dossier`'s two AS-8 legs, which were PUBLISHED RED and went green
  // untouched (the D1 idiom — the assert stated correct behaviour and waited).
  var _dossierDesk = useLayoutMode() === 'desktop';

  // Match the FCSpecDetail read-more pattern (line ~2670): collapse to a lead
  // paragraph, expand on tap. Replaces the 180px inner-scroll box that buried
  // the rest of long species backgrounds (Wookiee, Twi'lek etc.) on mobile.
  var DESC_COLLAPSED_LEN = 280;
  var descText = (species && species.description ? species.description : '').trim();
  var _useDE = useState(false), descExpanded = _useDE[0], setDescExpanded = _useDE[1];
  // B-19: option_choices are a PICK-ONE ("Corellians begin with one rank in Piloting Planetary OR
  // Space" — Suns of Fortune p.94), not a grant-all.
  //
  // B-124 — THE PICK LIVES IN THE DRAFT NOW, not in this component. It was `useState({})` here, and
  // the phone path does not keep this component mounted, so crossing the 1024 seam DESTROYED the
  // choice and silently re-locked SELECT THIS SPECIES — stranding a player on a screen they had
  // already satisfied. It is the one step choice that did not live where every other step choice
  // lives, which is exactly why it was the only one that did not survive a flip.
  // KEYED BY G8's STABLE SLUG (group-name → chosen option ID), the same vocabulary the SHEET
  // persists as `speciesOptionPicks` and `CharResolve._groupId` reads back. Draft and sheet now
  // speak one language, so no translation layer exists to drift. Reorder-proof by construction:
  // an index key would have moved if a group were ever re-ordered in the registry.
  var optionPicks = picks || {};
  var descIsLong = descText.length > DESC_COLLAPSED_LEN;
  var descShown = (descExpanded || !descIsLong)
    ? descText
    : (descText.slice(0, DESC_COLLAPSED_LEN).replace(/\s+\S*$/, '') + '\u2026');
  // Hide the SELECT bar until the reader scrolls to the bottom of the page (so it doesn't
  // imply "nothing more below"); latches so Read More expansion doesn't move the trigger.
  var rootRef = useRef(null);
  var ctaRevealed = useRevealNearBottom(rootRef, 90);

  // Render rows from the canonical structured sub-shapes (the legacy normalizer flattened
  // these into one abilities list; canonical keeps skill grants / talent grants / pick-one
  // option choices / special abilities distinct).
  // ── R-SDL-3 — ONE RESOLVER, ONE KEY-BRIDGE (ruling `species-detail-skill-legibility` §3) ──────
  // Both row types below fetch their sentence through here, and here is a thin call to
  // `skillDescFor` — the shipped wrapper around `CharResolve.skillDescription`, which is what the
  // creator's pickers, the sheet and the dice modal already use. A second lookup path, or a
  // prop-drilled copy of the vocab text, is a defect on its face (C-SDS-1).
  //
  // THE BRIDGE IS THE WHOLE TRAP, and the build card named it in advance: the vocab map is keyed by
  // the LEGACY SHEET KEY, while a species record names its skills by CANONICAL ID. Look it up by
  // canonical id and you get `''` — no error, no empty state, just a row that quietly renders no
  // sentence on the exact screen this lap exists to fix. Hence the mutant the gate owes.
  var abilityRows = [];
  (species.starting_skills || []).forEach(function(g) {
    abilityRows.push({
      type: 'FREE SKILL',
      label: (skillNames && skillNames[g.skill] || g.skill) + ' (Rank ' + g.rank_start + ')',
      // R-SDL-1: the row joins the idiom its siblings already use. FREE TALENT and ABILITY rows on
      // this same page carry their prose in this treatment; FREE SKILL was the ONE prose-less row
      // type, so a dossier that explains its talents and abilities went silent on its skills.
      // 319 rows across 301 records gain a sentence; none of them is authored here.
      desc: skillSentence(g.skill),
    });
  });
  (species.starting_talents || []).forEach(function(g) {
    var tl = talentDb && talentDb[g.talent];
    abilityRows.push({ type: 'FREE TALENT', label: (tl ? tl.name : g.talent) + (g.ranks > 1 ? ' (' + g.ranks + ')' : ''), desc: tl ? tl.description : undefined });
  });
  (species.special_abilities || []).forEach(function(sa) {
    abilityRows.push({ type: 'ABILITY', label: sa.name, desc: sa.description });
  });
  // B-19: option_choices render as a PICK-ONE group below (not flattened into abilities) — see the
  // CHOOSE-ONE section. Only the chosen option's skill grant applies at confirm.
  if (species.force_rating) {
    abilityRows.push({ type: 'ABILITY', label: 'Force Rating ' + species.force_rating });
  }
  var optionGroups = species.option_choices || [];
  // B-19: gate SELECT only on groups that actually grant a skill (the 28-species class). The ~6
  // non-skill option groups render as a visible pick-one too, but don't block the CTA (nothing
  // mechanical rides on them — matches the ruling's "6 non-skill groups unaffected").
  var allOptionsPicked = optionGroups.every(function(g) {
    var grantsSkill = (g.options || []).some(function(o) { return (o.skill_grants || []).length > 0; });
    // LAP B: a group whose MINIMUM is zero never gates the CTA, even if some option grants a skill.
    // Droid's menu is "choose 0-3" — the book OFFERS these, it does not require them, and locking
    // SELECT until a player takes one would invent a requirement the page explicitly denies.
    if (boundsOf(g).min === 0) return true;
    return !grantsSkill || pickedIdsFor(g, optionPicks).length > 0;
  });

  // ── FORK B, C-OCS-5 ────────────────────────────────────────────────────────────────────────
  // The dossier prints the RESOLVED species, not the record. Four sites below read these: the six
  // characteristic tiles and the wound / strain / XP tiles. They were classified in the ruled
  // consumer census as "the CUSTOM-species editor, NOT a variant site" — a misread. There is no
  // user-defined species editor in this file; `p-custom-species-*` names the CUSTOM CREATOR's
  // species step, and this is `FCSpeciesDetail`, the dossier the approved mockup draws.
  var eff = speciesEff(species, optionPicks);
  // The chosen options' XP deltas, summed across every group (Q2: deltas sum) — READ FROM THE CORE.
  // This was a local reduce over `speciesChosen`, a second copy of `CharResolve.speciesOptionXpDelta`
  // (the class the exception census names), and the two disagreed on an untouched preselect
  // species: the core resolves the group's first option, the copy did not, so this tile printed
  // Vaathkree at 80 where confirm records the Bulky form and the character gets 95. One writer.
  var optionXpDelta = (window.CharResolve && window.CharResolve.speciesOptionXpDelta)
    ? (Number(window.CharResolve.speciesOptionXpDelta(species, optionPicks)) || 0) : 0;

  // THE WIPE (Jason 2026-08-11, approved mockup frame A). Hero polish in the first cut.
  //
  // KEY-REMOUNT, not a class toggle, and the first attempt taught me why. Comparing against a ref
  // updated in an effect applied the class for exactly one render and removed it on the state update
  // that followed, both before paint — so the class was measurably absent 40ms later and the
  // animation never ran. Keying a tile on its own VALUE remounts only the tiles whose value moved,
  // which is precisely the rule the mockup states ("a tile whose value did not change does not
  // wipe"), and a CSS animation runs on mount. Unchanged tiles keep their key, so they never
  // re-animate no matter how often the card re-renders.
  //
  // ARMING keeps the first paint still: opening a dossier should not wipe nine tiles. Done by
  // comparing the species id at RENDER rather than in effects — my first attempt armed in one effect
  // and disarmed in another keyed on the species, and both ran on mount in order, so it ended
  // disarmed forever and nothing ever animated. The render-time compare has no ordering to get
  // wrong: the first render for a species is unarmed, every later render for the SAME species is
  // armed, and only tiles whose value changed remount and animate.
  // The class is applied PER TILE and only where that tile's value moved, because adding the class
  // is itself what starts the animation — arming it card-wide made all nine wipe on open. The
  // previous resolution is kept in a ref updated during RENDER, so it is not cleared by a following
  // state update the way an effect-updated one was. The key also carries the value, so a changed
  // tile remounts and restarts cleanly rather than relying on the class edge alone.
  var _wipePrev = useRef(null);
  var _wipeSameSpecies = (_wipePrev.current && _wipePrev.current.id) === (species && species.id);
  var _prevEff = _wipeSameSpecies ? _wipePrev.current.eff : null;   // null on the first paint
  _wipePrev.current = { id: species && species.id, eff: eff };
  function _moved(key, val) { return !!_prevEff && _prevEff[key] !== val; }
  function _movedChar(attr, val) { return !!_prevEff && _prevEff.characteristics[attr] !== val; }

  // WHAT IS TICKED IS WHAT IS PRINTED (ruling `csg-v7-option-characteristics-schema` §4 correction B).
  // A `preselect` group resolves to its first option when nothing is recorded, so the chip has to
  // agree or the card shows one variant's numbers with no variant selected.
  // LAP B — a group's bounds. ABSENT DEFAULTS TO 1/1: every group that predates the keys is a
  // pick-exactly-one, so absence must keep meaning that (ruling
  // `species-option-menu-multiselect-save-shape` Q1).
  // The chosen ids as an ARRAY, through the shared normalizer — a stored scalar reads as a
  // one-element list and no saved character is rewritten. `speciesChosen` is charResolve's
  // `_chosenOptions`; this app must not grow a second scalar-or-array branch (B-124).
  // A variant group states an axis (Sex, Subspecies, Caste); a skill group grants a rank and gates
  // the CTA. `preselect` is the authored discriminator — derived was refused because H'nemthe's sex
  // group grants no skill either (parent ruling §7).
  var variantGroups = optionGroups.filter(function(g) { return !!g.preselect; });
  var choiceGroups = optionGroups.filter(function(g) { return !g.preselect; });

  // ── R-SDL-2 — WHAT AN OPTION SAYS ABOUT ITSELF, in precedence order ──────────────────────────
  // (1) its own authored `description` — all 69 official options, ZERO visual change to any
  //     official species' choice. Returned as the raw string the row already rendered, so the
  //     before/after byte-identity the gate asserts is a property of the code, not of a diff.
  // (2) otherwise the granted skill's sentence, for each skill in `skill_grants` — the guide's 293.
  //     "Brawl or Survival" stops being two bare words at the moment a novice has to choose.
  // (3) otherwise THE DELTA RULE (amendment, 2026-08-12, on Jason's mockup review): the resolved
  //     values this option CHANGES and only those. Nothing is authored — the difference between
  //     Arkanian Baseline and Offshoot was already structured data, and the original scope line
  //     walled it off at the reader's expense.
  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out' }}>
      {/* ── D2b: THE DOSSIER GRID ────────────────────────────────────────────────────────────────
          THREE GROUPS, ALWAYS RENDERED IN THIS ORDER: (A) the portrait header, (B) the mechanical
          facts, (C) the prose. On MOBILE the grid is one column, so A→B→C stacks in exactly the
          order that shipped — this lap changes no phone pixel. On DESKTOP the same three children
          are PLACED: A spans the full width, C takes column 1 at the reading measure, B takes
          column 2 as the pinned companion.

          GRID, SPECIFICALLY, BECAUSE PLACEMENT IS A STYLE. A flex reflow would need the DOM order
          to change to put prose before facts; `gridColumn`/`gridRow` move them without touching the
          tree, which is what AS-8 requires and what keeps the mobile order identical for free.

          A SPANS RATHER THAN SITTING IN THE COMPANION, and the reason is the BACK control: it is
          absolutely positioned inside the portrait, so putting the portrait in the right-hand
          column would land "back" at the top-RIGHT of the screen. A back control belongs top-left.
          The ruling lists the portrait among the blocks that take the freed width; spanning gives
          it that width without moving the one control whose position carries meaning. */}
      <div data-species-dossier={_dossierDesk ? '1' : '0'} style={{
        display: 'grid',
        gridTemplateColumns: _dossierDesk
          ? 'minmax(0, var(--desk-prose-max)) minmax(0, 1fr)'
          : '1fr',
        columnGap: _dossierDesk ? 'var(--desk-gutter)' : 0,
        alignItems: 'start',
        width: '100%',
        maxWidth: _dossierDesk ? FC_SPECIES_DOSSIER_MAX : 'none',
        margin: '0 auto',
      }}>
      {/* Portrait — compact header in commercial mode */}
      <div style={{
        gridColumn: _dossierDesk ? '1 / -1' : 'auto',
        height: COMMERCIAL_MODE ? 'auto' : '280px', position: 'relative', overflow: 'hidden',
        backgroundImage: (!COMMERCIAL_MODE && img) ? 'url(' + img + ')' : 'none',
        backgroundSize: 'contain', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat',
        backgroundColor: COMMERCIAL_MODE ? 'transparent' : 'var(--doc-mat)',
        padding: COMMERCIAL_MODE ? '16px 16px 0' : 0,
      }}>
        {/* A removed portrait (see speciesImgPath) gets the DESIGNED placeholder centred in the
            280px header, not an empty panel — the sheet's own portrait-less idiom. */}
        {!COMMERCIAL_MODE && !img && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {window.SWRPGUI.Monogram({ name: species.name, accent: accent, size: 132 })}
          </div>
        )}
        {!COMMERCIAL_MODE && <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.85) 100%)',
        }} />}
        <div style={COMMERCIAL_MODE ? {} : { position: 'absolute', bottom: '12px', left: '16px', right: '16px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-xl)', color: 'var(--doc-ink)', letterSpacing: '0.04em' }}>{species.name}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--doc-ink-muted)', letterSpacing: '0.14em', marginTop: '4px' }}>
            {/* B-336 species wave: a key prints its title */}
            {(species.sources && species.sources[0] && gameLineBookLabel(species.sources[0])) || 'CORE RULEBOOK'}
          </div>
        </div>
        {/* Back button — absolute over portrait, or inline in commercial mode.
            C7: was 36x36 with NO accessible name — an SVG chevron in a button with no text and no
            aria-label, so a screen reader announced an empty button and a thumb got a target 8px
            under AS-6's floor. Both defects are PRE-EXISTING and neither was this lap's doing; the
            split merely put the detail on screen at a desktop width, where AS-9's sweep measured it
            for the first time. Worth stating plainly: the miss mattered most on PHONES, which is
            where this control has always lived, and the guard could not see it there because it
            measures each step's default view and the detail is a sub-view. 44x44 + a real name. */}
        <button onClick={onBack} aria-label="Back to the species list" style={{
          position: COMMERCIAL_MODE ? 'relative' : 'absolute',
          top: COMMERCIAL_MODE ? 0 : '8px', left: COMMERCIAL_MODE ? 0 : '8px',
          background: COMMERCIAL_MODE ? 'none' : 'rgba(0,0,0,0.5)',
          border: COMMERCIAL_MODE ? 'none' : 'none',
          borderRadius: COMMERCIAL_MODE ? 0 : '50%',
          width: COMMERCIAL_MODE ? 'auto' : '44px', height: COMMERCIAL_MODE ? 'auto' : '44px',
          display: 'flex', alignItems: 'center', justifyContent: COMMERCIAL_MODE ? 'flex-start' : 'center',
          cursor: 'pointer', gap: '6px',
          padding: COMMERCIAL_MODE ? '0 0 8px' : 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
            <polyline points="13,4 7,10 13,16" stroke="var(--doc-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {COMMERCIAL_MODE && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'var(--doc-accent)' }}>BACK</span>}
        </button>
      </div>

      {/* ── (B) THE MECHANICAL FACTS — the pinned companion at desktop, column 2.
             Characteristics, thresholds and the ability grants: what the player is actually
             choosing between. CHOOSE ONE rides with them and that placement is called out in the
             gate — the ruling enumerated four blocks for the freed width and did not name it, but
             option_choices ARE the pick-one form of the same starting-skill grant the abilities
             list prints above them as FREE SKILL rows. Splitting one mechanical family across two
             columns to honour a list literally would serve the list, not the reader.
             Padding-bottom is 0 on mobile so (C) supplies the 16px and the stacked rhythm is the
             one that shipped. */}
      <div style={{
        gridColumn: _dossierDesk ? 2 : 'auto',
        gridRow: _dossierDesk ? 2 : 'auto',
        padding: _dossierDesk ? '16px 16px 24px' : '16px 16px 0',
        display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0,
      }}>
        {/* FORK B: the variant block sits at the TOP of the mechanical column, above
            CHARACTERISTICS, because it changes the numbers printed below it. Same control as the
            pick-one below, one component (C-CSG7-3); only the heading, the teaching line and the
            gating differ. */}
        {variantGroups.length > 0 && (
          <div data-fc-variant-block="1">
            {variantGroups.map(function(g, gi) {
              return <FCSpeciesPickGroup key={'v' + gi} group={g} gi={'v' + gi}
                teach={'Pick one; you can change it until you select this species.'}
                species={species} optionPicks={optionPicks} skillNames={skillNames}
                accent={accent} onPickChange={onPickChange} />;
            })}
          </div>
        )}

        {/* Characteristics grid */}
        {/* SUMMARY FIRST (Jason 2026-08-13). The species' own sentence leads the card: it is
            what a player came to read, and it is the only block that says what this species IS
            rather than what it costs. It moved OUT of the prose column for that — three long
            ability paragraphs used to sit between the numbers and the choice, pushing
            MODIFICATIONS most of a screen down on a phone. Order is now: summary ->
            characteristics -> the choice -> abilities -> the rest of the prose. */}
        {descText && (
          <div>
            <div style={fcSectionHeaderStyle(accent)}>SUMMARY</div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6,
              color: 'var(--doc-ink-muted)', whiteSpace: 'pre-line',
            }}>{descShown}</div>
            {descIsLong && (
              <button type="button" onClick={function() { setDescExpanded(!descExpanded); }}
                aria-expanded={descExpanded}
                style={{
                  marginTop: '8px', padding: '0 16px', minHeight: '44px', minWidth: '44px',
                  background: 'none',
                  border: '1px solid var(--doc-rule)',
                  borderRadius: '16px',
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.18em',
                  color: 'var(--doc-accent)', cursor: 'pointer',
                }}>
                {descExpanded ? '\u25B2 LESS' : '\u25BC READ MORE'}
              </button>
            )}
          </div>
        )}
        <div data-tour-id="p-custom-species-chars">
          <div style={fcSectionHeaderStyle(accent)}>CHARACTERISTICS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
            {['brawn','agility','intellect','cunning','willpower','presence'].map(function(attr) {
              var val = eff.characteristics[attr];   // FORK B: the resolved variant, not the record
              return (
                <div key={attr + ':' + val} data-tour-id={'p-custom-species-char-' + attr}
                  className={_movedChar(attr, val) ? 'fc-wipe' : undefined}
                  style={{
                  background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
                  borderRadius: '6px', padding: '8px 6px', textAlign: 'center',
                  '--fc-wipe-i': ['brawn','agility','intellect','cunning','willpower','presence'].indexOf(attr),
                }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xl)', color: 'var(--doc-ink)' }}>{val}</div>
                  <div style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'var(--doc-ink)', marginTop: '3px', textTransform: 'uppercase' }}>{attr}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Thresholds + XP row */}
        <div data-tour-id="p-custom-species-health" style={{ display: 'flex', gap: '8px' }}>
          {[
            { label: 'WOUND', value: eff.wound_threshold_base, color: 'var(--doc-ink)', field: 'wound_threshold_base', i: 6 },
            { label: 'STRAIN', value: eff.strain_threshold_base, color: 'var(--doc-ink)', field: 'strain_threshold_base', i: 7 },
            // B-175(i) — THE XP TILE PREVIEWS THE DELTA, which it never has. `speciesEffective`
            // returns the ABSOLUTE and deliberately never applies `xp_adjustment` (that rides
            // `character.speciesOption.xp` at the call site, B-22), so the dossier printed a base
            // that contradicted the option the player had just taken. Summed HERE, at the one place
            // that displays it, so the two paths still have exactly one writer each: the resolver
            // owns the absolute, the creator owns the delta, and this reads both to print the truth.
            { label: 'XP', value: eff.starting_xp + optionXpDelta, color: 'var(--text-success)', field: 'starting_xp', i: 8 },
          ].map(function(item) {
            return (
              <div key={item.label + ':' + item.value}
                className={_moved(item.field, item.value) ? 'fc-wipe' : undefined}
                style={{ '--fc-wipe-i': item.i,
                flex: 1, background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
                borderRadius: '6px', padding: '10px 8px', textAlign: 'center',
              }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: item.color }}>{item.value}</div>
                <div style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'var(--doc-ink)', marginTop: '3px', textTransform: 'uppercase' }}>{item.label}</div>
              </div>
            );
          })}
        </div>

        {/* Abilities */}
        {choiceGroups.length > 0 && (
          <div data-fc-choice-block="1">
            {choiceGroups.map(function(group, gi) {
              var grantsSkill = (group.options || []).some(function(o) { return (o.skill_grants || []).length > 0; });
              // The gating group borrows the CTA's own words, so the control and the button that
              // blocks on it say the same thing (C-INERT-4 / §6 shape B, Jason-approved).
              var gb = boundsOf(group);
              // LAP B: a multi-pick group cannot borrow the pick-one teaching line. The mockup's
              // copy says the arity and says the zero case is legal, which is the whole point of
              // a 0-minimum group.
              var teach = gb.max > 1
                ? (gb.min > 0
                    ? 'Pick ' + gb.min + ' to ' + gb.max + '. You can change your mind until you select this species.'
                    : 'Pick none, or up to ' + gb.max + '. You can change your mind until you select this species.')
                : (grantsSkill
                    ? 'Choose one to continue. That skill starts with one free rank.'
                    : 'Choose one.');
              return <FCSpeciesPickGroup key={gi} group={group} gi={gi} teach={teach}
                species={species} optionPicks={optionPicks} skillNames={skillNames}
                accent={accent} onPickChange={onPickChange} />;
            })}
          </div>
        )}
        {abilityRows.length > 0 && (
          <div>
            <div style={fcSectionHeaderStyle(accent)}>SPECIAL ABILITIES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {abilityRows.map(function(row, i) {
                return (
                  <div key={i} style={{
                    background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
                    borderRadius: '6px', padding: '10px 12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: row.desc ? '4px' : 0 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em', color: 'var(--doc-accent)', opacity: 0.5, flexShrink: 0 }}>{row.type}</span>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--doc-ink)' }}>{row.label}</span>
                    </div>
                    {row.desc && <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--doc-ink-muted)' }}>
                      {glyphs(cleanMarkup(row.desc))}
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* B-19: pick-one option choices (Corellian Piloting Planetary OR Space, etc.). Only the
            chosen option's skill grant applies — SELECT THIS SPECIES is gated until each group is picked. */}

      </div>

      {/* ── (C) THE PROSE — column 1 at desktop, held at the reading measure.
             `--desk-prose-max` is the law D2 was most at risk of breaking and did not; this lap
             does not touch it either. The column is CAPPED at the measure by the grid track, so
             widening the pane can never widen the line — the freed width goes to (B) beside it,
             which is the whole point of the dossier pattern. */}
      <div style={{
        gridColumn: _dossierDesk ? 1 : 'auto',
        gridRow: _dossierDesk ? 2 : 'auto',
        padding: _dossierDesk ? '16px 16px 24px' : '0 16px 24px',
        display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0,
        // THE ELEMENT HOLDS ITS OWN MEASURE, not just the track it happens to sit in — found by a
        // mutant, not by reading. Swapping the two groups' columns left this group in the `1fr`
        // track and the prose measured 405px: the cap had been the TRACK all along, and the "prose
        // holds the measure" assert was passing on a property of the layout rather than of the
        // block. That is exactly C10's inert-cap shape. Same token as the track, so there is no
        // second source of truth — one name, read twice — and the measure now survives any future
        // change to the column sizing.
        maxWidth: _dossierDesk ? 'var(--desk-prose-max)' : 'none',
      }}>
        {/* AUTHORED LORE SECTIONS (ruling `f-eds-6-species-classification` §4, shape (c)).
           TOLERANT BY DESIGN: only species whose batch has landed carry these fields; every other
           record renders its `description` blob below exactly as before, so the eight batches can
           ship one at a time with no migration and no half-empty surface. */}
        {FC_SPECIES_SECTIONS.some(function(sc) { return species[sc.key]; }) && (
          <div style={{ marginBottom: '18px' }}>
            {FC_SPECIES_SECTIONS.map(function(sc) {
              var body = species[sc.key];
              if (!body) return null;
              return (
                <div key={sc.key} style={{ marginBottom: '14px' }}>
                  <div style={fcSectionHeaderStyle(accent)}>{sc.label}</div>
                  <div style={{
                    fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6,
                    color: 'var(--doc-ink-muted)', whiteSpace: 'pre-line',
                  }}>{body}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Background — read-more pattern matches FCSpecDetail. */}
      </div>
      </div>{/* ── end dossier grid ── */}

      <FCBottomAction label={allOptionsPicked ? "SELECT THIS SPECIES" : "PICK ONE SKILL TO CONTINUE"} disabled={!allOptionsPicked} onClick={function() { onConfirm(species, optionPicks); }} accent={accent} revealed={ctaRevealed} />
    </div>
  );
}

/* ── FC Career Browser ───────────────────────────────── */

// Per-book hue for career cards — EotE gold, AoR cyan, FaD violet.
// Same hex values used by QBGameLineStep so selection flows feel continuous.
var FC_BOOK_COLORS = {
  'Edge of the Empire': '#F5C842',
  'Age of Rebellion': '#00C8FF',
  'Force and Destiny': '#B080FF',
};
function FCCareerCard({ career, accent, selected, skillNames, onClick }) {
  var isSelected = selected && selected.id === career.id;
  var gameTags = { 'Edge of the Empire': 'EotE', 'Age of Rebellion': 'AoR', 'Force and Destiny': 'FaD' };
  var careerSource = gameLineTitle((career.sources && career.sources[0]) || '') || '';   // B-336 careers wave: a key resolves before the title match below
  var sourceTag = '';
  var bookHue = accent;
  Object.keys(gameTags).forEach(function(k) {
    if (careerSource.indexOf(k) >= 0) {
      sourceTag = gameTags[k];
      bookHue = 'var(--doc-accent)';   // R-CSC-3(b): EotE/AoR/FaD are one design system
    }
  });

  return (
    <button onClick={function() { onClick(career); }} style={Object.assign(
      categoryCardStyle(bookHue, isSelected ? 'active' : 'default'),
      {
        width: '100%',
        padding: '12px 14px', cursor: 'pointer', textAlign: 'left',
        transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
        boxShadow: isSelected ? '0 0 12px ' + bookHue + '22' : 'none',
      }
    )}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--doc-ink)', letterSpacing: '0.02em', flex: 1 }}>{career.name}</div>
        {sourceTag && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.14em', color: bookHue, padding: '3px 7px', border: '1px solid ' + bookHue + '55', borderRadius: '3px', fontWeight: 700 }}>{sourceTag}</span>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {career.career_skills.slice(0, 8).map(function(sk) {
          var name = (skillNames && skillNames[sk]) || sk;
          return <span key={sk} style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)', letterSpacing: '0.02em', color: 'var(--doc-ink)', padding: '3px 8px', background: 'var(--doc-paper-raised)', borderRadius: '10px' }}>{name}</span>;
        })}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--doc-ink-muted)', marginTop: '6px', letterSpacing: '0.14em' }}>
        {career.specializations.length} SPECIALIZATIONS
      </div>
    </button>
  );
}

// BOOKS-1c — the creator's ACTIVE SOURCES filter (F-3 in the creator; ruling books-1b-lap-plan-handoff).
// Character-defining content (species / careers / universal specs / force powers) is ERA-GATED: a
// record whose only active books are cores must have one of the selected era lines (isActiveWith's
// eraLines). Equipment (gear/weapons/armor) passes eraGated=false → the global OFF-set only, NOT the
// era set — equipment isn't era-bound (a droid tagged to one core is generic Star Wars kit). The
// creator gates its data load on SWRPGSources.ready(), so current() is present when pickers render;
// unfiltered only if the vocab failed to load (degrade-visible, never hide unasked).

function fcFilterActive(list, eraSet, eraGated) {
  var S = (typeof window !== 'undefined') && window.SWRPGSources;
  var cur = S && S.current();
  if (!cur || !cur.index) return list;
  var eras = eraGated ? (eraSet || []) : null;
  return list.filter(function(rec) { return S.isActiveWith(rec, cur.index, cur.off, eras); });
}

function FCCareerBrowser({ careers, selected, gameLine, skillNames, onSelect, accent }) {
  var _useS = useState(''), search = _useS[0], setSearch = _useS[1];

  // Sort: game-line-matching careers first.
  //
  // B-336 LOGIC SITE. This used to map the game line to a TITLE and substring-match the source:
  //   glMap = { eote: 'Edge of the Empire', ... };  src.indexOf(glStr) >= 0
  // It asks "is this career from my game line", which is a question the book VOCABULARY answers
  // outright — `line` is a column on the row. The title match was an indirection that happened to
  // work only while every registry stored display titles, and it would have failed SILENTLY under
  // B-336's key conversion: `'eote'.indexOf('Edge of the Empire')` is -1, so every career would
  // have sorted as "other" with no error and nothing visibly wrong.
  // Asking the vocabulary directly is both correct and form-agnostic — a title and its key resolve
  // to the same row — so this site stops caring which form the registry holds.
  var isMyLine = useCallback(function(c) {
    var S = window.SWRPGSources, cur = S && S.current();
    var idx = cur && cur.index;
    return !!(idx && S.isFromLine(idx, c, gameLine));
  }, [gameLine]);

  var sorted = useMemo(function() {
    var matching = [];
    var others = [];
    careers.forEach(function(c) { (isMyLine(c) ? matching : others).push(c); });
    // R-CPD-9's sweep, applied to a creator surface: the game-line GROUPING is the product decision
    // and stays, but WITHIN each group this preserved registry order — which is import-wave order.
    // `careers.json` breaks once (Warrior → Sith, the later book), so the rail printed Sith after
    // Warrior while the register beside it and `fcCareerRows` both read alphabetically. Same
    // comparator as R-CPD-4 and `fcCareerRows`, applied per group so the grouping survives.
    var byName = function(a, b) { return String(a && a.name).localeCompare(String(b && b.name)); };
    return matching.sort(byName).concat(others.sort(byName));
  }, [careers, gameLine, isMyLine]);

  var filtered = useMemo(function() {
    if (!search.trim()) return sorted;
    var q = search.toLowerCase().trim();
    return sorted.filter(function(c) { return c.name.toLowerCase().indexOf(q) >= 0; });
  }, [sorted, search]);

  // Find divider index — the SAME question as the sort above, so it uses the same predicate.
  // It was a second hand-written copy of the title match (B-336's LOGIC class, two of five).
  var dividerIdx = 0;
  filtered.forEach(function(c, i) { if (isMyLine(c)) dividerIdx = i + 1; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', animation: 'slideInRight 0.35s ease-out' }}>
      <FCSearchBar value={search} onChange={setSearch} accent={accent} placeholder="Search careers…" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 12px 24px' }}>
        {filtered.map(function(c, i) {
          return (
            <React.Fragment key={c.id}>
              {i === dividerIdx && dividerIdx > 0 && dividerIdx < filtered.length && !search.trim() && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'var(--doc-ink-muted)', padding: '14px 4px 6px', textAlign: 'center' }}>OTHER GAME LINES</div>
              )}
              <FCCareerCard career={c} accent={accent} selected={selected} skillNames={skillNames} onClick={onSelect} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ── FC Career Detail + Spec Picker ──────────────────── */

/* B-68 (lap C5): SPECIALIZATION IS ITS OWN STEP, so this component serves BOTH halves of what used
   to be one screen — `section='career'` renders the career's own case and confirms it;
   `section='specs'` renders only the choose-a-specialization surface, under the new step's own
   heading. One component, two sections, rather than a second screen that would drift from this one
   (R-CUI-2's habit, applied inside a component). Default 'all' preserves any other caller. */
function FCCareerDetail({ career, accent, skillNames, specsById, gameLine, currentSpecies, onSelectSpec, onBack, forceRating, section, onConfirmCareer }) {
  var _sec = section || 'all';
  var _showCareer = _sec === 'all' || _sec === 'career';
  var _showSpecs = _sec === 'all' || _sec === 'specs';
  if (!career) return null;

  // Shared spec-card renderer (career list + the T2-D universal shelf render identically).
  function specCard(spec) {
    if (!spec) return null;
    var skillCount = (spec.career_skills || []).length;
    var grantsFR = Number(spec.force_rating) >= 1;
    return (
      <button key={spec.id} onClick={function() { onSelectSpec(spec); }} style={{
        width: '100%',
        background: 'var(--doc-paper-raised)',
        border: '1px solid var(--doc-rule)',
        borderLeft: (spec.universal ? '4px' : '3px') + ' solid var(--doc-accent)',
        borderRadius: '0 6px 6px 0',
        padding: '14px', cursor: 'pointer', textAlign: 'left',
        transition: 'border-color 0.25s, background 0.25s, box-shadow 0.25s',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
      }}
      onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--doc-paper-raised)'; e.currentTarget.style.boxShadow = 'none'; }}
      onMouseLeave={function(e) { e.currentTarget.style.background = 'var(--doc-paper-raised)'; e.currentTarget.style.boxShadow = 'none'; }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--doc-ink)', letterSpacing: '0.02em' }}>{spec.name}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)', color: 'var(--doc-ink)', letterSpacing: '0.06em', marginTop: '3px' }}>
            {/* CR-RAW #3: they're CAREER skills (EotE p.35/p.102), not a vague "bonus" */}
            ADDS {skillCount} CAREER SKILL{skillCount !== 1 ? 'S' : ''}{spec.universal ? ' · UNIVERSAL' : ''}{grantsFR ? ' · FORCE RATING 1' : ''}
          </div>
          {/* B-41: shown on every carrier, met or not — the browsing player learns the axis exists. */}
          {minForceRatingOf(spec) > 0 && (
            <div style={{ marginTop: '3px' }}><ForceRatingReq rec={spec} forceRating={forceRating} /></div>
          )}
        </div>
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, opacity: 0.7 }}>
          <polyline points="7,4 13,10 7,16" stroke="var(--doc-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 24px' }}>
      {/* Career header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-lg)', color: 'var(--doc-ink)', letterSpacing: '0.04em', marginBottom: '4px' }}>{career.name}</div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--doc-ink-muted)', letterSpacing: '0.14em' }}>{gameLineTitle((career.sources && career.sources[0]) || '') || ''}</div>
      </div>

      {/* Career Skills */}
      <div style={{ marginBottom: '16px' }}>
        <div style={fcSectionHeaderStyle(accent)}>CAREER SKILLS</div>
        <div data-tour-id="p-custom-career-skills" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {career.career_skills.map(function(sk) {
            var name = (skillNames && skillNames[sk]) || sk;
            return <span key={sk} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--doc-ink-muted)', padding: '4px 8px', background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)', borderRadius: '4px' }}>{name}</span>;
          })}
        </div>
      </div>

      {/* Starting entitlements — F-EDS-6 careers lap. The Bounty Hunter's automatic IPKC was a real
         rule that lived ONLY in the registry's book prose, which this card never rendered, so it has
         never reached a player. Promoted to structured data (careers.json `starting_entitlements`)
         and given a line here: it informs the career choice and gates nothing. */}
      {(career.starting_entitlements || []).length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={fcSectionHeaderStyle(accent)}>STARTING ENTITLEMENT</div>
          {career.starting_entitlements.map(function(ent) {
            return (
              <div key={ent.id} style={{
                fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
                color: 'var(--doc-ink)', lineHeight: 1.5,
              }}>
                <span style={{ color: 'var(--doc-ink)', fontWeight: 700 }}>{ent.name}</span>
                {ent.note ? ' — ' + ent.note : ''}
              </div>
            );
          })}
        </div>
      )}

      {/* Description — CD-2: ambient accent tint so prose feels attached to
         the career identity rather than floating body text. Alpha 0.45 → 0.75
         for AA. Removed maxHeight scroll — let it grow, primary CTA is below.
         F-EDS-6: now reads the REGISTRY (the hardcoded override map that shadowed it is deleted).
         `.substring(0, 600)` dropped with it — it never fired (longest entry 463 chars) and a
         silent mid-sentence truncation is a worse failure than a long block on a surface people
         read to choose. The guard pins description length instead. */}
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6,
        color: 'var(--doc-ink)', marginBottom: '20px',
        padding: '12px 14px',
        background: 'var(--doc-paper-raised)',
        borderLeft: '2px solid var(--doc-rule)',
        borderRadius: '0 6px 6px 0',
      }}>
        {/* R3 Q2b: this line was an INLINE `\[.*?\]` catch-all — the fourth copy of the eater, not
            inside any function, sitting directly on the career card. careers.json carries 0 bracket
            tokens today (measured), so it ate nothing; it was a loaded gun aimed at the next lap
            that authors a career description with a dice symbol in it. Routed through the eater +
            the shared drawer like every other description surface. */}
        {glyphs(cleanMarkup(career.description || ''))}
      </div>

      {/* Specializations — CD-3: category-stripe card pattern matching other
         selector surfaces in the creator. Universal specs get a thicker
         stripe so they read as "works across all book contexts". */}
      {_showSpecs && (
      <div>
        <div style={fcSectionHeaderStyle(accent)}>CHOOSE YOUR SPECIALIZATION</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {career.specializations.map(function(specId) {
            return specCard((specsById && specsById[specId]) || null);
          })}
        </div>
      </div>
      )}

      {/* T2-D: universal specializations are takeable AT CREATION "just as any other
         specialization" (eote printed p.276) — Exile (eote) / Emergent (aor), matched to the
         character's line. Droids cannot be Force sensitive (eote printed p.275). */}
      {_showSpecs && (function() {
        if (!specsById || !gameLine) return null;
        // BOOKS-1c (campaign model): ACTIVE SOURCES (off-set) always; line-gating opt-in (default off).
        var _linePref = !!(window.SWRPGSources && window.SWRPGSources.lineFilterOn && window.SWRPGSources.lineFilterOn());
        var universals = fcFilterActive(
          Object.keys(specsById).map(function(id) { return specsById[id]; })
            .filter(function(s) { return s && s.universal && career.specializations.indexOf(s.id) === -1; }),
          [gameLine], _linePref)
          .sort(function(a, b) { return (a.name || '').localeCompare(b.name || ''); });
        if (!universals.length) return null;
        // B-388 — THE DROID RULE WITHHOLDS FOUR SPECS, NOT THE SHELF (R-WUS-A2-2).
        // This branch used to replace the ENTIRE shelf with one sentence, so a droid player lost
        // all seventeen universal specializations to a rule that closes exactly four. Pirate,
        // Recruit, Senator, Ship Captain and the rest have nothing to do with the Force and were
        // never closed to a droid; RAW closes FORCE SENSITIVITY (eote printed p.275), not
        // "specializations from outside your career".
        //
        // The predicate is `grantsFR` — the registry's `force_rating` FIELD, the same one the
        // spec card's own mark is computed from a few lines up (:3871) and the same distinction
        // the board settled for the marks: the FIELD means the spec hands you a rating, the TREE
        // means you may buy one inside it, and 42 specs have a Force Rating talent in-tree that
        // grants nothing on taking.
        //
        // RELABEL, NEVER SILENCE (R-CSC-1): a withheld spec keeps its place and its name and
        // states its reason, because a choice you cannot make is information and a list that
        // hides it teaches nothing. Retired Clone Trooper is NOT a Force spec and stays takeable
        // here; its own species requirement answers for it when opened.
        //
        // The rebuilt desktop shelf (`fcSpecUniversalRows`) took this shape from the start; this
        // is the LEGACY phone shelf catching up, which is why both are fixed in one increment.
        var isDroid = !!(currentSpecies && CharResolve.isDroidSpecies(currentSpecies.id));   // B-1606: the one droid test
        return (
          <div style={{ marginTop: '16px' }}>
            <div style={fcSectionHeaderStyle(accent)}>UNIVERSAL — OPEN TO ANY CAREER</div>
            {/* SAME SHAPE AS THE REBUILT SHELF (R-WUS-A2-2, and the lab's frame settles it): the
                four leave the LIST and are named in a plate beneath it. My first cut refused them
                in place; the board draws a `Withheld · N` plate, and on this card the frames win
                over prose. Still relabel-never-silence — a droid player reads which four and why,
                instead of finding a shorter list with no account of what left it. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {universals.filter(function(u) { return !(isDroid && Number(u.force_rating) >= 1); })
                         .map(specCard)}
            </div>
            {(function() {
              var held = universals.filter(function(u) { return isDroid && Number(u.force_rating) >= 1; });
              if (!held.length) return null;
              return (
                <div data-spec-withheld={String(held.length)} style={{ marginTop: '11px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)', lineHeight: 1.5, padding: '10px 12px', background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)', borderRadius: '6px' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em', color: 'var(--doc-accent)', marginBottom: '6px' }}>
                    {'WITHHELD \u00b7 ' + held.length}
                  </div>
                  <div>Droids cannot be Force sensitive (eote p.275).</div>
                  <div style={{ marginTop: '7px', color: 'var(--doc-ink)' }}>
                    {held.map(function(u) { return u.name; }).join(', ')}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}
      {/* WHAT THIS CAREER OPENS — names, not a picker (C8; ruling `creator-career-drawing-vs-c5`
          Q1, option (c) adopted alongside (a)).

          The career step's drawing wanted a selectable specialization list here with one button
          committing both halves. C5 dismantled exactly that, and the split STANDS. But the
          drawing's own sentence — "they are one decision in two parts" — was pointing at something
          real, and it is the `creator-gameline-drawing` principle one rung down: A DOOR SAYS WHAT
          WALKING THROUGH IT DOES. Picking Smuggler is picking Charmer, Gambler, Gunslinger, Pilot,
          Scoundrel or Thief, and a player choosing blind then discovering afterwards is recoverable
          but avoidable.

          So it TELLS, and does not offer: no cards, nothing clickable, and deliberately NO
          `onSelectSpec` — a preview that navigates is the nested browse wearing a different name.
          The choosing still happens on the specialization step, where C5 put it.
          Proportion measured before building: 19 of 20 careers open exactly 6, one opens 4 — a
          line of names, not a panel. */}
      {_showCareer && !_showSpecs && (career.specializations || []).length > 0 && (
        <div style={{ marginTop: '18px' }}>
          <div style={fcSectionHeaderStyle(accent)}>WHAT IT OPENS</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'var(--doc-ink-muted)', padding: '2px 2px 0' }}>
            {career.specializations
              .map(function(id) { return (specsById && specsById[id] && specsById[id].name) || null; })
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em', color: 'var(--doc-ink-muted)', marginTop: '7px' }}>
            YOU PICK ONE ON THE NEXT STEP
          </div>
        </div>
      )}

      {/* B-68: the career now COMMITS ON ITS OWN STEP. It used to be committed only when a
          specialization was confirmed — career and spec landed together — which is exactly why
          specialization could not be its own step until B-72's draft contract made the insertion
          safe. */}
      {_showCareer && !_showSpecs && onConfirmCareer && (
        <FCBottomAction label="SELECT THIS CAREER" onClick={function() { onConfirmCareer(career); }} accent={accent} />
      )}
    </div>
  );
}

/* ── B-41: the printed Force-rating PREREQUISITE (ruling `b41-min-force-rating-brief`) ──────────
   RAW: "Some powers have a prerequisite Force rating that a character must possess before he can
   purchase the power" (fad p.282; tree headers carry the number, e.g. Protect/Unleash
   "Prerequisites: Force Rating 3+", fad p.300). Three specs carry it too — Knight FR 2 (RotS p.26,
   tree header p.27), General FR 2 (CotR p.28), Master FR 3 (CotR p.29). Padawan prints none, which
   is what proves the line is PER-SPEC and not career-wide.
   Powers gate at BASIC-power purchase (not per upgrade — owning the basic power means the gate was
   already cleared, and a Force rating cannot fall); specs gate at acquisition, creation pick and
   later XP buy alike.
   REGISTRY-DERIVED on purpose: the number is read off the record, never an id list in code, so a
   future record carrying the field is enforced without touching this file (R-EDS6-6b spirit). */
// ONE home for the spec gates — `CharResolve.minForceRatingOf` / `meetsForceRating` / `specGate`
// (min_force_rating · species_requirement · universal). These two names stay for the call sites.
function minForceRatingOf(rec) { return CharResolve.minForceRatingOf(rec); }
function meetsForceRating(rec, forceRating) { return CharResolve.meetsForceRating(rec, forceRating); }
// ── THE SHORT PATH TO POWER (Collapse of the Republic p.17; ruling `short-path-to-power` R-SPP-1…8) ──
// A Jedi reaches the specialization step at Force rating 1 with no talents, so Knight/General (FR 2)
// are refused and the app already says why. The sidebar's way out: with the GM's permission, spend a
// flat 30 XP on ONE rank of the Force Rating talent, reaching FR 2. The buy is a TREELESS talent
// (R-SPP-9): it carries `source:'short_path'` so it persists (sheet.treelessTalents) and round-trips.
var SHORT_PATH_XP = 30;
var SHORT_PATH_TALENT = 'force_rating';
var SHORT_PATH_SPECKEY = '__treeless__';   // a NON-spec sentinel; fcTalentIsRefundable's guard skips it
// R-SPP-6: the offer belongs to Collapse of the Republic — hidden when that book is inactive.
function shortPathCotRActive() {
  try {
    var S = (typeof window !== 'undefined') && window.SWRPGSources;
    if (!S || !S.isBookActive || !S.current) return false;
    var cur = S.current();
    return !!S.isBookActive(cur.index, cur.state, 'collapse_of_the_republic');
  } catch (e) { return false; }
}
// R-SPP-1/3/4/6: OFFERED on a JEDI spec (R-SPP-4) blocked by a Force-rating-2 prerequisite — the path
// buys ONE rank, so it reaches FR 2 only and MASTER's FR 3 is never offered (R-SPP-3) — with the
// current rating still short, and only while CotR is active (R-SPP-6).
function shortPathOfferedFor(spec, career, forceRating) {
  return !!(career && career.id === 'jedi'
    && minForceRatingOf(spec) === 2
    && (Number(forceRating) || 0) < 2
    && shortPathCotRActive());
}
function shortPathTakenIn(talents) {
  return Object.keys(talents || {}).some(function (k) { return talents[k] && talents[k].source === 'short_path'; });
}
function shortPathPosKeyIn(talents) {
  return Object.keys(talents || {}).find(function (k) { return talents[k] && talents[k].source === 'short_path'; }) || null;
}
// The character's CURRENT rating = base + EVERY bonus (Force Rating talent ranks, species grants) —
// charResolve.js:502 over :307. Deliberately NOT `sheet.forceRatingBase`, and not SheetAdvancement's
// `forceRating` (which is a force-SENSITIVITY flag read from persisted inputs, no talent bonus): the
// Force Rating talent in your first specialization is precisely how a character climbs to FR 2
// (fad-gm p.26), so gating on the base alone would refuse a purchase RAW allows.
function resolvedForceRating(sheetLike) {
  if (!sheetLike) return 0;
  var d = CharResolve.computeDerived(sheetLike, null, window._canonicalCharbuild);
  return Number(d && d.forceRating) || 0;
}
// creatorForceRating — the same number, mid-creation, where no sheet exists yet. Base half reuses the
// controller's `hasForceAccess` (the identical species/career/spec/gameLine disjunction the sheet
// builder persists as forceRatingBase); bonus half reuses the SHARED resolver, because the talent step
// runs BEFORE the force-powers step and a Force Rating talent bought there legitimately reaches FR 2.
// B-1334: the sheet's one formula (CharResolve.forceRatingOf) — a Force Rating talent's bonus counts only with Force access.
function creatorForceRating(hasForceAccess, characterTalents, cb) {   // F-100 R-FEGC-1: registry-fed (throws without the charbuild); R-FEC-1 floor = max(base, floor) + bonus · F-100 M6 R5: `cb` = the draft's own view
  var keys = Object.keys(characterTalents || {}).map(function(pk) { return characterTalents[pk].talentKey; }), tals = (cb || window._canonicalCharbuild || window._canonicalCharbuildBase || {}).talents;
  var db = CharResolve.derivedBonusesFromTalents(keys, tals);
  return CharResolve.forceRatingOf(hasForceAccess ? 1 : 0, CharResolve.forceRatingFloorFromTalents(keys, tals), db && db.forceRatingBonus, hasForceAccess);
}
// ForceRatingReq — the prerequisite line, rendered on EVERY carrier of the field, met or unmet: a
// browsing player learns Force rating is a progression axis even when they already clear it. When
// unmet it is the visible REASON the buy is blocked — never a bare dead control.
function ForceRatingReq({ rec, forceRating, compact }) {
  var need = minForceRatingOf(rec);
  if (!need) return null;
  var have = Number(forceRating) || 0;
  var ok = have >= need;
  return h('span', { 'data-fr-req': need, 'data-fr-met': ok ? '1' : '0', style: {
    fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em',
    color: ok ? 'rgba(255,255,255,0.5)' : '#FFC4C4',
  } }, 'REQUIRES FORCE RATING ' + need + ((!ok && !compact) ? ' · YOU HAVE ' + have : ''));
}

// costForNewSpec — RAW spec-purchase cost (module scope so ADVANCEMENT + the SPECIALIZE tab share ONE
// source, bind B5). first career spec FREE (eote p.35); universal = base (p.284); other-career = base+10
// (p.102); base = 10 × (ownedCount+1).
function costForNewSpec(specRec, ownedCount, career) { return CharResolve.costForNewSpec(specRec, ownedCount, career); }

/* ── V2: NEW SPECIALIZATION journey (R-CS4e — the multiclass hero moment) ──
   A full-screen guided flow launched from ADVANCEMENT: three shelves (your career / universal /
   other careers, searchable) → preview (reuses the spec-detail visual pattern + the real tree) →
   CONFIRM beat → buy. Cost + FR-grant + career-skill status are all shown honestly before you commit. */
function NewSpecJourney({ data, sheet, sheetSpecs, career, accent, availableXp, costFor, skillNames, onBuy, onClose, tab, center }) {
  var _sel = useState(null), selId = _sel[0], setSelId = _sel[1];
  var _q = useState(''), q = _q[0], setQ = _q[1];
  var _dx = useState(false), descX = _dx[0], setDescX = _dx[1];
  var _dOpen = useState(false), detailOpen = _dOpen[0], setDetailOpen = _dOpen[1];  // a talent detail is up
  // B-657: the long shelf is a FOLD and the search is ON it (the frame's `details.sb-fold.sb-shelf`).
  // The lab draws a static `<details>` with a `readonly` input; a live search that "opens the fold"
  // needs the open state in React's hands, so this one flag is the paper face's only added state.
  var _fold = useState(false), foldOpen = _fold[0], setFoldOpen = _fold[1];
  var allSpecs = (data && data.specializations) ? data.specializations : {};
  var owned = {}; (sheetSpecs || []).forEach(function(s) { owned[s.id] = true; });
  var careerIds = (career && Array.isArray(career.specializations)) ? career.specializations : [];
  var firstFree = (sheetSpecs || []).length === 0 && careerIds.length > 0;  // R-CH2: free first pick is career-only
  var frNow = resolvedForceRating(sheet);   // B-41: resolved, so Force Rating talent ranks count




  // ── Shelf browser ── (every shelf alphabetical by display name)
  // B-180: the ACTIVE SOURCES gate reaches creation (careers :7951, universal specs :3670) but never
  // reached this advancement picker — so an inactive community book's specializations, and their whole
  // talent trees, were buyable post-creation (Jason's Sith report). ONE predicate over the purchasable
  // set — the same fcFilterActive the creator uses — applied to all three shelves (ruling
  // player-field-reports-2026-08-14 §10, R-PFR2-6: one predicate, no second filtering path). GRACEFUL by
  // construction: `owned` is excluded from every shelf below, so an owned inactive-book spec still renders
  // on the sheet and only NEW purchases are gated. The refusal is ABSENCE (no new-UI). Fail-open if
  // sources aren't ready yet, exactly as fcFilterActive does everywhere else.
  var _linePref = !!(window.SWRPGSources && window.SWRPGSources.lineFilterOn && window.SWRPGSources.lineFilterOn());
  var _gl = sheet && sheet.gameLine;
  var sourceActive = {};
  fcFilterActive(Object.keys(allSpecs).map(function(id) { return allSpecs[id]; }), _gl ? [_gl] : [], _linePref)
    .forEach(function(s) { if (s) sourceActive[s.id] = true; });
  var byName = function(a, b) { return (allSpecs[a].name || '').localeCompare(allSpecs[b].name || ''); };
  var careerShelf = careerIds.filter(function(id) { return allSpecs[id] && !owned[id] && sourceActive[id]; }).sort(byName);
  var universalShelf = Object.keys(allSpecs).filter(function(id) { return allSpecs[id].universal && !owned[id] && careerIds.indexOf(id) === -1 && sourceActive[id]; }).sort(byName);
  var otherShelf = Object.keys(allSpecs).filter(function(id) { return !allSpecs[id].universal && careerIds.indexOf(id) === -1 && !owned[id] && sourceActive[id]; }).sort(byName);
  if (q) { var ql = q.toLowerCase(); otherShelf = otherShelf.filter(function(id) { return (allSpecs[id].name || '').toLowerCase().indexOf(ql) >= 0; }); }

  // ═══ B-657 · THE NEW SPECIALIZATION ON PAPER — A WALK (ruling sheet-paper-concept A1 R-PSC-8;
  // lab 83bf1f2d `spec_journey`, Jason 2026-09-17 "Approved.") ═══════════════════════════════════
  // A room inherits the anatomy of ITS OWN SHAPE (the craft walk's finding, B-624): choosing a
  // specialization is ordered — the shelves, one preview, a commit — so it wears the creator's
  // furniture (the step rail, a foot that carries the commit) and not the board's. TWO steps: the
  // confirm is the preview's foot, as production's already is.
  // SAME COMPONENT, same state (selId · q · detailOpen), the same `costFor`, the same source gate
  // computed above, the same `ForceRatingReq` arithmetic (meetsForceRating / minForceRatingOf), and
  // the same NAVIGATE-then-buy order the dark face documented. The dark face and the legacy sheet's
  // ADVANCEMENT tab that rendered it are deleted (paper-sheet-delete); this is the one face.
  var rail = function(now) {
    return (
      <div className="sb-steps" data-testid="nsj-steps">
        {['Choose', 'Preview'].map(function(lab, i) {
          return <span className="sb-step" key={lab} data-state={i < now ? 'done' : i === now ? 'now' : 'next'} data-testid={'nsj-step-' + i}>{(i + 1) + ' · ' + lab}</span>;
        })}
      </div>);
  };
  var skName = function(sk) { return (skillNames && skillNames[sk]) || sk; };
  // the row's meta, the frame's words: "4 career skills · 2 already yours" / "no career skills",
  // and the ✦ mark on the SAME axis as the requirement, one row (B-41).
  var specMeta = function(recP) {
    var cs = recP.career_skills || [];
    var mine = career ? cs.filter(function(sk) { return (career.career_skills || []).indexOf(sk) >= 0; }).length : 0;
    var txt = cs.length ? (cs.length + ' career skill' + (cs.length === 1 ? '' : 's') + (mine ? (' · ' + mine + ' already yours') : '')) : 'no career skills';
    var grants = Number(recP.force_rating) >= 1;
    return <span className="sb-e-m">{txt}{grants ? <React.Fragment>{' · '}<span className="sb-fr">{'✦ Force rating 1'}</span></React.Fragment> : null}</span>;
  };
  // ONE ROW. The refusal is IN WORDS on the row, and an unmet Force rating OUTRANKS the price
  // (B-41 — it is the harder wall, and a price you could save for is not the reason you are refused).
  var specRow = function(id) {
    var recP = allSpecs[id]; if (!recP) return null;
    var cP = costFor(id), needP = minForceRatingOf(recP), frOkP = meetsForceRating(recP, frNow), affordP = cP <= availableXp;
    var refusedP = !frOkP || !affordP;
    return (
      <div className="sb-e sb-spec" key={id} data-refused={refusedP ? '1' : undefined} data-spec-id={id} data-spec-cost={cP}>
        <div className="sb-e-h">
          <span className="sb-e-n">{recP.name || id}</span>
          {specMeta(recP)}
          <span className="sb-spec-r">
            {!frOkP ? <span className="sb-refuse">{'REQUIRES FORCE RATING ' + needP}</span>
              : !affordP ? <span className="sb-refuse">{'NEED ' + cP + ' XP'}</span>
              : <span className="sb-sk-cost">{cP === 0 ? 'FREE' : cP + ' XP'}</span>}
            <button type="button" className="hp-quiet" data-testid={'nsj-card-' + id} aria-label={'Preview ' + (recP.name || id)}
              onClick={function() { setDescX(false); setDetailOpen(false); setSelId(id); }}>Preview</button>
          </span>
        </div>
      </div>);
  };
  var shelfHeadP = function(label, n, style) { return <div className="sb-grp" style={style}>{label}<span>{n}</span></div>; };
  if (selId && allSpecs[selId]) {
    // ── STEP 2 · PREVIEW — the specialization read whole, on the growth room's OWN two-zone grid
    // (§164): the reading in the spine's column, the tree on the stage in a plate that scrolls. A
    // readable four-column tree wants ~1,100px and the wide board is bounded, so the room cannot be
    // one tall plate.
    var pRec = allSpecs[selId], pCost = costFor(selId), pAfford = pCost <= availableXp;
    var pGrants = Number(pRec.force_rating) >= 1, pNeed = minForceRatingOf(pRec), pFrOk = meetsForceRating(pRec, frNow);
    var pIsCareer = careerIds.indexOf(selId) !== -1, pIsUni = !!pRec.universal;
    var pKind = pIsUni ? 'Universal' : (pIsCareer ? ((career && career.name ? career.name : 'Career') + ' · career') : 'Other career');
    var pHasFR = frNow >= 1;
    var pCs = pRec.career_skills || [];
    var pMine = career ? pCs.filter(function(sk) { return (career.career_skills || []).indexOf(sk) >= 0; }).length : 0;
    var pLine = !pFrOk ? ('Requires Force rating ' + pNeed + ' · you have ' + frNow + ' — the Force Rating talent in a specialization tree raises it.')
      : !pAfford ? ('This costs ' + pCost + ' XP · you have ' + availableXp + '.')
      : pCost === 0 ? ('Take ' + (pRec.name || selId) + ' — free, your first career specialization.')
      : ('Take ' + (pRec.name || selId) + ' for ' + pCost + ' XP' + ((pGrants && !pHasFR) ? ' — and awaken to the Force' : '') + '.');
    // THE READ-ONLY TREE IS THE SHARED COMPONENT (B-615's precedent, ruling tree-standardization-brief):
    // `talentTreeToModel({ readOnly: true, skin: 'board' })` → `DirectionalTree`. `readOnly` nulls every
    // cell's `buy`, so `tabFace` takes its READ branch and the badge is the board's cost CHIP
    // (`span.hp-tab > span.hp-tab-key`) — no `button.hp-tab` anywhere in this tree. `headPress`
    // keeps C3b: a press on a cell's head opens the talent's reading sheet, which is the only
    // thing a cell does here.
    var pTree = (data && data.talents && pRec.talent_tree)
      ? h(DirectionalTree, { accent: accent, headPress: true, onDetailToggle: setDetailOpen,
          model: talentTreeToModel({ specData: pRec, talentDb: data.talents, character: { talents: {} },
                                     readOnly: true, skin: 'board', interactive: true }) })
      : <div className="hp-none">Talent tree unavailable.</div>;
    return (
      <div className="sb-jwalk" data-testid="nsj-walk" data-step="preview">
        {rail(1)}
        <div className="sb-board sb-growth sb-jprev">
          <div className="sb-spine">
            <div className="hp-plate" data-pad="read" style={{ overflow: 'auto', minHeight: 0 }} data-testid="nsj-reading">
              <div className="hp-sechead" style={{ margin: '0 0 6px' }}><h2>{pRec.name || selId}</h2><span className="hp-cite">{pKind}</span></div>
              {pGrants ? <p className="sb-e-m sb-fr" style={{ margin: '0 0 8px' }}>{'✦ Grants Force rating 1'}</p> : null}
              {fcSpecDescText(pRec) ? <p className="sb-e-t">{fcSpecDescNode(pRec)}</p> : null}
              {shelfHeadP('Career skills it grants', pCs.length, { marginTop: 12 })}
              <p className="sb-e-m" style={{ margin: '4px 0 8px' }}>These become career skills — cheaper to raise. No free ranks.</p>
              <div className="sb-chips" data-testid="nsj-chips" data-mine={pMine}>
                {pCs.map(function(sk) {
                  var on = !!(career && (career.career_skills || []).indexOf(sk) >= 0);
                  return <span className="sb-chip" key={sk} aria-pressed={on ? 'true' : 'false'} data-skill={sk}>{skName(sk) + (on ? ' · yours' : '')}</span>;
                })}
              </div>
            </div>
          </div>
          <div className="sb-stage">
            <div className="hp-plate" data-pad="data" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div className="hp-sechead" style={{ margin: '0 0 6px' }}><h2>The talent tree</h2></div>
              <div className="hp-treescroll" data-testid="nsj-tree">{pTree}</div>
            </div>
          </div>
        </div>
        <div className="sb-walkfoot">
          <span className="sb-e-t" style={{ flex: 1 }} data-testid="nsj-foot">{pLine}</span>
          <button type="button" className="hp-quiet" data-testid="nsj-back" onClick={function() { setSelId(null); }}>Back to the shelves</button>
          {!pFrOk
            ? <span className="sb-refuse" data-testid="nsj-refuse">{'REQUIRES FORCE RATING ' + pNeed}</span>
            /* Order matters (spec-journey-tab): NAVIGATE first (onClose lands back on the growth room's
               tree), THEN buy — onBuy's onSheetChange re-renders and drops a nav queued after it. */
            : <button type="button" className="hp-commit" data-testid="nsj-confirm" disabled={!pAfford}
                onClick={pAfford ? function() { var bid = selId; onClose(); onBuy(bid); } : undefined}>{pCost === 0 ? 'Confirm — free' : 'Confirm — ' + pCost + ' XP'}</button>}
        </div>
      </div>);
  }
  // ── STEP 1 · CHOOSE — the three shelves, from the registry. The long shelf is FOLDED under its
  // count with the search ON the fold (the skill groups' idiom, B-628): 110 entries is not a thing
  // to scroll past to reach a foot. Every price is `costFor`; every refusal is on its row in words.
  return (
    <div className="sb-jwalk" data-testid="nsj-walk" data-step="choose">
      {rail(0)}
      <div className="hp-plate sb-jshelf" data-pad="read" data-testid="nsj-shelves">
        <p className="sb-e-t">{firstFree
          ? 'A new specialization opens a whole talent tree and its career skills. Your first one is FREE when it comes from your career; a universal or other-career pick costs XP (eote p.93).'
          : 'A new specialization opens a whole talent tree and its career skills. It costs 10 × your total number of specializations; one from another career costs 10 more (EotE p.93).'}</p>
        {careerShelf.length ? <React.Fragment>
          {shelfHeadP('Career · ' + ((career && career.name) || '—'), careerShelf.length, { marginTop: 12 })}
          {careerShelf.map(specRow)}
        </React.Fragment> : null}
        {universalShelf.length ? <React.Fragment>
          {shelfHeadP('Universal', universalShelf.length, { marginTop: 14 })}
          {universalShelf.map(specRow)}
        </React.Fragment> : null}
        <details className="sb-fold sb-shelf" data-testid="nsj-other-fold" open={foldOpen || !!q}
          onToggle={function(e) { setFoldOpen(!!e.target.open); }}>
          <summary className="sb-grp" style={{ marginTop: 14 }}>{'Other careers · +10 XP'}<span>{otherShelf.length}</span></summary>
          <input className="sb-filter" data-testid="nsj-search" value={q} aria-label="Search all specializations"
            placeholder="Search all specializations…" onChange={function(e) { setQ(e.target.value); }} />
          {otherShelf.map(specRow)}
        </details>
      </div>
      <div className="sb-walkfoot">
        <button type="button" className="hp-quiet" data-testid="nsj-close" onClick={onClose}>Back to growth</button>
      </div>
    </div>);
}

// ═══ F-100 · THE SIGNATURE ABILITY WALK (ruling f100-surfacing-creator-concept R-SCC-1 as errata'd — ADDENDUM 2 F5–F7,
// F12, F13; ADDENDUM 11 OWNS; D-229 Q1 (a); ADDENDUM 18 Q1 (a), Q5 (a)). The approved record
// Docs/mockups/f100-surfacing-creator.html @ 1e2438f9f frames K1–K4 draw it IN NewSpecJourney's own markup — the rail
// (1 · Choose, 2 · Preview), the shelf plate, the preview's two zones, the foot that carries the commit — so it reuses
// those classes and `nsj-*` testids whole (`data-walk="sig"` tells the two apart). Every legality word is SIG_WORDS'
// (CharResolve.sigAbilityChecks / sigBuyPlan, one copy source); every price is the record's cell; the "To use it" terms
// are sigActivation's over a HYPOTHETICAL sheet (the candidate entry added; nothing saved — D5). Rendered only with
// M12's key ON (Q1 (a) i); the writer is SheetAdvancement's buySigAbility.
//
// sigTreeModel(rec, opts) — the ability's tree as the shared DirectionalTree model: forcePowerToModel over the record (a
// signature tree has a Force power's shape), each cell's state then set per box. opts.mode 'read' (the preview: the base
// STARTING · its price, the upgrades their price, nothing pressable) or 'stage' (the growth stage: cell states as
// talents — owned / buyable / needxp / locked with its own words (R1) / held), opts.cellState(pos) → { state, reason? }
// for the stage; a cell whose cost is `inferred` is HELD on either face ("cost unverified — <book> p.<tree_page>").
function sigHeldWords(rec) {
  var book = gameLineBookLabel((rec.cite && rec.cite.book) || ((rec.sources || [])[0]) || '') || '';   // B-336 (ADDENDUM 62): a key prints its title
  var page = rec.tree_page != null ? rec.tree_page : (rec.cite && rec.cite.page);
  return { book: book, page: page, cell: 'cost unverified — ' + book + ' p.' + page };
}
function sigTreeModel(rec, opts) {
  opts = opts || {};
  var m = forcePowerToModel({ power: rec, ownedUpgradeNames: [], readOnly: opts.mode === 'read', skin: 'board', interactive: true });
  var byPos = {}, held = sigHeldWords(rec);
  CharResolve.sigTreeCells(rec).forEach(function(c) { byPos[c.pos] = c; });
  m.rows.forEach(function(row, ri) {
    row.cells.forEach(function(cell, ci) {
      if (!cell || cell.filler) return;
      var f = byPos[ri + '-' + ci]; if (!f) return;
      cell.cost = f.cost;
      if (f.cost_status === 'inferred') {
        cell.state = 'locked'; cell.statusLabel = 'HELD';
        cell.entry.buy = { state: 'held', cost: f.cost, reason: held.cell };
        return;
      }
      if (opts.mode === 'read') {
        cell.state = ri === 0 ? 'granted' : 'locked';
        cell.statusLabel = ri === 0 ? 'STARTING · ' + f.cost + ' XP' : f.cost + ' XP';
        return;
      }
      var st = opts.cellState ? opts.cellState(f.pos, f) : { state: 'locked' };
      cell.state = st.state === 'owned' ? 'owned' : (st.state === 'buyable' || st.state === 'needxp') ? 'available' : 'locked';
      cell.statusLabel = st.state === 'owned' ? 'OWNED' : (st.state === 'buyable' || st.state === 'needxp') ? f.cost + ' XP' : st.state === 'held' ? 'HELD' : 'LOCKED';
      cell.entry.buy = st.state === 'owned' ? { state: 'owned' }
        : st.state === 'buyable' ? { state: 'buyable', cost: f.cost, label: 'BUY UPGRADE', doBuy: function() { if (opts.onBuy) opts.onBuy(f.pos); } }
        : st.state === 'needxp' ? { state: 'needxp', cost: f.cost, label: 'BUY UPGRADE', doBuy: function() {} }
        : st.state === 'held' ? { state: 'held', cost: f.cost, reason: st.reason || held.cell }
        : { state: 'locked', cost: f.cost, reason: st.reason || null };
    });
  });
  // the preview is a READ tree (the Force tree's chip idiom); the stage BUYS like talents, so it takes the talent tree's
  // buy role, not the Force tree's read role (the record's sigModel, K4)
  if (opts.mode !== 'read') delete m.treeClass;
  return m;
}
// The shelf: every ability of the sheet's career not yet owned (by id), each tried on each owned in-career tree with the
// ONE legality function (the candidate entry appended); the trees it stands on, else the first tree's problems.
function sigShelfOf(sheet, data, career, specIds) {
  var reg = (data && data.signatureAbilities) || {}, careerId = career && career.id;
  var own = Array.isArray(sheet.signatureAbilities) ? sheet.signatureAbilities : [];
  var trees = (specIds || []).filter(function(id) { return career && (career.specializations || []).indexOf(id) !== -1; });
  return Object.keys(reg).filter(function(id) {
    return careerId && (reg[id].careers || []).indexOf(careerId) !== -1 && !own.some(function(e) { return e && e.id === id; });
  }).sort().map(function(id) {
    var rec = reg[id], base = CharResolve.sigTreeCells(rec).filter(function(c) { return c.pos === '0-0'; })[0] || { cost: 0 };
    var tries = trees.map(function(tree) {
      var t = Object.assign({}, sheet, { signatureAbilities: own.concat([{ id: id, tree: tree, owned: ['0-0'] }]) });
      var chk = CharResolve.sigAbilityChecks(t, data).slice(-1)[0];
      return { tree: tree, ok: !!chk.ok, problems: chk.problems };
    });
    var legal = tries.filter(function(x) { return x.ok; });
    var first = legal[0] || tries[0] || null;
    return { id: id, rec: rec, base: base, legal: legal.map(function(x) { return x.tree; }), tree: first ? first.tree : null,
      ok: legal.length > 0, problems: legal.length ? [] : (first ? first.problems : [{ check: 'V2', words: CharResolve.sigAbilityChecks(Object.assign({}, sheet, { signatureAbilities: own.concat([{ id: id, tree: null, owned: ['0-0'] }]) }), data).slice(-1)[0].problems[0].words }]) };
  });
}
function SigAbilityJourney({ data, sheet, sheetSpecs, career, accent, availableXp, onBuy, onClose }) {
  var _sel = useState(null), selId = _sel[0], setSelId = _sel[1];
  var _pick = useState({}), treePick = _pick[0], setTreePick = _pick[1];
  var _dOpen = useState(false), setDetailOpen = _dOpen[1];
  var specs = data && data.specializations ? data.specializations : {};
  var treeName = function(id) { return (specs[id] && specs[id].name) || id || '—'; };
  var cols = function(c) { return (c || []).join(' and '); };
  var shelf = sigShelfOf(sheet, data, career, (sheetSpecs || []).map(function(s) { return s && s.id; }).filter(Boolean));
  var baseCost = shelf.length ? shelf[0].base.cost : 30;
  var rail = function(now) {
    return (
      <div className="sb-steps" data-testid="nsj-steps">
        {['Choose', 'Preview'].map(function(lab, i) {
          return <span className="sb-step" key={lab} data-state={i < now ? 'done' : i === now ? 'now' : 'next'} data-testid={'nsj-step-' + i}>{(i + 1) + ' · ' + lab}</span>;
        })}
      </div>);
  };
  var sel = selId ? shelf.filter(function(x) { return x.id === selId; })[0] : null;
  if (sel) {
    // ── STEP 2 · PREVIEW — the ability read whole: its attach tree (or F13's pick row), "To use it", its tree.
    var rec = sel.rec, cost = sel.base.cost, afford = cost <= availableXp, isHeld = sel.base.cost_status === 'inferred';
    var slug = groupIdSlug('Attach under'), two = sel.legal.length > 1;
    var tree = two ? (treePick[slug] || null) : sel.tree;
    var hypo = Object.assign({}, sheet, { signatureAbilities: (sheet.signatureAbilities || []).concat([{ id: sel.id, tree: tree || sel.tree, owned: ['0-0'] }]) });
    var act = sel.ok ? CharResolve.sigActivation(hypo, data, sel.id) : null;
    var terms = [];
    if (act && act.destiny && act.destiny.value) terms.push(act.destiny.value + ' Destiny');
    if (act && act.uses_per_session && act.uses_per_session.value != null) terms.push(act.uses_per_session.value === 1 ? 'once per session' : act.uses_per_session.value + ' times per session');
    var hw = sigHeldWords(rec);
    var line = isHeld ? ((rec.name || sel.id) + ' can’t be bought yet: its costs aren’t checked against ' + hw.book + ' p.' + hw.page + '.')
      : !sel.ok ? sel.problems.map(function(p) { return p.words; }).join(' ')
      : two && !tree ? 'Pick the tree to continue'
      : !afford ? ('This costs ' + cost + ' XP · you have ' + availableXp + '.')
      : ('Take ' + (rec.name || sel.id) + ' for ' + cost + ' XP.');
    var canBuy = sel.ok && !isHeld && afford && !!tree;
    return (
      <div className="sb-jwalk" data-testid="nsj-walk" data-walk="sig" data-step="preview">
        {rail(1)}
        <div className="sb-board sb-growth sb-jprev">
          <div className="sb-spine">
            <div className="hp-plate" data-pad="read" style={{ overflow: 'auto', minHeight: 0 }} data-testid="nsj-reading">
              <div className="hp-sechead" style={{ margin: '0 0 6px' }}><h2>{rec.name || sel.id}</h2><span className="hp-cite">{((career && career.name) || 'Career') + ' · signature ability'}</span></div>
              {two
                ? <div data-testid="sig-attach-pick">{fcSpeciesOptionGroups({ option_choices: [{ name: 'Attach under', options: sel.legal.map(function(id) { return { id: id, name: treeName(id) }; }) }] },
                    treePick, {}, {}, {}, function(s, id) { var n = {}; n[s] = id; setTreePick(n); })}</div>
                : <p className="sb-e-m" style={{ margin: '0 0 8px' }} data-testid="sig-attach">{'Attaches under ' + treeName(sel.tree) + ' at columns ' + cols(rec.attach && rec.attach.columns) + '.'}</p>}
              <div className="sb-grp" style={{ marginTop: 12 }}>To use it</div>
              <p className="sb-e-t" style={{ margin: '4px 0 6px', fontWeight: 600 }} data-testid="sig-terms">{terms.join(' · ')}</p>
            </div>
          </div>
          <div className="sb-stage">
            <div className="hp-plate" data-pad="data" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div className="hp-sechead" style={{ margin: '0 0 6px' }}><h2>The ability’s tree</h2></div>
              <div className="hp-treescroll" data-testid="nsj-tree">{h(DirectionalTree, { accent: accent, headPress: true, onDetailToggle: setDetailOpen, model: sigTreeModel(rec, { mode: 'read' }) })}</div>
            </div>
          </div>
        </div>
        <div className="sb-walkfoot">
          <span className="sb-e-t" style={{ flex: 1 }} data-testid="nsj-foot">{line}</span>
          <button type="button" className="hp-quiet" data-testid="nsj-back" onClick={function() { setSelId(null); setTreePick({}); }}>Back to the shelves</button>
          {isHeld
            ? <span className="sb-refuse" data-testid="nsj-refuse">COST UNVERIFIED</span>
            /* NAVIGATE first, THEN buy (NewSpecJourney's order): onBuy's onSheetChange re-renders and drops a nav queued after it */
            : <button type="button" className="hp-commit" data-testid="nsj-confirm" disabled={!canBuy}
                onClick={canBuy ? function() { var id = sel.id, t = tree; onClose(id, t); onBuy(id, t); } : undefined}>{'Confirm — ' + cost + ' XP'}</button>}
        </div>
      </div>);
  }
  // ── STEP 1 · CHOOSE — the career's abilities, each with its attach line, its price, its refusal in SIG_WORDS' words.
  return (
    <div className="sb-jwalk" data-testid="nsj-walk" data-walk="sig" data-step="choose">
      {rail(0)}
      <div className="hp-plate sb-jshelf" data-pad="read" data-testid="nsj-shelves">
        <p className="sb-e-t">{'A signature ability attaches under the bottom row of one of your career’s trees, where you own the talents it joins. Its base ability costs ' + baseCost + ' XP; its upgrades are bought on its tree, like talents.'}</p>
        <div className="sb-grp" style={{ marginTop: 12 }}>{'Career · ' + ((career && career.name) || '—')}<span>{shelf.length}</span></div>
        {shelf.map(function(x) {
          var c = x.base.cost, aff = c <= availableXp, why = x.ok ? null : x.problems.map(function(p) { return p.words; }).join(' ');
          return (
            <div className="sb-e sb-spec" key={x.id} data-sig-id={x.id} data-refused={(why || !aff) ? '1' : undefined}>
              <div className="sb-e-h">
                <span className="sb-e-n">{x.rec.name || x.id}</span>
                <span className="sb-e-m">{'attaches under ' + treeName(x.tree) + ' at columns ' + cols(x.rec.attach && x.rec.attach.columns)}</span>
                <span className="sb-spec-r">
                  {x.ok && !aff ? <span className="sb-refuse">{'NEED ' + c + ' XP'}</span> : <span className="sb-sk-cost">{c + ' XP'}</span>}
                  <button type="button" className="hp-quiet" data-testid={'nsj-card-' + x.id} aria-label={'Preview ' + (x.rec.name || x.id)}
                    onClick={function() { setTreePick({}); setSelId(x.id); }}>Preview</button>
                </span>
              </div>
              {why ? <p className="sb-e-t hp-why">{why}</p> : null}
            </div>);
        })}
      </div>
      <div className="sb-walkfoot">
        <button type="button" className="hp-quiet" data-testid="nsj-close" onClick={function() { onClose(); }}>Back to growth</button>
      </div>
    </div>);
}

/* ── FC Specialization Detail ──────────────────────────── */

function FCSpecDetail({ spec, career, currentSpecies, speciesSkillChoices, speciesList, accent, skillNames, charbuild, talentDb, onConfirm, forceRating, shortPathTaken, onShortPathTake, onShortPathUndo, xpPool, xpRemaining }) {
  if (!spec) return null;

  // Resolve row-0 starting talents for preview
  var row0Talents = [];
  if (spec.talent_tree && spec.talent_tree[0] && talentDb) {
    row0Talents = spec.talent_tree[0].talents.map(function(t) {
      var td = talentDb[t.talent];
      return { key: t.talent, name: td ? td.name : t.talent, descriptionRaw: td ? (td.description || '') : '' };
    });
  }

  // R3 Q2b: this used to STRIP glyph tokens ("strip residual glyph tokens for the flat preview") —
  // a specialization-description surface deleting the one thing a dice symbol is for. There is no
  // such thing as a "residual" glyph token any more: Q1 made them canonical content. The string
  // stays a string here (the `descText &&` gate and any future length test need one); the DRAWER
  // runs at the render site below. specializations.json carries 0 tokens today, so this is a
  // latent-eater removal, same class as the career card's.
  var descText = fcSpecDescText(spec);
  // B-69: THE SP-2 COLLAPSE IS DELETED — a shim that outlived its cause.
  // It was added because "specialization descriptions run 200-300 words, pushing the primary CTA
  // off-screen". That premise did not survive the canonical migration: authored descriptions are
  // 270-450 characters (median 370) — about 40-70 words — so the 280-char threshold fired on 127 of
  // 129 while the button itself cost 53px (44 + 9 margin) to hide a median of 45px of prose, and
  // 22px (ONE LINE) in 26 cases. The affordance made the page LONGER on 122 of the 129.
  // Remedy is deletion, not tuning (ruling `creator-specialization-mockup`); evidence rides in
  // scripts/probe_spec_readmore.py. The description now simply renders.

  // Species requirement check (RAW restriction — e.g. Retired Clone Trooper → Clone).
  // Hide the SELECT bar until the reader scrolls to the bottom of this (long) spec page;
  // latches so Read More expansion doesn't move the reveal trigger.
  var rootRef = useRef(null);
  var ctaRevealed = useRevealNearBottom(rootRef, 90);

  // B-40: a spec may grant career skills the PLAYER chooses (Republic Representative's fourth is
  // "Knowledge (choose any one)"). Mirrors the species CHOOSE-ONE group: pick inline, CTA gated until
  // every grant is satisfied. The pool comes from the SHARED resolver — the same generic-by-group
  // branch the species picker uses, which throws on a pool the vocabulary does not define (B-36).
  var _choiceGrants = (spec.career_skill_choice_grants || []);
  var _useCP = useState({}), choicePicks = _useCP[0], setChoicePicks = _useCP[1];
  // Reset when the spec changes — a pick never outlives its subject (B-18's rule).
  useEffect(function() { setChoicePicks({}); }, [spec.id]);
  var _careerStatusIds = creatorCareerStatusIds(charbuild, career, spec.id, { speciesId: currentSpecies && currentSpecies.id,
    speciesSkillChoices: speciesSkillChoices });   // F-100 M7 C2
  var _poolFor = function(g) {
    return charbuild ? speciesChoicePoolOf(g, charbuild, _careerStatusIds) : [];
  };
  var _poolIdOf = function(g) { return Array.isArray(g.pool) ? g.pool.join('+') : g.pool; };
  var allChoicesPicked = _choiceGrants.every(function(g) {
    return (choicePicks[_poolIdOf(g)] || []).length >= (Number(g.count) || 1);
  });

  // C5 (ruling f-eds-6-specializations-full-read §7): the requirement is REGISTRY DATA, keyed by
  // canonical species id. It used to be a hardcoded FC_SPEC_SPECIES_REQUIREMENT map here — the same
  // app-shadows-registry class as the career-description override map the careers lap deleted.
  // (Named in prose, not as the symbol — narrative_prose.cjs counts that token and is right to.)
  // One source: the record.
  var requiredSpeciesId = spec.species_requirement;
  var requiresSpeciesSwitch = false;
  var requiredSpecies = null;
  if (requiredSpeciesId && currentSpecies && currentSpecies.id !== requiredSpeciesId) {
    requiresSpeciesSwitch = true;
    requiredSpecies = (speciesList || []).find(function(s) { return s.id === requiredSpeciesId; }) || { id: requiredSpeciesId, name: requiredSpeciesId };
  }

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'var(--doc-accent)', opacity: 0.6, marginBottom: '4px' }}>
          {career ? career.name.toUpperCase() + ' · SPECIALIZATION' : 'SPECIALIZATION'}
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-xl)', color: 'var(--doc-ink)', letterSpacing: '0.04em' }}>{spec.name}</div>
        {spec.universal && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--doc-ink-muted)', letterSpacing: '0.12em', marginTop: '4px' }}>UNIVERSAL SPECIALIZATION</div>
        )}
      </div>

      {/* T2-D: a skill-less universal (Exile/Emergent) says so honestly — the FR 1 is the
         whole package ("does not have any career skills", eote p.276 / aor p.292). */}
      {spec.universal && (!spec.career_skills || spec.career_skills.length === 0) && (
        <div style={{ marginBottom: '18px', fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', lineHeight: 1.5, padding: '10px 12px', background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)', borderRadius: '6px' }}>
          Adds <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>no career skills</span> — this
          specialization's whole package is <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>Force Rating 1</span>,
          plus its talent tree. No free spec ranks at creation.
        </div>
      )}

      {/* B-40: the chosen career skill(s). Same shape as the species CHOOSE-ONE group. */}
      {_choiceGrants.length > 0 && (
        <div style={{ marginBottom: '18px' }}>
          <div style={fcSectionHeaderStyle(accent)}>CHOOSE YOUR CAREER SKILL</div>
          {_choiceGrants.map(function(g, gi) {
            var poolId = _poolIdOf(g), pool = _poolFor(g), picked = choicePicks[poolId] || [];
            var need = Number(g.count) || 1;
            return (
              <div key={'csg' + gi} style={{ marginBottom: '10px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em', color: 'var(--doc-accent)', margin: '2px 0 6px' }}>
                  {String(poolId).toUpperCase().replace(/_/g, ' ')} — PICK {need}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {pool.map(function(sid) {
                    var on = picked.indexOf(sid) !== -1;
                    return (
                      <button key={sid} type="button" aria-pressed={on}
                        onClick={function() {
                          setChoicePicks(function(prev) {
                            var n = Object.assign({}, prev), cur = (n[poolId] || []).slice();
                            var at = cur.indexOf(sid);
                            if (at !== -1) cur.splice(at, 1);
                            else { cur.push(sid); if (cur.length > need) cur.shift(); }
                            n[poolId] = cur; return n;
                          });
                        }}
                        style={{
                          fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.04em',
                          padding: '6px 10px', borderRadius: '4px', cursor: 'pointer',
                          color: on ? 'var(--doc-accent)' : 'var(--doc-ink-muted)',
                          background: 'var(--doc-paper-raised)',
                          border: '1px solid ' + (on ? 'var(--doc-accent)' : 'var(--doc-rule)'),
                        }}>{(skillNames && skillNames[sid]) || sid}</button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', marginTop: '8px', lineHeight: 1.4 }}>
            The pick counts as a <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>career skill</span>.
          </div>
        </div>
      )}

      {/* Printed restriction on who may take this spec. INFORMS, ENFORCES NOTHING (Jason,
         2026-07-28, ruling `f-eds-6-specializations-full-read` §7): the table decides, as it does
         at a real table. The species half of Retired Clone Trooper's rule IS enforced, but by
         `species_requirement` above — this line is the part the product only tells you. */}
      {spec.restriction_note && (
        <div style={{ marginBottom: '18px', fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', lineHeight: 1.5, padding: '10px 12px', background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)', borderRadius: '6px' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.14em', color: 'var(--doc-accent)', opacity: 0.75, display: 'block', marginBottom: '4px' }}>AS PRINTED</span>
          {spec.restriction_note}
        </div>
      )}

      {/* Added career skills (CR-RAW #3: a spec's skills ARE career skills, permanently —
         EotE p.35/p.102; the old "BONUS SKILLS" label hid the career status the tester
         reported missing). */}
      {spec.career_skills && spec.career_skills.length > 0 && (
        <div style={{ marginBottom: '18px' }}>
          <div style={fcSectionHeaderStyle(accent)}>ADDED CAREER SKILLS</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {spec.career_skills.map(function(sk) {
              var name = (skillNames && skillNames[sk]) || sk;
              var isOverlap = career && career.career_skills.indexOf(sk) >= 0;
              return <span key={sk} style={{
                fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.04em',
                color: isOverlap ? 'var(--doc-accent)' : 'var(--doc-ink-muted)',
                padding: '4px 8px', background: 'var(--doc-paper-raised)',
                border: '1px solid ' + (isOverlap ? 'var(--doc-accent)' : 'var(--doc-rule)'),
                borderRadius: '4px',
              }}>{name}{isOverlap ? ' ·' : ''}</span>;
            })}
          </div>
          {career && (
            <div style={{ fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', letterSpacing: '0.02em', marginTop: '8px', lineHeight: 1.4 }}>
              These become <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>career skills</span> — permanently
              cheaper to train. Skills listed in both CAREER and ADDED grant <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>Rank 2</span> instead of Rank 1.
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {descText && (
        <div style={{ marginBottom: '18px' }}>
          <div style={fcSectionHeaderStyle(accent)}>DESCRIPTION</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'var(--doc-ink)', maxWidth: 'var(--desk-prose-max)' }}>
            {fcSpecDescNode(spec)}
          </div>
        </div>
      )}

      {/* Starting Talents */}
      {row0Talents.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={fcSectionHeaderStyle(accent)}>TOP-ROW TALENTS</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--doc-ink-muted)', letterSpacing: '0.04em', marginBottom: '8px', lineHeight: 1.5 }}>
            The cheapest talents in this specialization — 5 XP each, and the entry point to its tree.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {row0Talents.map(function(t, i) {
              return (
                <div key={i} style={{
                  background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
                  borderRadius: '6px', padding: '10px 12px',
                }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--doc-ink)', marginBottom: t.descriptionRaw ? '4px' : 0 }}>{t.name}</div>
                  {t.descriptionRaw && (
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--doc-ink-muted)' }}>
                      {glyphs(cleanMarkup(t.descriptionRaw))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Species-requirement warning */}
      {requiresSpeciesSwitch && (
        <div style={{
          marginBottom: '16px',
          background: 'var(--doc-paper-raised)',
          border: '1px solid var(--doc-rule)',
          borderRadius: '6px',
          padding: '14px',
        }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.12em', color: 'var(--doc-accent)', marginBottom: '8px' }}>SPECIES REQUIREMENT</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--doc-ink)' }}>
            <strong>{spec.name}</strong> requires the <strong>{requiredSpecies.name}</strong> species. Your current species is <strong>{currentSpecies.name}</strong>. Selecting this specialization will switch your species to {requiredSpecies.name}.
          </div>
        </div>
      )}

      {/* B-41: the printed Force-rating prerequisite — Knight/General 2 (RotS p.26, CotR p.28),
         Master 3 (CotR p.29). Unlike restriction_note above (which informs only), this one is
         STRUCTURE — what a legal character IS — so it gates, and says so in the same shape the
         species requirement already uses. */}
      {minForceRatingOf(spec) > 0 && !meetsForceRating(spec, forceRating) && (
        <div style={{
          marginBottom: '16px',
          background: 'var(--doc-paper-raised)',
          border: '1px solid var(--doc-rule)',
          borderRadius: '6px',
          padding: '14px',
        }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.12em', color: 'var(--doc-accent)', marginBottom: '8px' }}>FORCE RATING REQUIREMENT</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--doc-ink)' }}>
            <strong>{spec.name}</strong> requires a Force rating of <strong>{minForceRatingOf(spec)}</strong>. Yours is <strong>{Number(forceRating) || 0}</strong>. Raise it with the Force Rating talent in a specialization tree, then take this one.
          </div>
        </div>
      )}

      {/* THE SHORT PATH TO POWER (R-SPP-1): the offer sits on the refusal it answers. Only on a JEDI
          spec blocked by FR 2, when CotR is active (Master's FR 3 gets the requirement above and NO
          offer — R-SPP-3, the boundary teaches itself). Built from the approved mockup
          (Docs/mockups/short-path-to-power.html, JASON APPROVED 2026-08-14 @ 35c95d1). */}
      {shortPathOfferedFor(spec, career, forceRating) && !shortPathTaken && (
        <div data-testid="short-path-offer" style={{
          marginBottom: '16px', borderRadius: '6px', overflow: 'hidden',
          background: 'var(--doc-paper-raised)',
          border: '1px solid var(--doc-rule)', borderLeft: '3px solid var(--doc-accent)', padding: '14px',
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.09em', color: 'var(--doc-accent)', marginBottom: '7px' }}>THE SHORT PATH TO POWER</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--doc-ink)', marginBottom: '10px' }}>
            Your training was cut short by the war. Spend {SHORT_PATH_XP} XP now for a rank of the Force Rating talent, starting you at Force rating 2.
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.09em', color: 'var(--doc-ink-muted)', marginBottom: '3px' }}>
            COSTS {SHORT_PATH_XP} OF YOUR {Number(xpPool) || 0} XP · {Math.max(0, (Number(xpRemaining) || 0) - SHORT_PATH_XP)} LEFT TO BUILD WITH
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.09em', color: 'var(--doc-ink-muted)', marginBottom: '12px' }}>YOUR GM MUST ALLOW THIS OPTION.</div>
          <button type="button" data-testid="short-path-take" disabled={(Number(xpRemaining) || 0) < SHORT_PATH_XP}
            onClick={function() { if ((Number(xpRemaining) || 0) >= SHORT_PATH_XP && onShortPathTake) onShortPathTake(); }}
            style={{
              width: '100%', minHeight: '44px', borderRadius: '8px', border: 0,
              cursor: ((Number(xpRemaining) || 0) < SHORT_PATH_XP) ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.08em',
              color: 'var(--doc-action-ink)', opacity: ((Number(xpRemaining) || 0) < SHORT_PATH_XP) ? 0.5 : 1,
              background: 'var(--doc-action)',
            }}>
            {(Number(xpRemaining) || 0) < SHORT_PATH_XP ? ('NEEDS ' + SHORT_PATH_XP + ' XP') : ('TAKE THE SHORT PATH — ' + SHORT_PATH_XP + ' XP')}
          </button>
        </div>
      )}

      {/* Taken state (R-SPP-7): the buy is visible and undoable on the specs it unlocked. Absent on
          Master, which the path does not reach. */}
      {shortPathTaken && career && career.id === 'jedi' && minForceRatingOf(spec) === 2 && (
        <div data-testid="short-path-taken" style={{
          marginBottom: '16px', borderRadius: '6px', padding: '12px 14px',
          background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.09em', color: 'var(--doc-ink)' }}>
            ✦ FORCE RATING 2 · SHORT PATH TAKEN ({SHORT_PATH_XP} XP)
          </span>
          <button type="button" data-testid="short-path-undo" onClick={function() { if (onShortPathUndo) onShortPathUndo(); }}
            style={{
              marginLeft: 'auto', minHeight: '44px', borderRadius: '7px', cursor: 'pointer', padding: '0 16px',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xs)', letterSpacing: '0.07em',
              color: 'var(--doc-ink-muted)', background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
            }}>UNDO</button>
        </div>
      )}

      <div style={{ margin: '0 -16px' }}>
        <FCBottomAction
          label={minForceRatingOf(spec) > 0 && !meetsForceRating(spec, forceRating) ? 'REQUIRES FORCE RATING ' + minForceRatingOf(spec)
                 : !allChoicesPicked ? 'PICK YOUR CAREER SKILL TO CONTINUE'
                 : requiresSpeciesSwitch ? 'SWITCH SPECIES & SELECT' : 'SELECT THIS SPECIALIZATION'}
          disabled={!allChoicesPicked || !meetsForceRating(spec, forceRating)}
          onClick={function() { onConfirm(spec, requiresSpeciesSwitch ? requiredSpecies : null, choicePicks); }}
          accent={accent}
          revealed={ctaRevealed}
        />
      </div>
    </div>
  );
}

/* ── Species free-choice skill grant — ONE home, and the home is the SHARED CORE ──────────────
   The species bonus-skill pick (Human 2×non-career, Geonosian 1×any, Klatooinian 1×non-career,
   Mandalorian-Human combat/knowledge, Xi'Dec Constructor Athletics-or-Mechanics). RAW REQUIRES it —
   "of their choice" (EotE p.48). The rule used to be implemented HERE, which made it the one species
   exception resolved inside the creator instead of beside the other option keys in `charResolve.js`
   — invisible to the exception census and at the mercy of every creator revision. The two functions
   below are now THIN WRAPPERS over `CharResolve.speciesChoiceGrants` / `speciesChoicePool`; the
   custom creator, Guided Build and the picker all still call these names, so no call site moved.
   The grant resolves from (species, picks) — the banked `speciesOption.picks` — never from a copied
   grant list. First-grant-only stays (B-37 latent: no species carries two record-level grants). */
function speciesChoiceGrantOf(speciesData, speciesOption) {
  var picks = (speciesOption && speciesOption.picks) || {};
  var g = window.CharResolve ? window.CharResolve.speciesChoiceGrants(speciesData, picks) : [];
  return g.length ? g[0] : null;
}
function speciesChoicePoolOf(grant, charbuild, careerStatusIds) {
  return window.CharResolve ? window.CharResolve.speciesChoicePool(grant, charbuild, careerStatusIds) : [];
}

/* ── FC Skill Picker ─────────────────────────────────── */

/* THE PICKER'S THREE AUTHORED STRINGS, HOISTED TO ONE HOME (B-750) — and the reason is that the PAPER
   face needs two of them from OUTSIDE the component.

   Frame AE draws the picker's act in the page's own act row (`hp-phoneact`), which is the HOST's
   furniture and not the picker's, so the confirm word has to be reachable from `QuickBuild`. Copying it
   there would be a second spelling of one word in one file — the defect `paperPocketBeat` was lifted out
   of `fcReviewCreditsPlate` to close one row ago (B-723). So the words live here and every face reads
   them: the dark `FCBottomAction`, the paper row's `hp-why`, and the paper act row.

   B-756 ITEM (1), RULED KEEP-AS-DRAWN (release-r7-bug-burndown ADDENDUM 85): the approved §D frames draw the act
   ABSENT until the pick completes (AC/AD; the act on AE), and the counter states what remains, so there is no press
   to refuse. `FCBottomAction` keeps `revealed={count === max}`; the 'SELECT n MORE' label it could never show is
   DELETED (the dead branch PRAXIS §6 names) — the act reads CONFIRM, the one word both faces print.

   ⚠ AND ONE COST, REPORTED RATHER THAN PAID SILENTLY: the lab board derives these two labels and the
   picks-used pair by reading JSX TEXT off this component's own lines (`qsg_build.py`'s `_PK_ACT` and
   `_PK_LIM`, which `raise SystemExit` when the shape moves). Hoisting them moves those lines. The board
   is PINNED at lab `18301f1f` and reads carry at a pinned sha, so nothing is broken today; a later lab
   lap that re-points at a newer carry will stop on those two reads and re-take them from here. That is
   the aging-figures rule doing its job — a hard stop instead of a stale number. */
var FC_PICK_CONFIRM = 'CONFIRM';
var FC_PICK_LIMIT_CHIP = 'PICKS USED';
var FC_PICK_LIMIT_WHY = 'Deselect one to take this instead';

function FCSkillPicker({ skills, skillNames, skillSheetKeys, selected, max, accent, otherSelected, preRankedSkills, currentRanks, rankCap, skillCaps, onToggle, onConfirm, paper }) {
  // skills = array of CANONICAL skill ids to choose from
  // selected = array of currently selected canonical ids
  // otherSelected = array of ids already selected in another round (for overlap display)
  // skillSheetKeys bridges canonical id -> the legacy SHEET key used by the pools /
  // the vocab's skill descriptions / the species-grant state (3b migrates that keyspace).
  // currentRanks + rankCap (CR-RAW #1): sheetKey -> already-committed creation rank; a row at/over
  // the cap is disabled — RAW's "may not train above rank 2 at character creation" (EotE p.48).
  // B-55: ALL THREE creator instances now pass these. They used not to, and the gap shipped illegal
  // characters — species free rank + career pick + spec pick on one skill reached 3. The component
  // always supported the check; two of three call sites simply never armed it.
  // skillCaps = per-skill override (B-19: Corellian Piloting -> 3, Suns of Fortune p.95), the SAME
  // source and `caps[key] || CREATION_CAP` idiom FCSkillBuyList uses. Without it a flat cap of 2
  // would BLOCK a legal rank-3 pick — the overcorrection R-DT29-1 exists to prevent.
  // B-18 self-heal (defense in depth): count ONLY selections that are actually OFFERED here. A stale
  // id left over from a prior subject (a career/spec change) is never in `skills`, so it can't count
  // toward `max` — no "4 OF 3" lockout. Deselecting real rows stays live; the honest count gates CONFIRM.
  var _offered = {};
  (skills || []).forEach(function(id) { _offered[id] = true; });
  var count = (selected || []).filter(function(id) { return _offered[id]; }).length;
  var charAbbr = { BR: 'Brawn', AG: 'Agility', INT: 'Intellect', CUN: 'Cunning', WIL: 'Willpower', PR: 'Presence' };

  /* ── ONE ROW, ONE SET OF FACTS (B-750) — extracted because there are now TWO faces and a row's
     state is BEHAVIOUR, not decoration. `atCap` and `canSelect` are the RAW creation cap (EotE p.48)
     and the pick limit; a paper branch that recomputed them beside the dark one is the shape that
     ships two answers to one question. Not one term changed in the lift — the dark map below reads
     exactly these values, in this order, and `smoke_skills_transplant` / `smoke_b14_guided_species`
     drive the dark picker to prove the lift inert. */
  function rowFacts(sk) {
    var sheetKey = (skillSheetKeys && skillSheetKeys[sk]) || sk;
    var curRank = currentRanks ? (currentRanks[sheetKey] || 0) : 0;
    var isSelected = selected.indexOf(sk) >= 0;
    var effCap = (skillCaps && skillCaps[sheetKey]) || rankCap;
    var atCap = effCap != null && curRank >= effCap && !isSelected;
    return {
      sheetKey: sheetKey,
      name: (skillNames && skillNames[sk]) || sk,
      charKey: skillBaseAbbr(sheetKey) || '?',
      isSelected: isSelected,
      isOtherSelected: !!(otherSelected && otherSelected.indexOf(sk) >= 0),
      preRank: preRankedSkills ? (preRankedSkills[sheetKey] || 0) : 0,
      curRank: curRank,
      effCap: effCap,
      atCap: atCap,
      canSelect: !atCap && (isSelected || count < max),
      desc: skillDescFor(sheetKey),
    };
  }

  /* THE COUNTER, ONE HOME AND ONE SENTENCE. It is the only visible statement of what remains (see
     the B-756 (1) note above `FC_PICK_CONFIRM`), so the two faces must not be able to print two different
     ones. Left as a JSX TEXT LINE rather than a string because that is what it is on both faces — and
     because the lab board derives the counter's FORMAT by matching that line's shape, so a string
     would read to it as a reworded counter. */
  var _pickCounter = (
    <React.Fragment>
      {count} OF {max} SELECTED
    </React.Fragment>
  );

  /* ══ GUIDED'S GRANT PICK ON PAPER (B-750; frames AC · AD · AE of lab `18301f1f` §D, Jason-approved
     2026-09-21) ══════════════════════════════════════════════════════════════════════════════════
     R-QSG-3's device on the one component Guided and the full creator SHARE: the same component, the
     same state, the same writers, the same testids, and an `if (paper)` branch drawing the board's
     markup — `SellSheet`'s pattern (and `VesselSheet`'s, until the Ship Yard's S5 flip deleted it), not a second kernel and not a player-side copy.
     FOUR MOUNTS EXIST and this branch lights ONE: Guided's. The creator's three keep the dark face
     until their own row draws them.

     THE ROW IS THE REGISTER ROW, the object this board and the creator board already agree on: one
     strong selection state (a red spine and the designation in red — no checkbox, no tag, no rank
     chip, which `build.py`'s `skill_row` deleted from this exact subject), the reason line under a row
     that cannot be taken, and the tab carrying the row's own mark. THE TWO REFUSAL INKS ARE THE KIT'S
     AND MEAN DIFFERENT THINGS (`holocron.css` §14): red is a price you cannot meet YET, ink-2 is a
     limit you have REACHED. Picks-used is the second — nothing clears it but taking a pick back — so
     the row takes `data-limit="1"`, whose ink-2 spine and dashed tab the kit already draws.

     THE DESCRIPTION IS ON EVERY ROW, which is B-79/B-80's whole subject: the shipped picker used to
     reveal it only once a row was selected, a closed chain that left exactly half the list unreadable
     at the moment of deciding. It is unconditional in the product now, so it is unconditional here.

     THE COLUMN COUNT IS A CONSEQUENCE OF THE READING MEASURE, never a band — `qsg.css`'s `.gb-pick`
     carries the app's own `repeat(auto-fit, minmax(min(--desk-prose-max, 100%), 1fr))`, so the phone
     and the desk are ONE drawing asked the same question. Inside GUIDED the answer is always ONE
     column, because `QB_STEP_COLUMN_MAX` is `--desk-read-max` and two measures plus a gutter do not
     fit in it; that is arithmetic between three tokens and `qsg.css` says so where the rule lives.

     THREE ROW STATES ARE DRAWN AND THREE ANNOTATIONS ARE NOT, and the three that are not are
     STRUCTURALLY UNREACHABLE AT THIS MOUNT rather than skipped: Guided passes `otherSelected={[]}` and
     no `preRankedSkills`, so the CAREER and SPECIES chips belong to the creator's three mounts; and
     at-cap cannot occur, because every rank Guided has committed by step 3 is a career skill and a
     non-career pool excludes them. Their BEHAVIOUR is untouched — `atCap` still closes a row through
     the shared `rowFacts` above — but no paper ANNOTATION is invented for a state no approved frame
     draws (R-MU1). `smoke_qsg_transplant` asserts all three unreachable from the registries; when the
     creator's mounts take this face, their row draws them and their frames say how. */
  if (paper) {
    return (
      <div className="gb-pickwrap">
        <div className="hp-none gb-pickcount" data-tour-id="p-custom-skills-counter"
          role="status" aria-live="polite">{_pickCounter}</div>
        <div className="hp-plate" data-pad="bleed">
          <div className="gb-pick" data-skill-list="1">
            {skills.map(function (sk, idx) {
              var f = rowFacts(sk);
              var limit = !f.isSelected && !f.atCap && !f.canSelect;
              return (
                <button type="button" key={sk} className="hp-row gb-pickrow" data-noplate="1"
                  data-skill-row={sk}
                  data-tour-id={idx === 0 ? 'p-custom-skills-card' : undefined}
                  data-selected={f.isSelected ? '1' : undefined}
                  data-limit={limit ? '1' : undefined}
                  aria-pressed={f.isSelected}
                  aria-label={(f.isSelected ? 'Selected' : 'Not selected') + ': ' + f.name
                              + ' (' + (charAbbr[f.charKey] || f.charKey) + ')'}
                  onClick={function () { if (f.canSelect || f.isSelected) onToggle(sk); }}>
                  <span className="hp-row-body">
                    <span className="hp-row-desig">{f.name}</span>
                    {/* THE CHARACTERISTIC IS SPELLED, not abbreviated — `build.py`'s B-427: three
                        different abbreviation tables for six words lived on this board's neighbours
                        and two of them disagreed. The word is the component's OWN `charAbbr`, cased
                        up; no second table. */}
                    <span className="hp-none" data-tour-id={idx === 0 ? 'p-custom-skills-char' : undefined}>
                      {(charAbbr[f.charKey] || f.charKey).toUpperCase()}</span>
                    {f.desc ? <span className="gb-d" data-skill-desc={sk}>{f.desc}</span> : null}
                    {limit ? (
                      <span className="hp-why">{FC_PICK_LIMIT_CHIP}{' · '}{FC_PICK_LIMIT_WHY}</span>
                    ) : null}
                  </span>
                  <span className="hp-row-tab">{'›'}</span>
                </button>);
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── THE COMPOSITION (C11; ruling `c11-skills-band-composition` Q1 = (c)) ──────────────────────
  //
  // TWO COLUMNS, EACH AT THE PROSE MEASURE — and the threshold is the drawing's own arithmetic
  // rather than a number anyone chose. The Phase B mockup grids this list two-up above 760px; two
  // prose columns plus a gutter IS 760px. Read that way the drawing and the token agree, and the
  // decision fits on one screen without a scroll. Read LITERALLY — the list spanning the step's
  // full desktop width — each description MEASURES 89 characters per line at 1194 and 86 at 1440
  // (run as this lap's named mutant, not estimated: the brief's ~93 came from the token's mean
  // advance and the DOM says 89). Either way it is far outside the 45-75 band that
  // `--desk-prose-max` was calibrated over 724,066 characters to hold — on the prose that is this
  // lap's entire subject.
  //
  // DERIVED IN CSS, NEVER TYPED. `repeat(auto-fit, minmax(prose, 1fr))` makes the column count a
  // CONSEQUENCE of the token: two columns appear exactly when the container can hold two at the
  // measure, and if B-87's token is ever recalibrated the composition recomputes instead of
  // staling. A hardcoded 770px breakpoint would be the 66ch defect reborn as a media query.
  // `min(…, 100%)` keeps a narrow container from overflowing rather than clamping the track.
  //
  // A4: the column count is a geometry-only flip on an ALWAYS-MOUNTED container — no conditional
  // mount, no remount on a rotation across the threshold. C7's regression class, not repeated.
  //
  // The +24px is this list's own 12px gutters (border-box), added so the PROSE columns land on the
  // token rather than 12px short of it. The cap lives on the picker root so the counter and the
  // CONFIRM bar sit inside the same measure — C10's lesson: furniture that stretches past the
  // content it belongs to re-commits the fault the measure exists to prevent.
  var FC_SKILL_COMPOSED_MAX = 'calc(2 * var(--desk-prose-max) + var(--desk-gutter) + 24px)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out',
                  width: '100%', maxWidth: FC_SKILL_COMPOSED_MAX, margin: '0 auto' }}>
      <div data-tour-id="p-custom-skills-counter" role="status" aria-live="polite" style={{
        padding: '0 16px 8px',
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
        letterSpacing: '0.18em', color: 'rgba(255,255,255,0.7)',
      }}>
        {/* 9px was visually ambiguous — "0 OF 4" read as "8 OF 4" at
           iPhone-13 viewing distance (vet-r1 P1). 11px + wider tracking.
           B-750: the sentence itself is `_pickCounter` above, shared with the paper face. */}
        {_pickCounter}
      </div>
      <div data-skill-list="1" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(var(--desk-prose-max), 100%), 1fr))',
        columnGap: 'var(--desk-gutter)', rowGap: '4px', alignItems: 'start',
        padding: '0 12px 16px',
      }}>
        {skills.map(function(sk, idx) {
          // B-750: the row's facts come from `rowFacts` above, which the paper branch also reads —
          // one derive, two faces. Not one term of the arithmetic moved in the lift.
          var _f = rowFacts(sk);
          var sheetKey = _f.sheetKey;
          var name = _f.name;
          var charKey = _f.charKey;
          var isSelected = _f.isSelected;
          var isOtherSelected = _f.isOtherSelected;
          var preRank = _f.preRank;
          var curRank = _f.curRank;
          var effCap = _f.effCap;
          var atCap = _f.atCap;
          var canSelect = _f.canSelect;
          var desc = _f.desc;
          var pickerCharProp = ({BR:'brawn',AG:'agility',INT:'intellect',CUN:'cunning',WIL:'willpower',PR:'presence'})[charKey] || '';
          var pickerCharHue = FC_CHAR_COLORS[pickerCharProp] || accent;

          return (
            <button key={sk} onClick={function() { if (canSelect || isSelected) onToggle(sk); }}
            data-skill-row={sk}
            data-tour-id={idx === 0 ? 'p-custom-skills-card' : undefined}
            aria-pressed={isSelected}
            aria-label={(isSelected ? 'Selected' : 'Not selected') + ': ' + name + ' (' + (charAbbr[charKey] || charKey) + ')'}
            style={{
              width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px',
              padding: isSelected ? '14px 14px' : '12px 14px',
              background: isSelected ? pickerCharHue + '14' : pickerCharHue + '06',
              border: '1px solid ' + (isSelected ? pickerCharHue + '55' : pickerCharHue + '22'),
              borderLeft: '3px solid ' + (isSelected ? pickerCharHue : pickerCharHue + '88'),
              borderRadius: '0 6px 6px 0', cursor: canSelect || isSelected ? 'pointer' : 'default',
              // B-81: THE DIMMING NARROWS TO THE STATE THAT EARNS IT. It used to cover every
              // unselectable row — including the ones unselectable only because the player has
              // already made their picks, which is a REVERSIBLE state and the most common one on
              // this screen (the list is always exactly twice the picks — B-80). Fading a row a
              // player can have by deselecting another reads as "gone", and it takes the
              // description down to 40% with it. AT-CAP keeps the fade because it is genuinely
              // closed at creation: RAW caps a creation rank at 2 and nothing on this screen
              // reopens it. Reversible states stay legible and say what to do; closed ones dim.
              opacity: atCap && !isSelected ? 0.4 : 1,
              transition: 'border-color 0.25s, background 0.2s, box-shadow 0.25s', textAlign: 'left',
              boxShadow: isSelected ? '0 0 10px ' + pickerCharHue + '22' : 'none',
            }}>
              {/* Checkbox */}
              <div style={{
                width: '22px', height: '22px', borderRadius: '4px', flexShrink: 0, marginTop: '1px',
                border: '2px solid ' + (isSelected ? accent : 'rgba(255,255,255,0.15)'),
                background: isSelected ? accent : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s, border-color 0.15s',
              }}>
                {isSelected && <svg width="12" height="12" viewBox="0 0 16 16"><polyline points="3,8 7,12 13,4" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              {/* Skill info */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: isSelected ? '14px' : '13px', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>{name}</div>
                  <div data-tour-id={idx === 0 ? 'p-custom-skills-char' : undefined} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: pickerCharHue, letterSpacing: '0.14em', flexShrink: 0, fontWeight: 700 }}>
                    {charAbbr[charKey] || charKey}
                  </div>
                </div>
                {atCap && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em', padding: '2px 6px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '2px' }}>RANK {curRank}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.04em' }}>At the rank-{effCap} creation cap</span>
                  </div>
                )}
                {isOtherSelected && !isSelected && !atCap && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: accent, letterSpacing: '0.12em', padding: '2px 6px', border: '1px solid ' + accent + '50', borderRadius: '2px', background: accent + '10' }}>CAREER</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.04em' }}>Rank 1 — selecting grants Rank 2</span>
                  </div>
                )}
                {preRank > 0 && !isOtherSelected && !isSelected && !atCap && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: accent, letterSpacing: '0.12em', padding: '2px 6px', border: '1px solid ' + accent + '50', borderRadius: '2px', background: accent + '10' }}>SPECIES</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.04em' }}>Rank {preRank} — selecting grants Rank {preRank + 1}</span>
                  </div>
                )}
                {/* B-81 — THE PICK-LIMIT ROW SAYS WHY, in the idiom the at-cap row above already
                    uses. Twelve lines up, this same component has always explained one inert state
                    (`At the rank-{n} creation cap`) and said nothing at all about the other — one
                    disabled state with a reason, one without, in the same list. This is that
                    pattern applied, not a new one invented (B-90's shown-with-reason idiom).
                    It names the way OUT, because unlike the cap this state is the player's own and
                    they can undo it. */}
                {!isSelected && !atCap && !canSelect && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.12em', padding: '2px 6px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '2px', whiteSpace: 'nowrap' }}>{FC_PICK_LIMIT_CHIP}</span>
                    {/* The COUNT is not repeated here. "{count} of {max} chosen" is what the
                        counter at the top of the list already says, and printing it again on every
                        locked row is noise that squeezed the chip onto two lines at 390px. The row
                        owes the reader one thing the counter cannot give: what to DO about it.
                        `flexWrap` matches the drawing's own chip class rather than assuming one
                        line always fits — a longer translation would break that assumption. */}
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.04em' }}>{FC_PICK_LIMIT_WHY}</span>
                  </div>
                )}
                {/* B-79/B-80 — THE DESCRIPTION IS THE ROW, not a reward for choosing it.
                    This used to read `isSelected && desc`, the last link in a closed chain:
                    description ← selection ← click ← `canSelect`. At `count === max` that chain
                    shuts, so every unselected row sat inert with its text unreachable — and the
                    unreadable share is invariantly ONE HALF (B-80: 13 careers list 8 and grant 4,
                    the other 7 list 6 and grant 3). A player could not read their way to a
                    decision; they guessed, committed, read, and undid.
                    All 35 descriptions are authored, novice-facing prose written for exactly this
                    moment. The fix is SUBTRACTION of a lock, not addition of chrome — same
                    counter, same checkbox, same characteristic hue (ruling `creator-skills-mockup`).
                    It reaches the SPECIALIZATION picker by construction, where every row can be a
                    Rank 2: one component, two doors. */}
                {desc && (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div data-skill-desc={sk} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'rgba(255,255,255,0.65)' }}>
                      {desc}
                    </div>
                    {/* Still gated on SELECTED: the unselected case is already carried by the
                        CAREER chip above ("Rank 1 — selecting grants Rank 2"), and ungating this
                        would print the same fact twice on one row. */}
                    {isSelected && isOtherSelected && (
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: accent, letterSpacing: '0.04em', marginTop: '2px' }}>
                        ★ Already Rank 1 from career — this grants Rank 2
                      </div>
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <FCBottomAction label={FC_PICK_CONFIRM} onClick={onConfirm} disabled={count !== max} accent={accent} revealed={count === max} />
    </div>
  );
}

/* ── Talent-choice skill picker (B-181) ──────────────────────────────────────────────────────
   The pick is INSIDE the purchase (R-TCPK-1): choosing which skills a choice-talent (Well Rounded,
   Well Read) makes into career skills happens in one focused overlay, atomically with the buy. Two
   MODES: 'buy' (creation / advancement — the header shows the XP cost and CONFIRM completes the
   purchase) and 'choose' (the legacy migration, ruling talent-choice-legacy-migration §8 — the
   talent is already OWNED, so no XP is shown or charged and CONFIRM only files the choice). A reading
   strip (R-TCPK-5..8) between the chips and the pinned confirm bar shows any focused skill's
   description, its characteristic in the standing colour (FC_CHAR_COLORS) and the page cite verbatim
   — definitions come through the ONE shared CharResolve.skillDescription (skillDescFor), never a
   second copy (C-SDS-1). The strip GROWS TO FIT (Jason override of R-TCPK-6's fixed-height scroll);
   the confirm bar is PINNED so a taller strip never shifts it. Pool-parametric via speciesChoicePoolOf
   (any / knowledge / …) — the SAME resolver the species/spec pickers use, which throws on an undefined
   category so data drift is loud, not a silent over-grant. Already-career skills in the pool are shown
   for context but disabled (making an already-career skill a career skill grants nothing). Wired by
   both mounts: the creator talent step and the sheet ADVANCEMENT tree. */
function TalentSkillChoicePicker({ talentName, grants, charbuild, careerStatusIds, cost, mode, initialPicks, accent, onConfirm, onCancel }) {
  var _sel = useState(function() { return (initialPicks || []).slice(); });
  var selected = _sel[0], setSelected = _sel[1];
  var _foc = useState(null), focused = _foc[0], setFocused = _foc[1];
  var desktop = useLayoutMode() === 'desktop';
  var names = (charbuild && charbuild.skillNames) || {};
  var groups = (charbuild && charbuild.skillGroups) || {};
  var sheetKeys = (charbuild && charbuild.skillSheetKeys) || {};
  var cites = (charbuild && charbuild.skillCites) || {};
  // The pool = the union of every grant's pool, each grant resolved through the SAME species picker
  // core (one mechanism; unresolved category throws upstream). Deduped, order preserved.
  var pool = React.useMemo(function() {
    var out = [], seen = {};
    (grants || []).forEach(function(g) {
      speciesChoicePoolOf(g, charbuild, careerStatusIds).forEach(function(id) {
        if (!seen[id]) { seen[id] = true; out.push(id); }
      });
    });
    return out;
  }, [grants, charbuild, careerStatusIds]);
  var max = (grants || []).reduce(function(n, g) { return n + (Number(g.count) || 0); }, 0);
  // Novice-first arrangement: bucket the pool by category (general/combat/knowledge/social).
  var byGroup = React.useMemo(function() {
    var order = ['general', 'combat', 'knowledge', 'social'], buckets = {};
    pool.forEach(function(id) { var g = groups[id] || 'general'; (buckets[g] = buckets[g] || []).push(id); });
    return order.filter(function(g) { return buckets[g]; }).map(function(g) { return { group: g, ids: buckets[g] }; });
  }, [pool, groups]);
  function toggle(id) {
    setFocused(id);
    setSelected(function(prev) {
      if (prev.indexOf(id) >= 0) return prev.filter(function(k) { return k !== id; });
      if (prev.length >= max) return prev;                // honest cap at the grant count
      return prev.concat([id]);
    });
  }
  // The strip resolves the focused skill, else the most-recent selection, else the prompt.
  var stripId = focused || (selected.length ? selected[selected.length - 1] : null);
  var strip = null;
  if (stripId) {
    var sk = sheetKeys[stripId] || stripId;
    var abbr = skillBaseAbbr(sk, charbuild);
    var word = ({ BR: 'brawn', AG: 'agility', INT: 'intellect', CUN: 'cunning', WIL: 'willpower', PR: 'presence' })[abbr] || '';
    strip = { name: names[stripId] || stripId, desc: skillDescFor(sk), cite: cites[sk] || '', abbr: abbr, hue: FC_CHAR_COLORS[word] || accent };
  }
  var canConfirm = max > 0 && selected.length === max;
  var GROUP_LABEL = { general: 'GENERAL', combat: 'COMBAT', knowledge: 'KNOWLEDGE', social: 'SOCIAL' };
  return (
    <div role="dialog" aria-modal="true" aria-label={(talentName || 'Talent') + ' — choose your skills'}
      style={{ position: 'fixed', inset: 0, zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={onCancel} style={{ position: 'absolute', inset: 0, background: 'rgba(4,6,9,0.72)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: desktop ? '900px' : '560px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        border: '1px solid ' + accent + '52', borderRadius: '14px', background: '#0c111b',
        boxShadow: '0 24px 60px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
        {/* Header — talent + count + (buy mode) the XP cost */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#fff', letterSpacing: '0.03em' }}>
            {(talentName || 'TALENT').toUpperCase()} — CHOOSE YOUR SKILLS
            {mode === 'buy' && cost != null && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text-caution)', marginLeft: '10px' }}>{cost + ' XP'}</span>}
          </div>
          <div role="status" aria-live="polite" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.12em', color: 'var(--text-caution)', flexShrink: 0 }}>{selected.length} OF {max}</div>
        </div>
        {/* Binding line — the permanence, stated (R-TCPK-2, C-B181-1) */}
        <div style={{ fontSize: 'var(--fs-xs)', lineHeight: 1.5, color: 'rgba(255,255,255,0.74)', padding: '11px 16px', background: 'rgba(255,200,87,0.05)', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          {"This choice is bound to this purchase. Refund the talent and the choice goes with it; it can't be changed while you own the rank."}
        </div>
        {/* Main — chips + reading strip; scrolls, footer stays pinned */}
        <div style={{ display: 'flex', flexDirection: desktop ? 'row' : 'column', alignItems: 'stretch', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <div style={{ flex: desktop ? 1 : 'none', minWidth: 0, padding: '12px 16px' }}>
            {byGroup.map(function(bg) {
              return (
                <div key={bg.group} style={{ marginBottom: '12px' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.42)', marginBottom: '7px' }}>{GROUP_LABEL[bg.group] || bg.group.toUpperCase()}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                    {bg.ids.map(function(id) {
                      var isCareer = !!(careerStatusIds && careerStatusIds[id]);
                      var isChosen = selected.indexOf(id) >= 0;
                      var isFocused = focused === id;
                      var atCap = !isChosen && selected.length >= max;
                      var sk2 = sheetKeys[id] || id;
                      return (
                        <button key={id} type="button" data-skill-chip={id}
                          onClick={function() { toggle(id); }}
                          onFocus={function() { setFocused(id); }}
                          aria-pressed={isChosen}
                          aria-label={(isChosen ? 'Selected' + (isCareer ? ' (already career)' : '') : (isCareer ? 'Already a career skill' : 'Not selected')) + ': ' + (names[id] || id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px', minHeight: '44px', padding: '8px 12px', borderRadius: '9px',
                            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', textAlign: 'left', cursor: 'pointer',
                            border: '1px solid ' + (isChosen ? accent : isFocused ? 'var(--text-info)' : 'rgba(255,255,255,0.16)'),
                            background: isChosen ? accent + '28' : 'rgba(255,255,255,0.03)',
                            color: isChosen ? '#fff' : isCareer ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.86)',
                            borderStyle: isCareer ? 'dashed' : 'solid',
                            boxShadow: isFocused ? '0 0 0 2px rgba(126,196,255,0.5)' : isChosen ? '0 0 14px -4px ' + accent : 'none',
                            opacity: atCap ? 0.55 : 1,
                          }}>
                          {isChosen && <span style={{ color: accent, fontWeight: 700 }} aria-hidden="true">✓</span>}
                          <span>{names[id] || id}</span>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.42)' }}>{isCareer ? 'CAREER' : skillBaseAbbr(sk2, charbuild)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Reading strip — follows focus, rests on the selection, prompts when empty */}
          <div style={{ background: 'rgba(126,196,255,0.04)',
            borderTop: desktop ? 'none' : '1px solid rgba(255,255,255,0.1)',
            borderLeft: desktop ? '1px solid rgba(255,255,255,0.1)' : 'none',
            width: desktop ? '318px' : 'auto', flexShrink: 0, alignSelf: 'stretch' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(126,196,255,0.7)', padding: '9px 16px 5px' }}>SKILL REFERENCE</div>
            <div style={{ padding: '0 16px 14px' }}>
              {strip && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '4px' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#fff' }}>{strip.name}</span>
                    {strip.abbr && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em', border: '1px solid ' + strip.hue, color: strip.hue, borderRadius: '4px', padding: '1px 6px' }}>{strip.abbr}</span>}
                  </div>
                  <div style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'rgba(255,255,255,0.8)' }}>{strip.desc}</div>
                  {strip.cite && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--text-faint)', marginTop: '5px' }}>{strip.cite}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Footer — PINNED; CONFIRM enabled only at exactly the grant count */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 16px', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap', flexShrink: 0,
          flexDirection: desktop ? 'row' : 'column-reverse' }}>
          <button type="button" onClick={onCancel} style={{ minHeight: '46px', padding: '0 18px', borderRadius: '10px', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.04em', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', width: desktop ? 'auto' : '100%' }}>CANCEL</button>
          <button type="button" disabled={!canConfirm} onClick={function() { if (canConfirm) onConfirm(selected.slice()); }}
            style={{ minHeight: '46px', padding: '0 22px', borderRadius: '10px', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.04em',
              background: canConfirm ? accent : 'rgba(255,255,255,0.07)', color: canConfirm ? '#04121f' : 'rgba(255,255,255,0.4)',
              border: 'none', cursor: canConfirm ? 'pointer' : 'default', boxShadow: canConfirm ? '0 0 26px -8px ' + accent : 'none', width: desktop ? 'auto' : '100%' }}>
            {canConfirm ? 'CONFIRM' : 'SELECT ' + (max - selected.length) + ' MORE'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FCTrainingSummary({ careerSkills, specSkills, speciesSkills, skillNames, accent, onConfirm }) {
  var allSkills = {};
  (careerSkills || []).forEach(function(k) { allSkills[k] = (allSkills[k] || 0) + 1; });
  (specSkills || []).forEach(function(k) { allSkills[k] = (allSkills[k] || 0) + 1; });
  (speciesSkills || []).forEach(function(k) { allSkills[k] = (allSkills[k] || 0) + 1; });
  var sorted = Object.keys(allSkills).sort(function(a, b) {
    var na = (skillNames && skillNames[a]) || a;
    var nb = (skillNames && skillNames[b]) || b;
    return na.localeCompare(nb);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out' }}>
      <div style={{ padding: '0 16px 12px' }}>
        <div style={fcSectionHeaderStyle(accent)}>FREE TRAINING RANKS</div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, marginBottom: '12px' }}>
          These skills start with free ranks. Next you can spend starting XP to train more.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', padding: '0 12px 16px' }}>
        {sorted.map(function(k) {
          var name = (skillNames && skillNames[k]) || k;
          var rank = allSkills[k];
          return (
            <div key={k} style={{
              display: 'flex', alignItems: 'center', padding: '10px 14px',
              background: 'rgba(255,255,255,0.03)', borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ flex: 1, fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>{name}</div>
              <div style={{ display: 'flex', gap: '3px' }}>
                {Array.from({ length: rank }).map(function(_, i) {
                  return <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: accent }} />;
                })}
                {Array.from({ length: 5 - rank }).map(function(_, i) {
                  return <div key={i + rank} style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
      <FCBottomAction label="CONTINUE" onClick={onConfirm} accent={accent} />
    </div>
  );
}

/* ── FC Creation Skill Purchase (CR-RAW #2) ──────────────
   Buy skill ranks with starting XP (EotE p.35/p.102): career skills 5 × the new rank,
   non-career +5 more, nothing above rank 2 at creation (p.48). Costs come from THE shared
   CharResolve.skillRankCost (§3.2 one-pricing-path); career STATUS is the career ∪ spec
   union (RAW: spec skills ARE career skills, p.44/p.102 — the sheet's R-CSK1 rule).
   Single-tap buy: refund (−) makes it reversible here, unlike the sheet's permanent
   post-creation buys (which keep their two-tap arm).
   DESIGN (Jason 2026-07-11 — replicate the creator's novice-first information design):
   rows are the FCSkillPicker card idiom verbatim — char-hue color bar, tap-to-expand
   the registry's plain-language explanations, the CAREER chip users learned in the free-picks
   screens (here it carries "why this one is cheaper"). The list is grouped BY CHARACTERISTIC
   (header = the characteristic's name in its hue + the character's current value) so the
   color system carries MEANING: you roll characteristic + skill, and the novice can see
   which stat feeds each skill before spending. */

function FCSkillBuyList({ allSkillIds, skillNames, skillSheetKeys, charSkills, characteristics, careerIds, skillCaps, xpRemaining, accent, onBuy, onRefund, onDone }) {
  var CREATION_CAP = 2;  // EotE p.48 — the global creation cap
  var _skillCaps = skillCaps || {};  // B-19: per-skill overrides (Corellian Piloting → 3, Suns of Fortune p.95)
  var CHAR_ORDER = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
  var CHAR_LABEL = { brawn: 'BRAWN', agility: 'AGILITY', intellect: 'INTELLECT', cunning: 'CUNNING', willpower: 'WILLPOWER', presence: 'PRESENCE' };
  var ABBR_TO_PROP = { BR: 'brawn', AG: 'agility', INT: 'intellect', CUN: 'cunning', WIL: 'willpower', PR: 'presence' };
  var _exp = useState(null), expandedId = _exp[0], setExpandedId = _exp[1];

  function entryOf(id) { return charSkills[(skillSheetKeys && skillSheetKeys[id]) || id]; }
  function rankOf(id) {
    var s = entryOf(id);
    if (!s) return 0;
    return (s.freeCareer ? 1 : 0) + (s.freeSpec ? 1 : 0) + (s.freeSpecies ? 1 : 0) + (s.purchased || 0) + (s.bought ? s.bought.length : 0);
  }
  function boughtCount(id) { var s = entryOf(id); return (s && s.bought) ? s.bought.length : 0; }

  var byName = function(a, b) { return ((skillNames && skillNames[a]) || a).localeCompare((skillNames && skillNames[b]) || b); };
  var groups = {};
  allSkillIds.forEach(function(id) {
    var sheetKey = (skillSheetKeys && skillSheetKeys[id]) || id;
    var prop = ABBR_TO_PROP[skillBaseAbbr(sheetKey)];
    if (!prop) return;  // unknown key = data bug; validator territory, never a silent crash
    (groups[prop] = groups[prop] || []).push(id);
  });
  var spentAny = allSkillIds.some(function(id) { return boughtCount(id) > 0; });

  function chip(text, color, borderAlpha, bgAlpha) {
    return (
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: color, letterSpacing: '0.12em',
        padding: '2px 6px', border: '1px solid ' + color + (borderAlpha || '50'), borderRadius: '2px',
        background: bgAlpha ? color + bgAlpha : 'transparent', flexShrink: 0,
      }}>{text}</span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out' }}>
      {/* The one-line lesson the grouping teaches (mechanics voice, once, not per group). */}
      <div style={{ padding: '0 16px 10px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
        A skill rolls with its characteristic; higher characteristic, bigger pool.
        {/* CHAR-FIRST (ruling char-first-order): the F-10 "buy characteristics first" tip
            (u/littlestminish) is RETIRED — the step order now teaches it by structure
            (characteristics is step 5, before this buy screen). Superseded, not dropped. */}
      </div>
      {CHAR_ORDER.map(function(prop) {
        var ids = (groups[prop] || []).sort(byName);
        if (!ids.length) return null;
        var hue = FC_CHAR_COLORS[prop] || accent;
        var charVal = (characteristics && characteristics[prop]) || 0;
        return (
          <div key={prop} style={{ padding: '0 12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', padding: '0 4px 8px', borderLeft: '3px solid ' + hue, marginLeft: '-2px', paddingLeft: '9px' }}>
              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.18em', color: hue }}>{CHAR_LABEL[prop]}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.55)' }}>YOURS IS {charVal}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {ids.map(function(id) {
                var sheetKey = (skillSheetKeys && skillSheetKeys[id]) || id;
                var name = (skillNames && skillNames[id]) || id;
                var desc = skillDescFor(sheetKey);
                var isCareer = !!careerIds[id];
                var rank = rankOf(id);
                var bought = boughtCount(id);
                var atCap = rank >= (_skillCaps[sheetKey] || CREATION_CAP);
                var cost = atCap ? null : CharResolve.skillRankCost(rank + 1, isCareer);
                var affordable = cost != null && cost <= xpRemaining;
                var isExpanded = expandedId === id;
                return (
                  <div key={id} role="button" tabIndex={0} aria-expanded={isExpanded}
                    aria-label={'Skill: ' + name}
                    onClick={function() { setExpandedId(isExpanded ? null : id); }}
                    onKeyDown={function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedId(isExpanded ? null : id); } }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px',
                      padding: isExpanded ? '14px 14px' : '12px 14px',
                      background: isExpanded ? hue + '14' : hue + '06',
                      border: '1px solid ' + (isExpanded ? hue + '55' : hue + '22'),
                      borderLeft: '3px solid ' + (isExpanded ? hue : hue + '88'),
                      borderRadius: '0 6px 6px 0', cursor: 'pointer', textAlign: 'left',
                      transition: 'border-color 0.25s, background 0.2s, box-shadow 0.25s',
                      boxShadow: isExpanded ? '0 0 10px ' + hue + '22' : 'none',
                    }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: isExpanded ? '14px' : '13px', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>{name}</span>
                        {isCareer && chip('CAREER', accent, '50', '10')}
                      </div>
                      <div style={{ display: 'flex', gap: '3px', marginTop: '6px' }}>
                        {Array.from({ length: rank }).map(function(_, i) {
                          return <div key={i} style={{ width: '7px', height: '7px', borderRadius: '50%', background: accent }} />;
                        })}
                        {Array.from({ length: 5 - rank }).map(function(_, i) {
                          return <div key={i + rank} style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />;
                        })}
                      </div>
                      {isExpanded && desc && (
                        <div style={{ marginTop: '8px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'rgba(255,255,255,0.65)' }}>
                          {desc}
                          {isCareer && (
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: accent, letterSpacing: '0.04em', marginTop: '4px' }}>
                              ★ Career skill — each rank costs 5 XP less
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                      {atCap ? (
                        <span aria-label={'At creation cap: ' + name} style={{
                          fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em',
                          color: 'rgba(255,255,255,0.45)', padding: '4px 8px',
                          border: '1px solid rgba(255,255,255,0.18)', borderRadius: '3px', whiteSpace: 'nowrap',
                        }}>MAX AT CREATION</span>
                      ) : (
                        <button onClick={function(e) { e.stopPropagation(); if (affordable) onBuy(sheetKey, cost); }}
                          aria-label={'Buy rank: ' + name + ' for ' + cost + ' XP'}
                          disabled={!affordable}
                          style={{
                            // B-115 — AS-6's 44px tap floor. Was 34px, and it is this step's own
                            // control: the one a player presses repeatedly, on a phone, to spend
                            // the XP they just budgeted. It was boarded as a tolerated class in the
                            // AS-9 sub-view sweep rather than smoothed over, and its entry in
                            // `SUBVIEW_OFFENDER_CLASSES` is deleted in this same commit — leaving
                            // it would fire the staleness half of that contract, which is the
                            // contract working.
                            minHeight: '44px', padding: '0 10px', borderRadius: '6px',
                            cursor: affordable ? 'pointer' : 'default', opacity: affordable ? 1 : 0.35,
                            background: accent + '14', border: '1px solid ' + accent + '55',
                            fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.08em',
                            color: accent, fontWeight: 700, whiteSpace: 'nowrap',
                          }}>+{cost} XP</button>
                      )}
                      {bought > 0 && (
                        <button onClick={function(e) { e.stopPropagation(); onRefund(sheetKey); }}
                          aria-label={'Refund rank: ' + name}
                          style={{
                            // B-119 — AS-6's 44px floor, and the row exists because NO GUARD COULD
                            // SEE THIS. It renders only when `bought > 0`, and the AS-9 sub-view
                            // sweep measured each sub-view as it ARRIVED — nothing in it ever
                            // bought a rank. So this sat 16px under the floor, beside a buy button
                            // C11 had just raised to 44, invisible to a suite that was green.
                            // The fix is second: LAP 4 taught the driver to buy one rank FIRST
                            // (`_buy_one_rank`), watched the sweep go red naming `Refund rank:
                            // Athletics 55x28`, and only then changed this number — so the
                            // population, not the source read, is what proves it.
                            minHeight: '44px', padding: '0 8px', borderRadius: '6px', cursor: 'pointer',
                            background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
                            fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.08em',
                            color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap',
                          }}>− UNDO</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <FCBottomAction label={spentAny ? 'CONTINUE' : 'CONTINUE WITHOUT BUYING SKILLS'} onClick={onDone} accent={accent} />
    </div>
  );
}

/* ── FC Species-penalty buyout ────────────────────────────
   Ruling `species-followups-creator-moments` §3 (R-SFC-1/2/3) · price + census amended by
   `creation-buyout-count-and-price` (R-CBC-1/2/4) · mockup Docs/mockups/creation-buyout.html.

   35 species records print a sentence offering to remove a penalty for XP at character creation,
   and until now there was nowhere to pay it. This is where the player acts on it. It lives in the
   XP step and NOT on the species page, which is where the sentence is printed: a player choosing a
   species has not met the XP economy yet, so "is this worth 10?" has nothing to be weighed against.
   The printed sentence stays where it is and does the advertising.

   NOTHING HERE IS KEYED TO A NAME OR A PRICE. The offers arrive resolved from the FIELD
   (`CharResolve.speciesBuyoutOffers` → `buyout_xp`), which is what makes this correct for all four
   ability names on the shelf (Primitive, Easily Distracted, Small Comforts, Social Anxiety), for
   both prices (10 and 5), and for the one carrier that exists only on a picked subspecies. A
   hard-coded 10 charges Roonan and Toong double; a name match misses four records. */

// What the blocked control says about the purse, and it NEVER clamps a negative to zero. B-118
// (ruling `creator-characteristics-mockup`) settled this one step over: "0 remaining" against a
// purse of −65 is the honest-looking lie, and C10's own named mutant is exactly that clamp. An
// overdrawn re-entrant is a standing population here (F-45), so this states the overdraw plainly
// in the same words the characteristics step uses.
function shortfallLabel(xpRemaining) {
  return xpRemaining < 0
    ? 'overspent by ' + Math.abs(xpRemaining) + ' XP'
    : xpRemaining + ' remaining';
}

function FCBuyoutBlock({ offers, taken, xpRemaining, accent, onTake, onUndo }) {
  // The block is INVISIBLE when the species makes no offer — 497 of 532 species land here, and a
  // heading over an empty group is furniture teaching nothing.
  if (!offers || !offers.length) return null;
  var _taken = taken || {};

  return (
    <div data-buyout-block="1" style={{ padding: '0 16px 4px' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.2em',
        color: accent + 'BF', margin: '4px 0 8px',
      }}>SPECIES PENALTY</div>
      {offers.map(function(o) {
        var isTaken = !!_taken[o.slug];
        // Affordability is measured against what is left AFTER this purchase is already counted —
        // xpRemaining includes any buyout already taken, so an untaken row compares against the
        // live figure the player can see in the bar directly above it.
        var affordable = o.xp <= xpRemaining;
        return (
          <div key={o.slug} data-buyout-row={o.slug} data-buyout-xp={o.xp} data-buyout-taken={isTaken ? '1' : '0'}
            style={{
              border: '1px solid ' + (isTaken ? 'rgba(78,203,113,0.45)' : 'rgba(255,255,255,0.1)'),
              background: isTaken ? 'rgba(78,203,113,0.07)' : 'rgba(255,255,255,0.03)',
              borderRadius: '9px', padding: '11px 12px', marginBottom: '8px',
              transition: 'border-color 0.25s, background 0.25s',
            }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 'var(--fs-sm)', fontWeight: 700,
              color: '#fff', letterSpacing: '0.03em', textTransform: 'uppercase',
            }}>{o.name}</div>
            {/* The FULL registry description, not a summary — the shipped description-is-the-row
                idiom applied to a purchase. The player meets the penalty's MEANING at the moment of
                the decision, including the book's own closing sentence about the price. */}
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45,
              color: 'rgba(255,255,255,0.62)', marginTop: '5px',
            }}>{o.description}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
              {isTaken ? (
                <React.Fragment>
                  {/* A STATUS, not a control. The mockup draws this as a done-styled button; a
                      button that does nothing when pressed is an a11y defect, and the standard
                      outranks the mockup (C-MU-4). Same pixels, honest semantics — the UNDO beside
                      it is the only control, which is also what the player's finger wants. */}
                  <span role="status" style={{
                    minHeight: '44px', display: 'inline-flex', alignItems: 'center', padding: '9px 15px',
                    borderRadius: '7px', border: '1px solid rgba(78,203,113,0.5)', background: 'rgba(78,203,113,0.12)',
                    fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700,
                    letterSpacing: '0.06em', color: '#4ECB71',
                  }}>REMOVED — {o.xp} XP</span>
                  <button onClick={function() { onUndo(o.slug); }} aria-label={'Undo removing ' + o.name + ' and refund ' + o.xp + ' XP'}
                    style={{
                      minHeight: '44px', padding: '9px 15px', borderRadius: '7px', cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.05)',
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.08em',
                      color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap',
                    }}>UNDO</button>
                </React.Fragment>
              ) : (
                /* B-90's shown-with-reason idiom: an unaffordable control says WHY rather than
                   fading out silently, so a player who has spent down learns what the blocker is
                   instead of finding a dead button. */
                <button onClick={function() { if (affordable) onTake(o.slug, o.xp); }}
                  disabled={!affordable}
                  aria-label={affordable
                    ? ('Remove ' + o.name + ' permanently for ' + o.xp + ' XP')
                    : ('Cannot remove ' + o.name + ': needs ' + o.xp + ' XP, ' + shortfallLabel(xpRemaining))}
                  style={{
                    minHeight: '44px', padding: '9px 15px', borderRadius: '7px',
                    cursor: affordable ? 'pointer' : 'default',
                    border: '1px solid ' + (affordable ? accent + '8C' : 'rgba(255,255,255,0.14)'),
                    background: affordable ? accent + '1F' : 'rgba(255,255,255,0.03)',
                    fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700,
                    letterSpacing: '0.06em', color: affordable ? accent : 'rgba(255,255,255,0.55)',
                  }}>
                  {affordable
                    ? 'REMOVE PERMANENTLY — ' + o.xp + ' XP'
                    : 'NEEDS ' + o.xp + ' XP — ' + shortfallLabel(xpRemaining).toUpperCase()}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── FC Narrative Mechanic ────────────────────────────── */

// Reference Voice descriptions for core obligation/duty/morality types
// B-67(a) / ruling `mechanic-prose-two-sources` Q1=(b): the 129-entry FC_MECHANIC_TYPE_DESC
// constant that used to sit here is RETIRED. Its text was the better of the two bodies the creator
// carried, so it was promoted verbatim INTO the registries by
// `scripts/c9_promote_mechanic_prose.py` (129/129 round-trip identical) and is now resolved from
// the data at BOTH the list and the detail — one source of record, the F-58 precedent.
// The registries are the only home; a UI constant holding 129 descriptions is how the app came to
// have two different texts for the same 129 things without either seat noticing for nine days.
function mechTypeDesc(t) {
  if (!t) return '';
  // C-C9-1: `description` ONLY. This used to fall back to `descriptionRaw`, which held the
  // RETIRED F-EDS-1 text in 129 of these records — a live path to the prose this lap replaced,
  // harmless only for as long as every record happened to have a `description`. The stale
  // field is gone from the three mechanic registries and the fallback goes with it: a reader
  // that can still reach the old text is not one source of record.
  return cleanMarkup(t.description || '').trim();
}
// The LIST gloss: the first sentence of the same text the detail shows in full. Never a second
// text — that is the distinction the ruling turned on.
function mechTypeGloss(t) {
  var d = mechTypeDesc(t);
  if (!d) return '';
  var m = d.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return (m ? m[0] : d).trim();
}

function FCMechanicTypePicker({ types, selected, accent, onSelect }) {
  var _useS = useState(''), search = _useS[0], setSearch = _useS[1];
  var filtered = useMemo(function() {
    if (!search.trim()) return types;
    var q = search.toLowerCase().trim();
    return types.filter(function(t) { return t.name.toLowerCase().indexOf(q) >= 0; });
  }, [types, search]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out' }}>
      <FCSearchBar value={search} onChange={setSearch} accent={accent} placeholder="Search types…" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '0 12px 24px' }}>
        {filtered.map(function(t) {
          var isSelected = selected === t.key;
          return (
            <button key={t.key} onClick={function() { onSelect(t.key); }} style={{
              width: '100%', textAlign: 'left', padding: '14px',
              background: isSelected ? accent + '12' : 'rgba(255,255,255,0.02)',
              border: '1px solid ' + (isSelected ? accent + '50' : 'rgba(255,255,255,0.06)'),
              borderRadius: '6px', cursor: 'pointer',
              transition: 'border-color 0.25s, background 0.2s',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
            }}>
              {/* B-67(a): the GLOSS. This row was a bare name and a chevron, so a newcomer had to
                  tap through 41 obligations to learn what Addiction means versus Betrayal versus
                  Bounty — on the most abstract decision in the creator, the one they are least
                  equipped to make. Every type already carried a description; none of it was on
                  screen until the moment of choosing was already over.
                  ONE TEXT, TWO LENGTHS (ruling `mechanic-prose-two-sources`): the list shows the
                  FIRST SENTENCE, the detail shows the whole thing. Never two different texts a tap
                  apart — that was the option the ruling refused, and it is what showing the
                  registry's old copy here would have produced. */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-sm)', fontWeight: 700, color: isSelected ? accent : '#fff', letterSpacing: '0.05em' }}>
                  {t.name}
                  {/* B-67(b): the shadow, named on the row itself. The arrow is the pairing, not
                      decoration — this is one choice with two halves. */}
                  {t.pairedWeaknessName && (
                    <span style={{ fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: 'var(--fs-xs)', color: 'rgba(255,180,180,0.72)', letterSpacing: '0.02em' }}>
                      {' ↔ ' + t.pairedWeaknessName}
                    </span>
                  )}
                </div>
                {(function() {
                  var g = mechTypeGloss(t);   // the eater's output — drawn, not printed (R3 Q2b)
                  return g ? <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', lineHeight: 1.45, color: 'rgba(255,255,255,0.55)', marginTop: '4px' }}>{glyphs(g)}</div> : null;
                })()}
              </div>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, opacity: 0.4 }}>
                <polyline points="7,4 13,10 7,16" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── FC Mechanic (Obligation/Duty/Morality) Type Detail ── */
function FCMechanicTypeDetail({ typeObj, mechType, accent, onConfirm, weakObj }) {
  if (!typeObj) return null;
  var cleanDesc = mechTypeDesc(typeObj);        // one source: the registry (C9)
  // Veteran review fix: for Morality, surface the paired Emotional Weakness (F&D CRB p.53).
  var weakDesc = weakObj ? cleanMarkup(weakObj.description || weakObj.descriptionRaw || '').trim() : '';
  var mechLabels = { obligation: 'OBLIGATION', duty: 'DUTY', morality: 'MORALITY' };
  var ctaLabel = 'SELECT THIS ' + (mechType === 'morality' ? 'PAIR' : (mechLabels[mechType] || 'TYPE'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: accent, opacity: 0.6, marginBottom: '4px' }}>
          {mechType === 'morality' ? 'EMOTIONAL STRENGTH' : ((mechLabels[mechType] || 'TYPE') + ' · TYPE')}
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-xl)', color: '#fff', letterSpacing: '0.04em' }}>{typeObj.name}</div>
      </div>

      {cleanDesc && (
        <div style={{ marginBottom: weakObj ? '16px' : '20px' }}>
          <div style={fcSectionHeaderStyle(accent)}>{mechType === 'morality' ? 'STRENGTH' : 'DESCRIPTION'}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'rgba(255,255,255,0.65)', whiteSpace: 'pre-line' }}>
            {/* R3 Q2b: reads the eater's output, so it inherits whatever the eater stops removing.
                The obligation/duty/morality registries carry 0 bracket tokens (measured), so this is
                a no-op today and a leak-proofing tomorrow. */}
            {glyphs(cleanDesc)}
          </div>
        </div>
      )}

      {weakObj && (
        <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(255,120,120,0.04)', border: '1px solid rgba(255,120,120,0.18)', borderLeft: '3px solid rgba(255,120,120,0.6)', borderRadius: '6px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: '#FFB4B4', opacity: 0.8, marginBottom: '4px' }}>PAIRED EMOTIONAL WEAKNESS</div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: '#FFE0E0', marginBottom: '6px' }}>{weakObj.name}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.55, color: 'rgba(255,220,220,0.65)', whiteSpace: 'pre-line' }}>
            {glyphs(weakDesc)}
          </div>
        </div>
      )}

      <div style={{ margin: '0 -16px' }}>
        <FCBottomAction label={ctaLabel} onClick={onConfirm} accent={accent} />
      </div>
    </div>
  );
}

function FCTradePanel({ mechType, baseValue, descriptor, trades, tradeMode, selectedTradeIds, onToggleTrade, onConfirm, accent, partySize, onEditPartySize }) {
  // Compute current state
  var totalDelta = 0, xpAdj = 0, crAdj = 0;
  selectedTradeIds.forEach(function(id) {
    var t = trades.find(function(c) { return c.id === id; });
    if (t) { totalDelta += t.valueDelta; xpAdj += t.xpDelta; crAdj += t.creditsDelta; }
  });
  var finalValue = baseValue + totalDelta;

  function tradeLabel(t) {
    if (t.label) return t.label;   // RAW choices carry printed labels (UXQP-20)
    var parts = [];
    if (t.valueDelta > 0) parts.push('+' + t.valueDelta + ' ' + mechType);
    if (t.valueDelta < 0) parts.push(t.valueDelta + ' ' + mechType);
    var gives = [];
    if (t.xpDelta > 0) gives.push('+' + t.xpDelta + ' XP');
    if (t.xpDelta < 0) gives.push(t.xpDelta + ' XP');
    if (t.creditsDelta > 0) gives.push('+' + t.creditsDelta + ' credits');
    if (t.creditsDelta < 0) gives.push(t.creditsDelta + ' credits');
    return parts.join(', ') + ' → ' + gives.join(', ');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 16px' }}>
      {/* Current summary — TP-1: empty-state shows em-dash instead of raw "0"
         rendered in Orbitron, which read as a "prohibited" glyph at low alpha
         and confused users. Labels bumped 7px Share Tech Mono → 10px Rajdhani
         700 for readability. */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xl)', color: accent }}>{finalValue}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase' }}>{mechType}</div>
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xl)', color: xpAdj > 0 ? '#4ECB71' : xpAdj < 0 ? '#FF6B6B' : 'rgba(255,255,255,0.55)' }}>{xpAdj === 0 ? '\u2014' : (xpAdj > 0 ? '+' + xpAdj : xpAdj)}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase' }}>XP Bonus</div>
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xl)', color: crAdj > 0 ? '#4ECB71' : crAdj < 0 ? '#FF6B6B' : 'rgba(255,255,255,0.55)' }}>{crAdj === 0 ? '\u2014' : (crAdj > 0 ? '+' + crAdj : crAdj)}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase' }}>Credits</div>
        </div>
      </div>

      {/* Descriptor */}
      {descriptor && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.25)', marginBottom: '12px' }}>
          TYPE: <span style={{ color: accent }}>{descriptor.toUpperCase()}</span>
        </div>
      )}

      {/* Trade options */}
      <div style={fcSectionHeaderStyle(accent)}>{tradeMode === 'single_pick' ? 'CHOOSE ONE (OR NONE)' : 'OPTIONAL TRADES'}</div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, marginBottom: '12px' }}>
        {tradeMode === 'single_pick'
          ? 'As printed: a starting character may take ONE of these, or none. Picking another swaps your choice.'
          : 'Take on extra ' + mechType + ' in exchange for XP or credits — all optional. Each option ' +
            'can be picked once, and you can add at most +' + baseValue + ' in total (the same amount you started with).'}
      </div>

      {/* B-98 — WHERE THAT CAP CAME FROM, said on the screen it governs.
          The reach is `baseValue`, and `baseValue` comes from PARTY SIZE on the previous surface:
          2 PCs → 20 · 3 → 15 · 4-5 → 10 · 6+ → 5. That is a 3.5x swing in buying power (a party of
          two can take every trade — 15 XP or 3,500 credits; a party of six can take one +5) decided
          by a control the player set on another screen, and this panel said only "at most +N" as if
          N were a constant. Naming the provenance is half of it; the other half is the way BACK,
          because a number you cannot reach is not really a number you set.
          Morality is excluded by construction, not by a branch: its starting value is fixed at 50
          (F&D CRB p.52), so `onEditPartySize` is only passed where a party size exists. */}
      {partySize != null && onEditPartySize && (
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginBottom: '12px' }}>
          That +{baseValue} is set by your <b style={{ color: 'rgba(255,255,255,0.8)' }}>party of {partySize}</b> —
          a smaller party starts with more {mechType} and can trade more of it.
          <button type="button" onClick={onEditPartySize}
            aria-label={'Change party size (currently ' + partySize + ')'}
            style={{
              display: 'inline-flex', alignItems: 'center', minHeight: '44px', padding: '0 8px',
              marginLeft: '2px', background: 'none', border: 'none', color: accent,
              fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 700,
              textDecoration: 'underline', cursor: 'pointer',
            }}>Change party size</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
        {trades.map(function(t) {
          var isSelected = selectedTradeIds.indexOf(t.id) >= 0;
          // T2-C cap honesty (EotE p.40, Table 2-3 limits — D4-verified): a pick that would push
          // the total extra past the ORIGINAL starting value is disabled here, instead of
          // computeMechanic's null silently dropping the whole mechanic at confirm.
          var atCap = !isSelected && tradeMode !== 'single_pick'
            && Math.abs(totalDelta + t.valueDelta) > baseValue;
          return (
            <button key={t.id} onClick={function() { if (!atCap) onToggleTrade(t.id); }}
              disabled={atCap}
              aria-label={tradeLabel(t) + (atCap ? ' — over the starting-value cap' : '')} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
              padding: '12px 14px', textAlign: 'left',
              background: isSelected ? accent + '10' : 'rgba(255,255,255,0.02)',
              border: '1px solid ' + (isSelected ? accent + '50' : 'rgba(255,255,255,0.06)'),
              borderRadius: '6px', cursor: atCap ? 'default' : 'pointer', opacity: atCap ? 0.35 : 1,
              transition: 'border-color 0.2s, background 0.15s',
            }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
                border: '2px solid ' + (isSelected ? accent : 'rgba(255,255,255,0.15)'),
                background: isSelected ? accent : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isSelected && <svg width="12" height="12" viewBox="0 0 16 16"><polyline points="3,8 7,12 13,4" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 600, color: '#fff' }}>{tradeLabel(t)}</div>
                {atCap && (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', marginTop: '3px' }}>
                    Over the limit — you can only add +{baseValue} in total
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* TP-3: CTA label reflects the user's selection so the "no trades" path
         is an explicit choice, not an accidental one. */}
      <FCBottomAction
        label={selectedTradeIds.length === 0
          ? 'CONTINUE WITHOUT TRADES'
          : 'CONFIRM ' + selectedTradeIds.length + ' TRADE' + (selectedTradeIds.length !== 1 ? 'S' : '')}
        onClick={onConfirm} accent={accent} />
    </div>
  );
}

/* THE LAYERED MORALITY, BUILT IN ONE PLACE (R-MEP-1; lifted out of `FCMechanicStep`'s body by
   `build-phone-mechanic` so the confirm handler and the render read the SAME builder — two
   expressions of one save shape is the second-resolution-path defect PRAXIS §5 names, and the
   confirm now needs the object before the render's `var` is reached).

   Locked at 50, no credit/XP trade through it (cap guard by construction — this track contributes
   0 to the +2,500cr/+10XP creation cap). `descriptor` carries the strength; `descriptorWeak` is
   written ONLY when the player departed from the book's pairing, and is ABSENT otherwise — absence
   is what means "the pairing", so old saves and undeparted new ones read identically. Built
   through the shared write rule so the absence is not re-implemented at the one site that can get
   it wrong. NOTHING ABOUT THE SHAPE MOVED: same keys, same defaults, same patch call. */
function fcBuildMoralityLayer(gameData, moralStrength, moralWeak) {
  var layer = { type: 'morality', descriptor: moralStrength || '', baseValue: 50,
    finalValue: 50, xpAdjustment: 0, creditAdjustment: 0, tradeIds: [], entries: [] };
  if (moralStrength && moralWeak) {
    var _msObj = (((gameData.narrativeMechanics || {}).morPairs || {}).strengths || [])
      .filter(function(s) { return s.key === moralStrength; })[0];
    var _patch = CharResolve.moralityWeakPatch(_msObj, moralWeak);
    if (_patch.descriptorWeak) layer.descriptorWeak = _patch.descriptorWeak;
  }
  return layer;
}

function FCMechanicStep({ savedMechanic, gameLine, gameData, partySize, setPartySize, accent, onComplete, backRef, hasForce,
  /* THE TRANSPLANT'S SHELL PROPS. The step owned none of these before, because the
     shared creator header drew them; the board's frame carries its own bar, wave and
     rail, so the step is handed what it must print. Absent on the legacy path, which
     is why every one of them is read only inside the `transplanted` branch. */
  transplanted, band, stepIndex, stepTotal, steps, stepLabels, soFar, onBack, onJump,
  /* `stepReached` was PASSED by the transplanted mount and never destructured — the wide sheet's
     wave computes `data-done` off `stepIndex` alone and could not tell reached from passed. The
     phone shell reads it (`FCPhoneShell`: `stepReached == null ? stepIndex : stepReached`), so the
     prop has to arrive with a name here or the phone wave silently falls back to the cursor —
     A27-3's exact defect, which is why the fallback is not left to do the work. */
  stepReached,
  xpPool, xpRemaining, xpPoolWithMechanic }) {
  var _useMD = useState(null), mechDescriptor = _useMD[0], setMechDescriptor = _useMD[1];
  var _useMT = useState([]), selectedTradeIds = _useMT[0], setSelectedTradeIds = _useMT[1];
  var _useSV = useState('type'), subView = _useSV[0], setSubView = _useSV[1];
  var mdMech = useLayoutMode() === 'desktop';   // C9: list ‖ detail at both desktop bands
  // BOOKS-1c Part B (mode c, fad p.337-338): a Force element in an Obligation/Duty campaign
  // ALSO attaches Morality. We stash the primary mechanic and offer a one-tap attach/decline
  // BEFORE completing, so both tracks reach the sheet (populate-two-of-three).
  var _usePM = useState(null), pendingMech = _usePM[0], setPendingMech = _usePM[1];
  // THE REGISTER'S SEARCH — the board draws a search plate over the type list, and 41/49 rows is
  // a list you search rather than scroll. The shipped step had none because its list lived behind
  // a sub-view. Lives here with the other state so the transplanted return below stays hook-free.
  var _useQ = useState(''), mechQuery = _useQ[0], setMechQuery = _useQ[1];
  // THE TWO-TRACK PAIR (R-MEP-1). Its own state, because in the two-track case the MAIN register is
  // the Obligation — `mechDescriptor` is spoken for. `moralWeak` is null while the player is on the
  // book's pairing and holds a key only once they depart, which is exactly what gets persisted.
  var _useMS = useState(null), moralStrength = _useMS[0], setMoralStrength = _useMS[1];
  var _useMW = useState(null), moralWeak = _useMW[0], setMoralWeak = _useMW[1];
  var _useSQ = useState(''), strQuery = _useSQ[0], setStrQuery = _useSQ[1];
  var _useWQ = useState(''), weakQuery = _useWQ[0], setWeakQuery = _useWQ[1];
  // THE MORALITY LAYER'S ANSWER, HELD AS STATE INSTEAD OF ASKED ON A FOURTH PAGE (R-PMeC-2).
  // Mode c (fad p.338) attaches Morality to a Force-sensitive character in an Obligation or Duty
  // campaign, and the shipped flow asked "keep it, or ignore Morality and Conflict entirely?" on a
  // `moralityLayer` sub-view reached AFTER the commit. On the TRANSPLANTED path that sub-view is
  // never rendered — the branch below returns the sheet whatever `subView` says — so pressing the
  // commit as a two-track character set a sub-view nothing draws and the step did not advance.
  // MEASURED, not inferred: seeded Wookiee / Hired Gun / Force Sensitive Exile at 1920, Bounty and
  // Bravery chosen, commit pressed, `data-creator-step` still `mechanic` afterwards. A dead end is
  // not shippable (C-MU-4, the rule that kept the refund control against the board's silence).
  // KEPT IS THE DEFAULT, because it is the RAW default the frames' own two-track lead states
  // ("your own Morality rides alongside because your character is Force-sensitive") and the shipped
  // copy's advice ("Most Force-sensitive heroes should keep it"). The phone draws the toggle on the
  // Morality track card; the wide sheet's frame draws no control, so at that band the default is
  // what completes — which unstrands it without adding an object the approved frame does not draw.
  var _useMK = useState(true), moralityKept = _useMK[0], setMoralityKept = _useMK[1];

  // Expose an internal-back handler so the outer chrome's BACK button can rewind within this step.
  // Returns true if it handled the back; false to let the parent advance.
  useEffect(function() {
    if (!backRef) return;
    backRef.current.handleBack = function() {
      if (subView === 'moralityLayer') { setSubView('trades'); return true; }
      if (subView === 'trades') { setSubView('typeDetail'); return true; }
      if (subView === 'typeDetail') { setSubView('type'); return true; }
      return false;
    };
    return function() { if (backRef.current) backRef.current.handleBack = null; };
  }, [subView, backRef]);

  var mechType = mechanicForLine(gameLine);                          // B-107: one home
  var mechConfig = gameData.narrativeMechanics.config;
  var mechData = mechConfig.mechanics[mechType];
  var trades = mechData && mechData.tradeOptions ? mechData.tradeOptions.choices : [];

  // Get types list
  // D-C2-4 — THE REGISTER SHOWS THE SHELF'S SET. These three lists were the only creator registers
  // that ignored the bookshelf entirely: measured, every entry reached the screen whatever was
  // enabled (41/41 obligation, 49/49 duty, 39/39 morality). Not a filter that was wrong — a filter
  // that was never called. `fcFilterActive` is the creator's one home for the question and it is
  // NOT era-gated here: a campaign mechanic belongs to its line by definition, so gating it again
  // by era would refuse a player their own line's Obligations.
  // UNSOURCED ENTRIES STAY (shipped law, and `isActiveWith`'s first line): 8 obligation, 2 duty and
  // 1 morality record carry no book and are always offered.
  var types = [];
  if (mechType === 'obligation') {
    types = fcFilterActive(gameData.narrativeMechanics.oblTypes.items || [], null, false);
  } else if (mechType === 'duty') {
    types = fcFilterActive(gameData.narrativeMechanics.dutyTypes.items || [], null, false);
  } else if (mechType === 'morality') {
    // B-67(b): THE PAIRING IS THE MECHANIC, and it was invisible until after the choice.
    // The step asks a player to choose their emotional pair and then showed them single words —
    // Bravery, Love, Caution — while the shadow each one carries (Anger, Jealousy, Fear) stayed
    // hidden until the detail. You cannot weigh Bravery against Compassion without knowing you are
    // also choosing between Anger and Hatred. Resolved BY `weakKey` from `pairing`'s own mapping,
    // never by list position (see B-117, one path over, where position mispaired 39 of 39).
    var _mw = {};
    (gameData.narrativeMechanics.morPairs.weaknesses || []).forEach(function(w) { _mw[w.key] = w; });
    // D-C2-4 applies to the STRENGTH — the thing the player picks. The weakness is not filtered
    // separately: it is not chosen, it is the shadow `weakKey` names, and gating it independently
    // could pair an offered strength with a hidden weakness and print a blank shadow.
    types = fcFilterActive(gameData.narrativeMechanics.morPairs.strengths || [], null, false)
      .map(function(s) {
        var w = _mw[s.weakKey];
        // The DESCRIPTION rides along with the name, because the board's own morality frame
        // reads BOTH halves of the pair on the spend screen and the shadow's reading was the
        // half we were not carrying. Same `weakKey` mapping, same map step — a second lookup
        // for the same fact is the second-path defect by name (PRAXIS §5).
        return w ? Object.assign({}, s, { pairedWeaknessName: w.name,
                                          pairedWeaknessDesc: w.description || '' }) : s;
      });
  }

  // ── A3-2: THE OLD-DATA PATH ────────────────────────────────────────────────────────────────
  // A player who chose an Obligation from a book they have since switched off must not find their
  // own character's answer missing. Measured before this existed: the record survived in the draft
  // (`descriptor: "DISGRACED"`) and nothing blocked — but the register showed 33 of 41 and the
  // player's own pick was simply GONE from the screen. Surviving in storage is not the same as
  // surviving to the eye.
  //
  // SO THE SAVED PICK IS RE-ADMITTED, and only it: the filter still governs everything a player
  // has NOT chosen, so switching a book off still removes its OFFERS. `_fromRetired` marks the
  // row so the surface can say WHY it is here — the book label is the honest part, not decoration.
  // Never deleted, never blocking, and never silently re-enabling a book's whole table.
  var _savedKey = savedMechanic && savedMechanic.type === mechType ? savedMechanic.descriptor : null;
  if (_savedKey && !types.some(function(t) { return t.key === _savedKey; })) {
    var _all = mechType === 'obligation' ? (gameData.narrativeMechanics.oblTypes.items || [])
             : mechType === 'duty' ? (gameData.narrativeMechanics.dutyTypes.items || [])
             : (gameData.narrativeMechanics.morPairs.strengths || []);
    var _kept = _all.filter(function(t) { return t.key === _savedKey; })[0];
    if (_kept) types = types.concat([Object.assign({}, _kept, { _fromRetired: true })]);
  }

  // Compute base value from party size
  var baseValue = 10;
  if (mechData && mechData.startingValueRule) {
    var rule = mechData.startingValueRule;
    if (rule.basis === 'partySize' && rule.valuesByPartySize) {
      var found = rule.valuesByPartySize.find(function(v) { return String(v.partySize) === String(partySize) || (v.partySize === '6+' && partySize >= 6); });
      if (found) baseValue = found.startingValuePerPC;
    } else if (rule.basis === 'fixed') {
      baseValue = rule.startingValue || 50;
    }
  }

  var descriptorName = '';
  if (mechDescriptor) {
    var typeObj = types.find(function(t) { return t.key === mechDescriptor; });
    descriptorName = typeObj ? typeObj.name : mechDescriptor;
  }

  var tradeMode = (mechData && mechData.tradeOptions && mechData.tradeOptions.mode) || 'multi';
  var handleToggleTrade = useCallback(function(id) {
    setSelectedTradeIds(function(prev) {
      // UXQP-20 (RAW, fad p.52): single_pick = radio semantics — choose ONE or none.
      if (tradeMode === 'single_pick') {
        return prev.indexOf(id) >= 0 ? [] : [id];
      }
      return prev.indexOf(id) >= 0 ? prev.filter(function(k) { return k !== id; }) : prev.concat([id]);
    });
  }, [tradeMode]);

  var handleConfirm = useCallback(function() {
    var mech = computeMechanic(mechType, baseValue, mechDescriptor, selectedTradeIds, mechConfig);
    // T2-C: computeMechanic returns null when trades exceed the cap — the panel disables those
    // combos now, but if one slips through, keep the mechanic WITHOUT trades rather than
    // silently dropping the whole mechanic (the old failure).
    if (!mech && selectedTradeIds.length) {
      console.error('[mechanic] trade cap violated at confirm — keeping the mechanic, dropping trades');
      mech = computeMechanic(mechType, baseValue, mechDescriptor, [], mechConfig);
    }
    // mode c: Force character in an Obligation/Duty campaign → the Morality layer rides along.
    if (hasForce && (mechType === 'obligation' || mechType === 'duty')) {
      // THE TRANSPLANTED PATH ANSWERS HERE, because the `moralityLayer` SUB-VIEW BELOW IS NEVER
      // RENDERED ON IT — the branch that returns `FCMechanicSheet` ignores `subView` entirely, so
      // setting it left the step showing the same screen and going nowhere. Measured at 1920 on a
      // seeded Wookiee / Hired Gun / Force Sensitive Exile with Bounty and Bravery chosen: commit
      // pressed, `data-creator-step` still `mechanic`. `moralityKept` carries the answer instead —
      // the phone draws its toggle on the Morality track card (R-PMeC-2), and at mid/wide, where
      // the approved frame draws no control, the RAW default completes rather than stranding.
      if (transplanted) {
        onComplete(mech, moralityKept
          ? fcBuildMoralityLayer(gameData, moralStrength, moralWeak) : null);
        return;
      }
      setPendingMech(mech);
      setSubView('moralityLayer');
      return;
    }
    onComplete(mech, null);
  }, [mechType, baseValue, mechDescriptor, selectedTradeIds, mechConfig, onComplete, hasForce,
      transplanted, moralityKept, gameData, moralStrength, moralWeak]);

  // The layered Morality: locked at 50, no credit/XP trade through it (cap guard by construction —
  // this track contributes 0 to the +2,500cr/+10XP creation cap).
  //
  // R-MEP-1: the emotional pair is now named HERE, on the board's two-track screen, instead of
  // being deferred to the sheet. `descriptor` carries the strength; `descriptorWeak` is written
  // ONLY when the player departed from the book's pairing, and is ABSENT otherwise — absence is
  // what means "the pairing", so old saves and undeparted new ones read identically. Built through
  // the shared write rule so the absence is not re-implemented at the one site that can get it
  // wrong.
  var moralityLayer = fcBuildMoralityLayer(gameData, moralStrength, moralWeak);

  // ── THE TRANSPLANT (cluster two, screen two) ───────────────────────────────────────────────
  // Everything above is the DATA SOURCE and is untouched by this branch: the same filtered types,
  // the same `baseValue` off the same party-size rule, the same `handleConfirm`, the same saved-
  // pick re-admission. Below, the board's frame is handed those values. Nothing is recomputed.
  if (transplanted) {
    var LINE_COPY = {
      obligation: { name: FC_MECH_STEP_NAME.obligation, cite: 'Edge of the Empire p.38' },
      duty:       { name: FC_MECH_STEP_NAME.duty,       cite: 'Age of Rebellion p.44' },
      // THE BOARD DREW NO MORALITY FRAME (its section draws Obligation and Duty). This copy is
      // OURS, written to the same pattern in mechanics voice, and it is not a parity claim —
      // neither swept fixture reaches it. Morality is not a shared pool and must not borrow the
      // pooled sentence: it is personal, fixed at 50, and its trade is a one-off choice.
      // PRINTED p.48, NOT CORPUS p.52 (R-MCT-1). `swrpg_index/pdf_pages` reports fad CALIBRATED at
      // offset +4 — corpus = printed + 4 — so the 52 this line carried is the PDF page for printed
      // 48, which is what the board's own Morality lead cites. A player reading "p.52" and turning
      // to page 52 of Force and Destiny lands four pages past the rule. (The pair panel's cite
      // moved to p.48 with it at the 841b5aa7 refresh — R-MCT-2's authority, `mechanic-close-out`
      // A7; the pair rule and the Morality rule share the printed page.)
      // THE REGISTER'S THREE COPY LINES TRAVEL TOGETHER (R-MCO-A5-2: the unit is the DRAWING at
      // the address, not one string). The board's consular/healer Morality frame heads the list
      // "Emotional Strength", cites "39 strengths" and searches "Search Emotional Strength…"
      // (lab STABLE 841b5aa7, verbatim — A5-1 withdrew a pluralised re-derivation of this line).
      // Obligation and Duty have NO board frame of their own register, so their computed copy
      // stands ("Your Duty" · "N types" · "Search Duty…") until the board draws them.
      morality:   { name: FC_MECH_STEP_NAME.morality,   cite: 'Force and Destiny p.48',
                    list: { title: 'Emotional Strength', noun: 'strength', search: 'Search Emotional Strength…' } },
    };
    var copy = LINE_COPY[mechType] || LINE_COPY.obligation;
    // ONE COPY TABLE, one branch on the mechanic (the mechanism A5-2 leaves to the seat).
    var listCopy = copy.list
      ? { title: copy.list.title, cite: function(n) { return n + ' ' + copy.list.noun + (n === 1 ? '' : 's'); }, search: copy.list.search }
      : { title: 'Your ' + copy.name, cite: function(n) { return n + (n === 1 ? ' type' : ' types'); }, search: 'Search ' + copy.name + '…' };
    var pooled = mechType === 'obligation' || mechType === 'duty';
    // MODE C (fad p.338, the same disjunction `_hasForceAccess` already computes): a Force-sensitive
    // character in an Obligation/Duty campaign carries Morality TOO. It is the state the board's
    // two-track frames draw, and the reason this screen exists — the shipped flow asked about the
    // second track only after the first was finished (`moralityLayer`, below, still the sub-view
    // that ATTACHES it and is untouched here).
    var twoTrack = hasForce && pooled;
    var lead = twoTrack
      ? ('You carry <b>two</b> tracks. The group’s ' + copy.name + ' is shared and you set your '
         + 'share of it; your own <b>Morality</b> rides alongside because your character is '
         + 'Force-sensitive. Both are named below before either is set.')
      : pooled
      ? ('Your table runs one shared ' + copy.name + ' pool and this is <b>your share</b> of it. '
         + 'You may take on more of it at creation in exchange for experience or credits.')
      : ('Your ' + copy.name + ' is your own — it starts at 50 and moves with what you do. '
         + 'You may make one trade at creation, and only one.');

    // THE TRACK'S LIVE VALUE, not its base: a trade taken must show in the ledger it moves.
    // `computeMechanic` is the one engine for that arithmetic and is asked here rather than
    // re-derived — a second path to this number is the defect the shared-core law names.
    var _live = computeMechanic(mechType, baseValue, mechDescriptor, selectedTradeIds, mechConfig);
    var liveValue = _live && typeof _live.finalValue === 'number' ? _live.finalValue : baseValue;
    // B-1379 / B-1492: the header's experience follows the trade on THIS step — the one pool (`creationXpPool`) asked with
    // the live mechanic; Remaining moves by the same difference, since nothing spent changes when a trade is taken.
    var livePool = (xpPoolWithMechanic && _live) ? xpPoolWithMechanic(_live) : xpPool;
    var liveRemaining = (Number(xpRemaining) || 0) + (livePool - (Number(xpPool) || 0));

    var tracks = [{
      name: copy.name,
      scopeWord: pooled ? 'group' : 'personal',
      scope: pooled ? 'Group · shared with your party' : 'Personal · yours alone',
      value: liveValue,
      line: pooled ? 'The party’s shared track: this is your share'
                   : 'Your own track: nobody else contributes to it',
      prose: pooled
        ? ('Everyone at the table contributes to one pool and the GM rolls against the total at '
           + 'each session’s start. Set by party size.')
        : ('It starts at 50 and moves as you act. Nothing at creation sets it but the one trade '
           + 'below.'),
      foot: 'You may trade on this track',
    }];

    // THE SECOND TRACK. Fixed at 50 and carrying NO trade — the creation cap is a property of the
    // pooled track only (fad p.341-342), which is why `computeMechanic` is not asked for a value
    // here: there is no arithmetic to do, and inventing a second path to a constant would be the
    // shared-core defect by name. The board's own frame prints exactly these words.
    if (twoTrack) {
      tracks.push({
        name: 'Morality',
        scopeWord: 'personal',
        scope: 'Personal · yours alone',
        value: 50,
        line: 'Yours alone: Conflict and Morality are personal',
        prose: 'Your character is <b>Force-sensitive</b>, so Morality runs alongside the group '
             + 'track, starting at <b>50</b>. It brings no extra XP or credits. You name an '
             + 'emotional pair and Conflict moves the number in play.',
        foot: 'No trade through this track',
        // THE KEEP/DECLINE, CARRIED ON THE CARD THAT NAMES THE TRACK (R-PMeC-2). Read by the PHONE
        // sheet only: the approved wide frame draws no control, and R-MIL-2 forbids a seat adding
        // one to it — so at mid/wide the default answers and the frame is untouched.
        kept: moralityKept,
        onKeep: setMoralityKept,
      });
    }

    var _S = (typeof window !== 'undefined') && window.SWRPGSources;
    var _idx = _S && _S.current() ? _S.current().index : null;
    var _q = (mechQuery || '').trim().toLowerCase();
    var rows = types
      .filter(function(t) { return !_q || String(t.name || '').toLowerCase().indexOf(_q) >= 0; })
      .map(function(t) {
        var src = t.source && _idx && _S.sourceLabel ? _S.sourceLabel(_idx, t.source) : t.source;
        return {
          id: t.key, name: t.name, description: t.description || '',
          // A2-1 (D-C2-4): "each attributed row carrying its source". The board's own sample rows
          // print none — so this span is a RULED DELTA and says so on the element, which is the
          // declared mechanism R-TTP-1 enumerates rather than an undeclared addition. The rows
          // with no book stay unmarked and always-offered, per shipped law.
          source: src || undefined,
          sourceRuledDelta: src ? 'mechanic-row-source-a2-1' : undefined,
          // MORALITY ONLY — obligation and duty types have no shadow, so this is undefined
          // there and every branch below it stays off. Derived at render from the register's
          // own pairing; nothing new is persisted.
          pairedWeakness: t.pairedWeaknessName
            ? { name: t.pairedWeaknessName, description: t.pairedWeaknessDesc || '' }
            : undefined,
          why: t._fromRetired ? 'your pick · from a book you switched off' : undefined,
        };
      });
    var selectedRow = rows.filter(function(r) { return r.id === mechDescriptor; })[0] || null;

    var partySizes = null;
    var _rule = mechData && mechData.startingValueRule;
    if (_rule && _rule.basis === 'partySize' && _rule.valuesByPartySize) {
      partySizes = _rule.valuesByPartySize.map(function(v) {
        var n = v.partySize === '6+' ? 6 : Number(v.partySize);
        return {
          n: n, label: String(v.partySize), value: v.startingValuePerPC,
          on: v.partySize === '6+' ? partySize >= 6 : String(v.partySize) === String(partySize),
        };
      });
    }

    function _money(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    var tradeRows = trades.map(function(c) {
      var taken = selectedTradeIds.indexOf(c.id) >= 0;
      // WOULD THIS ONE BE LEGAL? Asked of the ENGINE, with the candidate added, so the cap that
      // disables a row is the same cap that would refuse it at confirm. A row disabled by a rule
      // written twice is a row that disagrees with itself.
      var probe = taken ? selectedTradeIds : selectedTradeIds.concat([c.id]);
      var name = c.label ? c.label : [
        c.xpDelta ? '+' + c.xpDelta + ' XP' : null,
        c.creditsDelta ? '+' + _money(c.creditsDelta) + ' credits' : null,
      ].filter(Boolean).join(' and ');
      return {
        id: c.id, name: name, trackLabel: copy.name,
        from: baseValue, to: baseValue + (c.valueDelta || 0),
        taken: taken,
        disabled: !computeMechanic(mechType, baseValue, mechDescriptor, probe, mechConfig),
      };
    });

    // ── THE PAIR PANEL (R-MEP-1/2) ────────────────────────────────────────────────────────────
    // Two-track only. Both registers take the SHELF'S SET through `fcFilterActive`, the same
    // D-C2-4 rule the main register takes — a strength from a book you switched off must not be
    // offered here either. The WEAKNESS list is the override menu, so it is filtered too; the
    // DERIVED partner is never filtered, because it is not offered, it is resolved (the same
    // reasoning the single-track morality branch above records).
    var pairPanel = null;
    if (twoTrack && gameData.narrativeMechanics.morPairs) {
      var _mp = gameData.narrativeMechanics.morPairs;
      var _strAll = fcFilterActive(_mp.strengths || [], null, false);
      var _weakAll = fcFilterActive(_mp.weaknesses || [], null, false);
      var _strObj = (_mp.strengths || []).filter(function(s) { return s.key === moralStrength; })[0] || null;
      // ONE resolver, even here: the panel shows what the SAVE would resolve to, so the screen and
      // the sheet can never disagree about which weakness this character has.
      var _resolved = _strObj
        ? CharResolve.moralityPair(
            { moralityLayer: { type: 'morality', descriptor: moralStrength,
                               descriptorWeak: moralWeak || undefined } }, _mp)
        : null;
      function _rows(list, q) {
        var _q = (q || '').trim().toLowerCase();
        return list
          .filter(function(t) { return !_q || String(t.name || '').toLowerCase().indexOf(_q) >= 0; })
          .map(function(t) { return { id: t.key, name: t.name }; });
      }
      var _weakSelKey = _resolved ? _resolved.weaknessKey : null;
      var _weakObj = (_mp.weaknesses || []).filter(function(w) { return w.key === _weakSelKey; })[0] || null;
      pairPanel = {
        // PRINTED p.48 (R-MCT-2; `mechanic-close-out` A7 — the board's pair_block cites it so at
        // lab STABLE 841b5aa7). THE SENTENCE IS THE BOARD'S, CHARACTER-EXACT (A7-1): the lab took
        // the ruled wording of `mechanic-emotional-pair-contract` Q1 unparaphrased so the app can
        // match it by string, and the pair-contract's rule — persist the DEPARTURE, never the
        // pairing — is what "the table's call" states.
        cite: 'Force and Destiny p.48',
        lead: "Strength and Weakness are paired on the book's d100 table.",
        strength: {
          title: 'Strength', count: _strAll.length, rows: _rows(_strAll, strQuery),
          selected: _strObj ? { id: _strObj.key } : null,
          query: strQuery, placeholder: 'Search strengths…', onSearch: setStrQuery,
          // CHANGING THE STRENGTH RE-LIGHTS ITS PARTNER — the override is dropped, because it was
          // an override OF a pairing that no longer applies. Keeping it would silently attach the
          // old weakness to a new strength, which is B-117's failure mode wearing a new hat.
          onPick: function(k) { setMoralStrength(k); setMoralWeak(null); },
          detail: _strObj ? { name: _strObj.name, description: _strObj.description || '' } : null,
        },
        weakness: {
          title: 'Weakness', count: _weakAll.length, rows: _rows(_weakAll, weakQuery),
          selected: _weakObj ? { id: _weakObj.key } : null,
          query: weakQuery, placeholder: 'Search weaknesses…', onSearch: setWeakQuery,
          // THE ABSENCE RULE, AT THE ONE PLACE A PLAYER CAN TRIGGER IT: picking the book's own
          // partner back sets null, not the key — so the save carries an override or nothing, and
          // `descriptorWeak` never appears holding the value it would have derived anyway.
          onPick: function(k) {
            setMoralWeak(_strObj && k === _strObj.weakKey ? null : k);
          },
          detail: _weakObj ? {
            name: _weakObj.name, description: _weakObj.description || '',
            note: _resolved && _resolved.pairedKey && _strObj
              ? 'Paired with ' + _strObj.name + ' on the book’s own table' : null,
          } : null,
        },
      };
    }

    var _next = (steps || [])[stepIndex + 1];
    return (
      <FCMechanicSheet
        band={band} stepName={copy.name}
        stepIndex={stepIndex} stepReached={stepReached} stepTotal={stepTotal}
        steps={steps} stepLabels={stepLabels} onJump={onJump} onBack={onBack}
        xpPool={livePool} xpSpentHere={0} xpRemaining={liveRemaining}
        soFar={soFar}
        lead={lead} cite={copy.cite}
        /* THE SECOND TRACK'S BOOK, beside the line's own. Mode c is FaD's rule applied inside an
           Edge/AoR campaign, so the head cites both — the board draws the pair. */
        cite2={twoTrack ? 'Force and Destiny p.338' : undefined}
        /* R-MEP-4, scoped to THIS surface and to the PINNED board: its two Force-sensitive
           mechanic frames omit the Force Powers step. Both characters are Force-sensitive — the
           two-track one by its specialisation, the morality one by its line — so `hasForce` is
           exactly the set of mechanic frames whose pinned board undercounts. */
        /* R-MEP-4's WAVE EXCEPTION IS RETIRED (R-MCO-2), on the expiry it was written with. It
           existed because the board pinned at `97226bf1` drew ELEVEN wave steps on its two
           Force-sensitive mechanic frames while the honest counter gives twelve, and it said in as
           many words that it "RETIRES the moment the set refreshes". The set has refreshed several
           times since and is now at `56186654`, where those frames draw TWELVE — so the exception
           had inverted: instead of excusing a step the board lacked, it was DELETING OUR OWN
           twelfth step from a comparison the board was ready to make, leaving the app two objects
           short. That is B-392, and it is the declaration-expiry trap rather than a wave defect.
           Nothing replaces it: the app and the board now draw the same twelve. */
        pair={pairPanel}
        tracks={tracks}
        listTitle={listCopy.title}
        listCite={listCopy.cite(rows.length)}
        searchValue={mechQuery} searchPlaceholder={listCopy.search}
        onSearch={setMechQuery}
        rows={rows} selectedRow={selectedRow}
        onPick={function(key) { setMechDescriptor(key); }}
        partySizes={partySizes} onPartySize={setPartySize}
        trades={tradeRows} onTrade={handleToggleTrade}
        tradeCite={pooled ? 'on the group track only' : 'one only'}
        commitLabel={_next && stepLabels ? ('Continue to ' + stepLabels[_next]) : 'Continue'}
        onConfirm={handleConfirm} />
    );
  }

  var mechLabels = { obligation: 'OBLIGATION', duty: 'DUTY', morality: 'MORALITY' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* THE SPLIT (C9; drawing frames 3/4/6). Party size STAYS WITH THE LIST, because it governs
          the whole pool rather than any one type — the drawing is explicit about that. The type
          detail sits beside the list instead of replacing it, so a player can read what Addiction
          actually costs at the table without losing the other forty.
          Same always-mounted shape species and career use: `mode="desktop"` unconditionally and
          only the WIDTHS move, so the picker never changes tree position across a band flip and an
          in-flight search survives a rotation (A4). `trades` and `moralityLayer` are later STAGES,
          not a master-detail pair, so they render full-width below and are untouched. */}
      {(subView === 'type' || subView === 'typeDetail') && (
      <SplitPane
        mode="desktop"
        railWidth={mdMech ? 'var(--desk-rail-w)' : (subView === 'type' ? '100%' : '0px')}
        detailMax={mdMech ? '620px' : null}
        rail={<div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Party size selector — Obligation/Duty only. Veteran review fix: F&D Morality
          uses fixed starting value 50 per CRB p.52; party size is irrelevant. */}
      {(subView === 'type' || mdMech) && (
        <div style={{ padding: '0 16px 12px' }}>
          {mechType !== 'morality' && (
            <React.Fragment>
              <div style={fcSectionHeaderStyle(accent)}>PARTY SIZE</div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', lineHeight: 1.5, marginBottom: '10px' }}>
                This is the shared {mechLabels[mechType].toLowerCase()} pool for the whole party. Smaller parties carry less — triggers fire less often during sessions.
              </div>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                {[2,3,4,5,6].map(function(n) {
                  var isActive = partySize === n;
                  return (
                    <button key={n} onClick={function() { setPartySize(n); }}
                      aria-pressed={isActive}
                      aria-label={'Party size ' + n + (n === 6 ? ' or more' : '')}
                      style={{
                      flex: 1, minHeight: '44px', padding: '10px 0', borderRadius: '6px', cursor: 'pointer',
                      background: 'var(--doc-paper-raised)',
                      border: '1px solid ' + (isActive ? 'var(--doc-accent)' : 'var(--doc-rule)'),
                      fontFamily: 'var(--font-body)', fontSize: 'var(--fs-lg)', fontWeight: 700,
                      color: isActive ? 'var(--doc-accent)' : 'var(--doc-ink-muted)',
                    }}>
                      {n}{n === 6 ? '+' : ''}
                    </button>
                  );
                })}
              </div>
            </React.Fragment>
          )}
          <div data-tour-id="p-custom-mechanic-pool" style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', marginBottom: '16px', letterSpacing: '0.02em' }}>
            {mechType === 'morality'
              ? <span>Starting <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>MORALITY</span> value: <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>{baseValue}</span></span>
              : <span>Base {mechLabels[mechType]} value: <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>{baseValue}</span></span>}
          </div>
          <div style={fcSectionHeaderStyle(accent)}>
            {mechType === 'morality' ? 'CHOOSE YOUR EMOTIONAL PAIR' : 'CHOOSE YOUR ' + mechLabels[mechType] + ' TYPE'}
          </div>
        </div>
      )}

      {(subView === 'type' || mdMech) && (
        <FCMechanicTypePicker types={types} selected={mechDescriptor} accent={accent} onSelect={function(key) {
          setMechDescriptor(key);
          setSubView('typeDetail');
        }} />
      )}
        </div>}
        detail={(function() {
          if (!mdMech && subView !== 'typeDetail') return null;
          var selTypeObj = types.find(function(t) { return t.key === mechDescriptor; });
        // Veteran review fix: pair the weakness for Morality per F&D CRB p.53.
        var weakObj = null;
        if (mechType === 'morality' && selTypeObj && selTypeObj.weakKey && gameData.narrativeMechanics.morPairs) {
          var weaks = gameData.narrativeMechanics.morPairs.weaknesses || [];
          weakObj = weaks.find(function(w) { return w.key === selTypeObj.weakKey; });
        }
        if (!selTypeObj) {
          // the just-looking moment, desktop only: the pane says what it is for.
          return mdMech ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'var(--doc-ink-muted)', maxWidth: '34ch' }}>
                Pick one to read what it means at the table — when it comes up, and what it costs you.
              </div>
            </div>
          ) : null;
        }
        return (
          <FCMechanicTypeDetail
            typeObj={selTypeObj}
            weakObj={weakObj}
            mechType={mechType}
            accent={accent}
            onConfirm={function() { setSubView('trades'); }}
          />
        );
        })()}
      />
      )}

      {subView === 'trades' && (
        <FCTradePanel mechType={mechType} baseValue={baseValue} descriptor={descriptorName} trades={trades} tradeMode={tradeMode} selectedTradeIds={selectedTradeIds} onToggleTrade={handleToggleTrade} onConfirm={handleConfirm} accent={accent}
          /* B-98: only where a party size actually sets the base — morality's is fixed at 50. */
          partySize={mechType === 'morality' ? null : partySize}
          onEditPartySize={mechType === 'morality' ? null : function() { setSubView('type'); }} />
      )}

      {subView === 'moralityLayer' && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={fcSectionHeaderStyle(accent)}>ALSO GIVES YOU MORALITY</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink)', lineHeight: 1.6, marginBottom: '10px' }}>
            Your character is Force-sensitive, so <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>Morality</span> tracks alongside their {mechLabels[mechType].toLowerCase()} — starting at <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>50</span>, and it stays there. {/* RELABEL, NEVER SILENCE (R-MCT-1). Saying only "no extra credits or XP" leaves a player who has read the Force and Destiny rules wondering where their creation trade went. It names WHY there is none here and WHERE the trade actually is — on the track this campaign already runs — so the absence is an answer rather than a hole. */}
            The creation trade belongs to whichever track your campaign runs on, so you take it on your <span style={{ color: 'var(--doc-accent)', fontWeight: 700 }}>{mechLabels[mechType].toLowerCase()}</span> above, not here; Morality brings no starting credits or XP of its own. You can name the emotional pair on your sheet.
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)', lineHeight: 1.5, marginBottom: '16px' }}>
            Most Force-sensitive heroes should keep it. Decline and your character ignores Morality and the Conflict rule entirely (fad p.341).
          </div>
          <button onClick={function() { onComplete(pendingMech, moralityLayer); }}
            style={{ width: '100%', minHeight: '48px', marginBottom: '8px', borderRadius: '8px', cursor: 'pointer',
              background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', letterSpacing: '0.04em', color: 'var(--doc-accent)' }}>
            KEEP MORALITY (50)
          </button>
          <button onClick={function() { onComplete(pendingMech, null); }}
            style={{ width: '100%', minHeight: '44px', borderRadius: '8px', cursor: 'pointer',
              background: 'var(--doc-paper-raised)', border: '1px solid var(--doc-rule)',
              fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)' }}>
            No thanks — ignore Morality &amp; Conflict
          </button>
        </div>
      )}
    </div>
  );
}

/* ── FC XP Budget Bar ────────────────────────────────── */

function FCXpBudgetBar({ xpPool, xpSpentOnChars, xpSpentOnTalents, xpSpentOnSkills, xpSpentOnBuyouts, xpRemaining, accent }) {
  // CR-RAW #2: optional skills segment (the creation buy-ranks screen); callers without it pass nothing.
  var _skills = xpSpentOnSkills || 0;
  // R-SFC-2: the species-penalty buyout is creation spending, so it is a SEGMENT — not an
  // unexplained shortfall. Without it the bar's parts stop summing to the pool the moment a player
  // buys one out: the missing 10 XP renders as free space, which is the one thing this bar exists
  // to state honestly. Optional, so every existing caller is unchanged (R-PR-7 uncontended claim).
  var _buyouts = xpSpentOnBuyouts || 0;
  var pctChars = xpPool > 0 ? (xpSpentOnChars / xpPool) * 100 : 0;
  var pctTalents = xpPool > 0 ? (xpSpentOnTalents / xpPool) * 100 : 0;
  var pctSkills = xpPool > 0 ? (_skills / xpPool) * 100 : 0;
  var pctBuyouts = xpPool > 0 ? (_buyouts / xpPool) * 100 : 0;
  var pctFree = Math.max(0, 100 - pctChars - pctTalents - pctSkills - pctBuyouts);
  var isOver = xpRemaining < 0;

  // Typography pass: remaining XP is the single most important number on the
  // screen — promote to 22px Orbitron 900 hero display. The "OF N TOTAL"
  // caption bumped from 7px 0.25 alpha (2.13:1 FAIL) to 11px 0.7 alpha
  // (9.76:1 PASS). Legend labels bumped from 6px → 10px with bar-colored dots.
  function LegendDot({ color }) {
    return <span aria-hidden="true" style={{
      display: 'inline-block', width: '8px', height: '8px',
      borderRadius: '2px', background: color, marginRight: '5px',
      verticalAlign: 'middle',
    }} />;
  }

  return (
    <div style={{ padding: '10px 16px 6px', flexShrink: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 'var(--fs-2xl)', fontWeight: 900,
            color: isOver ? '#FF6B6B' : accent,
            letterSpacing: '0.02em', lineHeight: 1,
            textShadow: (isOver ? '#FF6B6B' : accent) + '55 0 0 14px',
          }}>{xpRemaining}</span>
          <span style={{
            fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase',
          }}>XP left</span>
        </div>
        <span style={{
          fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
          letterSpacing: '0.14em', color: 'rgba(255,255,255,0.7)',
          textTransform: 'uppercase',
        }}>of {xpPool} total</span>
      </div>
      <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: pctChars + '%', background: '#FF6B6B', transition: 'width 0.3s ease' }} />
        <div style={{ width: pctTalents + '%', background: '#B07EFF', transition: 'width 0.3s ease' }} />
        <div style={{ width: pctSkills + '%', background: '#4EAAFF', transition: 'width 0.3s ease' }} />
        <div style={{ width: pctBuyouts + '%', background: '#4ECB71', transition: 'width 0.3s ease' }} />
        <div style={{ width: pctFree + '%', background: accent + '60', transition: 'width 0.3s ease' }} />
      </div>
      <div style={{ display: 'flex', gap: '14px', marginTop: '6px', flexWrap: 'wrap' }}>
        {xpSpentOnChars > 0 && (
          <span style={{
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: '#FF8A8A', textTransform: 'uppercase',
          }}><LegendDot color="#FF6B6B" />Chars {xpSpentOnChars}</span>
        )}
        {_skills > 0 && (
          <span style={{
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: '#7EC4FF', textTransform: 'uppercase',
          }}><LegendDot color="#4EAAFF" />Skills {_skills}</span>
        )}
        {xpSpentOnTalents > 0 && (
          <span style={{
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: '#C8A5FF', textTransform: 'uppercase',
          }}><LegendDot color="#B07EFF" />Talents {xpSpentOnTalents}</span>
        )}
        {_buyouts > 0 && (
          /* "Removed", not "Buyout": it is the word the row the player just pressed uses
             ("REMOVED — 10 XP"), so the ledger line and the control name the same act. */
          <span style={{
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: '#7FE0A0', textTransform: 'uppercase',
          }}><LegendDot color="#4ECB71" />Removed {_buyouts}</span>
        )}
        {xpRemaining > 0 && (
          <span style={{
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
            letterSpacing: '0.14em', color: accent, textTransform: 'uppercase',
          }}><LegendDot color={accent} />Free {xpRemaining}</span>
        )}
      </div>
    </div>
  );
}

/* ── FC Characteristic Spender ───────────────────────── */

function FCCharSpender({ character, speciesData, accent, xpRemaining, onCharChange }) {
  if (!character || !speciesData) return null;

  var attrs = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];

  function costToIncrease(attr) {
    var cur = character.characteristics[attr] || 2;
    if (cur >= 5) return null; // maxed
    return (cur + 1) * 10;
  }

  function canAffordIncrease(attr) {
    var cost = costToIncrease(attr);
    return cost !== null && xpRemaining >= cost;
  }

  function costToDecrease(attr) {
    var cur = character.characteristics[attr] || 2;
    var base = (speciesEff(speciesData, character.speciesOptionPicks).characteristics || {})[attr] || 2;
    if (cur <= base) return null; // at species base
    return cur * 10;
  }

  // THE PRICED LADDER (B-76; shape ratified by ruling `creator-characteristics-mockup`, which
  // answers B-75/B-76/B-77/B-78 in this one mechanism).
  //
  // The step used to print `NEXT: 30 XP` and nothing else, so a player deciding whether to reach 4
  // had to add 30+40 in their head — with no total to a target, no ceiling, and no sense of what a
  // click costs as a share of what they hold. Every rung above the current value is now priced
  // CUMULATIVELY FROM WHERE YOU ARE: from 1, reaching 2 costs 20, 3 costs 50, 4 costs 90, 5 costs
  // 140. Nothing is left for the player to compute.
  //
  // BINDING (1) — DASHED IS NOT GATED. An unaffordable rung is INFORMATION about the current purse,
  // not a lock: it says what the thing would cost, which is the whole point of showing it. In-flow
  // spending stays bounded by the pool (the + control below is unchanged), and inform-never-gate
  // continues to govern FINALIZE and re-entry. Nothing here turns a spend control into a credit line.
  function ladderFor(attr) {
    var cur = character.characteristics[attr] || 2;
    var rungs = [];
    var running = 0;
    for (var v = cur + 1; v <= 5; v++) {
      running += v * 10;                      // RAW: a new rank costs the new value x10 (aor p.102)
      rungs.push({ value: v, cost: running, affordable: running <= xpRemaining });
    }
    return rungs;
  }

  // B-77: the biggest single commitment in the creator was made through an unmarked control. One +1
  // from base 2 costs 30 XP — a median 30% of the whole starting pool, 43% for a Hutt. The number
  // alone does not carry that; the SHARE does.
  function nextShare(attr) {
    var cost = costToIncrease(attr);
    if (cost === null || xpRemaining <= 0) return null;
    return Math.round((cost / xpRemaining) * 100);
  }

  return (
    <div style={{ padding: '0 16px 24px', animation: 'slideInRight 0.3s ease-out' }}>
      {/* ONE warning, not two — and it lives in the SUBTITLE, which is the protected teaching
          layer. The step used to print the lock-in primer in the body AND repeat the step subtitle
          underneath, so the same reader was told to be careful twice. My first cut moved the
          permanence teaching INTO the body and out of the subtitle; `smoke_char_first` failed,
          correctly — it guards that the subtitle teaches the permanence why, and the ruling settles
          this wording "under the protected-teaching-layer rule". So the subtitle keeps the teaching
          and the body's duplicate goes, which satisfies both.
          B-78: what died is the ECONOMY advice — "take the opportunity now to maximize your
          abilities", unreachable for 120 of 124 species — and "very unique circumstances". The
          prices are on screen now; the copy can stop strategising. */}

      {/* THE PURSE. What is left, stated where the spending happens rather than only in the bar
          above. Red and honest when negative — the overdrawn re-entrant (F-45) reads a real number
          here, never NaN, never a floor at zero. */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', marginBottom: '14px' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.4)' }}>TO SPEND</span>
        <span data-creator-char-purse={xpRemaining} style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: xpRemaining < 0 ? 'var(--text-danger)' : accent }}>
          {xpRemaining} XP
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {attrs.map(function(attr, idx) {
          var cur = character.characteristics[attr] || 2;
          var base = (speciesEff(speciesData, character.speciesOptionPicks).characteristics || {})[attr] || 2;
          var costUp = costToIncrease(attr);
          var canAfford = canAffordIncrease(attr);
          var costDown = costToDecrease(attr);
          var isAboveBase = cur > base;
          var atBase = cur === base;
          var atMax = cur >= 5;

          return (
            <div key={attr} data-tour-id={idx === 0 ? 'p-custom-chars-box' : undefined} style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid ' + FC_CHAR_COLORS[attr] + '20',
              borderRadius: '8px', padding: '12px 10px', textAlign: 'center',
            }}>
              {/* Label — Option C typography matches the species-detail stat grid
                 for cross-screen consistency. 7px Share Tech Mono read as a
                 footnote against the 24px hero number; 10px Rajdhani 700 in the
                 characteristic hue reads as a proper label. */}
              <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.18em', color: FC_CHAR_COLORS[attr], marginBottom: '6px', textTransform: 'uppercase' }}>
                {FC_CHAR_LABELS[attr]}
                {base !== 2 && <span style={{ color: 'rgba(255,255,255,0.7)', marginLeft: '6px', fontWeight: 600, letterSpacing: '0.14em' }}>BASE {base}</span>}
              </div>
              {/* Value + controls. CH-3 fix: disabled buttons use dashed-border +
                 low saturation so "unavailable" reads distinct from "untapped". */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <button onClick={costDown ? function() { onCharChange(attr, cur - 1); } : undefined} disabled={!costDown}
                  aria-label={'Decrease ' + FC_CHAR_LABELS[attr] + (costDown ? ' (refund ' + costDown + ' XP)' : ' (at species base)')}
                  title={atBase ? 'At species baseline — can\'t go lower' : ''}
                  style={{
                  // AS-6 44px TAP FLOOR (lap C1). Was 32x32 — measured breaching at every
                  // width by the Phase A recon and by scripts/smoke_creator_as9.py.
                  width: '44px', height: '44px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: costDown ? 'none' : '1px dashed rgba(255,255,255,0.22)',
                  background: costDown ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: costDown ? '#fff' : 'rgba(255,255,255,0.3)',
                  fontFamily: 'var(--font-display)', fontSize: 'var(--fs-lg)', fontWeight: 700,
                  cursor: costDown ? 'pointer' : 'not-allowed',
                }}>−</button>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-2xl)', color: isAboveBase ? FC_CHAR_COLORS[attr] : '#fff', minWidth: '28px' }}>{cur}</div>
                <button onClick={canAfford ? function() { onCharChange(attr, cur + 1); } : undefined} disabled={!canAfford}
                  aria-label={'Increase ' + FC_CHAR_LABELS[attr] + (canAfford ? ' (spend ' + costUp + ' XP)' : costUp ? ' (needs ' + costUp + ' XP)' : ' (maxed at 5)')}
                  title={atMax ? 'Maxed at 5' : (costUp && !canAfford ? 'Needs ' + costUp + ' XP' : '')}
                  style={{
                  // AS-6 44px TAP FLOOR (lap C1). Was 32x32 — measured breaching at every
                  // width by the Phase A recon and by scripts/smoke_creator_as9.py.
                  width: '44px', height: '44px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: canAfford ? 'none' : '1px dashed ' + (atMax ? 'rgba(255,255,255,0.22)' : accent + '44'),
                  background: canAfford ? accent + '20' : 'transparent',
                  color: canAfford ? accent : (atMax ? 'rgba(255,255,255,0.3)' : accent + '66'),
                  fontFamily: 'var(--font-display)', fontSize: 'var(--fs-lg)', fontWeight: 700,
                  cursor: canAfford ? 'pointer' : 'not-allowed',
                }}>+</button>
              </div>
              {/* THE LADDER — every rung above here, priced from HERE. An unaffordable rung is
                  dashed: that is INFORMATION about the current purse, not a lock (binding 1). */}
              {atMax ? (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.6)', marginTop: '6px', fontWeight: 700 }}>MAX</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px', marginTop: '7px' }}>
                  {ladderFor(attr).map(function(r) {
                    return (
                      <span key={r.value} style={{
                        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.04em',
                        padding: '2px 5px', borderRadius: '3px',
                        border: '1px ' + (r.affordable ? 'solid ' + FC_CHAR_COLORS[attr] + '33' : 'dashed rgba(255,255,255,0.16)'),
                        color: r.affordable ? 'rgba(255,255,255,0.78)' : 'rgba(255,255,255,0.34)',
                      }}>
                        {r.value}<span style={{ opacity: 0.55 }}>{' \u00b7 '}</span>{r.cost}
                      </span>
                    );
                  })}
                </div>
              )}
              {/* B-77: the SHARE, because 30 XP means nothing without knowing it is a third of
                  everything you hold.
                  B-118 — AND IT STATES THE OVERDRAW RATHER THAN VANISHING. This line used to be
                  gated on `nextShare(attr) !== null`, which is null at a zero or negative purse —
                  so the price of the next point DISAPPEARED at exactly the moment a player most
                  needs it, and an overdrawn re-entrant (F-45's population) read a step that had
                  quietly stopped costing things. That is the same hiding the dashed rungs refuse
                  under binding (1).
                  A percentage is genuinely meaningless here — dividing by zero, or by a negative,
                  produces nonsense, not information — so the line keeps the COST and swaps the
                  share for the standing, in the CTA's own words ("OVERSPENT BY n XP") so the
                  screen speaks with one vocabulary. */}
              {!atMax && costUp !== null && (
                <div data-char-cost-line={attr} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.5)', marginTop: '5px', lineHeight: 1.35 }}>
                  {xpRemaining > 0
                    ? 'Next point ' + costUp + ' XP — ' + nextShare(attr) + '% of what you have left'
                    : (xpRemaining === 0
                        ? 'Next point ' + costUp + ' XP — nothing left to spend'
                        : 'Next point ' + costUp + ' XP — you are overspent by ' + Math.abs(xpRemaining) + ' XP')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── FC Characteristics — the teaching aside (desktop) ─── */

// C10 increment 3. The width a desktop hands this step goes HERE, and what it carries is already
// ruled fact rather than new invention (ruling `creator-c10-plan` §INCREMENT 3.2): the permanence
// rule, the cost curve behind the ladder, and what the share line on each box actually means.
//
// IT EXPANDS THE SUBTITLE, IT NEVER REPLACES IT. The subtitle is the protected teaching layer —
// `smoke_char_first` guards that it teaches the permanence WHY, and increment 1 already paid once
// for re-learning that by moving the teaching out of it. A phone reads the subtitle's one line; a
// desktop reads the same rule at length beside the controls. Neither reader is told less.
//
// PROSE ONLY, NO CONTROLS — which is why hiding it on a phone costs the tap-floor sweep nothing.
// It is `display:none` at mobile rather than unmounted: same tree, same position, geometry only (A4).
function FCCharTeachingAside({ hidden }) {
  var pStyle = { fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.55, color: 'rgba(255,255,255,0.55)', margin: '0 0 14px' };
  var bStyle = { color: 'rgba(255,255,255,0.82)', fontWeight: 700 };
  return (
    /* The PROSE COLUMN carries the token, not this box — padding on the width-bearing element
       would eat 16px out of a 373px measure that was calibrated character by character, and the
       token would silently deliver 63 cpl instead of 66. The outer box owns the spacing; the
       inner one owns the measure, and it is the inner one the guard reads. */
    <aside data-char-aside="1" style={{
      display: hidden ? 'none' : 'block',
      flex: '0 1 auto', minWidth: 0,
      padding: '14px 16px 24px 0',
    }}>
      <div data-char-aside-prose="1" style={{ maxWidth: 'var(--desk-prose-max)' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>HOW THIS STEP WORKS</div>
      <p style={pStyle}>
        <b style={bStyle}>This is the only step that raises them.</b> After creation your six
        characteristics are fixed — the only way up is a rare talent like Dedication. Skills and
        talents keep growing for the whole campaign; these do not.
      </p>
      <p style={pStyle}>
        <b style={bStyle}>A rank costs its own value ×10.</b> Reaching 3 costs 30 XP, reaching 4
        costs 40, reaching 5 costs 50 — so the same +1 gets dearer every time you take it. The
        rungs on each box have already added that up: the price beside a rung is the total from
        where you stand now, not the price of one more point.
      </p>
      <p style={pStyle}>
        <b style={bStyle}>The percentage is there because 30 XP means nothing on its own.</b> One
        point from a base of 2 is around a third of a typical starting pool. Each box states its
        next point as a share of what you have left, so the cost is in the units you are spending in.
      </p>
      </div>
    </aside>
  );
}

/* ── FC Talent Tree — LIFTED to lib/shared/treeView.jsx (F-100 M5 C7, ruling f100-m5-contracts §3 C7) ──
   The 11-function block lives there now, whole; this file keeps its three pinned helpers (glyphs, cleanMarkup,
   gameLineTitle) and INJECTS them, so the player tree draws as before. Module-top aliases re-point the 14 mounts,
   the 12 builder calls and FCTreeRail's entry body without touching a call site. */
window.SWRPGTree.configure({ clean: cleanMarkup, glyphs: glyphs, cite: gameLineTitle });
var { DirectionalTree, DirectionalTreeSheet, DirectionalTreeEntryBody, talentTreeToModel, forcePowerToModel } = window.SWRPGTree;

/* ── FC Force Powers Step (F&D only) ─────────────────── */
// Veteran review fix: F&D Custom was shipping no way to buy Force powers during creation.
// CRB p.282-299 lists the trees; each has a Basic Power that costs 10 XP with FR 1+ prereq.
// First cut: buy/unbuy basic power per tree. Upgrades deferred to in-play purchases.

// ── B-32: the ONE basic-Force-power buy core (ruling advancement-basic-force-power-triage §3) ──
// Shared by the CREATOR (FCForcePowersStep) and the sheet's ADVANCEMENT force section, so a
// Force-sensitive character can buy new basic powers in BOTH places off ONE RAW-cited implementation
// (never a second cost/list path — the anti-divergence pin guards this).
//
// basicForcePowerCost — printed basic cost = the canonical tree's row-0 anchor (5-20 XP, fad p.285).
// R-3B2: a canonical power missing its row-0 cost is a REGISTRY BUG — loud at render (trips every
// smoke's 0-console-errors assert; the card shows COST ? with BUY disabled), never a silent price.
// F-100 M12 B-1246: the read itself is CharResolve.forcePowerBasicCost, the ONE basic price (re-entry asks it too); this
// wrapper owns only the loud refusal.
function basicForcePowerCost(p, priceCtx) {
  var cost = CharResolve.forcePowerBasicCost(p, priceCtx);
  if (cost == null) {
    console.error('[canonical] force power ' + ((p && (p.id || p.name)) || '?') + ' missing row-0 basic cost — registry bug');
    return null;
  }
  return cost;
}
// B-88 — THE SIZE OF THE THING YOU ARE CHOOSING (lap C12; drawn in
// `Docs/mockups/creator-forcepowers.html`, scoped by `creator-program-completion-plan` LAP 2).
//
// The step priced the ENTRY and never the power. A basic power costs 5-20 XP; the same power fully
// bought costs 90-215, median 145 — NINE to TWENTY-ONE TIMES the number on the button. The screen's
// entire statement on that was one word: upgrades come "in play". So a player chose between powers
// on a price that is not the price of any of them, and the 253 upgrade cells that make up the real
// cost were not merely unpriced — before C3 they were not drawn at all.
//
// NOT A PRICE PAID NOW. This line never enters the XP budget and nothing here becomes buyable: it
// is the SHAPE of the commitment, printed where the commitment is made. `readOnly` on the tree
// (B-89, lap C3) made the same argument in geometry; this makes it in arithmetic.
//
// DERIVED FROM THE SAME STRUCTURE `basicForcePowerCost` READS, one cell deeper. The registry census:
// 21 basics + 253 upgrades = 274 cells, 3,080 XP to own every power — against a modal starting pool
// of 100. Nothing is hardcoded here and nothing is read back off the page (the census-echo rule): a
// registry edit re-prices these lines, and the guard recomputes its expectations the same way.
function forcePowerSize(p) {
  var tree = (p && p.tree) || [];
  var cells = [];
  tree.forEach(function(row) { (row.cells || []).forEach(function(c) { cells.push(c); }); });
  if (!cells.length) return null;
  var total = cells.reduce(function(sum, c) { return sum + (c.cost || 0); }, 0);
  // Row 0 cell 0 is the BASIC power — the thing the button already prices. Everything else is an
  // upgrade, which is why the count is cells-minus-one rather than a second registry field.
  return { upgrades: cells.length - 1, total: total };
}
// F-100 M11 R-M11R-5 (B-1240; ruling f100-m11-frames-card ADDENDUM 1 @ 406b526d): a reSpecialized power that REPLACES a
// book power carries `sources: []` (the pack's convention, R-M11B-10), and `isActiveWith` reads an empty list as
// unsourced app content — so with a pack on, the books switches could not hide it and the core-first sort lost it. The
// list asks the BOOK record of the same id instead (the device's pristine base view): filtered, core-sorted and
// name-sorted by it. A record with sources of its own, or an ADDED power (no book record), is read as itself:
// unsourced, always shown, sorted among the non-core. The record itself is never changed.
function fcPowerBookOf(rec) {
  if (!rec || (rec.sources && rec.sources.length)) return rec;
  var base = (typeof window !== 'undefined') && window._canonicalCharbuildBase;
  var book = base && base.forcePowers && base.forcePowers[rec.id];
  return book || rec;
}
// buildBasicForcePowerList — active-sources filter + opt-in line gate (BOOKS-1c) + core-first sort
// (F&D CRB powers ahead of supplements, then alphabetical) — each read off fcPowerBookOf (R-M11R-5).
function buildBasicForcePowerList(allPowers, gameLine) {
  var _linePref = !!(window.SWRPGSources && window.SWRPGSources.lineFilterOn && window.SWRPGSources.lineFilterOn());
  var _kept = {};
  fcFilterActive((allPowers || []).map(fcPowerBookOf), gameLine ? [gameLine] : [], _linePref)
    .forEach(function(b) { if (b) _kept[b.id] = true; });
  var filtered = (allPowers || []).filter(function(rec) { return rec && _kept[rec.id]; });
  // B-336 LOGIC SITE. This listed the three core TITLES and matched against them. "Is this a core
  // book" is `group === 'Core'` on the vocabulary row — no list to keep in step, and no silent
  // failure when the registry starts storing keys (a key matches none of the three titles, so core
  // powers would simply have stopped sorting first, invisibly).
  var _S = window.SWRPGSources, _cur = _S && _S.current(), _idx = _cur && _cur.index;
  var isCore = function(rec) { return !!(_idx && _S.hasCoreSource(_idx, fcPowerBookOf(rec))); };
  return filtered.slice().sort(function(a, b) {
    var aCore = isCore(a);
    var bCore = isCore(b);
    if (aCore !== bCore) return aCore ? -1 : 1;
    return (fcPowerBookOf(a).name || '').localeCompare(fcPowerBookOf(b).name || '');
  });
}
// ForcePowerBuyList — the shared basic-power card list. isOwned(p)→bool, onBuy(p, cost), onRefund(id)
// optional (creator only). hideOwned filters out owned powers (advancement, where owned live in the
// upgrade list already). tourId anchors the first card (creator tutorial).
// B-41: `forceRating` is the character's RESOLVED rating (resolvedForceRating), and a power whose
// printed prerequisite it does not meet is shown-and-priced but NOT buyable — the requirement badge
// is the reason, so the blocked control is never unexplained.
// `priceCtx` (F-100 M12, DARK): the sheet's CharResolve.careerPowerPriceCtx — the Mentor discount on a career power, after
// creation (FaD Core p.109). The creator passes none, so it prices row 0 (the discount never applies at creation).
function ForcePowerBuyList({ powers, isOwned, xpRemaining, accent, gameLine, onBuy, onRefund, hideOwned, tourId, onInspect, forceRating, paper, held, priceCtx }) {
  var list = buildBasicForcePowerList(powers, gameLine);
  if (hideOwned) list = list.filter(function(p) { return !isOwned(p); });
  if (list.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: onInspect ? '6px' : '8px' }}>
      {list.map(function(p, idx) {
        var owned = isOwned(p);
        var basicCost = basicForcePowerCost(p, priceCtx);
        var canAfford = owned || (basicCost != null && xpRemaining >= basicCost);
        // B-41: two independent reasons a buy can be refused — price and prerequisite. They are kept
        // separate so the surface can say WHICH one applies; `canBuy` is what the control obeys.
        var frMet = owned || meetsForceRating(p, forceRating);
        var canBuy = canAfford && frMet && !held;   // B-855: a HELD sheet learns nothing (the sheet passes `held`; the creator never does)
        // Full description; strip the redundant "Force Power: <Name>" header the CRB prose ships with.
        // R3 Q2b — THE EATER, not a catch-all. This read `.replace(/\[[^\]]+\]/g, '')`: the SAME
        // glyph-eater as `\[.*?\]` wearing a different spelling, which is exactly why the census
        // regex for the catch-all could not see it. It deletes every dice symbol out of a Force
        // power's prose on the card the player buys it from. `desc` stays a STRING here on purpose:
        // the "Force Power: <Name>" header surgery below is a regex over it, and it is handed to
        // `onInspect` as `sel.desc`, where the DETAILS pane runs the drawer. Both render sites in
        // this component draw it through `glyphs`.
        var desc = cleanMarkup(p.description || '').replace(/\s+/g, ' ').trim();
        var nameEsc = (p.name || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        desc = desc.replace(new RegExp('^Force\\s+Power\\s*:\\s*' + nameEsc + '\\s*', 'i'), '');
        desc = desc.replace(/^Force\s+Power\s*:\s*/i, '').trim();
        // B-336 LOGIC SITE. This preferred the F&D core entry by matching its full TITLE, and fell
        // back to sources[0] on a miss — so under key storage it would have silently stopped
        // preferring F&D rather than reporting anything. Ask the vocabulary for the row's id.
        var _allSrc = p.sources || [];
        var _S2 = window.SWRPGSources, _cur2 = _S2 && _S2.current(), _idx2 = _cur2 && _cur2.index;
        var _fadIdx = _idx2 ? _allSrc.findIndex(function(s) {
          var m = _S2.sourceMeta(_idx2, s);
          return !!(m && m.id === 'fad');
        }) : _allSrc.indexOf('Force and Destiny Core Rulebook');
        // B-336 force-powers wave (ADDENDUM 62): the chosen source prints its TITLE — a key never reaches the row or the pane.
        var _srcPick = _fadIdx >= 0 ? _allSrc[_fadIdx] : (_allSrc[0] || '');
        var srcLabel = _srcPick ? gameLineBookLabel(_srcPick) : '';
        // onInspect (ADVANCEMENT, Jason 2026-07-21): a COMPACT row — name + a lit owned / cost indicator, no
        // inline description or BUY; tapping teaches + buys in the DETAILS pane (like the owned powers).
        // B-625 (the paper order): the buy list on the paper sheet — each power an entry: the name, the cost in the
        // head (or the refusal: not enough XP · requires Force rating N), the size line, the description folded
        // behind a teaser, the source; LEARN as the kit's commit. Same list, same spine (onBuy / onRefund).
        if (paper) {
          var size = forcePowerSize(p); var need = minForceRatingOf(p); var have = Number(forceRating) || 0;
          var refusal = owned ? null : held ? 'Held' : !frMet ? ('Requires Force rating ' + need + ' — you have ' + have) : !canAfford ? ('Not enough XP — ' + basicCost + ' needed, ' + xpRemaining + ' left') : null;
          var teaser = desc.length > 110 ? desc.slice(0, 110).replace(/\s+\S*$/, '') + '…' : desc;
          // F-100 surfacing K8 (R-SCC-5, ADDENDUM 2 F4): the MENTOR DISCOUNT says itself — the price the row charges is
          // forcePowerBasicCost's with the sheet's priceCtx; the gap to the printed row-0 price (no ctx) is the discount,
          // so the button reads "Learn · N XP (Mentor −5)" and the row carries the because. Dark exactly as the price is.
          var printedCost = priceCtx ? CharResolve.forcePowerBasicCost(p) : basicCost;
          var mentorOff = (!owned && basicCost != null && printedCost != null && printedCost > basicCost) ? printedCost - basicCost : 0;
          return (
            <div className="sb-e sb-fp" key={p.id} data-testid={'growth-buy-row'} data-power={p.id} data-refused={refusal ? '1' : '0'} data-owned={owned ? '1' : '0'}>
              <div className="sb-e-h"><span className="sb-e-n">{p.name}</span>
                {owned ? <span className="sb-e-m">Owned</span> : refusal ? <span className="sb-refuse" data-testid="growth-buy-refusal">{refusal}</span> : <span className="sb-e-m">{basicCost == null ? 'Cost ?' : basicCost + ' XP'}</span>}
                {owned ? (onRefund ? <button type="button" className="hp-quiet" onClick={function() { onRefund(p.id); }}>Refund</button> : null)
                  : <button type="button" className={canBuy ? 'hp-commit' : 'hp-quiet'} data-testid="growth-buy" data-held={held ? '1' : undefined} disabled={!canBuy}
                      data-mentor={mentorOff ? mentorOff : undefined}
                      aria-label={'Learn ' + p.name + (basicCost != null ? ' for ' + basicCost + ' XP' : '') + (mentorOff ? ', Mentor discount' : '')}
                      onClick={function() { if (canBuy && basicCost != null) onBuy(p, basicCost); }}>{'Learn' + (basicCost != null ? ' · ' + basicCost + ' XP' : '') + (mentorOff ? ' (Mentor \u2212' + mentorOff + ')' : '')}</button>}
              </div>
              {mentorOff ? <p className="sb-e-t" data-mentor-why={p.id} style={{ margin: '4px 0 0', fontWeight: 600 }}>{'A career power \u2014 Mentor discount (FaD p.109)'}</p> : null}
              {size ? <div className="sb-e-l" data-force-size={p.id}><div>Upgrades <b>{size.upgrades}</b></div><div>Whole power <b>{size.total + ' XP'}</b></div></div> : null}
              {desc ? <details className="sb-fold"><summary className="sb-e-h"><span className="sb-e-n">What it does</span><span className="sb-e-m sb-fold-teaser">{teaser}</span><i className="sb-grp-glyph" aria-hidden="true">▾</i></summary><p className="sb-e-t">{glyphs(desc)}</p></details> : null}
              {srcLabel ? <p className="sb-e-t" style={{ margin: '4px 0 0' }}><span className="sb-e-m">{srcLabel}</span></p> : null}
            </div>);
        }
        if (onInspect) {
          return h('div', { key: p.id, 'data-tour-id': idx === 0 ? tourId : undefined,
            onClick: function() { onInspect({ power: p, cost: basicCost, owned: owned, canAfford: canAfford, desc: desc, srcLabel: srcLabel,
              frMet: frMet, frNeed: minForceRatingOf(p), frHave: Number(forceRating) || 0,
              doBuy: function() { if (!owned && canBuy && basicCost != null) onBuy(p, basicCost); },
              doRefund: function() { if (owned && onRefund) onRefund(p.id); } }); },
            style: { padding: '9px 12px', cursor: 'pointer', borderRadius: 6,
              background: owned ? accent + '12' : 'rgba(255,255,255,0.03)',
              border: '1px solid ' + (owned ? accent + '55' : 'rgba(255,255,255,0.08)'),
              borderLeft: '3px solid ' + (owned ? accent : 'rgba(255,255,255,0.15)'),
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 } },
            h('div', { style: { minWidth: 0 } },
              h('div', { style: { fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#fff', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, p.name),
              minForceRatingOf(p) ? h('div', { style: { marginTop: 2 } }, h(ForceRatingReq, { rec: p, forceRating: forceRating, compact: true })) : null),
            h('span', { style: { flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em', color: owned ? accent : 'rgba(255,255,255,0.55)', textShadow: owned ? '0 0 8px ' + accent + '66' : 'none' } }, owned ? '✦ OWNED' : (basicCost != null ? basicCost + ' XP' : 'COST ?')));
        }
        return (
          <div key={p.id} data-tour-id={idx === 0 ? tourId : undefined} style={{
            padding: '10px 12px',
            background: owned ? accent + '12' : 'rgba(255,255,255,0.03)',
            border: '1px solid ' + (owned ? accent + '55' : 'rgba(255,255,255,0.06)'),
            borderLeft: '3px solid ' + (owned ? accent : 'rgba(255,255,255,0.12)'),
            borderRadius: '6px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: '#fff' }}>
                {p.name}
                {owned && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.16em', color: accent, marginLeft: '6px' }}>OWNED</span>}
              </div>
              <button type="button" onClick={function() { if (owned) { if (onRefund) onRefund(p.id); } else if (canBuy) { onBuy(p, basicCost); } }}
                disabled={!owned && !canBuy}
                style={{
                  // AS-6 44px TAP FLOOR (lap C1). Was `padding: '4px 10px'` → 22px tall; the Phase A
                  // recon measured 21 of this step's controls under the floor at every width.
                  // min-height + centring rather than a bigger font: the label keeps its size.
                  minHeight: '44px', padding: '4px 14px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: owned ? 'rgba(255,80,80,0.15)' : (canBuy ? accent + '30' : 'rgba(255,255,255,0.04)'),
                  border: '1px solid ' + (owned ? 'rgba(255,120,120,0.5)' : (canBuy ? accent + '70' : 'rgba(255,255,255,0.1)')),
                  borderRadius: '4px',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xs)', letterSpacing: '0.12em',
                  color: owned ? '#FFC4C4' : (canBuy ? accent : 'rgba(255,255,255,0.25)'),
                  cursor: (!owned && !canBuy) ? 'not-allowed' : 'pointer',
                }}>
                {owned ? (onRefund ? 'REFUND' : 'OWNED') : basicCost == null ? 'COST ?' : (canBuy ? 'BUY ' + basicCost + ' XP' : basicCost + ' XP')}
              </button>
            </div>
            {/* B-88 — the size line, above the description exactly as drawn. It sits under the
                price it corrects, so the two numbers are read together rather than a screen apart. */}
            {(function() {
              var size = forcePowerSize(p);
              if (!size) return null;
              return (
                <div data-force-size={p.id} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.5)', marginBottom: '5px' }}>
                  {size.upgrades} upgrades · <b style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}>{size.total} XP</b> for the whole power
                </div>
              );
            })()}
            {/* R3 Q2b — the drawer. `desc` now carries its glyph tokens (the eater above is
                structural-only), so the card DRAWS the dice symbols instead of eating them. */}
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.55, color: 'rgba(255,255,255,0.75)' }}>{glyphs(desc)}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 12px', marginTop: '4px' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.65)' }}>{srcLabel || ''}</span>
              <ForceRatingReq rec={p} forceRating={forceRating} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FCForcePowersStep({ character, gameData, gameLine, xpRemaining, accent, onBuy, onRefund, onContinue, forceRating }) {
  var allPowers = (gameData && gameData.charbuild) ? Object.values(gameData.charbuild.forcePowers) : [];
  // B-89: the powers this character has actually bought — each gets its read-only upgrade tree below.
  var ownedPowers = allPowers.filter(function(p) { return !!(character.forcePowers || {})[p.id]; });
  // B-32: the list build, printed cost, and card render live in the shared ForcePowerBuyList (above),
  // so the creator and the sheet's ADVANCEMENT buy basic powers off ONE RAW-cited core.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 16px' }}>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, marginBottom: '14px' }}>
        Each basic Force power costs its <span style={{ color: accent, fontWeight: 700 }}>printed XP</span> (shown per power). You can purchase upgrades (Strength, Magnitude, Range, Duration, Control, Mastery) in play.
      </div>
      <div style={{ marginBottom: '16px' }}>
        <ForcePowerBuyList
          powers={allPowers}
          isOwned={function(p) { return !!(character.forcePowers || {})[p.id]; }}
          xpRemaining={xpRemaining}
          accent={accent}
          gameLine={gameLine}
          onBuy={function(p) { onBuy(p.id, p); }}
          onRefund={onRefund}
          forceRating={forceRating}
          tourId="p-custom-force-card"
        />
      </div>
      {/* B-89: the creator drew ZERO of the 274 upgrade cells while the sheet drew all of them —
          and the talents step one step earlier drew its whole tree. Two adjacent steps, one
          component family, opposite answers to "how much future do we show". The tree now appears
          ONCE A POWER IS OWNED (the ruled drawing, frame 3), READ-ONLY: it teaches what the power
          becomes without pretending the upgrades are buyable at creation. */}
      {ownedPowers.map(function(p) {
        return (
          <div key={p.id} data-force-tree={p.id} style={{ marginBottom: '18px' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
              {String(p.name || '').toUpperCase()} — WHAT IT BECOMES
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: '10px', maxWidth: 'var(--desk-prose-max)' }}>
              These upgrades are bought in play, not at creation. Shown so you can see where the power leads.
            </div>
            <DirectionalTree
              model={forcePowerToModel({ power: p, ownedUpgradeNames: [], readOnly: true })}
              accent={accent} />
          </div>
        );
      })}
      <FCBottomAction label={(character.forcePowers && Object.keys(character.forcePowers).length > 0) ? 'CONTINUE' : 'CONTINUE WITHOUT FORCE POWERS'} onClick={onContinue} accent={accent} />
    </div>
  );
}

/* ── FC Motivation Step ──────────────────────────────── */

/* ── C15 — THE MOTIVATION STEP (RAW Step 8) ───────────────────────────────────────────────────
   Replaces four free-text boxes (DESIRE / FEAR / STRENGTH / FLAW) — Jason's complaint 8 — with the
   book's own structure: ONE Motivation, a general CATEGORY then a SPECIFIC within it.

   AUTHORITIES, cited not re-decided: `motivation-hooks-raw-mapping` + Amendments 1-2 (ONE
   motivation, singular throughout — EotE Step Eight's Rodian walkthrough) · `b103-motivation-save-
   shape` (the save contract) · `c15-motivation-roll-has-no-ranges` (what ROLL can and cannot do
   today) · table numbers from checkpoint 25b0028: Table 2-5 category d10 p.103; Tables 2-6/2-7/2-8
   specifics p.104-105. The page budget is SPENT — nothing here re-reads a page.

   ROLL IS ASYMMETRIC BY DATA, NOT BY DESIGN, and the ruling fixed the shape:
     · CATEGORY tier — ROLL is LIVE. Table 2-5's d10 ranges (1-3 Ambition · 4-6 Cause · 7-9
       Relationship · 10 REROLL) are recorded in the ruling itself, so nothing is invented.
     · SPECIFIC tier — CHOOSE-only, with one stated line. The book's specific tables are RANGE
       WEIGHTED and those ranges were never ingested: all 283 specifics carry name + category and
       no roll data at all. A d100 button would have to invent the ranges, which is the one thing
       the plan forbade. Deferred to the ranges data lap, with the absence STATED so it reads as a
       decision rather than an oversight.
     · A uniform random pick dressed as the table was REJECTED BY NAME — the book's distribution is
       weighted, so uniform-that-looks-like-the-table moves the invention from the numbers to the
       distribution.

   A ROLLED and a CHOSEN motivation are INDISTINGUISHABLE in the save, which is correct: the save
   records WHAT the character wants, not HOW the player arrived at it. */

// Table 2-5 (EotE p.94): the category die. 10 REROLLS — the book's cell reads "roll once on each
// of any two categories", and Jason ruled ONE motivation with the 10 simply re-rolled. That is a
// DELIBERATE simplification of one cell of an optional table, chosen with the cell in front of him.
var FC_MOTIV_CATEGORY_TABLE = [
  { max: 3, name: 'Ambition' },
  { max: 6, name: 'Cause' },
  { max: 9, name: 'Relationship' },
];
var FC_MOTIV_TRIO = ['Ambition', 'Cause', 'Relationship'];

function FCMotivationStep({ accent, onConfirm, gameData, existing, mechanic, onEditMechanic }) {
  var cats = (gameData && gameData.charbuild && gameData.charbuild.motivationCategories) || [];
  var specs = (gameData && gameData.charbuild && gameData.charbuild.motivationSpecifics) || [];

  var _c = useState((existing && existing.category) || null), catId = _c[0], setCatId = _c[1];
  var _s = useState((existing && existing.specific) || null), specId = _s[0], setSpecId = _s[1];
  var _w = useState((existing && existing.custom) || ''), custom = _w[0], setCustom = _w[1];
  var _r = useState(null), lastRoll = _r[0], setLastRoll = _r[1];

  var byId = {};
  cats.forEach(function(c) { byId[c.id] = c; });
  var chosenCat = catId ? byId[catId] : null;

  // The specifics join on the category NAME, not its id — `category_source_key` holds "Ambition".
  var catSpecifics = chosenCat
    ? specs.filter(function(sp) { return sp.category_source_key === chosenCat.name; })
    : [];

  // THE POOL, in Jason's ruled tier order: the line's trio · supplements · the nine Locations.
  // Locations are detected by their own name prefix, which is how the registry distinguishes them.
  var trio = cats.filter(function(c) { return FC_MOTIV_TRIO.indexOf(c.name) >= 0; });
  var locations = cats.filter(function(c) { return /^Location:/.test(c.name); });
  var supplements = cats.filter(function(c) {
    return FC_MOTIV_TRIO.indexOf(c.name) < 0 && !/^Location:/.test(c.name);
  });
  var tiers = [
    { key: 'trio', label: 'THE BOOK\u2019S THREE', cats: trio },
    { key: 'supp', label: 'MORE CATEGORIES', cats: supplements },
    { key: 'loc', label: 'BY WORLD', cats: locations },
  ].filter(function(t) { return t.cats.length; });

  function rollCategory() {
    // Reroll a 10 rather than branching to a second motivation (Amendment 2).
    var n; do { n = Math.floor(Math.random() * 10) + 1; } while (n === 10);
    var hit = FC_MOTIV_CATEGORY_TABLE.filter(function(row) { return n <= row.max; })[0];
    var match = cats.filter(function(c) { return c.name === (hit && hit.name); })[0];
    setLastRoll(n);
    if (match) { setCatId(match.id); setSpecId(null); }
  }

  var picked = !!(specId || (custom || '').trim());
  function confirm() {
    onConfirm({
      category: catId || null,
      specific: specId || null,
      custom: (custom || '').trim() || null,
    });
  }

  var pill = function(active) {
    return {
      textAlign: 'left', width: '100%', padding: '10px 12px', borderRadius: '6px', cursor: 'pointer',
      minHeight: '44px', touchAction: 'manipulation',
      background: active ? accent + '22' : 'rgba(255,255,255,0.03)',
      border: '1px solid ' + (active ? accent + '88' : 'rgba(255,255,255,0.10)'),
      fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 600,
      color: active ? accent : '#fff',
    };
  };

  // ── C15 commit 3 — THE FaD EMOTIONAL PAIR: DISPLAYED, NEVER RE-ASKED ────────────────────────
  // A Force and Destiny character already chose their Morality's strength/weakness pair back on the
  // mechanic step. Asking again here would be the app forgetting what the player told it — so this
  // step SHOWS the pair and offers a jump back to where it was decided. EotE and AoR display
  // nothing: they have no pair, and an empty labelled box is worse than silence.
  // Resolved from the registry at render through the SAME morPairs lookup the review uses — the
  // strength carries `weakKey`, which names its partner. Never a second copy of the pairing.
  var pairStrength = null, pairWeakness = null;
  var _nm = gameData && gameData.narrativeMechanics;
  if (mechanic && mechanic.type === 'morality' && _nm && _nm.morPairs) {
    var _s = (_nm.morPairs.strengths || []).filter(function(t) { return t.key === mechanic.descriptor; })[0];
    if (_s) {
      pairStrength = _s.name;
      var _w = (_nm.morPairs.weaknesses || []).filter(function(x) { return x.key === _s.weakKey; })[0];
      if (_w) pairWeakness = _w.name;
    }
  }

  return (
    <div data-motivation-step="1" style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 16px' }}>

      {/* The pair, read-only, with the door back to where it was chosen. */}
      {pairStrength && (
        <div data-motiv-pair style={{
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '8px', padding: '12px 14px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.38)', marginBottom: '4px' }}>YOUR EMOTIONAL PAIR</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: '#fff' }}>
              <b style={{ color: accent }}>{pairStrength}</b>{pairWeakness ? ' / ' + pairWeakness : ''}
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>{' \u2014 chosen with your Morality.'}</span>
            </div>
          </div>
          {onEditMechanic && (
            <button type="button" data-motiv-pair-edit onClick={onEditMechanic}
              aria-label="Edit your emotional pair on the Morality step"
              style={{
                marginLeft: 'auto', background: 'none', border: 'none', color: accent, cursor: 'pointer',
                padding: '12px', minHeight: '44px', minWidth: '44px', touchAction: 'manipulation',
                fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)', letterSpacing: '0.14em',
              }}>{'\u270E'} EDIT</button>
          )}
        </div>
      )}

      {/* ── TIER 1 — THE CATEGORY. Choose and roll are peers: same row, neither is the default. ── */}
      <div style={fcSectionHeaderStyle(accent)}>MOTIVATION</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <button type="button" data-motiv-roll="category" onClick={rollCategory} style={{
          background: accent + '18', border: '1px solid ' + accent + '40', borderRadius: '6px', cursor: 'pointer',
          padding: '11px 18px', minHeight: '44px', touchAction: 'manipulation',
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.14em', color: accent,
        }}>ROLL d10</button>
        {lastRoll != null && (
          <span data-motiv-rolled={lastRoll} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.6)' }}>
            rolled {lastRoll} &mdash; {chosenCat ? chosenCat.name : ''}
          </span>
        )}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.32)', letterSpacing: '0.06em' }}>
          Table 2-5, eote p.94 &middot; a 10 rerolls
        </span>
      </div>

      {tiers.map(function(t) {
        return (
          <div key={t.key} style={{ marginBottom: '12px' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.38)', marginBottom: '6px' }}>{t.label}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '6px' }}>
              {t.cats.map(function(c) {
                var src = (c.sources || [])[0];
                if (src) src = gameLineBookLabel(src);   // B-336 motivations wave (ADDENDUM 99 C5): a key prints its book's title
                return (
                  <button key={c.id} type="button" data-motiv-cat={c.id} aria-pressed={catId === c.id}
                    onClick={function() { setCatId(c.id); setSpecId(null); setLastRoll(null); }}
                    style={pill(catId === c.id)}>
                    {c.name}
                    {/* The sourcebook is printed WHERE THE DATA CARRIES ONE — 3 of 32 do. The other
                        29 render no source line rather than an invented attribution (ruled). */}
                    {src && <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', fontWeight: 400, letterSpacing: '0.06em', color: 'rgba(255,255,255,0.35)', marginTop: '3px' }}>{src}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ── TIER 2 — THE SPECIFIC. Choose-only today; the absence of ROLL is stated, not hidden. ── */}
      {chosenCat && (
        <div data-motiv-specifics="1" style={{ marginTop: '6px' }}>
          <div style={fcSectionHeaderStyle(accent)}>{chosenCat.name.toUpperCase()} &mdash; SPECIFICALLY</div>
          <div data-motiv-noroll style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.42)', marginBottom: '8px' }}>
            ROLL uses the book&rsquo;s printed ranges &mdash; they aren&rsquo;t in the app&rsquo;s data yet. Choose for now.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '6px' }}>
            {catSpecifics.map(function(sp) {
              return (
                <button key={sp.id} type="button" data-motiv-spec={sp.id} aria-pressed={specId === sp.id}
                  onClick={function() { setSpecId(sp.id); setCustom(''); }}
                  style={pill(specId === sp.id)}>{sp.name}</button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── WRITE-YOUR-OWN — inside the picker, not a separate mode (ruled). ── */}
      <div style={{ marginTop: '14px' }}>
        <div style={fcSectionHeaderStyle(accent)}>OR WRITE YOUR OWN</div>
        <input type="text" data-motiv-custom value={custom}
          onChange={function(e) { setCustom(e.target.value); if (e.target.value.trim()) setSpecId(null); }}
          placeholder="In your own words"
          style={{
            width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', minHeight: '44px',
            padding: '10px 12px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-lg)', color: '#fff', outline: 'none',
          }} />
      </div>

      <FCBottomAction label={picked ? 'CONTINUE' : 'CONTINUE WITHOUT A MOTIVATION'} onClick={confirm} accent={accent} />
    </div>
  );
}

/* ── FC Gear Step (B-94, lap C4) ──────────────────────
   The step itself, once the catalogue moved into the shared store. It holds what the wizard should
   hold — the purse, what has been bought, and the door — and nothing the store already does well.
   The creator's own 283-item parallel gear browser is deleted; this is what replaced it. */
// ═══ B-563 · STEP 10 · GEAR — the revision's gear sheet, transplanted at all three bands (dark) ═══
// Lifted verbatim from revision/creator-2026-09-10 (fcGearLineStats … FCGearSheet). The legacy
// FCGearStep below stays for the visitor's page; the store overlay is shared by both.
function fcGearLineStats(row, reg) {
  var sec = row.section || 'gear';
  var rec = reg && reg[sec] && reg[sec][row.key];
  if (!rec) return '–';
  if (sec === 'weapons') {
    var d = rec.damage || {};
    var base = d.base && d.base !== 'flat' ? d.base.charAt(0).toUpperCase() + d.base.slice(1) : '';
    var v = Number(d.value) || 0;
    var dmg = base ? base + ' ' + (v >= 0 ? '+' : '') + v : String(v);
    return 'Dmg ' + dmg + ' · Crit ' + critText(rec.crit);
  }
  if (sec === 'armor') {
    var s = 'Soak +' + (Number(rec.soak) || 0);
    if (Number(rec.defense) > 0) s += ' · Def +' + Number(rec.defense);
    return s;
  }
  return '–';
}

// THE ONE CALL (R-GC-3, PRAXIS §5): the draft's rows, mapped into the resolver's id-ref shape with
// their registry records, and the character's Brawn. The resolver's `over` is the state.
//
// THE ONE SUIT IS WORN — Jason 2026-09-19, "Worn" (B-700). Asked whether the creator's gear preview
// should count the single armour as worn or as carried, he answered "Worn", so the preview passes
// `state: 'worn'`. That is the SAME shape the assembly writes (`buildCanonicalEquipment`, B-699 —
// `{ id: id, state: 'worn' }`) and the same key `deriveItemState` reads first, so the gear step, the
// review preview and the saved sheet now print ONE number where they printed two. `computeEncumbrance`
// reads that key off THIS entry (`armor.state === 'worn'` → `Math.max(0, enc − 3)`, and a worn suit's
// own `encumbrance_threshold_bonus` then counts), which is why the state sits on the record object
// handed over rather than anywhere else: this function passes registry RECORDS, not id-refs, and the
// one resolver keys the reduction on the entry's `state`. The −3 is EotE p.168's — "Reduce the
// encumbrance value of all armor by 3 when worn".
//
// THE OLD CITE WAS MISATTRIBUTED, not merely superseded, and that is worth writing down so it is not
// restored. This comment read: UNWORN — carried "as just another item" (eote p.152's words), under
// R-GC-3. EotE p.152 describes the carried state GENERALLY — "the encumbrance value of armor … is
// different when it's worn, where the weight is distributed appropriately, and when it's carried" —
// and gives NO creation-time default either way; and R-GC-3 says nothing about the state at all,
// only that this is ONE call over the mapped rows. RAW was silent, so the citation never held.
//
// A SECOND ARMOUR ROW STILL RIDES AS GEAR. Armour is SINGULAR at creation (R-ASS-1's worn-singular),
// so a second armour row on an old draft is carried as gear rather than dropped — which is now the
// same rule as the store's R-SPC-9: the first suit worn, the rest in the pack.
// F-100 M9 (R-M9B-3): `speciesDelta` is the species' encumbrance_threshold_delta off the creator's RECORD
// (CharResolve.speciesEncumbranceDeltaOf), passed through to the one computation; 0 with the species rules key OFF.
function fcGearEncumbrance(inv, reg, brawn, speciesDelta) {
  var eq = { weapons: [], armor: null, gear: [] };
  (inv || []).forEach(function(it) {
    var sec = it.section || 'gear';
    var rec = reg && reg[sec] && reg[sec][it.key];
    var base = Object.assign({}, rec || { name: it.name, encumbrance: Number(it.enc) || 0 });
    var n = Math.max(1, Number(it.qty) || 1);
    if (sec === 'weapons') { for (var i = 0; i < n; i++) eq.weapons.push(Object.assign({}, base)); }
    else if (sec === 'armor' && !eq.armor) { eq.armor = Object.assign(base, { state: 'worn' }); }
    else { eq.gear.push(Object.assign(base, { qty: n })); }
  });
  var CR = window.CharResolve;
  if (!CR || typeof CR.computeEncumbrance !== 'function') return null;
  return CR.computeEncumbrance(eq, { brawn: Number(brawn) || 0 }, speciesDelta);
}

/* ── THE GEAR STEP'S THREE SHARED PARTS ────────────────────────────────────────────────────────
   Lifted out of `FCGearSheet` so the phone sheet below draws THE SAME OBJECTS rather than a copy of
   them — the rule `build-phone-gameline` set when it lifted `fcGameLinePlates` out of its own tree,
   and PRAXIS §5's "a second resolution path for the same number is a defect" one level down: a
   second COPY of the same markup is the same defect wearing a template's clothes. Both bands call
   these; neither owns them.

   THE DOOR AND THE PER-LINE REMOVE ARE CARRIED FORWARD, NOT INVENTED (ruling `phone-gear-census`
   R-PGeC-2 / its KEEP-UNDRAWN rows). The approved PHONE frame `phone_gear()` draws neither — its
   only non-nav control is the commit — while the approved DESKTOP frame `board_gear_1920` draws
   the door once and Remove five times. So the phone keeps the desktop frame's own idiom for both
   and the lab is asked for a phone frame that shows them; the build does not wait. That is why
   these are functions and not phone-local JSX: the thing being carried forward is the DESKTOP
   FRAME'S markup, and the only way to be sure of that is to make it the same markup. */
function fcGearDoor(onShop) {
  return (
    <React.Fragment>
      {/* THE CATALOGUE IS A DOOR, NOT A LIST (Jason 2026-08-27; R-GC-4): the shared store. Its
          interior is out of this card's OWNS — the same component, unchanged, at every band. */}
      {/* B-1545 (release-r7-bug-burndown ADDENDUM 77, ASK 2 (a)): the plate that stood here is GONE. The copy-voice
          sweep's CUT-3 (`32cf86508`, census row :9278:12, "narrates the store") cut the string node before `<b>rarity</b>`
          and left the rest as a mid-sentence fragment ("rarity shown as the difficulty…"); its sibling row (:9670:187)
          shows the cut meant the whole sentence. The ruled cut is finished, not the board's sentence restored. */}
      {/* `data-testid` KEPT: `smoke_creator_as9`'s door leg picks the door by it (R-PGeC-2). */}
      <button type="button" className="hp-commit hp-door" data-testid="creator-open-store" onClick={onShop}>Open the Store</button>
    </React.Fragment>
  );
}
function fcGearKitSechead(lines) {
  return (
    <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
      <h2>What You Are Carrying</h2>
      <span className="hp-cite" data-gear-lines={lines}>{lines + (lines === 1 ? ' line' : ' lines')}</span></div>
  );
}
/* THE STATS SPAN IS A BAND DIFFERENCE THE FRAME ITSELF DECLARES, and it is the one place this
   build does NOT carry the desktop row forward whole. The two approved frames draw the row
   DIFFERENTLY on purpose: `board_gear_1920` runs name · STATS · price · enc · Remove (five
   objects), `phone_gear()` runs name · price · enc (three). That is not the same silence the door
   and Remove sit in — the phone frame OMITS those two entirely, which is why R-PGeC-2 fills them
   from the desktop, but it POSITIVELY DRAWS this row and says it has three spans. A frame that
   draws a thing is the authority on that thing, so the stats line goes at 390 and the row obeys
   the drawing. `compact` is a parameter and not a second copy of the plate: the difference is
   DECLARED in one place rather than duplicated into a phone-local block that can drift.
   FLAGGED at the gate — R-PGeC's KEEP/RETIRE table enumerates the door and Remove and does not
   reach this third object, so the reading above is the seat's and the architect should see it. */
function fcGearKitPlate(inv, reg, onSell, compact) {
  return (
    <div className="hp-plate" data-pad="data" data-gear-kit="1">
      {/* UNDRAWN — NOTHING CARRIED: the app's sentence, in idiom. */}
      {!inv.length ? <span className="hp-none" data-gear-empty="1">{'Nothing yet. A character can begin with no gear at all.'}</span> : null}
      {inv.map(function(it, i) {
        var n = Math.max(1, Number(it.qty) || 1);
        return (
          <div className="hp-buy hp-gearline" key={it.key + ':' + i} data-carried={it.key} data-gear-section={it.section || 'gear'}>
            <span className="hp-buy-name">{it.name + (n > 1 ? ' \u00d7' + n : '')}</span>
            {compact ? null : <span className="hp-buy-cost" data-gear-stats="1">{fcGearLineStats(it, reg)}</span>}
            <span className="hp-buy-step" data-gear-price={(Number(it.price) || 0) * n}>{((Number(it.price) || 0) * n) + ' cr'}</span>
            <span className="hp-buy-cost" data-gear-enc={(Number(it.enc) || 0) * n}>{((Number(it.enc) || 0) * n) + ' enc'}</span>
            <button type="button" className="hp-buy-btn" data-gear-remove={it.key} aria-label={'Remove ' + it.name}
              onClick={function() { onSell(it.key); }}>Remove</button>
          </div>
        );
      })}
    </div>
  );
}

/* ── THE BECAUSE LINES (F-100 surfacing, ruling `f100-surfacing-creator-concept` R-SCC-6 as errata'd, ADDENDUM 4 FLAG 1 (iii),
   ADDENDUM 18 Q4; approved frame K9) — one species row in the record's form: "+1,000 after creation — Deep Pockets (Muun)",
   "+1,100 — Vast Wealth (Anomid)", "−2 — No Hands (Fefze)" (D4: the typographic minus, the record's order). The rows are
   the purse reader's `rows` (the credits memo) and speciesEncumbranceRowsOf — the same walks the figures read. */
function fcBecauseLine(r) {
  var n = Number(r && r.amount) || 0;
  return (n < 0 ? '\u2212' : '+') + Math.abs(n).toLocaleString('en-US') + (r.timing === 'after_creation' ? ' after creation' : '')
    + ' \u2014 ' + r.ability + ' (' + r.species + ')';
}
function fcBecauseSpans(rows, attr) {
  return (rows || []).map(function(r, i) {
    var a = { key: attr + i, className: 'hp-why', style: { display: 'block', marginTop: '4px', color: 'var(--doc-ink)' } };
    a[attr] = '1';
    return React.createElement('span', a, fcBecauseLine(r));
  });
}

/* ── THE STARTING ENTITLEMENT (F-100 surfacing, ruling `f100-surfacing-creator-concept` R-SCC-3; D-229 Q1 (a) ii ships it
   LIVE, ADDENDUM 18; approved frame K7, `Docs/mockups/f100-surfacing-creator.html` @ 1e2438f9f) ──────────────────────
   ONE reader for the gear step, the review and the sheet GEAR card: the career record's `starting_entitlements`, each
   with `declined` read off the save's `entitlements` ([{id, declined: true}], written by the gear step's tick ONLY while
   ticked; absent = granted — brief §3.1). An id the career does not carry is ignored, so nothing invents a line. */
function fcEntitlementLines(careerRec, saved) {
  var off = {};
  (Array.isArray(saved) ? saved : []).forEach(function(e) { if (e && e.id && e.declined === true) off[e.id] = true; });
  return ((careerRec && careerRec.starting_entitlements) || []).filter(function(e) { return e && e.id; }).map(function(e) {
    return { id: e.id, name: e.name || e.id, note: e.note || '', declined: !!off[e.id] };
  });
}
// The tick's write: the next `entitlements` value for a press — the declined ids of THIS career, or undefined (the field
// goes; absent = granted).
function fcEntitlementToggle(lines, id) {
  var next = (lines || []).filter(function(l) { return l.id === id ? !l.declined : l.declined; })
    .map(function(l) { return { id: l.id, declined: true }; });
  return next.length ? next : undefined;
}
// The gear step's plate: "<name> — <note>" and the Decline tick in the option-row idiom (hp-head + hp-box, D6).
function FCEntitlementPlate({ lines, onToggle }) {
  if (!lines || !lines.length) return null;
  return (
    <div data-entitlements="1">
      <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Starting Entitlement</h2></div>
      <div className="hp-plate" data-pad="data">
        {lines.map(function(l, i) {
          return (
            <React.Fragment key={l.id}>
              <p className="hp-prose hp-prose-sm" data-entitlement-line={l.id}
                style={{ margin: (i ? '12px' : '0') + ' 0 8px', maxWidth: 'none' }}>{l.name + (l.note ? ' \u2014 ' + l.note : '')}</p>
              <button type="button" className="hp-head" data-state={l.declined ? 'active' : 'passive'}
                aria-pressed={l.declined ? 'true' : 'false'} data-entitlement-decline={l.id}
                aria-label={'Decline the ' + l.name}
                onClick={function() { onToggle(l.id); }}>
                <span className="hp-box" data-on={l.declined ? '1' : '0'} />
                <span className="hp-name">Decline</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ── THE GEAR PHONE SHEET (`phone-gear-census` R-PGeC-1) ───────────────────────────────────────
   ONE COLUMN IN THE FRAME'S ORDER, under the shared `FCPhoneShell`. `phone_gear()` at the freeze
   pin runs: runhead -> wave -> hr -> a two-cell status pill -> maker/desig -> the lead plate with
   its cites -> "What You Are Carrying" -> the kit rows -> a collapsed "So Far" -> the commit. The
   shell draws the first three and the last; this draws the rest, in that order.

   THE DOOR SITS WHERE THE DESKTOP PUTS IT — before the kit, because the desktop frame Jason
   approved draws it there and the phone frame draws it nowhere at all. Placing it after the kit
   would have been just as consistent with the phone frame's silence, which is exactly why the
   silence is not the authority here: the desktop frame is (R-PGeC-2).

   NO STEP-LEVEL SCROLL ZONE. The shell's own box scrolls since `phone-shell-scroll` merged, and a
   second scroller inside it is the trap that lap existed to remove — a pane that scrolls within a
   pane that scrolls strands the content of whichever one the thumb misses. The kit is a plain
   plate; it grows and the shell carries it. */
function FCGearSheetPhone(p) {
  var inv = p.inventory || [], credits = p.credits || { total: 500, spent: 0, remaining: 500 };
  var reg = p.reg || null;
  var enc = fcGearEncumbrance(inv, reg, p.brawn, p.speciesEncDelta);
  var overBudget = credits.remaining < 0;
  var overLimit = !!(enc && enc.over > 0);
  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  return (
    <FCPhoneShell band={p.band} rootAttrs={{ 'data-gear-step': '1' }}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      /* THE PILL IS THE RAIL, FOLDED. The desktop rail carries Starting / Spent / Left and Carried
         of limit; the frame's pill carries the two figures a player at the counter actually needs,
         and the rest is one tap away in the store. Same resolver, same numbers. */
      status={<React.Fragment>
        <div><span className="hp-status-k">Left</span>
          <span className="hp-status-v" data-creator-purse={credits.remaining}
            data-over={overBudget ? '1' : undefined}
            style={{ color: overBudget ? 'var(--doc-action)' : undefined }}>{credits.remaining + ' cr'}</span></div>
        <div><span className="hp-status-k">Carried</span>
          <span className="hp-status-v" data-gear-carried={enc ? enc.carried : ''} data-gear-limit={enc ? enc.limit : ''}
            data-over={overLimit ? '1' : undefined}
            style={{ color: overLimit ? 'var(--doc-action)' : undefined }}>
            {enc ? enc.carried + ' of ' + enc.limit : '\u2013'}</span>
          {fcBecauseSpans(p.speciesEncRows, 'data-creator-enc-why')}</div>
      </React.Fragment>}
      /* NEVER GATED, and now never TWO-WORDED either: the legacy phone step's
         "CONTINUE" / "CONTINUE WITHOUT GEAR" pair RETIRES with its mount (R-PGeC's table) for the
         frame's own "Continue to Review \u203a". The label is DERIVED from the walk rather than
         typed, so a character who skips a step still reads the truth. */
      act={<button type="button" className="hp-commit" data-creator-commit="1" onClick={p.onConfirm}>
        {'Continue to ' + nextLabel + ' '}<span>{'\u203a'}</span></button>}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">{fcMakerLine((p.stepIndex == null) ? null : p.stepIndex + 1, p.stepTotal)}</div>
      </div>
      <h1 className="hp-desig">{p.stepName}</h1>
      <div className="hp-desig-rule" />

      <div className="hp-plate" data-pad="read" style={{ marginTop: '11px' }}>
        <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
          {'You start with '}<b>{credits.total + ' credits'}</b>{'. On a weapon weigh '}<b>Damage</b>{', '}<b>Crit</b>{', '}
          <b>Range</b>{' and its qualities; on armour, '}<b>Soak</b>{' cuts damage while '}<b>Defence</b>{' adds setback dice to attackers.'}
          {/* F-100 surfacing K9 (R-SCC-6): the purse's because, after the sentence that says the purse (as drawn at 390) */}
          {fcBecauseSpans(credits.rows, 'data-creator-purse-why')}
        </p>
        {/* THE CITES ARE THE APP'S, NOT THE FRAME'S. The drawing carries two placeholder page
            numbers for the core book and neither is either rule's real printed page — the same
            class the motivation step's frame carried and the app's own history already rejected
            once. `fcStepCite` resolves both from `creator_step_cites.json`, and the desktop sheet
            has printed them all along (R-PGeC's head-cite row). */}
        {p.cite || p.encCite ? (
          <div style={{ marginTop: '11px' }}>
            {p.cite ? <span className="hp-cite" data-step-cite="starting_credits">{p.cite}</span> : null}
            {p.encCite ? <span className="hp-cite" data-step-cite="encumbrance" style={{ marginLeft: '7px' }}>{p.encCite}</span> : null}
          </div>
        ) : null}
      </div>
      {/* F-100 surfacing K7 (R-SCC-3): the entitlement under the plate that says the purse, as drawn at 390. */}
      <FCEntitlementPlate lines={p.entitlementLines} onToggle={p.onEntitlementToggle} />

      {/* THE TWO SPOKEN STATES, kept from the desktop sheet because they are the app's own law and
          the frame is silent on both: over budget and over the limit are SAID, never refused
          (`gear-concept` A1). The pill turns them red; these say what they mean. */}
      {overBudget ? (
        <span className="hp-none" data-gear-over-budget={-credits.remaining} style={{ display: 'block', marginTop: '11px' }}>
          {'You are over budget by ' + (-credits.remaining) + ' cr. Nothing is blocked \u2014 sell something back, or carry the debt and tell your GM.'}
        </span>
      ) : null}
      {overLimit ? (
        <span className="hp-none" data-gear-over-limit={enc.over} style={{ display: 'block', marginTop: '8px' }}>
          {'Over by ' + enc.over + ': ' + enc.setbacks + ' setback ' + (enc.setbacks === 1 ? 'die' : 'dice') + ' on Agility and Brawn checks'
            + (enc.freeManeuverLost ? ', and no free maneuver each turn.' : '.')}
        </span>
      ) : null}

      <div style={{ marginTop: '18px' }}>{fcGearDoor(p.onShop)}</div>
      {fcGearKitSechead(inv.length)}
      {fcGearKitPlate(inv, reg, p.onSell, true /* compact: the frame's three-span row */)}

      {/* SO FAR FOLDS on the phone — the frame makes it a `<details>` at the foot rather than a
          rail block there is no room for, the same idiom every sibling phone sheet carries. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'\u25be'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <table className="hp-ledger"><tbody>
            {(p.soFar || []).map(function(r) {
              return (
                <tr key={r.k}>
                  <td className="hp-lkey">{r.k}</td>
                  <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
      </details>
    </FCPhoneShell>
  );
}

function FCGearSheet(p) {
  // THE PHONE IS ITS OWN SHEET, not this one reflowed — see `FCGearSheetPhone`. Same branch
  // `FCCharSheet` takes, and the same reason: the frame gives the phone a status pill instead of
  // the rail's three plates, folds So Far into a `<details>`, and runs one column.
  if (p.band === 'mobile') return <FCGearSheetPhone {...p} />;
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal || (p.steps || []).length;
  var inv = p.inventory || [], credits = p.credits || { total: 500, spent: 0, remaining: 500 };
  var reg = p.reg || null;
  var enc = fcGearEncumbrance(inv, reg, p.brawn, p.speciesEncDelta);
  var overBudget = credits.remaining < 0;
  var overLimit = !!(enc && enc.over > 0);
  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  var lines = inv.length;

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-gear-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: Credits · Encumbrance · So Far · the commit. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1" data-rail-stack="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Credits</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="credits">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting</dt><dd data-gear-starting={credits.total}>{credits.total + ' cr'}</dd>
                {fcBecauseSpans(credits.rows, 'data-creator-purse-why')}</div>
              <div className="hp-xp"><dt>Spent</dt><dd data-gear-spent={credits.spent}>{credits.spent + ' cr'}</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Left</dt>
                <dd data-creator-purse={credits.remaining}>{credits.remaining + ' cr'}</dd></div>
            </dl>
            {/* UNDRAWN — OVER BUDGET, a SPOKEN state (Addendum 1): the app's own line, where ruled. */}
            {overBudget ? (
              <span className="hp-none" data-gear-over-budget={-credits.remaining} style={{ display: 'block', marginTop: '8px' }}>
                {'You are over budget by ' + (-credits.remaining) + ' cr. Nothing is blocked — sell something back, or carry the debt and tell your GM.'}
              </span>
            ) : null}
          </div>
          {/* F-100 surfacing K7 (R-SCC-3): the entitlement under the Credits plate, as drawn. */}
          <FCEntitlementPlate lines={p.entitlementLines} onToggle={p.onEntitlementToggle} />
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
            <h2>Encumbrance</h2><span className="hp-cite">Brawn + 5</span></div>
          <div className="hp-plate" data-pad="data" data-rail-section="encumbrance">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp" data-total="1">
                <dt>Carried</dt>
                <dd data-gear-carried={enc ? enc.carried : ''} data-gear-limit={enc ? enc.limit : ''}>
                  {enc ? enc.carried + ' of ' + enc.limit : '–'}</dd>
                {fcBecauseSpans(p.speciesEncRows, 'data-creator-enc-why')}</div>
            </dl>
            <p className="hp-prose hp-prose-sm" style={{ margin: '8px 0 0' }}>
              {'Over the limit is a '}<b>setback die</b>{' on Agility and Brawn checks for each point above, not a block.'}
            </p>
            {/* UNDRAWN — OVER THE LIMIT, spoken from the resolver's own figures. */}
            {overLimit ? (
              <span className="hp-none" data-gear-over-limit={enc.over} style={{ display: 'block', marginTop: '8px' }}>
                {'Over by ' + enc.over + ': ' + enc.setbacks + ' setback ' + (enc.setbacks === 1 ? 'die' : 'dice') + ' on Agility and Brawn checks'
                  + (enc.freeManeuverLost ? ', and no free maneuver each turn.' : '.')}
              </span>
            ) : null}
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          <div className="hp-railact" data-rail-section="commit">
            {/* NEVER GATED (Addendum 1): over budget and over the limit are spoken above, not refused here. */}
            <button type="button" className="hp-commit" data-creator-commit="1" onClick={p.onConfirm}>
              {(lines ? 'Continue to ' + nextLabel + ' ' : 'Continue without gear ')}<span>{'›'}</span>
            </button>
          </div>
        </div>

        {/* ── THE HEAD: the board's stephead pair, the lead, the line's two cites. */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div className="hp-stephead">
            <div className="hp-stephead-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                <span className="hp-slash"><i /><i /><i /></span>
                <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
              </div>
              <h1 className="hp-desig">{p.stepName}</h1>
              <div className="hp-desig-rule" />
            </div>
            <div className="hp-plate" data-pad="read"
              style={{ maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
                {'You start with '}<b>{credits.total + ' credits'}</b>{'. On a weapon weigh '}<b>Damage</b>{', '}<b>Crit</b>{', '}
                <b>Range</b>{' and its qualities; on armour, '}<b>Soak</b>{' cuts damage while '}<b>Defence</b>{' adds setback dice to attackers.'}
              </p>
              {p.cite || p.encCite ? (
                <div style={{ marginTop: '11px' }}>
                  {p.cite ? <span className="hp-cite" data-step-cite="starting_credits">{p.cite}</span> : null}
                  {p.encCite ? <span className="hp-cite" data-step-cite="encumbrance" style={{ marginLeft: '7px' }}>{p.encCite}</span> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the door, then what came back through it. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            {fcGearDoor(p.onShop)}
            {fcGearKitSechead(lines)}
            {fcGearKitPlate(inv, reg, p.onSell)}
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}

function FCGearStep({ character, credits, accent, onShop, onSell, onContinue }) {
  var inv = (character.gearLedger && character.gearLedger.inventory) || [];
  var over = credits.remaining < 0;
  return (
    <div style={{ padding: '0 16px 16px', animation: 'slideInRight 0.3s ease-out' }}>
      {/* The purse, in the characteristics step's idiom as ruled — one design language across steps. */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.45)' }}>PURSE</span>
        <span data-creator-purse={credits.remaining} style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'var(--fs-xl)', color: over ? 'var(--text-danger)' : accent }}>
          {credits.remaining}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'rgba(255,255,255,0.45)', marginLeft: '6px' }}>/ {credits.total} CREDITS</span>
        </span>
      </div>
      {over && (
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--text-danger)', lineHeight: 1.5, marginBottom: '10px', maxWidth: 'var(--desk-prose-max)' }}>
          You are over budget. Nothing is blocked — sell something back, or carry the debt and tell your GM.
        </div>
      )}
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: '14px', maxWidth: 'var(--desk-prose-max)' }}>
        Everything in the store is available to you — the whole catalogue, with each item's rarity shown as the difficulty of finding it. Restricted goods are marked <b style={{ color: 'rgba(255,255,255,0.75)' }}>(R)</b>; your GM decides whether you have them.
      </div>
      <button onClick={onShop} data-testid="creator-open-store" style={{
        width: '100%', minHeight: '52px', borderRadius: '8px', cursor: 'pointer', marginBottom: '18px',
        background: accent + '22', border: '1px solid ' + accent + '88', color: accent,
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.12em',
      }}>OPEN THE STORE</button>

      <div style={fcSectionHeaderStyle(accent)}>WHAT YOU ARE CARRYING</div>
      {!inv.length && (
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, padding: '8px 0' }}>
          Nothing yet. A character can begin with no gear at all.
        </div>
      )}
      {inv.map(function(it) {
        return (
          <div key={it.key} data-carried={it.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#fff' }}>{it.name}{(it.qty || 1) > 1 ? ' ×' + it.qty : ''}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.45)' }}>{it.price} CR{it.enc ? ' · ENC ' + it.enc : ''}</div>
            </div>
            <button onClick={function() { onSell(it.key); }} aria-label={'Remove ' + it.name} style={{
              minHeight: '44px', minWidth: '44px', borderRadius: '6px', cursor: 'pointer',
              background: 'none', border: '1px solid rgba(255,120,120,0.35)', color: '#FFC4C4',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em',
            }}>REMOVE</button>
          </div>
        );
      })}
      <FCBottomAction label={inv.length ? 'CONTINUE' : 'CONTINUE WITHOUT GEAR'} onClick={onContinue} accent={accent} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   THE DECODER FAMILY — ONE EATER, ONE DRAWER (R3 Q2b; ruling item-dice-glyphs-r3 §3)

   OggDude description text carries two different kinds of bracket. They need two different
   answers, and for years this app fused them into one function that answered "delete it" to both.

     STRUCTURAL MARKUP   [H1]…[h1]  [p]  [br]  [B]…[b]     → the EATER removes it (it is layout)
     DICE-GLYPH TOKENS   [SE] [DI] [AD] [TR] …             → the DRAWER renders it (it is MECHANICS)

   THE EATER is `cleanMarkup` below. It must NEVER contain a `\[.*?\]` catch-all: that catch-all
   ran after the structural passes and swallowed every glyph token as collateral. It is the defect
   R3 exists to close — three copies of it deleted 2,144 item rows' dice symbols, and its fourth
   copy here was silently eating the 756 glyph tokens in talents.json.

   THE DRAWER is `glyphs` below — a 3-line adapter over the ONE shared `glyphText`
   (lib/shared/ui.jsx, exported on window.SWRPGUI), the same adapter lib/store/view.jsx,
   lib/shared/yardView.jsx and lib/shared/vesselView.jsx already use. CONSUMED, never copied.

   `renderDescription` — RETIRED HERE. It was the twin: the eater's structural passes and the
   drawer's token pass fused into one local function, kept in sync with `glyphText` by hand across
   two files. B-7 flagged the missing parity guard a month before R3; the whole R3 arc is the bill.
   Its 10 call sites now read `glyphs(cleanMarkup(x))` — the two jobs, named, in order, visibly
   separate. Do not re-fuse them into a third function; `test/decoder_family_parity.cjs` fails if
   `renderDescription` comes back.
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

// THE EATER — structural markup only. NO `\[.*?\]`. NO glyph handling.
function cleanMarkup(text) {
  if (!text) return '';
  return text
    .replace(/\[H\d\].*?\[h\d\]\s*/gi, '')
    .replace(/\[p\]/gi, '\n')
    .replace(/\[br\]/gi, '\n')
    .replace(/\[B\](.*?)\[b\]/gi, '$1')
    .replace(/ +(\.(?![.\d])|[,;])/g, '$1')
    .replace(/^\s+/, '').trim();
}

// PROSE_GLYPH_SIZE — the em size the retired `renderDescription` passed to `symbolSpan`. Named and
// kept as this adapter's default so the twin's retirement is invisible to a reader: every one of
// its call sites drew at 1.3, and `glyphText`'s own default is 1.1.
var PROSE_GLYPH_SIZE = 1.3;

// THE DRAWER — consumed from its ONE home, window.SWRPGUI.glyphText (lib/shared/ui.jsx).
// Read off `window` at CALL time rather than the module-load destructure at the top of this file:
// it keeps the absent-renderer path real and exercisable (scripts/smoke_item_glyphs.py deletes the
// drawer at runtime and re-renders). DEGRADE RULE, matching the other three adapters: no drawer →
// the RAW STRING, never null. A missing renderer must not delete the description.
function glyphs(text, size, opts) {
  var U = window.SWRPGUI || {};
  return typeof U.glyphText === 'function' ? U.glyphText(text, size || PROSE_GLYPH_SIZE, opts) : text;
}
// B-1600 (ruling release-r7-bug-burndown ADDENDA 251, 276): the creator ground's prose callers pass this, so the
// derived board's `[data-doc-ground="creator"] .hp-sym` (1.02em) draws the glyph's box instead of an inline size.
// Only callers that render on that ground pass it; every other caller draws as before.
var CREATOR_GLYPHS = { cssSized: true };

// PLAIN-TEXT SINK PROJECTOR — for sinks that take a STRING and cannot host a React node. Removes
// EXACTLY the canonical `[XX]` token shape and nothing else; it is deliberately not `\[.*?\]`, so
// it can never grow into a fifth catch-all. JS mirror of
// `scripts/canonical/item_markup.strip_glyph_tokens`. Its callers:
//   · `pdfGlyphRuns` (below) — the printed sheet's dice projector, which matches every token the dice table knows and
//     prints it as a die (B-981, D-404 item 5); what reaches this function from there is only a token the table does
//     NOT know, removed as `glyphText` draws it on screen (nothing);
//   · the FFG form's `pdfText` — names and upgrades, and summaries whose rule carries no die (a form field holds one font);
//   · handleForcePowerBuy's persisted `.desc` — a save shape held byte-identical while the R-SY4 question of what a save
//     should store is with the architect (HOLD, not can't).
// Guard H1-H4 pins it narrow; if the HOLD is released, that caller leaves, not this function.
// (R3 Q2b's "a PDF cannot draw a glyph" is retired: D-404 item 5 chose the dice font over spelling the die out.)
function plainTextGlyphless(text) {
  return String(text || '').replace(/\[[A-Z]{2}\]/g, '');
}

// THE PRINTED SHEET'S DICE PROJECTOR (B-981, DECISIONS D-404 item 5 "Add a dice font"). A PDF page CAN draw a die once
// the app's own dice font is embedded, so the exporters no longer drop the tokens: this turns prose into RUNS, text and
// symbols in order, and `pdfSymbolKit` (beside ensurePDFLib) measures, wraps, fits and draws them. Both exporters and the
// signature block go through it; there is no second path.
//   · It MATCHES only the tokens the shared table knows (window.SWRPGDice.BRACKET_TOKENS), built from that table's keys,
//     so it is a matcher and never a deleter. A bracket-shaped token the table does not know is removed from the text run
//     by `plainTextGlyphless` above, the one stripper (H1-H4) — the same nothing that `glyphText` draws on screen (C-Q1-2).
//   · Each symbol prints from ONE font, public/fonts/sw-rpg-icons.woff (MIT; it carries every die shape and every result).
//     The six dice whose screen glyph is the Genesys font's letter take the same shape from this font (PDF_DIE_SHAPE), and
//     the screen's FILL FAMILY (light / Force die hollow, Force pip half) takes this font's hollow and half marks.
//   · A token the table knows that this font cannot draw THROWS — the export fails loudly; it never prints a silent gap.
var PDF_DIE_SHAPE = { j: '\ue900', k: '\ue902', l: '\ue904' };   // Genesys d6 / d8 / d12 letter → the same shape in sw-rpg-icons
var PDF_FILL_MARK = { hollow: { '\ue93e': '\ue93f', '\ue904': '\ue905' }, half: { '\ue93e': '\ue940' } };
function pdfSymbolChar(key) {
  var D = window.SWRPGDice || {};
  var sym = (D.SYMBOLS || {})[key];
  if (!sym) throw new Error('PDF dice: no symbol for "' + key + '"');
  var ch = sym.font === 'SWRPGIcons' ? sym.char : PDF_DIE_SHAPE[sym.char];
  var fill = (D.FILL_FAMILY || {})[key];
  if (ch && fill) ch = (PDF_FILL_MARK[fill] || {})[ch];
  if (!ch) throw new Error('PDF dice: the dice font has no mark for "' + key + '"');
  return ch;
}
function pdfGlyphRuns(text) {
  var bridge = (window.SWRPGDice || {}).BRACKET_TOKENS || {};
  var keys = Object.keys(bridge);
  if (!keys.length) throw new Error('PDF dice: the dice table (window.SWRPGDice.BRACKET_TOKENS) is not loaded');   // loud, never a silent strip
  var parts = String(text || '').split(new RegExp('(\\[(?:' + keys.join('|') + ')\\])'));
  var runs = [];
  parts.forEach(function (p, i) {
    if (i % 2) { runs.push({ sym: bridge[p.slice(1, -1)] }); return; }
    var t = plainTextGlyphless(p);
    if (t) runs.push({ text: t });
  });
  return runs;
}

// ── Canonical registries (ADR 0003 items cluster) ──────────────────────────
// Loaded once, cached on window — mirrors the existing window._qualitiesDb global-reference
// pattern. Both the creator (the shared STORE + the equipment builder) and the sheet viewer
// (resolveSheetEquipment) read this, so PC equipment is one canonical id-ref shape, resolved
// at render — never a persisted display string (the double-brawn guardrail, Q4).
function loadCanonicalRegistries() {
  // F-100 UD-1f (ruling f100-ud1-contracts H6): THE ONE items loader, lib/shared/itemRegistries.js —
  // shared with the store and BYOA, which used to fetch the same cache key in other shapes ("whoever
  // fetches first wins"). Same five files as before (weapons, armor, gear, attachments — F-86's B-226
  // — and the skills vocab — R-PCA-2's pool_mods anchor join), same `_canonicalRegistries` global,
  // now the DISCOVERY view (active user packs folded in); the pristine base is
  // `_canonicalRegistriesBase`, which is what a sheet without a user: stamp resolves against (H2).
  return window.SWRPGItemRegistries.load();
}

// B-27 (ruling creator-boot-failure-triage) — the shared creator boot-failure state.
// Both canonical loaders CACHE their promise even when it resolved null (a failed load),
// so a transient blip was a dead builder until hard reload; RETRY must clear them first.
function clearCanonicalLoadCaches() {
  window._canonicalRegistriesPromise = null;
  window._canonicalCharbuildPromise = null;
}

// One honest error screen behind all three creator doors (Quick Start / Guided / Custom) —
// never a forever-spinner, never a blank crash.
// B-1257 (DECISIONS D-411 item 13, page generator scripts/r7_paper_sweep_leftovers_page.py @ fa5981119): the builder's failed
// download is D-400's shared notice with RETRY on the creator's paper — today's sentence word for word, the "Couldn't load the
// galaxy" head dropped (the notice idiom has no head)
function DataLoadErrorScreen({ onRetry }) {
  return (
    <div data-testid="boot-error" data-doc-ground="creator" style={{ position: 'absolute', inset: 0, background: 'var(--doc-paper)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '24px', textAlign: 'center', zIndex: 5 }}>
      <SheetNotice testid="boot-notice" paper standalone actTestid="boot-retry" actionLabel="RETRY" onAction={onRetry}>
        Some of the character data didn&rsquo;t finish downloading. Check your connection and retry.
      </SheetNotice>
    </div>
  );
}

// OggDude _source_key -> { id, section } across the canonical registries. The QuickBuild role
// loadouts are keyed by OggDude Key; this bridges them to canonical id-refs. Built lazily + cached.
function canonicalRefForSourceKey(srcKey) {
  var regs = window._canonicalRegistries;
  if (!regs || !srcKey) return null;
  if (!window._canonSourceKeyMap) {
    var map = {};
    ['weapons', 'armor', 'gear'].forEach(function(sec) {
      var reg = regs[sec] || {};
      Object.keys(reg).forEach(function(id) { var sk = reg[id]._source_key; if (sk) map[sk] = { id: id, section: sec }; });
    });
    window._canonSourceKeyMap = map;
  }
  return window._canonSourceKeyMap[srcKey] || null;
}

// B-1195: every printed Critical goes through the one display in the item core ("—" for none; ItemResolve.critText).
function critText(c) { return window.ItemResolve.critText(c); }

// D-3.8: the ONE equipment builder. inventory [{key:<canonical id>, section, qty}] -> the unified
// canonical id-ref equipment {weapons:[{id}], armor:{id}|null, gear:[{id,qty}]}. Routes by the
// item's stored section (unambiguous — id spaces could overlap across registries). Also returns
// armorSoak, resolved from the canonical armor record, for the build-time derived.soak SNAPSHOT
// (a PRE-EXISTING computation re-keyed to canonical — NOT the deferred live re-derivation).
// R-F45-1: MOVED to lib/shared/charResolve.js with `normalizeCreated`, which is its only real
// consumer. The window-global default lives here so existing callers keep their one-argument form.
function buildCanonicalEquipment(inventory, regs) {
  return window.CharResolve.buildCanonicalEquipment(inventory, regs || window._canonicalRegistries);
}

// THE SELL WRITER — one function, two faces (the legacy GEAR tab's `executeSell` and the paper pack,
// B-613): credits up by the amount, the item out — a weapon by index, the worn suit to null, gear by
// index or ONE unit of a stack. Returns the next `equipment` for the caller to write.
function loadoutSellUpdate(persistedEq, t, amount) {
  persistedEq = persistedEq || {};
  var update = { credits: (Number(persistedEq.credits) || 0) + Math.max(0, Number(amount) || 0) };
  if (t.kind === 'weapon') {
    var nextW = (persistedEq.weapons || []).slice(); nextW.splice(t.idx, 1);
    update.weapons = nextW;
  } else if (t.kind === 'armor') {
    update.armor = null;
  } else {
    var nextG = (persistedEq.gear || []).slice();
    var entry = nextG[t.idx];
    if (entry && typeof entry === 'object' && (Number(entry.qty) || 1) > 1) {
      nextG[t.idx] = Object.assign({}, entry, { qty: (Number(entry.qty) || 1) - 1 });   // sell ONE unit
    } else {
      nextG.splice(t.idx, 1);
    }
    update.gear = nextG;
  }
  return Object.assign({}, persistedEq, update);
}

// Sheet talents / Force powers: persisted canonical id-refs ({id, rank} / {id, basic, upgrades})
// -> display shapes at render via the SHARED resolver (ADR 0003 charbuild 3b, D-3B1/D-3B3).
// Graceful on legacy display-shape entries (old saves) — passed through, never dropped.
// B-241 — ONE readiness hook for the canonical data every sheet surface renders from.
//
// THE DEFECT IT CLOSES. `sheetTalentsForDisplay` / `sheetForcePowersForDisplay` read
// `window._canonicalCharbuild` BARE, and that view lands a beat AFTER the registries on a cold
// direct-load of /player/sheet/<id>. A first paint before it arrives resolves every owned id-ref to
// "(unknown talent)" — and nothing re-runs the component afterwards, because no prop changes. The
// tab stays wrong forever on a character who owns talents.
//
// It CALLS both loaders rather than waiting for someone else to have started them: both are
// idempotent (each returns its cached promise), so this is self-healing by construction and does
// not depend on which surface happened to mount first. Three components already had this shape for
// the REGISTRIES alone and would still paint early against a missing charbuild; they now share this.
function useCanonicalReady() {
  var have = function () {
    return !!(window._canonicalRegistries && (window._canonicalCharbuild || window._canonicalCharbuildBase));
  };
  var _r = React.useState(have), ready = _r[0], setReady = _r[1];
  React.useEffect(function () {
    if (ready) return undefined;
    var live = true;
    // B-339 / R-BSR-1(i): gate on SWRPGSources.ready() too — B-180's THIRD application. The
    // creator does this (:8957) and SheetAdvancement does it (:17319); the SHEET's gear face,
    // which reaches its data through this hook, did not. `sources.js` builds
    // `window._swrpgSourcesIndex` ONLY inside ready(), so without this the index is undefined on
    // a sheet route and every consumer over it silently degrades. In LoadoutItemDetail that
    // degradation was INVISIBLE while `sources` held display titles — the resolver's `|| src`
    // fallback printed the same words a working resolver would — and became a player-visible
    // key ('unlimited_power') the moment B-332 shipped weapons in key form. Found by B-336's
    // A6-2 positive control, not by the static consumer census, which classed the site RESOLVES.
    // ready() resolves ~instantly, is cached on the root, and its own catch installs an empty
    // index rather than rejecting, so this cannot make the sheet slower to become ready in any
    // way a user can perceive, and cannot make it fail to become ready at all.
    var sourcesReady = (window.SWRPGSources && window.SWRPGSources.ready)
      ? window.SWRPGSources.ready() : Promise.resolve();
    Promise.all([loadCanonicalRegistries(), loadCanonicalCharacterBuild(), sourcesReady]).then(function () {
      if (live) setReady(true);
    });
    return function () { live = false; };
  }, [ready]);
  return ready;
}

// ── F-100 M5 C4 · THE SHEET'S STAMP VIEW is lib/shared/talentView.js (ruling f100-m5-contracts ADDENDUM 3, A3-1…A3-4) ──
// ONE wrapper, both apps: the view cache, the epoch and its subscriptions, sheetTalentView and useTalentViewEpoch moved
// there whole. Each host REGISTERS it at boot (A3-2): this line is the player's, gm/app.jsx carries the GM's. The
// consumers below ask window.SWRPGTalentView — sheetTalentView for a sheet's view (NULL = C4.4's fallback), and
// useTalentViewEpoch for the epoch a memo over a character's talent numbers or dice keys on (C4.6; B-844).
window.SWRPGTalentView.register();
// R6 lane D (ruling release-r6-bases-registry Q7): the group record's engine reader — CharResolve reads a member's group
// grants (career skills, the Mentor's kind, the AoR base's credits) through it. Dark: nothing writes a group yet.
if (window.SWRPGParty && window.SWRPGParty.registerEngineReader) window.SWRPGParty.registerEngineReader(window.CharResolve);

function sheetTalentsForDisplay(sheet) {
  // F-9 B3 — resolve owned talent id-refs against the sheet's STAMP: a respec sheet owns respec_*
  // talent ids that live only in the overlay, so names/descriptions must come from the stamped view
  // (applyStampSync uses the base + any already-cached overlay; vanilla sheets get the base).
  var cb = (window.SWRPGMods && sheet && sheet.mods && sheet.mods.length)
    ? window.SWRPGMods.applyStampSync(sheet.mods, window._canonicalCharbuildBase)
    : (window._canonicalCharbuildBase || window._canonicalCharbuild);
  return window.CharResolve.resolveSheetTalents(sheet && sheet.talents, cb ? cb.talents : {}, cleanMarkup);
}
// B-23: the species' granted talents (flat + the persisted option pick), DERIVED at render and
// resolved to display entries — shown on the sheet under a plain SPECIES tag (mirrors SPECIES
// ABILITY). Empty when charbuild isn't loaded or the species grants none.
// ── B-58: the `source` vocabulary, DECLARED ──────────────────────────────────────────────────
// Every value any producer stamps onto `sheet.source`. Declared so a reader can see the whole space
// rather than infer it from an inline comparison, and so `test/sheet_source_vocabulary.cjs` can fail
// when a producer invents a new one without deciding what it means.
//   FullCreator / QuickBuild — the two creators (normalizeCreated, caller-stamped since R1)
//   Original                 — our own authored pregens
//   <Line> Beginner Game     — the published Beginner Game pregens (EotE / AoR / FaD). RETIRED from
//     the shipped roster 2026-07-30, but these values STAY in the vocabulary: a player who picked one
//     before the change still has that sheet in their roster, and dropping the value would make an
//     existing character fail a predicate that exists to describe the legal space. The vocabulary
//     describes every source a sheet may CARRY, not only the ones a producer still emits.
var SHEET_SOURCES = ['FullCreator', 'QuickBuild', 'Original',
                     'EotE Beginner Game', 'AoR Beginner Game', 'FaD Beginner Game'];

// B-58's predicate. PERMISSIVE by ruling: ownership, not provenance, decides who may write a
// backstory — so this takes the sheet rather than the source string, and returns true for every
// character on the roster. It exists as a NAMED function precisely so the next affordance that wants
// to gate on provenance has to say so out loud instead of reusing a string comparison.
function backstoryIsEditable(sheet) {
  return !!sheet;
}

// B-1194 (f100-m9-build-card step 5): the species reads go through the SHEET'S OWN STAMP VIEW (CharResolve.talentViewFor,
// talentView.js sheetTalentView) — as its talents do — so a homebrew species' abilities stay on a sheet stamped with its
// collection when that collection is toggled off on this device. No stamp view (node, or C4.4's fallback) → the discovery
// view, as before.
function speciesTalentsForDisplay(sheet) {
  var cb = (window.CharResolve && window.CharResolve.talentViewFor && window.CharResolve.talentViewFor(sheet)) || window._canonicalCharbuild || window._canonicalCharbuildBase;
  if (!cb || !window.CharResolve) return [];
  return window.CharResolve.speciesTalentGrants(sheet, cb).map(function(g) {
    return window.CharResolve.resolveTalentEntry({ id: g.id, rank: g.rank }, cb.talents || {}, cleanMarkup);
  });
}
// B-47 Lap A: species SPECIAL ABILITIES derived at render, mirroring speciesTalentsForDisplay.
// `sheet.speciesAbility` (a persisted display string) is now a LEGACY FALLBACK, read only when the
// registry cannot resolve the species — see the resolver's note for why 27 of 30 pregens were mute.
function speciesAbilitiesForDisplay(sheet) {
  var cb = (window.CharResolve && window.CharResolve.talentViewFor && window.CharResolve.talentViewFor(sheet)) || window._canonicalCharbuild || window._canonicalCharbuildBase;   // B-1194: the stamp view, as above
  var derived = (cb && window.CharResolve && window.CharResolve.speciesAbilityGrants) ? (window.CharResolve.speciesAbilityGrants(sheet, cb) || []) : [];
  if (derived.length) return derived;
  // B-1010 (ruling species-ability-at-render §5.3): the legacy persisted string, ONE entry, labelled as the deleted face did
  var legacy = sheet && sheet.speciesAbility;
  if (!legacy) return [];
  return [typeof legacy === 'object' ? Object.assign({ _ability: true }, legacy) : { _ability: true, name: 'Species Ability', description: String(legacy) }];
}
// B-855 THE HOLD — the sheet-side inputs to CharResolve.sheetHold (ruling f100-b854 R-B854-2 as amended by
// f100-editor-contracts-2 R-EC2-1; released by f100-editor-drc-census-gate ADDENDUM 1 R-EDG-A1-2). The missing
// `user:` stamps come from the user store where this build has one (instance-aware missingFor); a build WITHOUT
// one cannot resolve any `user:` stamp, so every one of them is missing. `data` = the view the writer projects
// through (SheetAdvancement's resolveForStamp); null while it loads → not held (nothing can project yet).
function sheetHoldOf(sheet, data) {
  if (!sheet || !data || !window.CharResolve || !window.CharResolve.sheetHold) return null;
  var mods = Array.isArray(sheet.mods) ? sheet.mods : [];
  var store = window.SWRPGUserData && typeof window.SWRPGUserData.missingFor === 'function' ? window.SWRPGUserData : null;
  var missing = store ? store.missingFor(mods) : mods.filter(function (s) { return typeof s === 'string' && s.indexOf('user:') === 0; });
  return window.CharResolve.sheetHold(sheet, data, missing, !!store);
}
// F-100 M5 · U15 (ruling f100-m5-talents-buy-after-deleted-tree R-M5B-A1-2, APPROVED R-M5B-AP-1): EDIT IN CREATOR is HELD
// while the sheet owns an own specialization deleted from a present pack — re-entry would rebuild the character without
// it (measured, B-963). THE ONE PREDICATE, the same inputs as sheetHoldOf, over CharResolve.sheetDeletedRefs.
// R-M6G-12 (b)'s line (frame D1, approved ADDENDUM 7): a carried pack that failed to load holds the paper door.
var PACK_FAILED_DOOR_LINE = 'reSpecialized data didn\u2019t load \u2014 this character needs it to open in the creator.';
var DELETED_SPEC_DOOR_LINE = 'This character\u2019s specialization was deleted from your homebrew, so it can\u2019t be edited in the creator. Its talents still work.';
function deletedSpecHeldOf(sheet, data) {
  if (!sheet || !data || !window.CharResolve || !window.CharResolve.sheetDeletedRefs) return false;
  var mods = Array.isArray(sheet.mods) ? sheet.mods : [];
  var store = window.SWRPGUserData && typeof window.SWRPGUserData.missingFor === 'function' ? window.SWRPGUserData : null;
  var missing = store ? store.missingFor(mods) : mods.filter(function (s) { return typeof s === 'string' && s.indexOf('user:') === 0; });
  return window.CharResolve.sheetDeletedRefs(sheet, data, missing, !!store).some(function (r) { return r.kind === 'specialization'; });
}
// B-1595 (D-412 item 1, ADDENDUM 306): the door's line when the held specialization was REMOVED with its collection, not
// deleted (THE ONE PREDICATE, CharResolve.removedRef) and has a name — page 2 §1's PROPOSED words; otherwise today's line.
function deletedSpecDoorLine(sheet, data) {
  var UD = window.SWRPGUserData, CR = window.CharResolve;
  if (!sheet || !data || !UD || typeof UD.missingFor !== 'function' || !CR || !CR.removedRef) return DELETED_SPEC_DOOR_LINE;
  var specs = CR.sheetDeletedRefs(sheet, data, UD.missingFor(sheet.mods || []), true).filter(function (r) { return r.kind === 'specialization'; });
  var names = specs.map(function (r) { var q = CR.removedRef(sheet, r, UD, window.SWRPGMods && window.SWRPGMods.parseUserStamp); return q && q.name; });
  if (!names.length || names.some(function (n) { return !n; })) return DELETED_SPEC_DOOR_LINE;
  return m5Names(names) + ' \u2014 specialization gone, so this character can\u2019t be edited in the creator. Talents bought in it stay.';
}
// The reason line — the approved frames' words EXACTLY (card f100-editor-drc-census §2; frames H1/H2; R-B854-4/5:
// the words are the frame's, never a lap's). {names}: the unresolved records' display names, up to three, then
// "and N more"; where no name is recoverable, the kind. A hold with no unresolved reference (a stamp this device
// cannot resolve, while every reference resolves to the book) names "house rules made on another device" (R-EC2-1).
// Variant B never says "homebrew" (R-B854-2).
function holdReasonText(hold) {
  if (!hold) return null;
  var kinds = { species: 'a species', career: 'a career', specialization: 'a specialization', talent: 'a talent', 'force power': 'a Force power' };
  var items = [];
  hold.refs.forEach(function (r) { var n = r.name || kinds[r.kind] || 'a record'; if (items.indexOf(n) === -1) items.push(n); });
  var names = items.length
    ? items.slice(0, 3).join(', ') + (items.length > 3 ? ' and ' + (items.length - 3) + ' more' : '')
    : 'house rules made on another device';
  return hold.variant === 'A'
    ? 'This character uses homebrew that isn\'t on this device: ' + names + '. Buying is off here until it is. Nothing has been changed.'
    : 'This version of the app can\'t read part of this character: ' + names + '. Buying is off until it can. Nothing has been changed.';
}
// ── F-100 M5 C4.3 + C5.2 — THE TELLING (ruling f100-m5-contracts §3 C4.3, C4.5, C5.2; frames U8 · U9 · C6 "Changed") ──
// On the owning device, an ELIGIBLE difference between a sheet's persisted talent projection and its stamp view is WRITTEN
// automatically (SWRPGProjectionDiff.reproject → the sheet's normal save; positions and XP untouched, F24) and TOLD once in
// the reason-line slot: trees in the approved count-level words, talent records in U8's. A record deleted from a present
// own pack is not a hold (C5.2): U9's words, once. Got it dismisses on this device. Nothing here decides a number — the
// render already shows the new one (C4.1); this only keeps the record and tells the player why it moved.
var M5_SEEN_KEY = 'swrpg_house_notices_seen';   // device-local: {<character name>|<kind>|<ids>: 1} — Got it's memory (C4.3)
function m5SeenRead() { try { var v = JSON.parse(window.localStorage.getItem(M5_SEEN_KEY) || '{}'); return (v && typeof v === 'object') ? v : {}; } catch (e) { return {}; } }
function m5SeenWrite(key) { try { var v = m5SeenRead(); v[key] = 1; window.localStorage.setItem(M5_SEEN_KEY, JSON.stringify(v)); } catch (e) { /* storage refused: the notice simply shows again */ } }
function m5Names(list) {   // the approved names3: "A", "A and B", "A, B and C", "A, B, C …and N more"
  return list.length > 3 ? list.slice(0, 3).join(', ') + ' …and ' + (list.length - 3) + ' more'
    : list.length > 1 ? list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1] : list.join('');
}
function m5NameOf(view, section, id) {
  var reg = view && view[section], rec = reg && Object.prototype.hasOwnProperty.call(reg, id) ? reg[id] : null;
  return (rec && rec.name) || id;
}
// The notice words, EXACTLY the frames' (C6 "Changed" count-level for trees; U8 for talent records; U9 for a deletion).
function m5NoticeText(n) {
  if (n.kind === 'trees') return 'Your house rules changed ' + m5Names(n.names) + ". Some of this character's talents are different now. The sheet has been updated.";
  if (n.kind === 'talents') return 'Your house rules changed ' + m5Names(n.names) + '. The sheet has been updated.';
  if (n.kind === 'restored') return 'Your house rules no longer change ' + m5Names(n.names) + '. The sheet has been updated.';
  if (n.kind === 'deleted-talent') return 'This character has a talent that was deleted from your homebrew. It does nothing now.';
  // B-1190 (f100-m9-build-card step 10): F5's three notices, verbatim from the approved frames (f100-m9-species @ 9edd6618)
  if (n.kind === 'deleted-species') return 'This character’s species was deleted from your homebrew. Its abilities do nothing now.';
  if (n.kind === 'deleted-career') return 'This character’s career was deleted from your homebrew. Its career skills cost as non-career skills now.';
  if (n.kind === 'deleted-power') return 'This character has a Force power that was deleted from your homebrew. It does nothing now.';
  // B-1595 (D-411 bind 2): a record whose collection was REMOVED from the character (H2) was not deleted — the talent
  // row's wording, as H2's preview named it (B-1203)
  if (n.kind === 'removed-species') return n.names[0] + ' — its abilities do nothing now';
  if (n.kind === 'removed-career') return n.names[0] + ' — its career skills cost as non-career skills now';
  // D-412 item 1 (ADDENDUM 306): page 2 §1's PROPOSED words, H2's specGone in the notice's idiom
  if (n.kind === 'removed-spec') return m5Names(n.names) + ' — specialization gone; talents bought in it stay';
  if (n.kind === 'removed-power' || n.kind === 'removed-talent') return m5Names(n.names) + (n.names.length === 1 ? ' — does nothing now' : ' — do nothing now');
  return 'This character’s specialization was deleted from your homebrew. Its talents still work.';
}
function useProjectionTelling(sheet, onSheetChange) {
  var epoch = window.SWRPGTalentView.useTalentViewEpoch();
  var st = React.useState([]), told = st[0], setTold = st[1];   // notices raised by a write in this mount
  var sv = React.useState(0), seenTick = sv[0], setSeenTick = sv[1];
  var lastWrite = React.useRef(null);   // a loop breaker: the same projection is never written twice
  React.useEffect(function () {
    var P = window.SWRPGProjectionDiff, UD = window.SWRPGUserData, view = window.SWRPGTalentView.sheetTalentView(sheet);
    if (!sheet || !P || !P.reproject || !view || !UD || typeof UD.liveOwnSupplier !== 'function' || !onSheetChange) return;
    var ti = P.tellingInputs(sheet, UD, window.SWRPGMods, window._canonicalCharbuildBase);
    var r = P.reproject(sheet, view, ti.supplier, ti.opts);
    if (!r) return;
    var sig = JSON.stringify([r.sheet.talents, r.sheet.derivedBonuses, r.sheet.derivedConditional]);
    if (lastWrite.current === sig) return;
    lastWrite.current = sig;
    var nb = r.notice, add = [];
    if (nb.specs.length) add.push({ kind: 'trees', names: nb.specs.map(function (id) { return m5NameOf(view, 'specializations', id); }) });
    if (nb.talents.length) add.push({ kind: 'talents', names: nb.talents.map(function (id) { return m5NameOf(view, 'talents', id); }) });
    if (nb.restored.length) add.push({ kind: 'restored', names: nb.restored.map(function (id) { return m5NameOf(view, 'talents', id); }) });
    setTold(function (prev) { return prev.concat(add); });
    onSheetChange({ talents: r.sheet.talents, derivedBonuses: r.sheet.derivedBonuses, derivedConditional: r.sheet.derivedConditional });
  }, [sheet, epoch]);
  // U9 — a record deleted from a present own pack (C5.2; not a hold). Told once per character and record set.
  var deleted = [];
  (function () {
    var UD = window.SWRPGUserData, view = window.SWRPGTalentView.sheetTalentView(sheet);
    if (!sheet || !view || !UD || typeof UD.missingFor !== 'function' || !window.CharResolve.sheetDeletedRefs) return;
    var refs = window.CharResolve.sheetDeletedRefs(sheet, view, UD.missingFor(sheet.mods || []), true);
    var seen = m5SeenRead();
    // B-1190: the species, career and Force power kinds ride the same once-per-record-set telling, in F5's order
    var NK = { 'talent': 'deleted-talent', 'specialization': 'deleted-spec', 'species': 'deleted-species', 'career': 'deleted-career', 'force power': 'deleted-power' };
    // B-1595: removed, not deleted — THE ONE PREDICATE (CharResolve.removedRef, read by the creator door and the tree box
    // too): a minted id whose collection the sheet no longer carries, with a name (the sheet's saved one, else the device's
    // copy). With no name (the collection is not on this device) the deleted words stay (D-412 item 2).
    var RK = { 'talent': 'removed-talent', 'species': 'removed-species', 'career': 'removed-career', 'force power': 'removed-power', 'specialization': 'removed-spec' };
    function savedName(r) { var q = window.CharResolve.removedRef(sheet, r, UD, window.SWRPGMods && window.SWRPGMods.parseUserStamp); return q && q.name; }
    function removed(r) { return !!RK[r.kind] && !!savedName(r); }
    ['species', 'career', 'force power', 'talent', 'specialization'].forEach(function (kind) {
      var of = refs.filter(function (r) { return r.kind === kind; });
      [of.filter(function (r) { return !removed(r); }), of.filter(removed)].forEach(function (set, j) {
        var ids = set.map(function (r) { return r.id; }).sort();
        if (!ids.length) return;
        var key = (sheet.name || '') + '|' + kind + '|' + ids.join(',');
        if (seen[key]) return;
        deleted.push(j ? { kind: RK[kind], seenKey: key, names: set.map(savedName) } : { kind: NK[kind], seenKey: key });
      });
    });
  })();
  var notices = told.map(function (n, i) { return Object.assign({ at: i }, n); }).concat(deleted);
  function dismiss(n) {
    if (n.seenKey) { m5SeenWrite(n.seenKey); setSeenTick(seenTick + 1); return; }
    setTold(function (prev) { return prev.filter(function (x, i) { return i !== n.at; }); });
  }
  return { notices: notices, dismiss: dismiss };
}
// PAPER: the reason line's notice treatment (.sb-notice) with Got it — frames M2a/U8/U9.
function HouseRuleNotices({ telling }) {
  if (!telling || !telling.notices.length) return null;
  return telling.notices.map(function (n, i) {
    return h('div', { key: n.kind + i, className: 'sb-notice', role: 'status', 'data-testid': 'house-notice', 'data-kind-m5': n.kind },
      h('span', { className: 'sb-notice-t' }, m5NoticeText(n)),
      h('span', { className: 'sb-notice-a' }, h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'house-notice-ok', onClick: function () { telling.dismiss(n); } }, 'Got it')));
  });
}

// ─── HOMEBREW H1 — a character takes on homebrew made after it (card `homebrew-h1-build-card`; contract
// `homebrew-h1-contract` R-H1K-1..15; the approved frames Docs/mockups/homebrew-h1.html @ 813f0578, D-110 J1 (A) J2 (A)
// J3 (A)). Every word and number comes from window.SWRPGPackAttach (lib/shared/packAttach.js) over F-100's exports; these
// components only draw it, in the reason-line slot of the growth room, below the hold line and HouseRuleNotices
// (R-H1F-1): F4's offer, F3's after-line, then F1's Homebrew line. The list and the preview are paper windows on the sheet
// shell (the dice hub's stock), drawn exactly as the frames drew them: a bottom sheet / full-screen sheet on the phone
// band, a popover under the line / a centred card ≤ 560 on the desk band. The write is ONE onSheetChange call; Undo is
// this mount's memory (R-H1K-4); J3's Not now is the device-local `swrpg_homebrew_offer_seen` map (R-H1B-4, R-H1BP-2).
function h1SeenRead() { try { var v = JSON.parse(window.localStorage.getItem(window.SWRPGPackAttach.OFFER_SEEN_KEY) || '{}'); return (v && typeof v === 'object') ? v : {}; } catch (e) { return {}; } }
function h1SeenWrite(keys) { try { var v = h1SeenRead(); keys.forEach(function (k) { v[k] = 1; }); window.localStorage.setItem(window.SWRPGPackAttach.OFFER_SEEN_KEY, JSON.stringify(v)); } catch (e) { /* storage refused: the offer simply shows again */ } }
var H1_MONO = { fontFamily: 'var(--font-mono,monospace)', letterSpacing: '.08em', color: 'var(--doc-ink)' };
var H1_BODY = { fontFamily: 'var(--font-body,sans-serif)', color: 'var(--doc-ink)' };
function h1Shell() { return document.querySelector('[data-sheet-shell]') || document.body; }
// The paper window both drawn panels share (frames DRAW_JS `panel` + `head`).
function H1Window({ testid, label, title, sub, closeLabel, onClose, style, scrim, children, foot, modal, role }) {
  var ref = React.useRef(null);
  React.useEffect(function () {
    var el = ref.current; if (!el) return undefined;
    var first = el.querySelector('button, a[href], input'); if (first) first.focus();
    function onKey(e) { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }
    el.addEventListener('keydown', onKey);
    return function () { el.removeEventListener('keydown', onKey); };
  }, []);
  var win = h('div', { ref: ref, role: role || 'dialog', 'aria-modal': modal ? 'true' : undefined, 'aria-label': label, 'data-testid': testid,
      style: Object.assign({ position: 'fixed', zIndex: 4001, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--doc-paper)',
        boxShadow: '0 18px 44px rgba(0,0,0,.45), 0 0 0 1px var(--doc-rule)' }, style) },
    h('div', { style: { flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px 12px', borderBottom: '1px solid var(--doc-rule)', background: 'var(--doc-paper-raised)' } },
      h('div', { style: { flex: 1, minWidth: 0 } },
        h('div', { className: 'sb-e-n', style: { fontSize: 'calc(15 * var(--u, 1px))', letterSpacing: '.06em', lineHeight: 1.35 } }, title),
        (Array.isArray(sub) ? sub : sub ? [sub] : []).map(function (l, i) {   // H2's replace: the tell-apart lines, one per row
          return h('div', { key: i, 'data-testid': testid + '-sub', style: Object.assign({ marginTop: 4, fontSize: 'calc(12 * var(--u, 1px))' }, H1_MONO) }, l); })),
      closeLabel ? h('button', { type: 'button', className: 'hp-quiet', 'aria-label': closeLabel, 'data-testid': testid + '-close', onClick: onClose,
        style: { minWidth: 44, minHeight: 44, padding: 0 } }, '✕') : null),
    h('div', { style: { flex: '1 1 auto', minHeight: 0, overflow: 'auto', padding: '4px 16px 14px' } }, children),
    foot || null);
  return ReactDOM.createPortal(h(React.Fragment, null,
    scrim ? h('div', { 'data-testid': testid + '-scrim', className: 'hp-scrim', onClick: onClose, style: { zIndex: 4000 } }) : null,
    win), h1Shell());
}
// F1's list (§5; A1.3): every collection on this device, refusals in place, same names told apart (R-H1K-14).
// H2 (R-H2K-5, R-H2B-5): a refused row that is the replace case gains Use this one instead; a replace the guard refuses
// says why in that place (R-H2K-6). A held sheet's list carries only the way-out rows (R-H2F-2).
function HomebrewList({ who, rows, wide, anchor, onPick, onReplace, onClose }) {
  var W = window.SWRPGPackAttach.W;
  var style = wide
    ? (function () { var r = anchor ? anchor.getBoundingClientRect() : { bottom: 120, right: window.innerWidth - 16 };
        return { top: r.bottom + 8, left: Math.max(16, r.right - 460), width: 460, maxHeight: '70vh' }; })()
    : { left: 0, right: 0, bottom: 0, maxHeight: '78vh' };
  React.useEffect(function () {   // the desk popover has no scrim: a press outside it closes it
    if (!wide) return undefined;
    function away(e) { var w = document.querySelector('[data-testid="homebrew-list"]'); if (w && !w.contains(e.target) && !(anchor && anchor.contains(e.target))) onClose(); }
    document.addEventListener('mousedown', away);
    return function () { document.removeEventListener('mousedown', away); };
  }, [wide]);
  var body = rows.length ? rows.map(function (r) {
    return h('div', { key: r.ck, className: 'sb-e', 'data-testid': 'homebrew-list-row', 'data-ck': r.ck, 'data-refused': r.refuse ? '1' : undefined, style: { padding: '12px 0' } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
        h('div', { style: { flex: 1, minWidth: 0 } },
          h('div', { className: 'sb-e-n' }, r.name),
          r.meta ? h('div', { style: Object.assign({ marginTop: 3, fontSize: 'calc(11 * var(--u, 1px))' }, H1_MONO) }, r.meta) : null,
          r.tell.map(function (l, i) { return h('div', { key: i, 'data-testid': 'homebrew-list-tell', style: Object.assign({ marginTop: 2, fontSize: 'calc(14 * var(--u, 1px))' }, H1_BODY) }, l); }),
          r.refuse ? h('div', { 'data-testid': 'homebrew-list-refuse', style: Object.assign({ marginTop: 4, fontSize: 'calc(15 * var(--u, 1px))' }, H1_BODY) }, r.refuse) : null,
          r.replaceRefuse ? h('div', { 'data-testid': 'homebrew-list-replace-refuse', style: Object.assign({ marginTop: 6, fontSize: 'calc(15 * var(--u, 1px))' }, H1_BODY) }, r.replaceRefuse) : null),
        r.refuse
          ? (r.replace ? h('button', { type: 'button', className: 'hp-quiet', 'aria-label': W.useInstead + ' — ' + r.name, 'data-testid': 'homebrew-list-replace', onClick: function () { onReplace(r); } }, W.useInstead) : null)
          : h('button', { type: 'button', className: 'hp-quiet', 'aria-label': 'Add ' + r.name, 'data-testid': 'homebrew-list-add', onClick: function () { onPick(r); } }, W.btnAdd)));
  }) : h('div', { className: 'sb-e' },
      h('p', { className: 'sb-e-t', style: { marginTop: 10 } }, W.noneHere),
      h('div', { className: 'sb-pk-verbs' }, h('a', { className: 'hp-quiet', href: '/player/homebrew', 'data-testid': 'homebrew-list-editor' }, W.noneHereLink)));
  return h(H1Window, { testid: 'homebrew-list', label: W.pickHead, title: W.pickHead, sub: who, closeLabel: wide ? null : W.close, onClose: onClose,
    style: style, scrim: !wide }, body);
}
// F2 — the preview, which IS the write (R-H1K-4): sections only when non-empty, copies ticked (J2 A), the held line
// (R-H1B-3), and Add/Update · Cancel pinned at the foot.
function HomebrewPreview({ words, ticks, setTick, wide, onCommit, onCancel }) {
  var foot = h('div', { style: { flex: 'none', display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 16px', borderTop: '1px solid var(--doc-rule)', background: 'var(--doc-paper-raised)' } },
    h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'homebrew-preview-cancel', onClick: onCancel }, words.cancel),
    h('button', { type: 'button', className: 'hp-commit', 'data-testid': 'homebrew-preview-commit', onClick: onCommit }, words.primary));
  var body = words.groups.map(function (g, gi) {
    return h(React.Fragment, { key: gi },
      g.h ? h('div', { className: 'sb-grp', style: { marginTop: 12 } }, g.h) : null,   // H2's way out: one group, no head (F7)
      g.rows.map(function (r, i) {
        return h('div', { key: i, className: 'sb-e', 'data-testid': 'homebrew-preview-row', 'data-held': r.held || undefined, style: { padding: '8px 0' } },
          r.copy === undefined
            ? h('div', { style: Object.assign({ fontSize: 'calc(16 * var(--u, 1px))', lineHeight: 1.45 }, H1_BODY) }, r.t)
            : h('label', { style: { display: 'flex', alignItems: 'flex-start', gap: 12, minHeight: 44, cursor: 'pointer' } },
                h('input', { type: 'checkbox', checked: !!ticks[r.copy], 'data-testid': 'homebrew-preview-copy', onChange: function (e) { setTick(r.copy, e.target.checked); },
                  style: { flex: 'none', width: 22, height: 22, margin: '2px 0 0', accentColor: 'var(--doc-action)' } }),
                h('span', null,
                  h('span', { style: Object.assign({ display: 'block', fontSize: 'calc(16 * var(--u, 1px))', lineHeight: 1.45 }, H1_BODY) }, r.t),
                  h('span', { style: Object.assign({ display: 'block', marginTop: 2, fontSize: 'calc(12 * var(--u, 1px))', letterSpacing: '.06em' }, H1_MONO) }, r.sub))));
      }));
  });
  // H2 (frames F6–F8): the empty detach's lead (R-H2F-4), then the groups, the cross-device line (J1 (A), signed in only),
  // and the last line where there is one
  return h(H1Window, { testid: 'homebrew-preview', label: words.title, title: words.title, sub: words.sub, onClose: onCancel, scrim: true, modal: true, foot: foot,
      style: wide ? { left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 'min(560px, calc(100vw - 32px))', maxHeight: '82vh' } : { inset: 0 } },
    words.lead ? h('p', { className: 'sb-e-t', 'data-testid': 'homebrew-preview-lead', style: { margin: '14px 0 0', maxWidth: 'none', fontWeight: 600 } }, words.lead) : null,
    body,
    words.cross ? h('p', { className: 'sb-e-t', 'data-testid': 'homebrew-preview-cross', style: { margin: '16px 0 0', maxWidth: 'none' } }, words.cross) : null,
    words.last ? h('p', { className: 'sb-e-t', 'data-testid': 'homebrew-preview-last', style: { margin: (words.cross ? 8 : 16) + 'px 0 0', maxWidth: 'none', fontWeight: 600 } }, words.last) : null);
}
// H2 · J2 (A) (R-H2K-9, frames F5-A-menu / F9-A): a collection's one-item menu — a popover under its name on the desk band
// (no scrim; a press outside closes it), a bottom sheet with ✕ on the phone band; the collection's name is its head. A
// refused act shows its reason in the item's place (R-H2K-6). The held way out's refusal uses the same window.
function HomebrewMenu({ head, wide, anchor, item, refuse, onPick, onClose }) {
  var style = wide
    ? (function () { var r = anchor ? anchor.getBoundingClientRect() : { bottom: 120, left: 16 }, wd = refuse ? 420 : 300;
        // aligned to its anchor's left (the frames' F5-A-menu / F9-A), kept inside the viewport (an anchor at the line's right
        // edge — the held way out's refusal, implemented not drawn — would otherwise run off it)
        return { top: r.bottom + 8, left: Math.max(16, Math.min(r.left, window.innerWidth - wd - 16)), width: wd, maxHeight: '60vh' }; })()
    : { left: 0, right: 0, bottom: 0, maxHeight: '60vh' };
  React.useEffect(function () {
    if (!wide) return undefined;
    function away(e) { var w = document.querySelector('[data-testid="homebrew-menu"]'); if (w && !w.contains(e.target) && !(anchor && anchor.contains(e.target))) onClose(); }
    document.addEventListener('mousedown', away);
    return function () { document.removeEventListener('mousedown', away); };
  }, [wide]);
  return h(H1Window, { testid: 'homebrew-menu', role: 'menu', label: head, title: head, closeLabel: wide ? null : window.SWRPGPackAttach.W.close, onClose: onClose,
      style: style, scrim: !wide },
    h('div', { style: { paddingTop: 4 } },
      refuse
        ? h('div', { 'data-testid': 'homebrew-menu-refuse', style: Object.assign({ fontSize: 'calc(16 * var(--u, 1px))', lineHeight: 1.45 }, H1_BODY) }, refuse)
        : h('button', { type: 'button', className: 'hp-quiet', role: 'menuitem', 'data-testid': 'homebrew-menu-remove', onClick: onPick,
            style: { width: '100%', justifyContent: 'flex-start', textAlign: 'left' } }, item)));
}
// The growth room's Homebrew lines (F1 / F3 / F4) and their windows. `hold` is the room's own (B-855).
// HOMEBREW H2 (card `homebrew-h2-build-card` R-H2B-5; frames Docs/mockups/homebrew-h2.html @ f12904390, D-118): on an
// unheld sheet each name is its collection's Remove door (J2 (A)); a variant-A hold gains Remove missing homebrew, and
// + Add homebrew only where a swap lifts the hold (R-H2F-2); the list's My Data refusal gains Use this one instead. Every
// act opens the one preview, writes ONE patch and offers Undo (R-H2K-8). Nothing on the opening face; no new hold text.
function HomebrewLines({ sheet, onSheetChange, hold }) {
  var PA = window.SWRPGPackAttach;
  var epoch = window.SWRPGTalentView.useTalentViewEpoch();   // a pack change re-reads the list, the line and the offer
  var wide = useLayoutMode() === 'desktop';
  var _o = React.useState(null), open = _o[0], setOpen = _o[1];            // 'list' | { menu } | { plan } (F2)
  var _t = React.useState([]), ticks = _t[0], setTicks = _t[1];
  var _a = React.useState(null), after = _a[0], setAfter = _a[1];          // { text, undo } (F3 / F10)
  var _s = React.useState(0), seenTick = _s[0], setSeenTick = _s[1];
  var addRef = React.useRef(null), missRef = React.useRef(null);
  if (!PA || !sheet || !window.SWRPGUserData || !window.SWRPGMods) return null;
  var env = PA.envFromWindow(), held = !!hold;
  var who = sheet.name || 'this character';
  var named = PA.lineStamps(sheet, env, held);
  var ups = held ? [] : PA.updates(sheet, env);
  var offers = PA.offerFor(sheet, env, h1SeenRead(), held);
  var wayOut = held && hold.variant === 'A';                                  // R-H2K-4: a missing `user:` stamp
  var ways = held ? PA.wayOutRows(sheet, env) : [];                           // R-H2F-2: the swaps that lift the hold
  function preview(stamp, mode) {
    var plan = PA.attachPlan(sheet, stamp, Object.assign({}, env, { held: held }), mode);
    if (plan.refused) { setOpen(null); return; }
    setTicks(plan.items.copies.map(function () { return true; }));   // J2 (A) of D-110: ticked
    setOpen({ plan: plan, mode: plan.mode });
  }
  function commit() {
    var plan = open.plan, patch = PA.patchFor(plan, sheet, ticks), undo = PA.undoFor(sheet, patch);
    onSheetChange(function (prev) { return Object.assign({}, prev, patch); });   // R-H1K-2 / R-H2K-1: ONE call
    var seen = PA.seenOnCommit(plan, sheet, env);                                 // R-H2R-1: a Remove puts the offer away
    if (seen.length) { h1SeenWrite(seen); setSeenTick(seenTick + 1); }
    setAfter({ text: PA.afterWords(plan), undo: undo });
    setOpen(null);
  }
  function openMenu(n, el) {                                                  // J2 (A): the name opens its one-item menu
    var plan = PA.attachPlan(sheet, n.stamp, env, 'detach');
    setOpen({ menu: { head: n.name, anchor: el, plan: plan, item: PA.W.menuRemove(who), refuse: plan.refused === 'would-hold' ? PA.refusalWords(plan, who) : null } });
  }
  function openWayOut() {                                                     // R-H2K-4: the held way out, refused in place by the guard
    var plan = PA.attachPlan(sheet, null, env, 'missing');
    if (plan.refused === 'would-hold') { setOpen({ menu: { head: PA.W.removeMissing, anchor: missRef.current, refuse: PA.refusalWords(plan, who) } }); return; }
    if (plan.refused) { setOpen(null); return; }
    setTicks([]); setOpen({ plan: plan, mode: plan.mode });
  }
  var listRows = function () {
    if (held) return ways.map(function (r) { return Object.assign({}, r, { replace: true }); });
    return PA.candidates(sheet, env).map(function (r) {
      if (r.refuse !== PA.W.otherMy) return r;                                // census reading R-2: the drawn My Data door
      var plan = PA.attachPlan(sheet, r.stamp, env, 'replace');
      return Object.assign({}, r, { replace: !plan.refused, replaceRefuse: plan.refused === 'would-hold' ? PA.refusalWords(plan, who) : null });
    });
  };
  var names = named.length
    ? (held ? named.map(function (n) { return n.name; }).join(' · ')
      : named.map(function (n, i) {
          return h(React.Fragment, { key: n.stamp }, i ? h('span', { 'aria-hidden': 'true' }, '·') : null,
            h('button', { type: 'button', className: 'hp-quiet', 'aria-haspopup': 'menu', 'aria-expanded': open && open.menu && open.menu.head === n.name ? 'true' : 'false',
              'data-testid': 'homebrew-name', 'data-stamp': n.stamp, style: { margin: 0 },
              onClick: function (e) { openMenu(n, e.currentTarget); } }, n.name + ' ▾'));
        }))
    : PA.W.none;
  var addBtn = h('button', { type: 'button', className: 'hp-quiet', ref: addRef, 'data-testid': 'homebrew-add', 'aria-expanded': open === 'list' ? 'true' : 'false',
    onClick: function () { setOpen(open === 'list' ? null : 'list'); } }, PA.W.add);
  var line = h('div', { className: 'sb-notice', role: 'status', 'data-testid': 'homebrew-line', style: { position: 'relative' } },
    h('span', { className: 'sb-notice-t' }, h('b', null, PA.W.line + ' '),
      h('span', { 'data-testid': 'homebrew-line-names', style: held || !named.length ? undefined : { display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px', verticalAlign: 'middle' } }, names)),
    held
      ? (wayOut || ways.length ? h('span', { className: 'sb-notice-a', 'data-testid': 'homebrew-held-actions',
          style: wide ? undefined : { flex: '1 1 auto', minWidth: 0 } },   // D-121 (J3 (B)): on phones + Add homebrew wraps under Remove missing homebrew
          wayOut ? h('button', { type: 'button', className: 'hp-quiet', ref: missRef, 'data-testid': 'homebrew-missing', onClick: openWayOut }, PA.W.removeMissing) : null,
          ways.length ? addBtn : null) : null)
      : h('span', { className: 'sb-notice-a' },
          ups.length ? h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'homebrew-update', onClick: function () { preview(ups[0].to, 'update'); } }, PA.W.update) : null,
          addBtn));
  return h(React.Fragment, null,
    offers.length ? h('div', { className: 'sb-notice', role: 'status', 'data-testid': 'homebrew-offer' },
      h('span', { className: 'sb-notice-t' }, PA.W.offer),
      h('span', { className: 'sb-notice-a' },
        h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'homebrew-offer-add', onClick: function () { setOpen('list'); } }, PA.W.offerAdd),
        h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'homebrew-offer-not',
          onClick: function () { h1SeenWrite(offers.map(function (c) { return PA.seenKey(sheet, c.ck); })); setSeenTick(seenTick + 1); } }, PA.W.offerNot))) : null,
    after ? h('div', { className: 'sb-notice', role: 'status', 'data-testid': 'homebrew-after' },
      h('span', { className: 'sb-notice-t' }, after.text),
      h('span', { className: 'sb-notice-a' }, h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'homebrew-undo',
        onClick: function () { var u = after.undo; onSheetChange(function (prev) { return PA.restore(prev, u); }); setAfter(null); } }, PA.W.undo))) : null,
    line,
    open === 'list' && (!held || ways.length) ? h(HomebrewList, { who: who, rows: listRows(), wide: wide, anchor: addRef.current,
      onPick: function (r) { preview(r.stamp, 'attach'); }, onReplace: function (r) { preview(r.stamp, 'replace'); }, onClose: function () { setOpen(null); } }) : null,
    open && open.menu ? h(HomebrewMenu, { head: open.menu.head, wide: wide, anchor: open.menu.anchor, item: open.menu.item, refuse: open.menu.refuse,
      onPick: function () { var p = open.menu.plan; if (p && !p.refused) { setTicks([]); setOpen({ plan: p, mode: p.mode }); } }, onClose: function () { setOpen(null); } }) : null,
    open && open.plan ? h(HomebrewPreview, { words: PA.previewWords(open.plan, who, ticks), ticks: ticks, wide: wide,
      setTick: function (j, on) { var t = ticks.slice(); t[j] = on; setTicks(t); }, onCommit: commit, onCancel: function () { setOpen(null); } }) : null);
}

function sheetForcePowersForDisplay(sheet) {
  // HOMEBREW H1 (ruling `f100-h1-word` R-FHW-1): the character's STAMP view first, so a collection it carries resolves
  // here even where it is switched off; the discovery view answers only where that is null (C4.4, as collectPoolMods).
  var cb = window.CharResolve.talentViewFor(sheet) || window._canonicalCharbuild;
  return window.CharResolve.resolveSheetForcePowers(sheet && sheet.forcePowers, cb ? cb.forcePowers : {}, cleanMarkup);
}

// R1 (CREATOR_PLUMBING §5) — THE ONE ASSEMBLY. Both creators end here; Guided's 132-line parallel
// literal is gone. `source` is stamped by the CALLER (item 6) — it was hardcoded, which mis-stamped
// every dev test-bed sheet by construction. The default preserves the pre-R1 value for the review
// PREVIEW memo and for any caller that does not care.
// R-F45-1: `normalizeCreated` MOVED to lib/shared/charResolve.js (slice 2's precondition — the
// round-trip property needs the assembly and its inverse callable from one place, in node). What
// remains here is the thin adapter that supplies the three things it used to reach out of scope for,
// so every call site below is unchanged. The BODY is verbatim in the shared core; do not re-implement
// it here.
// The stamps a character in creation WILL carry — the one expression of them (ruling `b1161-homebrew-copy-hotfix`
// R-B1161-7). F-100 UD-1f (R-FUD-5): variants first, then every ACTIVE user pack in layer order —
// `user:<id>` (own, live) / `user:<id>@<v12>` (imported, frozen). The array order IS the sheet's
// frozen resolution order.
// F-100 M6 R5 (ruling f100-m6-pack R-M6G-2): a RE-ENTERED draft stamps EXACTLY the packs its character carried
// (`gameData.stampMods`, set by FullCreator's boot from the draft's `reentry` marker) — never this device's
// toggles, which re-stamped the fork (R5a/R5b) and appended the device's own user pack (R5d). A character's packs
// are fixed at creation (R-M6K-14). Read by finalize (`normalizeCreated`) and by the creator's store door
// (`storeShopFor`), so the draft the store judges carries the stamps finalize will give it (B-1161).
function creationStampsFor(gameData) {
  return (gameData && Array.isArray(gameData.stampMods)) ? gameData.stampMods.slice()
    : ((window.SWRPGMods && window.SWRPGMods.active()) || [])
      .concat(window.SWRPGUserData ? window.SWRPGUserData.activeStamps() : []);
}

function normalizeCreated(character, speciesData, specData, gameData, source) {
  return window.CharResolve.normalizeCreated(character, speciesData, specData, gameData, source, {
    registries: window._canonicalRegistries,
    activeMods: creationStampsFor(gameData),
    portraitFor: speciesImgPath,
  });
}

/* ══════════════════════════════════════════════════════
   TEST-BED PC GENERATOR — builds RAW-valid characters
   from minimal specs via the same pipeline the creator uses.
   Exposed on window.SWRPGTestBed for dev use.
══════════════════════════════════════════════════════ */
function buildTestPc(spec, gameData) {
  // spec: { id, name, gameLine, speciesKey, careerName, tier,
  //         mechanic: { type, descriptor, value },
  //         loadoutKey?, extraXp?, extraTalents?, extraSkills?, extraGear? }
  if (!gameData) throw new Error('buildTestPc: gameData not loaded');

  // Resolve species + career from catalog
  var speciesList = gameData.charbuild ? Object.values(gameData.charbuild.species) : [];
  // C-V7SW-1 (ruling `csg-v7-shelf-write` §4.2) — RESOLVE ON `id`, NOT `_source_key`.
  // C-OAB-6: `_source_key` is TRACEABILITY, never identity — it is not unique (the v7 import ships
  // `339` as BOTH Ssi-Ruu and Svivreni), so a `.find()` over it returns whichever record comes
  // first. This was the last site in the app that resolved on it.
  // The specs above were converted from OggDude keys to ids IN THE SAME CHANGE, because dropping
  // this arm on its own would have broken every one of them: the keys are upper-case ('TWI') and
  // the ids are lower-case ('twi_lek'), so id-only resolution matched NONE of the six species and
  // every spec would have thrown. A one-line deletion is scoped by reading what the line holds up
  // (R-V7SW-2), and this one was holding up the whole test bed.
  var species = speciesList.find(function(s) { return s.id === spec.speciesKey; });
  if (!species) throw new Error('buildTestPc: species not found: ' + spec.speciesKey);

  var careerName = spec.careerName;
  var autoBuild = QB_AUTO_BUILD[careerName];
  if (!autoBuild) throw new Error('buildTestPc: no QB_AUTO_BUILD for career: ' + careerName);
  var freeCareerSkills = QB_DEFAULT_SKILLS[careerName] || [];

  // Pick default loadout (first option) unless spec overrides
  var loadoutOptions = QB_LOADOUT_OPTIONS[careerName] || [];
  var loadout = loadoutOptions[0] || { items: [] };
  if (spec.loadoutKey) {
    var found = loadoutOptions.find(function(l) { return l.id === spec.loadoutKey; });
    if (found) loadout = found;
  }

  // Build fresh character object
  var character = createEmptyCharacter(spec.gameLine);
  character.identity.name = spec.name || 'Test PC';
  // normalizeCreated reads character.species.name and character.career.name
  character.species = { key: species.id, name: species.name };
  character.career = { name: careerName, key: careerName };

  // Apply species base characteristics
  character.characteristics = {
    brawn: species.brawn || 2, agility: species.agility || 2,
    intellect: species.intellect || 2, cunning: species.cunning || 2,
    willpower: species.willpower || 2, presence: species.presence || 2,
  };

  // Apply characteristic boosts
  if (autoBuild.charBoosts) {
    autoBuild.charBoosts.forEach(function(b) {
      var attr = b[0], target = b[1];
      var base = species[attr] || 2;
      if (target > base && target <= 5) character.characteristics[attr] = target;
    });
  }

  // Apply free career + spec skills
  freeCareerSkills.forEach(function(sk) {
    if (!character.skills[sk]) character.skills[sk] = { freeCareer: false, freeSpec: false, purchased: 0 };
    character.skills[sk].freeCareer = true;
  });
  (autoBuild.specSkills || []).forEach(function(sk) {
    if (!character.skills[sk]) character.skills[sk] = { freeCareer: false, freeSpec: false, purchased: 0 };
    character.skills[sk].freeSpec = true;
  });

  // Starting talent
  if (autoBuild.talent) {
    var t = autoBuild.talent;
    var posKey = t.specKey + '_r' + t.row + 'c' + t.col;
    var cbT = gameData.charbuild;
    var tId = Object.keys(cbT.talents).find(function(id) { return cbT.talents[id]._source_key === t.key; }) || t.key;
    var sId = Object.keys(cbT.specializations).find(function(id) { return cbT.specializations[id]._source_key === t.specKey; }) || t.specKey;
    character.talents[posKey] = { talentKey: tId, specKey: sId, row: t.row, col: t.col, cost: (t.row + 1) * 5 };
  }

  // Mechanic (obligation / duty / morality)
  var mechType = spec.mechanic && spec.mechanic.type ? spec.mechanic.type :
                 mechanicForLine(spec.gameLine);                     // B-107: one home
  var mechDescriptor = spec.mechanic && spec.mechanic.descriptor ? spec.mechanic.descriptor : null;
  var mechBase = spec.mechanic && spec.mechanic.value != null ? spec.mechanic.value :
                 (mechType === 'morality' ? 50 : 10);
  character.mechanic = computeMechanic(mechType, mechBase, mechDescriptor, [], gameData.narrativeMechanics.config);

  // Credits + inventory
  var creditAdj = character.mechanic ? (character.mechanic.creditAdjustment || 0) : 0;
  // THE ONE PURSE READER (R2): the spendable purse; the after-creation credits are the save's (normalizeCreated adds them).
  var _purse = CharResolve.startingCredits(creditAdj, species, character && character.speciesOptionPicks, character && character.creationBuyouts).purse;
  character.gearLedger.startingCredits = _purse;
  character.gearLedger.credits = _purse;
  // Loadouts are keyed by OggDude Key -> bridge to canonical id + section (ADR 0003); skip an item
  // absent from the canonical catalog (graceful).
  loadout.items.forEach(function(li) {
    var ref = canonicalRefForSourceKey(li.key);
    if (!ref) return;
    var rec = window._canonicalRegistries[ref.section][ref.id];
    for (var q = 0; q < li.qty; q++) {
      character.gearLedger.inventory.push({ key: ref.id, name: rec.name, price: rec.price || 0, enc: rec.encumbrance || 0, section: ref.section, qty: 1 });
    }
  });

  // XP adjustment (bonus for mid/late tiers) + spend it on archetype-appropriate upgrades.
  // Mid = +100 XP, Late = +300 XP. Spent following RAW cost tables (post-creation).
  // Store bonus as mechanic.xpAdjustment so normalizeCreated's xpPool reflects it.
  if (spec.extraXp) {
    if (character.mechanic) {
      character.mechanic.xpAdjustment = (character.mechanic.xpAdjustment || 0) + spec.extraXp;
    }
    applyTierUpgrades(character, spec, species);
  }

  // Resolve spec data (for normalizeCreated)
  var specKey = (autoBuild.talent && autoBuild.talent.specKey) || null;
  var specData = null;
  if (specKey) {
    // (Fixes the legacy dead read: the old normalized blob had no top-level specializations
    // key, so this lookup always returned undefined. The canonical registry resolves it.)
    specData = Object.values(gameData.charbuild.specializations).find(function(s) { return s._source_key === specKey; }) || null;
  }

  var sheet = normalizeCreated(character, species, specData, gameData);

  // QA A3: Force-user careers grant FR 1 at creation; normalizeCreated only reads species.
  // Patch it here + seed the archetype's basic power as a canonical id-ref (ADR 0003 3b —
  // the sheet renders the printed registry text).
  var FORCE_CAREERS = {
    'Mystic':    { fr: 1, powers: [ { id: 'heal_harm', basic: true, upgrades: [] } ] },
    'Consular':  { fr: 1, powers: [ { id: 'heal_harm', basic: true, upgrades: [] } ] },
    'Guardian':  { fr: 1, powers: [ { id: 'enhance', basic: true, upgrades: [] } ] },
    'Seeker':    { fr: 1, powers: [ { id: 'foresee', basic: true, upgrades: [] } ] },
    'Sentinel':  { fr: 1, powers: [ { id: 'sense', basic: true, upgrades: [] } ] },
    'Warrior':   { fr: 1, powers: [ { id: 'enhance', basic: true, upgrades: [] } ] },
  };
  var fc = FORCE_CAREERS[spec.careerName];
  if (fc) {
    // CS-2: patch the persisted INPUT (forceRatingBase), not a computed snapshot.
    sheet.forceRatingBase = Math.max(sheet.forceRatingBase || 0, fc.fr);
    if (!Array.isArray(sheet.forcePowers) || sheet.forcePowers.length === 0) {
      sheet.forcePowers = fc.powers.slice();
    }
  }

  return { character: character, sheet: sheet };
}

// ── Tier upgrade tables — archetype-appropriate purchases per career ──
// Each entry: { primary: main stat, secondary: backup stat, skills: [career skills to rank up], talents: [extra talents] }
// Talents reference known row-0/row-1 talents from the career's starting spec (kept simple).
var TIER_UPGRADES = {
  'Smuggler':      { primary: 'cunning',   secondary: 'agility',  skills: ['CHARM','COOL','DECEP','PILSP'], extraSpec: 'SCOUND',   extraTalents: [{key:'QUICKSTRIKE',specKey:'CHARMER',row:1,col:1,cost:10}] },
  'Bounty Hunter': { primary: 'agility',   secondary: 'cunning',  skills: ['PERCEP','RANGHVY','COERC','STEAL'], extraSpec: 'GADG',  extraTalents: [{key:'DEADLYACC',specKey:'ASSAS',row:1,col:2,cost:10}] },
  'Hired Gun':     { primary: 'brawn',     secondary: 'agility',  skills: ['ATHL','BRAWL','RANGHVY','DISC'], extraSpec: 'MERC',     extraTalents: [{key:'DURABLE',specKey:'BODYGUARD',row:1,col:0,cost:10}] },
  'Technician':    { primary: 'intellect', secondary: 'cunning',  skills: ['COMP','MECH','SKULL','PERCEP'], extraSpec: 'SLICER',     extraTalents: [{key:'TECHAPT',specKey:'MECH',row:1,col:1,cost:10}] },
  'Ace':           { primary: 'agility',   secondary: 'cunning',  skills: ['PILSP','GUNN','ASTRO','COOL'], extraSpec: 'HOTSHOT',      extraTalents: [{key:'SKJOCK',specKey:'PILOT',row:1,col:2,cost:10}] },
  'Commander':     { primary: 'presence',  secondary: 'willpower',skills: ['LEAD','COOL','DISC','VIGIL'], extraSpec: 'TACTICIAN',    extraTalents: [{key:'COM',specKey:'COMMODORE',row:1,col:1,cost:10}] },
  'Soldier':       { primary: 'brawn',     secondary: 'agility',  skills: ['ATHL','BRAWL','RANGHVY','VIGIL'], extraSpec: 'SHARPSHOOTER', extraTalents: [{key:'GRIT',specKey:'COMMANDO',row:1,col:1,cost:10}] },
  'Spy':           { primary: 'cunning',   secondary: 'agility',  skills: ['COMP','DECEP','STEAL','PERCEP'], extraSpec: 'INFIL',     extraTalents: [{key:'INDIS',specKey:'COURIER',row:1,col:0,cost:10}] },
  'Guardian':      { primary: 'brawn',     secondary: 'willpower',skills: ['BRAWL','LTSAB','DISC','VIGIL'], extraSpec: 'PROTECT',    extraTalents: [{key:'PARRY',specKey:'ARMORER',row:1,col:1,cost:10}] },
  'Mystic':        { primary: 'willpower', secondary: 'presence', skills: ['CHARM','COOL','KNOWLORE','PERCEP'], extraSpec: 'HEALER', extraTalents: [{key:'GRIT',specKey:'ADVISOR',row:1,col:0,cost:10}] },
  'Seeker':        { primary: 'agility',   secondary: 'cunning',  skills: ['ATHL','PERCEP','PILPLAN','VIGIL'], extraSpec: 'PATHFIND',  extraTalents: [{key:'PARRY',specKey:'ATARU',row:1,col:0,cost:10}] },
  'Sentinel':      { primary: 'cunning',   secondary: 'willpower',skills: ['COMP','DECEP','PERCEP','STEAL'], extraSpec: 'ARTISAN',    extraTalents: [{key:'MENTTOOLS',specKey:'ARTISAN',row:1,col:1,cost:10}] },
};

function applyTierUpgrades(character, spec, species) {
  var plan = TIER_UPGRADES[spec.careerName];
  if (!plan) return;
  var budget = spec.extraXp;
  var log = [];

  function spend(amount, reason) {
    if (budget >= amount) { budget -= amount; log.push({ cost: amount, reason: reason }); return true; }
    return false;
  }

  // 1. Bump primary characteristic to rank 4 (10 × new rating = 40 XP).
  //    RAW: characteristics normally only raise during creation. Post-creation
  //    bumps go via Dedication talents in spec trees. We treat Dedication-equivalent
  //    bumps as "narrative XP spend" and apply them directly for the test bed.
  if (plan.primary) {
    var cur = character.characteristics[plan.primary] || 2;
    if (cur < 4 && spend(4 * 10, 'raise ' + plan.primary + ' to 4')) {
      character.characteristics[plan.primary] = 4;
    }
  }

  // 2. Late-tier: bump secondary characteristic to rank 4 too.
  if (spec.tier === 'late' && plan.secondary) {
    var cur2 = character.characteristics[plan.secondary] || 2;
    if (cur2 < 4 && spend(4 * 10, 'raise ' + plan.secondary + ' to 4')) {
      character.characteristics[plan.secondary] = 4;
    }
  }

  // 3. Buy additional skill ranks. Career skill = 5 × rank. Cap at 3 for both tiers
  //    (rank 4 costs 20 XP each — too expensive for a flat 300 XP budget after characteristic bumps).
  var skillCap = 3;
  (plan.skills || []).forEach(function(sk) {
    if (!character.skills[sk]) character.skills[sk] = { freeCareer: false, freeSpec: false, purchased: 0 };
    var s = character.skills[sk];
    var currentRank = (s.freeCareer ? 1 : 0) + (s.freeSpec ? 1 : 0) + (s.freeSpecies ? 1 : 0) + (s.purchased || 0) + (s.bought ? s.bought.length : 0);
    while (currentRank < skillCap) {
      var nextRank = currentRank + 1;
      // R-CR65-1: THE shared cost function (plan.skills are career skills by construction) —
      // the last inline pricing site, delegated so it can't drift (§8 one-pricing-path).
      var cost = CharResolve.skillRankCost(nextRank, true);
      if (cost == null || !spend(cost, 'skill ' + sk + ' → rank ' + nextRank)) break;
      s.purchased = (s.purchased || 0) + 1;
      currentRank = nextRank;
    }
  });

  // 4. Add extra talents (cost listed in plan; late gets 2, mid gets 1).
  //    Skip talent if the same talentKey is already present (avoids duplicate Grit etc).
  function talentAlreadyPresent(key) {
    return Object.keys(character.talents).some(function(pk) { return character.talents[pk].talentKey === key; });
  }
  var talentCount = spec.tier === 'late' ? 2 : 1;
  var appliedTalents = 0;
  (plan.extraTalents || []).forEach(function(t) {
    if (appliedTalents >= talentCount) return;
    if (talentAlreadyPresent(t.key)) return;
    if (spend(t.cost || 10, 'talent ' + t.key)) {
      var posKey = t.specKey + '_r' + t.row + 'c' + t.col;
      character.talents[posKey] = { talentKey: t.key, specKey: t.specKey, row: t.row, col: t.col, cost: t.cost || 10 };
      appliedTalents++;
    }
  });

  // 5. Late tier: buy an extra specialization (10 × 2 specs = 20 XP for 2nd, 30 for 3rd).
  //    (was tracked in the removed xpLedger) and doesn't mutate character.specializations
  //    (the test bed doesn't use specializations display).
  if (spec.tier === 'late' && plan.extraSpec) {
    spend(20, 'extra spec: ' + plan.extraSpec);
  }

  // Record everything on the ledger
  // (the xpLedger writes that stood here go with the field — F-60. Nothing ever read them: the
  //  test-bed's own report reads sheet.xp, and `sheet.xpLedger` had 0 readers app-wide.)
}

// R-F60-2 (ruling `f60-subtraction`): this test-bed HAS coverage now and keeps it —
// `scripts/smoke_f60_subtraction.py` LEG 3 drives generateAll() over all 12 specs. It had none before
// F-60, which is how three `xpLedger.push` writes lived here unnoticed. Any lap touching buildTestPc
// or applyTierUpgrades runs LEG 3.
// Dev-only: exposes buildTestPc + kicks off a single-PC generation for verification.
// Called from a button on the GM library screen (not directly from user-facing UI).
window.SWRPGTestBed = {
  buildTestPc: buildTestPc,
  // ── 12 PC specs, 4 per tier ──
  // NOTE: Warrior and Consular careers are not in QB_AUTO_BUILD, so L3/L4
  // substitute to Guardian and Mystic respectively (closest Force-user archetype
  // supported by the quickbuild pipeline).
  specs: [
    // BEGINNER TIER — starting XP only (species-based)
    { id: 'B1', name: 'Kex Vorn',     gameLine: 'eote', speciesKey: 'human',    careerName: 'Smuggler',      tier: 'beginner',
      mechanic: { type: 'obligation', descriptor: 'DEBT',            value: 10 } },
    { id: 'B2', name: "Sura'Vass",    gameLine: 'eote', speciesKey: 'twi_lek',  careerName: 'Bounty Hunter', tier: 'beginner',
      mechanic: { type: 'obligation', descriptor: 'BOU',             value: 10 } },
    { id: 'B3', name: 'Thane Decker', gameLine: 'aor',  speciesKey: 'human',    careerName: 'Soldier',       tier: 'beginner',
      mechanic: { type: 'duty',       descriptor: 'COMBATVICTORY',   value: 10 } },
    { id: 'B4', name: 'Vey Shaan',    gameLine: 'fad',  speciesKey: 'mirialan', careerName: 'Mystic',        tier: 'beginner',
      mechanic: { type: 'morality',   descriptor: 'COMPASSION',      value: 50 } },

    // MID TIER — ~100 extra XP earned in play
    { id: 'M1', name: 'Valka Teel',   gameLine: 'eote', speciesKey: 'rodian',   careerName: 'Bounty Hunter', tier: 'mid',     extraXp: 100,
      mechanic: { type: 'obligation', descriptor: 'OBS',             value: 10 } },
    { id: 'M2', name: 'Jev Oras',     gameLine: 'aor',  speciesKey: 'bothan',   careerName: 'Spy',           tier: 'mid',     extraXp: 100,
      mechanic: { type: 'duty',       descriptor: 'INTELLIGENCE',    value: 10 } },
    { id: 'M3', name: 'Gero Larran',  gameLine: 'fad',  speciesKey: 'zabrak',   careerName: 'Guardian',      tier: 'mid',     extraXp: 100,
      mechanic: { type: 'morality',   descriptor: 'BRAVERY',         value: 50 } },
    { id: 'M4', name: 'Renz Malar',   gameLine: 'eote', speciesKey: 'human',    careerName: 'Hired Gun',     tier: 'mid',     extraXp: 100, loadoutKey: 'hg_heavy',
      mechanic: { type: 'obligation', descriptor: 'FAV',             value: 10 } },

    // LATE TIER — ~300 extra XP (Knight-level equivalent)
    { id: 'L1', name: 'Captain Nyla Vass',   gameLine: 'aor',  speciesKey: 'human',    careerName: 'Ace',       tier: 'late', extraXp: 300,
      mechanic: { type: 'duty',       descriptor: 'SPACESUPERIORITY', value: 10 } },
    { id: 'L2', name: 'Kessel Rynn',         gameLine: 'eote', speciesKey: 'twi_lek',  careerName: 'Smuggler',  tier: 'late', extraXp: 300,
      mechanic: { type: 'obligation', descriptor: 'BOU',              value: 20 } },
    { id: 'L3', name: 'Master Thell Durin',  gameLine: 'fad',  speciesKey: 'human',    careerName: 'Guardian',  tier: 'late', extraXp: 300,
      mechanic: { type: 'morality',   descriptor: 'JUSTICE',          value: 50 } },
    { id: 'L4', name: 'Zyra Venn',           gameLine: 'fad',  speciesKey: 'mirialan', careerName: 'Mystic',    tier: 'late', extraXp: 300,
      mechanic: { type: 'morality',   descriptor: 'MERCY',            value: 50 } },
  ],
  generateOne: function(specId, gameData) {
    var s = this.specs.find(function(x) { return x.id === specId; });
    if (!s) throw new Error('Spec not found: ' + specId);
    return buildTestPc(s, gameData);
  },
  generateAll: function(gameData) {
    return this.specs.map(function(s) {
      try { return { spec: s, result: buildTestPc(s, gameData) }; }
      catch (e) { return { spec: s, error: String(e) }; }
    });
  },
};

/* ── FC Review Step ──────────────────────────────────── */

/* ── PocketMoneyBeat (F-35) — the book's closing d100 spending-money roll, offered once at the
 *    finalize/REVIEW of every path that grants the 500 budget (custom + guided). ONE definition,
 *    mounted per path (no-compromise-mirror). RAW Step 9: eote p.98 / aor p.108 / fad p.110
 *    (the d100 RULE's page, oracle-verbatim per R-F35-1 — the section header is p.106, the rule a
 *    page in; cites point at the rule, not the chapter door). No narrative-dice
 *    glyphs — a plain percentile reveal (d100 is not a narrative die; the reel would be costume).
 *    Skippable, never gating (the CONFIRM button proceeds without it). One roll per character:
 *    character.pocketMoney = { value }, carried by the draft; a re-entered draft SHOWS the result
 *    and never re-offers the roll — the edit pencil is the only post-roll change (a table that
 *    rolled a physical d100 types theirs; record-with-assist, the table's dice win). ── */
var POCKET_MONEY_CITE = { eote: 'eote p.98', aor: 'aor p.108', fad: 'fad p.110' };
function PocketMoneyBeat({ value, gameLine, accent, onSet }) {
  var _ed = useState(false), editing = _ed[0], setEditing = _ed[1];
  var _df = useState(''), draft = _df[0], setDraft = _df[1];
  var rolled = value != null;
  var cite = POCKET_MONEY_CITE[gameLine] || POCKET_MONEY_CITE.eote;
  // clamp to the die's structure (1-100) — that's the shape of a d100, not a resource gate.
  function clamp(n) { n = Math.round(Number(n) || 0); return Math.max(1, Math.min(100, n)); }
  function roll() { onSet({ value: Math.floor(Math.random() * 100) + 1 }); }
  function commitEdit() { setEditing(false); if (draft !== '' && !isNaN(Number(draft))) onSet({ value: clamp(draft) }); }
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.72)', lineHeight: 1.5, marginBottom: '10px' }}>
        Pocket money &mdash; the book gives every new character one last d100 roll of spending money.
      </div>
      {!rolled ? (
        <button type="button" onClick={roll} style={{
          background: accent + '18', border: '1px solid ' + accent + '40', borderRadius: '6px', cursor: 'pointer',
          padding: '11px 18px', minHeight: '44px', touchAction: 'manipulation',
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.14em', color: accent,
        }}>ROLL d100</button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {editing ? (
            <input type="number" min="1" max="100" autoFocus value={draft}
              aria-label="Pocket money credits"
              onChange={function(e) { setDraft(e.target.value); }}
              onBlur={commitEdit}
              onKeyDown={function(e) { if (e.key === 'Enter') commitEdit(); }}
              style={{ width: '84px', boxSizing: 'border-box', background: 'rgba(0,0,0,0.3)', border: '1px solid ' + accent + '55',
                borderRadius: '6px', color: accent, padding: '9px 10px', minHeight: '44px',
                fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body)' }} />
          ) : (
            <span key={value} style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xl)', color: accent, animation: 'fadeIn 0.4s ease' }}>
              {'+ ' + value + ' credits'}
            </span>
          )}
          <button type="button" aria-label="Edit pocket money — enter a physical d100 roll" title="Enter a physical roll"
            onClick={function() { setDraft(String(value)); setEditing(true); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
              padding: '10px', minHeight: '44px', minWidth: '44px', touchAction: 'manipulation',
              fontFamily: 'var(--font-body)', fontSize: 'inherit' }}>{'✎'}</button>
        </div>
      )}
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.06em', marginTop: '8px' }}>{cite}</div>
    </div>
  );
}

// B-97 — THE REVIEW'S MISSING STEP (lap C13; drawn in `Docs/mockups/creator-review.html`,
// ratified by ruling `creator-review-mockup`, scoped by `creator-program-completion-plan` LAP 3).
//
// `FCReviewStep` contained ZERO occurrences of obligation, duty or morality, and its edit-jump map
// listed six targets that did not include the mechanic. It was the only creator step the review
// neither showed nor let a player return to — and the omission had a price attached: a character
// can trade +10 Obligation for 2,500 credits, and the review rendered CREDITS LEFT with nothing
// saying what they cost. **The last screen before BEGIN YOUR STORY showed the gain and hid the debt.**
//
// ONE GENERIC BLOCK WOULD BE WRONG, and the ruling refused it before this was built: Obligation
// RISES to buy XP and credits, Duty FALLS to buy exactly the same, and Morality is a single pick of
// five, two of which move Morality itself and buy nothing. A section written for Obligation states
// Duty backwards.
//
// SO NOTHING HERE IS WRITTEN PER GAME LINE. Every number comes from the mechanic the character
// actually carries (`computeMechanic`'s own output: baseValue, finalValue, xpAdjustment,
// creditAdjustment) and the SIGN of the delta carries the direction — Obligation's choices are
// +5/+10 and Duty's are -5/-10 in the registry, so "rises" and "falls" are read from the data
// rather than branched on the line. A fourth mechanic would render correctly without touching this.
function FCReviewMechanicSection({ character, gameData, accent, EditButton, step }) {
  var mech = character && character.mechanic;
  if (!mech || !mech.type) return null;
  var nm = (gameData && gameData.narrativeMechanics) || {};
  var label = ({ obligation: 'OBLIGATION', duty: 'DUTY', morality: 'MORALITY' })[mech.type] || String(mech.type).toUpperCase();

  // The descriptor's display NAME, resolved the same way the step itself resolves it — and for
  // morality by `weakKey`, which is the C9 pairing (B-117: list position mispaired 39 of 39, and
  // the correct mapping was in our own data the whole time). The review is the second surface to
  // print this pair, so it reads the mapping rather than a remembered order.
  var typeName = mech.descriptor || '';
  var pairedWeakness = '';
  if (mech.type === 'obligation' || mech.type === 'duty') {
    // B-1605: the one resolver, over the registries the creator already holds (no second lookup).
    typeName = SWRPGMechTypeNames.name(SWRPGMechTypeNames.fromRegistries(nm.oblTypes, nm.dutyTypes), mech.type, typeName);
  } else if (mech.type === 'morality' && nm.morPairs) {
    var s = (nm.morPairs.strengths || []).find(function(t) { return t.key === mech.descriptor; });
    if (s) {
      typeName = s.name;
      var w = (nm.morPairs.weaknesses || []).find(function(x) { return x.key === s.weakKey; });
      if (w) pairedWeakness = w.name;
    }
  }

  var base = mech.baseValue == null ? null : mech.baseValue;
  var finalV = mech.finalValue == null ? base : mech.finalValue;
  var delta = (base == null || finalV == null) ? 0 : finalV - base;
  var xp = mech.xpAdjustment || 0;
  var cr = mech.creditAdjustment || 0;
  // What the trade BOUGHT, in the mechanics voice and only the parts that happened.
  var bought = [];
  if (xp) bought.push(xp + ' XP');
  if (cr) bought.push(cr.toLocaleString() + ' credits');

  return (
    <React.Fragment>
      <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
        <span>{label}</span>
        <EditButton step={step} label={label.charAt(0) + label.slice(1).toLowerCase()} />
      </div>
      <div data-review-mechanic={mech.type} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' }}>
        <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: '#fff' }}>
          {typeName || '—'}
          {pairedWeakness && (
            <span style={{ fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>{' ↔ ' + pairedWeakness}</span>
          )}
        </div>
        {/* THE LINE THAT PAYS FOR THE SECTION. `base N` and `= final` are always shown, because the
            value itself is what the GM rolls against; the middle clause appears only when a trade
            happened, and states BOTH halves of it — what moved and what it bought. */}
        <div data-review-mechanic-line={mech.type} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.6)', marginTop: '5px' }}>
          base {base}
          {delta !== 0 && (
            <span>{'  '}{delta > 0 ? '+' : '−'}{Math.abs(delta)}
              {bought.length ? ' traded for ' + bought.join(' and ') : ' — moves ' + (typeName ? label.toLowerCase() : 'the value') + ' itself, buys nothing'}
            </span>
          )}
          {delta === 0 && bought.length > 0 && (
            <span>{'  — took ' + bought.join(' and ') + ', ' + label.toLowerCase() + ' unchanged'}</span>
          )}
          {'  =  '}<b style={{ color: 'rgba(255,255,255,0.85)' }}>{finalV}</b>
        </div>
      </div>
    </React.Fragment>
  );
}

function FCReviewStep({ character, speciesData, specData, gameData, accent, xpRemaining, credits, onNameChange, onGoToStep, onConfirm, onSetPocketMoney }) {
  // D2 CORRECTION (Jason) — NO ABBREVIATIONS WHERE THERE IS ROOM TO SPELL IT OUT.
  // "BR / AG / INT / CUN / WIL / PR" and "M.DEF / R.DEF" are compressions that exist because a
  // phone column is 390px wide. On a desktop review they are a puzzle for no reason: the reader has
  // the width, so they get the word. The short forms REMAIN on mobile, where they earn their keep —
  // this is a band-conditional LABEL, not a deletion, and the long names come from `FC_CHAR_FULL`,
  // which already existed, rather than a second list that could drift from it.
  var _fullLabels = useLayoutMode() === 'desktop';
  // RV-2 fix: quick-jump EDIT buttons per section instead of BACK-tap × 6.
  // FC_STEPS index map for the sections shown on review.
  // Derive review-EDIT jump targets from FC_STEPS so a step reorder can never desync them
  // (CHAR-FIRST: the old hardcoded positions would have pointed EDIT at the wrong steps).
  var editJumps = {
    // B-97: the mechanic step joins the map. Derived from FC_STEPS like every other target, so a
    // reorder cannot desync it — the same protection the CHAR-FIRST reorder proved these need.
    mechanic: FC_STEPS.indexOf('mechanic'),
    characteristics: FC_STEPS.indexOf('characteristics'),
    skills: FC_STEPS.indexOf('skills'),
    talents: FC_STEPS.indexOf('talents'),
    forcepowers: FC_STEPS.indexOf('forcepowers'),
    motivation: FC_STEPS.indexOf('motivation'),
    equipment: FC_STEPS.indexOf('gear'),
  };
  function EditButton({ step, label }) {
    if (!onGoToStep) return null;
    return (
      <button type="button" onClick={function() { onGoToStep(step, { fromReview: true }); }}
        aria-label={'Edit ' + label}
        style={{
          marginLeft: 'auto', background: 'none', border: 'none',
          color: accent, cursor: 'pointer',
          // 44pt hit region per iOS HIG (vet-r2 P3: 57x24 was below floor).
          padding: '12px 12px', minHeight: '44px',
          fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
          letterSpacing: '0.14em', display: 'inline-flex', alignItems: 'center', gap: '3px',
          touchAction: 'manipulation',
        }}>{'\u270E'} EDIT</button>
    );
  }
  var displayModel = useMemo(function() {
    return normalizeCreated(character, speciesData, specData, gameData);
  }, [character, speciesData, specData, gameData]);

  // Resolve the persisted id-ref equipment -> display shapes for the summary (ADR 0003 3b).
  // Registries are loaded by the creator (gameData.canonical) before this step renders.
  var displayEquipment = useMemo(function() {
    return resolveSheetEquipment(displayModel.equipment, displayModel);
  }, [displayModel]);

  var chars = displayModel.characteristics;
  // CS-2 (R-DAR): the review preview derives from the same shared core as the sheet (the third copy of
  // the arithmetic dissolves).
  var derived = CharResolve.computeDerived(displayModel, displayEquipment, (gameData && gameData.charbuild) || window._canonicalCharbuild);   // B-23: species talents derive at render · F-100 M6 R5: the draft's own view (a re-entered draft's stamp view; for a new character, the same toggle view)
  // B-746 — THE TWO TRACKS' CURRENT VALUES ON A CHARACTER BEING CREATED ARE 0, AS A LITERAL, AND
  // THAT IS THE DELIBERATE CHOICE. `displayModel` is `normalizeCreated`'s output, which builds its
  // sheet from an EXPLICIT key list (`charResolve.js:1586`) and never emits `currentWounds` or
  // `currentStrain` — nothing writes them before the record exists, because there is no damage
  // during creation. A `!= null ? … : 0` read here would therefore be a fallback that can never
  // fire, which is exactly the dead read B-737 deleted one hour ago (`sd.live.wounds`, and the
  // `Number(sd.woundThreshold)` fallback beside it). The sheet KEEPS that idiom, because there the
  // key really can be present; here the 0 is a stated fact, not a lookup dressed as one.
  var reviewCurWounds = 0;
  var reviewCurStrain = 0;
  // RV-1 fix: name required. Disable BEGIN YOUR STORY until a non-empty name
  // is entered. Surface the required state on the input with a red accent
  // border + explicit "Required" caption.
  var nameTrimmed = (character.identity.name || '').trim();
  var nameOk = nameTrimmed.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease-out', padding: '0 16px 16px' }}>
      {/* Name input */}
      <div style={{ marginBottom: '16px' }}>
        <div style={fcSectionHeaderStyle(accent)}>
          CHARACTER NAME {!nameOk && <span style={{ color: '#FF8A8A', marginLeft: '8px', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em' }}>· REQUIRED</span>}
        </div>
        <input type="text" value={character.identity.name} onChange={function(e) { onNameChange(e.target.value); }}
          placeholder="Enter your character's name…"
          aria-required="true"
          aria-invalid={!nameOk}
          style={{
            width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)',
            border: '1px solid ' + (nameOk ? accent + '55' : 'rgba(255,138,138,0.55)'),
            borderRadius: '6px',
            padding: '12px 14px', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-lg)', fontWeight: 700,
            color: '#fff', letterSpacing: '0.02em', outline: 'none',
            boxShadow: nameOk ? 'none' : '0 0 0 1px rgba(255,138,138,0.25) inset',
          }} />
        {!nameOk && (
          <div style={{
            marginTop: '6px',
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 600,
            color: '#FF8A8A', letterSpacing: '0.04em',
          }}>Enter a name to finish your character.</div>
        )}
      </div>

      {/* Identity card — unified Rajdhani hierarchy. Mono font was fighting the
         prose voice; character identity is prose, not data. */}
      <div style={{
        background: 'linear-gradient(135deg, ' + accent + '0A, rgba(255,255,255,0.02))',
        border: '1px solid ' + accent + '22',
        borderLeft: '3px solid ' + accent + '99',
        borderRadius: '8px', padding: '14px', marginBottom: '12px',
        display: 'flex', gap: '14px', alignItems: 'center',
      }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0,
          backgroundImage: displayModel.portrait ? 'url(' + displayModel.portrait + ')' : 'none',
          backgroundSize: '180%', backgroundPosition: 'center top',
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '2px solid ' + accent + '55',
          boxShadow: '0 0 12px ' + accent + '22',
          display: displayModel.portrait ? undefined : 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {/* Nunes retirement: no portrait -> the Monogram seam (the sheet's own placeholder). */}
          {!displayModel.portrait && window.SWRPGUI.Monogram({ name: displayModel.species || displayModel.name || '?', accent: accent, size: 40 })}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-lg)',
            color: 'rgba(255,255,255,0.85)', letterSpacing: '0.02em',
            lineHeight: 1.2,
          }}>{displayModel.species} {displayModel.career}</div>
          <div style={{
            fontFamily: 'var(--font-body)', fontWeight: 500, fontStyle: 'italic',
            fontSize: 'var(--fs-sm)', color: accent,
            letterSpacing: '0.02em', lineHeight: 1.3, marginTop: '3px',
          }}>{displayModel.specialization}</div>
        </div>
      </div>

      {/* B-97 — THE MECHANIC, FIRST, BECAUSE THAT IS ITS STEP ORDER. The mechanic step (5) runs
          before characteristics (6), and the ruling put this section in step order rather than
          appended at the end: a review that reads in a different order from the journey it
          summarises makes the reader re-find their place. */}
      <FCReviewMechanicSection character={character} gameData={gameData} accent={accent}
                               EditButton={EditButton} step={editJumps.mechanic} />

      {/* Characteristics */}
      <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
        <span>CHARACTERISTICS</span>
        <EditButton step={editJumps.characteristics} label="Characteristics" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px', marginBottom: '12px' }}>
        {['brawn','agility','intellect','cunning','willpower','presence'].map(function(attr) {
          return (
            <div key={attr} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: '4px', padding: '8px 2px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: FC_CHAR_COLORS[attr] }}>{chars[attr]}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em' }}>{_fullLabels ? FC_CHAR_FULL[attr].toUpperCase() : FC_CHAR_LABELS[attr]}</div>
            </div>
          );
        })}
      </div>

      {/* Derived stats */}
      {/* B-746 — A DISPLAYED THRESHOLD IS `n/threshold` (Jason 2026-09-20, verbatim: "Whenever a
          threshold is displayed, it should show n/threshold"). WOUND and STRAIN printed the ceiling
          alone, which says nothing about where the character stands, and it is the same number the
          sheet then prints as `0 / 13` — two surfaces disagreeing about the blank case, which is
          B-742's shape one door over. SOAK and the two defences are NOT thresholds: they are
          modifiers on a roll with no track counting up to them, so they keep their bare number and
          only these two rows gain the pair.
          `n` IS A LITERAL 0 here and the reasoning is at its declaration above: a character being
          created has taken no damage, and `normalizeCreated` never emits the two current keys, so a
          `!= null` read would be a fallback that can never fire — B-737's dead-read shape. The
          THRESHOLD half is live, from the shared core, which is what the guard discriminates on. */}
      <div data-tour-id="p-custom-review-derived" style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {[
          { label: 'SOAK', val: derived.soak },
          { label: 'WOUND', val: reviewCurWounds + '/' + (derived.woundThreshold || 0) },
          { label: 'STRAIN', val: reviewCurStrain + '/' + (derived.strainThreshold || 0) },
          { label: _fullLabels ? 'MELEE DEFENSE' : 'M.DEF', val: derived.meleeDefense },
          { label: _fullLabels ? 'RANGED DEFENSE' : 'R.DEF', val: derived.rangedDefense },
        ].map(function(d) {
          return (
            <div key={d.label} style={{ flex: 1, textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: '4px', padding: '8px 2px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: '#fff' }}>{d.val}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em' }}>{d.label}</div>
            </div>
          );
        })}
      </div>

      {/* Skills */}
      {Object.keys(displayModel.skills).length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
            <span>SKILLS</span>
            <EditButton step={editJumps.skills} label="Skills" />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {Object.keys(displayModel.skills).sort().map(function(k) {
              var name = SKILL_NAMES[k] || k;
              return <span key={k} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.65)', padding: '5px 9px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', letterSpacing: '0.02em' }}>{name} {displayModel.skills[k]}</span>;
            })}
          </div>
        </div>
      )}

      {/* Talents */}
      {displayModel.talents.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
            <span>TALENTS</span>
            <EditButton step={editJumps.talents} label="Talents" />
          </div>
          {sheetTalentsForDisplay(displayModel).map(function(t, i) {
            return (
              <div key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.65)', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', lineHeight: 1.5 }}>
                <strong style={{ color: '#fff' }}>{t.name}{t.rank > 1 ? ' ' + t.rank : ''}</strong>
                <span> — </span>
                <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{glyphs(cleanMarkup(t.descriptionRaw || t.description))}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Force Powers — F&D only, added at vet-r1 review step gap 2026-04-24. */}
      {Array.isArray(displayModel.forcePowers) && displayModel.forcePowers.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
            <span>FORCE POWERS</span>
            <EditButton step={editJumps.forcepowers} label="Force Powers" />
          </div>
          {sheetForcePowersForDisplay(displayModel).map(function(fp, i) {
            var name = fp.name || 'Unnamed Power';
            var desc = fp.description || fp.desc || '';
            return (
              <div key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.65)', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', lineHeight: 1.5 }}>
                <strong style={{ color: '#fff' }}>{name}</strong>
                {desc && <><span> — </span><span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{glyphs(cleanMarkup(desc)) || desc}</span></>}
              </div>
            );
          })}
        </div>
      )}

      {/* Motivations — before-commit confirmation the player's drives actually
         persisted (vet-r1 review step gap 2026-04-24). */}
      {displayModel.motivations && (function() {
        var m = displayModel.motivations;
        var entries = [
          { key: 'strength',  label: 'STRENGTH',  v: m.strength  },
          { key: 'flaw',      label: 'FLAW',      v: m.flaw      },
          { key: 'desire',    label: 'DESIRE',    v: m.desire    },
          { key: 'fear',      label: 'FEAR',      v: m.fear      },
        ].filter(function(e) { return e.v; });
        if (entries.length === 0) return null;
        return (
          <div style={{ marginBottom: '12px' }}>
            <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
              <span>MOTIVATIONS</span>
              <EditButton step={editJumps.motivation} label="Motivations" />
            </div>
            {entries.map(function(e) {
              var text = typeof e.v === 'string' ? e.v : (e.v && (e.v.label || e.v.name || e.v.value) || '');
              return (
                <div key={e.key} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.65)', padding: '5px 0', lineHeight: 1.5, display: 'flex', gap: '8px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.14em', color: accent, minWidth: '72px', flexShrink: 0, paddingTop: '2px' }}>{e.label}</span>
                  <span style={{ flex: 1, color: '#fff' }}>{text}</span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Equipment */}
      {(displayEquipment.weapons.length > 0 || displayEquipment.armor || displayEquipment.gear.length > 0) && (
        <div style={{ marginBottom: '12px' }}>
          <div style={Object.assign({}, fcSectionHeaderStyle(accent), { display: 'flex', alignItems: 'center' })}>
            <span>EQUIPMENT</span>
            <EditButton step={editJumps.equipment} label="Equipment" />
          </div>
          {displayEquipment.weapons.map(function(w, i) {
            var defNote = '';
            if (w.state !== 'stowed') {
              var quals = w._qualitiesRaw || [];
              for (var qi = 0; qi < quals.length; qi++) {
                if (quals[qi].id === 'defensive') defNote += ' · Defensive ' + (Number(quals[qi].rank) || 1);
                if (quals[qi].id === 'deflection') defNote += ' · Deflection ' + (Number(quals[qi].rank) || 1);
              }
            }
            return <div key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.65)', padding: '3px 0', letterSpacing: '0.02em' }}>{w.name} — {w.damage} dmg / {w.range}{defNote && <span style={{ color: '#4ECBFF' }}>{defNote}</span>}</div>;
          })}
          {displayEquipment.armor && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.65)', padding: '3px 0', letterSpacing: '0.02em' }}>{displayEquipment.armor.name} — +{displayEquipment.armor.soak} soak</div>}
          {displayEquipment.gear.map(function(g, i) {
            return <div key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.5)', padding: '2px 0', letterSpacing: '0.02em' }}>{g.name + (g.qty > 1 ? ' ×' + g.qty : '')}</div>;
          })}
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text-success)', marginTop: '6px', letterSpacing: '0.04em' }}>{displayEquipment.credits} credits remaining</div>
        </div>
      )}

      {/* Budgets */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: xpRemaining < 0 ? 'var(--doc-action)' : 'var(--doc-ink)' }}>{xpRemaining}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', marginTop: '2px' }}>XP LEFT</div>
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: (credits.remaining + (character.pocketMoney ? (character.pocketMoney.value || 0) : 0)) > 0 ? '#4ECB71' : 'rgba(255,255,255,0.3)' }}>{credits.remaining + (character.pocketMoney ? (character.pocketMoney.value || 0) : 0)}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', marginTop: '2px' }}>CREDITS LEFT</div>
        </div>
      </div>

      {/* B-55 / triage Finding 4 — the SIGNPOST. Force upgrades at creation are working as designed
          (the creator sells BASIC powers; upgrades are bought in play on the ADVANCEMENT tree, and
          leftover creation XP carries to the sheet) — but a new user reported not finding the two
          taps that get them there. This is the one line that was owed: the divergence was already
          deliberate and surfaced, it just was not signposted at the handoff moment. */}
      {xpRemaining > 0 && (
        <div data-testid="fc-xp-signpost" style={{
          background: 'rgba(78,170,255,0.06)', border: '1px solid rgba(78,170,255,0.28)',
          borderRadius: '8px', padding: '10px 12px', marginBottom: '16px',
        }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'rgba(255,255,255,0.78)' }}>
            Your <strong style={{ color: '#fff' }}>{xpRemaining} unspent XP</strong> carries over. Skill ranks,
            talents and <strong style={{ color: '#fff' }}>Force-power upgrades</strong> are all bought on the
            sheet's <strong style={{ color: '#fff' }}>ADVANCEMENT</strong> tab.
          </div>
        </div>
      )}

      {/* F-35: the pocket-money beat, under the credits summary (custom-creator mount). */}
      <PocketMoneyBeat value={character.pocketMoney ? character.pocketMoney.value : null} gameLine={character.gameLine} accent={accent} onSet={onSetPocketMoney} />

      {/* F-45 §3.6 — INFORM, NEVER GATE (ruling `f45-reentry-entry` §4, answered (a) by Jason).
          The first cut REFUSED to finalize while overdrawn, following Fork B's wording. That wording
          predates the inform-never-gate law, which names a disabled CONTINUE as its exact violation:
          say it loudly, in red, with the way out — and let the player through. The negative persists
          to the sheet, which renders it honestly (the XP tile above, fixed this same lap, no longer
          paints a negative balance the green of a settled one). The old `xpRemaining >= 0` guard is
          DELETED rather than bypassed, so there is no dormant gate to rediscover. */}
      {xpRemaining < 0 && (
        <div data-testid="fc-overdrawn-notice" style={{
          margin: '0 16px 10px', padding: '10px 12px', borderRadius: 8,
          background: 'rgba(255,85,85,0.08)', border: '1px solid rgba(255,85,85,0.45)',
          fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: '#FF8A8A',
        }}>
          You&rsquo;re <strong style={{ color: '#fff' }}>{Math.abs(xpRemaining)} experience over</strong> what
          this character has earned. You can finish anyway — the debt carries to the sheet, and you can
          refund a skill or talent whenever you like.
        </div>
      )}
      <FCBottomAction
        label={nameOk ? 'BEGIN YOUR STORY' : 'ENTER A NAME TO CONTINUE'}
        onClick={function() { if (nameOk) onConfirm(displayModel); }}
        disabled={!nameOk}
        accent={accent} />
    </div>
  );
}

// ═══ B-563 · STEP 12 · REVIEW — the revision's sheet, transplanted at all three bands (dark) ═══
// Lifted verbatim from revision/creator-2026-09-10 (… FCReviewSheet). The legacy
// FCReviewStep above stays for the visitor's page; the finish is the same onComplete on both paths.
/* ── THE REVIEW SHEET — the board's tree at mid + wide (`review-concept` R-RVC-2..8 + Addendum 1) ──
   A READING of the character, not a list: art · The Build · So Far (with the NAME at its head,
   A1) · Characteristics · Thresholds (before → after) · Trained Skills with their dice · Tracks ·
   Credits — each section with a quiet way back to the step that made it. The figures are the
   sheet's own routines (`normalizeCreated` → `computeDerived` · `computeMechanic` · `buildPool` ·
   `fcGearEncumbrance`), nothing computed twice. The four RULED elements the board has not drawn
   yet (F-35 pocket money · B-98 provenance · B-55 signpost · F-45 overdrawn) are built in idiom,
   none declared (R-RVC-5). The commit is REFUSED until the character has a name (RV-1, R-RVC-3)
   and never gated on anything else. The phone keeps the legacy `FCReviewStep`.
   B-1660 (D-433 item 3): a track line is its name and its value, no kind word, as creator-review.html draws it. */

/* ── THE REVIEW'S READING BLOCKS, LIFTED SO BOTH BANDS DRAW THE SAME OBJECTS ────────────────────
   `build-phone-review` (R-PRC-1). The phone review IS the desktop sheet in one column, so every
   plate below is drawn at BOTH bands — and the rule `build-phone-gameline` set with
   `fcGameLinePlates`, and `build-phone-gear` repeated with `fcGearDoor`/`fcGearKitPlate`, is that
   the two bands share the OBJECT rather than each keeping a copy of the markup. A second copy is
   the same defect one level up from a second resolver (PRAXIS §5): it agrees on the day it is
   written and drifts silently afterwards, and NO parity layer can see the drift, because each band
   is compared against its own frame and never against the other band.
   PROOF THAT THE LIFT IS THE SAME MARKUP AND NOT A LOOKALIKE: the four DESKTOP `board_review_*`
   parity frames stay green across it — the same check `build-phone-gear` used for the same move.
   Each helper takes ONE argument object and emits the plate WITH its `data-review-*` hooks, which
   both bands' suites read. `style` and `railSection` are the band's own: the frames give the phone
   its own margins, and DOM parity compares `data-pad`/`-size`/`-cols` and never a style. */

function fcReviewNamePlate(a) {
  return (
    <div className="hp-plate" data-flat="1" data-input="1" data-review-name-plate="1" style={{ marginBottom: '9px' }}>
      <input type="text" value={a.name} data-review-name="1" aria-label="Name" aria-required="true" aria-invalid={!a.nameOk}
        placeholder="Name" onChange={function(e) { a.onNameChange(e.target.value); }}
        style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 0, outline: 'none',
                 padding: '11px 12px', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '16px',
                 color: 'var(--doc-ink)', letterSpacing: '0.02em' }} />
    </div>
  );
}

/* THE REVIEW'S TWO SPOKEN XP BEATS (R-RVC-5c/d; carried to the phone by R-PRC-1, placed there by B-939):
   B-55's unspent signpost when experience is left, and F-45 §3.6's overdrawn notice when it is overspent —
   inform, never gate. ONE home for both bands' words. */
function fcReviewXpBeats(xpRemaining) {
  var left = Number(xpRemaining) || 0;
  return (
    <React.Fragment>
      {left < 0 ? (
        <span className="hp-none" data-review-overdrawn={-left} style={{ display: 'block', marginTop: '8px' }}>
          {Math.abs(left) + ' experience over what this character has earned. The debt carries to the sheet; refund a skill or talent whenever you like.'}</span>
      ) : null}
    </React.Fragment>
  );
}
function fcReviewSoFarPlate(a) {
  return (
    <div className="hp-plate" data-pad="data" data-rail-section={a.railSection} style={a.style}>
      <table className="hp-ledger"><tbody>
        {[{ k: 'Species', v: a.sp && a.sp.name }, { k: 'Career', v: a.displayModel.career },
          { k: 'Specialization', v: a.displayModel.specialization }, { k: 'Motivation', v: a.motName }].map(function(r) {
          return (
            <tr key={r.k}>
              <td className="hp-lkey">{r.k}</td>
              <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
            </tr>
          );
        })}
      </tbody></table>
    </div>
  );
}

function fcReviewThresholdsPlate(a) {
  // EXEMPT FROM THE n/threshold DISPLAY LAW, on Jason's word 2026-09-20 (B-747, option (a)). Every row here
  // is a DERIVATION and a DELTA — `Wound Threshold · 14 + Brawn   17 → 18` — so the ceiling IS the subject and
  // `0/17 → 0/18` is not a sentence. It is also an APPROVED frame (board_review_1920, phone_review) and the
  // review the creator transplant lights, which is why the exemption was ruled BEFORE B-618 rather than after:
  // without it the flip would have carried an unmet law onto the surface that replaces FCReviewStep's tiles.
  return (
    <div className="hp-plate" data-pad="data" data-review-thresholds="1">
      <table className="hp-ledger"><tbody>
        {[
          { key: 'wound', k: 'Wound Threshold', rule: (a.eff.wound_threshold_base != null ? a.eff.wound_threshold_base : '?') + ' + Brawn', b: a.before.woundThreshold, a: a.after.woundThreshold },
          { key: 'strain', k: 'Strain Threshold', rule: (a.eff.strain_threshold_base != null ? a.eff.strain_threshold_base : '?') + ' + Willpower', b: a.before.strainThreshold, a: a.after.strainThreshold },
          { key: 'soak', k: 'Soak', rule: 'Brawn, before armour', b: a.before.soak, a: a.after.soak },
        ].map(function(r) {
          return (
            <tr key={r.key} data-review-threshold={r.key} data-before={r.b} data-after={r.a}>
              <td className="hp-lkey">{r.k + ' '}<span className="hp-sep">{'·'}</span>{' ' + r.rule}</td>
              <td className="hp-lval">
                <span className="hp-none">{r.b}</span>{' '}<i style={{ fontStyle: 'normal', color: 'var(--doc-ink-muted)' }}>{'→'}</i>{' ' + r.a}
              </td>
            </tr>
          );
        })}
      </tbody></table>
    </div>
  );
}

function fcReviewSkillsPlate(a) {
  return (
    <div className="hp-plate" data-pad="data" data-review-skills={a.trained.length}>
      {a.trained.length ? (
        <table className="hp-ledger"><tbody>
          {a.trained.map(function(k) {
            var pl = a.poolOf(k);
            return (
              <tr key={k} data-review-skill={k} data-rank={a.rankOfSkill(k)}>
                <td className="hp-lkey">{a.skillNames[k] || SKILL_NAMES[a.sheetKeys[k] || k] || k}</td>
                <td className="hp-lval">
                  <span className="hp-pool" data-pool={pl ? (pl.proficiency || 0) + 'p' + (pl.ability || 0) + 'a' : ''}>
                    {pl && a.Dice ? a.Dice.renderPool(pl, { size: 1, cssSized: true }) : null}</span>
                </td>
              </tr>
            );
          })}
        </tbody></table>
      ) : <span className="hp-none" data-review-no-skills="1">{'No skill is trained yet.'}</span>}
    </div>
  );
}

/* ── THE GEAR PLATE (B-593, Jason's iPad walk: "Where's the gadgets and gear?") ───────────────
   The review named every step but one: gear fed encumbrance and the credits ledger and was never
   LISTED — a player read "410 cr spent" without seeing what it bought. The quick-build's review
   always listed it (`QBReviewStep`'s EQUIPMENT); this is that section in the sheet's own idiom —
   one ledger row per carried item, name × quantity against its line price, the same numbers the
   gear step's kit plate prints (`fcGearKitPlate`), with the step's edit button at its head. */
function fcReviewGearPlate(a) {
  return (
    <div className="hp-plate" data-pad="data" data-review-gear={a.inv.length}>
      {a.inv.length ? (
        <table className="hp-ledger"><tbody>
          {a.inv.map(function(it, i) {
            var n = Math.max(1, Number(it.qty) || 1), price = (Number(it.price) || 0) * n;
            return (
              <tr key={it.key + ':' + i} data-review-item={it.key} data-qty={n}>
                <td className="hp-lkey">{it.name + (n > 1 ? ' \u00d7' + n : '')}</td>
                <td className="hp-lval" data-review-item-price={price}>{price + ' cr'}</td>
              </tr>
            );
          })}
        </tbody></table>
      ) : <span className="hp-none" data-review-no-gear="1">{'Nothing carried. A character can begin with no gear at all.'}</span>}
    </div>
  );
}

function fcReviewTracksPlate(a) {
  return (
    <div className="hp-plate" data-pad="data" data-review-tracks={a.tracks.length}>
      {a.tracks.length ? (
        <table className="hp-ledger"><tbody>
          {a.tracks.map(function(t) {
            return (
              <tr key={t.key} data-review-track={t.key} data-value={t.value}>
                <td className="hp-lkey">{t.label}</td>
                <td className="hp-lval">{t.value}</td>
              </tr>
            );
          })}
        </tbody></table>
      ) : <span className="hp-none" data-review-no-tracks="1">{'No track is set.'}</span>}
      {/* UNDRAWN (R-RVC-5b): B-98's provenance — where the base came from, and what a trade bought. */}
      {a.tracks.filter(function(t) { return t.key !== 'morality'; }).map(function(t) {
        var bought = [];
        if (t.xp) bought.push(t.xp + ' XP');
        if (t.cr) bought.push(t.cr.toLocaleString() + ' credits');
        return (
          <span className="hp-none" key={t.key} data-review-provenance={t.key} style={{ display: 'block', marginTop: '8px' }}>
            {'Base ' + t.base + (a.partySize ? ' for a party of ' + a.partySize : '')
              + (t.delta ? ' · ' + (t.delta > 0 ? '+' : '−') + Math.abs(t.delta) + (bought.length ? ' traded for ' + bought.join(' and ') : '') : '')
              + (!t.delta && bought.length ? ' · took ' + bought.join(' and ') + ', ' + t.label.toLowerCase() + ' unchanged' : '') + '.'}
          </span>
        );
      })}
    </div>
  );
}

function fcReviewCreditsPlate(a) {
  return (
    <div className="hp-plate" data-pad="data" data-review-credits="1">
      <table className="hp-ledger"><tbody>
        <tr><td className="hp-lkey">Spent</td><td className="hp-lval" data-review-cr-spent={a.credits.spent}>{a.credits.spent + ' cr'}</td></tr>
        <tr><td className="hp-lkey">Left</td><td className="hp-lval" data-creator-purse={a.credits.remaining}>{a.credits.remaining + ' cr'}</td></tr>
        <tr><td className="hp-lkey">Encumbrance</td><td className="hp-lval" data-review-enc={a.enc ? a.enc.carried + '/' + a.enc.limit : ''}>{a.enc ? a.enc.carried + ' of ' + a.enc.limit : '–'}</td></tr>
        {a.pocket != null ? <tr data-review-pocket={a.pocket}><td className="hp-lkey">Pocket money</td><td className="hp-lval">{a.pocket + ' cr'}</td></tr> : null}
      </tbody></table>
      {/* F-100 surfacing K7 (R-SCC-3): the review repeats each GRANTED entitlement's line under Credits; a declined one
          is not carried, so it is not repeated (the sheet GEAR card's rule, R6). */}
      {(a.entitlementLines || []).filter(function(l) { return !l.declined; }).map(function(l) {
        return <p className="hp-prose hp-prose-sm" key={l.id} data-review-entitlement={l.id} style={{ margin: '8px 0 0', maxWidth: 'none' }}>
          {l.name + (l.note ? ' \u2014 ' + l.note : '')}</p>;
      })}
      {/* UNDRAWN (R-RVC-5a): F-35's pocket money — the book's one d100 after gear, rolled once,
          editable. B-723 LIFTED THE TRIO OUT to `paperPocketBeat` and this calls it, because Guided's
          paper review carries the same beat and no frame draws it on either door. Not one rendered
          string, class or `data-` attribute moved with it. */}
      {paperPocketBeat(a)}
    </div>
  );
}

function FCReviewSheet(p) {
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal || (p.steps || []).length;
  var character = p.character, gameData = p.gameData, sp = p.speciesData;
  var Dice = window.SWRPGDice || null;
  // THE ONE ASSEMBLY, inside the review's render where CREATOR_PLUMBING §4.7 names it (R-RVC-2).
  var displayModel = useMemo(function() {
    return normalizeCreated(character, sp, p.specData, gameData);
  }, [character, sp, p.specData, gameData]);
  var charbuild = (gameData && gameData.charbuild) || window._canonicalCharbuild;   // F-100 M6 R5: the draft's own view
  var noGear = { weapons: [], armor: null, gear: [] };
  // THRESHOLDS, before → after: TWO calls of the one core (R-RVC-4) — over the species' base
  // characteristics, then the character's; no armour on either (the board's "before armour" soak).
  var eff = speciesEff(sp, character.speciesOptionPicks) || {};
  var base = eff.characteristics || {};
  var before = useMemo(function() {
    // B-736: the substitution has to cover BOTH characteristic reads. `displayModel` now carries
    // `characteristicsAtCreation` (normalizeCreated persists it, and computeDerived reads the thresholds
    // from it), so overriding `characteristics` alone would leave the two thresholds computed from the
    // character's FINAL values and this "before" column would print the "after" numbers while soak
    // still moved. "Before" here means *if no starting XP had been spent on characteristics*, and at
    // that hypothetical the creation values ARE the species base — so both keys take `base`. R-RVC-4
    // is untouched: still two calls of the one shared core, over the species base then the character's.
    // B-1493 (release-r7-bug-burndown ADDENDUM 77: R-RVC-4 AMENDED) — "before" is THE SPECIES ALONE: its base
    // characteristics and only what the species grants free (its talents and abilities ride `speciesTalentGrants`
    // off the species the record still names). Nothing bought with XP (the tree talents, and the persisted
    // projection of them), no career and no specialization grant, no Battle Scar. A Human at Brawn 3 with two
    // Toughened read "10 + Brawn 16 → 17" — 16 matches no reading of its label; it reads 12 → 17 now, and the arrow
    // carries everything the player's choices did. Still TWO calls of the one core; no new arithmetic.
    return CharResolve.computeDerived(Object.assign({}, displayModel, {
      characteristics: base, characteristicsAtCreation: base,
      talents: [], derivedBonuses: { soakBonus: 0, woundThresholdBonus: 0, strainThresholdBonus: 0, meleeDefenseBonus: 0, rangedDefenseBonus: 0, forceRatingBonus: 0 },
      derivedConditional: [], career: null, careerId: null, specializations: [], specializationIds: [],
      rewards: [], equipment: { weapons: [], armor: null, gear: [], credits: 0 },   // no Battle Scar (rewards), no implant (gear)
    }), noGear, charbuild);
  }, [displayModel, base, charbuild]);
  var after = useMemo(function() {
    return CharResolve.computeDerived(displayModel, noGear, charbuild);
  }, [displayModel, charbuild]);
  var chars = displayModel.characteristics || {};
  // THE NAME (RV-1 · R-RVC-3 · A1): required; the commit refuses until it is non-empty.
  var name = (character.identity && character.identity.name) || '';
  var nameOk = name.trim().length > 0;
  // B-1558 (ruling release-r7-bug-burndown ADDENDUM 99 item 15; drawing https://claude.ai/artifact/4o2scTvExQWKmeiEExGfYu):
  // the step bar reaches Review with neither chosen, so Create also waits for a species and a career — the name check's idiom.
  var heldOk = !!(displayModel.species && displayModel.career);
  var createOk = nameOk && heldOk;
  var spentEarlier = Math.max(0, (p.xpPool || 0) - (p.xpRemaining || 0));
  var over = (p.xpRemaining || 0) < 0;
  // SO FAR's fourth row: the chosen SPECIFIC from `character.motivation` (R-RVC-4).
  var mot = character.motivation || null;
  var motName = '';
  if (mot) {
    var specRec = (gameData.charbuild.motivationSpecifics || []).filter(function(s) { return s.id === mot.specific; })[0];
    var catRec = (gameData.charbuild.motivationCategories || []).filter(function(c) { return c.id === mot.category; })[0];
    motName = (mot.custom && String(mot.custom).trim()) || (specRec && specRec.name) || (catRec && catRec.name) || '';
  }
  // TRAINED SKILLS: every ranked skill with its pool from the shared builder (the skills step's rows).
  // The assembly keys skills by the SHEET key (the OggDude short key, CREATOR_PLUMBING §4.3); the
  // registry's characteristic map is by canonical id — the same bridge the skills step crosses.
  var skillChars = gameData.charbuild.skillChars || {}, sheetKeys = gameData.charbuild.skillSheetKeys || {}, skillNames = gameData.charbuild.skillNames || {};
  function rankOfSkill(id) { return (displayModel.skills || {})[sheetKeys[id] || id] || 0; }
  // B-1503 (release-r7-bug-burndown ADDENDUM 76): THE SHEET'S ONE POOL PATH. The pool was `Dice.buildPool` over the
  // characteristic and rank alone, so an owned talent's dice (Physical Training's Boost on Athletics and Resilience,
  // AoR p.154) reached the saved sheet and never the Review — a second path for one quantity (PRAXIS §5). Now the
  // Review asks `sheetPoolOf` over the record it is about to write, with the same mods the sheet collects
  // (`collectPoolMods` over `resolveSheetEquipment`: the carried items' and the owned talents' dice).
  var reviewPoolMods = useMemo(function() {
    return collectPoolMods(resolveSheetEquipment(displayModel.equipment, displayModel));
  }, [displayModel]);
  function poolOf(id) {
    return skillChars[id] ? sheetPoolOf(displayModel, sheetKeys[id] || id, reviewPoolMods) : null;
  }
  // THE STRONGEST ROLL FIRST — the board's order (Brawl · Melee · Athletics · Resilience · Coercion
  // on its specimen): proficiency dice, then ability dice, then the name.
  var trained = Object.keys(skillChars).filter(function(id) { return rankOfSkill(id) > 0; })
    .sort(function(a, b) {
      var pa = poolOf(a) || {}, pb = poolOf(b) || {};
      return ((pb.proficiency || 0) - (pa.proficiency || 0)) || ((pb.ability || 0) - (pa.ability || 0))
        || (skillNames[a] || a).localeCompare(skillNames[b] || b);
    });
  // TRACKS: the mechanic (and the Morality layer on a two-track build) through the shared reads.
  var mech = character.mechanic && character.mechanic.type ? character.mechanic : null;
  var layer = character.moralityLayer && character.moralityLayer.type ? character.moralityLayer : null;
  var tracks = [];
  [mech, layer].forEach(function(m) {
    if (!m) return;
    var label = ({ obligation: 'Obligation', duty: 'Duty', morality: 'Morality' })[m.type] || m.type;
    var fin = m.finalValue != null ? m.finalValue : m.baseValue;
    tracks.push({ key: m.type, label: label, value: fin,
                  base: m.baseValue, delta: (m.baseValue != null && fin != null) ? fin - m.baseValue : 0,
                  xp: m.xpAdjustment || 0, cr: m.creditAdjustment || 0 });
  });
  // CREDITS + ENCUMBRANCE: the gear lap's one call over the same rows.
  var inv = (character.gearLedger && character.gearLedger.inventory) || [];
  var enc = fcGearEncumbrance(inv, p.reg, chars.brawn, CharResolve.speciesEncumbranceDeltaOf(sp, character.speciesOptionPicks, character.creationBuyouts));
  // POCKET MONEY (F-35, R-RVC-5a): rolled once, editable, the app's per-line cite.
  var pocket = character.pocketMoney && character.pocketMoney.value != null ? Number(character.pocketMoney.value) : null;
  var _pe = useState(false), pocketEditing = _pe[0], setPocketEditing = _pe[1];
  var _pd = useState(''), pocketDraft = _pd[0], setPocketDraft = _pd[1];
  function clampPocket(n) { n = Math.round(Number(n) || 0); return Math.max(1, Math.min(100, n)); }
  var pocketCite = POCKET_MONEY_CITE[character.gameLine] || POCKET_MONEY_CITE.eote;

  function stepNoOf(st) { var i = (p.steps || []).indexOf(st); return i >= 0 ? i + 1 : null; }
  function editButton(st, label) {
    var n = stepNoOf(st);
    if (n == null) return null;
    return (
      <button type="button" className="hp-quiet hp-backto" data-review-edit={st} aria-label={'Edit ' + label + ' — step ' + n}
        onClick={function() { p.onGoToStep(st); }}>{'Edit · step ' + n}</button>
    );
  }
  function commit() { if (createOk) p.onConfirm(displayModel); }
  // THE GEAR SECTION, drawn once and placed by band below (B-593).
  var gearStack = (
    <React.Fragment>
      <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Gear</h2>{editButton('gear', 'Gear')}</div>
      {fcReviewGearPlate({ inv: inv })}
    </React.Fragment>
  );

  /* ── THE PHONE: THE SAME SHEET IN ONE COLUMN (`phone-review-census` R-PRC-1) ──────────────────
     THE DERIVATION ABOVE IS NOT REPEATED, and that is the whole point of branching HERE rather
     than in a component of its own. Every number this screen prints — the two `computeDerived`
     calls, the trained-skill pools, the tracks, the encumbrance, the assembled `displayModel` —
     is computed once above and read by both returns. A `FCReviewSheetPhone` beside this function
     would have had to be handed all of it or recompute it, and recomputing is a second answer to
     "what is this character" on the one screen whose entire job is to state that answer.
     The sibling phone steps each got their own component because their derivation was a line or
     two; review's is seventy, which is why this one branches at the RETURN.

     The ORDER is the frame's, with R-PRC-1's two carried-forward sections in the DESKTOP sheet's
     own place — after Trained Skills, before So Far: status pill · maker · desig · lead + cite ·
     art · Characteristics · Thresholds · Trained Skills · [Tracks] · [Credits] · So Far with the
     name · commit. Tracks and Credits are drawn on NO phone frame; they are KEEP-UNDRAWN, carried
     in the desktop's approved idiom (R-PRC-1, R-MU1's "unchanged" being the desktop sheet here),
     and R-PRC-2 has asked the lab for the frame. They are named object by object in the gate as
     red on the board's account until it lands — never silently.

     THE EDIT LABEL IS THE PHONE FRAME'S OWN WORD. The desktop frames draw "Edit · step 6"; the
     phone frame draws "Edit", eleven characters shorter on a 390px sechead. Same class the gear
     and Force cards met and the architect ruled: a frame that DRAWS a thing is the authority on
     that thing (R-MIL-1), and these are two frames for two bands, not one frame disagreeing with
     itself. The ACCESSIBLE NAME keeps the step number at both bands — it is what tells a screen
     reader where the button goes, and no frame draws it. */
  function phoneEdit(st, label) {
    var n = stepNoOf(st);
    if (n == null) return null;
    return (
      <button type="button" className="hp-quiet hp-backto" data-review-edit={st} aria-label={'Edit ' + label + ' \u2014 step ' + n}
        onClick={function() { p.onGoToStep(st); }}>{'Edit'}</button>
    );
  }
  if (p.band === 'mobile') {
    return (
      <FCPhoneShell band={p.band} rootAttrs={{ 'data-review-step': '1' }}
        stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
        steps={p.steps} stepLabels={p.stepLabels}
        onBack={p.onBack} onJump={p.onJump}
        status={<React.Fragment>
          <div><span className="hp-status-k">XP left</span>
            <span className="hp-status-v" data-over={over ? '1' : undefined} data-creator-char-purse={p.xpRemaining}
              style={{ color: over ? 'var(--doc-action)' : undefined }}>{p.xpRemaining}</span></div>
          <div><span className="hp-status-k">Credits</span>
            <span className="hp-status-v" data-creator-purse={p.credits.remaining}>{p.credits.remaining + ' cr'}</span></div>
          <div><span className="hp-status-k">Enc</span>
            <span className="hp-status-v" data-review-enc={enc ? enc.carried + '/' + enc.limit : ''}>{enc ? enc.carried + ' of ' + enc.limit : '\u2013'}</span></div>
        </React.Fragment>}
        /* THE COMMIT CARRIES THE DESKTOP'S REFUSAL CONTRACT UNCHANGED (R-RVC-3, R-CSC-1): refused
           while the name is empty, and the refusal is PRINTED rather than only disabling. The
           phone frame draws only the released state, so the refused one is the desktop's. */
        act={<React.Fragment>
          <button type="button" className="hp-commit" data-creator-commit="1"
            data-refused={!nameOk ? 'name' : (heldOk ? undefined : 'species-career')} aria-disabled={createOk ? undefined : 'true'} onClick={commit}>
            {'Create This Character '}<span>{'\u203a'}</span>
          </button>
          {!nameOk ? <span className="hp-none" data-review-refused="name" style={{ display: 'block', marginTop: '8px' }}>{'Name your character to finish'}</span> : null}
          {!heldOk ? <span className="hp-none" data-review-refused="species-career" style={{ display: 'block', marginTop: '8px' }}>{'Choose a species and a career to finish'}</span> : null}
        </React.Fragment>}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">Character Creation</div>
        </div>
        <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName}</h1>
        <div className="hp-desig-rule" />
        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
          {p.cite ? (
            <div>
              <span className="hp-cite" data-step-cite="derived_attributes" style={{ marginRight: '7px' }}>{p.cite}</span>
            </div>
          ) : null}
        </div>

        <div style={{ margin: '16px 0 4px' }}>{fcSpeciesArt(sp, displayModel.portrait, 1)}</div>

        <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Characteristics</h2>{phoneEdit('characteristics', 'Characteristics')}</div>
        <div>
          <div className="hp-plate" data-pad="snug" data-review-rail="1">
            {/* `cols="3"` AND NO `size`: the rail's own variant switches, and the frame draws the
                phone's as 3x2. Passing the wide band's `lg` clipped WILLPOWER to "WILLPOWE" once
                already — the note on `FCbRail` records it. */}
            <FCbRail character={character} base={base} cols="3" />
          </div>
        </div>

        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Thresholds</h2></div>
        {fcReviewThresholdsPlate({ eff: eff, before: before, after: after })}

        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Trained Skills</h2>{phoneEdit('skills', 'Trained Skills')}</div>
        {fcReviewSkillsPlate({ trained: trained, poolOf: poolOf, rankOfSkill: rankOfSkill, skillNames: skillNames, sheetKeys: sheetKeys, Dice: Dice })}
        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Gear</h2>{phoneEdit('gear', 'Gear')}</div>
        {fcReviewGearPlate({ inv: inv })}
        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Tracks</h2>{phoneEdit('mechanic', 'Tracks')}</div>
        {fcReviewTracksPlate({ tracks: tracks, partySize: p.partySize })}

        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Credits</h2>{phoneEdit('gear', 'Credits')}</div>
        {fcReviewCreditsPlate({ credits: p.credits, enc: enc, entitlementLines: fcEntitlementLines((((gameData && gameData.charbuild) || {}).careers || {})[(character.career || {}).key], character.entitlements), pocket: pocket, pocketEditing: pocketEditing, setPocketEditing: setPocketEditing, pocketDraft: pocketDraft, setPocketDraft: setPocketDraft, clampPocket: clampPocket, pocketCite: pocketCite, onSetPocketMoney: p.onSetPocketMoney })}

        {/* SO FAR IS THE FRAME'S DISCLOSURE, AND IT IS OPEN WHILE THE NAME IS EMPTY (R-PRC-1).
            The name lives at its head, exactly where the desktop rail puts it — so a player who
            has not named the character cannot be refused by a control whose cause is folded away.
            The moment a name exists the disclosure behaves as the frame draws it: collapsed. */}
        <details className="hp-unit" open={!nameOk}>
          <summary>
            <div className="hp-head">
              <span className="hp-name">So Far</span>
              <span className="hp-glyph">{'\u25be'}</span>
            </div>
          </summary>
          {fcReviewNamePlate({ name: name, nameOk: nameOk, onNameChange: p.onNameChange })}
          {/* `railSection` at the phone too, so `smoke_review_transplant`'s ONE state reader finds So Far
              at every band instead of growing a phone-shaped copy of itself. Parity cannot see it: it
              compares `data-pad`/`-size`/`-cols` and no other data attribute. */}
          {fcReviewSoFarPlate({ sp: sp, displayModel: displayModel, motName: motName, railSection: 'sofar', style: { marginTop: '9px' } })}
          {/* B-939 · D-PS2 (b), Jason 2026-09-25 ("B-939b", on the rendered choice at
              Docs/mockups/paper-sweep-reentry-words.html @ 8ddae11f): the desk's two XP beats, carried to
              the phone by `phone-review-census` R-PRC-1, stand INSIDE So Far after its plate — read with the
              disclosure, as drawn. The same function as the desk, so the words have one home. */}
          {fcReviewXpBeats(p.xpRemaining)}
        </details>
      </FCPhoneShell>
    );
  }

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-review-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: the art plate · The Build · So Far (the name at its head) · the commit. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1" data-rail-stack="1">
          <div style={{ marginBottom: '13px' }}>{fcSpeciesArt(sp, displayModel.portrait, 1)}</div>
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool + ' XP'}</dd></div>
              <div className="hp-xp"><dt>Spent earlier</dt><dd data-review-spent={spentEarlier}>{spentEarlier + ' XP'}</dd></div>
              <div className="hp-xp"><dt>Spent here</dt><dd>0 XP</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Remaining</dt>
                <dd data-creator-char-purse={p.xpRemaining}>{p.xpRemaining + ' XP'}</dd></div>
            </dl>
            {/* UNDRAWN (R-RVC-5c/d): the unspent signpost (B-55) and the overdrawn state (F-45 §3.6),
                both SPOKEN — the commit below is never gated on experience. One home, `fcReviewXpBeats`. */}
            {fcReviewXpBeats(p.xpRemaining)}
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          {/* THE NAME, at the head of So Far (A1 R-RVC-A1-1), in the board's input-plate idiom. */}
          {fcReviewNamePlate({ name: name, nameOk: nameOk, onNameChange: p.onNameChange })}
          {fcReviewSoFarPlate({ sp: sp, displayModel: displayModel, motName: motName, railSection: 'sofar' })}
          <div className="hp-railact" data-rail-section="commit">
            {/* REFUSED without a name (R-CSC-1: the refusal is printed), released as the board's words. */}
            <button type="button" className="hp-commit" data-creator-commit="1"
              data-refused={!nameOk ? 'name' : (heldOk ? undefined : 'species-career')} aria-disabled={createOk ? undefined : 'true'} onClick={commit}>
              {'Create This Character '}<span>{'›'}</span>
            </button>
            {!nameOk ? <span className="hp-none" data-review-refused="name" style={{ display: 'block', marginTop: '8px' }}>{'Name your character to finish'}</span> : null}
            {!heldOk ? <span className="hp-none" data-review-refused="species-career" style={{ display: 'block', marginTop: '8px' }}>{'Choose a species and a career to finish'}</span> : null}
          </div>
        </div>

        {/* ── THE HEAD: the stephead pair, the lead, the line's cite. */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div className="hp-stephead">
            <div className="hp-stephead-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                <span className="hp-slash"><i /><i /><i /></span>
                <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
              </div>
              <h1 className="hp-desig">{p.stepName}</h1>
              <div className="hp-desig-rule" />
            </div>
            <div className="hp-plate" data-pad="read" style={{ maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              {p.cite ? <div><span className="hp-cite" data-step-cite="derived_attributes">{p.cite}</span></div> : null}
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the reading, in the board's two columns. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            <div className="hp-spend">
              <div>
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Characteristics</h2>{editButton('characteristics', 'Characteristics')}</div>
                <div>
                  <div className="hp-plate" data-pad="snug" data-review-rail="1">
                    <FCbRail character={character} base={base} size="lg" />
                  </div>
                </div>
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Thresholds</h2>{editButton('characteristics', 'Thresholds')}</div>
                {fcReviewThresholdsPlate({ eff: eff, before: before, after: after })}
              {/* GEAR'S PLACE DEPENDS ON THE BAND (B-593). The wide sheet is the approved two-column
                  frame; its first column had the room (Characteristics and Thresholds end 250px
                  above the pane's foot on a full build), so Gear stacks under Thresholds there and
                  the second column keeps the revision's own height. At mid, 1194×810 could not hold
                  that second column without scrolling, and the review is the one page a player
                  should take in at a glance — so the mid band draws THREE stacks, Gear under the
                  skills and Tracks · Credits in a column of its own, and the page is a true
                  snapshot: rail, head, three stacks and CREATE THIS CHARACTER all on screen. */}
              {p.band === 'mid' ? null : gearStack}
              </div>
              <div data-review-stack="skills">
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Trained Skills</h2>{editButton('skills', 'Trained Skills')}</div>
                {fcReviewSkillsPlate({ trained: trained, poolOf: poolOf, rankOfSkill: rankOfSkill, skillNames: skillNames, sheetKeys: sheetKeys, Dice: Dice })}
                {p.band === 'mid' ? gearStack : null}
              </div>
              <div data-review-stack="tracks">
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Tracks</h2>{editButton('mechanic', 'Tracks')}</div>
                {fcReviewTracksPlate({ tracks: tracks, partySize: p.partySize })}
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Credits</h2>{editButton('gear', 'Credits')}</div>
                {fcReviewCreditsPlate({ credits: p.credits, enc: enc, entitlementLines: fcEntitlementLines((((gameData && gameData.charbuild) || {}).careers || {})[(character.career || {}).key], character.entitlements), pocket: pocket, pocketEditing: pocketEditing, setPocketEditing: setPocketEditing, pocketDraft: pocketDraft, setPocketDraft: setPocketDraft, clampPocket: clampPocket, pocketCite: pocketCite, onSetPocketMoney: p.onSetPocketMoney })}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}


/* ── FC Tree Rail (B-86) ──────────────────────────────
   THE CREATOR'S SIDE OF THE ONE-COMPONENT CONTRACT.

   `DirectionalTree` documents two doors (":4172"): given `onInspect` a tapped cell routes to a
   DETAILS pane; without it the component falls back to its own bottom sheet. ADVANCEMENT passed
   `onInspect`; the creator passed NONE, so every width — including a 1440px desktop — got the
   MOBILE overlay. One component, two doors, and only one door had been given the desktop idiom.

   This is that door. It renders the SAME `cell.entry` the bottom sheet renders (name / meta / desc
   / buy / refund), so the two paths cannot drift: adopting rather than reimplementing is R-CUI-2,
   and this rail is deliberately a presentation of the shared entry, not a second model of it.

   Mounted only when the band is NOT mobile (C1's `useCreatorBand`), which is also why mobile stays
   byte-identical: no `onInspect` is passed there, so the component behaves exactly as it shipped. */
function FCTreeRail({ entry, accent, onClose }) {
  if (!entry) {
    return (
      <aside style={{ flex: '0 0 320px', minWidth: 0, padding: '14px 16px', alignSelf: 'flex-start',
        position: 'sticky', top: 0,
        border: '1px dashed var(--doc-rule)', borderRadius: '10px' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em',
          color: 'var(--doc-ink-muted)' }}>DETAILS</div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
          color: 'var(--doc-ink-muted)', marginTop: '8px', lineHeight: 1.5,
          maxWidth: 'var(--desk-prose-max)' }}>
          Select a talent to read what it does and what it costs.
        </div>
      </aside>
    );
  }
  var buy = entry.buy, refund = entry.refund;
  return (
    <aside style={{ flex: '0 0 320px', minWidth: 0, padding: '14px 16px', alignSelf: 'flex-start',
      position: 'sticky', top: 0,
      border: '1px solid var(--doc-rule)', borderRadius: '10px',
      background: 'var(--doc-paper-raised)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 'var(--fs-lg)',
          color: 'var(--doc-ink)', minWidth: 0 }}>{entry.name}</div>
        <button onClick={onClose} aria-label="Close details" style={{
          minWidth: '44px', minHeight: '44px', display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--doc-ink-muted)', fontSize: 'var(--fs-lg)' }}>&#10005;</button>
      </div>
      <DirectionalTreeEntryBody entry={entry} accent={accent} paper
        afterAction={function() { if (onClose) onClose(); }} />
    </aside>
  );
}

/* ── OggDude import (OGG-1) — slice 5b browser surface ──────────────────────────
   Reads a character export, runs the shared importer (window.SWRPGOggImport.importToCharacter),
   loads the result through the REAL normalizeCreated, and shows the match report before the user
   commits (R-OGG1-3: the receipt is the trust surface). Built from the approved mockup
   Docs/mockups/ogg-1-import.html @ 215b1f5 (states 3–7). */

// Re-encode an imported portrait to a sheet-appropriate bound (R-OGG-21). CHOSEN BOUND: longest edge
// 512px, JPEG quality 0.82 — a sheet portrait renders ~380px tall, so 512 is crisp, and JPEG collapses
// a 273 KB OggDude PNG into the tens-of-KB "same league as a hand-uploaded portrait." Async (Image
// load); resolves to the ORIGINAL data URI on any failure so a portrait is never lost to a re-encode
// error. The sheet's PROFILE upload takes the same bound (B-926), so the two paths store alike.
// R-S4G-16 (ruling `shipyard-s4-ground`: "one helper, two settings"): the re-encode itself lives in ONE shared helper,
// lib/shared/picture.js (window.SWRPGPicture), which the Ship Yard's ship pictures also use. The portrait keeps its single
// rung and its never-lose-it fallback: this name is the same-name adapter, and test/picture_helper.cjs pins both settings.
function reEncodePortrait(dataUri, maxEdge, quality) {
  var P = window.SWRPGPicture;
  if (!P) return Promise.resolve(dataUri || '');   // the helper not loaded: keep the original, as any failure does
  return P.reEncode(dataUri, { ladder: [[maxEdge || 512, quality || 0.82]] }).then(function (r) { return r.dataUrl; });
}

// THE ROOM'S OWN WORDS, IN ONE PLACE (B-770; R-CFP-3, the VERBATIM RULE and the load-bearing clause
// of `character-files-paper-concept`).
//
// WHY THIS CONSTANT EXISTS AT ALL, because a shared string table is not free. R-CFP-3 says every
// line of this room is written by the ENGINES or by the room and is rendered VERBATIM — no
// paraphrase, no summary, no sentence authored by a build row — with exactly ONE fenced exception
// (the count on a section head, which is B-771/B-772's and is not on this row's frames). The paper
// face and the legacy face draw the SAME room, so every one of these sentences would otherwise exist
// TWICE in one component, and two copies of a sentence under a verbatim rule is the drift that rule
// exists to prevent: the day someone fixes a typo on one face, the other face is silently wrong and
// nothing fails. One literal, two readers.
//
// NOTHING HERE IS NEW COPY. Every string below is the shipped room's own, moved and not rewritten —
// which is checkable, because the paper face and the dark face render the same characters and the
// suite asserts the legacy DOM is unchanged across this edit.
//
// ⚠ ONE CONSEQUENCE FOR THE LAB, disclosed rather than discovered: `lab/holocron/files_build.py`
// harvests these strings OUT OF THIS SOURCE by regex against their old JSX positions (`ENTRY_H`,
// `ENTRY_LEAD`, `DROP_*`, `IERR_*`, `XERR_*`, `LOADING`, `_SURF`). The board is PINNED at lab
// `e92c9b0a` and approved, so nothing about the approved frames moves; but a REBUILD of that board
// against a carry newer than this commit will need those harvest patterns re-pointed at `CF_COPY`.
// The strings themselves are unchanged, character for character, so the re-point is mechanical.
// F-100 M10 D-144: the receipt's Force line — null when nothing was left off (the section is not drawn), else
// "<n> purchased Force power upgrade(s) isn't/aren't … left off (<powers>)." from the importer's refused list.
function fcForceLeftOffLine(fu) {
  var n = (fu && Array.isArray(fu.refused)) ? fu.refused.length : 0;
  if (!n) return null;
  return n + (n === 1 ? CF_COPY.forceA : CF_COPY.forceB) + (fu.refusedPowers || []).join(', ') + CF_COPY.forceC;
}
// F-100 M12 D-161 @ ad1aa1d9 (R-M12B-35; amends frame K, R-MIL-2): the receipt's signature line — what, then why. null
// when nothing was left off (the section is not drawn), else "<n> purchased signature ability box(es) couldn't come across,
// so it was/they were left off (<abilities>)." — <n> is fcSigBoxesLeftOff, the PURCHASED BOXES left off (R-M12B-39, ADDENDUM
// 13 @ 313f0378: an ability refused whole counts every box it owned); fcSigWhyLines gives each V1–V7 sentence (CharResolve's
// own words, on the report), one per line, an identical sentence once.
function fcSigBoxesLeftOff(sa) {
  if (!sa) return 0;
  if (typeof sa.boxesLeftOff === 'number') return sa.boxesLeftOff;
  return Array.isArray(sa.refused) ? sa.refused.length : 0;
}
// R7 R-R7-O1 (ADDENDUM 15): the import's `report.credits` (present only when the file's pool is smaller than its gear's price)
// as one told line in CAME ACROSS, the balance with a true minus; null when the file's credits cover its gear.
function fcCreditsLine(rep) {
  var c = rep && rep.credits;
  if (!c || typeof c.balance !== 'number' || c.balance >= 0) return null;
  return CF_COPY.creditsA + '\u2212' + Math.abs(c.balance) + CF_COPY.creditsB;
}
// R7 B-1038 (ADDENDUM 33): the import's `report.xpCounted` ({file, sheet}, present only when they differ) as one told line in
// CAME ACROSS after the credits line; null on a record without it (old records, or a file whose spent the sheet matches).
function fcXpCountedLine(rep) {
  var x = rep && rep.xpCounted;
  if (!x || typeof x.file !== 'number' || typeof x.sheet !== 'number') return null;
  return CF_COPY.xpCountedA + x.file + CF_COPY.xpCountedB + x.sheet + CF_COPY.xpCountedC;
}
function fcSigLeftOffLine(sa) {
  var n = fcSigBoxesLeftOff(sa);
  if (!n) return null;
  return n + (n === 1 ? CF_COPY.sigA : CF_COPY.sigB) + (sa.refusedAbilities || []).join(', ') + CF_COPY.sigC;
}
function fcSigWhyLines(sa) {
  var out = [];
  ((sa && sa.problems) || []).forEach(function (p) { if (p && p.words && out.indexOf(p.words) === -1) out.push(p.words); });
  return out;
}
var CF_COPY = {
  back: '‹ Back',
  loading: 'Loading…',
  entryTitle: 'Character files',
  dropGlyph: '⇪',
  dropTitle: 'Bring your character across',
  dropBtn: 'Choose a file',
  pickTitle: 'Which character?',
  errGlyph: '⚠',
  importErrTitle: "That file didn't read as an OggDude character",
  importErrBoot: 'The character data is still loading — try again in a moment.',
  importErrBad: 'The importer expects the character export (an .xml file) from the OggDude desktop generator. Nothing was changed.',
  importErrBtn: 'Choose another file',
  exportErrTitle: 'That character could not be exported',
  exportErrBtn: 'Pick another character',

  // ── THE MATCH RECEIPT'S OWN WORDS (B-771) ────────────────────────────────────────────────────
  // MOVED OUT OF `MatchReport`, NOT REWRITTEN. Every literal below was a string in that component
  // and is here character for character; `MatchReport` now reads them from this table, so the paper
  // receipt and the dark receipt draw ONE literal each. That is R-CFP-3's whole point arriving on
  // the row where it bites hardest: the receipt is the screen where "the engines' words, verbatim"
  // is the product's claim, and a room with two copies of `Everything in this save maps to
  // something we carry.` is a room where fixing one of them is a silent lie on the other face.
  //
  // THE SENTENCES THAT CARRY ENGINE NUMBERS ARE STORED AS THEIR FRAGMENTS, in the order the room
  // prints them, because that is what the room actually holds: `EXPERIENCE` is four literals with
  // three `<b>` slots between them and `FORCE UPGRADES` is two with one. Storing the joined
  // sentence and re-splitting it at render would be a paraphrase machine with extra steps — the
  // fragments ARE the string, and a suite can assert each one appears in the page in its own right.
  //
  // THE FIVE SECTION WORDS AND THEIR FIVE MARKS are the room's too. The dark face paints the mark
  // in its own ink and the paper face sets it at the head of the `h2` (the board's arrangement), so
  // the CHARACTER is shared and only the presentation differs — which is the line between a face
  // and a rewrite.
  reportEyebrow: 'IMPORTED',
  reportNoName: 'Imported character',
  reportFrom: 'from ',
  reportFromFallback: 'the OggDude export',
  reportClean: 'Everything in this save maps to something we carry. Nothing was adapted, nothing is missing.',
  headMatched: 'CAME ACROSS',
  headAdapted: 'ADAPTED',
  headAbsent: 'NOT IN OUR DATA',
  headXp: 'EXPERIENCE',
  headForce: 'FORCE UPGRADES',
  headSig: 'SIGNATURE ABILITIES',
  headLeftOut: 'LEFT IN THE FILE',
  headRigger: 'SIGNATURE VEHICLE',   // release-r5-f1 D (F1 S4)
  wordRiggerMarked: 'Marked as yours: ',
  wordRiggerWeapon: 'Signature Vehicle',
  wordRiggerWeaponPick: 'its weapon pick',
  glyphMatched: '✓',
  glyphAdapted: '~',
  glyphAbsent: '✗',
  glyphNote: '◔',
  wordSpecies: 'species',
  wordCareer: 'career',
  wordSpecialization: 'specialization',
  wordTalents: 'talents',
  wordGear: 'gear',
  wordSkills: 'skills',
  wordCompanion: 'companion',
  wordCompanions: 'companions',
  wordRewards: 'rewards',
  wordSchematics: 'schematics',
  reportTail: '6 characteristics · XP + credits ledger',
  reportPortrait: ' · portrait (re-encoded)',
  // R7 R-R7-O1 (ruling release-r7-bug-burndown ADDENDUM 15): the import's below-zero balance, TOLD — fcCreditsLine
  creditsA: 'Credits: this file’s gear costs more than its credits, so the sheet starts at ',
  creditsB: '.',
  // R7 B-1038 (ruling release-r7-bug-burndown ADDENDUM 33, wording (b)): the import-moment XP pair, TOLD — fcXpCountedLine
  xpCountedA: 'XP spent: OggDude counted ',
  xpCountedB: '; this sheet counts ',
  xpCountedC: '.',
  adaptedNone: 'nothing needed re-modelling',
  absentCustom: 'a custom item',
  xpA: 'Your file records ',
  xpB: ' XP granted but ',
  xpC: ' spent — we set your total to ',
  xpD: ', so nothing is lost. Unspent XP could not be determined.',
  // Jason D-144 (amends frame K, R-MIL-2): the Force line names only what was LEFT OFF — one fragment per number, then the
  // powers' display names, then forceC. Drawn by fcForceLeftOffLine; absent when every purchased upgrade was carried.
  forceA: " purchased Force power upgrade isn't in this app's power trees, so it was left off (",
  forceB: " purchased Force power upgrades aren't in this app's power trees, so they were left off (",
  forceC: ').',
  // Jason D-161 (amends frame K, R-MIL-2): the signature line, D-144's idiom — what was left off, then (fcSigWhyLines) the
  // check's own sentence under it. Drawn by fcSigLeftOffLine; absent when every purchased box was carried.
  sigA: " purchased signature ability box couldn't come across, so it was left off (",
  sigB: " purchased signature ability boxes couldn't come across, so they were left off (",
  sigC: ').',
  reportCancel: 'Cancel',
  reportAdd: 'Add to my roster',
  reportAdding: 'Adding…',

  // ── THE PICK LIST'S AND THE EXPORT RECEIPT'S OWN WORDS (B-772) ────────────────────────────────
  // MOVED OUT OF `body()`'s `pick` and `exported` branches, NOT REWRITTEN. Every literal below was
  // typed inline in that function and is here character for character; the dark face now reads them
  // from this table, so the paper receipt and the dark receipt draw ONE literal each. This is the
  // screen where R-CFP-3's verbatim rule is the product's own claim twice over — the export report's
  // header comment already says "every line here is the ENGINE's, rendered VERBATIM" — so a room
  // holding two copies of `Nothing — everything made the trip.` is a room where fixing one of them
  // is a silent lie on the other face.
  //
  // THE FOUR SECTION WORDS are the room's `sect(...)` titles, in the room's own order. They carry NO
  // mark: the match receipt's five heads each have a glyph the dark face paints in its own ink, and
  // the export report's four have none at all — which is the board's arrangement too (`<h2>CARRIED`
  // against `<h2>✓ CAME ACROSS`), so `paperSechead` takes a null glyph rather than inventing one.
  pickEmptyTitle: 'No characters yet',
  pickEmptyBody: 'Make one, or import one, and it will appear here.',
  pickUnnamed: 'Unnamed',
  exportTick: '✓',
  exportReady: ' — ready',
  headCarried: 'CARRIED',
  headDefaults: 'THE FORMAT REQUIRES THESE — WE SENT ITS DEFAULTS',
  headCommunity: 'NEEDS COMMUNITY DATA',
  headDropped: 'COULD NOT BE EXPRESSED',
  // The room prints `<b>N fields</b>`; its tail sentence is cut (copy-voice-sweep player/app.jsx:11952:22, D-289). N is the
  // file's own count (B-711, `exportCarried` below) — it was the fixed literal 239 for every character.
  exportCarriedUnit: ' fields',
  exportDefaultsLead: "Your character never chose them. These are the format's own defaults, and you can change them wherever you open the file.",
  exportDefaultSocial: 'Social class',
  exportDefaultHook: 'Story hook',
  exportDefaultAttitude: 'Attitude to the Force',
  exportDefaultGender: 'Gender',
  exportNothing: 'Nothing — everything made the trip.',
  exportDownload: '⬇ DOWNLOAD',
};

// THE EXPORT RECEIPT'S TWO SENTENCE LANES, SPLIT ONCE (B-772) — the engine writes ONE receipt array
// and the page reads it as two registers, so the split lives here and both faces read it. The filter
// on `r.player` is the room's own: the engine's receipt carries rows with no reader line, and a row
// with nothing to say to a player is not a row on this page.
//
// R-CFP-1's TWO SENTENCE LANES ARE THESE TWO, and they are the reason `cf-lines` exists. Every line
// in them is the engine's own `row.player` — running to 139 characters — against an `hp-lval` that is
// 16px semibold, right-aligned and tabular. A SENTENCE IS NOT A LEDGER VALUE, so they take the
// ledger's hairline rhythm WITHOUT its columns, with the engine's own `row.field` as a quiet key
// above its own sentence. Nothing here shortens, re-orders or paraphrases either one.
// THE CARRIED COUNT (R7 B-711) — the fields in the file the player is about to download, counted by the
// export engine in the unit the concept's 239-field example was measured in (`SWRPGOggExport.fieldCount`).
// Both faces read this one expression.
function exportCarried(exported) {
  var X = window.SWRPGOggExport;
  return X.fieldCount(exported.xml) + CF_COPY.exportCarriedUnit;
}

function exportReceiptRows(exported) {
  var reader = ((exported && exported.rows) || []).filter(function (r) { return r && r.player; });
  return {
    deps: reader.filter(function (r) { return r.status === 'DEPENDENCY'; }),
    drops: reader.filter(function (r) { return r.status !== 'DEPENDENCY'; }),
  };
}

// THE FORMAT'S OWN DEFAULTS, RESOLVED ONCE (B-772) — `defaultNames` first, then `defaults`, which is
// the room's own resolution order and the one the approved board reproduced. The four labels are the
// room's words (`CF_COPY` above) and the four values are the ATTESTATION's, so this is a key/value
// register in the strict sense and it takes the ledger (R-CFP-1 ratifies it there). A key whose
// value the attestation does not carry drops out rather than printing an empty row.
function exportDefaultRows(attestation) {
  var a = attestation || {}, n = a.defaultNames || {}, d = a.defaults || {};
  return [
    [CF_COPY.exportDefaultSocial, n['Class/ClassKey'] || d['Class/ClassKey']],
    [CF_COPY.exportDefaultHook, n['Hook/HookKey'] || d['Hook/HookKey']],
    [CF_COPY.exportDefaultAttitude, n['Attitude/AttitudeKey'] || d['Attitude/AttitudeKey']],
    [CF_COPY.exportDefaultGender, d['Description/Gender']],
  ].filter(function (kv) { return kv[1]; });
}

// THE RECEIPT'S FIGURES, DERIVED ONCE (B-771) — the five counts and the one boolean both faces of
// the match receipt read. They were computed inside `MatchReport`; they are computed here because
// the paper receipt needs the same five and a second copy of `weapons.length + gear.length +
// (armor ? 1 : 0)` is the same drift `CF_COPY` exists to prevent, one level up from the sentences.
// NOTHING IS DERIVED THAT THE ROOM DID NOT ALREADY DERIVE — the expressions are the shipped ones,
// moved. `clean` is the report's own branch test, and the ADAPTED / NOT IN OUR DATA counts stay
// where they are (`rep.adapted.length`), because the room reads them straight off the engine.
function matchReceiptFigures(result) {
  var out = result.out, sheet = result.sheet, rep = out.oggSource.report;
  return {
    out: out, sheet: sheet, rep: rep,
    clean: (rep.adapted.length === 0 && rep.absent.length === 0),
    talents: (sheet.talents || []).length,
    specs: (sheet.specializationIds || []).length,
    gear: sheet.equipment.weapons.length + sheet.equipment.gear.length + (sheet.equipment.armor ? 1 : 0),
    skills: Object.keys(sheet.skills).length,
    portrait: !!out.portraitData,
  };
}

// RELEASE R3 (D-258) — the player's host for the shared backup screen: its band (the app's 1024 seam) and its maker.
function PlayerBackup({ onBack }) {
  var band = useLayoutMode();
  var UI = window.SWRPGUI;
  if (!UI || !UI.BackupScreen || !window.SWRPGBackup) return null;
  return React.createElement(UI.BackupScreen, { maker: PLAYER_BAR_COPY.app, band: band, onBack: onBack, backLabel: CF_COPY.back });
}

function ImportOgg({ onBack, onComplete, paper, onBackup }) {
  var _gd = useState(null), gameData = _gd[0], setGameData = _gd[1];
  var _idx = useState(null), index = _idx[0], setIndex = _idx[1];
  var _ph = useState('loading'), phase = _ph[0], setPhase = _ph[1];   // loading|drop|report|error|adding
  var _res = useState(null), result = _res[0], setResult = _res[1];
  var _drag = useState(false), dragOver = _drag[0], setDragOver = _drag[1];
  // EXPORT lane (build card `ogg-export-build-card`; drawing: Docs/mockups/ogg-export.html @ 03b79cad,
  // Jason-approved at v3). The room learns a second job — same doors, same route, one more phase.
  var _exp = useState(null), exported = _exp[0], setExported = _exp[1];
  var _roster = useState(null), roster = _roster[0], setRoster = _roster[1];
  var _expErr = useState(null), exportErr = _expErr[0], setExportErr = _expErr[1];
  var accent = '#4EAAFF';
  // B-770 — the app's 1024 seam, as `data-band` on the paper shell. Called UNCONDITIONALLY and above
  // every branch, because the `if (paper)` fork is a RETURN and a hook behind a return is the one
  // way this pattern breaks. The dark face computes it and ignores it, which costs a number.
  var filesMode = useLayoutMode();

  useEffect(function() {
    var alive = true;
    // The CSG key map joins the boot because WITHOUT IT a community-species character imports with
    // no species at all (B-282 as re-aimed, R-BPI-2): our records store those species under a
    // spreadsheet row id and the file names a mnemonic key. It is fetched rather than bundled, the
    // same posture as the other canonical data, and a failure to load is NOT fatal — 115 dataset
    // keys still resolve on their own and every non-community character is unaffected, so a missing
    // map degrades to yesterday's behaviour instead of breaking the door.
    var csgMapP = fetch('/lib/shared/oggCsgMap.json').then(function(r) { return r.ok ? r.json() : null; })
                    .catch(function() { return null; });
    // The EXPORT engine's two data dependencies. The attestation is what the format IS (element
    // order, required children, the background defaults); the skill vocabulary maps our sheet keys
    // to OggDude's. Both are needed only by the export lane, and both fail soft — the lane reports
    // an engine error rather than the room refusing to open, so IMPORT is never taken down by them.
    var attP = fetch('/lib/shared/oggAttestation.json').then(function(r) { return r.ok ? r.json() : null; })
                 .catch(function() { return null; });
    var vocabP = fetch('public/data/canonical/skills.vocab.json').then(function(r) { return r.ok ? r.json() : null; })
                   .catch(function() { return null; });
    // release-r5-crafting-sm (R-R5CC-3): the crafting templates' ogg_keys map a file's schematics; fail soft (every
    // schematic is then LEFT IN THE FILE), so import is never taken down by it.
    var craftTplP = fetch('public/data/canonical/crafting_templates.json').then(function(r) { return r.ok ? r.json() : null; })
                      .catch(function() { return null; }); var rewP = fetch('public/data/canonical/rewards.json').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; }), spendP = fetch('public/data/canonical/crafting_spend.json').then(function(r) { return r.ok ? r.json() : null; }).catch(function() { return null; });   // R6 lane C: the rewards' keys + Maker's Mark's ogg_key; fail soft (every reward then stays in the file)
    // release-r5-rigger (ruling CONDITION 2, D-R5R2): the vehicle library, whose `key` is OggDude's vehicle key,
    // so the export can write a Signature Vehicle's ship. Its shard list and cache stamp are VehicleLibraryController.js's own
    // globals (one list, never a copy); fail soft to null, and the export then hands no Signature Vehicle (the file as before).
    var vehLibP = (Array.isArray(window.VEHICLE_SHARDS) ? Promise.all(window.VEHICLE_SHARDS.map(function(slug) {
      return fetch('public/data/vehicle-library/' + slug + '.json?v=' + (window.VEHICLE_CACHE_VERSION || ''))
        .then(function(r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    })).then(function(shards) { return shards.reduce(function(all, d) { return all.concat((d && d.entries) || []); }, []); })
      : Promise.resolve(null)).catch(function() { return null; });
    // release-r6-companions-npcs Q3 (a): the creature library, so an imported companion that IS a library creature lands as a
    // `ref`; fail soft to null (every companion then lands as its own record), so import is never taken down by it.
    var npcsP = fetch('public/data/canonical/npcs.json').then(function(r) { return r.ok ? r.json() : null; })
                  .catch(function() { return null; });
    // release-r6-companions-npcs: a crafted droid companion exports through droidOf, which needs the spend rows; the crafting
    // pane's one loader (fail soft to null: the droid is then left out of the file and the receipt says so).
    // R7 R-R7-O2: the motivation registry, so a file's motive keys join both ways (fail soft to null: each is then told)
    var motP = fetch('public/data/canonical/motivations.json').then(function(r) { return r.ok ? r.json() : null; })
                 .catch(function() { return null; });
    // R7 B-1432: the qualities vocabulary, so a crafted weapon's qualities are written by OggDude's key (fail soft to null:
    // each quality is then told as left off)
    var qualP = fetch('public/data/canonical/qualities.vocab.json').then(function(r) { return r.ok ? r.json() : null; })
                  .catch(function() { return null; });
    Promise.all([loadQuickBuildData(), loadCanonicalRegistries(), loadCanonicalCharacterBuild(), csgMapP, attP, vocabP, craftTplP, vehLibP, npcsP,
                 loadCraftingRegistries(), rewP, spendP, motP, qualP]).then(function(res) {
      if (!alive) return;
      var cb = res[2], reg = res[1], csgMap = res[3], OGG = window.SWRPGOggImport;
      // F-100 M11 R-M11B-3: the index joins against the BOOK view (the imported sheet carries no stamp), never `cb`,
      // the device's folded view — a pack's record under a book id would steal that book key's join.
      var book = window._canonicalCharbuildBase;
      if (!cb || !book || !reg || !OGG) { setPhase('error'); setResult({ out: { error: 'boot' } }); return; }
      setGameData({ charbuild: cb, narrativeMechanics: res[0].narrativeMechanics, canonical: reg,
                    csgMap: csgMap, attestation: res[4],
                    skillVocab: (res[5] && res[5].entries) || [], craftTemplates: res[6] || null, vehicleLibrary: res[7] || null,
                    npcs: (res[8] && res[8].npcs) || null, craftRegs: res[9] || null,
                    rewards: res[10] || null, craftSpend: res[11] || null, motivations: res[12] || null,
                    qualityVocab: (res[13] && res[13].entries) || null });
      setIndex(OGG.importIndex(book, reg, csgMap));
      setPhase('entry');
    }).catch(function() { if (alive) { setPhase('error'); setResult({ out: { error: 'boot' } }); } });
    return function() { alive = false; };
  }, []);

  function handleText(text, filename) {
    var OGG = window.SWRPGOggImport;
    var out = OGG.importToCharacter(text, { index: index, adaptMap: OGG.ADAPT_MAP, emptyCharacter: createEmptyCharacter, importedAt: new Date().toISOString().slice(0, 10),
                                            craftTemplates: (gameData && gameData.craftTemplates) || null,
                                            skillVocab: (gameData && gameData.skillVocab) || null, npcs: (gameData && gameData.npcs) || null,
                                            rewards: (gameData && gameData.rewards) || null, craftSpend: (gameData && gameData.craftSpend) || null,
                                            motivations: (gameData && gameData.motivations) || null });   // R7 R-R7-O2
    if (out.error) { setResult({ out: out, filename: filename }); setPhase('error'); return; }
    var sheet;
    try {
      sheet = normalizeCreated(out.character, out.speciesRecord, null, gameData, 'ogg-import');
      // F-100 M12 B-1244: the signature abilities' V1–V7, on the sheet just made (the importer's one legality step) — what
      // fails is left off the sheet and named on the receipt; nothing is charged.
      OGG.settleSigAbilities(out, sheet, gameData && gameData.charbuild);
      OGG.settleSchematics(out, sheet);   // R-R5CC-3: the held schematics → sheet.craft_schematics
      OGG.settleRewards(out, sheet);      // R6 lane C: the rewards → sheet.rewards + the ledger
      OGG.settleCompanions(out, sheet);   // release-r6-companions-npcs: the file's owned Adversaries → sheet.companions
      // release-r5-f1 D (release-r5-rigger RIDERS; F1 S4): the Signature Vehicle, settled with the export's own rigger inputs — the
      // device's resolver context (the one builder), the Ship Yard state and the vehicle library; no library → nothing settled
      if (gameData && gameData.vehicleLibrary && OGG.settleRigger) {
        var rctx = yardResolveCtx([{ id: '__import', sheetData: sheet }].concat(window.SWRPGRoster ? window.SWRPGRoster.loadSavedCharacters() : []));
        OGG.settleRigger(out, sheet, { characterId: '__import', vessels: window.SWRPGVessels ? window.SWRPGVessels.load() : [], vehicles: gameData.vehicleLibrary,
          talents: ((rctx.characters || [])[0] || {}).talents || {}, roster: rctx.characters || [] });
      }
      OGG.settleXpCounted(out, sheet);   // R7 B-1038 (ADDENDUM 33): LAST — the file's XP spent vs this sheet's, told when they differ
    }
    catch (e) { setResult({ out: { error: 'assemble' }, filename: filename }); setPhase('error'); return; }
    setResult({ out: out, sheet: sheet, filename: filename });
    setPhase('report');
  }
  function readFile(file) {
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function(e) { handleText(String((e.target && e.target.result) || ''), file.name); };
    reader.onerror = function() { setResult({ out: { error: 'unreadable' }, filename: file.name }); setPhase('error'); };
    reader.readAsText(file);
  }
  function addToRoster() {
    if (!result || !result.sheet) return;
    setPhase('adding');
    var out = result.out, sheet = result.sheet;
    sheet.oggSource = out.oggSource;
    reEncodePortrait(out.portraitData).then(function(p) {
      if (p) sheet.portrait = p;
      onComplete(sheet, { gameLine: out.gameLine, filename: result.filename });
    });
  }

  // EXPORT — the engine does the work and AUTHORS the reader's lines; this only arranges them.
  // deps are passed explicitly rather than left to the module's browser branch: our canonical
  // globals are indexed BY ID and the engine wants arrays, so handing it the globals directly
  // would throw inside byId(). One conversion, at the one call site.
  function toArray(x) { return Array.isArray(x) ? x : (x ? Object.keys(x).map(function(k) { return x[k]; }) : []); }
  // R7 B-996: the file reads the SHEET's charbuild, its own stamp (`sheet.mods`) over the pristine base, as the sheet itself
  // renders (resolveForStamp, F-9 B3), never `gameData.charbuild`, the toggle view: with the toggle off, a reSpecialized
  // sheet's specs and `respec_*` talents had no record there and left the file without a receipt row. A stamped sheet
  // waits for its packs and comes back with the view; an unstamped one reads the pristine base at once.
  function runExport(record, stampView) {
    var M = window.SWRPGMods, stamped = record && (record.sheetData || record.selectedChar);
    if (stampView === undefined && M && M.resolveForStamp && stamped && stamped.mods && stamped.mods.length) {
      M.resolveForStamp(stamped.mods, window._canonicalCharbuildBase).catch(function() { return null; })
        .then(function(view) { runExport(record, view || null); });
      return;
    }
    var cb = stampView || window._canonicalCharbuildBase || (gameData && gameData.charbuild) || {};
    var X = window.SWRPGOggExport;
    var character = stamped;
    // B-852: the file carries the items THIS SHEET owns (its user: stamps over the pristine base) — the
    // seam the sheet itself renders through — never the DISCOVERY view the room loaded (`gameData.canonical`).
    var reg = sheetItemRegistries(character) || {};
    if (!X || !character) { setExportErr('engine'); setPhase('exported'); return; }
    var fileName = (String(record.name || 'Character').replace(/[^A-Za-z0-9]+/g, '_') || 'Character') + '.xml';
    var rows = [];
    var xml;
    try {
      xml = X.exportCharacter(character, {
        fileName: fileName, receipt: rows,
        deps: {
          attestation: gameData.attestation, csgMap: gameData.csgMap,
          species: toArray(cb.species), careers: toArray(cb.careers),
          specializations: toArray(cb.specializations), talents: toArray(cb.talents),
          signatureAbilities: toArray(cb.signatureAbilities),   // F-100 M12 B-1244: the file writes the sheet's abilities
          forcePowers: toArray(cb.forcePowers),   // R7 B-1225: the file writes the sheet's Force powers
          motivations: (gameData && gameData.motivations) || null,   // R7 R-R7-O2: the Motivation's motive keys
          weapons: toArray(reg.weapons), armor: toArray(reg.armor), gear: toArray(reg.gear),
          skillVocab: gameData.skillVocab,
          qualityVocab: gameData.qualityVocab || null,   // R7 B-1432: a crafted weapon's Quality keys
          craftTemplates: (gameData.craftTemplates && gameData.craftTemplates.templates) || null,   // the schematic rows' names
          rewards: gameData.rewards || null, craftSpend: gameData.craftSpend || null,   // R6 lane C: the reward rows' keys
          // release-r6-companions-npcs: a `ref` companion's creature, and a crafted droid's registries (droidOf)
          npcs: gameData.npcs || null,
          droid: gameData.craftRegs && gameData.craftRegs.tpl ? { templates: gameData.craftRegs.tpl.templates, spend: gameData.craftRegs.spend,
            cybernetics: window.SWRPGCompanions.cyberneticsOf(reg.gear) } : null,   // release-r6-companions-d365: Modular Hardware's implant
        },
        // release-r5-rigger (card ADDENDUM 2 C3; ruling CONDITION 2): the Signature Vehicle. The engine decides through the one bond
        // reader and holder rule over the device's resolver context (the one builder) and Ship Yard state; no library → none handed.
        rigger: gameData.vehicleLibrary ? {
          characterId: record.id, resolveCtx: yardResolveCtx([record]),
          vessels: window.SWRPGVessels ? window.SWRPGVessels.load() : [], vehicles: gameData.vehicleLibrary,
        } : undefined,
      });
    } catch (e) {
      setExportErr(String((e && e.message) || e));
      setPhase('exported');
      return;
    }
    setExportErr(null);
    setExported({ xml: xml, rows: rows, record: record, fileName: fileName });
    setPhase('exported');
  }

  function downloadExport() {
    if (!exported) return;
    var blob = new Blob([exported.xml], { type: 'application/xml' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = exported.fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function() { URL.revokeObjectURL(url); }, 0);
  }

  // THE ROOM'S PHASE TITLE, WRITTEN ONCE. Both faces print it — the dark face beside its Back
  // control, the paper face as the running head's folio — so it is one expression and not two.
  // The paper face takes it AS THE PRODUCT COMPUTES IT, which is the one place the board and the
  // room disagree and it is reported rather than reconciled in silence: frame U draws the EXPORT
  // ERROR with the folio reading `IMPORT — MATCH REPORT`, because the board's `phase=` argument is
  // the DRAWING's state name and the export error is `phase === 'exported'` in the product. Taking
  // the board's literal there would print a false label on a real screen, so the room's own
  // expression wins (C-MU-4: the standards and the product's own truth outrank a drawing) and the
  // export error's folio reads `EXPORT — REPORT`. Frames M and N are unaffected — their phase IS
  // `error` and the expression falls to `IMPORT — MATCH REPORT` exactly as drawn.
  var phaseTitle = phase === 'entry' || phase === 'loading' ? 'CHARACTER FILES'
    : phase === 'drop' ? 'IMPORT FROM OGGDUDE'
    : phase === 'pick' ? 'EXPORT — PICK A CHARACTER'
    : phase === 'exported' ? 'EXPORT — REPORT'
    : 'IMPORT — MATCH REPORT';
  // BACK steps out of the LANE before it steps out of the ROOM. A player who picked EXPORT and
  // wants IMPORT should not have to leave and come back — and the route is unchanged either way, so
  // nothing about `/player/import` moves (R-OEC-1). Hoisted out of `surfTop` so the paper runhead's
  // back control IS this handler and not a second one with the same body.
  function backOut() {
    if (phase === 'pick' || phase === 'exported' || phase === 'drop') {
      setExported(null); setExportErr(null); setResult(null); setPhase('entry');
    } else { onBack(); }
  }
  // THE TWO LANES' ACTS, hoisted for the same reason: one handler per lane, read by both faces.
  function enterImportLane() { setPhase('drop'); }
  function enterExportLane() { setRoster(loadSavedCharacters() || []); setPhase('pick'); }
  // THE TWO WAYS BACK OFF A FAILURE PAGE, and they are DIFFERENT ways on purpose — the import error
  // returns to the dropzone (choose another file), the export error returns to the pick list (pick
  // another character). Hoisted unchanged; the paper face presses the same two.
  function importErrBack() { setResult(null); setPhase('drop'); }
  function exportErrBack() { setExportErr(null); setPhase('pick'); }
  // THE DROP TARGET'S FOUR HANDLERS, hoisted whole. `dragOver` is a state in the room's source but
  // not one of its PHASES, which is why the census's phase list does not name it and why the board
  // called frame E "beyond the task and disclosed" — it is nonetheless a real drawn state and this
  // row builds it (R-CFP-4). One set of handlers, two faces.
  function dropOver(e) { e.preventDefault(); setDragOver(true); }
  function dropLeave() { setDragOver(false); }
  function dropDrop(e) {
    e.preventDefault(); setDragOver(false);
    readFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
  }
  function dropChoose(e) { readFile(e.target.files && e.target.files[0]); }

  var surfTop = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <button data-tour-id="p-import-back" onClick={backOut}
        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 15, fontFamily: 'var(--font-body)' }}>{CF_COPY.back}</button>
      <span style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.08em', fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
        {phaseTitle}
      </span>
    </div>
  );

  // The MONOGRAM ring — the roster idiom this app already uses, so a character looks the same here
  // as it does everywhere else.
  function mono(name) {
    return (
      <span style={{ width: 38, height: 38, borderRadius: '50%', flex: '0 0 auto', display: 'flex',
        alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.18)',
        background: 'rgba(255,255,255,0.04)', fontFamily: 'var(--font-display)', fontSize: 15,
        color: 'rgba(255,255,255,0.85)' }}>{String(name || '?').slice(0, 1).toUpperCase()}</span>
    );
  }

  // R-ERP-1 (`export-roster-portraits`) — the export row's identity slot shows the character's
  // PORTRAIT when the record carries one, and falls back to the monogram above exactly as before.
  // This is the pattern `SheetIdentity` and the roster card already ship, applied to one more row:
  // the row's code previously never looked at `portrait` at all, so a character WITH a face still
  // drew a letter. Same 38px circle, same ring, same flex — an image swaps into the existing slot.
  // THE COMMERCIAL_MODE GATE IS CARRIED OVER DELIBERATELY (roster card, player/app.jsx:1229): the
  // portraits are art we do not hold redistribution rights to, so the build that would ship them
  // commercially must fall to the monogram here as it does everywhere else. Dropping the gate on
  // one row is how a seam quietly stops being a seam.
  function rowAvatar(c) {
    var src = c && window.SWRPGRecordPortrait.recordPortraitOf(c);   // B-931: the one fold (R-PB9-4), not `c.portrait`
    if (!COMMERCIAL_MODE && src) {
      return (
        <span aria-hidden="true" style={{ width: 38, height: 38, borderRadius: '50%', flex: '0 0 auto',
          border: '1px solid rgba(255,255,255,0.18)', backgroundImage: `url('${src}')`,
          backgroundSize: 'cover', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat' }} />
      );
    }
    return mono(c && c.name);
  }

  function sect(title, children, tourId) {
    return (
      <div data-tour-id={tourId} style={{ marginTop: 26 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: '0.14em',
          color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>{title}</div>
        {children}
      </div>
    );
  }

  function body() {
    if (phase === 'loading') return <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>{CF_COPY.loading}</div>;

    // ── ENTRY — the room's two jobs, named in the format's own terms (Amendment 1: format-named,
    //    interchange-framed, and never a list of tools we have not run) ──────────────────────────
    if (phase === 'entry') {
      var card = function (id, glyph, label, onClick) {
        return (
          <button key={id} data-tour-id={id} onClick={onClick}
            style={{ flex: '1 1 200px', minHeight: 116, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 10, padding: '22px 18px',
              borderRadius: 14, cursor: 'pointer', color: '#fff',
              border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)',
              fontFamily: 'var(--font-display)', letterSpacing: '0.1em', fontSize: 13 }}>
            <span style={{ fontSize: 26, opacity: 0.85 }}>{glyph}</span>
            <span>{label}</span>
          </button>
        );
      };
      return (
        <div data-tour-id="p-import-entry" style={{ maxWidth: 620, margin: '0 auto', padding: '32px 20px' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8, color: '#fff' }}>{CF_COPY.entryTitle}</h3>
          {/* RELEASE R3 (card §4, D-258 — drawn on this live screen, frame CF): the one line under the title. */}
          {onBackup && (
            <p data-role="cf-backup-line" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 24 }}>
              To keep everything, not just characters:{' '}
              <a href="/player/backup" onClick={function (e) { e.preventDefault(); onBackup(); }}
                style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>Back up everything</a>
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {card('p-import-entry-import', '⬇', 'IMPORT', enterImportLane)}
            {card('p-import-entry-export', '⬆', 'EXPORT', enterExportLane)}
          </div>
        </div>
      );
    }

    // ── EXPORT · PICK ────────────────────────────────────────────────────────────────────────
    if (phase === 'pick') {
      var rows = roster || [];
      return (
        <div data-tour-id="p-export-pick" style={{ maxWidth: 620, margin: '0 auto', padding: '32px 20px' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8, color: '#fff' }}>{CF_COPY.pickTitle}</h3>
          {rows.length === 0 ? (
            <div data-tour-id="p-export-empty" style={{ textAlign: 'center', padding: '44px 20px',
              border: '1px dashed rgba(255,255,255,0.14)', borderRadius: 14 }}>
              <p style={{ color: '#fff', marginBottom: 6, fontFamily: 'var(--font-display)' }}>{CF_COPY.pickEmptyTitle}</p>
              <p style={{ color: 'rgba(255,255,255,0.55)' }}>{CF_COPY.pickEmptyBody}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map(function(c) {
                return (
                  <button key={c.id} data-tour-id="p-export-row" onClick={function() { runExport(c); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, width: '100%',
                      padding: '10px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)', color: '#fff' }}>
                    {rowAvatar(c)}
                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 15 }}>{c.name || CF_COPY.pickUnnamed}</span>
                      <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)' }}>
                        {[c.species, c.career].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 18 }}>›</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // ── EXPORT · THE REPORT ──────────────────────────────────────────────────────────────────
    // Every line here is the ENGINE's, rendered VERBATIM. The page never paraphrases, condenses or
    // drops one (build card CONTRACT 4) — which is why there is no text-shortening anywhere below.
    if (phase === 'exported') {
      if (exportErr || !exported) {
        return (
          <div data-tour-id="p-export-error" style={{ maxWidth: 520, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{CF_COPY.errGlyph}</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 10, color: '#fff' }}>{CF_COPY.exportErrTitle}</h3>
            <button onClick={exportErrBack} style={btn(accent, true)}>{CF_COPY.exportErrBtn}</button>
          </div>
        );
      }
      // B-772 — the two registers and the defaults ledger are DERIVED ABOVE THIS COMPONENT now
      // (`exportReceiptRows` / `exportDefaultRows`), because the paper face needs the same three and
      // a second copy of `r.status === 'DEPENDENCY'` or of the `defaultNames`-then-`defaults`
      // resolution order is the same drift `CF_COPY` exists to prevent, one level up from the
      // sentences. The expressions are the shipped ones, moved; nothing new is derived.
      var _xr = exportReceiptRows(exported);
      var deps = _xr.deps;
      var drops = _xr.drops;
      var defaults = exportDefaultRows(gameData && gameData.attestation);
      var line = function(t, i) {
        return <li key={i} style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, marginBottom: 8, maxWidth: '72ch' }}>{t}</li>;
      };
      return (
        <div data-tour-id="p-export-report" style={{ maxWidth: 620, margin: '0 auto', padding: '32px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#fff' }}>
            <span style={{ color: '#6BD68A', fontSize: 18 }}>{CF_COPY.exportTick}</span>
            <span><b>{exported.record && exported.record.name}</b>{CF_COPY.exportReady}</span>
          </div>

          {sect(CF_COPY.headCarried, (
            <p style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, maxWidth: '72ch' }}>
              <b>{exportCarried(exported)}</b>
            </p>
          ), 'p-export-carried')}

          {sect(CF_COPY.headDefaults, (
            <div>
              <p style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 10, maxWidth: '72ch' }}>
                {CF_COPY.exportDefaultsLead}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {defaults.map(function(kv, i) {
                  return (
                    <li key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16,
                      padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ color: 'rgba(255,255,255,0.55)' }}>{kv[0]}</span>
                      <span style={{ color: '#fff' }}>{kv[1]}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ), 'p-export-defaults')}

          {deps.length > 0 && sect(CF_COPY.headCommunity, (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>{deps.map(function(r, i) { return line(r.player, i); })}</ul>
          ), 'p-export-community')}

          {sect(CF_COPY.headDropped, (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {drops.length === 0
                ? <li style={{ color: 'rgba(255,255,255,0.5)' }}>{CF_COPY.exportNothing}</li>
                : drops.map(function(r, i) { return line(r.player, i); })}
            </ul>
          ), 'p-export-drops')}

          <button data-tour-id="p-export-download" onClick={downloadExport}
            style={Object.assign({ marginTop: 26, width: '100%', minHeight: 48 }, btn(accent, true))}>
            {CF_COPY.exportDownload} {exported.fileName}
          </button>
        </div>
      );
    }
    if (phase === 'error') {
      var reason = result && result.out && result.out.error;
      return (
        <div data-tour-id="p-import-error" style={{ maxWidth: 520, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{CF_COPY.errGlyph}</div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 10, color: '#fff' }}>{CF_COPY.importErrTitle}</h3>
          <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 24 }}>
            {reason === 'boot' ? CF_COPY.importErrBoot : CF_COPY.importErrBad}
          </p>
          <button onClick={importErrBack} style={btn(accent, true)}>{CF_COPY.importErrBtn}</button>
        </div>
      );
    }
    if (phase === 'report' || phase === 'adding') return <MatchReport result={result} accent={accent} onCancel={onBack} onAdd={addToRoster} adding={phase === 'adding'} />;
    // phase 'drop'
    return (
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '32px 20px' }}>
        <div
          data-tour-id="p-import-dropzone"
          onDragOver={dropOver}
          onDragLeave={dropLeave}
          onDrop={dropDrop}
          style={{ border: '2px dashed ' + (dragOver ? accent : 'rgba(255,255,255,0.18)'), borderRadius: 14, padding: '48px 28px', textAlign: 'center', background: dragOver ? accent + '10' : 'rgba(255,255,255,0.02)', transition: 'all .15s' }}>
          <div style={{ fontSize: 44, marginBottom: 14, opacity: 0.8 }}>{CF_COPY.dropGlyph}</div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 10, color: '#fff' }}>{CF_COPY.dropTitle}</h3>
          <label style={Object.assign({ display: 'inline-block', cursor: 'pointer' }, btn(accent, true))}>
            {CF_COPY.dropBtn}
            <input data-tour-id="p-import-file" type="file" accept=".xml,text/xml,application/xml" style={{ display: 'none' }}
              onChange={dropChoose} />
          </label>
        </div>
      </div>
    );
  }

  var legacyBody = (
    <div data-tour-id="p-import-screen" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: '#0b0b0d', color: '#fff', overflowY: 'auto' }}>
      {surfTop}
      <div style={{ flex: 1 }}>{body()}</div>
    </div>
  );

  if (!paper) return legacyBody;

  /* ══ THE PAPER RECEIPT — ONE KERNEL, ONE SWITCH (B-770; R-CFP-2) ════════════════════════════════
     Built from the APPROVED board `lab/holocron/files.html` @ lab `e92c9b0a`, Jason 2026-09-21
     ("Character files approved"; LAB_NOTES §200 for how it was drawn, §201 for what the approval
     covers). Frames this row draws: A · B · C · D · E · F · M · N · U · Y.
     B-771 ADDED G · H · I · J · K · L (the match receipt) and B-772 ADDED O · P · Q (the pick list
     and its empty state) and R · S · T (the export receipt and the download act) — so with B-772
     EVERY PHASE OF THIS ROOM IS DRAWN PAPER, `FILES_INTERIM_ZONES` is empty and `paperZone()` answers
     null for everything. The only frames the concept ruling leaves undrawn are W and X, which
     R-CFP-5 declines to build and rides on B-777.

     THIS IS THE SAME COMPONENT. Everything above this line — the six-promise boot, `handleText`,
     `readFile`, `addToRoster`, `runExport`, `downloadExport`, every `useState`, every
     `data-tour-id` — is untouched and is what the paper face presses. `SellSheet`, `VesselSheet` (until
     the Ship Yard's S5 flip deleted it), `Store` and `FCSkillPicker` take `paper` exactly this way: one `if (paper)` fork, the legacy
     body NAMED and returned above it rather than deleted, no second kernel and no wrapper
     component. The switch changes the FACE; it changes nothing the room DOES.

     IT IS A RECEIPT AND NOT A COLOPHON (R-CFP-1). The word "colophon" is retired for this surface:
     a colophon records a finished thing, and the match report is read BEFORE the commit with Cancel
     beside "Add to my roster".

     AND EVERY WORD ON THESE FRAMES IS THE ROOM'S (R-CFP-3). Not one sentence below is authored
     here — `CF_COPY` is the one table both faces read, `phaseTitle` is the room's own expression,
     and the ONE fenced gloss Jason approved (a count on a section head) belongs to B-771/B-772's
     frames and appears nowhere on this row's. */

  // THE PHONE / DESK FORK arrives from JS, as the landing, the sheet and both CREATE doors take it
  // (`useLayoutMode()` — the app's 1024 seam), and lands as `data-band` on the shell. There is not
  // one `@media` in the board's CSS and there is none in `files.css`.
  var band = filesMode;

  // THE DESIGNATION — the slash, the maker and the room's own title, over the kit's rule. Drawn on
  // the three phases the board gives one to (entry, loading) and withheld from the two that are a
  // single enclosure filling the page (drop, error), exactly as frames A/C and D/M/N/U draw them.
  //
  // B-771 PARAMETERISES IT AND CHANGES NOTHING IT DREW. The board's `desig()` takes the maker and
  // the title as arguments and the receipt passes different ones — `IMPORTED` over the character's
  // own name, which is frames G · H · I · J · K · L — so this is one builder with two callers rather
  // than a second copy of the slash, the maker and the rule. `paperDesig` below is the entry's call
  // with the entry's two arguments, so frames A · B · C · Y draw the same characters they did.
  function paperDesigOf(maker, title) {
    return (
      <React.Fragment>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">{maker}</div>
        </div>
        <h1 className="hp-desig">{title}</h1>
        <div className="hp-desig-rule" />
      </React.Fragment>
    );
  }
  var paperDesig = paperDesigOf(PLAYER_BAR_COPY.app, CF_COPY.entryTitle);

  // ── ENTRY (frames A · B · Y) — the kit's index of doors, with the room's OWN arrows in the row
  //    plate. `hp-row-plate` is the kit's 46px mat and a glyph on a mat is what that slot IS; the
  //    chevron refuses `hp-row-tab`, whose 76px ruled gutter is for a register of numerals (the
  //    board's own paragraph, and `files.css` carries it).
  function paperLane(id, glyph, label, onClick) {
    return (
      <button key={id} type="button" data-tour-id={id} className="hp-row" onClick={onClick}>
        <span className="hp-row-plate"><span className="cf-lane">{glyph}</span></span>
        <span className="hp-row-body"><span className="hp-row-desig">{label}</span></span>
        <span className="cf-chev">›</span>
      </button>
    );
  }

  // ── THE DROP TARGET (frames D · E · F) — the one concept the paper kit has no unit for. A plate
  //    is a printed CARD; a drop target is an EMPTY FIELD waiting to be filled, so it is the
  //    printed form's ruled enclosure, taking the kit's SELECTION ink when a file is over it.
  //    `data-over` is the drawn state and it is the room's own `dragOver`, not a second flag.
  //
  //    THE ACT IS A `<label>` AND NOT A `<button>`, and that is the app's shape winning over a
  //    drawing (C-MU-4). The board draws `<button class="hp-commit">Choose a file</button>` because
  //    a board draws no interaction; in the room the control must OPEN A FILE PICKER, which is the
  //    hidden `<input type="file" data-tour-id="p-import-file">` it wraps. Swapping the label for a
  //    button would either break the testid the suites drive or need a synthetic click — so the
  //    label wears `hp-commit` and every drawn pixel is the board's.
  function paperDrop() {
    return (
      <div data-tour-id="p-import-dropzone" className="cf-drop"
        data-over={dragOver ? '1' : undefined}
        onDragOver={dropOver} onDragLeave={dropLeave} onDrop={dropDrop}>
        <span className="cf-drop-g">{CF_COPY.dropGlyph}</span>
        <p className="cf-drop-t">{CF_COPY.dropTitle}</p>
        <label className="hp-commit" style={{ cursor: 'pointer' }}>
          {CF_COPY.dropBtn}
          <input data-tour-id="p-import-file" type="file" accept=".xml,text/xml,application/xml"
            style={{ display: 'none' }} onChange={dropChoose} />
        </label>
      </div>
    );
  }

  // ── THE FAILURE PAGE (frames M · N · U) — the drop target's twin and the same enclosure, solid
  //    rather than dashed: a form that came back rejected is still a form, and the reader should
  //    recognise the box they are being sent back to.
  //
  //    THE ERROR FRAMES DRAW A REAL REFUSAL. Frame M is the corpus's own
  //    `Levi__1L_ImperialSeries_Repulsor_Tank.xml` — a `CharVehicle`, not a character, which
  //    `importToCharacter` returns `{ error: 'unreadable' }` for rather than throwing. The corpus
  //    carries exactly ONE file the importer refuses and the board drew that one; the suite drives
  //    that file and never a synthesised failure.
  function paperStop(head, sentence, btnWord, onPress) {
    return (
      <div className="cf-stop">
        <span className="cf-stop-g">{CF_COPY.errGlyph}</span>
        <p className="cf-drop-t" style={{ marginBottom: 10 }}>{head}</p>
        {sentence ? <p className="hp-prose" style={{ margin: '0 auto 18px', maxWidth: 'var(--desk-prose-max)' }}>{sentence}</p> : null}
        <button type="button" className="hp-commit" onClick={onPress}>{btnWord}</button>
      </div>
    );
  }

  /* ── THE MATCH RECEIPT (frames G · H · I · J · K · L; B-771) ──────────────────────────────────
     IT IS A RECEIPT AND NOT A COLOPHON, and this is the screen where that distinction is the whole
     design (R-CFP-1). A colophon records a finished thing; this page is read BEFORE the commit with
     Cancel sitting beside "Add to my roster", and the act row below is therefore PRE-COMMIT
     furniture rather than a footer.

     THE LEDGER READING HOLDS HERE FOR TWO REGISTERS AND THIS ROW BUILDS BOTH. R-CFP-1 ratifies the
     ledger for three of the report's five registers — the format's defaults (B-772's, on the export
     receipt), CAME ACROSS (values), NOT IN OUR DATA (type → name) — and refuses it for the two
     SENTENCE lanes, which are also B-772's (`NEEDS COMMUNITY DATA` and `COULD NOT BE EXPRESSED`, the
     engine's own `row.player` lines). Measured against the approved board: `cf-lines` is emitted
     ZERO times on frames G–L and twice each on R and S, so this row reaches `cf-ledger-l` and
     `cf-prose` and NOT `cf-lines`. Said out loud because the brief for this row expected the
     sentence lanes here, and a rule reported as "reached" that draws nothing is a false claim about
     the cascade.

     WHAT CARRIES A SENTENCE ON *THIS* RECEIPT is EXPERIENCE and FORCE UPGRADES, and they are the
     ROOM's sentences with the engine's numbers in their slots — not ledger values, and not lines the
     page composes. They take `hp-prose` inside a `cf-prose` plate, which is the board's own
     arrangement for exactly that: a plate holds prose OR a ledger and never both, because two right
     edges inside one card reads as a broken card.

     EVERY WORD IS THE ROOM'S (R-CFP-3). `CF_COPY` above holds them, `MatchReport` reads the same
     table, and the four EXPERIENCE fragments and two FORCE UPGRADES fragments are the room's own
     literals in the room's own order. THE SECTION-HEAD CITES ARE NOT THE FENCED GLOSS: every one of
     them — `{matched} of {matched}`, the ADAPTED and NOT IN OUR DATA counts-or-sentences, the force
     upgrade count — is an expression the DARK receipt already prints in its own `gloss` span, so the
     product already shows them. R-CFP-3's one fenced relaxation is spent on the EXPORT receipt's
     four heads, which have no glosses in the product at all, and that is B-772's row.

     THE POPULATED `ADAPTED` ROW IS UNDRAWABLE AND THE CODE FOR IT IS WRITTEN ANYWAY. Zero of the ten
     committed saves adapt anything, so no specimen exists and no frame was drawn; the board carries
     `ADAPTED_TOTAL == 0` as a build-time assert and this row's suite carries the same assert over
     the same corpus. The branch below follows the board's own populated-ledger code path (which
     never executed there either) and keeps the engine's `note`, which the board could not draw and
     the DARK face does render — dropping it would be a paraphrase by omission on a screen whose
     whole claim is verbatim. THE DAY A SAVE ADAPTS SOMETHING the guard stops and the frame is owed.

     THE ANCHOR WRAPPER IS THE APP'S, as it was for the entry: the board draws the receipt's children
     straight into the sheet and the room carries `data-tour-id="p-import-report"`, which
     `smoke_ogg_import` and `smoke_ogg_as9` both drive. One wrapper div, no drawn pixel moved. */
  function paperLedger(rows, left) {
    // `<tbody>` IS WRITTEN AND THE BOARD'S `<table><tr>` IS THE SAME DOM. A browser inserts the
    // implied `tbody` when it parses the board's HTML; JSX has no parser to do it, so it is written.
    // The rendered tree is identical, which is why this is a note and not a delta.
    return (
      <table className={'hp-ledger' + (left ? ' cf-ledger-l' : '')}>
        <tbody>
          {rows.map(function(r, i) {
            // THE CONTINUATION ROW — key deliberately empty, the printed table's own convention
            // (`paper.css`). It carries a SENTENCE rather than a value, so it spans the pair and
            // sets left: a right-aligned sentence hanging off the value column reads as a number
            // that lost its key.
            if (r[0] === '') {
              return <tr key={i}><td className="hp-lval" colSpan="2" style={{ textAlign: 'left' }}>{r[1]}</td></tr>;
            }
            return (
              <tr key={i}>
                <td className="hp-lkey">{r[0]}</td>
                <td className="hp-lval" style={left ? { textAlign: 'left', fontWeight: 400 } : undefined}>{r[1]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  function paperSechead(glyph, head, cite) {
    // `data-stack="1"` UNCONDITIONALLY, as the board applies it: this room's longest head is 48
    // characters against a 360px measure at 390 and `hp-cite` is `white-space:nowrap`, so head and
    // cite cannot share a line and the rule between them collapses. The same head is the same length
    // at 1440, and a rule that fires at one band is a rule nobody re-reads at the other. The KIT's
    // fix is B-774 (`min-width:0` + `flex-wrap` on `hp-sechead`, or shorter copy, and copy is
    // Jason's); this is the board's local reading, ported (R-CFP-6).
    // B-772 — A NULL GLYPH IS A REAL CASE AND NOT A MISSING ARGUMENT. The match receipt's five heads
    // each carry a mark the dark face paints in its own ink; the EXPORT receipt's four carry none at
    // all, in the product and on the approved board alike (`<h2>CARRIED</h2>` against
    // `<h2>✓ CAME ACROSS</h2>`). So the glyph is optional rather than invented, and the truthy path
    // is left exactly as B-771 wrote it — the match receipt's `h2` holds the same three children it
    // held before this row, which is why this is one builder with two callers and not two builders.
    return (
      <div className="hp-sechead" data-stack="1">
        <h2>{glyph ? <React.Fragment>{glyph} {head}</React.Fragment> : head}</h2>
        {cite === null || cite === undefined ? null : <span className="hp-cite">{cite}</span>}
      </div>
    );
  }
  function paperReport() {
    var F = matchReceiptFigures(result);
    var sheet = F.sheet, rep = F.rep;
    var ca = [[CF_COPY.wordSpecies, sheet.species], [CF_COPY.wordCareer, sheet.career]];
    if (sheet.specialization) ca.push([CF_COPY.wordSpecialization, sheet.specialization]);
    // ONE CONDITIONAL COVERS BOTH ROWS, because that is the room's own shape: the dark face draws
    // `{talents} talents · {n} specialization(s)` as ONE line gated on the talent count, so a
    // character with specializations and no talents shows neither. Re-set as two ledger rows, gated
    // together, rather than split into two independent rows the room never had.
    if (F.talents) {
      ca.push([CF_COPY.wordTalents, String(F.talents)]);
      ca.push([CF_COPY.wordSpecialization + (F.specs === 1 ? '' : 's'), String(F.specs)]);
    }
    ca.push([CF_COPY.wordGear, String(F.gear)]);
    ca.push([CF_COPY.wordSkills, String(F.skills)]);
    ca.push(['', CF_COPY.reportTail + (F.portrait ? CF_COPY.reportPortrait : '')]);
    if (fcCreditsLine(rep)) ca.push(['', fcCreditsLine(rep)]);   // R7 R-R7-O1: a below-zero balance is told (ADDENDUM 15)
    if (fcXpCountedLine(rep)) ca.push(['', fcXpCountedLine(rep)]);   // R7 B-1038: the file's XP spent vs the sheet's (ADDENDUM 33)
    return (
      <div data-tour-id="p-import-report">
        {paperDesigOf(CF_COPY.reportEyebrow, sheet.name || CF_COPY.reportNoName)}
        <p className="hp-none" style={{ margin: '8px 0 0' }}>{CF_COPY.reportFrom}{result.filename || CF_COPY.reportFromFallback}</p>
        {F.clean ? (
        <div className="hp-plate" data-pad="read">
          <p className="hp-lead" style={{ margin: 0 }}>{CF_COPY.reportClean}</p>
        </div>
        ) : null}

        {paperSechead(CF_COPY.glyphMatched, CF_COPY.headMatched, rep.matched + ' of ' + rep.matched)}
        <div className="hp-plate" data-pad="data">{paperLedger(ca, false)}</div>

        {paperSechead(CF_COPY.glyphAdapted, CF_COPY.headAdapted, rep.adapted.length || CF_COPY.adaptedNone)}
        {rep.adapted.length ? (
          <div className="hp-plate" data-pad="data">
            {paperLedger(rep.adapted.map(function(a) {
              return [a.from, a.note
                ? <React.Fragment>{a.to} <span className="hp-sep">· {a.note}</span></React.Fragment>
                : a.to];
            }), true)}
          </div>
        ) : null}

        {paperSechead(CF_COPY.glyphAbsent, CF_COPY.headAbsent, rep.absent.length || null)}
        {rep.absent.length ? (
          <div className="hp-plate" data-pad="data">
            {paperLedger(rep.absent.map(function(a) {
              var name = a.name || a.key || CF_COPY.absentCustom;
              return [a.type, a.name && a.key
                ? <React.Fragment>{name} <span className="hp-sep">({a.key})</span></React.Fragment>
                : name];
            }), true)}
          </div>
        ) : null}

        {/* B-1409 — LEFT IN THE FILE (ruling `b1409-import-receipt-card` ADDENDUM 2): the companions the import
            strips and the rewards it does not carry, drawn ONLY when the file holds some (D-144's rule; D-259: no
            sentence). The mark is the room's other left-off heads'; the cite is the count of things left. A
            companion row names them when every entry has a name, else counts them; a reward's KEY resolves to
            the dataset's own name at render through the attestation's `rewardNames`, a missing key printing as is. */}
        {/* release-r5-f1 D (F1 S4): the Signature Vehicle settleRigger decided — BONDED names the ship marked as yours, every
            other outcome prints the engine's own player line. No VehicleKey in the file → no record → nothing drawn (D-144). */}
        {rep.rigger ? (
          <React.Fragment>
            {paperSechead(CF_COPY.glyphNote, CF_COPY.headRigger, null)}
            <div className="hp-plate cf-prose" data-pad="read" data-cf="rigger" data-status={rep.rigger.status}>
              <p className="hp-prose" style={{ margin: 0 }}>{rep.rigger.status === 'BONDED'
                ? <React.Fragment>{CF_COPY.wordRiggerMarked}<b>{importShipName(rep.rigger.vessel, gameData && gameData.vehicleLibrary)}</b></React.Fragment>
                : rep.rigger.player}</p>
            </div>
          </React.Fragment>
        ) : null}

        {rep.leftOut ? (
          <React.Fragment>
            {paperSechead(CF_COPY.glyphNote, CF_COPY.headLeftOut, String(rep.leftOut.reduce(function(n, e) { return n + e.count; }, 0)))}
            <div className="hp-plate" data-pad="data" data-cf="left-out">
              {paperLedger(rep.leftOut.map(function(e) {
                if (e.kind === 'rigger_weapon') return [CF_COPY.wordRiggerWeapon, CF_COPY.wordRiggerWeaponPick];   // B-1520 (D-379): its own row, never the companions branch
                if (e.kind === 'schematics') return [CF_COPY.wordSchematics, e.names.join(', ')];   // card ADDENDUM 4
                if (e.kind === 'rewards') {
                  return [CF_COPY.wordRewards, window.SWRPGOggImport.rewardNamesOf(e.keys,
                    (gameData && gameData.attestation && gameData.attestation.rewardNames) || {}).join(', ')];
                }
                return [CF_COPY.wordCompanions, e.names.length === e.count ? e.names.join(', ')
                  : e.count + ' ' + (e.count === 1 ? CF_COPY.wordCompanion : CF_COPY.wordCompanions)];
              }), true)}
            </div>
          </React.Fragment>
        ) : null}

        {rep.xp ? (
          <React.Fragment>
            {paperSechead(CF_COPY.glyphNote, CF_COPY.headXp, null)}
            <div className="hp-plate cf-prose" data-pad="read">
              <p className="hp-prose" style={{ margin: 0 }}>
                {CF_COPY.xpA}<b>{rep.xp.granted}</b>{CF_COPY.xpB}<b>{rep.xp.spent}</b>{CF_COPY.xpC}<b>{rep.xp.total}</b>{CF_COPY.xpD}
              </p>
            </div>
          </React.Fragment>
        ) : null}

        {fcForceLeftOffLine(rep.forceUpgrades) ? (
          <React.Fragment>
            {paperSechead(CF_COPY.glyphNote, CF_COPY.headForce, String(rep.forceUpgrades.refused.length))}
            <div className="hp-plate cf-prose" data-pad="read">
              <p className="hp-prose" style={{ margin: 0 }}>
                {fcForceLeftOffLine(rep.forceUpgrades)}
              </p>
            </div>
          </React.Fragment>
        ) : null}

        {fcSigLeftOffLine(rep.sigAbilities) ? (
          <React.Fragment>
            {paperSechead(CF_COPY.glyphNote, CF_COPY.headSig, String(fcSigBoxesLeftOff(rep.sigAbilities)))}
            <div className="hp-plate cf-prose" data-pad="read" data-cf="sig-left-off">
              <p className="hp-prose" style={{ margin: 0 }}>
                {fcSigLeftOffLine(rep.sigAbilities)}
              </p>
              {fcSigWhyLines(rep.sigAbilities).map(function (w, i) {
                return <p key={i} className="hp-prose" style={{ margin: 0 }}>{w}</p>;
              })}
            </div>
          </React.Fragment>
        ) : null}
      </div>
    );
  }

  /* ── THE PICK LIST (frames O · P · Q; B-772) ──────────────────────────────────────────────────
     THE ROSTER AS AN INDEX OF ROWS, which is the kit's `hp-index` and the entry's own two lanes one
     screen later: the identity slot is the row's 46px PLATE, the name is `hp-row-desig`, the species
     and career are the row's quiet line, and the chevron rides the row (never `hp-row-tab`, whose
     76px ruled gutter is for a register of numerals). Nothing here is a new unit.

     ⚠ THE BOARD DREW THE MONOGRAM AND SAID SO, AND THE ROOM HAS A SECOND BRANCH — a board SILENCE
     filled and flagged, exactly as B-771 handled the ADAPTED note. Frame O's own caption: "no pregen
     record carries a portrait, so `rowAvatar`'s image branch has nothing to draw and its fallback is
     what a player sees. The gate above it is carried deliberately (R-ERP-1) and this frame does not
     test it." A real roster record DOES carry a portrait, so dropping that branch on the paper face
     would delete a shipped feature (R-ERP-1, `export-roster-portraits`) on the strength of a board
     that could not photograph it. It is built in KIT VOCABULARY ONLY: `hp-row-plate` is already a
     46px mat that takes a wash (`paper.css`:764-765, `background-size:150% auto`, centre top) and the
     approved species rail uses exactly that idiom for exactly this job. No rule is added, no drawn
     pixel of frames O · P · Q moves, and `COMMERCIAL_MODE` keeps the gate it has everywhere else —
     dropping the gate on one row is how a seam quietly stops being a seam.

     THE EMPTY STATE IS DRAWN, SO IT IS DRIVEN (frame Q). It is a read PLATE carrying the room's own
     two sentences — a drawn absence rather than an omitted section, which is the board's own
     paragraph: a missing row is invisible, a row that says so is a job someone can pick up. The
     just-looking moment R-CFP's product-law check names is this exact plate. */
  function paperRowAvatar(c) {
    var src = c && window.SWRPGRecordPortrait.recordPortraitOf(c);   // B-931: the one fold (R-PB9-4), not `c.portrait`
    if (!COMMERCIAL_MODE && src) {
      // THE MAT ITSELF CARRIES THE WASH — the species rail's shape (`hp-row-plate` + a
      // `backgroundImage`), so the portrait fills the slot the monogram would have sat on.
      return <span className="hp-row-plate" aria-hidden="true"
        style={{ backgroundImage: "url('" + src + "')" }} />;
    }
    // THE MONOGRAM IS THE ROOM'S OWN (`mono`): the record's first letter, uppercased, with the room's
    // `'?'` fallback for a record with no name at all. The board's 16px is the pick row's size — the
    // entry's two lanes take `cf-lane`'s own 22px, and a roster letter is not a lane mark.
    return (
      <span className="hp-row-plate">
        <span className="cf-lane" style={{ fontSize: 16 }}>
          {String((c && c.name) || '?').slice(0, 1).toUpperCase()}
        </span>
      </span>
    );
  }
  function paperPick() {
    var rows = roster || [];
    return (
      <React.Fragment>
        {paperDesigOf(PLAYER_BAR_COPY.app, CF_COPY.pickTitle)}
        <div data-tour-id="p-export-pick">
          {rows.length === 0 ? (
            <div data-tour-id="p-export-empty" className="hp-plate" data-pad="read">
              <p className="cf-drop-t" style={{ marginBottom: 6 }}>{CF_COPY.pickEmptyTitle}</p>
              <p className="hp-prose" style={{ margin: 0 }}>{CF_COPY.pickEmptyBody}</p>
            </div>
          ) : (
            <div className="hp-index" style={{ marginTop: 14 }}>
              {rows.map(function (c) {
                return (
                  <button key={c.id} type="button" data-tour-id="p-export-row" className="hp-row"
                    onClick={function () { runExport(c); }}>
                    {paperRowAvatar(c)}
                    <span className="hp-row-body">
                      <span className="hp-row-desig">{c.name || CF_COPY.pickUnnamed}</span>
                      <span className="hp-none">{[c.species, c.career].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="cf-chev">›</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  /* ── THE EXPORT RECEIPT (frames R · S · T; B-772) ──────────────────────────────────────────────
     THE TWO SENTENCE LANES ARE HERE AND NOWHERE ELSE, and this is the row that reaches `.cf-lines`.
     R-CFP-1 ratifies the ledger for THREE of the report's registers and refuses it for TWO, and
     ADDENDUM 1 corrects where those two live: `cf-lines` is emitted ZERO times on the match receipt's
     frames and twice each on R and S. `NEEDS COMMUNITY DATA` and `COULD NOT BE EXPRESSED` carry the
     engine's own `row.player` lines — up to 139 characters — and `hp-lval` is 16px semibold,
     right-aligned and tabular. A SENTENCE IS NOT A LEDGER VALUE. So they take the ledger's hairline
     rhythm WITHOUT its columns, with the engine's own `row.field` as a quiet key above its own
     sentence, VERBATIM. The four rules were ported UNREACHED at B-770 and named so a reader could
     tell "not yet" from "not ever"; they are reached here.

     ⚠ THE SECTION-HEAD COUNT IS R-CFP-3'S ONE FENCED RELAXATION AND THIS ROW SPENDS IT. Everywhere
     else on this surface the page prints only what the product prints; these heads' counts are the
     single exception Jason approved as one of three separately-refusable decisions, and the fence is
     tighter than the ruling wrote it: B-770 had no section heads to put one on, and B-771 proved every
     one of ITS head cites was already product copy in the dark receipt's own `gloss` span — so the
     relaxation belonged to these four export heads or to nowhere. The DARK export report's `sect(...)`
     has no gloss slot at all, so none of these three figures is shown by the product today. They are
     COUNTS and never judgements: `defaults.length` of `defaults.length`, `deps.length`,
     `drops.length` — each derived from the array the page is about to draw, never typed. CARRIED
     takes none, because there is nothing on that section to count.

     THE SECTION A REPORT DOES NOT NEED IS ABSENT, NOT EMPTY — the room's own rule and not a drawing's:
     `NEEDS COMMUNITY DATA` is not rendered when the engine raised no dependency (frame T), while
     `COULD NOT BE EXPRESSED` is always rendered and says so in the room's own words when nothing was
     dropped. Both faces take that branch from the same two arrays.

     A PLATE HOLDS PROSE OR A LEDGER AND NEVER BOTH, which is why CARRIED and the defaults lead sit in
     `cf-prose` plates and the defaults ledger takes its own: two right edges inside one card reads as
     a broken card. The board's arrangement, unchanged. */
  function paperLines(rows) {
    return (
      <ul className="cf-lines">
        {rows.map(function (r, i) {
          return <li key={i}><span className="hp-none">{r.field}</span>{r.player}</li>;
        })}
      </ul>
    );
  }
  function paperExport() {
    var X = exportReceiptRows(exported);
    var defaults = exportDefaultRows(gameData && gameData.attestation);
    var name = exported.record && exported.record.name;
    return (
      <div data-tour-id="p-export-report">
        {paperDesigOf(PLAYER_BAR_COPY.app, name)}
        {/* THE ROOM'S FIRST LINE IS `✓ {name} — ready` AND IT STAYS WHOLE. The name is the page's
            title and is also inside the product's sentence; a title restated by its own caption is
            the printed convention, and cutting the sentence to avoid the repetition would be
            rewriting it. The board drew it the same way. */}
        <p className="hp-none" style={{ margin: '8px 0 0' }}>
          {CF_COPY.exportTick} {name}{CF_COPY.exportReady}
        </p>

        {/* THE FOUR SECTION ANCHORS RIDE THE PLATES, and that is the app's shape winning over a
            drawing exactly as `p-import-report` and `data-creator-back` did (C-MU-4). The board draws
            no `data-tour-id` anywhere, because a board draws no interaction; the DARK face carries
            `p-export-carried` / `p-export-defaults` / `p-export-community` / `p-export-drops` on its
            `sect(...)` wrappers and `smoke_ogg_export` drives four of them — including reading "The
            Down and Out" out of `p-export-defaults` and the engine's dropped line out of
            `p-export-drops`. Paper has no section wrapper (head and plate are siblings), so the
            anchor lands on the PLATE THAT HOLDS EACH SECTION'S CONTENT, which is where those reads
            look. `p-export-defaults` goes on the LEDGER rather than the lead, because the ledger is
            what carries the resolved names. Not one drawn pixel moves, and a flip-day red that would
            otherwise have been loaded here (the B-712 shape) is not. */}
        {paperSechead(null, CF_COPY.headCarried, null)}
        <div className="hp-plate cf-prose" data-pad="read" data-tour-id="p-export-carried">
          <p className="hp-prose" style={{ margin: 0 }}>
            <b>{exportCarried(exported)}</b>
          </p>
        </div>

        {paperSechead(null, CF_COPY.headDefaults, defaults.length + ' of ' + defaults.length)}
        <div className="hp-plate cf-prose" data-pad="read">
          <p className="hp-prose" style={{ margin: 0 }}>{CF_COPY.exportDefaultsLead}</p>
        </div>
        <div className="hp-plate" data-pad="data" data-tour-id="p-export-defaults">
          {paperLedger(defaults, false)}
        </div>

        {X.deps.length ? (
          <React.Fragment>
            {paperSechead(null, CF_COPY.headCommunity, String(X.deps.length))}
            <div className="hp-plate" data-pad="data" data-tour-id="p-export-community">
              {paperLines(X.deps)}
            </div>
          </React.Fragment>
        ) : null}

        {paperSechead(null, CF_COPY.headDropped, X.drops.length ? String(X.drops.length) : null)}
        <div className="hp-plate" data-pad="data" data-tour-id="p-export-drops">
          {X.drops.length ? paperLines(X.drops)
            : <p className="hp-none" style={{ margin: 0 }}>{CF_COPY.exportNothing}</p>}
        </div>
      </div>
    );
  }

  /* ── WHAT THIS ROW DRAWS, AND WHAT STANDS ON AN ISLAND ────────────────────────────────────────
     `paperBody()` returns the drawn paper for the five states B-770 owns and `null` for the three
     it does not; `null` is what puts a phase on a dark island below. The device is `sheet.css`'s
     `.sb-legacy`, `store.css`'s and `qsg.css`'s, a fourth time and for the reason all three gave:
     the dark bodies paint white type and accent hues on the assumption of a dark ground, so until a
     zone's paper drawing lands its body is the SAME component on a DARK island inside the paper
     shell — functional from the first row, and impossible to mistake for finished paper.

     THE ISLAND IS A **BODY** SWAP AND NEVER A SCREEN SWAP. It holds `body()`'s return, not the
     room's root: the root carries its own dark runhead, and mounting it here would draw two running
     heads on one page. The paper runhead stands above every phase, island or not. */
  function paperZone() {
    // `match-report` LEFT THIS FUNCTION AT B-771 and `pick` + `export-report` LEFT IT AT B-772 — every
    // phase of this room is drawn paper now, so the zones are GONE rather than a status line saying
    // so, and `FILES_INTERIM_ZONES` closed with them (3 → 2 → 0). This function therefore answers
    // `null` for every phase, which is the END STATE and not a stub: it is kept, with its ledger, so
    // `[data-files-interim]` can be ASKED and answer nothing rather than the query going quiet
    // because a selector broke. B-773 and the flip's deletion lap delete it (the switch is still OFF).
    return null;
  }

  function paperBody() {
    if (phase === 'loading') {
      // FRAME C — six promises boot the room and three of them fail soft. Until they land the room
      // draws ONE WORD, and that is the whole state.
      return <React.Fragment>{paperDesig}<p className="hp-none" style={{ margin: '26px 0' }}>{CF_COPY.loading}</p></React.Fragment>;
    }
    if (phase === 'entry') {
      return (
        <React.Fragment>
          {paperDesig}
          <div data-tour-id="p-import-entry">
            <div className="hp-index" style={{ marginTop: 14 }}>
              {paperLane('p-import-entry-import', '⬇', 'IMPORT', enterImportLane)}
              {paperLane('p-import-entry-export', '⬆', 'EXPORT', enterExportLane)}
            </div>
          </div>
        </React.Fragment>
      );
    }
    if (phase === 'drop') return paperDrop();
    if (phase === 'error') {
      var why = result && result.out && result.out.error;
      return (
        <div data-tour-id="p-import-error">
          {paperStop(CF_COPY.importErrTitle,
            why === 'boot' ? CF_COPY.importErrBoot : CF_COPY.importErrBad,
            CF_COPY.importErrBtn, importErrBack)}
        </div>
      );
    }
    if (phase === 'exported' && (exportErr || !exported)) {
      return (
        <div data-tour-id="p-export-error">
          {paperStop(CF_COPY.exportErrTitle, null,
            CF_COPY.exportErrBtn, exportErrBack)}
        </div>
      );
    }
    // FRAMES G–L (B-771). `adding` draws THE SAME PAGE — the board's frame L differs from frame G in
    // one word on one control and in nothing else, which is why it is one body and two act labels
    // rather than two drawings. `result.sheet` is the guard the dark face already has: a `report`
    // phase without one cannot happen (`handleText` sets the phase and the result together), and
    // falling through to an island would be a worse answer than falling through to nothing.
    if ((phase === 'report' || phase === 'adding') && result && result.sheet) return paperReport();
    // FRAMES O · P · Q (B-772). `roster` is the room's own `loadSavedCharacters()` read, taken when
    // the export lane is entered; an empty one draws frame Q's plate rather than an empty index.
    if (phase === 'pick') return paperPick();
    // FRAMES R · S · T (B-772). The failure branch above already took `exportErr || !exported`, so
    // reaching here means the engine returned and the receipt has rows to read.
    if (phase === 'exported') return paperExport();
    return null;                       // → an island, named by `paperZone()`
  }

  var paperZoneName = paperZone();
  var paperDrawn = paperBody();
  var paperIsland = paperZoneName
    ? <div className="cf-legacy" data-files-interim={paperZoneName}>{body()}</div>
    : null;

  /* ── THE PRE-COMMIT ACT ROW (frames G–L; B-771) ────────────────────────────────────────────────
     THE ACT LIVES IN THE SHELL AND NOT IN THE BODY, because the board puts it there and the two
     bands put it in two different places: the phone's `hp-phoneact` is a STICKY BAR outside
     `hp-phonebody` (so the receipt scrolls under a commit that never leaves the screen — the whole
     reason a receipt this long is still answerable at 390), and the desk's `hp-onecol-act` sits
     INSIDE the reading column under the last section, which is where a printed form puts its
     signature line. A body that carried its own act could not be both.

     AND THE ORDER IS THE BOARD'S, DIFFERENT PER BAND: quiet-then-commit at the phone (the commit is
     `width:100%` under the kit's own rule, so it takes the bottom line), commit-then-quiet at the
     desk. Read off frames G and H rather than chosen.

     IT PRESSES THE ROOM'S OWN TWO HANDLERS — `addToRoster` and `onBack` — which is exactly what the
     dark `MatchReport` is handed (`onAdd={addToRoster} onCancel={onBack}`). One act, two faces, no
     second writer: R-CFP-2's "the SAME state, writers and testids".

     `adding` RELABELS AND DISABLES and moves nothing else (frame L). `disabled` on `hp-commit` is
     the kit's own contract — `paper.css` already scopes its hover and active states with
     `:not(:disabled)` — so this needs no rule of its own, and the label swap is the room's own
     ternary read out of `CF_COPY`. */
  var paperAct = null;
  if ((phase === 'report' || phase === 'adding') && result && result.sheet) {
    paperAct = {
      anchor: 'p-import-add',
      quiet: CF_COPY.reportCancel, onQuiet: onBack,
      commit: phase === 'adding' ? CF_COPY.reportAdding : CF_COPY.reportAdd,
      onCommit: addToRoster, busy: phase === 'adding',
    };
  }
  /* ── THE DOWNLOAD ACT (frames R · S · T; B-772) ────────────────────────────────────────────────
     THE EXPORT RECEIPT'S ACT IS A COMMIT WITH NO QUIET BESIDE IT, which is the board's own reading
     and the difference between the two receipts said in furniture: the MATCH receipt is PRE-COMMIT
     (Cancel beside "Add to my roster", because the decision is not yet made) and the EXPORT receipt
     is POST-ENGINE — the file is already written and the one remaining act is taking it. There is
     nothing to cancel, so nothing pretends there is.

     AND THE ACT ENDS IN A DOWNLOAD, NOT A WRITE (R-CFP-2's durable-contract clause, restated where it
     applies). `downloadExport` makes a Blob, clicks an anchor and revokes the URL; it persists
     nothing, touches no roster record and changes no save shape. That is the whole reason this row
     needed no paper for a contract: the surface's only write is still the roster write the MATCH
     receipt already made.

     IT SITS IN THE SHELL for the reason B-771's act row does — the two bands put it in two different
     places (the phone's sticky bar OUTSIDE the scroller, the desk's line INSIDE the reading column)
     and a body carrying its own act could not be both. The label is the room's own expression, the
     engine's own `fileName` in its own slot. */
  if (phase === 'exported' && exported && !exportErr) {
    paperAct = {
      anchor: 'p-export-download',
      quiet: null, onQuiet: null,
      commit: CF_COPY.exportDownload + ' ' + exported.fileName,
      onCommit: downloadExport, busy: false,
    };
  }
  function paperActRow(desk) {
    if (!paperAct) return null;
    var quiet = paperAct.quiet
      ? <button type="button" className="hp-quiet" onClick={paperAct.onQuiet}>{paperAct.quiet}</button>
      : null;
    var commit = (
      <button type="button" data-tour-id={paperAct.anchor} className="hp-commit"
        onClick={paperAct.onCommit} disabled={paperAct.busy}>
        {paperAct.commit} <span>›</span>
      </button>
    );
    // THE COMMIT-ONLY ROW DRAWS ONE BUTTON AND NO SEPARATOR, in the same two containers: the board's
    // phone act is `<div class="hp-phoneact">{commit}</div>` and its desk act
    // `<div class="hp-onecol-act">{commit}</div>` on frames R · S · T, so the quiet half is absent
    // rather than present-and-empty.
    if (!quiet) {
      return desk
        ? <div className="hp-onecol-act">{commit}</div>
        : <div className="hp-phoneact">{commit}</div>;
    }
    return desk
      ? <div className="hp-onecol-act">{commit}{quiet}</div>
      : <div className="hp-phoneact">{quiet} {commit}</div>;
  }

  // THE RUNHEAD. Its left cell is a BUTTON and the board draws a span — reported both ways rather
  // than decided here, and the app's established shape wins on C-MU-4: the room ALREADY ships a back
  // BUTTON carrying `data-tour-id="p-import-back"` (the suites drive it), `qsg.css`'s shelf resolved
  // this exact question once already, and `paper.css` carries the AS-6 rule that buys that button
  // its 44px with hit area instead of with the bar's geometry. Nothing about the drawn pixels moves.
  var paperRunhead = (
    <div className="hp-runhead">
      {/* `data-creator-back` IS LOAD-BEARING AND NOT DECORATION — it is the attribute `paper.css`'s
          AS-6 rule keys on (`.hp-runhead [data-creator-back]`, :491), which gives the control a real
          44px rect with NEGATIVE block margins so the floor is bought with HIT AREA and not with the
          bar's geometry. Without it this button measured 32×11 and breached AS-6 while looking
          exactly right, which is what the suite's AS-6 leg caught. */}
      <button type="button" data-tour-id="p-import-back" data-creator-back="1" onClick={backOut}
        aria-label={'Back from ' + CF_COPY.entryTitle}
        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
        <span>{CF_COPY.back}</span>
      </button>
      <span className="hp-folio">{phaseTitle}</span>
    </div>
  );

  // THE PHONE (frames A · C · D · E · M · N · U). The BODY is the scroller, never the page (AS-4).
  if (band !== 'desktop') {
    return (
      <div className="cf-surface" data-doc-ground="creator" data-files-shell="1" data-band={band}>
        <div className="hp-sheet hp-phonesheet cf" data-doc-ground="creator">
          <div className="hp-phonebody" data-files-head={paperIsland ? '1' : undefined}>
            {paperRunhead}
            <hr className="hp-hr" />
            {paperDrawn}
          </div>
          {paperIsland}
          {paperActRow(false)}
        </div>
      </div>
    );
  }

  // THE DESK (frames B · F · Y). ONE MEASURE FOR THE WHOLE ROOM — `--desk-read-max`, the token the
  // reading measure already has. The room caps its own column at 620 AND 720 today; 720 IS that
  // token and 620 is not a token and is not derivable from one, so a paper face keeping both would
  // carry a magic number. Jason was told this was separately refusable and refused none of the three
  // decisions (LAB_NOTES §201). Frame Y is this same composition at the mid rung — the one band
  // between the two the rest of the board draws — so it is a BAND and not a second drawing.
  return (
    <div className="cf-surface" data-doc-ground="creator" data-files-shell="1" data-band={band}>
      <div className="hpw-shell">
        <div className="hp-sheet hp-wide" data-doc-ground="creator">
          <div className="hpw-bar">{paperRunhead}</div>
          <div className="hp-onecol">
            {paperIsland || (
              <div className="cf-scroll"><div className="cf-col">{paperDrawn}{paperActRow(true)}</div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function btn(accent, primary) {
  return primary
    ? { background: accent, color: '#0b0b0d', border: 'none', borderRadius: 8, padding: '12px 22px', fontFamily: 'var(--font-display)', letterSpacing: '0.04em', fontSize: 14, cursor: 'pointer', fontWeight: 600 }
    : { background: 'none', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '12px 22px', fontFamily: 'var(--font-display)', letterSpacing: '0.04em', fontSize: 14, cursor: 'pointer' };
}

// The receipt (R-OGG1-3 / R-OGG-19): CAME ACROSS (a count + a sample), ADAPTED, NOT IN OUR DATA — the
// honesty structure is present whether or not the adapted/absent rows are populated (mockup state 4).
function MatchReport({ result, accent, onCancel, onAdd, adding }) {
  // B-771 — the figures and the words both come from one place now (`matchReceiptFigures` and
  // `CF_COPY` above). NOT ONE CHARACTER OF THIS COMPONENT'S OUTPUT MOVES: the expressions are the
  // shipped ones by another name and every literal below is the same literal, read instead of
  // typed. The suite proves it two ways — the dark face is asserted to print each sentence, and
  // this function's source is asserted to hold no second copy of one.
  var F = matchReceiptFigures(result);
  var out = F.out, sheet = F.sheet, rep = F.rep;
  var clean = F.clean;
  var talentCount = F.talents, gearCount = F.gear;
  var sec = { border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, marginBottom: 12, overflow: 'hidden' };
  var secHead = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(255,255,255,0.03)' };
  var line = { padding: '7px 16px', color: 'rgba(255,255,255,0.72)', fontSize: 14, lineHeight: 1.5 };
  var gloss = { color: 'rgba(255,255,255,0.4)', fontSize: 12, marginLeft: 'auto' };
  return (
    <div data-tour-id="p-import-report" style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px' }}>
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: '0.14em', color: accent }}>{CF_COPY.reportEyebrow}</span>
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, marginBottom: 4, color: '#fff' }}>{sheet.name || CF_COPY.reportNoName}</div>
      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, marginBottom: 18 }}>{CF_COPY.reportFrom}{result.filename || CF_COPY.reportFromFallback}</div>
      {clean ? (
      <div style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 22 }}>
        {CF_COPY.reportClean}
      </div>
      ) : null}

      <div style={sec}>
        <div style={secHead}><span style={{ color: '#5fd08a' }}>{CF_COPY.glyphMatched}</span><span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em' }}>{CF_COPY.headMatched}</span><span style={gloss}>{rep.matched} of {rep.matched}</span></div>
        <div>
          <div style={line}><b style={{ color: '#fff' }}>{sheet.species}</b> · {CF_COPY.wordSpecies} &nbsp;·&nbsp; <b style={{ color: '#fff' }}>{sheet.career}</b> · {CF_COPY.wordCareer}{sheet.specialization ? <span> &nbsp;·&nbsp; <b style={{ color: '#fff' }}>{sheet.specialization}</b> · {CF_COPY.wordSpecialization}</span> : null}</div>
          {talentCount ? <div style={line}>{talentCount} {CF_COPY.wordTalents} · {F.specs} {CF_COPY.wordSpecialization}{F.specs === 1 ? '' : 's'}</div> : null}
          <div style={line}>{gearCount} {CF_COPY.wordGear} · {F.skills} {CF_COPY.wordSkills} · {CF_COPY.reportTail}{out.portraitData ? CF_COPY.reportPortrait : ''}</div>
          {fcCreditsLine(rep) ? <div style={line} data-cf="credits">{fcCreditsLine(rep)}</div> : null}
          {fcXpCountedLine(rep) ? <div style={line} data-cf="xp-counted">{fcXpCountedLine(rep)}</div> : null}
        </div>
      </div>

      <div style={sec}>
        <div style={secHead}><span style={{ color: 'rgba(255,255,255,0.5)' }}>{CF_COPY.glyphAdapted}</span><span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em' }}>{CF_COPY.headAdapted}</span><span style={gloss}>{rep.adapted.length || CF_COPY.adaptedNone}</span></div>
        {rep.adapted.map(function(a, i) { return <div key={i} style={line}><b style={{ color: '#fff' }}>{a.from}</b> → {a.to}{a.note ? <span style={{ color: 'rgba(255,255,255,0.4)' }}> · {a.note}</span> : null}</div>; })}
      </div>

      <div style={sec}>
        <div style={secHead}><span style={{ color: '#e0894a' }}>{CF_COPY.glyphAbsent}</span><span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em' }}>{CF_COPY.headAbsent}</span>{rep.absent.length ? <span style={gloss}>{rep.absent.length}</span> : null}</div>
        {rep.absent.map(function(a, i) { return <div key={i} style={line}><b style={{ color: '#fff' }}>{a.name || a.key || CF_COPY.absentCustom}</b> · {a.type}{a.name && a.key ? <span style={{ color: 'rgba(255,255,255,0.4)' }}> ({a.key})</span> : null}</div>; })}
      </div>

      {/* Honest rows for the two named gaps (R-OGG1-16 / C-OGG1-3): an XP total the file couldn't
          confirm, and force-power upgrades not yet carried. Shown ONLY when they occur. */}
      {rep.xp ? (
        <div style={Object.assign({}, sec, { borderColor: accent + '33' })}>
          <div style={secHead}><span style={{ color: accent }}>{CF_COPY.glyphNote}</span><span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em' }}>{CF_COPY.headXp}</span></div>
          <div style={line}>{CF_COPY.xpA}<b style={{ color: '#fff' }}>{rep.xp.granted}</b>{CF_COPY.xpB}<b style={{ color: '#fff' }}>{rep.xp.spent}</b>{CF_COPY.xpC}<b style={{ color: '#fff' }}>{rep.xp.total}</b>{CF_COPY.xpD}</div>
        </div>
      ) : null}
      {fcForceLeftOffLine(rep.forceUpgrades) ? (
        <div style={Object.assign({}, sec, { borderColor: accent + '33' })}>
          <div style={secHead}><span style={{ color: accent }}>{CF_COPY.glyphNote}</span><span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em' }}>{CF_COPY.headForce}</span><span style={gloss}>{rep.forceUpgrades.refused.length}</span></div>
          <div style={line}>{fcForceLeftOffLine(rep.forceUpgrades)}</div>
        </div>
      ) : null}
      {fcSigLeftOffLine(rep.sigAbilities) ? (
        <div data-cf="sig-left-off" style={Object.assign({}, sec, { borderColor: accent + '33' })}>
          <div style={secHead}><span style={{ color: accent }}>{CF_COPY.glyphNote}</span><span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em' }}>{CF_COPY.headSig}</span><span style={gloss}>{fcSigBoxesLeftOff(rep.sigAbilities)}</span></div>
          <div style={line}>{fcSigLeftOffLine(rep.sigAbilities)}</div>
          {fcSigWhyLines(rep.sigAbilities).map(function (w, i) { return <div key={i} style={line}>{w}</div>; })}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
        <button onClick={onCancel} style={btn(accent, false)}>{CF_COPY.reportCancel}</button>
        <button data-tour-id="p-import-add" onClick={onAdd} disabled={adding} style={Object.assign({}, btn(accent, true), adding ? { opacity: 0.6 } : {})}>{adding ? CF_COPY.reportAdding : CF_COPY.reportAdd}</button>
      </div>
    </div>
  );
}

/* ── Full Creator Shell ──────────────────────────────── */
// ═══ B-563 · THE REVISION REBUILD — INFRASTRUCTURE (increment 1b) ═══════════════════════════
// The shared pieces every transplanted step reads, ported VERBATIM from revision/creator-2026-09-10
// (822015f1): the step labels and cite, the moved-marker helpers, the folio, the characteristic rail,
// the So-Far table, the phone shell. INERT until a step's render block is lit; nothing below is
// reached by the live creator. Lit per step, one lap each, on the instruments (Docs/BOARD.md B-563).
var FC_STEP_LABELS = {
  gameline: 'Game line', species: 'Species', career: 'Career', specialization: 'Specialization',
  mechanic: 'Mechanic', characteristics: 'Characteristics', skills: 'Skills',
  talents: 'Talents', forcepowers: 'Force powers', motivation: 'Motivation', gear: 'Gear',
  review: 'Review',
};
/* B-1668 — THE STEP LIST'S WORDS ARE THE BOARD'S (the `hp-wave-lab` row of test/fixtures/board_* @ 02f95b78),
   save ONE entry: the mechanic step says the line's own word, the word its folio prints (DECISIONS D-437,
   ruling release-r7-bug-burndown ADDENDUM 413). Gated on the line being SET: `mechanicForLine` answers
   'morality' for a null line, and step 1 lists every step before a line is picked, so the board's
   "Mechanic" stands there. Every reader of the list — the wave's three renderers, their aria-labels,
   the rollback face's FCBuildRail — takes its words from this one builder. */
function fcStepLabels(gameLine) {
  if (!gameLine) return FC_STEP_LABELS;
  return Object.assign({}, FC_STEP_LABELS, { mechanic: FC_MECH_STEP_NAME[mechanicForLine(gameLine)] || FC_STEP_LABELS.mechanic });
}

var FC_CHAR_WORDS = { brawn: 'BRAWN', agility: 'AGILITY', intellect: 'INTELLECT',
                      cunning: 'CUNNING', willpower: 'WILLPOWER', presence: 'PRESENCE' };

// Career descriptions — Reference Voice (factual, encyclopedic, no game mechanics)
// Bystander-friendly skill descriptions for the skill picker
// FC_SKILL_DESC DELETED (F-58). The 35 skill explanations are REGISTRY data now —
// `skills.vocab.json` `description`, resolved at render via skillDescFor() below. They were moved
// VERBATIM by scripts/canonical/apply_skill_descriptions.py (parsed out of this file, never
// retyped), so no wording changed. The 36th key, CYBERNETICS, had no canonical skill behind it and
// was therefore never reachable; it is declared in that script and belongs to the F-60 lap.
//
// A skill's plain-language explanation, resolved from the registry at render — the same posture as
// speciesAbilitiesForDisplay. Keyed by the legacy SHEET key both the creator and the sheet hold, so
// the three call sites read exactly as they did. Uses the mod-overlaid view when one is active, so a
// pack that adds a skill brings its description with it.
// R-F58-4 (ruling `f58-skill-descriptions`) — LATENT SEAM, marked before it bites: this reads the
// GLOBAL overlaid view (whatever the mods TOGGLE has active), not the sheet's OWN stamp the way
// sheetTalentsForDisplay does via SWRPGMods.resolveForStamp. Moot today because no pack overlays
// skills, so base and overlay agree for all 35. The day a pack adds or reworded one, a stamped sheet
// would resolve its skill text against the *reader's* toggle instead of what the character was built
// under — the discovery-vs-ownership distinction (F-9 B2). Fix by resolving against the stamp then,
// not by widening this function's callers now.

var FC_EFF_NUMERIC = ['wound_threshold_base', 'strain_threshold_base', 'starting_xp'];
var fcMovedSeq = 0;

var FC_TALENT_POINTER = /^receive\s+(?:\d+\s+ranks?\s+in\s+)?the\s+talent\s+[\u201c"]?([^\u201c\u201d".]+)[.\s]*[\u201d"]?\s*\.?\s*$/i;

function fcTalentPointerName(ability) {
  var m = FC_TALENT_POINTER.exec(String((ability && ability.description) || '').trim());
  return m ? m[1].trim().toLowerCase() : null;
}

function fcMovedOf(rec) { return (rec && rec._moved_now) || null; }

function fcMovedKey(moved, key) { return (moved && moved.keys[key]) ? (key + '#' + moved.seq) : key; }

/* THE HELD COMMIT'S LABEL — B-409's COPY half (`creator-release-scope` A4 R-CRS-A4-2), one builder
   for both bands. "Choose a Skills to continue" hardcoded an article before a label that is usually
   plural (205 of 208 groups). Ruled: count 1 → "Pick one <noun> to continue" — the noun from the
   registry's option-group vocabulary (`noun`: skill · subspecies · gender · caste · size · age),
   seat 2's DATA half; until it lands a group without one reads "Pick one to continue" rather than
   inventing a singular; count N → "Pick <N> <label> to continue"; groups joined with " and ". The
   board's species refusal reads "Pick one skill to continue" — matched exactly once the noun is there. */
/* B-409 A1 (R-B409D-A1-2) — AN OPTIONAL GROUP IS NEVER OWED. A group whose `min_picks` is 0 is one
   the book OFFERS: the player may take none, so it cannot hold the commit. csg_droid's
   "Modifications" (0-3) is the whole population today, and it is out by its BOUNDS, never by a hand
   exemption — consistent with B-398, where the same group is excluded from `preselect` for the same
   reason. ONE predicate for both bands so the two entries cannot drift, and because the label
   formula below therefore never sees a min-0 group it gets NO defensive branch: an unreachable
   guard that reads as a safeguard is the defect PRAXIS §6 names. `|| 1` in the label stays as it
   is — its purpose is a group carrying no bounds at all, which is still reachable. */
/* R-CPD-6(b) — THE QUESTION IS ONE, THE CONTINUATIONS ARE TWO. The idle prompt asks on the group
   HEAD; the held commit's label lists the groups still owed. Both turn on the same question — is
   THIS group still owed — and it is asked here, once. ADR 0048's boundary is the reason this is a
   predicate rather than a shared continuation: the two callers' afterwards are a rendered attribute
   and a filtered list, which have nothing in common to share, and neither can forget to act on the
   answer (an unused predicate return renders nothing; B-436's shape was a caller that CALLED the
   check and then did not branch on it). One question, two honest uses. */
/* THE HEAD'S OWN WORDS, one formula. The two bands draw this head in different elements — the
   wide band's `hp-maker`, the phone's `hp-sechead > h2` — and they had drifted: the wide one
   states a RANGE where the bounds differ (`species-choice-prompt`'s fix, so Droid Modifications
   read "pick 0-3" and not "pick 0", a heading telling the player to pick none of the three things
   listed under it), the phone still printed `min` alone and said "pick 0". Same defect, one band
   behind — which is what a second copy of a formula always becomes. Fixed here, for both. */

function fcMovedMark(moved, key) { return (moved && moved.keys[key]) ? '1' : undefined; }

var fcMovedSeq = 0;

function fcFolio(stepNo, stepTot, stepName) {
  var pos = (stepNo == null) ? null : ('Step ' + stepNo + ' of ' + stepTot);
  if (!stepName) return pos || '';
  return pos ? (pos + ' \u00b7 ' + stepName) : stepName;
}

function fcCharRail(ch, cols, heading, moved) {
  return (
    <div className="hp-plate" data-pad="snug">
      {/* THE HEADING IS THE WIDE BAND'S. Measured at pin f39b31b6: the board's desktop species
          frame draws `hp-maker` "Starting Characteristics" above the rail and its PHONE entry
          frame draws the rail with no heading at all — same plate, same `data-cols="3"`, one
          fewer object. Omitted only on an explicit `null`, the same rule `FCPhoneShell` takes for
          its act bar, so a caller that simply does not pass it still gets the heading. */}
      {heading === null ? null
        : <div className="hp-maker" style={{ marginBottom: '8px' }}>Starting Characteristics</div>}
      <div className="hp-railwrap">
        <div className="hp-rail" data-cols={cols}>
          {FC_CHAR_ORDER.map(function(a) {
            /* `data-char` names the value this capsule holds. An instrument asking "did the mark
               land on the values that moved" needs to say WHICH capsule it is reading, and the only
               identity here was the label's painted word — which `text-transform` uppercases, the
               trap PRAXIS §6 names. An attribute, so no object is added. */
            return (
              <div className="hp-char" key={a} data-char={a}>
                {/* R-CPD-6(c) — the bead IS the value, so the mark goes on the bead and nothing is
                    added to the tree. The key carries the sequence so the same value moving twice
                    animates twice. */}
                <span className="hp-char-val"><span className="hp-char-bead"
                  key={fcMovedKey(moved, a)} data-moved={fcMovedMark(moved, a)}>{(ch || {})[a]}</span></span>
                <i className="hp-char-lab">{FC_CHAR_WORDS[a]}</i>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* THE SPECIES HEAD — its own composition, and the designation is the SELECTED SPECIES rather than
   the step. The maker counts the register ("Entry 510 of 537") because on this screen the folio
   is where you are IN A LIST, not where you are in the walk. */

function fcSkillChoicePhrase(g, skillNames) {
  if (!g || g.pool == null) return null;
  var n = (g.count == null) ? 1 : g.count;
  var ranks = n + ' rank' + (n === 1 ? '' : 's') + ' in ';
  var tail = ' \u2014 chosen at Skills';

  if (Array.isArray(g.pool)) {
    var pool = g.pool.map(function(k) { return (skillNames && skillNames[k]) || k; });
    if (!pool.length) return null;
    var list = pool.length === 1 ? pool[0]
      : pool.slice(0, -1).join(', ') + ' or ' + pool[pool.length - 1];
    return ranks + list + tail;
  }

  var key = String(g.pool);
  if (key === 'any') return ranks + 'any skill' + tail;
  if (key === 'non_career') return ranks + 'any non-career skill' + tail;

  // A GROUP TOKEN — "1 rank in any Combat skill — chosen at Skills".
  return ranks + 'any ' + key.charAt(0).toUpperCase() + key.slice(1) + ' skill' + tail;
}

// A species option's starting talent, read through the canonical view: its name (else the id, title-cased) and its rule words.
// ONE reader for the caption's predicate and the talent plate it decides about (B-386, D-400).
function fcOptionTalentOf(g) {
  var tals = ((window._canonicalCharbuild || {}).talents) || {};
  var id = g && (g.talent || g.id || g);
  var rec = id ? tals[id] : null;
  return { id: id, name: (rec && rec.name) || (id ? String(id).replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); }) : ''),
           description: (rec && rec.description) || '' };
}
function fcNestNeedsCaption(opt) {
  if (!opt) return false;
  var under = (opt.special_abilities || []).map(function(a) { return a && a.name; })
    .concat((opt.starting_talents || []).map(function(g) { return fcOptionTalentOf(g).name; })).filter(Boolean);
  var names = {}; under.forEach(function(n) { names[n] = 1; });
  var keys = Object.keys(names);
  return !(keys.length === 1 && keys[0] === opt.name);
}

function FCbSoFarTable({ rows }) {
  return (
    <table className="hp-ledger"><tbody>
      {rows.map(function(r) {
        return (
          <tr key={r.k}>
            <td className="hp-lkey">{r.k}</td>
            <td className="hp-lval">{r.v || '\u2014'}</td>
          </tr>
        );
      })}
    </tbody></table>
  );
}

function FCPhoneShell(p) {
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal;
  return (
    <div className="hp-sheet hp-phonesheet" data-doc-ground="creator" data-band={p.band}
      data-creator-rail="0" data-char-controls={p.charControls} {...(p.rootAttrs || {})}>
      <div className="hp-phonebody">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label={p.backLabel ? ('Back to ' + p.backLabel) : 'Back to the previous step'}
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            {/* THE BACK LABEL NAMES WHERE BACK GOES, and on one screen that is not the walk.
                The species step is TWO screens, and the board draws the ENTRY's runhead reading
                "‹ Species Register" — the drawn form of the back gesture
                (`species-phone-list-to-entry-transition` A2, lab build.py:736). Every other phone
                screen goes back to the walk and keeps "Character Creation". Measured across the
                f39b31b6 -> f244b338 refresh: `&nbsp;&nbsp;Character Creation</span>` 98 -> 93 and
                `&nbsp;&nbsp;Species Register</span>` 0 -> 5 — the four ACT frames plus the entry,
                and nothing else in the document. DOM parity does not compare text, so nothing
                reds over this: the driven guard is what asserts it. */}
            <span>{'\u2039\u00a0\u00a0' + (p.backLabel || 'Character Creation')}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot)}</span>
        </div>
        <nav className="hp-wave" aria-label="Character creation steps" data-rail-section="steps">
          {p.steps.map(function(st, i) {
            return (
              <button type="button" key={st} className="hp-wave-step"
                data-done={i < (p.stepReached == null ? p.stepIndex : p.stepReached) ? '1' : undefined}
                data-now={i === p.stepIndex ? '1' : undefined}
                aria-current={i === p.stepIndex ? 'step' : undefined}
                aria-label={'Step ' + (i + 1) + ' of ' + stepTot + ': ' + p.stepLabels[st]}
                onClick={function() { p.onJump(st); }}>
                <span className="hp-wave-lab" aria-hidden="true">{p.stepLabels[st]}</span>
                <span className="hp-wave-bar" />
              </button>
            );
          })}
        </nav>
        <hr className="hp-hr" />
        {/* THE STATUS STRIP is the shell's container and the step's ROWS. Characteristics prints
            three (the purse); species prints two (the XP the entry grants, and its place in the
            register).
            IT IS NOT ON EVERY PHONE FRAME, and this comment used to say it was — R-SPB-14 was
            written from the two steps that had frames at the time, and the game-line frame
            (`phone_gameline()`, at the freeze pin, and byte-identical at the two pins before it)
            draws NO `hp-status` element at all: 0 occurrences in the extracted body. Corrected
            here rather than worked around, because a comment a later build reads as a rule is how
            an empty strip gets shipped to satisfy a sentence.
            OMITTED ONLY BY TYPING `null`, exactly as the act bar below is. A caller that simply
            did not pass `status` STILL EMITS the strip — a step must not be able to lose its
            reporting line by forgetting a prop; only an explicit `null` is a decision a reader
            can see. Game line is the only strip-less consumer: it is step 1, before any purse,
            any register position and any line, so the strip would carry three dashes. */}
        {p.status === null ? null : <div className="hp-status">{p.status}</div>}
        {p.children}
      </div>
      {/* THE BAR IS OMITTED ONLY BY TYPING `null`, never by forgetting (`species-phone-register`
          A2, and the lab's `phone_sheet` made the same call for the same reason at build.py:2136).
          THE ROW IS THE ACT on the register list: a commit under 537 rows would be a button with
          nothing to commit, and the pre-shell drawing this screen carries forward never had one.
          `undefined` — a caller that simply did not pass `act` — STILL EMITS the bar, because a
          screen must not be able to lose its act by omission; only an explicit `null` is a
          decision a reader can see. The list is the only barless consumer; characteristics and the
          species ENTRY keep theirs, and `test/phone_shell_barless.cjs` asserts exactly that. */}
      {p.act === null ? null : <div className="hp-phoneact">{p.act}</div>}
    </div>
  );
}

// The species step's cite: creator_step_cites.json's `species` row (EotE, for every line, as it always printed),
// printed through the converter. It was typed as Step 3's CORPUS page, nine past its printed page (B-13's class).
function fcSpeciesCite(cites) { return fcStepCite(cites, 'species', 'eote'); }
// THE SKILLS STEP'S CITES ARE PER LINE AND DERIVED (`skills-transplant` R-ST-4): no typed printed
// figure lives in this file. `creator_step_cites.json` (scripts/derive_step_cites.py) records the
// CORPUS pages read for each line's core book and prints the page through the converter; the sheet
// reads it by `gameLine`. Absent line → no cite chip, never Edge's for everyone (R-EQW1).
/* THE COLLAPSED STRING IS DERIVED HERE AND STORED NOWHERE (ruling `tree-cell-refusal-home` ITEM 5,
   card `step-cites-per-page`). The registry used to carry a `cite` field — "Edge of the Empire
   p.34–35" — and that string was the problem it looks like the solution to: it is a PAGE REFERENCE
   in a file whose citations are audited by the text AROUND them, so a multi-page entry put two
   claims next to a quote that no single page carried. The registry now records one quote PER PAGE
   and no collapsed string at all, so every page reference in that file sits beside its own words.
   `p.34, p.35` collapses to `p.34–35`; a GAP STARTS A NEW GROUP, which is the whole point — the
   old generator printed first-and-last and rendered [34, 35, 92] as "p.34–92", a rule claimed
   across fifty-nine pages. This is the ONLY implementation of that rule in the repo; the generator
   deliberately prints its raw page list rather than keeping a second one (PRAXIS §5). */

// ═══ B-563 · THE REVISION REBUILD — SHARED SHELL (increment 1c) ═════════════════════════════════
// FCBrowseSheet (the desktop transplant's browse frame: rail + read + plate, shared by species,
// career and specialization), the step cites read from creator_step_cites.json (fcStepCiteStrings,
// fcStepCite, fcCitePages…), the game-line plates, pickedIdsOf, the wave nav. VERBATIM from
// 822015f1; INERT until a step's render block reads it.
function FCBrowseSheet(p) {
  var rec = p.selected;
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal;

  /* ── THE PHONE BAND (`phone-career-census` R-PCC-1) ──────────────────────────────────────────
     ONE COMPONENT, TWO BANDS — the shape `FCGameLineSheet` took one step over, and R-PPG-6's
     template says the shared component is restyled under the phone chrome rather than copied into
     a phone-only one. WHY IT LIVES HERE rather than in a new `FCCareerSheetPhone`: everything the
     frame draws is already a prop of this sheet — the head (`stepName`, `lead`, `cites`), the list
     (`rows`, `selected`, `onPick`, `listTitle`), the ledger (`soFar`) and the commit
     (`onConfirm`) — so a separate component would take the same eight props, call the same
     builders, and become the second place a career screen is described. What the phone adds is an
     ARRANGEMENT, and three slots carry it: `phoneStatus` (the strip's rows, which are the STEP'S
     nouns and must not be hardcoded in a shell four callers share), `phoneRows`/`phoneDivideAfter`
     (the picker's own grouping) and `phoneRead` (the read content in the frame's order, built at
     the call site from the SAME `fcReadProse`/`fcReadSkills` calls the desktop pane uses).
     A caller that passes none of them cannot reach this branch, so the specialization step and the
     two sheet-mounted rails are untouched by construction.

     WHAT THIS RETIRES AT MOBILE: `FCCareerBrowser`, `FCCareerCard`, `FCCareerDetail` and the
     `FCSearchBar` call inside the browser are no longer mounted for the career step at 390 — the
     approved frame draws no search affordance and the desktop rail was already ruled to have none
     ("twenty careers is a list you read, not one you filter", `visual-revision-cluster-one-career-
     gate` R-C1C-3). B-516's CAREER half closes by RETIREMENT, not by a label. The components stay
     for their other callers and are untouched.

     MOCKUP: `phone_career()` @ 6051229c on the lab's board, sheet `lab/holocron/phone_approval
     .html` @ c0744b3a, APPROVED by Jason 2026-09-09 (`parallel-pull-grant` A3, R-PPG-A3-1); the
     picker's CONTENTS are Jason's own word of the same day, "whole shelf" (`phone-career-census`
     R-PCC-A1-1 @ 09e9be31). Fixture `test/fixtures/board_career_phone.html`. */
  if (p.band === 'mobile' && p.phoneRead) {
    var pRows = p.phoneRows || p.rows || [];
    var divideAfter = p.phoneDivideAfter || 0;
    return (
      <FCPhoneShell band={p.band}
        stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
        steps={p.steps} stepLabels={p.stepLabels}
        onBack={p.onBack} onJump={p.onJump}
        status={p.phoneStatus}
        act={
          <button type="button" className="hp-commit" data-creator-commit="1"
            onClick={(rec && !p.commitDisabled) ? p.onConfirm : undefined}
            disabled={!rec || !!p.commitDisabled}>
            {rec ? <React.Fragment>{'Select ' + rec.name + ' '}<span>{'›'}</span></React.Fragment>
              : 'Choose one to continue'}
          </button>
        }>

        {/* The phone's own spacing, read off the fixture: a 14px lift and a 10px gap where the
            desktop head uses 12px and 11px. Neither is in a rule, so neither could be derived. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
        </div>
        <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName}</h1>
        <div className="hp-desig-rule" />
        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
          {p.lead ? <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}
            dangerouslySetInnerHTML={{ __html: p.lead }} /> : null}
          {/* ONE CITE CHIP, because the board draws one — `span.hp-cite` is a named object and a
              second would red DOM parity. The chip takes the FIRST of the SAME TOC-judged strings
              the desktop head prints (`creator_step_cites.json` through `fcStepCiteStrings`), never the
              frame's own "Edge of the Empire p.53", which is an unjudged designer placeholder of
              the class `visual-revision-cluster-one-career-gate` already refused once. Against the
              LEGACY phone step, which drew no citation at all, this is +1 chip and never −1. */}
          <div style={{ marginTop: '10px' }}>
            {(p.cites || []).slice(0, 1).map(function(c) {
              return (
                <span className="hp-cite" key={c} style={{ marginRight: '7px' }}
                  data-step-cite={String(p.stepName || '').toLowerCase()}>{c}</span>
              );
            })}
          </div>
        </div>

        {/* THE PICKER, in the species option-group idiom (`FCSpeciesSheetPhone`'s own `<details
            class="hp-unit">` → `hpw-scroll hp-picklist` → `hp-plate[data-pad=data]` → `hp-row`).
            COLLAPSED, as the frame draws it: the summary names the current pick, which is what a
            player on a phone needs to see; opening it is one tap. */}
        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
          <h2>{p.listTitle}</h2>
          <span className="hp-cite">{String(pRows.length)}</span>
        </div>
        {/* OPEN UNTIL A PICK IS MADE, FOLDED AFTER — `FCSpeciesSheetPhone`'s own `open={!pickedIds
            .length}` rule, and the approved frame is the folded half of it (it draws a career
            chosen and the picker shut). The unfolded half is the state no frame speaks to, and
            leaving a list a player has not used yet CLOSED would hide the whole catalogue behind a
            tap on first arrival — the just-looking moment, which the species picker already
            answered this way. */}
        <details className="hp-unit" data-fc-phone-picker="1" open={!rec}>
          <summary>
            <div className="hp-head">
              <span className="hp-name">
                {rec ? rec.name : ('Choose — ' + pRows.length + ' to read')}</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          <div className="hpw-scroll hp-picklist" style={{ marginTop: '9px' }}>
            <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
              {pRows.map(function(row, i) {
                return (
                  <React.Fragment key={row.id}>
                    {/* THE DIVIDER — the LIVE grouping, kept. Jason's word on this picker's
                        contents is "whole shelf" (R-PCC-A1-1), so it lists every career from the
                        enabled books with the player's own line first and the rest under this
                        rule, exactly as the legacy phone browser and the desktop rail's own
                        footnote already describe the catalogue. It is BOARD VOCABULARY —
                        `hp-none`, the register's muted quiet-label register — rather than the
                        legacy's six inline style declarations, and it carries no new hue.
                        THE FRAME DOES NOT DRAW IT, because the frame drew the six-career line
                        instead of the shelf; that divergence is one fact with one owner and it
                        is named at the gate rather than hidden by a declaration. */}
                    {(divideAfter > 0 && i === divideAfter && divideAfter < pRows.length) ? (
                      <div className="hp-none" data-career-divider="1"
                        style={{ padding: '14px 4px 6px', textAlign: 'center' }}>
                        {'OTHER GAME LINES'}</div>
                    ) : null}
                    {fcRegisterRow(row, rec, p.onPick)}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </details>

        {p.phoneRead}

        {/* SO FAR, folded into a `<details>` at the foot — the characteristics phone sheet's own
            idiom (`FCCharSheetPhone`), drawn here by the board too. Same `soFar` rows the desktop
            rail prints, same `hp-none` for a cell not yet filled. */}
        <details className="hp-unit" data-fc-phone-sofar="1">
          <summary>
            <div className="hp-head">
              <span className="hp-name">{'So Far'}</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">
                      {r.v ? r.v : <span className="hp-none">Not yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
        </details>
      </FCPhoneShell>
    );
  }

  return (
    <div className="hpw-shell">
    {/* `data-browse-step`: the step this generic sheet is drawing, so paper.css can rule ONE browser
        at ONE band (B-594's career snapshot) without touching the others. */}
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-browse-step={String(p.stepName || '').toLowerCase()}>

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'\u2039\u00a0\u00a0Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      {/* THE GRID IS THE STEP'S, and the board says which. `hpw-grid` alone is the SPECIES step's
          four-column template — the board's own comment calls it that — and `hpw-grid-spend` is
          the two-column override the spend steps take. Career and spec are spend-shaped; species
          is not, and it brings a fourth pane (the ledger) with it. One shell, two templates,
          because that is exactly what the board has. */}
      <div className={'hpw-grid' + (p.wideGrid ? '' : ' hpw-grid-spend')}>

        {/* ── THE RAIL: the budget, what is settled, and THE LIST ITSELF ─────────────────────── */}
        {/* THE AREA IS THE TEMPLATE'S, NOT THE PANE'S. `hpw-grid-spend` names this column `rail`;
            the species step's four-column `hpw-grid` names it `index`, and the stylesheet already
            assigns that. An inline `rail` was overriding it and putting the pane in an IMPLICIT
            column — six tracks where the board has four, and every pane 90px narrow. Inline styles
            win over any selector, so a hardcoded area is the one thing a template cannot correct. */}
        <div className="hpw-index hp-pane"
          style={p.wideGrid ? undefined : { gridArea: 'rail' }} data-creator-rail="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          {/* `data-rail-section` is a SUITE HOOK (R-TTP-1 allows them) and it is carried forward
              from the legacy rail rather than invented: the concept's §4 sections are the same
              three, so the instruments that ask for them by name keep working across the
              transplant instead of each one learning the board's class names. */}
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool} XP</dd></div>
              <div className="hp-xp"><dt>Spent here</dt><dd>{p.xpSpentHere} XP</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Remaining</dt>
                <dd data-creator-char-purse={p.xpRemaining}>{p.xpRemaining} XP</dd></div>
            </dl>
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {p.soFar.map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">
                      {r.v ? r.v : <span className="hp-none">Not yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>

          {/* ONE SECTION OR SEVERAL — the same objects either way (R-WUS-1), in the board's own
              nesting (measured at pin 881037eb, `board_spec_1366`). The FIRST section's head sits
              OUTSIDE the scroll; everything after it — that section's plate, then each further
              (head, plate) pair — sits INSIDE ONE `hpw-scroll`, so the shelves scroll TOGETHER as
              one list rather than as two boxes that scroll apart. A single-section rail therefore
              emits exactly what it always did: head, scroll, plate.

              `sections: [{title, cite, rows}]` is OPTIONAL and the single-list callers do not pass
              it: they keep `listTitle`/`listCite`/`rows`. That is the whole reason the prop is
              additive rather than a rewrite — the three other rails that consume this component
              (career at the career step, and the two the sheet mounts) must not move at all, and
              the parity sweep is what proves they did not.

              The SPEC step is the first caller with two: its career's own specializations, then the
              21 universal ones. Before this the rail could only say one thing, so `fcSpecRows` said
              the career's and the other 21 were unreachable at this band — 21 specs including every
              route into being Force-sensitive. */}
          {(function() {
            var secs = p.sections
              || [{ title: p.listTitle, cite: p.listCite, rows: p.rows }];
            var head = function(sec, key, first) {
              return (
                /* 22px ABOVE A SECOND SECTION, 20px above the first — the board's own values,
                   measured (`smoke_board_style_parity` caught the 2px as a real divergence at
                   object #53, not eyeballed). The extra two pixels are what separate one shelf
                   from the next; the first head has nothing above it to separate from. */
                <div className="hp-sechead" style={{ margin: (first ? '20px' : '22px') + ' 0 10px' }} key={key}
                  {...(sec.delta ? { 'data-ruled-delta': sec.delta } : {})}>
                  <h2>{sec.title}</h2>
                  <span className="hp-cite">{sec.cite}</span>
                </div>
              );
            };
            var plate = function(sec, key) {
              return (
                /* The board's own inline padding on this plate — 6px 12px against the derived
                   11/12. A rail of twenty-one rows is a denser object than a read plate, and the
                   board tightens it here rather than in the shared rule. */
                <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }} key={key}>
                  {/* `hp-index` wraps the register's rows on the species step and nothing else's —
                      the board draws it there and not on the career or spec rails. ONE row builder
                      serves both, so the two rails can never drift apart by being written twice. */}
                  {/* B-377 — THE COUNT IS AN ATTRIBUTE, NOT A SENTENCE TO PARSE. The legacy list
                      carried `data-species-count="1"` — a MARKER, with the number only in its prose
                      ("537 SPECIES AVAILABLE"), so anything wanting the figure had to read and parse
                      copy that the design owns and may change. The rebuilt register carries the
                      NUMBER as the value. Named by the CALLER so this shared component does not
                      hardcode one step's noun: `indexWrap` is the species-only switch today, and a
                      later step setting it must not silently inherit a species-named hook. Mounted
                      on `hp-index`, which wraps EVERY rendered row (no virtualisation here), so the
                      attribute and the row count are the same fact rather than two derivations. */}
                  {p.indexWrap
                    ? <div className="hp-index"
                        {...(p.countAttr ? { [p.countAttr]: sec.rows.length } : {})}>
                        {sec.rows.map(function(row) { return fcRegisterRow(row, rec, p.onPick); })}
                      </div>
                    : sec.rows.map(function(row) { return fcRegisterRow(row, rec, p.onPick); })}
                </div>
              );
            };
            return (
              <React.Fragment>
                {head(secs[0], 'h0', true)}
                {/* THE SEARCH PLATE — the board draws it as a flat, input-shaped plate above the
                    scroll. Its frame shows a static placeholder because a drawing cannot type; ours
                    is a real input, which is the difference the ruling confirmed is deliberate (the
                    species register searches, the career rail does not). The input carries no board
                    class, so the DOM layer sees the same tree either way. The one rail that
                    searches has exactly one section. */}
                {p.onSearch ? (
                  <div className="hp-plate" data-flat="1" data-input="1">
                    <input type="search" value={p.searchValue || ''} data-register-search="1"
                      placeholder={p.searchPlaceholder}
                      aria-label={p.searchPlaceholder}
                      onChange={function(e) { p.onSearch(e.target.value); }}
                      style={{
                        width: '100%', border: 0, background: 'transparent', outline: 'none',
                        padding: '11px 12px', fontFamily: 'var(--font-mono)', fontSize: '12px',
                        letterSpacing: '.12em', color: 'var(--doc-ink)', textTransform: 'uppercase',
                      }} />
                  </div>
                ) : null}
                <div className="hpw-scroll" style={p.onSearch ? { marginTop: '9px' } : undefined}>
                  {plate(secs[0], 'p0')}
                  {secs[0].foot || null}
                  {secs.slice(1).map(function(sec, i) {
                    return (
                      <React.Fragment key={sec.title || i}>
                        {head(sec, 'h' + (i + 1), false)}
                        {plate(sec, 'p' + (i + 1))}
                        {/* A section may state where its own membership came from, and say what it
                            withheld — both INSIDE the scroll, after the rows, which is where the
                            board draws them. */}
                        {sec.foot || null}
                      </React.Fragment>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          })()}

          <div className="hp-railact" data-rail-section="commit">
            <button type="button" className="hp-commit" data-creator-commit="1"
              onClick={(rec && !p.commitDisabled) ? p.onConfirm : undefined}
              disabled={!rec || !!p.commitDisabled}>
              {rec ? <React.Fragment>{'Select ' + rec.name + ' '}<span>{'\u203a'}</span></React.Fragment>
                : 'Choose one to continue'}
            </button>
            {/* CLASS C — A GATED COMMIT SAYS WHAT WOULD CLEAR IT (R-SCP-1; the lab's refusal
                frame, build.py :4183-4196). B-19's gate is UNCHANGED and correct; what was wrong
                is that it refused in SILENCE. It ships now and not earlier because the cure has
                to be performable: while the act was invisible this sentence would have named
                something the player could not do, which is the C-MU-4 dead-end class.
                In the board's own `hp-none` idiom, beside the commit, as the game-line step
                already hangs its consequence note. */}
            {rec && p.commitNote
              ? <span className="hp-none" data-commit-refusal="1">{p.commitNote}</span>
              : null}
          </div>
        </div>

        {/* ── THE HEAD ──────────────────────────────────────────────────────────────────────── */}
        <div className="hpw-head"
          style={p.wideGrid ? undefined : { gridArea: 'head', display: 'block' }}>
          {/* `hpw-steps` wraps the wave on the SPECIES head and on no other — the board draws it
              there because that head sits in the four-column grid and the wave takes its own row.
              An extra wrapper is invisible to a reader and not to the DOM layer, which named it
              at divergence #31. */}
          {p.stepsWrap ? <div className="hpw-steps">{fcWaveNav(p)}</div> : fcWaveNav(p)}
          {/* A STEP MAY BRING ITS OWN HEAD. Career and spec share the default below — slash,
              maker, designation, rule, lead plate, cites — because the board draws them the same.
              SPECIES does not: its designation is the SELECTED SPECIES rather than the step, its
              maker counts entries, and it carries a characteristics rail the other two have no
              use for. Five more booleans would have made this shell a configuration language;
              a slot keeps it a component, and the wave above stays shared because all three do
              draw that identically. */}
          {p.head ? p.head : (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <span className="hp-slash"><i /><i /><i /></span>
              <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
            </div>
            <h1 className="hp-desig">{p.stepName}</h1>
            <div className="hp-desig-rule" />
            <div className="hp-plate" data-pad="read"
              style={{ marginTop: '13px', maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              {p.lead ? <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}
                dangerouslySetInnerHTML={{ __html: p.lead }} /> : null}
              {/* THE FOOTNOTE'S ONE NON-REDUNDANT CLAUSE, carried IN the plate at the mid band
                  (B-594). At 1194×810 the footnote under the head cost the read pane 158px and
                  mostly restated the rail ("20 · FROM YOUR ENABLED BOOKS", every row naming its
                  book); paper.css hides it there and shows this line instead. Drawn nowhere else. */}
              {p.footnoteMid ? (
                <p className="hp-lead" data-browse-footnote-mid="1" style={{ margin: '8px 0 0' }}
                  dangerouslySetInnerHTML={{ __html: p.footnoteMid }} />
              ) : null}
              <div style={{ marginTop: '11px' }}>
                {p.cites.map(function(c, i) {
                  return (
                    <span className="hp-cite" key={c}
                      /* The SHELL is generic; the step is not. Hardcoding "career" here would
                         mislabel the next browser's cites the day it uses this sheet. */
                      data-step-cite={String(p.stepName || '').toLowerCase()}
                      style={i ? { marginLeft: '7px' } : undefined}>{c}</span>
                  );
                })}
              </div>
            </div>
            {/* The board's footnote under the head: what the list is drawn FROM. OPTIONAL — the
                career head carries one because the shelf question belongs there; the
                specialization head draws none, and an empty paragraph is an object the board
                does not draw. */}
            {p.footnote ? (
              <p className="hp-prose hp-prose-sm" style={{ margin: '11px 0 0' }} data-browse-footnote="1"
                dangerouslySetInnerHTML={{ __html: p.footnote }} />
            ) : null}
          </div>
          )}
        </div>

        {/* THE PORTRAIT PANE — `hpw-plate`, the species step's own, and the pre-ruled home for it
            (D-SP-2). The board carries the art TWICE in one tree: here as a pane that splits out
            at wide bands, and again inside the read pane's `hpw-foldplate` where it folds back in.
            Both are in its DOM and CSS shows one per band, so both are emitted. */}
        {p.platePane ? (
          <div className="hpw-plate hp-pane">{p.platePane}</div>
        ) : null}

        {/* ── THE READ PANE: the selected row's detail ───────────────────────────────────────── */}
        <div className="hpw-read hp-pane" style={p.rawRead ? undefined : { gridArea: 'spend' }}>
          {p.rawRead ? (rec ? p.readLeft : p.emptyRead) : (
          <div className="hp-spendwrap">
            {/* ══ THE STACKED ARRANGEMENT — `readLead` opts a step into IDENTITY → HERO → TEACHING
                (the board's layout pass, §73.1; census P1 RETIRES the old order; R-LLA-A2-1).

                The two arrangements are the SAME THREE OBJECTS in a different ORDER, which is why
                this is a branch here and not a second component: `hp-spendwrap` holds an unclassed
                full-width block, `hp-glosswrap`, and the two-column `hp-spend`. Today's order puts
                `hp-spend` first and the gloss under it; a step that passes `readLead` gets the
                lead block first, the gloss (its HERO) second, and the teaching columns last at the
                board's own `margin-top:24px`.

                WHY THE ORDER IS THE POINT, in the board's words: the tree used to render LAST,
                under Bonus Career Skills and the notes — so the step's own lead ("the tree is where
                your build's edge comes from") pointed at the thing furthest down its own page,
                which is hero-features-get-front-doors failing in place. THE GATES RIDE THE LEAD
                BLOCK, not the teaching: a restriction is the reason you may not have this at all,
                and putting it after the tree would let a reader study a tree they cannot take.

                NO PROP, NO CHANGE. Every step that passes no `readLead` renders exactly what it
                rendered before, so this carries no blast radius beyond the one step that opts in. */}
            {rec && p.readLead ? (
              <React.Fragment>
                <div>{p.readLead}</div>
                {p.belowRead ? <div className="hp-glosswrap">{p.belowRead}</div> : null}
                <div className="hp-spend" style={{ marginTop: '24px' }}>
                  <div>{p.readLeft}</div>
                  <div>{p.readRight}</div>
                </div>
              </React.Fragment>
            ) : (
            <React.Fragment>
            <div className="hp-spend">
              {rec ? (
                <React.Fragment>
                  {/* TWO COLUMNS, AND THE WRAPPERS ARE THE COLUMNS. `hp-spend` is a grid whose
                      CHILDREN are its tracks, so these unclassed divs are not decoration: without
                      them the secheads and plates become the tracks themselves and the pane's
                      widths come out transposed (the read plate 420 where the board draws 660).
                      They carry no class, which is exactly why the DOM layer cannot see them —
                      it walks NAMED objects — and why the style layer is the one that found it.

                      The COLUMNS are the shell's; their CONTENTS are the step's. */}
                  <div>{p.readLeft}</div>
                  <div>{p.readRight}</div>
                </React.Fragment>
              ) : (p.emptyRead || null)}
            </div>
            {/* BELOW THE TWO COLUMNS, NOT INSIDE ONE — `hp-glosswrap` is a SIBLING of `hp-spend`
                in the board's tree, so anything full-width under the read pane goes here. The
                specialization's talent preview is the first user; the characteristics sheet uses
                the same wrapper for its gloss. Putting it inside the right column instead cost
                exactly two objects and the DOM layer said so at divergence #122. */}
            {rec && p.belowRead ? (
              <div className="hp-glosswrap">{p.belowRead}</div>
            ) : null}
            </React.Fragment>
            )}
          </div>
          )}
        </div>

        {/* THE FOURTH PANE — the species step's ledger column. Present only where the board draws
            one, which is why it is a slot rather than a branch. */}
        {p.ledger ? (
          <div className="hpw-ledger hp-pane">{p.ledger}</div>
        ) : null}
        {/* THE CHOICE PANE — its own column on the species step, holding the pick-one gate a few
            species carry (B-19). Its HEAD is drawn whether or not this species has a choice, which
            is what the board's Wookiee frame shows: the column is part of the composition, not a
            thing that appears and disappears. The POPULATED form is not built here — the ruling
            pre-ruled it as `hp-row` pickers and the board actually draws `hp-head` buttons with an
            `hp-nest` disclosure, which is a divergent reading and goes back up (D-SP-2). */}
      </div>
    </div>
    </div>
  );
}

/* ══ FCCharSheet — THE TRANSPLANT (ruling `template-transplant-playbook` R-TTP-1/2 step 3) ═════
   THIS COMPONENT EMITS THE BOARD'S TREE. Not "styled toward it", not "conformant with it" — the
   same elements, classes, nesting and order as `test/fixtures/board_chars_1920.html`, which is the
   board's own markup extracted from the pinned blob. `test/board_dom_parity.cjs` compares the two
   and names the first divergence, so the claim is checkable rather than argued.

   WHY THE METHOD CHANGED. Jason, on the previous lap's side-by-sides: "Nope, they look different.
   I feel like the implementer is fighting against something." The thing being fought was the old
   screen's skeleton — every lap adapted it toward the board while structure the board never drew
   survived underneath, which converges asymptotically and cannot arrive. Ledger #104.

   THE LEGACY COMPONENTS ARE DEMOTED TO DATA SOURCES. Selectors, handlers and computed values flow
   in; nothing legacy owns layout any more. Blocks the board does not draw do not need finding and
   removing — they simply have nowhere to be.

   THE ALLOWED DIFFERENCES ARE R-TTP-1'S, EXHAUSTIVELY, and each one that appears below says which:
     · text and data values (every number on this page)
     · the RULED deltas — the refund control (template gate A1-2, kept because the board's own
       overspent frame names a way out it does not draw), the corpus-verified p.39 cite, and
       A1-4's copy
     · `data-*` hooks the suites address
     · React artifacts that produce no DOM difference
   Anything else wanted here routes to the architect BEFORE it ships.

   THE SHELL YIELDS LAYOUT, NEVER BEHAVIOUR (Addendum 2, A2-2). This step owns its sheet, so the
   creator's shared chrome stands aside for it — but back, route identity, the walk's contracts and
   the bar's state all survive: the runhead's control below IS the back affordance, wired to the
   same handler the shared bar used, and the wave's buttons drive the same `goToStep` the rail's
   step list drove (A2-1 — the buttons must actually jump). */

/* THE GAME LINE STEP'S LEAD AND ITS PLATES' SENTENCES ARE CUT (copy-voice-sweep player/app.jsx:14008..14011
   and :14133/:14138/:14143, D-289): the plates keep their name, mechanic word and cite; the lead goes with its words. */

/* FC_STEP_CITES IS RETIRED (B-1349, ruling `release-r7-bug-burndown` ADDENDUM 46). It was a hand-typed table of
   characteristics / gameline / career / specialization cites whose pages were judged "by TOC" (R-CVM-2, B-330) —
   and the index's TOC is the PDF's own bookmarks, i.e. CORPUS pages, so every row printed a corpus page as a
   printed one (the characteristics chip named Step 6's corpus page, nine past its printed page). The same pages, each re-read
   with its own sentence, now live in `scripts/derive_step_cites.py` and reach the creator through
   `creator_step_cites.json` (`gameData.charbuild.stepCites`), printed through the one converter like every other
   step's cite. No typed page figure lives in this file. */

/* The step's cites as PRINTED STRINGS — one reader for both the shared chip component and the
   transplanted heads, so a step can never show a page the table does not carry. A step with no
   verified citation returns an empty list and its head prints nothing (never an invented one). */

function fcStepCite(cites, step, line) {
  var m = cites && cites[step];
  var e = m && m[line];
  if (!e) return '';
  var pages = fcCitePages(e.printed || []);
  return pages && e.book ? (e.book + ' ' + pages) : (pages || '');
}

/* THE SAME CITATION, AS THE BOARD PRINTS IT: one chip per PRINTED page, the book named once and
   the rest bare — the convention `fcStepCiteStrings` already writes for the in-file table, applied
   to the canonical `creator_step_cites` entries the creator's transplanted steps read. The board's
   phone frames draw two chips in the lead plate where the app drew one collapsed string
   ("Edge of the Empire p.34–35"), which is ONE object's difference in a 192-object tree — a real
   parity divergence over a citation the app already holds at page grain. The PAGES are ours (34,
   35 — corpus-verified, `cite-verification-method` R-CVM-2); the board's own numbers on this frame
   are the designer's placeholders and are not copied. Empty entry, empty list: a step with no
   verified citation shows none, never an invented one. */

function pickedIdsOf(group, optionPicks) {
  var recorded = optionPicks[groupIdSlug(group.name)];
  if (Array.isArray(recorded)) return recorded.filter(function(x) { return x != null; });
  if (recorded != null) return [recorded];
  var pre = pickedIdFor(group, optionPicks);      // preselect stays the single-pick rule's
  return pre != null ? [pre] : [];
}

// The career step's lead keeps its first sentence and takes the census TIGHTEN for the rest (copy-voice-sweep
// player/app.jsx:23602:15, D-289); a name of its own, so the tightened sentence stands as its own string.
var FC_CAREER_LEAD_RULE = 'Career free ranks go only to career skills, which cost 5 XP less.';   // B-1663: D-433 item 4, verbatim

var FC_GAMELINE_PLATE = {
  eote: { sub: 'A Shared Debt' },
  aor: { sub: 'Your Stake in the Rebellion' },
  fad: { sub: 'Light and Dark' },
};

/* THE LEAD'S COPY, VERBATIM FROM THE FIXTURE (P1(a), ruling `template-visual-punch-list`).
   Jason: "Keep description verbage from mock." So this is the board's own sentence, character for
   character, and NOT the app's step subtitle.

   THE RECORDED CONSEQUENCE, because the ruling asked for it rather than a silent absorption: this
   SUPERSEDES A1-1's question-relocation on a transplanted step. "How strong is your foundation?"
   does not appear here any more — the standfirst is the board's copy. As the remaining screens
   transplant, each takes its own fixture's words, which retires the twelve-question set
   surface-by-surface. That is a walk-wide voice decision A1-1 had reserved for Jason, and this is
   him making it in practice.

   `FC_STEP_INFO.characteristics.subtitle` is untouched: it still feeds the UNTRANSPLANTED path and
   the suites that read it, and it becomes dead on this step only when the step is transplanted. */
var FC_CHAR_LEAD =
  'This is the <b>only</b> time XP can raise a characteristic (afterward only rare talents do). '
  + 'Cost is <b>new-rank &times; 10</b> (3&rarr;4 = 40, 4&rarr;5 = 50), you can&rsquo;t drop below '
  + 'your species base, and the creation max is <b>5</b>.';

/* The gloss's ONE cite. Every entry below carries the same page because the definitions are one
   printed spread, so the head states it once. Derived, not typed: `FC_CHAR_GLOSS_CITE` is asserted
   against the entries' own `cite` fields by `test/doc_token_spelling.cjs` legs 4-5, so a
   future entry from a different page cannot hide under a head cite that no longer covers it. */
var FC_CHAR_GLOSS_CITE = 'EDGE OF THE EMPIRE P.16';

var FC_CHAR_GLOSS = [
  { group: 'Physical', items: [
    { attr: 'brawn', sub: 'Muscle & Toughness',
      body: '<b>Brawn</b> is raw physical power. It drives <b>Athletics</b> and <b>Brawl</b>, and it sets two of your derived stats: your <b>soak</b> (the damage you shrug off) and your <b>wound threshold</b>.',
      cite: 'eote p.16, p.103' },
    { attr: 'agility', sub: 'Speed & Precision',
      body: '<b>Agility</b> is coordination and dexterity. It powers <b>Coordination</b> and is the key stat for <b>ranged combat</b> — Ranged (Light) and Ranged (Heavy).',
      cite: 'eote p.16' },
  ] },
  { group: 'Mental', items: [
    { attr: 'intellect', sub: 'Reason & Education',
      body: '<b>Intellect</b> is book-learning and analysis. It powers <b>Astrogation</b>, <b>Computers</b>, and every <b>Knowledge</b> skill.',
      cite: 'eote p.16' },
    { attr: 'cunning', sub: 'Guile & Instinct',
      body: '<b>Cunning</b> is street-smarts and craftiness. It powers <b>Deception</b>, <b>Perception</b>, and <b>Survival</b>.',
      cite: 'eote p.16' },
  ] },
  { group: 'Personality & force of spirit', items: [
    { attr: 'willpower', sub: 'Discipline & Nerve',
      body: '<b>Willpower</b> is self-control and mental fortitude. It powers <b>Coercion</b> and <b>Vigilance</b>, and it sets your <b>strain threshold</b>.',
      cite: 'eote p.16, p.103' },
    { attr: 'presence', sub: 'Charisma & Force of Will',
      body: '<b>Presence</b> is charm and force of personality. It powers <b>Charm</b> and <b>Leadership</b> — your social spine.',
      cite: 'eote p.16' },
  ] },
];

function FCCharGloss({ character, speciesData }) {
  if (!character || !speciesData) return null;
  var base = (speciesEff(speciesData, character.speciesOptionPicks).characteristics || {});
  return (
    <div data-char-gloss="1">
      {/* ONE CITE, ON THE HEAD (R-TCP-4). The first cut printed a page chip on each of the six
          entries and the census counted 7 cites against the board's 3. The board prints one, on
          this head, and its economy is right: all six definitions come from ONE book and ONE page,
          so six chips is the same fact printed six times.
          IT IS NOT the step's `characteristics` cite. That one carries the page that governs RAISING a
          characteristic (creator_step_cites.json, B-1349); these are DEFINITIONS and live on
          p.16. Two different claims, two different pages — which is exactly why the head cite is
          written from the gloss's own data rather than borrowed from the step's. */}
      <div className="hp-sechead">
        <h2>What Each One Is</h2>
        <span className="hp-cite" data-gloss-cite="1">{FC_CHAR_GLOSS_CITE}</span>
      </div>
      <div className="hp-gloss">
        {FC_CHAR_GLOSS.map(function(col) {
          return (
            <div className="hp-gloss-col" key={col.group}>
              <div className="hp-gloss-gp">{col.group}</div>
              {col.items.map(function(it) {
                var cur = character.characteristics[it.attr] || 2;
                return (
                  <div className="hp-gloss-it" key={it.attr}
                    data-raised={cur > (base[it.attr] || 2) ? '1' : undefined}>
                    <div className="hp-gloss-hd">
                      {/* R-CPD-8: its twin at the other band already read the word */}
                      <span className="hp-gloss-nm">{FC_CHAR_WORDS[it.attr]}</span>
                      <span className="hp-gloss-rt">{cur}</span>
                      <span className="hp-gloss-sub">{it.sub}</span>
                    </div>
                    <p className="hp-prose hp-prose-sm" style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--doc-ink-muted)', lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: it.body }} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* The WHAT-THIS-CHANGES rows as DATA. `FCWhatThisChanges` computed them and drew them in the same
   breath; R-TTP-1 demotes the legacy components to data sources, so the arithmetic is lifted out
   here and the board's own `hp-ledger` draws it. Same shared core (`CharResolve.computeDerived`),
   same empty-derivedBonuses input, same three rows — nothing about the numbers changed. */
function fcCharChangeRows(character, speciesData) {
  if (!character || !speciesData) return [];
  var eff = speciesEff(speciesData, character.speciesOptionPicks) || {};
  var chars = character.characteristics || {};
  function derive(extra) {
    var c = Object.assign({}, chars);
    if (extra) c[extra] = (Number(c[extra]) || 2) + 1;
    return CharResolve.computeDerived({
      characteristics: c, derivedBonuses: {},
      woundBase: eff.wound_threshold_base, strainBase: eff.strain_threshold_base,
    }, null, window._canonicalCharbuild) || {};
  }
  var now = derive(null);
  return [
    { k: 'Wound threshold', formula: (eff.wound_threshold_base || 10) + ' + Brawn',
      from: now.woundThreshold, to: derive('brawn').woundThreshold },
    { k: 'Strain threshold', formula: (eff.strain_threshold_base || 10) + ' + Willpower',
      from: now.strainThreshold, to: derive('willpower').strainThreshold },
    { k: 'Soak', formula: 'Brawn, before armour',
      from: now.soak, to: derive('brawn').soak },
  ];
}


var FC_CHAR_ATTRS = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];

function fcCharAt(character, a) { return (character.characteristics || {})[a] || 2; }
function fcCharRaised(character, base, a) { return fcCharAt(character, a) > (base[a] || 2); }

function FCbRail({ character, base, size, cols, onPick, picked }) {
  return (
    <div className="hp-railwrap">
      {/* `data-size` and `data-cols` are the board's own VARIANT SWITCHES, not decoration: the wide
          sheet's rail is the hero (`lg`) and the phone's folds to 3x2 (`cols="3"`). Passing the
          hero size to the phone clipped its labels to "WILLPOWE" — the guard could not see it
          because it compared elements and classes and ignored data attributes, which it no longer
          does. */}
      <div className="hp-rail" data-size={size} data-cols={cols}>
        {FC_CHAR_ATTRS.map(function(a, idx) {
          var up = fcCharRaised(character, base, a);
          var inner = (
            <React.Fragment>
              <span className="hp-char-val" data-char-capsule={up ? '1' : '0'}>
                <span className="hp-char-bead">{fcCharAt(character, a)}</span>
              </span>
              <i className="hp-char-lab">{FC_CHAR_WORDS[a]}</i>
            </React.Fragment>
          );
          /* A TILE IS A DOOR WHEN THE SHEET ASKS (B-598, Jason: "it can just load the characteristic
             that was tapped"): with `onPick` each tile is a button that names its characteristic
             and marks the one whose gloss is open; without it the tile is the plain figure it was. */
          if (onPick) {
            return (
              <button type="button" className="hp-char" key={a} data-raised={up ? '1' : undefined}
                data-char-pick={a} aria-pressed={picked === a ? 'true' : 'false'}
                aria-label={FC_CHAR_WORDS[a] + ' \u2014 read what it is'}
                data-tour-id={idx === 0 ? 'p-custom-chars-box' : undefined}
                onClick={function() { onPick(a); }}>{inner}</button>
            );
          }
          return (
            <div className="hp-char" key={a} data-raised={up ? '1' : undefined}
              data-tour-id={idx === 0 ? 'p-custom-chars-box' : undefined}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

/* IS A RAISE OUT OF REACH? — `creator-pre-deploy-batch` A2 (Jason: "just flash red in the remaining
   xp in 'The Build' box"). The unaffordable signal LEFT the raise row and landed on the purse, so
   the purse needs the fact the row used to hold, and it needs it in ONE place because two sheets
   draw it.
   THE PREDICATE IS "ANY RAISE THE PLAYER MIGHT WANT COSTS MORE THAN THEY HOLD", which is a 1:1
   relocation of the sentence it replaces: the old `Needs N XP · you hold M` appeared on EVERY
   unaffordable row, so the purse now carries the state exactly when at least one of those rows
   would have spoken. IMPLEMENTER'S CHOICE, disclosed in the gate: the narrower reading — fire only
   when NO raise at all is affordable — would be quieter, and it would also stay silent in the case
   the sentence was written for, a player who can still buy the cheap raise and not the one they are
   looking at.
   AT THE CAP IT IS FALSE BY CONSTRUCTION: a capped attribute offers no raise to be short of, which
   is retired Class B's whole point expressed as a mechanism (A3-1) — the purse stays quiet and
   nobody is sent after XP that cannot move the number. */
/* THE PURSE'S FLASH IS AN EVENT — ruling `creator-pre-deploy-batch` A27-4, deciding the open reading
   this seat flagged. Jason's word was "flash", and a flash answers an ATTEMPT: the purse takes the
   state when the player PRESSES a raise they cannot afford, shows it once, and settles back to
   quiet. At rest, at the cap, and for rows nobody pressed, the purse is silent — which is how
   A3-1's table holds AS WRITTEN, read per row: its purse column is the purse's answer to pressing
   THAT row.
   THE STANDING READING IS RETIRED. `data-short` used to be true whenever ANY raise was out of
   reach, which marked the purse red for most of the step's life — any purse under 30 XP, where
   every finished build ends — and at a cap with XP in hand.
   `fcRaiseOutOfReach` IS DELETED WITH IT, one symbol beyond A27-4's letter and disclosed: the ruling
   keeps it "as the per-row fact", but that fact is `afford`, computed inline in the row that needs
   it, and with the standing attribute gone the helper had ZERO callers. Delete, don't stub.
   THE SETTLE IS A TIMER, NOT A CLASS DANCE: one state, one duration, cleared on unmount. */
function useFcPurseFlash(ms) {
  var _s = useState(0), tick = _s[0], setTick = _s[1];
  var timer = useRef(null);
  useEffect(function() { return function() { if (timer.current) clearTimeout(timer.current); }; }, []);
  var fire = useCallback(function() {
    if (timer.current) clearTimeout(timer.current);
    setTick(function(t) { return t + 1; });
    timer.current = setTimeout(function() { setTick(0); }, ms || 900);
  }, [ms]);
  return [tick > 0, fire];
}

function FCbBuyRows({ character, base, xpLeft, onCharChange, onRefused, onPick }) {
  return FC_CHAR_ATTRS.map(function(a) {
    var v = fcCharAt(character, a), atMax = v >= 5, cost = atMax ? null : (v + 1) * 10;
    var afford = cost !== null && xpLeft >= cost;
    var atBase = v <= (base[a] || 2);
    // ONE FORM (`creator-pre-deploy-batch` A2-1 + A3-1, on Jason's "Retire it. Want one form."):
    // the cell prices the raise while one can be bought and is EMPTY at the cap. The three forms it
    // had — "· the last you hold", "Needs N XP · you hold M", "at cap" — are gone from the visible
    // row, and NOTHING they carried is lost:
    //   · the PRICE is now MORE constant than it was, which is what B-118 protects;
    //   · the REFUSAL is spoken, verbatim, by the button's own `aria-label`, composed separately
    //     from this string ("… — you hold 25" / "… is at the creation maximum of 5"), so a screen
    //     reader hears exactly what it heard before — the fact that made this safe to take;
    //   · the UNAFFORDABLE case is shown by the purse in The Build (below) plus the disabled button,
    //     which is B-90's "never a bare fade" satisfied by two signals before colour;
    //   · AT THE CAP the purse stays QUIET, and that silence is all of retired Class B: no amount of
    //     XP moves a capped rank, so nothing should send the player after more.
    var costText = atMax ? '' : cost + ' XP';
    return (
      <div className="hp-buy" key={a} data-raise-row={a}>
        {onPick ? (
          <button type="button" className="hp-buy-name hp-buy-name-door" data-char-pick={a}
            aria-label={FC_CHAR_WORDS[a] + ' \u2014 read what it is'}
            onClick={function() { onPick(a); }}>{FC_CHAR_WORDS[a]}</button>
        ) : <span className="hp-buy-name">{FC_CHAR_WORDS[a]}</span>}
        <span className="hp-buy-step">
          {atMax ? '\u2013' : <React.Fragment>{v}<i>{'\u2192'}</i>{v + 1}</React.Fragment>}
        </span>
        {/* NEUTRAL IN EVERY STATE: the refusal red moved to the purse with the sentence (A2-1). */}
        <span className="hp-buy-cost" data-char-cost-line={a}>{costText}</span>
        {/* BOTH BUTTONS IN ONE GRID CELL. `hp-buy` is the board's four-column grid
            (`1fr auto auto auto`) and the refund is our ruled fifth child, so as a sibling it
            wrapped to a row of its own and doubled every row's height. The wrapper is UNNAMED, so
            it changes no named-object path and the parity guard is unaffected — which is why the
            guard could not see this and the picture could. */}
        <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
        {/* REFUSED, NOT DISABLED (A27-4): a raise you cannot afford stays focusable and pressable so
            the press can ANSWER — the purse flashes. This is the skills sheet's own shipped idiom
            (`aria-disabled` + `data-refused`, the handler guarded), not a new one. AT THE CAP the
            control keeps `disabled`: no amount of XP moves it, there is nothing to answer, and the
            purse must stay quiet. */}
        <button type="button" className="hp-buy-btn" data-raise={a}
          data-at-cap={atMax ? '1' : undefined} data-afford={afford ? '1' : undefined}
          disabled={atMax}
          aria-disabled={(!atMax && !afford) ? 'true' : undefined}
          data-refused={(!atMax && !afford) ? '1' : undefined}
          aria-label={atMax ? FC_CHAR_WORDS[a] + ' is at the creation maximum of 5'
            : 'Raise ' + FC_CHAR_WORDS[a] + ' to ' + (v + 1) + ' for ' + cost + ' XP'
              + (afford ? '' : ' \u2014 you hold ' + xpLeft)}
          onClick={function() {
            if (!atMax && afford) { onCharChange(a, v + 1); return; }
            if (!atMax && !afford && onRefused) onRefused(a);   // the press the purse answers
          }}>
          {atMax ? '\u2013' : 'Raise'}
        </button>
        {/* THE RULED DELTA (composition-fix gate A1-2). The board draws no refund and its own
            overspent frame says "Refund a raise… and the way forward opens" — it names the way out
            it does not draw. C-MU-4: a dead end is not shippable. */}
        <button type="button" className="hp-buy-btn" data-ruled-delta="refund"
          data-refund={a} disabled={atBase}
          aria-label={atBase ? FC_CHAR_WORDS[a] + ' is at its species base'
            : 'Lower ' + FC_CHAR_WORDS[a] + ' to ' + (v - 1) + ', refunding ' + (v * 10) + ' XP'}
          onClick={function() { if (!atBase) onCharChange(a, v - 1); }}
          style={{ borderStyle: atBase ? 'dashed' : 'solid' }}>{'\u2212'}</button>
        </span>
      </div>
    );
  });
}

function FCbChangesTable({ rows }) {
  return (
    <table className="hp-ledger" data-what-changes="1"><tbody>
      {rows.map(function(r) {
        return (
          <tr key={r.k}>
            <td className="hp-lkey">{r.k}<span className="hp-sep">{'\u00b7'}</span>{r.formula}</td>
            <td className="hp-lval"><span className="hp-none">{r.from}</span>{' \u2192 '}{r.to}</td>
          </tr>
        );
      })}
    </tbody></table>
  );
}

function FCbGloss({ character, base, only }) {
  // ONE ENTRY (B-598): the tapped characteristic alone, in the same item markup, no columns.
  if (only) {
    var hit = null;
    FC_CHAR_GLOSS.forEach(function(col) { col.items.forEach(function(it) { if (it.attr === only) hit = { it: it, group: col.group }; }); });
    if (!hit) return null;
    return (
      <div className="hp-gloss" data-only={only}>
        <div className="hp-gloss-it" data-raised={fcCharRaised(character, base, only) ? '1' : undefined}>
          <div className="hp-gloss-hd">
            <span className="hp-gloss-nm">{FC_CHAR_WORDS[only]}</span>
            <span className="hp-gloss-rt">{fcCharAt(character, only)}</span>
            <span className="hp-gloss-sub">{hit.it.sub}</span>
            <span className="hp-gloss-gp" style={{ marginLeft: 'auto' }}>{hit.group}</span>
          </div>
          <p className="hp-prose hp-prose-sm" dangerouslySetInnerHTML={{ __html: hit.it.body }} />
        </div>
      </div>
    );
  }
  return (
    <div className="hp-gloss">
      {FC_CHAR_GLOSS.map(function(col) {
        return (
          <div className="hp-gloss-col" key={col.group}>
            <div className="hp-gloss-gp">{col.group}</div>
            {col.items.map(function(it) {
              return (
                <div className="hp-gloss-it" key={it.attr}
                  data-raised={fcCharRaised(character, base, it.attr) ? '1' : undefined}>
                  <div className="hp-gloss-hd">
                    <span className="hp-gloss-nm">{FC_CHAR_WORDS[it.attr]}</span>
                    <span className="hp-gloss-rt">{fcCharAt(character, it.attr)}</span>
                    <span className="hp-gloss-sub">{it.sub}</span>
                  </div>
                  <p className="hp-prose hp-prose-sm" dangerouslySetInnerHTML={{ __html: it.body }} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* THE BUY-OFF SECTION, shared. The board draws it on the WIDE buyout frame and its phone frame
   draws a species that has no offer, so the phone is SILENT rather than opposed — and C-MU-4 puts
   the feature above a mockup's silence: a penalty a player can buy off on a desktop and not on a
   phone is the same feature failing at a width, which is what `smoke_creation_buyout` sweeps
   AS-9's widths to catch. It caught exactly this. */
function FCbBuyOff({ offers, offerCount, taken, xpLeft, onTake, onUndo }) {
  if (!offers.length) return null;
  return (
    <div data-buyout-block="1">
      <div className="hp-sechead" style={{ margin: '0 0 11px' }}>
        <h2>Buy Off a Penalty</h2>
        <span className="hp-cite">{offerCount + ' species'}</span>
      </div>
      {offers.map(function(o) {
        var got = taken && taken[o.slug];
        // `data-buyout-xp` and `data-buyout-taken` are SUITE HOOKS (allowed by R-TTP-1) and they
        // are the row's own facts rather than text a probe has to parse out of a paragraph.
        return (
          <div className="hp-plate" data-pad="read" key={o.slug} data-buyout-row={o.slug}
            data-buyout-xp={o.xp} data-buyout-taken={got ? '1' : '0'}>
            <div className="hp-sechead" style={{ margin: '0 0 9px' }}>
              <h2>{o.name}</h2>
              <span className="hp-cite">{o.xp} XP</span>
            </div>
            <p className="hp-prose hp-prose-sm" style={{ margin: '0 0 11px' }}>{o.description}</p>
            <button type="button" className="hp-commit hp-door"
              data-buyout-action={got ? 'undo' : 'take'}
              disabled={!got && xpLeft < o.xp}
              onClick={function() { got ? onUndo(o.slug) : onTake(o.slug, o.xp); }}>
              {/* THE TAKEN ROW SAYS WHAT HAPPENED, which is the app's own contract and one the
                  board's specimen has no state for (it draws the offer untaken). Kept inside the
                  board's slot rather than beside it: same button, ruled words. */}
              {got ? 'REMOVED \u2014 ' + (got.xp || o.xp) + ' XP \u00b7 UNDO'
                : (xpLeft < o.xp
                  ? 'NEEDS ' + o.xp + ' XP \u2014 ' + (xpLeft < 0
                    ? 'OVERSPENT BY ' + Math.abs(xpLeft) + ' XP' : xpLeft + ' REMAINING')
                  : 'REMOVE PERMANENTLY \u2014 ' + o.xp + ' XP')}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ── THE CHARACTERISTICS PHONE SHEET ───────────────────────────────────────────────────────────
   A DIFFERENT TREE from the wide sheet, not the wide one under container queries — that was worth
   establishing before building it: the board gives the phone a status strip instead of the rail's
   plates, a different SECTION ORDER (rail -> Raise -> What This Changes, where the desktop runs
   rail -> What This Changes -> Raise), a gloss with no plate around it, and So Far folded into a
   `<details>` at the foot. What it shares with every other phone step is the SHELL above. */
function FCCharSheetPhone(p) {
  var base = p.base, stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal;
  // A27-4, the phone's own copy of the transient — this sheet draws both the refusing rows and the
  // purse that answers them, exactly as the desktop sheet does.
  var _flash = useFcPurseFlash(900), purseFlash = _flash[0], firePurseFlash = _flash[1];
  return (
    <FCPhoneShell band={p.band} charControls="1"
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      status={<React.Fragment>
        <div><span className="hp-status-k">Starting</span>
          <span className="hp-status-v">{p.xpPool} XP</span></div>
        <div><span className="hp-status-k">Spent here</span>
          <span className="hp-status-v">{p.xpSpentOnChars} XP</span></div>
        <div><span className="hp-status-k">Remaining</span>
          {/* THE REFUSAL TOKEN AT PHONE WIDTH, where the board is SILENT: its phone frame never
              draws an overspent state, and R-CSC-1 (refusal is the printed red) is app law that
              C-MU-4 puts above a mockup's omission. The desktop gets this from the board's own
              `.hp-xp[data-over="1"] dd`; the phone has no such rule to inherit, so it is stated. */}
          <span className="hp-status-v" data-over={p.xpRemaining < 0 ? '1' : undefined}
            data-short={(p.xpRemaining >= 0 && purseFlash) ? '1' : undefined}
            data-creator-char-purse={p.xpRemaining}
            style={{ color: p.xpRemaining < 0 ? 'var(--doc-action)' : undefined }}>
            {p.xpRemaining} XP</span></div>
      </React.Fragment>}
      act={<React.Fragment>
        {p.xpRemaining > 0 && (
          <div data-ruled-delta="unspent-note" className="hp-railnote">
            {p.xpRemaining + ' XP unspent \u2014 it carries into play'}
          </div>
        )}
        <button type="button" className="hp-commit" data-creator-commit="1"
          onClick={p.xpRemaining < 0 ? undefined : p.onNext} disabled={p.xpRemaining < 0}>
          {p.xpRemaining < 0 ? 'OVERSPENT BY ' + Math.abs(p.xpRemaining) + ' XP'
            : <React.Fragment>{'Continue to Skills '}<span>{'\u203a'}</span></React.Fragment>}
        </button>
      </React.Fragment>}>
      {/* The phone's own spacing, from its fixture: a 14px lift off the status strip and a
          10px gap, where the desktop row uses 11px and no lift. Measured, not assumed. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
      </div>
      {/* THE PHONE'S OWN SIZES, from its fixture's inline styles — 27px where the desktop clamp
          gives 30, and a 12px lead-plate margin. Neither is in a rule, so neither could be
          derived, and both are exactly the class Jason's P5 named at the other end of the
          ladder. */}
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>Characteristics</h1>
      <div className="hp-desig-rule" />
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}
          dangerouslySetInnerHTML={{ __html: p.standfirst }} />
        {/* ONE cite on the phone — the board prints one here and two on the wide sheet, and a
            count is structure, so the wide sheet's pair would be a divergence at this width. */}
        <span className="hp-cite" data-step-cite="characteristics">{p.cites[0]}</span>
      </div>

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Starting Characteristics</h2></div>
      <div className="hp-plate" data-pad="snug">
        <FCbRail character={p.character} base={base} cols="3" />
      </div>

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Raise</h2></div>
      <div className="hp-plate" data-pad="data">
        <FCbBuyRows character={p.character} base={base} xpLeft={p.xpRemaining} onRefused={firePurseFlash}
          onCharChange={p.onCharChange} />
      </div>

      <FCbBuyOff offers={p.offers} offerCount={p.offerCount} taken={p.taken}
        xpLeft={p.xpRemaining} onTake={p.onBuyoutTake} onUndo={p.onBuyoutUndo} />

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>What This Changes</h2></div>
      <div className="hp-plate" data-pad="data"><FCbChangesTable rows={p.changes} /></div>

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
        <h2>What Each One Is</h2>
        <span className="hp-cite" data-gloss-cite="1">{FC_CHAR_GLOSS_CITE}</span>
      </div>
      <FCbGloss character={p.character} base={base} />

      {/* SO FAR FOLDS on the phone — the board makes it a `<details>` at the foot rather than a
          block in a rail there is no room for. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'\u25be'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <FCbSoFarTable rows={p.soFar} />
        </div>
      </details>
    </FCPhoneShell>
  );
}

/* THE SAME CITATION, AS THE BOARD PRINTS IT: one chip per PRINTED page, the book named once and
   the rest bare — the convention `fcStepCiteStrings` already writes for the in-file table, applied
   to the canonical `creator_step_cites` entries the creator's transplanted steps read. The board's
   phone frames draw two chips in the lead plate where the app drew one collapsed string
   ("Edge of the Empire p.34–35"), which is ONE object's difference in a 192-object tree — a real
   parity divergence over a citation the app already holds at page grain. The PAGES are ours (34,
   35 — corpus-verified, `cite-verification-method` R-CVM-2); the board's own numbers on this frame
   are the designer's placeholders and are not copied. Empty entry, empty list: a step with no
   verified citation shows none, never an invented one. */
function fcStepCiteChips(cites, step, line) {
  var m = cites && cites[step];
  var e = m && m[line];
  if (!e) return [];
  // ONE CHIP PER CONTIGUOUS RUN, NOT PER PAGE (R-SCP-1, ruled at `step-cites-per-page` D1): the
  // approved frame draws `p.34–35` as ONE chip and the wide band's string already groups the same
  // list the same way. The chips CONSUME `fcCiteGroups` rather than re-deriving the grouping.
  // NO `cite` FALLBACK: the field is gone from the registry (it was the stored collapsed string,
  // now derived). An entry with no pages has no verified citation, so it draws NOTHING — never an
  // invented one, which is the rule this line always carried.
  var groups = fcCiteGroups(e.printed || []);
  return groups.map(function(r, i) {
    return (i === 0 ? (e.book ? e.book + ' p.' : 'p.') : 'p.') + fcCiteRun(r);
  });
}

function fcCitePages(pages) {
  var groups = fcCiteGroups(pages);
  return groups.length ? 'p.' + groups.map(fcCiteRun).join(', p.') : '';
}

function fcMakerLine(stepNo, stepTot) {
  return (stepNo == null) ? 'Character Creation'
    : ('Character Creation \u00b7 Step ' + stepNo + ' of ' + stepTot);
}

/* DONE MEANS REACHED, NOT "BEFORE THE CURSOR" — `creator-pre-deploy-batch` A27-3, and every
   `data-done` in the creator's wave reads it that way now. Reading the CURSOR made the record vanish
   on the way back: from Review ten steps read done; jump to Species and the same ten stopped,
   because the cursor had moved and nothing had been undone. `stepReached` is the furthest step of
   the session (`maxStepDisplayIndex` on the controller, in the wave's own reachable-list vocabulary)
   and it only advances, so the wave keeps what it knew. The three drawn states are unchanged — only
   which segment carries which, so no new pixel is invented (C-MU-3).
   The `== null` fallback keeps a sheet that has not been passed the prop drawing exactly as before,
   rather than blanking its whole done-run on a missing value. */

function fcWaveNav(p) {
  return (
    <nav className="hp-wave" aria-label="Character creation steps" data-rail-section="steps">
      {p.steps.map(function(st, i) {
        return (
          <button type="button" key={st} className="hp-wave-step"
            /* R-MEP-4 — A RULED EXCEPTION AGAINST THE PINNED BOARD, WITH A KNOWN EXPIRY.
               The fixtures are pinned at lab `97226bf1`, where the two Force-sensitive mechanic
               frames draw ELEVEN wave steps and "Step 5 of 11" — copied from the Edge frame. A
               Force-sensitive character reaches the Force Powers step, so the honest counter
               (`creator-ui-phase-b-concept`) gives twelve, and this is the twelfth.
               THE LAB HAS SINCE FIXED ITS BOARD: re-extracted at `2d8a2db3` both frames draw
               TWELVE. So this entry is already dead in the lab's world and stays only because the
               fixture set is pinned — it RETIRES the moment the set refreshes, and the refresh is
               its own scheduled act because it also moves shipped screens (the species ledger
               gained a `span.hp-sep` separator that the app does not draw). Measured, not assumed.
               OPT-IN PER SURFACE: marked unconditionally it also suppressed the step on the
               SPECIES frames, where even the pinned board draws all twelve — a green frame went
               to board 210 / app 207. An exception is scoped to the frames whose board is wrong. */
            data-ruled-delta={p.waveDelta && st === 'forcepowers' ? p.waveDelta : undefined}
            data-done={i < (p.stepReached == null ? p.stepIndex : p.stepReached) ? '1' : undefined}
            data-now={i === p.stepIndex ? '1' : undefined}
            aria-current={i === p.stepIndex ? 'step' : undefined}
            aria-label={'Step ' + (i + 1) + ' of ' + p.stepTotal + ': ' + p.stepLabels[st]}
            onClick={function() { p.onJump(st); }}>
            <span className="hp-wave-lab" aria-hidden="true">{p.stepLabels[st]}</span>
            <span className="hp-wave-bar" />
          </button>
        );
      })}
    </nav>
  );
}

/* ONE REGISTER ROW, for every browser rail. The board draws three variants of it and this is
   all three: a career row (book line, skill count), a specialization row (name and count only),
   and a species row (portrait plate, characteristic run, starting XP). Written once because a
   second copy is how two rails drift. */

function fcCiteGroups(pages) {
  if (!pages || !pages.length) return [];
  var runs = [[pages[0]]];
  for (var i = 1; i < pages.length; i++) {
    if (pages[i] === pages[i - 1] + 1) runs[runs.length - 1].push(pages[i]);
    else runs.push([pages[i]]);
  }
  return runs;
}

/* ONE GROUPING, TWO RENDERINGS (R-SCP-1). `fcCiteGroups` is the single implementation of "a gap
   starts a new group"; the wide band's collapsed STRING and the phone's CHIPS both consume its
   groups rather than each re-deriving them. Before this rider the chips walked the raw page list and
   drew one chip per PAGE, which put three chips where the approved frame draws two — and it was a
   second answer to one question, which PRAXIS §5 calls a defect whatever it is called. */

function fcCiteRun(r) {
  return r.length > 1 ? (r[0] + '\u2013' + r[r.length - 1]) : String(r[0]);
}

function fcRecordedPicks(sp, optionPicks) {
  var out = {};
  ((sp && sp.option_choices) || []).forEach(function(g) {
    var id = pickedIdFor(g, optionPicks || {});
    if (id != null) out[groupIdSlug(g.name)] = id;
  });
  return out;
}

/* B-445 — A GROUP'S PICK IS A LIST, AND `pickedIdFor` KEEPS ITS SCALAR CONTRACT.
   `lib/shared/charResolve.js`'s `_pickIdsOf` has read a scalar as a one-element list since the
   save-shape ruling; the resolver, the grants and the thresholds have all been list-aware the
   whole time. What went wrong on 2026-08-14 is that the REBUILT screen writes one id per group and
   reads `recorded[0]`, so a Droid's second modification replaced its first — the registry says
   three, the head says three, and the screen allowed one.
   TWO READERS, ONE SHAPE: single-pick callers keep `pickedIdFor` unchanged (its preselect rule is
   theirs and B-398 depends on it); the multi-pick rows read the list. Neither sees a raw value —
   the sentence the save-shape ruling wrote and B-124 paid for. */

// ═══ B-563 · THE BROWSE CLUSTER — read-pane helpers the career/spec sheets call (verbatim) ═════
function fcNoStackNote() {
  return (
    <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
      <div className="hp-maker" style={{ marginBottom: '6px' }}>Skills on both lists</div>
      <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
        {'A specialization\u2019s four skills are marked as career skills '}
        <b>if they are not already marked</b>
        {' \u2014 so a skill that is already on your career list gains nothing by appearing twice. '
         + 'Within the specialization\u2019s '}
        <b>two free ranks</b>
        {' you may not pick the same skill twice.'}
      </p>
      <p className="hp-prose hp-prose-sm" style={{ margin: '9px 0 0' }}>
        <b>{'One free rank from the career and one from the specialization may land on the same '
            + 'skill'}</b>
        {', and that is the intended way to begin play with a skill at '}
        <b>rank 2</b>
        {'.'}
      </p>
      <div style={{ marginTop: '10px' }}>
        {/* PRINTED PAGES, NOT CORPUS PAGES. The board's generator carries p.35 / p.43 and its own
            docstring calls them printed; `test/raw_citation_pages.cjs` — non-vacuous, its three
            oracle-confirmed founding cases reproducing — reads the text at p.26 and p.36, which is
            corpus-minus-offset in both books (PRAXIS §5: a book's printed page is NOT its PDF page).
            Corrected here rather than carried across, because these two numbers are copy a PLAYER
            reads on screen, and this is the class seat 2's ruled citation programme has been
            correcting repo-wide. The BOARD's copy is unchanged and diverges by these two numbers —
            neither parity layer compares text — and the lab is told, because a wrong cite in the
            design record outlives any one transplant of it. */}
        <span className="hp-cite">Edge of the Empire p.26</span>
        <span className="hp-cite" style={{ marginLeft: '7px' }}>Force and Destiny p.36</span>
      </div>
    </div>
  );
}

/* AS PRINTED — the book's own restriction, quoted and captioned as a quote (board `as_printed`).
 *
 * THE WORDING OF RECORD IS DATA, NOT COPY: `spec.restriction_note` in the canonical registry. The
 * caption is ours, the sentence is the registry's, and if the data grows this grows with it —
 * nothing here is authored, so there is no copy to drift from the book. Exactly one spec carries
 * one today (Retired Clone Trooper), and until R-WUS-1's shelf landed it was unreachable at this
 * band, which is why this had never been drawn here.
 *
 * A PLATE WITH A FURNITURE KICKER, NEVER A WARNING BLOCK, and the board says why: a restriction is
 * a LIMIT REACHED, and its §14 gives that ink-2 and never a fill — a fill would say "you do this"
 * about a thing you may not do. `--pp-ink-2` is the board's token; ours is `--doc-ink-muted` by
 * `scripts/derive_board_css.py`'s own map, not by eye.
 *
 * DECLARED, THEN RETIRED ON ITS OWN NAMED EXPIRY (R-WUS-2 -> R-WUS-A2-1). While every desktop
 * spec frame was `marauder` there was nothing at this band to compare against; the lab drew
 * `spec_step(... spec_id=retired_clone_trooper)` at pin 881037eb and the declaration came off in
 * the same increment. This plate is now COMPARED by parity. */

function fcReadProse(title, cite, text, first, marks, phone, noPlate) {
  /* `noPlate` draws the head alone: a specialization with an empty description keeps its name, book
     and marks, and draws no empty plate under them (R-M12B-176).

     `marks` OPTS INTO THE LEAD-BLOCK FORM, and the two differences are the same fact seen twice.
     In the TWO-COLUMN arrangement this prose sits in a 660px column, so `max-width:none` is
     load-bearing — `hp-prose` carries the app's reading measure, which is NARROWER than that
     plate, and dropping it drew 373px of text in a 660px plate. In the LEAD-BLOCK arrangement the
     same plate spans the WHOLE pane, and the same override then runs the prose to 125ch against a
     75ch cap. The board paid this exact regression in its own layout pass and took the override
     off: MEASURE is law and a wider plate does not widen the measure, so the lead form takes
     `--desk-prose-max` like every other sustained-reading block.

     The marks ride the head as plain cites beside the book (`UNIVERSAL`, `FORCE RATING n`) rather
     than taking their own row, which is what the board draws once the head is full-width. */
  var extra = marks || [];
  return (
    <React.Fragment>
      {title ? (
        <div className="hp-sechead"
          style={{ margin: phone ? '20px 0 9px' : (first ? '0 0 11px' : '22px 0 11px') }}>
          <h2>{title}</h2>
          {cite ? <span className="hp-cite">{cite}</span> : null}
          {extra.map(function(m) { return <span className="hp-cite" key={m}>{m}</span>; })}
        </div>
      ) : null}
      {noPlate ? null : (
        <div className="hp-plate" data-pad="read"
          style={(phone && !title) ? { marginTop: '9px' } : undefined}>
          <p className={phone ? 'hp-prose hp-prose-sm' : 'hp-prose'}
            style={(marks || phone) ? { margin: 0 } : { margin: 0, maxWidth: 'none' }}>{text}</p>
        </div>
      )}
    </React.Fragment>
  );
}

/* THE NO-STACKING NOTE — the board's `no_stack_note()` (build.py:1676), and it is ONE builder with
   TWO homes by ruling: the WIDE specialization read pane consumes it now, and the PHONE spec screen
   consumes THIS SAME function when that band is built (R-WCS-1; `no_stack_note` has its own second
   call site at build.py:2435, so a phone-only copy would be the divergence waiting to happen).

   IT IS RULES CONTENT, WHICH IS WHY IT IS NOT OPTIONAL. B-371 measured the wide pane six named
   objects short and this block is all six: the plate, the maker, two prose paragraphs and two
   cites. The app drew nothing in its place, so a player was told nothing about how a skill on two
   lists behaves — and R-CPR-30 puts rules content above a drawing's silence in any case.

   THE SECOND PARAGRAPH IS THE POINT, and R-CPR-62 ruled its shape before this was built: copy
   flattened to "duplicates never stack" would teach a player AWAY from the one legal move that
   earns rank 2 at creation. The board's own docstring says the same in nearly the same words. So
   the caution travels with the prohibition, and neither ships alone.

   COPY AND CITES ARE THE FIXTURE'S, VERBATIM (punch-list P1). TWO cites, not three: they are the
   two books whose PRINTED wording the lab checked, and the AoR parallel is deliberately uncited as
   unverified — the restraint is inherited rather than re-decided here. */

/* THE CAREER'S STARTING ENTITLEMENT (B-594 moved it out of the call site so two columns can share
   it): sechead + read plate, the same two objects the sections above it use. Exactly one career of
   twenty-one carries one (Bounty Hunter's Imperial Peace-Keeping Certificate); COLLAPSES WHEN ABSENT
   — `smoke_career_card_registry` (e) requires the other twenty to show no empty section. */
function fcCareerEntitlement(c) {
  var ent = ((c && c.starting_entitlements) || [])[0];
  if (!ent) return null;
  return (
    <React.Fragment>
      <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
        <h2>Starting Entitlement</h2>
        <span className="hp-cite">{ent.name}</span>
      </div>
      <div className="hp-plate" data-pad="read">
        <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>{ent.note}</p>
      </div>
    </React.Fragment>
  );
}

function fcReadSkills(title, cite, skills, first, beforePlate, phone) {
  return (
    <React.Fragment>
      <div className="hp-sechead"
        style={{ margin: phone ? '20px 0 9px' : (first ? '0 0 11px' : '22px 0 11px') }}>
        <h2>{title}</h2>
        {cite ? <span className="hp-cite">{cite}</span> : null}
      </div>
      {/* THE BOARD PUTS THE SPEC MARKS HERE — between this head and the skills plate, not in the
          head itself: at 390 a third and fourth chip on a row that already carries a heading and a
          count measured past the frame edge, so the marks take their own row. Optional, so the
          callers that have no marks to draw pass nothing and this renders what it always did. */}
      {beforePlate || null}
      <div className="hp-plate" data-pad="data">
        {skills.map(function(sk) {
          return (
            <div className="hp-skill" data-charword="1" key={sk.name} style={{ cursor: 'default' }}>
              <span className="hp-skill-name">{sk.name}</span>
              <span className="hp-skill-char">{sk.chr}</span>
              <span />
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
}

/* THE WAVE — the step strip every browser head carries. Extracted so the species head can wrap
   it in `hpw-steps` and the others cannot, without the nav being written twice. */

function fcReadTree(title, cite, model, headStyle) {
  /* RETIRED ONTO THE ONE TREE COMPONENT (ruling `talents-tree-skin` R-TTS-2). This used to draw the
     board's cells itself from a flattened node list — a second tree renderer, shipped before R-C3-7
     was written. It now hands a read-only model to `DirectionalTree`'s board skin
     (`interactive: false`): the same `hp-tree > hp-tal` DOM, drawn by the same code that draws the
     interactive talents step. The wrapper (sechead + `hp-treescroll`) is the board's and stays. */
  return (
    <React.Fragment>
      <div className="hp-sechead" style={headStyle || { margin: '26px 0 12px' }}>
        <h2>{title}</h2>
        {cite ? <span className="hp-cite">{cite}</span> : null}
      </div>
      <div className="hp-treescroll">
        <DirectionalTree model={model} accent="var(--doc-action)" />
      </div>
    </React.Fragment>
  );
}

/* ══ FCGameLineSheet — CLUSTER TWO'S FIRST TRANSPLANT (the GAME LINE step) ═════════════════
   Fixtures: `test/fixtures/board_gameline_1920.html` / `_1366.html`, extracted from the pinned
   board at `97226bf1` under the heading "Step 1 · Game Line".

   A ONE-COLUMN STEP, and that is the whole reason it is its own component rather than a flag on
   `FCBrowseSheet`. The browsers are `hpw-grid`: an index rail, a read pane, panes beside them.
   This step has no list to browse and nothing to read beside it — the board draws `hp-onecol`,
   a single measure with the three lines stacked down it. Bending the browser shell into that
   shape would have cost the shell a flag for every difference and left neither screen legible.

   WHAT TRAVELS AND WHAT DOES NOT: the board's TREE travels exactly (R-TTP-1). The board's own
   COPY does not — the app keeps its approved sentences, which R-TTP-1 lists among the allowed
   differences and which the career screen already established. The board's CITES do not travel
   either: they are the designer's placeholders (the career frame cited "EotE p.34", Competitive
   Checks), and every page this app prints is TOC-judged under B-330 instead.
*/
/* THE LINE PLATE'S ART, GENERATED FROM THE LINE'S OWN COLOURS. The board's plate carries the
   book cover; we carry a gradient built from the accent and dark tones `FC_GAME_LINES` has shipped
   since B-108, as an inline SVG data URI. No asset crosses, nothing is fetched, and the <img> the
   board draws is really an image rather than an empty element pretending to be one. */
/* THE GAME LINE'S PLATE ART — ruling `creator-pre-deploy-batch` R-CPD-3.
   The board draws each line's CORE RULEBOOK COVER here. The app drew a generated gradient, and the
   reason was never design: `nunes-art-retirement` deleted the three banner images for RIGHTS on
   2026-07-23, and the f-101 transplant filled the surviving slot with something the app owns — a
   correct clean-room call on 1 September that `art-extraction-amendment` (R-AEA-1) made unnecessary
   on 2 September by permitting FFG material. So the covers come back: the SAME three images the
   board fixture already embeds, extracted to `public/art/gamelines/`, each with its provenance row
   (R-AEA-2(c) — an asset without its row does not ship). What does NOT come back is the Nunes
   banners; R-AEA-2(b) holds them out and this changes nothing about it.
   THE GRADIENT SURVIVES AS THE FALLBACK, not as decoration: a line whose asset is missing draws the
   thing the app owns rather than a broken image, and the plate is never empty. */

var FC_LINE_PLATE_ART = { eote: '/art/gamelines/eote.webp', aor: '/art/gamelines/aor.webp',
                          fad: '/art/gamelines/fad.webp' };

function fcLinePlateArt(l) {
  var cover = FC_LINE_PLATE_ART[l && l.id];
  if (cover) return cover;
  // NO LITERALS HERE, and `test/ground_token_contract`'s surface ratchet is why: my first cut
  // carried two hex fallbacks and the guard counted them as the app gaining hardcoded colour
  // (1329 -> 1331). Every value comes from the line's OWN record instead, so a line that ever
  // changes its palette changes this with it.
  var stops = String(l.grad || '').match(/#[0-9a-f]{3,8}/gi) || [l.accent, l.accent];
  var a = stops[0] || l.accent, b = stops[stops.length - 1] || l.accent;
  var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 84" preserveAspectRatio="none">'
    + '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">'
    + '<stop offset="0" stop-color="' + a + '"/>'
    + '<stop offset="1" stop-color="' + b + '"/>'
    + '</linearGradient></defs>'
    + '<rect width="60" height="84" fill="url(#g)"/>'
    + '<rect x="4" y="4" width="52" height="76" fill="none" stroke="' + (l.accent || a)
    + '" stroke-opacity="0.55" stroke-width="1"/>'
    + '</svg>';
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

/* THE THREE LINE PLATES, WRITTEN ONCE AND DRAWN BY BOTH BANDS. The board draws the same
   `button.hp-line` object at 1920, at 1366 and at 390 — same plate, same body, same foot — so a
   phone copy of this markup would be the second-resolution-path defect PRAXIS §5 names, one band
   away from drifting. `FCGameLineSheet` composes it into `hp-linescroll` at the desktop bands and
   straight into the phone body at `mobile`, which is exactly what the two frames differ by. */
function fcGameLinePlates(p) {
  return (p.lines || []).map(function(l) {
    var on = p.selected === l.id;
    return (
      <button type="button" className="hp-line" key={l.id}
        data-selected={on ? '1' : undefined}
        aria-pressed={on ? 'true' : 'false'}
        onClick={function() { p.onPick(l.id); }}>
        {/* THE ELEMENT TRAVELS; THE PUBLISHER'S ART DOES NOT. The board's plate holds
            an <img alt="… core rulebook cover"> — FFG's own cover, a different asset
            class from the species backdrop washes, and this project's hardest standing
            line is about publisher artwork.
            A RULED DELTA WAS THE WRONG TOOL HERE and the parity engine said so: the
            exception excludes the node from the APP'S skeleton while the board keeps its
            plate AND its image, so declaring it made the two trees LESS alike, not more.
            The board draws an image in this slot, so the app draws an image in this
            slot — one it OWNS: a gradient generated from the line's own accent colours,
            which the app has shipped since B-108, rendered as an inline SVG. Clean-room
            end to end, and the tree matches object for object. */}
        <span className="hp-line-plate">
          {/* R-CPD-3: the cover is CONTENT now, not a decorative fill, so it is described.
              The board's own frame alt-texts each image to its core rulebook; an empty alt
              was right for the gradient and is wrong for a book cover. */}
          <img src={fcLinePlateArt(l)}
            alt={FC_LINE_PLATE_ART[l && l.id] ? (l.title || l.fallbackTitle) + ' core rulebook cover' : ''} />
        </span>
        <span className="hp-line-body">
          <span className="hp-line-name">{l.title}</span>
          {/* B-722 \u2014 TWO OPTIONAL MODEL FIELDS, so Guided's own page draws its own card through THIS
              helper instead of a second copy of the object. `mechanicSub` absent \u21d2 the mechanic's
              WORD alone, which is what the pin's `body_gameline` draws on frames P and Q
              (`l["mechanic"].title()`); `note` present \u21d2 the sentence about what that mechanic DOES,
              on its own muted line, which is one of the three WHAT-IT-DECIDES facts `QBGameLineStep`
              already shows. The creator's model carries a `mechanicSub` and no `note`, so every
              string it renders is unchanged \u2014 checked by the creator's own suites. */}
          <span className="hp-line-cur">{l.mechanicName}
            {l.mechanicSub ? <React.Fragment><i>{'\u00b7'}</i>{l.mechanicSub}</React.Fragment> : null}</span>
          {l.desc ? <span className="hp-line-text">{l.desc}</span> : null}
          {l.note ? <span className="hp-line-text" style={{ color: 'var(--doc-ink-muted)' }}>{l.note}</span> : null}
          <span className="hp-line-foot">
            <span className="hp-none">{l.careerCount + ' careers'}</span>
            <span className="hp-cite">{l.cite}</span>
          </span>
        </span>
      </button>
    );
  });
}

function FCGameLineSheet(p) {
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1;
  var stepTot = p.stepTotal || (p.steps || []).length;

  /* ── THE PHONE BAND (`phone-gameline-census` R-PGC-1) ────────────────────────────────────────
     ONE COMPONENT, TWO BANDS — the ruling's own words, and the same shape `FCCharSheet` took to
     `FCCharSheetPhone`: what changes at 390 is the CHROME (`FCPhoneShell` instead of the
     `hpw-shell` + `hp-onecol` composition) and the head's arrangement, not the step. The three
     line plates are the SAME objects, drawn by the helper above; the commit is the SAME control,
     moved into the shell's `hp-phoneact` where the board draws it, still disabled until a line is
     picked. `QBGameLineStep` and `HubCard` are untouched — the guided door keeps its ratified
     two-tap accordion (`creator-d3-gameline-accordion`), which is a different door and not this
     card's (R-PGC-1).

     MOCKUP: `phone_gameline()` @ 6051229c on the lab's board, sheet `lab/holocron/phone_approval
     .html` @ c0744b3a, APPROVED by Jason 2026-09-09 (`parallel-pull-grant` A3, R-PPG-A3-1).
     Fixture `test/fixtures/board_gameline_phone.html`.

     NO `hp-status` ON THIS SCREEN, and it is TYPED (`status={null}`) rather than forgotten — the
     shell's own `act === null` idiom, extended to the strip for the same reason. The frame draws
     no status div at all (0 occurrences, verified in the cut at all three pins): step 1 is before
     any purse, any register position and any line, so a strip here would have three dashes and
     nothing to report. `FCPhoneShell`'s comment claimed every phone frame carried one; that was
     true of the two steps it was written for and is corrected there.

     THE HEAD PLATE CARRIES ONE CITE CHIP, because the board draws one. The desktop head pairs
     EotE with FaD (the two books whose Step 2 defines a mechanic); at 390 the frame draws a single
     chip, so the mobile band takes the FIRST of the same already-TOC-judged strings rather than
     inventing a phone-only page. Nothing is LOST against the legacy phone step, which drew no
     citation at all — this is +1 chip on the screen, not −1. */
  if (p.band === 'mobile') {
    return (
      <FCPhoneShell band={p.band}
        stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
        steps={p.steps} stepLabels={p.stepLabels}
        onBack={p.onBack} onJump={p.onJump}
        status={null}
        act={
          <button type="button" className="hp-commit" data-creator-commit="1"
            disabled={!p.selected} onClick={p.onCommit}>
            {p.commitLabel}<span>{'\u203a'}</span>
          </button>
        }>

        {/* The phone's own spacing, read off its fixture: a 14px lift and a 10px gap, where the
            desktop row uses 11px and no lift — the same measured pair every other phone step
            carries. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
        </div>
        {/* 27px where the desktop clamp gives 30, and a 12px lead-plate margin: neither is in a
            rule, so neither could be derived — both are the fixture's own inline values. */}
        <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName}</h1>
        <div className="hp-desig-rule" />
        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
          {p.lead ? <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}>{p.lead}</p> : null}
          <div style={{ marginTop: p.lead ? '10px' : 0 }}>{(p.cites || []).slice(0, 1)}</div>
        </div>
        <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
          <h2>{p.listTitle}</h2>
          <span className="hp-cite">{p.listCite}</span>
        </div>
        {/* NO `hp-linescroll` WRAPPER AT 390. The desktop's scroller exists because the sheet is a
            fixed-height column with the commit pinned under it; the phone body IS the scroller and
            the act bar is already outside it, so a second scroll zone here would be the trap
            B-509 is fixing one step over. The board draws `hp-lines` as a direct child of
            `hp-phonebody` and this emits exactly that. */}
        <div className="hp-lines">{fcGameLinePlates(p)}</div>
        {/* B-583 — THE SOURCES STRIP ON THE PHONE, under the three lines: the name and CHANGE,
            no counts; the same component the desk bands draw beside the commit (B-582). The phone
            body scrolls, the act bar sits outside it, so the strip is the body's last object. */}
        <FCSourcesBox band={p.band} compact reentry={p.reentry} />
      </FCPhoneShell>
    );
  }

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band}>

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'\u2039\u00a0\u00a0Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hp-onecol">
        {fcWaveNav(p)}

        <div className="hp-onecol-head">
          <div className="hp-onecol-title">
            {/* THE SLASH AND THE MAKER SHARE A ROW, and the wrapper is why the board's maker
                measures 263px inside a 1500px title while ours measured the full 1500. It carries
                NO CLASS — which is exactly why the DOM layer could not see the difference: that
                engine tracks `hp-*`/`hpw-*` objects, so an unclassed flex wrapper is invisible to
                it and visible only to the style layer, as the width of everything it holds. Same
                wrapper `fcSpeciesHead` already uses, for the same reason. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <span className="hp-slash"><i /><i /><i /></span>
              <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
            </div>
            <h1 className="hp-desig">{p.stepName}</h1>
            <div className="hp-desig-rule" />
          </div>
          {/* THE STANDFIRST HOLDS A READING MEASURE, not the page's width. The board's plate
              computes 678px at 1920 — which is `--doc-lead-max` (640) plus the plate's own 38px of
              padding, the same cap `fcSpeciesHead` already applies inline. Without it the plate
              took the full 1500 and every object under it inherited the wrong height. */}
          <div className="hp-plate" data-pad="read"
            style={{ maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
            <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>{p.lead}</p>
            {/* THE BOARD'S OWN WRAPPER, and it carries the 11px that separated the chips from
                the standfirst. Unclassed — so the DOM layer cannot see it and the style layer saw
                only its effect: our plate ran 11px short at every width. The second unclassed
                wrapper this screen has needed; both were found by measuring, not by reading. */}
            <div style={{ marginTop: '11px' }}>{p.cites}</div>
          </div>
          {/* A0 — THE SOURCES BOX. Its ruled home was the head beside the step counter (F-102
              creator slice, record `Docs/mockups/sources-box.html` @ f566bc0b, R-FSA-A9-1). AT THE
              DESK BANDS IT NOW SITS IN THE ACT ROW beside the commit — Jason's word on his walk
              (2026-09-14, B-582: "can we move it here?"), which outranks the record; the phone keeps
              it in the head, where its act row is the bottom bar. One component, two homes by band. */}
        </div>

        {/* THE MARGIN IS THE BOARD'S OWN INLINE STYLE, carried with the tree. Its stylesheet has
            one rule for this object (12px, inside the <=1366 query) over a 26px base, and neither
            is the 20px it computes at 1920 — the frame markup sets it inline, four times over.
            Emitted here rather than written into our sheet as a bare number nobody could source. */}
        <div className="hp-sechead hp-onecol-sechead" style={{ margin: '20px 0 12px' }}>
          <h2>{p.listTitle}</h2>
          <span className="hp-cite">{p.listCite}</span>
        </div>

        <div className="hp-linescroll">
          <div className="hp-lines">{fcGameLinePlates(p)}</div>
        </div>

        <div className="hp-onecol-act">
          <button type="button" className="hp-commit" data-creator-commit="1"
            disabled={!p.selected} onClick={p.onCommit}>
            {p.commitLabel}<span>{'\u203a'}</span>
          </button>
          {p.consequence ? <span className="hp-none">{p.consequence}</span> : null}
          {/* B-582 — the sources strip, in the space beside the commit at mid and wide. */}
          {p.band !== 'mobile' ? <FCSourcesBox band={p.band} reentry={p.reentry} /> : null}
        </div>
      </div>

    </div>
    </div>
  );
}

/* ══ THE SOURCES BOX — F-102's CREATOR SLICE (frames A0 · A1 · A6) ═══════════════════════════
   MOCKUP: `Docs/mockups/sources-box.html` @ f566bc0b (branch `feat/motivation-census`), the R-MU1
   record; APPROVED: Jason, 2026-09-05, recorded at
   `Docs/rulings/f102-sources-box-summary-and-attribution.md` Addendum 9 (R-FSA-A9-1). The
   specimen it descends from is `lab/holocron/facet_specimen.html` @ 443d54b4 (frames A0/A1/A6,
   R-FCC-3), whose `sb-*` idiom crosses through `scripts/derive_sources_box_css.py`.

   WHAT IT IS: a second VIEW of the one shelf model (`lib/shared/sources.js` + the overlay index in
   `lib/shared/mods.js`), never a second store of truth (R-FCS PRODUCT-LAW CHECK). Every number on
   it is DERIVED AT RENDER — `SWRPGSources.attribution*` over the registries the creator already
   holds, `SWRPGMods.variantCounts` over a pack's manifest — and `test/sources_box_no_typed_numbers`
   holds this block free of a typed figure. The Settings-menu panel (`openActiveSources`) stays: it
   is the drawer, this is the front door (hero-features-get-front-doors).

   THE TWO FRAMES:
     A0  one box in the game-line head: "Active sources · N of M on · +X species …" + CHANGE.
     A1  CHANGE opens a full-screen overlay on the creator ground: the same head with DONE, then
         the shelf — ON first, then OFF, each group headed by its own summed line, one
         three-column row per book (tick · label · adds run) and per community variant (the verb
         "rewrites" in the same run style; R-FSA-A4-1 / A6-1). Every row is a switch.
   A6 — the general offer that stood on the game-line surface while no unofficial book was on — is
   CUT (Jason, `creator-pre-deploy-batch` A24: "Remove it and keep the source box as currently
   ruled"). What it told a reader who never opens CHANGE (that N off books hold +X species) is
   ACCEPTED AS LOST, deliberately and by his call, and the box gains nothing to compensate.

   THE OVERLAY IS A PORTAL, and the reason is measurable: `.hpw-shell` is a size container
   (`container-type:inline-size`, paper.css), which applies layout containment, which makes it the
   containing block for FIXED descendants — an overlay rendered inside the sheet would be fixed
   to the sheet's box, not the viewport. It carries `data-doc-ground="creator"` + `data-band`
   itself so the derived rules scope onto it at the body.

   BANDS: the game line is transplanted at mid + wide; the phone disposition rides R-CRS-2 item 6
   with the rest of the step. The record's 390 frames are carried as `[data-band="mobile"]` rules
   and the `cap` below, ready for that band the day it lights. */
var FC_SOURCES_GROUP_ORDER = ['Core', 'Era', 'Supplement', 'Community', 'UserData'];
var _fcSourcesAttrCache = { index: null, cb: null, reg: null, attr: null };

// The registries the creator ALREADY holds, keyed the way `attribution()` reads them. An absent
// registry is reported MISSING by the engine and never rendered as a zero (R-DRC-1). `view` is a
// RE-ENTERED draft's own build (F-100 M6 R-M6G-12 (a)): its counts come from the packs its character
// was made with, never from the device's toggles.
function fcSourcesSets(view) {
  var cb = view || window._canonicalCharbuild || null;
  var reg = window._canonicalRegistries || null;
  var sets = {};
  if (cb) {
    sets.species = cb.species; sets.careers = cb.careers; sets.specializations = cb.specializations;
    sets.talents = cb.talents; sets.force_powers = cb.forcePowers;
  }
  if (reg) { sets.weapons = reg.weapons; sets.armour = reg.armor; sets.gear = reg.gear; }
  return { sets: sets, cb: cb, reg: reg };
}

function fcSourcesAttribution(SS, index, view) {
  var got = fcSourcesSets(view);
  var c = _fcSourcesAttrCache;
  if (c.attr && c.index === index && c.cb === got.cb && c.reg === got.reg) return c.attr;
  var attr = SS.attribution(index, got.sets);
  _fcSourcesAttrCache = { index: index, cb: got.cb, reg: got.reg, attr: attr };
  return attr;
}

// `rewrites N trees · +N specs · +N talents` — the variant's run, zeros omitted; null counts say so.
function fcVariantRun(counts) {
  if (!counts) return '';
  var parts = [];
  if (counts.rewrites) parts.push('rewrites ' + counts.rewrites + ' trees');
  if (counts.specs) parts.push('+' + counts.specs + ' specs');
  if (counts.talents) parts.push('+' + counts.talents + ' talents');
  return parts.join(' \u00b7 ');
}

// F-100 M6 R-M6G-12 (a) (ruling `f100-m6-pack`; frames C1–C4, approved at ADDENDUM 7): on a RE-ENTERED
// draft each reSpecialized row says whether THIS CHARACTER carries that line — its `reentry.mods`, the
// stamps it was made with — checked or unchecked as the character is, and it cannot be switched (a
// re-entered draft's view never moves with the toggles, R-M6G-2). A line the character carries in an
// older version shows that version's own label and counts; a line it does not carry shows the offer's.
function fcReentryVariants(MODS, offered, mods) {
  var packOf = function(st) { return String(st).split('@')[0]; };
  var lineOf = function(st) { var v = MODS.variantOf(st); return (v && v.line) || String(st).split('@')[1].split('-')[0]; };
  return offered.map(function(row) {
    var own = mods.filter(function(st) { return packOf(st) === packOf(row.stamp) && lineOf(st) === lineOf(row.stamp); })[0];
    var use = own || row.stamp, v = MODS.variantOf(use);
    return { stamp: use, label: v ? v.label : row.label, kind: row.kind, counts: v ? MODS.variantCounts(v) : row.counts,
             active: !!own, locked: true };
  });
}

// The whole model, read fresh on every render — the shelf is small and the subscriptions below
// re-render on change, so nothing here is worth a stale memo. `reentry` ({ mods, view }) is a
// RE-ENTERED draft's own packs and view, or null for a new character.
function fcSourcesModel(reentry) {
  var SS = window.SWRPGSources, MODS = window.SWRPGMods;
  var cur = SS && SS.current && SS.current();
  if (!cur || !cur.index) return null;
  var index = cur.index;
  var state = SS.load();
  var grouped = SS.groupedBooks(index, state);
  var books = [];
  FC_SOURCES_GROUP_ORDER.forEach(function(g) {
    (grouped[g] || []).forEach(function(b) {
      books.push({ id: b.id, label: b.label, group: g, active: !!b.active, official: SS.groupDefaultOn(g) });
    });
  });
  var attr = fcSourcesAttribution(SS, index, reentry ? reentry.view : null);
  var activeMods = MODS ? (MODS.active() || []) : [];
  var variants = (MODS && MODS.SHIPPED_STAMPS ? MODS.SHIPPED_STAMPS : []).map(function(stamp) {
    var v = MODS.variantOf(stamp);
    return { stamp: stamp, label: v ? v.label : stamp, kind: (v && v.kind) || 'variant',
             counts: v ? MODS.variantCounts(v) : null, active: activeMods.indexOf(stamp) !== -1 };
  });
  if (reentry && MODS) variants = fcReentryVariants(MODS, variants, reentry.mods);
  var onBooks = books.filter(function(b) { return b.active; });
  var offBooks = books.filter(function(b) { return !b.active; });
  var rewrites = 0;
  variants.forEach(function(v) { if (v.active && v.counts) rewrites += v.counts.rewrites; });
  // `unofficial` went out with A6 (`creator-pre-deploy-batch` A24). It is ONE SYMBOL BEYOND the
  // ruling's enumerated list and it is disclosed rather than quietly kept: after the offer's gate
  // and its scope line were deleted, `m.unofficial` had zero readers, and the deletion discipline
  // is delete-don't-stub. `b.official` still decides which group a book sits in, so nothing else
  // moved.
  return {
    SS: SS, MODS: MODS, index: index, state: state, attr: attr,
    books: books, variants: variants, onBooks: onBooks, offBooks: offBooks,
    nOn: onBooks.length, nAll: books.length, rewrites: rewrites,
  };
}

// `N of M on · +X species · +Y careers …` (+ ` · rewrites N trees` while a variant is on — stated
// separately, never folded into +specs; R-FSA-A4-2). `cap` is the narrow band's "+N more" rule.
function fcSourcesHead(m, cap) {
  var line = m.SS.attributionText(m.SS.attributionSum(m.attr, m.onBooks.map(function(b) { return b.id; })), cap);
  var s = m.nOn + ' of ' + m.nAll + ' on';
  if (line) s += ' \u00b7 ' + line;
  if (m.rewrites) s += ' \u00b7 rewrites ' + m.rewrites + ' trees';
  return s;
}

// Subscribe to the shelf + the variants toggle; warm the packs so their manifests are readable.
function useFcSourcesShelf() {
  var _t = useState(0), setTick = _t[1];
  useEffect(function() {
    var on = function() { setTick(function(t) { return t + 1; }); };
    var SS = window.SWRPGSources, MODS = window.SWRPGMods;
    if (SS && SS.ready) SS.ready().then(on);
    if (MODS && MODS.warm && MODS.SHIPPED_STAMPS) MODS.warm(MODS.SHIPPED_STAMPS).then(on);
    window.addEventListener('swrpg-sources-changed', on);
    window.addEventListener('swrpg-mods-changed', on);
    return function() {
      window.removeEventListener('swrpg-sources-changed', on);
      window.removeEventListener('swrpg-mods-changed', on);
    };
  }, []);
}

// ONE row shape for books and variants alike (R-FSA-A6-1): [tick] [label] [run], three grid
// columns, the state carried by the tick and the greyed TONE — never by a second line. R-CPD-1 renames
// the STATE to "Off" (`multi-source-picker` §1: grey means "you can't have this", the opposite of true);
// the TONE itself is unchanged and still reads at the AA floor.
function fcSourcesRow(p) {
  return (
    <button type="button" role="switch" aria-checked={p.on ? 'true' : 'false'} key={p.key}
      className={'sb-bk' + (p.on ? '' : ' mk-off') + (p.kind ? ' mk-variant' : '')}
      data-sources-row={p.key} data-sources-on={p.on ? '1' : undefined}
      aria-disabled={p.locked ? 'true' : undefined} data-sources-locked={p.locked ? '1' : undefined}
      style={p.locked ? { cursor: 'default' } : undefined}
      onClick={p.locked ? undefined : p.onToggle}>
      <span className="sb-tick" aria-hidden="true">{p.on ? '\u2713' : ''}</span>
      <span className="sb-bk-name">{p.label}{p.kind ? <span className="mk-kind">{p.kind}</span> : null}</span>
      {/* R-FSB-1: "nothing yet" promised something coming and was false for an adventure module;
          the empty run is scope-true — the book adds no CREATOR option (it may still be a fine book). */}
      <span className="sb-bk-add">{p.run ? p.run : <span className="mk-none">no creator options</span>}</span>
    </button>
  );
}

function fcSourcesGroup(m, title, books, variants, cap, onBook, onVariant, lockedNote) {
  if (!books.length && !variants.length) return null;
  var sum = m.SS.attributionText(m.SS.attributionSum(m.attr, books.map(function(b) { return b.id; })), cap);
  return (
    <div className="sb-grp" key={title} data-sources-group={title}>
      <div className="sb-grp-h">
        <span className="sb-tick" aria-hidden="true" />
        <span className="sb-bk-name">{title}</span>
        <span className="mk-grp-sum">{sum}</span>
      </div>
      {books.map(function(b) {
        return fcSourcesRow({ key: b.id, on: b.active, label: b.label,
          run: m.SS.attributionText(m.SS.attributionParts(m.attr, b.id), cap),
          onToggle: function() { onBook(b); } });
      })}
      {variants.map(function(v) {
        return fcSourcesRow({ key: v.stamp, on: v.active, label: v.label, kind: v.kind, locked: v.locked,
          run: fcVariantRun(v.counts), onToggle: function() { onVariant(v); } });
      })}
      {/* R-M6G-12 (a) / R-M6G-23: why the reSpecialized rows cannot be switched, ONCE (the caller picks the
          group), in the kit's teaching line; its width keeps to the band's own (frames C1–C4). */}
      {lockedNote
        ? <p className="sb-teach" data-sources-locked-note="1" style={{ maxWidth: 'min(62ch, 100cqw)' }}>{FC_SOURCES_LOCKED_LINE}</p> : null}
    </div>
  );
}

var FC_SOURCES_REFUSED = 'Keep at least one core rulebook on \u2014 the creator needs one.';
// R-M6G-12 (a)'s line (frames C1–C4, approved ADDENDUM 7).
var FC_SOURCES_LOCKED_LINE = 'Set when this character was made. Existing characters keep the version they were built with.';

function FCSourcesOverlay(p) {
  var m = p.model;
  var doneRef = useRef(null);
  var pendingFocus = useRef(null);
  var _r = useState(''), refused = _r[0], setRefused = _r[1];
  useEffect(function() { if (doneRef.current) doneRef.current.focus(); }, []);
  // ESCAPE IS LISTENED FOR AT THE DOCUMENT, not on the dialog: a toggled row MOVES between the
  // ON and OFF groups, which remounts its button and drops focus to the body — and a
  // keydown on the body never reaches a handler on the overlay. Capture phase, so the creator's
  // own key handling (back-to-close) does not act on the same press first.
  useEffect(function() {
    function onDoc(e) { if (e.key === 'Escape') { e.stopPropagation(); p.onClose(); } }
    document.addEventListener('keydown', onDoc, true);
    return function() { document.removeEventListener('keydown', onDoc, true); };
  }, [p.onClose]);
  // …and the moved row takes its focus with it, so a keyboard user is still standing on the
  // switch they just flipped rather than nowhere.
  useEffect(function() {
    var id = pendingFocus.current;
    if (!id) return;
    pendingFocus.current = null;
    var el = document.querySelector('[data-sources-overlay="1"] [data-sources-row="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
    if (el && document.activeElement !== el) el.focus();
  });
  function toggleBook(b) {
    var res = m.SS.setBook(m.state, m.index, b.id, !b.active);
    if (res.refused) { setRefused(FC_SOURCES_REFUSED); return; }
    setRefused(''); pendingFocus.current = b.id; m.SS.save(res.state);
  }
  function toggleVariant(v) {
    var list = (m.MODS.active() || []).slice();
    var i = list.indexOf(v.stamp);
    if (i === -1) list.push(v.stamp); else list.splice(i, 1);
    pendingFocus.current = v.stamp; m.MODS.setActive(list);
  }
  var onV = m.variants.filter(function(v) { return v.active; });
  var offV = m.variants.filter(function(v) { return !v.active; });
  // R-M6G-23: the locked line is drawn ONCE, after the last reSpecialized row — in the last group holding a
  // locked row (Off follows On), so a character carrying one line only does not read it twice (frame C4).
  var isLocked = function(v) { return v.locked; };
  var noteIn = offV.some(isLocked) ? 'Off' : onV.some(isLocked) ? 'On' : null;
  var node = (
    <div className="sb-overlay" role="dialog" aria-modal="true" aria-label="Active sources"
      data-doc-ground="creator" data-band={p.band} data-sources-overlay="1"
      data-cap={'overlay @ ' + p.band}>
      <section className="sb-band sb-band-1">
        <div className="sb-band-h">
          <div className="sb-band-t">Active sources</div>
          <div className="sb-band-scope" data-sources-head="1">{fcSourcesHead(m, p.cap)}</div>
          <button type="button" className="sb-toggle" data-sources-done="1" ref={doneRef} onClick={p.onClose}>
            Done<span className="sb-chev">{'\u203a'}</span>
          </button>
        </div>
        {refused ? <div className="sb-refused" role="status" data-sources-refused="1">{refused}</div> : null}
        <div className="sb-zones" data-wide={p.band === 'wide' ? '1' : undefined}>
          {fcSourcesGroup(m, 'On', m.onBooks, onV, p.cap, toggleBook, toggleVariant, noteIn === 'On')}
          {fcSourcesGroup(m, 'Off', m.offBooks, offV, p.cap, toggleBook, toggleVariant, noteIn === 'Off')}
        </div>
        {/* F-100 C2 · "Your homebrew" (M3a/b, M5a/b; ruling f100-items-shell-faces R-FISF-3): the collections,
            switched like a book. Only while swrpg_homebrew_editor is on — off, nothing renders here. */}
        {window.SWRPGEditor && window.SWRPGEditor.on() && window.SWRPGEditorView
          ? React.createElement(window.SWRPGEditorView.HomebrewSources, { band: p.band }) : null}
      </section>
    </div>
  );
  return ReactDOM.createPortal(node, document.body);
}

/* R-CPD-2: ONE cap for the A0 head at every band — the value the phone has always used. It lives
   here rather than inline at two sites, because the defect being corrected was exactly two sites
   drifting apart (the phone capped, the desktop did not). */
var FC_SOURCES_HEAD_CAP = 3;

// A0 — the band in the head. Owns the overlay's open state and returns focus to CHANGE on close.
function FCSourcesBox(p) {
  useFcSourcesShelf();
  var _o = useState(false), open = _o[0], setOpen = _o[1];
  var changeRef = useRef(null);
  var m = fcSourcesModel(p.reentry);
  if (!m) return null;
  // R-CPD-2 (`creator-pre-deploy-batch`), correcting R-FSA-A3-1, which was the architect's: the A0
  // line listed EVERY attribution category at desktop and turned a header box into a full-width
  // band — 135 characters over nine categories on the day it was read. Jason's answer to the fork
  // was "Cap it." THE HEAD TAKES THE CAP THE PHONE ALREADY APPLIES, at every band: one constant,
  // not a second rule, so the two bands cannot drift apart again.
  //   THE OVERLAY IS NOT CAPPED, and that is the other half of the ruling: the full attribution
  // keeps its home one tap away behind CHANGE, where a reader who wants the inventory can have it.
  // So the head's cap and the overlay's are separate values on purpose.
  var headCap = FC_SOURCES_HEAD_CAP;
  var overlayCap = p.band === 'mobile' ? FC_SOURCES_HEAD_CAP : null;
  function close() { setOpen(false); if (changeRef.current) changeRef.current.focus(); }
  return (
    <React.Fragment>
      {/* R-CPD-2: the span is GONE. `gridColumn:'1 / -1'` made the box straddle both of the head's
          columns at <=1366 and pushed it onto its own row — a BAND. Without it the box takes the
          head's first column, under the step counter, which is where the mockup's caption has said
          it belongs since v1 ("a box in the header, beside the step counter"). Above 1366 the head
          is not a grid at all, so the span was inert there and its removal changes nothing.
          WIDTH IS NOT PINNED HERE, and one attempt is worth recording: `width:max-content` collapsed
          the box to 48px, because `.sb-band-h` is a flex ROW whose scope line is `flex:1 1 auto` —
          under max-content that basis resolves to nothing and the line wrapped nine ways at BOTH
          bands. What makes this a box rather than a band is the CAP on its line plus the column it
          sits in; the row inside it is meant to fill whatever it is given.
          COLUMN TWO, not one: the head's first column is `auto` and sized by the step counter — 303px
          measured — which wrapped the capped line NINE ways. Column two is the `1fr` beside it, which
          is where "a box in the header, beside the step counter" actually puts it. Above 1366 there is
          no grid and the property is inert. */}
      <div className="sb-band sb-band-1" data-sources-box="1" data-compact={p.compact ? '1' : undefined}
        style={{ marginTop: '16px', gridColumn: '2' }}>
        <div className="sb-band-h">
          <div className="sb-band-t">Active sources</div>
          {/* B-583 — the PHONE's version is the name and the door (Jason 2026-09-14: "just have it
              say Active Sources and the change button"); the counts wait behind CHANGE. */}
          {p.compact ? null : <div className="sb-band-scope" data-sources-head="1">{fcSourcesHead(m, headCap)}</div>}
          <button type="button" className="sb-toggle" data-sources-change="1" ref={changeRef}
            aria-haspopup="dialog" aria-expanded={open ? 'true' : 'false'}
            onClick={function() { setOpen(true); }}>
            Change<span className="sb-chev">{'\u203a'}</span>
          </button>
        </div>
      </div>
      {open ? <FCSourcesOverlay model={m} band={p.band} cap={overlayCap} onClose={close} /> : null}
    </React.Fragment>
  );
}

/* ── THE MECHANIC SHEET (cluster two, screen two) ─────────────────────────────────────────────
   The board's mechanic frame, emitted (R-TTP-1). It is the CHARACTERISTICS shell — `hpw-grid
   hpw-grid-spend`, rail/head/spend — and not the browsers' four-column one, because that is what
   the board draws it in.

   THE CHANGE THIS SCREEN IS: the shipped step asks its questions in SEQUENCE (pick a type, then
   read it, then a separate trades panel), so a player priced a trade against a track they had
   already committed to and could not see the two together. The board names, scopes and prices
   BOTH before either is touched — the tracks block sits above the picker, and Trade sits beside
   it, on one screen. `FCMechanicStep` keeps every selector and handler and is demoted to this
   sheet's data source; the `type`/`typeDetail`/`trades` sub-views collapse into this one frame.
   `moralityLayer` is untouched: it is a LATER stage asking a different question, not a pane.

   NOT A SECOND RESOLUTION PATH. Every figure here — the base value, the trade arithmetic, the
   filtered type list, the saved-pick re-admission — is computed in `FCMechanicStep` exactly as it
   was and handed down. This component chooses no numbers. */
/* ── THE MECHANIC PHONE SHEET (the board's three 390 frames) ───────────────────────────────────
   `phone-mechanic-census` R-PMeC-1: the ONE shared component is `FCMechanicStep`'s existing
   `transplanted` branch → `FCMechanicSheet`, taught the mobile band here. A DIFFERENT TREE from
   the wide sheet, not the wide one under container queries — the same call `FCCharSheet` makes one
   screen over, and for the same measured reasons: the board gives the phone a STATUS STRIP instead
   of the rail's plates, folds each register into a `<details class="hp-unit">` whose summary names
   the choice, splits the two-track pair into TWO separate collapsed pickers with their own section
   heads (where the wide sheet draws them as a two-column `hp-spend`), and puts So Far and the
   commit where `FCPhoneShell` puts them for every step.

   THE FRAMES ARE `phone_mechanic()`, `phone_mechanic(spec_id=marauder, kind=duty, step=[5,11])`
   and `phone_mechanic(spec_id=marauder, kind=morality)` at the freeze pin — Jason approved them on
   `lab/holocron/phone_approval.html` @ c0744b3a (`parallel-pull-grant` A3).

   WHAT IS DERIVED AND NOT COPIED (`phone-mechanic-census` A1, R-PMeC-A1-2). The duty and morality
   frames emit the literal "Obligation" as the track NAME, and the morality one draws Morality's
   fixed personal 50 as a party pool — the lab's own defect (`build.py:3306`), which its default
   frame and this app both get right. Both frames are Jason-approved, so no seat corrects them
   (R-MIL-2): this sheet renders `{t.name}` and the group/personal designation FROM THE RECORD, and
   the two fixtures' `hp-track-name` divergence is red on the board's account under B-524 until he
   rules the law-or-mock line.

   AND WHAT THE FRAMES DO NOT DRAW, kept because the step does not work without it: the PARTY SIZE
   row and the TRADE panel (RAW — eote p.38 / aor p.44 / fad p.48's one creation trade), and, on
   the two-track state, the Morality keep/decline the card carries forward (R-PMeC-2). Each is
   named at the gate as an object the board is silent about; none is declared `data-ruled-delta`,
   because that list routes to the architect BEFORE it ships (R-TTP-1).

   THE SEARCH PLATE IS NOT CARRIED (`phone-mechanic-census`, the KEEP/RETIRE table): none of the
   three frames draws one, the picklist scrolls inside its own clip, and B-516's mechanic half
   closes by that retirement. */
function fcMechPhoneUnit(o) {
  return (
    <React.Fragment>
      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
        <h2>{o.title}</h2><span className="hp-cite">{o.cite}</span></div>
      {/* UNCONTROLLED, so the browser owns the disclosure and React never fights a player who
          opened it. The frames all draw it CLOSED with the choice named in the summary, which is
          also the only arrangement that fits three registers on one 790px screen. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">{o.chosen || 'Choose one'}</span>
            <span className="hp-glyph">{'▾'}</span>
          </div>
        </summary>
        <div className="hpw-scroll hp-picklist" style={{ marginTop: '9px' }}>
          <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
            {o.rows.map(function(r) { return fcRegisterRow(r, o.selected, o.onPick); })}
          </div>
        </div>
      </details>
      {o.detail ? (
        <div className="hp-plate" data-pad="read" style={{ marginTop: '9px' }}>
          <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>{o.detail}</p>
        </div>
      ) : null}
    </React.Fragment>
  );
}

/* THE MORALITY KEEP/DECLINE OFFER — ONE BLOCK, BOTH BANDS (C-1, ruling `build-phone-mechanic`
   R-BPM-A2-2). It began as phone-only markup inside `FCMechanicSheetPhone`, because the approved
   wide frame draws no control and R-MIL-2 forbids a seat adding one to a frame on its own
   authority. A2-2 is the authority that was missing: the keep/decline is where the app records the
   GROUP's option (FaD's (c) against (a)/(b)) and the app has no campaign object, so the question
   exists and is not the app's to answer silently. At mid and wide it was answered silently — the
   `moralityLayer` sub-view that used to ask it is never rendered on the transplanted path, so
   `moralityKept` defaulted to true and completed. The offer is built at every band instead, and
   default-keep becomes a PRE-SELECTED ANSWER TO A VISIBLE QUESTION.
   ONE HELPER, NOT TWO COPIES. Both sheets draw the identical control, and a second copy of a
   control is the parallel-reimplementation defect by name — the same law that keeps one resolver
   per shared quantity. The wrapper stays UNNAMED and `data-mech-undrawn`, exactly as the phone's
   did: the board has no word for this object, so it is red on the BOARD's account by count under
   D1 and `MECH_STRIP_UNDRAWN=1` strips it for the drawn-parts comparison. The lab is asked to draw
   it into the mechanic frames at both bands (R-PMeC-2, widened by A2-2). */
function fcMoralityKeepOffer(t) {
  if (!t || !t.onKeep) return null;
  return (
    <div data-mech-undrawn="morality-keep" style={{ marginTop: '8px' }}>
      {/* THE QUESTION, IN THE CAMPAIGN'S WORDS (A2-2 (1)). Not "do you want Morality" — the
          rulebook offers the TABLE three ways to run a Force-sensitive in an Obligation or Duty
          game, and this control records which one this table took. Mechanics voice: say what it
          is. */}
      <span className="hp-none" data-morality-offer="1">
        {'Your table uses Morality for Force-sensitives \u2014 keep \u00b7 decline (fad p.338-339).'}
      </span>
      <button type="button" className="hp-buy-btn" data-morality-keep="1"
        data-on={t.kept ? '1' : undefined}
        aria-pressed={t.kept ? 'true' : 'false'}
        aria-label={t.kept ? 'Keeping Morality at 50 \u2014 press to decline it'
          : 'Morality declined \u2014 press to keep it at 50'}
        onClick={function() { t.onKeep(!t.kept); }}>
        {t.kept ? 'Keeping' : 'Declined'}</button>
      {/* RELABEL, NEVER SILENCE (R-MCT-1): a control that offers a choice without naming its
          consequence is the silent half of the defect that rule was written for. RE-READ AGAINST
          THE PRINTED PAGE (A2-2 (3)): the old string said the character "ignores Morality and the
          Conflict rule entirely", which is broader than the text. What the rule says is the three
          things below, and the cite carries the PRINTED page 338 (341 is the corpus page; the
          offset belongs to `swrpg_index/pdf_pages.py`, never to a string). "Entirely" is dropped.
          READ AT THE PAGE, not taken on trust — `test/raw_citation_pages.cjs` scans `.jsx` but its
          living population is 0 and a planted wrong page did NOT redden it, so its green is no
          evidence about this string (flagged at the gate). The corpus was read instead: fad corpus
          341 reports `printed_start` 338 and carries "PCs from Force and Destiny who are not using
          the Morality mechanic should ignore any Conflict they earn, and they cannot become dark
          side Force users or light side paragons" — so the words below are the printed rule's, and
          338-339 is the run of the three group options this offer records. */}
      <span className="hp-none" data-morality-decline-note="1">
        {'Decline and your character tracks no Morality, ignores any Conflict earned, and can '
         + 'become neither a dark side Force user nor a light side paragon (fad p.338).'}
      </span>
    </div>
  );
}

function FCMechanicSheetPhone(p) {
  var sel = p.selectedRow;
  var tracks = p.tracks || [];
  // POOLED IS READ FROM THE RECORD, never from the frame's words: the status strip's middle label
  // is "Your share" on a group track and "Yours" on a personal one, and the morality frame draws
  // the wrong one of those two (A1). `scopeWord` is what `FCMechanicStep` derived above.
  var pooled = !!(tracks[0] && tracks[0].scopeWord === 'group');
  return (
    <FCPhoneShell band={p.band}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      status={<React.Fragment>
        <div><span className="hp-status-k">Starting</span>
          <span className="hp-status-v">{p.xpPool} XP</span></div>
        <div><span className="hp-status-k">{pooled ? 'Your share' : 'Yours'}</span>
          <span className="hp-status-v">{tracks[0] ? tracks[0].value : 0}</span></div>
        <div><span className="hp-status-k">Tracks</span>
          <span className="hp-status-v">{tracks.length}</span></div>
      </React.Fragment>}
      act={
        <button type="button" className="hp-commit" data-creator-commit="1"
          onClick={sel ? p.onConfirm : undefined} disabled={!sel}>
          {sel ? <React.Fragment>{p.commitLabel + ' '}<span>{'›'}</span></React.Fragment>
            : 'Choose one to continue'}
        </button>}>

      {/* The phone's own spacing, from its fixture: a 14px lift off the status strip and a 10px
          gap, where the wide sheet's head row uses 11px and no lift. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">{fcMakerLine(p.stepIndex == null ? null : p.stepIndex + 1, p.stepTotal)}</div>
      </div>
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName}</h1>
      <div className="hp-desig-rule" />
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        {p.lead ? <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}
          dangerouslySetInnerHTML={{ __html: p.lead }} /> : null}
        {/* BOTH cites where the frame draws both: the two-track frame carries the line's own book
            and Force and Destiny beside it, and a count is structure. The PAGE NUMBERS are the
            app's TOC-judged ones, never the frames' (`creator-gameline-drawing`/B-330 precedent). */}
        <div style={{ marginTop: '10px' }}>
          <span className="hp-cite" data-step-cite={String(p.stepName || '').toLowerCase()}>{p.cite}</span>
          {p.cite2 ? <span className="hp-cite" style={{ marginLeft: '7px' }}>{p.cite2}</span> : null}
        </div>
      </div>

      <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
        <h2>What You Will Carry</h2>
        <span className="hp-cite">{tracks.length + (tracks.length === 1 ? ' track' : ' tracks')}</span>
      </div>
      <div className="hp-tracks">
        {tracks.map(function(t, i) {
          return (
            <div className="hp-track" data-primary={i === 0 ? '1' : undefined} key={t.name}>
              <div className="hp-track-top">
                <span className="hp-track-scope">{t.scope}</span>
                <span className="hp-track-val">{t.value}</span>
              </div>
              <div className="hp-track-name">{t.name}</div>
              <div className="hp-track-line">{t.line}</div>
              <p className="hp-prose hp-prose-sm" style={{ margin: '7px 0 0' }}
                dangerouslySetInnerHTML={{ __html: t.prose }} />
              <div className="hp-track-foot">
                <span className="hp-none">{t.foot}</span>
                {/* THE MORALITY LAYER'S CHOICE, ON THE TRACK IT IS ABOUT (R-PMeC-2). The shipped
                    flow asked it on a fourth page AFTER the commit; on the transplanted path that
                    page is never rendered, so the question had nowhere to be asked at all. It is
                    the same two answers — keep the 50, or ignore Morality and the Conflict rule
                    entirely (fad p.341) — put on the card that names the track. Undrawn: a frame
                    request rides R-PMeC-2 and this is named at the gate. */}
                {/* THE CONTROL SITS IN AN UNNAMED WRAPPER, and that is load-bearing rather than
                    tidy: the board has no word for this object, and the parity layers compare
                    NAMED objects only — so a bare `<div>` adds nothing to either comparison, while
                    restyling `hp-track-foot` into a flex row to seat the button would have moved a
                    property the board DOES speak about (measured: `gap` board `normal` vs app
                    `10px`, on every track card of all three frames). The block flows under the
                    track's own line instead. */}
                {fcMoralityKeepOffer(t)}
              </div>
            </div>
          );
        })}
      </div>

      {fcMechPhoneUnit({
        title: p.listTitle, cite: p.listCite,
        chosen: sel ? sel.name : null, rows: p.rows, selected: sel, onPick: p.onPick,
        detail: sel ? sel.description : null,
      })}

      {/* THE PAIR, RESTATED WHERE THE CHOICE IS KEPT — single-track Morality only, because the
          two-track state draws its own two registers below instead. The board's phone frame heads
          it "The Pair" and prints the strength and its shadow as two paragraphs; the shadow is
          DERIVED off the same `weakKey` mapping the register rows use (B-117), never stored. */}
      {sel && sel.pairedWeakness ? (
        <React.Fragment>
          <div className="hp-sechead" style={{ margin: '18px 0 9px' }}>
            <h2>The Pair</h2>
            <span className="hp-cite">the weakness follows the strength</span></div>
          <div className="hp-plate" data-pad="read">
            <p className="hp-prose hp-prose-sm" style={{ margin: '0 0 7px' }}>
              <b>{sel.name}</b>{' ' + sel.description}</p>
            <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
              <b>{sel.pairedWeakness.name}</b>{' ' + sel.pairedWeakness.description}</p>
          </div>
        </React.Fragment>
      ) : null}

      {/* THE TWO-TRACK PAIR — the frame's second and third collapsed pickers, each under its own
          section head ("Emotional Strength" 39, "Emotional Weakness" 48), where the wide sheet
          draws them side by side under one "Your Emotional Pair" head. Same two lists, same
          override rule: the Weakness register is an OVERRIDE of the book's pairing, never a second
          roll (R-MEP-1/2). No search plate at this width, as above. */}
      {p.pair ? [p.pair.strength, p.pair.weakness].map(function(col, i) {
        return (
          <React.Fragment key={col.title}>
            {fcMechPhoneUnit({
              title: i === 0 ? 'Emotional Strength' : 'Emotional Weakness',
              cite: col.count,
              chosen: col.detail ? col.detail.name : null,
              rows: col.rows, selected: col.selected, onPick: col.onPick,
              detail: col.detail ? col.detail.description : null,
            })}
          </React.Fragment>
        );
      }) : null}

      {/* PARTY SIZE — the Obligation/Duty rule only; Morality's starting value is fixed (fad p.52),
          so `partySizes` is null there and this block is absent, exactly as on the wide sheet. */}
      {p.partySizes ? (
        <div data-mech-undrawn="party">
          <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
            <h2>Party Size Sets the Share</h2></div>
          <div className="hp-plate" data-pad="data">
            <div className="hp-partyrow">
              {p.partySizes.map(function(o) {
                return (
                  <button type="button" className="hp-party" key={o.label}
                    data-on={o.on ? '1' : undefined}
                    aria-pressed={o.on ? 'true' : 'false'}
                    aria-label={'Party size ' + o.label + (o.label === '6+' ? ' or more' : '')}
                    onClick={function() { p.onPartySize(o.n); }}>
                    <span>{o.label}</span><span className="hp-none">{o.value}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      <div data-mech-undrawn="trade">
      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
        <h2>Trade</h2><span className="hp-cite">{p.tradeCite}</span></div>
      <div className="hp-plate" data-pad="data">
        {p.trades.map(function(t) {
          return (
            <div className="hp-buy" key={t.id}>
              <span className="hp-buy-name">{t.name}</span>
              {/* A TRADE THAT DOES NOT MOVE THE TRACK SAYS SO (R-MCT-1) — the wide sheet's rule,
                  unchanged: an arrow between two identical numbers reads as a change that did not
                  happen. */}
              <span className="hp-buy-step">
                {t.from === t.to
                  ? 'no change to the track'
                  : <React.Fragment>
                      {t.trackLabel + ' ' + t.from + ' '}<i>{'→'}</i>{' ' + t.to}
                    </React.Fragment>}</span>
              <button type="button" className="hp-buy-btn"
                data-on={t.taken ? '1' : undefined}
                aria-pressed={t.taken ? 'true' : 'false'}
                disabled={!t.taken && t.disabled}
                onClick={function() { p.onTrade(t.id); }}>
                {t.taken ? 'Taken' : 'Take'}</button>
            </div>
          );
        })}
      </div>
      </div>

      {/* SO FAR FOLDS on the phone — the board makes it a `<details>` at the foot rather than a
          block in a rail there is no room for, and it is the same builder every phone step uses. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'▾'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <FCbSoFarTable rows={p.soFar} />
        </div>
      </details>
    </FCPhoneShell>
  );
}

function FCMechanicSheet(p) {
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal;
  var sel = p.selectedRow;
  // THE PHONE IS ITS OWN SHEET, not this one reflowed — see `FCMechanicSheetPhone` above. The
  // same call `FCCharSheet` makes one screen over, and the ONE component the mount still names.
  if (p.band === 'mobile') return <FCMechanicSheetPhone {...p} />;

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-mechanic-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: the budget, what is settled, and WHAT YOU ARE CARRYING ───────────────
             The browsers put their LIST here; this step does not. Its rail's third block is the
             tracks LEDGER — the running total of what the character will walk out with — because
             the list belongs beside the trade that prices it. The board is explicit about that
             and it is the whole reason this step has its own sheet. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1" data-rail-stack="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool} XP</dd></div>
              <div className="hp-xp"><dt>Spent here</dt><dd>{p.xpSpentHere} XP</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Remaining</dt>
                <dd data-creator-char-purse={p.xpRemaining}>{p.xpRemaining} XP</dd></div>
            </dl>
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {p.soFar.map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          {/* The board's rail cite here is a BARE COUNT ("1") where the spend pane's is "1 track".
              Two different sentences about the same number, and both are the board's. */}
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
            <h2>Your Tracks</h2><span className="hp-cite">{p.tracks.length}</span></div>
          <div className="hp-plate" data-pad="data" data-rail-section="tracks">
            <table className="hp-ledger"><tbody>
              {p.tracks.map(function(t) {
                return (
                  <tr key={t.name}>
                    <td className="hp-lkey">
                      {t.name + ' '}<span className="hp-sep">{'·'}</span>{' ' + t.scopeWord}</td>
                    <td className="hp-lval">{t.value}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          <div className="hp-railact" data-rail-section="commit">
            <button type="button" className="hp-commit" data-creator-commit="1"
              onClick={sel ? p.onConfirm : undefined} disabled={!sel}>
              {sel ? <React.Fragment>{p.commitLabel + ' '}<span>{'›'}</span></React.Fragment>
                : 'Choose one to continue'}
            </button>
          </div>
        </div>

        {/* ── THE HEAD ───────────────────────────────────────────────────────────────────────
             No `hpw-steps` wrapper: the board wraps the wave on the SPECIES head only, because
             that head sits in the four-column grid. This one is the characteristics head's shape. */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <span className="hp-slash"><i /><i /><i /></span>
              <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
            </div>
            <h1 className="hp-desig">{p.stepName}</h1>
            <div className="hp-desig-rule" />
            <div className="hp-plate" data-pad="read"
              style={{ marginTop: '13px', maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              {p.lead ? <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}
                dangerouslySetInnerHTML={{ __html: p.lead }} /> : null}
              <div style={{ marginTop: '11px' }}>
                <span className="hp-cite" data-step-cite={String(p.stepName || '').toLowerCase()}>{p.cite}</span>
                {p.cite2 ? (
                  <span className="hp-cite" style={{ marginLeft: '7px' }}>{p.cite2}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the tracks, then the picker BESIDE its price ──────────────────── */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            <div className="hp-sechead" style={{ margin: '0 0 12px' }}>
              <h2>What You Will Carry</h2>
              <span className="hp-cite">{p.tracks.length + (p.tracks.length === 1 ? ' track' : ' tracks')}</span>
            </div>
            <div className="hp-tracks">
              {p.tracks.map(function(t, i) {
                return (
                  <div className="hp-track" data-primary={i === 0 ? '1' : undefined} key={t.name}>
                    <div className="hp-track-top">
                      <span className="hp-track-scope">{t.scope}</span>
                      <span className="hp-track-val">{t.value}</span>
                    </div>
                    <div className="hp-track-name">{t.name}</div>
                    <div className="hp-track-line">{t.line}</div>
                    {/* THE SAME TREATMENT THE LEAD ABOVE ALREADY TAKES. The board bolds two words
                        inside the second track's prose ("Force-sensitive", "50"), and `<b>` is an
                        UNNAMED element — invisible to the DOM layer, visible to a reader. Rendering
                        it as text would pass parity while printing the board's markup at the
                        player. These strings are ours, authored above; none is player input. */}
                    <p className="hp-prose hp-prose-sm" style={{ margin: '7px 0 0' }}
                      dangerouslySetInnerHTML={{ __html: t.prose }} />
                    <div className="hp-track-foot">
                      <span className="hp-none">{t.foot}</span>
                      {/* C-1 (R-BPM-A2-2): the SAME offer the phone card draws, so the
                          two-track question is VISIBLE at mid and wide instead of being
                          answered by the default on the way past. */}
                      {fcMoralityKeepOffer(t)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* TWO COLUMNS, AND THE UNCLASSED WRAPPERS ARE THE COLUMNS — the same fact the
                browsers' read pane records: `hp-spend` is a grid whose CHILDREN are its tracks,
                so removing these divs transposes the widths. Invisible to the DOM layer (it
                walks named objects); the style layer is what catches their loss. */}
            <div className="hp-spend" style={{ marginTop: '24px' }}>
              <div>
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}>
                  <h2>{p.listTitle}</h2><span className="hp-cite">{p.listCite}</span></div>
                <div className="hp-plate" data-flat="1" data-input="1">
                  <input type="search" value={p.searchValue || ''} data-register-search="1" placeholder={p.searchPlaceholder}
                    aria-label={p.searchPlaceholder}
                    onChange={function(e) { p.onSearch(e.target.value); }}
                    style={{
                      width: '100%', border: 0, background: 'transparent', outline: 'none',
                      padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: '12px',
                      letterSpacing: '.12em', color: 'var(--doc-ink)', textTransform: 'uppercase',
                    }} />
                </div>
                <div className="hpw-scroll hp-picklist" style={{ marginTop: '8px' }}>
                  <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
                    {p.rows.map(function(row) { return fcRegisterRow(row, sel, p.onPick); })}
                  </div>
                </div>
                {/* THE SELECTED TYPE'S READING. The board draws it under its own section head,
                    named for the type — the shipped step put this behind a `typeDetail` sub-view
                    that replaced the list, which is the arrangement this screen exists to undo. */}
                {sel ? (
                  <React.Fragment>
                    {/* THE HEAD IS NAMED FOR WHAT IS UNDER IT. Obligation and Duty draw the type's
                        own name, which is what their board frames draw. Morality's block holds TWO
                        readings — the strength and the shadow it resolves to — so the board heads
                        it "The Pair" and cites the rule; a head reading "Bravery" over a paragraph
                        about Anger would be the wrong label, not merely a shorter one. */}
                    <div className="hp-sechead" style={{ margin: '18px 0 10px' }}>
                      <h2>{sel.pairedWeakness ? 'The Pair' : sel.name}</h2>
                      {sel.pairedWeakness
                        ? <span className="hp-cite">the weakness follows the strength</span>
                        : null}</div>
                    <div className="hp-plate" data-pad="read">
                      <p className="hp-prose hp-prose-sm"
                        style={{ margin: sel.pairedWeakness ? '0 0 8px' : 0 }}>
                        {sel.pairedWeakness ? <b>{sel.name}</b> : null}
                        {sel.pairedWeakness ? ' ' : ''}{sel.description}</p>
                      {/* THE SHADOW, RESTATED WHERE THE CHOICE IS KEPT. `FCMechanicTypeDetail`
                          already shows it while the player is BROWSING (B-67(b)) — but the board
                          draws it AGAIN here, on the screen that commits, and this is the half a
                          player is least likely to have read. Derived, never stored: it comes off
                          the same `weakKey` mapping the register rows use (B-117), so there is no
                          second resolution path for the same fact. */}
                      {sel.pairedWeakness ? (
                        <React.Fragment>
                          <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
                            <b>{sel.pairedWeakness.name}</b>
                            {' ' + sel.pairedWeakness.description}</p>
                          <div style={{ marginTop: '9px' }}>
                            {/* THE A6 SENTENCE, character-exact to the board at 841b5aa7 (both
                                Morality states carry it; `mechanic-close-out` A7). */}
                            <span className="hp-none">
                              {'the weakness follows the strength; changing it is the table\u2019s call.'}</span>
                          </div>
                        </React.Fragment>
                      ) : null}
                    </div>
                  </React.Fragment>
                ) : null}
              </div>
              <div>
                {/* PARTY SIZE IS THE OBLIGATION/DUTY RULE ONLY. Morality's starting value is FIXED
                    (fad p.52), so the block is absent there. THIS NOTE USED TO SAY the board "drew
                    no Morality frame" and that no swept fixture reached this null — both were true
                    when written and are false now: `board_mech_morality_{1366,1920}` are swept, and
                    they reach it. The board CONFIRMS the branch rather than leaving it unspoken —
                    its obligation frame draws five `hp-party` buttons and its morality frame draws
                    none. Measured, not inferred. */}
                {p.partySizes ? (
                  <React.Fragment>
                    <div className="hp-sechead" style={{ margin: '0 0 11px' }}>
                      <h2>Party Size Sets the Share</h2></div>
                    <div className="hp-plate" data-pad="data">
                      <div className="hp-partyrow">
                        {p.partySizes.map(function(o) {
                          return (
                            <button type="button" className="hp-party" key={o.label}
                              data-on={o.on ? '1' : undefined}
                              aria-pressed={o.on ? 'true' : 'false'}
                              aria-label={'Party size ' + o.label + (o.label === '6+' ? ' or more' : '')}
                              onClick={function() { p.onPartySize(o.n); }}>
                              <span>{o.label}</span><span className="hp-none">{o.value}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </React.Fragment>
                ) : null}
                <div className="hp-sechead" style={{ margin: '20px 0 11px' }}>
                  <h2>Trade</h2><span className="hp-cite">{p.tradeCite}</span></div>
                <div className="hp-plate" data-pad="data">
                  {p.trades.map(function(t) {
                    return (
                      <div className="hp-buy" key={t.id}>
                        <span className="hp-buy-name">{t.name}</span>
                        {/* A TRADE THAT DOES NOT MOVE THE TRACK SAYS SO (R-MCT-1). Morality's
                            first three options — +10 XP, +2,500 credits, +5 XP · +1,000 credits —
                            leave the track at 50, and this row printed `Morality 50 → 50`: an
                            arrow between two identical numbers, which reads as a change that did
                            not happen rather than as a trade with no cost to the track. "no change
                            to the track" is the board's own line for those three and is RAW.
                            THE RULE IS GENERAL, not a Morality special case: any trade whose
                            `from` equals its `to` says so. Obligation and Duty are untouched
                            because every one of their trades moves the track (10 → 15, 10 → 20). */}
                        <span className="hp-buy-step">
                          {t.from === t.to
                            ? 'no change to the track'
                            : <React.Fragment>
                                {t.trackLabel + ' ' + t.from + ' '}<i>{'→'}</i>{' ' + t.to}
                              </React.Fragment>}</span>
                        <button type="button" className="hp-buy-btn"
                          data-on={t.taken ? '1' : undefined}
                          aria-pressed={t.taken ? 'true' : 'false'}
                          disabled={!t.taken && t.disabled}
                          onClick={function() { p.onTrade(t.id); }}>
                          {t.taken ? 'Taken' : 'Take'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── YOUR EMOTIONAL PAIR (R-MEP-1/2; fad printed p.48, R-MCT-2) ─────────────────────────────────────
                 The board's SECOND `hp-spend` block, drawn only in the two-track state. Two
                 registers, because the book has two tables: "picking a Strength lights its
                 partner — the weakness follows the strength; changing it is the table's call" is
                 the board's sentence at 841b5aa7 (the pair-contract's rule, printed p.48), not a
                 liberty the board took. The Weakness register is therefore an OVERRIDE, never a
                 second roll — the lit partner is `weakKey`'s, which is B-117's fix and stays the
                 default for everyone who does not depart from it. */}
            {p.pair ? (
              <React.Fragment>
                <div className="hp-sechead" style={{ margin: '24px 0 12px' }}>
                  <h2>Your Emotional Pair</h2><span className="hp-cite">{p.pair.cite}</span></div>
                <p className="hp-prose hp-prose-sm" style={{ margin: '0 0 12px' }}>{p.pair.lead}</p>
                <div className="hp-spend">
                  {[p.pair.strength, p.pair.weakness].map(function(col) {
                    return (
                      <div key={col.title}>
                        <div className="hp-sechead" style={{ margin: '0 0 10px' }}>
                          <h2>{col.title}</h2><span className="hp-cite">{col.count}</span></div>
                        <div className="hp-plate" data-flat="1" data-input="1">
                          <input type="search" value={col.query || ''} placeholder={col.placeholder}
                            aria-label={col.placeholder}
                            onChange={function(e) { col.onSearch(e.target.value); }}
                            style={{
                              width: '100%', border: 0, background: 'transparent', outline: 'none',
                              padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: '12px',
                              letterSpacing: '.12em', color: 'var(--doc-ink)',
                              textTransform: 'uppercase',
                            }} />
                        </div>
                        <div className="hpw-scroll hp-picklist" style={{ marginTop: '8px' }}>
                          <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
                            {col.rows.map(function(r) {
                              return fcRegisterRow(r, col.selected, col.onPick);
                            })}
                          </div>
                        </div>
                        {col.detail ? (
                          <React.Fragment>
                            <div className="hp-sechead" style={{ margin: '18px 0 10px' }}>
                              <h2>{col.detail.name}</h2></div>
                            <div className="hp-plate" data-pad="read">
                              <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
                                {col.detail.description}</p>
                              {/* THE PAIRING, SAID OUT LOUD. The board prints it under the
                                  weakness so a player can see what the book would have given them
                                  — which is the only way an override reads as a choice rather
                                  than as the app having forgotten the pair. */}
                              {col.detail.note ? (
                                <div style={{ marginTop: '9px' }}>
                                  <span className="hp-none">{col.detail.note}</span></div>
                              ) : null}
                            </div>
                          </React.Fragment>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

function FCCharSheet(p) {
  var ATTRS = FC_CHAR_ATTRS;
  var ch = p.character, sp = p.speciesData;
  if (!ch || !sp) return null;
  var base = (speciesEff(sp, ch.speciesOptionPicks).characteristics || {});
  // THE PHONE IS ITS OWN SHEET, not this one reflowed — see `FCCharSheetPhone`.
  if (p.band === 'mobile') {
    return <FCCharSheetPhone {...p} base={base} />;
  }
  var xpLeft = p.xpRemaining;
  // A27-4: the purse's transient answer to a refused press. Held HERE because this sheet draws both
  // the rows that refuse and the purse that answers.
  var _flash = useFcPurseFlash(900), purseFlash = _flash[0], firePurseFlash = _flash[1];
  // B-598 (Jason: "load the characteristic that was tapped"): at the mid band the tiles and the
  // raise-row names are doors, and the gloss shows ONE entry — the tapped one — or a hint.
  var _gp = useState(null), glossPick = _gp[0], setGlossPick = _gp[1];
  var glossDoor = p.band === 'mid' ? setGlossPick : undefined;
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal;
  var folio = fcFolio(stepNo, stepTot, 'Characteristics');

  function raised(a) { return (ch.characteristics[a] || 2) > (base[a] || 2); }
  function cur(a) { return ch.characteristics[a] || 2; }

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-char-step="1">

      {/* ── THE BAR. The board's runhead, and the app's BACK behaviour inside it (A2-2): the
           control is a real button on the same handler the shared header's back used. */}
      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'\u2039\u00a0\u00a0Character Creation'}</span>
          </button>
          <span className="hp-folio">{folio}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE INDEX PANE: three blocks, and the commit at the foot. No step list — the wave
             below carries navigation now (A2-1), and it retires in this same commit. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1" data-rail-stack="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool} XP</dd></div>
              <div className="hp-xp"><dt>Spent here</dt><dd>{p.xpSpentOnChars} XP</dd></div>
              {/* The purse hook sits on the `dd` that CARRIES THE COLOUR. The board's rule is
                  `.hp-xp[data-over="1"] dd { color: … }`, so a probe reading the wrapper's colour
                  reads the inherited ink and reports the refusal token missing. */}
              {/* A2-3: a SECOND, WEAKER state on the same element and through the same board rule —
                  `data-short`, NOT a widened `data-over`. Overspent and can't-afford are different
                  conditions, and the overspent prose below must not fire for the second. The purse
                  hook the instruments read is unchanged.
                  A27-4: the state is the FLASH, set by a press on a refused raise and settling on
                  its own — not a standing read of "any raise out of reach". */}
              <div className="hp-xp" data-total="1" data-over={xpLeft < 0 ? '1' : undefined}
                data-short={(xpLeft >= 0 && purseFlash) ? '1' : undefined}>
                <dt>Remaining</dt>
                <dd data-creator-char-purse={xpLeft}>{xpLeft} XP</dd></div>
            </dl>
            {xpLeft < 0 && (
              <React.Fragment>
                <div className="hp-overspent">{'Overspent by ' + Math.abs(xpLeft) + ' XP'}</div>
                <p className="hp-prose hp-prose-sm" style={{ margin: '9px 0 0', maxWidth: '36ch' }}>
                  The step holds here until the balance is back to nought. Refund a raise, or take
                  back a talent, and the way forward opens.
                </p>
              </React.Fragment>
            )}
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data">
            <FCbSoFarTable rows={p.soFar} />
          </div>
          {/* BOUGHT HERE — the board's step-6 buyout VARIANT draws a purchase log in the rail
              (Addendum 3, option (d)). Gated on the species carrying an OFFER, not merely on
              having bought something: the board's overspent frame has three raises and draws no
              such block, so "has purchases" is the wrong condition and the parity guard said so
              the moment the two frames disagreed. */}
          {p.offers.length > 0 && p.boughtHere.length > 0 && (
            <React.Fragment>
              <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Bought Here</h2></div>
              <div className="hp-plate" data-pad="data">
                <table className="hp-ledger"><tbody>
                  {p.boughtHere.map(function(r) {
                    return (
                      <tr key={r.k}>
                        <td className="hp-lkey">{r.k}</td>
                        <td className="hp-lval">{r.xp} XP</td>
                      </tr>
                    );
                  })}
                </tbody></table>
              </div>
            </React.Fragment>
          )}
          <div className="hp-railact" data-rail-section="commit">
            {/* A1-4(2)'s RULED FACT-LINE. The board draws no such note, so it is a ruled delta and
                carries the marker that keeps it out of the parity comparison by NAME — the same
                mechanism as the refund control, and for the same reason: an allowed difference has
                to be enumerable, or it is just a difference. Furniture voice, never a warning:
                carrying XP into play is a legal and common choice. */}
            {xpLeft > 0 && (
              <div data-ruled-delta="unspent-note" className="hp-railnote">
                {xpLeft + ' XP unspent \u2014 it carries into play'}
              </div>
            )}
            {/* A1-4(2)'s ruled labels, in the board's own commit. */}
            <button type="button" className="hp-commit" data-creator-commit="1"
              onClick={xpLeft < 0 ? undefined : p.onNext} disabled={xpLeft < 0}>
              {xpLeft < 0 ? 'OVERSPENT BY ' + Math.abs(xpLeft) + ' XP'
                : <React.Fragment>{'Continue to Skills '}<span>{'\u203a'}</span></React.Fragment>}
            </button>
          </div>
        </div>

        {/* ── THE HEAD. The wave IS the navigation (A2-1: the buttons jump). */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          <nav className="hp-wave" aria-label="Character creation steps" data-rail-section="steps">
            {p.steps.map(function(st, i) {
              return (
                <button type="button" key={st} className="hp-wave-step"
                  data-done={i < (p.stepReached == null ? p.stepIndex : p.stepReached) ? '1' : undefined}
                  data-now={i === p.stepIndex ? '1' : undefined}
                  aria-current={i === p.stepIndex ? 'step' : undefined}
                  aria-label={'Step ' + (i + 1) + ' of ' + stepTot + ': ' + p.stepLabels[st]}
                  onClick={function() { p.onJump(st); }}>
                  <span className="hp-wave-lab" aria-hidden="true">{p.stepLabels[st]}</span>
                  <span className="hp-wave-bar" />
                </button>
              );
            })}
          </nav>
          {/* THE VARIANT WRAPS ITS WHOLE HEAD. On the buyout frame the board draws
              `hp-stephead > [hp-stephead-title, hp-plate]` — the lead plate is INSIDE the stephead,
              a sibling of the title, not after it. On the plain frame there is no stephead at all.
              Both shapes are the board's, so the wrapper is conditional; emitting it always would
              redden the plain frame and emitting it around the title only left the plate outside,
              which is what the containment check reported at object #26. */}
          {(function() {
            var titleBits = (
              <React.Fragment>
                <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                  <span className="hp-slash"><i /><i /><i /></span>
                  <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
                </div>
                <h1 className="hp-desig">Characteristics</h1>
                <div className="hp-desig-rule" />
              </React.Fragment>
            );
            var leadPlate = (
              /* THE PLATE'S MEASURE IS THE BOARD'S OWN INLINE STYLE, carried. It is not in a rule —
                 the board writes `max-width:calc(var(--pp-lead-max) + 38px)` on the element — so no
                 amount of deriving its stylesheet would ever have produced it, and the plate ran
                 the full head width (1514px against the board's 678). That is P1's size and P3's
                 flush right edge in one number. */
              <div className="hp-plate" data-pad="read"
                style={{ marginTop: '13px', maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
                {/* A1-4(1)/(3): the question opens the standfirst, the rule is stated ONCE here,
                    and the transaction below carries no sentence. */}
                <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}
                  dangerouslySetInnerHTML={{ __html: p.standfirst }} />
                <div style={{ marginTop: '11px' }}>
                  {p.cites.map(function(c) {
                    return <span className="hp-cite" key={c} data-step-cite="characteristics">{c}</span>;
                  })}
                </div>
              </div>
            );
            // THE BOARD WRAPS EVERYTHING AFTER THE WAVE in one div with `margin-top:12px`. It is
            // unnamed, so the DOM layer could not see it — and its absence made the head exactly
            // 12px short at EVERY desktop band, which is what the style layer reported as its last
            // remaining object. A wrapper that carries a margin is not decoration.
            return p.offers.length > 0 ? (
              <div className="hp-stephead">
                <div className="hp-stephead-title">{titleBits}</div>
                {leadPlate}
              </div>
            ) : (
              <div style={{ marginTop: '12px' }}>{titleBits}{leadPlate}</div>
            );
          })()}
        </div>

        {/* ── THE READ PANE. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            <div className="hp-spend">
              {/* `data-char-controls` is a SUITE HOOK on the board's own block — R-TTP-1 lists
                  `data-*` hooks among the allowed differences, so the measure asserts get their
                  anchor back without a wrapper the board does not draw. */}
              <div data-char-controls="1">
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}>
                  <h2>{p.offers.length ? 'Raise a Characteristic' : 'Starting Characteristics'}</h2>
                  {p.offers.length > 0 && <span className="hp-cite">{'new rank \u00d7 10'}</span>}
                </div>
                <div>
                  <div className="hp-plate" data-pad="snug">
                    <FCbRail character={ch} base={base} size="lg" onPick={glossDoor} picked={glossPick} />
                  </div>
                </div>
                {/* THE BOARD'S OWN INLINE MARGINS, matched value for value. These sit on the
                    element in the fixture rather than in a rule, so nothing derives them and a
                    plausible-looking 20px stayed wrong until the style layer measured it. */}
                <div className="hp-sechead" style={{ margin: '22px 0 11px' }}><h2>What This Changes</h2></div>
                <div className="hp-plate" data-pad="data">
                  <FCbChangesTable rows={p.changes} />
                </div>
              </div>
              {/* BUY OFF A PENALTY — the board's variant block. It renders from the OFFER FIELD,
                  never from the word "Primitive": the registry carries the same printed offer on
                  five different penalties and two species reach it only through a subspecies, so a
                  surface keyed to one name would miss four of them. That is the board's own note on
                  this frame, and it is a data rule, so it is honoured here rather than quoted. */}
              <FCbBuyOff offers={p.offers} offerCount={p.offerCount} taken={p.taken}
                xpLeft={xpLeft} onTake={p.onBuyoutTake} onUndo={p.onBuyoutUndo} />
              <div>
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>Raise</h2></div>
                <div className="hp-plate" data-pad="data">
                  <FCbBuyRows character={ch} base={base} xpLeft={xpLeft} onCharChange={p.onCharChange} onPick={glossDoor}
                    onRefused={firePurseFlash} />
                </div>
              </div>
            </div>
            {/* THE GLOSS IS ONE ENTRY AT THE MID BAND (B-598, Jason's iPad walk). At 1194×810 the
                working half — tiles, what-this-changes, the raise panel — fits the read pane; the
                six-entry gloss under it was 583px that pushed the pane 593 over. Here the tiles
                and the raise-row names are doors: tap BRAWN and the gloss below is Brawn's entry
                alone, tap another and it swaps — Jason: "it allows some exploration of the
                definitions". Untapped it is a one-line hint. The wide sheet and the phone draw
                the six open, as before. */}
            {p.band === 'mid' ? (
              <div className="hp-glosswrap" data-char-gloss-pick={glossPick || ''}>
                <div className="hp-sechead" style={{ margin: '26px 0 12px' }}>
                  <h2>{glossPick ? 'What ' + FC_CHAR_WORDS[glossPick] + ' Is' : 'What Each One Is'}</h2>
                  <span className="hp-cite" data-gloss-cite="1">{FC_CHAR_GLOSS_CITE}</span>
                </div>
                {glossPick ? (
                <div className="hp-plate" data-pad="read">
                  <FCbGloss character={ch} base={base} only={glossPick} />
                </div>
                ) : null}
              </div>
            ) : (
            <div className="hp-glosswrap">
              <div className="hp-sechead" style={{ margin: '26px 0 12px' }}>
                <h2>What Each One Is</h2>
                <span className="hp-cite" data-gloss-cite="1">{FC_CHAR_GLOSS_CITE}</span>
              </div>
              <div className="hp-plate" data-pad="read">
                <FCbGloss character={ch} base={base} />
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

/* ── THE CHARACTERISTIC RAIL (board §5) — READ-ONLY ────────────────────────────────────────────
   Six capsules, one row, always the same six, always in the printed order, never re-sorted by
   value and never abbreviated. This is what shows a player what they HAVE; the ledger below is
   the only thing that CHANGES it (R-VTG-4).

   IT CARRIES THE MARK. `data-raised` is the gold, and gold means exactly one thing on this
   surface: a value a decision lifted above its species baseline. Not "the highest number" — the
   numeral already says that, and a colour spent on what is already visible stops meaning anything
   at the moment it is finally needed.

   IT ALSO CARRIES TWO ANCHORS IT INHERITED FROM THE RETIRED STRIP, deliberately and not by
   accident: `data-char-capsule` (R-PAT-5's opacity subject, read by the paper-contrast and bands
   suites) and the tutorial's `p-custom-chars-box` coach-mark target. Neither suite was authorized
   to re-point at this gate, so the anchors move to the object that inherited their job rather
   than dying with the element that used to host them. That is the deletion discipline doing its
   work: the grep found five consumers, three of them un-repointable, and the design absorbed them. */
function FCCharRail({ character, speciesData }) {
  if (!character || !speciesData) return null;
  var attrs = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
  var base = (speciesEff(speciesData, character.speciesOptionPicks).characteristics || {});
  return (
    <div className="hpw-railwrap" data-char-rail="1">
      {/* HERO SIZE (R-TCP-3, board §5). "The rail is the SUBJECT here so it takes the hero size,
          and the transaction sits beside it." */}
      <div className="hp-rail" data-size="lg">
        {attrs.map(function(attr, idx) {
          var cur = character.characteristics[attr] || 2;
          var raised = cur > (base[attr] || 2);
          return (
            <div key={attr} className="hp-char" data-raised={raised ? '1' : undefined}
              data-tour-id={idx === 0 ? 'p-custom-chars-box' : undefined}>
              <span className="hp-char-val" data-char-capsule={raised ? '1' : '0'}>
                <span className="hp-char-bead">{cur}</span>
              </span>
              <i className="hp-char-lab">{FC_CHAR_WORDS[attr]}</i>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── THE RAISE LEDGER — THE ONE WAY TO SPEND (board's spend ledger; R-VTG-4) ───────────────────
   One row per characteristic: the name, the transaction, the price, the act. The board draws no
   +/- strip at any band, so this is the whole control surface for the most expensive purchase in
   character creation.

   THE THREE ROW STATES ARE THE BOARD'S OWN CLASSES, and the distinction between the last two is
   the part worth not collapsing:

     SELLABLE   `2 -> 3` · `30 XP` · a filled RAISE.
     CLASS A    the price you cannot meet YET. `Needs 30 XP · you hold 10` — it PRICES THE THING
                IT REFUSES. This is B-118's law, learned in production the hard way: the price of
                the next point used to vanish at exactly the moment a player most needed it. The
                refusal is arithmetic and temporary; the figure never leaves.
     CLASS B    the limit you have REACHED. `at cap` in neutral ink, never red — no amount of XP
                moves it, and folding this into class A would tell a player to go and find
                experience they cannot spend.

   THE REFUND IS OURS, NOT THE BOARD'S — A RULED DIVERGENCE, CITED HERE BECAUSE IT WAS RULED.
   The board's own overspent frame (110 of 90, Brawn at cap) draws no control that lowers
   anything, while the prose printed on that same frame says:

       "The step holds here until the balance is back to nought. Refund a raise, or take back
        a talent, and the way forward opens."

   So the mockup CONTRADICTS ITSELF: it names the way out and does not draw it, and a player who
   mis-clicks into an overdraw would have no way back on that screen. C-MU-4 governs — the
   standards outrank the mockup and a mockup is a proposal — and a dead end is not shippable.
   KEPT by ruling `visual-revision-creator-template-gate` Addendum 1, A1-2. The board gap is the
   lab's to correct or not, under its own autonomy; it is not ours to fix in their tree.

   THE PURSE LIVES HERE because the strip that used to host it is gone and
   `data-creator-char-purse` has a consumer (`smoke_creation_buyout`) that was never authorized to
   re-point. It states a real number at every value, including a negative one. */
function FCRaiseLedger({ character, speciesData, xpRemaining, onCharChange }) {
  if (!character || !speciesData) return null;
  var attrs = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
  var base = (speciesEff(speciesData, character.speciesOptionPicks).characteristics || {});

  return (
    <div data-raise-ledger="1" className="hpw-raise">
      <div className="hp-sechead"><h2>Raise</h2></div>

      {/* THE PURSE. Red and honest when negative — the overdrawn re-entrant (F-45) reads a real
          number here, never NaN, never floored at zero. */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'var(--doc-ink-muted)' }}>TO SPEND</span>
        <span data-creator-char-purse={xpRemaining} style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: xpRemaining < 0 ? 'var(--doc-action)' : 'var(--doc-ink)' }}>
          {xpRemaining} XP
        </span>
      </div>

      {/* NO SENTENCE HERE (A1-4(3)). The rule this block used to restate — only-time, new-rank x10,
          species floor, max 5 — now lives once, in the standfirst, which is where it is read. The
          board removed exactly this block for saying "what the standfirst above already says … the
          same fact twice, on one screen, forty millimetres apart", and our first cut reintroduced
          it. The head label is `Raise` and the rows do the rest: each states its own price, and
          the refusal states why. State a rule once where it is read; enforce it where it would
          break; let the refusal say why. */}

      <div className="hp-plate" data-pad="data">
        {attrs.map(function(attr) {
          var cur = character.characteristics[attr] || 2;
          var atMax = cur >= 5;
          var cost = atMax ? null : (cur + 1) * 10;
          var afford = cost !== null && xpRemaining >= cost;
          var atBase = cur <= (base[attr] || 2);

          // The cost line, in the board's three registers. Class A prices what it refuses.
          var costText;
          // ONE FORM, the same as the transplanted rows above (A2-1 + A3-1). Both implementations
          // carry the change: a form that survives at one band is the four-form row still shipped.
          costText = atMax ? '' : cost + ' XP';

          return (
            <div key={attr} className="hp-buy" data-raise-row={attr}>
              <span className="hp-buy-name">{FC_CHAR_WORDS[attr]}</span>
              <span className="hp-buy-step">
                {atMax ? '\u2013' : (<React.Fragment>{cur}<i>{'\u2192'}</i>{cur + 1}</React.Fragment>)}
              </span>
              {/* NEUTRAL IN EVERY STATE (A2-1): the refusal red left this cell with its sentence. */}
              <span className="hp-buy-cost" data-char-cost-line={attr}
                style={{ color: 'var(--doc-ink-muted)' }}>
                {costText}
              </span>
              <span style={{ display: 'flex', gap: '6px' }}>
                <button type="button" className="hp-buy-btn" data-raise={attr}
                  data-at-cap={atMax ? '1' : undefined} data-afford={afford ? '1' : undefined}
                  disabled={atMax || !afford}
                  aria-label={atMax ? FC_CHAR_WORDS[attr] + ' is at the creation maximum of 5'
                    : 'Raise ' + FC_CHAR_WORDS[attr] + ' to ' + (cur + 1) + ' for ' + cost + ' XP'
                      + (afford ? '' : ' \u2014 you hold ' + xpRemaining)}
                  onClick={function() { if (!atMax && afford) onCharChange(attr, cur + 1); }}
                  style={{ cursor: (atMax || !afford) ? 'not-allowed' : 'pointer' }}>
                  {atMax ? '\u2013' : 'Raise'}
                </button>
                <button type="button" className="hp-buy-btn" data-refund={attr} disabled={atBase}
                  aria-label={atBase ? FC_CHAR_WORDS[attr] + ' is at its species base'
                    : 'Lower ' + FC_CHAR_WORDS[attr] + ' to ' + (cur - 1) + ', refunding ' + (cur * 10) + ' XP'}
                  onClick={function() { if (!atBase) onCharChange(attr, cur - 1); }}
                  style={{ padding: '0 12px', cursor: atBase ? 'not-allowed' : 'pointer',
                           borderStyle: atBase ? 'dashed' : 'solid' }}>
                  {'\u2212'}
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══ FCSkillsSheet — CLUSTER THREE'S FIRST TRANSPLANT (the SKILLS step) ══════════════════════
   MOCKUP (C-MU-2): the lab board `lab/holocron/index.html` @ b818e23f (`board_pin.BOARD_SHA`),
   frames `skills_step(species_id=wookiee, career_id=hired_gun, spec_id=marauder,
   career_picks=[athletics,brawl,melee,resilience], spec_picks=[melee,coercion],
   raises={brawn=4,willpower=2})` ×4 (the desktop ladder, ordinals 0–3) and `phone_skills()` —
   extracted as `test/fixtures/board_skills_*.html` at skills step 0 (f52caf6a). APPROVED under
   the R-VR-6 substitution and the R-CRS-2 term (the board is the rendered artifact; Jason's local
   walk is the final gate, R-CRS-3), ruled `cluster-three-concept` R-C3-1..9.

   THE TREE IS THE BOARD'S (R-TTP-1): runhead · rail (The Build — the two pick counters as `hp-xp`
   rows · So Far ×3 · the commit) · head (wave · stephead · lead) · spend pane in two columns —
   Career Skills and Specialization Skills as `hp-skill` rows (name · governing characteristic ·
   the pool you would roll), then What You Will Roll (a ledger of every ranked skill with its
   provenance after the `hp-sep`) and the teaching plate (A Rank Upgrades Before It Adds — B-1348, D-411 item 24, EotE p.19).
   The COPY is the app's where it has approved copy and the board's where it had none (the lead,
   the teaching plate). The NUMBERS are the character's: every pool is `SWRPGDice.buildPool` over
   the live characteristic and the live rank, never a typed glyph string.

   THE POOLS STAY ON THE APP'S DICE MODULE (R-C3-4): `window.SWRPGDice.renderPool` emits the same
   `span.hp-sym` the board draws, one span per die, so the tree matches; the GLYPH inside each span
   is the dice module's (the board types the lab font's `l`/`k` letters) — ONE declared TEXT delta
   at the style layer, declared in the gate (the walker never reads text), never a second glyph
   table.

   WHAT THE BOARD DOES NOT DRAW, built inside its frame in its idiom (R-C3-9), each an APP-SIDE
   ADDITION named UNDRAWN in the gate with a screenshot:
     · the SPECIES-CHOICE block — a third pick list for a species whose grant is a CHOICE (Human
       2×non-career, Geonosian 1×any, …), the same `hp-skill` rows under their own section head;
       absent for a Wookiee, which is why the board never drew it;
     · the BUY RANKS block — after the free ranks, XP buys more (EotE p.35/p.102; rank 2 cap at
       creation): a ledger per characteristic in the board's trade-row idiom (`hp-buy-btn`, the
       motivation roll's control), price from `CharResolve.skillRankCost`, committed through the
       same `BUY_SKILL_RANK` / `REFUND_SKILL_RANK` the legacy screen dispatches;
     · the NOTHING-PICKED state — the pools all read at rank 0, the ledger says so in an
       `hp-none`, and the commit is REFUSED with the reason beside it (R-CSC-1) until every free
       rank is placed, which is the legacy pickers' rule carried (R-CPR-28).

   THE PICKS ARE THE CHARACTER'S, NOT THIS SCREEN'S. Every press flips ONE flag in the reducer
   (`TOGGLE_FREE_SKILL`; the legacy pickers batched a set to a CONFIRM per round), so what this sheet shows is read back from
   `character.skills` on every render and a reload lands on the same picks — R-C3-8's measured
   buy → reload → read-back leg holds by construction rather than by a second store. */
var FC_SKILL_CHAR_PROP = { BR: 'brawn', AG: 'agility', INT: 'intellect', CUN: 'cunning', WIL: 'willpower', PR: 'presence' };
var FC_SKILL_CHAR_NAME = { brawn: 'Brawn', agility: 'Agility', intellect: 'Intellect', cunning: 'Cunning', willpower: 'Willpower', presence: 'Presence' };
var FC_SKILLS_CREATION_CAP = 2;   // EotE p.48 — the global creation cap (FCSkillBuyList's CREATION_CAP)

function FCSkillsSheet(p) {
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal || (p.steps || []).length;
  /* THE PHONE'S TWO PIECES OF STATE, declared UNCONDITIONALLY at the top because hook order is not
     allowed to depend on a band (the desktop return below never reads either). `phoneView` is the
     step's own sequence at 390 — the round, the training summary, Buy Ranks — which the board draws
     as three separate frames of one screen and the legacy phone step ran as three `ctrl.subView`s;
     `buyOpen` is which Buy Ranks row is expanded, the legacy `FCSkillBuyList`'s expand-on-tap kept.
     LOCAL, not `ctrl.subView`: the picks are the character's and persist through a reload by
     construction (R-C3-8), and WHICH SCREEN OF THE STEP you were on is not a durable fact — a
     reload lands on the round with every pick still placed, which is the same read-back guarantee
     one screen wider. */
  var _pv = React.useState('round'), phoneView = _pv[0], setPhoneView = _pv[1];
  var _bo = React.useState(null), buyOpen = _bo[0], setBuyOpen = _bo[1];
  var names = p.skillNames || {}, chars = p.skillChars || {}, keys = p.skillSheetKeys || {};
  var charSkills = p.charSkills || {}, cx = p.characteristics || {}, caps = p.skillCaps || {};
  var careerIds = p.careerSkills || [], specIds = p.specSkills || [], speciesIds = p.speciesPool || [];
  var grant = p.speciesGrant || null;
  var Dice = window.SWRPGDice || null;

  function keyOf(id) { return keys[id] || id; }
  function entryOf(id) { return charSkills[keyOf(id)] || null; }
  function rankOf(id) {
    var s = entryOf(id);
    if (!s) return 0;
    return (s.freeCareer ? 1 : 0) + (s.freeSpec ? 1 : 0) + (s.freeSpecies ? 1 : 0)
         + (s.purchased || 0) + (s.bought ? s.bought.length : 0);
  }
  function capOf(id, listCap) { return caps[keyOf(id)] || listCap || FC_SKILLS_CREATION_CAP; }
  function abbrOf(id) { return FC_CHAR_ABBR[chars[id]] || ''; }
  function charValOf(id) { return cx[chars[id]] || 0; }
  function poolOf(id) {
    return Dice ? Dice.buildPool({ characteristic: charValOf(id), skillRank: rankOf(id) }) : null;
  }
  function pool(pl, extra) {
    // THE ONE DECLARED TEXT DELTA (R-C3-4): the spans are the board's, the glyphs are the module's.
    // Declared in the GATE, not on the element — the parity walker compares elements, classes,
    // nesting and order, never text, and an element carrying `data-ruled-delta` is DROPPED from the
    // app's tree (its exception mechanism), which made every pool read as missing on the first sweep.
    return (
      <span className="hp-pool"
        data-pool={pl ? (pl.proficiency || 0) + '/' + (pl.ability || 0) : undefined} {...(extra || {})}>
        {Dice && pl ? Dice.renderPool(pl, { size: 1, cssSized: true }) : null}
      </span>
    );
  }

  // The three rounds' picks, READ from the character — the flag on the sheet entry is the pick.
  var pickedCareer = careerIds.filter(function(id) { var s = entryOf(id); return !!(s && s.freeCareer); });
  var pickedSpec = specIds.filter(function(id) { var s = entryOf(id); return !!(s && s.freeSpec); });
  var pickedSpecies = speciesIds.filter(function(id) { var s = entryOf(id); return !!(s && s.freeSpecies); });
  var careerMax = p.careerMax || 0, specMax = specIds.length ? (p.specMax || 0) : 0;
  var speciesMax = grant ? (grant.count || 1) : 0;
  var careerDone = pickedCareer.length >= careerMax;
  var specDone = pickedSpec.length >= specMax;
  var speciesDone = pickedSpecies.length >= speciesMax;
  var allDone = careerDone && specDone && speciesDone;
  // B-590 — AUTO-SCROLL ON THE PHONE: when a list fills, the next list (or the ledger, when there is
  // none) scrolls into view, so the screen itself walks the player through the round (Jason
  // 2026-09-14: "auto scroll would also help"). A top-level hook: bands are decided below.
  var _prevDone = useRef({ career: careerDone, spec: specDone, species: speciesDone });
  useEffect(function() {
    var prev = _prevDone.current; _prevDone.current = { career: careerDone, spec: specDone, species: speciesDone };
    if (p.band !== 'mobile') return;
    var target = null;
    if (!prev.career && careerDone) target = !specDone ? '[data-skills-list="spec"]' : (!speciesDone ? '[data-skills-list="species"]' : '[data-skills-ledger]');
    else if (!prev.spec && specDone) target = !speciesDone ? '[data-skills-list="species"]' : '[data-skills-ledger]';
    else if (!prev.species && speciesDone) target = '[data-skills-ledger]';
    if (!target) return;
    try { var el = document.querySelector(target); var head = el && el.previousElementSibling; if (el) (head || el).scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
  }, [careerDone, specDone, speciesDone]);
  var anyPicked = pickedCareer.length + pickedSpec.length + pickedSpecies.length > 0;

  function toggle(list, picked, max, flag, id) {
    var on = picked.indexOf(id) >= 0;
    if (!on && picked.length >= max) return;
    p.onToggle(flag, id);     // ONE flag flipped in the reducer (TOGGLE_FREE_SKILL), never the whole set
  }

  // A row is REFUSED (never hidden) when its OTHER ranks already sit at the cap — the B-55 rule:
  // species free rank + career pick + spec pick on one skill reached 3 once, and shipped.
  function skillRow(id, listKey, picked, max, flag, listCap) {
    var on = picked.indexOf(id) >= 0;
    var others = rankOf(id) - (on ? 1 : 0);
    var atCap = !on && others >= capOf(id, listCap);
    var full = !on && picked.length >= max;
    var refused = atCap ? 'at the creation cap' : (full ? 'all ' + max + ' placed' : null);
    return (
      <button type="button" className="hp-skill" key={listKey + ':' + id}
        data-picked={on ? '1' : undefined} aria-pressed={on ? 'true' : 'false'}
        data-skill-pick={listKey + ':' + id} data-refused={refused ? '1' : undefined}
        aria-disabled={refused ? 'true' : undefined}
        title={refused ? (names[id] || id) + ' — ' + refused : undefined}
        onClick={function() { if (refused) return; toggle(listKey, picked, max, flag, id); }}>
        <span className="hp-skill-name">{names[id] || id}</span>
        <span className="hp-skill-char">{abbrOf(id)}</span>
        {pool(poolOf(id))}
      </button>
    );
  }

  function pickList(title, cite, ids, listKey, picked, max, flag, listCap, first, hook) {
    return (
      <React.Fragment key={listKey}>
        <div className="hp-sechead" style={{ margin: first ? '0 0 11px' : '20px 0 10px' }}>
          <h2>{title}</h2><span className="hp-cite">{cite}</span>
        </div>
        <div className="hp-plate" data-pad="data" {...hook}>
          {ids.map(function(id) { return skillRow(id, listKey, picked, max, flag, listCap); })}
        </div>
      </React.Fragment>
    );
  }

  // WHAT YOU WILL ROLL — every skill with a rank, strongest pool first (the board's order:
  // proficiency, then dice, then the name), with WHERE the ranks came from after the separator.
  var ranked = Object.keys(names).filter(function(id) { return rankOf(id) > 0; }).map(function(id) {
    var s = entryOf(id) || {}, from = [];
    // A fixed species rank (the Wookiee's Brawl) is `purchased` with no flag; a CHOSEN one is the
    // `freeSpecies` flag. Either reads "species" — the board's "species + career" on Brawl.
    if (s.purchased > 0 || s.freeSpecies) from.push('species');
    if (s.freeCareer) from.push('career');
    if (s.freeSpec) from.push('spec');
    if (s.bought && s.bought.length) from.push('bought');
    var pl = poolOf(id) || { proficiency: 0, ability: 0 };
    return { id: id, name: names[id] || id, from: from.join(' + '), pool: pl,
             prof: pl.proficiency || 0, dice: (pl.proficiency || 0) + (pl.ability || 0) };
  }).sort(function(a, b) { return (b.prof - a.prof) || (b.dice - a.dice) || a.name.localeCompare(b.name); });

  // THE TEACHING PLATE'S EXAMPLES ARE COMPUTED, NOT TYPED: the character's highest characteristic
  // and a career skill it governs (the board's Wookiee: Brawn 4, Melee), at rank 0, 1, 2, then the
  // same rank on a characteristic of 2 — four pools through the engine that rolls them.
  var teachProp = 'brawn', teachVal = -1;
  ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'].forEach(function(k) {
    if ((cx[k] || 0) > teachVal) { teachVal = cx[k] || 0; teachProp = k; }
  });
  var teachSkill = careerIds.filter(function(id) { return chars[id] === teachProp; })
    .sort(function(a, b) { return (rankOf(b) - rankOf(a)) || (names[a] || a).localeCompare(names[b] || b); })[0]
    || careerIds[0] || 'melee';
  if (chars[teachSkill] && chars[teachSkill] !== teachProp) { teachProp = chars[teachSkill]; teachVal = cx[teachProp] || 0; }
  var teachName = names[teachSkill] || 'Melee', teachChar = FC_SKILL_CHAR_NAME[teachProp] || 'Brawn';
  var teachRows = [[teachVal, 0], [teachVal, 1], [teachVal, 2], [2, 2]].map(function(r) {
    return { lab: teachChar + ' ' + r[0] + ', ' + teachName + ' ' + r[1],
             pool: Dice ? Dice.buildPool({ characteristic: r[0], skillRank: r[1] }) : null };
  });
  var teachClose = teachVal > 2
    ? teachVal + ' dice with two upgraded beats two dice with two upgraded.'
    : null;

  // BUY RANKS (undrawn): every skill by its characteristic, price from the one cost rule.
  var buyGroups = {};
  Object.keys(names).forEach(function(id) {
    var prop = chars[id];
    if (!prop) return;
    (buyGroups[prop] = buyGroups[prop] || []).push(id);
  });
  var boughtAny = Object.keys(names).some(function(id) { var s = entryOf(id); return !!(s && s.bought && s.bought.length); });

  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  var missing = [];
  if (!careerDone) missing.push((careerMax - pickedCareer.length) + ' career');
  if (!specDone) missing.push((specMax - pickedSpec.length) + ' specialization');
  if (!speciesDone) missing.push((speciesMax - pickedSpecies.length) + ' species');
  var spentEarlier = Math.max(0, (p.xpPool || 0) - (p.xpRemaining || 0) - (p.xpSpentOnSkills || 0));

  function commit() { if (allDone) p.onConfirm(); }

  /* ══ THE SKILLS PHONE BAND ═══════════════════════════════════════════════════════════════════
     MOCKUP (C-MU-2): `phone_skills()` at board `6051229c` — the finished "What Have You Trained?"
     state, approved by Jason 2026-09-09 on `lab/holocron/phone_approval.html` @ `c0744b3a`
     (`parallel-pull-grant` A3, R-PPG-A3-1) — and the step's three working states at board
     `10ff0155`, approved by Jason 2026-09-09 ("1. Approved 2. with 3. A", ruling
     `catchup-pin-approval` R-CPA-1/3): `phone_skills(career_picks=[brawl,athletics],
     spec_picks=[], view=round)`, `phone_skills(view=summary)`, `phone_skills(view=buy,
     control=badge)`. `control=separate` is NOT built (R-CPA-3, his "A").

     THE SAME COMPONENT, NOT A SECOND ONE (R-PSC-3). Everything below is composed from the closures
     this function already owns — `rankOf`, `capOf`, `entryOf`, `poolOf`, `pool`, `ranked`,
     `toggle`, the pick arrays and the maxima — so there is exactly one answer on this screen to
     "what rank is this skill", "what does the next one cost" and "what would I roll", at every
     band. What differs at 390 is the TREE, because the board draws a different frame there and not
     this two-column grid narrowed: a status strip instead of the rail's plates, both pick lists
     stacked in one column, So Far folded into a `<details>` at the foot, and the commit in an
     `hp-phoneact` outside the scrolling body. The scaffold is `FCPhoneShell`, the same emitter the
     species, characteristics and talents phone bands use (R-SPB-9).

     NO SCROLL ZONE OF ITS OWN (`phone-shell-scroll` D-BPT-1, merged at `ab52f250`): the creator's
     mobile mount box IS the zone, and a second `overflow-y` inside it would nest two scrollers.

     WHAT THE FRAMES DO NOT DRAW, carried forward in their idiom rather than invented — each named
     UNDRAWN in the gate: the REFUSED commit while free picks are unplaced (R-CSC-1 is app law and
     C-MU-4 puts it above a mockup's omission — the same call `FCCharSheetPhone` records making for
     the overspent purse one step earlier); the SPECIES-CHOICE pick list, for a species whose grant
     is a choice (the board's Wookiee has none, which is why no frame draws one); and the REFUND
     badge beside the buy badge, which is the desktop's own both-controls-in-one-cell shape in the
     row's drawn idiom.

     TWO RAW NUMBERS ARE REFUSED AND THIS BUILD CHANGES NEITHER (`phone-skills-census` A1,
     R-PSC-A1-3): whether a free pick may stack onto a species starting rank, and what becomes of a
     pick spent at the cap. The app's answers are `rankOf`/`capOf`'s, unchanged and shared with the
     desktop; the gate states what they are. The cap itself is RAW: rank 2 at creation across all
     three lines (AoR p.102), career -> spec free ranks stacking to that ceiling (EotE p.35). */
  if (p.band === 'mobile') {
    // THE CHARACTERISTIC READS AS ITS WORD, not the desktop's two-letter tag: every phone frame
    // draws "BRAWN" / "WILLPOWER" where the wide bands draw "BR" / "WIL". `data-charword` on the
    // row is the SHIPPED rule that gives the middle column `auto` instead of the 42px that fits an
    // abbreviation (R-CPD-8, paper.css:785) — so the word gets its room without this card touching
    // `.hp-skill`'s base grid, which stays B-489's question (R-PSC-3).
    var charTag = function(id) { return (FC_SKILL_CHAR_NAME[chars[id]] || '').toUpperCase(); };

    // A ROW IS AT-CAP WHILE THE MARK STILL MEANS SOMETHING, AND ONLY AN ADD IS REFUSED. The two
    // approved frames fix the predicate between them: the ROUND frame draws `data-at-cap="1"` on
    // Brawl — a PICKED row at rank 2, from the Wookiee's grant plus this round's pick — with
    // "Rank 2 — the creation cap." under it, while the FINISHED, SUMMARY and BUY frames draw the
    // mark ZERO times even though Brawl and Melee are still at rank 2 there. What separates them is
    // whether the list can still take a pick: the mark answers "why can't I put one here", so it
    // shows while there is a pick to place and goes quiet once the round is done. Unpicking stays
    // legal at every point (it is what takes the skill back off the cap), and the refusal to ADD is
    // the desktop's B-55 predicate unchanged — the pick is not spent, the count does not move, and
    // the reason is printed where the press happened.
    var phoneSkillRow = function(id, listKey, picked, max, flag, listCap) {
      var on = picked.indexOf(id) >= 0;
      var others = rankOf(id) - (on ? 1 : 0);
      var cap = capOf(id, listCap);
      var atCap = rankOf(id) >= cap && picked.length < max;
      var full = !on && picked.length >= max;
      var refused = (!on && others >= cap) ? 'at the creation cap' : (full ? 'all ' + max + ' placed' : null);
      return (
        <React.Fragment key={listKey + ':' + id}>
          <button type="button" className="hp-skill" data-charword="1"
            data-picked={on ? '1' : undefined} data-at-cap={atCap ? '1' : undefined}
            aria-pressed={on ? 'true' : 'false'}
            /* THE ACCESSIBLE NAME IS THE APP'S OWN, kept rather than reinvented (the same call the
               buy badges make below): read by its content this button announces "Athletics BRAWN
               Proficiency Ability Ability Ability", which is the pool spelled out and not what the
               control does. The legacy picker's form — "Not selected: Athletics (BR)" — says the
               state, the skill and its characteristic, and it is what every shipped driver over
               these rounds asks for, so keeping it retires no assert for a rename. */
            aria-label={(on ? 'Selected' : 'Not selected') + ': ' + (names[id] || id) + ' (' + abbrOf(id) + ')'}
            data-skill-pick={listKey + ':' + id} data-refused={refused ? '1' : undefined}
            aria-disabled={refused ? 'true' : undefined}
            title={refused ? (names[id] || id) + ' — ' + refused : undefined}
            onClick={function() { if (refused) return; toggle(listKey, picked, max, flag, id); }}>
            <span className="hp-skill-name">{names[id] || id}</span>
            <span className="hp-skill-char">{charTag(id)}</span>
            {pool(poolOf(id))}
          </button>
          {atCap ? (
            <div className="hp-why" data-skills-cap={id} style={{ margin: '6px 0 2px' }}>
              {'Rank ' + cap + ' — the creation cap.'}</div>
          ) : null}
        </React.Fragment>
      );
    };

    var phonePickList = function(title, cite, ids, listKey, picked, max, flag, listCap, hook) {
      return (
        <React.Fragment key={listKey}>
          <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
            <h2>{title}</h2><span className="hp-cite">{cite}</span>
          </div>
          <div className="hp-plate" data-pad="data" {...(hook || {})}>
            {ids.map(function(id) { return phoneSkillRow(id, listKey, picked, max, flag, listCap); })}
          </div>
        </React.Fragment>
      );
    };

    // THE PROVENANCE IS THE CHARACTER'S OWN SOURCE MAP, read off the sheet entry's flags — the same
    // read `ranked` does for the wide band's ledger, with the frame's WORDS ("specialization", not
    // the desktop's "spec"). Nothing here is typed: a skill shows "species + career" because a
    // species grant and a career pick both landed on it.
    var phoneSrc = function(id) {
      var s = entryOf(id) || {}, from = [];
      if (s.purchased > 0 || s.freeSpecies) from.push('species');
      if (s.freeCareer) from.push('career');
      if (s.freeSpec) from.push('specialization');
      if (s.bought && s.bought.length) from.push('bought with XP');
      return from.join(' + ');
    };

    /* BUY RANKS — THE PRICE BADGE IS THE BUY BUTTON (Jason, "A": `catchup-pin-approval` R-CPA-3).
       The frame draws the row in the board's CELL idiom — `hp-tal` / `hp-head` with its `hp-box`
       mark and name, the body opening under it with "Rank N -> N+1. CHARACTERISTIC." and the price
       as a `button.hp-tab` whose `hp-tab-paint` is the pressable face. The row expands on tap,
       which is exactly what the legacy `FCSkillBuyList` row did, so the gesture is carried, not
       invented. Price, affordability and the commit are the ONE cost path
       (`CharResolve.skillRankCost` -> `BUY_SKILL_RANK` / `REFUND_SKILL_RANK`), shared with the
       desktop sheet a hundred lines up.
       THE NUMBERS ARE THE CHARACTER'S, NOT THE DRAWING'S: the frame's sample row prices a rank
       2 -> 3 that the shipped app refuses at creation (`atCap`), and R-PSC-A1-3 says this build
       states what the app does and changes it in neither direction. A skill at the cap therefore
       shows its reason and no price. */
    var phoneBuyRow = function(id) {
      var key = keyOf(id), rank = rankOf(id), isCareer = !!(p.careerStatus || {})[id];
      var s = entryOf(id), bought = (s && s.bought) ? s.bought.length : 0;
      var atCap = rank >= capOf(id);
      var cost = atCap ? null : CharResolve.skillRankCost(rank + 1, isCareer);
      var can = cost != null && cost <= (p.xpRemaining || 0);
      var lastPaid = bought ? (s.bought[s.bought.length - 1] || 0) : 0;
      var open = buyOpen === id;
      var press = function() { setBuyOpen(open ? null : id); };
      return (
        <div className="hp-tal" key={id} data-role="buy" data-reach={atCap ? 'locked' : 'reachable'}
          data-skills-buy-row={id} data-rank={rank} data-career={isCareer ? '1' : undefined}
          style={{ position: 'relative' }}>
          {/* THE PRESS IS ON THE HEAD, which is where the frame draws `role="button" tabindex="0"`
              and where `paper.css` already floors a control at 44px (AS-6 by construction rather
              than by a rule written here) — the same placement the talents phone sheet asked
              `DirectionalTree` for. */}
          {/* THE ACCESSIBLE NAMES ARE THE APP'S OWN VOCABULARY, kept rather than reinvented: the
              at-cap row answers to `At creation cap: <skill>` and the badges to `Buy rank: <skill>
              for N XP` / `Refund rank: <skill>`, which is what the legacy buy list said and what
              every shipped driver over this screen asks for. A restyle that renamed them would
              have re-pointed four suites for nothing. */}
          <div className="hp-head" data-state={open ? 'active' : undefined}
            role="button" tabIndex={0} aria-expanded={open ? 'true' : 'false'}
            aria-label={atCap ? ('At creation cap: ' + (names[id] || id))
              : ((names[id] || id) + ' — rank ' + rank + ', next rank ' + cost + ' XP')}
            onClick={press}
            onKeyDown={function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); press(); } }}>
            <span className="hp-box" data-on={bought ? '1' : '0'} />
            <span className="hp-name">{names[id] || id}</span>
          </div>
          {open ? (
            <div className="hp-plate hp-tal-body">
              <p>{atCap
                ? ('Rank ' + rank + '. ' + charTag(id) + '. The creation cap.')
                : ('Rank ' + rank + ' → ' + (rank + 1) + '. ' + charTag(id) + '.')}</p>
              {/* WHAT THE SKILL IS, which is what the gesture was FOR. The legacy buy row's
                  expand printed the registry's novice-facing definition ("Tap a skill to see what
                  it does"), and an expand that reveals only a price is a gesture that lost its
                  reason. UNDRAWN — the frame's body carries one paragraph — built inside the drawn
                  slot in its idiom (R-C3-9) and named in the gate; the words are the ONE shared
                  reader's (`skillDescFor` -> `CharResolve.skillDescription`, F-58/R-F58-4), never
                  a second definition table. */}
              {skillDescFor(key) ? <p data-skills-desc={id}>{skillDescFor(key)}</p> : null}
              {/* AN UNNAMED WRAPPER, so both parity engines look straight through it (the same
                  device and the same reason as the wide sheet's buy cell): it exists only so a
                  bought row can show Refund BESIDE Buy without the two absolutely-positioned tabs
                  landing on top of each other. */}
              <span data-skills-buy-acts="1">
                {bought ? (
                  <button type="button" className="hp-tab" data-state="active" data-buy="refund"
                    data-skills-refund-btn={id} aria-label={'Refund rank: ' + (names[id] || id)}
                    onClick={function() { p.onRefund(key); }}>
                    <span className="hp-tab-paint">{'Refund +' + lastPaid}</span></button>
                ) : null}
                {cost != null ? (
                  <button type="button" className="hp-tab" data-state="active" data-buy="buy"
                    data-skills-buy-btn={id} aria-disabled={can ? undefined : 'true'}
                    data-refused={can ? undefined : '1'}
                    aria-label={'Buy rank: ' + (names[id] || id) + ' for ' + cost + ' XP'}
                    onClick={function() { if (can) p.onBuy(key, cost); }}>
                    <span className="hp-tab-paint">{'Cost ' + cost}</span></button>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      );
    };

    var phoneBuyIds = (function() {
      var ids = Object.keys(names).filter(function(id) { return !!chars[id]; });
      var byName = function(a, b) { return (names[a] || a).localeCompare(names[b] || b); };
      var boughtIds = ids.filter(function(id) { var s = entryOf(id); return !!(s && s.bought && s.bought.length); }).sort(byName);
      return boughtIds.concat(ids.filter(function(id) { return boughtIds.indexOf(id) < 0; }).sort(byName));
    })();

    /* THE COMMIT IS ONE WORDING ACROSS ALL THREE SCREENS — "Continue to <Next Step> ›", which is
       what every one of the four approved frames draws (R-PSC-1: the four legacy labels CONFIRM /
       CONTINUE / DONE / the picker's own confirm collapse into the phone-sheet pattern the talents
       band already adopted). It advances the step's own sequence first and the WALK last, exactly
       as the legacy step's three subViews did. */
    /* B-590 (Jason 2026-09-14): "the Continue button should also work as a prompt — Pick Career
       Skills, then Pick Specialization Skills — then offer to upgrade skills without forcing the
       user". THE COMMIT SAYS THE CURRENT TASK and pressing it GOES THERE: while a list is short the
       label is "Pick career skills · 1 of 3" and the press scrolls that list into view (the old
       "Place 2 career picks first." note under a refused "Continue to Talents" told a player what
       was missing only after the wrong press). When every list is full the label is
       "Continue to <next>" and the press is the walk — the SUMMARY stop is gone (its "Training So
       Far" table rides the Buy Ranks screen), and BUY RANKS is an OFFER above the commit, not a
       screen the walk passes through: "180 XP unspent · buy ranks ›". R-PSC-1's one-wording rule
       yields to his word; the three approved frames still draw as they did, in order, for a player
       who takes the offer. */
    var phase = !careerDone ? 'career' : (!specDone ? 'spec' : (!speciesDone ? 'species' : 'done'));
    var phaseLabel = phase === 'career' ? 'Pick career skills \u00b7 ' + pickedCareer.length + ' of ' + careerMax
                   : phase === 'spec' ? 'Pick specialization skills \u00b7 ' + pickedSpec.length + ' of ' + specMax
                   : phase === 'species' ? 'Pick ' + (p.speciesName || 'species') + ' skills \u00b7 ' + pickedSpecies.length + ' of ' + speciesMax
                   : null;
    var phoneScrollTo = function(sel) {
      try { var el = document.querySelector(sel); var head = el && el.previousElementSibling; if (el) (head || el).scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
    };
    var phoneCommit = function() {
      if (phoneView === 'round' && !allDone) { phoneScrollTo('[data-skills-list="' + phase + '"]'); return; }
      p.onConfirm();
    };
    var phoneOffer = phoneView === 'round' && allDone && (p.xpRemaining || 0) > 0;
    var phoneRefused = false;

    return (
      <FCPhoneShell band={p.band}
        stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
        steps={p.steps} stepLabels={p.stepLabels}
        onBack={p.onBack} onJump={p.onJump}
        status={<React.Fragment>
          <div><span className="hp-status-k">Career picks</span>
            <span className="hp-status-v" data-skills-count="career">
              {pickedCareer.length + ' of ' + careerMax}</span></div>
          {specMax ? (
            <div><span className="hp-status-k">Spec picks</span>
              <span className="hp-status-v" data-skills-count="spec">
                {pickedSpec.length + ' of ' + specMax}</span></div>
          ) : null}
          {grant ? (
            <div><span className="hp-status-k">Species picks</span>
              <span className="hp-status-v" data-skills-count="species">
                {pickedSpecies.length + ' of ' + speciesMax}</span></div>
          ) : null}
          {/* THE XP RAIL IS THE STRIP'S REMAINING OBJECT on the Buy Ranks screen — the phone idiom
              the characteristics and talents bands already use for the purse, where the legacy step
              mounted `FCXpBudgetBar`. UNDRAWN: the approved buy frame's strip carries the two pick
              counters only, and a Buy Ranks screen that never says what is left would lose a fact
              the legacy screen showed ("same functionality as prior"). Named in the gate. */}
          {phoneView === 'buy' ? (
            <div><span className="hp-status-k">Remaining</span>
              <span className="hp-status-v" data-skills-remaining={p.xpRemaining}
                data-over={(p.xpRemaining || 0) < 0 ? '1' : undefined}
                style={{ color: (p.xpRemaining || 0) < 0 ? 'var(--doc-action)' : undefined }}>
                {(p.xpRemaining || 0) + ' XP'}</span></div>
          ) : null}
        </React.Fragment>}
        act={<React.Fragment>
          {phoneOffer ? (
            <button type="button" className="hp-none" data-skills-buy-offer="1"
              style={{ display: 'block', background: 'none', border: 0, padding: '6px 0', marginBottom: '6px', font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer', minHeight: '44px' }}
              onClick={function() { setPhoneView('buy'); setTimeout(function() { phoneScrollTo('[data-skills-buy]'); }, 60); }}>
              {(p.xpRemaining || 0) + ' XP unspent \u00b7 buy ranks \u203a'}
            </button>
          ) : null}
          <button type="button" className="hp-commit" data-creator-commit="1" onClick={phoneCommit}
            data-skills-phase={phase}>
            {(phaseLabel || ('Continue to ' + nextLabel)) + ' '}<span>{'›'}</span>
          </button>
        </React.Fragment>}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">Character Creation</div>
        </div>
        <h1 className="hp-desig" style={{ fontSize: '27px' }} data-skills-view={phoneView}>{p.stepName}</h1>
        <div className="hp-desig-rule" />
        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
          {/* THE LEAD IS THE PHONE FRAME'S OWN SENTENCE — shorter than the wide band's, which also
              explains the both-lists rank 2 and what happens after creation. Its NUMBERS are read
              off the career and specialization records, so a Force and Destiny career says six and
              three where an Edge career says eight and four. */}
          <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}>
            {'Choose '}<b>{careerMax + ' of your ' + careerIds.length}</b>{' career skills'}
            {specMax ? <React.Fragment>{' and '}<b>{specMax + ' of your ' + specIds.length}</b>{' specialization skills'}</React.Fragment> : null}
            {'. A rank '}<b>{'upgrades'}</b>{' a die first; past the characteristic, it adds one.'}
          </p>
          {(p.citeChips && p.citeChips.length) || p.cite ? (
            <div style={{ marginTop: '10px' }}>
              {((p.citeChips && p.citeChips.length) ? p.citeChips : [p.cite]).map(function(c) {
                return (
                  <span className="hp-cite" key={c} data-step-cite="skills"
                    style={{ marginRight: '7px' }}>{c}</span>
                );
              })}
            </div>
          ) : null}
        </div>

        {phonePickList('Career Skills', 'choose ' + careerMax, careerIds, 'career',
                       pickedCareer, careerMax, 'freeCareer', null, { 'data-skills-list': 'career' })}
        {specIds.length ? phonePickList('Specialization Skills', 'choose ' + specMax, specIds, 'spec',
                       pickedSpec, specMax, 'freeSpec', null, { 'data-skills-list': 'spec' }) : null}
        {/* UNDRAWN — the species-choice round, for a species whose grant is a CHOICE. */}
        {grant ? phonePickList((p.speciesName || 'Species') + ' Skills',
                       'choose ' + speciesMax + (grant.pool === 'non_career' ? ' non-career' : ''),
                       speciesIds, 'species', pickedSpecies, speciesMax, 'freeSpecies',
                       grant.rank_cap != null ? grant.rank_cap : null,
                       { 'data-skills-list': 'species', 'data-undrawn': 'species-choice' }) : null}

        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>What You Will Roll</h2></div>
        <div className="hp-plate" data-pad="data" data-skills-ledger="1">
          {ranked.length ? (
            <table className="hp-ledger"><tbody>
              {ranked.map(function(r) {
                return (
                  <tr key={r.id} data-skills-roll={r.id}>
                    <td className="hp-lkey">{r.name}</td>
                    <td className="hp-lval">{pool(r.pool)}</td>
                  </tr>
                );
              })}
            </tbody></table>
          ) : (
            <span className="hp-none" data-skills-none="1">
              {'Nothing ranked yet — every pool above is your characteristic alone. Pick above and the rows appear here.'}</span>
          )}
        </div>

        {phoneView === 'buy' ? (
          <React.Fragment>
            <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
              <h2>Training So Far</h2>
              <span className="hp-cite">{ranked.length + ' trained'}</span></div>
            <div className="hp-plate" data-pad="data" data-skills-training="1">
              <table className="hp-ledger"><tbody>
                {ranked.map(function(r) {
                  return (
                    <tr key={r.id} data-skills-train={r.id}>
                      <td className="hp-lkey">{r.name}</td>
                      <td className="hp-lval">{rankOf(r.id) + ' '}<span className="hp-sep">{'·'}</span>
                        <span className="hp-rowsrc">{phoneSrc(r.id)}</span></td>
                    </tr>
                  );
                })}
              </tbody></table>
            </div>
          </React.Fragment>
        ) : null}

        {phoneView === 'buy' ? (
          <React.Fragment>
            <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
              <h2>Buy Ranks</h2>
              {/* THE CITE SLOT CARRIES THE PURSE, as the wide band's does and with its hooks: the
                  frame prints a book page here, and the app has no VERIFIED citation for what a
                  rank costs (`creator_step_cites` has no such row) — an invented one would be the
                  defect a citation exists to prevent. */}
              <span className="hp-cite" data-skills-spent={p.xpSpentOnSkills || 0} data-skills-remaining={p.xpRemaining}>
                {(p.xpSpentOnSkills || 0) + ' of ' + (p.xpPool || 0) + ' XP · rank ' + FC_SKILLS_CREATION_CAP + ' cap'}</span></div>
            <div className="hp-plate" data-pad="data" data-skills-buy="1">
              {phoneBuyIds.map(function(id) { return phoneBuyRow(id); })}
            </div>
          </React.Fragment>
        ) : null}

        {/* SO FAR FOLDS on the phone, as it does on characteristics and talents and for the same
            reason: the board gives the phone a `<details>` at the foot where the wide band has a
            rail. */}
        <details className="hp-unit">
          <summary>
            <div className="hp-head">
              <span className="hp-name">So Far</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
            <FCbSoFarTable rows={p.soFar} />
          </div>
        </details>
      </FCPhoneShell>
    );
  }

  // THE DICE LESSON'S PLATE, drawn once (B-599): open under its head on the wide sheet, inside a
  // closed fold at the mid band.
  var teachPlate = (
    <div className="hp-plate" data-pad="read">
      <div className="hp-teach" data-skills-teach="1">
        {teachRows.map(function(r) {
          return (
            <div key={r.lab}>
              <span className="hp-teach-lab">{r.lab}</span>
              {pool(r.pool)}
            </div>
          );
        })}
      </div>
      {teachClose ? <p className="hp-prose hp-prose-sm" style={{ margin: '11px 0 0' }}>{teachClose}</p> : null}
    </div>
  );

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-skills-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: the two pick counters (the board's Build block here counts picks, not
             XP), what is settled, the commit. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1" data-rail-stack="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp" data-skills-count="career"><dt>Career picks</dt>
                <dd>{pickedCareer.length + ' of ' + careerMax}</dd></div>
              {specMax ? (
                <div className="hp-xp" data-skills-count="spec"><dt>Specialization picks</dt>
                  <dd>{pickedSpec.length + ' of ' + specMax}</dd></div>
              ) : null}
              {grant ? (
                <div className="hp-xp" data-skills-count="species"><dt>Species picks</dt>
                  <dd>{pickedSpecies.length + ' of ' + speciesMax}</dd></div>
              ) : null}
            </dl>
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          <div className="hp-railact" data-rail-section="commit">
            {/* REFUSED WHERE YOU WOULD PRESS IT (R-CSC-1): the button stays, the reason sits beside it. */}
            {!allDone ? (
              <span className="hp-none" data-skills-missing="1" style={{ display: 'block', marginBottom: '8px' }}>
                {'Place ' + missing.join(', ') + (missing.length > 1 ? ' picks' : (missing[0] && missing[0].indexOf('1 ') === 0 ? ' pick' : ' picks')) + ' first.'}
              </span>
            ) : null}
            <button type="button" className="hp-commit" data-creator-commit="1" onClick={commit}
              aria-disabled={allDone ? undefined : 'true'} data-refused={allDone ? undefined : '1'}>
              {'Continue to ' + nextLabel + ' '}<span>{'›'}</span>
            </button>
          </div>
        </div>

        {/* ── THE HEAD: the career browser's default head, not the motivation frame's `hp-stephead`
             pair — the board's skills frame draws slash · maker · designation · rule · lead plate
             stacked in unnamed wrappers (divergence #57 said so on the first sweep). */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <span className="hp-slash"><i /><i /><i /></span>
              <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
            </div>
            <h1 className="hp-desig">{p.stepName}</h1>
            <div className="hp-desig-rule" />
            <div className="hp-plate" data-pad="read"
              style={{ marginTop: '13px', maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
                {'Choose '}<b>{careerMax + ' of your ' + careerIds.length}</b>{' career skills'}
                {specMax ? <React.Fragment>{' and '}<b>{specMax + ' of your ' + specIds.length}</b>{' specialization skills'}</React.Fragment> : null}
                {'; each takes one free rank. A skill on both lists can be picked twice to start at '}
                <b>{'rank ' + FC_SKILLS_CREATION_CAP}</b>{', the most you can begin with. After creation every rank costs XP, so these are the best ranks you will ever buy.'}
              </p>
              {p.cite ? (
                <div style={{ marginTop: '11px' }}>
                  <span className="hp-cite" data-step-cite="skills">{p.cite}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the picks on the left, the result and the rule on the right. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            <div className="hp-spend">
              <div>
                {pickList('Career Skills', 'Choose ' + careerMax + ' of ' + careerIds.length, careerIds,
                          'career', pickedCareer, careerMax, 'freeCareer', null, true,
                          { 'data-skills-list': 'career' })}
                {specIds.length ? pickList('Specialization Skills', 'Choose ' + specMax + ' of ' + specIds.length, specIds,
                          'spec', pickedSpec, specMax, 'freeSpec', null, false,
                          { 'data-skills-list': 'spec' }) : null}
                {/* UNDRAWN — the species-choice round, for a species whose grant is a CHOICE. */}
                {grant ? pickList((p.speciesName || 'Species') + ' Skills',
                          'Choose ' + speciesMax + (grant.pool === 'non_career' ? ' non-career' : '') + ' of ' + speciesIds.length,
                          speciesIds, 'species', pickedSpecies, speciesMax, 'freeSpecies',
                          grant.rank_cap != null ? grant.rank_cap : null, false,
                          { 'data-skills-list': 'species', 'data-undrawn': 'species-choice' }) : null}
              </div>

              <div>
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>What You Will Roll</h2></div>
                <div className="hp-plate" data-pad="data" data-skills-ledger="1">
                  {ranked.length ? (
                    <table className="hp-ledger"><tbody>
                      {ranked.map(function(r) {
                        return (
                          <tr key={r.id} data-skills-roll={r.id}>
                            <td className="hp-lkey">{r.name}<span className="hp-sep">{'·'}</span>{r.from}</td>
                            <td className="hp-lval">{pool(r.pool)}</td>
                          </tr>
                        );
                      })}
                    </tbody></table>
                  ) : (
                    <span className="hp-none" data-skills-none="1">
                      {'Nothing ranked yet — every pool above is your characteristic alone. Pick on the left and the rows appear here.'}</span>
                  )}
                </div>

                {/* UNDRAWN — BUY RANKS. The board draws the free ranks only; the app's legacy screen
                    sold more for XP after them, and a transplant carries every rules-carrying element
                    (R-CPR-28). The board's trade-row idiom: a ledger with a small action per row.
                    Declared `data-ruled-delta="skills-transplant"` from `skills-transplant` R-ST-2 until
                    the refresh to lab STABLE 4582d484, where the board DRAWS it (`force-transplant`
                    A1 R-FT-A1-1) — the declaration came off so the block compares directly. */}
                <div className="hp-sechead" style={{ margin: '20px 0 10px' }} data-skills-buy-block="1">
                  <h2>Buy Ranks</h2>
                  <span className="hp-cite" data-skills-spent={p.xpSpentOnSkills || 0} data-skills-remaining={p.xpRemaining}>
                    {(p.xpSpentOnSkills || 0) + ' of ' + (p.xpPool || 0) + ' XP · rank ' + FC_SKILLS_CREATION_CAP + ' cap'}</span></div>
                {/* THE BOARD'S ROW (4582d484): name · the pool · "rank N" · ONE small action. Bought
                    skills first, then every skill alphabetically — the board samples eight; the app
                    lists all 35 (a COMPOSITION, like the registers). A bought skill below the cap
                    keeps its Buy beside its Refund: the second rank is RAW-legal (R-CPR-28). */}
                <div className="hp-plate" data-pad="data" data-skills-buy="1">
                  {/* B-483 — THE LIST SCROLLS, NOT THE PLATE. The plate paints its paper face as an
                      `inset:1px` ::after (paper.css §3), and an absolutely positioned box inside a
                      SCROLL CONTAINER is anchored to the top of the travel — so while the plate was
                      itself the scroller (B-450) the face slid away and every row past the first
                      512px was drawn on the hairline colour. Jason saw it on the served merge. This
                      wrapper carries NO board class, so both parity engines look straight through it
                      (`board_dom_parity.cjs` compares named ancestors only; R-TTP-1's allowed
                      differences) and it needs no `data-ruled-delta`. */}
                  <div data-skills-buy-scroll="1">
                  {(function() {
                    var ids = Object.keys(names).filter(function(id) { return !!chars[id]; });
                    var byName = function(a, b) { return (names[a] || a).localeCompare(names[b] || b); };
                    var boughtIds = ids.filter(function(id) { var s = entryOf(id); return !!(s && s.bought && s.bought.length); }).sort(byName);
                    var rest = ids.filter(function(id) { return boughtIds.indexOf(id) < 0; }).sort(byName);
                    return boughtIds.concat(rest).map(function(id) {
                      var key = keyOf(id), rank = rankOf(id), isCareer = !!(p.careerStatus || {})[id];
                      var s = entryOf(id), bought = (s && s.bought) ? s.bought.length : 0;
                      var atCap = rank >= capOf(id);
                      var cost = atCap ? null : CharResolve.skillRankCost(rank + 1, isCareer);
                      var can = cost != null && cost <= (p.xpRemaining || 0);
                      var lastPaid = bought ? (s.bought[s.bought.length - 1] || 0) : 0;
                      return (
                        <div className="hp-buy" key={id} data-skills-buy-row={id} data-rank={rank} data-career={isCareer ? '1' : undefined}>
                          <span className="hp-buy-name">{names[id] || id}</span>
                          <span className="hp-buy-cost">{pool(poolOf(id))}</span>
                          <span className="hp-buy-step">{'rank ' + rank}</span>
                          {/* BOTH BUTTONS IN ONE GRID CELL — the same fix the characteristics rows
                              already carry, applied where it was never carried across. `hp-buy` is
                              the board's FOUR-column grid (`1fr auto auto auto`); once a skill is
                              bought this row renders FIVE children — name · pool · rank · Refund ·
                              Buy — so the fifth wrapped to a row of its own and doubled the line.
                              The wrapper is UNNAMED, so it changes no named-object path and the
                              parity guard is unaffected, WHICH IS WHY THE GUARD COULD NOT SEE THIS
                              AND THE PICTURE COULD (`creator-pre-deploy-batch` A3-2; the
                              characteristics comment says the same thing about the same grid). */}
                          <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                          {bought ? (
                            <button type="button" className="hp-buy-btn" data-skills-refund-btn={id}
                              onClick={function() { p.onRefund(key); }}>{'Refund · +' + lastPaid + ' XP'}</button>
                          ) : null}
                          {cost != null ? (
                            <button type="button" className="hp-buy-btn" data-skills-buy-btn={id}
                              aria-disabled={can ? undefined : 'true'} data-refused={can ? undefined : '1'}
                              onClick={function() { if (can) p.onBuy(key, cost); }}>{'Buy · ' + cost + ' XP'}</button>
                          ) : null}
                          </span>
                        </div>
                      );
                    });
                  })()}
                  </div>
                </div>
                <div>
                  <span className="hp-none">{'Career skills cost less. Nothing goes above rank ' + FC_SKILLS_CREATION_CAP + ' at creation.'}</span>
                </div>
                {/* THE TEACHING FOLDS AT THE MID BAND (B-599, Jason's iPad walk): at 1194×810 the
                    two pick lists and the roll ledger + Buy Ranks are the page; the dice lesson
                    under them (400px) is a closed fold with the same head, opened by the reader —
                    the characteristics step's idiom (B-598). The wide sheet draws it open. */}
                {p.band === 'mid' ? (
                  <details className="hp-fold" data-skills-teach-fold="1">
                    <summary>
                      <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
                        <h2>A Rank Upgrades Before It Adds</h2>
                        {p.diceCite ? <span className="hp-cite" data-step-cite="dice_pool">{p.diceCite}</span> : null}
                        <span className="hp-glyph" aria-hidden="true">{'\u25be'}</span>
                      </div>
                    </summary>
                    {teachPlate}
                  </details>
                ) : (
                  <React.Fragment>
                <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
                  <h2>A Rank Upgrades Before It Adds</h2>
                  {p.diceCite ? <span className="hp-cite" data-step-cite="dice_pool">{p.diceCite}</span> : null}</div>
                {teachPlate}
                  </React.Fragment>
                )}

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}

/* ══ FCTalentsSheet — CLUSTER THREE'S SECOND TRANSPLANT (the TALENTS step) ══════════════════════
   MOCKUP (C-MU-2): the lab board `lab/holocron/index.html` @ b818e23f (`board_pin.BOARD_SHA`),
   frames `talents_step(species_id=wookiee, career_id=hired_gun, spec_id=marauder,
   owned=[[10,0],[10,1],[5,0]], prior=60)` ×4 (the desktop ladder, ordinals 0–3) and
   `phone_talents()` — extracted as `test/fixtures/board_talents_*.html` at talents step 0
   (c82440aa). APPROVED under the R-VR-6 substitution and the R-CRS-2 term; ruled
   `cluster-three-concept` R-C3-2/3/5/6/7/8 and `talents-tree-skin` R-TTS-1..5.

   THE TREE IS THE BOARD'S (R-TTP-1): runhead · rail (The Build ×4 — Starting experience · Spent
   earlier · Spent here · Remaining · So Far ×3 · BOUGHT HERE, a ledger of this step's purchases with
   the talent's activation after the separator · the commit) · head (wave · the career browser's
   default head · the lead + ONE per-line cite through the converter) · spend pane (sechead "<Spec>
   Talent Tree · <book>" · `hp-treescroll` holding `hp-skills` "Bonus career skills: …" and THE TREE
   · "Reading a Cell · <cite>" with the board's copy).

   THE TREE IS THE ONE COMPONENT'S BOARD SKIN (R-TTS-1): `DirectionalTree` over `talentTreeToModel`
   with `skin: 'board'` — the same model, tap, buy and refund handlers the legacy step and ADVANCEMENT
   use; this sheet only wraps it. A pressed cell opens the SELECTED-CELL block (below), which is the
   board-idiom twin of the dark theme's entry body: the same `entry.buy` / `entry.refund` /
   `entry.choose` thunks, the board's small action (`hp-buy-btn`) and its refusal line (`hp-none`).

   WHAT THE BOARD DOES NOT DRAW, built inside its frame in its idiom (R-C3-3), each an APP-SIDE
   ADDITION named UNDRAWN in the gate with a screenshot, none declared (R-TTP-1):
     · the SELECTED CELL block — sechead (name · activation line) + read plate (effect, the cheapest
       route where it differs, the source cite) + the action or its refusal; absent until a cell is
       pressed, so the swept frames never carry it;
     · NOTHING BOUGHT — Bought Here reads its emptiness (`hp-none`), the commit reads "Continue
       without talents";
     · OVERSPENT — the commit REFUSED with the reason beside it (R-CSC-1) until a refund clears it;
     · the B-181 caution on an owned choice-talent still lacking its skills (`data-needs-choice` on
       the cell, the CHOOSE action in the selected block; the picker itself stays the AFTER increment,
       R-C3-6, mounted unchanged beside this sheet). */
/* ── THE TALENTS PHONE SHEET (the board's `phone_talents()` frame) ─────────────────────────────
   R-PTC-3, built to `test/fixtures/board_talents_phone.html` — the byte-identical pull of the
   frame at pin `6051229c`, approved by Jason 2026-09-09 (`lab/holocron/phone_approval.html`
   @ `c0744b3a`, `parallel-pull-grant` A3).

   A COMPOSITION, not a second tree. The scaffold is `FCPhoneShell` (the same emitter the species
   and characteristics phone bands use — R-SPB-9), and the tree is the ONE component's BOARD SKIN
   over the SAME model, handlers and sheet the wide band builds: `DirectionalTree` with no
   `onInspect`, so a press sets its own `detail` and mounts `DirectionalTreeSheet`. THE SLIDING
   SHEET REMAINS THE PURCHASE SURFACE ON PHONE and is unchanged inside — Jason's word
   (`tree-cell-buy-in-the-cell` A1, R-TCB-A1-1), so the cell is not the buy control here and its
   refusal register (NOT ENOUGH XP · PREREQUISITES NOT MET · cannot-refund · the cheapest route)
   rides forward exactly as it shipped.

   WHAT THE FRAME DRAWS, in its order: runhead · wave · rule · status (Spent here · Remaining) ·
   slash + maker · desig + rule · the lead plate with its cite · the sechead ("<Spec> Tree" ·
   "swipe →") · `hp-treescroll` holding the tree · the "So Far" ledger as a `<details>` · and the
   commit in `hp-phoneact` outside the scrolling body.

   THE MAKER READS "Character Creation" WITH NO STEP NUMBER, which is what all four phone frames
   draw and what the folio beside it already says; the wide band's `fcMakerLine` prints the step
   because its head has no folio. Not a divergence to reconcile — two different heads.

   WHAT THE FRAME DOES NOT DRAW, carried forward in its idiom rather than invented (R-PTC-1's
   KEEP-UNDRAWN rows): the OVERSPENT refusal on the commit, in the wide sheet's own words and
   attributes so one behaviour has one wording; and the "Continue without talents" form of the
   commit when nothing has been bought. Both are the app's XP-budget behaviour, untouched by a
   chrome restyle. */
function FCTalentsSheetPhone(p) {
  var spec = p.spec || {};
  // BOUGHT — this specialization's purchases, the same predicate the wide sheet's ledger uses. The
  // phone draws no "Bought Here" list (the frame puts the So Far ledger in that slot and mobile
  // never had one), so only its EMPTINESS is read, by the commit.
  var boughtCount = Object.keys(p.talents || {}).map(function(k) { return (p.talents || {})[k]; })
    .filter(function(t) { return t && t.specKey === spec.id && (t.cost || 0) > 0; }).length;
  var overspent = (p.xpRemaining || 0) < 0;
  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  return (
    <FCPhoneShell band={p.band}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      status={<React.Fragment>
        <div><span className="hp-status-k">Spent here</span>
          <span className="hp-status-v">{(p.xpSpentOnTalents || 0) + ' XP'}</span></div>
        <div><span className="hp-status-k">Remaining</span>
          <span className="hp-status-v">{(p.xpRemaining || 0) + ' XP'}</span></div>
      </React.Fragment>}
      act={<React.Fragment>
        {overspent ? (
          <span className="hp-none" data-tal-overspent="1" style={{ display: 'block', marginBottom: '8px' }}>
            {'Overspent by ' + Math.abs(p.xpRemaining) + ' XP — refund a talent first.'}</span>
        ) : null}
        <button type="button" className="hp-commit" data-creator-commit="1"
          onClick={function() { if (!overspent) p.onConfirm(); }}
          aria-disabled={overspent ? 'true' : undefined} data-refused={overspent ? '1' : undefined}>
          {(boughtCount ? 'Continue to ' + nextLabel + ' ' : 'Continue without talents ')}<span>{'›'}</span>
        </button>
      </React.Fragment>}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">Character Creation</div>
      </div>
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName || 'Talents'}</h1>
      <div className="hp-desig-rule" />
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        {/* THE LEAD IS THE PHONE FRAME'S OWN SENTENCE, shorter than the wide band's (which prints
            row 1/2/3 by name); the board writes both and they are not the same copy. */}
        <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}>
          {'The tree is priced by '}<b>row</b>{': five times the row. You may buy a talent in the '}
          <b>top row</b>{' or one '}<b>connected by a line</b>{' to one you already own.'}
        </p>
        {p.cite ? (
          <div style={{ marginTop: '10px' }}>
            <span className="hp-cite" data-step-cite="talents" style={{ marginRight: '7px' }}>{p.cite}</span>
          </div>
        ) : null}
      </div>

      {/* THE SECHEAD'S CITE IS THE GESTURE, NOT THE BOOK. At 390 the board's four 215px-minimum
          columns overflow ON PURPOSE and `hp-treescroll` scrolls them horizontally, so the frame
          spends this slot telling the player that rather than repeating the spec's source (which
          the wide band's sechead carries and the lead's cite already names). */}
      <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
        <h2>{(spec.name || 'Specialization') + ' Tree'}</h2>
        <span className="hp-cite">{'swipe →'}</span>
      </div>
      {/* B-1022 (D-400): the one shared notice with B-30's RETRY, as on the desk band below. */}
      {p.modsNotice ? <ModsLoadNotice onRetry={p.onModsRetry} /> : null}
      <div className="hp-treescroll">
        {/* THE ACCENT IS THE APP'S, not the paper token: the board skin's cells take no accent at
            all (they are CSS classes), and the ONLY consumer of it here is `DirectionalTreeSheet`,
            which composes it into gradients and borders — `var(--doc-action)` would build
            `var(--doc-action)55` and paint nothing. The sheet is kept UNCHANGED, so it keeps the
            colour it had at 390 before this restyle. */}
        {p.model ? (
          <DirectionalTree model={p.model} accent={p.accent} headPress />
        ) : null}
      </div>

      {/* SO FAR FOLDS on the phone, as it does on characteristics and for the same reason: the
          board gives the phone a `<details>` at the foot where the wide band has a rail. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'▾'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <FCbSoFarTable rows={p.soFar} />
        </div>
      </details>
    </FCPhoneShell>
  );
}

function FCTalentsSheet(p) {
  // THE PHONE IS ITS OWN SHEET, not this one reflowed — the same shape `FCCharSheet` takes, and
  // for the same reason: the board draws a different frame at 390, not this grid narrowed.
  if (p.band === 'mobile') return <FCTalentsSheetPhone {...p} />;
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal || (p.steps || []).length;
  var _sel = useState(null), selId = _sel[0], setSelId = _sel[1];
  // THE TALENT-CHOICE PICKER (B-181 / B-302; `talent-choice-picker-concept` R-TCPK-1..3, the lab's four
  // picker frames at 4582d484 the record): INLINE under the pressed cell — opened by the buy of a
  // choice-talent (the purchase lands first, the board's flow) or by the "Choose your skills." line on
  // an owned, never-chosen one; the picks are local until the confirm FILES them against the cell
  // (R-TCP-1, positional) — bound at purchase (R-TCT-3), count = the field's grants (R-TCT-2).
  var _pk = useState(null), picking = _pk[0], setPicking = _pk[1];       // the cellId being chosen for
  var _pks = useState([]), picks = _pks[0], setPicks = _pks[1];
  var _pq = useState(''), pickQuery = _pq[0], setPickQuery = _pq[1];
  var model = p.model, rows = (model && model.rows) || [];
  var spec = p.spec || {}, talentDb = p.talentDb || {};
  var tree = spec.talent_tree || [];
  var rowCost = function(i) { return tree[i] && tree[i].cost != null ? tree[i].cost : (i + 1) * 5; };

  // The selected cell is looked up on EVERY render, so a buy or refund refreshes its block from the
  // model the same press rebuilt — a held entry object would describe the cell as it was.
  var selCell = null;
  rows.forEach(function(row) { (row.cells || []).forEach(function(c) {
    if (c && !c.filler && c.entry && c.entry.cellId === selId) selCell = c; }); });
  var entry = selCell ? selCell.entry : null;
  useEffect(function() { if (selId && !selCell) setSelId(null); }, [selId, selCell]);
  // The picker follows the pressed cell: another cell pressed, or the cell refunded, closes it.
  useEffect(function() { if (picking && picking !== selId) { setPicking(null); setPicks([]); setPickQuery(''); } }, [picking, selId]);
  useEffect(function() { if (picking && entry && !(entry.buy && entry.buy.state === 'owned')) { setPicking(null); setPicks([]); } }, [picking, entry]);

  /* THE BADGE SAYS "BUY + SKILLS", SO PRESSING IT MUST DO BOTH. The picker used to be opened by the
     block's own Buy handler (`buy.doBuy(); if (isChoiceTalent) openPicker(e)`); that handler went
     with the block, and for one increment a choice talent BOUGHT AND ASKED NOTHING — a talent that
     silently grants no skills, which is worse than the old separate box it replaced. Caught by
     `picker-reveal`'s own leg reporting `mounted: false`, which is the leg doing its job on a
     defect it was not written for.
     ONCE PER CELL, by ref: a player who closes the picker without choosing is not re-trapped in it
     on the next render — the talent keeps `needsChoice` and the tree keeps saying so. */
  var autoPicked = React.useRef({});

  /* ESCAPE CLOSES THE PICKER (C-2). R-TCB-A6-1 contracted Escape as the dismissal for a shown
     refusal; this is the same idiom one object over, and it is the way out that does not depend on
     a control being on screen — the CHOOSE line is hidden while the picker is open, so without
     this the toggle above would have nothing to toggle. Listens on the document so it works while
     the picker's own search input has focus, which is where a reader's hands actually are. */
  useEffect(function() {
    if (!picking) return undefined;
    function onKey(ev) {
      if (ev.key !== 'Escape') return;
      ev.stopPropagation();
      closePicker();
    }
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [picking]);
  useEffect(function() {
    if (picking) return;
    var need = null;
    rows.forEach(function(row) { (row.cells || []).forEach(function(c) {
      // `needsChoice` is on the CELL, not on its entry — `data-needs-choice` is rendered from
      // `cell.needsChoice` (:11293). Reading `entry.needsChoice` found undefined on every cell and
      // the effect never fired, which looked exactly like the effect not running at all.
      if (!need && c && !c.filler && c.entry && c.needsChoice
          && !autoPicked.current[c.entry.cellId]) need = c.entry; }); });
    if (!need) return;
    autoPicked.current[need.cellId] = 1;
    // SELECT IT FIRST. `openPicker` sets `picking` and nothing else; the guard above closes any
    // picker whose cell is not the selected one, so opening it without selecting shut it in the
    // same tick — the picker "did not mount" while the buy had worked perfectly. It never showed
    // before because `openPicker` was only ever called from inside the selected cell's own block.
    setSelId(need.cellId);
    openPicker(need);
  }, [rows, picking]);

  /* PICKER-REVEAL (R-TCB-A10-1). Jason: the picker "hides beneath the talent tree", and he took the
     cheaper of the two shapes — the surface brings the picker to you rather than a pill telling you
     to scroll. So: when the picker MOUNTS BELOW THE FOLD, the scrolling surface brings its head to
     the top. Smooth normally; INSTANT under `prefers-reduced-motion`, which is the whole reason this
     reads the query rather than always animating.
     IT SCROLLS THE PANE, NOT THE WINDOW. The creator's page does not scroll — `.hpw-read.hp-pane`
     does (measured: document scrollHeight == innerHeight while the pane runs 1420 in a 703 box), so
     `scrollIntoView` on the window would move nothing and look exactly like a feature that does not
     work. ONLY WHEN BELOW THE FOLD: if the head is already in view the surface stays still, because
     moving a page the reader is already reading is its own defect. */
  useEffect(function() {
    if (!picking) return;
    var head = document.querySelector('[data-tal-picking="1"]');
    var pane = head && head.closest('.hpw-read');
    if (!head || !pane) return;
    var hr = head.getBoundingClientRect(), pr = pane.getBoundingClientRect();
    if (hr.top <= pr.bottom - 24 && hr.top >= pr.top) return;      // already in view: stay still
    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    pane.scrollTo({ top: pane.scrollTop + (hr.top - pr.top) - 12,
                    behavior: reduce ? 'auto' : 'smooth' });
  }, [picking]);

  // C-1: every cell whose choice is FILED, in tree order. The receipt's subject is the TREE — a
  // filed choice is a fact about the character, not about what happens to be pressed — so this is
  // read off the model's cells and never off `entry`.
  var filedChoices = [];
  rows.forEach(function(row) { (row.cells || []).forEach(function(c) {
    if (c && !c.filler && c.entry && c.entry.choiceReceipt && c.entry.choiceReceipt.length
        && picking !== c.entry.cellId) filedChoices.push(c.entry); }); });

  // The grants, the pool and the count for the pressed cell's talent (registry-only detection).
  var grants = entry ? ((window.CharResolve && CharResolve.talentChoiceGrantsOf(talentDb[entry.talentKey])) || []) : [];
  var pickCount = grants.reduce(function(n, g) { return n + (Number(g.count) || 0); }, 0);
  var pickPool = (function() {
    var out = [], seen = {};
    grants.forEach(function(g) {
      (speciesChoicePoolOf(g, p.charbuild, p.careerStatus || {}) || []).forEach(function(id) { if (!seen[id]) { seen[id] = true; out.push(id); } });
    });
    return out;
  })();
  var poolLabel = grants.length && grants.every(function(g) { return g.pool === 'any'; }) ? 'any'
    : grants.map(function(g) { return String(g.pool || 'any').replace('_', '-'); }).join(' / ');
  // Skills already chosen for this talent on OTHER cells (rank scaling, R-TCPK-3) read as TAKEN.
  var takenElsewhere = {};
  if (entry) {
    // F-100 M8 R-M8G-4: the owned cells of this spec, each read through the ONE reader (CharResolve.pickOf).
    Object.keys(p.talents || {}).forEach(function(k) {
      var m = /^(.*)_r(\d+)c(\d+)$/.exec(k);
      if (!m || m[1] !== entry.specKey) return;
      var pos = m[2] + '-' + m[3];
      if (pos === entry.row + '-' + entry.col) return;
      var t = p.talents[k];
      if (t && t.talentKey === entry.talentKey) (CharResolve.pickOf(p, entry.specKey, pos, 'career_skills') || []).forEach(function(id) { takenElsewhere[id] = true; });
    });
  }
  var q = String(pickQuery || '').trim().toLowerCase();
  var shownPool = q ? pickPool.filter(function(id) { return String(skillNamesOf(id)).toLowerCase().indexOf(q) >= 0; }) : pickPool;
  function skillNamesOf(id) { return (p.skillNames || {})[id] || id; }
  function togglePick(id) {
    if (takenElsewhere[id]) return;
    setPicks(function(prev) {
      if (prev.indexOf(id) >= 0) return prev.filter(function(k) { return k !== id; });
      if (prev.length >= pickCount) return prev;
      return prev.concat([id]);
    });
  }
  /* C-2 — THE PICKER HAS A WAY OUT (ruling `tree-cell-buy-in-the-cell-gate` R-TCG-1 on FINDING 3).
     It had none: the only `setPicking(null)` paths were confirming the picks and selecting another
     cell, and since A6-1 retired the cell as a control nothing on this skin can select one — so a
     player who opened the picker was in it until they finished, and the board's `unchosen` frame
     (the CHOOSE line with the picker SHUT) was unreachable.
     ONE close path, used by both ways out, because two would be two resolutions of one question.
     It also marks the cell in `autoPicked`, which is what makes a dismissal STICK: the auto-open
     effect re-fires the moment `picking` goes null, and without the mark it would re-open the
     picker the user just dismissed — the "once per cell, by ref" promise, now actually kept for a
     picker that was opened by hand as well as by the effect. */
  function closePicker() {
    if (picking) autoPicked.current[picking] = 1;
    setPicking(null); setPicks([]); setPickQuery('');
  }
  function openPicker(e) {
    // THE OPENER IS A TOGGLE: the control that opened the picker closes it, and the same press
    // opens it again (R-TCG-1: "reopening is the same press"). Nothing new is DRAWN — the board's
    // picking frames carry no dismiss, so rendering one would put the app ahead of the record.
    if (picking === e.cellId) { closePicker(); return; }
    var existing = CharResolve.pickOf(p, e.specKey, e.row + '-' + e.col, 'career_skills') || [];   // R-M8G-4: the one reader
    setPicks(existing.slice()); setPickQuery(''); setPicking(e.cellId);
  }
  function confirmPicks() {
    if (!entry || picks.length < pickCount) return;
    p.onFileChoices(entry.specKey, entry.row, entry.col, picks.slice());
    setPicking(null); setPicks([]); setPickQuery('');
  }

  // BOUGHT HERE — this specialization's purchases, in the order they were made (the board's ledger
  // lists Toughened 5 · Feral Strength 10 · Toughened 10, which is buy order, not tree order).
  var bought = Object.keys(p.talents || {}).map(function(k) { return p.talents[k]; })
    .filter(function(t) { return t && t.specKey === spec.id && (t.cost || 0) > 0; })
    .map(function(t) {
      var info = talentDb[t.talentKey] || {};
      return { key: t.specKey + '_r' + t.row + 'c' + t.col, name: info.name || t.talentKey,
               act: String(info.activation || 'passive') === 'passive' ? 'passive' : 'active', cost: t.cost };
    });
  var spentHere = p.xpSpentOnTalents || 0;
  var spentEarlier = Math.max(0, (p.xpPool || 0) - (p.xpRemaining || 0) - spentHere);
  var overspent = (p.xpRemaining || 0) < 0;
  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  // The spec's own book, from its registry `sources` through the sources index (`sourceMeta`, the
  // Force list's own resolver — a core alias or a supplement's name both resolve to its label).
  // F-100 M12 (R-M12B-5): a pack-added tree names no book, so the label is the pack's (fcRecordSourceLabel).
  var bookName = fcRecordSourceLabel('specializations', spec, function(src) {
    if (!src) return '';
    var SS = window.SWRPGSources, cur = SS && SS.current && SS.current(), idx = cur && cur.index;
    var meta = idx && SS.sourceMeta ? SS.sourceMeta(idx, src) : null;
    return String((meta && meta.label) || src).replace(/ Core Rulebook$/, '');
  });
  var skillNames = p.skillNames || {};

  function commit() { if (!overspent) p.onConfirm(); }

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-talents-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: the four-row budget, what is settled, what this step bought, the commit. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1" data-rail-stack="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool} XP</dd></div>
              {spentEarlier > 0 ? <div className="hp-xp"><dt>Spent earlier</dt><dd>{spentEarlier} XP</dd></div> : null}
              <div className="hp-xp"><dt>Spent here</dt><dd data-tal-spent={spentHere}>{spentHere} XP</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Remaining</dt>
                <dd data-creator-char-purse={p.xpRemaining}>{p.xpRemaining} XP</dd></div>
            </dl>
            {/* THE OVERSPENT BLOCK inside the Build plate — the board's (4582d484 `_overspent` and the
                lab's "no XP" specimen, which is below nought): the characteristics step's own object. */}
            {overspent ? (
              <React.Fragment>
                <div className="hp-overspent" data-tal-overspent-block="1">{'Overspent by ' + Math.abs(p.xpRemaining) + ' XP'}</div>
                <p className="hp-prose hp-prose-sm" style={{ margin: '9px 0 0', maxWidth: '36ch' }}>
                  {'The step holds here until the balance is back to nought. Refund a raise, or take back a talent, and the way forward opens.'}</p>
              </React.Fragment>
            ) : null}
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          {/* The head and plate draw only with a purchase in it: the empty line is cut (D-289), and a wrapper left empty goes,
              its head with it (R-CVB-3; ruling copy-voice-sweep-build C1). */}
          {bought.length ? (
          <React.Fragment>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Bought Here</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="bought" data-tal-bought={bought.length}>
              <table className="hp-ledger"><tbody>
                {bought.map(function(b) {
                  return (
                    <tr key={b.key} data-tal-bought-row={b.key}>
                      <td className="hp-lkey">{b.name}<span className="hp-sep">{'· ' + b.act}</span></td>
                      <td className="hp-lval">{b.cost + ' XP'}</td>
                    </tr>
                  );
                })}
              </tbody></table>
          </div>
          </React.Fragment>
          ) : null}
          <div className="hp-railact" data-rail-section="commit">
            {overspent ? (
              <span className="hp-none" data-tal-overspent="1" style={{ display: 'block', marginBottom: '8px' }}>
                {'Overspent by ' + Math.abs(p.xpRemaining) + ' XP — refund a talent first.'}</span>
            ) : null}
            <button type="button" className="hp-commit" data-creator-commit="1" onClick={commit}
              aria-disabled={overspent ? 'true' : undefined} data-refused={overspent ? '1' : undefined}>
              {(bought.length ? 'Continue to ' + nextLabel + ' ' : 'Continue without talents ')}<span>{'›'}</span>
            </button>
          </div>
        </div>

        {/* ── THE HEAD: the career browser's default head (the board's talents frame draws it so). */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <span className="hp-slash"><i /><i /><i /></span>
              <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
            </div>
            <h1 className="hp-desig">{p.stepName}</h1>
            <div className="hp-desig-rule" />
            <div className="hp-plate" data-pad="read"
              style={{ marginTop: '13px', maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
                {'The tree is priced by '}<b>row</b>{': row 1 is '}<b>{rowCost(0) + ' XP'}</b>
                {', row 2 is ' + rowCost(1) + ', row 3 is ' + rowCost(2) + ': '}<b>five times the row</b>
                {'. You can only buy a talent in the '}<b>top row</b>{' or one '}<b>connected by a line</b>
                {' to a talent you already own.'}
              </p>
              {p.cite ? (
                <div style={{ marginTop: '11px' }}>
                  <span className="hp-cite" data-step-cite="talents">{p.cite}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the tree, the pressed cell, the reading note. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-sechead" style={{ margin: '0 0 12px' }}>
            <h2>{(spec.name || 'Specialization') + ' Talent Tree'}</h2>
            {bookName ? <span className="hp-cite">{bookName}</span> : null}
          </div>
          {/* B-1022 (D-400): the desk band's own line is RETIRED — one shared notice with RETRY, both bands. */}
          {p.modsNotice ? <ModsLoadNotice onRetry={p.onModsRetry} /> : null}
          <div className="hp-treescroll">
            <div className="hp-skills" style={{ margin: '0 0 13px' }}>
              {'Bonus career skills: ' + (spec.career_skills || []).map(function(id) { return skillNames[id] || id; }).join(', ')}
            </div>
            {model ? (
              <DirectionalTree model={model} accent="var(--doc-action)"
                onInspect={function(e) { setSelId(e && e.cellId ? e.cellId : null); }} />
            ) : null}
          </div>

          {/* THE PRESSED CELL, in the board's shape (4582d484): the name · the activation word · Close;
              the reading plate (effect · the cheapest route, always · the source); the ONE action or
              its refusal BESIDE the plate; then the picker's three states (R-TCPK-2's three points). */}
          {entry ? (
            <React.Fragment>
              {/* THE BLOCK UNDER THE TREE IS GONE (B-506, Jason 2026-09-09: "The desktop will get rid
                  of the description box below the talent tree"). What stood here — the name/Close
                  head, the description, the cheapest-route line and the cite, and the buy/refund
                  action — is retired: the ACTION moved onto the cell's own badge, and the reading
                  is the cell's body, which draws it already.
                  IT SURVIVES FOR EXACTLY ONE CASE (R-TCB-A9-1, his direct word): "If there's a
                  buy + skills picker, keep the box for that situation." So the picker below still
                  mounts — the list, the confirm, the bound-at-purchase note — and nothing else. */}
              {entry.needsChoice && picking !== entry.cellId ? (
                <div>
                  <span className="hp-none" data-tal-choose="1" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                    onClick={function() { openPicker(entry); }}
                    onKeyDown={function(ev) { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); openPicker(entry); } }}>
                    {'Choose your skills.'}</span>
                </div>
              ) : null}
              {picking === entry.cellId ? (
                <React.Fragment>
                  <div className="hp-sechead" style={{ margin: '20px 0 10px' }} data-tal-picking="1">
                    <h2>Choose Your Skills</h2>
                    {/* THE HEAD SAYS WHY (R-TCB-A10-1): the surface just moved under the reader, and
                        a page that jumps without saying why is the thing this is meant to avoid. */}
                    <span className="hp-cite" data-tal-pick-why="1">{'the purchase continues here'}</span>
                    <span className="hp-cite">{pickCount + ' ' + poolLabel + ' of ' + pickPool.length}</span>
                  </div>
                  <div className="hp-plate" data-flat="1" data-input="1">
                    <input type="search" value={pickQuery} data-tal-pick-search="1"
                      placeholder="Search Skills…" aria-label="Search Skills…"
                      onChange={function(ev) { setPickQuery(ev.target.value); }}
                      style={{
                        width: '100%', border: 0, background: 'transparent', outline: 'none',
                        padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: '12px',
                        letterSpacing: '.12em', color: 'var(--doc-ink)', textTransform: 'uppercase',
                      }} />
                  </div>
                  <div className="hpw-scroll hp-picklist" style={{ marginTop: '8px' }}>
                    <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
                      {shownPool.map(function(id) {
                        var on = picks.indexOf(id) >= 0, taken = !!takenElsewhere[id];
                        var full = !on && picks.length >= pickCount;
                        return (
                          <button type="button" className="hp-row" key={id} data-row-id={id} data-noplate="1"
                            data-selected={on ? '1' : undefined} aria-pressed={on ? 'true' : 'false'}
                            data-tal-pick-row={id} data-taken={taken ? '1' : undefined}
                            data-refused={(taken || full) ? '1' : undefined} aria-disabled={(taken || full) ? 'true' : undefined}
                            title={taken ? 'Already chosen for another rank of this talent' : undefined}
                            onClick={function() { if (!taken) togglePick(id); }}>
                            <span className="hp-row-body">
                              <span className="hp-row-desig">{skillNamesOf(id)}</span>
                              <span className="hp-rowsrc">{FC_CHAR_ABBR[(p.skillChars || {})[id]] || ''}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* DRAWN AT THE FREEZE PIN. This read "UNDRAWN — the board's picking frame draws
                      the open list and no control" (R-TCPK-2's second point), which was true of the
                      pin it was written against and is not true of `6051229c`: both picking fixtures
                      draw this `hp-buy-btn` AND the `data-tal-bound` line, with the confirm in its
                      refused state on `board_talents_choice_picking` (`Pick 2 more`, `data-refused`)
                      and offered on `board_talents_choice_placed` (`Make them career skills`). The
                      wording here matches the board's on both. Whether that closes B-410's residual
                      is the row's to say, not this comment's. */}
                  <div>
                    <button type="button" className="hp-buy-btn" data-tal-pick-confirm="1"
                      aria-disabled={picks.length < pickCount ? 'true' : undefined}
                      data-refused={picks.length < pickCount ? '1' : undefined} onClick={confirmPicks}>
                      {picks.length < pickCount
                        ? 'Pick ' + (pickCount - picks.length) + ' more'
                        : 'Make ' + (pickCount === 1 ? 'it a career skill' : 'them career skills')}
                    </button>
                    <span className="hp-none" data-tal-bound="1" style={{ display: 'block', marginTop: '8px' }}>
                      {'This choice is bound to this purchase — refund the talent and the choice goes with it; it can’t be changed while you own the rank.'}</span>
                  </div>
                </React.Fragment>
              ) : null}
            </React.Fragment>
          ) : null}

          {/* THE RECEIPT IS THE TREE'S, NOT THE SELECTED CELL'S (C-1 — ruling
              `tree-cell-buy-in-the-cell-gate` R-TCG-1 on FINDING 2). It used to stand inside the
              selected cell's block, which meant a player saw "Career Skills Added" exactly once —
              while the auto-open still held that cell selected — and never again: on the next load
              nothing selects a cell on this skin, so the record of WHICH skills a choice talent
              granted was not on the step at all. `board_talents_choice_chosen` draws it
              persistently, so it is drawn persistently: one block per cell whose choice is FILED,
              in tree order, in the board's own markup and in the board's place (after the tree,
              before Reading a Cell).
              Hidden only while that cell's OWN picker is open, which is what the board's two
              picking frames draw — they carry no receipt because the picks there are the picker's
              local selection and nothing is filed yet.
              The count is the CELL's own `choiceCount`, never the step-level `pickCount`: that
              variable is derived from the SELECTED entry, and reading the selection here is exactly
              the bug being removed. */}
          {filedChoices.map(function(e) {
            return (
              <React.Fragment key={'receipt-' + e.cellId}>
                <div className="hp-sechead" style={{ margin: '20px 0 10px' }} data-tal-receipt-head="1" data-tal-receipt-cell={e.cellId}>
                  <h2>Career Skills Added</h2>
                  <span className="hp-cite">{e.choiceReceipt.length + ' of ' + (e.choiceCount || e.choiceReceipt.length)}</span>
                </div>
                <div className="hp-plate" data-pad="data" data-tal-receipt="1">
                  <table className="hp-ledger"><tbody>
                    {e.choiceReceipt.map(function(nm) {
                      return <tr key={nm}><td className="hp-lkey">{nm}</td><td className="hp-lval">career skill</td></tr>;
                    })}
                  </tbody></table>
                </div>
                <div>
                  <span className="hp-none" data-tal-bound="1">
                    {'Bound at purchase. These are on the sheet for good — there is nothing here to change them back.'}</span>
                </div>
              </React.Fragment>
            );
          })}

          <div className="hp-sechead" style={{ margin: '24px 0 12px' }}>
            <h2>Reading a Cell</h2>
            {p.cellCite ? <span className="hp-cite" data-step-cite="talent_cell">{p.cellCite}</span> : null}
          </div>
          <div className="hp-plate" data-pad="read">
            <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
              <b>Passive</b>{' talents always apply; '}<b>active</b>
              {' ones need an action to use, and the head says which, in the colour the book prints its own legend in. A '}
              <b>ranked</b>{' talent can be bought again and each rank stacks, which is why the same name appears more than once in a tree.'}
            </p>
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}

// The line's own book label through the sources index (shared by the Force and motivation sheets).
function fcMotivLineBook(line) {
  var SS = window.SWRPGSources;
  var cur = SS && SS.current && SS.current();
  var meta = cur && cur.index && cur.index.byId && cur.index.byId[line];
  return meta && meta.label ? meta.label : null;
}

// ═══ B-563 · STEP 11 · MOTIVATION — the revision's sheet, transplanted at all three bands (dark) ═══
// Lifted verbatim from revision/creator-2026-09-10 (fcMotivTiers … FCMotivationSheet). The legacy
// FCMotivationStep stays for the visitor's page; the confirm handler is the same on both paths.
function fcMotivTiers(cats, line, rolls) {
  cats = cats || [];
  var book = fcMotivLineBook(line);
  var table = rolls && rolls.lines && rolls.lines[line];
  var bands = table && table.bands ? table.bands : null;
  var byId = {};
  cats.forEach(function(c) { byId[c.id] = c; });
  // B-336 (ADDENDUM 62, (C)): a category belongs to the line's book when one of its sources NAMES that book, in any form —
  // a vocabulary key resolves to the same label a title already is.
  var _SS = window.SWRPGSources, _cur = _SS && _SS.current && _SS.current(), _ix = _cur && _cur.index;
  var _labelOf = function(s) { return (_ix && _SS.sourceLabel) ? _SS.sourceLabel(_ix, s) : s; };
  var trio = book ? cats.filter(function(c) { return (c.sources || []).some(function(s) { return s === book || _labelOf(s) === book; }); }) : [];
  if (bands) {
    var ordered = bands.map(function(b) { return byId[b.category]; }).filter(Boolean);
    var seen = {};
    ordered.forEach(function(c) { seen[c.id] = true; });
    trio = ordered.concat(trio.filter(function(c) { return !seen[c.id]; }));
  }
  var isTrio = {};
  trio.forEach(function(c) { isTrio[c.id] = true; });
  var locations = cats.filter(function(c) { return !isTrio[c.id] && /^Location:/.test(c.name); });
  var supplements = cats.filter(function(c) { return !isTrio[c.id] && !/^Location:/.test(c.name); });
  return {
    book: book, bands: bands, cite: table && table.cite ? table.cite : null,
    tiers: [
      { key: 'trio', label: book || 'Core rulebook', cats: trio },
      { key: 'supp', label: 'Supplements', cats: supplements },
      { key: 'loc', label: 'Locations', cats: locations },
    ].filter(function(t) { return t.cats.length; }),
  };
}
// One d10, a 10 rerolled (Amendment 2: ONE motivation; the book's "roll on two" cell is recorded
// on the data as `ten` and not enacted). Returns the band's category id, or null off-table.
function fcMotivRoll(bands) {
  if (!bands || !bands.length) return null;
  var n;
  do { n = Math.floor(Math.random() * 10) + 1; } while (n === 10);
  var hit = bands.filter(function(b) { return n >= b.min && n <= b.max; })[0];
  return { n: n, category: hit ? hit.category : null };
}
// The book, short, the way the board prints a row's source ("Edge of the Empire", "Nexus of Power").
function fcMotivSrc(rec) {
  // B-336 motivations wave (ADDENDUM 99 C5): the shared resolver answers a key or a title; gameLineTitle keeps the shelf's
  // " Core Rulebook" strip
  var s = (rec && rec.sources || [])[0];
  return s ? gameLineTitle(s) : 'Unsourced';
}

/* `FCMotivationStep` — DELETED at `build-phone-motivation` (`phone-motivation-census` R-PMC-1).
   It was the LEGACY phone step and had exactly ONE caller, the `!fcTransplanted` mount below;
   with `FC_TRANSPLANTED_STEPS.motivation` lit at every band that mount is gone and the component
   with it. Grepped before deleting: 0 remaining references in `player/app.jsx`, `lib/`, `gm/`,
   `scripts/*.py`, `test/*.cjs` and `Docs/NAVIGATION.md` — the one surviving mention is a prose
   line in `scripts/make_motivation_mockup.py`'s docstring describing the FOUR-INPUT step of two
   revisions ago (it cites `app.jsx:4592`), which is history, not a caller.
   WHAT WENT WITH IT, each still reachable on the phone through the restyled sheet: the flat
   32-category grid and the flat specifics grid (RETIRED to the two collapsed pickers), the ROLL
   d10 button (first row inside the Category picker), its `rolled N — <name>` line, the specifics'
   no-roll note, the Write Your Own input, and the FaD emotional-pair box with its EDIT link.
   The two-state commit label (`CONTINUE` / `CONTINUE WITHOUT A MOTIVATION`) is the ONE behaviour
   that does not survive: the frame draws one state and R-PMC-1's table retires the pair.
   `smoke_fc_continue_review.py`'s two asserts over it are re-pointed in this card, each naming
   the ruling. */

/* ══ FCMotivationSheet — CLUSTER TWO'S THIRD TRANSPLANT (the MOTIVATION step) ═════════════════
   MOCKUP (C-MU-2): the lab board `lab/holocron/index.html` @ b818e23f (`board_pin.BOARD_SHA`),
   frames `motivation_step()` ×4 (the ladder), the four states (`cat=none, state=empty` ·
   `state=category` · `state=rolled, roll=42` · `cat=none, state=custom`), the three per-line
   frames (`career_id=jedi` · `cat=bel, career_id=ace` · `cat=fai, career_id=consular`) —
   extracted as `test/fixtures/board_motiv_*.html` at `motivation-step-0-b818e23f`. APPROVED
   under the R-VR-6 substitution and the R-CRS-2 term (the board is the rendered artifact;
   Jason's local walk is the final gate, R-CRS-3), ruled `motivation-census` R-MOT-4/5 + A3 + A6,
   `motivation-step-0-b818e23f` R-MS0-2..4, `motivation-roll-ranges` R-MRR-5.

   THE TREE IS THE BOARD'S (R-TTP-1): runhead · rail (The Build ×4 · So Far ×4 · Categories, the
   tiered picklist, the commit) · head (wave · stephead · lead) · spend pane (the category's
   read, the search plate, the specifics, Your Motivation, Write Your Own). The COPY is the app's
   where the app has approved copy and the board's where the app had none (the empty state's
   plate, the custom state's note). The CITES are ours: book + section from the registry's own
   `description_cite` (R-MOT-2 — the page is appended only when the converter produces it, and
   today it does not), never the board's designer "p.95–96".

   WHAT THE BOARD DOES NOT DRAW, built inside its frame in its idiom (R-MOT-4's instruction),
   each an APP-SIDE ADDITION named in the gate; the first two were DECLARED `data-ruled-delta=
   "motivation-build-1"` (R-MB1-2) until the refresh to lab STABLE 4582d484, where the board draws
   all three (`motivation-build-1` A2 R-MB1-A2-1) — the declarations came off and the pair block
   compares directly:
     · the ROLL control (the lead says "or roll for the category"; the board draws no button) —
       the board's small action button (`hp-buy-btn`, the trade rows' "Take") beside the
       Categories head, absent when the line has no table;
     · the specifics tier's stated absence of a roll (`c15-motivation-roll-has-no-ranges`'s
       verbatim line, an `hp-none`);
     · (the Write Your Own INPUT sits INSIDE the board's prose plate before words exist — an
       unnamed element in the board's own input idiom, so the plate count is the board's);
     · the FaD emotional PAIR, shown never re-asked (C15 commit 3, R-MEP-2), as a ledger.
   The LINE is the character's game line (`ctrl.gameLine`), not the career's book: the board's
   `career_id=jedi` frame derives its tiers from the career and draws no core tier for a
   Rise-of-the-Separatists career; the app draws FaD's trio for a FaD character holding it. */
function FCMotivationSheet(p) {
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal || (p.steps || []).length;
  var cats = p.cats || [], specs = p.specs || [];
  var _c = useState((p.existing && p.existing.category) || null), catId = _c[0], setCatId = _c[1];
  var _s = useState((p.existing && p.existing.specific) || null), specId = _s[0], setSpecId = _s[1];
  var _w = useState((p.existing && p.existing.custom) || ''), custom = _w[0], setCustom = _w[1];
  var _r = useState(null), lastRoll = _r[0], setLastRoll = _r[1];
  var _q = useState(''), specQuery = _q[0], setSpecQuery = _q[1];

  var byId = {};
  cats.forEach(function(c) { byId[c.id] = c; });
  var chosenCat = catId ? byId[catId] : null;
  var derived = fcMotivTiers(cats, p.line, p.rolls);
  var catSpecifics = chosenCat
    ? specs.filter(function(sp) { return sp.category_source_key === chosenCat.name; }) : [];
  var q = String(specQuery || '').trim().toLowerCase();
  var shownSpecifics = q
    ? catSpecifics.filter(function(sp) { return String(sp.name || '').toLowerCase().indexOf(q) >= 0; })
    : catSpecifics;
  var chosenSpec = specId ? specs.filter(function(sp) { return sp.id === specId; })[0] : null;
  var customText = String(custom || '').trim();
  var picked = !!(specId || customText);

  // The FaD pair, resolved through the ONE shared helper (R-MEP-2), shown and never re-asked.
  var pairStrength = null, pairWeakness = null;
  if (p.narrativeMechanics && p.narrativeMechanics.morPairs) {
    var _pair = CharResolve.moralityPair({ mechanic: p.mechanic, moralityLayer: p.moralityLayer },
                                         p.narrativeMechanics.morPairs);
    if (_pair) { pairStrength = _pair.strength; pairWeakness = _pair.weakness || null; }
  }

  function pickCat(id) { setCatId(id); setSpecId(null); setLastRoll(null); setSpecQuery(''); }
  function roll() {
    var r = fcMotivRoll(derived.bands);
    if (!r) return;
    setLastRoll(r.n);
    if (r.category && byId[r.category]) { setCatId(r.category); setSpecId(null); setSpecQuery(''); }
  }
  function confirm() {
    p.onConfirm({ category: catId || null, specific: specId || null, custom: customText || null });
  }

  // The head's cite: the LINE's core book and the section the registry cites for its trio
  // (R-MOT-2's book + section form; no page until the converter produces one).
  var _cite0 = (derived.tiers[0] && derived.tiers[0].cats[0] && derived.tiers[0].cats[0].description_cite) || null;
  var headCite = derived.book
    ? derived.book.replace(/ Core Rulebook$/, '') + (_cite0 && _cite0.section ? ' · ' + _cite0.section : '')
    : '';
  // So Far names the chosen SPECIFIC; written words stay in the spend pane's ledger ("In your own
  // words") and the rail reads "Not yet" — the board's custom frame draws it exactly so.
  var sofarValue = chosenSpec ? chosenSpec.name : null;
  // The board's head draws TWO cite chips (designer placeholders); ours are real: the section the
  // registry cites for the line's trio, and the line's ROLL TABLE from `category_rolls` — book,
  // table and PRINTED page, content-verified at `motivation-roll-ranges` (the one page this step
  // can print today, because it was read and calibrated rather than typed).
  var rollCite = derived.cite
    ? (derived.book || '').replace(/ Core Rulebook$/, '') + ' · ' + derived.cite.table
      + (derived.cite.page ? ' · p.' + derived.cite.page : '')
    : '';
  /* THE ROLL'S LEGEND AT 390, the legacy phone step's own line carried forward: the line's table,
     its book and its PRINTED page (the one page this step can print, calibrated at
     `motivation-roll-ranges`), and the ten's rule in the registry's own words. */
  var rollLegend = (derived.cite
    ? derived.cite.table + ', ' + (derived.book || '').replace(/ Core Rulebook$/, '')
      + (derived.cite.page ? ' p.' + derived.cite.page : '')
    : 'the line’s table')
    + ((p.rolls && p.rolls.ten) ? ' · ' + String(p.rolls.ten) : '');
  var spentEarlier = Math.max(0, (p.xpPool || 0) - (p.xpRemaining || 0));

  /* `phone` DROPS THE SOURCE LINE, and that is the frame's own reading rather than a trim: the
     board's phone picker rows are name-only (`hp-row` > `hp-row-body` > `hp-row-desig`, nothing
     else), and the sourcebook is the DESKTOP rail's object. Only 3 of 32 categories carry a source
     at all, so at 390 it would be a line that appears on three rows and nowhere else. */
  function catRow(c, phone) {
    var on = catId === c.id;
    return (
      <button type="button" className="hp-row" key={c.id} data-row-id={c.id} data-noplate="1"
        data-selected={on ? '1' : undefined} aria-current={on ? 'true' : undefined}
        data-motiv-cat={c.id} aria-pressed={on ? 'true' : 'false'}
        onClick={function() { pickCat(c.id); }}>
        <span className="hp-row-body">
          <span className="hp-row-desig">{c.name}</span>
          {phone ? null : <span className="hp-rowsrc">{fcMotivSrc(c)}</span>}
        </span>
      </button>
    );
  }
  function specRow(sp, phone) {
    var on = specId === sp.id;
    return (
      <button type="button" className="hp-row" key={sp.id} data-row-id={sp.id} data-noplate="1"
        data-selected={on ? '1' : undefined} aria-current={on ? 'true' : undefined}
        data-motiv-spec={sp.id} aria-pressed={on ? 'true' : 'false'}
        onClick={function() { setSpecId(sp.id); setCustom(''); }}>
        <span className="hp-row-body">
          <span className="hp-row-desig">{sp.name}</span>
          {phone ? null : <span className="hp-rowsrc">{fcMotivSrc(sp)}</span>}
        </span>
      </button>
    );
  }


  /* ── THE PHONE BAND (`phone-motivation-census` R-PMC-1) ───────────────────────────────────────
     ONE COMPONENT, TWO BANDS — R-PPG-6's template: the shared component is restyled under the phone
     chrome, never copied into a phone-only one. Everything this branch draws is already computed
     above for the desktop return — `derived`/`chosenCat`/`catSpecifics`/`chosenSpec`/`customText`,
     `pickCat`/`roll`/`confirm`, `headCite`, the FaD pair — so the phone adds an ARRANGEMENT and not
     a second description of the step. `FCMotivationStep`, the legacy phone step, stops being mounted
     once `FC_TRANSPLANTED_STEPS.motivation` gains `mobile: 1`.

     MOCKUP (C-MU-2): `phone_motivation()` on the lab's board at the freeze pin `6051229c` — ONE
     address, byte-identical at `5763dc2c`/`bd6f7767`/`6051229c` and equal to the checked-in fixture
     `test/fixtures/board_motiv_phone.html` (body sha256 `d6ec6a96931b2536`) — approved by Jason
     2026-09-09 on the ten-step sheet `lab/holocron/phone_approval.html` @ `c0744b3a`
     (`parallel-pull-grant` A3, R-PPG-A3-1).

     THE FOUR THINGS THE FRAME DOES NOT DRAW, carried forward exactly where the ruling's KEEP/RETIRE
     table places them (R-PMC-1; each is an APP-SIDE ADDITION, red on the BOARD's account under
     R-PMC-2's frame request and named as such at the gate — never a declaration, which would delete
     the object from the comparison and overstate the divergence):
       · the ROLL d10 control — the FIRST row inside the OPEN Category picker, in the picker's own
         `hp-row` idiom rather than the desktop rail's `hp-buy` trade row, because inside a picklist
         a trade row is a foreign object; `data-motiv-roll="category"` is kept, which is what every
         existing driver and `smoke_board_dom_parity`'s `motiv_rolled` capture press;
       · its `rolled N — <name>` line, under the summary and above the list, present only after a
         roll (so it is absent from the state the frame draws and costs the comparison nothing);
       · the specifics' stated absence of a roll (`c15-motivation-roll-has-no-ranges`, verbatim), an
         `hp-none` inside the Specific picker;
       · Write Your Own, at the FOOT of the Specific picker. A BARE `<input>` with no plate around
         it: the plate would be a named object the board does not draw, and the input itself carries
         no board class, so the control is kept and the tree gains nothing.
     And the FaD emotional pair keeps its own ledger and its edit line BELOW the pickers, unchanged
     from the desktop's shape, for a Force-and-Destiny or dual-track identity.

     THE CATEGORY LIST IS FLAT, IN THE RULED TIER ORDER. The frame draws a flat run of rows with no
     tier head above it, and R-MS0-2 already rules that a register's row SEQUENCE on these frames is
     a sample rather than a claim — so what survives from the legacy step's tiering is the ORDER
     (the line's own trio first, then supplements, then the nine Locations — B-394's repair, read
     from the same `fcMotivTiers` both bands already call) and what does not is the three `hp-maker`
     tier HEADS the desktop draws. They are named in the gate's DECISIONS as a one-line restoration
     rather than dropped silently: the ruling retires the category grid and names REACHABILITY as
     the property to preserve, and every one of the 32 categories is reachable in the ruled order. */
  if (p.band === 'mobile') {
    var phoneCats = derived.tiers.reduce(function(acc, t) { return acc.concat(t.cats); }, []);
    return (
      <FCPhoneShell band={p.band}
        stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
        steps={p.steps} stepLabels={p.stepLabels}
        onBack={p.onBack} onJump={p.onJump}
        status={<React.Fragment>
          <div><span className="hp-status-k">Categories</span>
            <span className="hp-status-v">{cats.length}</span></div>
          <div><span className="hp-status-k">Specifics</span>
            {/* THE CHOSEN CATEGORY'S SPECIFICS, which is what the frame's "18" is (`amb`'s
                `specific_ids.length`, checked against the registry). Before a category is chosen
                there is no such number, and the register's own total (283) would be a different
                fact wearing the same label. */}
            <span className="hp-status-v">{chosenCat ? String(catSpecifics.length) : '–'}</span></div>
        </React.Fragment>}
        act={
          /* ALWAYS AVAILABLE, ONE LABEL. The frame draws one state and the two-state legacy copy
             ("CONTINUE" / "CONTINUE WITHOUT A MOTIVATION") retires with it (R-PMC-1's table).
             CONTINUING WITHOUT A MOTIVATION STILL WORKS — `confirm` writes nulls exactly as it did,
             and the label no longer announces the omission. `smoke_fc_continue_review.py`'s label
             asserts are re-pointed in this card, each naming the ruling. */
          <button type="button" className="hp-commit" data-creator-commit="1" onClick={confirm}>
            {'Continue to Gear '}<span>{'›'}</span>
          </button>
        }>

        {/* The phone's own spacing, read off the fixture: a 14px lift and a 10px gap. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
        </div>
        <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName}</h1>
        <div className="hp-desig-rule" />
        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
          {/* ONE CITE CHIP, and it is THE APP'S. The frame prints "Edge of the Empire p.95–96", a
              designer placeholder this exact step already refused once on the desktop; ours is
              `headCite` — the line's book plus the section `amb.description_cite` carries, with no
              page, because the registry's page is null and the converter has produced none. The
              desktop's SECOND chip (the roll table's printed page) is not drawn here: the board
              draws one chip, and the roll table's cite belongs beside the roll, which on the phone
              lives inside the picker. */}
          <div style={{ marginTop: '10px' }}>
            <span className="hp-cite" data-step-cite="motivation" style={{ marginRight: '7px' }}>{headCite}</span>
          </div>
        </div>

        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
          <h2>{'Category'}</h2><span className="hp-cite">{String(cats.length)}</span></div>
        {/* OPEN UNTIL A CATEGORY IS CHOSEN, FOLDED AFTER — `FCSpeciesSheetPhone`'s rule and
            `build-phone-career`'s precedent; the approved frame is the folded half of it (it draws a
            category chosen and the picker shut). Leaving the whole catalogue behind a tap on first
            arrival is the just-looking moment lost. */}
        <details className="hp-unit" data-fc-phone-picker="category" open={!chosenCat}>
          <summary>
            <div className="hp-head">
              <span className="hp-name">{chosenCat ? chosenCat.name : 'Choose a category'}</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          {/* ONE LINE UNDER THE SUMMARY, TWO STATES — the roll's provenance before a roll (the
              legacy phone step's own line: the line's table, its book and printed page, and the
              ten's rule) and its RESULT after one. Two objects here would be two undrawn objects
              where one carries both facts, and `hp-none` is the board's own quiet-label register. */}
          {derived.bands ? (
            <div style={{ marginTop: '9px' }}>
              <span className="hp-none"
                data-motiv-rolled={lastRoll != null ? lastRoll : undefined}>
                {lastRoll != null
                  ? ('rolled ' + lastRoll + ' — ' + (chosenCat ? chosenCat.name : 'nothing on the table'))
                  : rollLegend}</span>
            </div>
          ) : null}
          <div className="hpw-scroll hp-picklist" style={{ marginTop: '9px' }}>
            <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
              {/* THE ROLL, FIRST IN THE LIST and only while the LINE has a table in the data
                  (R-MRR-2's absent case: no table, no control). The row's second line is the
                  A CATEGORY ROW'S EXACT SHAPE (`hp-row` > `hp-row-body` > `hp-row-desig`), because
                  this frame is a COMPOSITION SPECIMEN: its register collapses to its FIRST row and
                  that row's SHAPE is the one claim the comparison still makes about rows. A roll
                  row carrying a second line would red that claim while reading like a divergence
                  in the list. Its legend lives under the summary instead. */}
              {derived.bands ? (
                <button type="button" className="hp-row" data-noplate="1"
                  data-motiv-roll="category" onClick={roll}>
                  <span className="hp-row-body">
                    <span className="hp-row-desig">
                      {'Roll ' + ((p.rolls && p.rolls.die) || 'd10') + ' for the category'}</span>
                  </span>
                </button>
              ) : null}
              {phoneCats.map(function(c) { return catRow(c, true); })}
            </div>
          </div>
        </details>

        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <table className="hp-ledger"><tbody>
            <tr><td className="hp-lkey">{'Specifics'}</td>
              <td className="hp-lval">{chosenCat ? String(catSpecifics.length) : '–'}</td></tr>
          </tbody></table>
        </div>

        <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
          <h2>{'Specific'}</h2>
          <span className="hp-cite">{String(chosenCat ? catSpecifics.length : 0)}</span></div>
        <details className="hp-unit" data-fc-phone-picker="specific"
          open={!!chosenCat && !chosenSpec && !customText}>
          <summary>
            <div className="hp-head">
              <span className="hp-name">
                {chosenSpec ? chosenSpec.name
                  : (customText ? 'In your own words'
                    : (chosenCat ? 'Choose a specific' : 'Choose a category first'))}</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          <div className="hpw-scroll hp-picklist" style={{ marginTop: '9px' }}>
            <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
              {catSpecifics.map(function(sp) { return specRow(sp, true); })}
            </div>
          </div>
          {/* THE SPECIFICS' ROLL IS DEFERRED, AND THE ABSENCE IS STATED IN THE PLACE IT WOULD SIT
              (`c15-motivation-roll-has-no-ranges`, the desktop's line verbatim). */}
          <div style={{ marginTop: '9px' }}>
            <span className="hp-none" data-motiv-noroll="1">
              {'ROLL uses the book’s printed ranges — they aren’t in the app’s data yet. Choose for now.'}</span>
          </div>
          {/* WRITE YOUR OWN, at the picker's foot. A BARE input: a plate around it would be a named
              object the board does not draw, and the input carries no board class, so the control
              is kept and the compared tree gains nothing. Typing clears the chosen specific, exactly
              as both live paths already do. */}
          <input type="text" value={custom} data-motiv-custom="1" placeholder="Or write your own"
            aria-label="Write your own Motivation"
            onChange={function(e) { setCustom(e.target.value); if (e.target.value.trim()) setSpecId(null); }}
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: '9px', minHeight: '44px',
              border: '1px solid var(--doc-rule)', borderRadius: '6px', background: 'transparent',
              outline: 'none', padding: '10px 12px', fontFamily: 'var(--font-body)',
              fontSize: '13px', color: 'var(--doc-ink)',
            }} />
        </details>

        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }} data-motiv-ledger="1">
          <table className="hp-ledger"><tbody>
            <tr><td className="hp-lkey">{'Category'}</td>
              <td className="hp-lval">{chosenCat ? chosenCat.name : '–'}</td></tr>
            {/* "PRINTED IN" IS THE CHOSEN SPECIFIC'S BOOK, through the app's own `fcMotivSrc` — the
                same helper the desktop rail already uses for a row's source. Net-new as a ROW and
                not as a derivation. */}
            <tr><td className="hp-lkey">{'Printed in'}</td>
              <td className="hp-lval">{chosenSpec ? fcMotivSrc(chosenSpec) : '–'}</td></tr>
          </tbody></table>
        </div>

        {pairStrength ? (
          <React.Fragment>
            <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
              <h2>{'Emotional Strength and Weakness'}</h2>
              <span className="hp-cite">{'chosen on Morality'}</span></div>
            <div className="hp-plate" data-pad="data" data-motiv-pair="1">
              <table className="hp-ledger"><tbody>
                <tr><td className="hp-lkey">{'Strength'}</td><td className="hp-lval">{pairStrength}</td></tr>
                {pairWeakness ? (
                  <tr><td className="hp-lkey">{'Weakness'}</td><td className="hp-lval">{pairWeakness}</td></tr>
                ) : null}
              </tbody></table>
            </div>
            <div>
              <span className="hp-none" data-motiv-pair-edit="1" role={p.onEditMechanic ? 'button' : undefined}
                tabIndex={p.onEditMechanic ? 0 : undefined} onClick={p.onEditMechanic || undefined}
                onKeyDown={p.onEditMechanic ? function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); p.onEditMechanic(); } } : undefined}
                style={p.onEditMechanic ? { cursor: 'pointer' } : undefined}>
                {'Edit on the Morality step.'}</span>
            </div>
          </React.Fragment>
        ) : null}

        {/* SO FAR, folded at the foot — the frame's three rows, which are `p.soFar` exactly. The
            desktop rail appends a MOTIVATION row to the same list; the frame does not draw one, and
            on this screen the chosen motivation is already on screen twice (the picker summaries
            and the ledger above). */}
        <details className="hp-unit" data-fc-phone-sofar="1">
          <summary>
            <div className="hp-head">
              <span className="hp-name">{'So Far'}</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
        </details>
      </FCPhoneShell>
    );
  }

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-motivation-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: the budget (FOUR rows here — the board draws "Spent earlier", which the
             mechanic's rail does not), what is settled, and the CATEGORIES, tiered by the LINE. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool} XP</dd></div>
              <div className="hp-xp"><dt>Spent earlier</dt><dd>{spentEarlier} XP</dd></div>
              <div className="hp-xp"><dt>Spent here</dt><dd>0 XP</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Remaining</dt>
                <dd data-creator-char-purse={p.xpRemaining}>{p.xpRemaining} XP</dd></div>
            </dl>
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).concat([{ k: 'Motivation', v: sofarValue }]).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
            <h2>Categories</h2><span className="hp-cite">{cats.length}</span></div>
          {/* THE ROLL. Present only while the line's table exists in the data (R-MRR-2's absent
              case: no table, no control, never Edge's).
              THE OUTER NOTE THAT STOOD HERE IS GONE (`creator-pre-deploy-batch` A9-2): it called the
              roll "an addition the board does not draw", which the inner note beside it already
              contradicts — the board HAS drawn it since 4582d484 and its fixtures carry the
              `hp-buy` row. A provenance note that says UNDRAWN about a drawn object is how a
              declaration outlives its reason.
              AND THE BLOCK PAYS FOR ITS OWN SPACE (A9-1): it was inserted BETWEEN a `hp-sechead` and
              the content that head introduces, so it consumed the 10px the categories plate used to
              inherit and contributed none of its own — the why-line abutted the plate at ZERO
              separation, which is what Jason saw. The margin goes on the INSERTED element, never on
              `.hpw-scroll`: that is a shared scroll primitive used across the creator and must not
              grow a margin for one caller. */}
          {derived.bands ? (
            <React.Fragment>
              {/* THE ROLL, in the board's trade-row shape now that the board draws it (4582d484,
                  `motivation-build-1` A2): the name, the small action, the WHY line — the line's
                  bands from `category_rolls`, then the ten's rule in the registry's own words. */}
              <div className="hp-buy" data-motiv-rollblock="1">
                <span className="hp-buy-name">{'Roll ' + ((p.rolls && p.rolls.die) || 'd10') + ' for the category'}</span>
                <button type="button" className="hp-buy-btn" data-motiv-roll="category" onClick={roll}>Roll</button>
              </div>
              {/* THE GAP GOES AFTER THE BLOCK'S LAST LINE, not inside it: the cite is optional, so
                  the why-line pays only when it IS the last line. NO WRAPPER ELEMENT — an added node
                  is one the board's tree does not have, and the parity walker would rightly say so. */}
              <div className="hp-why" data-motiv-rollwhy="1" style={{ marginBottom: rollCite ? 0 : '10px' }}>
                {derived.bands.map(function(b) { return (byId[b.category] ? byId[b.category].name : b.category) + ' ' + b.min + '–' + b.max; }).join(' · ')
                  + (p.rolls && p.rolls.ten ? ' · ' + String(p.rolls.ten) : '')}
              </div>
              {rollCite ? <div style={{ marginBottom: '10px' }}><span className="hp-cite" data-step-cite="motivation-roll">{rollCite}</span></div> : null}
            </React.Fragment>
          ) : null}
          <div className="hpw-scroll" data-rail-section="categories">
            <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
              {derived.tiers.map(function(t) {
                return (
                  <React.Fragment key={t.key}>
                    {/* "Core Rulebook" is dropped from the tier's word as every cite on this sheet
                        already drops it (B-603): in a 285px rail "Edge of the Empire Core Rulebook · 3"
                        wrapped its count onto a second line. */}
                    <div className="hp-maker" data-motiv-tier={t.key}>{String(t.label || '').replace(/ Core Rulebook$/, '') + ' · ' + t.cats.length}</div>
                    {t.cats.map(function(c) { return catRow(c, false); })}{/* B-1564: never `map(catRow)` — map's index would land in `phone` */}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="hp-railact" data-rail-section="commit">
            <button type="button" className="hp-commit" data-creator-commit="1" onClick={confirm}>
              {(picked ? 'Continue to Gear ' : 'Continue without a motivation ')}<span>{'›'}</span>
            </button>
          </div>
        </div>

        {/* ── THE HEAD: the board's stephead pair, then the lead in its reading measure. */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div className="hp-stephead">
            <div className="hp-stephead-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                <span className="hp-slash"><i /><i /><i /></span>
                <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
              </div>
              <h1 className="hp-desig">{p.stepName}</h1>
              <div className="hp-desig-rule" />
            </div>
            <div className="hp-plate" data-pad="read"
              style={{ maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              <div style={{ marginTop: '11px' }}>
                <span className="hp-cite" data-step-cite="motivation">{headCite}</span>
                {/* The second chip: the LINE's step page, the one page this step can print today —
                    `category_rolls`' cite page, converter-produced at `motivation-roll-ranges`. The
                    table cite itself now sits under the roll block in the rail, where the board draws it. */}
                {derived.cite && derived.cite.page ? (
                  <span className="hp-cite" style={{ marginLeft: '7px' }} data-step-cite="motivation-page">
                    {(derived.book || '').replace(/ Core Rulebook$/, '') + ' p.' + derived.cite.page}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the category's reading, its specifics, the choice, your own words. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            <div className="hp-spend">
              <div data-motiv-stack="pick">
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}>
                  <h2>{chosenCat ? chosenCat.name : 'No category yet'}</h2>
                  <span className="hp-cite">{chosenCat
                    ? catSpecifics.length + (catSpecifics.length === 1 ? ' specific' : ' specifics')
                    : cats.length + ' to choose from'}</span>
                </div>
                <div className="hp-plate" data-pad="read">
                  <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
                    {chosenCat
                      ? (chosenCat.description || '')
                      : 'Pick a category on the left and its specifics appear here. Nothing is chosen yet, and nothing below is filled in — this is the state you arrive in.'}
                  </p>
                  {chosenCat && chosenCat.description_cite ? (
                    <span className="hp-cite" style={{ display: 'inline-block', marginTop: '9px' }}>
                      {fcMotivSrc(chosenCat) + (chosenCat.description_cite.section ? ' · ' + chosenCat.description_cite.section : '')
                        + (chosenCat.description_cite.page ? ' · p.' + chosenCat.description_cite.page : '')}
                    </span>
                  ) : null}
                </div>
                {chosenCat ? (
                  <React.Fragment>
                    <div className="hp-plate" data-flat="1" data-input="1" style={{ marginTop: '10px' }}>
                      <input type="search" value={specQuery} data-register-search="1"
                        placeholder={'Search ' + chosenCat.name + '…'} aria-label={'Search ' + chosenCat.name + '…'}
                        onChange={function(e) { setSpecQuery(e.target.value); }}
                        style={{
                          width: '100%', border: 0, background: 'transparent', outline: 'none',
                          padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: '12px',
                          letterSpacing: '.12em', color: 'var(--doc-ink)', textTransform: 'uppercase',
                        }} />
                    </div>
                    <div className="hpw-scroll hp-picklist" style={{ marginTop: '8px' }} data-motiv-specifics="1">
                      <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
                        {shownSpecifics.map(specRow)}
                      </div>
                    </div>
                    {/* The specifics' roll is DEFERRED to the ranges lap, and the absence is stated
                        in the place it would sit (`c15-motivation-roll-has-no-ranges`, verbatim). */}
                    <div style={{ marginTop: '9px' }}>
                      <span className="hp-none" data-motiv-noroll="1">
                        {'ROLL uses the book’s printed ranges — they aren’t in the app’s data yet. Choose for now.'}</span>
                    </div>
                  </React.Fragment>
                ) : null}

              </div>
              {/* THE SECOND STACK (B-602): Your Motivation and Write Your Own. The wide sheet keeps them
                  UNDER the picker in the first column (paper.css pins the stack there); the mid band
                  puts them in the second column, which was empty, so the page is a snapshot. */}
              <div data-motiv-stack="own">
                <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Your Motivation</h2></div>
                <div className="hp-plate" data-pad="data" data-motiv-ledger="1">
                  <table className="hp-ledger"><tbody>
                    {customText ? (
                      <tr><td className="hp-lkey">In your own words</td><td className="hp-lval">{customText}</td></tr>
                    ) : (
                      <React.Fragment>
                        <tr><td className="hp-lkey">Category</td>
                          <td className="hp-lval">{chosenCat ? chosenCat.name : '–'}</td></tr>
                        <tr><td className="hp-lkey">Specific</td>
                          <td className="hp-lval">{chosenSpec ? chosenSpec.name : '–'}</td></tr>
                        {lastRoll != null ? (
                          <tr><td className="hp-lkey">Rolled</td>
                            <td className="hp-lval" data-motiv-rolled={lastRoll}>{lastRoll + ' on ' + ((p.rolls && p.rolls.die) || 'd10')}</td></tr>
                        ) : null}
                      </React.Fragment>
                    )}
                  </tbody></table>
                </div>

                {pairStrength ? (
                  <React.Fragment>
                    <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
                      <h2>Emotional Strength and Weakness</h2><span className="hp-cite">chosen on Morality</span></div>
                    <div className="hp-plate" data-pad="data" data-motiv-pair="1">
                      <table className="hp-ledger"><tbody>
                        <tr><td className="hp-lkey">Strength</td><td className="hp-lval">{pairStrength}</td></tr>
                        {pairWeakness ? <tr><td className="hp-lkey">Weakness</td><td className="hp-lval">{pairWeakness}</td></tr> : null}
                      </tbody></table>
                    </div>
                    {/* The board draws this as a quiet line BESIDE the plate (4582d484); it stays the way
                        back to the Morality step — a pressable line, not a button, so the tree is the board's. */}
                    <div>
                      <span className="hp-none" data-motiv-pair-edit="1" role={p.onEditMechanic ? 'button' : undefined}
                        tabIndex={p.onEditMechanic ? 0 : undefined} onClick={p.onEditMechanic || undefined}
                        onKeyDown={p.onEditMechanic ? function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); p.onEditMechanic(); } } : undefined}
                        style={p.onEditMechanic ? { cursor: 'pointer' } : undefined}>
                        {'Edit on the Morality step.'}</span>
                    </div>
                  </React.Fragment>
                ) : null}

                <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Write Your Own</h2></div>
                {/* THE BOARD DRAWS ONE PLATE HERE IN EITHER STATE — prose before words exist, the
                    input plate with the words once they do. The app keeps that count: in the
                    unwritten state the input lives INSIDE the prose plate (an unnamed element, the
                    board's own idiom for inputs), so a player can start writing without the tree
                    gaining an object the board does not draw. */}
                {customText ? (
                  <div className="hp-plate" data-flat="1" data-input="1">
                    <input type="text" value={custom} data-motiv-custom="1" placeholder="In your own words"
                      onChange={function(e) { setCustom(e.target.value); if (e.target.value.trim()) setSpecId(null); }}
                      style={{
                        width: '100%', border: 0, background: 'transparent', outline: 'none',
                        padding: '11px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--doc-ink)',
                      }} />
                  </div>
                ) : (
                  <div className="hp-plate" data-pad="read">
                    <input type="text" value={custom} data-motiv-custom="1" placeholder="In your own words"
                      onChange={function(e) { setCustom(e.target.value); if (e.target.value.trim()) setSpecId(null); }}
                      style={{
                        width: '100%', boxSizing: 'border-box', border: '1px solid var(--doc-rule)', background: 'transparent',
                        outline: 'none', padding: '10px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--doc-ink)',
                      }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}


/* ══ FCForceSheet — CLUSTER THREE'S THIRD TRANSPLANT (the FORCE POWERS step) ═════════════════════
   MOCKUP (C-MU-2): the lab board `lab/holocron/index.html` @ b818e23f (`board_pin.BOARD_SHA`),
   frames `force_step()` ×4 (the desktop ladder under #force, ordinals 0–3) and `phone_force()` —
   extracted as `test/fixtures/board_force_*.html` at force step 0 (16e25204). APPROVED under the
   R-VR-6 substitution and the R-CRS-2 term; ruled `cluster-three-concept` R-C3-2/3/5/7/8,
   `talents-tree-skin` R-TTS-1 (the board skin), `talents-transplant` R-TT-4 ("then FORCE").

   THE TREE IS THE BOARD'S (R-TTP-1): runhead · rail (The Build ×4 · So Far · the POWERS register —
   `hp-row` rows with the power's name, its book and its printed basic cost, the shown power
   `data-selected` · the commit) · head (the `hp-stephead` pair · the lead + TWO per-line cites) ·
   spend pane (`hp-forcelay`: the power's column — sechead "<Power> · N XP" + its description plate,
   sechead "Force Rating" + the `hp-frrow` plate with the rating and its Force dice — beside "What
   the Power Holds", the upgrade tree as the ONE tree component's BOARD SKIN, read-only: upgrades
   are bought in play, not at creation (B-89)).

   ONE REGISTER, THE SHARED LIST: `buildBasicForcePowerList` (B-32's one RAW-cited core — active
   sources, core books first) feeds the rows; `basicForcePowerCost` prices them from the tree's row
   0; `meetsForceRating` refuses a power above the character's rating WITH the reason (B-41).

   WHAT THE BOARD DOES NOT DRAW, built inside its frame in its idiom (R-C3-3), each an APP-SIDE
   ADDITION named UNDRAWN in the gate with a screenshot, none declared (R-TTP-1):
     · the BUY / REFUND action under the power's plate — the board prices the rows and draws no
       control; `hp-buy-btn` "Buy · N XP" / "Refund · +N XP", or the refusal as `hp-none` ("Not
       enough XP" · "Needs Force rating N — yours is M");
     · NO POWER — nothing owned: Spent here 0, the register's first power shown unowned (its basic
       cell unowned), the commit "Continue without Force powers";
     · A SECOND POWER — two owned: Spent here sums both, the register marks both `data-owned`;
     · the pressed upgrade cell's reading (the board's cite says "press a cell to read it" and draws
       no reading) — the talents step's selected block, without an action (nothing is buyable here).
   The board's So Far draws a FOURTH row ("Also · Force Sensitive Exile", a second specialization) —
   a state creation cannot produce (the mechanic lap's precedent); the app draws three. */
/* ── CAREER POWERS ON THE FORCE STEP (F-100 surfacing, ruling `f100-surfacing-creator-concept` R-SCC-2 as
   errata'd, DECISIONS D-229 Q2 (a); approved frame K5, `Docs/mockups/f100-surfacing-creator.html` @ 1e2438f9f) ────
   The free Force power from the career's pair and from the STARTING tree's (primer p.4; D2: a later tree's pair feeds
   only the Mentor list), asked in the species option-group idiom (`fcSpeciesOptionGroups`) — "From the Consular · pick
   1". THE PICKS LIVE IN THE SAVE, not in screen state: they are read back off the Force map's `granted` markers
   (`fcCareerPowerPicks`), so a re-entered creation (sheetToCreatorState carries the markers) shows its answers.
   THE WRITE (R-Y2-1, on paper in R-SCC-2 and the brief §3.1): `forcePowers[id] = {name, desc, cost: 0, basic: true,
   granted: "career" | "bonus" | "both", granted_upgrade?}` — the one power picked in both groups is ONE entry marked
   "both". A TOLD double (two named lists) writes NO `granted_upgrade` (R4); a CHOICE row (Padawan, Master — the data's
   `upgrade_if_owned`, R-M12C-87) whose pick is the career's power opens the "one free upgrade" group, and its answer
   rides as `granted_upgrade` with the key once in `upgrades`. Every entry passes CharResolve.forcePowerGrantProblems
   before the dispatch; a refusal writes nothing. DARK twice: careerPowersOf answers empty with M12's key OFF, and the
   pairs exist only with the reSpecialized fad-v1 switch ON — so with either OFF the section is absent and nothing is
   written (R-M12B-79). */
var FC_CP_INTRO = 'Your career and your starting specialization each give you one Force power free. Pick one from each list.';
var FC_CP_TOLD = 'Picked twice. The primer prints a free upgrade only on the Padawan and Master rows, so this pick gives no upgrade.';
var FC_CP_CHOICE_TOLD = 'Picked twice: one free upgrade';
var FC_CP_BAR = 'Pick your Career Powers to continue';

// The CHOICE row's upgrade group: the power's upgrades "that could normally be purchased" next — row 1's cells linked up
// to the basic power — none a Mastery ("one non-Mastery upgrade", primer p.4). The primer is SILENT on which upgrades
// beyond "non-Mastery"; the tree's own purchase rule (an upgrade links to an owned cell) narrows it to these.
function fcCareerPowerUpgradeOptions(rec) {
  var row = rec && rec.tree && rec.tree[1], seen = {}, out = [];
  ((row && row.cells) || []).forEach(function(c) {
    if (!c || !c.ability || !c.links || !c.links.up || seen[c.ability]) return;
    var ab = (rec.abilities || {})[c.ability] || {}, name = ab.name || c.ability;
    if (/mastery/i.test(name)) return;
    seen[c.ability] = true;
    out.push({ id: c.ability, name: name, cost: c.cost });
  });
  return out;
}

// The groups the creator asks, from the ONE list (CharResolve.careerPowersOf); null when there is nothing to ask.
function fcCareerPowerGroups(character, charbuild, gameLine) {
  var CR = window.CharResolve;
  if (!character || !charbuild || !CR || !CR.careerPowersOf) return null;
  var specs = (character.specializations || []).slice().sort(function(a, b) { return (b.isStarting ? 1 : 0) - (a.isStarting ? 1 : 0); });
  var who = { careerId: (character.career || {}).key, specializationIds: specs.map(function(s) { return s.key; }).filter(Boolean) };
  var cp = CR.careerPowersOf(who, charbuild), groups = [];
  var fps = charbuild.forcePowers || {}, careers = charbuild.careers || {}, specRecs = charbuild.specializations || {};
  var nm = function(id) { return (fps[id] || {}).name || id; };
  if (cp.career.length) {
    var crec = careers[who.careerId] || {};
    groups.push({ name: 'From the ' + (crec.name || (character.career || {}).name || who.careerId), list: 'career',
      options: cp.career.map(function(id) { return { id: id, name: nm(id) }; }) });
  }
  cp.bonus.filter(function(b) { return b.spec === cp.starting; }).forEach(function(b) {
    var sname = (specRecs[b.spec] || {}).name || b.spec;
    if (b.powers) groups.push({ name: 'From ' + sname, list: 'bonus', options: b.powers.map(function(id) { return { id: id, name: nm(id) }; }) });
    else {
      var n = Number(b.choice.count) || 1;
      groups.push({ name: 'From ' + sname, list: 'bonus', choice: b.choice, min_picks: n, max_picks: n,
        options: buildBasicForcePowerList(Object.values(fps), gameLine).map(function(p) { return { id: p.id, name: p.name }; }) });
    }
  });
  return groups.length ? { cp: cp, groups: groups } : null;
}

// The picks, read back off the save's Force map: a group's answer is the power whose marker names that group's list.
function fcCareerPowerPicks(groups, forcePowersMap) {
  var map = forcePowersMap || {}, picks = {};
  (groups || []).forEach(function(g) {
    var want = g.list === 'career' ? { career: true, both: true } : { bonus: true, both: true };
    var ids = (g.options || []).filter(function(o) { var e = map[o.id]; return e && want[e.granted]; }).map(function(o) { return o.id; });
    if (ids.length) picks[groupIdSlug(g.name)] = boundsOf(g).max > 1 ? ids : ids[0];
  });
  return picks;
}

// Everything the section and the commit read, in one answer: the groups (the upgrade group appended when a CHOICE pick
// doubles the career's), the picks, the TOLD / CHOICE double, and whether any group is still owed (the bar).
function fcCareerPowerState(character, charbuild, gameLine) {
  var base = fcCareerPowerGroups(character, charbuild, gameLine);
  if (!base) return null;
  var map = (character && character.forcePowers) || {};
  var groups = base.groups.slice(), picks = fcCareerPowerPicks(groups, map);
  var careerG = groups.filter(function(g) { return g.list === 'career'; })[0];
  var bonusG = groups.filter(function(g) { return g.list === 'bonus'; })[0];
  var careerPick = careerG ? pickedIdsOf(careerG, picks)[0] : null;
  var doubled = (careerPick != null && bonusG && pickedIdsOf(bonusG, picks).indexOf(careerPick) !== -1) ? careerPick : null;
  var told = false, upgrade = null;
  if (doubled != null) {
    if (bonusG.choice && bonusG.choice.upgrade_if_owned) {
      var rec = (charbuild.forcePowers || {})[doubled] || {};
      upgrade = { name: (rec.name || doubled) + ' · one free upgrade', list: 'upgrade', power: doubled,
                  options: fcCareerPowerUpgradeOptions(rec) };
      var e = map[doubled];
      if (e && e.granted_upgrade != null) picks[groupIdSlug(upgrade.name)] = e.granted_upgrade;
    } else told = true;
  }
  var owed = groups.concat(upgrade ? [upgrade] : []).some(function(g) { return fcSpeciesGroupOwed(g, picks); });
  return { groups: groups, upgrade: upgrade, picks: picks, doubled: doubled, told: told, owed: owed };
}

// THE WRITE: the next Force map for a press, or { problems } when CharResolve.forcePowerGrantProblems refuses an entry.
// Every granted entry is rebuilt from the picks (a swap drops the old one); a power bought at its price and then picked
// becomes the free pick (its XP returns — the grant prices it 0).
function fcCareerPowerWrite(st, slug, optId, forcePowersMap, charbuild) {
  var CR = window.CharResolve, map = forcePowersMap || {};
  var all = st.groups.concat(st.upgrade ? [st.upgrade] : []);
  var g = all.filter(function(x) { return groupIdSlug(x.name) === slug; })[0];
  if (!g) return { problems: ['no Career Powers group ' + slug] };
  var picks = Object.assign({}, st.picks);
  if (boundsOf(g).max > 1) {
    var list = pickedIdsOf(g, picks).slice(), at = list.indexOf(optId);
    if (at >= 0) list.splice(at, 1);
    else if (list.length >= boundsOf(g).max) return { problems: [] };      // refused in place (the idiom's cap)
    else list.push(optId);
    picks[slug] = list;
  } else picks[slug] = optId;
  var kinds = {};
  st.groups.forEach(function(x) {
    pickedIdsOf(x, picks).forEach(function(id) { kinds[id] = kinds[id] && kinds[id] !== x.list ? 'both' : x.list; });
  });
  // the upgrade answer survives only while its power is still the doubled CHOICE pick
  var upKey = null;
  if (st.upgrade && kinds[st.upgrade.power] === 'both') upKey = g.list === 'upgrade' ? optId : (picks[groupIdSlug(st.upgrade.name)] || null);
  var next = {};
  Object.keys(map).forEach(function(id) { if (!map[id] || map[id].granted == null) next[id] = map[id]; });
  var fps = (charbuild && charbuild.forcePowers) || {}, problems = [];
  Object.keys(kinds).forEach(function(id) {
    var rec = fps[id] || {};
    var desc = plainTextGlyphless(cleanMarkup(rec.description || '')).replace(/\s+/g, ' ').trim();
    var entry = { name: rec.name || id, desc: desc.slice(0, 220), cost: 0, basic: true, granted: kinds[id] };
    if (kinds[id] === 'both' && upKey != null && st.upgrade && id === st.upgrade.power) { entry.granted_upgrade = upKey; entry.upgrades = [upKey]; }
    (CR && CR.forcePowerGrantProblems ? CR.forcePowerGrantProblems(entry) : []).forEach(function(p) { problems.push(id + ': ' + p); });
    next[id] = entry;
  });
  return problems.length ? { problems: problems } : { next: next };
}

// The section: the sechead, the intro plate, the groups in the species idiom, the TOLD sentence or the CHOICE's upgrade
// group. `onPick(slug, id)` is the creator's write handler.
function FCCareerPowers({ st, skillNames, skillSheetKeys, onPick, refused }) {
  if (!st) return null;
  return (
    <div data-career-powers="1" data-cp-owed={st.owed ? '1' : undefined}>
      <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>Career Powers</h2></div>
      <div className="hp-plate" data-pad="data">
        <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>{FC_CP_INTRO}</p>
        {fcSpeciesOptionGroups({ option_choices: st.groups }, st.picks, skillNames || {}, {}, skillSheetKeys || {}, onPick)}
        {st.told ? <span className="hp-why" data-cp-told="1" style={{ marginTop: '10px', color: 'var(--doc-ink)' }}>{FC_CP_TOLD}</span> : null}
        {st.upgrade ? (
          <React.Fragment>
            <span className="hp-why" data-cp-choice-told="1" style={{ marginTop: '12px', color: 'var(--doc-ink)' }}>{FC_CP_CHOICE_TOLD}</span>
            {fcSpeciesOptionGroups({ option_choices: [st.upgrade] }, st.picks, skillNames || {}, {}, skillSheetKeys || {}, onPick)}
          </React.Fragment>
        ) : null}
        {refused && refused.length
          ? <span className="hp-why" data-standalone="1" data-cp-refused="1" style={{ marginTop: '10px' }}>{'Not saved — ' + refused.join('; ')}</span>
          : null}
      </div>
    </div>
  );
}

/* ── THE FORCE POWER'S ACTION, ONE CORE FOR BOTH BANDS (`phone-force-census` R-PFC-1) ──────────
   Lifted verbatim out of `FCForceSheet` so the phone sheet below calls it rather than carrying a
   second copy of the same five outcomes. The census is explicit that the CAPABILITY is kept while
   the list-row button shape retires, and A1 takes the two refusal spans "verbatim from the desktop
   `force_step`" — verbatim is a promise a copy cannot keep past its first edit, so the two bands
   share the function instead.
   NOTE FOR THE GATE: A1 names TWO refusal frames (`rating`, `needxp`) and this predicate emits
   THREE — `cost` ("No printed cost in the data") is a data-defect state the lab did not draw. It is
   kept because A1 also says to read the reason from the APP'S OWN refusal predicate rather than a
   hardcoded string, and dropping the third would mean a power with no printed cost falls through to
   a BUY button that cannot price it. Flagged, not decided here. */
function fcForcePowerAction(power, owned, frHave, xpRemaining, onBuy, onRefund) {
  if (!power) return null;
  owned = owned || {};
  var cost = basicForcePowerCost(power);
  // F-100 surfacing K5 (UNDRAWN, disclosed): a FREE Career Powers pick is changed in its group, never refunded — the
  // refund below prints the basic price for an entry whose cost is 0, a number the grant never charged.
  if (owned[power.id] && owned[power.id].granted != null) {
    return <span className="hp-why" data-standalone="1" data-force-refused="granted">{'Free — a Career Power. Change it under Career Powers.'}</span>;
  }
  if (owned[power.id]) {
  return <button type="button" className="hp-buy-btn" data-force-refund={power.id}
    onClick={function() { onRefund(power.id); }}>{'Refund · +' + ((owned[power.id] && owned[power.id].cost) || cost || 0) + ' XP'}</button>;
}
  // B-434/A8-2 — THE REASON REGISTER, NOT THE ASIDE INK. These three were `hp-none`
  // (`--doc-ink-muted`, 11px), the class the creator uses for incidental notes like "Career
  // skills cost less" — so the reason a purchase is refused was drawn in the ink reserved for
  // things that do not matter, which is why Jason could not find it. `.hp-why` is the dedicated
  // reason class and `data-standalone="1"` selects its third context (the wash plate, his shape).
  // `data-force-refused` is KEPT: `smoke_force_transplant` reads it, and renaming a consumed
  // hook is a separate act with its own consumer check.
  if (!meetsForceRating(power, frHave)) return <span className="hp-why" data-standalone="1" data-force-refused="rating">{'Needs Force rating ' + minForceRatingOf(power) + ' — yours is ' + frHave + '.'}</span>;
  if (cost == null) return <span className="hp-why" data-standalone="1" data-force-refused="cost">{'No printed cost in the data — cannot be bought.'}</span>;
  if (!(cost != null && cost <= (xpRemaining || 0))) return <span className="hp-why" data-standalone="1" data-force-refused="needxp">{'Not enough XP — ' + cost + ' needed.'}</span>;
  return <button type="button" className="hp-buy-btn" data-force-buy={power.id}
    onClick={function() { onBuy(power.id, power); }}>{'Buy · ' + cost + ' XP'}</button>;
}

/* ── THE FORCE POWERS PHONE SHEET (`phone-force-census` R-PFC-1/2, + Addendum 1) ───────────────
   THE SECOND phone build over the ONE tree component, and it DIFFS AGAINST THE FIRST rather than
   re-deriving it (R-PFC-2): the shell, the board skin, the `headPress` prop and the read/buy badge
   role all land here as `build-phone-talents` and `build-phone-gear` left them.

   WHAT THE FRAME DRAWS, in its order: runhead · wave · rule · status (Spent here · Remaining ·
   Rating) · slash + maker · desig + rule · the lead plate with its cite · "Choose a Power" · the
   PICKER as a `<details>` whose summary names the chosen power · that power's reading · "Force
   Rating" with its die · "What It Holds" · `hp-treescroll` holding the read tree · the legend ·
   "So Far" as a `<details>` · the commit in `hp-phoneact`.

   THE ALWAYS-VISIBLE LIST OF EVERY POWER'S CARD RETIRES (R-PFC-1). Every power is still reachable
   — it moved BEHIND a press, into the picker the board draws — so "same functionality as prior"
   holds; the reading does not vanish, it stops being sixteen cards deep on a phone.

   THE TREE IS READ ROLE AND STAYS READ ROLE. Upgrades are bought in play, not at creation (B-89),
   so its cells draw the board's COST CHIP (`span.hp-tab > span.hp-tab-key`, 15 of them on the
   frame) and nothing here can buy one. A press opens the component's own sliding sheet as READING,
   the transient overlay Jason kept for talents (R-TCB-A1-1) — which is why `headPress` is passed
   and `onInspect` is NOT: `onInspect` is what the wide band uses to route a press into its own
   pane instead.

   THE ACTION AND ITS REFUSALS SIT UNDER THE PICKER, where the power was chosen (Addendum 1). The
   frame draws neither — its one state is a power already owned — so both are carried forward
   through `fcForcePowerAction`, the SAME function the wide band calls. */
function FCForceSheetPhone(p) {
  var powers = p.powers || [], owned = p.owned || {};
  var ownedIds = Object.keys(owned);
  var firstShown = ownedIds.length ? (powers.filter(function(x) { return owned[x.id]; })[0] || powers[0]) : powers[0];
  var _sel = useState(firstShown ? firstShown.id : null), selId = _sel[0], setSelId = _sel[1];
  var power = powers.filter(function(x) { return x.id === selId; })[0] || firstShown || null;
  useEffect(function() { if (!power && firstShown) setSelId(firstShown.id); }, [power, firstShown]);
  var Dice = window.SWRPGDice || null;
  var frHave = Number(p.forceRating) || 0;
  var overspent = (p.xpRemaining || 0) < 0;
  var cpHeld = !!(p.careerPowers && p.careerPowers.owed);   // F-100 surfacing K5: the bar (R-SCC-2)
  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  // THE READING IS THE BASIC ABILITY'S TEXT, the same field the wide band's plate reads and the
  // tree's basic cell already draws — one source (`force-transplant` A3 R-FT-A3-1), not
  // `power.description`, which is the one-line summary and not the register's reading.
  /* THE PHONE READS THE POWER'S SUMMARY, NOT THE BASIC ABILITY'S TEXT — and the two approved frames
     say so themselves. `board_force_1920` draws the ability text in TWO paragraphs ("The user lifts
     and moves an object without touching it…"); `phone_force()` draws ONE, "Moves objects at a
     distance, from small items up to considerable mass with training.", which is `power.description`
     BYTE-FOR-BYTE off the registry. Same class the gear card met and the architect ruled: a frame
     that DRAWS a thing is the authority on that thing (R-MIL-1), where its silence about a control
     is filled from the sibling frame. Reading the wide band's two paragraphs here would put a
     desktop-length passage on a phone that was drawn a sentence. */
  var reading = cleanMarkup(String((power && power.description) || '')).replace(/\s+/g, ' ').trim();
  // THE MODEL IS THE WIDE BAND'S CALL, ARGUMENT FOR ARGUMENT (`basicOwned`, `ownedUpgradeNames: []`,
  // `readOnly`, the board skin). A phone-shaped variant of it would be a second answer to "what does
  // this tree hold", which is the defect PRAXIS §5 names one level down from a second resolver.
  var isOwned = !!(power && owned[power.id]);
  var model = power ? forcePowerToModel({ power: power, ownedUpgradeNames: [], readOnly: true,
                                          skin: 'board', basicOwned: isOwned, cssSized: true }) : null;
  return (
    <FCPhoneShell band={p.band} rootAttrs={{ 'data-force-step': '1' }}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      status={<React.Fragment>
        {/* THE SAME HOOKS THE WIDE BAND'S RAIL CARRIES (`data-force-spent`, `data-creator-char-purse`),
            so `smoke_force_transplant`'s ONE state reader works at every band instead of gaining a
            phone-shaped copy of itself. Parity is unaffected: it compares `data-pad`/`-size`/`-cols`
            and nothing else. */}
        <div><span className="hp-status-k">Spent here</span>
          <span className="hp-status-v" data-force-spent={p.xpSpentOnForcePowers || 0}>{(p.xpSpentOnForcePowers || 0) + ' XP'}</span></div>
        <div><span className="hp-status-k">Remaining</span>
          <span className="hp-status-v" data-over={overspent ? '1' : undefined} data-creator-char-purse={p.xpRemaining || 0}
            style={{ color: overspent ? 'var(--doc-action)' : undefined }}>{(p.xpRemaining || 0) + ' XP'}</span></div>
        <div><span className="hp-status-k">Rating</span>
          <span className="hp-status-v" data-force-rating={frHave}>{frHave}</span></div>
      </React.Fragment>}
      /* THE COMMIT KEEPS ITS TWO WORDINGS (R-PFC-1) — "Continue to <next>" when a power is owned,
         "Continue without Force powers" when none is, derived from the walk exactly as the desktop
         does it (lab E-30). The overspend refusal rides the existing `commit_refused` seam
         (Addendum 1) and takes no chevron. */
      act={<button type="button" className="hp-commit" data-creator-commit="1"
        data-force-commit-refused={overspent ? '1' : undefined}
        data-cp-held={!overspent && cpHeld ? '1' : undefined} aria-disabled={overspent || cpHeld ? 'true' : undefined}
        onClick={overspent || cpHeld ? undefined : p.onConfirm} disabled={overspent || cpHeld}>
        {overspent
          ? 'Overspent by ' + Math.abs(p.xpRemaining || 0) + ' XP'
          : cpHeld ? FC_CP_BAR : <React.Fragment>{(ownedIds.length ? 'Continue to ' + nextLabel : 'Continue without Force powers') + ' '}<span>{'\u203a'}</span></React.Fragment>}
      </button>}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">Character Creation</div>
      </div>
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName || 'Force Powers'}</h1>
      <div className="hp-desig-rule" />
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}>
          {'Buy a power\u2019s '}<b>basic</b>{' form for its listed XP; upgrades come later, each bought separately. Your '}
          <b>Force rating</b>{' is how many Force dice you roll.'}
        </p>
        {p.cite ? (
          <div style={{ marginTop: '10px' }}>
            <span className="hp-cite" data-step-cite="forcepowers" style={{ marginRight: '7px' }}>{p.cite}</span>
          </div>
        ) : null}
      </div>

      {/* F-100 surfacing K5 (R-SCC-2, D-229 Q2 (a)): Career Powers above the picker, as drawn at 390. */}
      <FCCareerPowers st={p.careerPowers} skillNames={p.skillNames} skillSheetKeys={p.skillSheetKeys}
        onPick={p.onCareerPick} refused={p.cpRefused} />

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
        <h2>Choose a Power</h2>
        <span className="hp-cite" data-force-count={powers.length}>{powers.length}</span></div>
      {/* THE PICKER IS THE BOARD'S OWN DISCLOSURE: its summary names the power you are reading, so
          the head doubles as the answer to "which one is this?" and the list is one press away. */}
      <details className="hp-unit" data-force-picker="1">
        <summary>
          <div className="hp-head">
            <span className="hp-name">{power ? power.name : 'Choose a Power'}</span>
            <span className="hp-glyph">{'\u25be'}</span>
          </div>
        </summary>
        <div className="hpw-scroll hp-picklist" style={{ marginTop: '9px' }}>
          <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
            {powers.map(function(x) {
              return (
                <button type="button" className="hp-row" key={x.id} data-noplate="1"
                  data-force-row={x.id} data-owned={owned[x.id] ? '1' : undefined}
                  data-selected={power && x.id === power.id ? '1' : undefined}
                  aria-pressed={power && x.id === power.id ? 'true' : 'false'}
                  onClick={function() { setSelId(x.id); }}>
                  <span className="hp-row-body"><span className="hp-row-desig">{x.name}</span></span>
                </button>
              );
            })}
          </div>
        </div>
      </details>

      {reading ? (
        <div className="hp-plate" data-pad="read" data-force-power={power ? power.id : undefined} style={{ marginTop: '9px' }}>
          <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>{reading}</p>
        </div>
      ) : null}
      {/* UNDRAWN, CARRIED FORWARD (Addendum 1): the action or its reason, under the picker where the
          power was chosen. The same function the wide band calls — never a second copy. */}
      <div style={{ marginTop: '11px' }} data-force-action="1">
        {fcForcePowerAction(power, owned, frHave, p.xpRemaining, p.onBuy, p.onRefund)}
      </div>

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Force Rating</h2></div>
      <div className="hp-plate" data-pad="data">
        <div className="hp-frrow">
          <span className="hp-track-val">{frHave}</span>
          <span className="hp-pool">
            {Dice && Dice.renderPool ? Dice.renderPool({ force: frHave }, { cssSized: true }) : null}
          </span>
        </div>
      </div>

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
        <h2>What It Holds</h2>
        <span className="hp-cite">{'swipe \u2192'}</span></div>
      <div className="hp-treescroll">
        {model ? <DirectionalTree model={model} accent="var(--doc-action)" headPress /> : null}
      </div>
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'\u25be'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <table className="hp-ledger"><tbody>
            {(p.soFar || []).map(function(r) {
              return (
                <tr key={r.k}>
                  <td className="hp-lkey">{r.k}</td>
                  <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
      </details>
    </FCPhoneShell>
  );
}

function FCForceSheet(p) {
  // THE PHONE IS ITS OWN SHEET — see `FCForceSheetPhone`. Same branch `FCCharSheet`, `FCGearSheet`
  // and `FCTalentsSheet` take, and the same reason: the frame gives the phone a status pill, a
  // picker where the wide band has a register, and one column.
  if (p.band === 'mobile') return <FCForceSheetPhone {...p} />;
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1, stepTot = p.stepTotal || (p.steps || []).length;
  var powers = p.powers || [], owned = p.owned || {};
  var ownedIds = Object.keys(owned);
  var firstShown = ownedIds.length ? (powers.filter(function(x) { return owned[x.id]; })[0] || powers[0]) : powers[0];
  var _sel = useState(firstShown ? firstShown.id : null), selId = _sel[0], setSelId = _sel[1];
  var _cell = useState(null), cellId = _cell[0], setCellId = _cell[1];
  var Dice = window.SWRPGDice || null;
  var power = powers.filter(function(x) { return x.id === selId; })[0] || firstShown || null;
  useEffect(function() { if (!power && firstShown) setSelId(firstShown.id); }, [power, firstShown]);

  var cost = power ? basicForcePowerCost(power) : null;
  var isOwned = !!(power && owned[power.id]);
  var frNeed = power ? minForceRatingOf(power) : 0, frHave = Number(p.forceRating) || 0;
  var frMet = !power || meetsForceRating(power, frHave);
  var canAfford = cost != null && cost <= (p.xpRemaining || 0);
  var spentHere = p.xpSpentOnForcePowers || 0;
  var spentEarlier = Math.max(0, (p.xpPool || 0) - (p.xpRemaining || 0) - spentHere);
  var nextStep = (p.steps || [])[(p.stepIndex || 0) + 1];
  var nextLabel = nextStep && p.stepLabels ? p.stepLabels[nextStep] : 'the next step';
  // THE PLATE READS THE BASIC ABILITY'S TEXT — the field the tree's basic cell already draws (one
  // source; `force-transplant` A3 R-FT-A3-1), split at its blank lines into the board's paragraphs;
  // `power.description` (the one-line summary) is not the register's reading of the power.
  var basicKey = power && power.tree && power.tree[0] && power.tree[0].cells[0] ? power.tree[0].cells[0].ability : null;
  var basicText = (basicKey && power.abilities && power.abilities[basicKey] && power.abilities[basicKey].description) || (power && power.description) || '';
  var paras = String(basicText).split(/\n\s*\n/).map(function(t) { return cleanMarkup(t).replace(/\s+/g, ' ').trim(); }).filter(Boolean);

  // The tree: the ONE component's board skin over the shared adapter, read-only (upgrades are not
  // bought at creation, B-89); the basic cell reads OWNED only once the power is bought.
  var model = power ? forcePowerToModel({ power: power, ownedUpgradeNames: [], readOnly: true,
                                          skin: 'board', basicOwned: isOwned, cssSized: true }) : null;
  var rows = (model && model.rows) || [], selCell = null;
  rows.forEach(function(row) { (row.cells || []).forEach(function(c) {
    if (c && !c.filler && c.entry && c.key === cellId) selCell = c; }); });
  useEffect(function() { setCellId(null); }, [selId]);

  function srcLabel(rec) {
    // K4 (b), DECISIONS D-148 (frames F4 @ Docs/mockups/f100-m11-fad.html): a pack's power — reworked or added — is
    // labelled with the pack's own label, the approved tree marker's form ("reSpecialized — Force and Destiny").
    var MODS = window.SWRPGMods, v = MODS && MODS.variantOfRecord && MODS.variantOfRecord(MODS.active(), 'force_powers', rec);
    if (v && v.label) return v.label;
    var s = (rec.sources || [])[0];
    return s ? (fcMotivLineBook(s) || '').replace(/ Core Rulebook$/, '') : '';
  }
  function commit() { p.onConfirm(); }

  function action() { return fcForcePowerAction(power, owned, frHave, p.xpRemaining, p.onBuy, p.onRefund); }

  return (
    <div className="hpw-shell">
    <div className="hp-sheet hp-wide" data-doc-ground="creator" data-band={p.band} data-force-step="1">

      <div className="hpw-bar">
        <div className="hp-runhead">
          <button type="button" onClick={p.onBack} data-creator-back="1"
            aria-label="Back to the previous step"
            style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span>{'‹  Character Creation'}</span>
          </button>
          <span className="hp-folio">{fcFolio(stepNo, stepTot, p.stepName)}</span>
        </div>
      </div>

      <div className="hpw-grid hpw-grid-spend">

        {/* ── THE RAIL: the four-row budget, what is settled, the POWERS register, the commit. */}
        <div className="hpw-index hp-pane" style={{ gridArea: 'rail' }} data-creator-rail="1">
          <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Build</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="experience">
            <dl style={{ margin: 0 }}>
              <div className="hp-xp"><dt>Starting experience</dt><dd>{p.xpPool} XP</dd></div>
              {spentEarlier > 0 ? <div className="hp-xp"><dt>Spent earlier</dt><dd>{spentEarlier} XP</dd></div> : null}
              <div className="hp-xp"><dt>Spent here</dt><dd data-force-spent={spentHere}>{spentHere} XP</dd></div>
              <div className="hp-xp" data-total="1">
                <dt>Remaining</dt>
                <dd data-creator-char-purse={p.xpRemaining}>{p.xpRemaining} XP</dd></div>
            </dl>
          </div>
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}><h2>So Far</h2></div>
          <div className="hp-plate" data-pad="data" data-rail-section="sofar">
            <table className="hp-ledger"><tbody>
              {(p.soFar || []).map(function(r) {
                return (
                  <tr key={r.k}>
                    <td className="hp-lkey">{r.k}</td>
                    <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                  </tr>
                );
              })}
            </tbody></table>
          </div>
          {/* F-100 surfacing K5 (R-SCC-2, D-229 Q2 (a)): Career Powers above the POWERS register, as drawn. */}
          <FCCareerPowers st={p.careerPowers} skillNames={p.skillNames} skillSheetKeys={p.skillSheetKeys}
            onPick={p.onCareerPick} refused={p.cpRefused} />
          <div className="hp-sechead" style={{ margin: '20px 0 10px' }}>
            <h2>Powers</h2><span className="hp-cite">{powers.length + ' · from your enabled books'}</span></div>
          <div className="hpw-scroll" data-rail-section="powers">
            <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
              {powers.map(function(rec) {
                var on = power && rec.id === power.id, c = basicForcePowerCost(rec);
                return (
                  <button type="button" className="hp-row" key={rec.id} data-row-id={rec.id} data-noplate="1"
                    data-selected={on ? '1' : undefined} aria-current={on ? 'true' : undefined}
                    data-force-row={rec.id} data-owned={owned[rec.id] ? '1' : undefined}
                    aria-pressed={on ? 'true' : 'false'}
                    onClick={function() { setSelId(rec.id); }}>
                    <span className="hp-row-body">
                      <span className="hp-row-desig">{rec.name}</span>
                      <span className="hp-rowsrc">{srcLabel(rec)}</span>
                    </span>
                    {/* An OWNED power's tab reads "✦ OWNED" and drops the unit — the board's row at c5bb4836. */}
                    {owned[rec.id]
                      ? <span className="hp-row-tab">{'✦ OWNED'}</span>
                      : <span className="hp-row-tab">{c == null ? '?' : c}<span className="hp-xpu">XP</span></span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="hp-railact" data-rail-section="commit">
            {/* the bar (R-SCC-2): held, in its own words, until every Career Powers group is answered */}
            {p.careerPowers && p.careerPowers.owed
              ? <button type="button" className="hp-commit" data-creator-commit="1" data-cp-held="1" disabled aria-disabled="true">{FC_CP_BAR}</button>
              : <button type="button" className="hp-commit" data-creator-commit="1" onClick={commit}>
                  {(ownedIds.length ? 'Continue to ' + nextLabel + ' ' : 'Continue without Force powers ')}<span>{'›'}</span>
                </button>}
          </div>
        </div>

        {/* ── THE HEAD: the board's stephead pair, then the lead with its two cites. */}
        <div className="hpw-head" style={{ gridArea: 'head', display: 'block' }}>
          {fcWaveNav(p)}
          <div className="hp-stephead">
            <div className="hp-stephead-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                <span className="hp-slash"><i /><i /><i /></span>
                <div className="hp-maker">{fcMakerLine(stepNo, stepTot)}</div>
              </div>
              <h1 className="hp-desig">{p.stepName}</h1>
              <div className="hp-desig-rule" />
            </div>
            <div className="hp-plate" data-pad="read"
              style={{ maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
              <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
                {'Buy a power’s '}<b>basic form</b>{' for its listed XP; expand it later with '}<b>upgrades</b>
                {' (Strength, Magnitude, Duration, Range, Control, Mastery), each bought separately. Using one rolls '}
                <b>Force dice</b>{', and your '}<b>Force rating</b>{' is how many you roll.'}
              </p>
              {p.cite || p.ratingCite ? (
                <div style={{ marginTop: '11px' }}>
                  {p.cite ? <span className="hp-cite" data-step-cite="force_powers">{p.cite}</span> : null}
                  {p.ratingCite ? <span className="hp-cite" data-step-cite="force_rating" style={{ marginLeft: '7px' }}>{p.ratingCite}</span> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* ── THE SPEND PANE: the power beside its tree. */}
        <div className="hpw-read hp-pane" style={{ gridArea: 'spend' }}>
          <div className="hp-spendwrap">
            <div className="hp-forcelay">
              <div className="hp-forcecol">
                <div className="hp-sechead" style={{ margin: '0 0 10px' }}>
                  <h2>{power ? power.name : 'No power'}</h2>
                  {power ? <span className="hp-cite">{cost == null ? '? XP' : cost + ' XP'}</span> : null}
                </div>
                <div className="hp-plate" data-pad="read" data-force-power={power ? power.id : undefined}>
                  {power ? paras.map(function(t, i) {
                    return <p className="hp-prose hp-prose-sm" key={i} style={{ margin: i ? '9px 0 0' : 0 }}>{glyphs(t, 1, CREATOR_GLYPHS)}</p>;
                  }) : <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>{'No Force power is listed for your enabled books.'}</p>}
                  {/* The buy / refund action, or its refusal, under the power's reading — DRAWN by the
                      board since c5bb4836 (`force-transplant` A3), so no longer declared. */}
                  {power ? <div style={{ marginTop: '11px' }} data-force-action="1">{action()}</div> : null}
                </div>
                <div className="hp-sechead" style={{ margin: '16px 0 10px' }}><h2>Force Rating</h2></div>
                <div className="hp-plate" data-pad="data">
                  <div className="hp-frrow">
                    <span className="hp-track-val" data-force-rating={frHave}>{frHave}</span>
                    <span className="hp-pool" data-pool={'f' + frHave}>
                      {Dice ? Dice.renderPool({ force: frHave }, { size: 1, cssSized: true }) : null}
                    </span>
                  </div>
                  <p className="hp-prose hp-prose-sm" style={{ margin: '9px 0 0' }}>
                    {'How many Force dice you roll. Each shows light or dark pips. Light spends freely; dark costs strain and Conflict.'}
                  </p>
                </div>
              </div>
              <div>
                <div className="hp-sechead" style={{ margin: '0 0 11px' }}>
                  <h2>What the Power Holds</h2></div>
                <div className="hp-treescroll">
                  {model ? (
                    <DirectionalTree model={model} accent="var(--doc-action)" headPressWhereNoBadge
                      onInspect={function(e) { setCellId(e && e.cellKey ? e.cellKey : null); }} />
                  ) : null}
                </div>
                {/* THE PRESSED CELL'S READING (`board_force_cell`); no action, upgrades are bought in play. B-528: reached by the head press above (`headPressWhereNoBadge`). */}
                {selCell ? (
                  <React.Fragment>
                    <div className="hp-sechead" style={{ margin: '20px 0 10px' }} data-force-cell={selCell.key}>
                      <h2>{selCell.entry.name}</h2>
                      <button type="button" className="hp-quiet" data-force-cell-close="1" aria-label="Close the cell"
                        onClick={function() { setCellId(null); }}>{'Close'}</button>
                    </div>
                    <div className="hp-plate" data-pad="read" data-force-cell-detail="1">
                      <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>{glyphs(cleanMarkup(selCell.entry.desc || ''), 1, CREATOR_GLYPHS)}</p>
                    </div>
                  </React.Fragment>
                ) : null}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    </div>
  );
}

function fcStepCiteStrings(cites, step, gameLine) {
  var row = cites && cites[step], cite = row && row[gameLine];
  if (!cite) return [];
  var pages = cite.printed || [];   // B-1349: the converter's PRINTED pages (creator_step_cites.json)
  return pages.map(function(pg, i) {
    // The board prints the book once and then bare page numbers — the printed convention for a
    // second reference to the same book, and it is the board's, not an abbreviation of ours.
    return (i === 0 ? cite.book + ' p.' : 'p.') + pg;
  });
}

// ═══ B-563 · THE BROWSE CLUSTER — the revision's specialization functions (verbatim) ════════════
/* A SPECIALIZATION'S DESCRIPTION, OR '' WHEN IT HAS NONE (R-M12B-176, D-210). The one record test
   every site that draws the description reads: an empty description draws no box and no paragraph.
   Eight reSpecialized trees ship an empty description in every pack; no book tree does. */
function fcSpecDescText(spec) {
  return String((spec && spec.description) || '').trim();
}
/* B-1002 (D-154; f100-triage-census-card ADDENDUM 8 Q3 — SHOW): THE DESCRIPTION AS DRAWN, with the line breaks its record
   carries. The reSpecialized packs ship the authors' own notes ("At a Glance", "Media Touchstones", …) as headings on
   their own lines; drawn as one run of prose they read as a misprint. `pre-line` keeps the breaks and still wraps; a book
   description carries no newline (0 of 137), so it draws as before. Every site that draws the description reads this. */
function fcSpecDescNode(spec, opts) {
  return <span style={{ whiteSpace: 'pre-line' }}>{glyphs(cleanMarkup(fcSpecDescText(spec)).trim(), null, opts)}</span>;
}

function fcSpecAsPrinted(spec) {
  var note = (spec || {}).restriction_note;
  if (!note) return null;
  return (
    <div className="hp-plate" data-pad="read" style={{ marginTop: '11px' }}>
      <div className="hp-maker" style={{ marginBottom: '6px' }}>As printed</div>
      <p className="hp-prose hp-prose-sm" style={{ margin: 0, color: 'var(--doc-ink-muted)' }}>
        {note}
      </p>
    </div>
  );
}

/* THE TWO FACTS THE CORNER TAB CANNOT CARRY (board `spec_marks` / `marks_html`). The tab already
 * accounts for the skill COUNT, so these never repeat it: a spec is UNIVERSAL, or it grants a FORCE
 * RATING, or neither. Marks in the cite register — state is a mark, not a new colour.
 *
 * BOTH DERIVED FROM THE REGISTRY, NEVER TYPED, and the second one is the trap. FORCE RATING comes
 * from the `force_rating` FIELD (4 of 137 carry it), NOT from the talent tree: 42 specs have a Force
 * Rating talent somewhere in their tree, and the two facts are different — the field means the spec
 * HANDS you a rating for taking it, the tree means you may BUY ranks inside it. A tree-derived mark
 * would print FORCE RATING 1 on 38 specs that grant none. This is the same derivation production
 * already ships (`Number(spec.force_rating) >= 1`), which is what the board cites as correct.
 *
 * Returns null when a spec carries neither, so nothing on the page announces an absence — 116 of
 * 137 are simply quiet here. DECLARED, THEN RETIRED (R-WUS-2 -> R-WUS-A2-1): the lab drew
 * `spec_step(... force_sensitive_exile)` at pin 881037eb — Exile is the named force-carrier
 * specimen, and Retired Clone Trooper is `force_rating: None` and draws UNIVERSAL alone. Both
 * marks are now COMPARED by parity.
 *
 * WHICH marks, and now ONLY which — the SET, not a placement. The layout pass moved the marks onto
 * the IDENTITY head as plain cites beside the book (§73.1), so the renderer that drew them as their
 * own row between the skills head and its plate (`fcSpecMarks`) had no consumer left and is gone
 * rather than kept warm for a screen nobody has drawn: the phone does not reach this step at all
 * (`FC_TRANSPLANTED_STEPS.specialization` is `{mid:1, wide:1}`), and when it does, its renderer is
 * written against the frame the lab draws for it, from this list. The derivation is the part that
 * is RAW, and it stays in one place. */

function fcSpecMarkList(spec) {
  var marks = [];
  if (spec && spec.universal) marks.push('UNIVERSAL');
  var fr = Number((spec || {}).force_rating);
  if (fr >= 1) marks.push('FORCE RATING ' + fr);
  return marks;
}

/* `phone` — the same block at 390, and the ONLY difference is the head's margins (20/9 against the
   desktop's 22/11), read off `phone_career()`. The skills plate and its rows are identical at both
   bands, which is the point: one builder, one tree, two margins. */

function fcSpecWhy(gate) {
  if (!gate) return null;
  if (gate.frRefused) {
    return 'Needs Force rating ' + gate.needFR + ' \u00b7 you have ' + (Number(gate.frRating) || 0);
  }
  return null;
}

/* (2) THE SPECIES SWITCH — the So-Far idiom: sechead + read plate + its action row. A rail plate,
   because the requirement is a fact about the CHARACTER, which is what that column states. */

function fcSpecSwitchPlate(sw, onSwitch) {
  if (!sw) return null;
  return (
    <React.Fragment>
      <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
        <h2>Species Requirement</h2>
        <span className="hp-cite">{sw.name}</span>
      </div>
      <div className="hp-plate" data-pad="read">
        <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>
          {'This specialization is only open to a ' + sw.name
            + '. Taking it changes your species, and your characteristics change with it.'}
        </p>
        <div className="hp-railact" data-rail-section="switch" style={{ marginTop: '11px' }}>
          <button type="button" className="hp-commit" onClick={onSwitch}>
            {/* \u203a, the board's commit chevron (`&rsaquo;`), not \u2019 the apostrophe. This door
                read "Switch to Clone \u2019" on screen — a typo against the board's own vocabulary,
                which the other four `hp-commit` labels in this file already spell correctly. */}
            {'Switch to ' + sw.name + ' \u203a'}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

/* (3) THE SKILL-CHOICE POOLS — read-pane plates of `hp-row` pickers. The board already has a row
   that means "one of these, pick it": the register row. B-40/B-36's SAVE SHAPE is untouched — the
   picks are the same map the legacy detail kept, only drawn in the board's vocabulary. */

function fcSpecPoolPlates(pools, picks, onPick) {
  if (!pools || !pools.length) return null;
  return pools.map(function(pool) {
    var taken = picks[pool.id] || [];
    return (
      <React.Fragment key={pool.id}>
        <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
          <h2>{String(pool.id).replace(/_/g, ' ')}</h2>
          <span className="hp-cite">{'pick ' + pool.count}</span>
        </div>
        <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
          {pool.options.map(function(opt) {
            var on = taken.indexOf(opt.id) >= 0;
            var full = taken.length >= pool.count && !on;
            return (
              <button type="button" className="hp-row" data-noplate="1" key={opt.id}
                data-selected={on ? '1' : undefined}
                data-limit={full ? '1' : undefined}
                aria-pressed={on ? 'true' : 'false'}
                onClick={full ? undefined : function() { onPick(pool.id, opt.id, pool.count); }}>
                <span className="hp-row-body">
                  <span className="hp-row-desig">{opt.name}</span>
                  {full ? <span className="hp-why">{'pick ' + pool.count + ' \u2014 change one to swap'}</span> : null}
                </span>
                <span className="hp-row-tab">{on ? '\u2713' : ''}</span>
              </button>
            );
          })}
        </div>
      </React.Fragment>
    );
  });
}

/* (4) THE SHORT PATH — the BUYOUT SPECIMEN'S pattern: a read plate carrying the printed offer,
   with `hp-commit hp-door` as its door. That is the board's own vocabulary for an optional priced
   purchase, which is exactly what this is, so the offer stops being a bespoke card.
   THE TESTIDS ARE PRESERVED — `short-path-offer`, `short-path-take`, `short-path-taken`,
   `short-path-undo` — because `smoke_short_path` drives on them and R-SPP's asserts are law, not
   decoration. The composition moved; the contract did not. */

function fcSpecShortPath(offered, taken, xpPool, xpRemaining, onTake, onUndo) {
  var afford = (Number(xpRemaining) || 0) >= SHORT_PATH_XP;
  if (offered) {
    return (
      <React.Fragment>
        <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
          <h2>The Short Path to Power</h2>
          <span className="hp-cite">{SHORT_PATH_XP + ' XP'}</span>
        </div>
        <div className="hp-plate" data-pad="read" data-testid="short-path-offer">
          <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>
            {'Your training was cut short by the war. Spend ' + SHORT_PATH_XP + ' XP now for a '
              + 'rank of the Force Rating talent, starting you at Force rating 2. Your GM must '
              + 'allow this option.'}
          </p>
          <p className="hp-prose hp-prose-sm" style={{ margin: '9px 0 0', maxWidth: 'none' }}>
            {'Costs ' + SHORT_PATH_XP + ' of your ' + (Number(xpPool) || 0) + ' XP \u00b7 '
              + Math.max(0, (Number(xpRemaining) || 0) - SHORT_PATH_XP) + ' left to build with.'}
          </p>
          <button type="button" className="hp-commit hp-door" data-testid="short-path-take"
            disabled={!afford} onClick={afford ? onTake : undefined}>
            {afford ? ('Take the short path \u2014 ' + SHORT_PATH_XP + ' XP')
                    : ('Needs ' + SHORT_PATH_XP + ' XP')}
          </button>
        </div>
      </React.Fragment>
    );
  }
  if (taken) {
    return (
      <React.Fragment>
        <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
          <h2>Force Rating 2</h2>
          <span className="hp-cite">{'short path \u00b7 ' + SHORT_PATH_XP + ' XP'}</span>
        </div>
        <div className="hp-plate" data-pad="read" data-testid="short-path-taken">
          <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>
            {'Bought at this step. Undo it and the XP comes back.'}
          </p>
          <button type="button" className="hp-commit hp-door" data-testid="short-path-undo"
            onClick={onUndo}>{'Undo the short path'}</button>
        </div>
      </React.Fragment>
    );
  }
  return null;
}

/* (4b) THE SHORT PATH IN THE PHONE FRAME'S IDIOM (`phone_short_path()` @ lab 10ff0155, approved by
   Jason 2026-09-09 — `catchup-pin-approval` R-CPA-1). Same offer, same RAW, same four testids; the
   board draws it as an `hp-offer` block INSIDE the card that refused, with the two figures as an
   `hp-ledger` and the GM's permission as an `hp-warn` rather than as a third sentence. It is a
   SECOND PRESENTATION of one offer, never a second rule: the price, the affordability test and the
   take/undo handlers are `fcSpecShortPath`'s own arguments, passed through unchanged, so the two
   cannot come to disagree about what the offer costs or whether it can be taken.
   THE TESTIDS ARE PRESERVED — `short-path-offer`, `short-path-take`, `short-path-taken`,
   `short-path-undo` — because `smoke_short_path` drives on them and R-SPP's asserts are law. */

function fcSpecShortPathPhone(offered, taken, xpPool, xpRemaining, onTake, onUndo) {
  var afford = (Number(xpRemaining) || 0) >= SHORT_PATH_XP;
  if (offered) {
    return (
      <div className="hp-offer" data-testid="short-path-offer" style={{ marginTop: '11px' }}>
        <div className="hp-maker" style={{ marginBottom: '7px' }}>The Short Path to Power</div>
        <p className="hp-prose hp-prose-sm" style={{ margin: '0 0 10px' }}>
          {'Your training was cut short by the war. Spend '}<b>{SHORT_PATH_XP + ' XP'}</b>
          {' now for a rank of the Force Rating talent, starting you at '}<b>Force rating 2</b>{'.'}
        </p>
        <table className="hp-ledger" style={{ marginBottom: '10px' }}><tbody>
          <tr><td className="hp-lkey">Costs</td>
            <td className="hp-lval">{SHORT_PATH_XP + ' of your ' + (Number(xpPool) || 0) + ' XP'}</td></tr>
          <tr><td className="hp-lkey">Left to build with</td>
            <td className="hp-lval">{Math.max(0, (Number(xpRemaining) || 0) - SHORT_PATH_XP)}</td></tr>
        </tbody></table>
        <p className="hp-warn">Your GM must allow this option.</p>
        <button type="button" className="hp-commit hp-door" data-testid="short-path-take"
          disabled={!afford} onClick={afford ? onTake : undefined}>
          {afford ? 'Take the Short Path' : ('Needs ' + SHORT_PATH_XP + ' XP')}
        </button>
      </div>
    );
  }
  if (taken) {
    return (
      <div className="hp-offer" data-testid="short-path-taken" style={{ marginTop: '11px' }}>
        <div className="hp-maker" style={{ marginBottom: '7px' }}>{'Force Rating 2'}</div>
        <p className="hp-prose hp-prose-sm" style={{ margin: '0 0 10px' }}>
          {'Bought at this step \u2014 ' + SHORT_PATH_XP + ' XP. Undo it and the XP comes back.'}
        </p>
        <button type="button" className="hp-commit hp-door" data-testid="short-path-undo"
          onClick={onUndo}>{'Undo the short path'}</button>
      </div>
    );
  }
  return null;
}

/* (4c) THE UNPICKED SCREEN'S SPECIALIZATION CARD (`build-phone-spec` D1(a), increment C-1; the
   approved frame `phone_short_path()` @ lab 02f95b78, `catchup-pin-approval` R-CPA-1).

   THE STEP HAS TWO ARRANGEMENTS, not one screen with a hole in it: UNPICKED is this card list over
   a Force Rating ledger, PICKED is the picker composition. That is what the two approved frames
   draw, and an approved frame is law for what it draws (R-MIL-1). The switch is whether a
   specialization is selected, and nothing else.

   THE CARD IS A CONTROL, and the board's silence is not the authority for leaving it inert. The
   drawing gives the unpicked screen a commit reading "Choose a Specialization" and four cards to
   choose from; a card you cannot press is the dead-control shape, and the board's markup carries no
   aria anywhere — the species register settled that class already (C-MU-4: the a11y floor outranks
   a mockup's silence). So the affordance rides the object the board DOES draw: `div.hp-plate` keeps
   its element and its class and gains role, tabindex, an aria-label and a press. Parity compares the
   element, the class and the nesting; none of them moves.

   A BLOCKED CARD IS STILL PRESSABLE — R-CSC-1's own rule on the register one arrangement over: a
   choice you cannot make yet is information, so it keeps its place, its name, its figure and its
   reason, and pressing it opens the reading. The COMMIT is what holds. */
/* THE FRAME SPELLS ITS SMALL NUMBER ("Two of these four"), which is the printed convention the
   board uses throughout; above ten it prints the figure. One place, because the lead uses it twice. */

function fcSpecCount(n) {
  var w = ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
  return (n >= 1 && n <= 10) ? w[n] : String(n);
}

function fcSpecCard(row, gate, forceRating, offer, onPick) {
  var refused = !!row.refused;
  var need = row.needFR || 0;
  return (
    <div className="hp-plate" data-pad="read" key={row.id} data-spec-card={row.id}
      data-refused={refused ? '1' : undefined}
      role="button" tabIndex={0}
      aria-label={(refused ? 'Blocked: ' : 'Choose ') + row.name}
      style={{ marginBottom: '14px', cursor: 'pointer' }}
      onClick={function() { onPick(row.id); }}
      onKeyDown={function(e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(row.id); }
      }}>
      <div className="hp-sechead" style={{ margin: '0 0 9px' }}>
        <h2>{row.name}</h2>
        <span className="hp-cite">{refused ? 'Blocked' : 'Open'}</span>
      </div>
      {/* THE FIGURE AND THE NAMES ARE THE REGISTRY'S, never typed: the same `career_skills` the
          picked arrangement's chips read, printed as the board's `hp-namerun` with `hp-sep`
          between. A specialization that grants none says so rather than drawing an empty run. */}
      <div className="hp-maker" style={{ marginBottom: '6px' }}>
        {row.skillNames.length
          ? ('Adds ' + row.skillNames.length + ' career skill'
             + (row.skillNames.length === 1 ? '' : 's'))
          : 'Adds no career skills'}
      </div>
      {row.skillNames.length ? (
        <div className="hp-namerun">
          {row.skillNames.map(function(n, i) {
            return (
              <React.Fragment key={n}>
                {i ? <span className="hp-sep">{'\u00b7'}</span> : null}
                {(i ? ' ' : '') + n + (i < row.skillNames.length - 1 ? ' ' : '')}
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
      {refused && need ? (
        <div className="hp-req" data-short="1">
          {'Requires Force rating ' + need}
          <span className="hp-sep">{'\u00b7'}</span>
          {'you have ' + (Number(forceRating) || 0)}
        </div>
      ) : null}
      {/* THE OFFER SITS ON THE CARD THAT REFUSED, exactly as it sits in the plate that refuses on
          the other arrangement (R-SPP-1). Its own press must not also choose the card under it. */}
      {offer ? (
        <div onClick={function(e) { e.stopPropagation(); }}
          onKeyDown={function(e) { e.stopPropagation(); }}>{offer}</div>
      ) : null}
    </div>
  );
}

/* (5) THE SKILL-LESS UNIVERSAL — the "states with nothing in them" idiom, whose rule the board
   states itself: none of them apologises for being empty; each says what the space is for. */

function fcSpecSkillLessPlate(marks, phone) {
  return (
    <React.Fragment>
      <div className="hp-sechead"
        style={{ margin: phone ? '20px 0 9px' : '0 0 11px' }}>
        <h2>Bonus Career Skills</h2>
        <span className="hp-cite">none</span>
      </div>
      {/* THE MARKS STILL TAKE THEIR OWN ROW AT 390 — the same slot `fcReadSkills` gives them, on
          the branch that replaces it. `force_sensitive_exile` is BOTH the named force-carrier
          specimen and the skill-less one, so a marks row hung only on the other branch would draw
          on no reachable spec at all: the note this function has always carried, now executable. */}
      {(marks && marks.length) ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', margin: '0 0 9px' }}>
          {marks.map(function(m) { return <span className="hp-cite" key={m}>{m}</span>; })}
        </div>
      ) : null}
      {/* THE MARKS USED TO BE REPEATED HERE, and the reason was real: `force_sensitive_exile` is
          BOTH the named force-carrier specimen (UNIVERSAL + FORCE RATING 1, R-WUS-A1-1) and
          skill-less, so a marks row hung only on `fcReadSkills` drew on no reachable spec at all.
          The layout pass answers that more completely than the repeat did — the marks are on the
          IDENTITY head, which every spec renders whether it grants skills or not, so there is no
          branch left for them to fall through. Kept as a note rather than deleted silently,
          because the bug it prevented is the kind that comes back. */}
      <div className="hp-plate" data-pad="read">
        <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>
          {'This one adds no skills. What it opens is its tree \u2014 and, for a universal '
            + 'specialization, a Force rating you would otherwise have to buy.'}
        </p>
      </div>
    </React.Fragment>
  );
}

/* WHAT THE TREE HOLDS — the board's talent PREVIEW, and it is a preview on purpose.
   It draws every node the specialization opens and offers none of them: no node is a button, and
   the head's cite says where the buying happens ("bought at step 8"). That is the C5 distinction
   the career step already keeps — tell what it opens, do not offer it — carried one step along.
   `data-down` is the record's own link, `data-state` the talent's activation. */

function FCSpecSheetPhone(p) {
  var sel = p.selected, gate = p.gate;
  var selDesc = fcSpecDescText(sel);
  var selShortPath = gate ? fcSpecShortPathPhone(gate.shortPath, p.shortPathTaken,
    p.xpPool, p.xpRemaining, p.onShortPathTake, p.onShortPathUndo) : null;
  var stepNo = (p.stepIndex == null) ? null : p.stepIndex + 1;
  /* THE PICKER'S LIST, AND ITS COUNT IS WHAT IT LISTS (R-PSpC-A1-1, R-FMC-4). The career's own
     specializations, plus the universal actually taken — which is why the two mark frames cite 7
     over a six-specialization career and the default frame cites 6. A cite of 6 on those frames
     would be false to the render, and no seat corrects an approved frame.
     THE ROWS DROP THEIR TAB. `fcSpecRows` carries `count`/`unit` for the desktop rail, where the
     board draws a skill figure on every row; the phone frames draw a bare name and `fcRegisterRow`
     already gates the tab on `unit`, so the figure is dropped by not passing it rather than by a
     second row renderer. */
  var bare = function(r) {
    return { id: r.id, name: r.name, refused: r.refused, why: r.why };
  };
  var pickRows = p.rows.map(bare);
  if (sel && sel.universal && !pickRows.some(function(r) { return r.id === sel.id; })) {
    pickRows.push({ id: sel.id, name: sel.name });
  }
  var shelfRows = p.shelfRows.filter(function(r) { return !(sel && r.id === sel.id); }).map(bare);
  var marks = sel ? fcSpecMarkList(sel) : [];
  var frNow = Number(p.forceRating) || 0;
  var frBase = Number(p.forceRatingBase) || 0;
  /* THE UNPICKED ARRANGEMENT'S CARDS are the career's own rows, already carrying the refusal rule
     and the registry's skill names from the mount — the SAME `fcSpecRows.map(fcSpecRefusal)` the
     picker reads, decorated once at the call site rather than resolved twice here. */
  /* THE CARD LIST SORTS BY REQUIRED FORCE RATING, THEN NAME (`build-phone-spec` A4,
     R-BPSp-A4-1), and the key is the whole of the ruling. The approved frame draws Padawan ·
     General · Knight · Master, which the build first read as "open first, then blocked
     alphabetically" — a STATE key, and taking the Short Path would then reshuffle the four cards
     under the reader's thumb, which is exactly the movement R-CSC-1 forbids ("a refused row keeps
     its place"). Read as the REQUIREMENT each card carries — 1 · 2 · 2 · 3, then name — the key
     produces the frame's order exactly AND never moves when the rating rises. Same sequence, and
     only one of the two readings is stable, so the app takes that one.
     THE PICKER'S ROWS ARE UNTOUCHED: they are the career's list in the registry's own order, which
     every other rail in the creator draws, and the ruling scopes this to the card list only. */
  var cards = p.rows.slice().sort(function(a, b) {
    return ((a.needFR || 0) - (b.needFR || 0)) || String(a.name).localeCompare(String(b.name));
  });
  var blocked = cards.filter(function(r) { return r.refused; }).length;
  /* `data-spec-picker` NAMES WHICH UNIT THIS IS, and it is a suite hook (R-TTP-1's allowed class),
     not decoration. The step draws two `details.hp-unit` on the picked arrangement — the career's
     picker and the declared universal shelf — and a driver that reaches for "the first one" reads
     whichever the arrangement happens to put first. It already did: a probe written against the
     single-arrangement screen read the SHELF's summary as the picker's the moment C-1 gave the
     unpicked state cards instead of a picker. Same convention as the career sheet's own
     `data-fc-phone-picker`. */
  var unit = function(title, rows, isOpen, delta, foot, which) {
    return (
      <details className="hp-unit" open={isOpen || undefined} data-ruled-delta={delta}
        data-spec-picker={which}>
        <summary>
          <div className="hp-head">
            <span className="hp-name">{title}</span>
            <span className="hp-glyph">{'▾'}</span>
          </div>
        </summary>
        <div className="hpw-scroll hp-picklist" style={{ marginTop: '9px' }}>
          <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
            {rows.map(function(r) { return fcRegisterRow(r, sel, p.onPick); })}
          </div>
          {foot || null}
        </div>
      </details>
    );
  };
  return (
    <FCPhoneShell band={p.band} rootAttrs={{ 'data-spec-step': '1' }}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      /* THE PILL IS THE RAIL, FOLDED — the desktop rail's Starting / Spent here / Remaining becomes
         the two figures this step actually moves: the purse it starts from, and how many
         specializations are on offer. Nothing is spent here, so a "Spent" cell would read 0 XP on
         every character. */
      /* THE SECOND CELL FOLLOWS THE ARRANGEMENT. The picked screen counts what the picker lists;
         the UNPICKED frame draws the reader's own FORCE RATING, which is the fact its whole screen
         turns on — two of these four are closed until it rises. Derived, never typed: a character
         with no rating has nothing to state there and gets the count back. */
      status={<React.Fragment>
        <div><span className="hp-status-k">Starting</span>
          <span className="hp-status-v">{p.xpPool + ' XP'}</span></div>
        {(!sel && frNow >= 1) ? (
          <div><span className="hp-status-k">Rating</span>
            <span className="hp-status-v" data-spec-rating={frNow}>{frNow}</span></div>
        ) : (
          <div><span className="hp-status-k">Options</span>
            <span className="hp-status-v" data-spec-options={pickRows.length}>{pickRows.length}</span></div>
        )}
      </React.Fragment>}
      /* THE COMMIT NAMES WHAT IT TAKES (R-PTC-1's pattern, the frame's own words) and it carries the
         gates: a refused spec, an unmet choice pool and a required species switch each hold the
         door, exactly as the desktop's does — so no legal character becomes reachable that was not
         before, and none becomes unreachable that was. */
      act={<button type="button" className="hp-commit" data-creator-commit="1"
        disabled={!!p.commitDisabled} onClick={p.onConfirm}>
        {(sel ? ('Select ' + sel.name + ' ') : 'Choose a Specialization ')}
        <span>{'›'}</span></button>}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">{fcMakerLine(stepNo, p.stepTotal)}</div>
      </div>
      {/* 27px, INLINE, as every phone sheet before this one carries it. The base rule is
          `clamp(30px, 7.5vw, 46px)`, which floors at 30 on a 390 viewport — 3px over what the
          board draws, and a real divergence at the style layer rather than an eyeballed one. */}
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>{p.stepName}</h1>
      <div className="hp-desig-rule" />

      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        {/* THE LEAD FOLLOWS THE ARRANGEMENT TOO, and the refusal sentence is DERIVED rather than
            copied: the frame reads "Two of these four are closed to you until your Force rating
            rises" over a Jedi with exactly two blocked at rating 1, and a sentence that prints a
            constant is false the moment the character is not that Jedi. Both numbers are counted
            off the rows the screen is drawing. A screen with nothing blocked keeps the step's own
            lead, which is what the picked arrangement prints. */}
        <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
          {(!sel && blocked > 0) ? (
            <React.Fragment>
              {fcSpecCount(blocked) + ' of these ' + fcSpecCount(cards.length).toLowerCase() + ' are closed to you until your '}
              <b>Force rating</b>{' rises. The card that says no also says what would answer it.'}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {'Your first is '}<b>free</b>{' when it comes from your own career.'}
            </React.Fragment>
          )}
        </p>
        {/* ONE CITE AT 390, AND THE NUMBER IS THE APP'S, NOT THE FRAME'S. The board draws a single
            chip here where its DESKTOP frame draws two, so the phone prints one — the approved
            frame's arrangement (R-MIL-1), and a gain on the legacy phone step, which cited nothing
            at all. The PAGE is `creator_step_cites.json`'s career row (B-1349: printed through the converter — and the frame's own
            number turns out to match it; it had been read as the designer's placeholder, the class the gear and motivation builds already refused to
            carry across. The second page — the cost of ADDITIONAL specializations — keeps its chip
            on the desktop sheet, and its absence at 390 is recorded at the gate. */}
        {p.cites.length ? (
          <div style={{ marginTop: '10px' }}>
            <span className="hp-cite" data-step-cite="specialization"
              style={{ marginRight: '7px' }}>{p.cites[0]}</span>
          </div>
        ) : null}
      </div>

      {/* ── THE UNPICKED ARRANGEMENT (`phone_short_path()`): the Force Rating ledger, then one
             card per specialization. Drawn only for a character who HAS a rating, because that is
             the only reader the frame is about and a "From the career / Now" ledger reading 0 and 0
             states nothing. ─────────────────────────────────────────────────────────────────── */}
      {(!sel && frNow >= 1) ? (
        <React.Fragment>
          <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
            <h2>Force Rating</h2>
          </div>
          <div className="hp-plate" data-pad="data">
            <table className="hp-ledger"><tbody>
              <tr><td className="hp-lkey">From the career</td>
                <td className="hp-lval" data-fr-base={frBase}>{frBase}</td></tr>
              <tr><td className="hp-lkey">Now</td>
                <td className="hp-lval" data-fr-now={frNow}>{frNow}</td></tr>
            </tbody></table>
          </div>
        </React.Fragment>
      ) : null}
      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}>
        <h2>{sel ? ((p.careerName || '') + ' Specializations') : 'Specializations'}</h2>
        <span className="hp-cite">{String(sel ? pickRows.length : cards.length)}</span>
      </div>
      {sel
        ? unit(sel.name, pickRows, false, undefined, null, 'career')
        : cards.map(function(r) {
            return fcSpecCard(r, gate, p.forceRating,
              (r.shortPath ? fcSpecShortPathPhone(true, false, p.xpPool, p.xpRemaining,
                                                  p.onShortPathTake, p.onShortPathUndo) : null),
              p.onPick);
          })}
      {shelfRows.length || p.shelfWithheld.length ? (
        <React.Fragment>
          <div className="hp-sechead" style={{ margin: '20px 0 9px' }}
            data-ruled-delta="spec-phone-universal-shelf">
            <h2>Universal Specializations</h2>
            <span className="hp-cite">{'open to any career · ' + shelfRows.length}</span>
          </div>
          {unit('Open to any career', shelfRows, false, 'spec-phone-universal-shelf',
            p.shelfWithheld.length ? (
              /* RELABEL, NEVER SILENCE (R-CSC-1, B-388). A droid loses four universals to a printed
                 rule; the shelf says WHICH four and WHY rather than becoming quietly shorter. The
                 `data-spec-withheld` hook is the legacy phone step's own and is carried, not
                 invented, so the suite that proved this at 390 keeps proving it. */
              <div className="hp-plate" data-pad="read" style={{ marginTop: '9px' }}
                data-spec-withheld={p.shelfWithheld.length}>
                <div className="hp-maker" style={{ marginBottom: '6px' }}>
                  {'Withheld · ' + p.shelfWithheld.length}
                </div>
                <p className="hp-prose hp-prose-sm" style={{ margin: 0, color: 'var(--doc-ink-muted)' }}>
                  Droids cannot be Force sensitive (eote p.275).
                </p>
                <p className="hp-prose hp-prose-sm" style={{ margin: '7px 0 0', color: 'var(--doc-ink-muted)' }}>
                  {p.shelfWithheld.map(function(r) { return r.name; }).join(', ')}
                </p>
              </div>
            ) : null, 'universal')}
        </React.Fragment>
      ) : null}

      {sel ? (
        <React.Fragment>
          {/* THE SPEC'S OWN CARD — description, then the printed restriction, then the gates. The
              order is the desktop read pane's (identity, then whether you pass), which is census
              P1's reorder carried to this band unchanged rather than re-argued. An empty
              description drops its paragraph, and a card left holding nothing is not drawn
              (R-M12B-176). */}
          {(selDesc || (gate && (gate.frRefused || selShortPath))) ? (
          <div className="hp-plate" data-pad="read" style={{ marginTop: '9px' }}>
            {selDesc ? (
              <p className="hp-prose hp-prose-sm" style={{ margin: 0 }}>
                {fcSpecDescNode(sel, CREATOR_GLYPHS)}
              </p>
            ) : null}
            {/* THE REFUSAL IS THE FRAME'S OWN IDIOM (`phone_short_path()` @ lab 10ff0155, approved
                by Jason 2026-09-09 — `catchup-pin-approval` R-CPA-1): `hp-req[data-short]` states
                the rule and the reader's own figure, inside the card that says no. R-CSC-1 — a
                choice you cannot make yet is information, refused where you would take it. */}
            {gate && gate.frRefused ? (
              <div className="hp-req" data-short="1" style={{ marginTop: '10px' }}>
                {'Requires Force rating ' + gate.needFR}
                <span className="hp-sep">{'·'}</span>
                {'you have ' + (Number(p.forceRating) || 0)}
              </div>
            ) : null}
            {/* THE OFFER SITS WITH THE REFUSAL IT ANSWERS — the frame draws them in one card, and
                separating an offer from its refusal is the dead-control shape. */}
            {selShortPath}
          </div>
          ) : null}
          {fcSpecAsPrinted(sel)}
          {gate ? fcSpecPoolPlates(gate.pools, p.picks, p.onPoolPick) : null}
          {gate ? fcSpecSwitchPlate(gate.switchTo, p.onSwitch) : null}
          {/* THE MARKS TAKE THEIR OWN ROW BETWEEN THE HEAD AND THE PLATE — the board's arrangement
              at 390, and the reason `fcReadSkills` grew a `beforePlate` slot in the first place: a
              third and fourth chip on a row already carrying a heading and a count measures past
              the frame edge. A spec with neither mark passes nothing and the row is not drawn. */}
          {/* THE HEADING DERIVES FROM THE RECORD (`build-phone-spec` A1, R-BPSp-A1-1, reversing
              D5). The exile frame prints "Bonus Career Skills / choose 2 later" over an EMPTY
              plate — copy printed unconditionally over conditional data, which promises a choice
              the character does not have: four of 137 specializations carry `career_skills: []`
              and every one of them trades those skills for a Force rating (EotE p.266 · AoR p.292,
              R-BPSp-A2-1). The desktop has always derived; the phone now derives the same way, and
              the exile fixture diverges by this plate on the BOARD'S account until the lab's copy
              derives too. Copy follows data. */}
          {(p.specSkills.length === 0)
            ? fcSpecSkillLessPlate(marks, true)
            : fcReadSkills('Bonus Career Skills', 'choose 2 later', p.specSkills, true,
              marks.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', margin: '0 0 9px' }}>
                  {marks.map(function(m) { return <span className="hp-cite" key={m}>{m}</span>; })}
                </div>
              ) : null, true /* phone: the 20/9 head margins */)}
          {fcNoStackNote()}
          {p.tree ? fcReadTree('What the Tree Holds', 'swipe →', p.tree,
            { margin: '20px 0 9px' }) : null}
        </React.Fragment>
      ) : null}

      {/* SO FAR FOLDS on the phone — a `<details>` at the foot rather than a rail block there is no
          room for, the same idiom every sibling phone sheet carries. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'▾'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <table className="hp-ledger"><tbody>
            {(p.soFar || []).map(function(r) {
              return (
                <tr key={r.k}>
                  <td className="hp-lkey">{r.k}</td>
                  <td className="hp-lval">{r.v ? r.v : <span className="hp-none">Not yet</span>}</td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
      </details>
    </FCPhoneShell>
  );
}

// ═══ B-563 · THE SPECIES STEP — the revision's species functions (increment 2) ═════════════════
// Ported VERBATIM from revision/creator-2026-09-10 (822015f1): the phone register and entry
// (FCSpeciesListPhone, FCSpeciesSheetPhone, fcRegisterRow), the desktop transplant's parts
// (fcSpeciesHead/Art/Ledger/…), the option-group asks (fcSpeciesOptionGroups) and their helpers.
// Wired by the species render blocks behind FC_TRANSPLANTED_STEPS (increment 3).
/* The board prints "35 species" beside the buyout head. DERIVED, not typed: it is the count of
   species in the shipped registry carrying a printed buy-off offer, so a registry edit moves the
   number instead of making the page lie. Computed once, lazily, because it walks every species. */
var _fcBuyoutSpeciesCount = null;
Object.defineProperty(window, 'FC_BUYOUT_SPECIES_COUNT_RECOUNT', {
  value: function() { _fcBuyoutSpeciesCount = null; }, writable: false, configurable: true,
});
function fcBuyoutSpeciesCount() {
  if (_fcBuyoutSpeciesCount !== null) return _fcBuyoutSpeciesCount;
  var all = (window._canonicalSpecies && (window._canonicalSpecies.species || window._canonicalSpecies)) || null;
  if (!all) return 0;
  var list = Array.isArray(all) ? all : Object.keys(all).map(function(k) { return all[k]; });
  _fcBuyoutSpeciesCount = list.filter(function(rec) {
    try {
      return window.CharResolve
        && (CharResolve.speciesBuyoutOffers(rec, null) || []).length > 0;
    } catch (e) { return false; }
  }).length;
  return _fcBuyoutSpeciesCount;
}

function fcSpeciesEffRecord(sp, picks) {
  if (!sp) return sp;
  var eff = speciesEff(sp, picks || {}) || {};
  // THE XP ADJUSTMENT IS THE CALL SITE'S BY THE RESOLVER'S OWN CONTRACT: `speciesEffective` sums the
  // threshold deltas and REPLACES absolutes, and deliberately leaves `xp_adjustment` to the call site
  // ("it keeps flowing through `character.speciesOption.xp` at the call site, B-22 — summing it in
  // both places is a double-count"). The dossier is a call site: a Gand's Has Lungs is +10 on the
  // record and the sheet will hold 110, so the entry says 110 (R-ASQ-1). THE ONE REDUCE, not a second
  // (R-ASQ-A2-1): `CharResolve.speciesOptionXpDelta` is the XP-delta reduce the creator's confirm and
  // the sheet already share (C-SAE-2); it reads recorded picks only, so the PRESELECT-aware map is
  // built here once through the app's own reader and handed to it — the same map every other line
  // of this record was resolved under.
  var effPicks = {};
  (sp.option_choices || []).forEach(function(g) {
    // B-445 — THE WHOLE LIST, not its first element. `CharResolve.speciesOptionXpDelta` sums
    // across picks and `_pickIdsOf` takes a scalar or an array, so handing it `pickedIdFor`'s
    // first id would have priced a Droid's second and third modifications at ZERO — a silent
    // wrong number on the entry, which is worse than the visible defect this card is about.
    // Single-pick groups still hand it the scalar, so their preselect rule is untouched.
    if (boundsOf(g).max > 1) {
      var ids = pickedIdsOf(g, picks || {});
      if (ids.length) effPicks[groupIdSlug(g.name)] = ids;
    } else {
      var id = pickedIdFor(g, picks || {});
      if (id != null) effPicks[groupIdSlug(g.name)] = id;
    }
  });
  var CR = window.CharResolve;
  var xpAdj = (CR && typeof CR.speciesOptionXpDelta === 'function') ? (Number(CR.speciesOptionXpDelta(sp, effPicks)) || 0) : 0;
  var xp = eff.starting_xp != null ? eff.starting_xp : sp.starting_xp;
  return Object.assign({}, sp, {
    characteristics: eff.characteristics || sp.characteristics,
    wound_threshold_base: eff.wound_threshold_base != null ? eff.wound_threshold_base : sp.wound_threshold_base,
    strain_threshold_base: eff.strain_threshold_base != null ? eff.strain_threshold_base : sp.strain_threshold_base,
    starting_xp: xp != null ? xp + xpAdj : xp,
  });
}

/* R-CPD-6(c) — WHAT MOVED, from the app's OWN resolver and nothing else. The change highlight
   marks the values a pick actually moved, and the set is the DIFF OF TWO RESOLUTIONS of the same
   record — the picks before, the picks after — both taken through `fcSpeciesEffRecord`, which is
   `CharResolve.speciesEffective` plus the call site's XP adjustment. Never a second key list: the
   approved mockup's own fixture kept one, it read a key no option carries, and it disagreed with
   the app on the first real specimen (R-AM-1). A mark computed from the resolution can only ever
   name numbers the entry actually draws, which is the whole point of the piece.

   FOUNDING CASE (R-CPR-39), asserted in the suite and named here: `csg_sith` Kissai -> Zuguruk
   moves SEVEN of nine — brawn, agility, intellect, cunning, both thresholds and the starting XP;
   willpower and presence do not move.

   PRESELECT IS WHY THE FIRST PICK MARKS ANYTHING. A variant species with no recorded pick already
   resolves to its FIRST option (`fcSpeciesEffRecord`'s Correction 2), so "before" is Kissai even
   though nothing was chosen — and picking Kissai explicitly moves nothing and marks nothing, which
   is correct and is its own leg.

   `seq` IS THE RESTART. A CSS animation does not re-run because an attribute changed, so picking
   Zuguruk, then Kissai, then Zuguruk again would mark the same set twice and animate once. The
   marked elements take `seq` into their React key and remount, which restarts it — one keyframe,
   no alternating animation names, no JS. */

function fcSpeciesMovedSet(sp, prevPicks, nextPicks) {
  if (!sp) return null;
  var before = fcSpeciesEffRecord(sp, prevPicks || {}) || {};
  var after = fcSpeciesEffRecord(sp, nextPicks || {}) || {};
  var keys = {};
  FC_CHAR_ORDER.forEach(function(a) {
    if ((before.characteristics || {})[a] !== (after.characteristics || {})[a]) keys[a] = 1;
  });
  FC_EFF_NUMERIC.forEach(function(k) { if (before[k] !== after[k]) keys[k] = 1; });
  return Object.keys(keys).length ? { keys: keys, seq: ++fcMovedSeq } : null;
}

/* THE MARK RIDES THE RESOLVED PROJECTION, and that is a deliberate choice with a reason. The
   entry's numbers are drawn by five builders at two bands, all of which already receive this
   record and none of which receive anything else in common; threading a set through five
   signatures (`fcSpeciesLedger` would take a NINTH positional argument) is how a caller silently
   passes it in the wrong slot. `fcSpeciesEffRecord` builds a FRESH object every render and is
   never persisted — it is a render projection already — so "which of my numbers just moved" is a
   property of the projection rather than of the species. The underscore says it is not registry
   data, and `fcSpeciesEffMarked` is the only thing that sets it. */

function fcSpeciesEffMarked(sp, picks, moved) {
  var rec = fcSpeciesEffRecord(sp, picks);
  return (rec && moved) ? Object.assign({}, rec, { _moved_now: moved }) : rec;
}

function fcSpeciesGroupHeadLabel(g, picks) {
  var b = boundsOf(g);
  var label = (g.display_name || g.name) + ' \u00b7 '
    + (b.min === b.max ? ('pick ' + b.min) : ('pick ' + b.min + '\u2013' + b.max));
  // B-445, frames M1-M4 (approved 2026-09-08): a group that allows several says how many are
  // taken. Without it three ticks under "pick 0-3" leave the player counting. ONLY when several
  // are allowed — a single-pick head gains nothing from "1 picked" and every shipped head is one.
  if (b.max > 1 && picks) {
    label += ' \u00b7 ' + pickedIdsOf(g, picks).length + ' picked';
  }
  return label;
}

function fcSpeciesGroupOwed(g, picks) {
  if (g && g.min_picks === 0) return false;
  // B-445 — OWED WHILE `picked < min`, which is the same sentence for one pick and for three.
  // `boundsOf` defaults min to 1, so a group carrying no bounds still asks exactly as it did.
  return pickedIdsOf(g, picks).length < boundsOf(g).min;
}

function fcSpeciesOwedGroups(groups, picks) {
  return (groups || []).filter(function(g) { return fcSpeciesGroupOwed(g, picks); });
}

function fcSpeciesOwedLabel(owed) {
  var parts = (owed || []).map(function(g) {
    var n = boundsOf(g).min || 1;
    // the species' career pick names its POOL ("Pick 2 Knowledge skills to continue", the approved F2), never the ability
    var label = (g.career_pick && g.career_pick.noun) || g.display_name || g.name || '';
    if (n === 1) return 'Pick one' + (g.noun ? ' ' + g.noun : '');
    return 'Pick ' + n + ' ' + label;
  });
  return parts.length ? parts.join(' and ') + ' to continue' : null;
}

/* THE SPECIES' CAREER PICK (ruling `f100-cerean-intellectuals-card` ADDENDUM 6, D-261; brief ADDENDUM 1 B1). The groups are
   `CharResolve.speciesCareerPickGroups` — the option groups' own shape, drawn by the same renderers, and NEVER appended to
   `sp.option_choices` (D-CI4), so the variant machinery (the resolved record, "Resolved as", the "What … gives you" fold, the
   withheld XP) never sees them. The draft picks are keyed like the option picks (`groupIdSlug(name)`); the species COMMIT is
   the ONE writer of `speciesSkillChoices`, keyed by species id and pool id (R-CI-2). */

// THE TEACH LINE (the approved F1, R-MIL-1) — under the group's head on both hosts, and the sheet's ask reason (one sentence).
function fcCareerPickTeach(g) {
  if (!g || !g.career_pick) return null;
  return 'Pick ' + boundsOf(g).min + ' ' + g.career_pick.noun + '; they become career skills (no free rank)';
}

// The career groups of a species, from the shared core (one builder: the creator, the sheet's ask, the career line).
function fcCareerPickGroups(sp, cb) {
  var CR = window.CharResolve;
  return (sp && cb && CR && CR.speciesCareerPickGroups) ? CR.speciesCareerPickGroups(sp, cb) : [];
}

// The draft picks seeded from the record (B-124's survive-a-flip law): `choices[sp.id][poolId]` → `{ slug: [ids] }`, each id
// one the group offers, at most `max_picks`. A species with no saved pick seeds empty.
function fcCareerPicksSeed(sp, groups, choices) {
  var byPool = (sp && choices && choices[sp.id]) || {};
  var out = {};
  (groups || []).forEach(function(g) {
    var ids = (g.options || []).map(function(o) { return o.id; });
    var got = (byPool[g.career_pick.poolId] || []).filter(function(id) { return ids.indexOf(id) >= 0; });
    if (got.length) out[groupIdSlug(g.name)] = got.slice(0, boundsOf(g).max);
  });
  return out;
}

// THE WRITER'S VALUE — `{ [sp.id]: { [poolId]: [ids] } }` for the species being committed, and nothing for any other
// species (a re-species leaves no stale pick, R-CI-2). No pick → `{}`, which the save drops (`normalizeCreated` writes the
// member only when a pick exists).
function fcSpeciesSkillChoicesFor(sp, groups, picks) {
  var byPool = {};
  (groups || []).forEach(function(g) {
    var ids = pickedIdsOf(g, picks || {});
    if (ids.length) byPool[g.career_pick.poolId] = ids.slice(0, boundsOf(g).max);
  });
  var out = {};
  if (sp && Object.keys(byPool).length) out[sp.id] = byPool;
  return out;
}

// B-445's MULTI-PICK RULE, one body for the option groups and the career groups: TOGGLE, and at the cap a press is REFUSED in
// place (null) — never a silent swap of the earliest pick.
function fcMultiPickNext(grp, picks, optId) {
  var list = pickedIdsOf(grp, picks).slice();
  var at = list.indexOf(optId);
  if (at >= 0) list.splice(at, 1);
  else if (list.length >= boundsOf(grp).max) return null;      // refused, in place
  else list.push(optId);
  return list;
}

/* THE RECORDED PICKS, COMPLETED — B-418 (`b417-preselect-rule-narrowed` R-B417-2). An untouched
   PRESELECTED group resolved to its first option through `speciesEffective`'s fallback but was never
   RECORDED: `applySpeciesPicks`' persisted map (`speciesOption.picks`, which the assembly hands the
   sheet as `speciesOptionPicks`) held only what the player pressed, so the pre-ticked option's
   `skill_grants` never reached the sheet and the sheet's ask mount asked the question again. At
   COMMIT — the creator's confirm and the Guided seed — every group `pickedIdFor` already resolves
   (a recorded pick, or a preselected group's first option) is written into the map, so grants flow
   through the ONE path (a recorded pick; PRAXIS §5) and the sheet holds a complete answer set.
   `speciesEffective`'s fallback stays as the guard for saved characters that predate this. */

/* ONE PHONE OPTION GROUP (brief `f100-cerean-intellectuals` ADDENDUM 1 B1, ruled at card ADDENDUM 7): the block
   `FCSpeciesSheetPhone` drew inline, lifted whole so the species' variant groups and its career pick draw through ONE
   renderer (a sibling copy is refused, PRAXIS §5). `species` is the RAW record (the run's base, B-588); `picks` and
   `onPick` are the group's own (the option picks, or the career picks). A career group adds its teach line under the head
   and carries no run (career status, never a variant). */
function fcPhoneOptionGroup(g, gi, picks, species, skillNames, onPick) {
  var slug = groupIdSlug(g.name);
  var opts = g.options || [];
  var picked = pickedIdFor(g, picks);
  // B-445 — per ID, not per group. `picked` stays for the single-pick summary and the
  // `open` rule; the ROWS read the list, so a second modification joins the first.
  var pickedIds = pickedIdsOf(g, picks);
  var atCap = pickedIds.length >= boundsOf(g).max;
  var chosen = opts.filter(function(o) { return o.id === picked; })[0];
  return (
    <React.Fragment key={slug || gi}>
      {/* R-CPD-6(b) AT THIS BAND TOO, and the approved page asks at both (frame A4, 390).
          The two heads share NO element and no class — `hp-maker` there, `hp-sechead > h2`
          here — so what they share is the SEMANTICS: `data-ask` says the group is still
          owed, one predicate decides it for both, and one CSS rule keyed on that attribute
          owns both heads. The card's mechanism note named the wide head only; this is the
          B-124 / B-409 two-entries shape, found by reading for it. */}
      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}
        data-group-head={g.name}
        data-ask={fcSpeciesGroupOwed(g, picks) ? '1' : undefined}>
        <h2><span className="hp-caret" aria-hidden="true"
          data-ruled-delta="idle-prompt-caret">{'\u25B8'}</span>
          {fcSpeciesGroupHeadLabel(g, picks)}</h2>
        <span className="hp-cite">{opts.length}</span>
      </div>
      {/* THE SPECIES' CAREER PICK teaches under its head (the approved F1, `hp-req`). */}
      {g.career_pick
        ? <div className="hp-req" data-career-pick-teach={g.career_pick.poolId} style={{ margin: '0 0 8px' }}>
            {fcCareerPickTeach(g)}</div>
        : null}
      {/* B-589 — OPEN IS THE PLAYER'S AFTER MOUNT. `open={!pickedIds.length}` re-rendered the
          <details> closed on every pick, so in a pick-0–3 group the list snapped shut after
          the first choice and a tap to reopen was undone by the next render (Jason 2026-09-14:
          "it doesn't give you an indicator of the picks selected" — the list was gone before
          the mark could be seen). The initial state is set once, on mount, and React never
          touches `open` again. */}
      <details className="hp-unit" data-fc-choice-block="1"
        ref={function(el) { if (el && el.dataset.fcInit !== '1') { el.dataset.fcInit = '1'; el.open = !pickedIds.length; } }}>
        <summary>
          <div className="hp-head">
            {/* B-445 — the summary NAMES WHAT IS TAKEN, and with several it names them all.
                `chosen` alone would show the first of three and read as the old defect. */}
            <span className="hp-name">
              {pickedIds.length
                ? opts.filter(function(o) { return pickedIds.indexOf(o.id) >= 0; })
                      .map(function(o) { return o.name; }).join(' \u00b7 ')
                : (chosen ? chosen.name : 'Choose — ' + opts.length + ' to read')}</span>
            <span className="hp-glyph">{'▾'}</span>
            {/* B-588 (Jason 2026-09-14, the phone: "difficult to know what the picker does and
                what changes at this size"): the closed head says WHAT THE PICK CHANGES — the
                picked option's run, the same resolved absolutes the desk head carries (B-585).
                One pick only; with several the names already fill the line. */}
            {(function() {
              if (!pickedIds.length || g.career_pick) return null;   // a career pick has no run
              var picked = opts.filter(function(o) { return pickedIds.indexOf(o.id) >= 0; });
              // `species` is the RAW record, NOT the resolved one (its XP already carries the
              // pick's delta) and the run would add the delta again — measured XP 160 beside a
              // ledger saying 175. The desk head builds from the raw record; so does this.
              // With several picks, one line each, led by the option's name.
              var lines = picked.map(function(po) {
                var run = fcSpeciesHeadRun(species, g, po, skillNames);
                var txt = [run.numeric, run.names].filter(Boolean).join(' \u00b7 ');
                return txt ? { id: po.id, txt: (picked.length > 1 ? po.name + ' \u2014 ' : '') + txt } : null;
              }).filter(Boolean);
              return lines.length
                ? <span className="hp-run hp-run-phone">{lines.map(function(l) {
                    return <span key={l.id} data-picked-run={l.id} style={{ display: 'block' }}>{l.txt}</span>; })}</span>
                : null;
            })()}
          </div>
        </summary>
        <div className="hpw-scroll hp-picklist">
          <div className="hp-plate" data-pad="data">
            {opts.map(function(opt) {
              var on = pickedIds.indexOf(opt.id) >= 0;
              // AT THE CAP, an unpicked row is refused IN PLACE with the reason beside it —
              // approved frame M4, and it reuses `hp-row[data-limit]`, the treatment the
              // creator already draws for a full pool. No new vocabulary on this band.
              var limited = atCap && !on;
              return (
                <button type="button" className="hp-row" key={opt.id}
                  data-on={on ? '1' : '0'} data-opt-head={opt.id}
                  data-limit={limited ? '1' : undefined}
                  aria-pressed={on ? 'true' : 'false'}
                  onClick={function() { onPick(slug, opt.id); }}>
                  {/* B-589 — THE MARK: the same `hp-box` the desk head carries, so a picked row
                      says so on the row itself and not only in the head's summary. */}
                  <span className="hp-box hp-row-box" data-on={on ? '1' : '0'} aria-hidden="true" />
                  <span className="hp-row-body">
                    <span className="hp-row-desig">{opt.name}</span>
                    {limited
                      ? <span className="hp-why">{'pick ' + boundsOf(g).max
                          + ' \u2014 remove one to swap'}</span>
                      : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </details>
    </React.Fragment>
  );
}

function FCSpeciesSheetPhone(p) {
  // B-399: the phone entry's numbers (the strip's XP, the rail, the ledger) read the resolved
  // variant through the one resolution point; the groups and everything textual stay the record's.
  var sp = fcSpeciesEffMarked(p.species, p.picks, p.moved) || p.species;
  var groups = (sp && sp.option_choices) || [];
  var owed = fcSpeciesOwedGroups(groups, p.picks);
  var pending = owed.length > 0;
  // THE SPECIES' CAREER PICK (D-CI3, D-CI4): it holds the COMMIT on its own predicate (`fcSpeciesGroupOwed`, picked < min)
  // and names its pool in the hold; it is not a variant, so `pending` (the withheld XP, the lead, the consequence fold)
  // never sees it — the numbers print while it is owed (the approved F2).
  var careerGroups = p.careerGroups || [];
  var careerOwed = fcSpeciesOwedGroups(careerGroups, p.careerPicks || {});
  var held = pending || careerOwed.length > 0;
  var entryMaker = 'Species Register · Entry ' + p.index + ' of ' + p.total;
  return (
    <FCPhoneShell band={p.band}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      /* BACK GOES TO THE REGISTER, not to the previous step — this is screen two of two. */
      backLabel="Species Register"
      onBack={p.onBack} onJump={p.onJump}
      status={<React.Fragment>
        <div><span className="hp-status-k">Starting</span>
          {/* THE BOARD WITHHOLDS THE FIGURE WHILE A PICK IS OWED, and its own lead says why —
              "before the numbers below are printed, not after". The strip keeps that promise. */}
          <span className="hp-status-v" data-species-xp={sp ? sp.starting_xp : undefined}>
            {pending || !sp ? '—' : sp.starting_xp + ' XP'}</span></div>
        <div><span className="hp-status-k">Entry</span>
          <span className="hp-status-v">{p.index + ' / ' + p.total}</span></div>
      </React.Fragment>}
      act={
        <button type="button" className="hp-commit" data-creator-commit="1"
          onClick={held ? undefined : p.onConfirm} disabled={held}>
          {held
            ? fcSpeciesOwedLabel(owed.concat(careerOwed))
            : <React.Fragment>{'Select ' + ((sp && sp.name) || 'Species') + ' '}
                <span>{'›'}</span></React.Fragment>}
        </button>}>

      {/* THE STEP'S OWN HEAD — the register, not the entry. Its maker names the LIST; the entry's
          own head below names the ENTRY and its place in it. The board draws both. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">The Species Register</div>
      </div>
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>{(sp && sp.name) || 'Species'}</h1>
      <div className="hp-desig-rule" />
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        <div><span className="hp-cite" data-step-cite="species">{p.cite}</span></div>
      </div>

      {fcSpeciesArt(sp, p.wash, '250px')}

      {/* THE ENTRY'S OWN HEAD, drawn a second time with the register position. */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">{entryMaker}</div>
        </div>
        <h1 className="hp-desig" style={{ fontSize: '27px' }}>{(sp && sp.name) || 'Species'}</h1>
        <div className="hp-desig-rule" />
      </div>
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        <p className="hp-lead" style={{ margin: 0 }}>{sp ? sp.description : ''}</p>
      </div>

      {/* ── THE ACT, and it is the OPTION GROUP'S — `data-fc-choice-block` is the same hook the
          wide band's compact act carries (R-CPR-77), so one walker scopes the species choice at
          both widths by ONE attribute. Open while the pick is owed, folded once it is made, which
          is what the board's two Gand frames and its two Xi'Dec frames draw. */}
      {groups.map(function(g, gi) { return fcPhoneOptionGroup(g, gi, p.picks, p.species, p.skillNames, p.onPick); })}
      {/* THE SPECIES' CAREER PICK (the Cerean's Intellectuals), the same renderer over its own picks — after the variants. */}
      {careerGroups.map(function(g, gi) { return fcPhoneOptionGroup(g, gi, p.careerPicks || {}, p.species, p.skillNames, p.onCareerPick); })}

      {/* THE CHOSEN OPTION'S CONSEQUENCES — Jason's one home for them, and the SAME builder the
          wide band's `hpw-conseq` slot takes, so the two widths cannot drift into saying different
          things about one pick. The board wraps it in a data plate at 390. */}
      {!pending && groups.length ? (
        /* B-588 — FOLDED: the consequence nest stood open under the picker (300px of prose for
           one granted ability) and was what made the picker read as "taking up the screen". It is
           a closed unit now, one head line that names what it holds; the run on the picker head
           above says what changed, and this is where to read why. */
        <details className="hp-unit" data-conseq-fold="1">
          <summary>
            <div className="hp-head">
              <span className="hp-name">{'What ' + groups.map(function(g) {
                var ids = pickedIdsOf(g, p.picks || {});
                return (g.options || []).filter(function(x) { return ids.indexOf(x.id) >= 0; }).map(function(o) { return o.name; }).join(' \u00b7 ');
              }).filter(Boolean).join(' \u00b7 ') + ' gives you'}</span>
              <span className="hp-glyph">{'▾'}</span>
            </div>
          </summary>
          <div className="hp-plate" data-pad="data">
            {fcSpeciesChosenConsequences(sp, p.picks, p.skillNames, p.skillDescs, p.skillSheetKeys)}
          </div>
        </details>
      ) : null}

      <div className="hp-sechead" style={{ margin: '20px 0 9px' }}><h2>Species Abilities</h2></div>
      {fcSpeciesResolved(sp, p.picks)}

      <div>{fcSpeciesRailPlate(sp, '3')}</div>

      {/* THE REFUSAL, and it says what clears it. The board draws it only while a pick is owed —
          R-SPB-14 — and it names the thing the choice decides rather than scolding. */}
      {pending ? (
        <div className="hp-req"><b>starting XP</b>{' are set by the choice above'}</div>
      ) : null}

      <div>{fcSpeciesLedgerTable(sp, p.skillNames, fcSpeciesAbilityNote(sp, p.picks),
        fcSpeciesGrantedAbilityCount(sp, p.picks))}</div>

      {/* WHEN THE REGISTER LISTS NO ABILITY the board says so rather than leaving a silence a
          player must interpret. Its copy, verbatim (punch-list P1). The WIDE band draws no such
          note and the board gives no desktop frame for a no-ability species, so extending it there
          would be invention — flagged in `species-phone-has-no-register-frame`, not done here. */}
      {fcSpeciesAbilityPlates(sp)}
      {fcSpeciesNoAbilityNote(sp)}
      {/* THE READING PLATE, LAST — where the board's `narrow_entry()` places it (after the ledger and
          the abilities, the last thing before the commit); the SAME builder the wide entry draws
          (B-400, R-CRS-A4-1). The pinned `phone_species_entry` frames were drawn without it — named
          in the gate, the board's account: its desktop and pre-shell entries both draw the plate. */}
      <div style={{ marginTop: '20px' }}>{fcSpeciesUnitsPlate(fcSpeciesUnitsOf(sp))}</div>
    </FCPhoneShell>
  );
}

function FCSpeciesListPhone(p) {
  var rows = p.rows || [];
  var q = String(p.query || '').trim();
  var filtered = !!q;
  return (
    <FCPhoneShell band={p.band}
      stepIndex={p.stepIndex} stepReached={p.stepReached} stepTotal={p.stepTotal}
      steps={p.steps} stepLabels={p.stepLabels}
      onBack={p.onBack} onJump={p.onJump}
      status={<React.Fragment>
        {/* THE STRIP CARRIES WHAT IS BANKED AND WHAT THIS STEP WILL SET — never the register's
            counts, which live in the run directly above the list where they can be read against
            the thing they count. Stating a figure twice on one screen is the defect the board's
            own step rule taught it once already. The XP is a DASH until a row is pressed, because
            it is not knowable until then and a dash says exactly that. */}
        <div><span className="hp-status-k">Line</span>
          <span className="hp-status-v">{p.lineShort || '—'}</span></div>
        <div><span className="hp-status-k">Starting XP</span>
          <span className="hp-status-v" data-species-xp={p.selected ? fcSpeciesEffRecord(p.selected, {}).starting_xp : undefined}>
            {p.selected ? fcSpeciesEffRecord(p.selected, {}).starting_xp + ' XP' : '—'}</span></div>
      </React.Fragment>}
      /* THE RULED OMISSION, typed rather than forgotten (A2's term). */
      act={null}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <span className="hp-slash"><i /><i /><i /></span>
        <div className="hp-maker">The Species Register</div>
      </div>
      <h1 className="hp-desig" style={{ fontSize: '27px' }}>Select a Species</h1>
      <div className="hp-desig-rule" />
      <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>
        <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0, fontSize: '16px' }}>
          {'Your species sets six starting characteristics, two thresholds, and the experience you '
           + 'have to spend on everything else. It is the only choice in creation you cannot '
           + 'revise later.'}
        </p>
        <div style={{ marginTop: '10px' }}>
          <span className="hp-cite" data-step-cite="species">{p.cite}</span></div>
      </div>

      {/* THE SEARCH PLATE, carried forward from the pre-shell drawing unchanged in idiom: an input
          is a thing you act on, so it keeps its lift even inside another plate. The INPUT carries
          no board class, so the DOM layer sees the same tree as the board's static div — the
          precedent the wide register already set and the ruling confirmed is deliberate. */}
      <div className="hp-plate" data-flat="1" data-input="1" style={{ marginTop: '14px' }}>
        <input type="search" value={p.query || ''} data-register-search="1"
          placeholder="Search the register&hellip;" aria-label="Search the register"
          onChange={function(e) { p.onSearch(e.target.value); }}
          style={{
            width: '100%', border: 0, background: 'transparent', outline: 'none',
            padding: '11px 12px', fontFamily: 'var(--font-mono)', fontSize: '12px',
            letterSpacing: '.12em', color: 'var(--doc-ink)', textTransform: 'uppercase',
          }} />
      </div>

      {/* THE SHOWING-COUNT RUN, directly above the rows it counts. Both figures are DERIVED — the
          shown count off the rows actually drawn, the total off the same unfiltered list the row
          memo filters — so the run and the list can never disagree. B-377's `data-species-count`
          rides `hp-index` below, where it names the rendered row count and nothing else. */}
      <div className="hp-runhead" style={{ margin: '14px 0 8px' }}>
        <span>{'Showing  ' + (filtered ? q : 'All species')}</span>
        <span className="hp-folio">
          {filtered ? rows.length + ' of ' + p.total : p.total + ' entries'}</span>
      </div>

      <div className="hp-plate" data-pad="data" style={{ padding: '6px 12px' }}>
        <div className="hp-index" data-species-count={rows.length}>
          {rows.map(function(row) { return fcRegisterRow(row, p.selected, p.onPick); })}
        </div>
      </div>

      {/* SO FAR FOLDS on the phone, as it does on every phone step. The ABSENT value is the
          board's `hp-none` "Not yet" rather than the shared table's bare dash — passed as the
          value from HERE, so `FCbSoFarTable` is untouched and the characteristics frame that also
          consumes it cannot be moved by this card. */}
      <details className="hp-unit">
        <summary>
          <div className="hp-head">
            <span className="hp-name">So Far</span>
            <span className="hp-glyph">{'▾'}</span>
          </div>
        </summary>
        <div className="hp-plate" data-pad="data" style={{ marginTop: '9px' }}>
          <FCbSoFarTable rows={[
            { k: 'Game line', v: p.lineTitle || <span className="hp-none">Not yet</span> },
            { k: 'Species', v: (p.selected && p.selected.name) || <span className="hp-none">Not yet</span> },
            { k: 'Career', v: (p.career && p.career.name) || <span className="hp-none">Not yet</span> },
          ]} />
        </div>
      </details>
    </FCPhoneShell>
  );
}


/* The read pane's SECTIONS, as the board draws them: a heading with a cite beside it, then one
   plate — prose in a read plate, or rows in a data plate. The career step is built from these two
   and nothing else, which is why they are functions here rather than markup repeated at the call
   site; the specialization step, when it lands, adds the tree and reuses both. */
/* `phone` IS THE PHONE ARRANGEMENT OF THE SAME BLOCK, not a second builder (`phone-career-census`
   R-PCC-1). Three differences, each read off `phone_career()` rather than invented:
     · a NULL title draws NO head — the phone career screen's description sits straight under the
       picker, whose summary already names the career, so a second heading would say the name twice;
       the head-less plate takes the frame's own 9px lift instead.
     · the prose is `hp-prose hp-prose-sm`, the board's small measure at 390.
     · the head, where there is one, takes the frame's 20/9 margins against the desktop's 22/11.
   The desktop callers pass nothing and render exactly what they always did. */

function fcRegisterRow(row, rec, onPick) {
  return (
    <button type="button" className="hp-row" key={row.id} data-row-id={row.id}
      data-noplate={row.plateClass ? undefined : '1'}
      /* `data-selected` is what the board's CSS keys on — the red keyline, the tinted ground and
         the ink on the name all hang off it. `aria-current` stays beside it: the board's markup
         carries no aria at all, and the a11y floor outranks the mockup (C-MU-4). */
      data-selected={rec && rec.id === row.id ? '1' : undefined}
      /* A REFUSED ROW IS STILL A ROW (R-CSC-1). It keeps its place, its name and its figure, and
         gains the red keyline, the dashed tab and a reason. It is NOT removed: a choice you
         cannot make yet is information, and a list that hides it teaches nothing. Still
         selectable so its detail can be read; the COMMIT is what holds. */
      data-refused={row.refused ? '1' : undefined}
      aria-current={rec && rec.id === row.id ? 'true' : undefined}
      onClick={function() { onPick(row.id); }}>
      {/* THE PORTRAIT PLATE IS THE SPECIES RAIL'S. A career has no face, so the board drops the
          plate there and keeps it here. The wash is the APP'S OWN portrait, never the board's
          embedded lab art: `speciesImgPath` returns null under COMMERCIAL_MODE by design, and
          baking the board's washes into CSS would put species art into the one distribution built
          to have none. The board's class travels; the image does not. */}
      {row.plateClass
        ? <span className={'hp-row-plate ' + row.plateClass}
            style={row.wash ? { backgroundImage: 'url(' + row.wash + ')' } : undefined} />
        : null}
      <span className="hp-row-body">
        <span className="hp-row-desig">{row.name}</span>
        {row.run ? <span className="hp-run">{row.run}</span> : null}
        {row.why ? <span className="hp-why">{row.why}</span> : null}
        {/* The BOOK LINE is the career rail's, not every rail's: a specialization's book is its
            career's, so the board prints it once and not again on the next screen. */}
        {row.source
          ? <span className="hp-rowsrc" data-ruled-delta={row.sourceRuledDelta}>{row.source}</span>
          : null}
        {/* THE MULTI-SOURCE MARK — the sixth specimen-gap fill (Addendum 1, option (a)). The head
            says HOW MANY species come from more than one active book; without this a player could
            not see WHICH. Board vocabulary only: `hp-rowsrc` is the register's own quiet-label
            register (the career rail's book line), so it inherits the board's muted ink and
            introduces no new hue. Placed in the ROW BODY beside the name because the row's TAB
            already carries the XP figure. `data-source-chip` is the shipped contract the picker's
            instruments already read — the mark is new, the contract is not. */}
        {row.srcCount > 1
          ? <span className="hp-rowsrc" data-source-chip={row.srcCount}
              /* DECLARED, because it is an ADDITION the board cannot speak to: its register sample
                 holds no multi-source species. `visual-revision-cluster-one-gate` A1(a) ruled the
                 mark in as the sixth specimen-gap fill; R-SCP-A5-1 routed this declaration and
                 granted it. Undeclared it was an unmarked extra in the board's tree and the parity
                 engine counted it — correctly: the fault was never the mark, it was that nothing
                 told the engine the mark was ruled. Token follows the `mechanic-row-source-a2-1`
                 convention: surface, object, granting clause. */
              data-ruled-delta="species-row-multisource-a1-a">
              {row.srcCount + ' SOURCES'}
            </span>
          : null}
      </span>
      {/* THE TAB IS THE BROWSERS', NOT EVERY RAIL'S. A career row tabs its skill count, a species
          row its starting XP — and the MECHANIC register tabs nothing, because an Obligation has
          no figure to carry. It was emitted unconditionally here and rendered an empty tab with an
          empty unit inside it: two named objects the board does not draw, which is exactly what
          R-TTP-1 calls "in the app and not the board". Gated on `unit`, which all three browsers
          pass and the mechanic does not, so their rows are byte-identical across this change. */}
      {row.unit ? (
      <span className="hp-row-tab">
        {row.xpn ? <span className="hp-xpn">{row.xpn}</span> : row.count}
        <span className="hp-xpu">{row.unit}</span>
      </span>
      ) : null}
    </button>
  );
}

/* THE CHARACTERISTIC RAIL — six beads under a label. The board draws it TWICE on the species
   screen: once in the head (`data-cols="3"`, two rows of three) and once in the ledger column.
   Both are in its DOM; which one a reader sees is the band's business, not the markup's. One
   function so the two can never disagree about what the numbers are. */

function fcSpeciesHead(sp, index, total, lead, cites) {
  return (
    <React.Fragment>
      <div className="hpw-title">
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
          <span className="hp-slash"><i /><i /><i /></span>
          <div className="hp-maker">
            {'Species Register \u00b7 Entry ' + index + ' of ' + total}
          </div>
        </div>
        <h1 className="hp-desig">{sp ? sp.name : 'Species'}</h1>
        <div className="hp-desig-rule" />
      </div>
      {(sp || lead) ? (
      <div className="hpw-desc">
        <div className="hp-plate" data-pad="read"
          style={{ marginTop: '13px', maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>
          <p className="hp-lead" data-step-standfirst="1" style={{ margin: 0 }}>
            {sp ? glyphs(cleanMarkup(sp.description || ''), null, CREATOR_GLYPHS) : lead}
          </p>
          {/* NO CITE ON THIS HEAD. The board prints none here and it is right not to: the
              register entry's source is a printed FACT about the entry, and the ledger's
              `Source` row is where the other printed facts live. */}
        </div>
      </div>
      ) : null}
      <div className="hpw-headrail">
        <div>{fcCharRail(sp && sp.characteristics, '3', undefined, fcMovedOf(sp))}</div>
      </div>
    </React.Fragment>
  );
}

/* THE REGISTER ENTRY — the species read pane's dossier, drawn as the board draws it: an art
   plate, then one `hp-unit` per field the record carries. `details`/`summary` because the board
   uses them: the unit is disclosable, and open by default so nothing is hidden behind a click. */
/* THE ART PLATE — one figure, two homes. The board draws it in the standalone `hpw-plate` pane at
   the bands where the column splits out (band 430) and inside the read pane's `hpw-foldplate`
   where it folds back in (band 320). Both live in its tree at once and CSS picks; so this is
   written once and called twice, which is the only way the two can't drift. */

function fcSpeciesArt(sp, portrait, bandPx) {
  // THE BACKDROP AND THE PORTRAIT ARE TWO THINGS, and until now they were one (R-SPS-A1-1). This
  // figure set `--doc-wash` to the PORTRAIT and then drew the same file again as its `<img>`, so
  // the "wash" was the picture behind the picture: species with no portrait had no backdrop either,
  // and under COMMERCIAL_MODE — where `speciesImgPath` returns null by design — every plate lost
  // both at once. The crossed washes are the backdrop the board actually draws, one per species;
  // the portrait is the figure on top of it. Wiring them apart is the whole of the wash-crossing
  // WIRING half, and it is why the data half crossed 413 files that nothing consumed.
  //
  // `--doc-wash` is left UNSET when a species has no wash: `grounds/creator.css` already declares
  // `--doc-wash-default` (the algorithmic wear) as the layer beneath it, so the plate is never bare
  // and the five unresolved species take the default rather than a hole.
  var wash = (typeof speciesWashPath === 'function') ? speciesWashPath(sp) : null;
  return (
    <figure className="hp-artplate">
      <div className="hp-artplate-in" data-band="1"
        data-wash={wash ? '1' : undefined}
        style={{
          '--doc-band': bandPx,
          ...(wash ? { '--doc-wash': 'url(' + wash + ')' } : null),
        }}>
        <span className="hp-artlabel">Archive Image</span>
        <span className="hp-tick tr" />
        <span className="hp-tick bl" />
        {/* NO <img> WITHOUT A PORTRAIT. `speciesImgPath` returns null under COMMERCIAL_MODE and
            for species whose art was removed; the plate then stands empty rather than drawing a
            broken frame, which is the shipped card's own choice. It is no longer BARE in that case,
            because the wash behind it is a separate layer now and does not depend on the portrait. */}
        {portrait ? <img src={portrait} alt={((sp || {}).name || 'Species') + ': archive image'} /> : null}
      </div>
    </figure>
  );
}

/* THE SPECIES SIDEBAR — `hp-note` + `hp-note-title`, the board's own shape, from
   `sidebar_title` / `sidebar_text`. 408 of 537 species carry this prose and the app rendered it
   NOWHERE; the only swept species frame was a Wookiee, which has no sidebar, so no instrument
   could see the gap (Ledger #114's shape a second time). Ruled in by R-SCP-A6-1.
   NULL WHEN THE SPECIES HAS NONE — an empty note is an object the board does not draw, and the
   129 species without the field must not gain a blank box. */

function fcSpeciesSidebar(sp) {
  var title = sp && sp.sidebar_title;
  var text = sp && sp.sidebar_text;
  if (!title && !text) return null;
  return (
    <div style={{ marginTop: '14px' }}>
      <div className="hp-note">
        {title ? <div className="hp-note-title">{title}</div> : null}
        {text ? <p>{text}</p> : null}
      </div>
    </div>
  );
}

/* THE FOUR DOSSIER UNITS — Physiology · Society · Homeworld · Language — the board's register
   entry opens with, in its order; each a field the record already carries, nothing authored here.
   ONE BUILDER FOR BOTH BANDS (B-400, `creator-release-scope` A4 R-CRS-A4-1): the desktop memo and
   the phone entry both read this, so a unit cannot exist on one band and not the other. */

function fcSpeciesUnitsOf(sp) {
  if (!sp) return [];
  return [
    { k: 'Physiology', v: sp.physiology },
    { k: 'Society', v: sp.society },
    { k: 'Homeworld', v: sp.homeworld },
    { k: 'Language', v: sp.language },
  ].filter(function(u) { return u.v; });
}

/* THE READING PLATE — "The Register Entry" and its disclosable units, the first open. One
   builder, two homes: the wide entry's fold arrangement and the phone entry's foot, where the
   board's `narrow_entry()` places it (after the ledger, the last thing before the commit). B-400:
   the phone rebuild had dropped it, so 530 species' authored prose was out of reach of the
   creator's primary form factor while the desktop drew it. */

function fcSpeciesUnitsPlate(units) {
  if (!units || !units.length) return null;
  return (
    <React.Fragment>
      <div className="hp-sechead" style={{ margin: '0 0 11px' }}><h2>The Register Entry</h2></div>
      <div className="hp-plate" data-pad="bleed" data-species-units={units.length} style={{ paddingTop: 0 }}>
        {units.map(function(u, i) {
          return (
            /* THE FIRST UNIT IS OPEN AND THE REST ARE NOT — the board's own choice, measured on
               its rendered frame, not read off the markup. A register entry is long, and the board
               discloses it one section at a time with the first already showing. */
            <details className="hp-unit" key={u.k} open={i === 0}>
              <summary>
                <span className="hp-head">
                  <span className="hp-name">{u.k}</span>
                  <span className="hp-glyph">{'\u25be'}</span>
                </span>
              </summary>
              <p className="hp-prose" style={{ margin: '11px 15px 15px' }}>
                {glyphs(cleanMarkup(u.v || ''), null, CREATOR_GLYPHS)}
              </p>
            </details>
          );
        })}
      </div>
    </React.Fragment>
  );
}

function fcSpeciesEntry(sp, units, wash) {
  return (
    <React.Fragment>
      <div className="hpw-foldplate" style={{ marginBottom: '14px' }}>
        {fcSpeciesArt(sp, wash, '320px')}
        {/* THE SIDEBAR'S SECOND HOME. The board draws the note TWICE in one frame — once in the
            standalone plate pane and once here, in the fold arrangement's own note slot — which
            is the same "one figure, two homes" the art plate above it has. This slot was emitted
            EMPTY, exactly as `hpw-headpick` was: a slot the board fills and the app did not.
            SAME BUILDER, so the two homes cannot drift into saying different things about one
            species. */}
        <div className="hpw-foldnote">{fcSpeciesSidebar(sp)}</div>
      </div>
      {fcSpeciesUnitsPlate(units)}
    </React.Fragment>
  );
}

function fcSpeciesRailPlate(sp, cols) {
  /* `cols` IS THE CALLER'S, and passing it unconditionally was a regression this sweep caught
     before the gate. The board draws this rail TWICE on the species screen with DIFFERENT
     attributes: the PHONE entry draws `hp-rail[data-cols="3"]` (two rows of three at 390) and the
     desktop LEDGER column draws a bare `hp-rail` under `hpw-statrail-rail`. Unifying the duplicate
     fixed the phone's four frames and reddened the desktop's two — equal object counts, 209·209,
     one attribute the app now had and the board did not. Same shared-fix hazard PRAXIS §7 names,
     in the same hour and in the opposite direction. */
  return fcCharRail((sp || {}).characteristics, cols, null, fcMovedOf(sp));
}

/* THE "NO ABILITY" NOTE. The board draws it on its two Gand phone frames — a species whose register
   entry lists no special ability — and the app produced nothing there, which leaves a player reading
   a silence they have to interpret. Its copy is the fixture's, verbatim (punch-list P1).

   IT MIRRORS TO THE WIDE BAND by ruling (`species-phone-has-no-register-frame`): a fact shown at one
   width and withheld at another is the no-compromise-mirror question, and the board simply has no
   desktop frame for a no-ability species rather than a desktop frame that omits it.

   WHICH IS WHY `data-ruled-delta` GOES ON THE WIDE COPY AND NOT THE PHONE'S. That attribute DELETES
   the element from the compared tree, so it is right only where the board draws nothing — the
   desktop. Putting it on the phone's copy would make the app read as MISSING a note the board's own
   frame draws, overstating the divergence in the one direction a parity engine must never fail in
   (PRAXIS §6). Same object, one declaration, and the declaration is band-specific on purpose. */
/* THE NOTE IS ABOUT AN EMPTY SECTION, and the section grew (R-WCS-A11-1). It used to test the
   ability count alone, which was right while abilities were the only thing in it. Now the free
   TALENT plates sit in the same section, so a species with no ability and a talent — `bothan` is
   exactly that — printed "the register lists no special ability" directly above a rendered talent.
   The board's own frame draws no note there.

   THE ALL-POINTER CASE FALLS OUT OF THIS, it does not need its own arm. A species whose every
   ability is a pointer has TALENTS by definition (that is what the pointers point at), so the first
   line already returns. A FIRST CUT ADDED A THIRD CONDITION — recount the abilities after
   suppression and suppress the note if none remained — and its BITE DID NOT BITE: disabling it
   changed no assert, because the talents clause above had already returned in every case that
   reaches it. Unreachable machinery that reads as a safeguard is worse than none, so it came out.
   Two lines, both bitten. */

function fcSpeciesNoAbilityNote(sp, ruledDelta) {
  if (!sp) return null;
  // THE SECTION IS NON-EMPTY IF IT HAS EITHER KIND OF THING IN IT, and that is the whole rule.
  if ((sp.starting_talents || []).length) return null;
  if ((sp.special_abilities || []).length) return null;
  return (
    <div style={{ marginTop: '10px' }} data-ruled-delta={ruledDelta || undefined}>
      <div className="hp-note">
        <div className="hp-note-title">Species Abilities</div>
        <p>{'The register lists no special ability for this species. That is the register\u2019s '
          + 'answer, not a gap in this page.'}</p>
      </div>
    </div>
  );
}

function fcSpeciesLedgerTable(sp, skillNames, abilityNote, grantedAbilities) {
  // R-CPD-6(c) — the three numbers the resolution owns besides the characteristics. The mark goes
  // on the VALUE CELL, which is the element that already exists: wrapping the number in a span of
  // its own would add three objects to a tree the board's frames are compared against, and
  // declaring them `data-ruled-delta` would delete the NUMBER from that comparison (the walker
  // skips the element AND its subtree), which is the overstatement PRAXIS §6 warns about.
  var moved = fcMovedOf(sp);
  // NULL-SAFE because it now has TWO callers. The wide sheet only ever reached here under a
  // truthy `sp`, so the bare `sp.special_abilities` was safe by its caller's guard rather than by
  // its own — and the phone's first render (step open, nothing selected yet) is exactly the state
  // that guard did not cover. A shared builder cannot inherit one caller's preconditions.
  if (!sp) return null;
  var abilities = sp.special_abilities || [];
  var granted = grantedAbilities || 0;
  // `starting_skills` is a LIST OF GRANTS, not a rank map: [{skill, rank_start, rank_limit}], and
  // it is `null` for a species that grants none. Read as a map it produced "0 [object Object]" —
  // caught by the ledger comparison, which is the kind of thing a parity layer is for.
  var skills = sp.starting_skills || [];
  return (
    <div className="hp-plate" data-pad="data">
      <table className="hp-ledger"><tbody>
        <tr><td className="hp-lkey">Wound Threshold</td>
          <td className="hp-lval" key={fcMovedKey(moved, 'wound_threshold_base')}
            data-moved={fcMovedMark(moved, 'wound_threshold_base')}>
            {sp.wound_threshold_base}{' '}<span className="hp-none">+ Brawn</span></td></tr>
        <tr><td className="hp-lkey">Strain Threshold</td>
          <td className="hp-lval" key={fcMovedKey(moved, 'strain_threshold_base')}
            data-moved={fcMovedMark(moved, 'strain_threshold_base')}>
            {sp.strain_threshold_base}{' '}<span className="hp-none">+ Willpower</span></td></tr>
        <tr><td className="hp-lkey">Starting Experience</td>
          <td className="hp-lval" key={fcMovedKey(moved, 'starting_xp')}
            data-moved={fcMovedMark(moved, 'starting_xp')}>{sp.starting_xp + ' XP'}</td></tr>
        <tr><td className="hp-lkey">Starting Skills</td>
          <td className="hp-lval">
            {skills.length
              ? skills.map(function(g) {
                  return (skillNames[g.skill] || g.skill) + ' ' + g.rank_start;
                }).join(', ')
              : <span className="hp-none">None</span>}
            {/* B-372 — THE SEPARATOR IS AN OBJECT, NOT A LINE BREAK. The board draws
                `<span class="hp-sep"> \u00b7 </span>` between the grant and its limit; this emitted a
                `<br>`, which names nothing and left the app one named object SHORT of the board at
                every width (divergence #197). Fixed HERE, in the builder both bands now share —
                which is what R-SPB-7 meant by "fixed once for both": before the extraction this was
                two edits in two trees, and one of them would have been forgotten. */}
            {/* THE CLAUSE IS FIELD-DRIVEN, AND THE SEPARATOR GOES WITH IT (R-WCS-A13-1). The
                paragraph that stood here said "every grant carries `rank_limit`" — and that is
                FALSE: `bothan.starting_skills` carries `rank_limit: 2`, `csg_gendai`'s carries
                none. The `|| 2` fallback then printed "max 2 at creation" over a record that
                states no limit, which is the app inventing a rule and asserting it in a ledger.
                The board prints the qualifier only where the field is there, so
                `board_freetalent_gendai_1366` was red at board 224 · app 231 for two objects the
                record never authorised — the `hp-sep` and the `hp-none` together, which is why
                both are gated on the same condition rather than only the clause. */}
            {skills.length && skills[0].rank_limit
              ? <span className="hp-sep">{' \u00b7 '}</span> : null}
            {skills.length && skills[0].rank_limit
              ? <span className="hp-none">{'max ' + skills[0].rank_limit + ' at creation'}</span>
              : null}
          </td></tr>
        <tr><td className="hp-lkey">Species Abilities</td>
          {/* "None · +1 from Lungless" is a row contradicting itself in the space of four words, and
              it was STRUCTURAL as well as incoherent: `hp-none` is an object, so the app drew one
              MORE than the board's picked frame (gand_picked 139 vs 138). A species whose own record
              lists none but whose PICK grants one HAS one — count it. Species carrying their own
              abilities take the same branch as before, which is why the desktop's choice frames do
              not move. */}
          <td className="hp-lval">{(abilities.length + granted)
            ? (abilities.length + granted) + ' listed below'
            : <span className="hp-none">None</span>}{abilityNote || null}</td></tr>
        <tr><td className="hp-lkey">Source</td>
          <td className="hp-lval">{gameLineTitle((sp.sources || [])[0])}</td></tr>
      </tbody></table>
    </div>
  );
}

/* B-379(a) — THE FREE TALENT JOINS THE SHARED ABILITY PLATES (R-WCS-A8-1), and the reason it moved
   here is a finding rather than tidiness. Built first in `fcSpeciesLedger`, it reached the DESKTOP
   detail only — and measuring 390 showed the phone drawing ZERO, because `species-phone-register`
   lit the band and replaced the LEGACY phone screen that had been drawing the talent all along. So
   the desktop-only narrowing was sized against a state a previous card had already changed.
   R-CPR-28 carries rules-bearing content the legacy screen drew onto the rebuilt one, and this
   function is the ONE place both bands already call (`FCSpeciesSheetPhone` and the wide ledger), so
   the phone gets it with no second copy — which is the whole reason it is here and not there.

   THE TALENTS DB IS READ FROM THE CANONICAL VIEW rather than threaded through two call sites, the
   idiom this file already uses in three places. A talent the registry names but the view has not
   loaded yet falls back to its id, so the plate never renders a blank head. */
/* A PURE TALENT POINTER — an ability whose ENTIRE description is "you get this talent", nothing
   more. When the talent plate beside it renders that same talent, the ability is saying the plate's
   sentence twice and the board draws only the plate (R-WCS-A11-1, mirroring the lab's own
   resolution).

   THE FOUR PRINTED FORMS, DERIVED FROM THE REGISTRY RATHER THAN GUESSED — every ability description
   in `species.json` that mentions a talent at all falls into exactly four shapes, and only TWO of
   them are pointers:
     x157  "Receive the talent “X”."                     -> POINTER
     x4    "Receive N ranks in the talent “X”."           -> POINTER
     x1    "Upgrade the difficulty of Discipline checks to affect a Dashade with a Force power or
            talent by one."                               -> MENTION ONLY (dashade / Force Resistance)
     x1    "When fighting against overwhelming odds, count as having the talent “X” or increase
            ranks in “X” by N."                           -> MENTION ONLY (csg_ranat / Never Surrender)
   The last two must NOT be suppressed: they are rules of their own that happen to name a talent.
   TWO CORRECTIONS THE FIRST CUT NEEDED, both found by running the pattern over the whole
   registry rather than over the specimen: the PERIOD SITS INSIDE THE CLOSING QUOTE
   (“Durable.”, not “Durable”.) so a pattern expecting the quote first matched nothing and
   the suppression silently did not fire; and a talent NAME may contain a curly apostrophe
   (“Nobody’s Fool.”), so excluding it from the name class dropped 21 real pointers.
   Verified over the register: every "Receive [N ranks in] the talent" description matches,
   0 unmatched, and both mention-only forms are rejected.
   That is what "nothing looser" buys — a regex matching "talent" anywhere would delete a Dashade's
   Force Resistance, which no plate replaces. Anchored at both ends for the same reason. */

function fcSpeciesAbilityPlates(sp) {
  if (!sp) return null;
  var abilities = sp.special_abilities || [];
  var _cb = (typeof window !== 'undefined')
    ? (window._canonicalCharbuild || window._canonicalCharbuildBase) : null;
  var talents = (sp.starting_talents || []).map(function(t) {
    var rec = (_cb && _cb.talents && _cb.talents[t.talent]) || null;
    var src = (rec && rec.sources && rec.sources[0]) || null;
    return { key: t.talent, name: (rec && rec.name) || t.talent,
             desc: rec && rec.description, ranks: t.ranks || 1,
             /* `gameLineTitle`, NOT `fcBookLabel` — the latter is a component-LOCAL alias
                (`var fcBookLabel = gameLineTitle` inside FullCreator) and is invisible to a
                module-level builder. Using it here threw `fcBookLabel is not defined` inside the
                map and took the ENTIRE species ledger down with it: zero plates of any kind, not
                just the talents. Caught by reading the console on the first drive after the
                change, which is the only reason it was seconds rather than a gate. */
             cite: src ? gameLineTitle(src) : null };
  });
  // AN ABILITY THAT IS A PURE POINTER TO A TALENT THE PLATES RENDER IS DROPPED — the plate says it
  // better, with the talent's own rules text and its cite, and the board draws only the plate. The
  // match is on the talent NAME the pointer quotes, so an ability pointing at a talent this species
  // does NOT grant survives untouched.
  var rendered = {};
  talents.forEach(function(t) { rendered[String(t.name || '').toLowerCase()] = true; });
  var shown = abilities.filter(function(ab) {
    var pointed = fcTalentPointerName(ab);
    return !(pointed && rendered[pointed]);
  });
  return (
    <React.Fragment>
      {shown.map(function(ab, i) {
        return (
          <div style={{ marginTop: '10px' }} key={(ab.name || i) + '-' + i}>
            <div className="hp-plate" style={{ marginTop: '9px' }}>
              <div className="hp-head" data-state="active">
                <span className="hp-box" data-on="1" />
                <span className="hp-name">{ab.name}</span>
              </div>
              <p className="hp-prose hp-prose-sm" style={{ margin: '11px 13px 13px' }}>
                {glyphs(cleanMarkup(ab.description || ''), null, CREATOR_GLYPHS)}
              </p>
            </div>
          </div>
        );
      })}
      {/* THE FREE TALENT PLATES, built to the BOARD'S FRAMES rather than to a description of them
          (R-WCS-A11-1: "to the frames' form"). Measured at pin 657f62de across all five —
          `wide(key=bothan|anx|csg_gendai)` and `phone_species_entry(key=bothan|anx)` — and the
          frames differ from the addendum's prose in three ways, so the frames won:
            · there is NO "FREE TALENT" kicker. Zero occurrences in all five frames. The plate is
              identified by `data-talent`, not by a label above it — so the earlier build's
              `hp-maker` comes OFF.
            · ACTIVATION is not drawn. All four specimen talents are `passive` and no frame prints
              it anywhere.
            · the RANK is a CORNER TAB, not part of the name. The board's own caption says it:
              "the corner tab prints the rank only here; four entries in the register carry one" —
              and four is exactly what the registry holds, so `(Rank 2)` in the name was wrong.
          THE CITE IS THE TALENT'S FIRST SOURCE through the shared book label, never a hand-written
          string: `durable.sources` is ["eote","aor","fad"] and the frame prints "Edge of the
          Empire". A talent whose registry entry carries no source prints no cite rather than an
          empty one.
          THE DECLARATION IS OFF (R-WCS-A11-1). The board now DRAWS this object on both bands, so
          `data-ruled-delta` would delete a drawn element from the comparison and make the app read
          as missing it — the exact inversion PRAXIS §6 warns about. It was right while no frame
          existed; it is wrong now. */}
      {talents.map(function(t, i) {
        return (
          <div className="hp-plate" data-talent={t.key} key={'ft-' + t.key + '-' + i}
            style={{ marginTop: '9px', position: 'relative' }}>
            <div className="hp-head" data-state="passive">
              <span className="hp-box" data-on="1" />
              <span className="hp-name">{t.name}</span>
            </div>
            {t.desc
              ? <p className="hp-prose hp-prose-sm" style={{ margin: '11px 13px 13px' }}>
                  {glyphs(cleanMarkup(t.desc), null, CREATOR_GLYPHS)}
                </p>
              : null}
            {t.cite
              ? <div style={{ margin: '0 13px 11px' }}>
                  <span className="hp-cite">{t.cite}</span>
                </div>
              : null}
            {t.ranks > 1
              ? <span className="hp-tab"><span className="hp-tab-key">Rank</span>{t.ranks}</span>
              : null}
          </div>
        );
      })}
    </React.Fragment>
  );
}

/* THE LEDGER PANE — the species step's fourth column: the starting characteristics as a stat
   rail, then the printed facts as a ledger, then one plate per species ability. Every value is
   read off the record; `hp-none` carries the qualifier the board prints beside a figure
   ("+ Brawn", "max 2 at creation") so a number never appears without the rule that shapes it. */

function fcSpeciesLedger(sp, skillNames, cb, headpick, conseq, resolved, abilityNote, grantedAbilities) {
  // The three inner objects moved to their own builders above (R-SPB-9) and took their locals with
  // them; what is left here is the WIDE band's arrangement of them — the statrail, the act's home,
  // and the two slots the board fills below the ledger.
  return (
    <React.Fragment>
      <div className="hpw-statrail">
        <div className="hpw-statrail-rail">
          <div className="hp-maker" style={{ marginBottom: '8px' }}>Starting Characteristics</div>
          <div>
            {fcSpeciesRailPlate(sp)}
          </div>
        </div>
        {/* R-SCP-1 — THE COMPACT ACT, in the home the board draws it in. This slot was
            emitted EMPTY at every band while the act's only other home was `display:none` on
            BOTH sides — so the app built the whole picker and no player could reach it (B-347,
            Jason's walk). That second home is now DELETED (R-SCP-A1-1, Jason's word): this is the
            only home the act has. Still emitted when there is nothing to pick, because the board
            keeps its slots in the tree at every band. */}
        {/* `data-fc-choice-block` — the SAME name the legacy block carries (this file's
            `FCSpeciesDetail`, and commercial's), so a cross-tree walker scopes the species
            choice by ONE attribute instead of learning each tree's idiom. R-CPR-77. It goes
            on the compact act's container, which is where the choice now lives — NOT on the
            pane B-347 deleted, which is what the board row still names. */}
        <div className="hpw-headpick" data-fc-choice-block="1">{headpick || null}</div>
      </div>
      <div className="hp-sechead" style={{ margin: '0 0 8px' }}><h2>Species Abilities</h2></div>
      <div className="hpw-resolved">{resolved || null}</div>
      {fcSpeciesLedgerTable(sp, skillNames, abilityNote, grantedAbilities)}
      {fcSpeciesAbilityPlates(sp)}
      {fcSpeciesNoAbilityNote(sp, 'species-no-ability-note')}
      {/* R-SDL-1'S FREE-SKILL ROWS — ruled content the board's one exemplar could not show, and
          the fourth instance of R-C1C-9's specimen-of-one shape on this cluster.
          THE CONFLICT, stated because it is between two APPROVED records rather than a drawing
          and an opinion: `species-detail-skill-legibility` (R-SDL-1, with its own approved mockup)
          exists because a dossier that prints prose for its talents and abilities and NOTHING for
          its skills leaves "Brawl or Survival" as two bare words at the moment a novice must
          choose. The board's species frame draws a Wookiee, whose one skill needs no explaining,
          so its ledger prints the figure alone. Building the frame as drawn deleted the sentences.
          FILLED IN BOARD PRIMITIVES (plate + head + prose, the treatment the ABILITIES beside them
          already use — which is literally what R-SDL-1 asks for), and DECLARED: `data-ruled-delta`
          puts these nodes outside the parity comparison with the ruling that owns them named on
          the element. A divergence that cites its ruling is the opposite of an allowlist. */}
      {(sp.starting_skills || []).map(function(g, i) {
        var key = (cb && cb.skillSheetKeys && cb.skillSheetKeys[g.skill]) || g.skill;
        var desc = (cb && cb.skillDescs && cb.skillDescs[key]) || '';
        return (
          <div style={{ marginTop: '10px' }} key={'fs-' + g.skill + '-' + i}
            data-ruled-delta="species-detail-skill-legibility">
            <div className="hp-plate">
              <div className="hp-head" data-state="passive">
                <span className="hp-box" data-on="1" />
                <span className="hp-name">
                  {(skillNames[g.skill] || g.skill) + ' (Rank ' + g.rank_start + ')'}
                </span>
                <span className="hp-maker">FREE SKILL</span>
              </div>
              <p className="hp-prose hp-prose-sm" style={{ margin: '11px 13px 13px' }}>
                {glyphs(cleanMarkup(desc), null, CREATOR_GLYPHS)}
              </p>
            </div>
          </div>
        );
      })}
      {/* `hpw-conseq` — and it is JASON'S ONE HOME for what a chosen option does. This slot was
          emitted EMPTY, the third of three the board fills and the app did not (`hpw-headpick`,
          `hpw-foldnote`, this). The consequences used to be drawn as a nest under EVERY option in
          the act; the lab read its origin commit and found the act never carried one at any pin —
          what I had transplanted was the hidden pane's arrangement, shown to nobody. R-SCP-A8-1
          moves them here, for the CHOSEN option only, as the board draws it. */}
      <div className="hpw-conseq">{conseq || null}</div>
    </React.Fragment>
  );
}

/* THE CHOICE PANE, POPULATED — the board's DRAWN form, which is not the form its own ruling
   presumed. D-SP-2 pre-ruled `hp-row` pickers; the board actually draws `hp-head` BUTTONS carrying
   a box, a name and the option's characteristic run, with an `hp-nest` disclosure beneath the
   chosen one ("Granted by X", the grant in a plate, then "Also grants …"). The drawn form governs
   (Addendum 1), and this is it.

   THE CONTRACTS ARE UNTOUCHED. Picks are the same `optionPicks` map keyed by `groupIdSlug`, read
   through the same `pickedIdFor`/`speciesChosen` helpers the shipped picker uses — B-19's
   chosen-only grant and B-40/B-36's save shape are the app's, not this component's, and this only
   draws them. B-319 is why that matters here rather than being a nicety: the pane renders the
   RECORD AS SHIPPED, so when the data lap corrects a grant the pane is already right, and it
   pre-empts nothing in the meantime.

   R-SDL-2 IS SATISFIED IN THE NEST (D-SP-6). Every option carries its sentence, because the whole
   point of that ruling is choosing BETWEEN them — and the board's exemplar draws a nest only under
   the selected one, so the nests under the others are declared with `data-ruled-delta` naming the
   ruling that owns them. */
/* THE CHOSEN OPTION'S CONSEQUENCES — the conseq block, and Jason's ONE home for them.
   THE ACT CARRIES NO NEST. The lab read its own origin commit: the compact act never carried one
   at any pin, and the nest-under-each-option this app drew was `pick_full`'s — the hidden pane's
   arrangement, shown to nobody, which I transplanted because the panel was the only place it had
   ever appeared. R-SCP-A8-1 removes it from the act; the consequences render HERE, for the chosen
   option only, as the board draws them.
   R-SDL-2 IS NOT DROPPED BY THAT — it is met PER HEAD in the act's run slot, numerically where the
   option moves numbers and by gloss where it does not (`fcSpeciesOptionGloss`). */
// The board's predicate for the nest caption (R-SPY-3): the set of names the plates beneath carry —
// the option's abilities and its talents — must differ from {the option's name} for the caption to
// say anything. A talent's name resolves through the canonical view when it is loaded, else its id.

function fcSpeciesChosenConsequences(sp, picks, skillNames, skillDescs, skillSheetKeys) {
  var out = [];
  ((sp && sp.option_choices) || []).forEach(function(group, gi) {
    var chosenId = pickedIdFor(group, picks || {});
    if (!chosenId) return;
    var opt = (group.options || []).filter(function(o) { return o.id === chosenId; })[0];
    if (!opt) return;
    var grants = opt.skill_grants || [];
    out.push(
      <React.Fragment key={groupIdSlug(group.name) + '-' + gi}>
                  <div className="hp-nest" data-opt-desc={opt.id}>
                    {/* THE MAKER LINE ATTRIBUTES; WHERE IT ATTRIBUTES NOTHING IT GOES — the board's rule
                        (`build.py` :441–453 @ 4128208f), adopted on `species-parity` R-SPY-3 (B-389).
                        "Granted by X" exists because a plate's head names the GRANT, not the option that
                        brought it; when EVERY plate beneath is already titled with the option's own name
                        (Lungless grants Lungless) the line would say one fact twice, so it goes — and it
                        returns the instant a plate carries anything the head does not (Constructor grants
                        Long Limbs). Derived from the plates, never listed. */}
                    {fcNestNeedsCaption(opt) ? <div className="hp-maker" data-opt-caption={opt.id}>{'Granted by ' + opt.name}</div> : null}
                    {/* THE GRANTED ABILITY, in a plate — the board's exemplar draws exactly this
                        ("Long Limbs" with its sentence under the chosen gender) and I had rendered
                        only the skill grants. An option that grants an ability and does not say so
                        is the same defect R-SDL-1 was written against, one level in. */}
                    {(opt.special_abilities || []).map(function(ab, ai) {
                      return (
                        <div className="hp-plate" key={'ab-' + ai}>
                          <div className="hp-head" data-state='active'>
                            <span className="hp-box" data-on='1' />
                            <span className="hp-name">{ab.name}</span>
                          </div>
                          <p className="hp-prose hp-prose-sm" style={{ margin: '10px 13px 12px' }}>
                            {glyphs(cleanMarkup(ab.description || ''), null, CREATOR_GLYPHS)}
                          </p>
                        </div>
                      );
                    })}
                    {grants.map(function(g) {
                      var key = (skillSheetKeys && skillSheetKeys[g.skill]) || g.skill;
                      return (
                        <div className="hp-plate" key={g.skill}>
                          <div className="hp-head" data-state='active'>
                            <span className="hp-box" data-on='1' />
                            <span className="hp-name">
                              {(skillNames[g.skill] || g.skill) + ' (Rank ' + g.rank_start + ')'}
                            </span>
                          </div>
                          <p className="hp-prose hp-prose-sm"
                            style={{ margin: '10px 13px 12px' }}>
                            {glyphs(cleanMarkup((skillDescs && skillDescs[key]) || ''), null, CREATOR_GLYPHS)}
                          </p>
                        </div>
                      );
                    })}
                    {/* B-386 (DECISIONS D-400, Jason "Name it", as drawn at artifact 24fPVtS8M2Z77eQdqX32pe): the chosen
                        option's STARTING TALENTS, in the grants' plate with the talent's rule words. Sluissi's two options
                        each carry one; before this the pick named its talent on the choice button and nowhere after. */}
                    {(opt.starting_talents || []).map(function(g, ti) {
                      var t = fcOptionTalentOf(g);
                      if (!t.name) return null;
                      return (
                        <div className="hp-plate" key={'tal-' + ti} data-opt-talent={t.id}>
                          <div className="hp-head" data-state='active'>
                            <span className="hp-box" data-on='1' />
                            <span className="hp-name">{t.name}</span>
                          </div>
                          {t.description ? <p className="hp-prose hp-prose-sm" style={{ margin: '10px 13px 12px' }}>
                            {glyphs(cleanMarkup(t.description), null, CREATOR_GLYPHS)}
                          </p> : null}
                        </div>
                      );
                    })}
                    {/* THE TRAILING MAKER — the board draws it, and until now the app did not. */}
                    {(function() {
                      var lines = (opt.skill_choice_grants || [])
                        .map(function(g) { return fcSkillChoicePhrase(g, skillNames); })
                        .filter(Boolean);
                      return lines.length
                        ? <div className="hp-maker">{'Also grants ' + lines.join(' \u00b7 ')}</div>
                        : null;
                    })()}
        </div>
      </React.Fragment>
    );
  });
  return out.length ? <React.Fragment>{out}</React.Fragment> : null;
}

/* THE RESOLVED LINE — the board's `hpw-resolved`, the FOURTH slot emitted empty (after
   `hpw-headpick`, `hpw-foldnote` and `hpw-conseq`). It names what the pick RESOLVED TO and how
   many the group held: "Resolved as Constructor" with a folio reading "5 variants". Both are read
   off the record — the chosen option's name and the group's own option count — so a species with
   a differently-sized group says its own number.
   NULL BEFORE A PICK: there is nothing resolved yet, and the board draws no empty runhead. */

function fcSpeciesResolved(sp, picks) {
  var out = [];
  ((sp && sp.option_choices) || []).forEach(function(group, gi) {
    var chosenId = pickedIdFor(group, picks || {});
    if (!chosenId) return;
    var opts = group.options || [];
    var opt = opts.filter(function(o) { return o.id === chosenId; })[0];
    if (!opt) return;
    out.push(
      <div className="hp-runhead" style={{ margin: '0 0 8px' }}
        key={groupIdSlug(group.name) + '-' + gi}>
        <span>{'Resolved as ' + opt.name}</span>
        <span className="hp-folio">{opts.length + ' variants'}</span>
      </div>
    );
  });
  return out.length ? <React.Fragment>{out}</React.Fragment> : null;
}

/* THE HEAD RUN — RESOLVED ABSOLUTES, LABELLED, FOR WHAT THIS OPTION *MOVES* (Jason, "Baseline.",
   2026-09-07; lab LAB_NOTES §73.3; ruling `lab-laps-layout` R-LLL-4, board row B-479).
   A10 withdrew A9's signed deltas and that stands: a head reads "XP 110", never "+10 XP" — a
   player comparing Gand's two breathing options wants the two NUMBERS THEY WOULD HAVE, and a
   delta makes them do arithmetic against a base printed elsewhere. Gand draws "XP 110" / "XP 100".

   ⚠ THE PREDICATE IS THE SPECIES BASELINE, NOT THE SIBLING GROUP. A head prints a field when THIS
   OPTION MOVES it off the species' own value, and stays QUIET when it does not. What changed is
   only WHICH SLOTS EARN A FIGURE; the vocabulary — resolved, labelled, unsigned — did not move.

   WHAT THIS REPLACES. The shipped rule printed every scalar whose resolved value VARIED ACROSS THE
   SIBLING GROUP. Varying is a property of the GROUP; moving is a property of the OPTION — so under
   the old rule an option that restated all six characteristics identically to the species still
   printed a rail, because a SIBLING moved one. Measured over the shipped registry
   (`public/data/canonical/species.json`, 32 variant groups / 76 option heads): 58 scalar slots
   (group x {WT,ST,XP}) vary, 75 of the head-slots they print are at the species' own value and go
   quiet, and 23 of the register's 49 characteristic rails restate the species unchanged. The
   lab's independent count over the same register is the same three numbers.

   MODAL WAS REJECTED BY MEASUREMENT, not by taste: quieting against the group's most common value
   silences a distinct value arbitrarily wherever no value is modal — `csg_geonosian`'s intellect
   reads [1,2,3] and there is no honest answer to "which of three is the norm". The species
   baseline is a number the reader can already see: the rail beside the head prints it, so a quiet
   slot is never a number left to inference.

   THE RAIL IS ONE SLOT, NOT SIX, and that is the board's own arrangement rather than a choice made
   here: the run prints the six values POSITIONALLY, so a partial rail would be six positions with
   holes in it. An option that moves ANY characteristic prints all six (the reader compares two
   whole rails); an option that moves NONE prints no characteristic at all.

   GRANTS ANSWER TO THE SIBLINGS, and the two predicates are commented apart deliberately: the
   lab's `variant_grants` keeps the group test, so the second row shows what the option HANDS you
   where that distinguishes it from its siblings. Skill grants ride that row with the ability
   names — a rank handed to you is a grant, not a figure the species already had — which is where
   the board draws them at the pin.

   THE FULL DELTA SET, and the half the shipped derivation still missed: `wound_threshold_adjustment`
   and `strain_threshold_adjustment` were read by nothing, so the droid's Large (+2) and Small (-2)
   and Vaathkree's Bulky (+2) resolved to the UNADJUSTED threshold — three live options whose heads
   were silent about a move they make. Under a predicate whose whole subject is "does this option
   move the value", a resolver that cannot see the move is the predicate being wrong in the other
   direction, so it is folded in here (gate `dispatch-baseline`, DECISIONS D-1).

   NOT DECLARED with `data-ruled-delta`: the board draws `span.hp-run` and `span.hp-run.hp-run-grants`
   in exactly these places at the pin, and a declaration DELETES the element from the compared tree
   (PRAXIS §6) — declaring one the board DOES draw reads as absence and overstates the divergence. */

function fcSpeciesHeadRun(sp, group, opt, skillNames) {
  var opts = (group && group.options) || [];
  var base = (sp && sp.characteristics) || {};
  /* THE RESOLVED RAIL — the option's own block if it carries one, then the PARTIAL overrides over
     whatever is in force. Order matters and it is the lab's: `characteristic_overrides` composes
     with `characteristics` rather than racing it. (No option in the shipped register carries both
     today — measured, 0 of 76 — so this is the shape being right rather than a live fix.) */
  function railOf(o) {
    var ch = {};
    FC_CHAR_ORDER.forEach(function(a) {
      var own = o.characteristics && o.characteristics[a];
      ch[a] = own != null ? own : base[a];
    });
    if (o.characteristic_overrides) {
      FC_CHAR_ORDER.forEach(function(a) {
        var ov = o.characteristic_overrides[a];
        if (ov != null) ch[a] = ov;
      });
    }
    return ch;
  }
  /* A RESOLVED SCALAR — the option's own base if it names one, else the species'; then the
     adjustment, which is additive over whichever base is in force. */
  function scalarOf(o, field, adjField) {
    var v = o[field] != null ? o[field] : (sp ? sp[field] : undefined);
    if (v != null && o[adjField] != null) v = v + o[adjField];
    return v;
  }
  /* WHAT THE OPTION HANDS YOU, BY NAME — abilities first, then skill ranks. Names, not counts:
     "2 abilities" is a question the head cannot answer. */
  function grantsOf(o) {
    var out = (o.special_abilities || []).map(function(a) { return a.name; });
    (o.skill_grants || []).forEach(function(g) {
      out.push(((skillNames && skillNames[g.skill]) || g.skill)
               + ' ' + (g.rank_start == null ? 1 : g.rank_start));
    });
    return out;
  }

  var mine = railOf(opt);
  // THE RAIL MOVES only when the option actually carries a block or an override AND the resolved
  // rail differs from the species' own. An option that restates all six identically moves nothing.
  var railMoves = !!(opt.characteristics || opt.characteristic_overrides)
    && FC_CHAR_ORDER.some(function(a) { return mine[a] !== base[a]; });

  var fields = [];
  if (railMoves) {
    FC_CHAR_ORDER.forEach(function(a) { fields.push(FC_CHAR_WORDS[a] + ' ' + mine[a]); });
  }
  [['WT', 'wound_threshold_base', 'wound_threshold_adjustment'],
   ['ST', 'strain_threshold_base', 'strain_threshold_adjustment'],
   ['XP', 'starting_xp', 'xp_adjustment']].forEach(function(f) {
    var v = scalarOf(opt, f[1], f[2]);
    if (v != null && sp && v !== sp[f[1]]) fields.push(f[0] + ' ' + v);
  });

  // THE SECOND ROW keeps the SIBLING test (the lab's `variant_grants`): uniform grants tell a
  // reader nothing about the choice, and a group where every option hands you the same thing has
  // nothing to distinguish on the head.
  var mineGrants = grantsOf(opt);
  var sigs = {};
  opts.forEach(function(o) { sigs[grantsOf(o).join(' · ')] = 1; });
  var names = (mineGrants.length && Object.keys(sigs).length > 1)
    ? mineGrants.join(' · ') : '';

  return { numeric: fields.join(' · '), names: names };
}

/* HOW MANY ABILITIES THE CHOSEN OPTIONS ADD — read by the ledger row AND by the qualifier note
   beside it, so the count and its explanation cannot drift apart. Same walk as
   `fcSpeciesAbilityNote`, kept beside it for that reason. */

function fcSpeciesGrantedAbilityCount(sp, picks) {
  var n = 0;
  ((sp && sp.option_choices) || []).forEach(function(group) {
    var chosenId = pickedIdFor(group, picks || {});
    if (!chosenId) return;
    var opt = (group.options || []).filter(function(o) { return o.id === chosenId; })[0];
    n += (opt && (opt.special_abilities || []).length) || 0;
  });
  return n;
}

/* THE ABILITY QUALIFIER — the board's "+1 from Constructor" beside the ledger's ability count.
   A chosen option can grant abilities the species record does not list, so the count in the row is
   incomplete without saying where the extra came from. Derived: the number of the chosen option's
   own `special_abilities` and the option's name — no copy of mine.
   NULL BEFORE A PICK, and null when the option grants none: the board draws neither. */

function fcSpeciesAbilityNote(sp, picks) {
  var out = [];
  ((sp && sp.option_choices) || []).forEach(function(group, gi) {
    var chosenId = pickedIdFor(group, picks || {});
    if (!chosenId) return;
    var opt = (group.options || []).filter(function(o) { return o.id === chosenId; })[0];
    var n = opt && (opt.special_abilities || []).length;
    if (!n) return;
    // THE BOARD'S TWO PHRASES (species parity card, B-389's read): "all from Lungless" where the
    // species lists no ability of its own and every plate below came with the pick; "+1 from
    // Constructor" where the pick adds to the species' own. Text, not structure — the walker does
    // not see it — but the ledger should not say "+1" beside a count that IS that one.
    var own = (sp.special_abilities || []).length;
    out.push(
      <React.Fragment key={groupIdSlug(group.name) + '-' + gi}>
        <span className="hp-sep">{' \u00b7 '}</span>
        <span className="hp-none">{(own ? '+' + n + ' from ' : 'all from ') + opt.name}</span>
      </React.Fragment>
    );
  });
  return out.length ? <React.Fragment>{out}</React.Fragment> : null;
}

/* THE GLOSS — R-SDL-2 met PER HEAD for an option that moves no numbers.
   The board's run slot carries the numeric DELTA, which is how a reader compares Xi'Dec's genders
   at a glance. An option that changes no characteristics (Gand's Has Lungs / Lungless, Koorivar)
   has nothing to put there, and an empty run is an object the board does not draw — so the slot
   carries a one-line gloss instead. ARM 1: the registry's own sentence, VERBATIM. ARM 2, only
   where there is none: derived from what the option actually grants. Never authored here.
   Gap-fill in the board's idiom, declared with `data-ruled-delta` citing A8 until the lab draws
   it (R-SCP-A8-3). */
/* A PICK-ONE SKILL POOL, SAID ONCE. `skill_choice_grants` is the FOURTH grant shape and the only
   one nothing rendered: `fcSpeciesOptionGloss` reads `skill_grants`, the nest renders a plate per
   `skill_grants`, and an option carrying a POOL instead of named grants produced silence. That is
   how `board_species_phone_xidec_picked` came up one object short — the board draws a trailing
   `hp-maker` "Also grants Athletics 1, Mechanics 1" and the app drew nothing.

   THE BOARD'S LINE ASSERTS BOTH SKILLS, ours says what the RECORD says: a choice of one, resolved
   at the Skills step. Both are faithful to their own source and the sources disagree — that is
   B-319 (registry ships pick-one where CSG p.392 grants both), which stays the home of the
   difference and closes it with NO UI work here. Parity strips text, so the object is the board's
   and needs no `data-ruled-delta` (`species-phone-has-no-register-frame` A8, R-SPR-A8-1).

   COUNT, POOL, AND WHERE IT RESOLVES — `rank_cap` is deliberately not printed: it bounds what the
   skills step will allow, not what this grant hands you, and a cap on a head reads as a promise.
   ONE derivation with two consumers (this line and the gloss below), because a second spelling of
   a species' grant is how a phone and a desktop start disagreeing about the same record. */
/* B-390 — `pool` IS NOT ALWAYS A LIST OF SKILL IDS, AND ASSUMING IT WAS UNMOUNTED THE APP.
   This read `(g.pool || []).map(...)`. A STRING pool has no `.map`, so picking such an option threw
   `TypeError: ((e && e.pool) || []).map is not a function` mid-render — React unwound and `#root`
   went from one child to ZERO. A blank page, from a species option.

   THE SHAPES ARE THE REGISTRY'S, censused rather than guessed (537 species):
     array         8   ['brawl','lore'] — explicit ids                    (worked before)
     'non_career'  7   any skill not already a career skill
     'knowledge'   5   a skill GROUP token
     'combat'      3   a skill GROUP token
     'any'         1   any skill at all
   Only the array shape worked. The four string kinds all crashed the moment they reached a
   sentence — which is why the failure looked species-specific: exactly FOUR options across THREE
   species carry a string pool where an option gloss is built (`mandalorian_human` x2 by group,
   `csg_kaminoan` and `csg_ssi_ruu` by `non_career`). The rest carry theirs at the ROOT, which
   never reaches this builder. So B-390 was carded on Mandalorian Human and is two species wider.

   THE GROUP TOKEN NEEDS NO LOOKUP, and that is a guarantee rather than an assumption:
   `speciesChoicePoolOf` records that the pool value is schema-validated (`species.schema.json`) and
   its enum pinned to the skills vocabulary by `validate_registries` L1cb.5 — and that function
   THROWS on a token the vocabulary does not know, so an unpinned value could never reach a rendered
   sentence in the first place. Any string that is not `any` or `non_career` is therefore a skill
   GROUP, and the display name is the token capitalised. Threading `skillGroups` down two more
   signatures to re-check what the validator already guarantees would be a second rule about the
   same fact. */

function fcSpeciesOptionGloss(opt, skillNames) {
  if (!opt) return null;
  if (opt.description) return cleanMarkup(String(opt.description));          // arm 1, verbatim
  var parts = (opt.special_abilities || []).map(function(a) { return a.name; });
  (opt.skill_grants || []).forEach(function(g) {
    parts.push(((skillNames && skillNames[g.skill]) || g.skill) + ' ' + g.rank_start);
  });
  // APPEND-ONLY, and the restraint is the point: adding the pool unconditionally would give a
  // gloss to options that have none today, and a gloss that appears is a `span.hp-run` that
  // appears — a new named object on every desktop frame drawing such an option. The pool joins a
  // sentence that was going to be printed anyway; it never creates one.
  if (parts.length) {
    (opt.skill_choice_grants || []).forEach(function(g) {
      var ph = fcSkillChoicePhrase(g, skillNames);
      if (ph) parts.push(ph);
    });
  }
  return parts.length ? parts.join(' \u00b7 ') : null;                        // arm 2, derived
}

/* THE OPTION GROUPS — the board's own compact act, in `hpw-headpick`: group title + "pick N" +
   one `hp-head` per option with `hp-box` as the mark, the option's characteristic run as the
   delta, and the §5b nest beneath the selected one. R-SCP-1 named this home; R-SCP-A1-1 (Jason's
   word) then DELETED the hidden second one, so there is now exactly one place a player picks and
   exactly one renderer behind it. `data-opt-head` / `data-opt-desc` travel with the options. */

function fcSpeciesOptionGroups(sp, picks, skillNames, skillDescs, skillSheetKeys, onPick) {
  var groups = (sp && sp.option_choices) || [];
  return (
    <React.Fragment>
      {groups.map(function(group, gi) {
        var slug = groupIdSlug(group.name);
        var chosen = pickedIdFor(group, picks || {});
        var chosenIds = pickedIdsOf(group, picks || {});
        var groupAtCap = chosenIds.length >= boundsOf(group).max;
        return (
          <React.Fragment key={slug + '-' + gi}>
            {/* R-CPD-6(b) — THE IDLE PROMPT, on the element the ruling names: the group head that
                says "pick N", which is the same element carrying B-409's grammar. `data-ask` is
                SEMANTIC — it says the group is still owed, not that something is moving — so the
                caret marks the unanswered head at rest, under reduced motion, and while the
                breathing runs, and the CSS is the only thing that knows about motion at all.
                THE SETTLED STATE IS THE ATTRIBUTE STILL STANDING WITH THE ANIMATION ENDED (the
                card leaves "how the prompt's settled state is represented" to this seat): the
                approved page draws A2 without the caret and A5 with it, and those two frames are
                the same state — unanswered, not moving — so they cannot both be built. A5 is the
                one that says what the caret is FOR ("the caret still marks the unanswered head"),
                and a standing mark for a standing fact is the reading that needs no JS, no
                animationend listener and no second state. Reported here per the card. */}
            {/* B-448 — `data-group-head` IS THE DOOR, and this suite's own comment asked for it
                before it broke: "there is no data-hook on the option group to aim at — a hook would
                be the right door and the app does not offer one yet." `smoke_c_ocs_5_variant_block`
                found the group by `innerText` prefix; the idle-prompt caret prepended a glyph to
                that text and the finder matched nothing, so the suite reported the PRODUCT as
                broken. It carries the group's registry NAME, which is its identity — not the
                painted label, which `text-transform` uppercases and a pick-count already decorates
                once. Both bands carry it, because the two heads are different elements. */}
            <div className="hp-maker" style={{ margin: '16px 0 8px' }}
              data-group-head={group.name}
              data-ask={fcSpeciesGroupOwed(group, picks || {}) ? '1' : undefined}>
              {/* DECLARED: the board draws no caret in this head, so it is an ADDITION and says so
                  (PRAXIS §6 — declare additions; a content difference needs none). Decorative:
                  the held commit's own sentence carries the information in words, so the glyph is
                  `aria-hidden` and no reader loses anything by not seeing it. */}
              <span className="hp-caret" aria-hidden="true"
                data-ruled-delta="idle-prompt-caret">{'\u25B8'}</span>
              {/* THE HEADING IS THE REGISTRY'S OWN GROUP NAME, never a generic label — Droids
                   read "Modifications", Kaminoans "Castes", Xi'Dec "Genders", and a species that
                   calls its choice something else says that instead (Jason's term, R-SCP-A6-2).
                   AND THE COUNT REFLECTS THE BOUNDS — the formula is `fcSpeciesGroupHeadLabel`,
                   which the phone's head now shares. */}
              {fcSpeciesGroupHeadLabel(group, picks || {})}
            </div>
            {/* THE SPECIES' CAREER PICK teaches under its head (the approved F1, `hp-req`) — the one rule sentence. */}
            {group.career_pick
              ? <div className="hp-req" data-career-pick-teach={group.career_pick.poolId} style={{ margin: '0 0 8px' }}>
                  {fcCareerPickTeach(group)}</div>
              : null}
            {(group.options || []).map(function(opt) {
              var on = chosenIds.indexOf(opt.id) >= 0;
              // approved frame M2: at the cap the desktop refuses in place too. `hp-head` has no
              // shipped treatment for this, which is exactly why the frame went to Jason first.
              var optLimited = groupAtCap && !on;
              var grants = opt.skill_grants || [];
              return (
                <React.Fragment key={opt.id}>
                  <button type="button" className="hp-head" data-state={on ? 'active' : 'passive'}
                    style={{ marginTop: '6px' }}
                    /* R-C1G-1's anchor. The board composes an option as BUTTON + NEST where
                       R-SDL-3 composed one row, so a reader that knows only the nest sees the
                       sentence and not the delta. This pairs with `data-opt-desc` on the nest so
                       an instrument can read the pair IN DOM ORDER and assert the same
                       information the single-row claim asserted. */
                    data-opt-head={opt.id}
                    data-limit={optLimited ? '1' : undefined}
                    aria-pressed={on ? 'true' : 'false'}
                    onClick={function() { onPick(slug, opt.id); }}>
                    <span className="hp-box" data-on={on ? '1' : '0'} />
                    <span className="hp-name">{opt.name}</span>
                    {/* THE RUN — resolved ABSOLUTES for what VARIES among the siblings
                        (R-SCP-A10-1), never signed deltas: a reader comparing two options wants
                        the two numbers they would HAVE. Uniform fields are off the head; prose
                        grants take their own line as names. A head with no mechanical delta at
                        all takes the one-line gloss instead — that, and only that, is what the
                        gloss is for. */}
                    {(function() {
                      // B-585 (Jason 2026-09-14): the run draws on the SELECTED option only — the
                      // unpicked heads carry the name alone. Five unpicked runs at the mid band ran
                      // 200px past the column and scrolled the whole ledger sideways.
                      // a career pick is career STATUS, never a variant: its picked head carries the name alone (F1)
                      if (!on || group.career_pick) return null;
                      var run = fcSpeciesHeadRun(sp, group, opt, skillNames);
                      if (run.numeric || run.names) {
                        return (
                          <React.Fragment>
                            {/* THE NUMERIC RUN IS *NOT* DECLARED, and that is a correction of my
                                own first attempt. `data-ruled-delta` DROPS the element, and the
                                board draws a run in exactly this place — ours differs in CONTENT
                                (resolved absolutes vs the old positional form), and NEITHER PARITY
                                LAYER COMPARES TEXT. Declaring it made the app read as having no run
                                at all, which overstates the difference by five objects and hides
                                that the structure already agrees. THAT EXPIRY HAS NOW LANDED:
                                the 2026-09-03 refresh moved the pin to index.html f39b31b6, which
                                is downstream of ad828c78, so the board's A10 form is the pinned
                                form and the content difference this paragraph named is closed. */}
                            {run.numeric
                              ? <span className="hp-run">{run.numeric}</span>
                              : null}
                            {/* DECLARATION RETIRED 2026-09-03, `species-phone-register` step 0
                                (R-SPR-A2-1: "the head-run declared delta — ad828c78's expiry —
                                RETIRES if the frames now carry the runs"). It does: the board's
                                #optiondelta lap draws a SECOND run per head, `span.hp-run
                                .hp-run-grants`, and the pinned frame carries four of them.
                                So this is no longer an ADDITION and must not be declared —
                                `data-ruled-delta` DELETES the element from the compared tree, and
                                a declaration over something the board DOES draw reads as absence
                                and overstates the divergence (the trap PRAXIS §6 names). Measured
                                at the refresh: board 249 · app 244, first divergence #192 sitting
                                exactly here. The class joins to match the board's own. */}
                            {run.names
                              ? <span className="hp-run hp-run-grants" data-run-names="1">{run.names}</span>
                              : null}
                          </React.Fragment>
                        );
                      }
                      var gl = fcSpeciesOptionGloss(opt, skillNames);
                      return gl
                        ? <span className="hp-run" data-ruled-delta="species-option-gloss-a8">{gl}</span>
                        : null;
                    })()}
                  </button>
                  {/* B-445, approved frame M2 — THE REASON, under the head it refuses. Same words
                      as the phone's, because it is the same refusal: `pick N — remove one to
                      swap`. It says what would clear it, which is the difference between a dead
                      control and a refusal a player can act on (the A8-2 lesson, third register). */}
                </React.Fragment>
              );
            })}
            {/* B-585 — THE REFUSAL, ONCE PER GROUP (Jason 2026-09-14: "should only be displayed
                once"): at the cap it stood under every unpicked head, four times for the Xi'Dec.
                One line under the group, the same sentence, still saying what would clear it. */}
            {groupAtCap && (group.options || []).length > chosenIds.length
              ? <span className="hp-why" data-group-limit={slug}>
                  {'pick ' + boundsOf(group).max + ' \u2014 remove one to swap'}</span>
              : null}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
}

/* THE FIVE RULED BEHAVIOURS (ruling `visual-revision-cluster-one-career-gate`, R-C1C-1).
   The board's specialization frame draws none of them — it draws a Marauder, who needs none — so
   R-C1C-9's specimen-of-one rule applies and each is COMPOSED FROM BOARD PRIMITIVES rather than
   invented or dropped. The ruling named the home for each; these functions are those homes.
   Every one is CONDITIONAL, so the Marauder frame both guards measure is unchanged. */

/* (1) THE REFUSAL — R-CSC-1's printed form, on the row it refuses. `data-refused` gives the row
   the red keyline and the dashed tab the board specifies; `hp-why` is the reason line, which the
   board's own note calls the whole point ("a refusal that does not say why is a dead control").
   The tab keeps its figure: B-118's law says a price must not vanish at the moment it is needed. */

// ── THE SPECIES-CONFIRM FLOW — one hook (audit item F) ─────────────────────────────────────
// Everything that happens between "the player tapped a species" and "the character carries it":
// the source picker for twins, the option picks, the apply/reseed of characteristics and grants,
// the change review when a re-pick would move numbers, and the confirm that advances the step.
// Lifted verbatim out of FullCreator (257 lines of its 2,171). Inputs: the controller, the game
// data, the deduped species groups. Outputs: the handlers the species step renders with, and the
// two pieces of state the shell renders (the change review, the source picker).
function useSpeciesConfirm(ctrl, gameData, cbSpeciesGroups) {
  // Canonical species -> { stateSkillKey: rank } free-rank grants. B-19: a pick-one option_cho — Docs/CREATOR_COMMENTARY.md § "useSpeciesConfirm › speciesSkillGrants"
  var speciesSkillGrants = useCallback(function(sp, optionPicks) {
    var cb = gameData && gameData.charbuild;
    if (!sp || !cb) return {};
    return (window.CharResolve && window.CharResolve.speciesSkillGrants)
      ? window.CharResolve.speciesSkillGrants(sp, optionPicks, cb.skillSheetKeys)
      : {};
  }, [gameData]);

  // Step 1 handler: Species
  // R-MSP-C/D2 — THE SOURCE QUESTION IS ASKED ON PICK, and only when there is one to ask. A row
  // whose name is supplied by a single active source behaves exactly as it always has: tap, detail,
  // no overlay, no extra tap. `null` = no question in flight.
  var _useSrc = useState(null), srcCandidates = _useSrc[0], setSrcCandidates = _useSrc[1];
  var srcProceed = useRef(null);   // the phone register's continuation across the source question (revision)

  var handleSpeciesSelect = useCallback(function(sp, proceed) {
    var S = window.SWRPGSources;
    var key = (S && S.dedupeName) ? S.dedupeName(sp && sp.name) : null;
    var group = key ? (cbSpeciesGroups[key] || []) : [];
    if (group.length > 1) {
      // The list showed ONE row and the shelf has several records behind it. Ask, rather than
      // hand over the precedence winner the dedupe happened to keep.
      srcProceed.current = proceed || null;
      setSrcCandidates(group);
      return;
    }
    if (proceed) { proceed(sp); return; }
    ctrl.setSubView('speciesDetail');
    ctrl.setSelectedSpeciesData(sp);
  }, [ctrl, cbSpeciesGroups]);

  var handleSourceUse = useCallback(function(rec) {
    setSrcCandidates(null);
    var proceed = srcProceed.current;
    srcProceed.current = null;
    // The band that ASKED is the band that continues — answering on the phone must land on the
    // phone's entry screen, not open a sub-view the phone register does not use.
    if (proceed) { proceed(rec); return; }
    ctrl.setSubView('speciesDetail');
    ctrl.setSelectedSpeciesData(rec);
  }, [ctrl]);

  // CANCEL PICKS NOTHING (R-MSP-D2). It closes the question and leaves the player on the list with
  // no species selected — falling back to the precedence winner here would reinstate the exact
  // silent choice this surface exists to end.
  var handleSourceCancel = useCallback(function() {
    // Drop the continuation too. A cancelled question that left it armed would fire the PREVIOUS
    // band's navigation on the next name the player taps.
    srcProceed.current = null;
    setSrcCandidates(null);
  }, []);


  // B-124 — the pick-one writer. It lands on the CHARACTER, which is what the draft persists,  — Docs/CREATOR_COMMENTARY.md § "useSpeciesConfirm › applySpeciesPicks"
  var applySpeciesPicks = useCallback(function(sp, optionPicks, prevPicks) {
    if (!sp) return;
    var eff = speciesEff(sp, optionPicks);
    // C-SVR-1 (ruling `species-variant-resolver` §4) — RE-SEED ONLY WHAT A PICK ACTUALLY CHANGED — Docs/CREATOR_COMMENTARY.md § "useSpeciesConfirm › reseed"
    var reseed = (prevPicks === undefined);
    if (reseed) {
      var _applied = ctrl.character && ctrl.character.speciesPicksApplied;
      var _sameSpecies = !!(ctrl.character && ctrl.character.species
                            && ctrl.character.species.key === sp.id);
      var _charsMoved = (_applied && _sameSpecies && window.CharResolve
                         && window.CharResolve.speciesCharsMoved)
        ? window.CharResolve.speciesCharsMoved(sp, _applied, optionPicks)
        : true;
      if (_charsMoved) {
        ctrl.dispatch({ type: 'SET_FIELD', field: 'characteristics', value: {
          brawn: eff.characteristics.brawn, agility: eff.characteristics.agility, intellect: eff.characteristics.intellect,
          cunning: eff.characteristics.cunning, willpower: eff.characteristics.willpower, presence: eff.characteristics.presence,
        }});
      }
    }
    // B-19: per-skill creation cap where a chosen option grant names a higher rank_limit than the
    // global 2 (Corellian Piloting → 3, Suns of Fortune p.95). Transient creation state — read by
    // the buy-ranks screen; not persisted to the saved sheet.
    var caps = {};
    var cb = gameData && gameData.charbuild;
    (sp.option_choices || []).forEach(function(oc) {
      var pickId = optionPicks ? optionPicks[groupIdSlug(oc.name)] : null;
      if (pickId == null) return;
      var chosen = (oc.options || []).filter(function(o) { return o.id === pickId; })[0];
      (chosen && chosen.skill_grants || []).forEach(function(g) {
        if (g.rank_limit && g.rank_limit > 2 && cb) caps[cb.skillSheetKeys[g.skill] || g.skill] = g.rank_limit;
      });
    });
    ctrl.updateCharacter('speciesSkillCaps', caps);
    // B-22: resolve the chosen options' STRUCTURED grants once — the later XP-pool and choice-pi — Docs/CREATOR_COMMENTARY.md § "useSpeciesConfirm › optXp"
    var optXp = (window.CharResolve && window.CharResolve.speciesOptionXpDelta)
      ? window.CharResolve.speciesOptionXpDelta(sp, optionPicks) : 0;
    var optSkillChoice = [], optTalents = [], optPicks = {};
    // LAP B: a group may now yield MORE THAN ONE option (Droid's 0-3 menu), so every branch below
    // accumulates across the chosen set instead of a single `chosen`. Read through the shared
    // normalizer — a scalar pick still resolves to a one-element list, so nothing about a saved
    // character changes.
    (sp.option_choices || []).forEach(function(oc) {
      var chosenSet = speciesChosen(oc, optionPicks);
      if (!chosenSet.length) return;
      chosenSet.forEach(function(chosen) {
        if (chosen.skill_choice_grants) optSkillChoice = optSkillChoice.concat(chosen.skill_choice_grants);
        if (chosen.starting_talents) optTalents = optTalents.concat(chosen.starting_talents);
      });
      // B-23 (G8): bank the PICK by stable keys (group-name slug -> option id) so finalize can
      // persist it — the one irreproducible fact (which option) the sheet can't re-derive. A
      // multi-pick group banks the LIST; a single-pick group keeps banking a scalar, so nothing
      // that already round-trips changes shape.
      var ids = chosenSet.map(function(o) { return o.id; }).filter(Boolean);
      if (ids.length) optPicks[groupIdSlug(oc.name)] = (ids.length > 1 ? ids : ids[0]);
    });
    ctrl.updateCharacter('speciesOption', { xp: optXp, skillChoiceGrants: optSkillChoice, startingTalents: optTalents, picks: optPicks });
  }, [ctrl, gameData]);

  var handleSpeciesPick = useCallback(function(slug, optionId) {
    var next = Object.assign({}, (ctrl.character && ctrl.character.speciesOptionPicks) || {});
    next[slug] = optionId;
    ctrl.updateCharacter('speciesOptionPicks', next);
    // C-OCS-4: if this species is already the confirmed one, the pick takes full effect now. Before
    // confirm there is nothing seeded to diverge from, and confirm applies it on the way through.
    var sel = ctrl.selectedSpeciesData;
    if (sel && ctrl.character && ctrl.character.species && ctrl.character.species.key === sel.id) {
      // C-SVR-1: hand the PREVIOUS picks through so the re-seed can be conditional on what changed.
      applySpeciesPicks(sel, next, (ctrl.character && ctrl.character.speciesOptionPicks) || {});
    }
  }, [ctrl, applySpeciesPicks]);

  var handleSpeciesConfirm = useCallback(function(sp, optionPicks, careerPicks) {
    // B-418: the map is COMPLETED before anything reads it — an untouched preselected group is
    // recorded as its first option here, once, so the persisted map, the grants and the applied
    // record below all see the same recorded pick (one path).
    optionPicks = fcRecordedPicks(sp, optionPicks);
    ctrl.setSelectedSpeciesData(sp);
    ctrl.updateCharacter('species', { key: sp.id, name: sp.name });
    ctrl.updateCharacter('speciesOptionPicks', Object.assign({}, optionPicks));   // B-418: the recorded map, the sheet's vocabulary
    // THE ONE WRITER of `speciesSkillChoices` (ruling `f100-cerean-intellectuals-card` R-CI-2, ADDENDUM 6): the species'
    // career pick, `{ [sp.id]: { [poolId]: [ids] } }`, only the species being committed (a re-species leaves no stale pick).
    // A caller with no draft (the legacy detail) commits what the record already holds for this species. Untouched for a
    // species with no career pick and no saved map, so every other species' commit writes nothing new.
    var careerGroups = fcCareerPickGroups(sp, gameData && gameData.charbuild);
    var priorChoices = ctrl.character && ctrl.character.speciesSkillChoices;
    if (careerGroups.length || (priorChoices && Object.keys(priorChoices).length)) {
      var cp = careerPicks || fcCareerPicksSeed(sp, careerGroups, priorChoices);
      ctrl.updateCharacter('speciesSkillChoices', fcSpeciesSkillChoicesFor(sp, careerGroups, cp));
    }
    // FORK B / C-OCS-4: the characteristics seed, the skill caps and the banked option grants are
    // all pick-dependent, so they live in ONE function that the pick handler calls too.
    applySpeciesPicks(sp, optionPicks);
    // Apply species free skill ranks (e.g. Twi'lek: Charm, Deception) — B-19: only the CHOSEN
    // pick-one option (optionPicks from the species-detail picker), never all options.
    // B-147 — MERGE THE SPECIES GRANT, NEVER REPLACE THE SKILLS OBJECT.
    // This used to build a fresh object from the grants and dispatch it as `skills`, and the
    // reducer's default branch is a wholesale replacement (player/index.html:341-342). So
    // re-selecting a species threw away every purchased rank, every `bought` entry, and the
    // freeCareer / freeSpec flags the career and specialisation steps had set. `SET_CAREER`
    // twenty lines below in the same reducer has a preservation routine written for exactly this
    // hazard; species confirm had none. Latent going forwards, because a species is chosen before
    // any of that exists, and reachable by going back.
    //
    // The species' FIXED grant lands in `purchased`, which is the same field a bought rank uses, so
    // it cannot be identified after the fact. `speciesGrantApplied` records what this step last
    // granted, so a re-confirm can remove exactly that and nothing else. Absent-tolerant: on a first
    // confirm there is nothing to remove and the merge is identical to the old replacement, which is
    // why the forward flow is unchanged.
    var grants = speciesSkillGrants(sp, optionPicks);
    var applied = (ctrl.character && ctrl.character.speciesGrantApplied) || {};
    var nextSkills = (window.CharResolve && window.CharResolve.speciesGrantMerge)
      ? window.CharResolve.speciesGrantMerge((ctrl.character && ctrl.character.skills) || {}, applied, grants)
      : grants;
    ctrl.dispatch({ type: 'SET_FIELD', field: 'skills', value: nextSkills });
    ctrl.updateCharacter('speciesGrantApplied', grants);
    // C-SVRA-3: what the last CONFIRM applied, so a later SELECT can tell whether the player has
    // changed anything since and needs the review.
    ctrl.updateCharacter('speciesPicksApplied', Object.assign({}, optionPicks || {}));
    ctrl.setSubView(null);
    ctrl.nextStep();
  }, [ctrl, speciesSkillGrants, gameData, applySpeciesPicks]);

  // C-SVRA-3 — THE CHANGE REVIEW (Jason 2026-08-11; frame H of the approved mockup).
  // Pressing SELECT on a species that is ALREADY yours, after changing a pick that moves a resolved
  // value, opens a review first: what it costs, then Confirm or Go Back. This is the creator's
  // inform-never-gate law (app.jsx:6864, where the overdrawn-XP guard was DELETED rather than
  // bypassed) reaching the last place that still changed a character silently.
  var _useSR = useState(null), speciesReview = _useSR[0], setSpeciesReview = _useSR[1];

  var handleSpeciesSelectPress = useCallback(function(sp, optionPicks, careerPicks) {
    var ch = ctrl.character || {};
    var already = ch.species && ch.species.key === sp.id;
    var appliedPicks = ch.speciesPicksApplied;
    // No review before the species is yours, and none when nothing resolved moved: the same
    // predicate C-SVR-1 uses, now deciding whether to SHOW rather than whether to seed.
    if (!already || appliedPicks === undefined || !speciesNeedsReseed(sp, appliedPicks, optionPicks)) {
      handleSpeciesConfirm(sp, optionPicks, careerPicks);
      return;
    }
    var before = speciesEff(sp, appliedPicks), after = speciesEff(sp, optionPicks);
    var optXp = function(picks) {
      var n = 0;
      (sp.option_choices || []).forEach(function(oc) {
        // Same normalizer, same reason — the delta SUMS across a multi-pick group (Q2).
        speciesChosen(oc, picks).forEach(function(o) {
          if (o && o.xp_adjustment) n += o.xp_adjustment;
        });
      });
      return n;
    };
    // XP TO SPEND, before versus after. The pool moves by the variant's own starting XP and by any
    // delta option; and because confirming re-seeds the characteristics, every XP the player spent
    // raising them is refunded. That refund is the larger half of the number and the reason the
    // figure is usually positive.
    var poolDelta = (after.starting_xp + optXp(optionPicks)) - (before.starting_xp + optXp(appliedPicks));
    setSpeciesReview({
      sp: sp, picks: optionPicks, careerPicks: careerPicks,
      xpSwing: poolDelta + (ctrl.xpSpentOnChars || 0),
      changed: (sp.option_choices || []).filter(function(oc) {
        var g = groupIdSlug(oc.name);
        return (appliedPicks || {})[g] !== (optionPicks || {})[g];
      }).map(function(oc) {
        var find = function(picks) {
          var id = picks ? picks[groupIdSlug(oc.name)] : null;
          var o = id != null ? (oc.options || []).filter(function(x) { return x.id === id; })[0] : null;
          return o ? o.name : ((oc.options || [])[0] || {}).name;
        };
        return { group: oc.display_name || oc.name, from: find(appliedPicks), to: find(optionPicks) };
      }),
    });
  }, [ctrl, handleSpeciesConfirm]);
  return { speciesSkillGrants: speciesSkillGrants, handleSpeciesSelect: handleSpeciesSelect, handleSourceUse: handleSourceUse, handleSourceCancel: handleSourceCancel, applySpeciesPicks: applySpeciesPicks, handleSpeciesPick: handleSpeciesPick, handleSpeciesConfirm: handleSpeciesConfirm, handleSpeciesSelectPress: handleSpeciesSelectPress, srcCandidates: srcCandidates, setSrcCandidates: setSrcCandidates, speciesReview: speciesReview, setSpeciesReview: setSpeciesReview };
}

function FullCreator({ onBack, onComplete, resumeDraft }) {
  var _useGD = useState(null), gameData = _useGD[0], setGameData = _useGD[1];
  var _useLD = useState(true), loading = _useLD[0], setLoading = _useLD[1];
  var _useLE = useState(false), loadError = _useLE[0], setLoadError = _useLE[1];
  var ctrl = useFullCreatorController(resumeDraft, gameData && gameData.charbuild);   // F-100 M10 B-1222: the view upgrades price from
  var fcStepLabelsNow = fcStepLabels(ctrl.gameLine);   // B-1668: the step list's words, keyed by the line
  var mode = useLayoutMode();  // F-46 D1 adoption 2 (custom creator)
  // The three-band resolution for this surface. Published on the shell as `data-band` so
  // the AS-9 smoke can assert the band actually resolved at each width, and so C3+ (the
  // first composition consumers) read one value instead of re-deriving the seams.
  var band = useCreatorBand();
  // B-86: the inspected tree cell for the DETAILS rail. Only ever set at mid/wide — on mobile no
  // `onInspect` is passed, so DirectionalTree keeps its own bottom sheet and the phone is untouched.
  var _tsel = useState(null), treeSel = _tsel[0], setTreeSel = _tsel[1];
  // B-94: the store sub-view's own tab + badge state. The wizard is immersive (the global bottom
  // bar is suppressed), so the creator renders the store's contextual bar itself — the same shared
  // BottomNav the player app switches to under ADR 0010, not a copy of it.
  var _stab = useState('weapons'), storeTab = _stab[0], setStoreTab = _stab[1];
  var _scnt = useState({ cart: 0, holds: 0 }), storeCounts = _scnt[0], setStoreCounts = _scnt[1];
  // B-30: if the GAME-VARIANTS toggle is ON but its overlay pack failed to load, the creator would
  // silently show base trees — surface an honest notice on the talents step with a RETRY that
  // re-fetches the pack + reloads the charbuild.
  var _fcModsFailed = useModsLoadError();
  // F-100 M6 R5 (ruling f100-m6-pack R-M6G-2): a RE-ENTERED draft carries its character's own packs on its `reentry`
  // marker. For such a draft the boot view, every step, the review and finalize's stamps come from THAT list; the
  // toggles never move it. A new character's draft has no marker, and its path is unchanged.
  var _reentryMods = (resumeDraft && resumeDraft.flow === 'custom' && resumeDraft.reentry
    && Array.isArray(resumeDraft.reentry.mods)) ? resumeDraft.reentry.mods : null;
  // B-30's RETRY, the creator's one handler: drop the failed stamps, clear the load caches, boot again (both faces).
  var _fcModsRetry = function() { if (window.SWRPGMods && window.SWRPGMods.retry) window.SWRPGMods.retry(); clearCanonicalLoadCaches(); bootLoad(); };
  var _fcModsNotice = (function() {
    var act = _reentryMods || ((window.SWRPGMods && window.SWRPGMods.active) ? window.SWRPGMods.active() : []);
    return !!(act.length && _fcModsFailed.length && act.some(function(s) { return _fcModsFailed.indexOf(s) >= 0; }));
  })();
  // R-M6G-12 (a): the sources box reads a re-entered draft's own packs and its own view (frames C1–C4).
  var _fcSourcesReentry = _reentryMods ? { mods: _reentryMods, view: (gameData && gameData.charbuild) || null } : null;
  var mechanicBackRef = useRef({ handleBack: null });

  // B-27: the boot is retryable — any fetch rejection OR a null-resolving loader (both
  // loaders swallow failures into null) lands on the shared error screen, never a
  // forever-spinner or a null dereference.
  function bootLoad() {
    setLoadError(false); setLoading(true);
    // BOOKS-1c: gate on SWRPGSources.ready() too, so the ACTIVE SOURCES index is loaded before any
    // picker renders (fcFilterActive reads it synchronously — no flash of unfiltered content).
    var sourcesReady = (window.SWRPGSources && window.SWRPGSources.ready) ? window.SWRPGSources.ready() : Promise.resolve();
    Promise.all([loadQuickBuildData(), loadCanonicalRegistries(), loadCanonicalCharacterBuild(), sourcesReady]).then(function(res) {
      if (!res[1] || !res[2]) { setLoadError(true); setLoading(false); return; }
      // F-100 M6 R5: a re-entered draft resolves through its character's own stamp view over the pristine base.
      var cbReady = (_reentryMods && window.SWRPGMods)
        ? window.SWRPGMods.resolveForStamp(_reentryMods, window._canonicalCharbuildBase) : Promise.resolve(res[2]);
      return cbReady.then(function(cb) {
        if (!cb) { setLoadError(true); setLoading(false); return; }
        var gd = { charbuild: cb, narrativeMechanics: res[0].narrativeMechanics, canonical: res[1] };
        if (_reentryMods) gd.stampMods = _reentryMods.slice();   // what finalize stamps (normalizeCreated)
        setGameData(gd);
        setLoading(false);
      });
    }).catch(function(err) {
      console.warn('[creator] boot load failed:', err);
      setLoadError(true); setLoading(false);
    });
  }
  useEffect(function() { bootLoad(); }, []);

  // BOOKS-1c: re-render when the ACTIVE SOURCES set OR the "limit to campaign era" preference changes
  // (a GM may toggle it in ⚙ mid-session), so the pickers re-filter live.
  var _srcTick = useState(0), srcTick = _srcTick[0], setSrcTick = _srcTick[1];
  useEffect(function() {
    var on = function() { setSrcTick(function(t) { return t + 1; }); };
    window.addEventListener('swrpg-sources-changed', on);
    return function() { window.removeEventListener('swrpg-sources-changed', on); };
  }, []);

  // F-9 C1 — the reSpecialized toggle flips mid-creator: the mod REPLACES tree data (not just gates
  // it), so re-RESOLVE the charbuild against the new active set and swap it into gameData live. Clear
  // the cache first so the reload re-runs B2's merge (order-independent of B2's own invalidator).
  useEffect(function() {
    var on = function() {
      window._canonicalCharbuild = null; window._canonicalCharbuildPromise = null;
      if (_reentryMods) return;   // F-100 M6 R5: a re-entered draft's view is its own stamps'; a toggle never moves it
      loadCanonicalCharacterBuild().then(function(cb) {
        if (cb) setGameData(function(prev) { return prev ? Object.assign({}, prev, { charbuild: cb }) : prev; });
      });
    };
    window.addEventListener('swrpg-mods-changed', on);
    return function() { window.removeEventListener('swrpg-mods-changed', on); };
  }, []);

  var accent = ctrl.gameLine ? QB_ACCENT[ctrl.gameLine] : '#F5A623';
  var bg = ctrl.gameLine ? QB_GRAD[ctrl.gameLine] : 'linear-gradient(160deg, #0d0805 0%, #000 100%)';

  // Canonical registry views (maps id->record; lists for the browsers).
  // BOOKS-1c (campaign model): pickers honor ACTIVE SOURCES (off-set) always; line-gating is the
  // OPT-IN "limit to campaign era" preference (default OFF — the game encourages cross-era). When on,
  // content gates to the campaign's line (eraSet = [the campaign, i.e. gameLine]).
  var linePref = !!(window.SWRPGSources && window.SWRPGSources.lineFilterOn && window.SWRPGSources.lineFilterOn());
  var eraSet = ctrl.gameLine ? [ctrl.gameLine] : [];
  // F-67 (ruling `csg-species-import-census` AMENDMENT 1 A-2) — ONE RECORD PER SPECIES NAME. — Docs/CREATOR_COMMENTARY.md § "FullCreator › cbSpeciesList"
  var cbSpeciesList = useMemo(function() {
    if (!gameData) return [];
    var active = fcFilterActive(Object.values(gameData.charbuild.species), eraSet, linePref);
    var S = window.SWRPGSources, cur = S && S.current();
    // The register is ALPHABETICAL across the whole shelf (revision: species-register-order) — a
    // reader scanning for a name must not find the cores first and the supplements after.
    var list = (cur && cur.index) ? S.dedupeByName(active, cur.index) : active;
    return list.slice().sort(function(a, b) {
      return String(a && a.name).localeCompare(String(b && b.name));
    });
  }, [gameData, ctrl.gameLine, linePref, srcTick]);
  // THE GROUPS THE DEDUPE IS ABOUT TO DISCARD (ruling `multi-source-picker`, R-MSP-A/C).
  // Same input, same key, one line earlier: `dedupeByName` above keeps one record per name, and
  // until now the fact that a name HAD other candidates died right here — silently, which is the
  // defect. Grouping from the same `active` list means the chip's number and the list's collapse
  // can never disagree; deriving it from anything else is how a marker starts lying.
  var cbSpeciesGroups = useMemo(function() {
    if (!gameData) return {};
    var active = fcFilterActive(Object.values(gameData.charbuild.species), eraSet, linePref);
    var S = window.SWRPGSources;
    return (S && S.groupByName) ? S.groupByName(active) : {};
  }, [gameData, ctrl.gameLine, linePref, srcTick]);
  var cbCareerList = useMemo(function() {
    return gameData ? fcFilterActive(Object.values(gameData.charbuild.careers), eraSet, linePref) : [];
  }, [gameData, ctrl.gameLine, linePref, srcTick]);

  // Step 0 handler: Game Line
  var handleGameLine = useCallback(function(gl) {
    ctrl.setGameLine(gl);
    ctrl.dispatch({ type: 'HYDRATE', value: createEmptyCharacter(gl) });
    ctrl.nextStep();
  }, [ctrl]);


  // The species-confirm flow lives in useSpeciesConfirm (audit item F) — see it for the whole
  // select → source pick → option picks → apply/reseed → review → confirm chain.
  var _sc = useSpeciesConfirm(ctrl, gameData, cbSpeciesGroups);
  var speciesSkillGrants = _sc.speciesSkillGrants;
  var handleSpeciesSelect = _sc.handleSpeciesSelect;
  var handleSourceUse = _sc.handleSourceUse;
  var handleSourceCancel = _sc.handleSourceCancel;
  var applySpeciesPicks = _sc.applySpeciesPicks;
  var handleSpeciesPick = _sc.handleSpeciesPick;
  var handleSpeciesConfirm = _sc.handleSpeciesConfirm;
  var handleSpeciesSelectPress = _sc.handleSpeciesSelectPress;
  var srcCandidates = _sc.srcCandidates;
  var setSrcCandidates = _sc.setSrcCandidates;
  var speciesReview = _sc.speciesReview;
  var setSpeciesReview = _sc.setSpeciesReview;

  // ═══ B-563 · SPECIES STEP WIRING (increment 3) — the transplant flag and the species-phone state ═══
  // Which steps render the revision's transplanted composition, per band. Lit ONE STEP PER LAP on the
  // instruments (creator_eyes at three bands, the census, the map, the step's smoke with real presses).
  // A step not in this table renders the legacy shell below exactly as before.
  var FC_TRANSPLANTED_STEPS = {
    gameline:       { mobile: 1, mid: 1, wide: 1 },
    species:        { mobile: 1, mid: 1, wide: 1 },
    career:         { mobile: 1, mid: 1, wide: 1 },
    specialization: { mobile: 1, mid: 1, wide: 1 },
    mechanic:       { mobile: 1, mid: 1, wide: 1 },
    characteristics: { mobile: 1, mid: 1, wide: 1 },
    skills:         { mobile: 1, mid: 1, wide: 1 },
    talents:        { mobile: 1, mid: 1, wide: 1 },
    forcepowers:    { mobile: 1, mid: 1, wide: 1 },
    gear:           { mobile: 1, mid: 1, wide: 1 },
    motivation:     { mobile: 1, mid: 1, wide: 1 },
    review:         { mobile: 1, mid: 1, wide: 1 },
  };
  // DARK UNTIL THE TWELFTH STEP LANDS (B-563; Jason's HOLOTABLE posture: dark deploys, one
  // feature-complete launch). The transplant is lit by `fcTransplantSwitch()` — a localStorage key or
  // `?transplant=1` — which the instruments set and players do not have. Every step's code ships on
  // commercial as it lands, verified lit; what a player sees stays the legacy creator until the
  // switch is retired with the last step.
  var fcTransplanted = fcTransplantSwitch() && !!(FC_TRANSPLANTED_STEPS[ctrl.currentStep] || {})[band];
  /* B-671 — THE CREATOR'S STORE DOOR, ON PAPER (R-SPC-2: "the creator's gear step — under the
     creator's own switch"; lab frame K, `creator_board()`). ONE predicate, named once, because three
     things downstream must agree about it: the face the kernel draws, the overlay's GROUND (a paper
     catalog inside a `#02010a` box would show a black margin at the one place the store does not
     cover), and WHICH purse strip renders — the kernel's, as the frame draws it, or the shipped dark
     one. It is the AND of both switches, which is row 1's reasoning unchanged: the store's switch
     decides the face, the creator's decides whether this door is standing in paper at all, and either
     one off leaves the door exactly as it ships. */
  var creatorStorePaper = storeTransplantSwitch() && fcTransplanted;
  // B-1644 (ruling `release-r7-bug-burndown` ADDENDUM 373 A + CONDITION) — this Store has no ship seam (B-1643's 'off'), so a
  // vessel line on the one global slip is named by the Yard's own registry cache. The names load ONLY while the gear step's
  // store is open AND the slip holds a vpart:/vmod:/vweap: line: a vessel-free creator fetches no vehicle file. No line of
  // that kind can be added here (no parts shelf, no hold door), so the slip read at render is the slip the store opens on.
  var creatorVesselNamesOn = ctrl.currentStep === 'gear' && ctrl.subView === 'store' && !!window.SWRPGStore
    && (window.SWRPGStore.session.cart || []).some(function (l) { return /^(vpart|vmod|vweap):/.test(String(l && l.itemId)); });
  var creatorVesselName = window.SWRPGVesselView && window.SWRPGVesselView.useCounterNames
    ? window.SWRPGVesselView.useCounterNames(creatorVesselNamesOn) : null;
  /* B-671 — THE CREATOR'S PURSE, AS FRAME K DRAWS IT: the same strip as the sheet's door (`.cr-purse`,
     PURSE · the reading · one act) with the door's own two differences — the value is measured against a
     STARTING ALLOWANCE, so it prints `left / 500 CREDITS`, and the denominator exists ONLY here
     (`ctrl.credits.total`; the kernel's `shopFor` carries the remainder and nothing else). The act is
     DONE rather than COUNTER, because this door closes into the wizard instead of walking to a tab.
     `hook` carries the shipped `data-creator-purse` attribute across the face change — three suites
     drive it. Named rather than inline for the same reason as `storePurse`: the store map reads the
     mount's object literal. */
  var creatorPurse = { value: ctrl.credits.remaining, total: ctrl.credits.total,
                       hook: 'data-creator-purse', label: 'DONE',
                       onAct: function() { ctrl.setSubView(null); } };
  useEffect(function() { if (fcTransplanted) { fcUpdateScrollCues(); var t = setTimeout(fcUpdateScrollCues, 400); return function() { clearTimeout(t); }; } });   // B-586

  var fcCharChanges = fcTransplanted
    ? fcCharChangeRows(ctrl.character, ctrl.selectedSpeciesData) : [];

  // BOUGHT HERE — the rail's purchase log on the board's buyout variant. Every raise above the
  // species base, priced by the same RAW rule the ledger sells at, plus each buyout actually taken
  // at the COST STORED ON THE ENTRY (never re-derived at read — R-SFC-2's own discipline, and the
  // reason a later price change cannot silently rewrite what a player paid).
  var fcBoughtHere = (function() {
    if (!fcTransplanted || !ctrl.character || !ctrl.selectedSpeciesData) return [];
    var eff = speciesEff(ctrl.selectedSpeciesData, ctrl.character.speciesOptionPicks) || {};
    var bases = eff.characteristics || {};
    var rows = [];
    ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'].forEach(function(a) {
      var to = (ctrl.character.characteristics || {})[a] || 2, from = bases[a] || 2;
      for (var v = from + 1; v <= to; v++) {
        rows.push({ k: FC_CHAR_WORDS[a].charAt(0) + FC_CHAR_WORDS[a].slice(1).toLowerCase()
          + ' ' + (v - 1) + ' \u2192 ' + v, xp: v * 10 });
      }
    });
    var taken = ctrl.character.creationBuyouts || {};
    (fcBuyoutOffers || []).forEach(function(o) {
      if (taken[o.slug]) rows.push({ k: 'Remove ' + o.name, xp: taken[o.slug].xp });
    });
    return rows;
  })();
  // The board prints two cites on the standfirst's plate. Ours prints the corpus-verified pair for
  // the live game line — a RULED delta (R-VTG-3: EotE p.39, because the board's p.92 is the book's
  // own broken cross-reference) and one of R-TTP-1's enumerated allowed differences.
  var fcCharCites = (function() {
    // TWO CHIPS, because the board draws two — and the count is structure, not text, so one chip
    // would be a real divergence rather than a data difference.
    //
    // WHICH two is a judgement I am FLAGGING, not assuming. The board prints EotE and AoR on an
    // EotE character, saying in effect "this rule is in both core books" — which is true, and
    // R-EQW1 makes the three cores peers. Printing the OTHER lines' pages to a Force and Destiny
    // player would be strictly worse than what we shipped, so the order here is: THE PLAYER'S OWN
    // LINE FIRST, then the core that most players will also own. Every page is the ruled,
    // corpus-verified one (R-VTG-3).
    var cites = gameData && gameData.charbuild && gameData.charbuild.stepCites;
    var row = (cites && cites.characteristics) || {};   // B-1349: derived, printed through the converter
    var mine = row[ctrl.gameLine];
    var second = row[ctrl.gameLine === 'eote' ? 'aor' : 'eote'];
    return [mine, second].filter(function(c) { return c && c.printed && c.printed.length; })
      .map(function(c) { return c.book.toUpperCase() + ' P.' + c.printed[0]; });
  })();
  var _useSQ = useState(''), fcSpeciesQuery = _useSQ[0], setFcSpeciesQuery = _useSQ[1];
  var _useSPS = useState('list'), fcPhoneScreen = _useSPS[0], setFcPhoneScreen = _useSPS[1];
  var fcSpeciesRows = useMemo(function() {
    var q = String(fcSpeciesQuery || '').trim().toLowerCase();
    var list = q
      ? (cbSpeciesList || []).filter(function(sp) {
          return String(sp.name || '').toLowerCase().indexOf(q) >= 0;
        })
      : (cbSpeciesList || []);
    // HOW MANY ACTIVE RECORDS SUPPLY THIS ROW'S NAME — carried on the ROW, derived from the same
    // groups the chips read and keyed the same way, so a row counts as multi-sourced if and only
    // if the list actually collapsed something into it. Not a second source of truth: this is the
    // legacy browser's own `countFor`, moved to where the transplanted register can reach it.
    var SRC = window.SWRPGSources;
    var countFor = function(sp) {
      var key = (SRC && SRC.dedupeName) ? SRC.dedupeName(sp && sp.name) : null;
      var g = key && cbSpeciesGroups ? cbSpeciesGroups[key] : null;
      return g ? g.length : 1;
    };
    return list.map(function(sp) {
      // B-399: a row's run and XP are the RESOLVED variant's — the selected species under its picks,
      // every other under preselect (the first option) or its base when it carries no variant group.
      var effRow = fcSpeciesEffRecord(sp, (ctrl.selectedSpeciesData && sp.id === ctrl.selectedSpeciesData.id) ? fcSpeciesPicks : {}) || sp;
      var ch = effRow.characteristics || {};
      var srcCount = countFor(sp);
      return {
        id: sp.id, name: sp.name, srcCount: srcCount, multi: srcCount > 1,
        plateClass: 'sp-' + String(sp.id || '').replace(/[^a-z0-9_]/gi, '_').toLowerCase(),
        wash: speciesImgPath(sp),
        run: FC_CHAR_ORDER.map(function(a) { return ch[a]; }).join(' \u00b7 '),
        xpn: effRow.starting_xp, unit: 'XP',
      };
    });
  }, [cbSpeciesList, cbSpeciesGroups, srcTick, fcSpeciesQuery, ctrl.selectedSpeciesData, fcSpeciesPicks]);
  var fcSpeciesMultiCount = useMemo(function() {
    return fcSpeciesRows.filter(function(r) { return r.multi; }).length;
  }, [fcSpeciesRows]);
  var _useOP = useState({}), fcSpeciesPicks = _useOP[0], setFcSpeciesPicks = _useOP[1];
  var _useMV = useState(null), fcSpeciesMoved = _useMV[0], setFcSpeciesMoved = _useMV[1];
  var fcSpeciesId = (ctrl.selectedSpeciesData || {}).id;
  // B-575 — THE PICKS HYDRATE FROM THE RECORD ON RESUME. This state began EMPTY on every species
  // change, including the mount a resumed draft lands on: a player who had picked Offshoot, moved
  // on, and came BACK (or reloaded) saw Baseline ticked and the base numbers in the ledger, and
  // CONFIRM would have re-applied Baseline over their choice — the legacy dossier reads
  // `character.speciesOptionPicks` and never had this. Found by the output-contract probe
  // (`scripts/probe_exception_contract.py`, lit): every number-moving key read BASE in the picked
  // state while a real press read the golden. So: when the selected species IS the confirmed one,
  // start from the recorded map (the same one confirm writes, B-418); otherwise empty, as before.
  useEffect(function() {
    var ch = ctrl.character;
    var same = fcSpeciesId && ch && ch.species && ch.species.key === fcSpeciesId;
    setFcSpeciesPicks(same ? Object.assign({}, ch.speciesOptionPicks || {}) : {});
    setFcSpeciesMoved(null);
  }, [fcSpeciesId]);
  var fcOnSpeciesPick = React.useCallback(function(slug, optId) {
    // ONE SNAPSHOT FOR BOTH RESULTS (R-CPD-6(c)). The new picks and the mark are two readings of
    // the SAME transition, so `prev` is read once here rather than by two independent functional
    // updaters that could disagree about what "before" was. The functional form existed to be safe
    // against a queued update; there is no such queue for a picker one tap at a time, and a shared
    // "before" is the stronger property.
    var next = Object.assign({}, fcSpeciesPicks);
    // B-445 — THE RULE LIVES HERE, so neither band can hold a different one. A group that allows
    // several TOGGLES: press an unpicked option and it joins the list, press a picked one and it
    // leaves. AT THE CAP A PRESS IS REFUSED IN PLACE and changes nothing — never a silent swap of
    // the earliest pick, which is the one behaviour a player cannot see happening (R-SMR-1, and the
    // approved frames M2/M4 draw the refusal).
    var grp = ((ctrl.selectedSpeciesData || {}).option_choices || []).filter(function(g) {
      return groupIdSlug(g.name) === slug;
    })[0];
    if (grp && boundsOf(grp).max > 1) {
      var list = fcMultiPickNext(grp, fcSpeciesPicks, optId);
      if (!list) return;                                      // refused, in place
      next[slug] = list;
    } else {
      next[slug] = optId;                                     // single-pick, unchanged
    }
    setFcSpeciesMoved(fcSpeciesMovedSet(ctrl.selectedSpeciesData, fcSpeciesPicks, next));
    setFcSpeciesPicks(next);
  }, [ctrl.selectedSpeciesData, fcSpeciesPicks]);
  // THE SPECIES' CAREER PICK — its groups (the shared core), its DRAFT picks (a sibling of `fcSpeciesPicks`, keyed the same
  // way), seeded from the record on species change so a saved pick survives a flip back (B-124), and written only by the
  // species commit (`handleSpeciesConfirm`, the ONE writer of `speciesSkillChoices`).
  var fcCareerGroups = useMemo(function() {
    return fcCareerPickGroups(ctrl.selectedSpeciesData, gameData && gameData.charbuild);
  }, [ctrl.selectedSpeciesData, gameData]);
  var fcCareerGroupsKey = fcCareerGroups.map(function(g) { return g.name + '|' + g.career_pick.poolId; }).join(',');
  var _useCK = useState({}), fcCareerPicks = _useCK[0], setFcCareerPicks = _useCK[1];
  useEffect(function() {
    setFcCareerPicks(fcCareerPicksSeed(ctrl.selectedSpeciesData, fcCareerGroups,
      ctrl.character && ctrl.character.speciesSkillChoices));
  }, [fcSpeciesId, fcCareerGroupsKey]);
  var fcOnCareerPick = React.useCallback(function(slug, optId) {
    var grp = fcCareerGroups.filter(function(g) { return groupIdSlug(g.name) === slug; })[0];
    if (!grp) return;
    var list = fcMultiPickNext(grp, fcCareerPicks, optId);
    if (!list) return;                                        // refused at the cap, in place
    var next = Object.assign({}, fcCareerPicks);
    next[slug] = list;
    setFcCareerPicks(next);
  }, [fcCareerGroups, fcCareerPicks]);
  var fcSpeciesUnits = useMemo(function() {
    return fcSpeciesUnitsOf(ctrl.selectedSpeciesData);     // B-400: the ONE builder both bands read
  }, [ctrl.selectedSpeciesData]);

  // ═══ B-563 · THE BROWSE CLUSTER — career + specialization locals (verbatim from the revision) ═══
  var fcSpecRows = useMemo(function() {
    var career = ctrl.selectedCareerData;
    if (!career || !gameData) return [];
    var all = gameData.charbuild.specializations || {};
    return (career.specializations || []).map(function(id) {
      var r = all[id] || {};
      return { id: id, name: r.name || id,
               count: (r.career_skills || []).length, unit: 'skills' };
    });
  }, [ctrl.selectedCareerData, gameData]);

  var fcSpecRefusal = function(r) {
    if (r.refused) return r;
    var all = (gameData && gameData.charbuild.specializations) || {};
    var rec = all[r.id];
    var need = rec ? minForceRatingOf(rec) : 0;
    if (need > 0 && rec && !meetsForceRating(rec, fcForceRating)) {
      return Object.assign({}, r, {
        refused: true,
        why: 'Needs Force rating ' + need + ' \u00b7 you have ' + (Number(fcForceRating) || 0),
      });
    }
    return r;
  };

  var fcSpecUniversalRows = useMemo(function() {
    var career = ctrl.selectedCareerData;
    if (!career || !gameData) return [];
    var all = gameData.charbuild.specializations || {};
    var own = career.specializations || [];
    var linePref = !!(window.SWRPGSources && window.SWRPGSources.lineFilterOn
                      && window.SWRPGSources.lineFilterOn());
    var isDroid = !!(ctrl.selectedSpeciesData && CharResolve.isDroidSpecies(ctrl.selectedSpeciesData.id));   // B-1606: the one droid test
    return fcFilterActive(
      Object.keys(all).map(function(id) { return all[id]; })
        .filter(function(r) { return r && r.universal && own.indexOf(r.id) === -1; }),
      [ctrl.gameLine], linePref)
      .sort(function(a, b) { return (a.name || '').localeCompare(b.name || ''); })
      .map(function(r) {
        return { id: r.id, name: r.name || r.id, withheld: isDroid && Number(r.force_rating) >= 1,
                 count: (r.career_skills || []).length, unit: 'skills' };
      });
  }, [ctrl.selectedCareerData, ctrl.selectedSpeciesData, ctrl.gameLine, gameData]);

  var fcSpecShelfShown = fcSpecUniversalRows.filter(function(r) { return !r.withheld; });

  var fcSpecShelfWithheld = fcSpecUniversalRows.filter(function(r) { return r.withheld; });

  var fcSpecSkills = useMemo(function() {
    var rec = ctrl.selectedSpecData;
    if (!rec || !gameData) return [];
    var cb = gameData.charbuild, names = cb.skillNames || {}, chars = cb.skillChars || {};
    return (rec.career_skills || []).map(function(id) {
      // R-CPD-8 (`creator-pre-deploy-batch`), ENFORCING board §5 ban item 8 — "never abbreviated
      // (mechanics-voice rule; and the book prints the words)", whose own note adds that a truncated
      // label IS an abbreviation and the ban has no small print. The words already existed here.
      return { name: names[id] || id, chr: (FC_CHAR_WORDS[chars[id]] || '').toString() };
    });
  }, [ctrl.selectedSpecData, gameData]);

  var fcSpecTree = useMemo(function() {
    var rec = ctrl.selectedSpecData;
    if (!rec || !gameData) return null;
    return talentTreeToModel({ specData: rec, talentDb: gameData.charbuild.talents || {},
                               character: { talents: {} }, readOnly: true, skin: 'board', interactive: false, cssSized: true });
  }, [ctrl.selectedSpecData, gameData]);

  var fcGameLineRows = useMemo(function() {
    var careers = ((gameData && gameData.charbuild) || window._canonicalCharbuild || {}).careers || {};   // F-100 M6 R5: the draft's own view
    var perSource = {};
    Object.keys(careers).forEach(function(id) {
      ((careers[id] || {}).sources || []).forEach(function(src) {
        perSource[src] = (perSource[src] || 0) + 1;
      });
    });
    var MECH = { obligation: 'Obligation', duty: 'Duty', morality: 'Morality' };
    return FC_GAME_LINES.map(function(l) {
      var cite = ((gameData && gameData.charbuild && gameData.charbuild.stepCites || {}).gameline || {})[l.id];   // B-1349: derived
      return {
        id: l.id,
        title: gameLineTitle(l.id) || l.fallbackTitle,
        grad: l.grad,
        accent: l.accent,
        mechanicName: MECH[l.mechanic] || l.mechanic,
        // The board's plate carries a short subtitle under the mechanic's name ("A Shared Debt").
        // Its sentence is cut (copy-voice-sweep, D-289), and the description slot goes with it.
        mechanicSub: (FC_GAMELINE_PLATE[l.id] || {}).sub,
        // COUNTED, NOT TYPED. The board's plate says "6 careers" for all three; ours says what the
        // player's own shelf actually offers, which is the same number until a community book is
        // switched on and then is not.
        careerCount: perSource[l.id] || 0,
        cite: cite && cite.printed && cite.printed.length ? (l.id + ' p.' + cite.printed[0]) : null,
      };
    });
    // `gameData` is in the keys because the count reads the CHARBUILD: on a cold /player/custom this memo ran before
    // it arrived and printed "0 careers" on all three plates for good (verify lap, smoke_gameline_career_count).
  }, [srcTick, gameData]);

  var _useGL = useState(null), fcGameLinePick = _useGL[0], setFcGameLinePick = _useGL[1];

  var _useSCP = useState({}), fcSpecPicks = _useSCP[0], setFcSpecPicks = _useSCP[1];

  var fcSpecId = (ctrl.selectedSpecData || {}).id;

  useEffect(function() { setFcSpecPicks({}); }, [fcSpecId]);

  var fcForceRating = gameData ? creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild) : 0;   // F-100: registry-fed, throws without the charbuild — the LOADING frame (above `if (loading)`, no gameData yet) reads 0; nothing consuming it renders until gameData lands

  var fcSpecGate = useMemo(function() {
    var sp = ctrl.selectedSpecData;
    if (!sp) return null;
    var needFR = minForceRatingOf(sp);
    var reqId = sp.species_requirement;
    var cur = ctrl.selectedSpeciesData;
    var grants = sp.career_skill_choice_grants || [];
    var cb = gameData && gameData.charbuild;
    var statusIds = creatorCareerStatusIds(cb, ctrl.selectedCareerData, sp.id, {   // F-100 M7 C2: the one answer
      id: ctrl.character.id,   // R6 lane D (Q6 c)
      talents: ctrl.character.talents, talentSkillChoices: ctrl.character.talentSkillChoices,
      speciesId: cur && cur.id, speciesOptionPicks: ctrl.character.speciesOptionPicks,
      speciesSkillChoices: ctrl.character.speciesSkillChoices });
    // THE POOL HELPER RETURNS SKILL IDS, AND THE PLATE READS OBJECTS — resolved HERE, where the
    // name map already is, rather than by teaching `speciesChoicePoolOf` a second return shape.
    // That helper is shared with the SPECIES step, whose picker takes ids plus `skillNames`
    // separately; changing it would have given one question two answers.
    //
    // WHAT THIS FIXES (B-387). `fcSpecPoolPlates` reads `opt.id` and `opt.name`. Handed raw strings
    // both are `undefined`, so: every option rendered as a BLANK row, `taken.indexOf(undefined)`
    // never matched so nothing could latch, and `onPick` recorded `undefined` — which means
    // `allPicked` could never be satisfied and the commit stayed GATED FOREVER. Seven visible,
    // nameless, unpressable-in-effect rows above a button that never opens.
    //
    // IT WAS INVISIBLE UNTIL THIS CARD. Exactly one specialization carries a choice grant
    // (`republic_representative`, `knowledge`, pick 1) and it is UNIVERSAL — so before R-WUS-1's
    // shelf it was unreachable at this band and the defect could not be reached to be seen. The
    // shelf is what surfaced it, which is why it is fixed in the same increment: a shelf that shows
    // a specialization you cannot take is worse than the shelf that hid it.
    //
    // THE SAVE SHAPE IS UNCHANGED — `fcSpecPicks[poolId] = [skillId, ...]`, which is what the
    // legacy detail has always written (`app.jsx:4456-4467`, mapping the same helper's output as
    // strings). This restores the intended write; it does not alter a persisted contract.
    var poolNames = (cb && cb.skillNames) || {};
    var pools = grants.map(function(g) {
      var pid = Array.isArray(g.pool) ? g.pool.join('+') : g.pool;
      return { id: pid, count: Number(g.count) || 1,
               options: (cb ? speciesChoicePoolOf(g, cb, statusIds) : []).map(function(sid) {
                 return { id: sid, name: poolNames[sid] || sid };
               }) };
    });
    // THE SPECIES LOOKUP IS DONE FIRST, ON ITS OWN, and that ordering is deliberate.
    // `test/min_force_rating.cjs` guards that nothing DELETES OR FILTERS owned powers or specs on
    // a failed prerequisite, and it does so by grepping for `splice`/`filter(`/`delete` within 200
    // characters of `meetsForceRating`. Written inline beside the Force check, this list lookup —
    // which is about a SPECIES requirement and touches nothing owned — tripped that proximity and
    // reddened a guard whose claim remains true. Hoisting it is the honest fix: the two
    // computations were unrelated and only ever adjacent, and the guard keeps its full strength
    // over the code it is actually aimed at. FLAGGED at the gate: the pattern is proximity-based
    // and will false-positive again on any neighbour that filters anything.
    var switchTo = null;
    if (reqId && cur && cur.id !== reqId) {
      var found = (cbSpeciesList || []).filter(function(x) { return x.id === reqId; })[0];
      switchTo = found || { id: reqId, name: reqId };
    }
    return {
      needFR: needFR,
      frRefused: needFR > 0 && !meetsForceRating(sp, fcForceRating),
      switchTo: switchTo,
      pools: pools,
      allPicked: pools.every(function(pl) {
        return (fcSpecPicks[pl.id] || []).length >= pl.count;
      }),
      shortPath: shortPathOfferedFor(sp, ctrl.selectedCareerData, fcForceRating)
        && !shortPathTakenIn(ctrl.character.talents),
      skillLess: !!sp.universal && (sp.career_skills || []).length === 0,
    };
  }, [ctrl.selectedSpecData, ctrl.selectedSpeciesData, ctrl.selectedCareerData, gameData,
      fcForceRating, fcSpecPicks, cbSpeciesList, ctrl.character.talents,
      ctrl.character.talentSkillChoices, ctrl.character.speciesOptionPicks,
      ctrl.character.speciesSkillChoices]);

  var fcBookLabel = gameLineTitle;

  var fcCareerRows = useMemo(function() {
    // ALPHABETICAL, because the board's rail is. The registry's own order is alphabetical only by
    // accident of when each career was added — Sith, from a later book, sits at the end of the file
    // and would print after Warrior while the board files it between Sentinel and Smuggler. A rail
    // you read rather than filter (the board is explicit that twenty-one careers is a list, not a
    // search) has to be in the order a reader expects.
    return (cbCareerList || []).slice().sort(function(a, b) {
      return String(a.name).localeCompare(String(b.name));
    }).map(function(c) {
      return {
        id: c.id, name: c.name,
        source: fcRecordSourceLabel('careers', c, fcBookLabel),
        count: (c.career_skills || []).length, unit: 'skills',
      };
    });
  }, [cbCareerList]);

  /* THE PHONE PICKER'S OWN ORDER — the SHELF, grouped (`phone-career-census` R-PCC-A1-1, Jason's
     word "whole shelf", 2026-09-09). Every career from the enabled books, the player's own line
     first and the other lines after a divider: the grouping the legacy phone browser has always
     drawn and the desktop rail's footnote already describes ("what you may choose from is set by
     the books you have enabled, not by your game line").
     THE SAME PREDICATE AND THE SAME COMPARATOR AS THE RAIL, on purpose. `SWRPGSources.isFromLine`
     asks the book vocabulary directly — B-336's LOGIC site, where a title substring-match would
     fail SILENTLY under a key conversion — and `localeCompare` is `fcCareerRows`' own sort applied
     per group, so the two lists can never disagree about where a career belongs or what it is
     called. `FCCareerBrowser`'s copy of this pair retires from the phone with this mount.
     THE ROWS CARRY NO `source`. The frame's picker rows are name-only (`hp-row` → `hp-row-body` →
     `hp-row-desig`, nothing else); the book line is the DESKTOP rail's object, and emitting it
     here would be twenty-one objects the board does not draw. */

  var fcCareerPhonePick = useMemo(function() {
    var list = (cbCareerList || []).slice();
    var byName = function(a, b) { return String(a && a.name).localeCompare(String(b && b.name)); };
    var S = window.SWRPGSources, cur = S && S.current(), idx = cur && cur.index;
    var mine = [], others = [];
    list.forEach(function(c) {
      ((idx && S.isFromLine(idx, c, ctrl.gameLine)) ? mine : others).push(c);
    });
    mine.sort(byName); others.sort(byName);
    var row = function(c) { return { id: c.id, name: c.name }; };
    return { rows: mine.map(row).concat(others.map(row)), own: mine.length, total: list.length };
  }, [cbCareerList, ctrl.gameLine]);

  var fcCareerSkills = useMemo(function() {
    var rec = ctrl.selectedCareerData;
    if (!rec || !gameData) return [];
    var cb = gameData.charbuild, names = cb.skillNames || {}, chars = cb.skillChars || {};
    return (rec.career_skills || []).map(function(id) {
      // R-CPD-8 (`creator-pre-deploy-batch`), ENFORCING board §5 ban item 8 — "never abbreviated
      // (mechanics-voice rule; and the book prints the words)", whose own note adds that a truncated
      // label IS an abbreviation and the ban has no small print. The words already existed here.
      return { name: names[id] || id, chr: (FC_CHAR_WORDS[chars[id]] || '').toString() };
    });
  }, [ctrl.selectedCareerData, gameData]);

  var fcCareerSpecs = useMemo(function() {
    var rec = ctrl.selectedCareerData;
    if (!rec || !gameData) return [];
    var all = gameData.charbuild.specializations || {};
    return (rec.specializations || []).map(function(id) {
      return (all[id] && all[id].name) || id;
    });
  }, [ctrl.selectedCareerData, gameData]);

  // Step 2 handlers: Career & Specialization
  var handleCareerSelect = useCallback(function(career) {
    ctrl.setSelectedCareerData(career);
    ctrl.setSubView('careerDetail');
  }, [ctrl]);

  // B-68: the CAREER commits on its own step now. It used to ride handleSpecConfirm — career and
  // spec written together — so the two could not be separated until B-72's by-name draft contract
  // made inserting a step safe.
  var handleCareerConfirm = useCallback(function(career) {
    ctrl.updateCharacter('career', { key: career.id, name: career.name });
    ctrl.setSubView(null);
    ctrl.nextStep();
  }, [ctrl]);

  // First tap: open the spec detail screen (does not commit)
  var handleSpecPreview = useCallback(function(spec) {
    ctrl.setSelectedSpecData(spec);
    ctrl.setSubView('specDetail');
  }, [ctrl]);

  // Confirm from the spec detail screen: commit career + spec, grant row-0 talents, advance. — Docs/CREATOR_COMMENTARY.md § "FullCreator › handleShortPathTake"
  var handleShortPathTake = useCallback(function() {
    if (shortPathTakenIn(ctrl.character.talents)) return;   // R-SPP-3: no stacking
    ctrl.dispatch({ type: 'ADD_TALENT', talentKey: SHORT_PATH_TALENT, specKey: SHORT_PATH_SPECKEY,
      row: 0, col: 0, cost: SHORT_PATH_XP, source: 'short_path' });
  }, [ctrl]);
  // R-SPP-7: reversible within creation — remove the rank, and the 30 XP returns to the pool.
  var handleShortPathUndo = useCallback(function() {
    var pk = shortPathPosKeyIn(ctrl.character.talents);
    if (pk) ctrl.dispatch({ type: 'REMOVE_TALENT', posKey: pk });
  }, [ctrl]);

  var handleSpecConfirm = useCallback(function(spec, overrideSpecies, choicePicks) {
    // B-41 backstop: the SELECT bar is already blocked and the panel above it says why (RotS p.26,
    // CotR p.28/29). This catches any path that reaches the confirm without passing that bar.
    if (!meetsForceRating(spec, creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild))) return;
    var career = ctrl.selectedCareerData;
    // B-40: store the player's chosen career skill(s) in the persisted save shape, keyed by
    // spec id then pool id (ruling `f-eds-6-spec-choice-save-shape` §3). Written only when a
    // choice was actually made — absent means the spec grants its fixed skills alone.
    setFcSpecSkillChoices(function(prev) {
      var n = Object.assign({}, prev);
      if (choicePicks && Object.keys(choicePicks).length) n[spec.id] = choicePicks;
      else delete n[spec.id];
      return n;
    });
    ctrl.updateCharacter('specSkillChoices',
      (choicePicks && Object.keys(choicePicks).length) ? (function() { var o = {}; o[spec.id] = choicePicks; return o; })() : {});
    if (overrideSpecies) {
      // Switch species and reset characteristics to the new species baseline.
      // Species-granted skill ranks from the original species are preserved by SET_CAREER;
      // however when we switch species here we deliberately reset them to the new baseline.
      ctrl.setSelectedSpeciesData(overrideSpecies);
      ctrl.updateCharacter('species', { key: overrideSpecies.id, name: overrideSpecies.name });
      ctrl.dispatch({ type: 'SET_FIELD', field: 'characteristics', value: {
        brawn: overrideSpecies.characteristics.brawn, agility: overrideSpecies.characteristics.agility,
        intellect: overrideSpecies.characteristics.intellect, cunning: overrideSpecies.characteristics.cunning,
        willpower: overrideSpecies.characteristics.willpower, presence: overrideSpecies.characteristics.presence,
      }});
      // Re-apply species free skill ranks for the new species
      var grants = speciesSkillGrants(overrideSpecies);
      var newSkills = {};
      Object.keys(grants).forEach(function(k) {
        newSkills[k] = { freeCareer: false, freeSpec: false, purchased: grants[k] };
      });
      ctrl.dispatch({ type: 'SET_FIELD', field: 'skills', value: newSkills });
    }
    // B-68: the career is NOT written here any more — its own step committed it. Kept as a
    // backstop for the re-entry path, which can land on the specialization step with a career
    // already chosen but the character record not yet carrying it.
    if (career && !(ctrl.character.career && ctrl.character.career.key)) {
      ctrl.updateCharacter('career', { key: career.id, name: career.name });
    }
    ctrl.dispatch({ type: 'SET_SPEC', value: { key: spec.id, name: spec.name } });
    // RAW (EotE p.93 Acquiring Talents / p.128): there are NO free talents at creation — every talent is
    // purchased at its row cost (top row = 5 XP each). The player buys top-row talents from the tree
    // (row 0 is always eligible via fcTalentIsAvailable). The old free row-0 auto-grant (the D-CS4-2
    // house rule) is removed; only career/spec SKILL ranks are free by RAW, not talents.
    ctrl.setSelectedSpecData(spec);
    ctrl.setSubView(null);
    ctrl.nextStep();
  }, [ctrl, speciesSkillGrants]);

  // Step 3 handlers: Skills
  var _useFCS = useState([]), freeCareerSkills = _useFCS[0], setFreeCareerSkills = _useFCS[1];
  var _useFSS = useState([]), freeSpecSkills = _useFSS[0], setFreeSpecSkills = _useFSS[1];
  var _useFSpS = useState([]), freeSpeciesSkills = _useFSpS[0], setFreeSpeciesSkills = _useFSpS[1];
  // B-40: the spec's player-CHOSEN career skill(s), in the persisted save shape
  // ({specId: {poolId: [skillId]}}, ruling `f-eds-6-spec-choice-save-shape` §3) so creator state and
  // sheet state are the same object — no translation at finalize, nothing to drift.
  var _useSSC = useState({}), fcSpecSkillChoices = _useSSC[0], setFcSpecSkillChoices = _useSSC[1];
  // B-18: no free-skill selection outlives its SUBJECT. When the career / spec / species changes
  // (now a mainstream path — the campaign model ships cross-era careers with different free_ranks),
  // clear the matching picker state so it can't arrive holding stale picks the reducer already
  // stripped from the character (the Sentinel "4 OF 3 SELECTED" lockout). The picker's self-heal
  // above is the second line of defense; this is the reset that keeps the two in sync.
  var _fcCarId = ctrl.selectedCareerData && ctrl.selectedCareerData.id;
  var _fcSpcId = ctrl.selectedSpecData && ctrl.selectedSpecData.id;
  var _fcSpsId = ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.id;
  useEffect(function() { setFreeCareerSkills([]); }, [_fcCarId]);
  useEffect(function() { setFreeSpecSkills([]); }, [_fcSpcId]);
  useEffect(function() { setFreeSpeciesSkills([]); }, [_fcSpsId]);

  // CR-RAW #1: species free-choice skill grant (skill_choice_grants — Human 2×non-career,
  // Geonosian 1×any, Klatooinian 1×non-career; rank_cap 2 = the creation cap, EotE p.48).
  // ponytail: reads grants[0] only — every granted species carries exactly one entry, pinned
  // by validator L1cb.scg; a future multi-entry species needs a sequenced picker here.
  var fcSpeciesChoiceGrant = useMemo(function() {
    // B-22: the grant can live on the species ROOT (Human/Geonosian) OR on a chosen pick-one
    // OPTION (Mandalorian Human combat/knowledge, banked at confirm as speciesOption). Resolved
    // through the shared one-home helper (B-14: Guided Build reads the same one).
    return speciesChoiceGrantOf(ctrl.selectedSpeciesData, ctrl.character && ctrl.character.speciesOption);
  }, [ctrl.selectedSpeciesData, ctrl.character && ctrl.character.speciesOption]);

  // B-21: the third grant class — a species that trains EXTRA free ranks at creation (Droid: 6 — Docs/CREATOR_COMMENTARY.md § "FullCreator › _fcPicks"
  var _fcPicks = window.CharResolve ? window.CharResolve.creationFreePicks(ctrl.selectedSpeciesData, ctrl.selectedCareerData) : { career: 4, spec: 2 };
  var fcCareerFreeRanks = _fcPicks.career;
  var fcSpecFreeRanks = _fcPicks.spec;

  // Career-STATUS ids at creation: the career's skills ∪ the chosen spec's — RAW: a
  // specialization's skills ARE career skills (EotE p.35/p.102; the sheet's R-CSK1 union — the
  // creator converges on the same rule). Feeds the species-choice 'non_career' pool AND the
  // buy-ranks pricing (CR-RAW #2).
  // F-100 M7 C2 (R-M7K-7): the one answer (CharResolve.careerSkillStatus) over the creator's state. B-40's chosen spec
  // skill ("Knowledge (choose any one)") counts exactly like the fixed three; a choice or fixed-grant TALENT bought in
  // the creator now counts too, so a rank bought after it prices as career (the brief's §7 C2 interaction).
  var fcCareerStatusIds = useMemo(function() {
    var sp = ctrl.selectedSpecData;
    return creatorCareerStatusIds(gameData && gameData.charbuild, ctrl.selectedCareerData, sp && sp.id, {
      id: ctrl.character.id,   // R6 lane D (Q6 c)
      talents: ctrl.character.talents, talentSkillChoices: ctrl.character.talentSkillChoices,
      specSkillChoices: fcSpecSkillChoices, speciesId: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.id,
      speciesOptionPicks: ctrl.character.speciesOptionPicks, speciesSkillChoices: ctrl.character.speciesSkillChoices });
  }, [gameData, ctrl.selectedCareerData, ctrl.selectedSpecData, ctrl.selectedSpeciesData, fcSpecSkillChoices,
      ctrl.character.talents, ctrl.character.talentSkillChoices, ctrl.character.speciesOptionPicks,
      ctrl.character.speciesSkillChoices, ctrl.character.id]);

  // The pick pool as canonical ids, name-sorted (shared one-home helper; 'non_career' excludes the
  // career-status set, 'combat'/'knowledge' resolve against the skill registry's group).
  var fcSpeciesChoicePool = useMemo(function() {
    return speciesChoicePoolOf(fcSpeciesChoiceGrant, gameData && gameData.charbuild, fcCareerStatusIds);
  }, [fcSpeciesChoiceGrant, gameData, fcCareerStatusIds]);

  // Selections filtered to the live pool — a career/spec change can invalidate an earlier pick
  // (the stale id would otherwise sit invisibly in the count and wedge the CONFIRM gate).
  var fcSpeciesChoiceSelected = useMemo(function() {
    return freeSpeciesSkills.filter(function(id) { return fcSpeciesChoicePool.indexOf(id) >= 0; });
  }, [freeSpeciesSkills, fcSpeciesChoicePool]);

  // Already-committed creation ranks by sheet key (career/spec free picks + fixed species
  // grants) — feeds the picker's rank-cap disable. freeSpecies excluded: those ARE the
  // picker's own selections.
  var fcCommittedRanks = useMemo(function() {
    var out = {};
    var chSkills = ctrl.character && ctrl.character.skills ? ctrl.character.skills : {};
    Object.keys(chSkills).forEach(function(k) {
      var s = chSkills[k];
      var r = (s.freeCareer ? 1 : 0) + (s.freeSpec ? 1 : 0) + (s.purchased || 0);
      if (r > 0) out[k] = r;
    });
    return out;
  }, [ctrl.character]);

  // Species-granted skill ranks (e.g. Twi'lek CHARM, DECEP) — used to show hints in the picker.
  // Keyed by both oggdude and sheet keys to tolerate either source.
  var fcSpeciesGrantedRanks = useMemo(function() {
    var out = {};
    var chSkills = ctrl.character && ctrl.character.skills ? ctrl.character.skills : {};
    Object.keys(chSkills).forEach(function(k) {
      var s = chSkills[k];
      if (s && s.purchased > 0 && !s.freeCareer && !s.freeSpec) out[k] = s.purchased;
    });
    return out;
  }, [ctrl.character]);

  var handleCareerSkillToggle = useCallback(function(sk) {
    setFreeCareerSkills(function(prev) {
      return prev.indexOf(sk) >= 0 ? prev.filter(function(k) { return k !== sk; }) : prev.concat([sk]);
    });
  }, []);

  var handleSpecSkillToggle = useCallback(function(sk) {
    setFreeSpecSkills(function(prev) {
      return prev.indexOf(sk) >= 0 ? prev.filter(function(k) { return k !== sk; }) : prev.concat([sk]);
    });
  }, []);

  var handleSpeciesSkillToggle = useCallback(function(sk) {
    setFreeSpeciesSkills(function(prev) {
      return prev.indexOf(sk) >= 0 ? prev.filter(function(k) { return k !== sk; }) : prev.concat([sk]);
    });
  }, []);

  // THE TRANSPLANTED SKILLS SHEET COMMITS EACH PICK AS IT IS MADE (cluster three): ONE flag is
  // flipped per press through `TOGGLE_FREE_SKILL`, which reads the reducer's own state — never a
  // "whole set" computed from the picks a stale render showed (three presses in one task kept only
  // the last). The local round state is kept in step, by functional update for the same reason, so
  // a band change mid-step (mobile ⇄ mid) finds the legacy pickers holding what the sheet holds.
  var _fcToggleLocal = function(id) {
    return function(prev) { return prev.indexOf(id) >= 0 ? prev.filter(function(k) { return k !== id; }) : prev.concat([id]); };
  };
  var handleSkillsToggle = useCallback(function(flag, id) {
    var keys = gameData.charbuild.skillSheetKeys;
    ({ freeCareer: setFreeCareerSkills, freeSpec: setFreeSpecSkills, freeSpecies: setFreeSpeciesSkills })[flag](_fcToggleLocal(id));
    ctrl.dispatch({ type: 'TOGGLE_FREE_SKILL', flag: flag, skill: keys[id] || id });
  }, [ctrl, gameData]);
  var handleSkillsSheetConfirm = useCallback(function() {
    ctrl.setSubView(null);
    ctrl.nextStep();
  }, [ctrl]);

  // T2-D: a spec with NO career skills (Force Sensitive Exile/Emergent — "does not have any
  // career skills associated with it", eote p.276 / aor p.292; the FR 1 is the whole package)
  // has no spec-skills round: RAW gives no substitute free ranks, so the flow skips it.
  var fcSpecHasSkills = !!(ctrl.selectedSpecData && (ctrl.selectedSpecData.career_skills || []).length);

  var handleCareerSkillsConfirm = useCallback(function() {
    var keys = gameData.charbuild.skillSheetKeys;
    ctrl.dispatch({ type: 'SET_FREE_CAREER_SKILLS', value: freeCareerSkills.map(function(id) { return keys[id] || id; }) });
    ctrl.setSubView(fcSpecHasSkills ? 'specSkills' : (fcSpeciesChoiceGrant ? 'speciesSkills' : 'trainingSummary'));
  }, [ctrl, freeCareerSkills, gameData, fcSpecHasSkills, fcSpeciesChoiceGrant]);

  var handleSpecSkillsConfirm = useCallback(function() {
    var keys = gameData.charbuild.skillSheetKeys;
    ctrl.dispatch({ type: 'SET_FREE_SPEC_SKILLS', value: freeSpecSkills.map(function(id) { return keys[id] || id; }) });
    ctrl.setSubView(fcSpeciesChoiceGrant ? 'speciesSkills' : 'trainingSummary');
  }, [ctrl, freeSpecSkills, gameData, fcSpeciesChoiceGrant]);

  var handleSpeciesSkillsConfirm = useCallback(function() {
    var keys = gameData.charbuild.skillSheetKeys;
    ctrl.dispatch({ type: 'SET_FREE_SPECIES_SKILLS', value: fcSpeciesChoiceSelected.map(function(id) { return keys[id] || id; }) });
    ctrl.setSubView('trainingSummary');
  }, [ctrl, fcSpeciesChoiceSelected, gameData]);

  var handleTrainingConfirm = useCallback(function() {
    // CR-RAW #2: free ranks → the buy-ranks screen (skippable in one tap).
    ctrl.setSubView('buySkills');
  }, [ctrl]);

  var handleBuySkillRank = useCallback(function(sheetKey, cost) {
    ctrl.dispatch({ type: 'BUY_SKILL_RANK', skill: sheetKey, cost: cost });
  }, [ctrl]);

  var handleRefundSkillRank = useCallback(function(sheetKey) {
    ctrl.dispatch({ type: 'REFUND_SKILL_RANK', skill: sheetKey });
  }, [ctrl]);

  var handleBuySkillsDone = useCallback(function() {
    ctrl.setSubView(null);
    ctrl.nextStep();
  }, [ctrl]);

  // ── Species-penalty buyout (R-SFC-2) ────────────────────────────────────────────────────────
  // The offers the RESOLVED species makes — record + the recorded option picks, read through the
  // shared resolver. This is why the Kamarian's offer appears only under Badlander and vanishes
  // under Mountain Dweller with no special case anywhere on this screen.
  var fcBuyoutOffers = useMemo(function() {
    if (!ctrl.selectedSpeciesData || !window.CharResolve) return [];
    return CharResolve.speciesBuyoutOffers(ctrl.selectedSpeciesData, ctrl.character && ctrl.character.speciesOptionPicks) || [];
  }, [ctrl.selectedSpeciesData, ctrl.character && ctrl.character.speciesOptionPicks]);

  var handleBuyoutTake = useCallback(function(slug, xp) {
    var next = Object.assign({}, (ctrl.character && ctrl.character.creationBuyouts) || {});
    next[slug] = { xp: xp };   // the COST PAID is stored on the entry, never re-derived at read
    ctrl.updateCharacter('creationBuyouts', next);
  }, [ctrl]);

  var handleBuyoutUndo = useCallback(function(slug) {
    var next = Object.assign({}, (ctrl.character && ctrl.character.creationBuyouts) || {});
    delete next[slug];          // R-SFC-2: undo IS deleting the key; the XP refunds through the summer
    ctrl.updateCharacter('creationBuyouts', next);
  }, [ctrl]);

  // A PAID-FOR OFFER THAT STOPS BEING OFFERED IS REFUNDED, not kept. Change species after buying
  // out Primitive — or flip Kamarian's subspecies from Badlander to Mountain Dweller — and the
  // ability is gone, so charging for its removal would bill a player for a penalty they no longer
  // have. Only fires when there is something stale, so it cannot loop.
  useEffect(function() {
    var held = (ctrl.character && ctrl.character.creationBuyouts) || {};
    var keys = Object.keys(held);
    if (!keys.length) return;
    var live = {};
    fcBuyoutOffers.forEach(function(o) { live[o.slug] = true; });
    var stale = keys.filter(function(k) { return !live[k]; });
    if (!stale.length) return;
    var next = {};
    keys.forEach(function(k) { if (live[k]) next[k] = held[k]; });
    ctrl.updateCharacter('creationBuyouts', next);
  }, [fcBuyoutOffers, ctrl.character && ctrl.character.creationBuyouts]);

  // Step 7 handler: Motivation — Docs/CREATOR_COMMENTARY.md § "FullCreator › handleMotivationConfirm"
  var handleMotivationConfirm = useCallback(function(motivation) {
    ctrl.dispatch({ type: 'SET_FIELD', field: 'motivation', value: motivation });
    ctrl.nextStep();
  }, [ctrl]);

  // Step 8 handlers: Gear
  var handleGearBuy = useCallback(function(item) {
    // The draft's row key is the CANONICAL id; item.type is its section — store both so the canonical
    // equipment builder routes the id-ref to the right registry (D-3.8). R-SPC-3 (B-666): a store
    // display shape's `key` is the section-qualified catalog address, so the BARE `regId` is what a
    // draft row may hold. (This handler has no live call site today — the store's finalize writes the
    // ledger — but a bare row is the contract `fcGearLineStats` and `storeShopFor` read.)
    ctrl.dispatch({ type: 'ADD_ITEM', item: { key: item.regId || item.key, name: item.name, price: item.price, enc: item.enc, section: item.type, qty: 1 } });
  }, [ctrl]);

  var handleGearSell = useCallback(function(key) {
    ctrl.dispatch({ type: 'REMOVE_ITEM', key: key });
  }, [ctrl]);

  // B-94: THE STORE, HOSTED AT CREATION (lap C4) — Docs/CREATOR_COMMENTARY.md § "FullCreator › storeShopFor"
  var storeShopFor = useMemo(function() {
    var inv = (ctrl.character.gearLedger && ctrl.character.gearLedger.inventory) || [];
    var eq = { weapons: [], armor: null, gear: [], credits: ctrl.credits.remaining };
    inv.forEach(function(it) {
      var n = Math.max(1, it.qty || 1);
      if (it.section === 'weapons') { for (var i = 0; i < n; i++) eq.weapons.push({ id: it.key }); }
      else if (it.section === 'armor') { eq.armor = { id: it.key, state: 'worn' }; }   // B-706 (2): the creator's suit is worn (B-699's shape)
      else { eq.gear.push({ id: it.key, qty: n }); }
    });
    // B-1161 (R-B1161-7): the draft carries the stamps finalize will give it, so the store judges what this character
    // will resolve — every homebrew item in an active collection stays the bare id the creator's ledger keeps.
    return { id: 'draft', name: (ctrl.character.identity && ctrl.character.identity.name) || 'Your character',
             accent: accent, sheetData: { equipment: eq, mods: creationStampsFor(gameData) } };
  }, [ctrl.character.gearLedger, ctrl.credits.remaining, ctrl.character.identity, accent, gameData]);

  /* The inverse, and the ONE write. `buildNextEquipment` returns id-refs
     (`{weapons:[{id}], armor:{id}, gear:[{id,qty}], credits}`); the draft stores
     `{key, name, price, enc, section, qty}` — so resolve name/price/enc from the registry exactly
     as the species-grant path already does, and REPLACE the inventory rather than appending, since
     the module hands back the whole equipment state. */
  var handleStoreFinalizeInCreator = useCallback(function(charId, nextEquipment) {
    // B-200: resolve against the CANONICAL REGISTRIES, which is the id-space the store shops fro — Docs/CREATOR_COMMENTARY.md § "FullCreator › store registries"
    var reg = (gameData && gameData.canonical) || window._canonicalRegistries;
    if (!reg || !nextEquipment) return;
    var rows = [];
    var missed = [];
    var push = function(id, section, qty) {
      var rec = (reg.weapons && reg.weapons[id]) || (reg.armor && reg.armor[id]) || (reg.gear && reg.gear[id]);
      // B-200: LOUD on a code path. The id came from the store's own catalogue seconds ago, so a miss
      // here is a bug, not history — the standing invariant is loud-on-code-paths / graceful-on-old-
      // data. The silent skip this replaces is what hid a 100% failure.
      if (!rec) { missed.push(section + ':' + id); return; }
      rows.push({ key: id, name: rec.name, price: rec.price || 0, enc: rec.encumbrance || 0,
                  section: section, qty: qty || 1 });
    };
    // B-1161 (R-B1161-10): an inline copy (a RE-ENTERED draft buying from a collection its character does not carry)
    // is read by its record's id, as a bare id is — the ledger keeps it as at the cut, never a silent drop. B-1163.
    (nextEquipment.weapons || []).forEach(function(w) { var id = w && (w.id || (w._inline && w._inline.id)); if (id) push(id, 'weapons', 1); });
    var a = nextEquipment.armor, aId = a && (a.id || (a._inline && a._inline.id));
    if (aId) push(aId, 'armor', 1);
    (nextEquipment.gear || []).forEach(function(g) { var id = g && (g.id || (g._inline && g._inline.id)); if (id) push(id, 'gear', g.qty || 1); });
    if (missed.length) {
      console.error('[B-200] creator store finalize could not resolve ' + missed.length +
                    ' purchased id-ref(s) against the canonical registries: ' + missed.join(', ') +
                    ' — these purchases were DROPPED. This is a code-path defect, not stale data.');
    }
    ctrl.dispatch({ type: 'SET_FIELD', field: 'gearLedger',
      value: Object.assign({}, ctrl.character.gearLedger, { inventory: rows }) });
    ctrl.setSubView(null);                                 // done shopping → back to the wizard
  }, [ctrl, gameData]);

  // Step 6 handlers: Talents
  // B-181: the creator's talent-choice skill picker open-state (same shape as the sheet's). A choice-
  // talent's buy raises it; CONFIRM dispatches the purchase AND files the skills in one tick.
  var _cTalPick = useState(null), creatorTalentPicker = _cTalPick[0], setCreatorTalentPicker = _cTalPick[1];
  // B-1055 MOUNT (ruling release-r7-bug-burndown ADDENDUM 99 item 14, ADDENDUM 102): Dedication's buy raises the sheet's
  // ONE picker (M7CharPickSheet, R-M7G-22) before the spend lands; a choice buys AND files the record, Cancel buys nothing.
  var _cCharPick = useState(null), creatorCharPick = _cCharPick[0], setCreatorCharPick = _cCharPick[1];
  var raiseCreatorCharPick = useCallback(function(talentKey, specKey, row, col, cost) {
    var cellEl = document.querySelector('[data-testid="talent-cell-' + row + '-' + col + '"]');
    setCreatorCharPick({ mode: 'buy', talentKey: talentKey, specKey: specKey, row: row, col: col, cost: cost,
      anchor: cellEl ? (cellEl.querySelector('button.hp-tab, .hp-head[role=button]') || cellEl) : null });
  }, []);
  var handleCreatorCharChoose = useCallback(function(c) {
    var cp = creatorCharPick; setCreatorCharPick(null);
    if (!cp) return;
    // ADDENDUM 108 C1: a pre-build draft (no field) carries every record it holds; materialize that BEFORE this pick files
    if (!Array.isArray(ctrl.character.dedicationCarried)) ctrl.updateCharacter('dedicationCarried', CharResolve.creatorDedicationCarried(ctrl.character));
    ctrl.dispatch({ type: 'ADD_TALENT', talentKey: cp.talentKey, specKey: cp.specKey, row: cp.row, col: cp.col, cost: cp.cost });
    var next = JSON.parse(JSON.stringify(ctrl.character.talentCharacteristicChoices || {}));
    (next[cp.specKey] || (next[cp.specKey] = {}))[cp.row + '-' + cp.col] = c;
    ctrl.updateCharacter('talentCharacteristicChoices', next);
  }, [ctrl, creatorCharPick]);
  var closeCreatorCharPick = useCallback(function() { setCreatorCharPick(null); }, []);

  var handleTalentBuy = useCallback(function(talentKey, specKey, row, col, cost) {
    var trec = gameData && gameData.charbuild.talents ? gameData.charbuild.talents[talentKey] : null;
    var grants = (window.CharResolve && CharResolve.talentChoiceGrantsOf(trec)) || [];
    if (talentKey === 'dedication') { raiseCreatorCharPick(talentKey, specKey, row, col, cost); return; }
    if (grants.length) {
      // B-181: a choice-talent — raise the picker instead of buying blind; CONFIRM does both.
      setCreatorTalentPicker({ mode: 'buy', talentKey: talentKey, talentName: (trec && trec.name) || talentKey, specKey: specKey, row: row, col: col, cost: cost, grants: grants, initialPicks: [] });
      return;
    }
    ctrl.dispatch({ type: 'ADD_TALENT', talentKey: talentKey, specKey: specKey, row: row, col: col, cost: cost });
  }, [ctrl, gameData, raiseCreatorCharPick]);

  var handleTalentRefund = useCallback(function(posKey) {
    ctrl.dispatch({ type: 'REMOVE_TALENT', posKey: posKey });
  }, [ctrl]);

  // THE TRANSPLANTED TALENTS SHEET'S THREE (B-181 / B-302 on the board skin): a DIRECT buy — the
  // choice-talent's picker is the sheet's own inline block, not the overlay — the filer that writes
  // the picks against the cell (R-TCP-1), and a refund that takes the cell's choice with it
  // (structural binding, R-TCT-3 / R-TCP-1: "the cell's entry goes with the cell").
  var handleTalentBuyDirect = useCallback(function(talentKey, specKey, row, col, cost) {
    if (talentKey === 'dedication') { raiseCreatorCharPick(talentKey, specKey, row, col, cost); return; }   // B-1055 MOUNT
    ctrl.dispatch({ type: 'ADD_TALENT', talentKey: talentKey, specKey: specKey, row: row, col: col, cost: cost });
  }, [ctrl, raiseCreatorCharPick]);
  var handleTalentChoicesFile = useCallback(function(specKey, row, col, skills) {
    var prev = ctrl.character.talentSkillChoices || {};
    var next = JSON.parse(JSON.stringify(prev));
    (next[specKey] || (next[specKey] = {}))[row + '-' + col] = (skills || []).slice();
    ctrl.updateCharacter('talentSkillChoices', next);
  }, [ctrl]);
  var handleTalentRefundWithChoices = useCallback(function(posKey) {
    var t = (ctrl.character.talents || {})[posKey];
    ctrl.dispatch({ type: 'REMOVE_TALENT', posKey: posKey });
    if (t && ctrl.character.talentSkillChoices && ctrl.character.talentSkillChoices[t.specKey]
        && ctrl.character.talentSkillChoices[t.specKey][t.row + '-' + t.col]) {
      var next = JSON.parse(JSON.stringify(ctrl.character.talentSkillChoices));
      delete next[t.specKey][t.row + '-' + t.col];
      if (!Object.keys(next[t.specKey]).length) delete next[t.specKey];
      ctrl.updateCharacter('talentSkillChoices', next);
    }
    // B-1055: the cell's characteristic pick goes with the cell, as its skills do (R-TCP-1) — a re-entered draft can carry one.
    var tcc = ctrl.character.talentCharacteristicChoices;
    if (t && tcc && tcc[t.specKey] && tcc[t.specKey][t.row + '-' + t.col] != null) {
      var nextC = JSON.parse(JSON.stringify(tcc));
      delete nextC[t.specKey][t.row + '-' + t.col];
      if (!Object.keys(nextC[t.specKey]).length) delete nextC[t.specKey];
      ctrl.updateCharacter('talentCharacteristicChoices', nextC);
      // A RE-ENTERED Dedication's point is already in the live block (CharResolve's `dedicationCarried`); its refund
      // takes the point, its share of the post-creation gain and the carry together, so the finish adds nothing back.
      var picked = tcc[t.specKey][t.row + '-' + t.col], carried = CharResolve.creatorDedicationCarried(ctrl.character);   // absent → every record (ADDENDUM 108 C1)
      if (t.talentKey === 'dedication' && carried.indexOf(posKey) !== -1 && ctrl.character.characteristics[picked] != null) {
        var g = ctrl.character.characteristicGainsAfterCreation;
        ctrl.updateCharacter('characteristics', Object.assign({}, ctrl.character.characteristics,
          { [picked]: (Number(ctrl.character.characteristics[picked]) || 0) - 1 }));
        if (g && Number(g[picked]) > 0) ctrl.updateCharacter('characteristicGainsAfterCreation', Object.assign({}, g, { [picked]: Number(g[picked]) - 1 }));
        ctrl.updateCharacter('dedicationCarried', carried.filter(function(k) { return k !== posKey; }));
      }
    }
  }, [ctrl]);

  // B-181: CONFIRM a choice-talent — dispatch the purchase (buy mode) AND file the chosen skills. Both
  // state updates land in the same React tick, so the talent is never owned without its skills; the
  // choice reaches the sheet at finalize via normalizeCreated (character.talentSkillChoices → sheet).
  var handleTalentChoiceConfirm = useCallback(function(skills) {
    if (!creatorTalentPicker) return;
    var p = creatorTalentPicker;
    if (p.mode === 'buy') {
      ctrl.dispatch({ type: 'ADD_TALENT', talentKey: p.talentKey, specKey: p.specKey, row: p.row, col: p.col, cost: p.cost });
    }
    var prev = ctrl.character.talentSkillChoices || {};
    var next = JSON.parse(JSON.stringify(prev));
    (next[p.specKey] || (next[p.specKey] = {}))[p.row + '-' + p.col] = (skills || []).slice();
    ctrl.updateCharacter('talentSkillChoices', next);
    setCreatorTalentPicker(null);
  }, [ctrl, creatorTalentPicker]);

  // Veteran review fix: Force powers buy/refund. Stored on character.forcePowers as
  // { [Key]: { name, desc, cost } }. XP cost is 10 per basic power (CRB p.282).
  var handleForcePowerBuy = useCallback(function(powerKey, powerData) {
    var existing = (ctrl.character.forcePowers) || {};
    if (existing[powerKey]) return;
    // B-41 backstop: the card's BUY is already blocked and badged with the requirement (fad p.285).
    if (!meetsForceRating(powerData, creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild))) return;
    var next = Object.assign({}, existing);
    // R3 Q2b — the second `\[[^\]]+\]` glyph-eater, and the one that WRITES. This value is — Docs/CREATOR_COMMENTARY.md § "FullCreator › motivation description"
    var desc = plainTextGlyphless(cleanMarkup(powerData.description || '')).replace(/\s+/g, ' ').trim();
    // Printed basic cost from the canonical tree's row 0 (varies 5-20 XP, fad p.285), through the ONE basic price
    // (F-100 M12 B-1246: this read was a second copy of basicForcePowerCost's).
    // R-3B2: missing row-0 cost = registry bug — block the purchase LOUDLY (basicForcePowerCost logs it), never charge
    // a silent made-up price (the step card already shows COST ? / disabled).
    var basicCost = basicForcePowerCost(powerData);
    if (basicCost == null) return;
    next[powerKey] = { name: powerData.name, desc: desc.slice(0, 220), cost: basicCost, basic: true };
    ctrl.dispatch({ type: 'SET_FIELD', field: 'forcePowers', value: next });
  }, [ctrl]);

  // F-100 surfacing K5 (R-SCC-2, D-229 Q2 (a)): the Career Powers pick — the state read off the save, the write
  // through fcCareerPowerWrite (forcePowerGrantProblems before the dispatch; a refusal writes nothing and is named).
  var fcCareerPowers = useMemo(function() {
    return gameData && ctrl.character ? fcCareerPowerState(ctrl.character, gameData.charbuild, ctrl.gameLine) : null;
  }, [gameData, ctrl.character, ctrl.gameLine]);
  var _cpRef = useState(null), cpRefused = _cpRef[0], setCpRefused = _cpRef[1];
  var handleCareerPowerPick = useCallback(function(slug, optId) {
    if (!fcCareerPowers) return;
    var w = fcCareerPowerWrite(fcCareerPowers, slug, optId, ctrl.character.forcePowers, gameData && gameData.charbuild);
    if (!w.next) { if (w.problems.length) setCpRefused(w.problems); return; }
    setCpRefused(null);
    ctrl.dispatch({ type: 'SET_FIELD', field: 'forcePowers', value: w.next });
  }, [ctrl, gameData, fcCareerPowers]);

  var handleForcePowerRefund = useCallback(function(powerKey) {
    var existing = (ctrl.character.forcePowers) || {};
    if (!existing[powerKey]) return;
    var next = Object.assign({}, existing);
    delete next[powerKey];
    ctrl.dispatch({ type: 'SET_FIELD', field: 'forcePowers', value: next });
  }, [ctrl]);

  // Resolve full spec data (with talent tree) from gameData
  var fullSpecData = useMemo(function() {
    if (!gameData || !ctrl.selectedSpecData) return null;
    // Canonical: the registry record (id-keyed) IS the full spec, talent_tree included.
    return gameData.charbuild.specializations[ctrl.selectedSpecData.id] || ctrl.selectedSpecData;
  }, [gameData, ctrl.selectedSpecData]);

  // Step 5 handler: Characteristics
  var handleCharChange = useCallback(function(attr, newVal) {
    var chars = Object.assign({}, ctrl.character.characteristics);
    chars[attr] = newVal;
    ctrl.dispatch({ type: 'SET_FIELD', field: 'characteristics', value: chars });
  }, [ctrl]);

  // Step 4 handler: Mechanic
  var handleMechanicComplete = useCallback(function(mech, moralityLayer) {
    ctrl.updateCharacter('mechanic', mech);
    // BOOKS-1c Part B: the layered Morality (mode c) rides alongside; null when native-morality or declined.
    ctrl.updateCharacter('moralityLayer', moralityLayer || null);
    if (mech) {
      // Credits derive from the PRIMARY track only — the layer trades nothing (cap guard).
      ctrl.dispatch({ type: 'SET_CREDITS', value: CharResolve.startingCredits(mech.creditAdjustment, ctrl.selectedSpeciesData, ctrl.character && ctrl.character.speciesOptionPicks, ctrl.character && ctrl.character.creationBuyouts, ctrl.character).purse });
    }
    ctrl.nextStep();
  }, [ctrl]);

  // Back handler
  var handleBack = useCallback(function() {
    if (ctrl.subView) {
      // Handle skills sub-view back navigation (specSkills may not exist — skill-less specs, T2-D)
      if (ctrl.currentStep === 'skills' && ctrl.subView === 'buySkills') { ctrl.setSubView('trainingSummary'); return; }
      if (ctrl.currentStep === 'skills' && ctrl.subView === 'trainingSummary') { ctrl.setSubView(fcSpeciesChoiceGrant ? 'speciesSkills' : (fcSpecHasSkills ? 'specSkills' : null)); return; }
      if (ctrl.currentStep === 'skills' && ctrl.subView === 'speciesSkills') { ctrl.setSubView(fcSpecHasSkills ? 'specSkills' : null); return; }
      if (ctrl.currentStep === 'skills' && ctrl.subView === 'specSkills') { ctrl.setSubView(null); return; }
      // Career sub-view: spec detail → back to career detail (spec list)
      if (ctrl.currentStep === 'specialization' && ctrl.subView === 'specDetail') { ctrl.setSubView(null); return; }
      ctrl.setSubView(null); return;
    }
    // Mechanic step manages its own subView internally; intercept via ref
    if (ctrl.currentStep === 'mechanic' && mechanicBackRef.current && mechanicBackRef.current.handleBack) {
      if (mechanicBackRef.current.handleBack()) return;
    }
    if (ctrl.step === 0) { onBack(); return; }
    ctrl.prevStep();
  }, [ctrl, onBack, fcSpeciesChoiceGrant, fcSpecHasSkills]);

  // Step info with dynamic subtitles
  var stepInfo = FC_STEP_INFO[ctrl.currentStep] || {};
  var title = stepInfo.title;
  var subtitle = stepInfo.subtitle;
  if (ctrl.currentStep === 'mechanic' && ctrl.gameLine) {
    title = FC_MECHANIC_TITLES[ctrl.gameLine] || stepInfo.title;      // B-67(c)
    subtitle = FC_MECHANIC_SUBTITLES[ctrl.gameLine] || stepInfo.subtitle;
  }
  if (ctrl.currentStep === 'skills') {
    // CR-RAW #4: the count is RAW per line — 4 for EotE/AoR (8 career skills), 3 for FaD (6 skills), per
    // FaD core p.46. The subtitle hardcoded "4", so a FaD build read "Choose 4" over an "0 OF 3" counter.
    // Read the career's free_ranks (the data is correct — free_ranks === career_skills // 2 universally).
    // B-21: the totals already include any species bonus_free_ranks (Droid 6/3, Arkanian 6/2).
    if (!ctrl.subView) { subtitle = 'Your career grants you the ability to rank up ' + fcCareerFreeRanks + ' skills for free.'; }
    else if (ctrl.subView === 'specSkills') { subtitle = 'Choose ' + fcSpecFreeRanks + ' of your specialization\'s skills to gain a free rank. They count as career skills from now on.'; }
    else if (ctrl.subView === 'speciesSkills') {
      // CR-RAW #1: mechanics voice — say what the grant is (count + pool + who grants it).
      var _g = fcSpeciesChoiceGrant || { count: 1, pool: 'any' };
      var _spName = ctrl.selectedSpeciesData ? ctrl.selectedSpeciesData.name : 'Your species';
      subtitle = _spName + 's start with a free rank in ' + _g.count +
        (_g.pool === 'non_career' ? ' non-career skill' : ' skill') + (_g.count > 1 ? 's' : '') +
        ' of your choice. Choose ' + _g.count + '.';
    }
    else if (ctrl.subView === 'trainingSummary') { title = 'Training Complete'; subtitle = 'Here\'s what your character has learned so far.'; }
    else if (ctrl.subView === 'buySkills') {
      // CR-RAW #2 (EotE p.35/p.102/p.48) — mechanics voice: say the price rule and the cap.
      title = 'Buy Skill Ranks';
      subtitle = 'Spend starting XP on more ranks. Career skills cost less. Nothing goes above rank 2 at creation.';
    }
  }

  if (loadError) return <DataLoadErrorScreen onRetry={function() { clearCanonicalLoadCaches(); bootLoad(); }} />;
  if (loading) return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--doc-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* B-437 — the pulse is a CLASS now, so `prefers-reduced-motion` can reach it; the inline
          `animation` it replaces could not be guarded by any rule. Ink unchanged.
          AND ITS GROUND IS NOT THE CREATOR'S, which I had to be shown twice in one lap. This screen
          renders BEFORE `data-doc-ground="creator"` is on the tree, so `--doc-paper` and
          `--doc-ink-muted` resolve to the SHELL's values, not the parchment set: measured on the
          served page the ground is rgb(11,15,23) and the pair reads **6.82:1** at rest and
          **5.21:1** at the .85 trough. The arithmetic that said 7.87 / 5.42 was correct about the
          creator's parchment and about nothing on this screen — the same mistake as the plate, one
          surface over. The suite measures it rather than trusting either figure. */}
      <div className="breathing-text" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.2em', color: 'var(--doc-ink-muted)' }}>LOADING GALACTIC DATA…</div>
    </div>
  );

  // ── THE LAYOUT POLICY, in one table and one call (audit item F) ──────────────────────────
  // What the 80 lines that stood here decided, and why, is Docs/CREATOR_COMMENTARY.md § "FullCreator
  // › layout policy" (rulings creator-species-split-composition, creator-c10-plan, c11-skills-band-
  // composition, creator-d2-width-alignment; ADR 0024; B-86). The rules themselves are these:
  var _layout = creatorLayout(ctrl.currentStep, ctrl.subView, band, mode);
  var stepIsMasterDetail = _layout.masterDetail;
  var scrollKey = _layout.scrollKey;
  var showBuildRail = _layout.showBuildRail;
  var stepColumnMax = _layout.columnMax;
  var FC_CHAR_MEASURE = FC_CHAR_MEASURE_PX;

  return (
    /* THIS BOX IS THE CREATOR'S MOBILE SCROLL ZONE, AND IT PAINTS PAPER ONLY WHERE PAPER IS DRAWN.
        Two defects lived one property apart on this element; both are Jason's, both measured.

        B-509 — THE SHELL TRAP (AS-4). This box is `position:absolute; inset:0`, so it is exactly
        viewport-high, and it held `overflow:hidden` while `.hp-phonesheet` inside it is a
        `min-height:100%` flex column with VISIBLE overflow. Measured at 390x790 on the species
        register: the sheet's content is 6,751px in a 790px box, the box knows it (`scrollHeight`
        6751) and clips it, nothing in the chain up to `.app-shell` scrolls, and the document is
        scrollable by 0px — so 94 of 114 controls were unreachable and a real wheel gesture moved
        nothing. The zone is THIS box rather than the sheet: it is the element already sized to the
        viewport, so it needs no `flex`/`min-height:0` pairing to become a scroller, and the sheet's
        `min-height:100%` keeps doing what it was written to do (fill at minimum, grow past it).
        `overscroll-behavior:contain` so the scroll never chains to the page — AS-4 is a ZONE
        scrolls, never the page, and `.app-shell` stays `overflow:hidden` by design. `overflow-x`
        stays hidden: the horizontal axis was never the defect and a 2px overhang lives on B-504.
        The legacy steps' own `overflow-y:auto` zone is unaffected — its content is bounded, so this
        box does not also scroll on those steps. ON THE CARRY BRANCH THE ZONE FOLLOWS `fcTransplanted`
        TOO (B-563, dark): a legacy step keeps the live tree's `overflow:hidden` byte for byte, so a
        visitor's creator is unchanged until the twelfth step lights; the transplanted steps scroll.

        B-517 — THE GROUND. This box painted `var(--doc-paper)` under EVERY step, including the ten
        legacy ones that still carry the dark-theme ink they shipped with: pale names on pale paper,
        measured at 1.00:1 on talents and Force powers, which is Jason's "unreadable on a phone".
        The ground now follows `fcTransplanted` — the SAME predicate that already decides whether the
        shell stands aside for the board's own sheet (R-TTP-1), so this is not a second resolution
        path for the same question (PRAXIS §5). A step drawn at this band gets the revision's paper;
        a step still on its legacy shape keeps `bg`, the game-line gradient it ran on before the
        shell painted over it. That is a RESTORATION of shipped behaviour, not a new shape, so
        nothing approved is contradicted (C-MU-3) — and when each band is restyled, its paper
        arrives with its frame. `bg` was computed and deliberately unused here; it is used again. */
    <div data-band={band} data-doc-ground={fcTransplanted ? 'creator' : undefined} data-creator-step={ctrl.currentStep}
      data-mechanic-type={ctrl.gameLine ? mechanicForLine(ctrl.gameLine) : undefined}
      style={fcTransplanted
        ? { position: 'absolute', inset: 0, background: 'var(--doc-paper)', display: 'flex', flexDirection: 'column', overflowX: 'hidden', overflowY: 'auto', overscrollBehavior: 'contain', transition: 'background 0.5s ease' }
        : { position: 'absolute', inset: 0, background: bg, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'background 0.5s ease' }}>
      {/* C-SVRA-3 — the change review. Rendered at the creator's root rather than beside the dossier
          because the dossier is passed as a PROP to the master-detail shell (`detail={...}`), which
          is an expression and takes no siblings. The sheet is `position: fixed`, so where it sits in
          the tree does not affect where it appears. */}
      {speciesReview && (
        <FCSpeciesChangeReview
          review={speciesReview}
          accent={accent}
          onBack={function() { setSpeciesReview(null); }}
          onConfirm={function() {
            var r = speciesReview;
            setSpeciesReview(null);
            handleSpeciesConfirm(r.sp, r.picks, r.careerPicks);
          }} />
      )}
      {/* D2: the heading is handed the SAME cap the body uses and the rail's gutter, so it centres
          over its own content instead of over the whole window (ledger #32). */}
      {/* THE SHELL STANDS ASIDE FOR A TRANSPLANTED STEP: its header and body render only for legacy
          steps; a transplanted step mounts its own sheet (below) with its own head, folio and commit. */}
      {!fcTransplanted && (
      <div data-tour-id="p-custom-header"><QBStepHeader title={title} subtitle={subtitle} step={ctrl.stepDisplayIndex} totalSteps={ctrl.reachableTotal} onBack={handleBack} accent={accent}
        contentMax={stepColumnMax} railWidth={showBuildRail ? 'var(--desk-rail-w)' : null} /></div>
      )}
      {/* F-46 D1: on desktop the step CONTENT centers at the reading-column cap (header/footer span — Docs/CREATOR_COMMENTARY.md § "FullCreator › master-detail content cap" */}
      {/* A master-detail step takes the same content-max exemption the TREE steps take, and for the
          same measured reason (B-86): the rail is a FIXED 320px, so under the 720px reading cap the
          detail beside it would be left ~400px — and the detail is the half that has to READ. The
          concept's own mid-tier rule is "width goes to CONTENT" (§3). */}
      {/* WIDE band: the step body and the SHELL's build rail sit side by side. The rail wrapper is
          always mounted and only its width flips (A4), so crossing 1367 restyles rather than
          remounting the step beside it. The rail is on the RIGHT — concept §4's "a right rail" and
          the drawing's third grid column both put it there. */}
      {/* ═══ B-563 · THE BROWSE CLUSTER — species · career · specialization, transplanted, all three bands (revision/creator-2026-09-10) ═══ */}
{/* GAME LINE — cluster two's first transplant. It mounts HERE, beside the other
    transplanted steps and OUTSIDE the `!fcTransplanted` block below, because that block is
    the LEGACY layout in its entirety. My first cut put this branch inside it, which meant
    that at exactly the bands where the transplant is lit the legacy block does not render
    and neither did the new screen: the creator came up empty at 1440 and correct at 390.
    The empty container was the tell — the step rendered nothing rather than throwing. */}
{ctrl.currentStep === 'gameline' && fcTransplanted && (
  <FCGameLineSheet
    band={band} stepName="Game Line" onBack={handleBack} reentry={_fcSourcesReentry}
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} stepLabels={fcStepLabelsNow}
    /* TWO CHIPS, because the board draws two — the count is STRUCTURE, so one chip would be
       a real divergence rather than a data difference, exactly as it is on characteristics.
       WHICH two is a judgement I am FLAGGING, not assuming, and the same one that step
       flagged: no line is chosen yet on this screen, so there is no "player's own line" to
       lead with. The board pairs EotE with FaD and this mirrors that pair — but every PAGE
       is ours, TOC-judged under B-330 (the board's own "p.29"/"p.52" are unjudged designer
       numbers), and both land inside their book's Step 2, where the mechanic is defined. */
    cites={['eote', 'fad'].map(function(k, ci) {
      var c = ((gameData && gameData.charbuild && gameData.charbuild.stepCites || {}).gameline || {})[k];   // B-1349: derived
      // The board spaces the SECOND chip off the first with an inline 7px, the same way it
      // sets the sechead's margin in markup rather than in its stylesheet.
      return c ? <span className="hp-cite" key={k} data-step-cite="gameline"
        style={ci ? { marginLeft: '7px' } : undefined}>
        {c.book.toUpperCase() + ' P.' + (c.printed || [])[0]}</span> : null;
    }).filter(Boolean)}
    listTitle="The Three Lines"
    listCite="choose one" 
    lines={fcGameLineRows}
    selected={fcGameLinePick}
    onPick={setFcGameLinePick}
    commitLabel={fcGameLinePick
      ? ('Select ' + (gameLineTitle(fcGameLinePick) || ''))
      : 'Choose a line to continue'}
    onCommit={function() { if (fcGameLinePick) handleGameLine(fcGameLinePick); }} />
)}

{/* MECHANIC — cluster two, screen two. Mounted HERE, outside the `!fcTransplanted` block,
    for the same reason every transplanted step is: that block draws the legacy chrome and
    the board's frame brings its own bar, wave and rail. Lighting the registry without
    moving the mount rendered the step into a skipped branch — a blank creator ground with
    no console error, which reads exactly like a crashed component and is not one. */}
{ctrl.currentStep === 'mechanic' && fcTransplanted && gameData && (
  <FCMechanicStep
    savedMechanic={(ctrl.character || {}).mechanic} gameLine={ctrl.gameLine}
    gameData={gameData} partySize={ctrl.partySize} setPartySize={ctrl.setPartySize}
    accent={accent} onComplete={handleMechanicComplete} backRef={mechanicBackRef}
    hasForce={ctrl.hasForceAccess}
    xpPoolWithMechanic={ctrl.xpPoolWithMechanic}
    transplanted band={band}
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining}
    soFar={[
      { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
      { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
      { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
    ]}
    onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('mechanic') - 1)); }}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{ctrl.currentStep === 'characteristics' && fcTransplanted && (
  <FCCharSheet
    character={ctrl.character} speciesData={ctrl.selectedSpeciesData} band={band}
    xpPool={ctrl.xpPool} xpSpentOnChars={ctrl.xpSpentOnChars} xpRemaining={ctrl.xpRemaining}
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    standfirst={FC_CHAR_LEAD} cites={fcCharCites}
    soFar={[
      { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
      { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
      { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
    ]}
    changes={fcCharChanges}
    offers={fcBuyoutOffers} offerCount={fcBuyoutSpeciesCount()}
    taken={ctrl.character && ctrl.character.creationBuyouts}
    boughtHere={fcBoughtHere}
    onBack={handleBack} onNext={ctrl.nextStep} onCharChange={handleCharChange}
    onBuyoutTake={handleBuyoutTake} onBuyoutUndo={handleBuyoutUndo}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{ctrl.currentStep === 'skills' && fcTransplanted && gameData && ctrl.character && (
  <FCSkillsSheet
    band={band} stepName="Skills"
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining} xpSpentOnSkills={ctrl.xpSpentOnSkills}
    soFar={[
      { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
      { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
      { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
    ]}
    cite={fcStepCite(gameData.charbuild.stepCites, 'skills', ctrl.gameLine)}
    citeChips={fcStepCiteChips(gameData.charbuild.stepCites, 'skills', ctrl.gameLine)}
    diceCite={fcStepCite(gameData.charbuild.stepCites, 'dice_pool', ctrl.gameLine)}
    skillNames={gameData.charbuild.skillNames} skillChars={gameData.charbuild.skillChars || {}}
    skillSheetKeys={gameData.charbuild.skillSheetKeys}
    charSkills={ctrl.character.skills || {}} characteristics={ctrl.character.characteristics}
    skillCaps={ctrl.character.speciesSkillCaps || {}}
    careerSkills={ctrl.selectedCareerData ? (ctrl.selectedCareerData.career_skills || []) : []}
    specSkills={ctrl.selectedSpecData ? (ctrl.selectedSpecData.career_skills || []) : []}
    careerMax={fcCareerFreeRanks} specMax={fcSpecFreeRanks}
    speciesGrant={fcSpeciesChoiceGrant} speciesPool={fcSpeciesChoicePool}
    speciesName={ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name}
    careerStatus={fcCareerStatusIds}
    onToggle={handleSkillsToggle}
    onBuy={handleBuySkillRank} onRefund={handleRefundSkillRank}
    onConfirm={handleSkillsSheetConfirm}
    onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('skills') - 1)); }}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{/* TALENTS — cluster three, screen two. The tree is the ONE component's board skin over the
    SAME model, handlers and B-181 interception the legacy step builds (R-TTS-1); the choice
    picker is mounted beside it unchanged (R-C3-6: the picker's own increment comes after). */}
{ctrl.currentStep === 'talents' && fcTransplanted && gameData && ctrl.character && fullSpecData && (function() {
  var talentsSheet = (
    <FCTalentsSheet
      band={band} stepName="Talents" accent={accent}
      stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
      steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
      xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining} xpSpentOnTalents={ctrl.xpSpentOnTalents}
      soFar={[
        { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
        { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
        { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
      ]}
      cite={fcStepCite(gameData.charbuild.stepCites, 'talents', ctrl.gameLine)}
      cellCite={fcStepCite(gameData.charbuild.stepCites, 'talent_cell', ctrl.gameLine)}
      spec={fullSpecData} talentDb={gameData.charbuild.talents || {}} skillNames={gameData.charbuild.skillNames || {}}
      skillChars={gameData.charbuild.skillChars || {}} charbuild={gameData.charbuild} careerStatus={fcCareerStatusIds}
      talents={ctrl.character.talents || {}} talentSkillChoices={ctrl.character.talentSkillChoices || {}} talentChoices={ctrl.character.talentChoices} modsNotice={!!_fcModsNotice}
      onModsRetry={_fcModsRetry}
      onFileChoices={handleTalentChoicesFile}
      model={talentTreeToModel({ specData: fullSpecData, talentDb: gameData.charbuild.talents || {}, character: ctrl.character,
        // THE INLINE PICKER (the transplant's own): the buy lands directly and the sheet opens the
        // picker for a choice-talent. THE PHONE KEEPS THE OVERLAY INTERCEPTION it has always had:
        // its purchase surface is `DirectionalTreeSheet`, kept by Jason's word (R-TCB-A1-1), and
        // that sheet has no room for the sheet-inline picker the wide band draws under the pressed
        // cell. So at 390 the buy of a choice-talent raises `TalentSkillChoicePicker` exactly as it
        // did before the restyle — same handlers, same overlay, mounted below.
        availableXp: ctrl.xpRemaining,
        onBuy: band === 'mobile' ? handleTalentBuy : handleTalentBuyDirect,
        // THE REFUND TAKES THE CELL'S CHOICE WITH IT AT EVERY BAND (B-574). The revision handed the
        // phone the bare `handleTalentRefund`, so a phone refund of a choice-talent left its filed
        // skills on `talentSkillChoices` — career skills for a talent no longer owned, measured on
        // the revision's own build. The buy still differs by band (overlay vs inline picker); the
        // refund never did.
        onRefund: handleTalentRefundWithChoices,
        talentSkillChoices: ctrl.character.talentSkillChoices, talentChoices: ctrl.character.talentChoices, skillNames: gameData.charbuild.skillNames || {},
        onChooseSkills: band === 'mobile'
          ? function(talentKey, specKey, row, col) {
              var trec = gameData && gameData.charbuild.talents ? gameData.charbuild.talents[talentKey] : null;
              var grants = (window.CharResolve && CharResolve.talentChoiceGrantsOf(trec)) || [];
              var pos = row + '-' + col;
              var existing = CharResolve.pickOf(ctrl.character, specKey, pos, 'career_skills') || [];   // R-M8G-4: the one reader
              setCreatorTalentPicker({ mode: 'choose', talentKey: talentKey, talentName: (trec && trec.name) || talentKey, specKey: specKey, row: row, col: col, grants: grants, initialPicks: existing });
            }
          : function() {},
        skin: 'board', cssSized: true })}
      onConfirm={function() { ctrl.setSubView(null); ctrl.nextStep(); }}
      onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('talents') - 1)); }}
      onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
  );
  return (
    <React.Fragment>
      {/* ── THE STEP'S OWN SCROLL ZONE AT 390 (AS-4; R-PTC-3's REACH term) ────────────────
          The creator's step container is `position:absolute; inset:0; overflow:hidden` and
          exactly the viewport's height. The TEN LEGACY STEPS each carry their own
          `overflow-y:auto` zone inside it and scroll; the two steps already lit at mobile do
          NOT, which is B-509 — 5,961px unreachable on the species register, 1,435px on
          characteristics, measured. Lighting talents at mobile would have joined that class:
          the phone sheet is 1,738px of content in a 790px box, and every cell below the fold
          would have been unreachable by any gesture — a REGRESSION against the legacy step
          this restyle replaces, which scrolled.
          THIS IS NOT B-509'S FIX and does not touch it: the shell's box and `.hp-phonesheet`'s
          own scroll rules are `phone-shell-scroll`'s OWNS (seat 2) and are untouched here.
          This is the same per-step zone the legacy talents step had, carried through the
          restyle — "same functionality as prior" — and it sits OUTSIDE `.hp-phonesheet`, so
          the emitted tree DOM and parity are unaffected. When the shell's general fix lands,
          this zone is subsumed by it and can retire on that card's line. */}
      {/* THE PER-STEP ZONE IS RETIRED (D-BPT-1). The comment above said it plainly — "when the
          shell's general fix lands, this zone is subsumed by it and can retire on that card's
          line" — and B-509's fix landed at `ab52f250`: the creator's mobile mount box is now
          `overflow-y:auto` with `overscroll-behavior:contain`, so ONE zone scrolls every step
          instead of each step bringing its own. Two zones is not twice as safe: nested
          scrollers eat each other's gestures at the boundary, and AS-4 asks for A zone, not a
          stack of them. The REACH leg below is what proves the shell's zone actually reaches
          this step's content; it is the honest test of a removal, not of an addition. */}
      {talentsSheet}
      {/* B-1055 MOUNT: the one Dedication picker, both bands (ADDENDUM 99 item 14). */}
      {creatorCharPick && h(M7CharPickSheet, { pick: creatorCharPick, charbuild: gameData.charbuild,
          characteristics: CharResolve.creatorCharacteristicsWithPending(ctrl.character), sheet: ctrl.character,
          onClose: closeCreatorCharPick, onChoose: handleCreatorCharChoose })}
      {/* B-181 AT 390 — the talent-choice skill picker, raised by a choice-talent's buy or by
          the sheet's "needs its skills" prompt. It travelled with the legacy step; the legacy
          step is dark at mobile from this card on, so it is mounted here, unchanged. */}
      {band === 'mobile' && creatorTalentPicker && (
        <TalentSkillChoicePicker
          talentName={creatorTalentPicker.talentName} grants={creatorTalentPicker.grants}
          charbuild={gameData.charbuild} careerStatusIds={fcCareerStatusIds}
          cost={creatorTalentPicker.cost} mode={creatorTalentPicker.mode}
          initialPicks={creatorTalentPicker.initialPicks} accent={accent}
          onConfirm={handleTalentChoiceConfirm}
          onCancel={function() { setCreatorTalentPicker(null); }} />
      )}
    </React.Fragment>
  );
})()}
{ctrl.currentStep === 'forcepowers' && fcTransplanted && gameData && ctrl.character && (
  <FCForceSheet
    band={band} stepName="Force Powers"
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining} xpSpentOnForcePowers={ctrl.xpSpentOnForcePowers}
    soFar={[
      { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
      { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
      { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
    ]}
    cite={fcStepCite(gameData.charbuild.stepCites, 'force_powers', ctrl.gameLine)}
    ratingCite={fcStepCite(gameData.charbuild.stepCites, 'force_rating', ctrl.gameLine)}
    powers={buildBasicForcePowerList(Object.values(gameData.charbuild.forcePowers || {}), ctrl.gameLine)}
    owned={ctrl.character.forcePowers || {}}
    forceRating={creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild)}
    onBuy={handleForcePowerBuy} onRefund={handleForcePowerRefund}
    careerPowers={fcCareerPowers} onCareerPick={handleCareerPowerPick} cpRefused={cpRefused}
    skillNames={gameData.charbuild.skillNames} skillSheetKeys={gameData.charbuild.skillSheetKeys}
    onConfirm={function() { if (fcCareerPowers && fcCareerPowers.owed) return; ctrl.setSubView(null); ctrl.nextStep(); }}
    onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('forcepowers') - 1)); }}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{/* GEAR — B-563 step 10: the revision's sheet at all three bands, behind the switch. Same
    handlers as the legacy step (the store door, sell, continue); the purse and encumbrance cites
    from the step-cites registry. */}
{ctrl.currentStep === 'gear' && fcTransplanted && gameData && ctrl.character && (
  <FCGearSheet
    band={band} stepName="Gear"
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    soFar={[
      { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
      { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
      { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
    ]}
    cite={fcStepCite(gameData.charbuild.stepCites, 'starting_credits', ctrl.gameLine)}
    encCite={fcStepCite(gameData.charbuild.stepCites, 'encumbrance', ctrl.gameLine)}
    credits={ctrl.credits}
    inventory={(ctrl.character.gearLedger && ctrl.character.gearLedger.inventory) || []}
    reg={(gameData && gameData.canonical) || window._canonicalRegistries || null}
    brawn={ctrl.character.characteristics && ctrl.character.characteristics.brawn}
    speciesEncDelta={CharResolve.speciesEncumbranceDeltaOf(ctrl.selectedSpeciesData, ctrl.character.speciesOptionPicks, ctrl.character.creationBuyouts)}
    speciesEncRows={CharResolve.speciesEncumbranceRowsOf(ctrl.selectedSpeciesData, ctrl.character.speciesOptionPicks, ctrl.character.creationBuyouts)}
    entitlementLines={fcEntitlementLines(ctrl.selectedCareerData, ctrl.character.entitlements)}
    onEntitlementToggle={function(id) { ctrl.updateCharacter('entitlements', fcEntitlementToggle(fcEntitlementLines(ctrl.selectedCareerData, ctrl.character.entitlements), id)); }}
    onShop={function() { ctrl.setSubView('store'); }} onSell={handleGearSell}
    onConfirm={function() { ctrl.setSubView(null); ctrl.nextStep(); }}
    onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('gear') - 1)); }}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{/* MOTIVATION — B-563 step 11: the revision's sheet at all three bands, behind the switch. The
    confirm handler is the legacy step's; the FaD pair is SHOWN here and decided on the mechanic step. */}
{ctrl.currentStep === 'motivation' && fcTransplanted && gameData && (
  <FCMotivationSheet
    band={band} stepName="Motivation"
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining}
    soFar={[
      { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
      { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
      { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
    ]}
    cats={gameData.charbuild.motivationCategories || []}
    specs={gameData.charbuild.motivationSpecifics || []}
    rolls={gameData.charbuild.categoryRolls || null}
    line={ctrl.gameLine}
    existing={ctrl.character && ctrl.character.motivation}
    mechanic={ctrl.character && ctrl.character.mechanic}
    moralityLayer={ctrl.character && ctrl.character.moralityLayer}
    narrativeMechanics={gameData.narrativeMechanics}
    onEditMechanic={function() { ctrl.goToStep(FC_STEPS.indexOf('mechanic')); }}
    onConfirm={handleMotivationConfirm}
    onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('motivation') - 1)); }}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{/* REVIEW — B-563 step 12, the last: the revision's sheet at all three bands, behind the switch.
    The finish is the same onComplete the legacy step calls; the assembly runs inside the sheet. */}
{ctrl.currentStep === 'review' && fcTransplanted && gameData && ctrl.character && (
  <FCReviewSheet
    band={band} stepName="Review"
    stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
    steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
    character={ctrl.character} speciesData={ctrl.selectedSpeciesData} specData={fullSpecData} gameData={gameData}
    xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining} credits={ctrl.credits} partySize={ctrl.partySize}
    reg={(gameData && gameData.canonical) || window._canonicalRegistries || null}
    cite={fcStepCite(gameData.charbuild.stepCites, 'derived_attributes', ctrl.gameLine)}
    onNameChange={function(v) { ctrl.updateCharacter('name', v); }}
    onSetPocketMoney={function(v) { ctrl.updateCharacter('pocketMoney', v); }}
    onGoToStep={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st), { fromReview: true }); }}
    onConfirm={function() { onComplete({ character: ctrl.character, speciesData: ctrl.selectedSpeciesData, specData: fullSpecData, gameData: gameData, reentry: (resumeDraft && resumeDraft.reentry) || null }, ctrl.gameLine, ctrl.selectedSpeciesData); }}
    onBack={function() { ctrl.goToStep(Math.max(0, FC_STEPS.indexOf('review') - 1)); }}
    onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
)}
{ctrl.currentStep === 'career' && fcTransplanted && (
        <FCBrowseSheet
          band={band} stepName="Career"
          stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
          steps={ctrl.reachableSteps || []}
          stepLabels={fcStepLabelsNow}
          xpPool={ctrl.xpPool} xpSpentHere={0} xpRemaining={ctrl.xpRemaining}
          soFar={[
            { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
            { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
            { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
          ]}
          listTitle="Careers" rows={fcCareerRows} selected={ctrl.selectedCareerData}
          listCite={fcCareerRows.length + ' \u00b7 from your enabled books'}
          readLeft={(function() {
            var c = ctrl.selectedCareerData || {};
            return (
              <React.Fragment>
                {/* THROUGH THE EATER AND THE SHARED DRAWER, exactly as the card it replaces did.
                    A career description may carry dice-symbol tokens (`[SE]`, `[BO]`), and
                    rendering it as plain text prints the literal brackets on the page —
                    `smoke_r3q2b_talent_glyphs` (D) injects one and checks precisely that. R3 Q2b
                    deleted an inline catch-all here and routed this surface through the shared
                    pair; building the frame as drawn had quietly unrouted it again. The
                    specialization description will want the same treatment when that step lands. */}
                {fcReadProse(c.name, fcRecordSourceLabel('careers', c, fcBookLabel),
                             glyphs(cleanMarkup(c.description || ''), null, CREATOR_GLYPHS), true)}
                <div className="hp-sechead" style={{ margin: '22px 0 11px' }}>
                  <h2>Specializations</h2>
                  <span className="hp-cite">{String(fcCareerSpecs.length)}</span>
                </div>
                <div className="hp-plate" data-pad="read">
                  <p className="hp-prose hp-prose-sm" style={{ margin: 0, maxWidth: 'none' }}>
                    {fcCareerSpecs.join(' \u00b7 ')}
                  </p>
                  <p className="hp-prose hp-prose-sm" style={{ margin: '9px 0 0', maxWidth: 'none' }} data-career-spec-note="1">
                    {'You choose one at the next step, and it is free when it comes from your own career.'}
                  </p>
                </div>

                {/* STARTING ENTITLEMENT — content the BOARD'S FRAME NEVER HAD OCCASION TO DRAW.
                    Exactly ONE career of twenty-one carries one (Bounty Hunter's Imperial
                    Peace-Keeping Certificate) and the frame draws Hired Gun, so the drawing's
                    silence here is a GAP IN THE RECORD, not a decision to drop it — which is why
                    it is filled with the board's own primitives (sechead + read plate, the same
                    two objects the sections above use) rather than treated as a design fork.
                    Building the frame exactly as drawn had deleted a ruled, registry-driven line
                    from the one career that has it; `smoke_career_card_registry` (d) noticed.
                    COLLAPSES WHEN ABSENT — its own assert (e) requires that the other twenty
                    careers show no empty section. */}
                {/* At the MID band the entitlement sits under Career Skills instead (B-594): the
                    left column is the long one there, and Bounty Hunter's — the only career
                    that has one — was the last 38px keeping the page from being a snapshot. */}
                {band !== 'mid' ? fcCareerEntitlement(c) : null}
              </React.Fragment>
            );
          })()}
          /* THE PANE BEFORE A PICK draws nothing: its sentence is cut (copy-voice-sweep player/app.jsx:23589:18, D-289). */
          readRight={(
            <React.Fragment>
              {fcReadSkills('Career Skills',
                fcCareerSkills.length + ' \u00b7 ' + ((ctrl.selectedCareerData || {}).free_ranks || 0)
                  + ' free ranks', fcCareerSkills, true)}
              {band === 'mid' ? fcCareerEntitlement(ctrl.selectedCareerData || {}) : null}
            </React.Fragment>
          )}
          lead={'Your career sets <b>' + (((ctrl.selectedCareerData || {}).career_skills || []).length || 8)
            + ' career skills</b>. ' + FC_CAREER_LEAD_RULE}
          cites={fcStepCiteStrings(gameData && gameData.charbuild && gameData.charbuild.stepCites, 'career', ctrl.gameLine)}
          onBack={handleBack}
          onPick={function(id) {
            // The board's rail rows carry ids; the shipped handlers take the RECORD (they read
            // `.id`/`.name` off it). Resolving here keeps both handlers untouched — the legacy
            // step still calls them the same way, which is what makes this a transplant rather
            // than a fork.
            var rec = (cbCareerList || []).filter(function(c) { return c.id === id; })[0];
            if (rec) handleCareerSelect(rec);
          }}
          onConfirm={function() {
            if (ctrl.selectedCareerData) handleCareerConfirm(ctrl.selectedCareerData);
          }}
          /* ── THE PHONE BAND'S THREE SLOTS (`phone-career-census` R-PCC-1) ───────────────────
             Ignored at mid and wide; they are what turns the sheet's props into the arrangement
             `phone_career()` draws. Nothing here computes a number the desktop pane does not
             already compute — the read content below is the SAME `fcReadProse`/`fcReadSkills`
             calls, in the frame's own order, so a change to what a career screen says lands on
             both bands at once. */
          phoneStatus={<React.Fragment>
            <div><span className="hp-status-k">Starting</span>
              <span className="hp-status-v">{ctrl.xpPool + ' XP'}</span></div>
            {/* "CAREERS 6 OF 21" — the own-line count of the enabled total, both COMPUTED. The
                second figure is what the picker lists (Jason's "whole shelf"); the first is how
                many of them are the line picked at step 1, which is the fact the strip is for. */}
            <div><span className="hp-status-k">Careers</span>
              <span className="hp-status-v">
                {fcCareerPhonePick.own + ' of ' + fcCareerPhonePick.total}</span></div>
          </React.Fragment>}
          phoneRows={fcCareerPhonePick.rows}
          phoneDivideAfter={fcCareerPhonePick.own}
          phoneRead={(function() {
            var c = ctrl.selectedCareerData;
            /* NOTHING PICKED YET — the same words the wide pane's `emptyRead` uses, because the
               board's own "states with nothing in them" section says each empty state SAYS WHAT
               THE SPACE IS FOR, and the phone must not apologise differently from the desktop. */
            if (!c) {
              /* Its sentence is cut (D-289) and the pane draws nothing — but an EMPTY FRAGMENT, not
                 null: the shell takes the phone band only `p.band === 'mobile' && p.phoneRead`, and a
                 null here dropped a phone with no career picked into the desktop frame. */
              return <React.Fragment />;
            }
            var ent = (c.starting_entitlements || [])[0];
            return (
              <React.Fragment>
                {/* THE DESCRIPTION, HEAD-LESS — the picker's summary above it already names the
                    career, so the frame draws the plate alone. Through the eater and the shared
                    drawer, exactly as the wide pane is: a career description may carry dice-symbol
                    tokens (`[SE]`, `[BO]`) and plain text prints the literal brackets
                    (`smoke_r3q2b_talent_glyphs` (D) injects one and checks precisely that). */}
                {fcReadProse(null, null, glyphs(cleanMarkup(c.description || ''), null, CREATOR_GLYPHS), true, null, true)}
                {/* CAREER SKILLS — the frame draws this block SECOND at 390 where the wide sheet
                    puts it in the right-hand column; same builder, same eight rows, same cite. */}
                {fcReadSkills('Career Skills',
                  fcCareerSkills.length + ' · ' + (c.free_ranks || 0) + ' free ranks',
                  fcCareerSkills, false, null, true)}
                {/* SPECIALIZATIONS AS A PLAIN LINE (R-PCC ruling's KEEP-DRAWN row) — names only,
                    not clickable, which is what the legacy phone detail already did and what the
                    frame draws. The wide pane's second sentence ("You choose one at the next
                    step…") is NOT drawn at 390 and is not carried: the next step's own screen
                    makes the same statement, and it is named in the gate rather than added
                    silently to a frame Jason approved. */}
                {fcReadProse('Specializations', String(fcCareerSpecs.length),
                             fcCareerSpecs.join(' · '), false, null, true)}
                {/* STARTING ENTITLEMENT — content the frame never had occasion to draw, because
                    exactly ONE career of twenty-one carries one (Bounty Hunter's Imperial
                    Peace-Keeping Certificate) and the frame draws Hired Gun. A GAP IN THE RECORD,
                    not a decision to drop it — the wide pane fills it with the board's own two
                    primitives and so does this, through the same builder. Collapses when absent,
                    so the other twenty careers show no empty section. */}
                {ent ? fcReadProse('Starting Entitlement', ent.name, ent.note, false, null, true)
                     : null}
              </React.Fragment>
            );
          })()}
          onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
      )}

{ctrl.currentStep === 'specialization' && fcTransplanted && band === 'mobile' && (
        <FCSpecSheetPhone
          band={band} stepName="Specialization"
          stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex}
          stepTotal={ctrl.reachableTotal}
          steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
          xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining}
          careerName={(ctrl.selectedCareerData || {}).name}
          /* THE SAME DATA PREP THE DESKTOP READS, refusal rule included — one resolution path for
             "may this character take this specialization", never a second. */
          /* DECORATED ONCE, HERE, over the SAME rows the desktop rail reads: the refusal rule,
             then the two facts the unpicked arrangement's cards print — the registry's own skill
             NAMES and the printed Force-rating prerequisite. Resolved at the call site because the
             name map and `minForceRatingOf` already live here; a second resolution inside the sheet
             would be a second answer to "what does this specialization add". */
          rows={fcSpecRows.map(fcSpecRefusal).map(function(r) {
            var all = (gameData && gameData.charbuild.specializations) || {};
            var names = (gameData && gameData.charbuild.skillNames) || {};
            var rec = all[r.id] || {};
            return Object.assign({}, r, {
              needFR: minForceRatingOf(rec),
              shortPath: shortPathOfferedFor(rec, ctrl.selectedCareerData, fcForceRating)
                && !shortPathTakenIn(ctrl.character.talents),
              skillNames: (rec.career_skills || []).map(function(id) { return names[id] || id; }),
            });
          })}
          shelfRows={fcSpecShelfShown.map(fcSpecRefusal)}
          shelfWithheld={fcSpecShelfWithheld}
          selected={ctrl.selectedSpecData} gate={fcSpecGate}
          specSkills={fcSpecSkills} tree={fcSpecTree}
          forceRating={fcForceRating}
          /* THE LEDGER'S TWO ROWS ARE THE APP'S OWN DERIVATION, not two reads of one number:
             `creatorForceRating` is (career access ? 1 : 0) + every talent bonus, so the BASE is
             the first half and NOW is the whole — which is what makes the Short Path visible in the
             ledger the moment it is taken. */
          forceRatingBase={ctrl.hasForceAccess ? 1 : 0}
          picks={fcSpecPicks}
          onPoolPick={function(poolId, optId, count) {
            setFcSpecPicks(function(prev) {
              var next = Object.assign({}, prev);
              var cur = (next[poolId] || []).slice();
              var at = cur.indexOf(optId);
              if (at >= 0) cur.splice(at, 1);
              else if (cur.length < count) cur.push(optId);
              next[poolId] = cur;
              return next;
            });
          }}
          onSwitch={function() {
            handleSpecConfirm(ctrl.selectedSpecData, fcSpecGate && fcSpecGate.switchTo, fcSpecPicks);
          }}
          shortPathTaken={shortPathTakenIn(ctrl.character.talents)
            && (ctrl.selectedCareerData || {}).id === 'jedi'
            && minForceRatingOf(ctrl.selectedSpecData || {}) === 2}
          onShortPathTake={handleShortPathTake} onShortPathUndo={handleShortPathUndo}
          soFar={[
            { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
            { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
            { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
          ]}
          cites={fcStepCiteStrings(gameData && gameData.charbuild && gameData.charbuild.stepCites, 'specialization', ctrl.gameLine)}
          onBack={handleBack}
          onPick={function(id) {
            var all = (gameData && gameData.charbuild.specializations) || {};
            if (all[id]) handleSpecPreview(all[id]);
          }}
          commitDisabled={!ctrl.selectedSpecData
            || !!(fcSpecGate && (fcSpecGate.frRefused || !fcSpecGate.allPicked || fcSpecGate.switchTo))}
          onConfirm={function() {
            if (!ctrl.selectedSpecData || !fcSpecGate) return;
            if (fcSpecGate.frRefused || !fcSpecGate.allPicked || fcSpecGate.switchTo) return;
            handleSpecConfirm(ctrl.selectedSpecData, null, fcSpecPicks);
          }}
          onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
      )}

{ctrl.currentStep === 'specialization' && fcTransplanted && band !== 'mobile' && (
        <FCBrowseSheet
          band={band} stepName="Specialization"
          stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
          steps={ctrl.reachableSteps || []}
          stepLabels={fcStepLabelsNow}
          xpPool={ctrl.xpPool} xpSpentHere={0} xpRemaining={ctrl.xpRemaining}
          soFar={[
            { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
            { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
            { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
          ]}
          /* TWO SHELVES, ONE RAIL (R-WUS-1). The first head names the CAREER whose specializations
             these are — the board's own "Hired Gun Specializations", which is what stops six rows
             reading as the whole set. The second names what it lists for the same reason, and that
             is precisely why the 21 could not simply be appended to the first: the head would then
             be a false statement about 21 of its own rows.

             THE SECOND SECTION IS DECLARED, and the declaration is correct HERE in a way it was not
             the last time this seat reached for it: `data-ruled-delta` DELETES the element from the
             compared tree, so it is right only for an object the board does NOT draw. The board has
             no two-shelf `spec_step` frame at all (every one is `spec_id=marauder`, a career spec),
             so this shelf is a genuine ADDITION rather than a content difference. It expires
             against the lab's two-shelf frame, which is queued with a stable pin — at which point
             parity compares it and the declaration comes off. */
          sections={[
            { title: ((ctrl.selectedCareerData || {}).name || '') + ' Specializations',
              cite: String(fcSpecRows.length),
              rows: fcSpecRows.map(fcSpecRefusal) },
            /* THE DECLARATION IS OFF (R-WUS-A2-1). It was right while no board frame drew a
               two-shelf rail; the lab has now drawn one at pin 881037eb, so this section is
               COMPARED rather than excluded — which is the whole point of an expiry being named
               when it is written. */
            { title: 'UNIVERSAL SPECIALIZATIONS',
              /* The CITE counts what is OFFERED, never the membership before the rules ran — a
                 head that said 21 over 17 rows would be the same false statement the separate
                 section exists to avoid. */
              cite: 'open to any career \u00b7 ' + fcSpecShelfShown.length,
              rows: fcSpecShelfShown.map(fcSpecRefusal),
              foot: (
                <React.Fragment>
                  {/* WHERE THIS SHELF CAME FROM, stated rather than mimed. The board draws this
                      line because a static frame showing all 21 would be claiming a filter ran;
                      ours says the same thing and is TRUE, because the filter really did run. */}
                  <div className="hp-maker" style={{ marginTop: '7px' }}>
                    {'Active sources \u00b7 game-line filter '
                      + ((window.SWRPGSources && window.SWRPGSources.lineFilterOn
                          && window.SWRPGSources.lineFilterOn()) ? 'on' : 'off')}
                  </div>
                  {fcSpecShelfWithheld.length ? (
                    <div className="hp-plate" data-pad="read" style={{ marginTop: '11px' }}>
                      <div className="hp-maker" style={{ marginBottom: '6px' }}>
                        {'Withheld \u00b7 ' + fcSpecShelfWithheld.length}
                      </div>
                      <p className="hp-prose hp-prose-sm" style={{ margin: 0, color: 'var(--doc-ink-muted)' }}>
                        Droids cannot be Force sensitive (eote p.275).
                      </p>
                      <p className="hp-prose hp-prose-sm" style={{ margin: '7px 0 0', color: 'var(--doc-ink-muted)' }}>
                        {fcSpecShelfWithheld.map(function(r) { return r.name; }).join(', ')}
                      </p>
                    </div>
                  ) : null}
                </React.Fragment>
              ) },
          ]}
          selected={ctrl.selectedSpecData}
          /* ── THE LEAD BLOCK: IDENTITY, THEN WHETHER YOU PASS ──────────────────────────────
             Census P1 RETIRES the old order (`lab-laps-adoption` A1; the layout pass §73.1). The
             board's reasoning, which is the whole reason this moved: the tree used to render LAST,
             under Bonus Career Skills and the notes, so the step's own lead — "the tree is where
             your build's edge comes from" — pointed at the thing furthest down its own page.

             THE GATES ARE HERE AND NOT IN THE TEACHING, and the board says why in as many words: a
             restriction is the reason you may not have this AT ALL, and putting it after the tree
             would let a reader study a tree they cannot take. Identity, then whether you pass, then
             the hero, then what it teaches.

             THE MARKS RIDE THE IDENTITY HEAD NOW. They used to sit on their own row between the
             skills head and its plate — the phone's shape, and right for it, because at 390 a third
             and fourth chip on a row carrying a heading and a count measured past the frame edge.
             This step is `{mid:1, wide:1}` in `FC_TRANSPLANTED_STEPS` and is not drawn at 390 at
             all, so that constraint does not reach the arrangement the board draws here. */
          readLead={(function() {
            var sp = ctrl.selectedSpecData || {};
            return (
              <React.Fragment>
                {fcReadProse(sp.name, fcRecordSourceLabel('specializations', sp, fcBookLabel),
                            fcSpecDescNode(sp, CREATOR_GLYPHS), true,
                            fcSpecMarkList(sp), false, !fcSpecDescText(sp))}
                {/* (7) AS PRINTED — the registry's own `restriction_note`. Null for 136 of 137. */}
                {fcSpecAsPrinted(sp)}
                {/* (3) the choice pools, as board rows — a gate: they hold the commit until taken. */}
                {fcSpecGate ? fcSpecPoolPlates(fcSpecGate.pools, fcSpecPicks,
                  function(poolId, optId, count) {
                    setFcSpecPicks(function(prev) {
                      var next = Object.assign({}, prev);
                      var cur = (next[poolId] || []).slice();
                      var at = cur.indexOf(optId);
                      if (at >= 0) cur.splice(at, 1);
                      else if (cur.length < count) cur.push(optId);
                      next[poolId] = cur;
                      return next;
                    });
                  }) : null}
                {/* (2) the species requirement. */}
                {fcSpecGate ? fcSpecSwitchPlate(fcSpecGate.switchTo, function() {
                  handleSpecConfirm(ctrl.selectedSpecData, fcSpecGate.switchTo, fcSpecPicks);
                }) : null}
                {/* (4) THE SHORT PATH, and the record is SILENT about where it sits on this step:
                    census P4 says it is drawn in `spp_card`, not in `spec_step`, and rules KEEP
                    AS-IS — do not move it into the step. It is not being moved into the step; it
                    was already here and stays. It rides the gates rather than the teaching because
                    it ANSWERS the Force-rating refusal the gates above raise, and separating an
                    offer from the refusal it answers is the dead-control shape. Disclosed as a
                    placement the drawing does not fix. */}
                {fcSpecGate ? fcSpecShortPath(
                  fcSpecGate.shortPath,
                  shortPathTakenIn(ctrl.character.talents) && (ctrl.selectedCareerData || {}).id === 'jedi'
                    && minForceRatingOf(ctrl.selectedSpecData || {}) === 2,
                  ctrl.xpPool, ctrl.xpRemaining,
                  handleShortPathTake, handleShortPathUndo) : null}
              </React.Fragment>
            );
          })()}
          /* ── THE TEACHING COLUMNS, BENEATH THE TREE ───────────────────────────────────────
             `first` is TRUE on the skills head now: it opens its own column, so it takes the
             board's `margin:0 0 11px` rather than the 22px a following head takes. */
          readLeft={(function() {
            return (fcSpecGate && fcSpecGate.skillLess)
              ? fcSpecSkillLessPlate(null, false)
              : fcReadSkills('Bonus Career Skills', 'choose 2 later', fcSpecSkills, true);
          })()}
          /* (6) B-371 — the no-stacking note, the teaching's second column. It carries its own
             `margin-top:12px`, which is the board's. */
          readRight={fcNoStackNote()}
          belowRead={fcSpecTree ? fcReadTree('What the Tree Holds', 'bought at step 8', fcSpecTree) : null}
          cites={fcStepCiteStrings(gameData && gameData.charbuild && gameData.charbuild.stepCites, 'specialization', ctrl.gameLine)}
          onBack={handleBack}
          onPick={function(id) {
            var all = (gameData && gameData.charbuild.specializations) || {};
            if (all[id]) handleSpecPreview(all[id]);
          }}
          /* THE COMMIT CARRIES THE GATES. A refused spec, an unmet choice pool and a required
             species switch each hold the door — the same three conditions the shipped detail
             enforces, in the same order, so no legal character becomes reachable that was not
             before and none becomes unreachable that was. */
          commitDisabled={!!(fcSpecGate && (fcSpecGate.frRefused || !fcSpecGate.allPicked
                                            || fcSpecGate.switchTo))}
          onConfirm={function() {
            if (!ctrl.selectedSpecData || !fcSpecGate) return;
            if (fcSpecGate.frRefused || !fcSpecGate.allPicked || fcSpecGate.switchTo) return;
            handleSpecConfirm(ctrl.selectedSpecData, null, fcSpecPicks);
          }}
          onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
      )}

{ctrl.currentStep === 'species' && fcTransplanted && band === 'mobile'
        && fcPhoneScreen === 'entry' && ctrl.selectedSpeciesData && (
        <FCSpeciesSheetPhone
          band={band}
          stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
          steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
          species={ctrl.selectedSpeciesData}
          index={Math.max(1, fcSpeciesRows.map(function(r) { return r.id; })
            .indexOf((ctrl.selectedSpeciesData || {}).id) + 1)}
          total={fcSpeciesRows.length}
          cite={fcSpeciesCite(gameData && gameData.charbuild && gameData.charbuild.stepCites)}
          wash={speciesImgPath(ctrl.selectedSpeciesData)}
          picks={fcSpeciesPicks} onPick={fcOnSpeciesPick} moved={fcSpeciesMoved}
          careerGroups={fcCareerGroups} careerPicks={fcCareerPicks} onCareerPick={fcOnCareerPick}
          skillNames={(gameData && gameData.charbuild.skillNames) || {}}
          skillDescs={(gameData && gameData.charbuild.skillDescs) || {}}
          skillSheetKeys={(gameData && gameData.charbuild.skillSheetKeys) || {}}
          onBack={function() { setFcPhoneScreen('list'); }}
          onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }}
          onConfirm={function() {
            if (ctrl.selectedSpeciesData) {
              handleSpeciesSelectPress(ctrl.selectedSpeciesData, fcSpeciesPicks, fcCareerPicks);
            }
          }} />
      )}

{ctrl.currentStep === 'species' && fcTransplanted && band === 'mobile'
        && fcPhoneScreen !== 'entry' && (
        <FCSpeciesListPhone
          band={band}
          stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
          steps={ctrl.reachableSteps || []} stepLabels={fcStepLabelsNow}
          rows={fcSpeciesRows}
          total={(cbSpeciesList || []).length}
          query={fcSpeciesQuery} onSearch={setFcSpeciesQuery}
          selected={ctrl.selectedSpeciesData}
          career={ctrl.selectedCareerData}
          lineTitle={ctrl.gameLine ? gameLineTitle(ctrl.gameLine) : null}
          lineShort={ctrl.gameLine
            ? gameLineTitle(ctrl.gameLine).replace('Edge of the Empire', 'Edge')
                .replace(' Core Rulebook', '')
            : null}
          cite={fcSpeciesCite(gameData && gameData.charbuild && gameData.charbuild.stepCites)}
          onPick={function(id) {
            var sp = (cbSpeciesList || []).filter(function(x) { return x.id === id; })[0];
            // B-436 — through the ONE handler that asks the source question. The phone's own
            // navigation travels as the continuation; the decision of whether to ask does not.
            if (sp) handleSpeciesSelect(sp, function(rec) {
              ctrl.setSelectedSpeciesData(rec);
              setFcPhoneScreen('entry');
            });
          }}
          onBack={handleBack}
          onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
      )}

{ctrl.currentStep === 'species' && fcTransplanted && band !== 'mobile' && (
        <FCBrowseSheet
          band={band} stepName="Species" wideGrid
          stepIndex={ctrl.stepDisplayIndex} stepReached={ctrl.maxStepDisplayIndex} stepTotal={ctrl.reachableTotal}
          steps={ctrl.reachableSteps || []}
          stepLabels={fcStepLabelsNow}
          xpPool={ctrl.xpPool} xpSpentHere={0} xpRemaining={ctrl.xpRemaining}
          soFar={[
            { k: 'Species', v: ctrl.selectedSpeciesData && ctrl.selectedSpeciesData.name },
            { k: 'Career', v: ctrl.selectedCareerData && ctrl.selectedCareerData.name },
            { k: 'Specialization', v: ctrl.selectedSpecData && ctrl.selectedSpecData.name },
          ]}
          listTitle="The Register"
          /* THE SHELF FACT RIDES THE LIST HEAD (D-SIH-1(a)). The legacy register printed
              "N SPECIES · M FROM MORE THAN ONE SOURCE" as its own status line; the board draws no
              such line and building the frame as drawn dropped the fact entirely — a player could
              no longer tell that any of their species came from more than one active book. It
              returns in the head's `hp-cite`, which is where the board already prints shelf facts
              about a list ("21 · from your enabled books" on the career rail). Mechanics voice:
              says what it is. The multi clause is omitted when nothing is multi-sourced, because
              a zero is not a fact worth a clause. */
          countAttr="data-species-count"
          listCite={fcSpeciesRows.length + ' entries'
            + (fcSpeciesMultiCount > 0
                ? ' \u00b7 ' + fcSpeciesMultiCount + ' from more than one source' : '')}
          indexWrap stepsWrap
          searchValue={fcSpeciesQuery} searchPlaceholder="Search the register…"
          onSearch={setFcSpeciesQuery}
          rows={fcSpeciesRows} selected={ctrl.selectedSpeciesData}
          rawRead
          platePane={ctrl.selectedSpeciesData
            ? (<React.Fragment>
                {fcSpeciesArt(ctrl.selectedSpeciesData,
                              speciesImgPath(ctrl.selectedSpeciesData), '430px')}
                {/* The board draws the sidebar in the PLATE pane, beneath the figure — and only
                    there. `fcSpeciesArt` has two homes (this pane and the entry's foldplate), so
                    the note is composed HERE rather than inside the figure, which would put it in
                    both. */}
                {fcSpeciesSidebar(ctrl.selectedSpeciesData)}
              </React.Fragment>)
            : null}
          readLeft={ctrl.selectedSpeciesData
            ? fcSpeciesEntry(ctrl.selectedSpeciesData, fcSpeciesUnits,
                             speciesImgPath(ctrl.selectedSpeciesData))
            : null}
          ledger={ctrl.selectedSpeciesData
            ? fcSpeciesLedger(fcSpeciesEffMarked(ctrl.selectedSpeciesData, fcSpeciesPicks, fcSpeciesMoved),
                              (gameData && gameData.charbuild.skillNames) || {},
                              gameData && gameData.charbuild,
                              /* R-SCP-1's compact act, into the ledger's statrail. Same builder
                                 and the SAME handler as the hidden pane below — one act, two of
                                 the board's homes, written once. */
                              <React.Fragment>
                                {fcSpeciesOptionGroups(
                                  ctrl.selectedSpeciesData, fcSpeciesPicks,
                                  (gameData && gameData.charbuild.skillNames) || {},
                                  (gameData && gameData.charbuild.skillDescs) || {},
                                  (gameData && gameData.charbuild.skillSheetKeys) || {},
                                  fcOnSpeciesPick)}
                                {/* THE SPECIES' CAREER PICK (D-CI4): the same renderer over its own groups and
                                    picks, never appended to the record's option_choices. */}
                                {fcSpeciesOptionGroups(
                                  { option_choices: fcCareerGroups }, fcCareerPicks,
                                  (gameData && gameData.charbuild.skillNames) || {},
                                  (gameData && gameData.charbuild.skillDescs) || {},
                                  (gameData && gameData.charbuild.skillSheetKeys) || {},
                                  fcOnCareerPick)}
                              </React.Fragment>,
                              /* R-SCP-A8-1: the CHOSEN option's consequences, in the ledger's
                                 conseq block — Jason's one home for them. */
                              fcSpeciesChosenConsequences(
                                ctrl.selectedSpeciesData, fcSpeciesPicks,
                                (gameData && gameData.charbuild.skillNames) || {},
                                (gameData && gameData.charbuild.skillDescs) || {},
                                (gameData && gameData.charbuild.skillSheetKeys) || {}),
                              fcSpeciesResolved(ctrl.selectedSpeciesData, fcSpeciesPicks),
                              fcSpeciesAbilityNote(ctrl.selectedSpeciesData, fcSpeciesPicks),
                              fcSpeciesGrantedAbilityCount(ctrl.selectedSpeciesData, fcSpeciesPicks))
            : null}
          head={fcSpeciesHead(
            fcSpeciesEffMarked(ctrl.selectedSpeciesData, fcSpeciesPicks, fcSpeciesMoved),  // B-399 + the mark
            Math.max(1, fcSpeciesRows.map(function(r) { return r.id; })
              .indexOf((ctrl.selectedSpeciesData || {}).id) + 1),
            fcSpeciesRows.length,
            null,
            null)}
          lead="" cites={[]}
          onBack={handleBack}
          onPick={function(id) {
            var rec = (cbSpeciesList || []).filter(function(x) { return x.id === id; })[0];
            if (rec) handleSpeciesSelect(rec);
          }}
          /* THE PICK-ONE GATE HOLDS THE DOOR, as it does on the shipped screen: a species with an
             unanswered choice cannot be committed, and the picks travel with the commit. */
          /* B-1387 — the hold reads the SAME owed list as the note below and the phone (`fcSpeciesGroupOwed`, B-409
             R-B409D-A1-2: an optional group is never owed), so the Droid's "Modifications" (0–3) no longer holds the
             desk's commit with no reason printed. The career groups were already on it (Q-B1 (b)). */
          commitDisabled={!!(ctrl.selectedSpeciesData
            && (fcSpeciesOwedGroups(ctrl.selectedSpeciesData.option_choices, fcSpeciesPicks).length > 0
            || fcSpeciesOwedGroups(fcCareerGroups, fcCareerPicks).length > 0))}
          /* THE CURE, NAMED — and named with words already on the screen. The sentence is the
             step's own existing "Choose one to continue" idiom; the only variable is the GROUP'S
             OWN display label, which the act prints two objects away in its maker line. Nothing
             new is invented here, which is the point: this is a refusal note, not a copy
             decision. Lists every unanswered group so a species with two owed picks does not
             clear one and refuse in silence again. */
          commitNote={(function() {
            var sp = ctrl.selectedSpeciesData;
            if (!sp) return null;
            var owed = fcSpeciesOwedGroups(sp.option_choices, fcSpeciesPicks)
              .concat(fcSpeciesOwedGroups(fcCareerGroups, fcCareerPicks));
            return fcSpeciesOwedLabel(owed);      // B-409: one builder, the board's words
          })()}
          onConfirm={function() {
            if (ctrl.selectedSpeciesData) {
              handleSpeciesSelectPress(ctrl.selectedSpeciesData, fcSpeciesPicks, fcCareerPicks);
            }
          }}
          onJump={function(st) { ctrl.goToStep(FC_STEPS.indexOf(st)); }} />
      )}

      {!fcTransplanted && (
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      {/* A master-detail step keeps the READING CAP. I first gave it the tree steps' content-max — Docs/CREATOR_COMMENTARY.md § "FullCreator › CenterColumn" */}
      <CenterColumn mode={mode} max={stepColumnMax}>
      <div style={{ flex: 1, overflowY: FC_MASTER_DETAIL_STEPS[ctrl.currentStep] ? 'hidden' : 'auto', overflowX: 'hidden' }} key={scrollKey}>
        {/* B-30: honest reSpec-pack-failure notice on the talents step (where the overlay trees show). */}
        {ctrl.currentStep === 'talents' && _fcModsNotice && (
          <div style={{ padding: '0 20px' }}>
            <ModsLoadNotice onRetry={_fcModsRetry} />
          </div>
        )}
        {ctrl.currentStep === 'gameline' && <QBGameLineStep onSelect={handleGameLine} />}
        {/* SPECIES — master-detail on desktop, swap on the phone. The SAME two components in both
            shapes; only the container differs, so the list keeps its sort state and its scroll
            across a pick (the scroll-key exception above) and the detail simply updates beside it.
            Sorting therefore drives BOTH panes for free: the rail IS the browser. */}
        {ctrl.currentStep === 'species' && (
          <SplitPane
            // `mode="desktop"` UNCONDITIONALLY, and that is the A4 fix rather than a cheat. — Docs/CREATOR_COMMENTARY.md § "FullCreator › species SplitPane"
            mode="desktop"
            railWidth={stepIsMasterDetail ? 'var(--desk-rail-w)' : (ctrl.subView === 'speciesDetail' ? '0px' : '100%')}
            // D2b — `detailMax` DELETED here, which the ruling names as one of its two acceptable — Docs/CREATOR_COMMENTARY.md § "FullCreator › career SplitPane"
            detailMax={null}
            rail={<FCSpeciesBrowser speciesList={cbSpeciesList} selected={ctrl.selectedSpeciesData} onSelect={handleSpeciesSelect} accent={accent} picks={(ctrl.character && ctrl.character.speciesOptionPicks) || {}} groups={cbSpeciesGroups} />}
            detail={(stepIsMasterDetail ? ctrl.selectedSpeciesData : ctrl.subView === 'speciesDetail' && ctrl.selectedSpeciesData) ? (
              <FCSpeciesDetail species={ctrl.selectedSpeciesData} accent={accent} skillNames={gameData ? gameData.charbuild.skillNames : {}} talentDb={gameData ? gameData.charbuild.talents : {}} onConfirm={handleSpeciesSelectPress} onBack={function() { ctrl.setSubView(null); }}
                /* B-124: the pick reads from and writes to the DRAFT, so it outlives this
                   component's mount — which the phone path does not preserve across the 1024 seam. */
                picks={(ctrl.character && ctrl.character.speciesOptionPicks) || {}}
                onPickChange={handleSpeciesPick} />
            ) : (
              /* The just-looking moment: on desktop the empty pane says what it is FOR rather than
                 sitting blank. On the phone the pane has no width, so this never shows there. */
              stepIsMasterDetail ? (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'rgba(255,255,255,0.42)', maxWidth: '34ch' }}>
                    Pick a species to read what it is like to be one — and what it starts you with.
                  </div>
                </div>
              ) : null
            )}
          />
        )}
        {/* CAREER — master-detail on desktop, swap on the phone, exactly as species. The drawing's — Docs/CREATOR_COMMENTARY.md § "FullCreator › career step block" */}
        {ctrl.currentStep === 'career' && (
          <SplitPane
            mode="desktop"
            railWidth={stepIsMasterDetail ? 'var(--desk-rail-w)' : (ctrl.subView === 'careerDetail' ? '0px' : '100%')}
            detailMax={stepIsMasterDetail ? '620px' : null}
            rail={<FCCareerBrowser careers={cbCareerList} selected={ctrl.selectedCareerData} gameLine={ctrl.gameLine} skillNames={gameData ? gameData.charbuild.skillNames : {}} onSelect={handleCareerSelect} accent={accent} />}
            detail={(stepIsMasterDetail ? ctrl.selectedCareerData : ctrl.subView === 'careerDetail' && ctrl.selectedCareerData) ? (
              <FCCareerDetail section="career" career={ctrl.selectedCareerData} accent={accent} skillNames={gameData ? gameData.charbuild.skillNames : {}} specsById={gameData ? gameData.charbuild.specializations : {}} gameLine={ctrl.gameLine} currentSpecies={ctrl.selectedSpeciesData} onConfirmCareer={handleCareerConfirm} onBack={function() { ctrl.setSubView(null); }} forceRating={creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild)} />
            ) : (
              stepIsMasterDetail ? (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'rgba(255,255,255,0.42)', maxWidth: '34ch' }}>
                    Pick a career to see the eight skills it trains — and the specializations it opens.
                  </div>
                </div>
              ) : null
            )}
          />
        )}
        {/* B-68: the SPECIALIZATION STEP — its own step, its own heading, its own back. It had
            neither before: it was a sub-view of career, which is why the pass missed it entirely
            until Jason asked for it by name (R-CSM-2's origin). */}
        {ctrl.currentStep === 'specialization' && !ctrl.subView && gameData && (
          <FCCareerDetail section="specs" career={ctrl.selectedCareerData} accent={accent} skillNames={gameData.charbuild.skillNames} specsById={gameData.charbuild.specializations} gameLine={ctrl.gameLine} currentSpecies={ctrl.selectedSpeciesData} onSelectSpec={handleSpecPreview} onBack={function() { ctrl.prevStep(); }} forceRating={creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild)} />
        )}
        {ctrl.currentStep === 'specialization' && ctrl.subView === 'specDetail' && (
          <FCSpecDetail spec={ctrl.selectedSpecData} career={ctrl.selectedCareerData} currentSpecies={ctrl.selectedSpeciesData} speciesSkillChoices={ctrl.character.speciesSkillChoices} speciesList={cbSpeciesList} accent={accent} skillNames={gameData ? gameData.charbuild.skillNames : {}} charbuild={gameData ? gameData.charbuild : null} talentDb={gameData ? gameData.charbuild.talents : {}} onConfirm={handleSpecConfirm} forceRating={creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild)}
            shortPathTaken={shortPathTakenIn(ctrl.character.talents)} onShortPathTake={handleShortPathTake} onShortPathUndo={handleShortPathUndo} xpPool={ctrl.xpPool} xpRemaining={ctrl.xpRemaining} />
        )}
        {/* B-55 / R-DT29-2 — the cap is armed at ALL FOUR FCSkillPicker mounts: these three plus the
            guided build's species pick (QuickBuild). `fcCommittedRanks` is correct at each step here
            because each picker's CONFIRM dispatches before the next renders — by the spec picker the
            career pick is committed, by the species picker both are. A new mount passes currentRanks +
            rankCap + skillCaps, or says in a comment why not. */}
        {ctrl.currentStep === 'skills' && !ctrl.subView && (
          <FCSkillPicker skills={ctrl.selectedCareerData ? ctrl.selectedCareerData.career_skills : []} skillNames={gameData ? gameData.charbuild.skillNames : {}} skillSheetKeys={gameData ? gameData.charbuild.skillSheetKeys : {}} selected={freeCareerSkills} max={fcCareerFreeRanks} accent={accent} preRankedSkills={fcSpeciesGrantedRanks} currentRanks={fcCommittedRanks} rankCap={2} skillCaps={ctrl.character.speciesSkillCaps || {}} onToggle={handleCareerSkillToggle} onConfirm={handleCareerSkillsConfirm} />
        )}
        {ctrl.currentStep === 'skills' && ctrl.subView === 'specSkills' && (
          <FCSkillPicker skills={ctrl.selectedSpecData ? ctrl.selectedSpecData.career_skills : []} skillNames={gameData ? gameData.charbuild.skillNames : {}} skillSheetKeys={gameData ? gameData.charbuild.skillSheetKeys : {}} selected={freeSpecSkills} max={fcSpecFreeRanks} accent={accent} otherSelected={freeCareerSkills} preRankedSkills={fcSpeciesGrantedRanks} currentRanks={fcCommittedRanks} rankCap={2} skillCaps={ctrl.character.speciesSkillCaps || {}} onToggle={handleSpecSkillToggle} onConfirm={handleSpecSkillsConfirm} />
        )}
        {ctrl.currentStep === 'skills' && ctrl.subView === 'speciesSkills' && (
          <FCSkillPicker skills={fcSpeciesChoicePool} skillNames={gameData ? gameData.charbuild.skillNames : {}} skillSheetKeys={gameData ? gameData.charbuild.skillSheetKeys : {}} selected={fcSpeciesChoiceSelected} max={fcSpeciesChoiceGrant ? fcSpeciesChoiceGrant.count : 1} accent={accent} otherSelected={freeCareerSkills.concat(freeSpecSkills)} preRankedSkills={fcSpeciesGrantedRanks} currentRanks={fcCommittedRanks} rankCap={fcSpeciesChoiceGrant ? fcSpeciesChoiceGrant.rank_cap : 2} skillCaps={ctrl.character.speciesSkillCaps || {}} onToggle={handleSpeciesSkillToggle} onConfirm={handleSpeciesSkillsConfirm} />
        )}
        {ctrl.currentStep === 'skills' && ctrl.subView === 'trainingSummary' && (
          <FCTrainingSummary careerSkills={freeCareerSkills} specSkills={freeSpecSkills} speciesSkills={fcSpeciesChoiceSelected} skillNames={gameData ? gameData.charbuild.skillNames : {}} accent={accent} onConfirm={handleTrainingConfirm} />
        )}
        {ctrl.currentStep === 'skills' && ctrl.subView === 'buySkills' && gameData && (
          <React.Fragment>
            <FCXpBudgetBar xpPool={ctrl.xpPool} xpSpentOnChars={ctrl.xpSpentOnChars} xpSpentOnTalents={ctrl.xpSpentOnTalents} xpSpentOnSkills={ctrl.xpSpentOnSkills} xpRemaining={ctrl.xpRemaining} accent={accent} />
            <FCSkillBuyList allSkillIds={Object.keys(gameData.charbuild.skillNames)} skillNames={gameData.charbuild.skillNames} skillSheetKeys={gameData.charbuild.skillSheetKeys} charSkills={ctrl.character.skills || {}} characteristics={ctrl.character.characteristics} careerIds={fcCareerStatusIds} skillCaps={ctrl.character.speciesSkillCaps || {}} xpRemaining={ctrl.xpRemaining} accent={accent} onBuy={handleBuySkillRank} onRefund={handleRefundSkillRank} onDone={handleBuySkillsDone} />
          </React.Fragment>
        )}
        {ctrl.currentStep === 'mechanic' && gameData && (
          <FCMechanicStep gameLine={ctrl.gameLine} gameData={gameData} partySize={ctrl.partySize} setPartySize={ctrl.setPartySize} accent={accent} onComplete={handleMechanicComplete} backRef={mechanicBackRef} hasForce={ctrl.hasForceAccess} />
        )}
        {ctrl.currentStep === 'characteristics' && (
          // THE COMPOSITION (C10 increment 3). ONE TREE AT EVERY BAND — A4's letter, and C7's — Docs/CREATOR_COMMENTARY.md § "FullCreator › characteristics row"
          <div style={{ display: 'flex', gap: 'var(--desk-gutter)', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div data-char-controls="1" style={{ flex: '1 1 auto', minWidth: 0, maxWidth: FC_CHAR_MEASURE }}>
              <FCXpBudgetBar xpPool={ctrl.xpPool} xpSpentOnChars={ctrl.xpSpentOnChars} xpSpentOnTalents={ctrl.xpSpentOnTalents} xpSpentOnSkills={ctrl.xpSpentOnSkills} xpSpentOnBuyouts={ctrl.xpSpentOnBuyouts} xpRemaining={ctrl.xpRemaining} accent={accent} />
              {/* D-SFM2 (endorsed): the buyout block LEADS the XP step — first, above the — Docs/CREATOR_COMMENTARY.md § "FullCreator › FCBuyoutBlock" */}
              <FCBuyoutBlock offers={fcBuyoutOffers} taken={ctrl.character && ctrl.character.creationBuyouts} xpRemaining={ctrl.xpRemaining} accent={accent} onTake={handleBuyoutTake} onUndo={handleBuyoutUndo} />
              <FCCharSpender character={ctrl.character} speciesData={ctrl.selectedSpeciesData} accent={accent} xpRemaining={ctrl.xpRemaining} onCharChange={handleCharChange} />
              <FCBottomAction label={ctrl.xpRemaining < 0 ? 'OVERSPENT BY ' + Math.abs(ctrl.xpRemaining) + ' XP' : (ctrl.xpSpentOnChars > 0 ? 'CONTINUE' : 'CONTINUE WITHOUT SPENDING XP')} onClick={ctrl.nextStep} disabled={ctrl.xpRemaining < 0} accent={accent} />
            </div>
            <FCCharTeachingAside hidden={band === 'mobile'} />
          </div>
        )}
        {ctrl.currentStep === 'talents' && (
          <React.Fragment>
            <FCXpBudgetBar xpPool={ctrl.xpPool} xpSpentOnChars={ctrl.xpSpentOnChars} xpSpentOnTalents={ctrl.xpSpentOnTalents} xpSpentOnSkills={ctrl.xpSpentOnSkills} xpRemaining={ctrl.xpRemaining} accent={accent} />
            {/* B-85: EVERY tree is 5 rows x 4 at 5/10/15/20/25 = 300 XP, against starting pools of — Docs/CREATOR_COMMENTARY.md § "FullCreator › talents tree caption" */}
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, padding: '0 16px', marginBottom: '12px', maxWidth: 'var(--desk-prose-max)' }}>
              A full tree costs <b style={{ color: 'rgba(255,255,255,0.8)' }}>300 XP</b> and no character can finish one at creation — the largest starting pool in the game is 175. Buy one or two now; the rest is what your character grows into.
            </div>
            {(function() {
              var _treeModel = talentTreeToModel({ specData: fullSpecData, talentDb: gameData ? gameData.charbuild.talents : {}, character: ctrl.character, availableXp: ctrl.xpRemaining, onBuy: handleTalentBuy, onRefund: handleTalentRefund,
                // B-181: the receipt + needs-its-skills detection read the character's choices; the choose
                // handler covers a re-entered draft whose owned choice-talent still lacks its skills.
                talentSkillChoices: ctrl.character.talentSkillChoices, talentChoices: ctrl.character.talentChoices, skillNames: gameData ? gameData.charbuild.skillNames : {},
                onChooseSkills: function(talentKey, specKey, row, col) {
                  var trec = gameData && gameData.charbuild.talents ? gameData.charbuild.talents[talentKey] : null;
                  var grants = (window.CharResolve && CharResolve.talentChoiceGrantsOf(trec)) || [];
                  var pos = row + '-' + col;
                  var existing = CharResolve.pickOf(ctrl.character, specKey, pos, 'career_skills') || [];   // R-M8G-4: the one reader
                  setCreatorTalentPicker({ mode: 'choose', talentKey: talentKey, talentName: (trec && trec.name) || talentKey, specKey: specKey, row: row, col: col, grants: grants, initialPicks: existing });
                } });
              // MOBILE IS BYTE-IDENTICAL: no onInspect, no wrapper — exactly what shipped.
              if (band === 'mobile') return <DirectionalTree model={_treeModel} accent={accent} />;
              return (
                <div data-tree-split="talents" style={{ display: 'flex', gap: 'var(--desk-gutter)', alignItems: 'flex-start', padding: '0 16px' }}>
                  <div style={{ flex: '0 0 auto', minWidth: 0 }}>
                    <DirectionalTree model={_treeModel} accent={accent} onInspect={function(entry) { setTreeSel(entry); }} />
                  </div>
                  <FCTreeRail entry={treeSel} accent={accent} onClose={function() { setTreeSel(null); }} />
                </div>
              );
            })()}
            {/* B-181: the talent-choice skill picker — raised by a choice-talent's buy (Well Rounded /
                Well Read) or a re-entered draft's needs-its-skills prompt. */}
            {creatorTalentPicker && gameData && (
              <TalentSkillChoicePicker
                talentName={creatorTalentPicker.talentName} grants={creatorTalentPicker.grants}
                charbuild={gameData.charbuild} careerStatusIds={fcCareerStatusIds}
                cost={creatorTalentPicker.cost} mode={creatorTalentPicker.mode}
                initialPicks={creatorTalentPicker.initialPicks} accent={accent}
                onConfirm={handleTalentChoiceConfirm}
                onCancel={function() { setCreatorTalentPicker(null); }} />
            )}
            {/* B-1055 MOUNT: the legacy step's handleTalentBuy raises the same Dedication picker. */}
            {creatorCharPick && gameData && h(M7CharPickSheet, { pick: creatorCharPick, charbuild: gameData.charbuild,
              characteristics: CharResolve.creatorCharacteristicsWithPending(ctrl.character), sheet: ctrl.character,
              onClose: closeCreatorCharPick, onChoose: handleCreatorCharChoose })}
            <FCBottomAction label={ctrl.xpRemaining < 0 ? 'OVERSPENT BY ' + Math.abs(ctrl.xpRemaining) + ' XP' : ((ctrl.character.talents && Object.keys(ctrl.character.talents).some(function(k) { return ctrl.character.talents[k].cost > 0; })) ? 'CONTINUE' : 'CONTINUE WITHOUT TALENTS')} onClick={ctrl.nextStep} disabled={ctrl.xpRemaining < 0} accent={accent} />
          </React.Fragment>
        )}
        {ctrl.currentStep === 'forcepowers' && gameData && (
          <React.Fragment>
            <FCXpBudgetBar xpPool={ctrl.xpPool} xpSpentOnChars={ctrl.xpSpentOnChars} xpSpentOnTalents={ctrl.xpSpentOnTalents} xpSpentOnSkills={ctrl.xpSpentOnSkills} xpRemaining={ctrl.xpRemaining} accent={accent} />
            <FCForcePowersStep character={ctrl.character} gameData={gameData} gameLine={ctrl.gameLine} xpRemaining={ctrl.xpRemaining} accent={accent} onBuy={handleForcePowerBuy} onRefund={handleForcePowerRefund} onContinue={ctrl.nextStep} forceRating={creatorForceRating(ctrl.hasForceAccess, ctrl.character.talents, gameData && gameData.charbuild)} />
          </React.Fragment>
        )}
        {ctrl.currentStep === 'motivation' && (
          <FCMotivationStep accent={accent} onConfirm={handleMotivationConfirm} gameData={gameData}
            /* C15: a re-entered draft SHOWS its motivation rather than starting blank (F-45). */
            existing={ctrl.character && ctrl.character.motivation}
            /* C15 commit 3: the FaD pair is SHOWN here, decided on the mechanic step. */
            mechanic={ctrl.character && ctrl.character.mechanic}
            onEditMechanic={function() { ctrl.goToStep(FC_STEPS.indexOf('mechanic')); }} />
        )}
        {ctrl.currentStep === 'gear' && gameData && (
          <FCGearStep character={ctrl.character} credits={ctrl.credits} accent={accent}
            onShop={function() { ctrl.setSubView('store'); }}
            onSell={handleGearSell} onContinue={ctrl.nextStep} />
        )}
        {ctrl.currentStep === 'review' && gameData && (
          <FCReviewStep character={ctrl.character} speciesData={ctrl.selectedSpeciesData} specData={fullSpecData} gameData={gameData} accent={accent} xpRemaining={ctrl.xpRemaining} credits={ctrl.credits} onNameChange={function(v) { ctrl.updateCharacter('name', v); }} onGoToStep={ctrl.goToStep} onConfirm={function() { onComplete({ character: ctrl.character, speciesData: ctrl.selectedSpeciesData, specData: fullSpecData, gameData: gameData, reentry: (resumeDraft && resumeDraft.reentry) || null }, ctrl.gameLine, ctrl.selectedSpeciesData); }} onSetPocketMoney={function(v) { ctrl.updateCharacter('pocketMoney', v); }} />
        )}
      </div>
      </CenterColumn>
      </div>
      <div data-creator-rail={showBuildRail ? '1' : '0'} style={{
        // D2 (ruled): the rail takes its TOKEN, not a hardcoded number. The two had drifted —
        // `--desk-rail-w` is 320 and this said 250 — and the drift was invisible until the heading
        // was taught to reserve the rail's gutter: it reserved 320, the rail occupied 250, and the
        // title landed 35px off. A hardcoded width that disagrees with the token it is named after
        // is a second source of truth, and this is what it cost.
        width: showBuildRail ? 'var(--desk-rail-w)' : 0, flexShrink: 0,
        display: showBuildRail ? 'block' : 'none',
        overflowY: 'auto',
        borderLeft: showBuildRail ? '1px solid rgba(255,255,255,0.08)' : 'none',
      }}>
        {showBuildRail && <FCBuildRail ctrl={ctrl} accent={accent} />}
      </div>
      </div>
      )}

      {/* The source question, mounted at the creator shell's own overlay layer — the same axis the
          store uses (a sub-view above the wizard), so no new navigation concept and the creator's
          existing back-handling still applies. It renders only while a question is in flight. */}
      {ctrl.currentStep === 'species' && srcCandidates && (
        <FCSourcePicker candidates={srcCandidates} accent={accent} ground={fcTransplanted ? band : null}
          onUse={handleSourceUse} onCancel={handleSourceCancel} />
      )}

      {/* B-94 / ruling `creator-c4-store-chrome` Q1 = (c): the store opens as a SUB-VIEW — the
          creator's own existing axis (species detail, the skill pickers, the mechanic's layers all
          use it), so no new navigation concept and the back-handling the creator already owns.
          Full-screen, above the wizard: the module gets the surface it was designed for at every
          width, and neither component is bent to fit the other.

          B-671 — THE OVERLAY'S GROUND FOLLOWS THE FACE. `#02010a` is the dark store's box and it shows
          wherever the kernel's own surface does not reach — under the bar cluster, and for the one
          frame between mount and the catalog's load. On paper that margin must be the paper's, so the
          box takes the creator ground's own `--doc-paper` (inherited: this overlay is a descendant of
          the shell's `data-doc-ground="creator"` root, so the token resolves without a second ground
          declaration). */}
      {ctrl.currentStep === 'gear' && ctrl.subView === 'store' && window.SWRPGStore && (
        <div className="creator-store-overlay" style={{ position: 'absolute', inset: 0, zIndex: 30, display: 'flex', flexDirection: 'column', background: creatorStorePaper ? 'var(--doc-paper)' : '#02010a', animation: 'fadeIn 0.25s ease' }}>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {React.createElement(window.SWRPGStore.Store, {
              accent: accent,
              // B-665 (R-SPC-2: "the creator's gear step — under the creator's own switch"). BOTH
              // switches, and the AND is the point: a paper catalog opening on top of the DARK
              // creator would be the one thing a dark period must never show, so the store's own
              // switch decides the face and the creator's switch decides whether this door is
              // standing in paper at all. B-671 is the row that walks the door; B-674 flips all four.
              paper: creatorStorePaper,
              tab: storeTab,
              shopFor: storeShopFor,
              purse: creatorPurse,       // B-671 — frame K's strip; see `creatorPurse` above
              roster: [storeShopFor],              // B-203: finalizeFor looks up the target by id in the roster
              qualitiesDb: gameData && gameData.charbuild ? gameData.charbuild.qualities : null,
              onFinalize: handleStoreFinalizeInCreator,
              onCounts: function(c) { setStoreCounts(c); },
              onBrowse: function() { setStoreTab('weapons'); },
              vesselName: creatorVesselName,   // B-1644 (ADDENDUM 373): no seam here; the Yard names a vessel line
            })}
          </div>
          {/* THE PURSE, on the store's own bar (ruled). BottomNav is navigation-ONLY by contract
              (Apple HIG, lib/shared/ui.jsx:425), so the purse is a strip immediately above it —
              part of the bar cluster a shopper's eyes are already on, without breaking that
              contract. Red-and-honest when negative: the overdrawn re-entrant browses freely
              (F-45's standing edge population), never blocked.

              B-671 — ON PAPER THIS STRIP IS THE KERNEL'S, and one strip renders, never two. Frame K
              draws the purse INSIDE the store's own drawing (`creator_board()`'s last child), which is
              also where frames O/P put the sheet's door's, so the paper face has one implementation for
              both doors rather than a host copy beside a kernel one. Its words, its rule above the bar
              and its `data-creator-purse` hook all survive the move (the `purse` prop above carries the
              attribute name down). This element is the DARK door's, unchanged, and it goes when the
              legacy face does. */}
          {!creatorStorePaper && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '8px 16px', background: 'rgba(7,10,16,0.94)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.45)' }}>PURSE</span>
            <span data-creator-purse={ctrl.credits.remaining} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', fontWeight: 700, letterSpacing: '0.06em', color: ctrl.credits.remaining < 0 ? 'var(--text-danger)' : accent }}>
              {ctrl.credits.remaining} / {ctrl.credits.total} CREDITS
            </span>
            <button onClick={function() { ctrl.setSubView(null); }} style={{ minHeight: '44px', padding: '0 14px', borderRadius: '6px', cursor: 'pointer', background: 'none', border: '1px solid rgba(255,255,255,0.18)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.75)' }}>DONE</button>
          </div>
          )}
          <BottomNav app="player" label="Store" accent={accent} activeId={storeTab}
            items={[
              { id: 'weapons', label: 'Weapons', icon: storeGlyph('pistol'), onSelect: function() { setStoreTab('weapons'); } },
              { id: 'armor', label: 'Armor', icon: storeGlyph('armor'), onSelect: function() { setStoreTab('armor'); } },
              { id: 'gear', label: 'Gear', icon: storeGlyph('storage'), onSelect: function() { setStoreTab('gear'); } },
              { id: 'holds', label: 'Wish list', icon: navBadge(holdsGlyph(), storeCounts.holds), onSelect: function() { setStoreTab('holds'); } },
              { id: 'counter', label: 'Counter', icon: navBadge(navIcon('shopping-cart'), storeCounts.cart), onSelect: function() { setStoreTab('counter'); } },
            ]} />
        </div>
      )}
    </div>
  );
}

// B-140: pdf-lib loaded on demand at export time, not at boot.
var _pdfLibPromise = null;
function ensurePDFLib() {
  if (window.PDFLib) return Promise.resolve(window.PDFLib);
  if (_pdfLibPromise) return _pdfLibPromise;
  _pdfLibPromise = new Promise(function(resolve, reject) {
    var s = document.createElement('script');
    s.src = '/player/pdf-lib.min.js';
    s.onload = function() {
      if (!window.PDFLib) { _pdfLibPromise = null; reject(new Error('PDF library script loaded from ' + s.src + ' but window.PDFLib is absent (server may have returned HTML instead of JS)')); return; }
      resolve(window.PDFLib);
    };
    s.onerror = function() { _pdfLibPromise = null; reject(new Error('Failed to load PDF library')); };
    document.head.appendChild(s);
  });
  return _pdfLibPromise;
}

// B-981: fontkit lets pdf-lib embed a font file (pdf-lib alone embeds only its standard fonts). Loaded with pdf-lib, at
// export time only, never at boot.
var _fontkitPromise = null;
function ensureFontkit() {
  if (window.fontkit) return Promise.resolve(window.fontkit);
  if (_fontkitPromise) return _fontkitPromise;
  _fontkitPromise = new Promise(function(resolve, reject) {
    var s = document.createElement('script');
    s.src = '/player/fontkit.umd.min.js';
    s.onload = function() {
      if (!window.fontkit) { _fontkitPromise = null; reject(new Error('Font library script loaded from ' + s.src + ' but window.fontkit is absent')); return; }
      resolve(window.fontkit);
    };
    s.onerror = function() { _fontkitPromise = null; reject(new Error('Failed to load the font library')); };
    document.head.appendChild(s);
  });
  return _fontkitPromise;
}

// THE PRINTED SHEET'S SYMBOL KIT (B-981, D-404 item 5) — what `pdfGlyphRuns`' runs need from one document: the dice font
// embedded (subset) from public/fonts/sw-rpg-icons.woff, the paper ground's seventeen symbol inks, and width / wrap / fit /
// draw over runs. Both exporters build one per document; the FFG form draws a field whose text carries a die onto the page
// at the field's own box, because a form field holds one font.
//   · THE INKS are read live from the paper ground (`[data-doc-ground="creator"]`, lib/shared/grounds/creator.css: the
//     seventeen `--doc-sym-*`, each >= 4.5:1 on paper), never copied here. The dice table's own fallbacks are tuned for
//     the dark ground (setback #AAAAAA, light #EEEEEE) and would vanish on white.
//   · LOUD, NOT SILENT: a token whose mark the font lacks, or a symbol with no paper ink, throws, and the export fails
//     with the sheet's PDF line (B-1594). A gap in the printed rule is the defect this replaces.
// `fitLine`/`wrapLines`/`wrapText` stay for text with no die in it; these are the same rules over runs.
async function pdfSymbolKit(PDFLib, pdfDoc, body) {
  var PDF_SYM_SCALE = 1.15, PDF_SYM_DROP = 0.12;   // the screen's glyph is larger than its text and sits a little low
  // B-1594 D-R7PF1 (ADDENDUM 301): the font library and the dice-font file are LOADS ('load': S1's line, with RETRY). Neither
  // is cached on failure (ensureFontkit drops its promise; the font is a plain fetch each export), so RETRY fetches again.
  var fontkit, iconBytes;
  try {
    fontkit = await ensureFontkit();
    var resp = await fetch('/public/fonts/sw-rpg-icons.woff');
    if (!resp.ok) throw new Error('PDF dice font did not load (' + resp.status + ')');
    iconBytes = await resp.arrayBuffer();
  } catch (e) { throw pdfLoadFailure(e); }
  pdfDoc.registerFontkit(fontkit);
  var icons = await pdfDoc.embedFont(iconBytes, { subset: true });
  var bridge = (window.SWRPGDice || {}).BRACKET_TOKENS || {};
  var probe = document.createElement('div');
  probe.setAttribute('data-doc-ground', 'creator');
  probe.style.display = 'none';
  document.body.appendChild(probe);
  var cs = window.getComputedStyle(probe);
  var chars = {}, inks = {};
  try {
    Object.keys(bridge).forEach(function (tok) {
      var key = bridge[tok];
      var ch = pdfSymbolChar(key);
      if (!icons.embedder.font.hasGlyphForCodePoint(ch.codePointAt(0))) throw new Error('PDF dice: sw-rpg-icons has no glyph for "' + key + '"');
      var hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(cs.getPropertyValue('--doc-sym-' + key.replace(/_/g, '-')).trim());
      if (!hex) throw new Error('PDF dice: the paper ground has no ink for "' + key + '"');
      chars[key] = ch;
      inks[key] = PDFLib.rgb(parseInt(hex[1], 16) / 255, parseInt(hex[2], 16) / 255, parseInt(hex[3], 16) / 255);
    });
  } finally { probe.remove(); }

  var runText = function (r) { return pdfEncodable(body, r.text); };
  var runWidth = function (r, size) {
    return r.sym ? icons.widthOfTextAtSize(chars[r.sym], size * PDF_SYM_SCALE) : body.widthOfTextAtSize(runText(r), size);
  };
  var width = function (runs, size) { return runs.reduce(function (w, r) { return w + runWidth(r, size); }, 0); };
  // Words are what sits between spaces; a die touching text ("Hard ([DI][DI])") is part of that word.
  var words = function (runs) {
    var out = [[]];
    runs.forEach(function (r) {
      if (r.sym) { out[out.length - 1].push(r); return; }
      runText(r).replace(/\s+/g, ' ').split(' ').forEach(function (piece, i) {
        if (i) out.push([]);
        if (piece) out[out.length - 1].push({ text: piece });
      });
    });
    return out.filter(function (w) { return w.length; });
  };
  var join = function (ws) {
    var line = [];
    ws.forEach(function (w, i) { if (i) line.push({ text: ' ' }); line.push.apply(line, w); });
    return line;
  };
  var wrap = function (runs, maxWidth, size) {
    var lines = [], cur = [];
    words(runs).forEach(function (w) {
      if (cur.length && width(join(cur.concat([w])), size) > maxWidth) { lines.push(join(cur)); cur = [w]; }
      else cur.push(w);
    });
    if (cur.length) lines.push(join(cur));
    return lines;
  };
  // fitLine's rule over runs: trim to the box word by word, ending in an ellipsis.
  var ELL = { text: '…' };
  var fit = function (runs, maxWidth, size) {
    var ws = words(runs);
    if (width(join(ws), size) <= maxWidth) return join(ws);
    while (ws.length > 1 && width(join(ws).concat([ELL]), size) > maxWidth) ws.pop();
    var line = join(ws);
    while (line.length && width(line.concat([ELL]), size) > maxWidth) {
      var last = line[line.length - 1];
      if (last.sym || last.text.length <= 1) line.pop();
      else line[line.length - 1] = { text: last.text.slice(0, -1) };
    }
    var tail = line[line.length - 1];
    if (tail && !tail.sym) line[line.length - 1] = { text: tail.text.replace(/[\s,;:.]+$/, '') };
    return line.concat([ELL]);
  };
  var draw = function (page, runs, x, y, size, color) {
    runs.forEach(function (r) {
      if (r.sym) page.drawText(chars[r.sym], { x: x, y: y - size * PDF_SYM_DROP, size: size * PDF_SYM_SCALE, font: icons, color: inks[r.sym] });
      else page.drawText(runText(r), { x: x, y: y, size: size, font: body, color: color });
      x += runWidth(r, size);
    });
  };
  var hasSym = function (runs) { return runs.some(function (r) { return r.sym; }); };
  return { width: width, wrap: wrap, fit: fit, draw: draw, hasSym: hasSym };
}

/* ══════════════════════════════════════════════════════
   PDF EXPORT — Custom character sheet (COMMERCIAL_MODE)
   Generates a two-page PDF from scratch using pdf-lib.
   Page 1: Identity, characteristics, derived, skills, weapons
   Page 2: Talents, gear, mechanic, motivations, backstory
   Uses Helvetica/Helvetica-Bold (universal, no licensing).
══════════════════════════════════════════════════════ */

async function generateCustomPdf(sheet) {
  var PDFLib;
  try { PDFLib = await ensurePDFLib(); } catch(e) { throw pdfLoadFailure(e); }
  // R-M7G-9: the skill column and the CAREER box read the vocabulary — wait for it, so a blank means the registries
  // FAILED (the loader resolves null), never that they had not loaded yet.
  await loadCanonicalCharacterBuild();
  var rgb = PDFLib.rgb;

  var pdfDoc = await PDFLib.PDFDocument.create();
  var helvB = await pdfDoc.embedFont(PDFLib.StandardFonts.HelveticaBold);
  var helv = await pdfDoc.embedFont(PDFLib.StandardFonts.Helvetica);
  var courier = await pdfDoc.embedFont(PDFLib.StandardFonts.Courier);
  var kit = await pdfSymbolKit(PDFLib, pdfDoc, helv);   // B-981: rule text keeps its dice

  // Page dimensions (US Letter)
  var W = 612, H = 792;
  var margin = 36;
  // Printer-friendly palette: white background, dark text, subtle accents
  var accent = rgb(0.4, 0.35, 0.1);    // muted gold (prints well)
  var white = rgb(0, 0, 0);            // "white" = black text on light bg
  var darkBg = rgb(1, 1, 1);           // page background = white
  var midGray = rgb(0.5, 0.5, 0.5);
  var lightGray = rgb(0.25, 0.25, 0.25);
  var faintLine = rgb(0.8, 0.8, 0.8);

  // Helpers
  function drawBox(page, x, y, w, h, opts) {
    page.drawRectangle({
      x: x, y: y, width: w, height: h,
      borderColor: opts.border || faintLine,
      borderWidth: opts.borderWidth || 0.5,
      color: opts.fill || undefined,
    });
  }
  function text(page, str, x, y, opts) {
    page.drawText(pdfEncodable(opts.font || helv, str || ''), {   // B-987
      x: x, y: y,
      font: opts.font || helv,
      size: opts.size || 8,
      color: opts.color || lightGray,
    });
  }
  function label(page, str, x, y, size) {
    text(page, String(str).toUpperCase(), x, y, { font: helvB, size: size || 6, color: midGray });
  }
  function sectionHeader(page, str, x, y, w) {
    page.drawRectangle({ x: x, y: y - 2, width: 2, height: 12, color: accent });
    page.drawRectangle({ x: x, y: y - 2, width: w, height: 12, color: rgb(0.92, 0.92, 0.90) });
    text(page, String(str).toUpperCase(), x + 8, y, { font: helvB, size: 7, color: accent });
  }

  var chars = sheet.characteristics || {};
  var skills = sheet.skills || {};
  var eq = resolveSheetEquipment(sheet.equipment, sheet);  // id-refs -> display shapes (ADR 0003 3b)
  var derived = CharResolve.computeDerived(sheet, eq, window._canonicalCharbuild);      // CS-2 (R-DAR) + B-23 species talents
  var weapons = eq.weapons || [];
  var armor = eq.armor;
  var gear = eq.gear || [];
  var talents = sheetTalentsForDisplay(sheet);        // id-refs -> display (charbuild 3b)
  var forcePowers = sheetForcePowersForDisplay(sheet);
  // Every narrative track the sheet carries (a Force character in an Obligation/Duty campaign
  // carries two — BOOKS-1c Part B). Draw a block for each.
  var mechBlocks = [];
  if (sheet.obligation) mechBlocks.push({ label: 'Obligation', track: 'obligation', mech: sheet.obligation });
  if (sheet.duty)       mechBlocks.push({ label: 'Duty',       track: 'duty',       mech: sheet.duty });
  if (sheet.morality)   mechBlocks.push({ label: 'Morality',   track: 'morality',   mech: sheet.morality });
  var mechNames = (sheet.obligation || sheet.duty) ? await SWRPGMechTypeNames.load(qbFetchJson) : null;   // B-1605
  var motivations = sheet.motivations || {};
  var xp = sheet.xp || {};

  // ════════════════════════════════════════════════════
  // PAGE 1
  // ════════════════════════════════════════════════════
  var p1 = pdfDoc.addPage([W, H]);
  p1.drawRectangle({ x: 0, y: 0, width: W, height: H, color: darkBg });

  // ── Title bar ──
  p1.drawRectangle({ x: 0, y: H - 40, width: W, height: 40, color: rgb(0.94, 0.94, 0.92) });
  text(p1, 'CHARACTER RECORD', margin, H - 28, { font: helvB, size: 14, color: accent });
  text(p1, 'NARRATIVE DICE SYSTEM', W - margin - 130, H - 26, { font: courier, size: 7, color: midGray });

  // ── Identity block ──
  var iy = H - 56;
  sectionHeader(p1, 'IDENTITY', margin, iy, W - margin * 2);
  iy -= 18;
  var idFields = [
    ['CHARACTER', sheet.name || '', 160],
    ['SPECIES', sheet.species || '', 100],
    ['CAREER', sheet.career || '', 100],
    ['SPECIALIZATION', sheet.specialization || '', 140],
  ];
  var ix = margin;
  idFields.forEach(function(f) {
    label(p1, f[0], ix, iy + 10);
    text(p1, f[1], ix, iy, { font: helvB, size: 10, color: white });
    drawBox(p1, ix - 2, iy - 4, f[2], 18, {});
    ix += f[2] + 8;
  });

  // ── Characteristics ──
  var cy = iy - 30;
  sectionHeader(p1, 'CHARACTERISTICS', margin, cy, W - margin * 2);
  cy -= 20;
  var charKeys = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
  var charLabels = ['BRAWN', 'AGILITY', 'INTELLECT', 'CUNNING', 'WILLPOWER', 'PRESENCE'];
  var cw = (W - margin * 2 - 30) / 6;
  charKeys.forEach(function(k, ci) {
    var cx = margin + ci * (cw + 5);
    drawBox(p1, cx, cy - 30, cw, 44, { fill: rgb(0.93, 0.93, 0.91), borderWidth: 1 });
    label(p1, charLabels[ci], cx + 4, cy + 8, 5);
    text(p1, String(chars[k] || 0), cx + cw / 2 - 6, cy - 22, { font: helvB, size: 22, color: white });
  });

  // ── Derived stats ──
  var dy = cy - 46;
  sectionHeader(p1, 'DERIVED', margin, dy, W - margin * 2);
  dy -= 18;
  // B-746 — A DISPLAYED THRESHOLD IS `n/threshold` (Jason 2026-09-20). The custom PDF's WOUNDS and
  // STRAIN boxes printed the ceiling alone, so a printed sheet said nothing about where the
  // character stood — and unlike the FFG form there is no second "Current" box on this page to say
  // it elsewhere. This is a SINGLE drawn box, so the pair goes inside it; the FFG export's two
  // NAMED FORM FIELDS stay two fields and are not merged (`:24010`). `n` is read exactly as the
  // sheet reads it (`!= null ? … : 0` — `:26147`, `:32499`): absent means UNDAMAGED, which is the
  // same call B-742 settles for the FFG path, so the two exports now agree on a blank record.
  // SOAK and the two defences are not thresholds — no track counts up to them — so they are
  // untouched and stay bare numbers.
  var pdfCurWounds = sheet.currentWounds != null ? sheet.currentWounds : 0;
  var pdfCurStrain = sheet.currentStrain != null ? sheet.currentStrain : 0;
  var derivedFields = [
    ['SOAK', derived.soak || 0],
    ['WOUNDS', pdfCurWounds + '/' + (derived.woundThreshold || 0)],
    ['STRAIN', pdfCurStrain + '/' + (derived.strainThreshold || 0)],
    ['M.DEF', derived.meleeDefense || 0],
    ['R.DEF', derived.rangedDefense || 0],
  ];
  if (derived.forceRating > 0) derivedFields.push(['FORCE', derived.forceRating]);
  var dw = (W - margin * 2 - (derivedFields.length - 1) * 5) / derivedFields.length;
  derivedFields.forEach(function(f, fi) {
    var dx = margin + fi * (dw + 5);
    drawBox(p1, dx, dy - 24, dw, 36, { fill: rgb(0.93, 0.93, 0.91) });
    label(p1, f[0], dx + 4, dy + 6, 5);
    // CENTRED ON ITS OWN MEASURED WIDTH, because `dw / 2 - 6` was tuned for a one- or two-character
    // number and `12/14` is five: the old constant would have pushed a paired value off centre and,
    // on the widest pair, out of the box. pdf-lib's own metrics, so no glyph table is guessed at.
    var ds = String(f[1]);
    text(p1, ds, dx + (dw - helvB.widthOfTextAtSize(ds, 16)) / 2, dy - 16, { font: helvB, size: 16, color: accent });
  });

  // R-SSC-7 (frame P1 of the approved record; D-230 Q1 (a)): EVERY DERIVED NUMBER SAYS WHY ON PAPER TOO — the rows of
  // CharResolve.derivedBecause (the trace of the walk that printed the boxes above; never saved) in becauseNoteText's words
  // with "to" for the arrow the page's WinAnsi font cannot draw (D-SSF2), one footnote line per number under the DERIVED row;
  // SKILLS moves down by them. Each line shows when its reader answers (the engine-dark rows are absent with the key OFF).
  var pdfBecause = (CharResolve.derivedBecause && sheet.characteristics) ? CharResolve.derivedBecause(sheet, eq, window._canonicalCharbuild) : {};
  var pdfNotes = [['SOAK', 'soak'], ['WOUNDS', 'woundThreshold'], ['STRAIN', 'strainThreshold'], ['ENCUMBRANCE', 'encumbranceThreshold']]
    .filter(function (n) { return (pdfBecause[n[1]] || []).length; })
    .map(function (n) { return n[0] + '  ' + pdfBecause[n[1]].map(function (r) { return becauseNoteText(r, 'to'); }).join('  \u00b7  '); });
  var noteY = dy - 34;
  pdfNotes.forEach(function (n) {
    wrapText(n, W - margin * 2 - 4, helv, 6).forEach(function (line) { text(p1, line, margin + 2, noteY, { size: 6, color: lightGray }); noteY -= 8; });
  });
  var noteH = pdfNotes.length ? (dy - 34) - noteY + 4 : 0;

  // ── Skills ──
  var sy = dy - 40 - noteH;
  sectionHeader(p1, 'SKILLS', margin, sy, W - margin * 2);
  sy -= 14;
  var allSkillKeys = Object.keys(SKILL_NAMES).sort(function(a, b) { return SKILL_NAMES[a].localeCompare(SKILL_NAMES[b]); });
  var colH = Math.ceil(allSkillKeys.length / 2);
  var colW = (W - margin * 2 - 10) / 2;

  allSkillKeys.forEach(function(key, si) {
    var col = si < colH ? 0 : 1;
    var row = si < colH ? si : si - colH;
    var sx = margin + col * (colW + 10);
    var sRowY = sy - row * 12;
    var rank = skills[key] || 0;
    var _sc = sheetSkillCharacteristic(sheet, key);   // F-100 M7 C2: the characteristic this skill rolls with
    var charAbbr = (_sc && CharResolve.CHAR_ABBR[_sc.characteristic]) || '';

    // Alternating row background
    if (row % 2 === 0) {
      p1.drawRectangle({ x: sx, y: sRowY - 3, width: colW, height: 12, color: rgb(0.95, 0.95, 0.93) });
    }

    text(p1, SKILL_NAMES[key], sx + 2, sRowY, {
      size: 7, color: rank > 0 ? white : rgb(0.72, 0.72, 0.72),
      font: rank > 0 ? helvB : helv,
    });
    text(p1, charAbbr, sx + colW - 60, sRowY, { size: 6, color: midGray, font: courier });

    // Rank dots
    for (var dot = 0; dot < 5; dot++) {
      var dotX = sx + colW - 35 + dot * 8;
      if (dot < rank) {
        p1.drawCircle({ x: dotX, y: sRowY + 3, size: 3, color: accent });
      } else {
        p1.drawCircle({ x: dotX, y: sRowY + 3, size: 3, borderColor: rgb(0.75, 0.75, 0.75), borderWidth: 0.5 });
      }
    }
  });

  // ── Weapons ──
  var wy = sy - colH * 12 - 16;
  sectionHeader(p1, 'WEAPONS', margin, wy, W - margin * 2);
  wy -= 14;

  // Column headers
  var wCols = [
    { label: 'WEAPON', x: margin, w: 160 },
    { label: 'SKILL', x: margin + 165, w: 90 },
    { label: 'DMG', x: margin + 260, w: 35 },
    { label: 'CRIT', x: margin + 300, w: 35 },
    { label: 'RANGE', x: margin + 340, w: 55 },
    { label: 'SPECIAL', x: margin + 400, w: W - margin - 400 },
  ];
  wCols.forEach(function(c) { label(p1, c.label, c.x, wy + 2, 5); });
  wy -= 4;
  p1.drawLine({ start: { x: margin, y: wy }, end: { x: W - margin, y: wy }, thickness: 0.5, color: faintLine });

  weapons.slice(0, 6).forEach(function(w, wi) {
    var wry = wy - (wi + 1) * 14;
    if (wi % 2 === 0) {
      p1.drawRectangle({ x: margin, y: wry - 3, width: W - margin * 2, height: 14, color: rgb(0.95, 0.95, 0.93) });
    }
    text(p1, w.name || '', wCols[0].x, wry, { size: 8, color: white, font: helvB });
    text(p1, w.skill || '', wCols[1].x, wry, { size: 7, color: lightGray });
    text(p1, String(w.damage != null ? w.damage : '-'), wCols[2].x, wry, { size: 8, color: accent, font: helvB });
    text(p1, critText(w.crit), wCols[3].x, wry, { size: 8, color: accent, font: helvB });
    text(p1, w.range || '-', wCols[4].x, wry, { size: 7, color: lightGray });
    var quals = Array.isArray(w.qualities) ? w.qualities.join(', ') : (w.qualities || '');
    text(p1, quals.substring(0, 40), wCols[5].x, wry, { size: 6, color: midGray });
  });

  // ── Armor ──
  if (armor) {
    var ay = wy - (Math.min(weapons.length, 6) + 1) * 14 - 10;
    sectionHeader(p1, 'ARMOR', margin, ay, W - margin * 2);
    ay -= 14;
    text(p1, armor.name || '', margin + 2, ay, { size: 8, color: white, font: helvB });
    // B-1100 (D-404 item 6): the line carries soak only. Defence prints once, in the R.DEF / M.DEF boxes, which read the
    // derived (split, rigged) values; the armour's single book `defense` here disagreed with them.
    text(p1, 'Soak +' + (armor.soak || 0), margin + 180, ay, { size: 7, color: accent });
  }

  // ── XP + Credits footer ──
  var footY = 50;
  p1.drawLine({ start: { x: margin, y: footY + 14 }, end: { x: W - margin, y: footY + 14 }, thickness: 0.5, color: faintLine });
  label(p1, 'TOTAL XP', margin, footY + 4, 5);
  text(p1, String(xp.total || 0), margin + 40, footY, { font: helvB, size: 10, color: accent });
  label(p1, 'AVAILABLE XP', margin + 80, footY + 4, 5);
  text(p1, String(xp.remaining != null ? xp.remaining : 0), margin + 140, footY, { font: helvB, size: 10, color: accent });
  label(p1, 'CREDITS', margin + 180, footY + 4, 5);
  text(p1, String(eq.credits || 0), margin + 220, footY, { font: helvB, size: 10, color: accent });
  var enc = CharResolve.computeEncumbrance(eq, sheet.characteristics || {}, CharResolve.speciesEncumbranceDelta(sheet));
  if (enc) {
    label(p1, 'ENCUMBRANCE', margin + 270, footY + 4, 5);
    text(p1, enc.carried + ' / ' + enc.limit, margin + 340, footY, { font: helvB, size: 10, color: lightGray });
  }

  // ════════════════════════════════════════════════════
  // PAGE 2
  // ════════════════════════════════════════════════════
  var p2 = pdfDoc.addPage([W, H]);
  p2.drawRectangle({ x: 0, y: 0, width: W, height: H, color: darkBg });

  // Title
  p2.drawRectangle({ x: 0, y: H - 30, width: W, height: 30, color: rgb(0.94, 0.94, 0.92) });
  text(p2, (sheet.name || 'CHARACTER').toUpperCase() + ' \u2014 PAGE 2', margin, H - 22, { font: helvB, size: 10, color: accent });

  var p2y = H - 48;

  // ── Talents ──
  if (talents.length > 0) {
    sectionHeader(p2, 'TALENTS', margin, p2y, W - margin * 2);
    p2y -= 14;
    talents.forEach(function(t) {
      text(p2, t.name || '', margin + 2, p2y, { font: helvB, size: 8, color: white });
      p2y -= 10;
      if (t.description) {
        // B-981 (D-404 item 5): `t.description` is the EATER's output and keeps its glyph tokens; the page draws them as
        // dice through the one projector (pdfGlyphRuns → kit). R3 Q2b's drop is retired with it.
        kit.wrap(pdfGlyphRuns(t.description), W - margin * 2 - 10, 7).forEach(function(line) {
          kit.draw(p2, line, margin + 8, p2y, 7, rgb(0.55, 0.55, 0.55));
          p2y -= 9;
        });
      }
      p2y -= 4;
    });
  }

  // ── Species abilities · Signature abilities ── (R-SSC-7, frame P1; ADDENDUM 10 Q1 (a)) in the TALENTS block's own form and
  // helpers: the species' abilities (speciesAbilitiesForDisplay — LIVE) and each standing signature ability through the
  // sheet's one reader (sheetSignatureEntries — "<name> · <n> upgrades · <XP> XP", its activation terms, its words; DARK: []
  // with M12's engine key OFF, so the block is not drawn). A refused entry is not printed: the sheet names its reasons.
  [['SPECIES ABILITIES', (speciesAbilitiesForDisplay(sheet) || []).map(function (a) { return { name: a.name, words: a.description || '' }; })],
   ['SIGNATURE ABILITIES', sheetSignatureEntries(sheet).filter(function (e) { return e.ok; })
     .map(function (e) { return { name: e.name + ' \u00b7 ' + e.meta, words: e.words, extra: e.activation }; })]].forEach(function (blk) {
    if (!blk[1].length) return;
    p2y -= 4;
    sectionHeader(p2, blk[0], margin, p2y, W - margin * 2);
    p2y -= 14;
    blk[1].forEach(function (t) {
      text(p2, t.name || '', margin + 2, p2y, { font: helvB, size: 8, color: white });
      p2y -= 10;
      if (t.extra) { text(p2, t.extra, margin + 8, p2y, { size: 7, color: lightGray }); p2y -= 9; }
      kit.wrap(pdfGlyphRuns(t.words), W - margin * 2 - 10, 7).forEach(function (line) {   // B-981 — see TALENTS above
        kit.draw(p2, line, margin + 8, p2y, 7, rgb(0.55, 0.55, 0.55));
        p2y -= 9;
      });
      p2y -= 4;
    });
  });

  // ── Force Powers ──
  if (forcePowers.length > 0) {
    p2y -= 4;
    sectionHeader(p2, 'FORCE POWERS', margin, p2y, W - margin * 2);
    p2y -= 14;
    forcePowers.forEach(function(fp) {
      text(p2, fp.name || '', margin + 2, p2y, { font: helvB, size: 8, color: white });
      p2y -= 10;
      if (fp.description) {
        kit.wrap(pdfGlyphRuns(fp.description), W - margin * 2 - 10, 7).forEach(function(line) {   // B-981 — see TALENTS above
          kit.draw(p2, line, margin + 8, p2y, 7, rgb(0.55, 0.55, 0.55));
          p2y -= 9;
        });
      }
      p2y -= 4;
    });
  }

  // ── Gear list ──
  if (gear.length > 0) {
    p2y -= 4;
    sectionHeader(p2, 'GEAR', margin, p2y, W - margin * 2);
    p2y -= 14;
    gear.forEach(function(g, gi) {
      var gName = typeof g === 'string' ? g : (g.name || '');
      var gQty = typeof g === 'object' && g.qty > 1 ? ' x' + g.qty : '';
      var gEnc = typeof g === 'object' && g.encumbrance ? '  Enc ' + g.encumbrance : '';
      if (gi % 2 === 0) {
        p2.drawRectangle({ x: margin, y: p2y - 3, width: W - margin * 2, height: 12, color: rgb(0.95, 0.95, 0.93) });
      }
      text(p2, gName + gQty + gEnc, margin + 2, p2y, { size: 7, color: lightGray });
      p2y -= 12;
    });
  }

  // ── Mechanic (Obligation / Duty / Morality) — one block per track ──
  mechBlocks.forEach(function(blk) {
    var mech = blk.mech;
    p2y -= 10;
    sectionHeader(p2, blk.label.toUpperCase(), margin, p2y, W - margin * 2);
    p2y -= 16;
    text(p2, (mechTypeName(mechNames, blk.track, mech.type) || '').toUpperCase(), margin + 2, p2y, { font: helvB, size: 9, color: white });   // B-1605: the name, never the stored key
    text(p2, 'Value: ' + (mech.value || 0), margin + 200, p2y, { font: helvB, size: 9, color: accent });
    p2y -= 12;
    if (mech.description) {
      var mechLines = wrapText(mech.description, W - margin * 2 - 10, helv, 7);
      mechLines.forEach(function(line) {
        text(p2, line, margin + 2, p2y, { size: 7, color: rgb(0.55, 0.55, 0.55) });
        p2y -= 9;
      });
    }
  });

  // ── Motivation (B-103 rule 4: PDF DUAL-MODE) ──────────────────────────────────────────────
  // `motivation` present → the ONE structured row. Legacy present → the four rows exactly as
  // before. BOTH present → both, STRUCTURED FIRST. The point of the dual mode is that nothing a
  // player printed last month changes shape behind their back — a legacy sheet's PDF is
  // byte-for-byte the document it was, and a new sheet gains a row rather than replacing one.
  // Names are resolved from the registry at render (id-refs never print raw).
  var _mot = sheet.motivation;
  if (_mot && (_mot.category || _mot.specific || _mot.custom)) {
    p2y -= 10;
    sectionHeader(p2, 'MOTIVATION', margin, p2y, W - margin * 2);
    p2y -= 16;
    var _cb = (window._canonicalCharbuild || {});
    var _catName = (( _cb.motivationCategories || []).filter(function(c) { return c.id === _mot.category; })[0] || {}).name;
    var _specName = (( _cb.motivationSpecifics || []).filter(function(x) { return x.id === _mot.specific; })[0] || {}).name;
    var _val = _mot.custom || [_catName, _specName].filter(Boolean).join(' \u2014 ') || '';
    if (_val) {
      label(p2, _catName && !_mot.custom ? 'MOTIVATION' : 'MOTIVATION', margin + 2, p2y + 6, 5);
      text(p2, _val, margin + 70, p2y + 2, { size: 8, color: white });
      p2y -= 16;
    }
  }

  // ── Motivations (LEGACY — the player's own words, printed exactly as they always were) ──
  if (motivations.desire || motivations.fear || motivations.strength || motivations.flaw) {
    p2y -= 10;
    sectionHeader(p2, 'MOTIVATIONS', margin, p2y, W - margin * 2);
    p2y -= 16;
    var motFields = [
      ['DESIRE', motivations.desire],
      ['FEAR', motivations.fear],
      ['STRENGTH', motivations.strength],
      ['FLAW', motivations.flaw],
    ];
    motFields.forEach(function(m) {
      if (!m[1]) return;
      label(p2, m[0], margin + 2, p2y + 6, 5);
      text(p2, m[1], margin + 70, p2y + 2, { size: 8, color: white });
      p2y -= 16;
    });
  }

  // ── Backstory ──
  if (sheet.backstory) {
    p2y -= 10;
    sectionHeader(p2, 'BACKSTORY', margin, p2y, W - margin * 2);
    p2y -= 14;
    var bsLines = wrapText(sheet.backstory, W - margin * 2 - 4, helv, 7);
    bsLines.slice(0, 20).forEach(function(line) {
      text(p2, line, margin + 2, p2y, { size: 7, color: rgb(0.6, 0.6, 0.6) });
      p2y -= 9;
    });
  }

  // ── Description ──
  var ap = sheet.appearance || {};
  if (ap.age || ap.gender || ap.height) {
    p2y -= 10;
    sectionHeader(p2, 'DESCRIPTION', margin, p2y, W - margin * 2);
    p2y -= 14;
    var descParts = [];
    if (ap.age) descParts.push('Age: ' + ap.age);
    if (ap.gender) descParts.push('Gender: ' + ap.gender);
    if (ap.height) descParts.push('Height: ' + ap.height);
    if (ap.build) descParts.push('Build: ' + ap.build);
    if (ap.hair) descParts.push('Hair: ' + ap.hair);
    if (ap.eyes) descParts.push('Eyes: ' + ap.eyes);
    text(p2, descParts.join('  |  '), margin + 2, p2y, { size: 7, color: lightGray });
    p2y -= 12;
    if (ap.notableFeatures) {
      text(p2, 'Notable: ' + ap.notableFeatures, margin + 2, p2y, { size: 7, color: lightGray });
    }
  }

  // Footer
  p2.drawLine({ start: { x: margin, y: 40 }, end: { x: W - margin, y: 40 }, thickness: 0.5, color: faintLine });
  text(p2, BOOK_LABELS[sheet.book] || '', margin, 28, { size: 6, color: midGray, font: courier });
  text(p2, 'Generated ' + new Date().toLocaleDateString(), W - margin - 100, 28, { size: 6, color: midGray, font: courier });

  return await pdfDoc.save();
}

// B-987 — THE ONE WinAnsi FILTER for both exporters. A pdf-lib standard font (Helvetica, Courier) is WinAnsi-only, so one
// character it cannot encode (a `→` in a name) threw at the write or at updateFieldAppearances and lost the whole export,
// its JSON copy with it. Every free-text write goes through here: a character the font cannot encode is DROPPED (the drop
// R3 Q2b already made for talents and Force powers; what to print INSTEAD is B-981's question, not this filter's). Line
// breaks are kept, so a multi-line box still breaks where the player broke it.
function pdfEncodable(font, str) {
  var s = String(str == null ? '' : str);
  if (/^[\x20-\x7E\r\n]*$/.test(s)) return s;
  return Array.from(s).filter(function (ch) {
    if (ch === '\n' || ch === '\r') return true;
    try { font.encodeText(ch); return true; } catch (_) { return false; }
  }).join('');
}

// Simple text wrapping for pdf-lib (no native support).
function wrapText(str, maxWidth, font, fontSize) {
  if (!str) return [];
  var words = pdfEncodable(font, str).split(/\s+/);
  var lines = [];
  var current = '';
  words.forEach(function(word) {
    var test = current ? current + ' ' + word : word;
    var w = font.widthOfTextAtSize(test, fontSize);
    if (w > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);
  return lines;
}

/* ══════════════════════════════════════════════════════
   PDF EXPORT — FFG form-fillable character sheets
══════════════════════════════════════════════════════ */

// Skill key mapping: player.html SKILL_NAMES key → PDF AcroForm field name
var PDF_SKILL_MAP = {
  ASTRO:'Astrogation', ATHL:'Athletics', BRAWL:'Brawl', CHARM:'Charm', COERC:'Coercion',
  COMP:'Computers', COOL:'Cool', COORD:'Coordination', KNOWCORE:'CoreWorlds',
  DECEP:'Deception', DISC:'Discipline', KNOWED:'Education', GUNN:'Gunnery',
  LEAD:'Leadership', LTSAB:'Lightsaber', KNOWLORE:'Lore', MECH:'Mechanics', MED:'Medicine',
  MELEE:'Melee', NEGOT:'Negotiation', KNOWOUT:'OuterRim', PERCEP:'Perception',
  PILPLAN:'PilotingPlanetary', PILSP:'PilotingSpace', RANGHVY:'RangedHvy', RANGLT:'RangedLight',
  RESIL:'Resilience', SKULL:'Skullduggery', STEAL:'Stealth', STREET:'Streetwise',
  SURV:'Survival', KNOWUND:'Underworld', VIGIL:'Vigilance', KNOWWARF:'Warfare', KNOWXENO:'Xenology',
};

// Career checkbox field names per sheet type
var PDF_CAREER_CB = {
  EotE: {
    Astrogation:'Check Box8', Athletics:'Check Box9', Charm:'Check Box10',
    Coercion:'Check Box11', Computers:'Check Box12', Cool:'Check Box13',
    Coordination:'Check Box14', Deception:'Check Box15', Discipline:'Check Box16',
    Leadership:'Check Box17', Mechanics:'Check Box18', Medicine:'Check Box19',
    Negotiation:'Check Box20', Perception:'Check Box21', PilotingPlanetary:'Check Box22',
    PilotingSpace:'Check Box23', Resilience:'Check Box24', Skullduggery:'Check Box25',
    Stealth:'Check Box26', Streetwise:'Check Box27', Survival:'Check Box28',
    Vigilance:'Check Box29', Brawl:'Check Box30', Gunnery:'Check Box31',
    Melee:'Check Box32', RangedLight:'Check Box33', RangedHvy:'Check Box34',
    CoreWorlds:'Check Box35', Education:'Check Box36', Lore:'Check Box37',
    OuterRim:'Check Box38', Underworld:'Check Box39', Xenology:'Check Box40',
  },
  AoR: {
    Astrogation:'Check Box8', Athletics:'Check Box9', Charm:'Check Box10',
    Coercion:'Check Box11', Computers:'Check Box12', Cool:'Check Box13',
    Coordination:'Check Box14', Deception:'Check Box15', Discipline:'Check Box16',
    Leadership:'Check Box17', Mechanics:'Check Box18', Medicine:'Check Box19',
    Negotiation:'Check Box20', Perception:'Check Box21', PilotingPlanetary:'Check Box22',
    PilotingSpace:'Check Box23', Resilience:'Check Box24', Skullduggery:'Check Box25',
    Stealth:'Check Box26', Streetwise:'Check Box27', Survival:'Check Box28',
    Vigilance:'Check Box29', Brawl:'Check Box30', Gunnery:'Check Box31',
    Melee:'Check Box32', RangedLight:'Check Box33', RangedHvy:'Check Box34',
    CoreWorlds:'Check Box35', Education:'Check Box36', Lore:'Check Box37',
    OuterRim:'Check Box38', Underworld:'Check Box39', Xenology:'Check Box40',
    Warfare:'Check Box48',
  },
  FaD: {
    Astrogation:'Check Box8', Athletics:'Check Box9', Charm:'Check Box10',
    Coercion:'Check Box11', Computers:'Check Box12', Cool:'Check Box13',
    Coordination:'Check Box14', Deception:'Check Box15', Discipline:'Check Box16',
    Leadership:'Check Box17', Mechanics:'Check Box18', Medicine:'Check Box19',
    Negotiation:'Check Box20', Perception:'Check Box21', PilotingPlanetary:'Check Box22',
    PilotingSpace:'Check Box23', Resilience:'Check Box24', Skullduggery:'Check Box25',
    Stealth:'Check Box26', Streetwise:'Check Box27', Survival:'Check Box28',
    Vigilance:'Check Box29', Brawl:'Check Box30', Gunnery:'Check Box31',
    Melee:'Check Box32', RangedLight:'Check Box33', RangedHvy:'Check Box34',
    CoreWorlds:'Check Box35', Education:'Check Box36', Lore:'Check Box37',
    OuterRim:'Check Box38', Underworld:'Check Box39', Xenology:'Check Box40',
    Lightsaber:'Check Box Lightsaber',
  },
};

async function generateFFGPdf(sheet) {
  var PDFLib;
  try { PDFLib = await ensurePDFLib(); } catch(e) { throw pdfLoadFailure(e); }
  // R-M7G-9: the skill column and the CAREER box read the vocabulary — wait for it, so a blank means the registries
  // FAILED (the loader resolves null), never that they had not loaded yet.
  await loadCanonicalCharacterBuild();
  var sheetKey = sheet.book === 'aor' ? 'AoR' : sheet.book === 'fad' ? 'FaD' : 'EotE';
  var sheetFile = encodeURIComponent(sheetKey + ' Character Sheet (Form Fillable).pdf');

  var pdfBytes;
  try {
    var resp = await fetch('public/pdf/templates/' + sheetFile);
    if (!resp.ok) throw new Error(resp.statusText);
    pdfBytes = await resp.arrayBuffer();
  } catch (e) {
    throw pdfLoadFailure(e);
  }

  var pdfDoc = await PDFLib.PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  var form = pdfDoc.getForm();
  var helvetica = await pdfDoc.embedFont(PDFLib.StandardFonts.Helvetica);
  var kit = await pdfSymbolKit(PDFLib, pdfDoc, helvetica);   // B-981: rule text keeps its dice
  // B-978 — NO SILENT FAILURES. Every intended write that fails is COUNTED by field name and reported
  // once at the end (`PDF export: …`). The empty catches here hid B-978 for the life of the exporter:
  // every talent row printed blank on every line and nothing said so. A field the template does not
  // carry is not written at all (B-979), so a name in this list is always a real miss.
  var failedWrites = [];
  // B-987: every text write is filtered to what Helvetica can encode (pdfEncodable), so updateFieldAppearances cannot throw.
  var set = function(name, val) { try { form.getTextField(name).setText(pdfEncodable(helvetica, val)); } catch(_) { failedWrites.push(name); } };
  var setSmall = function(name, val, fontSize) { try { var f = form.getTextField(name); f.setFontSize(fontSize || 7); f.setText(pdfEncodable(helvetica, val)); } catch(_) { failedWrites.push(name); } };
  var checkCB = function(name) { try { form.getCheckBox(name).check(); } catch(_) { failedWrites.push(name); } };
  // B-979 R4(c) (ruling release-r7-bug-burndown ADDENDUM 126): what a template CARRIES is read from the loaded form, never
  // typed by game line, the way talentRows is — so a template that gains a field gets its write.
  var fieldNames = form.getFields().map(function (f) { return f.getName(); });
  var hasField = function (name) { return fieldNames.indexOf(name) !== -1; };
  // B-979 (D-404 item 7): what the template has NO field for is not lost — each item is one line in NotableFeatures,
  // fitted by the talents' fitLine rule, written once at the end (below the Force powers). The presence tests above decide
  // which items; the player's own notable features follow them.
  var noBox = [];

  // ── Identity ──
  set('Name', sheet.name || '');
  set('Player', sheet.playerName || '');
  set('Species', sheet.species || '');
  set('Career', sheet.career || '');
  set('Specializations', sheet.specialization || '');

  // ── Appearance ──
  var ap = sheet.appearance || {};
  set('Age', ap.age || ''); set('Gender', ap.gender || '');
  set('Height', ap.height || ''); set('Build', ap.build || '');
  set('Hair', ap.hair || ''); set('Eyes', ap.eyes || '');

  // ── Characteristics ──
  var chars = sheet.characteristics || {};
  set('Brawn', chars.brawn || 2); set('Agility', chars.agility || 2);
  set('Intellect', chars.intellect || 2); set('Cunning', chars.cunning || 2);
  set('Willpower', chars.willpower || 2); set('Presence', chars.presence || 2);

  // ── Derived stats ── CS-2 (R-DAR): resolve-at-render, so the PDF matches the live sheet (incl. armor).
  var eq = resolveSheetEquipment(sheet.equipment, sheet);
  var derived = CharResolve.computeDerived(sheet, eq, window._canonicalCharbuild);   // B-23
  set('Soak', derived.soak || 0);
  set('MelDef', derived.meleeDefense || 0);
  set('RngDef', derived.rangedDefense || 0);
  set('WT', derived.woundThreshold || 0);
  set('ST', derived.strainThreshold || 0);
  // B-737 (b) — THE "Current" FIELDS CARRY THE SHEET'S CURRENT STATE, not the threshold again.
  // Both were `derived.*Threshold`, so a printed sheet reported the same number in both boxes no
  // matter how much damage the character was carrying. `sheet.currentWounds` / `sheet.currentStrain`
  // are what the sheet's two trackers write and what its own two readers read (`:26140-26141`,
  // `:32492-32493`). NEVER TOUCHED = THE KEY IS ABSENT: no record ships with either field (no
  // writer outside the trackers; `public/data/pregen_sheets.json` carries neither).
  //
  // B-746 / B-742 CLOSED IN DIRECTION (a) — THE FALLBACK IS 0, NOT THE THRESHOLD (Jason 2026-09-20).
  // B-737 kept the threshold here, and that left the printed sheet and the sheet on screen
  // disagreeing about the SAME blank case: the sheet's own two readers default an absent key to 0
  // (`:26147`, `:32499`) and this path printed the ceiling. Both tracks COUNT UP from nothing and
  // are clamped at threshold × 2, so a box named "Current" holding the threshold says the character
  // is at his wound threshold — Incapacitated (EotE CRB p.215; FaD CRB p.222) — which is the exact
  // opposite of an untouched character. All eighteen Quick Start records carry neither key, so every
  // printed pregen read as dying. The two boxes stay SEPARATE named form fields: the threshold in
  // `WT`/`ST`, the current value in `WT Current`/`ST Current`, never merged into one string.
  set('WT Current', sheet.currentWounds != null ? sheet.currentWounds : 0);
  set('ST Current', sheet.currentStrain != null ? sheet.currentStrain : 0);
  // B-979: only FaD's template carries a ForceRating field today; on EotE/AoR this write never landed.
  if (hasField('ForceRating')) set('ForceRating', derived.forceRating || 0);
  else if (derived.forceRating > 0) noBox.push('Force rating: ' + derived.forceRating);

  // ── XP, Credits, Encumbrance ──
  var xp = sheet.xp || {};
  set('Total XP', xp.total || '');
  set('Current XP', xp.remaining != null ? xp.remaining : (xp.total || ''));
  set('Credits', sheet.equipment ? sheet.equipment.credits : '');
  var enc = CharResolve.computeEncumbrance(eq, sheet.characteristics || {}, CharResolve.speciesEncumbranceDelta(sheet));
  // B-979: only EotE's template carries an Encumbrance field today; on AoR/FaD this write never landed.
  if (enc && hasField('Encumbrance')) set('Encumbrance', enc.carried + ' / ' + enc.limit);
  else if (enc) noBox.push('Encumbrance: ' + enc.carried + ' / ' + enc.limit);

  // ── Skills: rank pips + career checkboxes ──
  var skills = sheet.skills || {};
  var careerCBMap = PDF_CAREER_CB[sheetKey] || {};
  // B-1035 (F-100 M7 C2, R-M7K-7): the CAREER box means career — the one answer, CharResolve.careerSkillStatus —
  // never "trained" (it ticked every rank > 0 and missed every untrained career skill). No charbuild, no box: an
  // empty box is honest, a guessed one is not.
  var _pdfCb = window._canonicalCharbuild;
  var careerStatus = _pdfCb ? CharResolve.careerSkillStatus(sheet, _pdfCb) : {};
  Object.keys(PDF_SKILL_MAP).forEach(function(skillKey) {
    var pdfField = PDF_SKILL_MAP[skillKey];
    var rank = skills[skillKey] || 0;
    // Check rank pips
    for (var pip = 0; pip < 5; pip++) {
      if (pip < rank) checkCB(pdfField + '.' + pip);
    }
    if (careerStatus[skillKey] && careerCBMap[pdfField]) checkCB(careerCBMap[pdfField]);
  });

  // ── Weapons ──
  var RANGE_PDF = { Engaged:'Engaged', Short:'Short', Medium:'Medium', Long:'Long', Extreme:'Extreme' };
  var _eqR = eq;
  var weapons = _eqR.weapons || [];
  for (var wi = 0; wi < Math.min(weapons.length, 8); wi++) {
    var w = weapons[wi];
    var n = wi + 1;
    set('Weapon' + n, w.name || '');
    set('WeaponSkill' + n, w.skill || '');
    set('WeaponDamage' + n, w.damage || '');
    set('WeaponCrit' + n, critText(w.crit));
    set('WeaponRange' + n, RANGE_PDF[w.range] || w.range || '');
    var quals = w.qualities || '';
    set('WeaponSpecial' + n, quals.length > 60 ? quals.substring(0, 59) + '...' : quals);
  }

  // ── Armor → Weapons&Armor field ──
  var armor = _eqR.armor;
  if (armor) {
    var armorText = armor.name + ' Soak +' + (armor.soak || 0);
    setSmall('Weapons&Armor', armorText, 8);
  }

  // ── Gear ──
  var gear = (_eqR.gear || []).map(function(g) { return g.name + (g.qty > 1 ? ' ×' + g.qty : ''); });
  var gearText = gear.join('\n');
  if (sheetKey === 'AoR') {
    var mid = Math.ceil(gear.length / 2);
    setSmall('PersonalGear1', gear.slice(0, mid).join('\n'), 8);
    setSmall('PersonalGear2', gear.slice(mid).join('\n'), 8);
  } else {
    setSmall('PersonalGear', gearText, 8);
  }

  // ── Narrative Mechanic ──
  if (sheetKey === 'EotE' && sheet.obligation) {
    set('Obligation1', (sheet.obligation.type || '').toUpperCase ? mechTypeName(await SWRPGMechTypeNames.load(qbFetchJson), 'obligation', sheet.obligation.type) : '');   // B-1605
    set('Magnitude1', sheet.obligation.value || '');
    if (sheet.obligation.description) setSmall('ObligationDesc1', sheet.obligation.description, 6);
  } else if (sheetKey === 'AoR' && sheet.duty) {
    set('Duty1', mechTypeName(await SWRPGMechTypeNames.load(qbFetchJson), 'duty', sheet.duty.type || ''));   // B-1605
    set('Magnitude1', sheet.duty.value || '');
  } else if (sheetKey === 'FaD') {
    if (sheet.morality) {
      set('EmotionalStrength', sheet.morality.strength || sheet.morality.type || '');
      set('EmotionalWeakness', sheet.morality.weakness || '');
      set('Morality', sheet.morality.value || 50);
      set('Conflict', 0);
    }
  }

  // ── Motivations ──
  var mot = sheet.motivations || {};
  if (mot.desire || mot.strength) {
    set('Motivation1', mot.desire || mot.strength || '');
    var motDesc = [];
    if (mot.desire) motDesc.push('Desire: ' + mot.desire);
    if (mot.fear) motDesc.push('Fear: ' + mot.fear);
    if (mot.strength) motDesc.push('Strength: ' + mot.strength);
    if (mot.flaw) motDesc.push('Flaw: ' + mot.flaw);
    setSmall('Mot1Desc', motDesc.join(', '), 6);
  }

  // ── Backstory → BackgroundNotes or NotableFeatures ──
  // B-979: only EotE's template carries BackgroundNotes today; on AoR/FaD this write never landed.
  if (sheet.backstory && hasField('BackgroundNotes')) setSmall('BackgroundNotes', sheet.backstory, 7);
  else if (sheet.backstory) noBox.push('Backstory: ' + sheet.backstory);

  // ── Talents + Force powers (B-978) ──
  // Each talent row is a non-terminal PARENT `TalentNN` over three terminal text fields: `.0` NAME,
  // `.1` PAGE #, `.2` ABILITY SUMMARY. The old loop wrote the parent, which pdf-lib cannot resolve, so
  // every row printed blank on every line. The row and slot counts are READ from the loaded template
  // (EotE 28 rows, AoR 14, FaD 12; FaD alone has 3 Force-power slots), never a literal. When a
  // character owns more than fit, the last row says how many are not shown instead of dropping them.
  var countOf = function (re) {
    var seen = {};
    fieldNames.forEach(function (n) { var m = re.exec(n); if (m) seen[m[1]] = true; });
    return Object.keys(seen).length;
  };
  var talentRows = countOf(/^Talent(\d\d)\.\d$/);
  var powerSlots = countOf(/^ForcePower(\d\d)$/);
  var num2 = function (i) { return (i < 10 ? '0' : '') + i; };
  var overflowNote = function (n) { return '+' + n + ' more: see Holocron'; };
  // A form field holds ONE font, so a field cannot carry a die. pdfText is for text with none (names, upgrades, and every
  // summary whose rule has no die): the tokens it meets are unknown ones (plainTextGlyphless), and a character Helvetica
  // cannot encode is dropped (pdfEncodable, B-987's one filter). A summary WITH a die goes to drawRuns below (B-981).
  var pdfText = function (s) {
    return pdfEncodable(helvetica, plainTextGlyphless(s).replace(/\s+/g, ' ').trim());
  };
  var boxWidth = function (name) {
    try { return form.getTextField(name).acroField.getWidgets()[0].getRectangle().width - 4; } catch (_) { return 0; }
  };
  var boxHeight = function (name) {
    try { return form.getTextField(name).acroField.getWidgets()[0].getRectangle().height - 4; } catch (_) { return 0; }
  };
  var fits = function (s, width, size) { return helvetica.widthOfTextAtSize(s, size) <= width; };
  // Trim to the box at `size`, word by word, ending in an ellipsis.
  var fitLine = function (s, width, size) {
    if (fits(s, width, size)) return s;
    var words = s.split(' ');
    while (words.length > 1 && !fits(words.join(' ') + '…', width, size)) words.pop();
    var out = words.join(' ');
    while (out.length && !fits(out + '…', width, size)) out = out.slice(0, -1);
    return out.replace(/[\s,;:.]+$/, '') + '…';
  };
  var wrapLines = function (s, width, size) {
    var lines = [], cur = '';
    s.split(' ').forEach(function (w) {
      var next = cur ? cur + ' ' + w : w;
      if (cur && !fits(next, width, size)) { lines.push(cur); cur = w; } else cur = next;
    });
    if (cur) lines.push(cur);
    return lines;
  };
  var SUMMARY_PT = 7;
  // B-981 (D-404 item 5): a summary whose rule carries a die is DRAWN on the page inside the field's own box, through the
  // one projector (pdfGlyphRuns → kit), and the field is left empty and read-only so nothing types over it. Every field
  // without a die keeps the form write above, byte for byte.
  var drawRuns = function (name, lines, size) {
    try {
      var field = form.getTextField(name);
      var widget = field.acroField.getWidgets()[0];
      var r = widget.getRectangle();
      var page = pdfDoc.findPageForAnnotationRef(pdfDoc.context.getObjectRef(widget.dict));
      var lh = helvetica.heightAtSize(size);
      var y = lines.length === 1 && r.height < lh * 2 ? r.y + (r.height - size) / 2 + size * 0.22 : r.y + r.height - 2 - size;
      var align = field.getAlignment();   // the field's own quadding: EotE's talent column is right-aligned
      lines.forEach(function (line) {
        var w = kit.width(line, size);
        var x = align === PDFLib.TextAlignment.Right ? r.x + r.width - 2 - w : align === PDFLib.TextAlignment.Center ? r.x + (r.width - w) / 2 : r.x + 2;
        kit.draw(page, line, x, y, size, PDFLib.rgb(0, 0, 0));
        y -= lh;
      });
      field.setText('');
      field.enableReadOnly();
    } catch (_) { failedWrites.push(name); }
  };

  // R-SSC-7 (frame P2): each standing signature ability joins the talents box as "<name> (Signature)" with its words — DARK
  // (sheetSignatureEntries is [] with M12's engine key OFF); the form has no footnote room, so it prints no because notes.
  var talents = sheetTalentsForDisplay(sheet).concat(sheetSignatureEntries(sheet).filter(function (e) { return e.ok; })
    .map(function (e) { return { name: e.name + ' (Signature)', ranked: false, description: e.words }; }));
  var talentsShown = talents.length > talentRows ? talentRows - 1 : talents.length;
  for (var ti = 0; ti < talentsShown; ti++) {
    var t = talents[ti];
    var trow = 'Talent' + num2(ti + 1);
    set(trow + '.0', pdfText(t.name + (t.ranked ? ' ' + (t.rank || 1) : '')));
    var truns = pdfGlyphRuns(t.description || '');
    if (kit.hasSym(truns)) drawRuns(trow + '.2', [kit.fit(truns, boxWidth(trow + '.2'), SUMMARY_PT)], SUMMARY_PT);
    else {
      var tsum = pdfText(t.description || '');
      if (tsum) setSmall(trow + '.2', fitLine(tsum, boxWidth(trow + '.2'), SUMMARY_PT), SUMMARY_PT);
    }
  }
  if (talents.length > talentRows && talentRows > 0) set('Talent' + num2(talentRows) + '.0', overflowNote(talents.length - talentsShown));

  // FaD's three power slots: the name in `ForcePowerNN`, the summary and bought upgrades in `_Desc`.
  // The upgrades are kept whole and the summary gives way to them when the box is full.
  var powers = powerSlots ? sheetForcePowersForDisplay(sheet) : [];
  var powersShown = powers.length > powerSlots ? powerSlots - 1 : powers.length;
  var DESC_PT = 8;
  for (var pi = 0; pi < powersShown; pi++) {
    var fp = powers[pi];
    var slot = 'ForcePower' + num2(pi + 1);
    set(slot, pdfText(fp.name || ''));
    var counts = {}, order = [];
    (fp.upgradeNames || fp.upgrades || []).forEach(function (u) {
      if (!counts[u]) { counts[u] = 0; order.push(u); }
      counts[u]++;
    });
    var upText = order.length ? pdfText('Upgrades: ' + order.map(function (u) { return u + (counts[u] > 1 ? ' ×' + counts[u] : ''); }).join(', ')) : '';
    var dw = boxWidth(slot + '_Desc');
    var maxLines = Math.max(1, Math.floor(boxHeight(slot + '_Desc') / helvetica.heightAtSize(DESC_PT)));
    var upLines = upText ? wrapLines(upText, dw, DESC_PT) : [];
    var room = Math.max(0, maxLines - upLines.length);
    var fpRuns = pdfGlyphRuns(fp.description || '');
    if (kit.hasSym(fpRuns)) {   // B-981: the same rule over runs, drawn in the box
      var sumRuns = kit.wrap(fpRuns, dw, DESC_PT);
      if (sumRuns.length > room) {
        sumRuns = sumRuns.slice(0, room);
        if (room) sumRuns[room - 1] = kit.fit(sumRuns[room - 1].concat([{ text: '…' }]), dw, DESC_PT);
      }
      drawRuns(slot + '_Desc', sumRuns.concat(upLines.map(function (l) { return [{ text: l }]; })), DESC_PT);
      continue;
    }
    var sumLines = wrapLines(pdfText(fp.description || ''), dw, DESC_PT);
    if (sumLines.length > room) {
      sumLines = sumLines.slice(0, room);
      if (room) sumLines[room - 1] = fitLine(sumLines[room - 1] + '…', dw, DESC_PT);
    }
    setSmall(slot + '_Desc', sumLines.concat(upLines).join('\n'), DESC_PT);
  }
  if (powers.length > powerSlots && powerSlots > 0) set('ForcePower' + num2(powerSlots), overflowNote(powers.length - powersShown));
  if (!powerSlots) {
    var unboxedPowers = sheetForcePowersForDisplay(sheet).map(function (p) { return p.name; }).filter(Boolean);
    if (unboxedPowers.length) noBox.push('Force powers: ' + unboxedPowers.join(', '));
  }

  // B-979: the NotableFeatures box — the template's homeless items, one fitted line each, then the player's own words.
  var notable = noBox.map(function (l) { return fitLine(pdfText(l), boxWidth('NotableFeatures'), 7); });
  if (sheet.notableFeatures) notable.push(sheet.notableFeatures);
  if (notable.length) setSmall('NotableFeatures', notable.join('\n'), 7);

  if (failedWrites.length) {
    console.warn('PDF export: ' + failedWrites.length + ' field write(s) failed on the ' + sheetKey + ' template: ' + failedWrites.join(', '));
  }

  // Update field appearances with embedded font
  form.updateFieldAppearances(helvetica);

  return await pdfDoc.save();
}


// B-1594 (D-411 items 17–18): a failed PDF never opens a browser dialog. The exporters THROW, and a failed load (pdf-lib, or
// the FFG template) is tagged 'load'; anything else that throws while the file is made is 'make'. downloadCharacterPDF
// RETURNS the cause ('load' | 'make', null when the file was handed over) and the sheet draws it (SheetPaper's PDF line).
function pdfLoadFailure(e) {
  var err = e instanceof Error ? e : new Error(String(e));
  err.pdfCause = 'load';
  return err;
}
var PDF_LOAD_LINE = 'The PDF couldn\u2019t load. Check your connection, then retry.';
var PDF_MAKE_LINE = 'This character\u2019s PDF couldn\u2019t be made.';

async function downloadCharacterPDF(sheet) {
  try {
    var pdfBytes = COMMERCIAL_MODE ? await generateCustomPdf(sheet) : await generateFFGPdf(sheet);
    var blob = new Blob([pdfBytes], { type: 'application/pdf' });
    var url = URL.createObjectURL(blob);
    var baseName = (sheet.name || 'character').replace(/[^a-zA-Z0-9]/g, '_');
    var filename = baseName + '_Sheet.pdf';
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.open(url, '_blank');
      setTimeout(function() { URL.revokeObjectURL(url); }, 10000);
    } else {
      var a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    // B-715 (D-404 item 8): the PDF button downloads the PDF and nothing else. It used to write a second
    // `swrpg-character-sheet` JSON beside it that no product path reads back; the whole-save backup is the one
    // file that restores (D-252/D-258).
    return null;
  } catch (e) {
    console.error('PDF generation failed:', e);
    return e && e.pdfCause === 'load' ? 'load' : 'make';
  }
}

/* ══════════════════════════════════════════════════════
   CHARACTER SHEET — FFG-inspired 3-page swipeable viewer
══════════════════════════════════════════════════════ */

const SKILL_NAMES = {
  ASTRO:'Astrogation',ATHL:'Athletics',BRAWL:'Brawl',CHARM:'Charm',COERC:'Coercion',
  COMP:'Computers',COOL:'Cool',COORD:'Coordination',DECEP:'Deception',DISC:'Discipline',
  GUNN:'Gunnery',KNOWCORE:'Core Worlds',KNOWED:'Education',KNOWLORE:'Lore',
  KNOWOUT:'Outer Rim',KNOWUND:'Underworld',KNOWWARF:'Warfare',KNOWXENO:'Xenology',
  LEAD:'Leadership',LTSAB:'Lightsaber',MECH:'Mechanics',MED:'Medicine',MELEE:'Melee',
  NEGOT:'Negotiation',PERCEP:'Perception',PILPLAN:'Piloting (Planetary)',
  PILSP:'Piloting (Space)',RANGHVY:'Ranged (Heavy)',RANGLT:'Ranged (Light)',
  RESIL:'Resilience',SKULL:'Skulduggery',STEAL:'Stealth',STREET:'Streetwise',
  SURV:'Survival',VIGIL:'Vigilance',
};

// F-46 dc3 v3: the FFG skill categories, for the cockpit's segmented skills toggle (TRAINED/GENERAL/
// COMBAT/KNOWLEDGE). Combat = the 6 attack skills; Knowledge = the KNOW* keys; General = the rest.
var COMBAT_SKILL_KEYS = { BRAWL:1, GUNN:1, LTSAB:1, MELEE:1, RANGHVY:1, RANGLT:1 };
function skillCategoryOf(key) {
  if (/^KNOW/.test(key)) return 'KNOWLEDGE';
  if (COMBAT_SKILL_KEYS[key]) return 'COMBAT';
  return 'GENERAL';
}

var h = React.createElement;

// ══════════════════════════════════════════════════════
// Styling primitives — ported from gm.html so the
// Player Datapad sheet matches the NPC reference voice.
// Keep these at the top of the sheet layer; every page
// below assumes they exist.
// ══════════════════════════════════════════════════════

// PrimaryButton → window.SWRPGUI (lib/shared/ui.jsx), destructured at top. [Phase 1b] (SectionHeader and StatBox
// were destructured for the old sheet and went with it — paper-sheet-delete.)

function PlayerBehaviorRow({ label, text, accent, source }) {
  return h('div', { style: { marginBottom: 2 } },
    h('div', { style: {
      fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)',
      letterSpacing: '0.18em', color: accent, opacity: 0.7,
      marginBottom: 3,
    }}, label),
    h('div', { style: {
      fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.55,
      color: 'rgba(255,255,255,0.7)',
    }}, text || '\u2014'),
    source ? h('div', { style: {
      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)',
      letterSpacing: '0.12em', color: accent + '77', marginTop: 4,
    }}, 'SOURCE \u00B7 ' + String(source).toUpperCase()) : null
  );
}

// Credits control — the only numeric field with a wide delta range
// (1 → 500+). Value is tap-to-edit; two pills open a floating grid of
// delta buttons for ADD or SPEND. Grid closes on backdrop tap, ESC, or
// selecting a delta. Delta grid: 1, 5, 10, 25, 50, 100, 250, 500.
var CREDIT_DELTAS = [1, 5, 10, 25, 50, 100, 250, 500];
function CreditsControl({ value, accent, onChange }) {
  var v = Number(value) || 0;
  var _ed = React.useState(false), editing = _ed[0], setEditing = _ed[1];
  var _dr = React.useState(String(v)), draft = _dr[0], setDraft = _dr[1];
  var _mode = React.useState(null), mode = _mode[0], setMode = _mode[1]; // 'add' | 'spend' | null
  var inputRef = React.useRef(null);

  React.useEffect(function() {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);
  React.useEffect(function() { setDraft(String(v)); }, [v, editing]);
  React.useEffect(function() {
    if (!mode) return;
    function onKey(e) { if (e.key === 'Escape') setMode(null); }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [mode]);

  function commit() {
    var n = parseInt(draft, 10);
    if (!isNaN(n) && n >= 0) onChange(n);
    setEditing(false);
  }
  function applyDelta(d) {
    // Use the functional setter so rapid taps stack correctly. Reading `v`
    // from the closure would freeze on the first-tap value when several
    // delta presses fire before the parent re-renders.
    onChange(function(prev) {
      var base = typeof prev === 'number' ? prev : (Number(prev) || 0);
      return Math.max(0, base + d);
    });
    // Stay open — players often stack multiple deltas (+100 +50 +25 etc).
    // Close via Done button, backdrop, ESC, or the × in the corner.
  }


  // B-630 (the paper order): the purse on paper — the balance as an entry whose value is a press-to-edit field,
  // ADD and SPEND as the kit's chips, and the delta pad INLINE beneath them (no fixed dark modal on paper — the
  // pack is a board and the purse is one of its entries). Same state, same writers, same testids.
  return (
    <div className="sb-e sb-purse" data-testid="pack-purse" data-mode={mode || ''}>
      <div className="sb-e-h"><span className="sb-e-n">Credits</span>
        {editing
          ? <input ref={inputRef} className="sb-filter sb-purse-in" type="number" data-testid="credits-input" value={draft} aria-label="Credits on hand" min={0}
              onChange={function(e) { setDraft(e.target.value); }} onBlur={commit}
              onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); commit(); } else if (e.key === 'Escape') { setDraft(String(v)); setEditing(false); } }} />
          : <button type="button" className="sb-purse-val" data-testid="credits-edit" aria-label={'Credits on hand: ' + v + ' — press to set'} onClick={function() { setEditing(true); }}>{v.toLocaleString()}<span className="sb-of"> cr</span></button>}
      </div>
      <div className="sb-critchips" role="group" aria-label="Credits">
        <button type="button" className="hp-quiet" data-testid="credits-add" data-on={mode === 'add' ? '1' : '0'} aria-pressed={mode === 'add' ? 'true' : 'false'} onClick={function() { setMode(mode === 'add' ? null : 'add'); }}>Add</button>
        <button type="button" className="hp-quiet" data-testid="credits-spend" data-on={mode === 'spend' ? '1' : '0'} aria-pressed={mode === 'spend' ? 'true' : 'false'} disabled={v === 0} onClick={function() { setMode(mode === 'spend' ? null : 'spend'); }}>Spend</button>
        {/* Jason 2026-09-16: "Can credits be manually typed?" — they could (press the number), it did not look
            pressable; SET says so and opens the same field */}
        <button type="button" className="hp-quiet" data-testid="credits-set" data-on={editing ? '1' : '0'} aria-pressed={editing ? 'true' : 'false'} onClick={function() { setMode(null); setEditing(true); }}>Set</button>
      </div>
      {mode
        ? <div className="sb-purse-pad" data-testid="credits-pad">
            <div className="sb-grp" style={{ paddingTop: 8 }}>{mode === 'add' ? 'Add credits' : 'Spend credits'}<span>{v.toLocaleString() + ' cr'}</span></div>
            <div className="sb-critchips">
              {CREDIT_DELTAS.map(function(d) {
                var sign = mode === 'add' ? 1 : -1, signed = sign * d, disabled = mode === 'spend' && v + signed < 0;
                return <button type="button" key={d} className="hp-quiet" data-testid={'credits-delta-' + (mode === 'add' ? 'plus' : 'minus') + '-' + d} disabled={disabled} onClick={function() { applyDelta(signed); }}>{(mode === 'add' ? '+' : '−') + d}</button>;
              })}
              <button type="button" className="hp-commit" data-testid="credits-done" onClick={function() { setMode(null); }}>Done</button>
            </div>
          </div>
        : null}
    </div>
  );
}

// Horizontal track bar with "current / threshold" overlay. Used for
// wounds + strain. Turns red when current >= threshold (incap).
function TrackBar({ label, current, max, accent }) {
  var cur = Number(current) || 0;
  var mx  = Number(max) || 0;
  var pct = mx > 0 ? Math.min(100, (cur / mx) * 100) : 0;
  var isDanger = mx > 0 && cur >= mx;
  var fillColor = isDanger ? '#FF6B6B' : accent;
  return h('div', null,
    h('div', { style: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      marginBottom: 4,
    }},
      h('span', { style: {
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em',
        color: 'rgba(255,255,255,0.5)',
      }}, label),
      h('span', { style: {
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)',
        color: isDanger ? '#FF6B6B' : '#fff',
      }}, cur + ' / ' + mx)
    ),
    h('div', { style: {
      position: 'relative', height: 10, borderRadius: 5,
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid ' + accent + '22', overflow: 'hidden',
    }},
      h('div', { style: {
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: pct + '%',
        background: 'linear-gradient(90deg, ' + fillColor + '66 0%, ' + fillColor + 'cc 100%)',
        transition: 'width 0.3s ease',
      }})
    )
  );
}

// Combat profile tracker — ported from the GM library's old tracker (retired with its dark face, B-871).
// Unlike the GM version (ephemeral React state), this drives from the sheet
// via `current` + `onChange` so adjustments persist across character switches.
// R-SSC-1′ (b) (D-257): the threshold stands alone with its mark (`whyDir`); the key and its meter are the press (`onWhy`, the
// threshold's why-sheet), the − and + steppers outside it. Without `onWhy` the key is a plain label, as before.
function PlayerTrackerBox({ label, threshold, current, onChange, accent, whyDir, onWhy, whyOpen }) {
  var cur = Number(current) || 0;
  var thr = Number(threshold) || 0;
  // B-610 — THE PAPER TRACKER (the board's `.sb-vitrow`, SHEET_SHAPE §6.1 + §11's inks): the same
  // control — the same onChange, the same clamp, the same aria labels — drawn as the lab's row. The
  // three states are MARKS (a rule at PRESSED, a reversal at DOWN) with the inks on top: CLEAR below
  // half the threshold, PRESSED at or past half, DOWN past the threshold — past, not at: "when a PC
  // suffers wounds GREATER THAN his wound threshold" (EotE CRB p.215; FaD CRB p.222). The word ships
  // with the mark (WCAG 1.4.1) and it is the book's word, Incapacitated, on both tracks.
  var pct = thr > 0 ? Math.max(0, Math.min(100, Math.round(cur * 100 / thr))) : 0;
  var st = !thr ? 'clear' : (cur > thr ? 'down' : (cur >= thr / 2 ? 'pressed' : 'clear'));
  var word = String(label || '').charAt(0) + String(label || '').slice(1).toLowerCase();
  return (
    <div className="sb-vitrow" data-state={st} data-track={String(label || '').toLowerCase()} data-testid={'tracker-' + String(label || '').toLowerCase()}>
      <div {...(onWhy ? sheetWhyPress(sheetWhyLabel((word === 'Wounds' ? 'Wound' : word) + ' threshold', thr, whyDir), onWhy, whyOpen) : {})}
        style={onWhy ? Object.assign({ display: 'flex', flexDirection: 'column', justifyContent: 'center' }, SHEET_WHY_PRESS_STYLE) : undefined}>
        <div className="sb-vitkey">{word}</div>
        <div className="sb-meter"><i style={{ width: pct + '%' }} /></div>
        {st === 'down' ? <div className="sb-vitword">Incapacitated</div> : null}
      </div>
      <div className="sb-trk">
        <button type="button" className="sb-trk-btn" aria-label={'Decrease ' + String(label || '').toLowerCase()} onClick={function () { onChange(Math.max(0, cur - 1)); }}>{'\u2212'}</button>
        <span className="sb-vitval sb-trk-val">{cur}<span className="sb-of"> / {thr}</span><SheetWhyMark dir={whyDir} /></span>
        <button type="button" className="sb-trk-btn" aria-label={'Increase ' + String(label || '').toLowerCase()} onClick={function () { onChange(Math.min(thr * 2, cur + 1)); }}>+</button>
      </div>
    </div>
  );
}

// Talent list block — ported from gm.html TalentBlock. Renders a list of
// { name, description, activation?, source? } with a left-accent rule on
// descriptions. Used for talents, species ability, force powers, abilities.
function TalentList({ items, accent }) {
  if (!items || items.length === 0) return null;
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
    items.map(function(t, i) {
      var nameStr = typeof t === 'string' ? t : (t.name || 'Unnamed');
      // Canonical ranked talents carry a collapsed rank — render "Grit 2" (charbuild 3b)
      if (typeof t === 'object' && t && (t.rank || 1) > 1) nameStr += ' ' + t.rank;
      var desc = typeof t === 'string' ? '' : (t.description || '');
      // Veteran review fix: prefer raw (markup) description so dice tokens render.
      var descRaw = typeof t === 'object' && t ? (t.descriptionRaw || t.description) : desc;
      var activation = typeof t === 'object' && t && t.activation ? t.activation : null;
      return h('div', { key: i },
        h('div', { style: {
          fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-sm)',
          color: '#fff', marginBottom: 2,
        }},
          nameStr,
          activation ? h('span', { style: {
            marginLeft: 8,
            fontFamily: 'var(--font-body)', fontWeight: 400, fontSize: 'var(--fs-xs)',
            color: 'rgba(255,255,255,0.3)',
          }}, formatActivation(activation).toUpperCase()) : null
        ),
        desc ? h('div', { style: {
          fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.55,
          color: 'rgba(255,255,255,0.55)',
          paddingLeft: 10, borderLeft: '2px solid ' + accent + '22',
        }}, glyphs(cleanMarkup(descRaw))) : null
      );
    })
  );
}

// (M1: dead CombatTrackerBox removed — R-FPA5 player-half fold-in; zero JSX usage, grep-proven.)

// Pill used for skills in the trained-only grid view.
function SkillPill({ label, rank, accent }) {
  return h('span', { style: {
    padding: '4px 10px',
    background: accent + '10',
    border: '1px solid ' + accent + '33',
    borderRadius: 12,
    fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)',
    letterSpacing: '0.08em',
    color: 'rgba(255,255,255,0.75)',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  }},
    label,
    rank > 0 ? h('span', { style: { color: accent, fontWeight: 700 } }, '\u2022 ' + rank) : null
  );
}


// ── Page 0: Stats (with identity card) ──
// ── Page 0: COMBAT — runtime state the player adjusts mid-session ──
// ── THE hub's weapon row. The ONE weapon dice-pool builder is SWRPGSheetPool.weaponPoolOf (lib/shared/sheetPool.js, ruling
// f100-surfacing-sheet-concept R-SSC-4 + ADDENDUM 10 C3 (a)): the hub row and the sheet's WEAPONS card both read it, so the
// card's dice equal the row's. This header stays (30 readers slice it by name) and resolves the skill with the app's one
// resolver below. `derived` is unread (kept for the header). `arguments[7]` = the hub's DIFFICULTY chip; `arguments[8]` = the
// session's committed Force set (ruling `f100-surfacing-force-flip-card` ADDENDUM 12 F2 (i): a committed Control's characteristic).
function resolveWeaponSkillKey(rawSkill) {
  if (!rawSkill) return '';
  var norm = OGG_TO_SHEET_SKILL[rawSkill] || rawSkill;
  if (SKILL_NAMES[norm]) return norm;
  var target = String(rawSkill).toLowerCase().trim();
  var keys = Object.keys(SKILL_NAMES);
  for (var i = 0; i < keys.length; i++) { if (SKILL_NAMES[keys[i]].toLowerCase() === target) return keys[i]; }
  var stripped = target.replace(/[^a-z]/g, '');
  for (var j = 0; j < keys.length; j++) { if (SKILL_NAMES[keys[j]].toLowerCase().replace(/[^a-z]/g, '') === stripped) return keys[j]; }
  return '';
}

/** The display half for this check: relevant, NOT applied, each carrying its reason. `characteristic` (optional, F-100 M8
 *  B-1065) is the one the check rolls, so a characteristic-anchored entry is reminded where it would also apply. */
function poolModRemindersFor(mods, skillKeyOrId, characteristic) {
  var P = window.SWRPGPoolMods, idx = poolModSkillIndex();
  if (!P || !idx || !mods || !mods.length) return [];
  var ctx = P.contextFor(skillKeyOrId, idx, characteristic);
  if (!ctx) return [];
  return P.reminders(mods, ctx);
}

function computeWeaponPool(sheet, weapon, targetBand, derived, accent, resolvedEquipment, taken) {
  return window.SWRPGSheetPool.weaponPoolOf(sheet, weapon, { skillKey: resolveWeaponSkillKey(weapon.skillKey || weapon.skill || ''), band: targetBand,
    equipment: resolvedEquipment, taken: taken, difficultySet: arguments[7], accent: accent, forceCommitted: arguments[8] });
}

// F-100 M8 step 3 (frame R2): the next Critical Injury roll's modifiers in words, from the one core (SWRPGPoolMods.critRoll,
// N6's order: every +10, then the reductions, then the floor). → { words: '+10 −10', reductions: ['−10 Durable, after
// every +10 (never below 1)'], parts: ['1 injury already suffered +10'] }. The crit table's button (CritTableRef) prints the
// same words from the same call; `parts` (the additions, in the core's own label) open the crit plate's why-sheet (D-257).
function sheetCritNext(sheet, critCount) {
  var P = window.SWRPGPoolMods, plus = critCount * 10, minus = 0, reductions = [], parts = [];
  if (P && P.critRoll && P.rollNumbersAt) {
    var r = P.critRoll(0, critCount, P.rollNumbersAt(collectRollNumbers(sheet), 'crit_roll_delta', { moment: 'critical_injury', sheet: sheet }));
    plus = 0;
    r.lines.forEach(function (l) {
      if (l.amount > 0) { plus += l.amount; parts.push(l.label + ' +' + l.amount); }
      else { minus += l.amount; reductions.push('−' + Math.abs(l.amount) + ' ' + l.label + ', after every +10 (never below ' + (l.floor != null ? l.floor : 1) + ')'); }
    });
  }
  return { words: '+' + plus + (minus ? ' −' + Math.abs(minus) : ''), reductions: reductions, parts: parts };
}

// THE MEDICINE CHECK on a critical injury — ONE function: the pool the crit plate's TREAT has always built, now rolled
// in the dice hub's treat section (B-1209 retire, D-172; card `paper-n7-b1209-rollwindow-lab-card` ADDENDUM 2 C-3).
// The injury's difficulty enters the spec `withPoolMods` folds, so an effect that removes difficulty (the shipped
// `gear/antishock_blanket`) still lands; nothing steps the difficulty raw afterwards (B-848's trap).
// B-1263 (card `paper-n7-b1263-build-card` C-1, D-187): the fold takes the HUB's one `poolMods` with this row's ticked offers
// TAKEN (SWRPGPoolMods.takeOffers), exactly as a skill row's spec does — no collect of its own, so the treat row's offers and
// its pool read the same list. `characteristic` is the one the treat roll rolls (Intellect), for the row's offers and told lines.
// → { difficulty: the injury's own (its dice, else its tier, else 2), tier: its tier word or '', characteristic, spec: the folded pool }
// B-1586: the skill a character treats its own injury with — Mechanics for a droid (EotE p.220), Medicine otherwise.
// The droid test is B-1606's one predicate over the sheet's species id — and, for a save with no id, its species name
// (B-1612: a legacy "Droid" save, which R-MRN-2 never stamps). Read by critTreatSpec and by the crit plate's
// button (ADDENDUM 300 F1), so the button names the skill the row it opens rolls. → { key: 'MECH'|'MED', name, droid }
function critTreatSkill(sheet) {
  var droid = !!(window.CharResolve && window.CharResolve.isDroidSpecies && window.CharResolve.isDroidSpecies((sheet || {}).speciesId, (sheet || {}).species));
  var key = droid ? 'MECH' : 'MED';
  return { key: key, name: (SKILL_NAMES && SKILL_NAMES[key]) || (droid ? 'Mechanics' : 'Medicine'), droid: droid };
}
function critTreatSpec(sheet, c, accent, poolMods, taken) {
  var diffDice = c.difficulty_dice || ({Easy:1, Average:2, Hard:3, Daunting:4})[c.severityTier] || 2;
  var medChars = sheet.characteristics || {};
  var medSkills = sheet.skills || {};
  var intVal = Number(medChars.intellect) || 0;
  // B-1586 (D-411 item 29; ruling release-r7-bug-burndown ADDENDA 281, 283, 293): a droid is repaired with Mechanics, not
  // Medicine, and "a droid using Mechanics to heal himself only increases the difficulty by one" (EotE p.220). The droid
  // test is the one B-1606 wrote (`CharResolve.isDroidSpecies`), through critTreatSkill.
  var tsk = critTreatSkill(sheet), droid = tsk.droid, sk = tsk.key;
  var medRank = 0;
  Object.keys(medSkills).forEach(function(k) { if ((k || '').toUpperCase() === sk) medRank = Number(medSkills[k]) || 0; });
  var mods = taken && window.SWRPGPoolMods && window.SWRPGPoolMods.takeOffers ? window.SWRPGPoolMods.takeOffers(poolMods, taken) : poolMods;
  // R-B1263B-1: the fold reads the characteristic the row's offers are listed by, as a skill row passes `ch`
  var ch = 'intellect';
  // D-406 item 22 (B-1273; ruling release-r7-bug-burndown ADDENDUM 200 / 202 pick 1): the treat row always rolls the
  // character's own Medicine on the character's own injury, and treating yourself adds two difficulty (EotE p.219, Medical
  // Care; p.113, Medicine). The +2 goes into the pool before the offers fold, and its reason is the row's first applied line,
  // in the hub's own sentence ("+2 difficulty — treating your own injury (EotE p.219)").
  // A droid's self-repair adds one, in the same line's idiom ("+1 difficulty — a droid repairing itself (EotE p.220)").
  var selfDiff = diffDice + (droid ? 1 : 2);
  var spec = withPoolMods({
    characteristic: intVal, skillRank: medRank, difficulty: selfDiff, accent: accent,
    skill: { key: sk, name: tsk.name },
  }, mods, sk, ch);
  spec = Object.assign({}, spec, { _poolModChanges: [{ die: 'difficulty', direction: 'add', from: diffDice, to: selfDiff,
    source: droid ? 'a droid repairing itself (EotE p.220)' : 'treating your own injury (EotE p.219)' }].concat(spec._poolModChanges || []) });
  // `skill` is the key the row rolls (MED, or MECH for a droid): the hub reads its offers and told lines by it
  return { difficulty: selfDiff, tier: c.severityTier || '', characteristic: ch, skill: sk, spec: spec };
}

// <SheetCrits> — critical injuries, ONE shared section with the FULL workflow (next-crit banner · rows
// with TREAT (Medicine check) + remove · add via crit table or manual · Healing & Recovery). Rendered on
// BOTH devices so the TREAT/HEAL CAPABILITY holds everywhere (ruling central-overview-density FORK B
// parity guard) — the desktop cockpit previously showed a read-only condensed list and could NOT treat.
function SheetCrits({ sheet, accent, onSheetChange, woundsOver, equipment, openHub }) {
  sheet = sheet || {};
  var crits = Array.isArray(sheet.criticalInjuries) ? sheet.criticalInjuries : [];
  var _addingCrit = React.useState(false), addingCrit = _addingCrit[0], setAddingCrit = _addingCrit[1];
  var _critDraft = React.useState({ severity: 10, name: '', effect: '' }), critDraft = _critDraft[0], setCritDraft = _critDraft[1];
  function patch(u) { if (onSheetChange) onSheetChange(u); }
  function addCrit() {
    var entry = { id: 'crit_' + Date.now(), severity: Number(critDraft.severity) || 10, name: critDraft.name || 'Unnamed Injury', effect: critDraft.effect || '' };
    patch({ criticalInjuries: crits.concat([entry]) });
    setCritDraft({ severity: 10, name: '', effect: '' });
    setAddingCrit(false);
  }
  function removeCrit(id) { patch({ criticalInjuries: crits.filter(function(c) { return c.id !== id; }) }); }
  // THE MEDICINE CHECK — opens the dice hub ON THIS INJURY (B-1209 retire, D-172): the hub's treat section rolls
  // `critTreatSpec`'s pool and a success writes the injury off at the roll, through the sheet's writer.
  function treatCrit(c) { openHub({ treat: c }); }
  // ADDENDUM 300 F1: the button names the skill the treat row rolls (Mechanics for a droid), the registry name at render
  var treatSkill = critTreatSkill(sheet);
  // B-610 — THE PAPER CRIT PLATE (the board's `.sb-crit`, SHEET_SHAPE §3.2 item 4 + the census's own
  // block): the count and the next roll's modifier (crits × 10, production's :12151), the nudge past
  // the threshold (inform, never gate), a row per injury with MEDICINE as the treat, the permanent
  // RECORD INJURY door (the front door Jason asked for from the table) — and, because the board did
  // not draw the recording state, production's own flow on the kit: the crit table's attach, the
  // manual entry, ADD / CANCEL, HEALING & RECOVERY. Same state, same handlers, same testids.
  var nudge = woundsOver ? <p className="sb-critnudge" role="status" aria-live="polite">Wounds are past the threshold — you may have taken a Critical Injury.</p> : null;
  // F-100 M8 step 3 (frame R2; R-M8K-18/19): "Next crit roll" reads the SAME core the crit table's roll does —
  // SWRPGPoolMods.critRoll over the owner's crit_roll_delta rows (Durable) — so the panel and the button agree.
  // R-SSC-1′ (e) (D-257): the figure stays on the plate with its mark — a reduction lowers it, ↓ — and IS the press; the
  // additions and the reductions open in the crit's why-sheet (sheet-why-crit), not under the plate
  var critNext = sheetCritNext(sheet, crits.length);
  var _cw = React.useState(null), critWhy = _cw[0], setCritWhy = _cw[1];   // the pressed figure, while its sheet is open
  var closeCritWhy = React.useCallback(function () { setCritWhy(null); }, []);
  var critDir = critNext.reductions.length ? 'down' : null;
  var critFig = critNext.words + ' to d100';
  var stat = crits.length > 0 ? (
    <React.Fragment>
      <div className="sb-critstat" role="status" aria-live="polite"><b>{'\u2715' + crits.length}</b>
        <span {...sheetWhyPress(sheetWhyLabel('Next crit roll', critFig, critDir), setCritWhy, !!critWhy)}
          style={Object.assign({ display: 'inline-flex', alignItems: 'center', columnGap: '.3em' }, SHEET_WHY_PRESS_STYLE)}>Next crit roll:<b data-m8="next">{critFig}<SheetWhyMark dir={critDir} /></b></span></div>
      {critWhy ? <M7PaperSheet anchor={critWhy} title="Next crit roll" meta={(sheet.name || '') + ' · ' + crits.length + (crits.length === 1 ? ' injury' : ' injuries')} onClose={closeCritWhy} kind="why" testid="sheet-why-crit">
        <SheetWhyBody whyKey="crit" parts={critNext.parts.map(function (t) { return { text: t }; })} rows={critNext.reductions} total={critFig} />
      </M7PaperSheet> : null}
    </React.Fragment>) : null;
  var rows = crits.map(function (c) {
    return (
      <div className="sb-critrow" key={c.id} data-testid="crit-row">
        <span className="sb-critsev">{String(c.severity)}</span>
        <span className="sb-critname">{c.name}{c.effect ? <span className="sb-critfx">{glyphs(cleanMarkup(c.effect))}</span> : null}</span>
        <span className="sb-critacts">
          {window.SWRPGDice && openHub ? <button type="button" className="sb-crittreat" aria-label={'Treat ' + c.name + ' with a ' + treatSkill.name + ' check'} onClick={function () { treatCrit(c); }}>{treatSkill.name}</button> : null}
          <button type="button" className="sb-critx" aria-label={'Remove ' + c.name} title="Remove injury" onClick={function () { removeCrit(c.id); }}>{'\u00d7'}</button>
        </span>
      </div>
    );
  });
  var form = addingCrit ? (
    <div className="sb-critform" data-testid="crit-form">
      {/* B-626: the crit table on paper — the kit's own (CritTableRef with `paper`), the rows as entries */}
      {CritTableRef ? <CritTableRef paper variant="character" accent={accent} critCount={crits.length} rollSources={collectRollNumbers(sheet)} sheet={sheet}
        onAttach={function (entry) { patch({ criticalInjuries: crits.concat([entry]) }); setAddingCrit(false); }} /> : null}
      <div className="sb-grp">Or enter it by hand</div>
      <div className="sb-critchips" role="group" aria-label="Severity">
        {[10, 20, 30, 40, 50].map(function (sv) {
          var active = critDraft.severity === sv;
          return <button type="button" key={sv} className="hp-quiet" aria-pressed={active} data-on={active ? '1' : '0'}
            onClick={function () { setCritDraft(Object.assign({}, critDraft, { severity: sv })); }}>{String(sv)}</button>;
        })}
      </div>
      <input className="sb-filter" data-testid="crit-name" value={critDraft.name} placeholder="Injury name (e.g. Knocked Senseless)" aria-label="Injury name"
        onChange={function (e) { setCritDraft(Object.assign({}, critDraft, { name: e.target.value })); }} />
      <input className="sb-filter" data-testid="crit-effect" value={critDraft.effect} placeholder="Effect" aria-label="Effect"
        onChange={function (e) { setCritDraft(Object.assign({}, critDraft, { effect: e.target.value })); }} />
      <div className="sb-critverbs">
        <button type="button" className="hp-commit" data-testid="crit-add" onClick={addCrit}>Add</button>
        <button type="button" className="hp-quiet" onClick={function () { setAddingCrit(false); }}>Cancel</button>
      </div>
    </div>) : null;
  var door = !addingCrit ? (
    <button type="button" className="sb-critdoor" data-testid="crit-record-door" onClick={function () { setAddingCrit(true); }}>{'\uFF0B Record injury'}</button>) : null;
  // B-1208: the sheet is paper, so its crit door opens the card's paper window (frames `recovery.sheet.critdoor.l2.*`).
  var recovery = (openRecoveryCard && !addingCrit) ? (
    <button type="button" className="hp-quiet sb-critrecover" onClick={function () { openRecoveryCard({ accent: '#4ECB71', paper: true }); }}>Healing &amp; recovery</button>) : null;
  return <div className="sb-crit" data-testid="sheet-crits">{nudge}{stat}{rows}{form}{door}{recovery}</div>;
}

// ══ THE DICE HUB (F-87) ══════════════════════════════════════════════════════════════════════
// Built from the approved drawing `Docs/mockups/dice-hub.html` @ `5628fbdb`; Jason's approval is on
// record in `Docs/gates/dice-hub-mockup.md` (APPROVAL section, @ `3a891bdc`) and IS, by name, the
// R-PMP §5.1 range-picker reversal. Card: `dice-hub-build-card` (C-DHB-*). Concept: D-DH-1..7.
//
// CONTRACT — ONE CORE (D-DH-2). No arithmetic is restated here. Weapon pools come from
// `computeWeaponPool`, skills from the cockpit's own {characteristic, skillRank, difficulty:2}
// shape, Force from `SheetForceZone`'s FR-minus-committed, every die count from
// `SWRPGDice.buildPool`, and results from `SWRPGDice.rollAndResolve` + `BasicResultPanel`. A hub
// pool that disagreed with a cockpit pool would be the defect this repo calls one-core.
//
// CONTRACT — THE APPLIED LINE CLAIMS ONLY WHAT THE APP COMPUTES (card §2.4). Range difficulty,
// over-encumbrance setback and Force commitment are stated as FACT because the cores produce them.
// EVERY item-sourced modifier renders as a dimmed verbatim REMINDER until B-222 certifies
// `pool_mods`' semantics — the Medpac included, which the approved page drew as APPLIED. The
// drawing is one data lap ahead of the build and the ruled scope outranks the drawn exemplar
// (C-MU-4); this comment is that divergence, disclosed rather than silent.
var DICE_HUB_BANDS = ['Engaged', 'Short', 'Medium', 'Long', 'Extreme'];

// F-100 M8 step 3 (frame R1; f100-m8-contracts R-M8K-27 R1): the talent damage the sheet's WEAPONS card shows, through the
// SAME core the hub's row reads (SWRPGPoolMods.rollNumbersAt, with computeWeaponPool's check context and pick reader) so
// one number never reads two ways. → { total, applied: [line], banded: [{ line, bands: ['Short', 'Engaged'] }] } —
// `applied` holds at every band (added beside the damage, never into it); a `range_band` row (R-M8K-26) is listed with its
// bands, nearest last, and never added: the card has no band. null before the vocabulary loads.
function sheetWeaponTalentDamage(sheet, w) {
  var P = window.SWRPGPoolMods, idx = poolModSkillIndex();
  var skKey = resolveWeaponSkillKey((w && (w.skillKey || w.skill)) || '');
  if (!P || !P.rollNumbersAt || !idx || !skKey) return null;
  var sc = sheetSkillCharacteristic(sheet, skKey);
  var ctx = P.contextFor(skKey, idx, (sc && sc.characteristic) || undefined), owned = collectRollNumbers(sheet);
  var pickFor = function (tid, pid) { var v = CharResolve.pickOf ? CharResolve.pickOf(sheet, '_talent', tid, pid) : undefined; return v == null ? null : v; };
  var base = P.rollNumbersAt(owned, 'damage_delta', { ctx: ctx, band: '', sheet: sheet, pickFor: pickFor });
  var banded = {}, order = [];
  DICE_HUB_BANDS.slice().reverse().forEach(function (b) {
    P.rollNumbersAt(owned, 'damage_delta', { ctx: ctx, band: b.toLowerCase(), sheet: sheet, pickFor: pickFor }).applied.forEach(function (l) {
      if (!Array.isArray(l.row && l.row.range_band)) return;
      var k = l.talentId + ':' + l.rowId;
      if (!banded[k]) { banded[k] = { line: l, bands: [] }; order.push(k); }
      banded[k].bands.push(b);
    });
  });
  // the Critical rating with the APPLIED crit_rating_delta rows (Iron Body, R-M8K-25: the row's weapon record), the hub's number
  var cctx = Object.assign({}, ctx); if (w && w.regId) cctx.weapon = w.regId;
  var critAt = P.rollNumbersAt(owned, 'crit_rating_delta', { ctx: cctx, band: '', sheet: sheet, pickFor: pickFor });
  var critSetAt = P.rollNumbersAt(owned, 'crit_rating_set', { ctx: cctx, band: '', sheet: sheet, pickFor: pickFor });   // F-100 M9 (R-M9S-4): DARK
  var critNum = Number(w && w.crit), critPrinted = isFinite(critNum) && critNum > 0 ? critNum : null;
  return { total: base.total, applied: base.applied, banded: order.map(function (k) { return banded[k]; }),
           critLines: critAt, critSetLines: critSetAt, critPrinted: critPrinted, critThreshold: P.critRatingWith ? P.critRatingWith(critPrinted, critAt, critSetAt) : critPrinted };
}

// ── THE HUB'S OWN pool_mods READERS ARE GONE (R-PCA-2 / R-BPH-2) ─────────────────────────────
// Four functions stood here — `diceHubPoolMods`, `poolModAnchoredTo`, `poolModUnanchored`,
// `diceHubReminders`. The first read `window._canonicalRegistries` DIRECTLY, beside the resolve
// path, under a comment admitting it ("the resolve path does not carry it through … flagged in the
// gate"). That flag is discharged here: `canonicalToStore` now carries `pool_mods`, so the hub
// asks the same assembler the cockpit and the skills list ask, through the same seam
// (`collectPoolMods` / `withPoolMods` / `poolModRemindersFor`). A field carried BESIDE the core
// instead of THROUGH it is the bolt-on the card forecloses by name; deleting these is that
// foreclosure landing, not a tidy-up.
//
// The OR-semantics reader moved WITH them — `matches()` in poolMods.js is now the only one, so the
// hub's three bucketing sites and the sheet's cannot drift apart.
function DiceHubStatusLine(props) {
  // APPLIED — stated as fact, because a core computed it.
  // F-100 M8 step 3 (item 23, AS-9 SCALE at 2560/3840): the sheet's WEAPONS card and injury panel draw these lines too, so
  // they grow by the sheet's unitless unit `--u-k` (sheet.css: 1 → 1.3 → 1.8). Outside the sheet's .sbwrap (the hub's
  // drawer) it is absent and reads 1, so the hub, and every width below 2300, draws exactly as before.
  var k = 'var(--u-k, 1)';
  // `m8` marks a line M8 composes ('applied'), so a suite reads M8's lines by its mark (item 23's OWN filter)
  return h('div', { className: 'dh-sl', 'data-m8': props.m8 || undefined, style: {
    display: 'flex', gap: 'calc(7px * ' + k + ')', alignItems: 'flex-start', marginTop: 'calc(5px * ' + k + ')',
    fontSize: 'calc(var(--fs-sm) * ' + k + ')', lineHeight: 1.35, color: props.tone } },
    h('span', { 'aria-hidden': 'true', style: { fontSize: 'calc(11px * ' + k + ')', lineHeight: 1.5, flexShrink: 0 } }, props.glyph || '▸'),
    h('span', null, props.children),
    // B-1451 (D-406 item 14): a line with a source opens its words through the roll options' own (i); absent, as before
    props.info && (window.SWRPGUI || {}).OfferInfo ? h('span', { style: window.SWRPGUI.InfoSlot }, h(window.SWRPGUI.OfferInfo, { info: props.info, offerKey: props.infoKey })) : null);
}
// B-1451: the (i)'s box for a line whose owner is a talent or a species ability (`talentId`, `rowId`), or null — each reader
// calls SWRPGUI.offerInfo inline (the hub's readers are sliced by name), this one is the sheet card's
function diceHubLineInfo(talentId, rowId, tal) {
  var UI = window.SWRPGUI || {};
  return talentId && UI.offerInfo ? UI.offerInfo(talentId, rowId, tal) : null;
}

/**
 * The APPLIED item lines — the F-87 fence lifting (R-PCA-4 / R-BPH-4).
 *
 * D-DH-3 badged EVERY item-sourced modifier "not applied" because the data could not be trusted to
 * say which way it pointed or whether it had a condition. Its own premise was "until B-222
 * certifies"; that certification has landed, so an entry the walk settled as unconditional now
 * states its dice as FACT — the same class as the range difficulty and the encumbrance setback
 * beside it, and exactly what the approved drawing showed the Medpac doing (C-MU-4's designed
 * expiry). Keeping the badge here would be the new falsehood.
 *
 * The number comes from the assembler's own change record — from → to, floored — never from the
 * entry's raw `count`, so a 'remove' that found no die to remove reports nothing rather than
 * claiming a reduction that did not happen.
 */
function DiceHubAppliedLines(spec, checkName, qualityMods, sheet) {
  // F-100 M8 step 3: a TICKED offer's change is the offer's own (its tick shows it), never a second "applied" line; an
  // automatic symbol (C3) reads "+1 advantage on every Coercion check — Fearsome Rep" (frame R5).
  // B-219 (R-B219C-9): a WEAPON QUALITY's symbol is this weapon's, not every check's — "+1 advantage — Superior", the
  // shared sentence with no check name (its existing optional argument). `qualityMods` = computeWeaponPool's quality rows.
  var changes = ((spec && spec._poolModChanges) || []).filter(function (c) { return !c.taken; });
  var words = (window.SWRPGUI || {}).appliedLineText;   // the ONE sentence (lib/shared/ui.jsx), shared with the Yard's seat card
  var fromQuality = {};
  (qualityMods || []).forEach(function (m) { if (m && m.source) fromQuality[m.source] = true; });
  var UI = window.SWRPGUI || {}, tal = changes.length && UI.offerTalentsFor ? UI.offerTalentsFor(sheet || null, changes) : null;   // B-1451: the owner's view
  return changes.map(function (c, k) {
    // B-1590 (D-411 item 1): an item's line opens its item's words, as a talent's opens its talent's (SWRPGUI.offerInfoOf)
    var info = UI.offerInfoOf ? UI.offerInfoOf(c.talentId, c.rowId, c.item, tal) : null, ik = c.talentId ? c.talentId + ':' + c.rowId : info ? info.id : undefined;
    if (c.symbol) return h(DiceHubStatusLine, { key: 'pm' + k, m8: 'applied', tone: 'var(--text-info)', info: info, infoKey: ik }, words(c, fromQuality[c.source] ? undefined : checkName));
    var helps = (c.die === 'boost') ? c.direction === 'add' : c.direction === 'remove';
    return h(DiceHubStatusLine, { key: 'pm' + k, m8: 'applied', tone: helps ? 'var(--text-info)' : 'var(--text-caution)', info: info, infoKey: ik }, words(c, checkName));
  });
}

function DiceHubReminder(props) {
  var m = props.mod;
  // DISPLAYED, never applied (D-DH-3). Non-interactive to assistive tech as drawn: the whole row is
  // one presentational span, so a screen reader cannot mistake a reminder for a control.
  return h('div', { className: 'dh-rem', role: 'presentation', style: {
    display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 5,
    fontSize: 'var(--fs-sm)', lineHeight: 1.35, color: 'var(--text-muted)' } },
    h('span', { 'aria-hidden': 'true', style: { fontSize: 11, lineHeight: 1.5, flexShrink: 0 } }, '▫'),
    h('span', null,
      h('span', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text-primary)' } },
        m.count + ' ' + m.die),
      ' — ' + (m.source || ''),
      m.condition ? h('span', { style: { fontStyle: 'italic' } }, ' “' + m.condition + '”') : null,
      h('span', { style: { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.09em',
        color: 'var(--text-warning)', border: '1px solid rgba(255,138,61,.45)', borderRadius: 5,
        padding: '1px 5px', marginLeft: 6, whiteSpace: 'nowrap' } }, "GM's call")),   // B-1456 (D-406 item 11): why it has no tick
    props.info && (window.SWRPGUI || {}).OfferInfo ? h('span', { style: window.SWRPGUI.InfoSlot }, h(window.SWRPGUI.OfferInfo, { info: props.info, offerKey: m.key })) : null);   // B-1451
}

// ── F-100 M8 step 3 · the frames' lines on the hub (Docs/mockups/f100-m8-offers.html, approved D-26; f100-m8-build-card
// R-M8B-11..13, R-M8K-26/28). Every line below READS a core: the roll numbers from SWRPGPoolMods.rollNumbersAt (through
// computeWeaponPool's `damageLines`), the dice offers from SWRPGPoolMods.reminders, the fold from the one `augment`. The
// hub decides nothing; it draws, and holds a row's ticks for ONE roll (R-M8B-12 (a)), never written anywhere.
// a weapon's damage as the hub prints it: the resolved number, Brawn added where the weapon's damage is Brawn-based
// (ItemResolve.weaponDamage, the one brawn-add). The sheet's entry prints the table's notation — "+N" for a Brawn-based
// weapon (lib/items.js), N for a flat one — and the card keeps it (ADDENDUM 4 flag 1: two notations, one core).
function diceHubWeaponDamage(sheet, w) {
  var IR = window.ItemResolve, raw = w && w.damage, dt = IR.damageTextValue(raw), v = dt.value;   // B-1429: "−1" is Brawn too
  if (raw == null || raw === '' || v == null) return null;
  return IR.weaponDamage({ damage: { value: v, base: dt.base } }, sheet).total;
}
// the "+N on one hit" beside the damage: the APPLIED damage rows, plus an offered one only while it is ticked
function diceHubDamagePlus(lines, taken) {
  var P = window.SWRPGPoolMods, n = (lines && lines.total) || 0;
  // a number that RIDES a target tick (`tickedBy`) is never added into the hit: diceHubDamageAs words it (D-294 L2)
  ((lines && lines.offered) || []).forEach(function (l) { if (!l.tickedBy && taken && P && taken[P.offerKey({ talentId: l.talentId, id: l.rowId })]) n += l.amount; });
  return n;
}
// F-100 THE SURFACING MISSION (D-294 L2; card f100-surfacing-roll-options-card ADDENDUM 8 F6): with a talent's TARGET tick
// taken, the damage line reads "as strain", and each number riding that tick its amount and skill — "as strain (+1,
// Medicine)" — beside the damage, never in it. `offers` = SWRPGQualityRows.talentTargetOffers at the row; `lines` its
// damageLines; `taken` the row's ticks. Nothing ticked → null.
function diceHubDamageAs(offers, lines, taken) {
  var on = (offers || []).filter(function (o) { return taken && taken[o.key]; });
  if (!on.length) return null;
  var plus = [];
  on.forEach(function (o) {
    ((lines && lines.offered) || []).forEach(function (l) {
      if (l.tickedBy !== o.rowId || l.talentId !== o.talentId) return;
      var a = (l.row || {}).amount, sk = a && typeof a === 'object' && a.of === 'skill_ranks' ? (SKILL_NAMES[a.key] || a.key) : null;
      plus.push('+' + l.amount + (sk ? ', ' + sk : ''));
    });
  });
  return 'as strain' + (plus.length ? ' (' + plus.join('; ') + ')' : '');
}
// F-100 THE SURFACING MISSION (D-294 L3; ADDENDUM 8 F12): the reroll — `doRoll` itself, ONCE, over the pool the row ROLLED
// (`prev.spec`); the patch carries the ticks taken at the first roll (so a mode or a target tick still echoes) and names who
// rerolled it, so the row lists no second offer. The limit is shown, never tracked: the next fresh roll offers it again.
function diceHubReroll(doRoll, key, prev, name) {
  doRoll(key, prev.spec);
  return { taken: prev.taken || null, rerolledBy: name };
}
// D-352 (card f100-surfacing-roll-options-card ADDENDUM 67; D-350's constraint, ADDENDUM 64's design): ONE Reroll per hub,
// on the LATEST rolled result only, ON THE RESULT'S LINE right after the result box. The rerolls a rolled row offers come from
// SWRPGPoolMods.rerollsAt (DARK OFF: [] with the key OFF, so nothing draws). The model, pure:
//   done   — the result was rerolled: "Rerolled — <talent>" in the button's place (older results keep their mark);
//   one    — one talent qualifies: the Reroll button, then the talent's name with its (i) (the approved L3, moved);
//   choice — two or more qualify: ONE Reroll button that opens the talent choice (each with its (i));
//   null   — not rolled, not the latest result, or nothing qualifies.
function diceHubRerollModel(ro, rerolls, latest) {
  if (!ro) return null;
  if (ro.rerolledBy) return { kind: 'done', name: ro.rerolledBy };
  if (!latest || !(rerolls || []).length) return null;
  return { kind: rerolls.length === 1 ? 'one' : 'choice', rows: rerolls };
}
function diceHubRerollSlot(ro, rerolls, latest, onReroll) {
  var m = diceHubRerollModel(ro, rerolls, latest);
  return m ? h(DiceHubReroll, { model: m, onReroll: onReroll }) : null;
}
function diceHubRerollInfo(r) {
  var UI = window.SWRPGUI || {};
  return UI.offerInfo ? UI.offerInfo(r.talentId, r.id, null, { cost: UI.offerTags ? UI.offerTags(Object.assign({}, r, { limit: null })) : [] }) : null;
}
// the slot itself: the choice is a usePopover menu (Esc on the ONE stack, an outside press, Back), portalled to the body as
// the info box is (a fixed menu inside the hub's window would be clipped by it). THE BACK FENCE (ADDENDUM 67, R-UBC1 hazard 2):
// a talent's box opened INSIDE the choice takes no Back entry (OfferInfo `noBack`) — its close would pop the entry the
// choice's own Back listens for, and the choice would close with it.
function DiceHubReroll(props) {
  var m = props.model, UI = window.SWRPGUI || {};
  var _o = React.useState(false), open = _o[0], setOpen = _o[1];
  var _p = React.useState(null), pos = _p[0], setPos = _p[1];
  var btnRef = React.useRef(null), popRef = React.useRef(null);
  (UI.usePopover || function () {})(open, function () { setOpen(false); }, btnRef, popRef);
  React.useLayoutEffect(function () {
    if (!open) { if (pos) setPos(null); return; }
    var b = btnRef.current, p = popRef.current; if (!b || !p) return;
    var br = b.getBoundingClientRect(), pr = p.getBoundingClientRect(), W = document.documentElement.clientWidth || window.innerWidth, H = window.innerHeight, G = 8;
    var top = br.bottom + 6; if (top + pr.height > H - G && br.top - 6 - pr.height >= G) top = br.top - 6 - pr.height;
    var next = { left: Math.max(G, Math.min(W - G - pr.width, br.left)), top: top };
    if (!pos || pos.left !== next.left || pos.top !== next.top) setPos(next);
  }, [open, pos]);
  var nameStyle = { fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 15, lineHeight: 1.3, color: 'var(--doc-ink)' };
  if (m.kind === 'done') return h('span', { 'data-testid': 'dice-hub-rerolled', 'data-m8': 'applied', className: 'dh-sl',
    style: { fontSize: 'var(--fs-sm)', lineHeight: 1.35, color: 'var(--text-info)' } }, 'Rerolled — ' + m.name);
  if (m.kind === 'one') {
    var r = m.rows[0], info = diceHubRerollInfo(r);
    return h('span', { className: 'm8-offer sb-dh-reroll', 'data-dh-reroll': 'one', 'data-offer-key': r.key || '', style: { display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 } },
      h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'dice-hub-reroll', 'aria-label': 'Reroll — ' + r.source, onClick: function () { props.onReroll(r.source); } }, 'Reroll'),
      h('b', { style: nameStyle }, r.source),
      info && UI.OfferInfo ? h(UI.OfferInfo, { info: info, offerKey: r.key }) : null);
  }
  var RD = window.ReactDOM, menu = open ? h('div', { ref: popRef, role: 'menu', 'aria-label': 'Which talent rerolls this check', 'data-testid': 'dice-hub-reroll-choice',
      'data-doc-ground': 'creator', style: { position: 'fixed', zIndex: 9400, left: pos ? pos.left : 0, top: pos ? pos.top : 0, visibility: pos ? 'visible' : 'hidden',
        width: 'min(320px, calc(100vw - 16px))', boxSizing: 'border-box', padding: '4px 0 4px 6px', background: 'var(--doc-paper-raised)', color: 'var(--doc-ink)',
        border: '1px solid var(--doc-rule)', borderRadius: 8, boxShadow: '0 10px 28px rgba(0,0,0,.22)' } },
    m.rows.map(function (r) {
      var info = diceHubRerollInfo(r);
      return h('div', { key: r.key, className: 'm8-offer', 'data-offer-key': r.key || '', style: { display: 'flex', alignItems: 'center' } },
        h('button', { type: 'button', role: 'menuitem', 'data-testid': 'dice-hub-reroll-pick', onClick: function () { setOpen(false); props.onReroll(r.source); },
          style: { flex: '1 1 auto', minWidth: 0, minHeight: 44, textAlign: 'left', background: 'none', border: 0, padding: '0 8px', cursor: 'pointer' } },
          h('b', { style: nameStyle }, r.source)),
        info && UI.OfferInfo ? h(UI.OfferInfo, { info: info, offerKey: r.key, noBack: true }) : null);
    })) : null;
  return h('span', { className: 'sb-dh-reroll', 'data-dh-reroll': 'choice', style: { display: 'inline-flex', alignItems: 'center' } },
    h('button', { ref: btnRef, type: 'button', className: 'hp-quiet', 'data-testid': 'dice-hub-reroll', 'aria-haspopup': 'menu', 'aria-expanded': open ? 'true' : 'false',
      'aria-label': 'Reroll — choose a talent', onClick: function () { setOpen(!open); } }, 'Reroll'),
    menu ? (RD && RD.createPortal && document.body ? RD.createPortal(menu, document.body) : menu) : null);
}
// one applied roll number's words: "+1 damage to one hit — Feral Strength", with its band (R-M8K-26) or its pick (R6)
function diceHubDamageLineText(l, band) {
  var r = l.row || {}, pk = r._pick, who = l.source;
  if (pk) who += ', ' + (SKILL_NAMES[(window._canonicalCharbuild && window._canonicalCharbuild.skillSheetKeys || {})[pk.value]] || pk.value) + (pk.per === 'rank' ? ' (rank ' + pk.rank + ')' : '');
  if (Array.isArray(r.range_band) && band) who += ' (' + band + ' range)';
  return (l.amount < 0 ? '−' : '+') + Math.abs(l.amount) + ' damage to one hit — ' + who;
}
// the APPLIED crit_rating_delta rows' line: "Crit 5 → 4 — Iron Body" (frame F1, the Unarmed row and the WEAPONS unit;
// NOT BUILT at step 3, drawn at B2 with the member, f100-m8-build-card R-M8B-23). `at` = rollNumbersAt's result; the
// numbers are the printed rating and SWRPGPoolMods.critRatingWith's (never below the floor). Nothing applied → null.
function diceHubCritLineText(printed, now, at) {
  var ap = at && at.applied ? at.applied.filter(function (l) { return l.amount; }) : [];
  if (!ap.length || printed == null || now == null) return null;
  return 'Crit ' + printed + ' → ' + now + ' — ' + ap.map(function (l) { return l.source; }).join(', ');
}
// the weapon row's ONE Crit line (the hub): a TICKED offered crit SET (`b1205:offered-crit-sets`) names itself first —
// "Crit 5 → 3 — Claws (Tarc)", H7's words — then any applied delta; otherwise diceHubCritLineText's (deltas only, so an
// APPLIED set such as the Trandoshan's draws no line, as M9 F4 has it). null = no line.
// `everySet` (the sheet's WEAPONS card, B-1316): every APPLIED set names itself — the card has no tick, and M9 F4 draws the
// Trandoshan's Unarmed card with "Crit 5 → 3 — Claws (Trandoshan)" (frame S3); the hub passes nothing and is unchanged.
function diceHubCritLine(spec, sheet, everySet) {
  var cst = ((spec && spec.critSetLines && spec.critSetLines.applied) || []).filter(function (l) { return everySet || l._taken; });
  if (!cst.length || spec.critPrinted == null || spec.critThreshold == null) return diceHubCritLineText(spec.critPrinted, spec.critThreshold, spec.critLines);
  var cb = (window.CharResolve && CharResolve.talentViewFor ? CharResolve.talentViewFor(sheet) : null) || window._canonicalCharbuild;
  return 'Crit ' + spec.critPrinted + ' → ' + spec.critThreshold + ' — ' + cst.map(function (l) { return diceHubRollOwnerName(l, cb); })
    .concat(((spec.critLines && spec.critLines.applied) || []).filter(function (l) { return l.amount; }).map(function (l) { return l.source; })).join(', ');
}
// F-100 M8 · a talent's changes to ONE item (Jury Rigged, frames J4–J7 @ d58a3bae, D-59), read from the display's
// `_talentEffects` (ItemResolve.applyCharacterEffects lists each change it made, {op, amount, source, talent, from, to[,
// target]}); never re-derived. Two ranks changing the same number on one item read as ONE line, first `from` → last `to`.
// `ops` narrows to the ops a surface prints; an unchanged number was never listed.
function m8ItemChanges(r, ops) {
  var by = {}, order = [];
  ((r && r._talentEffects) || []).forEach(function (f) {
    if (!f || (ops && ops.indexOf(f.op) === -1)) return;
    var k = f.op + ':' + (f.target || '') + ':' + f.talent;
    if (!by[k]) { by[k] = Object.assign({}, f); order.push(k); } else by[k].to = f.to;
  });
  return order.map(function (k) { return by[k]; });
}
// the hub's line under its damage and Crit (J4, Iron Body's form): "Crit 3 → 2 — Jury Rigged", "Damage 4 → 5 — Jury
// Rigged" — the damage as the hub prints it (Brawn added, diceHubWeaponDamage), the card keeps the table's "+1 → +2" (J5)
function m8WeaponChangeText(sheet, f, notation) {
  if (f.op === 'crit_delta') return 'Crit ' + f.from + ' → ' + f.to + ' — ' + f.source;
  if (f.op !== 'damage_delta') return null;
  var a = notation === 'card' ? f.from : diceHubWeaponDamage(sheet, { damage: f.from }), b = notation === 'card' ? f.to : diceHubWeaponDamage(sheet, { damage: f.to });
  return a == null || b == null ? null : 'Damage ' + a + ' → ' + b + ' — ' + f.source;
}
// B-219 (R-B219C-14 (2)): the damage the hub prints with a GRANTED quality's change on it (computeWeaponPool's
// `qualityDamage`), and that change's line in the item-change form — "Damage 6 → 7 — Superior". Native: nothing.
function diceHubQualityDamage(sheet, w, spec) {
  var base = diceHubWeaponDamage(sheet, w), lines = [], now = base;
  ((spec && spec.qualityDamage) || []).forEach(function (f) {
    if (now == null || !f.amount) return;
    lines.push('Damage ' + now + ' → ' + (now + f.amount) + ' — ' + f.source);
    now += f.amount;
  });
  return { damage: now, lines: lines };
}
function DiceHubDamage(props) {
  var mono = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.16em', color: 'var(--doc-ink-muted)', textTransform: 'uppercase' };
  var big = { fontFamily: 'var(--font-body)', fontSize: 15, letterSpacing: 0, color: 'var(--doc-ink)', textTransform: 'none', fontWeight: 600 };
  return h('div', { className: 'm8-dmg', 'data-m8': 'damage', style: Object.assign({}, mono, { marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline' }) },
    h('span', null, 'Damage ', h('b', { style: big }, props.damage == null ? '—' : String(props.damage)),
      props.plus ? h('span', { className: 'm8-plus', style: { fontFamily: 'var(--font-body)', fontSize: 15, letterSpacing: 0, color: 'var(--doc-action)', textTransform: 'none' } }, ' +' + props.plus + ' on one hit') : null,
      // D-294 L2: a taken target tick's words ("as strain (+1, Medicine)"), the .m8-plus idiom
      props.as ? h('span', { className: 'm8-plus', style: { fontFamily: 'var(--font-body)', fontSize: 15, letterSpacing: 0, color: 'var(--doc-action)', textTransform: 'none' } }, ' ' + props.as) : null),
    h('span', null, 'Crit ', h('b', { style: big }, critText(props.crit))));
}
// B-1499 (D-406 item 13): a rolled HIT prints its damage under DAMAGE — the weapon's damage plus each uncanceled success
// ("each uncanceled success adds +1 damage to a successful attack", EotE p.205): "Hit: 5 + 3 = 8 damage". A miss, an unrolled
// row or a weapon with no damage number draws nothing. Pure words, so a guard executes it.
function diceHubHitWords(damage, resolved) {
  var su = resolved && resolved.succeeded ? Number(resolved.success) || 0 : 0;
  if (!su || damage == null || isNaN(Number(damage))) return null;
  return 'Hit: ' + damage + ' + ' + su + ' = ' + (Number(damage) + su) + ' damage';
}
function diceHubHitDamage(damage, resolved) {
  var w = diceHubHitWords(damage, resolved);
  return w ? h('div', { 'data-testid': 'dice-hub-hit-damage', 'data-m8': 'hit', style: { marginTop: 6, fontFamily: 'var(--font-body)', fontSize: 15,
    fontWeight: 600, lineHeight: 1.35, color: 'var(--doc-ink)' } }, w) : null;
}
// an unmade pick a row could use: it adds nothing and asks (R-M8K-6 (a), frame R6)
function DiceHubPickPrompt(props) {
  var q = props.pick;
  return h('div', { className: 'm8-told', 'data-m8': 'prompt', 'data-pick': q.talentId + ':' + q.rank, style: {
    display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 8, fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.4, color: 'var(--doc-ink-muted)' } },
    h('span', { 'aria-hidden': 'true' }, '▫'),
    h('span', null, h('b', null, q.name + ', rank ' + q.rank + ' '), 'has no skill chosen yet, so it adds nothing.',
      props.onChoose ? h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'pick-prompt-choose', style: { marginTop: 6, display: 'block' },
        onClick: function () { props.onChoose(q); } }, 'Choose a skill') : null));
}
// the unmade picks a row reaches: the pick's pool is a skill group (the row's group) or a list of skill ids
function diceHubPromptsFor(unmade, ctx) {
  if (!ctx) return [];
  return (unmade || []).filter(function (q) {
    var pool = q.pool;
    if (!pool || pool === 'any') return true;
    if (Array.isArray(pool)) return pool.indexOf(ctx.skillId) !== -1;
    return ctx.group === pool;
  });
}
// a row's offers: every certified reminder is an OFFER (R-M8K-28); an uncertified one stays the "GM's call" reminder (B-1456),
// since it has no direction to fold. Offered roll numbers (a cost or a condition) are offers too.
function diceHubOfferEls(rowKey, reminders, rollOffered, taken, toggle, sheet) {
  var UI = window.SWRPGUI || {}, P = window.SWRPGPoolMods;
  var cb = (sheet && window.CharResolve && CharResolve.talentViewFor ? CharResolve.talentViewFor(sheet) : null) || window._canonicalCharbuild;
  // the owner's lookup is the ONE shared function (SWRPGUI.offerTalentsFor, R-SYC-3 / D-SYF2 (a)): the sheet's stamp view first
  var tal = UI.offerTalentsFor ? UI.offerTalentsFor(sheet, (reminders || []).concat(rollOffered || [])) : null;
  var out = [];
  (reminders || []).forEach(function (m, k) {
    // B-1590 (D-411 item 1): an item-owned reminder or offer opens its item's words (SWRPGUI.offerInfoOf); B-1609 (D-412
    // item 5): a weapon quality's, its quality's registry words
    if (m.reason === 'uncertified' || !UI.OfferRow || !UI.offerOf) { out.push(h(DiceHubReminder, { key: 'r' + k, mod: m, info: UI.offerInfoOf ? UI.offerInfoOf(m.talentId, m.id, m.item, tal, undefined, m.quality) : null })); return; }
    var o = UI.offerOf(m, tal);
    out.push(h(UI.OfferRow, { key: 'o' + k, offerKey: o.key, name: o.name, text: o.text, tags: o.tags, on: !!(taken && taken[o.key]),
      onToggle: function () { toggle(rowKey, o.key); },
      // 5h (brief §4/§5): the who tag and the (i) — null with the key OFF (offerOf itself is unchanged: a dozen suites slice it alone)
      who: UI.offerWho ? UI.offerWho(m) : null, info: UI.offerInfoOf ? UI.offerInfoOf(m.talentId, m.id, m.item, tal, { cost: UI.offerTags(Object.assign({}, m, { limit: null }), tal) }, m.quality) : null }));
  });
  // the offered ROLL NUMBERS: a damage row ("+1 damage to one hit") and a species crit SET ("Crit → 3 “When using claws”",
  // `b1205:offered-crit-sets`) — the SET through offerOf, so its condition is its ability's own words
  (rollOffered || []).forEach(function (l, k) {
    if (!UI.OfferRow || !P || l.tickedBy) return;   // a number riding a target tick draws no tick of its own (ADDENDUM 8 F6)
    var key = P.offerKey({ talentId: l.talentId, id: l.rowId });
    // B-1455 (D-406 item 10): the damage row quotes its condition as its crit SET sibling does — both through offerOf
    var text = (l.op === 'crit_rating_set' || l.op === 'damage_delta') && UI.offerOf
      ? UI.offerOf({ key: key, op: l.op, amount: l.amount, talentId: l.talentId, condition: (l.row || {}).condition, source: l.source }, tal).text
      : '+' + l.amount + ' damage to one hit';
    out.push(h(UI.OfferRow, { key: 'n' + k, offerKey: key, name: diceHubRollOwnerName(l, cb), text: text,
      tags: UI.offerTags ? UI.offerTags(l.row || {}) : [], on: !!(taken && taken[key]), onToggle: function () { toggle(rowKey, key); },
      who: UI.offerWho ? UI.offerWho(l.row) : null,   // 5h: null with the key OFF
      info: UI.offerInfo ? UI.offerInfo(l.talentId, l.rowId, tal, { cost: UI.offerTags(Object.assign({}, l.row || {}, { limit: null })) }) : null }));
  });
  return out;
}
// a roll number's owner as a line names it: the talent's name, or a species ability's with its species — "Claws (Tarc)"
// (H7's words). `cb` = the view the sheet resolves through (its stamp view, else the discovery view).
function diceHubRollOwnerName(l, cb) {
  var m = /^species:([^:]+):/.exec(String((l && l.talentId) || '')), rec = m && cb && cb.species ? cb.species[m[1]] : null;
  return rec && rec.name ? l.source + ' (' + rec.name + ')' : l.source;
}
// the automatic symbols a pool carries (C3 `symbol`; frame R5), in the app's own result glyphs, after the dice
function diceHubAutoSymbols(dice) {
  var a = dice && dice._autoSymbols, D = window.SWRPGDice;
  if (!a || !D || !D.renderResults || !Object.keys(a).some(function (k) { return a[k] > 0; })) return null;
  var r = { success: 0, advantage: 0, triumph: 0, despair: 0, threat: 0, failure: 0, light: 0, dark: 0 };
  Object.keys(a).forEach(function (k) { if (r[k] != null) r[k] += a[k]; });
  return h('span', { 'data-m8': 'symbol', style: { display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6 } },
    h('span', { className: 'm8-plus', style: { color: 'var(--doc-action)' } }, '+'), D.renderResults(r, { size: 1 }));
}

// One rollable row: identity · pool · ROLL (or the result, rolled in place, with its reroll slot) · status lines.
function DiceHubRow(props) {
  var accent = props.accent, rolled = props.rolled;
  // B-1286 (hub frames ADDENDUM 5): while a result shows, the row draws the pool it ROLLED (`rolled.spec`, its ticks and
  // its chips included), never the pool the controls now describe — a lowered pool and its result never disagree
  var spec = (rolled && rolled.spec) || props.spec;
  var barred = !!(spec && spec.barred);
  var D = window.SWRPGDice;
  var dice = barred ? null : D.buildPool(spec);
  // D-352: the result box and the reroll slot as ONE unit on the row's line — the slot right after the box; where the pair
  // cannot fit (390) the slot wraps directly UNDER the box, still above the row's controls. No slot: the box alone, as before.
  var result = rolled ? h('span', { 'data-testid': 'dice-hub-result', className: 'sb-dh-result dh-result', role: 'img', 'aria-label': rolled.aria }, D.renderResults(rolled.resolved)) : null;
  if (result && props.reroll) result = h('span', { className: 'sb-dh-resline', 'data-dh-resline': '1',
    style: { display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 8, minWidth: 0 } }, result, props.reroll);
  // B-638 · the row on PAPER: an entry — the name, the pool's glyphs, ROLL as the board's chevron (the result in
  // its place once rolled, the refusal word when barred), the status lines beneath. Same spec, same roll.
  return h('div', { 'data-testid': props.testid, 'data-dh-row': props.rowKey, className: 'sb-e sb-dh-row', 'data-rolled': rolled ? '1' : '0' },
    h('div', { className: 'sb-dh-line' },
      h('span', { className: 'sb-e-n sb-dh-name' }, props.name),
      dice ? h('span', { className: 'sb-pool dh-pool', 'data-dh-pool': JSON.stringify(dice) }, D.renderPool(dice, { size: 1 })) : null,
      dice ? diceHubAutoSymbols(dice) : null,
      // R-SHC-9: a barred Force row says how many dice the session holds ("+3 held"), in the stack's glyph
      barred && spec.held ? h('span', { className: 'sb-pool-held', 'aria-label': spec.held + ' committed' }, '+' + spec.held + ' held') : null,
      rolled
        ? result
        : (barred
          ? h('span', { 'data-testid': 'dice-hub-barred', className: 'sb-refuse' }, spec.barredReason)
          : h('button', { type: 'button', 'data-testid': 'dice-hub-roll', className: 'sb-roll', 'aria-label': 'Roll ' + props.name, onClick: props.onRoll }, 'Roll'))),
    props.children);
}

// The overlay. Opens OVER the sheet, never routes, closes to the same scroll position (D-DH-6).
// B-638 · THE HUB IS A WINDOW YOU CAN MOVE (Jason 2026-09-16: "Can the dice hub window be able to be moved
// around the screen by the user?"). A pointer on the head drags it; the offset is clamped to the viewport and
// kept for the session; a touch drags it the same way (pointer events). The head is the only handle, so the
// rows' own presses never move the window.
function useDragWindow() {
  var _pos = React.useState(null), pos = _pos[0], setPos = _pos[1];   // null = where the shell put it
  var ref = React.useRef(null), drag = React.useRef(null);
  var onPointerDown = function (e) {
    if (e.button > 0) return;
    if (e.target.closest && e.target.closest('button, input, a')) return;
    var el = ref.current; if (!el) return;
    var r = el.getBoundingClientRect();
    drag.current = { x: e.clientX, y: e.clientY, left: r.left, top: r.top, w: r.width, h: r.height };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    e.preventDefault();
  };
  var onPointerMove = function (e) {
    var d = drag.current; if (!d) return;
    var left = d.left + (e.clientX - d.x), top = d.top + (e.clientY - d.y);
    var maxL = Math.max(0, window.innerWidth - d.w), maxT = Math.max(0, window.innerHeight - 44);
    setPos({ left: Math.min(maxL, Math.max(0, left)), top: Math.min(maxT, Math.max(0, top)) });
  };
  var onPointerUp = function (e) { drag.current = null; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {} };
  return { ref: ref, pos: pos, handle: { onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: onPointerUp } };
}

// One trained-skill row of the dice hub: the characteristic is THE shared answer (F-100 M7 C2, R-M7K-9/10 — best-of).
// Named and top-level so a guard can EXECUTE it (R-M7G-8); `DiceHub` maps its trained keys through it.
// → null before the vocabulary loads (R-M7G-9): the hub drops the row and draws it when the charbuild lands.
// THE FORCE SWITCH (ruling `f100-surfacing-force-flip-card` ADDENDUM 9 E-FFL-4, §2.6 of its brief): `arguments[2]` (optional) =
// the session's committed Force set (the header stays two-argument: four guards slice it by name) — `cv`, `rank` and `chars` (the `using` switch's map) read CharResolve's two committed
// readers, so a committed Control's characteristic or Farsight's Perception reads at the row as at the stage. Absent, or
// the key OFF, the sheet's own numbers, exactly as before.
function diceHubSkillRow(sheet, k) {
  var committed = arguments[2];
  var _sc = sheetSkillCharacteristic(sheet, k), prop = (_sc && _sc.characteristic) || '';   // F-100 M7 C2
  if (!_sc) return null;
  var CR = window.CharResolve, chars = sheet.characteristics || {}, rank = sheetSkillRank(sheet.skills, k);
  if (committed != null && CR && CR.characteristicsWithCommits) {
    var cbv = (CR.talentViewFor && CR.talentViewFor(sheet)) || window._canonicalCharbuild;
    chars = CR.characteristicsWithCommits(sheet, cbv, committed);
    rank = CR.skillRankWithCommits(sheet, cbv, committed, k, rank);
  }
  return { key: k, name: SKILL_NAMES[k], cv: chars[prop] || 0, rank: rank, sc: _sc, chars: chars };
}

// F-100 M8 B-1065 (ruling f100-m8-contracts R-M8K-2 (iii)) — a hub skill row's spec, named and top-level so a guard EXECUTES
// it (R-M7G-8). The row rolls the switch's characteristic for one roll (M7 W3), else the best-of; the item and talent dice
// RE-MATCH that characteristic, so an entry anchored to a characteristic follows the roll, not the vocabulary's base.
// `using` is the row's switch: a characteristic (M7 W3), or (F-100 M8 M2, f100-m8-batches R-M8BG-233) a SKILL SUBSTITUTION
// { skill, sheetKey, characteristic, talentId } — the substitute's ranks on `characteristic` for one roll.
function diceHubRolledCharacteristic(row, using) {
  if (using && typeof using === 'object') return using.characteristic;
  return using || (row && row.sc && row.sc.characteristic) || undefined;
}
function diceHubSkillSpec(sheet, row, poolMods, using, accent) {
  var sub = using && typeof using === 'object' ? using : null, mods = poolMods;
  if (sub) using = sub.characteristic;   // M2: from here the switch is the characteristic rolled, as in M7
  var ch = diceHubRolledCharacteristic(row, using);
  var cv = using ? (Number((row.chars || sheet.characteristics || {})[using]) || 0) : row.cv;   // the row's committed map (E-FFL-4)
  // M2: a pressed substitution rolls the substitute's ranks, and takes its talent's same-check offers with it (Natural
  // Remedy's +1 difficulty sibling, A5.4) — the line said so; they are not offered apart (diceHubSubstituteTalents)
  if (sub && sub.talentId && window.SWRPGPoolMods) {
    var taken = {};
    poolModRemindersFor(poolMods, row.key, ch).forEach(function (m) { if (m.talentId === sub.talentId && m.key) taken[m.key] = true; });
    mods = window.SWRPGPoolMods.takeOffers(poolMods, taken);
  }
  // the surfacing hub's DIFFICULTY chip (R-SHC-10): `arguments[5]` — the difficulty set for ONE roll; absent, Average (2)
  var dset = arguments[5];
  return withPoolMods({ characteristic: cv, skillRank: sub ? sheetSkillRank(sheet.skills, sub.sheetKey) : row.rank,
    difficulty: dset != null && isFinite(Number(dset)) ? Number(dset) : 2, accent: accent }, mods, row.key, ch);
}
// M2: the talents a row offers a skill substitution from — their same-check offers ride on the substitution's line
function diceHubSubstituteTalents(sc) {
  var out = {};
  ((sc && sc.substitutes) || []).forEach(function (u) { if (u.talentId) out[u.talentId] = true; });
  return out;
}
// F-100 M8 B-1061 (C2, R-M8K-2) — a hub Force row's spec, named and top-level so a guard EXECUTES it (R-M7G-8). The owner's
// auto-applying dice fold through the one fold with the pool builder's kind `force_power` and NO skill, so only a row
// anchored to `roll: force_power` reaches it (poolMods' roll-only context) — never an `all`/skill/characteristic entry.
// F-100 M10 (ruling f100-m10-shapes-card R-M10S-3, V4): `powerId` — the power this row rolls — rides on the roll-only
// context, so a row anchored to THAT power (`{scope: force_power, value}`) reaches it (DARK behind SWRPGForceRules).
// F-100 M10 B-1223 (R-M10S-11; Jason D-143, overruling the dice-hub drawing @ 5628fbdb on this row only): a Force power
// check is Force dice only, up to the Force rating less committed dice (FaD p.280) — no characteristic or skill die. A
// combined check is offered on the SKILL's own roll by the power's pool rows, never built here.
function diceHubForceSpec(sheet, poolMods, available, accent, powerId) {
  return withPoolMods({ characteristic: 0, skillRank: 0,
                        difficulty: 0, force: available, accent: accent }, poolMods, null, undefined, 'force_power', powerId);
}
// A hub weapon row's item reminders, asked about the characteristic its pool rolls — the best-of `computeWeaponPool`
// rolled — so the hub itself reads no characteristic (R-M7G-8's composers). B-1065.
// F-100 M8 step 3 (frame F6: "a TOLD row at its moment", Surgeon at the Medicine check; R-M8K-5): the owner's TOLD rows
// whose moment is a check and whose anchors this row's check matches (the ONE matcher, SWRPGPoolMods.matches) — the
// talent's own words at its span, SHOWN and never computed, with the hub's "not applied" tag (contracts flag 8). A told row
// with no anchor is told on the talent, not at a check. `characteristic` omitted (a weapon row): the sheet's own, read here
// as diceHubWeaponReminders reads it, so DiceHub reads no characteristic itself (f100_m7_composers S1).
function diceHubToldFor(sheet, skillKey, characteristic) {
  var P = window.SWRPGPoolMods, idx = poolModSkillIndex(), CR = window.CharResolve;
  if (characteristic === undefined && skillKey) characteristic = (sheetSkillCharacteristic(sheet, skillKey) || {}).characteristic || undefined;
  var cb = (CR && CR.talentViewFor && CR.talentViewFor(sheet)) || window._canonicalCharbuild;
  if (!P || !idx || !skillKey || !cb || !cb.talents || !CR || !CR.ownedTalentToldSources) return [];
  var ctx = P.contextFor(skillKey, idx, characteristic), out = [];
  if (!ctx) return [];
  CR.ownedTalentToldSources(sheet, cb).forEach(function (t) {
    t.told.forEach(function (row) {
      if (!row || (row.moment || 'check') !== 'check' || !Array.isArray(row.applies_to) || !row.applies_to.length) return;
      if (!P.matches({ appliesTo: row.applies_to }, ctx)) return;
      var w = row.words || {}, words = t.description.slice(w.start, w.end).trim();
      // R-M8BG-38 (f100-m8-batches ADDENDUM 10): the row's ruled `limit` (use_limit: shown, never tracked) rides with it
      if (words) out.push({ key: t.id + ':' + row.id, name: t.name, words: words, limit: row.limit || null, talentId: t.id, rowId: row.id });   // B-1451: its owner
    });
  });
  return out;
}
// the ONE told line (SWRPGUI.ToldLine, lib/shared/ui.jsx — frame F6's .m8-told, D-26; R-M8BG-38/40's limit tag): moved there by
// card `f100-surfacing-yard-concept` ADDENDUM 8 Q2 (a) so the sheet's gun rows and the Yard's seat card draw it too; this name
// stays as its one-line caller for its slicers
function DiceHubTold(props) {
  return h((window.SWRPGUI || {}).ToldLine, props);
}
// R6 S3 (card release-r6-ui ADDENDUM 6; D-278 S3-roll "a roll result with Table 3-8 shown on demand"): once a network's
// check has rolled, ONE door — "Spending the result", with the table's cite — opens Table 3-8's rows as told lines (the hub's
// DiceHubTold: told, never applied). The rows are rewards.json's `contact_results` (ruling release-r6-ui-c0 U7). The door is
// the hub's one fold (SWRPGUI.GearFold) with its own words, so it draws the fold's look. → [] before the roll or the registry.
function diceHubContactResults(given, rolledGiven, reg, open, onToggle) {
  var rows = given && given.kind === 'contact' && rolledGiven && reg && Array.isArray(reg.contact_results) ? reg.contact_results : [];
  var UI = window.SWRPGUI || {};
  if (!rows.length || !UI.GearFold) return [];
  var els = rows.map(function (r, i) { return h(DiceHubTold, { key: 't38' + i, name: r.spend, words: r.result }); });
  var fold = UI.GearFold('t38', els, open, onToggle, 'dice-hub-t38-door');
  return [React.cloneElement(fold[0], { key: 't38door' }, 'Spending the result ' + (open ? '▾' : '▸'),
    h('span', { 'data-testid': 'dice-hub-t38-cite', style: { marginLeft: 'auto', color: 'var(--doc-ink-muted)', fontSize: 'var(--fs-sm)' } }, rows[0].cite))]
    .concat(fold.slice(1));
}
function diceHubWeaponReminders(sheet, poolMods, spec) {
  var sk = spec && spec.skillKey;
  // B-219 (R-B219C-8): the weapon's own quality rows ride first — an OFFERED Cumbersome is this row's offer — the same list
  // computeWeaponPool folded, so a tick here is the tick it takes
  return poolModRemindersFor(((spec && spec.qualityMods) || []).concat(poolMods || []), sk, (sk && (sheetSkillCharacteristic(sheet, sk) || {}).characteristic) || undefined);
}

// F-100 M7 W3 — the lines under a hub skill row (Docs/mockups/f100-m7-correct.html @ 0e35174f, W3; ruling f100-m7-build
// R-M7G-21, -30). Pure and top-level, so a guard EXECUTES it. → { caption, others: [{ characteristic, text }],
// offers: [{ characteristic, text }] } from skillCharacteristic's answer `sc`:
//   caption — only when the skill HAS an alternative: "Rolls with Agility (Renegade Form)" (the best-of's source), or
//             "Rolls with Brawn" when the base wins; with a switch pressed (`using`), "Rolls with Brawn this roll" and the
//             comparison names what is not in use: "Agility would roll 1 yellow, 3 green" (R-M7G-21(b), -32);
//   others  — every characteristic the row could switch to for one roll, alternatives first, then the base. One:
//             "Brawn would roll 1 yellow, 1 green" (its pool, from the dice module). Two or more: "Presence 3 · Makashi
//             Technique", "Brawn 2" (value, and the talent that gives it). The button reads "Use <X> this roll";
//   offers  — the reminders (R-M7K-4: offered, never applied; the strain stays the player's to mark, R-M7G-21(d); a
//             once-per-session offer is offered every time, untracked, (e)): "<Talent> — suffer 2 strain to roll Charm
//             with Cunning" · "<Talent> — once per session, roll this with Cunning" · "<Talent> — navigating on a world?
//             Roll with Intellect". Their button reads "Use".
function m7Gerund(phrase) {
  var m = /^to\s+(\S+)(.*)$/.exec(String(phrase || '').trim());
  if (!m) return String(phrase || '').trim();
  var v = m[1], ing = /[^e]e$/.test(v) ? v.slice(0, -1) + 'ing' : v + 'ing';
  return ing + m[2];
}
function m7HubLines(sheet, key, rank, sc, cb, using) {
  var poolMods = arguments[6];   // F-100 M8 M2: optional, the row's pool mods (a substitution's "+N difficulty"); the header stays M7's (guards slice it)
  var out = { caption: null, others: [], offers: [], subCaption: null, subs: [] };
  if (!sc) return out;
  var chars = (sheet && sheet.characteristics) || {}, name = SKILL_NAMES[key] || key;
  var talentOf = function (because) { return because && because !== 'base' ? m7TalentName(cb, because) : ''; };
  var alts = sc.alternatives || [];
  var all = [{ characteristic: sc.characteristic, because: sc.because }].concat(alts, [{ characteristic: sc.base, because: 'base' }]);
  // R-M7G-21(b) (D-13, frames v5): a pressed switch rolls its characteristic this roll and the caption says so. On a
  // captioned row that is ANY pressed switch, an offer's included (R-M7G-41: the screen never contradicts its dice). The
  // comparison names what is NOT in use, with its pool (R-M7G-32).
  var pressedSub = using && typeof using === 'object' ? using : null;
  var pressed = pressedSub ? null : (using || null);
  if (alts.length) {
    var t = talentOf(sc.because);
    out.caption = pressed ? 'Rolls with ' + m7CapChar(pressed) + ' this roll' : 'Rolls with ' + m7CapChar(sc.characteristic) + (t ? ' (' + t + ')' : '');
    // the options the row could switch to, beside the best-of; each keeps ITS button ("Use <X> this roll")
    var opts = all.filter(function (o, i, a) {
      return o.characteristic !== sc.characteristic && a.findIndex(function (x) { return x.characteristic === o.characteristic; }) === i;
    });
    var poolWords = function (c) {
      var D = window.SWRPGDice, pool = D ? D.buildPool({ characteristic: Number(chars[c]) || 0, skillRank: rank, difficulty: 0 }) : {};
      return [[pool.proficiency, 'yellow'], [pool.ability, 'green']].filter(function (x) { return x[0]; }).map(function (x) { return x[0] + ' ' + x[1]; }).join(', ') || 'no dice';
    };
    opts.forEach(function (o) {
      var text;
      if (opts.length === 1) {
        // one alternative: the comparison names the characteristic NOT in use, with its pool (pressed: the best-of)
        var cmp = pressed === o.characteristic ? sc.characteristic : o.characteristic;
        text = m7CapChar(cmp) + ' would roll ' + poolWords(cmp);
      } else {
        var tn = talentOf(o.because);
        text = m7CapChar(o.characteristic) + ' ' + (Number(chars[o.characteristic]) || 0) + (tn ? ' · ' + tn : '');
      }
      out.others.push({ characteristic: o.characteristic, text: text });
    });
  }
  // offers in the frame's order: a paid offer, then a one-situation offer, then a once-per-session offer (W3 draws
  // Cunning Persona above Clever Solution); data order within a kind
  var kindOf = function (r) { return r.cost ? 0 : r.condition ? 1 : r.limit ? 2 : 3; };
  var LIM = { once_per_round: 'once per round', once_per_encounter: 'once per encounter', once_per_session: 'once per session' };
  (sc.reminders || []).map(function (r, i) { return { r: r, i: i }; })
    .sort(function (a, b) { return kindOf(a.r) - kindOf(b.r) || a.i - b.i; }).map(function (x) { return x.r; }).forEach(function (r) {
    var tn = talentOf(r.because), c = m7CapChar(r.characteristic), text;
    // F-100 THE SURFACING MISSION (roll-options card ADDENDUM 14 F5): a Destiny-priced swap words its price and its limit,
    // the substitute line's words — "Wise Warrior — once per round, spend 1 Destiny Point to roll Brawl with Presence"
    if (r.cost && r.cost.destiny) text = tn + ' — ' + (LIM[r.limit] ? LIM[r.limit] + ', ' : '') + 'spend ' + r.cost.destiny
      + (r.cost.destiny === 1 ? ' Destiny Point' : ' Destiny Points') + ' to roll ' + name + ' with ' + c;
    else if (r.cost && r.cost.strain) text = tn + ' — suffer ' + r.cost.strain + ' strain to roll ' + name + ' with ' + c;
    // R7 B-1471: every limit is worded, not only once per session (reSpecialized's Sly As A Tooka is once per encounter)
    else if (LIM[r.limit]) text = tn + ' — ' + LIM[r.limit] + ', roll this with ' + c;
    else if (r.condition) text = tn + ' — ' + m7Gerund(r.condition) + '? Roll with ' + c;
    else text = tn + ' — roll with ' + c;
    out.offers.push({ characteristic: r.characteristic, text: text });
  });
  // F-100 M8 M2 (f100-m8-batches R-M8BG-233; the approved mockup Docs/mockups/f100-m8-skill-swap.html @ b2aeb49b, drawing
  // (a), D-81): one line per substitute skill, its button "Use <Skill> this roll". The words are the talent's own (its
  // condition verbatim, R-M8BG-238), then what rolls: "Natural Remedy — healing wounds and critical injuries? Roll Survival
  // instead, +1 difficulty" · "Voice of Reason — once per encounter, roll Lore with Presence". Pressed, the row keeps its
  // name and a caption leads: "Rolls Survival this roll (Natural Remedy)" · "Rolls Lore with Presence this roll (…)".
  (sc.substitutes || []).forEach(function (u) {
    var tn = talentOf(u.because), sn = SKILL_NAMES[u.sheetKey] || u.skill, c = m7CapChar(u.characteristic);
    var verb = 'roll ' + sn + (u.to ? ' with ' + c : ' instead'), lead;
    if (u.cost && u.cost.strain) lead = 'suffer ' + u.cost.strain + ' strain to ' + verb;
    else if (u.cost && u.cost.destiny) lead = 'spend ' + u.cost.destiny + (u.cost.destiny === 1 ? ' Destiny Point' : ' Destiny Points') + ' to ' + verb;
    else if (u.limit && LIM[u.limit]) lead = LIM[u.limit] + ', ' + verb;
    else lead = verb;
    // the talent's same-check difficulty adds ride on the line (taken with the swap, diceHubSkillSpec); `poolMods` is the
    // row's own (optional: a caller without it draws no "+N difficulty")
    var add = 0;
    poolModRemindersFor(poolMods, key, u.characteristic).forEach(function (m) {
      if (m.talentId === u.talentId && m.die === 'difficulty' && m.direction === 'add') add += Number(m.count) || 0;
    });
    var text = tn + ' — ' + (u.condition ? m7Gerund(u.condition) + '? ' + lead.charAt(0).toUpperCase() + lead.slice(1) : lead) + (add ? ', +' + add + ' difficulty' : '');
    var val = { skill: u.skill, sheetKey: u.sheetKey, characteristic: u.characteristic, talentId: u.talentId };
    var on = !!(pressedSub && pressedSub.skill === u.skill && pressedSub.talentId === u.talentId);
    out.subs.push({ id: u.talentId + ':' + u.skill, value: val, text: text, btn: 'Use ' + sn + ' this roll', pressed: on });
    if (on) {
      var cap = 'Rolls ' + sn + (u.to ? ' with ' + c : '') + ' this roll' + (tn ? ' (' + tn + ')' : '');
      if (alts.length) out.caption = cap; else out.subCaption = cap;
    }
  });
  return out;
}
function M7HubLine(p) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }} data-m7-hub-line={p.kind}>
      <span style={Object.assign({}, M7_LINE_STYLE, { margin: 0, fontSize: 15 })}>{p.text}</span>
      {p.btn ? <button type="button" className="hp-quiet" aria-pressed={p.pressed ? 'true' : 'false'} data-testid="m7-hub-use" onClick={p.onPress}
        style={p.pressed ? { background: 'var(--doc-action)', color: 'var(--doc-paper-raised)', borderColor: 'var(--doc-action)' } : undefined}>{p.btn}</button> : null}
    </div>);
}

// ══ THE SURFACING HUB (ruling `f100-surfacing-hub-concept` R-SHC-1..13; HANDOFF f100-surfacing-hub-build — ADDENDUM 6 as
// ruled by ADDENDA 7–9). Built from the APPROVED frames `Docs/mockups/f100-surfacing-hub.html` @ 269d99205 (Artifact
// 475TjS78bdX3cheZsS8X4T; D-229 hub (b)(a)(a) @ 3adb14d96 — LAW, R-MIL-1): the chips on the band line (TARGET · DIFFICULTY,
// H1/H4/H5), the MODE ticks (H1), THE SPEND MENU after the roll (H2), the carried-gear fold (H3), the Force row's dials, its
// Duration commit and its barred state (H6). Every number below READS a core — the spend rows SWRPGQualityRows.spendRows,
// the target lines .targetRows, the dials SWRPGPoolMods.powerDialsAt, the commit's dice CharResolve.durationCommitDice,
// affordability SWRPGUI.spendAfford; the hub decides nothing and saves nothing (ticks and chips are EPHEMERAL, one roll).
// H8 (the signature activation) is OUT until B-1310; nothing of it is drawn here.
var DICE_HUB_DIFFICULTY = [['Simple', 0], ['Easy', 1], ['Average', 2], ['Hard', 3], ['Daunting', 4], ['Formidable', 5]];
var DICE_HUB_TARGETS = [['none', 'None'], ['personal', 'Personal'], ['droid', 'Droid'], ['vehicle', 'Vehicle']];
function diceHubDifficultyName(n) {
  var x = DICE_HUB_DIFFICULTY.filter(function (d) { return d[1] === n; })[0];
  return x ? x[0] : String(n);
}
// a spend price in the dice font's own tokens (SWRPGUI.glyphText draws them): [{ad: 2}] → "[AD][AD]"; alternatives " or "
// `fp` (F-100 THE SURFACING MISSION, ADDENDUM 8 F11): a talent spend row priced in Force points
// B-1510 (roll-options card ADDENDUM 38 F3): a third entry is the plural, read where a count is printed; only `fp` has one
var DICE_HUB_SYM = { ad: ['[AD]', 'advantage'], tr: ['[TR]', 'triumph'], th: ['[TH]', 'threat'], de: ['[DE]', 'despair'], su: ['[SU]', 'success'], fp: ['[FP]', 'Force point', 'Force points'] };
function diceHubPriceTokens(req) {
  return (req || []).map(function (c) {
    return Object.keys(c).map(function (k) { var t = (DICE_HUB_SYM[k] || [k])[0], s = ''; for (var i = 0; i < (Number(c[k]) || 0); i++) s += t; return s; }).join('');
  }).join(' or ');
}
function diceHubPriceWords(req) {
  return (req || []).map(function (c) {
    return Object.keys(c).map(function (k) { var s = DICE_HUB_SYM[k] || [k, k]; return c[k] + ' ' + (c[k] !== 1 && s[2] ? s[2] : s[1]); }).join(' + ');
  }).join(' or ');
}
// THE SPEND MENU's rows for one rolled weapon (R-SHC-2 as ADDENDUM 3 errs it; QS1–QS3), pure so a guard executes it.
// `menu` = SWRPGQualityRows.spendRows' rows; `resolved` = the roll's result; `spent` = the menu's ticks in tick order,
// { <row key>: true }. Ticked rows pay first, in tick order, from the roll's symbols (the first clause they can afford); an
// unticked row the rest cannot afford is INERT (R5). QS2: an `any` delta (Jury Rigged) is taken by the FIRST row ticked —
// its price lowered (never below 1), tagged "lowered — <source>", the head line saying where it went; until then every
// row's affordability reads the lowered price, its cap the printed one. QS3: a set field rides as "set — <source>".
// → { rows: [{ key, price (tokens), priceWords, name, text, tags, on, inert, cap }], lines: [text], tally: text }
function diceHubSpendModel(menu, resolved, spent) {
  var UI = window.SWRPGUI || {}, afford = UI.spendAfford || function () { return false; };
  // `fp` (ADDENDUM 8 F11): the roll's light-side pips, which a talent spend row may be priced in — the tally only
  // B-1490 (ADDENDUM 38 F3): the engine's `advantage` is NET (advantage − threat) and it returns `netThreat`, never `threat` —
  // so a net-threat roll offers 0 advantage (never a negative count), and `th` reads `netThreat`
  var r0 = resolved || {}, tally = { ad: Math.max(0, r0.advantage || 0), tr: r0.triumph || 0, th: r0.netThreat || 0, de: r0.despair || 0, su: r0.success || 0, fp: r0.light || 0 };
  var symWords = function (k, n) { var s = DICE_HUB_SYM[k] || [k, k]; return n + ' ' + (n !== 1 && s[2] ? s[2] : s[1]); };   // B-1510
  var left = Object.assign({}, tally);
  var anyD = ((menu && menu[0] && menu[0].anyDeltas) || []).filter(function (d) { return d && d.by; });
  var by = anyD.reduce(function (t, d) { return t + d.by; }, 0), anySrc = anyD.map(function (d) { return d.source; }).filter(Boolean).join(', ');
  var lower = function (req) { return (req || []).map(function (c) { var o = {}; Object.keys(c).forEach(function (k) { o[k] = k === 'ad' ? Math.max(1, c[k] - by) : c[k]; }); return o; }); };
  var hasAd = function (req) { return (req || []).some(function (c) { return c.ad > 0; }); };
  var keyOf = function (r) { return 'spend:' + r.quality + ':' + r.rowId; };
  // B-1470: what the roll shows of each symbol a price reads, in the order the price first names them ([{tr:1},{ad:2}] at
  // 0 triumph and 1 advantage → "0 triumph and 1 advantage"); an advantage-only price reads "<n> advantage" as before
  var shows = function (req) {
    var ks = [];
    (req || []).forEach(function (c) { Object.keys(c).forEach(function (k) { if (ks.indexOf(k) === -1) ks.push(k); }); });
    return ks.map(function (k) { return symWords(k, tally[k] || 0); }).join(' and ');
  };
  // W4 (ADDENDUM 47 F7): a count is a numeral + ONE symbol — "0 [TR] and 1 [AD]"
  var symTok = function (k, n) { return n + ' ' + (DICE_HUB_SYM[k] || [k])[0]; };
  var showsTokens = function (req) {
    var ks = [];
    (req || []).forEach(function (c) { Object.keys(c).forEach(function (k) { if (ks.indexOf(k) === -1) ks.push(k); }); });
    return ks.map(function (k) { return symTok(k, tally[k] || 0); }).join(' and ');
  };
  var byKey = {}; (menu || []).forEach(function (r) { byKey[keyOf(r)] = r; });
  var paid = {}, takenBy = null;
  Object.keys(spent || {}).filter(function (k) { return spent[k] && byKey[k]; }).forEach(function (k) {
    var r = byKey[k], low = !!(by && !takenBy && hasAd(r.req)), req = low ? lower(r.req) : r.req;
    // a talent row priced only in a Destiny Point (`cost`, no `req`) takes nothing from the roll (ADDENDUM 8 F2/F4)
    var clause = r.req ? (req || []).filter(function (c) { return afford([c], left); })[0] : {};
    if (!clause) return;
    Object.keys(clause).forEach(function (s) { left[s] = (left[s] || 0) - clause[s]; });
    if (low) takenBy = r;
    paid[k] = { req: req, lowered: low };
  });
  var rows = (menu || []).map(function (r) {
    var k = keyOf(r), p = paid[k], on = !!p;
    var lowerable = !!(by && hasAd(r.req) && (p ? p.lowered : !takenBy));
    var req = p ? p.req : (lowerable ? lower(r.req) : r.req);
    var inert = !on && !!r.req && !afford(req, left);
    var tags = [];
    // a talent row's price outside the roll and its limit, as offerTags words them (shown, never tracked)
    if ((r.cost || r.limit) && UI.offerTags) tags = tags.concat(UI.offerTags({ cost: r.cost, limit: r.limit }));
    if (r.set && r.set.length && r.set[0].source) tags.push('set — ' + r.set[0].source);
    if (r.repeatMax && r.repeatMax > 1) tags.push('up to ' + r.repeatMax);
    if (p && p.lowered) tags.push('lowered — ' + anySrc);
    var cap = by && hasAd(r.req) ? 'Printed ' + diceHubPriceWords(r.req) + '; ' + diceHubPriceWords(lower(r.req)) + ' with ' + anySrc + '.'
      : (inert ? 'The roll shows ' + shows(r.req) + '; this needs ' + diceHubPriceWords(r.req) + '.' : null);   // B-1470
    var shown = p && p.lowered ? p.req : r.req;
    return { key: k, price: diceHubPriceTokens(shown), priceWords: diceHubPriceWords(shown), name: r.source, text: r.effect, tags: tags, on: on, inert: inert, cap: cap };
  });
  // 5h (brief §4/§5/§8; ADDENDUM 62 F5 (a)): with the key ON only — a talent row's who tag and box (its cost the row's price),
  // and W4's token form of a cap BESIDE its words (the words stay the screen-reader name); OFF, every row object is as it was
  if (UI.rollOptionsShown && UI.rollOptionsShown()) rows.forEach(function (o, i) {
    var r = menu[i];
    var who = r.talentId && UI.offerWho ? UI.offerWho(r) : null, info = r.talentId && UI.offerInfo
      ? UI.offerInfo(r.talentId, r.rowId, null, { price: r.req ? o.price : null, priceWords: r.req ? o.priceWords : null, cost: UI.offerTags({ cost: r.cost }) }) : null;
    if (who) o.who = who;
    if (info) o.info = info;
    if (o.cap) o.capTokens = by && hasAd(r.req) ? 'Printed ' + diceHubPriceTokens(r.req) + '; ' + diceHubPriceTokens(lower(r.req)) + ' with ' + anySrc + '.'
      : 'The roll shows ' + showsTokens(r.req) + '; this needs ' + diceHubPriceTokens(r.req) + '.';
  });
  var lines = by ? [by + ' less advantage on one quality you name — ' + anySrc + (takenBy ? ' — used on ' + String(takenBy.source || '').replace(/\s+\d+$/, '') : '')] : [];
  // B-1477 (ADDENDUM 38 F3): the footer names advantage first, as drawn, then every other symbol a ticked row spent, then a
  // ticked row's Destiny Point price (shown, never charged) — one sentence; an advantage-only menu reads as it always did
  var terms = ['ad'].concat(['tr', 'de', 'th', 'su', 'fp'].filter(function (k) { return tally[k] - left[k] > 0; }))
    .map(function (k) { return (tally[k] - left[k]) + ' of ' + symWords(k, tally[k]); });
  var dp = Object.keys(paid).reduce(function (n, k) { var c = byKey[k].cost; return n + ((c && Number(c.destiny)) || 0); }, 0);
  if (dp) terms.push(dp + (dp === 1 ? ' Destiny Point' : ' Destiny Points'));
  var said = terms.length > 1 ? terms.slice(0, -1).join(', ') + ' and ' + terms[terms.length - 1] : terms[0];
  if (UI.rollOptionsShown && UI.rollOptionsShown()) {   // W4 (5h, F5 (a)): the tally's token form BESIDE its words (the words below, untouched)
    var tt = ['ad', 'tr', 'de', 'th', 'su', 'fp'].filter(function (k, i) { return !i || tally[k] - left[k] > 0; })
      .map(function (k) { return (tally[k] - left[k]) + ' of ' + symTok(k, tally[k]); });
    if (dp) tt.push(dp + (dp === 1 ? ' Destiny Point' : ' Destiny Points'));
    var withTokens = { rows: rows, lines: lines, tally: said + ' spent' };
    withTokens.tallyTokens = (tt.length > 1 ? tt.slice(0, -1).join(', ') + ' and ' + tt[tt.length - 1] : tt[0]) + ' spent';
    return withTokens;
  }
  return { rows: rows, lines: lines, tally: said + ' spent' };
}
// THE FORCE ROW's menu after its roll (H6; R-SHC-2/5): the power's DIALS (powerDialsAt) priced in Force points, and each
// owned Duration upgrade whose own words name a commit (CharResolve.durationCommitDice > 0) as "Commit ◆◆◆ to sustain" —
// the ONE spend that writes, through the sheet's one writer `onCommit` (session only). Pure, so a guard executes it.
// `light` = the roll's light pips; `spent` = the dial ticks in order; `committed` = the session's set; `available` = free dice.
function diceHubForceMenuModel(dials, durations, light, spent, committed, available) {
  var left = Number(light) || 0, paid = {};
  var keyOf = function (d) { return 'dial:' + d.talentId + ':' + d.rowId; };
  var costOf = function (d) { return Number(d && d.cost && d.cost.force_points) || 0; };
  var byKey = {}; (dials || []).forEach(function (d) { byKey[keyOf(d)] = d; });
  Object.keys(spent || {}).filter(function (k) { return spent[k] && byKey[k]; }).forEach(function (k) {
    var c = costOf(byKey[k]); if (c > left) return; left -= c; paid[k] = true;
  });
  var fp = function (n) { var s = ''; for (var i = 0; i < n; i++) s += '[FP]'; return s; };
  var fo = function (n) { var s = ''; for (var i = 0; i < n; i++) s += '[FO]'; return s; };
  var rows = (dials || []).map(function (d) {
    var k = keyOf(d), c = costOf(d), on = !!paid[k];
    return { key: k, price: fp(c), priceWords: c + (c === 1 ? ' Force point' : ' Force points'), name: d.source, text: d.words || '',
             tags: d.repeatable ? ['repeatable'] : [], on: on, inert: !on && c > left, cap: null };
  }).concat((durations || []).map(function (u) {
    var on = !!(committed && typeof committed === 'object' && committed[u.key]);
    // THE FORCE SWITCH (D-300 (2); ADDENDUM 12 F4 (a)): a Control's row reads '<power> (<upgrade>)' as Duration's does; a
    // STACKING row (Farsight's Control: Perception) counts its held commits as a tag ("×2"), offers ONE commit per roll —
    // ticked, it reads `on` and inert until the next roll (`spent['commit:<key>']`) — and never releases (the sheet's −
    // does). The cap (F5) is diceHubForceMenuCapped's, after this model.
    var tags = ['commit ' + u.dice + ' Force'].concat(u.tag ? [u.tag] : []), inert;
    if (u.stacks) {
      var n = on ? Math.floor((Number(committed[u.key]) || 0) / (u.dice || 1)) : 0;
      if (n) tags.push('×' + n);
      inert = !!(spent && spent['commit:' + u.key]) || (Number(available) || 0) < u.dice;
    } else inert = !on && (Number(available) || 0) < u.dice;
    return { key: 'commit:' + u.key, commitKey: u.key, stacks: !!u.stacks, price: fo(u.dice), priceWords: 'commit ' + u.dice + ' Force ' + (u.dice === 1 ? 'die' : 'dice'),
             name: u.power + ' (' + (u.label || 'Duration') + ')', text: u.words, tags: tags, on: on, inert: inert, cap: null };
  }));
  var used = (Number(light) || 0) - left;
  var UIf = window.SWRPGUI || {};
  if (UIf.rollOptionsShown && UIf.rollOptionsShown())   // W4 (5h, F5 (a)): the token form BESIDE the words
    return { rows: rows, lines: [], tally: used + ' of ' + (Number(light) || 0) + ' Force points spent',
             tallyTokens: used + ' of ' + (Number(light) || 0) + ' [FP] spent' };
  return { rows: rows, lines: [], tally: used + ' of ' + (Number(light) || 0) + ' Force points spent' };
}
// THE CAP ON THE HUB'S COMMIT ROWS (ruling `f100-surfacing-force-flip-card` ADDENDUM 12 F5 (a): commitAtCap on EVERY
// commit-costed key; pure, so a guard executes it): `capOf(key)` → the capped quantity's name or null (CharResolve.commitAtCap
// over the sheet and the set as held). A row one more commit would move nothing — a free Control, or a stacking one — is held
// inert with "<name> at its maximum"; a held non-stacking row stays live (its tick releases). Duration keys carry no
// commit-costed effect row, so commitAtCap answers null and their rows pass through untouched.
function diceHubForceMenuCapped(fm, capOf) {
  return Object.assign({}, fm, { rows: (fm.rows || []).map(function (r) {
    if (!r.commitKey || (r.on && !r.stacks)) return r;
    var c = capOf ? capOf(r.commitKey) : null;
    return c ? Object.assign({}, r, { inert: true, cap: c + ' at its maximum' }) : r;
  }) });
}
// a power's owned Duration upgrades that commit dice: [{ key: '<power>.<ability>', power, dice, words }] (R-SHC-5, C2)
// THE FORCE SWITCH (ruling `f100-surfacing-force-flip-card` §2.8 of its brief; D-300 (2); ADDENDUM 12 F4): WIDENED in place, the
// name kept — with the Force key ON it also lists each owned non-basic upgrade with an `effects` row costed `commit_force`
// (Enhance's Control: Brawn / Agility, Farsight's Control: Perception), so the sheet's ticks and the hub's rows read ONE list.
// Every row: { key, power, dice, words, label ('Duration' | the upgrade's name), stacks (commitStacks), tag }. The TAG by one
// rule (ADDENDUM 8 F6): the ability's words "after successfully activating the <P> power" → "after a successful <P> check"
// (Farsight's Control: Perception alone today). The key OFF: the Duration rows exactly as before (key, power, dice, words).
function diceHubDurationCommits(fp, cb) {
  var rec = fp && fp.id && cb && cb.forcePowers ? cb.forcePowers[fp.id] : null, out = [];
  if (!rec || !rec.abilities || !window.CharResolve || !CharResolve.durationCommitDice) return out;
  var fOn = !!(window.SWRPGForceRules && SWRPGForceRules.on && SWRPGForceRules.on());
  (fp.upgradeKeys || []).forEach(function (k) {
    var ab = rec.abilities[k];
    if (!ab) return;
    if (ab.family === 'duration') {
      var n = CharResolve.durationCommitDice(ab);
      if (n > 0) out.push({ key: fp.id + '.' + k, power: rec.name || fp.name, dice: n, words: ab.description || '', label: 'Duration', stacks: false, tag: null });
      return;
    }
    if (!fOn || ab.family === 'basic' || !(ab.effects || []).some(function (e) { return e && e.cost && e.cost.commit_force; })) return;
    if (out.some(function (u) { return u.key === fp.id + '.' + k; })) return;   // an upgrade owned twice ticks once
    var v = CharResolve.commitValueOf(fp.id + '.' + k, cb), d = v === true ? 1 : v;
    if (!(d > 0)) return;
    var tm = /after successfully activating the (.+?) power/i.exec(String(ab.description || ''));
    out.push({ key: fp.id + '.' + k, power: rec.name || fp.name, dice: d, words: ab.description || '', label: ab.name || k,
               stacks: !!(CharResolve.commitStacks && CharResolve.commitStacks(fp.id + '.' + k, cb)), tag: tm ? 'after a successful ' + tm[1] + ' check' : null });
  });
  return out;
}
// H4 (R-SHC-8, D-229 Q2 (a)): what the weapon's qualities do to the TARGET chosen, after the roll — from targetRows' rows:
// "Ion: damage dealt as system strain — vs a vehicle (EotE p.156)", "Pierce 2 — ignores up to 2 soak". No soak or wound is
// entered (the app does not simulate the target).
function diceHubTargetLines(tr, kind) {
  var Q = window.SWRPGQualityRows, KIND = { personal: 'a person', droid: 'a droid', vehicle: 'a vehicle' };
  var cite = function (c) { return c && Q && Q.citeWords ? ' (' + Q.citeWords(c) + ')' : ''; };
  return ((tr && tr.rows) || []).map(function (r) {
    // W5 (D-332; ADDENDUM 58 ITEM 2, 5h): a soak-ignoring row says it in the book's own sentence — "…; this strain is not reduced by soak"
    if (r.op === 'damage_as') { var vw = String(r.value).replace(/_/g, ' ');
      return r.source + ': damage dealt as ' + vw + (r.soak === 'ignored' ? '; this ' + vw + ' is not reduced by soak' : '') + ' — vs ' + (KIND[kind] || kind) + cite(r.cite); }   // soak: a talent row's member (Pressure Point; ADDENDUM 25 F3, an ITEM for Jason)
    if (r.op === 'soak_ignore') return r.source + ' — ignores up to ' + r.amount + ' soak';
    if (r.op === 'armor_ignore') return kind === 'vehicle' ? r.source + ' — ignores up to ' + r.amount + ' armor' : r.source + ' — ignores up to ' + (10 * r.amount) + ' soak';
    return null;
  }).filter(Boolean);
}
// H5 (R-SHC-10): a TICKED floored difficulty removal the fold left alone (the pool is at its floor) says so — "Difficulty
// stays 1 — Master Doctor removes none below the floor (EotE p.138)". `reminders` = the row's offers, `taken` its ticks.
function diceHubFloorLines(spec, reminders, taken, talents) {
  var D = window.SWRPGDice, Q = window.SWRPGQualityRows, changes = (spec && spec._poolModChanges) || [];
  var built = D && spec ? D.buildPool(spec) || {} : {};
  return (reminders || []).map(function (m) {
    if (!m || !m.key || !(taken && taken[m.key]) || m.die !== 'difficulty' || m.direction !== 'remove') return null;
    var rec = talents && m.talentId ? talents[m.talentId] : null;
    var row = rec ? (rec.pool_mods || []).filter(function (r) { return r && r.id === m.id; })[0] : null;
    if (!row || row.floor == null) return null;
    if (changes.some(function (c) { return c.taken && c.source === m.source && c.die === 'difficulty'; })) return null;
    return 'Difficulty stays ' + (built.difficulty || 0) + ' — ' + m.source + ' removes none below the floor'
      + (row.cite && Q && Q.citeWords ? ' (' + Q.citeWords(row.cite) + ')' : '');
  }).filter(Boolean);
}
// the band chip's idiom for the two new chips (their OWN testids, so D2's one-band-chip pins hold; ink: sheet.css)
function DiceHubChip(p) {
  return h('span', { className: 'dh-chip', style: { display: 'inline-flex', alignItems: 'center', gap: 9 } },
    h('span', { style: { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.16em', color: 'var(--doc-ink-muted)' } }, p.label),
    h('button', { type: 'button', 'data-testid': p.testid, 'aria-expanded': p.open ? 'true' : 'false', 'aria-label': p.aria + ': ' + p.value, onClick: p.onToggle,
      style: { minHeight: 44, padding: '0 13px', borderRadius: 9, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, touchAction: 'manipulation',
        fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-sm)' } },
      p.value, h('span', { 'aria-hidden': 'true', style: { fontSize: 10 } }, '▾')));
}
function DiceHubChipStrip(p) {
  return h('div', { role: 'group', 'aria-label': p.label, 'data-dh-strip': p.kind, style: { display: 'grid', gridTemplateColumns: 'repeat(' + (p.cols || 3) + ',1fr)', gap: 5, marginTop: 7 } },
    p.options.map(function (o) {
      return h('button', { key: o.key, type: 'button', 'data-testid': p.testid, 'aria-pressed': o.on ? 'true' : 'false', onClick: function () { p.onPick(o.key); },
        style: { minHeight: 44, minWidth: 0, borderRadius: 8, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 1, fontFamily: 'var(--font-body)', padding: '0 3px' } },
        h('span', { style: { fontSize: 13, fontWeight: 600, lineHeight: 1.1 } }, o.v),
        o.sub ? h('span', { style: { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.06em' } }, o.sub) : null);
    }));
}
// THE SPEND MENU (R-SHC-2, the one new shape): under the result, in place — the head, any head line (QS2), one row per
// activation (its price in the dice symbols, the ONE offer row's tick, the effect, the source), the tally.
function DiceHubSpend(p) {
  var UI = window.SWRPGUI || {};
  if (!UI.OfferRow) return null;
  return h('div', { 'data-testid': 'dice-hub-spend', className: 'dh-spend' },
    h('div', { className: 'sb-grp dh-spend-head' }, 'What the roll buys', p.note ? h('span', null, p.note) : null),
    (p.lines || []).map(function (t, k) { return h(DiceHubStatusLine, { key: 'hl' + k, tone: 'var(--text-info)' }, t); }),
    p.rows.map(function (r) {
      return h('div', { key: r.key, 'data-testid': 'dice-hub-spend-row', 'data-spend-key': r.key, className: 'dh-spend-row', 'data-inert': r.inert ? '1' : '0' },
        h('span', { className: 'dh-spend-price', role: 'img', 'aria-label': r.priceWords }, UI.glyphText ? UI.glyphText(r.price) : r.price),
        h('div', { className: 'dh-spend-body' }, h(UI.OfferRow, { offerKey: r.key, name: r.name, text: r.text, tags: r.tags, on: r.on, inert: r.inert, cap: r.cap,
          capTokens: r.capTokens, who: r.who, info: r.info, onToggle: function () { p.onToggle(r); } })));
    }),
    h('div', { 'data-testid': 'dice-hub-spend-tally' }, h(DiceHubStatusLine, { tone: 'var(--doc-ink-muted)', glyph: '·' },
      // W4 (5h, F5 (a)): the token form drawn, the words its screen-reader name; absent (the key OFF), the words as before
      p.tallyTokens && UI.glyphText ? h('span', { role: 'img', 'aria-label': p.tally }, UI.glyphText(p.tallyTokens)) : p.tally)));
}
// H3 (D-229 Q1 (b), ADDENDUM 7 Q3 (a)): a row's offers OWNED by carried gear (holder 'gear'), folded behind ONE line. Worn armour
// and the weapon in hand stay open. Ephemeral per row. The fold is the ONE shared one (SWRPGUI.GearFold, lib/shared/ui.jsx; yard
// ADDENDUM 8 Q2 (a), B-1389's one style); this name stays as its one-line caller for its slicers.
function diceHubGearFold(rowKey, els, open, onToggle) {
  return (window.SWRPGUI || {}).GearFold(rowKey, els, open, onToggle, 'dice-hub-gear-fold');
}
// B-1453 (D-406 item 12): a talent's or species' conditional offer anchored to EVERY check (`anyCheck`: Shortcut's "During a
// chase") folds behind ONE line — "1 more for a situation ▸" — in the carried-gear fold's look (H3), closed by default; nothing
// prompts (D-319). Its open state rides gearOpen under '<rowKey>:sit'. Carried gear stays in its own fold.
function diceHubSituational(m) { return !!(m && m.holder !== 'gear' && m.anyCheck && m.reason === 'condition'); }
function diceHubSituationFold(rowKey, els, gearOpen, onToggle) {
  return (window.SWRPGUI || {}).GearFold(rowKey + ':sit', els, !!(gearOpen && gearOpen[rowKey + ':sit']), onToggle, 'dice-hub-situation-fold', ' more for a situation');
}


// B-1488 (roll-options card ADDENDUM 38 F4 (a)): every sheet's hub offers the Unarmed rules profile (weapons.json `unarmed`,
// store-hidden) — the row the pregens already list and draw — so a character made in the creator has its Brawn-damage Brawl
// row, and Iron Body's and a species' claws lines (`weapon_record: unarmed`) a row to sit on. Appended LAST and only when no
// listed weapon IS `unarmed` (its regId), so every existing row keeps its index (band, ticks, the 'w'+i keys). Pure, so a
// guard executes it; the sheet is never written (resolve-at-render).
function diceHubWeaponsWithUnarmed(weapons, sheet) {
  var list = weapons || [];
  if (list.some(function (w) { return w && w.regId === 'unarmed'; })) return list;
  var u = (resolveSheetEquipment({ weapons: [{ id: 'unarmed' }] }, sheet).weapons || [])[0];
  return u && !u._unresolved ? list.concat([u]) : list;
}
function DiceHub({ sheet, accent, onClose, initialStrip, committed: committedProp, onCommit, onChoosePick, treat, onTreated, given }) {
  var win = useDragWindow();
  var _band = React.useState({}), band = _band[0], setBand = _band[1];        // EPHEMERAL (R-DRB-2)
  // B-611 (R-PSC-2): ROLL on the paper stage opens the hub ON THAT SKILL's strip — the same overlay,
  // landing where the press was. `initialStrip` is a sheet skill key; the hub lists trained skills,
  // so an untrained key joins the list for this opening (its pool is the ability dice alone).
  var _open = React.useState(initialStrip != null ? initialStrip : null), openStrip = _open[0], setOpenStrip = _open[1];   // a weapon strip is its INDEX, and 0 is a strip
  var _rolled = React.useState({}), rolled = _rolled[0], setRolled = _rolled[1];
  // D-352: the row key of the LATEST roll — the one result that may offer a Reroll (older results show none)
  var _latest = React.useState(null), latestKey = _latest[0], setLatest = _latest[1];
  // F-100 M8 step 3 (R-M8B-12, way 1 of every fork): a row's ticked offers, { <rowKey>: { <offerKey>: true } }. A tick lasts
  // ONE roll — rolling clears the row's ticks (a); every offer opens unticked, with no setting (c); nothing is written to
  // the character or any store. EPHEMERAL, like the band and the switch.
  var _ticks = React.useState({}), ticks = _ticks[0], setTicks = _ticks[1];
  function toggleOffer(rowKey, key) {
    setTicks(function (m) { var n = Object.assign({}, m), r = Object.assign({}, n[rowKey] || {}); if (r[key]) delete r[key]; else r[key] = true; n[rowKey] = r; return n; });
    setRolled(function (m) { var n = Object.assign({}, m); delete n[rowKey]; return n; });
  }
  // THE SURFACING HUB's controls — EPHEMERAL like the band (never written anywhere; gone when the hub closes):
  //   dset    { <rowKey>: n }      the DIFFICULTY chip, moved (ADDENDUM 7 Q6 (a): an unmoved chip reads the row's own
  //                                price and follows RANGE; a moved one holds for ONE roll — the roll clears it)
  //   tgt     { <rowKey>: kind }   the TARGET chip (D-229 Q2 (a)); 'none' / absent = no target
  //   chipOpen{ <rowKey>: 'target' | 'difficulty' }  the one open chip strip of a row
  //   gearOpen{ <rowKey>: true }   the carried-gear fold, open (D-229 Q1 (b)); closed by default, a roll leaves it
  var _dset = React.useState({}), dset = _dset[0], setDset = _dset[1];
  var _tgt = React.useState({}), tgt = _tgt[0], setTgt = _tgt[1];
  var _chip = React.useState({}), chipOpen = _chip[0], setChipOpen = _chip[1];
  var _gf = React.useState({}), gearOpen = _gf[0], setGearOpen = _gf[1];
  // R6 S3 (card release-r6-ui ADDENDUM 6): after a network's check, Table 3-8 on demand — rewards.json's contact_results
  // (U7), fetched only when the hub opens on a network. The door is EPHEMERAL, like the gear fold.
  var _t38 = React.useState(false), t38Open = _t38[0], setT38Open = _t38[1];
  var rwdReg = useRewardsReg(!!(given && given.kind === 'contact'));
  var dropKey = function (m, k) { if (!(k in m)) return m; var n = Object.assign({}, m); delete n[k]; return n; };
  function toggleGear(rowKey) { setGearOpen(function (m) { var n = Object.assign({}, m); if (n[rowKey]) delete n[rowKey]; else n[rowKey] = true; return n; }); }
  function toggleChip(rowKey, kind) {
    setChipOpen(function (m) { var n = Object.assign({}, m); if (n[rowKey] === kind) delete n[rowKey]; else n[rowKey] = kind; return n; });
    setOpenStrip(null);   // one strip open at a time on a row: the RANGE strip closes
  }
  function pickDifficulty(rowKey, n) {
    setDset(function (m) { var x = Object.assign({}, m); x[rowKey] = n; return x; });
    setChipOpen(function (m) { return dropKey(m, rowKey); });
    setRolled(function (m) { return dropKey(m, rowKey); });
  }
  function pickTarget(rowKey, kind) {   // the pool does not change; a shown result keeps its lines, now for this target
    setTgt(function (m) { var x = Object.assign({}, m); if (kind === 'none') delete x[rowKey]; else x[rowKey] = kind; return x; });
    setChipOpen(function (m) { return dropKey(m, rowKey); });
  }
  // the spend menu's ticks live WITH the roll (`rolled[key].spent`): a new roll is a new menu; nothing is saved
  function toggleSpend(rowKey, key) {
    setRolled(function (m) {
      var r = m[rowKey]; if (!r) return m;
      var sp = Object.assign({}, r.spent || {}); if (sp[key]) delete sp[key]; else sp[key] = true;
      var n = Object.assign({}, m); n[rowKey] = Object.assign({}, r, { spent: sp }); return n;
    });
  }
  // F-100 M7 W3 (R-M7G-21(a), -30): a skill row's switch, { <key>: <characteristic> } — for ONE roll, then it reverts.
  // EPHEMERAL: never written to the sheet, gone when the hub closes.
  var _sw = React.useState({}), sw = _sw[0], setSw = _sw[1];
  var _ref = React.useState(false), refOpen = _ref[0], setRefOpen = _ref[1];
  var canonReady = useCanonicalReady();   // B-241: now waits on the CHARBUILD too, not just the registries
  // Escape closes — the no-trap escape D-DH-6 requires, alongside the scrim and the ✕.
  React.useEffect(function () {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return function () { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  var equipment = React.useMemo(function () { return resolveSheetEquipment(sheet.equipment, sheet); }, [sheet, canonReady]);
  // B-240: mine, from the F-87 lap — six neighbouring call sites passed the charbuild and this
  // one did not. `derived` gates the FORCE section AND feeds computeWeaponPool's Force dice.
  var derived = CharResolve.computeDerived(sheet, equipment, window._canonicalCharbuild);
  var enc = CharResolve.computeEncumbrance(equipment, sheet.characteristics || {}, CharResolve.speciesEncumbranceDelta(sheet));
  // R-PCA-2: ONE collect for the whole hub, through the seam every other surface uses. What each
  // row does with it — apply, remind, ignore — is the assembler's answer, not the hub's.
  var _tvEpoch = window.SWRPGTalentView.useTalentViewEpoch();   // A3-2 / B-844: a talent's dice resolve through the stamp view — the memo keys on its epoch
  // F-100 M10 (R-M10B-6c): the session's committed set rides in, so a row counting the Force rating reads the dice LEFT
  // (CharResolve.forceDiceAvailable, the same function as `available` below) — DARK: with the key OFF it reads nothing.
  var poolMods = React.useMemo(function () { return collectPoolMods(equipment, { committed: committedProp || 0 }); }, [equipment, _tvEpoch, committedProp]);
  var D = window.SWRPGDice;

  function doRoll(key, spec) {
    var res = D.rollAndResolve(D.buildPool(spec));
    var r = res.resolved || res;
    // the automatic symbols this roll carried, named under the result (frame R5)
    var auto = ((spec && spec._poolModChanges) || []).filter(function (c) { return c.symbol; });
    setRolled(function (m) {
      var n = Object.assign({}, m);
      // B-1263 C-4: the pool ROLLED, ticks included; `taken` = the ticks it rolled with (the spend menu lists a ticked
      // MODE's rows, R-SHC-4); `spent` = the spend menu's ticks (R-SHC-2), empty at the roll
      n[key] = { resolved: r, aria: D.formatResults(r), auto: auto, spec: spec, taken: ticks[key] || null, spent: {} };
      return n;
    });
    setTicks(function (m) { if (!m[key]) return m; var n = Object.assign({}, m); delete n[key]; return n; });   // (a): one roll
    setLatest(key);   // D-352: this result is now the latest
    setDset(function (m) { return dropKey(m, key); });   // ADDENDUM 7 Q6 (a): a moved DIFFICULTY chip holds for one roll
    return r;   // the treat row writes from the roll it made (B-1209 retire)
  }
  // F-100 THE SURFACING MISSION (D-294 L3): Reroll — the ONE roll again over the pool rolled, its ticks carried, marked
  function rerollRow(key, name) {
    var prev = rolled[key]; if (!prev) return;
    var patch = diceHubReroll(doRoll, key, prev, name);
    setRolled(function (m) { if (!m[key]) return m; var n = Object.assign({}, m); n[key] = Object.assign({}, m[key], patch); return n; });
  }
  // D-294 L2: a talent's target tick reads the roll's ticks while a result shows (the tick taken at the roll stays ticked,
  // as drawn); pressing it sets this row's ticks from what it SHOWS and clears the result, as toggleOffer does
  function toggleTarget(rowKey, key, shownOn) {
    setTicks(function (m) { var n = Object.assign({}, m), r = Object.assign({}, n[rowKey] || {}); if (shownOn) delete r[key]; else r[key] = true; n[rowKey] = r; return n; });
    setRolled(function (m) { var n = Object.assign({}, m); delete n[rowKey]; return n; });
  }
  // the lines under a rolled row: what the result already includes (an automatic symbol), named
  function rolledLines(key) {
    var r = rolled[key];
    return r && r.auto ? r.auto.map(function (c, k) {
      return h(DiceHubStatusLine, { key: 'ra' + k, m8: 'applied', tone: 'var(--text-info)' }, 'The result includes +' + (c.to - c.from) + ' ' + c.symbol + ' — ' + c.source);
    }) : [];
  }
  // the owner's unmade skill picks, asked on the rows they could reach (R-M8K-6 (a))
  var unmade = React.useMemo(function () {
    var cb = (CharResolve.talentViewFor && CharResolve.talentViewFor(sheet)) || window._canonicalCharbuild;
    try { return cb && cb.talents && CharResolve.unmadeRollPicks ? CharResolve.unmadeRollPicks(sheet, cb) : []; } catch (e) { return []; }
  }, [sheet, canonReady, _tvEpoch]);
  var poolIdx = poolModSkillIndex();

  // ── WEAPONS ────────────────────────────────────────────────────────────────────────────────
  var weaponRows = diceHubWeaponsWithUnarmed(equipment.weapons, sheet).map(function (w, i) {   // B-1488
    var chosen = band[i] || null;
    var rk = 'w' + i;
    // R7 B-1454: a ticked mode's range cap (Stun setting: "their range changes to short and cannot be increased", EotE p.157)
    // is the core's (`rangeCap`); a band chosen past it gives way to it while the mode is ticked, and comes back unticked
    var capOf = ticks[rk] ? computeWeaponPool(sheet, w, null, derived, accent, equipment, ticks[rk], dset[rk], committedProp) : null;
    var cap = (capOf && capOf.rangeCap) || null;
    if (cap && chosen && DICE_HUB_BANDS.indexOf(chosen) > DICE_HUB_BANDS.indexOf(cap)) chosen = null;
    var spec = computeWeaponPool(sheet, w, chosen, derived, accent, equipment, ticks[rk], dset[rk], committedProp);
    if (!spec) return null;   // R-M7G-9: no pool before the vocabulary loads — the row draws when it lands
    // R-BBL-3: the chip appears only where CHOOSING CHANGES SOMETHING. Derived from what the core
    // returns for each band — never a skill list — so melee rows (band-invariant) and single-band
    // weapons both fall out as static labels without the hub deciding anything.
    // R7 B-1454: each band is read with ONLY the capping mode's tick, so a band past the cap reads BARRED (greyed, "Beyond the
    // weapon's range.") and no other ticked offer re-prices the strip
    var capTaken = null;
    if (cap) (spec.modes || []).forEach(function (m) { if (m.rangeMax && ticks[rk][m.key]) (capTaken = capTaken || {})[m.key] = true; });
    var byBand = DICE_HUB_BANDS.map(function (b) { return { band: b, spec: computeWeaponPool(sheet, w, b, derived, accent, equipment, capTaken) }; });
    var legal = byBand.filter(function (x) { return !x.spec.barred; });
    var prices = {}; legal.forEach(function (x) { prices[x.spec.difficulty] = 1; });
    var hasChip = legal.length > 1 && Object.keys(prices).length > 1;
    var shownBand = chosen || cap || w.range || '—';
    var name = w.nickname ? '“' + w.nickname + '” · ' + w.name : w.name;
    var lines = [];
    if (!spec.barred) {
      lines.push(spec.difficultySet != null
        // the DIFFICULTY chip moved (R-SHC-10): the number rolled is the player's for this roll, not the range's
        ? h(DiceHubStatusLine, { key: 'd', tone: 'var(--text-caution)' }, '+' + spec.difficultySet + ' difficulty — ' + diceHubDifficultyName(spec.difficultySet) + ', set at the table')
        : h(DiceHubStatusLine, { key: 'd', tone: 'var(--text-caution)' },
        '+' + spec.rangeDifficulty + ' difficulty — ' + shownBand + ' range ',   // B-219: the range's own, never a quality's
        h('span', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--doc-ink-muted)' } },
          '(' + (chosen || cap ? 'set at table' : 'printed') + ')')));
      if (spec.encumbranceSetback) lines.push(h(DiceHubStatusLine, { key: 's', tone: 'var(--text-caution)' },
        '+' + spec.encumbranceSetback + ' setback — over encumbrance (' + enc.carried + '/' + enc.limit + ')'));   // B-219: the load's own
    }
    // The item half. `computeWeaponPool` already FOLDED the auto-applying dice into `spec` — the
    // hub never applies anything itself — so these two calls only report what that one builder did
    // and what it deliberately left out. Both ask about THIS weapon's own skill (`spec.skillKey`,
    // resolved once inside the builder), which is why a Ranged (Light) weapon no longer collects
    // the whole `combat` group's reminders the way the hand-rolled bucket did.
    // F-100 M8 step 3 (frames F1 R1/R3): the talent damage APPLIED at this attack, beside the damage and never in it,
    // with its band or its pick named; an unmade pick this attack could use asks and adds nothing (R6)
    var dl = spec.damageLines || null;
    // B-1451: its (i), read through the owner's view so a species-owned line ("species:<id>:<ability>") resolves too
    var dlTal = dl && (window.SWRPGUI || {}).offerTalentsFor ? window.SWRPGUI.offerTalentsFor(sheet || null, dl.applied) : null;
    if (!spec.barred && dl) dl.applied.filter(function (l) { return l.amount; }).forEach(function (l, k) { lines.push(h(DiceHubStatusLine, { key: 'dl' + k, m8: 'applied', tone: 'var(--text-info)',
      info: diceHubLineInfo(l.talentId, l.rowId, dlTal), infoKey: l.talentId + ':' + l.rowId }, diceHubDamageLineText(l, shownBand))); });
    var cl = spec.barred ? null : diceHubCritLine(spec, sheet);
    if (cl) lines.push(h(DiceHubStatusLine, { key: 'cl', m8: 'applied', tone: 'var(--text-info)' }, cl));
    DiceHubAppliedLines(spec, SKILL_NAMES[spec.skillKey], spec.qualityMods, sheet).forEach(function (el) { lines.push(el); });
    if (!spec.barred && window.SWRPGPoolMods && poolIdx && spec.skillKey)
      diceHubPromptsFor(unmade, window.SWRPGPoolMods.contextFor(spec.skillKey, poolIdx)).forEach(function (q, k) {
        lines.push(h(DiceHubPickPrompt, { key: 'pp' + k, pick: q, onChoose: onChoosePick || null })); });
    // R-SHC-4 (H1): the weapon's MODES first, each ONE tick tagged MODE above the other offers — its words its rows' own
    // ("Auto-fire — +1 difficulty"), or the mode's (R3: "Stun Setting — damage dealt as strain; Short range at most"); the
    // tick toggles the mode's key, which computeWeaponPool's fold takes (unticked, a mode applies nothing)
    var UIh = window.SWRPGUI || {};
    if (!spec.barred && UIh.OfferRow) (spec.modes || []).forEach(function (m) {
      var txt = (m.poolRows || []).map(function (r) { return UIh.offerEffect ? UIh.offerEffect(r) : ''; }).filter(Boolean).join('; ') || m.words || '';
      lines.push(h(UIh.OfferRow, { key: 'md' + m.key, offerKey: m.key, name: m.source, text: txt, tags: ['mode'], on: !!(ticks[rk] && ticks[rk][m.key]),
        onToggle: function () { toggleOffer(rk, m.key); } }));
    });
    // F-100 THE SURFACING MISSION (D-294 L2; ADDENDUM 8 F5/F6): a talent's TARGET ticks right after the mode ticks, untagged —
    // "Pressure Point — Deal as strain (+Medicine)" (talentTargetOffers, DARK OFF); while a result shows, the tick reads the
    // roll's own ticks (`taken`), so the damage words and the TARGET echo below agree with it
    var Pw = window.SWRPGPoolMods, Qw = window.SWRPGQualityRows;
    var wcb = (CharResolve.talentViewFor && CharResolve.talentViewFor(sheet)) || window._canonicalCharbuild || {};
    var wctx = !spec.barred && Pw && poolIdx && spec.skillKey ? Pw.contextFor(spec.skillKey, poolIdx) : null; if (wctx && w.regId) wctx.weapon = w.regId;   // the row's record, as computeWeaponPool's rctx (R-M8K-25): Pressure Point's tick sits on the Unarmed row only (roll-options card ADDENDUM 25 F1)
    var tOffers = wctx && wcb.talents && Qw && Qw.talentTargetOffers ? Qw.talentTargetOffers(sheet, wcb, wctx) : [];
    var tNow = ticks[rk] || (rolled[rk] && rolled[rk].taken) || null;
    if (UIh.OfferRow) tOffers.forEach(function (o) {
      var on = !!(tNow && tNow[o.key]);
      lines.push(h(UIh.OfferRow, { key: 'tt' + o.key, offerKey: o.key, name: o.source, text: o.label, tags: [], on: on, onToggle: function () { toggleTarget(rk, o.key, on); },
        who: UIh.offerWho ? UIh.offerWho(o) : null, info: UIh.offerInfo ? UIh.offerInfo(o.talentId, o.rowId, wcb.talents) : null }));   // 5h: null with the key OFF
    });
    // H3 (D-229 Q1 (b)): carried gear's offers fold behind one line; the weapon's own, the armour's, talents' and species' stay open
    var wRem = diceHubWeaponReminders(sheet, poolMods, spec);
    diceHubOfferEls(rk, wRem.filter(function (m) { return m.holder !== 'gear' && !diceHubSituational(m); }),
      (!spec.barred && dl ? dl.offered : []).concat(!spec.barred && spec.critSetLines ? spec.critSetLines.offered : []), ticks[rk], toggleOffer, sheet)
      .forEach(function (el) { lines.push(el); });
    diceHubSituationFold(rk, diceHubOfferEls(rk, wRem.filter(diceHubSituational), [], ticks[rk], toggleOffer, sheet), gearOpen, toggleGear)
      .concat(diceHubGearFold(rk, diceHubOfferEls(rk, wRem.filter(function (m) { return m.holder === 'gear'; }), [], ticks[rk], toggleOffer, sheet), !!gearOpen[rk], toggleGear))
      .forEach(function (el) { lines.push(el); });
    if (!spec.barred) diceHubToldFor(sheet, spec.skillKey)
      .forEach(function (t) { lines.push(h(DiceHubTold, { key: 'td' + t.key, name: t.name, words: t.words, limit: t.limit, talentId: t.talentId, rowId: t.rowId })); });
    lines = lines.concat(rolledLines('w' + i));
    // H4 (R-SHC-8, D-229 Q2 (a)): the TARGET chip has something to say only with M12's key ON (targetRows null OFF —
    // ADDENDUM 7 Q1 (a)); after a roll, what the weapon's qualities do to the target chosen
    var Q = window.SWRPGQualityRows, qdb = window._qualitiesDb;
    var targetable = !spec.barred && !!(Q && Q.targetRows && qdb && Q.targetRows(w, { kind: 'personal' }, { vocab: qdb }));
    var kind = targetable ? (tgt[rk] || null) : null;
    var ro = rolled[rk];
    var tTaken = ro ? tOffers.filter(function (o) { return ro.taken && ro.taken[o.key]; }).map(function (o) { return { talentId: o.talentId, source: o.source, row: o.row }; }) : [];
    if (ro && kind) diceHubTargetLines(Q.targetRows(w, { kind: kind }, { vocab: qdb, talentTargets: tTaken }), kind)
      .forEach(function (t, k) { lines.push(h(DiceHubStatusLine, { key: 'tg' + k, m8: 'applied', tone: 'var(--text-info)' }, t)); });
    // H2 (R-SHC-2): after a roll, THE SPEND MENU — the weapon's activations (dark with M12's key OFF: spendRows lists none),
    // a ticked MODE's rows only while it was ticked at the roll (R-SHC-4), droid variants only against a droid (R6)
    // D-294 L1 (ADDENDUM 8 F12): the owner's talent spend rows join the SAME menu (talentSpendRows, DARK OFF)
    if (ro && Q && Q.spendRows) {
      var tk = ro.taken || {}, rspec = ro.spec || spec, hm = ro.resolved && ro.resolved.succeeded ? 'hit' : 'miss';
      var menu = Q.spendRows(w, { vocab: qdb, when: hm,   // no vocabulary → [] (spendRows' own test)
        modes: (rspec.modes || []).filter(function (m) { return tk[m.key]; }).map(function (m) { return m.modeId; }), target: kind ? { kind: kind } : undefined })
        .concat(wctx && wcb.talents && Q.talentSpendRows ? Q.talentSpendRows(sheet, wcb, wctx, { when: hm }) : []);
      if (menu.length) {
        var sm = diceHubSpendModel(menu, ro.resolved, ro.spent);
        lines.push(h(DiceHubSpend, { key: 'spend', rows: sm.rows, lines: sm.lines, tally: sm.tally, tallyTokens: sm.tallyTokens, onToggle: function (r) { toggleSpend(rk, r.key); } }));
      }
    }
    // D-294 L3 on a WEAPON row (ADDENDUM 8 F12: Natural Marksman's, Brawler's checks) — D-352: on the result's line
    var wReroll = diceHubRerollSlot(ro, ro && wctx && Pw.rerollsAt ? Pw.rerollsAt(poolMods, wctx) : [], latestKey === rk, function (nm) { rerollRow(rk, nm); });
    // the chips (H1): DIFFICULTY reads the row's own price ("Long · 3") until moved (ADDENDUM 7 Q6 (a))
    var dNow = spec.difficultySet != null ? diceHubDifficultyName(spec.difficultySet) : shownBand + ' · ' + spec.rangeDifficulty;
    var dPressed = spec.difficultySet != null ? spec.difficultySet : spec.rangeDifficulty;

    return h(DiceHubRow, { key: 'w' + i, testid: 'dice-hub-weapon', rowKey: 'w' + i, name: name,
      spec: spec, accent: accent, rolled: rolled['w' + i], reroll: wReroll,
      onRoll: function () { doRoll('w' + i, spec); } },
      spec.barred ? null : h(DiceHubDamage, { damage: diceHubQualityDamage(sheet, w, spec).damage, crit: spec.critThreshold, plus: diceHubDamagePlus(dl, ticks['w' + i]),
        as: diceHubDamageAs(tOffers, dl, tNow) }),
      spec.barred ? null : diceHubHitDamage(diceHubQualityDamage(sheet, w, spec).damage, ro && ro.resolved),
      spec.barred ? null : diceHubQualityDamage(sheet, w, spec).lines.map(function (t, k) {
        return h(DiceHubStatusLine, { key: 'qd' + k, m8: 'applied', tone: 'var(--text-info)' }, t); }),
      spec.barred ? null : m8ItemChanges(w, ['crit_delta', 'damage_delta']).map(function (f, k) {
        var t = m8WeaponChangeText(sheet, f, 'hub');
        return t ? h(DiceHubStatusLine, { key: 'ic' + k, m8: 'applied', tone: 'var(--text-info)' }, t) : null; }),
      h('div', { 'data-dh-chips': rk, style: { display: 'flex', alignItems: 'center', gap: 9, marginTop: 8, flexWrap: 'wrap', rowGap: 8 } },
        h('span', { style: { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.16em', color: 'var(--doc-ink-muted)' } }, 'RANGE'),
        hasChip
          ? h('button', { type: 'button', 'data-testid': 'dice-hub-band-chip',
              'aria-expanded': openStrip === i ? 'true' : 'false',
              'aria-label': 'Target range: ' + shownBand,
              onClick: function () { setOpenStrip(openStrip === i ? null : i); setChipOpen(function (m) { return dropKey(m, rk); }); },
              style: { minHeight: 44, padding: '0 13px', borderRadius: 9, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 7, touchAction: 'manipulation',
                fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-sm)' } },   // its ink, edge and fill are sheet.css's (`.sb-dh [data-testid="dice-hub-band-chip"]`)
              shownBand, h('span', { 'aria-hidden': 'true', style: { fontSize: 10 } }, '▾'))
          : h('span', { 'data-testid': 'dice-hub-band-static', style: {
              fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-sm)' } }, shownBand),
        targetable ? h(DiceHubChip, { label: 'TARGET', aria: 'Target', testid: 'dice-hub-target-chip', open: chipOpen[rk] === 'target',
          value: (DICE_HUB_TARGETS.filter(function (t) { return t[0] === (kind || 'none'); })[0] || ['', 'None'])[1], onToggle: function () { toggleChip(rk, 'target'); } }) : null,
        spec.barred ? null : h(DiceHubChip, { label: 'DIFFICULTY', aria: 'Difficulty', testid: 'dice-hub-difficulty-chip', open: chipOpen[rk] === 'difficulty',
          value: dNow, onToggle: function () { toggleChip(rk, 'difficulty'); } })),
      chipOpen[rk] === 'target' && targetable ? h(DiceHubChipStrip, { kind: 'target', label: 'Target', testid: 'dice-hub-target-option', cols: 4,
        options: DICE_HUB_TARGETS.map(function (t) { return { key: t[0], v: t[1], on: (kind || 'none') === t[0] }; }), onPick: function (k) { pickTarget(rk, k); } }) : null,
      chipOpen[rk] === 'difficulty' && !spec.barred ? h(DiceHubChipStrip, { kind: 'difficulty', label: 'Difficulty', testid: 'dice-hub-difficulty-option', cols: 3,
        options: DICE_HUB_DIFFICULTY.map(function (d) { return { key: d[1], v: d[0], sub: d[1] + 'D', on: d[1] === dPressed }; }), onPick: function (n) { pickDifficulty(rk, n); } }) : null,
      openStrip === i && hasChip ? h('div', { role: 'group', 'aria-label': 'Target range', style: {
        display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 5, marginTop: 7 } },
        byBand.map(function (x) {
          var inert = !!x.spec.barred, on = (x.band === shownBand) && !inert;
          return h('button', { key: x.band, type: 'button', disabled: inert,
            'aria-disabled': inert ? 'true' : 'false', 'aria-pressed': on ? 'true' : 'false',
            'data-testid': inert ? 'dice-hub-band-inert' : 'dice-hub-band-option',
            // The chip passes the CORE'S CANONICAL TOKEN, never a raw string — C-BBA-1's canonBand
            // seam is the mechanism, and handing it a display label would re-open the case bug.
            onClick: inert ? null : function () {
              setBand(function (m) { var n = Object.assign({}, m); n[i] = x.band; return n; });
              setOpenStrip(null);
              setRolled(function (m) { var n = Object.assign({}, m); delete n['w' + i]; return n; });
            },
            style: { minHeight: 44, minWidth: 0, borderRadius: 8, cursor: inert ? 'not-allowed' : 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
              opacity: inert ? 0.55 : 1,   // its ink, edge and fill (pressed, inert) are sheet.css's `.sb-dh [data-testid="dice-hub-band-…"]`
              fontFamily: 'var(--font-body)', padding: '0 3px' } },
            h('span', { style: { fontSize: 13, fontWeight: 600, lineHeight: 1.1 } }, x.band),
            h('span', { style: inert
              ? { fontSize: 10.5, lineHeight: 1.15, textAlign: 'center' }
              : { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '.06em' } },
              inert ? x.spec.barredReason : x.spec.difficulty + 'D'));
        })) : null,
      lines);
  }).filter(Boolean);

  // ── SKILLS ── the FULL trained list (C-DHM-5; the mockup's six was frame economy). Same call
  // shape the cockpit's own skill roller uses: {characteristic, skillRank, difficulty: 2}.
  // B-619: ranks read through `sheetSkillRank` — the alias-aware read the paper stage uses — so a rank
  // saved under the OggDude key (`LTSABER` for the sheet's `LTSAB`) draws its row and rolls its rank.
  // THE FORCE SWITCH (E-FFL-4): the trained test reads the row's rank WITH the session's commits, so Farsight's committed
  // Perception lists Perception at rank 0 + commits; the key OFF reads the saved rank, exactly as before.
  var trained = Object.keys(SKILL_NAMES).map(function (k) { return diceHubSkillRow(sheet, k, committedProp); })
    .filter(function (r) { return r && (r.rank > 0 || (initialStrip && r.key === initialStrip)); }).sort(function (a, b) { return a.name.localeCompare(b.name); });

  var skillRows = trained.map(function (s) {
    var using = sw[s.key] || null;   // F-100 M7 W3: the switch, if pressed — the dice redraw from the spec they roll
    var rolledChar = diceHubRolledCharacteristic(s, using);
    var taken = ticks[s.key];   // F-100 M8 step 3: this row's ticked offers, for one roll
    var spec = diceHubSkillSpec(sheet, s, taken && window.SWRPGPoolMods ? window.SWRPGPoolMods.takeOffers(poolMods, taken) : poolMods, using, accent, dset[s.key]);
    // THE JOIN THAT USED TO BE A GUESS. This was `s.name.toLowerCase().replace(/ /g, '_')` — which
    // turns "Ranged (Light)" into `ranged_(light)` and matches no anchor in the registry, so every
    // parenthesised and hyphenated skill silently showed none of its item effects. The vocabulary
    // index does the join on registry data (`aliases` carries the sheet's ALL-CAPS key), so the
    // three spellings the ingest documents cannot come apart from the sheet's fourth.
    var applied = DiceHubAppliedLines(spec, s.name, null, sheet);
    // F-100 M8 step 3 (R-M8K-28): every certified offered entry is an OFFER row (the one row), an uncertified one stays reminded
    // F-100 M8 M2: a talent that offers this row a skill substitution draws its same-check offers ON that line (taken with it)
    var subT = diceHubSubstituteTalents(s.sc);
    var sRem = poolModRemindersFor(poolMods, s.key, rolledChar).filter(function (m) { return !subT[m.talentId]; });
    var tcb = (CharResolve.talentViewFor && CharResolve.talentViewFor(sheet)) || window._canonicalCharbuild || {};
    // H3 (D-229 Q1 (b)): the row's own offers open, then carried gear's behind the fold; H5: a ticked floored removal at its
    // floor says so ("Difficulty stays 1 — Master Doctor removes none below the floor (EotE p.138)")
    var reminders = diceHubOfferEls(s.key, sRem.filter(function (m) { return m.holder !== 'gear' && !diceHubSituational(m); }), [], taken, toggleOffer, sheet)
      .concat(diceHubFloorLines(spec, sRem, taken, tcb.talents).map(function (t, k) { return h(DiceHubStatusLine, { key: 'fl' + k, m8: 'applied', tone: 'var(--text-caution)' }, t); }))
      .concat(diceHubSituationFold(s.key, diceHubOfferEls(s.key, sRem.filter(diceHubSituational), [], taken, toggleOffer, sheet), gearOpen, toggleGear))
      .concat(diceHubGearFold(s.key, diceHubOfferEls(s.key, sRem.filter(function (m) { return m.holder === 'gear'; }), [], taken, toggleOffer, sheet), !!gearOpen[s.key], toggleGear))
      .concat(diceHubToldFor(sheet, s.key, rolledChar).map(function (t) { return h(DiceHubTold, { key: 'td' + t.key, name: t.name, words: t.words, limit: t.limit, talentId: t.talentId, rowId: t.rowId }); }))   // frame F6
      .concat(rolledLines(s.key));
    // F-100 M7 W3 (R-M7G-21(g), D-13): under one row, the characteristic line FIRST (with its switch), then the applied
    // dice, then the offers (M7's, then the items' reminders). The untrained strip ROLL opened is the same row (f).
    var m7 = m7HubLines(sheet, s.key, s.rank, s.sc, window._canonicalCharbuild, using, poolMods);
    // the switch holds a characteristic (M7) or a substitution (M2); pressing the pressed one releases it
    var swId = function (v) { return v && typeof v === 'object' ? 'sub:' + v.talentId + ':' + v.skill : v; };
    var press = function (c) {
      setSw(function (m) { var n = Object.assign({}, m); if (swId(n[s.key]) === swId(c)) delete n[s.key]; else n[s.key] = c; return n; });
      setRolled(function (m) { var n = Object.assign({}, m); delete n[s.key]; return n; });
    };
    var m7Box = function (key, kids) { return h('div', { key: key, 'data-m7-hub': s.key, style: { display: 'grid', gap: 6, margin: '6px 0 2px' } }, kids); };
    var head = m7.caption ? m7Box('m7c', [h(M7HubLine, { key: 'c', kind: 'caption', text: m7.caption })].concat(
      m7.others.map(function (o) { return h(M7HubLine, { key: 'o' + o.characteristic, kind: 'other', text: o.text, btn: 'Use ' + m7CapChar(o.characteristic) + ' this roll',
        pressed: using === o.characteristic, onPress: function () { press(o.characteristic); } }); }))) : null;
    var offers = m7.offers.length ? m7Box('m7f', m7.offers.map(function (o, k) { return h(M7HubLine, { key: 'f' + k, kind: 'offer', text: o.text, btn: 'Use',
        pressed: using === o.characteristic, onPress: function () { press(o.characteristic); } }); })) : null;
    // M2 (drawing (a), D-81): the substitution lines, the pressed caption first
    var subsBox = m7.subs.length ? m7Box('m7s', (m7.subCaption ? [h(M7HubLine, { key: 'sc', kind: 'caption', text: m7.subCaption })] : []).concat(
      m7.subs.map(function (u) { return h(M7HubLine, { key: 's' + u.id, kind: 'offer', text: u.text, btn: u.btn, pressed: u.pressed,
        onPress: function () { press(u.value); } }); }))) : null;
    // H5 (R-SHC-10, D-229 Q3 (a)): the DIFFICULTY chip on the row's chip line — Average until the player sets it
    var sd = dset[s.key];
    var chips = h('div', { key: 'chips', 'data-dh-chips': s.key, style: { display: 'flex', alignItems: 'center', gap: 9, marginTop: 8, flexWrap: 'wrap', rowGap: 8 } },
      h(DiceHubChip, { label: 'DIFFICULTY', aria: 'Difficulty', testid: 'dice-hub-difficulty-chip', open: chipOpen[s.key] === 'difficulty',
        value: diceHubDifficultyName(sd != null ? sd : 2), onToggle: function () { toggleChip(s.key, 'difficulty'); } }));
    var strip = chipOpen[s.key] === 'difficulty' ? h(DiceHubChipStrip, { key: 'dstrip', kind: 'difficulty', label: 'Difficulty', testid: 'dice-hub-difficulty-option', cols: 3,
      options: DICE_HUB_DIFFICULTY.map(function (d) { return { key: d[1], v: d[0], sub: d[1] + 'D', on: d[1] === (sd != null ? sd : 2) }; }), onPick: function (n) { pickDifficulty(s.key, n); } }) : null;
    // F-100 THE SURFACING MISSION (D-294 L1/L3; ADDENDUM 8): after a roll, the owner's talent spend rows in the hub's ONE spend
    // menu (talentSpendRows); the reroll and its "Rerolled — <talent>" mark sit in the result's slot (D-352) — all DARK OFF
    var sro = rolled[s.key], Ps = window.SWRPGPoolMods, Qs = window.SWRPGQualityRows, extra = [];
    var sctx = sro && Ps && poolIdx ? Ps.contextFor(s.key, poolIdx) : null;
    var smenu = sctx && tcb.talents && Qs && Qs.talentSpendRows ? Qs.talentSpendRows(sheet, tcb, sctx, { when: sro.resolved && sro.resolved.succeeded ? 'hit' : 'miss' }) : [];
    if (smenu.length) {
      var ssm = diceHubSpendModel(smenu, sro.resolved, sro.spent);
      extra.push(h(DiceHubSpend, { key: 'spend', rows: ssm.rows, lines: ssm.lines, tally: ssm.tally, tallyTokens: ssm.tallyTokens, onToggle: function (r) { toggleSpend(s.key, r.key); } }));
    }
    var sReroll = diceHubRerollSlot(sro, sctx && Ps.rerollsAt ? Ps.rerollsAt(poolMods, sctx) : [], latestKey === s.key, function (nm) { rerollRow(s.key, nm); });   // D-352
    var lines = [chips, strip, head, subsBox].concat(applied, [offers], reminders, extra).filter(Boolean);
    return h(DiceHubRow, { key: s.key, testid: 'dice-hub-skill', rowKey: s.key, name: s.name,
      spec: spec, accent: accent, rolled: rolled[s.key], reroll: sReroll,
      onRoll: function () { doRoll(s.key, spec); if (using) setSw(function (m) { var n = Object.assign({}, m); delete n[s.key]; return n; }); } }, lines);
  });

  // ── FORCE ── absent ENTIRELY for a non-Force character, matching SheetForceZone's null (card §2.2).
  var fr = derived.forceRating || 0;
  var powers = fr >= 1 ? sheetForcePowersForDisplay(sheet) : [];
  // B-622: the paper sheet's unit commits dice for the session and the hub rolls what is left; the legacy cockpit passes nothing (its zone kept its own).
  // F-100 M10 (R-M10S-5, R-M10B-6c): `committedProp` is the session's committed SET ({<power id>: true, <power>.<ability>: n},
  // R-SHC-5) or a count;
  // the ONE committed-count function reads either — the same max(0, fr − committed) as before.
  var available = CharResolve.forceDiceAvailable(fr, committedProp || 0);
  // H6 (R-SHC-2/5/9): after a power's roll, its DIALS (powerDialsAt, priced in Force points) and each owned Duration upgrade
  // that commits dice — the commit is the one spend that writes, through the sheet's ONE writer (`onCommit`, session only).
  // Both read the Force key (dark OFF). With every die committed the rows are BARRED (key-independent: live, ADDENDUM 7 Q1 (a)).
  var fOn = !!(window.SWRPGForceRules && SWRPGForceRules.on && SWRPGForceRules.on());
  var fcb = (CharResolve.talentViewFor && CharResolve.talentViewFor(sheet)) || window._canonicalCharbuild || {};
  var fOwned = fOn && powers.length && CharResolve.forcePowerRowSources ? CharResolve.forcePowerRowSources(sheet, fcb) : [];
  var held = CharResolve.committedForceDice ? CharResolve.committedForceDice(committedProp || 0) : 0;
  var forceRows = powers.map(function (fp, i) {
    var nm = typeof fp === 'string' ? fp : (fp.name || 'Power');
    var pid = typeof fp === 'string' ? null : fp.id;
    var spec = diceHubForceSpec(sheet, poolMods, available, accent, pid);
    if (fr > 0 && available === 0) spec = Object.assign({}, spec, { barred: true, barredReason: 'No Force die free — release one on the sheet', held: held });
    // Natural Mystic (D-350; card ADDENDUM 62 F7 (a), brief §7): its reroll in the result's slot (D-352), as the skill rows
    // place theirs — gated by the talent's DARK row (rerollsAt), not by the Force switch
    var ro = rolled['f' + i], kids = [];
    if (ro && fOn && pid && window.SWRPGPoolMods) {
      var P = window.SWRPGPoolMods;
      var ctx = poolIdx ? P.contextFor(null, poolIdx, undefined, 'force_power', pid) : null;
      var abil = (fcb.forcePowers && fcb.forcePowers[pid] && fcb.forcePowers[pid].abilities) || {};
      var dials = (ctx && P.powerDialsAt ? P.powerDialsAt(fOwned, ctx, sheet) : []).map(function (d) {
        return Object.assign({}, d, { words: (abil[String(d.talentId).split(':').pop()] || {}).description || '' }); });
      var fm = diceHubForceMenuModel(dials, diceHubDurationCommits(fp, fcb), (ro.resolved && ro.resolved.light) || 0, ro.spent, committedProp, available);
      fm = diceHubForceMenuCapped(fm, function (k) { return CharResolve.commitAtCap ? CharResolve.commitAtCap(sheet, fcb, committedProp, k) : null; });   // F5
      if (fm.rows.length) kids.push(h(DiceHubSpend, { key: 'spend', note: 'priced in Force points', rows: fm.rows, lines: fm.lines, tally: fm.tally, tallyTokens: fm.tallyTokens,
        onToggle: function (r) {
          if (r.commitKey && r.stacks) { if (!r.inert && onCommit) { onCommit(r.commitKey, true); toggleSpend('f' + i, r.key); } }   // one more commit per roll, never a release
          else if (r.commitKey) { if (onCommit) onCommit(r.commitKey, !r.on); } else toggleSpend('f' + i, r.key); } }));
    }
    var Pr = window.SWRPGPoolMods, rctx = ro && Pr && poolIdx ? Pr.contextFor(null, poolIdx, undefined, 'force_power', pid) : null;
    var fReroll = diceHubRerollSlot(ro, rctx && Pr.rerollsAt ? Pr.rerollsAt(poolMods, rctx) : [], latestKey === 'f' + i, function (nm) { rerollRow('f' + i, nm); });   // D-352
    return h(DiceHubRow, { key: 'f' + i, testid: 'dice-hub-force', rowKey: 'f' + i, name: nm,
      spec: spec, accent: accent, rolled: ro, reroll: fReroll,
      onRoll: function () { doRoll('f' + i, spec); } }, kids);
  });

  // The footer's catch-all: entries where NO anchor was ever recorded. They match no check, so no
  // row can carry them and none of them can ever apply — showing them here is the only way the
  // player learns the item claims something at all. `unanchored` lives in the assembler so this
  // "null matches nothing" rule has one expression, not two (R-PCA-2).
  var unscoped = (window.SWRPGPoolMods
    ? poolMods.filter(window.SWRPGPoolMods.unanchored)
    : []);

  // PORTALLED to document.body, following this file's own precedent (`renderShell`, :3957). The
  // sheet sits inside an ancestor with `position: relative; z-index: 1`, which opens a stacking
  // context — so no z-index the hub could name would ever have lifted it above the suite bar and
  // the bottom nav; they are simply in a different context. Measured, not guessed: the first
  // attempt raised the number and changed nothing. The portal is the mechanism, the z-index only
  // orders it against the app's other body-level overlays.
  // ═══ B-638 · THE HUB ON PAPER: a window on the sheet, not a modal over it — no scrim (the sheet stays readable
  // and pressable beside it), the plate with a head you drag (⠿ · DICE · the name · ✕), the sections as entries,
  // the reference folded at the foot. Escape and ✕ close it. Same rows, same rolls, same testids.
  var secP = function (label, note) { return h('div', { className: 'sb-grp' }, label, note ? h('span', null, note) : null); };
  // ── TREAT AN INJURY — B-1209 retire (D-172; the Fork R frames, ui-lab 45dd0a8d8 `retire.*`, are LAW). Opened from the
  // crit plate's Medicine on ONE injury, the hub carries the three things it lacked: the injury's difficulty preset
  // (critTreatSpec, folded before the roll), the write at the roll (a success writes the injury off through the sheet's
  // writer, `onTreated`; a failure writes nothing) and the entry that names the injury. The hub's own parts, nothing new.
  // B-1263 (D-187's frames, ui-lab 2d19c0239, B1): the row draws the SKILL rows' own lines by their own calls on its MED context —
  // applied, then the offers (ticked through `ticks.treat`, taken by critTreatSpec's fold), then the told lines, then what a
  // roll carried — between its first line and its last (the promise or the outcome). After a roll the pool shown is the pool
  // ROLLED, and "difficulty N" reads that pool's difficulty dice, so neither contradicts a lowered pool (C-4, findings 6/7).
  // R6 U6 (card release-r6-ui ADDENDUM 5): the hub opened on ONE given check — a Follower's or a network's — { spec, label },
  // the spec from SWRPGRewards' rewardSpec/contactSpec only. An ordinary spec row: doRoll, the DIFFICULTY chip (Average until
  // set, as a skill's), a School's flat symbols riding the spec's `_autoSymbols` (the pool's existing added-result path).
  var givenSection = null;
  if (given && given.spec) {
    var gd = dset.given;
    var gSpec = Object.assign({}, given.spec, { difficulty: gd != null ? gd : 2 });
    var gChips = h('div', { key: 'chips', 'data-dh-chips': 'given', style: { display: 'flex', alignItems: 'center', gap: 9, marginTop: 8, flexWrap: 'wrap', rowGap: 8 } },
      h(DiceHubChip, { label: 'DIFFICULTY', aria: 'Difficulty', testid: 'dice-hub-difficulty-chip', open: chipOpen.given === 'difficulty',
        value: diceHubDifficultyName(gd != null ? gd : 2), onToggle: function () { toggleChip('given', 'difficulty'); } }));
    var gStrip = chipOpen.given === 'difficulty' ? h(DiceHubChipStrip, { key: 'dstrip', kind: 'difficulty', label: 'Difficulty', testid: 'dice-hub-difficulty-option', cols: 3,
      options: DICE_HUB_DIFFICULTY.map(function (d) { return { key: d[1], v: d[0], sub: d[1] + 'D', on: d[1] === (gd != null ? gd : 2) }; }), onPick: function (n) { pickDifficulty('given', n); } }) : null;
    givenSection = h('div', { 'data-testid': 'dice-hub-given' },
      h(DiceHubRow, { testid: 'dice-hub-given-row', rowKey: 'given', name: given.label || '', spec: gSpec, accent: accent, rolled: rolled.given,
        onRoll: function () { doRoll('given', gSpec); } }, [gChips, gStrip].concat(rolledLines('given'), diceHubContactResults(given, rolled.given, rwdReg, t38Open,
          function () { setT38Open(function (o) { return !o; }); })).filter(Boolean)));
  }
  var treatSection = null;
  if (treat) {
    var tTaken = ticks.treat;
    var tr = critTreatSpec(sheet, treat, accent, poolMods, tTaken);
    var tDone = rolled.treat || null;
    var tSpec = (tDone && tDone.spec) || tr.spec;
    var tBuilt = D.buildPool(tSpec) || {};
    var tOk = tDone ? !!(tDone.resolved && tDone.resolved.success > 0) : null;
    var tName = tr.spec.skill.name;   // Medicine, or Mechanics for a droid (B-1586)
    var tMid = DiceHubAppliedLines(tSpec, tName, null, sheet)
      .concat(diceHubOfferEls('treat', poolModRemindersFor(poolMods, tr.skill, tr.characteristic), [], tTaken, toggleOffer, sheet))
      .concat(diceHubToldFor(sheet, tr.skill, tr.characteristic).map(function (t) { return h(DiceHubTold, { key: 'td' + t.key, name: t.name, words: t.words, limit: t.limit, talentId: t.talentId, rowId: t.rowId }); }))
      .concat(rolledLines('treat'));
    var tLines = [
      h(DiceHubStatusLine, { key: 'tn', tone: 'var(--text-caution)' }, 'Treating ' + treat.name + ' — '
        + (tr.tier ? (/^[AEIOU]/.test(tr.tier) ? 'an ' : 'a ') + tr.tier + ' injury, difficulty ' : 'difficulty ') + ((tBuilt.difficulty || 0) + (tBuilt.challenge || 0))),
    ].concat(tMid, [
      tDone ? h('div', { key: 'to', role: 'status', 'data-testid': 'dice-hub-treat-outcome' },
          h(DiceHubStatusLine, { tone: 'var(--text-info)', glyph: tOk ? '✓' : '▸' },
            tOk ? 'Treated — ' + treat.name + ' is off your injuries.' : 'Not treated — ' + treat.name + ' stays on your injuries.'))
        : h(DiceHubStatusLine, { key: 'tw', tone: 'var(--text-info)' }, 'A success removes it from your injuries.'),
    ]);
    // the head as the frames draw it: BOTH words are the group's note runs (`.sb-grp > span`), so the label reads in the
    // note's ink, not the section labels' (frame_delta, scripts/shots_b1209_retire.py, against lab 45dd0a8d8)
    treatSection = h('div', { 'data-testid': 'dice-hub-treat' },
      h('div', { className: 'sb-grp' }, h('span', null, 'Treat an injury'), h('span', null, 'the injury sets the difficulty')),
      h(DiceHubRow, { testid: 'dice-hub-treat-row', rowKey: 'treat', name: tName, spec: tSpec, accent: accent, rolled: tDone,
        onRoll: function () { var r = doRoll('treat', tr.spec); if (r && r.success > 0 && onTreated) onTreated(treat.id); } }, tLines));
  }
  return ReactDOM.createPortal(h('div', { 'data-testid': 'dice-hub-scrim', 'data-paper': '1', className: 'sb-dh-backdrop' },
    h('div', { 'data-testid': 'dice-hub', 'data-paper': '1', role: 'dialog', 'aria-label': 'Dice hub', className: 'hp-sheet sb-dh', 'data-doc-ground': 'creator', ref: win.ref,
      style: win.pos ? { left: win.pos.left, top: win.pos.top, right: 'auto' } : null },
      h('div', Object.assign({ className: 'sb-dh-head', 'data-testid': 'dice-hub-head' }, win.handle),
        h('span', { className: 'sb-dh-grip', 'aria-hidden': 'true' }, '⠿'),
        h('span', { className: 'sb-dh-title' }, 'Dice'),
        h('span', { className: 'sb-dh-name' }, sheet.name || ''),
        h('button', { type: 'button', 'data-testid': 'dice-hub-close', className: 'hp-quiet', onClick: onClose, 'aria-label': 'Close' }, '✕')),
      h('div', { className: 'hp-plate sb-dh-body', 'data-pad': 'data' },
        givenSection,
        treatSection,
        weaponRows.length ? secP('Weapons') : null, weaponRows,
        skillRows.length ? secP('Skills', trained.filter(function (t) { return (sheet.skills || {})[t.key] > 0; }).length + ' trained · difficulty 2 until the GM sets it') : null, skillRows,
        forceRows.length ? secP('Force', available + ' of ' + fr + ' Force ' + (fr === 1 ? 'die' : 'dice')) : null, forceRows,
        // R7 as ADDENDUM 9 rules it: the foot list KEPT for what it now holds — the UNCERTIFIED item rows (no direction: they
        // reach no roll and nothing else tells them); B-1196's directed rows left it as offers. Its head says so (C-MU-3:
        // no approved frame draws the foot; disclosed in the gate).
        unscoped.length ? secP('From your gear') : null,
        unscoped.map(function (m, k) { return h(DiceHubReminder, { key: 'u' + k, mod: m }); }),
        h('details', { className: 'sb-fold', 'data-testid': 'dice-hub-reference-fold', open: refOpen || undefined, onToggle: function (e) { setRefOpen(e.target.open); } },
          h('summary', { className: 'sb-e-h', 'data-testid': 'dice-hub-reference-door', 'aria-expanded': refOpen ? 'true' : 'false' }, h('span', { className: 'sb-e-n' }, 'Dice reference'), h('span', { className: 'sb-e-m sb-fold-teaser' }, 'What each symbol means'), h('i', { className: 'sb-grp-glyph', 'aria-hidden': 'true' }, '▾')),
          refOpen ? h('div', { 'data-testid': 'dice-hub-reference' }, h(DiceReferenceBody, { accent: accent })) : null))
    )), document.body);
}

// The reference the hub's footer door opens (D-DH-3 — the R-PMP reference, reachable where the
// rolling happens). Glyph IDENTITY comes from the shared SYMBOLS table, so the die shapes here are
// the same objects every other surface draws (D-3A2, one table app-wide); only the one-line
// explanations are local copy.
//
// FLAGGED, not decided: the Dice Tutor has its own `DiceReference` (lib/dice/diceSurface.jsx:1361)
// and the player shell does not load that module. Rather than reach across an app boundary — beyond
// this lap's OWNS — the seven rows are drawn here. That leaves TWO reference surfaces in the
// product, which is the shape Jason has rejected before; consolidating them into `lib/shared` is a
// real follow-up and the gate says so rather than this comment being the only record of it.
function DiceReferenceBody({ accent }) {
  var SY = (window.SWRPGDice && window.SWRPGDice.SYMBOLS) || {};
  var ROWS = [
    ['proficiency', 'Proficiency', 'Your best dice — one per rank, up to your characteristic.'],
    ['ability', 'Ability', 'The rest of the characteristic-vs-rank pair.'],
    ['boost', 'Boost', 'Something is helping. Added by gear, aim, allies.'],
    ['difficulty', 'Difficulty', 'How hard the task is. The GM sets it.'],
    ['challenge', 'Challenge', 'Difficulty upgraded — an adversary, or a nastier task.'],
    ['setback', 'Setback', 'Something is in the way. Darkness, load, cover.'],
    ['force', 'Force', 'Force rating. Light and dark pips, no cancellation.'],
  ];
  // B-639 · ON PAPER: the same seven rows as the kit's ledger rows; the symbol's ink is the ground's (--doc-sym-*, F-101)
  return h('div', { className: 'sb-dref' }, ROWS.map(function (r) {
    var s = SY[r[0]];
    return h('div', { key: r[0], className: 'sb-dref-row', 'data-sym': r[0] },
      h('span', { 'aria-hidden': 'true', className: 'sb-dref-glyph', style: { fontFamily: s ? "'" + s.font + "'" : 'inherit', color: s ? s.color : 'inherit' } }, s ? s.char : ''),
      h('span', { className: 'sb-e-n' }, r[1]),
      h('span', { className: 'sb-e-t' }, r[2]));
  }));
}

// PROV-1 — "gear that remembers": the owner block on each LOADOUT item. A nickname (edit-in-place)
// + a provenance list of player-owned notes with dates (add / delete). R-ITM9: the prose is the
// player's — editable, deletable, never auto-written beyond the store's acquire stamp. Collapsed to a
// single "◈ story" affordance when there's nothing to show yet, so a fresh sheet stays clean. The
// name-with-nickname resolves at the card (Kessel Kiss · Vibro-ax). id-refs only — this is instance
// state on the persisted entry (§8); the canonical record is never touched.
function ItemOwnerBlock(props) {
  var nickname = props.nickname || '';
  var prov = Array.isArray(props.provenance) ? props.provenance : [];
  var _nd = React.useState(nickname), nickDraft = _nd[0], setNickDraft = _nd[1];
  var _note = React.useState(''), noteDraft = _note[0], setNoteDraft = _note[1];
  React.useEffect(function () { setNickDraft(props.nickname || ''); }, [props.nickname]);

  function commitNick() {
    var v = nickDraft.trim();
    if (v !== (props.nickname || '')) props.onNickname(v || null);
  }
  function addNote() {
    var v = noteDraft.trim(); if (!v) return;
    props.onProvenance(prov.concat([{ note: v, when: new Date().toISOString().slice(0, 10) }]));
    setNoteDraft('');
  }
  function deleteNote(idx) {
    props.onProvenance(prov.filter(function (_, i) { return i !== idx; }));
  }
  // B-635 (Jason 2026-09-16: "the story section looks like it uses the legacy UI … match the description"):
  // on paper the block is a FOLD like the description — STORY with a teaser (the name, the count of notes, or
  // the invitation), the name field and the notes inside. Same state, same writers, same testids.
  var teaser = nickname ? nickname + (prov.length ? ' · ' + prov.length + (prov.length === 1 ? ' note' : ' notes') : '')
    : (prov.length ? prov.length + (prov.length === 1 ? ' note' : ' notes') : null);
  return (
    <details className="sb-fold" data-testid="item-story-fold" onClick={function (e) { e.stopPropagation(); }}>
      <summary className="sb-e-h"><span className="sb-e-n">Story</span>{teaser ? <span className="sb-e-m sb-fold-teaser" data-testid="item-story-teaser">{teaser}</span> : null}<i className="sb-grp-glyph" aria-hidden="true">▾</i></summary>
      <div className="sb-grp" style={{ paddingTop: 6 }}>Name</div>
      <input className="sb-filter" type="text" value={nickDraft} data-testid="item-nickname" aria-label="Item nickname" style={{ margin: 0 }}
        onChange={function (e) { setNickDraft(e.target.value); }} onBlur={commitNick} onKeyDown={function (e) { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }} />
      {prov.length ? <div className="sb-grp" style={{ paddingTop: 10 }}>History</div> : null}
      {prov.map(function (pn, pi) {
        return (
          <div className="sb-att" key={pi} data-testid="item-story-note">
            <div className="sb-e-h"><span className="sb-e-t" style={{ margin: 0, flex: '1 1 auto' }}>{pn.note}</span>{pn.when ? <span className="sb-e-m">{pn.when}</span> : null}
              <button type="button" className="hp-quiet" aria-label="Delete this note" onClick={function () { deleteNote(pi); }}>Remove</button></div>
          </div>);
      })}
      <div className="sb-pk-verbs" style={{ marginTop: 10, paddingTop: 10 }}>
        <input className="sb-filter" type="text" value={noteDraft} data-testid="item-note" aria-label="Add a history note" style={{ margin: 0, flex: '1 1 auto', minWidth: 160 }}
          onChange={function (e) { setNoteDraft(e.target.value); }} onKeyDown={function (e) { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }} />
        <button type="button" className="hp-quiet" aria-label="Add note" disabled={!noteDraft.trim()} onClick={addNote} data-testid="item-note-add">Add</button>
      </div>
    </details>
  );
}

/* ── F-56 CRAFTING (ruling `crafting-journey-shape-and-save-shapes`) ───────────────────────────────
   Crafting is a TAKEOVER of the GEAR pane (`GearCraftStage`), reached from the managed face. It was
   a separate WORKSHOP tab until F-86 §2.7 deleted that tab; the beat itself never changed hands.
   Projects persist (`sheet.crafts`), many per character.
   Save shapes and their vocabularies live in lib/shared/craftStore.js — the only writer. */
var _craftRegs = null, _craftRegsP = null;
function loadCraftingRegistries() {
  if (_craftRegs) return Promise.resolve(_craftRegs);
  if (_craftRegsP) return _craftRegsP;
  _craftRegsP = Promise.all([
    fetch('public/data/canonical/crafting_templates.json').then(function (r) { return r.json(); }),
    fetch('public/data/canonical/crafting_spend.json').then(function (r) { return r.json(); }),
    // D4's CHOICE step lists a template's skill group, and setChoice checks a pick against it (release-r5-f1)
    fetch('public/data/canonical/skills.vocab.json').then(function (r) { return r.json(); }),
  ]).then(function (a) { _craftRegs = { tpl: a[0], spend: a[1], skills: (a[2] && a[2].entries) || [] }; return _craftRegs; })
    .catch(function () { return null; });
  return _craftRegsP;
}

// §2.7: `WorkshopShell` is DELETED, not stubbed. It was the WORKSHOP tab's whole body — a topbar,
// a MODIFY/CRAFT tab strip and a bench — and every one of those jobs now belongs to the GEAR pane:
// MODIFY is `GearModifyStage`, CRAFT is `GearCraftStage`, and the bench's own tab strip is the thing
// the fold exists to remove. `CraftWorkshop` below is the one piece that SURVIVES; its mount moved to
// `GearCraftStage`, so it is reached from the managed face rather than from a tab that no longer exists.

// The workshop's check pool — named and top-level so a guard can EXECUTE it (R-M7G-8); `CraftWorkshop`'s `craftPool`
// calls it with the component's sheet. The characteristic is THE shared answer (F-100 M7 C2).
const CRAFT_DIFF_DICE = { simple: 0, easy: 1, average: 2, hard: 3, daunting: 4, formidable: 5 };
const CRAFT_SKILL_KEY = { mechanics: 'MECH', survival: 'SURV', discipline: 'DISC', lore: 'KNOWLORE' };   // + alchemy's (UP p.82, p.85)
const CRAFT_CHAR_PROP = { BR: 'brawn', AG: 'agility', INT: 'intellect', CUN: 'cunning', WIL: 'willpower', PR: 'presence' };
function craftSkillKey(canonId) { return CRAFT_SKILL_KEY[canonId] || String(canonId || '').toUpperCase(); }
// → null when the vocabulary has no answer for the skill (before it loads).
// F-100 M8 B-1062 (C2, R-M8K-2): the crafting roll folds the owner's AUTO-APPLYING item and talent dice through the one
// fold (`withPoolMods` over `collectPoolMods`), with the pool builder's kind `craft` — so a row anchored to `roll: craft`
// (Inventor) applies HERE and on no plain Mechanics row, and a row anchored to the check's skill applies as it does on the
// hub. `dice` is the BUILT pool the workshop rolls and draws; `total`/`upg`/`diff` stay the bare check's numbers.
function craftPoolOf(sheet, t, canonId) {
  var key = craftSkillKey(canonId);
  var _sc = sheetSkillCharacteristic(sheet, key);   // F-100 M7 C2: the characteristic this skill rolls with
  if (!_sc) return null;   // R-M7G-9: no pool until the vocabulary answers (the workshop waits on it: "Loading the workshop…")
  var charKey = CharResolve.CHAR_ABBR[_sc.characteristic] || 'INT';
  var chars = sheet.characteristics || {};
  var charVal = chars[CRAFT_CHAR_PROP[charKey]] || 2;         // defensive: an old sheet still rolls
  var rank = (sheet.skills || {})[key] || 0;
  var diff = CRAFT_DIFF_DICE[t.check.difficulty] != null ? CRAFT_DIFF_DICE[t.check.difficulty] : 2;
  // R-R5CC-3: a schematic removes `level` difficulty steps from THIS template's check, floored at Simple (0 dice) — the
  // only difficulty path, so the workshop's pool, the hub and the result all read the same number.
  if (window.SWRPGCraft && t.id) diff = window.SWRPGCraft.schematicDiff(diff, sheet.craft_schematics, t.id);
  var spec = withPoolMods({ characteristic: charVal, skillRank: rank, difficulty: diff },
    collectPoolMods(resolveSheetEquipment(sheet.equipment, sheet)), key, CRAFT_CHAR_PROP[charKey], 'craft');
  // R-R5CC-4, THE FORCE LEG: an alchemy template's check adds one Force die per point of CURRENT Force rating (UP p.80),
  // read the way the sheet reads it (resolvedForceRating, the sheet's one read; no second derivation). `gate` is
  // the engine's refusal below rating 1, with the book's reason; a non-alchemy template adds none and is never refused.
  var force = 0, gate = { ok: true };
  if (window.SWRPGCraft && window.SWRPGCraft.isAlchemy(t)) {
    var fr = resolvedForceRating(sheet);
    force = window.SWRPGCraft.craftForceDice(t, fr);
    gate = window.SWRPGCraft.craftGate(t, fr);
    if (force) spec = Object.assign({}, spec, { force: (spec.force || 0) + force });
  }
  return { charVal: charVal, charKey: charKey, rank: rank, name: (typeof SKILL_NAMES !== 'undefined' && SKILL_NAMES[key]) || key,
           total: Math.max(charVal, rank), upg: Math.min(charVal, rank), diff: diff, force: force, gate: gate,
           spec: spec, dice: window.SWRPGDice.buildPool(spec) };
}

// ═══ RELEASE R5 · F1, the Workshop's faces (D-291 D3–D5; D-368 C; D-370 E; D-371 F; D-377 A) ═══════════════════════════════
// D3: the templates BY KIND, in the drawn order; a kind shows only when its data fills it (R-WS-4). Ranged and lightsaber
// templates make weapons and walk the melee face, so they list under Weapons (ruling release-r5-f1-c0 R-R5F1C-1).
var CRAFT_KINDS = [['Weapons', ['brawl_melee', 'ranged', 'lightsaber']], ['Armour', ['armor']], ['Gadgets', ['gadget']],
  ['Cybernetics', ['cyber']], ['Alchemy', ['potion', 'talisman']], ['Fortifications', ['fortification']],
  ['Droids', ['droid_chassis', 'droid_directive']]];   // R6-S5 (D-389 S5-choose): the chassis and the directive, last
var CRAFT_DIFF_ORDER = ['simple', 'easy', 'average', 'hard', 'daunting', 'formidable'];
// a droid's registries for droidOf (companions.js), as ALLIES' companionProfile hands them: the templates, the spend table, the
// sheet's cybernetics
function craftDroidRegs(regs, sheet) {
  var C = window.SWRPGCompanions;
  return regs && C ? { templates: regs.tpl.templates, spend: regs.spend, cybernetics: C.cyberneticsOf((sheetItemRegistries(sheet) || {}).gear) } : null;
}
// D4 (card ADDENDUM 7; D-395): a directive programs one of the character's crafted droids — the pick where Made from stood
// (the free-text made_from RETIRED for directives). Each chip: the droid's name, then its chassis.
function CraftPrograms(p) {
  var C = window.SWRPGCompanions, TPL = p.templates || {};
  var ds = C ? C.normalizeCompanions(p.sheet && p.sheet.companions).filter(function (c) { return C.isCrafted(c.own); }) : [];
  return (
    <div className="sb-e" data-testid="craft-programs">
      <div className="sb-e-h"><span className="sb-e-n">Programs</span></div>
      {ds.length ? <GrowthChips aria="Programs" testid="craft-prog" cur={p.value} onPick={p.onPick}
        opts={ds.map(function (c) { var T0 = TPL[c.own.template_id]; return [c.id, (c.name || (T0 && T0.name) || c.id) + (T0 && c.name ? ' · ' + T0.name : '')]; })} />
        : <p className="sb-e-t" data-testid="craft-programs-none">No droid to program</p>}
    </div>);
}
// D4 · the directive's result step: its skill picks (the template's skill_picks, one row per pick, each its group's skills at the
// printed rank), written through SWRPGCraft.setPick(sel, 'directive', { skills }) — read by droidOf in order
function CraftDirectiveSkills(p) {
  var sp = ((p.template && p.template.result_profile) || {}).skill_picks || [];
  var picks = (((p.sel.picks || {}).directive || {}).skills) || [], slots = [];
  sp.forEach(function (x) { for (var k = 0; k < (Number(x.count) || 0); k++) slots.push(x); });
  if (!slots.length) return null;
  return (
    <div className="sb-e" data-testid="craft-dir-skills">
      {slots.map(function (x, i) {
        return (
          <div key={i}>
            <div className="sb-e-h"><span className="sb-e-n">Programmed skill</span><span className="sb-e-m">{'Rank ' + x.rank}</span></div>
            <GrowthChips aria="Programmed skill" testid={'craft-dir-skill-' + i} cur={picks[i] || null}
              opts={(p.skills || []).filter(function (e) { return e.group === x.group; }).map(function (e) { return [e.id, e.label]; })}
              onPick={function (id) { var next = picks.slice(); next[i] = id; p.upsert(window.SWRPGCraft.setPick(p.sel, 'directive', { skills: next })); }} />
          </div>);
      })}
    </div>);
}
// D8 (card ADDENDUM 7; D-395): a chassis's result shows the droid's own fields — the landing finish would write (landingOf, pure),
// resolved as ALLIES resolves the card (SWRPGCompanions.profileOf): Soak, Wounds, Defence (W2) and the characteristics
var CRAFT_DROID_CHARS = [['brawn', 'Br'], ['agility', 'Ag'], ['intellect', 'Int'], ['cunning', 'Cun'], ['willpower', 'Will'], ['presence', 'Pr']];
function CraftDroidFields(p) {
  var C = window.SWRPGCompanions, Craft = window.SWRPGCraft, droid = craftDroidRegs(p.regs, p.sheet);
  if (!C || !Craft || !droid) return null;
  var out = Craft.landingOf(p.sel, p.template, p.index, (p.sheet && p.sheet.equipment) || {}, { companions: [], droid: droid });
  var row = out && (out.companions || [])[0], pr = row ? C.profileOf(row, {}, { droid: droid }) : null;
  if (!pr || !pr.record) return null;
  var r = pr.record, d = r.derived || {}, ch = r.characteristics || {};
  return (
    <div data-testid="craft-droid-fields">
      <div className="sb-e-l"><div>Soak <b data-testid="craft-droid-soak">{d.soak}</b></div><div>Wounds <b data-testid="craft-droid-wounds">{d.wounds}</b></div>
        <div>Defence <b data-testid="craft-droid-def">{(d.melee_defense || 0) + '/' + (d.ranged_defense || 0)}</b></div></div>
      <div className="sb-e-l">{CRAFT_DROID_CHARS.map(function (c) { return <div key={c[0]}>{c[1] + ' '}<b data-testid={'craft-droid-' + c[0]}>{ch[c[0]]}</b></div>; })}</div>
    </div>);
}
// D5's refusal, as drawn: the book's reason with its page (Unlimited Power p.80: "a current Force rating of at least 1")
var CRAFT_ALCHEMY_REFUSED = { words: 'Alchemy needs a Force rating of 1 or more.', cite: 'Unlimited Power p.80' };
// the difficulty a check is MADE at: the printed one, less a held schematic's steps, floored at Simple (R-R5CC-3) — the same
// steps craftPoolOf takes off the dice, so the words and the pool agree (B-1523)
function craftDiffName(sheet, t) {
  var i = CRAFT_DIFF_ORDER.indexOf(t.check.difficulty);
  var lvl = window.SWRPGCraft ? window.SWRPGCraft.schematicLevel(sheet && sheet.craft_schematics, t.id) : 0;
  return i < 0 ? t.check.difficulty : CRAFT_DIFF_ORDER[Math.max(0, i - lvl)];
}
// C (D-368): a held + says the book's limit (D1's idiom); a not_for row is refused at the tap, so it never reaches a count
function craftHeldWords(lim) {
  if (!lim) return null;
  if (lim.limit === 'max_count') return 'The book allows ' + lim.max + '.';
  if (lim.limit === 'once') return 'The book allows 1.';
  return null;
}
// A (D-377): a crafted hilt with no crystal reads "Crystal Needed" where its damage sits (and no Critical) — never a 0.
// A HILT is a lightsaber profile carrying `crystal_damage_mod` (the Endless Vigil templates) with no crystal installed.
var HILT_NEEDED = 'Crystal Needed';
var HILT_RULE = { words: 'For game purposes, this means that these crafting rules only provide a lightsaber hilt, which is incapable of producing a blade without a crystal.', cite: 'Endless Vigil p.84' };
function hiltNeedsCrystal(rec, attachments, attReg) {
  if (!rec || rec.crystal_damage_mod == null) return false;
  return !(attachments || []).some(function (a) { var r = (attReg || {})[a && a.id]; return !!(r && r.is_crystal); });
}

// D4 on the PACK panel (D-291 D4; the lab's ItemTold): a crafted item says what it was BUILT FOR (its saved choice, named at
// render) and what it was BUILT WITH that the app cannot apply — each told row of `_inline.built_with`, its name, cite and
// effect. Spend ids are unique across every category, so a row is found without the template. Nothing for a bought item.
function PackCraftTold(p) {
  var r = p.entry && p.entry._inline;
  var _r = React.useState(_craftRegs), regs = _r[0], setRegs = _r[1];
  var crafted = !!(r && (r.chosen || (r.built_with || []).length));
  React.useEffect(function () { if (crafted && !regs) loadCraftingRegistries().then(setRegs); }, [crafted]);
  if (!crafted || !regs) return null;
  var spend = (regs.spend && regs.spend.spend) || {}, rowOf = function (id) {
    var hit = null;
    Object.keys(spend).some(function (c) { return ['improvements', 'flaws'].some(function (side) {
      hit = (spend[c][side] || []).filter(function (x) { return x.id === id; })[0] || null; return !!hit; }); });
    return hit;
  };
  var seen = {}, told = (r.built_with || []).map(rowOf).filter(function (x) { if (!x || !x.told || seen[x.id]) return false; seen[x.id] = 1; return true; });
  var skillLabel = function (id) { var e = (regs.skills || []).filter(function (s) { return s.id === id; })[0]; return e ? e.label : id; };
  var chosen = r.chosen ? (r.chosen.skill ? skillLabel(r.chosen.skill)
    : String(r.chosen.characteristic || '').charAt(0).toUpperCase() + String(r.chosen.characteristic || '').slice(1) + ' +1') : null;
  if (!chosen && !told.length) return null;
  return <div data-testid="pack-craft-told">
    {chosen ? <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Built for</span><span className="sb-e-m" data-testid="pack-built-for">{chosen}</span></div></div> : null}
    {told.length ? <div className="sb-e" data-testid="pack-built-with"><div className="sb-e-h"><span className="sb-e-n">Built with</span></div>
      {told.map(function (x) { return <div className="sb-att" key={x.id}><div className="sb-e-h"><span className="sb-e-n">{x.name}</span><span className="sb-e-m">{x.cite}</span></div>
        <p className="sb-e-t">{glyphs(x.effect)}</p></div>; })}</div> : null}
  </div>;
}

function CraftWorkshop({ sheet, accent, onSheetChange }) {
  var Craft = window.SWRPGCraft;
  var _r = React.useState(_craftRegs), regs = _r[0], setRegs = _r[1];
  React.useEffect(function () { if (!regs) loadCraftingRegistries().then(setRegs); }, []);
  var canonReady = useCanonicalReady();   // R-M7G-9: the check pool reads the vocabulary's characteristic — wait for it
  var _sel = React.useState(null), selId = _sel[0], setSelId = _sel[1];
  var _new = React.useState(false), newMode = _new[0], setNewMode = _new[1];
  var _tpl = React.useState(null), pickTpl = _tpl[0], setPickTpl = _tpl[1];
  var _rev = React.useState(null), revealFor = _rev[0], setRevealFor = _rev[1];
  var _nm = React.useState(''), craftName = _nm[0], setCraftName = _nm[1];
  var _mf = React.useState(''), madeFrom = _mf[0], setMadeFrom = _mf[1];
  var _acqNote = React.useState(''), acqNote = _acqNote[0], setAcqNote = _acqNote[1];
  // C-MEL-1: which of a template's either/or skills the crafter is rolling. Deliberately LOCAL — it
  // shapes one roll and is not resumable state, so it does not widen the ruled save shape (D-CJ4's logic).
  var _sk = React.useState(null), skillPick = _sk[0], setSkillPick = _sk[1];
  // D4's CHOICE step: the option tapped before CONTINUE TO CONSTRUCTION writes it through Craft.setChoice (local until then)
  var _cp = React.useState(null), choicePick = _cp[0], setChoicePick = _cp[1];

  if (!regs || !Craft || !canonReady || !window._canonicalCharbuild) {
    return h('div', { 'data-testid': 'craft-workshop', style: { padding: 24, color: 'rgba(255,255,255,0.6)' } }, 'Loading the workshop…');
  }
  var TPL = regs.tpl.templates;
  var construction = regs.tpl.construction;
  var projects = sheet.crafts || [];
  var notes = sheet.craft_notes || [];
  var credits = (sheet.equipment && sheet.equipment.credits) || 0;
  var sel = projects.filter(function (p) { return p.id === selId; })[0] || null;
  // finding 3 (release-r5-f1-c0 §4): every step reads the spend table of the template in hand — its OWN category — so a
  // lightsaber (or any kind) project prices, buys and applies its own rows; melee is only the fallback with nothing in hand.
  var curT = sel ? TPL[sel.template_id] : (pickTpl ? TPL[pickTpl] : null);
  var cat = (curT && curT.category) || 'brawl_melee';
  var catRows = (regs.spend.spend && regs.spend.spend[cat]) || { improvements: [], flaws: [] };
  var index = Craft.indexSpend(regs.spend, cat);
  // a remembered note names a row of any category it was bought in
  var noteRow = function (id) {
    var all = (regs.spend && regs.spend.spend) || {}, hit = null;
    Object.keys(all).some(function (c) { return ['improvements', 'flaws'].some(function (s) { hit = (all[c][s] || []).filter(function (r) { return r.id === id; })[0] || null; return !!hit; }); });
    return hit;
  };

  // Crafting writes must PERSIST, not just re-render: a project the app forgets on reload is the exact
  // failure "paper that remembers" exists to prevent. handleSheetChange write-throughs a vitals/identity
  // whitelist OR an explicit commerce patch; crafting spends credits and puts an item on the character,
  // which is that same "a transaction changed what you own" path — so every write here declares it.
  function save(patch) { onSheetChange(patch); }
  function writeProjects(next, extra) {
    save(Object.assign({ crafts: next }, extra || {}));
  }
  function upsert(p) {
    writeProjects(projects.map(function (x) { return x.id === p.id ? p : x; }));
  }
  function stageLabel(p) {
    if (p.stage === 'materials') return { t: 'MATERIALS NEEDED', c: 'var(--text-warning)' };
    if (p.stage === 'construction') return { t: 'READY TO BUILD', c: 'var(--text-success)' };
    // B-1634 (ruling release-r7-bug-burndown ADDENDUM 347): a FAILED craft left on the bench has no results to spend
    // (B-1496 dropped that head on the walk's own failed screen), so its row reads the verdict's own word, "Failed"
    if (Craft.outcome(p.roll) === 'failed') return { t: 'FAILED', c: 'var(--text-danger)' };
    return { t: 'SPEND YOUR RESULTS', c: accent };
  }

  function craftQualityChips(base, modified) {
    var baseIds = {};
    (base.qualities || []).forEach(function (q) { baseIds[q.id] = q.rank || 0; });
    var chips = [];
    ((modified || base).qualities || []).forEach(function (q) {
      var isNew = !(q.id in baseIds);
      var isRankUp = !isNew && baseIds[q.id] < (q.rank || 0);
      chips.push({ id: q.id, label: (q.name || q.id) + (q.rank ? ' ' + q.rank : ''), isNew: isNew || isRankUp });
    });
    return chips;
  }

  // The in-app roll: the character's own pool against the template's printed difficulty, through the
  // SHARED dice engine — the same faces HoloDice and every weapon ROLL use. No second dice model.
  //
  // C-MEL-1: the pool is built from the TEMPLATE'S OWN `check.skills`, never a hardcoded assumption.
  // Four of the six melee templates print "Mechanics OR Survival" — the book lets a survivalist lash
  // together a club — and the registry stores that faithfully. Assuming Mechanics would show a
  // Survival-built character the wrong pool on the one surface whose honest pool is the promise.
  // Where the book prints a choice the CRAFTER chooses (RAW gives them the choice); where it prints
  // one skill there is no picker to offer.
  function chosenSkill(t) {
    var list = (t && t.check && t.check.skills) || [];
    return (skillPick && list.indexOf(skillPick) !== -1) ? skillPick : list[0];
  }
  var DIFF_DICE = CRAFT_DIFF_DICE, CHAR_PROP = CRAFT_CHAR_PROP;
  function craftPool(t, canonId) { return craftPoolOf(sheet, t, canonId); }
  function rollCheck(t, canonId) {
    var D = window.SWRPGDice;
    var pool = craftPool(t, canonId);
    if (!D || !D.rollAndResolve || !pool) return { su: 0, fa: 0, ad: 0, th: 0, tr: 0, de: 0 };
    var out = D.rollAndResolve(pool.dice);   // B-1062: the folded pool the workshop draws, never a bare re-count
    var r = out.resolved || {};
    var su = r.success || 0, ad = r.advantage || 0;
    var out2 = {
      su: su > 0 ? su : 0, fa: su < 0 ? -su : 0,
      ad: ad > 0 ? ad : 0, th: ad < 0 ? -ad : 0,
      tr: r.triumph || 0, de: r.despair || 0,
    };
    // D5 (R-R5CC-4): an alchemy roll's Force dice keep their light and dark pips — the spend rows are priced in them
    if (pool.force) { out2.ls = r.light || 0; out2.ds = r.dark || 0; }
    return out2;
  }

  // The tally lives inside the shared control's own state; read it off the rendered counters rather
  // than duplicating it here. Keeps ONE source of truth for what the dice showed (R-RR-3: the control
  // is consumed as-is, so we read its DOM rather than reach into it).
  function readTally() {
    var out = { su: 0, fa: 0, ad: 0, th: 0, tr: 0, de: 0 };
    var host = document.querySelector('[data-testid=craft-tally]');
    if (!host) return out;
    var keys = { SUCCESS: 'su', FAILURE: 'fa', ADVANTAGE: 'ad', THREAT: 'th', TRIUMPH: 'tr', DESPAIR: 'de' };
    Array.prototype.forEach.call(host.querySelectorAll('button[aria-label^="add "]'), function (b) {
      var k = keys[b.getAttribute('aria-label').replace('add ', '')];
      var box = b.parentNode;
      var num = box && box.querySelector('div');
      if (k && num) out[k] = parseInt(num.textContent, 10) || 0;
    });
    return out;
  }

  function finish(p, name) {
    var t = TPL[p.template_id];
    var failed = Craft.outcome(p.roll) === 'failed';
    var eq = sheet.equipment || {};
    var siblings = Craft.siblingNames(eq, t);
    var picked = (p.improvements || []).concat(p.flaws || []);
    var remembered = Craft.rememberable(picked, index);
    var patch = {
      crafts: projects.filter(function (x) { return x.id !== p.id; }),
      craft_notes: Craft.addNotes(notes, remembered, p.id),
    };
    if (!failed) {
      // THE LANDING ARM (R-R5CC-2, B-1426): a weapon to equipment.weapons, gear (gadget, cybernetic) to equipment.gear,
      // each as {_inline}; and a bought schematic row adds 1 to that template's level (R-R5CC-3, ADDENDUM 3).
      // A fortification to sheet.fortifications, equipment handed back untouched (ruling release-r5-crafting-fib-c0 Q3 (a)).
      var landed = Craft.landingOf(p, t, index, eq, { name: name, siblings: siblings, fortifications: sheet.fortifications,
        companions: sheet.companions, droid: craftDroidRegs(regs, sheet) });
      patch.equipment = landed.equipment;   // + extra_copy rows
      if (landed.fortifications) patch.fortifications = landed.fortifications;
      // R6-S5 · B-1528 (card release-r6-ui §2.6): a chassis lands a companion and a directive programs one — both through
      // landingOf → droidLanding, in `companions`, never in equipment (which comes back untouched)
      if (landed.companions) patch.companions = landed.companions;
      var schem = Craft.schematicPatch(p, index, sheet.craft_schematics);
      if (schem) patch.craft_schematics = schem;
    }
    save(patch);
    setSelId(null); setCraftName(''); setMadeFrom('');
  }


  // §2.7: the standalone chrome is GONE with the tab that needed it. `CraftWorkshop` now has exactly
  // one mount (`GearCraftStage`) and it is always in-shell, so the old `!inShell` arms — a WORKSHOP
  // header with `craft-close`, and a `craft-back-list` crumb — were unreachable the moment the tab
  // died. The takeover owns both jobs: `gear-craft-back` returns to GEAR, `gear-craft-done` is the
  // same exit. `onClose`, `onJourneyChange` and `craftBackToList` went with those arms — each had
  // exactly one consumer and it was the deleted chrome. Note for whoever wants it back: there is no
  // longer an in-journey "back to the template list" control, and there was none in production
  // either (`craft-back-list` was `!inShell`-gated and both mounts passed `inShell: true`), so this
  // prunes dead code rather than a capability. Adding one is undrawn UI — R-MU1.


  // ═══ B-624 · THE CRAFT WALK ON PAPER (the lab's craft_walk, all four steps — lab 13d6f46d). A room inherits the
  // anatomy of its OWN shape: crafting is ordered, one decision per stage, a commit that advances, an end — so
  // it wears the creator's furniture, the step map and the forward chevron, which the sheet's board does not.
  // The BENCH (the projects) is the room at rest; TEMPLATE · MATERIALS · CONSTRUCTION · RESULT are the steps.
  // Every state, every writer (Craft.*), every testid is the dark workshop's; the tally (ResultsReference) is
  // the one island left inside it.
  var stepIdx = newMode ? 0 : !sel ? -1 : sel.stage === 'materials' ? 1 : sel.stage === 'construction' ? 2 : 3;
  // D4 (D-291; drawings-card ADDENDUM 1): a template that takes a CHOICE walks Template · Materials · Choose · Construction ·
  // Result, and Construction is unreachable until the choice is saved. The step is DERIVED from the saved `choice`
  // (Craft.validChoice) — no new stage, no new field (release-r5-f1-c0 §3, R-Y2-1).
  var selT = sel ? TPL[sel.template_id] : null;
  var takesChoice = !newMode && !!(selT && selT.choice);
  var choosing = !!(takesChoice && sel.stage === 'construction' && !Craft.validChoice(selT, sel.choice, regs.skills));
  var steps = takesChoice ? ['Template', 'Materials', 'Choose', 'Construction', 'Result'] : ['Template', 'Materials', 'Construction', 'Result'];
  var railIdx = !takesChoice || stepIdx <= 1 ? stepIdx : (choosing ? 2 : stepIdx + 1);
  var rail2 = stepIdx >= 0 ? (
    <div className="sb-steps" data-testid="craft-steps">
      {steps.map(function (lab, i) { return <span className="sb-step" key={lab} data-state={i < railIdx ? 'done' : i === railIdx ? 'now' : 'next'} data-testid={'craft-step-' + i}>{(i + 1) + ' · ' + lab}</span>; })}
    </div>) : null;
  var toBench = function () { setSelId(null); setNewMode(false); setPickTpl(null); setRevealFor(null); };
  var cap = function (s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; };
  var D = window.SWRPGDice, sg = D && D.symbolGlyph;
  var poolLine = function (t, sk) {
    var pd = craftPool(t, sk); if (!pd) return null; var pos = [], neg = [], dp = pd.dice || {};   // B-1062: the folded pool rollCheck rolls
    // D5 (D-291 D5-check; R-R5CC-4): an alchemy check's Force dice are drawn in the pool they are rolled with
    ['proficiency', 'ability', 'boost', 'force'].forEach(function (k) { for (var i = 0; i < (dp[k] || 0); i++) pos.push(k); });
    ['challenge', 'difficulty', 'setback'].forEach(function (k) { for (var i = 0; i < (dp[k] || 0); i++) neg.push(k); });
    return (
      <div className="sb-e-l" data-testid="craft-pool">
        <div>Check <b>{cap(craftDiffName(sheet, t)) + ' ' + pd.name}</b></div>
        <div>Pool <b>{sg ? <span className="sb-pool" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>{pos.map(function (dk, i) { return sg(dk, i, 17); })}<span className="sb-of"> vs </span>{neg.map(function (dk, i) { return sg(dk, 'n' + i, 17); })}</span> : (pos.length + ' vs ' + neg.length)}</b></div>
        <div>{cap(String(CHAR_PROP[pd.charKey] || 'intellect'))} <b>{pd.charVal}</b></div>
        <div>{pd.name} <b>{pd.rank}</b></div>
      </div>);
  };
  var skillLabel = function (id) { var e = (regs.skills || []).filter(function (s) { return s.id === id; })[0]; return e ? e.label : cap(id); };
  // the result's ledger PER KIND (D-291 D4): a fortification its numbers (fortificationOf's, no Silhouette, D-367), armour its
  // soak and defence, gear its encumbrance and hard points, a weapon its damage — and a hilt "Crystal Needed" there (D-377)
  var cell = function (lab, v, tid) { return <div key={lab} data-testid={tid}>{lab} <b>{v == null ? '—' : String(v)}</b></div>; };
  var profileLedger = function (prof, extra) {
    if (prof && window.SWRPGCraft.isFortificationProfile(prof)) {
      return <div className="sb-e-l" data-testid="craft-ledger-fort">{cell('Ranged defence', prof.defense_ranged)}{cell('Occupancy', prof.passengers)}{cell('Armour', prof.armor)}{extra || null}</div>;
    }
    if (prof && !prof.damage && window.SWRPGCraft.isArmorProfile(prof)) {
      return <div className="sb-e-l" data-testid="craft-ledger-armor">{cell('Soak', prof.soak)}{cell('Defence', prof.defense || 0)}{cell('Encumbrance', prof.encumbrance)}{cell('Hard points', prof.hardpoints)}{extra || null}</div>;
    }
    if (prof && !prof.damage) {
      return <div className="sb-e-l" data-testid="craft-ledger-gear">{cell('Encumbrance', prof.encumbrance)}{cell('Hard points', prof.hardpoints)}{extra || null}</div>;
    }
    if (hiltNeedsCrystal(prof, null)) {
      return (
        <React.Fragment>
          <div className="sb-e-l" data-testid="craft-ledger-hilt">
            <div data-testid="hilt-needed"><b>{HILT_NEEDED}</b></div>
            {prof.range ? <div>Range <b>{cap(String(prof.range))}</b></div> : null}
            {cell('Encumbrance', prof.encumbrance)}{cell('Hard points', prof.hardpoints)}
            {extra || null}
          </div>
          {(prof.qualities || []).length ? <div style={{ marginTop: 6 }}>{(prof.qualities || []).map(function (q) { return <span className="sb-qual" key={q.id || q.name}>{(q.name || q.id) + (q.rank ? ' ' + q.rank : '')}</span>; })}</div> : null}
          <p className="sb-e-t" data-testid="hilt-rule">{HILT_RULE.words}<span className="sb-e-m">{' ' + HILT_RULE.cite}</span></p>
        </React.Fragment>);
    }
    var d = prof.damage || {}; var dmg = d.base === 'brawn' ? window.ItemResolve.brawnDamageText(d.value) : String(d.value != null ? d.value : '—');
    return (
      <React.Fragment>
        <div className="sb-e-l">
          <div>Damage <b>{dmg}</b></div><div>Critical <b>{critText(prof.crit)}</b></div>
          {prof.range ? <div>Range <b>{cap(String(prof.range))}</b></div> : null}
          <div>Encumbrance <b>{prof.encumbrance != null ? prof.encumbrance : '—'}</b></div><div>Hard points <b>{prof.hardpoints != null ? prof.hardpoints : '—'}</b></div>
          {extra || null}
        </div>
        {(prof.qualities || []).length ? <div style={{ marginTop: 6 }}>{(prof.qualities || []).map(function (q) { return <span className="sb-qual" key={q.id || q.name}>{(q.name || q.id) + (q.rank ? ' ' + q.rank : '')}</span>; })}</div> : null}
      </React.Fragment>);
  };
  var body2, foot2, title2;
  if (stepIdx === -1) {
    title2 = ['The bench', projects.length ? projects.length + ' in progress' : 'Nothing in progress'];
    body2 = (
      <React.Fragment>
        {projects.length ? projects.map(function (pj) {
          var st = stageLabel(pj); var t = TPL[pj.template_id] || {};
          return <button type="button" key={pj.id} className="sb-pk-r" data-testid={'craft-project-' + pj.id} onClick={function () { setSelId(pj.id); setNewMode(false); }}>
            <span className="sb-pk-c">›</span><span className="sb-pk-n">{t.name || pj.template_id}</span><span className="sb-pk-t">{st.t.charAt(0) + st.t.slice(1).toLowerCase()}</span></button>;
        }) : <div className="hp-none">Nothing on the bench. Start a craft and it stays here between sessions.</div>}
        {notes.length ? <div className="sb-e" data-testid="craft-notes"><div className="sb-e-h"><span className="sb-e-n">Remembered</span><span className="sb-e-m">The table applies these</span></div>
          {notes.map(function (n, i) { var row = noteRow(n.row_id); return <div className="sb-att" key={i}><div className="sb-e-h"><span className="sb-e-n">{row ? row.name : n.row_id}</span>
            <button type="button" className="hp-quiet" aria-label="Dismiss this note" onClick={function () { save({ craft_notes: Craft.dismissNote(notes, i) }); }}>Done</button></div>{row ? <p className="sb-e-t">{glyphs(row.effect)}</p> : null}</div>; })}</div> : null}
      </React.Fragment>);
    foot2 = <button type="button" className="hp-commit" data-testid="craft-new" onClick={function () { setNewMode(true); setSelId(null); }}>Start a new craft</button>;
  } else if (stepIdx === 0) {
    var ids = Craft.offeredTemplates(TPL); var tp = pickTpl ? TPL[pickTpl] : null;
    // D5: alchemy is refused below Force rating 1 — the engine's gate decides, the kind carries the book's reason (UP p.80)
    var fr0 = resolvedForceRating(sheet);
    title2 = ['Template', ids.length + ' templates'];
    body2 = (
      <React.Fragment>
        <div className="sb-pk-list-inline" data-testid="craft-template-list">
          {CRAFT_KINDS.map(function (k) {
            var kIds = ids.filter(function (id) { return k[1].indexOf(TPL[id].category) !== -1; });
            if (!kIds.length) return null;
            var refused = !Craft.craftGate(TPL[kIds[0]], fr0).ok;
            return (
              <React.Fragment key={k[0]}>
                <div className="sb-pk-h" data-testid={'craft-kind-' + k[0].toLowerCase()}><b>{k[0]}</b><span data-testid="craft-kind-count">{refused ? '—' : String(kIds.length)}</span></div>
                {refused ? <p className="sb-e-t" data-testid="craft-alchemy-refusal">{CRAFT_ALCHEMY_REFUSED.words}<span className="sb-e-m">{' ' + CRAFT_ALCHEMY_REFUSED.cite}</span></p>
                  : kIds.map(function (id) {
                    var x = TPL[id], on = pickTpl === id, can = credits >= x.material_price, lvl = Craft.schematicLevel(sheet.craft_schematics, id);
                    return <button type="button" key={id} className="sb-pk-r" data-testid={'craft-template-' + id} data-selected={on ? 'true' : 'false'} data-refused={can ? '0' : '1'} onClick={function () { setPickTpl(id); }}>
                      <span className="sb-pk-c">›</span><span className="sb-pk-n">{x.name}{lvl ? <span className="sb-mark" data-testid="craft-schematic-mark">Schematic</span> : null}</span><span className="sb-pk-t">{x.check.skills.map(skillLabel).join(' / ') + ' · ' + cap(craftDiffName(sheet, x)) + ' · ' + x.time.hours + ' h · ' + x.material_price + ' cr'}</span></button>;
                  })}
              </React.Fragment>);
          })}
        </div>
        {tp ? (
          <div className="sb-e" data-testid="craft-template-read">
            <div className="sb-e-h"><span className="sb-e-n">{tp.name}</span><span className="sb-e-m">{tp.cite || ''}</span></div>
            {Craft.schematicLevel(sheet.craft_schematics, tp.id) ? (function () { var lv = Craft.schematicLevel(sheet.craft_schematics, tp.id); return (
              <div className="sb-e-l" data-testid="craft-schematic">
                <div>Schematic <b>{lv + (lv === 1 ? ' step easier' : ' steps easier')}</b></div>
                <div>Check <b>{cap(tp.check.difficulty) + ' → ' + cap(craftDiffName(sheet, tp))}</b></div>
              </div>); })() : null}
            {profileLedger(tp.result_profile)}
            <div className="sb-e-h" style={{ marginTop: 10 }}><span className="sb-e-n">The cost</span></div>
            <div className="sb-e-l"><div>Materials <b>{tp.material_price + ' cr'}</b></div><div>Rarity <b>{tp.material_rarity}</b></div><div>Time <b>{tp.time.hours + ' hours'}</b></div></div>
            {poolLine(tp, tp.check.skills[0])}
            {credits < tp.material_price ? <p className="sb-e-t"><span className="sb-refuse">{(tp.material_price - credits) + ' cr short'}</span></p> : null}
          </div>) : null}
      </React.Fragment>);
    foot2 = (
      <React.Fragment>
        <button type="button" className="hp-quiet" onClick={toBench}>Back</button>
        <button type="button" className="hp-commit" data-testid="craft-start" disabled={!tp} onClick={function () { if (!tp) return; var pj = Craft.newProject(pickTpl, tp.category, projects); writeProjects(projects.concat([pj])); setSelId(pj.id); setNewMode(false); setPickTpl(null); }}>Start this craft</button>
      </React.Fragment>);
  } else if (stepIdx === 1) {
    var t1 = TPL[sel.template_id]; var short = credits - t1.material_price;
    var dirWaits = t1.category === 'droid_directive' && !madeFrom;   // D4: a directive's materials wait for the droid it programs
    title2 = ['Acquire materials', t1.name];
    body2 = (
      <React.Fragment>
        <div className="sb-e"><p className="sb-e-t" style={{ margin: 0 }}>Whatever you use counts as one item at the template’s price and rarity. What it actually is, is up to you.</p>
          <div className="sb-e-l"><div>Material price <b>{t1.material_price + ' cr'}</b></div><div>Rarity <b>{t1.material_rarity}</b></div><div>You have <b>{credits + ' cr'}</b></div></div>
          {short < 0 ? <p className="sb-e-t" data-testid="craft-shortfall"><span className="sb-refuse">{Math.abs(short) + ' cr short — not a block'}</span>{' Buy anyway, or salvage, steal or be gifted them.'}</p> : null}</div>
        <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Buy them</span></div>
          <div className="sb-pk-verbs" style={{ marginTop: 8 }}><button type="button" className="hp-commit" data-testid="craft-buy" disabled={dirWaits} onClick={function () {
            var next = Craft.acquireMaterials(sel, { method: 'bought', paid: t1.material_price, made_from: madeFrom || null });
            save({ crafts: projects.map(function (x) { return x.id === sel.id ? next : x; }), equipment: Object.assign({}, sheet.equipment, { credits: credits - t1.material_price }) }); setMadeFrom(''); }}>{'Spend ' + t1.material_price + ' cr'}</button></div></div>
        <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Got them another way</span></div>
          <input className="sb-filter" type="text" value={acqNote} placeholder="How? (optional)" data-testid="craft-acq-note" aria-label="How the materials were acquired" style={{ margin: 0 }} onChange={function (e) { setAcqNote(e.target.value); }} />
          <div className="sb-pk-verbs" style={{ marginTop: 8 }}><button type="button" className="hp-quiet" data-testid="craft-acquire-other" disabled={dirWaits} onClick={function () { var next = Craft.acquireMaterials(sel, { method: 'other', note: acqNote || null, made_from: madeFrom || null }); upsert(next); setAcqNote(''); }}>Mark as acquired</button></div></div>
        {t1.category === 'droid_directive' ? <CraftPrograms sheet={sheet} templates={TPL} value={madeFrom || null} onPick={setMadeFrom} />
          : <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Made from</span><span className="sb-e-m">Optional</span></div>
          <input className="sb-filter" type="text" value={madeFrom} placeholder="beskar · wroshyr wood · salvaged hull plate…" data-testid="craft-made-from" aria-label="What it is made from" style={{ margin: 0 }} onChange={function (e) { setMadeFrom(e.target.value); }} /></div>}
      </React.Fragment>);
    foot2 = <button type="button" className="hp-quiet" onClick={toBench}>Back to the bench</button>;
  } else if (stepIdx === 2 && choosing) {
    // D4 · THE CHOICE STEP (D-291): a General skill, or Brawn or Agility with each value before and after. The pick is local
    // until CONTINUE TO CONSTRUCTION writes it through Craft.setChoice (crafting-sm FLAG 4).
    var ch = selT.choice;
    var opts = ch.kind === 'skill'
      ? (regs.skills || []).filter(function (e) { return e.group === ch.group; }).map(function (e) { return { v: e.id, label: e.label, meta: null }; })
      : (ch.among || []).map(function (c) { var v = (sheet.characteristics || {})[c] || 0; return { v: c, label: cap(c), meta: v + ' → ' + (v + 1) }; });
    title2 = [selT.name, 'Choose'];
    body2 = (
      <div className="sb-e" data-testid="craft-choice">
        <div className="sb-e-h"><span className="sb-e-n">{ch.kind === 'skill' ? 'Choose a General skill' : 'Brawn or Agility'}</span><span className="sb-e-m">{selT.cite}</span></div>
        <div className="sb-pk-list-inline">
          {opts.map(function (o) { var on = choicePick === o.v; return (
            <button type="button" key={o.v} className="sb-pk-r" data-testid={'craft-choice-' + o.v} data-selected={on ? 'true' : 'false'} aria-pressed={on ? 'true' : 'false'} onClick={function () { setChoicePick(o.v); }}>
              <span className="sb-pk-c">›</span><span className="sb-pk-n">{o.label}</span>{o.meta ? <span className="sb-pk-t">{o.meta}</span> : <span />}</button>); })}
        </div>
      </div>);
    foot2 = (
      <React.Fragment>
        <button type="button" className="hp-quiet" onClick={toBench}>Back to the bench</button>
        {choicePick ? <button type="button" className="hp-commit" data-testid="craft-choice-continue" onClick={function () {
          var c = ch.kind === 'skill' ? { skill: choicePick } : { characteristic: choicePick };
          upsert(Craft.setChoice(sel, selT, c, regs.skills)); setChoicePick(null); }}>Continue to construction</button> : null}
      </React.Fragment>);
  } else if (stepIdx === 2) {
    var t2 = TPL[sel.template_id]; var sk2 = chosenSkill(t2); var melee2 = catRows;
    title2 = [t2.name, t2.cite];
    body2 = (
      <React.Fragment>
        <div className="sb-e">
          <p className="sb-e-t" style={{ margin: 0 }}>{'Materials acquired. Make a ' + cap(craftDiffName(sheet, t2)) + ' ' + t2.check.skills.map(cap).join(' or ') + ' check to build it. Every success past the first cuts ' + construction.time_reduction_per_extra_success.hours + ' hours from the ' + t2.time.hours + '-hour build, to a floor of ' + construction.time_minimum.hours + ' hour.'}</p>
          <p className="sb-e-t"><span className="sb-e-m">{construction.cite}</span></p>
          {t2.check.skills.length > 1 ? <div className="sb-critchips" data-testid="craft-skill-choice">{t2.check.skills.map(function (s) { var on = chosenSkill(t2) === s; return <button type="button" key={s} className="hp-quiet" data-testid={'craft-skill-' + s} data-on={on ? '1' : '0'} aria-pressed={on ? 'true' : 'false'} onClick={function () { setSkillPick(s); }}>{(craftPool(t2, s) || { name: s }).name}</button>; })}</div> : null}
          {poolLine(t2, sk2)}
          {sel.choice ? <p className="sb-e-t" data-testid="craft-built-for">Built for <b>{sel.choice.skill ? skillLabel(sel.choice.skill) : cap(sel.choice.characteristic)}</b></p> : null}
          <div className="sb-pk-verbs" style={{ marginTop: 10 }}>
            <button type="button" className="hp-commit" data-testid="craft-roll" onClick={function () { var next = Craft.logRoll(sel, rollCheck(t2, chosenSkill(t2))); upsert(next); setRevealFor(sel.id); }}>Roll here</button>
          </div>
        </div>
        <div className="sb-e">
          {/* the reference carries its own head (the tally's label and CLEAR); the walk adds none */}
          {window.SWRPGUI && window.SWRPGUI.ResultsReference ? <div data-testid="craft-tally">{h(window.SWRPGUI.ResultsReference, { paper: true, accent: accent, symbols: ['su', 'fa', 'ad', 'th', 'tr', 'de'], initialTally: sel.roll || undefined,
            source: { pos: melee2.improvements, neg: melee2.flaws, posLabel: '▲ WHAT ADVANTAGE COULD BUY', negLabel: '▼ WHAT THREAT COULD COST', tallyLabel: 'WHAT DID YOUR DICE SHOW?' },
            // B-1652 (D-427 item 1; page @ 8a97d5207): the table's result is reported UNDER the counters it reads, not beside Roll here
            afterTally: <div className="sb-pk-verbs" style={{ marginTop: 10 }}>
              <button type="button" className="hp-quiet" data-testid="craft-log-result" onClick={function () { var next = Craft.logRoll(sel, readTally()); upsert(next); setRevealFor(sel.id); }}>Report the table’s result</button>
            </div> })}</div> : null}</div>
      </React.Fragment>);
    foot2 = <button type="button" className="hp-quiet" onClick={toBench}>Back to the bench</button>;
  } else {
    var t3 = TPL[sel.template_id]; var verdict = Craft.outcome(sel.roll); var failed = verdict === 'failed'; var r3 = sel.roll || {};
    // B-1633 (D-417 item 7): Threat lets the GM add a flaw, it does not make one (Fully Operational p.77), so the bench
    // says "Crafted" until a flaw is recorded on the item. The words only — `verdict` still steers the spend ledger.
    var word = { crafted: 'Crafted', crafted_flawed: (sel.flaws || []).length ? 'Crafted, with a flaw' : 'Crafted', failed: 'Failed' }[verdict];
    var hours = Craft.buildHours(t3, sel.roll, construction);
    var line = Craft.netSuccesses(r3) + ' net ' + (Math.abs(Craft.netSuccesses(r3)) === 1 ? 'success' : 'successes') + ' vs ' + cap(craftDiffName(sheet, t3)) + ' (' + (DIFF_DICE[craftDiffName(sheet, t3)]) + ')' + (r3.ad ? ' · ' + r3.ad + ' Advantage' : '') + (r3.tr ? ' · ' + r3.tr + ' Triumph' : '') + (r3.th ? ' · ' + r3.th + ' Threat' : '') + (r3.de ? ' · ' + r3.de + ' Despair' : '');
    if (revealFor === sel.id) {
      title2 = [t3.name, 'The result'];
      body2 = (
        <div className="sb-e" data-testid="craft-reveal" data-verdict={verdict}>
          <div className="sb-e-h"><span className="sb-e-n">{failed ? 'What you salvaged' : 'Your ' + t3.name}</span><span className={failed ? 'sb-refuse' : 'sb-e-m'} data-testid="craft-verdict">{word}</span></div>
          <p className="sb-e-t">{line}{failed ? ' — the product is unusable and the materials are lost.' : '. Build time ' + t3.time.hours + ' h → ' + hours + ' h.'}</p>
          {failed ? null : profileLedger(t3.result_profile, <div>Built in <b>{hours + ' h'}</b></div>)}
        </div>);
      foot2 = (
        <React.Fragment>
          <button type="button" className="hp-quiet" onClick={toBench}>Back to the bench</button>
          {failed ? <button type="button" className="hp-commit" data-testid="craft-fail-continue" onClick={function () { setRevealFor(null); }}>Continue</button>
                  : <button type="button" className="hp-commit" data-testid="craft-reveal-continue" onClick={function () { setRevealFor(null); }}>Spend your results</button>}
        </React.Fragment>);
    } else {
      var hasNeg = (r3.th || 0) + (r3.de || 0) + (r3.ds || 0) > 0;
      var kind3 = Craft.outputOf(t3);
      // the template's BASE item (its own effects and choice applied): what every row's limits and fit are asked of
      // (ruling release-r5-crafting-kp-c0 Q3b; the kp rider — `not_for` is asked at the tap)
      var base3 = window.SWRPGCrafting.applyTemplateEffects(t3.result_profile, t3, sel.choice, { kind: kind3 });
      // E (D-370): a failed check offers exactly the rows printed for a failure; a success offers none of them (craftStore.eligible)
      var offer = function (rows) { return (rows || []).filter(function (row) { return Craft.eligible(row, verdict); }); };
      var posRows = offer(catRows.improvements), negRows = offer(catRows.flaws);
      var picked3 = (sel.improvements || []).concat(sel.flaws || []);
      var applied = failed ? null : kind3 === 'fortification'
        ? { profile: Object.assign({}, base3, Craft.fortificationOf({ template_id: t3.id, built_with: picked3 }, TPL, index)) }
        : window.SWRPGCrafting.applyCraftingSpend(base3, picked3, index, { kind: kind3 });
      var siblings = Craft.siblingNames(sheet.equipment || {}, t3);
      var posLedger = Craft.spendLedger(sel.improvements || [], index, { ad: r3.ad || 0, tr: r3.tr || 0, ls: r3.ls || 0 }, verdict, base3, sheet);   // R-R6RC-3: the holder, for a Maker's Mark (craftStore.costOf)
      var negLedger = Craft.spendLedger(sel.flaws || [], index, { th: r3.th || 0, de: r3.de || 0, ds: r3.ds || 0 }, verdict, base3, sheet);
      var spendRowP = function (row, side, taken, remaining) {
        var n = Craft.timesTaken(taken, row.id), once = !!(row.flags && row.flags.once_only);
        var lim = Craft.limitOf(row, base3, n), aff = Craft.affordableFrom(row, remaining, verdict, sheet);
        var refusedFit = !n && !!Craft.limitOf(row, base3, 0);   // not_for on this base: refused at the tap
        var tid = 'spend-row-' + row.id;
        if (!once && n > 0) {
          // C (D-368): a row bought already, with no once-only limit, is the shared + / − count (SWRPGUI.CountStep, ADDENDUM 18)
          var U = window.SWRPGUI, reason = craftHeldWords(lim);
          return (
            <div className="sb-att" key={row.id} data-testid={tid} data-taken={String(n)} data-refused="0">
              {h(U.CountStep, { label: row.name, count: n, testid: 'spend-step-' + row.id, reason: reason, plusHeld: !!lim || !aff,
                onMinus: function () { upsert(Craft.dropOne(sel, row.id, index)); },
                onPlus: function () { upsert(Craft.takeAgain(sel, row.id, index, base3)); },
                minusLabel: 'Return one ' + row.name, plusLabel: reason || ('Buy ' + row.name + ' again') })}
              <div className="sb-e-m" style={{ marginTop: 2 }}>{glyphs(row.cost)}</div>
              <p className="sb-e-t">{glyphs(row.effect)}<span className="sb-e-m">{' ' + row.cite}</span></p>
            </div>);
        }
        var isTaken = n > 0, can = isTaken || (aff && !refusedFit);
        return (
          <div className="sb-att" key={row.id} data-testid={tid} data-taken={isTaken ? '1' : '0'} data-refused={can ? '0' : '1'}>
            <div className="sb-e-h"><span className="sb-e-n">{row.name}</span><span className="sb-e-m">{glyphs(row.cost)}{once ? ' · once only' : ''}</span>
              <button type="button" className={isTaken ? 'hp-commit' : 'hp-quiet'} disabled={!can} aria-pressed={isTaken ? 'true' : 'false'} onClick={function () { if (can) upsert(Craft.toggleRow(sel, row.id, index, base3)); }}>{isTaken ? 'Taken' : (side === 'flaw' ? 'Record' : 'Take')}</button></div>
            <p className="sb-e-t">{glyphs(row.effect)}<span className="sb-e-m">{' ' + row.cite}</span></p>
          </div>);
      };
      title2 = [failed ? 'What you salvaged' : 'Your ' + t3.name, failed ? word : 'Built in ' + hours + ' h'];
      body2 = (
        <React.Fragment>
          {!failed ? <div className="sb-e" data-testid="craft-applied">
            <div className="sb-e-h"><span className="sb-e-n">{t3.name}</span><span className="sb-e-m">{word}</span></div>
            {t3.category === 'droid_chassis' ? <CraftDroidFields sel={sel} template={t3} index={index} regs={regs} sheet={sheet} /> : profileLedger(applied.profile)}
            {craftQualityChips(base3, applied.profile).filter(function (ch) { return ch.isNew; }).length ? <p className="sb-e-t"><span className="sb-e-m">New: </span>{craftQualityChips(base3, applied.profile).filter(function (ch) { return ch.isNew; }).map(function (ch) { return ch.label; }).join(' · ')}</p> : null}
          </div> : null}
          <div className="sb-e" data-testid="craft-budget"><div className="sb-e-h">{/* D-406 item 7 (B-1496, L2 @ 4ec53a8b3): a failed craft has no results to spend, so its panel drops the heading */}{failed ? null : <span className="sb-e-n">Spend your results</span>}<span className="sb-e-m">{'Left to spend · ' + (posLedger.remaining.ad || 0) + ' Advantage · ' + (posLedger.remaining.tr || 0) + ' Triumph' + (Craft.isAlchemy(t3) ? ' · ' + (posLedger.remaining.ls || 0) + ' light side' : '')}</span></div>
            {posRows.map(function (row) { return spendRowP(row, 'improvement', sel.improvements || [], posLedger.remaining); })}</div>
          {hasNeg ? <div className="sb-e">
            {negRows.map(function (row) { return spendRowP(row, 'flaw', sel.flaws || [], negLedger.remaining); })}</div> : null}
          {!failed && t3.category === 'droid_directive' ? <CraftDirectiveSkills sel={sel} template={t3} skills={regs.skills} upsert={upsert} /> : null}
          {!failed ? <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Name it</span></div>
            <input className="sb-filter" type="text" value={craftName} data-testid="craft-name" aria-label="Name the thing you made" placeholder={Craft.defaultName(t3, siblings)} style={{ margin: 0 }} onChange={function (e) { setCraftName(e.target.value); }} /></div> : null}
        </React.Fragment>);
      foot2 = (
        <React.Fragment>
          <button type="button" className="hp-quiet" onClick={toBench}>Back to the bench</button>
          <button type="button" className="hp-commit" data-testid="craft-finish" onClick={function () { finish(sel, failed ? null : craftName); }}>{failed ? 'Close this attempt' : t3.category === 'droid_chassis' ? 'Add to Allies' : 'Add to gear'}</button>
        </React.Fragment>);
    }
  }
  return (
    <div className="sb-craft" data-testid="craft-workshop" data-step={stepIdx}>
      {rail2}
      <div className="hp-plate" data-pad="read" data-testid="craft-stage-scroll">
        <div className="sb-e-h" style={{ marginBottom: 8 }}><span className="sb-e-n" style={{ fontSize: 15 }}>{title2[0]}</span>{title2[1] ? <span className="sb-e-m">{title2[1]}</span> : null}</div>
        {body2}
      </div>
      <div className="sb-walkfoot">{foot2}</div>
    </div>
  );
}

// ══ THE ITEM-STATE MACHINE — ONE definition, module scope (F-86 build card §2.3) ═════════════
// RELOCATED out of LoadoutZonePage, which the fold has now DELETED. The card's wording is exact:
// "the existing STATE_CYCLE machine relocated not forked". So it moves out here where both the
// dying zone page and the new GEAR panel call the SAME code — a second copy would be the
// no-compromise-mirror failure the whole consolidation exists to remove.
//
// Armor is the only kind that can be WORN, so it cycles through three states and everything else
// cycles through two. That asymmetry lived in `stateChip`; it lives here now, once.
var ITEM_STATE_CYCLE = { worn: 'carried', carried: 'stowed', stowed: 'worn' };
var ITEM_STATE_CYCLE_NO_WORN = { carried: 'stowed', stowed: 'carried' };
function itemStateCycleFor(kind) {
  return kind === 'armor' ? ITEM_STATE_CYCLE : ITEM_STATE_CYCLE_NO_WORN;
}
function nextItemState(kind, current) {
  return itemStateCycleFor(kind)[current] || 'carried';
}

// The PERSIST half, also relocated. Takes the persisted equipment block and the patch function
// rather than closing over a component's scope, so the panel and the zone page share one writer.
// Writes the PERSISTED entry only (build card §2.9) — every rendered number stays a projection.
// R-SSC-5 / D-229 sheet Q2 (a) (ruling f100-surfacing-sheet-concept ADDENDUM 10 C2 (a), Q4 (a)): the player's improvised
// mark — the ONE writer of `sheet.equipment.weapons[i].improvised`. Ticked writes `improvised: true` and nothing else;
// unticked DELETES the key (absent = not marked). The whole entry is marked (a qty 2 entry is not split, Q4 (a)). Read only
// by ItemResolve's `_kindFits` (`item_kind.improvised`, Creative Killer AoR p.145); never written by resolution.
function persistImprovised(persistedEq, patchEquipment, idx, on) {
  var next = (persistedEq.weapons || []).slice(), e = Object.assign({}, next[idx]);
  if (on) e.improvised = true; else delete e.improvised;
  next[idx] = e;
  patchEquipment({ weapons: next });
}
function persistItemState(persistedEq, patchEquipment, kind, idx, newState, extra) {
  var patch = Object.assign({ state: newState }, extra || {});
  if (newState !== 'stowed') patch.stowage = null;
  if (kind === 'weapon') {
    var next = (persistedEq.weapons || []).slice();
    next[idx] = Object.assign({}, next[idx], patch);
    delete next[idx].loc; delete next[idx].equipped;
    patchEquipment({ weapons: next });
  } else if (kind === 'armor') {
    var a = Object.assign({}, persistedEq.armor, patch);
    delete a.loc; delete a.equipped; delete a.worn;
    patchEquipment({ armor: a });
  } else {
    var nextG = (persistedEq.gear || []).slice();
    nextG[idx] = Object.assign({}, nextG[idx], patch);
    delete nextG[idx].loc; delete nextG[idx].equipped;
    patchEquipment({ gear: nextG });
  }
}

// The remaining two writers, relocated on the same terms as persistItemState (F-86 §2.3): the
// panel needs REMOVE and STOWAGE, and LoadoutZonePage — the only place they existed — was deleted
// by §2.7. Arguments, not closures, so both callers share one implementation.
function persistRemoveItem(persistedEq, patchEquipment, kind, idx) {
  if (kind === 'weapon') {
    var next = (persistedEq.weapons || []).slice(); next.splice(idx, 1);
    patchEquipment({ weapons: next });
  } else if (kind === 'armor') {
    patchEquipment({ armor: null });
  } else {
    var nextG = (persistedEq.gear || []).slice(); nextG.splice(idx, 1);
    patchEquipment({ gear: nextG });
  }
}
function persistStowage(persistedEq, patchEquipment, kind, idx, stowage) {
  if (kind === 'weapon') {
    var next = (persistedEq.weapons || []).slice();
    next[idx] = Object.assign({}, next[idx], { stowage: stowage || null });
    patchEquipment({ weapons: next });
  } else if (kind === 'armor') {
    patchEquipment({ armor: Object.assign({}, persistedEq.armor, { stowage: stowage || null }) });
  } else {
    var nextG = (persistedEq.gear || []).slice();
    nextG[idx] = Object.assign({}, nextG[idx], { stowage: stowage || null });
    patchEquipment({ gear: nextG });
  }
}

// A fitted part's NAME, the pack's rule (B-1656 lifts it here so the weapon card reads the same words): the registry's name
// through the one resolver; a dangling id is a VISIBLE '(unknown attachment)', never dropped (attachments are edited by index).
function pcAttachmentName(att, attachmentReg) {
  var IR = window.ItemResolve;
  var canon = IR ? IR.pcResolveAttachment(att, attachmentReg) : att;
  if (!canon) return '(unknown attachment)';
  return canon.name || (typeof att === 'string' ? att : 'Unknown');
}

function LoadoutItemDetail({ sheet, equipment, accent, target, onSheetChange, onClose, qualitiesDb, onModify, onSell }) {
  var _dropArmed = React.useState(false), dropArmed = _dropArmed[0], setDropArmed = _dropArmed[1];
  var persistedEq = sheet.equipment || {};
  var IR = window.ItemResolve;
  // B-852: the records come from the items view THIS SHEET owns (its user: stamps over the pristine base),
  // never from the DISCOVERY view — a switched-on pack must not rewrite an item the sheet did not take from it.
  var regs = sheetItemRegistries(sheet) || {};
  var attachmentReg = regs.attachments || {};
  var kind = target.kind;
  var idx = target.idx;
  var item, persisted, itemHp;
  if (kind === 'weapon') {
    item = (equipment.weapons || [])[idx];
    persisted = (persistedEq.weapons || [])[idx] || {};
    itemHp = item && item.hp;
  } else if (kind === 'armor') {
    item = equipment.armor;
    persisted = persistedEq.armor || {};
    itemHp = item && item.hp;
  } else {
    item = (equipment.gear || [])[idx];
    persisted = (persistedEq.gear || [])[idx] || {};
    itemHp = item && item.hp;
  }
  if (!item) return h('div', null, 'Item not found');

  var canonRec = null;
  // R-SPC-3: `canonicalToStore` now emits a SECTION-QUALIFIED `key` ('weapons:vibro_ax') and carries
  // the bare registry id as `regId`. A registry is keyed by the BARE id, so the lookup reads `regId`
  // and falls back to `key` for a legacy pre-id-ref entry, whose `key` (when it has one) is bare.
  var lookupId = item.regId || item.key;
  if (lookupId) {
    var regMap = kind === 'weapon' ? regs.weapons : kind === 'armor' ? regs.armor : regs.gear;
    canonRec = (regMap && regMap[lookupId]) || null;
  }
  var baseModLines = (window.SWItems && window.SWItems.baseModLines) ? window.SWItems.baseModLines(canonRec) : [];   // B-137: the one reader
  var itemSources = (canonRec && canonRec.sources) || [];
  // B-339 / R-BSR-1(i): this walked `aliasToId` -> `byId` -> `label` itself. That was a SECOND
  // RESOLUTION PATH for a quantity `lib/shared/sources.js` already owns — the defect class by
  // name (CLAUDE.md §5) — and it is DISSOLVED into the shared resolver rather than repaired in
  // place. `lib/store/view.jsx:28` declined to add a third for the same reason and named this
  // copy while doing so. Behaviour is unchanged for every input the old one handled: the shared
  // sourceLabel(index, src) has the identical alias->id->label walk and the same `return src`
  // fallback for a display title, a hidden source code (aorbeta/fadbeta) or an unmapped string.
  function sourceLabel(src) {
    var S = window.SWRPGSources;
    return (S && S.sourceLabel) ? S.sourceLabel(window._swrpgSourcesIndex || null, src) : src;
  }

  var readableModText = (window.SWItems && window.SWItems.readableModText) || function(t) { return t; };

  var installedAttachments = item.attachments || [];
  // B-227: the PC lane, which DEGRADES. The loud `hardpointAccounting` threw on a dangling
  // attachment id-ref and took the whole panel down with it — the player saw no loadout at all
  // because one saved id no longer resolves. The canonical-integrity throw is unchanged and still
  // guards code/NPC paths (ruling `gear-consolidation-build` §3).
  var hpAcct = IR ? IR.pcHardpointAccounting(itemHp, installedAttachments, attachmentReg, canonRec) : { used: 0, capacity: 0, over: false, uncosted: 0, unresolved: 0 };   // B-1468: the stock crystal

  function patchEquipment(update) {
    if (onSheetChange) onSheetChange({ equipment: Object.assign({}, persistedEq, update) });
  }
  function updateItem(patchObj) {
    if (kind === 'weapon') {
      var next = (persistedEq.weapons || []).slice();
      next[idx] = Object.assign({}, next[idx], patchObj);
      patchEquipment({ weapons: next });
    } else if (kind === 'armor') {
      patchEquipment({ armor: Object.assign({}, persistedEq.armor, patchObj) });
    } else {
      var nextG = (persistedEq.gear || []).slice();
      nextG[idx] = Object.assign({}, nextG[idx], patchObj);
      patchEquipment({ gear: nextG });
    }
  }
  // B-1403 (R-B1403-7): every attachments[] write goes through `ItemResolve.writeAttachment`, the one writer.
  function writeAtt(op) {
    var next = IR ? IR.writeAttachment(persistedEq, { kind: kind, idx: idx }, op) : persistedEq;
    if (next !== persistedEq && onSheetChange) onSheetChange({ equipment: next });
  }
  function removeAttachment(attIdx) { writeAtt({ op: 'detach', index: attIdx }); }
  // L1 RETIRE (release-r5-f1-c0): the option chip that flipped one key is gone — an option's count lives on MODIFY (D1)
  // the ROOM check (ruling `b1403-store-attachments-owns` R-B1403-13 (iii)): today's arithmetic, moved into the writer
  // unchanged — no kind, no limits, a duplicate allowed; the list above and its `canAdd` are untouched (B-1422 is the row)
  function addAttachment(attId) {
    writeAtt({ op: 'attach', check: 'room', id: attId, registry: attachmentReg, record: canonRec, hp: itemHp });
  }

  var statPairs = [];
  // A (D-377): a crafted hilt with no crystal reads "Crystal Needed" where its damage sits — no Critical, never a 0
  var hiltNeeded = kind === 'weapon' && hiltNeedsCrystal(persisted && persisted._inline, persisted && persisted.attachments, attachmentReg);
  if (hiltNeeded) {
    statPairs.push({ label: 'NEEDED', needed: true });
    statPairs.push({ label: 'RANGE', value: item.range });
    statPairs.push({ label: 'SKILL', value: item.skill });
  } else if (kind === 'weapon') {
    var dmgDisplay = item.damage;
    if (IR && IR.damageTextValue(dmgDisplay).base === 'brawn') dmgDisplay = 'Brawn ' + dmgDisplay;   // B-1429: "−1" too
    statPairs.push({ label: 'DAMAGE', value: dmgDisplay, mod: item._dmgMod });
    statPairs.push({ label: 'CRITICAL', value: critText(item.crit) });
    statPairs.push({ label: 'RANGE', value: item.range });
    statPairs.push({ label: 'SKILL', value: item.skill });
  } else if (kind === 'armor') {
    statPairs.push({ label: 'SOAK', value: item.soak });
    // B-1098: the panel reads the projection's sides (computeDerived's `defense_<side> ?? defense`). One DEFENSE line when
    // they agree; two, "Ranged defence" and "Melee defence", where they differ (D-411 item 27, page v3 @ 788df1c53). The one
    // line is spelt as the sheet spells it, "Defence" (B-1615, D-414 item 4)
    var defR = item.defense_ranged != null ? item.defense_ranged : (item.defense || 0);
    var defM = item.defense_melee != null ? item.defense_melee : (item.defense || 0);
    if (defR === defM) statPairs.push({ label: 'DEFENCE', value: defR });
    else { statPairs.push({ label: 'RANGED DEFENCE', value: defR }); statPairs.push({ label: 'MELEE DEFENCE', value: defM }); }
  }

  var cap = hpAcct.capacity;
  var hpLabelText = hpAcct.uncosted > 0
    ? hpAcct.used + '+? / ' + cap + ' HP'
    : hpAcct.used + ' / ' + cap + ' HP';

  // B-623: the card's MODEL is computed once (name · hp · uncosted · the auto mods · the toggled mods) and
  // rendered twice — the dark card below, byte-for-byte, and the paper card in the `paper` composition.
  var attModels = installedAttachments.map(function(att, ai) {
    // B-227: graceful in the PC lane. A null here is a saved id the registry no longer holds, and
    // it becomes a VISIBLE MARKER rather than a thrown render — the '(unknown weapon)' precedent
    // from the equipment lane, same shape, deliberately not a new one. The marker is not a skip:
    // attachments are edited BY INDEX (`uninstall(ai)`), so dropping the row would silently
    // re-point every later edit onto the wrong attachment.
    var canonRaw = IR ? IR.pcResolveAttachment(att, attachmentReg) : att;
    var canon = canonRaw || {};
    var attName = pcAttachmentName(att, attachmentReg);
    var attHp = canon.hp;
    var isUncosted = attHp == null;

    var baseMods = [];
    if (canon.damage_mod != null) baseMods.push('Damage ' + (canon.damage_mod > 0 ? '+' : '') + canon.damage_mod);
    if (canon.crit_mod != null) baseMods.push('Crit ' + (canon.crit_mod > 0 ? '+' : '') + canon.crit_mod);
    if (canon.crit_set != null) baseMods.push('Crit set to ' + canon.crit_set);
    if (canon.soak_mod != null) baseMods.push('Soak ' + (canon.soak_mod > 0 ? '+' : '') + canon.soak_mod);
    if (canon.defense_melee_mod != null) baseMods.push('Melee Def ' + (canon.defense_melee_mod > 0 ? '+' : '') + canon.defense_melee_mod);
    if (canon.defense_ranged_mod != null) baseMods.push('Ranged Def ' + (canon.defense_ranged_mod > 0 ? '+' : '') + canon.defense_ranged_mod);
    if (canon.encumbrance_mod != null) baseMods.push('Enc ' + (canon.encumbrance_mod > 0 ? '+' : '') + canon.encumbrance_mod);
    if (canon.encumbrance_threshold_bonus) baseMods.push('Carry +' + canon.encumbrance_threshold_bonus);
    if (canon.qualities_add) {
      // B-1544: a quality prints the qualities vocabulary's LABEL ("Auto-fire"), never its key ("auto_fire").
      var qLab = IR && IR.qualityLabelsOf ? IR.qualityLabelsOf(window._qualitiesDb || qualitiesDb) : {};
      canon.qualities_add.forEach(function(q) {
        baseMods.push((qLab[q.key] || q.key || 'Quality') + (q.rank != null ? ' ' + q.rank : ''));
      });
    }

    return { ai: ai, attName: attName, attHp: attHp, isUncosted: isUncosted, baseMods: baseMods };
  });

  var canAdd = !hpAcct.over && (hpAcct.used + hpAcct.uncosted < hpAcct.capacity);
  var _addOpen = React.useState(false), addOpen = _addOpen[0], setAddOpen = _addOpen[1];
  var _addFilter = React.useState(''), addFilter = _addFilter[0], setAddFilter = _addFilter[1];
  var availableAttachments = React.useMemo(function() {
    var list = [];
    var keys = Object.keys(attachmentReg);
    for (var i = 0; i < keys.length; i++) {
      var a = attachmentReg[keys[i]];
      if (!a || !a.name) continue;
      // B-1422 (D-395 (a)): the kind lives on `type` (weapon / armor / gear); `category` is carried by no record
      if (kind === 'weapon' && a.type !== 'weapon') continue;
      if (kind === 'armor' && a.type !== 'armor') continue;
      list.push({ id: keys[i], name: a.name, hp: a.hp });
    }
    list.sort(function(a, b) { return a.name.localeCompare(b.name); });
    return list;
  }, [kind]);

  // The panel has no back bar: it is not a place you navigated to, so there is nowhere to go
  // back to. Closing it is deselecting, which the list owns (F-86 §2.3).
  // §2.7: the `< LOADOUT` back bar is GONE with the page it belonged to, and so is the `variant`
  // prop that selected between them — this component has exactly one mount and it is the panel, so
  // `variant`/`asPanel` had no reader left. Keeping an unread seam "for a future presentation" is
  // the stub §2.7 forbids; if a second presentation is ever wanted, it re-enters here deliberately.







  var itemType = item.type || (kind === 'weapon' ? 'Weapon' : kind === 'armor' ? 'Armor' : 'Gear');
  var typeLabel = kind === 'weapon' && item.skill
    ? itemType.toUpperCase() + ' · ' + item.skill.toUpperCase()
    : itemType.toUpperCase();






  var provBlock = h(ItemOwnerBlock, {
    nickname: persisted.nickname,
    provenance: persisted.provenance,
    onNickname: function(v) { updateItem({ nickname: v }); },
    onProvenance: function(arr) { updateItem({ provenance: arr }); },
  });


  // ── STATE (panel only) — the verb the dying zone page used to own (F-86 §2.3) ──
  // Uses the module-scope machine relocated in the previous increment: one cycle, one writer.
  // Armor gets worn/carried/stowed; everything else gets carried/stowed.
  var panelPersistedEq = sheet.equipment || {};
  function panelPatchEquipment(update) {
    if (onSheetChange) onSheetChange({ equipment: Object.assign({}, panelPersistedEq, update) });
  }
  var curState = (item && item.state) || 'carried';
  var improvisedOn = !!(window.SWRPGEngineRules && window.SWRPGEngineRules.on());
  var improvisedMarked = kind === 'weapon' && !!(panelPersistedEq.weapons || [])[target.idx] && panelPersistedEq.weapons[target.idx].improvised === true;
  // ── STOWAGE + DROP (panel only) — the last two verbs the dying zone page owned (§2.3) ──
  // Both go through the module-scope writers relocated alongside the state machine, so the panel
  // and the zone page cannot drift while both exist.
  var curStowage = (item && item.stowage) || '';




  // ═══ B-623 · THE ITEM PANEL ON PAPER (the lab's pack frame: name · type · the ledger · the description ·
  // Attachments with the hard points · Stowed in · the verbs). The same state, writers and testids as the dark
  // panel above — MODIFY · SELL · DROP (two-tap) · SET STATE · stowage · attachments (add, remove, toggle a mod)
  // — drawn as the board's entries. The head is the pack's own line above the panel (SheetPack).
  var stateNames = Object.keys(itemStateCycleFor(kind));
  var attFiltered = availableAttachments.filter(function(a) { return !addFilter || a.name.toLowerCase().indexOf(addFilter.toLowerCase()) >= 0; });
  return (
    <div className="sb-panel" data-testid="gear-panel">
      <div className="sb-pk-verbs" data-testid="gear-verb-bar" style={{ marginTop: 0 }}>
        {onModify && itemHp > 0 ? <button type="button" className="hp-commit" data-testid="detail-modify" onClick={function() { onModify({ kind: kind, idx: idx }); }}>Modify</button> : null}
        {onSell ? <button type="button" className="hp-quiet" data-testid="gear-sell" aria-label={'Sell ' + (item.name || 'this item')} onClick={function () { onSell({ kind: kind, idx: target.idx, item: item }); }}>Sell</button> : null}
        <button type="button" className={dropArmed ? 'sb-confirm' : 'hp-quiet'} data-testid={dropArmed ? 'gear-drop-confirm' : 'gear-drop'}
          aria-label={dropArmed ? 'Confirm dropping this item' : 'Drop this item'}
          onClick={function () { if (!dropArmed) { setDropArmed(true); return; } persistRemoveItem(panelPersistedEq, panelPatchEquipment, kind, target.idx); if (onClose) onClose(); }}>
          {dropArmed ? 'Confirm — drop it' : 'Drop'}</button>
      </div>
      <div className="sb-e" data-testid="gear-panel-state">
        <div className="sb-e-h"><span className="sb-e-n">State</span><span className="sb-e-m">{curState}</span></div>
        <div className="sb-critchips" role="group" aria-label="Set state">
          {stateNames.map(function (st) {
            var on = st === curState;
            return <button type="button" key={st} className="hp-quiet" data-testid={'gear-state-' + st} data-on={on ? '1' : '0'} aria-pressed={on ? 'true' : 'false'} aria-label={'Set state to ' + st}
              onClick={function () { persistItemState(panelPersistedEq, panelPatchEquipment, kind, target.idx, st); }}>{st}</button>;
          })}
        </div>
        {curState === 'stowed'
          ? <div data-testid="gear-panel-stowage" style={{ marginTop: 8 }}>
              <div className="sb-grp" style={{ paddingTop: 4 }}>Stowed in</div>
              <input className="sb-filter" type="text" value={curStowage} placeholder="ship locker, speeder, footlocker…" data-testid="gear-stowage-input" aria-label="Where this item is stowed" style={{ margin: 0 }}
                onChange={function (e) { persistStowage(panelPersistedEq, panelPatchEquipment, kind, target.idx, e.target.value); }} />
            </div>
          : null}
      </div>
      {/* R-SSC-5 / D-229 sheet Q2 (a), frame S4: the improvised tick — weapons only, drawn only with M12's engine key ON (the
          row it feeds, Creative Killer's, is DARK; an inert control OFF is not drawn, ADDENDUM 10 Q1 (a)). 44 px, OfferRow's tick look. */}
      {kind === 'weapon' && improvisedOn
        ? <div className="sb-e" data-testid="gear-panel-improvised">
            <div className="sb-e-h"><span className="sb-e-n">Improvised weapon</span><span className="sb-e-m">{improvisedMarked ? 'marked' : 'not marked'}</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
              <button type="button" className="m8-tick" data-testid="gear-improvised-tick" aria-pressed={improvisedMarked ? 'true' : 'false'}
                aria-label={(improvisedMarked ? 'Untick ' : 'Tick ') + 'Improvised weapon'}
                onClick={function () { persistImprovised(panelPersistedEq, panelPatchEquipment, target.idx, !improvisedMarked); }}
                style={{ flex: '0 0 auto', width: 44, height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  border: '1px solid ' + (improvisedMarked ? 'var(--doc-action)' : 'var(--doc-rule, rgba(0,0,0,.35))'),
                  background: improvisedMarked ? 'var(--doc-action)' : 'var(--doc-paper-raised)', font: '600 20px/1 var(--font-mono)',
                  color: improvisedMarked ? 'var(--doc-paper-raised)' : 'transparent' }}>✓</button>
            </div>
          </div>
        : null}
      {/* B-634 (Jason 2026-09-16: "something to encapsulate the long prose"): the description is a FOLD — the
          walk's idiom on the kit's own <details> — closed by default with a one-line teaser in the head; a press
          opens the whole text. The ledger and the verbs are what the panel is opened for; the prose waits. */}
      {item.description
        ? (function () {
            var paras = item.description.split('\n').filter(function(x) { return x.trim(); });
            var full = paras.join(' ');
            var teaser = full.length > 120 ? full.slice(0, 120).replace(/\s+\S*$/, '') + '…' : full;
            return (
              <details className="sb-fold" data-testid="gear-desc-fold">
                <summary className="sb-e-h"><span className="sb-e-n">Description</span><span className="sb-e-m sb-fold-teaser">{teaser}</span><i className="sb-grp-glyph" aria-hidden="true">▾</i></summary>
                {paras.map(function (para, pi) { return <p className="sb-e-t" key={pi} data-testid="gear-desc-text">{para}</p>; })}
              </details>);
          })()
        : null}
      {baseModLines.length > 0
        ? <div className="sb-e" data-testid="base-mods"><div className="sb-e-h"><span className="sb-e-n">What it does</span></div>
            {baseModLines.map(function(line, mi) { return <p className="sb-e-t" key={mi}>{glyphs(line)}</p>; })}</div>
        : null}
      {statPairs.length
        ? <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">{kind === 'weapon' ? 'Combat' : 'Protection'}</span>{typeLabel ? <span className="sb-e-m">{typeLabel}</span> : null}</div>
            <div className="sb-e-l">{statPairs.map(function(sp) { if (sp.needed) return <div key="needed" data-testid="hilt-needed"><b>{HILT_NEEDED}</b></div>; return <div key={sp.label}>{sp.label.charAt(0) + sp.label.slice(1).toLowerCase()} <b>{sp.value != null ? String(sp.value) : '—'}{sp.mod ? <span className="sb-of">{' ' + sp.mod}</span> : null}</b></div>; })}</div>
            {hiltNeeded ? <p className="sb-e-t" data-testid="hilt-rule">{HILT_RULE.words}<span className="sb-e-m">{' ' + HILT_RULE.cite}</span></p> : null}
            {kind === 'weapon' && improvisedOn ? m8ItemChanges(item, ['crit_delta', 'damage_delta']).map(function (f, k) { var t = m8WeaponChangeText(sheet, f, 'card'); return t ? <DiceHubStatusLine key={'ic' + k} m8="applied" tone="var(--doc-ink-muted)">{t}</DiceHubStatusLine> : null; }) : null}</div>
        : null}
      {item.qualities && item.qualities.length ? <div style={{ margin: '6px 0' }}>{item.qualities.map(function(q, qi) { return <span className="sb-qual" key={qi}>{q}</span>; })}</div> : null}
      <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Cost</span></div>
        <div className="sb-e-l">
          {item.encumbrance != null ? ((item.encumbrance_threshold_bonus || 0) > 0
            ? <React.Fragment><div>Encumbrance <b>0</b></div><div>Carry <b>{'+' + item.encumbrance_threshold_bonus}</b></div></React.Fragment>
            : (kind === 'armor' && item.state === 'worn')
            ? <div>Encumbrance <b><span className="sb-of" style={{ textDecoration: 'line-through' }}>{String(item.encumbrance)}</span>{' ' + Math.max(0, (item.encumbrance || 0) - 3)}</b><span className="sb-of"> worn</span></div>
            : <div>Encumbrance <b>{String(item.encumbrance)}</b></div>) : null}
          {item.price != null ? <div>Price <b>{item.price.toLocaleString() + ' cr'}</b></div> : null}
          {item.rarity != null ? <div>Rarity <b>{String(item.rarity)}</b></div> : null}
          {item.restricted ? <div><span className="sb-refuse">Black market</span></div> : null}
        </div></div>
      {itemSources.length > 0
        ? <div className="sb-e"><div className="sb-e-h"><span className="sb-e-n">Source</span></div>
            {itemSources.map(function(src, si) { var lbl = sourceLabel(src); if (typeof src === 'string' && lbl === src && typeof BOOK_LABELS !== 'undefined' && BOOK_LABELS[src]) lbl = BOOK_LABELS[src]; return <p className="sb-e-t" key={si} data-testid="item-source">{lbl}</p>; })}</div>
        : null}
      <PackCraftTold entry={persisted} />
      {provBlock}
      {(itemHp != null && itemHp > 0)
        ? <div className="sb-e" data-testid="gear-attachments">
            <div className="sb-e-h"><span className="sb-e-n">Attachments</span><span className="sb-e-m" data-testid="gear-hp">{hpLabelText}</span></div>
            {hpAcct.uncosted > 0 ? <p className="sb-e-t"><span className="sb-refuse">{hpAcct.uncosted + ' attachment' + (hpAcct.uncosted === 1 ? ' has' : 's have') + ' no stated hard-point cost'}</span></p> : null}
            {attModels.length
              ? attModels.map(function(m) {
                  return (
                    <div className="sb-att" key={m.ai} data-uncosted={m.isUncosted ? '1' : '0'}>
                      <div className="sb-e-h"><span className="sb-e-n">{m.attName}</span><span className="sb-e-m">{m.isUncosted ? '? HP' : m.attHp + ' HP'}</span>
                        <button type="button" className="hp-quiet" aria-label={'Remove ' + m.attName} onClick={function() { removeAttachment(m.ai); }}>Remove</button></div>
                      {m.baseMods.map(function(bm, bi) { return <p className="sb-e-t" key={'b' + bi}>{bm}<span className="sb-of"> · auto</span></p>; })}
                    </div>);
                })
              : <p className="sb-e-t">No attachments installed.</p>}
            <div className="sb-pk-verbs" style={{ marginTop: 8 }}>
              <button type="button" className="hp-quiet" data-testid="attachment-add" disabled={!canAdd} onClick={function() { if (canAdd) setAddOpen(!addOpen); }}>{canAdd ? 'Add attachment' : 'Add attachment — no free hard points'}</button>
            </div>
            {addOpen
              ? <div className="sb-att-add">
                  <input className="sb-filter" type="search" value={addFilter} placeholder={'Filter ' + availableAttachments.length + ' attachments'} aria-label="Filter attachments" onChange={function(e) { setAddFilter(e.target.value); }} />
                  <div className="sb-att-opts">{attFiltered.map(function(a) {
                    return <button type="button" key={a.id} className="sb-pk-r" data-testid={'attachment-option-' + a.id} onClick={function() { addAttachment(a.id); setAddOpen(false); setAddFilter(''); }}>
                      <span className="sb-pk-c">›</span><span className="sb-pk-n">{a.name}</span><span className="sb-pk-t">{a.hp != null ? a.hp + ' HP' : '? HP'}</span></button>; })}</div>
                </div>
              : null}
          </div>
        : null}
    </div>
  );
}

// ── F-86 MODIFY takeover — Mock 2 frame O3 ───────────────────────────────────────────────────
//
// A takeover of the WHOLE tab pane, not a section inside the panel: O3 draws the topbar and the
// tab strip surviving and everything below them replaced. SheetLoadout early-returns this, so the
// word is literal — there is no loadout underneath to scroll past, only DONE.
//
// The live delta is why this surface exists, and it is computed the one legal way (build card
// §2.4): `applyInstalledAttachments` (B-1185's one path, the sheet's own) runs TWICE — over the installed set, and over installed +
// the focused candidate — and the two results are differenced. No second arithmetic path; a
// candidate moves the DMG cell because the SHARED core moved it, or it does not move at all.
//
// Won't-fit candidates stay VISIBLE and inert with their reason (§2.4), which is what makes the
// HP cap enforcement legible instead of a dead button: the cap is not a guard that fires on click,
// it is a sentence on the card.
function GearModifyStage({ sheet, equipment, persistedEq, accent, target, onSheetChange, onDone }) {
  var IR = window.ItemResolve;
  var SW = window.SWItems;
  var regs = sheetItemRegistries(sheet) || {};   // B-852: the view THIS SHEET owns, never discovery
  var attachmentReg = regs.attachments || {};
  var _focus = React.useState(null), focusId = _focus[0], setFocusId = _focus[1];
  var _filter = React.useState(''), filter = _filter[0], setFilter = _filter[1];
  // D-GMD-1 — the refused set is COLLAPSED by default. At the drawn fixture that is 81 of 128
  // candidates; at that scale the inert rows drown the decision, which is why §2.4's blanket
  // visible-inert was amended on Jason's evidence.
  var _showNo = React.useState(false), showRefused = _showNo[0], setShowRefused = _showNo[1];
  // D-GMD-7 — long effect text discloses per row, on its OWN control. Not the card's click: a
  // click means FOCUS here, and most truncated rows are REFUSED and therefore unclickable.
  var _open = React.useState({}), openText = _open[0], setOpenText = _open[1];

  var kind = target.kind, idx = target.idx;
  var item = kind === 'weapon' ? (equipment.weapons || [])[idx]
    : kind === 'armor' ? equipment.armor
    : (equipment.gear || [])[idx];
  var persisted = kind === 'weapon' ? (persistedEq.weapons || [])[idx]
    : kind === 'armor' ? (persistedEq.armor || {})
    : (persistedEq.gear || [])[idx];

  // The CANONICAL record, not the display shape. canonicalToStore whitelists fields and drops
  // `categories`/`_source_key` — the two the fit check reads — so the constraint has to be asked
  // of the record the registry holds (resolve-at-render: the display shape is the projection).
  // R-SPC-3 (B-666): the display shape's `key` is the SECTION-QUALIFIED catalog address now
  // ('weapons:vibro_ax'); a registry is keyed by the BARE id, which the same shape carries as
  // `regId`. `key` is the fallback for a legacy pre-id-ref entry, whose key (if any) is bare.
  var regMap = kind === 'weapon' ? regs.weapons : kind === 'armor' ? regs.armor : regs.gear;
  var canonId = item && (item.regId || item.key);
  var canonRec = (canonId && regMap) ? (regMap[canonId] || null) : null;
  // A (D-377, finding 1): a crafted item has no registry row — its record is the inline profile the Workshop wrote, so the fit
  // and the writer ask THAT (a hilt's `crystal_damage_mod` is what lets a crystal fit and read its numbers).
  if (!canonRec && persisted && persisted._inline) canonRec = persisted._inline;

  // source key → readable name, so an `item_limit` refusal names models instead of OggDude codes.
  var keyNames = React.useMemo(function () {
    var m = {};
    [regs.weapons, regs.armor, regs.gear].forEach(function (r) {
      if (!r) return;
      Object.keys(r).forEach(function (k) { if (r[k] && r[k]._source_key && !m[r[k]._source_key]) m[r[k]._source_key] = r[k].name; });
    });
    return m;
  }, [regs.weapons, regs.armor, regs.gear]);

  if (!item || !IR) {
    return h('div', { className: 'hp-plate', 'data-pad': 'read', 'data-testid': 'gear-modify-stage', 'data-paper': '1' },
      h('div', { className: 'hp-none' }, 'Item not found.'),
      h('div', { className: 'sb-pk-verbs' }, h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'gear-modify-done', onClick: onDone }, 'Done')));
  }

  var installed = item.attachments || [];
  // B-227: PC lane — the MODIFY panel degrades on a dangling id-ref for the same reason the
  // loadout panel does. Its INSTALLED list below already fell back to `attachmentReg[id] || {}`,
  // so the list survived a bad id while this accounting call above it threw first.
  var hpAcct = IR.pcHardpointAccounting(item.hp, installed, attachmentReg, canonRec);   // B-1468: the stock crystal
  var hpFree = Math.max(0, hpAcct.capacity - hpAcct.used - hpAcct.uncosted);

  // B-1403 (R-B1403-7): both writes go through the ONE attachments[] writer, `ItemResolve.writeAttachment`, which the
  // store's FINALIZE and the loadout panel call too. Unchanged here: install still writes `{ id, mods: [] }` and stays free
  // (D-279); the helper's fit + HP check is the same verdict that already made a refused candidate unfocusable.
  function install(attId) {
    var att = attachmentReg[attId] || {};
    var names = ((att.constraints || {}).item_limit || []).map(function (k) { return keyNames[k] || k; });
    var next = IR.writeAttachment(persistedEq, { kind: kind, idx: idx },
      { op: 'attach', check: 'fit', id: attId, registry: attachmentReg, record: canonRec, hp: item.hp, itemLimitNames: names });
    if (next !== persistedEq && onSheetChange) onSheetChange({ equipment: next });
    setFocusId(null);
  }

  // D-GMD-3 draws a remove control beside each INSTALLED row, so the stage needs the inverse of
  // install(). Same write path, same shape — the panel has its own `removeAttachment`, but that one
  // lives in `LoadoutItemDetail` and closing over the panel's state from here would be a second
  // writer on one quantity.
  function uninstall(attIdx) {
    var next = IR.writeAttachment(persistedEq, { kind: kind, idx: idx }, { op: 'detach', index: attIdx });
    if (next === persistedEq) return;
    if (onSheetChange) onSheetChange({ equipment: next });
    setFocusId(null);
  }

  // D1 (D-291; card release-r5-f1 §2.2, ADDENDUM 18): each option of an installed attachment is ONE SWRPGUI.CountStep — N
  // installs are N entries of its key (R-R5A-1), written through the one writer; the + holds at the printed count (`rank`,
  // absent = 1) with the book's reason. Its words are the registry's own, per install (D-291's note on the Actuating Module).
  function modOptions(rec, a, ai) {
    var U = window.SWRPGUI || {};
    var opts = (rec && rec.added_mods) || [];
    if (!opts.length || !U.CountStep) return null;
    var keys = (a && a.mods) || [];
    function step(op, key) {
      var next = IR.writeAttachment(persistedEq, { kind: kind, idx: idx }, { op: op, index: ai, mod: key, registry: attachmentReg });
      if (next !== persistedEq && onSheetChange) onSheetChange({ equipment: next });
    }
    return h('div', { 'data-testid': 'gear-modify-options-' + ai },
      h('div', { className: 'sb-e-m' }, 'Options'),
      opts.map(function (m, mi) {
        var key = m.key || m.id || ('mod_' + mi), max = IR.optionInstallCap(m);   // the engine's one cap rule (ruling release-r5-f1 C1)
        var n = keys.filter(function (k) { return k === key; }).length, held = n >= max;
        return h(U.CountStep, { key: key, label: readableModText(m.description || key), count: n, testid: 'gear-modify-opt-' + ai + '-' + key,
          reason: held ? craftHeldWords({ limit: 'max_count', max: max }) : null, max: max, minusHeld: n < 1, plusHeld: held,
          onMinus: function () { step('mod-drop', key); }, onPlus: function () { step('mod-add', key); } });
      }));
  }

  // ── candidates: every attachment of this item's kind, fitting ones first ────────────────────
  var installedIds = (installed || []).map(function (a) { return a && a.id; }).filter(Boolean);
  var installedKey = installedIds.join('|');
  var candidates = React.useMemo(function () {
    var out = [];
    Object.keys(attachmentReg).forEach(function (id) {
      var att = attachmentReg[id];
      // `type`, not `category` — the registry has never carried a `category` field, so the old
      // bench's `a.category !== kind` filter excluded nothing and showed armour mods on blasters.
      if (!att || !att.name || att.type !== kind) return;
      // D-GMD-3 — AN INSTALLED ATTACHMENT LEAVES THE LIST. Without this it stayed offered, and
      // clicking it again installed a SECOND COPY: two identical rows in the panel's ATTACHMENTS,
      // two hard points spent, surviving a reload. That is B-254's third hypothesis, confirmed by
      // repro and worse than it was filed — the row read as "you can only select one", but the
      // stage was in fact happy to sell you the same part twice.
      // Compared by ID, which is what the persisted entries carry; a genuine duplicate-install
      // case (if one ever surfaces in RAW) is Jason's call, not a silent allowance.
      if (installedIds.indexOf(id) >= 0) return;
      var names = ((att.constraints || {}).item_limit || []).map(function (k) { return keyNames[k] || k; });
      var fit = IR.attachmentFit(canonRec, att, { kind: kind, hpFree: hpFree, itemLimitNames: names });
      out.push({ id: id, att: att, fit: fit });
    });
    out.sort(function (a, b) {
      if (a.fit.fits !== b.fit.fits) return a.fit.fits ? -1 : 1;   // fitting first…
      // …then CHEAPEST FIRST, as the approved drawing orders it. Sorting by name alone put a 3 HP
      // restricted attachment above the 1 HP ones, which reads as "here is what you should look at"
      // when the thing that decides most choices is what you can afford. Uncosted (`hp` null) sort
      // last within their group: an unknown price is not a cheap one.
      var ah = (a.att.hp == null) ? 99 : a.att.hp;
      var bh = (b.att.hp == null) ? 99 : b.att.hp;
      if (ah !== bh) return ah - bh;
      return a.att.name.localeCompare(b.att.name);
    });
    return out;
    // `installedKey` is in the deps DELIBERATELY. `hpFree` also changes on every install, so the
    // memo would recompute anyway today — but that is a coincidence of the current budget, not a
    // contract: install something costing 0 HP and the list would silently keep offering it.
  }, [attachmentReg, kind, canonRec, hpFree, keyNames, installedKey]);

  var shown = filter
    ? candidates.filter(function (c) { return c.att.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0; })
    : candidates;
  var focused = focusId ? candidates.filter(function (c) { return c.id === focusId; })[0] : null;

  // ── D-GMD-1 — three buckets, and only one of them collapses ─────────────────────────────────
  // FITTING renders first-class. OVER-BUDGET stays VISIBLE-INERT — the cap being a sentence on the
  // card is what makes enforcement legible, and that is the half of §2.4's premise that survives.
  // REFUSED (a constraint said no) collapses behind one line, because at the real count the inert
  // rows drown the decision rather than informing it.
  var hpOnly = function (c) {
    return c.fit.reasons.length === 1 && /HP free$/.test(c.fit.reasons[0]);
  };
  var fittingRows = shown.filter(function (c) { return c.fit.fits; });
  var overRows = shown.filter(function (c) { return !c.fit.fits && hpOnly(c); });
  var refusedRows = shown.filter(function (c) { return !c.fit.fits && !hpOnly(c); });

  // ── the delta: the shared core, twice ──────────────────────────────────────────────────────
  // `pending` is installed + the focused candidate. Differencing two runs of ONE function is what
  // keeps the preview honest — whatever the core does to a real install it does here first.
  var deltaCells = [];
  var deltaLive = false;
  if (canonRec && (kind === 'weapon' || kind === 'armor')) {
    var pending = focused ? installed.concat([{ id: focused.id, mods: [] }]) : installed;
    deltaLive = !!focused && focused.fit.fits;
    // B-1185 (R-B1185-1) + B-1099 (D-411 item 28): "was" is the sheet's own projection and "now" is that run again with the
    // focused part appended — the ONE path the sheet reads (resolveSheetEquipment: the attachments through
    // ItemResolve.applyInstalledAttachments, then the holder's talents through ItemResolve.applyCharacterEffects), formatted
    // by canonicalToStore inside it, so the FFG "+N" brawn convention has exactly one owner. The pending equipment is INSTALL's
    // own write (ItemResolve.writeAttachment, install()'s arguments), held here and never saved.
    var pendNames = focused ? (((focused.att.constraints || {}).item_limit || []).map(function (k) { return keyNames[k] || k; })) : [];
    var pendEq = focused ? IR.writeAttachment(persistedEq, { kind: kind, idx: idx },
      { op: 'attach', check: 'fit', id: focused.id, registry: attachmentReg, record: canonRec, hp: item.hp, itemLimitNames: pendNames }) : persistedEq;
    var resB = resolveSheetEquipment(persistedEq, sheet), resA = resolveSheetEquipment(pendEq, sheet);
    if (kind === 'weapon') {
      var fb = (resB.weapons || [])[idx] || {}, fa = (resA.weapons || [])[idx] || {};
      deltaCells = [
        { label: 'DMG', was: fb.damage, now: fa.damage },
        { label: 'CRIT', was: critText(fb.crit), now: critText(fa.crit) },
        { label: 'ENC', was: fb.encumbrance, now: fa.encumbrance },
        { label: 'QUALITIES', wide: true, was: (fb.qualities || []).join(', ') || '—', now: (fa.qualities || []).join(', ') || '—' },
      ];
      // A (D-377, redraw A): a hilt with no crystal folds DMG + CRIT into one cell — "Crystal Needed → Damage 6 · Critical 2" with
      // a crystal focused (the pending run's own numbers), Crystal Needed alone with none. Never a 0 where the damage sits.
      if (hiltNeedsCrystal(canonRec, installed, attachmentReg)) {
        var hiltLive = !hiltNeedsCrystal(canonRec, pending, attachmentReg);
        deltaCells = [{ label: 'HILT', hilt: true, live: hiltLive, dmg: deltaCells[0].now, critShown: deltaCells[1].now }].concat(deltaCells.slice(2));   // the CRIT cell's own critText (B-1195: one printing site)
      }
    } else {
      var side = function (r, s) { return r['defense_' + s] != null ? r['defense_' + s] : (r.defense || 0); };
      var ab = resB.armor || {}, aa = resA.armor || {};
      deltaCells = [
        { label: 'SOAK', was: ab.soak || 0, now: aa.soak || 0 },
        { label: 'DEF MELEE', was: side(ab, 'melee'), now: side(aa, 'melee') },
        { label: 'DEF RANGED', was: side(ab, 'ranged'), now: side(aa, 'ranged') },
        { label: 'ENC', was: ab.encumbrance, now: aa.encumbrance },
      ];
    }
  }

  // ── what a candidate does, in one line ──────────────────────────────────────────────────────
  // still read ABOVE (the options' CountStep label, ~:28266, hoisted); F-1 moved only effectParts' quote off it
  var readableModText = (SW && SW.readableModText) || function (t) { return t; };
  function effectParts(att) {
    var parts = [];
    function push(text, tone) { parts.push({ text: text, tone: tone }); }
    var sign = function (n) { return (n > 0 ? '+' : '') + n; };
    if (att.damage_mod != null) push('Damage ' + sign(att.damage_mod), att.damage_mod > 0 ? 'up' : 'down');
    if (att.damage_set != null) push('Damage ' + att.damage_set, 'plain');
    // A LOWER crit rating is better — the tone follows the benefit, not the sign.
    if (att.crit_mod != null) push('Critical ' + (att.crit_mod > 0 ? '+' : '−') + Math.abs(att.crit_mod), att.crit_mod < 0 ? 'up' : 'down');
    if (att.crit_set != null) push('Critical ' + att.crit_set, 'plain');
    if (att.range_mod != null) push('Range ' + sign(att.range_mod) + ' band', att.range_mod > 0 ? 'up' : 'down');
    if (att.soak_mod != null) push('Soak ' + sign(att.soak_mod), att.soak_mod > 0 ? 'up' : 'down');
    if (att.defense_melee_mod != null) push('Melee defence ' + sign(att.defense_melee_mod), att.defense_melee_mod > 0 ? 'up' : 'down');
    if (att.defense_ranged_mod != null) push('Ranged defence ' + sign(att.defense_ranged_mod), att.defense_ranged_mod > 0 ? 'up' : 'down');
    if (att.encumbrance_mod != null) push('Encumbrance ' + sign(att.encumbrance_mod), att.encumbrance_mod > 0 ? 'down' : 'up');
    if (att.encumbrance_threshold_bonus) push('Carry +' + att.encumbrance_threshold_bonus, 'up');
    // B-257 — NOT every added quality is a benefit. These six are drawbacks in RAW, confirmed
    // against the shipped `qualities.vocab.json` descriptions rather than from memory: Cumbersome
    // and Unwieldy gate you on a characteristic, Inaccurate adds setback, Limited Ammo caps uses,
    // Prepare costs maneuvers, Slow-Firing costs rounds. Toning them green put "Cumbersome 2" in
    // benefit colour beside a correctly-amber "Encumbrance +3" — the drawing transcribed that
    // faithfully rather than quietly improving it, which is how it got noticed.
    // `stun_damage` is deliberately NEITHER: strain instead of wounds is a property, not a verdict.
    var DRAWBACK_QUALITIES = {
      cumbersome: 1, inaccurate: 1, limited_ammo: 1, prepare: 1, slow_firing: 1, unwieldy: 1,
    };
    var NEUTRAL_QUALITIES = { stun_damage: 1 };
    var qualityTone = function (key) {
      var k = String(key || '').toLowerCase();
      if (DRAWBACK_QUALITIES[k]) return 'down';
      if (NEUTRAL_QUALITIES[k]) return 'plain';
      return 'up';
    };
    // B-1544: added and removed qualities print the vocabulary's LABEL, never the key; the tone still reads the key.
    var qLab = IR.qualityLabelsOf ? IR.qualityLabelsOf(window._qualitiesDb) : {};
    (att.qualities_add || []).forEach(function (q) {
      push((qLab[q.key] || q.key || 'Quality') + (q.rank != null ? ' ' + q.rank : ''), qualityTone(q.key));
    });
    // …and the mirror: REMOVING a drawback is a BENEFIT. Uniform 'down' here was the same bug
    // upside down — "Removes Cumbersome" is good news drawn as a cost.
    (att.qualities_remove || []).forEach(function (q) {
      var key = (q && q.key) || q;
      var t = qualityTone(key);
      push('Removes ' + (qLab[key] || key), t === 'down' ? 'up' : (t === 'plain' ? 'plain' : 'down'));
    });
    // Nothing structured: quote the book rather than inventing a summary (R-ISC-3).
    if (!parts.length) {
      var bm = (att.base_mods || [])[0];
      if (bm && bm.description) {
        // D-GMD-7 — the book's own line can be a whole procedure (490 characters at the longest in
        // this registry, median 141 for the long ones). Rendered in full it makes a row four lines
        // tall, and at 43 fitting candidates the list stops being scannable. So it CLAMPS, and
        // carries the full text alongside for the row's own disclosure control.
        // F-1 (release-r7-bug-burndown ADDENDUM 37): the quote KEEPS its [XX] dice tokens (the chip draws them as glyphs,
        // as "What it does" does), and the clamp is the one token-safe SWItems.clampTokenText, so a die is never cut in half.
        var full = String(bm.description).replace(/\s+/g, ' ').trim();
        var cut = (SW && SW.clampTokenText) ? SW.clampTokenText(full, 104) : full;
        push(cut, 'plain');
        if (cut !== full) parts.push({ text: full, tone: 'plain', full: true });
      }
    }
    return parts;
  }


  // ── the focused candidate's cost, pending in the sockets (the paper `socksP` below) ───
  var pendCost = (focused && focused.fit.fits && typeof focused.att.hp === 'number') ? focused.att.hp : 0;

  var itemName = (persisted && persisted.nickname) || item.name;
  var subLine = kind === 'weapon'
    ? [item.name !== itemName ? item.name : item.type, item.skill].filter(Boolean).join(' · ')
    : (item.type || (kind === 'armor' ? 'Armour' : 'Gear'));



  // ── B-631 · MODIFY ON PAPER — the same state, writers and verdicts (hpAcct · install · uninstall · candidates ·
  // deltaCells · effectParts), composed on the pack's idiom: the CANDIDATES as a list on the left (fitting first,
  // cheapest first; over-budget rows visible-inert with their reason; the refused set behind a fold that says
  // how many), THE BAND on the right — the item, its socket track, WHAT CHANGES as was → now, INSTALL priced in
  // hard points, DONE, INSTALLED with a way back out. Two columns from the board's band (sheet.css), one below.
  // Lab frames: "Modify" · "Modify · a part focused" (lab 6b9acfe9). Testids are the dark stage's.
  var PAPER_LABEL = { 'DMG': 'Damage', 'CRIT': 'Critical', 'ENC': 'Encumbrance', 'QUALITIES': 'Qualities', 'SOAK': 'Soak', 'DEF MELEE': 'Melee defence', 'DEF RANGED': 'Ranged defence' };
  var candP = function (c) {
    var isFocus = focusId === c.id;
    var parts = effectParts(c.att);
    var visible = parts.filter(function (x) { return !x.full; });
    var fullPart = parts.filter(function (x) { return x.full; })[0];
    var isOpen = !!openText[c.id];
    var cite = [];
    if (c.att.price != null) cite.push(c.att.price.toLocaleString() + ' cr');
    if (c.att.rarity != null) cite.push('rarity ' + c.att.rarity);
    if ((c.att.sources || []).length) cite.push(c.att.sources.map(function (k) {   // the panel's rule: the sources index names the book, the core three fall back to BOOK_LABELS
      var ix = window._swrpgSourcesIndex || null; var id = ix && ix.aliasToId && ix.aliasToId[k]; var meta = id && ix.byId && ix.byId[id];
      return (meta && meta.label) || ((typeof BOOK_LABELS !== 'undefined' && BOOK_LABELS[k]) ? BOOK_LABELS[k] : k); }).join(' · '));
    var toggle = function () { setFocusId(isFocus ? null : c.id); };
    return h('div', { key: c.id, className: 'sb-att sb-cand', 'data-testid': 'gear-cand-' + c.id,
      'data-fits': c.fit.fits ? 'true' : 'false', 'data-reason': c.fit.reasons.join('; '), 'data-focus': isFocus ? '1' : null,
      role: c.fit.fits ? 'button' : null, tabIndex: c.fit.fits ? 0 : null,
      'aria-pressed': c.fit.fits ? isFocus : null, 'aria-disabled': c.fit.fits ? null : 'true',
      onClick: c.fit.fits ? toggle : null,
      onKeyDown: c.fit.fits ? function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } : null },
      h('div', { className: 'sb-e-h' }, h('span', { className: 'sb-e-n' }, c.att.name), h('span', { className: 'sb-e-m' }, (c.att.hp != null ? c.att.hp : '?') + ' HP')),
      (visible.length || fullPart) ? h('p', { className: 'sb-e-t' },
        (fullPart && isOpen ? [{ text: fullPart.text, tone: 'plain' }] : visible).map(function (x, pi) {
          return h('span', { key: pi }, pi ? ' · ' : null, h(x.tone === 'up' ? 'b' : 'span', { className: x.tone === 'down' ? 'sb-down' : null }, glyphs(x.text)));   // F-1: dice as glyphs
        }),
        fullPart ? h('button', { type: 'button', className: 'hp-quiet sb-more', 'data-testid': 'gear-more-' + c.id,
          'aria-expanded': isOpen ? 'true' : 'false', 'aria-label': (isOpen ? 'Show less about ' : 'Show more about ') + c.att.name,
          onClick: function (e) { e.stopPropagation(); var next = Object.assign({}, openText); if (isOpen) { delete next[c.id]; } else { next[c.id] = true; } setOpenText(next); } },
          isOpen ? 'Less' : 'More') : null) : null,
      h('div', { className: 'sb-mod-cite' },
        c.fit.reasons.map(function (r, ri) { return h('span', { key: 'r' + ri, className: 'sb-refuse' }, 'won’t fit — ' + r); }),
        c.fit.unevaluated.map(function (u, ui) { return h('span', { key: 'u' + ui, className: 'sb-e-m' }, u); }),
        c.att.restricted ? h('span', { className: 'sb-refuse' }, 'restricted') : null,
        cite.length ? h('span', null, cite.join(' · ')) : null));
  };
  var socksP = [];
  for (var sp = 0; sp < hpAcct.capacity; sp++) {
    socksP.push(h('span', { key: sp, className: 'sb-sock', 'data-sock': sp < hpAcct.used ? 'full' : (sp < hpAcct.used + pendCost ? 'pend' : 'free') }));
  }
  var bandP = h('div', { className: 'hp-plate sb-mod-band', 'data-pad': 'read', 'data-testid': 'gear-modify-band' },
    h('div', { className: 'sb-e-h' }, h('span', { className: 'sb-e-n sb-mod-name' }, itemName), subLine ? h('span', { className: 'sb-e-m' }, subLine) : null),
    hpAcct.capacity > 0 ? h('div', { className: 'sb-mod-socks' }, socksP, h('span', { className: 'sb-e-m', 'data-testid': 'gear-modify-hp' }, hpAcct.used + ' of ' + hpAcct.capacity + ' hard points')) : null,
    hpAcct.uncosted > 0 ? h('div', { className: 'sb-e-m sb-mod-uncosted' }, hpAcct.uncosted + ' fitted with no printed hard-point cost') : null,
    deltaCells.length ? h('div', { className: 'sb-e', 'data-testid': 'gear-modify-delta', 'data-live': deltaLive ? 'true' : 'false' },
      h('div', { className: 'sb-e-h' }, h('span', { className: 'sb-e-n' }, 'What changes'),
        focused ? h('span', { className: 'sb-e-m' }, ['Focused: ', h('b', { key: 'f' }, focused.att.name)]) : null),
      h('div', { className: 'sb-e-l' }, deltaCells.map(function (c) {
        if (c.hilt) return h('div', { key: 'hilt', 'data-testid': 'delta-hilt', 'data-now': c.live ? 'Damage ' + c.dmg + ' · Critical ' + c.critShown : HILT_NEEDED },
          h('b', null, HILT_NEEDED, c.live ? [' → ', h('span', { key: 'n', className: 'sb-mod-now' }, 'Damage ' + c.dmg + ' · Critical ' + c.critShown)] : null));
        var changed = String(c.was) !== String(c.now);
        return h('div', { key: c.label, 'data-testid': 'delta-' + c.label.toLowerCase().replace(/ /g, '-'), 'data-was': String(c.was), 'data-now': String(c.now) },
          PAPER_LABEL[c.label] || c.label, ' ', h('b', null, changed ? [String(c.was), ' → ', h('span', { key: 'n', className: 'sb-mod-now' }, String(c.now))] : String(c.now)));
      }))) : null,
    h('div', { className: 'sb-pk-verbs' },
      (focused && focused.fit.fits) ? h('button', { type: 'button', className: 'hp-commit', 'data-testid': 'gear-modify-install', onClick: function () { install(focused.id); } },
        'Install — ' + (focused.att.hp != null ? focused.att.hp + ' HP' : '? HP')) : null,
      h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'gear-modify-done', onClick: onDone }, 'Done')),
    h('div', { className: 'sb-e', 'data-testid': installed.length ? 'gear-modify-installed' : null },
      h('div', { className: 'sb-e-h' }, h('span', { className: 'sb-e-n' }, 'Installed'), !installed.length ? h('span', { className: 'sb-e-m' }, 'Nothing yet') : null),
      installed.map(function (a, ai) {
        var resolved = IR.pcResolveAttachment(a, attachmentReg);
        var rec = resolved || {};
        var nm = resolved ? (rec.name || (a && a.id) || 'Attachment') : '(unknown attachment)';
        return h('div', { key: (a && a.id) || ai, className: 'sb-att', 'data-uncosted': (resolved && rec.hp == null) ? '1' : null },
          h('div', { className: 'sb-e-h' }, h('span', { className: 'sb-e-n' }, nm), h('span', { className: 'sb-e-m' }, (rec.hp != null ? rec.hp : '?') + ' HP'),
            h('button', { type: 'button', className: 'hp-quiet', style: { minHeight: 44 }, 'data-testid': 'gear-modify-uninstall-' + ai, 'aria-label': 'Remove ' + (rec.name || 'attachment'), onClick: function () { uninstall(ai); } }, 'Remove')),
          modOptions(rec, a, ai));
      })));
  var listP = h('div', { className: 'hp-plate sb-mod-list', 'data-pad': 'data', 'data-testid': 'gear-cand-list' },
    h('div', { className: 'sb-e-h' }, h('span', { className: 'sb-e-n' }, 'Attachments'),
      h('span', { className: 'sb-e-m' }, fittingRows.length + ' fit' + (overRows.length ? ' · ' + overRows.length + ' over budget' : ''))),
    h('input', { type: 'text', className: 'sb-filter', value: filter, placeholder: 'Filter attachments', 'data-testid': 'gear-cand-filter', 'aria-label': 'Filter attachments',
      onChange: function (e) { setFilter(e.target.value); } }),
    (fittingRows.length === 0 && overRows.length === 0)
      ? h('p', { className: 'sb-e-t', 'data-testid': 'gear-cand-none' }, hpFree <= 0 ? 'No hard points free — remove something to fit more.' : 'Nothing here fits this item.') : null,
    fittingRows.map(candP), overRows.map(candP),
    refusedRows.length ? h('details', { className: 'sb-fold', open: showRefused || undefined, onToggle: function (e) { setShowRefused(e.target.open); } },
      h('summary', { className: 'sb-e-h', 'data-testid': 'gear-refused-toggle', 'aria-expanded': showRefused ? 'true' : 'false' },
        h('span', { className: 'sb-e-n' }, refusedRows.length + ' don’t fit this item'),
        h('span', { className: 'sb-e-m sb-fold-teaser' }, 'the constraint said no — readable, never installable')),
      showRefused ? refusedRows.map(candP) : null) : null);
  return h('div', { className: 'sb-mod', 'data-testid': 'gear-modify-stage', 'data-paper': '1' }, listP, bandP);
}

// ── Page 3: PROFILE — portrait, motivations, backstory, physical description ──
// B-1498 (B-289's class): an Obligation or Duty entry's stored `type` is a VOCABULARY KEY when the creator wrote it
// ("COMBATVICTORY") and a display name when an import or a pregen wrote it. PROFILE resolves the NAME at render through
// the one resolver, `lib/shared/mechTypeNames.js` (B-1605), fetched once when a face first needs it; a type that is no key
// is drawn as stored.
function useMechTypeNames(need) {
  var _n = React.useState(SWRPGMechTypeNames.peek()), names = _n[0], setNames = _n[1];
  React.useEffect(function () { if (!names && need) SWRPGMechTypeNames.load(qbFetchJson).then(function (m) { if (m) setNames(m); }); }, [!!need]);
  return names;
}
var mechTypeName = SWRPGMechTypeNames.name;

function SheetProfile({ char, sheet, accent, onSheetChange, onEditInCreator, onPortrait, portraitSrc }) {
  var rwRegP = useRewardsReg(honourRowsOf(sheet).length > 0);   // R6 S2-honours: the registry only when the band has a row
  var mechNames = useMechTypeNames(!!(sheet && (sheet.obligation || sheet.duty)));   // B-1498: entry types resolve to names
  var _pf = React.useState(null), paperField = _pf[0], setPaperField = _pf[1];   // B-620: the one liturgy row being edited on paper
  var _bso = React.useState(false), bsOpen = _bso[0], setBsOpen = _bso[1];         // B-620: the backstory fold's editor, opened by its own press (the dark page opens the editor on an empty backstory; the fold stays closed)

  // B-855 (frame H3): PROFILE owns the EDIT IN CREATOR door — a projecting writer (R-B854-1) — so it
  // resolves the SAME view the growth writers project through (SheetAdvancement :32919) and asks the
  // one predicate. No second predicate, no second view shape.
  var _hv = React.useState(null), _holdView = _hv[0], _setHoldView = _hv[1];
  // F-100 M6 R-M6G-12 (b) (frame D1): a carried pack that failed to load holds the PAPER door — B-855's look, B-30's
  // words and RETRY. The handler already refuses such a re-entry (R-M6G-10); this says why and offers the re-fetch.
  // The predicate is the handler's: the sheet's own stamps that SWRPGMods.failed() names. Checked before the other holds.
  var _doorFailed = useModsLoadError();
  var _drt = React.useState(0), _doorRetryTick = _drt[0], _setDoorRetryTick = _drt[1];
  var packFailed = !!(sheet && Array.isArray(sheet.mods) && sheet.mods.some(function (s) { return _doorFailed.indexOf(s) >= 0; }));
  function retryDoorPacks() { if (window.SWRPGMods && window.SWRPGMods.retry) window.SWRPGMods.retry(); _setDoorRetryTick(function (t) { return t + 1; }); }
  React.useEffect(function () {
    var alive = true;
    loadCanonicalCharacterBuild().then(function () {
      return window.SWRPGMods
        ? window.SWRPGMods.resolveForStamp(sheet.mods, window._canonicalCharbuildBase)
        : window._canonicalCharbuild;
    }).then(function (view) { if (alive && view) _setHoldView(view); });
    return function () { alive = false; };
  }, [JSON.stringify(sheet.mods), _doorRetryTick]);
  var hold = sheetHoldOf(sheet, _holdView);
  var holdText = holdReasonText(hold);
  var deletedSpecHeld = !hold && deletedSpecHeldOf(sheet, _holdView);   // F-100 M5 U15

  // Mechanic (Obligation / Duty / Morality) — lives on PROFILE because it's
  // a session-bookend narrative tool, not per-action combat state.
  // Which narrative tracks to show. Primary is book-priority (as shipped); a Force character in an
  // Obligation/Duty campaign (BOOKS-1c Part B, mode c) ALSO carries Morality alongside it — so the
  // sheet renders every track present, not just the first match.
  var book = sheet.book;
  var mechTracks = [];
  if (book === 'aor' && sheet.duty)          mechTracks.push({ key: 'duty', label: 'DUTY' });
  else if (book === 'fad' && sheet.morality) mechTracks.push({ key: 'morality', label: 'MORALITY' });
  else if (sheet.obligation)                 mechTracks.push({ key: 'obligation', label: 'OBLIGATION' });
  else if (sheet.duty)                       mechTracks.push({ key: 'duty', label: 'DUTY' });
  else if (sheet.morality)                   mechTracks.push({ key: 'morality', label: 'MORALITY' });
  if (sheet.morality && !mechTracks.some(function(t) { return t.key === 'morality'; })) {
    mechTracks.push({ key: 'morality', label: 'MORALITY' });
  }

  // (earnXp + the EXPERIENCE section removed — Amendment 1; XP editing lives in ADVANCEMENT.)
  var existingBackstory = sheet.backstory || char.description || '';
  // B-58 — the `source` stamp is a DECLARED vocabulary behind a NAMED predicate.
  // It used to be an inline string comparison: `source === 'FullCreator' || 'QuickBuild' || !backstory`.
  // Every shipped pregen carries a backstory AND a source outside that pair ('Original',
  // '<Line> Beginner Game'), so backstory was READ-ONLY on all 30 of them — not by decision, by string
  // accident, and any future producer stamping a new source would have inherited the same silence.
  //
  // PRODUCT ANSWER (Jason, 2026-07-30): a player may edit the backstory of ANY character on their
  // roster, pregens included. The roster copy is their own character; personalising it is what
  // "paper that remembers" is for, and the shipped prose is one carousel re-pick away
  // (handleSelectCharacter re-fetches pregen_sheets.json every time).
  // This is load-bearing for F-62: creation deliberately never asks for a backstory *because* it can
  // be added here afterwards — so "here" has to actually work, for pregens too.
  var isEditable = backstoryIsEditable(sheet);
  var _bs = React.useState(existingBackstory), backstoryText = _bs[0], setBackstoryText = _bs[1];
  // R-CS2: seed editing-open when the backstory is editable and empty, so the quick-add textarea
  // stays mounted (the render key is `isEditing` alone — typing no longer flips it to read-only
  // before a blur/commit can fire). Persists via onSheetChange on blur + DONE.
  var _ed = React.useState(isEditable && !existingBackstory), isEditing = _ed[0], setIsEditing = _ed[1];
  var fileInputRef = React.useRef(null);
  // B-1258 (b) (DECISIONS D-411 item 11, the 245 page): a picked file that is not a picture is refused in words, under the
  // PORTRAIT field, until the next pick
  var _pr = React.useState(false), portraitRefused = _pr[0], setPortraitRefused = _pr[1];
  var motivations = sheet.motivations || {};

  // Physical description state — seeded from the persisted sheet and committed
  // back through onSheetChange on blur. CS-1 (R-CS1): was local-only useState, so
  // edits vanished on unmount while the PDF exporter read the persisted fields.
  var _pd = React.useState(function() {
    var ap = sheet.appearance || {};
    return {
      age: ap.age || '', gender: ap.gender || '', height: ap.height || '', build: ap.build || '',
      hair: ap.hair || '', eyes: ap.eyes || '', notableFeatures: ap.notableFeatures || '',
      playerName: sheet.playerName || '',
    };
  }), physDesc = _pd[0], setPhysDesc = _pd[1];
  function commitPhysDesc(next) {
    if (!onSheetChange) return;
    onSheetChange({
      appearance: {
        age: next.age, gender: next.gender, height: next.height, build: next.build,
        hair: next.hair, eyes: next.eyes, notableFeatures: next.notableFeatures,
      },
      playerName: next.playerName,
    });
  }

  // SESSION NOTES (ruling table-feedback-crits-notes-mods §2) and THE JOURNAL (F-8): PROFILE is their
  // one host, at every band. Each is its own field — `sheet.notes`, `sheet.journal` — and its own writer.
  var sessionNotesBlock = h('div', { style: { marginBottom: 16 } },
    h(NotesPad, {
      notes: (sheet && sheet.notes) || '',
      onNotesChange: function (v) { if (onSheetChange) onSheetChange({ notes: v }); },
    }));
  var sessionJournalBlock = h('div', { style: { marginBottom: 16 } },
    h(JournalLog, {
      journal: sheet && sheet.journal,
      onJournalChange: function (next) { if (onSheetChange) onSheetChange({ journal: next }); },
    }));

  // ── F-45 §3.4 — RE-ENTRY. The owner action that reopens a finished character in the creator. ──
  // Lives on PROFILE, at the FOOT of the dossier: reachable, never in the way of play. Fork A (Jason): the
  // FULL creator for everyone. Not destructive — the finish adds a new record and the original is never
  // written — so the door arms with the plain note (R-PSW-6, "B-938 b"; R-PSDR-1: the dark face's loss
  // sentence went with that face; `CharResolve.reentryLossSummary` stays, test-only). Two-tap idiom.
  var _armEdit = React.useState(false), editArmed = _armEdit[0], setEditArmed = _armEdit[1];
  // ═══ B-620 · PROFILE ON PAPER — the lab's u_profile: the LITURGY. Fifteen named fields across the mechanic
  // card, MOTIVATION, PERSONALITY, DESCRIPTION, BACKSTORY, PLAYER; a field the record cannot fill says
  // "Not recorded" and keeps its place. Every value is a press-to-edit field on the same writers the dark
  // page used (commitPhysDesc, the mechanic's patch, onSheetChange). NOTES and JOURNAL fold; EDIT IN
  // CREATOR keeps its two-tap arm.
  var mechPatch = function (key, field, value) { if (!onSheetChange) return; var next = Object.assign({}, sheet[key] || {}); next[field] = value; var up = {}; up[key] = next; onSheetChange(up); };
  var fld = function (id, label, value, opts) {
    opts = opts || {};
    var editing = paperField === id;
    var shown = (value == null || value === '') ? null : String(value);
    return (
      <div className="sb-fld" key={id} data-testid={'prof-fld-' + id} data-editing={editing ? '1' : '0'}>
        <span>{label}</span>
        {editing
          ? <input className="sb-filter sb-fld-in" type={opts.number ? 'number' : 'text'} autoFocus defaultValue={shown || ''} min={opts.min} max={opts.max} aria-label={label} placeholder={opts.placeholder || ''}
              onBlur={function (e) { opts.commit(e.target.value); setPaperField(null); }}
              onKeyDown={function (e) { if (e.key === 'Enter') { e.preventDefault(); opts.commit(e.target.value); setPaperField(null); } if (e.key === 'Escape') { e.preventDefault(); setPaperField(null); } }} />
          : <button type="button" className="sb-fld-val" data-testid={'prof-edit-' + id} aria-label={(shown ? 'Edit ' : 'Record ') + label.toLowerCase()} onClick={function () { setPaperField(id); }}>
              {shown ? <b>{shown}</b> : <i className="hp-none">Not recorded</i>}</button>}
      </div>);
  };
  var mechCards = mechTracks.map(function (t) {
    var mech = sheet[t.key] || {}; var isMor = t.key === 'morality'; var entries = Array.isArray(mech.entries) ? mech.entries : [];
    var sum = entries.reduce(function (n, en) { return n + (parseInt(en.magnitude, 10) || 0); }, 0);
    var patchEntries = function (next) { var total = next.reduce(function (n, en) { return n + (parseInt(en.magnitude, 10) || 0); }, 0); var up = {}; up[t.key] = Object.assign({}, mech, { entries: next, value: total }); if (onSheetChange) onSheetChange(up); };
    var word = t.label.charAt(0) + t.label.slice(1).toLowerCase();
    return (
      <div className="sb-e" key={t.key} data-testid={'prof-mech-' + t.key}>
        <div className="sb-e-h"><span className="sb-e-n">{word}</span><span className="sb-e-m">{'Value ' + (mech.value || 0) + (isMor && (mech.conflict || 0) > 0 ? ' · Conflict ' + mech.conflict : '')}</span></div>
        {fld(t.key + '-value', 'Value', mech.value || 0, { number: true, min: 0, max: 100, commit: function (v) { var n = parseInt(v, 10); if (!isNaN(n)) mechPatch(t.key, 'value', Math.max(0, Math.min(100, n))); } })}
        {/* B-1630 (DECISIONS D-417 item 3): the pair the creator saved, by name (sheetData.morality.strength / .weakness), read-only;
            a pair the record cannot fill says Not recorded (the liturgy) */}
        {isMor ? [['morality-strength', 'Emotional strength', mech.strength], ['morality-weakness', 'Emotional weakness', mech.weakness]].map(function (r) {
          return <div className="sb-fld" key={r[0]} data-testid={'prof-fld-' + r[0]}><span>{r[1]}</span>{r[2] ? <b>{r[2]}</b> : <i className="hp-none">Not recorded</i>}</div>; }) : null}
        {isMor ? fld('morality-conflict', 'Conflict', mech.conflict != null ? mech.conflict : 0, { number: true, min: 0, max: 20, commit: function (v) { var n = parseInt(v, 10); if (!isNaN(n)) mechPatch('morality', 'conflict', Math.max(0, Math.min(20, n))); } }) : null}
        {!isMor ? entries.map(function (en, i) {
          var enName = mechTypeName(mechNames, t.key, en.type);   // B-1498: the name, never the stored key
          return (
            <div className="sb-fld" key={'e' + i} data-testid={'prof-mech-entry-' + i}>
              <span>{enName || 'Entry'}</span>
              <b>{String(en.magnitude || 0)}
                <span className="sb-critchips" style={{ display: 'inline-flex', marginLeft: 8, marginTop: 0 }}>
                  <button type="button" className="hp-quiet" aria-label={'Lower ' + (enName || 'entry') + ' by 5'} onClick={function () { patchEntries(entries.map(function (x, j) { return j === i ? Object.assign({}, x, { magnitude: Math.max(0, (parseInt(x.magnitude, 10) || 0) - 5) }) : x; })); }}>−5</button>
                  <button type="button" className="hp-quiet" aria-label={'Raise ' + (enName || 'entry') + ' by 5'} onClick={function () { patchEntries(entries.map(function (x, j) { return j === i ? Object.assign({}, x, { magnitude: (parseInt(x.magnitude, 10) || 0) + 5 }) : x; })); }}>+5</button>
                  <button type="button" className="hp-quiet" aria-label={'Remove ' + (enName || 'entry')} onClick={function () { patchEntries(entries.filter(function (_, j) { return j !== i; })); }}>Remove</button>
                </span></b>
            </div>);
        }) : null}
        {!isMor ? <div className="sb-pk-verbs" style={{ marginTop: 8, paddingTop: 8 }}><button type="button" className="hp-quiet" onClick={function () { patchEntries(entries.concat([{ type: word, magnitude: 5 }])); }}>{'Add ' + t.label.toLowerCase()}</button></div> : null}
        {!isMor && entries.length && sum !== (mech.value || 0) ? <p className="sb-e-t"><span className="sb-refuse">{'Entries sum to ' + sum}</span></p> : null}
        {mech.description ? <p className="sb-e-t">{mech.description}</p> : null}
      </div>);
  });
  var motv = sheet.motivation, motText = '';
  if (motv && (motv.category || motv.specific || motv.custom)) {
    var mcb = (window._canonicalCharbuild || {});
    var mcat = ((mcb.motivationCategories || []).filter(function (c) { return c.id === motv.category; })[0] || {}).name;
    var mspec = ((mcb.motivationSpecifics || []).filter(function (x) { return x.id === motv.specific; })[0] || {}).name;
    motText = motv.custom || [mcat, mspec].filter(Boolean).join(' — ');
  }
  var backstoryFull = backstoryText || (isEditable ? '' : (sheet.backstory || char.description || ''));
  var bsTeaser = backstoryFull ? (backstoryFull.length > 110 ? backstoryFull.slice(0, 110).replace(/\s+\S*$/, '') + '…' : backstoryFull) : 'No backstory on file';
  var descRows = [['age', 'Age'], ['gender', 'Gender'], ['height', 'Height'], ['build', 'Build'], ['hair', 'Hair'], ['eyes', 'Eyes'], ['notableFeatures', 'Notable features']];
  return (
    <div className="sb-profile" data-testid="sheet-profile">
      {/* (a) VARIANT, for Architect UI's render (paper-verify C11): census row 25's KEEP — the name and the portrait as
          edit paths, on the liturgy's own press-to-edit field and the kit's quiet controls. The writers are the dark
          page's: `onSheetChange({ name })` and the portrait through `reEncodePortrait` (B-926's bound). */}
      <div className="sb-e" data-testid="prof-identity"><div className="sb-e-h"><span className="sb-e-n">Character</span></div>
        {fld('charname', 'Name', sheet.name, { commit: function (v) { v = (v || '').trim(); if (v && v !== sheet.name && onSheetChange) onSheetChange({ name: v }); } })}
        <div className="sb-fld" data-testid="prof-portrait"><span>Portrait</span>
          <b>{portraitSrc ? 'On file' : <i className="hp-none">Not recorded</i>}
            <span className="sb-critchips" style={{ display: 'inline-flex', marginLeft: 8, marginTop: 0 }}>
              <button type="button" className="hp-quiet" data-testid="prof-portrait-change" onClick={function () { if (fileInputRef.current) fileInputRef.current.click(); }}>{portraitSrc ? 'Change' : 'Add'}</button>
              {portraitSrc && onPortrait ? <button type="button" className="hp-quiet" data-testid="prof-portrait-remove" onClick={function () { onPortrait(''); }}>Remove</button> : null}
            </span></b>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} data-testid="prof-portrait-input"
            onChange={function (e) { var file = e.target.files && e.target.files[0]; if (!file || !onPortrait) return; e.target.value = '';
              /* B-1258: the ONE image check first (`SWRPGPicture.check`), so a file that is not a picture is never kept as a
                 blank "On file"; `reEncodePortrait`'s keep-the-original fallback stays for the OggDude import it was made for. */
              var P = window.SWRPGPicture; if (!P) return;
              setPortraitRefused(false);
              P.check(file).then(function (r) { if (!r.ok) { setPortraitRefused(true); return; } return P.readFile(file).then(reEncodePortrait).then(function (pp) { if (pp) onPortrait(pp); }); }); }} />
        </div>
        {portraitRefused ? <p className="sb-e-t" data-testid="prof-portrait-refused"><span className="sb-refuse" role="alert" style={{ whiteSpace: 'normal', display: 'inline-block' }}>That file isn’t a picture this app can read.</span></p> : null}
      </div>
      {mechCards}
      {/* R6 S2-honours (R-R4D-5, the band only) + D7 (ADDENDUM 7): HONOURS after the Duty/Obligation block, where the approved D7 frame mounts it */}
      <SheetHonours sheet={sheet} reg={rwRegP} onSheetChange={onSheetChange} />
      <div className="sb-e" data-testid="prof-motivation"><div className="sb-e-h"><span className="sb-e-n">Motivation</span>{motText ? <span className="sb-e-m">{motText}</span> : null}</div>
        {motText ? null : <div className="sb-fld"><span>Motivation</span><i className="hp-none">Not recorded</i></div>}</div>
      <div className="sb-e" data-testid="prof-personality"><div className="sb-e-h"><span className="sb-e-n">Personality</span></div>
        {[['desire', 'Desire', 'e.g. Freedom'], ['fear', 'Fear', 'e.g. Failure'], ['strength', 'Strength', 'e.g. Courage'], ['flaw', 'Flaw', 'e.g. Arrogance']].map(function (m) {
          return fld(m[0], m[1], motivations[m[0]], { placeholder: m[2], commit: function (v) { var next = Object.assign({}, sheet.motivations || {}); next[m[0]] = (v || '').trim() || null; if (onSheetChange) onSheetChange({ motivations: next }); } });
        })}</div>
      <div className="sb-e" data-testid="prof-description"><div className="sb-e-h"><span className="sb-e-n">Description</span></div>
        {descRows.map(function (d) {
          return fld(d[0], d[1], physDesc[d[0]], { commit: function (v) { var next = Object.assign({}, physDesc); next[d[0]] = v; setPhysDesc(next); commitPhysDesc(next); } });
        })}</div>
      <details className="sb-fold" data-testid="prof-backstory" open={bsOpen || undefined}>
        <summary className="sb-e-h"><span className="sb-e-n">Backstory</span><span className="sb-e-m sb-fold-teaser">{bsTeaser}</span><i className="sb-grp-glyph" aria-hidden="true">▾</i></summary>
        {bsOpen
          ? <React.Fragment>
              <textarea className="sb-filter sb-fld-area" rows={6} autoFocus value={backstoryText} placeholder="Write your character’s backstory…" aria-label="Backstory" data-testid="prof-backstory-text"
                onChange={function (e) { setBackstoryText(e.target.value); }} onBlur={function () { if (onSheetChange) onSheetChange({ backstory: backstoryText }); }} />
              <div className="sb-pk-verbs" style={{ marginTop: 8, paddingTop: 8 }}><button type="button" className="hp-commit" data-testid="prof-backstory-save" onClick={function () { setBsOpen(false); setIsEditing(false); if (onSheetChange) onSheetChange({ backstory: backstoryText }); }}>Save</button></div>
            </React.Fragment>
          : <React.Fragment>
              <p className="sb-e-t" style={{ whiteSpace: 'pre-wrap' }}>{backstoryFull || 'No backstory on file.'}</p>
              {isEditable ? <div className="sb-pk-verbs" style={{ marginTop: 6, paddingTop: 8 }}><button type="button" className="hp-quiet" data-testid="prof-backstory-edit" onClick={function () { setBsOpen(true); setIsEditing(true); }}>{backstoryFull ? 'Edit' : 'Write it'}</button></div> : null}
            </React.Fragment>}
      </details>
      <div className="sb-e" data-testid="prof-player"><div className="sb-e-h"><span className="sb-e-n">Player</span></div>
        {fld('playerName', 'Name', physDesc.playerName, { placeholder: 'Your name', commit: function (v) { var next = Object.assign({}, physDesc, { playerName: v }); setPhysDesc(next); commitPhysDesc(next); } })}</div>
      <details className="sb-fold" data-testid="prof-notes"><summary className="sb-e-h"><span className="sb-e-n">Notes</span><span className="sb-e-m sb-fold-teaser">{(sheet && sheet.notes) ? String(sheet.notes).slice(0, 80) : 'Session notes'}</span><i className="sb-grp-glyph" aria-hidden="true">▾</i></summary>
        {sessionNotesBlock}</details>
      <details className="sb-fold" data-testid="prof-journal"><summary className="sb-e-h"><span className="sb-e-n">Journal</span><span className="sb-e-m sb-fold-teaser">{'The session log'}</span><i className="sb-grp-glyph" aria-hidden="true">▾</i></summary>
        {sessionJournalBlock}</details>
      {/* B-855 · frame H3: while held, the reason line + the door in its held quiet state with the
          refusal word (R-B854-1; R-EDG-1: the door is held at its shipped home, PROFILE). A press
          points to the reason line; no dialog, no re-entry. */}
      {/* F-100 M5 · frame U15 (APPROVED, R-M5B-AP-1): H3's held door, then the approved line under it. */}
      {/* F-100 M6 · frame D1 (APPROVED, R-M6G-26): H3's held door when a carried pack failed to load — the
          notice first, its own action slot carrying B-30's RETRY, then the held door with its refusal word. */}
      {onEditInCreator && packFailed ? <div className="sb-e" data-testid="prof-edit-held">
            <div className="sb-notice" role="status" data-hold="pack-failed" data-testid="profile-hold" tabIndex={-1}><span className="sb-notice-t">{PACK_FAILED_DOOR_LINE}</span><span className="sb-notice-a"><button type="button" className="hp-quiet" data-testid="door-pack-retry" onClick={retryDoorPacks}>Retry</button></span></div>
            <div className="sb-hold-door"><button type="button" className="hp-quiet" data-testid="edit-in-creator-btn" data-held="1" aria-disabled="true"
              onClick={function () { var el = document.querySelector('[data-testid="profile-hold"]'); if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); } }}>Edit in creator</button><span className="sb-refuse">Held</span></div>
          </div>
      : onEditInCreator && deletedSpecHeld ? <div className="sb-e" data-testid="prof-edit-held">
            <div className="sb-hold-door"><button type="button" className="hp-quiet" data-testid="edit-in-creator-btn" data-held="1" aria-disabled="true"
              onClick={function () { var el = document.querySelector('[data-testid="profile-hold"]'); if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); } }}>Edit in creator</button><span className="sb-refuse">Held</span></div>
            <div className="sb-notice" role="status" data-hold="deleted-spec" data-testid="profile-hold" tabIndex={-1} style={{ marginTop: 12 }}><span className="sb-notice-t">{deletedSpecDoorLine(sheet, _holdView)}</span></div>
          </div>
      : onEditInCreator ? (hold
        ? <div className="sb-e" data-testid="prof-edit-held">
            <div className="sb-notice" role="status" data-hold={hold.variant} data-testid="profile-hold" tabIndex={-1}><span className="sb-notice-t">{holdText}</span></div>
            <div className="sb-hold-door"><button type="button" className="hp-quiet" data-testid="edit-in-creator-btn" data-held="1" aria-disabled="true"
              onClick={function () { var el = document.querySelector('[data-testid="profile-hold"]'); if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); } }}>Edit in creator</button><span className="sb-refuse">Held</span></div>
          </div>
        : <div className="sb-e" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* B-755: the confirm hands `handleEditInCreator` the character and the sheet, as the dark door
            and the species ask do — called bare it returned at `if (!sheet)` and the door did nothing. */}
        <button type="button" className={editArmed ? 'sb-confirm' : 'hp-quiet'} data-testid="edit-in-creator-btn" aria-pressed={editArmed ? 'true' : 'false'}
          onClick={function () { if (editArmed) { setEditArmed(false); onEditInCreator(char, sheet); } else setEditArmed(true); }}>{editArmed ? 'Tap again to continue' : 'Edit in creator'}</button>
        {editArmed ? <span className="sb-e-m" data-testid="edit-in-creator-note">Leaves the sheet; the creator opens on this character.</span> : null}</div>) : null}
    </div>
  );
}

// (ForcePowerTreeView + FCTalentTree REMOVED — ruling tree-standardization-brief: both tree renderers
//  are unified into DirectionalTree + two adapters. F&D p.286 confirmed the connected-prerequisite rule
//  is identical for talents and Force upgrades — that shared rule (fcTalentIsAvailable/Purchased) now
//  serves the one component; the divergent renderers are gone.)
// (SheetForce REMOVED — ruling force-tab-dissolution: the FORCE tab is dissolved. Its machinery lives on
//  in SheetForceZone (OVERVIEW cockpit commit/ROLL) + the ADVANCEMENT fptree center mode.)

// TreeFitBox — scales its child (a fixed-size talent tree) to fit the available box with NO scroll,
// centered, never upscaling past 1:1. Same intent as the FORCE tab's adaptive rail (measure → fit any
// tree, smallest to tallest). scrollWidth/Height report the pre-transform layout size, so measuring the
// scaled inner still yields the tree's natural size; a ResizeObserver on both box + inner re-fits on a
// panel resize OR a spec switch (different tree = different natural height).
// Legibility floor (ruling advancement-ipad-band-triage §3/C1): the natural tree cell is 82px (app.jsx
// makeTree `cellW`); never scale a cell below ~66px, so tree text stays readable. When the tree can't fit
// the box at that floor (short tablet heights split the left column in half), the tree ZONE SCROLLS rather
// than shrinking to the 16px micro-tree an iPad exposed — "fit by arrangement, never by unreadable text."
var TREE_MIN_SCALE = 66 / 82;
function TreeFitBox({ children }) {
  var wrapRef = useRef(null), innerRef = useRef(null);
  var _s = useState({ scale: 1, scroll: false, w: 0, h: 0 });
  var st = _s[0], setSt = _s[1];
  useLayoutEffect(function() {
    var w = wrapRef.current, inner = innerRef.current;
    if (!w || !inner) return;
    function measure() {
      var cw = w.clientWidth, ch = w.clientHeight, tw = inner.scrollWidth, th = inner.scrollHeight;
      if (!tw || !th || !cw || !ch) return;
      var fit = Math.min(cw / tw, ch / th, 1);
      if (!(fit > 0)) fit = 1;
      var scroll = fit < TREE_MIN_SCALE;          // can't fit above the floor → scroll instead of shrink
      var scale = scroll ? TREE_MIN_SCALE : fit;
      setSt(function(prev) {
        if (Math.abs(prev.scale - scale) < 0.005 && prev.scroll === scroll && prev.w === tw && prev.h === th) return prev;
        return { scale: scale, scroll: scroll, w: tw, h: th };
      });
    }
    measure();
    var ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(measure) : null;
    if (ro) { ro.observe(w); ro.observe(inner); }
    var t = setTimeout(measure, 120);  // re-fit after fonts/layout settle
    return function() { if (ro) ro.disconnect(); clearTimeout(t); };
  }, []);
  var scale = st.scale, scroll = st.scroll;
  return h('div', { ref: wrapRef, style: {
      flex: '1 1 auto', minHeight: 0, minWidth: 0,
      display: scroll ? 'block' : 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: scroll ? 'auto' : 'hidden' } },
    // sizer gives the scroll container the SCALED footprint (a CSS transform doesn't change scrollWidth/Height)
    h('div', { style: scroll ? { width: Math.round(st.w * scale), height: Math.round(st.h * scale), margin: '0 auto' } : null },
      h('div', { ref: innerRef, style: {
        transform: 'scale(' + scale + ')',
        transformOrigin: scroll ? 'top left' : 'center center',
        flexShrink: 0 } }, children)));
}

// ── SheetAdvancement — spend session XP on skills, talents, Force powers.
// Replaces the dead-end "TREE is informational only" pattern: this is where
// post-creation purchasing actually happens. Lazy-loads oggdude data for
// career-skill detection (career skills cost 5 less per rank).
function SheetAdvancement({ sheet, accent, onSheetChange, initialPick, initialUnit, openHubGiven }) {
  var _modsFailed = useModsLoadError();  // B-30
  var _rt = useState(0), retryTick = _rt[0], setRetryTick = _rt[1];
  // This sheet's overlay failed to load iff it carries a stamp AND one of its stamps is in the failed set.
  var _modsNotice = !!(sheet && sheet.mods && sheet.mods.length && _modsFailed.length
    && sheet.mods.some(function(s) { return _modsFailed.indexOf(s) >= 0; }));
  // B-250 — the marker the load-seam repair stamped (R-B250-3). Reading `sheet` means the notice
  // appears on the load AFTER the repair too, which is the whole point of persisting it: the
  // repair itself is silent and one-shot, so without this the player never learns their budget
  // was zeroed.
  var _xpRepaired = !!(sheet && sheet._xpRepairedAt);
  // DISMISS deletes the field rather than setting a second flag — one less state to reason about,
  // and it rides the same autosave write-through as every other patch.
  var _dismissXpRepaired = useCallback(function() {
    if (onSheetChange) onSheetChange(function(prev) {
      var next = Object.assign({}, prev);
      delete next._xpRepairedAt;
      return next;
    });
  }, [onSheetChange]);
  var _retryMods = useCallback(function() { if (window.SWRPGMods && window.SWRPGMods.retry) window.SWRPGMods.retry(); setRetryTick(function(t) { return t + 1; }); }, []);
  var xp = sheet.xp || { total: 0, spent: 0, remaining: 0 };
  var totalXp = xp.total || 0;
  var spentXp = xp.spent || 0;
  var availableXp = xp.remaining != null ? xp.remaining : (totalXp - spentXp);

  // B-239 — ONE affordability predicate for all five buy spines. Four of them read a bare
  // `availableXp < cost`, and in JS `100 < undefined` is FALSE: an absent cost did not block the
  // purchase, and the next lines computed `availableXp - cost` / `spentXp + cost`, turning the
  // character's XP into **NaN**. After that every comparison against NaN is also false, so EVERY
  // later purchase was free too — one missing cost silently converted the whole budget to
  // unlimited. `buyBasicPower` carried a partial fix (`cost == null || …`) that never propagated,
  // and did not cover NaN — the exact value the cascade spreads.
  //
  // FAILS CLOSED ON BOTH SIDES. `cost` must be a finite number, and so must `availableXp`: a
  // character whose XP is ALREADY poisoned must not be able to buy anything either, which is what
  // stops the cascade rather than merely not starting it. Blocking a purchase is recoverable;
  // silently granting an infinite XP budget is not.
  function cannotAfford(cost) {
    return !Number.isFinite(cost) || !Number.isFinite(availableXp) || availableXp < cost;
  }
  var skills = sheet.skills || {};
  // `powers` = the PERSISTED entries (mutation target: buyForceUpgrade writes these back);
  // `powersDisplay` = resolved display shapes, index-aligned (map-preserving resolver).
  var powers = Array.isArray(sheet.forcePowers) ? sheet.forcePowers : [];
  var powersDisplay = sheetForcePowersForDisplay(sheet);
  // B-32: force-sensitivity gate for the basic-power ADD list — read from persisted inputs
  // (forceRatingBase, set by the creator + the B-20 fix; or the creation derived snapshot). No async.
  var forceRating = Math.max(Number(sheet.forceRatingBase) || 0, (sheet.derived && Number(sheet.derived.forceRating)) || 0);
  // B-41: the sensitivity flag above answers "is this character Force-sensitive at all"; the
  // PREREQUISITE needs the resolved rating, which includes Force Rating talent ranks — the very
  // mechanism a character uses to reach FR 2 (fad-gm p.26). Kept separate rather than widening the
  // flag: `forceRating >= 1` gates the whole ADD list and must keep reading persisted inputs.
  //
  // C1 (ruling b41-min-force-rating R-B41-1) — FLOORED BY THE SENSITIVITY VALUE, and it must stay
  // that way: the gate may never refuse a purchase the app's own flag says is legal.
  // Why it can differ: computeDerived has two branches (R-DAR1 legacy law, charResolve.js:430). A
  // sheet WITHOUT `derivedBonuses` returns the baked `derived` snapshot; a sheet WITH it returns
  // forceRatingBase + forceRatingBonus. WHEN THIS FLOOR WAS WRITTEN, all 30 shipped pregens shipped with no
  // `derivedBonuses` and the 10 Force users among them kept their rating ONLY in the snapshot (forceRatingBase: 0)
  // (B-735, trued 2026-10-28: the roster is now eighteen and every record carries the block since 49f5478db, so no
  // shipped pregen reaches the snapshot branch; the floor stays for saved legacy records, R-DAR1) — and
  // `buyTalent` writes `derivedBonuses` while never writing `forceRatingBase`. So the first talent
  // purchase flipped the branch and dropped the resolved rating to 0, badging every Force power
  // "YOU HAVE 0" on characters we ship. Measured: all 10 went 1 -> 0.
  // The floor is the sensitivity value and never exceeds it, so FR 2/3 gating is untouched.
  // The underlying data defect (pregens missing forceRatingBase) is B-49, not this lap.
  var frResolved = React.useMemo(function() {
    return Math.max(resolvedForceRating(sheet), forceRating);
  }, [sheet, forceRating]);

  var advDesktop = useLayoutMode() === 'desktop';   // dc3 sheet-tabs: ADVANCEMENT desktop growth workshop
  // DETAILS contextual window (clearance B3) — selected {key,kind,name,meta,desc}; parity on mobile.
  var _sel = useState(null), sel = _sel[0], setSel = _sel[1];
  // XP hero click-to-edit (clearance B3): award/adjust available XP; total follows so `spent` stays
  // consistent (no new save-shape). The hero strip is shared, so the edit is reachable on mobile too.
  var _xpEdit = useState(false), xpEditing = _xpEdit[0], setXpEditing = _xpEdit[1];
  // Desktop master-detail: which owned spec's tree fills the center column (default = first owned).
  // Clicking a spec in SPECIALIZATIONS switches it. Mobile ignores this (it stacks every tree).
  var _actSpec = useState(null), activeSpecId = _actSpec[0], setActiveSpecId = _actSpec[1];
  // Modular center (ruling advancement-modular-center): the center STAGE swaps mode. 'tree' = the
  // selected spec's talent tree (default); 'selector' = the New-Spec journey ON the stage (desktop) /
  // full-screen (mobile). The SPECIALIZE tab is gone — this is its home now.
  var _cmode = useState('tree'), centerMode = _cmode[0], setCenterMode = _cmode[1];
  var _gUnit = useState(initialUnit || 'skills'), growthUnit = _gUnit[0], setGrowthUnit = _gUnit[1];   // B-615: the paper spine's open unit; B-1646: a door may name it
  var _rwSel = useState(null), rwSel = _rwSel[0], setRwSel = _rwSel[1];   // R6 S2: the REWARDS type on the stage
  var rwReg = useRewardsReg();
  var _sigSel = useState(null), sigSel = _sigSel[0], setSigSel = _sigSel[1];   // F-100 R-SCC-1: the signature ability on the stage {id, tree}
  // Force-tab dissolution (ruling force-tab-dissolution): a 3rd center mode 'fptree' — a Force power's
  // upgrade tree on the center stage in BUY mode (desktop; mobile keeps the buy modal). Retires the
  // desktop buy modal. fpSel = the power being grown.
  var _fpSel = useState(null), fpSel = _fpSel[0], setFpSel = _fpSel[1];
  function setAvailableXp(v) {
    var n = Math.max(0, Math.floor(Number(v) || 0));
    if (onSheetChange) onSheetChange({ xp: Object.assign({}, xp, { remaining: n, total: spentXp + n }) });
  }

  // UXQP-23: two-tap XP-buy confirm (replaces native window.confirm; mirrors the
  // equipment/roster delete pattern at 8349). armedBuyKey discriminates the armed buy.
  var _armBuy = React.useState(null), armedBuyKey = _armBuy[0], setArmedBuyKey = _armBuy[1];
  React.useEffect(function() {
    if (armedBuyKey == null) return;
    var t = setTimeout(function() { setArmedBuyKey(null); }, 3000);
    return function() { clearTimeout(t); };
  }, [armedBuyKey]);

  // Canonical charbuild registries for career-skill detection + the skill catalog
  // (ADR 0003 3b: __SWForceData + the raw oggdude blob fetch RETIRED).
  // F-9 B3 — the sheet resolves its charbuild against its OWN stamp (sheet.mods), NOT the global
  // toggle view: a respec sheet keeps its trees/talents with the toggle off; a vanilla sheet keeps
  // the base trees with the toggle on (the (b)/(d) fixtures). resolveForStamp returns the pristine
  // base for an absent/empty stamp at zero cost, so vanilla sheets are unchanged.
  var _data = useState(null);
  var data = _data[0], setData = _data[1];
  useEffect(function() {
    var alive = true;
    // B-180: gate on SWRPGSources.ready() too — the creator does this (:7893) but the sheet did not,
    // so on the advancement path `SWRPGSources.current()` was null when NewSpecJourney rendered and its
    // source filter (fcFilterActive) fell through fail-open. Readying the index before setData makes the
    // advancement spec gate actually bite. Vanilla sheets are unaffected (ready() resolves ~instantly and
    // is cached).
    var sourcesReady = (window.SWRPGSources && window.SWRPGSources.ready) ? window.SWRPGSources.ready() : Promise.resolve();
    Promise.all([loadCanonicalCharacterBuild(), sourcesReady]).then(function() {
      return window.SWRPGMods
        ? window.SWRPGMods.resolveForStamp(sheet.mods, window._canonicalCharbuildBase)
        : window._canonicalCharbuild;
    }).then(function(view) { if (alive && view) setData(view); });
    return function() { alive = false; };
  }, [JSON.stringify(sheet.mods), retryTick]);  // B-30: retryTick re-runs the resolve after a RETRY

  // Resolve a Force power (tree + abilities) from the loaded charbuild for the DirectionalTree adapter —
  // the data-load that used to live INSIDE ForcePowerTreeView, hoisted to the call-site (id first, then a
  // name-index fallback for legacy saves). Retires the renderer's internal async fetch.
  function resolveFpPower(sel) {
    if (!sel || !data || !data.forcePowers) return null;
    if (sel.powerId && data.forcePowers[sel.powerId]) return data.forcePowers[sel.powerId];
    if (sel.name && window.CharResolve) {
      var hit = CharResolve.buildNameIndex(data.forcePowers).byName[String(sel.name).trim().toLowerCase()];
      if (typeof hit === 'string') return data.forcePowers[hit];
    }
    return null;
  }

  // Career skills for this character (lower XP cost per rank). Canonical id is
  // authoritative (D-3B2); collision-aware name fallback covers legacy saves. The
  // registry's career_skills are canonical skill ids -> sheet short keys via the
  // vocab alias bridge (35/35 verified at 3a).
  // F-100 M7 W2 (R-M7G-20): the status's SOURCES too, for the price row's because-suffix. Keyed on the whole sheet: a
  // talent grant, a pick or a species option moves the status as surely as a spec does (the old key list missed them).
  var careerStatus = useMemo(function() {
    var ks = sheetCareerSkillKeys(sheet, data);   // null while loading; [] for a career the registry does not know (kept)
    return ks == null ? null : (ks.length ? CharResolve.careerSkillStatus(sheet, data) : {});
  }, [data, sheet]);
  var careerSkills = careerStatus ? Object.keys(careerStatus) : null;

  function nextSkillRankCost(skillKey, currentRank) {
    // CR-RAW #2: delegates to THE shared cost function (one pricing path — §3.2); the creator's
    // buy-ranks screen calls the same core. Returns null past rank 5.
    var inCareer = careerSkills && careerSkills.indexOf(skillKey) !== -1;
    return CharResolve.skillRankCost((currentRank || 0) + 1, inCareer);
  }

  function buySkillRank(skillKey, currentRank, cost) {
    if (hold || cannotAfford(cost)) return;   // B-855: held → refused, the record untouched
    // UXQP-23: confirmation is the two-tap arm on the button; this is the pure commit.
    var newSkills = Object.assign({}, skills);
    newSkills[skillKey] = (currentRank || 0) + 1;
    var newXp = Object.assign({}, xp, {
      remaining: availableXp - cost,
      spent: spentXp + cost,
    });
    if (onSheetChange) onSheetChange({ skills: newSkills, xp: newXp });
  }

  // ── CS-4: talent purchases (own spec tree) ──────────────────────────
  // F-100 M7 W4 — THE ONE CHARACTERISTIC PICKER (R-M7K-37; R-M7G-22, -23, -24): Dedication's raise and Renegade Form's
  // pick at the buy, an unpicked swap chosen in place, and the owned cell's chosen panel (1440). Dedication's old dark
  // dialog is retired onto it (R-M7G-22, D-13).
  var _charPick = React.useState(null), charPick = _charPick[0], setCharPick = _charPick[1];
  // F-100 M8 step 3 (frame R6): the hub's "Choose a skill" lands here with the unmade pick, and the one picker opens on it
  var _m8Opened = React.useRef(null);
  React.useEffect(function () {
    var q = initialPick; if (!q || !data || _m8Opened.current === q || q.specId == null || typeof q.pos !== 'string') return;
    _m8Opened.current = q;
    var rc = q.pos.split('-');
    setCharPick({ mode: 'm8', pickId: q.pickId, kind: 'skill', talentKey: q.talentId, specKey: q.specId, row: Number(rc[0]), col: Number(rc[1]), anchor: null,
    cost: (function () { var sp = data && data.specializations, rec = Array.isArray(sp) ? sp.filter(function (x) { return x && x.id === q.specId; })[0] : (sp && sp[q.specId]);
      var tr = rec && rec.talent_tree && rec.talent_tree[Number(rc[0])]; return tr ? tr.cost : '—'; })() });
  }, [initialPick, data]);
  var closeCharPick = React.useCallback(function() { setCharPick(null); }, []);
  // B-181: the talent-choice skill picker's open-state — { mode:'buy'|'choose', talentKey, talentName,
  // specKey, row, col, cost, grants, initialPicks }. Raised by the tree's onBuy (buy mode: buy + file
  // the skills atomically) or by the needs-its-skills onChooseSkills (choose mode: file only, no XP).
  var _talPick = React.useState(null), talentPicker = _talPick[0], setTalentPicker = _talPick[1];
  // B-29: heal born-legacy specs at render — a no-talent character (Guided ALL, Custom
  // no-talent, pregens) owns its chosen spec with zero cells bought, so ADVANCEMENT shows the
  // buyable tree instead of misfiling it as pre-tree-tracking legacy. Existing characters
  // un-legacy on next open; a TRUE legacy sheet (no resolvable spec) still gets the honest stub.
  var sheetSpecs = CharResolve.resolveSheetSpecs(sheet, data);
  // B-659: the career through `CharResolve.sheetCareerId` — the id-ref, else the strict name lookup
  // `migrateSheet` writes back with — so a pre-id-ref save prices its own career on the FIRST visit,
  // not after the reload that carries the migrated id into the render copy.
  var career = (data && data.careers) ? data.careers[CharResolve.sheetCareerId(sheet, data.careers)] || null : null;   // for the New-Spec selector (modular center)
  // B-855 THE HOLD (ruling f100-b854 R-B854-1/2/3; R-B854-2 as amended by f100-editor-contracts-2 R-EC2-1). Every
  // writer below that PROJECTS through `data` refuses while this is set, and the record stays byte-identical;
  // wounds, strain, notes, credits and gear are other tabs' patches and are never held. One predicate, in the shared core.
  var hold = sheetHoldOf(sheet, data);
  var holdText = holdReasonText(hold);
  // F-100 M5 C4.3 / C5.2: the automatic write of an eligible difference, and its notice (U8 · C6 Changed · U9).
  var telling = useProjectionTelling(sheet, onSheetChange);

  // Resolve owned "row-col" positions across all specs → talent ids at those cells (repeats = ranks;
  // the ranked-stacking basis, eote p.128). The one input to derivedBonusesFromTalents on any buy.
  function ownedTalentIdsAll(specsList) {
    var ids = [];
    (specsList || []).forEach(function(s) {
      var rec = data && data.specializations ? data.specializations[s.id] : null;
      var tree = rec && rec.talent_tree;
      if (!tree) return;
      (s.owned || []).forEach(function(pos) {
        var rc = String(pos).split('-'); var r = +rc[0], c = +rc[1];
        var cell = tree[r] && tree[r].talents && tree[r].talents[c];
        if (cell && cell.talent) ids.push(cell.talent);
      });
    });
    return ids;
  }

  // DirectionalTree's talent adapter consumes a posKey→entry map ("<specId>_r<row>c<col>"); build it from persisted ownership.
  function ownedTalentsMap(spec) {
    var rec = data && data.specializations ? data.specializations[spec.id] : null;
    var tree = rec && rec.talent_tree;
    var map = {};
    (spec.owned || []).forEach(function(pos) {
      var rc = String(pos).split('-'); var r = +rc[0], c = +rc[1];
      var cell = tree && tree[r] && tree[r].talents && tree[r].talents[c];
      map[spec.id + '_r' + r + 'c' + c] = { talentKey: cell ? cell.talent : null, specKey: spec.id, row: r, col: c, cost: (tree && tree[r] ? tree[r].cost : 0) };
    });
    return map;
  }

  function buyTalent(talentKey, specKey, row, col, cost, charChoice) {
    if (hold || cannotAfford(cost)) return;   // B-855
    var specs = sheetSpecs.map(function(s) { return { id: s.id, owned: (s.owned || []).slice() }; });
    var target = null;
    for (var i = 0; i < specs.length; i++) { if (specs[i].id === specKey) { target = specs[i]; break; } }
    if (!target) { target = { id: specKey, owned: [] }; specs.push(target); }
    var pos = row + '-' + col;
    if (target.owned.indexOf(pos) === -1) target.owned.push(pos);
    // Recompute derivedBonuses from the WHOLE owned set (idempotent — no incremental drift). CS-2's
    // computeDerived absorbs it live: the OVERVIEW threshold moves the moment you buy (R-DAR4).
    var patch = {
      specializations: specs,
      // R-PFR-1 — THE SECOND RECORD, which this writer had never updated. `specializations[].owned`
      // above is positions; `talents` is the id-ref list that ABILITIES, the OVERVIEW cockpit and
      // BOTH PDF exports actually read. Creation wrote both and advancement wrote one, so a talent
      // bought here cost XP, lit up on the tree, and appeared nowhere else — including on the sheet
      // the player prints, which is where the defect was worst.
      // Projected through the SHARED core (never a copy in the sheet) from the whole owned set, so
      // it is idempotent and cannot drift from creation's shape: same function, same `{id, rank}`,
      // ranks collapsed identically.
      // F-100 M5 R-M5B-1/2: through THE ONE writer that keeps what a tree absent from `data` held (an own specialization
      // deleted from a present pack is not a hold, C5.2, so this buy can run over it); every tree present = the projection.
      talents: CharResolve.talentsKeepingAbsentTrees(sheet.talents, sheetSpecs, specs, (data && data.specializations) || {}),
      derivedBonuses: CharResolve.derivedBonusesFromTalents(ownedTalentIdsAll(specs), data && data.talents),
      // R-CS4d: owned equipment-conditional talents (armor_master pair) — computeDerived applies them
      // live against the equipped armor (buying Armor Master + wearing armor → soak +1 on the spot).
      derivedConditional: CharResolve.conditionalTalentsFromOwned(ownedTalentIdsAll(specs), data && data.talents),
      xp: Object.assign({}, xp, { remaining: availableXp - cost, spent: spentXp + cost }),
    };
    // B-1651: a buy that added a tree above also adds its id, as buyNewSpec does. Otherwise the key stays out of the patch.
    var specIds = CharResolve.ownedSpecIds(sheet.specializationIds, specs);
    if (specIds !== sheet.specializationIds) patch.specializationIds = specIds;
    // Dedication (eote p.134): +1 to the chosen characteristic, hard cap 6.
    if (talentKey === 'dedication' && charChoice) {
      var chars = Object.assign({}, sheet.characteristics || {});
      // B-736 — STAMP BEFORE RAISE (ruling `threshold-freezing-shape` Addendum 1 (1)). This branch is
      // the ONLY place in the app that raises a characteristic after creation, so it is the only place
      // that can invalidate the creation values the wound and strain thresholds are frozen at
      // (EotE p.94 · AoR p.104 · FaD p.104 — further Brawn/Willpower increases do not move them).
      // A record built before that ruling carries no `characteristicsAtCreation`; `computeDerived` falls
      // back to its LIVE characteristics, which is exactly right for it — until this very line moves
      // one. So the PRE-RAISE map (`chars` is still a verbatim copy of the persisted block here) is
      // stamped into the SAME patch, and the freeze point is by construction the value the character
      // held before its first post-ruling increase. That is what makes the read fallback a freeze
      // rather than a rename: without this the threshold would recompute from live Brawn on every read
      // and a SECOND Dedication buy would lift it again.
      // THROUGH THE SHARED CORE, never a copy here: `creationCharacteristicsStamp` is the write half of
      // the field `computeDerived` reads, it returns null for a record that already carries its creation
      // values (so the frozen number can never drift upward) and null when there is nothing to copy, and
      // it is the function `test/char_resolve_derived.cjs` calls — so this stamp is guardable at all.
      // The sheet handed in is the PRE-patch record; `patch.characteristics` below is built separately.
      // One patch, for the reason the B-181 comment directly below states: a raised characteristic with
      // no recorded creation value is unrepresentable.
      var _creationStamp = CharResolve.creationCharacteristicsStamp(sheet);
      if (_creationStamp) patch.characteristicsAtCreation = _creationStamp;
      chars[charChoice] = Math.min(CharResolve.CHARACTERISTIC_CAP, (Number(chars[charChoice]) || 0) + 1);
      patch.characteristics = chars;
    }
    // F-100 M7 R-M7K-3 — THE PICK MAP. A talent whose pick is a CHARACTERISTIC files it positionally, the SAME address
    // as talentSkillChoices (R-TCP-1), in this same patch: `talentCharacteristicChoices[spec]["r-c"] = <characteristic>`.
    //   · Dedication — a RECORD only, from the M7 build on: the +1 stays in `characteristics` above, and the record lets a
    //     later removal know which characteristic to lower (a Dedication bought before the build has none, R-M7K-14);
    //   · a `skill_characteristic` swap whose `to` is `chosen` (Renegade Form) — READ at render by
    //     CharResolve.skillCharacteristic; the picker that asks is step 2's (R-M7K-37's one picker).
    var _talRec = data && data.talents && data.talents[talentKey];
    var _chosenSwap = !!(_talRec && (_talRec.effects || []).some(function(e) { return e && e.op === 'skill_characteristic' && e.to === 'chosen'; }));
    if ((talentKey === 'dedication' || _chosenSwap) && typeof charChoice === 'string' && CharResolve.CHAR_ABBR[charChoice]) {
      var tcc = sheet.talentCharacteristicChoices ? JSON.parse(JSON.stringify(sheet.talentCharacteristicChoices)) : {};
      (tcc[specKey] || (tcc[specKey] = {}))[pos] = charChoice;
      patch.talentCharacteristicChoices = tcc;
    }
    // B-181: a choice-talent (Well Rounded / Well Read) files its player-chosen career skills into the
    // SAME patch as the purchase — the owned position, the XP spend and the choice land atomically, so
    // an owned-but-unpicked state is unrepresentable (R-TCPK-1). `charChoice.skills` is the picked
    // canonical-id list; keyed by spec id then position, the shared save shape talentChoiceSkillsAll
    // reads back at render. Disjoint from Dedication above (a talent is one or the other, never both).
    var _choiceGrants = (window.CharResolve && CharResolve.talentChoiceGrantsOf(data && data.talents && data.talents[talentKey])) || [];
    if (_choiceGrants.length && charChoice && charChoice.skills) {
      var tsc = sheet.talentSkillChoices ? JSON.parse(JSON.stringify(sheet.talentSkillChoices)) : {};
      (tsc[specKey] || (tsc[specKey] = {}))[pos] = charChoice.skills.slice();
      patch.talentSkillChoices = tsc;
    }
    if (onSheetChange) onSheetChange(patch);
  }

  // B-181 migration (ruling talent-choice-legacy-migration §8): file the skills for an ALREADY-owned
  // choice-talent — CHOOSE-IN-PLACE. No XP (nothing is bought), no position change (already owned), no
  // refund. Idempotent on the positional entry (R-TCLM-4): writes exactly talentSkillChoices[spec][pos]
  // and touches no other save field, so the needs-its-skills prompt never returns and nothing else moves.
  // F-100 M7 R-M7G-24 — choose in place: an owned `to: chosen` swap bought with no pick (before M7, or in the creator,
  // B-1055) files its pick at the same address buyTalent writes, with no XP — B-181's choose-in-place, for a characteristic.
  function chooseTalentCharacteristic(specKey, pos, characteristic) {
    if (!CharResolve.CHAR_ABBR[characteristic]) return;
    var tcc = sheet.talentCharacteristicChoices ? JSON.parse(JSON.stringify(sheet.talentCharacteristicChoices)) : {};
    (tcc[specKey] || (tcc[specKey] = {}))[pos] = characteristic;
    // B-1055 (ruling release-r7-bug-burndown ADDENDUM 86): placing an OWED Dedication (bought in the creator, no pick, no
    // point) applies its +1 here, with no XP. Only `characteristics` moves — `characteristicsAtCreation` is NOT re-stamped
    // (CALL 2 (i)), so thresholds and the B-778 walk read as before. The cap is the talent's own 6 (Q1, ADDENDUM 85:
    // FaD p.102 Table 2-2, "only standard limits apply"); the picker disables a characteristic already at it.
    var owedHere = CharResolve.dedicationOwedCells(sheet, data && data.specializations).indexOf(specKey + ' ' + pos) !== -1;
    if (owedHere) {
      var chars = Object.assign({}, sheet.characteristics || {});
      if ((Number(chars[characteristic]) || 0) >= CharResolve.CHARACTERISTIC_CAP) return;
      chars[characteristic] = (Number(chars[characteristic]) || 0) + 1;
      if (onSheetChange) onSheetChange({ talentCharacteristicChoices: tcc, characteristics: chars });
      return;
    }
    if (onSheetChange) onSheetChange({ talentCharacteristicChoices: tcc });
  }
  function chooseTalentSkills(specKey, pos, skills) {
    var tsc = sheet.talentSkillChoices ? JSON.parse(JSON.stringify(sheet.talentSkillChoices)) : {};
    (tsc[specKey] || (tsc[specKey] = {}))[pos] = (skills || []).slice();
    if (onSheetChange) onSheetChange({ talentSkillChoices: tsc });
  }

  // ── R-CS4e / V2: buy a NEW specialization ────────────────────────────
  // Cost (eote p.93/p.284): 10 × total specs after purchase; +10 if OUTSIDE the character's career;
  // universal specs never take the surcharge. The new tree starts empty — non-ranked talents already
  // owned elsewhere are claimed FREE by tapping them on the tree (crossTreeFreeIds → cost 0).
  // (costForNewSpec moved to module scope — shared with the SPECIALIZE tab, bind B5.)

  function buyNewSpec(specId) {
    if (hold) return;   // B-855: re-projects the talents list through `data`
    var specRec = data && data.specializations ? data.specializations[specId] : null;
    if (!specRec) return;
    if (sheetSpecs.some(function(s) { return s.id === specId; })) return;  // already owned
    var career = data.careers ? data.careers[CharResolve.sheetCareerId(sheet, data.careers)] || null : null;   // B-659
    var cost = costForNewSpec(specRec, sheetSpecs.length, career);
    if (cannotAfford(cost)) return;
    // B-41: Knight/General need Force rating 2, Master 3 (RotS p.26, CotR p.28/29). Backstop behind
    // the journey's blocked CONFIRM, which carries the visible reason.
    if (!meetsForceRating(specRec, frResolved)) return;
    var specs = sheetSpecs.map(function(s) { return { id: s.id, owned: (s.owned || []).slice() }; });
    specs.push({ id: specId, owned: [] });
    var patch = {
      specializations: specs,
      // B-1651: the id list the OggDude export and the career powers read, in the same patch (one unit, U-XP)
      specializationIds: CharResolve.ownedSpecIds(sheet.specializationIds, specs),
      xp: Object.assign({}, xp, { remaining: availableXp - cost, spent: spentXp + cost }),
    };
    // Force-sensitive specs grant Force Rating 1 — never additive, never lowers it (eote p.276-286).
    if (Number(specRec.force_rating) >= 1) patch.forceRatingBase = Math.max(Number(sheet.forceRatingBase) || 0, 1);
    if (onSheetChange) onSheetChange(patch);
  }

  // B-1526 (release-r7-bug-burndown ADDENDUM 47): the tree's cells price through CharResolve's ONE upgrade price — the group
  // Mentor's upgrade discount (Disciples of Harmony pp.79–80); the cell's cost is what buyForceUpgrade charges.
  var fpUpgradeCost = function (power) { return function (k, c) { return CharResolve.sheetForcePowerUpgradeCost(sheet, data, power, k, c); }; };
  // R7 B-1536 (ADDENDUM 271): the open tree's local names + cells after a buy — the cell joins only while in step, the rule
  // buyForceUpgrade writes to the save, so the badges and the record agree.
  function fpOwnedAfterBuy(prev, abilityKey, cell) {
    var names = (prev.ownedUpgradeNames || []).slice();
    var own = Array.isArray(prev.ownedCells) ? prev.ownedCells.slice() : (names.length ? null : []);
    if (own && own.length === names.length && cell) own.push(cell); else own = null;
    names.push(abilityKey);
    return { ownedUpgradeNames: names, ownedCells: own };
  }
  function buyForceUpgrade(powerIndex, abilityKey, abilityName, cost, cell) {
    if (hold || cannotAfford(cost)) return;   // B-855
    // UXQP-23 (D-B2=b): native confirm dropped — the force-tree modal's open + cell-tap
    // already gate this deliberate buy.
    var newPowers = powers.slice();
    var p = Object.assign({}, newPowers[powerIndex]);
    var ups = Array.isArray(p.upgrades) ? p.upgrades.slice() : [];
    // Canonical id-ref entries persist the ability KEY only; legacy entries keep their
    // {name, key} object shape (they migrate to id-refs on next boot).
    // R7 B-1536 (ruling release-r7-bug-burndown ADDENDUM 269): the cell bought ('row-col', from the tree) joins `owned`, one
    // per `upgrades` element — only while `owned` is in step (a fresh list, or as long as `upgrades`); an entry already out
    // of step keeps tree-order pricing, as CharResolve.forcePowerOwnedProblems reads it.
    var own = Array.isArray(p.owned) ? p.owned.slice() : (ups.length ? null : []);
    if (CharResolve.isCanonicalPowerEntry(p)) {
      if (own && own.length === ups.length && cell) { own.push(cell); p.owned = own; }
      ups.push(abilityKey);
    }
    else ups.push({ name: abilityName, key: abilityKey, desc: '' });
    p.upgrades = ups;
    newPowers[powerIndex] = p;
    var newXp = Object.assign({}, xp, {
      remaining: availableXp - cost,
      spent: spentXp + cost,
    });
    if (onSheetChange) onSheetChange({ forcePowers: newPowers, xp: newXp });
  }

  // F-100 creator concept R-SCC-1 / ADDENDUM 11 (D-229 Q1 (a)): THE ONE SIGNATURE WRITER — the base cell ('0-0') starts the
  // entry, an upgrade cell ('r-c') appends to it; XP CHARGED as every purchase charges it, the cost the record's
  // (CharResolve.sigBuyPlan, which refuses V0–V7 in SIG_WORDS' words, an owned cell, an `inferred` cost and the key OFF).
  // Fail-closed on `hold` and `cannotAfford`, through the same onSheetChange patch every spine uses.
  function buySigAbility(id, tree, cell) {
    if (hold) return;   // B-855
    var plan = CharResolve.sigBuyPlan(sheet, data, id, tree, cell);
    if (!plan.ok || cannotAfford(plan.cost)) return;
    var newXp = Object.assign({}, xp, { remaining: availableXp - plan.cost, spent: spentXp + plan.cost });
    if (onSheetChange) onSheetChange({ signatureAbilities: plan.signatureAbilities, xp: newXp });
  }

  // B-32: buy a NEW basic force power in advancement (the creator-only path, now on the sheet too).
  // Appends the canonical id-ref entry (powersFromCreatorState's shape: {id, basic, upgrades:[]}) and
  // deducts XP (mirroring buyForceUpgrade) — the bought power then appears in the owned list above with
  // its upgrade tree. Cost/list/card come from the shared ForcePowerBuyList (no second RAW path).
  function buyBasicPower(p, cost) {
    if (hold || cannotAfford(cost)) return;   // B-855
    // B-41: the printed prerequisite (fad p.285). The affordance above is already blocked and says
    // why, so reaching here means a non-UI path — this is the backstop, not the user-facing refusal.
    if (!meetsForceRating(p, frResolved)) return;
    var newPowers = powers.concat([{ id: p.id, basic: true, upgrades: [] }]);
    var newXp = Object.assign({}, xp, { remaining: availableXp - cost, spent: spentXp + cost });
    if (onSheetChange) onSheetChange({ forcePowers: newPowers, xp: newXp });
  }

  // Tree-buy modal state
  var _treeBuy = useState(null);
  var treeBuy = _treeBuy[0], setTreeBuy = _treeBuy[1];


  // Build skill list — show all FFG skills, mark trained ranks, expose BUY for next rank.
  // Canonical vocab: skillNames (id -> label) + skillSheetKeys (id -> sheet short key).
  var SKILL_KEYS_DISPLAY = useMemo(function() {
    if (data && data.skillNames) {
      return Object.keys(data.skillNames).map(function(id) {
        return { key: data.skillSheetKeys[id] || id, name: data.skillNames[id] || id, char: '' };
      }).sort(function(a, b) { return a.name.localeCompare(b.name); });
    }
    // Fallback: derive from already-trained skills only
    return Object.keys(skills).map(function(k) { return { key: k, name: k, char: '' }; }).sort(function(a, b) { return a.name.localeCompare(b.name); });
  }, [data, skills]);

  // ── Central-source content blocks (clearance B2) — ONE definition each; the desktop growth-workshop
  //    grid AND the mobile stack compose the SAME blocks (no per-platform re-declaration = no BR/AG drift). ──




  // FORCE POWERS section — collapses entirely for a non-Force character (clearance B1); each owned power
  // row is click-to-inspect (DETAILS). B-32 basic-power ADD path preserved.
  // FORCE POWERS content split from its header so ONE body feeds both contexts: the mobile stack (with its
  // sectionHead) AND the desktop ADVANCEMENT's expandable FORCE section (header supplied by the section shell,
  // like the OVERVIEW cockpit's right column — Jason 2026-07-21).
  var forceHasContent = (forceRating >= 1 || powers.length > 0);

  // TALENTS — the specialization talent trees (DirectionalTree via the talent adapter + its connection rule;
  // buy spine unchanged, clearance B5). The "Pick New Specialization" CTA + owned-spec list live in the
  // SPECIALIZATIONS block (specsBlock) so the desktop workshop can column them separately.
  var specLess = sheetSpecs.length === 0;
  // R-B43-1: a spec-less sheet that still RECORDS a specialization or talents is a save whose specialization
  // our registry no longer recognises — the honest stub, not "you haven't picked one". ONE test and ONE copy
  // for both faces (the dark talentBlock and the paper growth room's stage).
  var specLegacy = specLess && (
    (sheet.specialization && String(sheet.specialization).length > 0) ||
    (Array.isArray(sheet.talents) && sheet.talents.length > 0));
  var SPEC_LEGACY_COPY = 'This character predates talent-tree tracking, so there’s no tree to buy from here. Talents bought at creation still show on the ABILITIES page. To start tracking new purchases, choose a specialization below — your character keeps everything it already has.';
  // Which spec's tree the desktop center shows (default = first owned; SPECIALIZATIONS clicks switch it).
  var currentSpecId = (activeSpecId && sheetSpecs.some(function(s) { return s.id === activeSpecId; }))
    ? activeSpecId : (sheetSpecs[0] ? sheetSpecs[0].id : null);
  // B-615: the tree's model and mount, HOISTED out of talentBlock's IIFE so the PAPER composition draws
  // the same tree — `skin: 'board'`, buy in the cell — through the same onBuy / onChooseSkills spines.
  // One tree, two arrangements (the desktop's DETAILS-routed inspect, the paper's cell).
  var _crossTreeFreeIds = [];
  if (data) ownedTalentIdsAll(sheetSpecs).forEach(function(tid) {
    var t = data.talents ? data.talents[tid] : null;
    if (t && t.ranked === false && _crossTreeFreeIds.indexOf(tid) === -1) _crossTreeFreeIds.push(tid);
  });
  var _makeTree = function(spec, paperOpts) {
    var rec = data.specializations ? data.specializations[spec.id] : null;
    if (!rec || !rec.talent_tree) return null;
    // ONE tree component (ruling tree-standardization-brief): the talent adapter builds the normalized
    // model; DirectionalTree renders it (Force SHEET_PANEL skin, TreeFitBox fit). Dedication stays in
    // this onBuy (coverage pin C); desktop closes the DETAILS pane after a normal buy.
    var model = talentTreeToModel({
      skin: paperOpts ? 'board' : undefined, interactive: paperOpts ? true : undefined,
      specData: rec, talentDb: data.talents, character: { talents: ownedTalentsMap(spec) },
      goneRemoved: function(id) { return window.CharResolve.removedRef(sheet, { field: 'talents', id: id }, window.SWRPGUserData, window.SWRPGMods && window.SWRPGMods.parseUserStamp); },
      crossFreeIds: _crossTreeFreeIds, allowRefund: false, readOnly: false, availableXp: availableXp, heldReason: holdText,
      onBuy: function(talentKey, specKey, row, col, cost) {
        var trec = data.talents && data.talents[talentKey];
        var grants = (window.CharResolve && CharResolve.talentChoiceGrantsOf(trec)) || [];
        if (talentKey === 'dedication' || m7ChosenSwapOf(trec)) {
          // F-100 M7 W4 (R-M7G-22, -23): Dedication and a chosen swap ask first; the choice buys AND files the pick in one
          // patch (buyTalent's charChoice). At 390 this opens as the tree's paper sheet closes on the buy (its afterAction).
          var cellEl = document.querySelector('[data-testid="growth-talent-tree"] [data-testid="talent-cell-' + row + '-' + col + '"]');
          setCharPick({ mode: 'buy', talentKey: talentKey, specKey: specKey, row: row, col: col, cost: cost,
            anchor: cellEl ? (cellEl.querySelector('button.hp-tab, .hp-head[role=button]') || cellEl) : null });
          if (advDesktop) setSel(null);
        }
        else if (grants.length) {
          // B-181: a choice-talent (Well Rounded / Well Read) — raise the picker; CONFIRM buys AND
          // files the chosen skills in one atomic patch (buyTalent's charChoice.skills path).
          setTalentPicker({ mode: 'buy', talentKey: talentKey, talentName: (trec && trec.name) || talentKey, specKey: specKey, row: row, col: col, cost: cost, grants: grants, initialPicks: [] });
          if (advDesktop) setSel(null);
        }
        else { buyTalent(talentKey, specKey, row, col, cost); if (advDesktop) setSel(null); }
      },
      onRefund: function() {},
      // B-181: the sheet's saved choices feed the receipt + the needs-its-skills detection; the
      // choose handler raises the picker in CHOOSE-IN-PLACE mode on an already-owned talent.
      talentSkillChoices: sheet.talentSkillChoices, talentChoices: sheet.talentChoices, skillNames: data.skillNames,
      characteristicChoices: sheet.talentCharacteristicChoices || {},   // F-100 M7 W4: the receipt, and (passed at all) choose in place
      characteristicOwed: CharResolve.dedicationOwedCells(sheet, data.specializations),   // B-1055: an owed Dedication places in place
      onChooseSkills: function(talentKey, specKey, row, col) {
        var trec = data.talents && data.talents[talentKey];
        var grants = (window.CharResolve && CharResolve.talentChoiceGrantsOf(trec)) || [];
        var pos = row + '-' + col;
        var existing = CharResolve.pickOf(sheet, specKey, pos, 'career_skills') || [];   // R-M8G-4: the one reader
        setTalentPicker({ mode: 'choose', talentKey: talentKey, talentName: (trec && trec.name) || talentKey, specKey: specKey, row: row, col: col, grants: grants, initialPicks: existing });
        if (advDesktop) setSel(null);
      },
    });
    return h(DirectionalTree, {
      model: model, accent: accent, headPress: paperOpts ? !!paperOpts.headPress : undefined, headPressWhereNoBadge: paperOpts ? !!paperOpts.headPressWhereNoBadge : undefined,
      // F-100 M7 W4 (R-M7G-23): at 1440 an owned cell with a characteristic pick opens W4's "chosen" panel beside it
      // and (R-M7G-24, both bands) an owned swap with NO pick opens the one picker to choose in place, no XP
      onHeadPick: paperOpts ? function(entry, el) {
        // F-100 M8 step 3 (frame R6): an owned cell carrying a per-rank skill or item pick opens the same picker, to choose in place
        if (entry.m8Pick) { setCharPick({ mode: 'm8', pickId: entry.m8Pick.id, kind: entry.m8Pick.kind, optionId: entry.m8Pick.optionId || null, talentKey: entry.talentKey, specKey: entry.specKey,
          row: entry.row, col: entry.col, cost: entry.buy ? entry.buy.cost : entry.reachCost, anchor: el }); return; }
        setCharPick({ mode: entry.charChoiceNeeded ? 'place' : 'chosen', talentKey: entry.talentKey, specKey: entry.specKey, row: entry.row, col: entry.col,
          cost: entry.buy ? entry.buy.cost : entry.reachCost, receipt: entry.charChoiceReceipt, anchor: el });
      } : undefined,
      // Desktop: route to the DETAILS pane (prose + buy there); it relies on the buyTalent spine's XP
      // guard, so needxp→buyable there. Mobile leaves onInspect undefined → the unified sheet shows the
      // full state set (incl. NOT ENOUGH XP), matching the retired per-tree sheet.
      onInspect: (!paperOpts && advDesktop) ? function(entry) {
        setSel({ key: 'talent:' + entry.cellId, kind: 'TALENT', name: entry.name, meta: entry.meta,
          desc: entry.desc ? cleanMarkup(entry.desc) : '', buyingDoes: entry.buyingDoes, sourceCite: entry.sourceCite,
          // B-181: carry the choice-talent state into the desktop DETAILS pane so its buy label,
          // needs-its-skills prompt and receipt match the mobile bottom sheet (one behaviour, two containers).
          isChoiceTalent: entry.isChoiceTalent, choiceCount: entry.choiceCount, needsChoice: entry.needsChoice, choiceReceipt: entry.choiceReceipt, choose: entry.choose,
          buy: !entry.buy ? null
            : entry.buy.state === 'held' ? { state: 'locked', reason: entry.buy.reason }   // B-855: DETAILS' own disabled state, the reason in its slot
            : { state: entry.buy.state === 'needxp' ? 'buyable' : entry.buy.state, cost: entry.buy.cost, label: entry.buy.label, doBuy: entry.buy.doBuy } });
      } : undefined,
    });
  };





  // B-181: the career-skill set (canonical ids) for the talent-choice picker — every skill already
  // career for THIS character (career + owned specs' career skills + their chosen skills + other
  // choice-talents), so the picker shows them for context but disables them (making an already-career
  // skill a career skill grants nothing). Built only while the picker is open. Same shared derivers the
  // career-status union uses (specChoiceSkillsAll / talentChoiceSkillsAll), never a re-implementation.
  var talentPickerCareerIds = null;
  if (talentPicker && data) {
    talentPickerCareerIds = {};
    // Resolve career the SAME way the careerSkills memo does (R-CSK1): canonical id first, collision-
    // aware name fallback for legacy saves that carry `career` as a name — else the picker would fail
    // to mark the real career skills on pregens/legacy shapes and offer an already-career skill.
    // F-100 M7 C2 (R-M7K-7): the one answer, asked over the specs this surface shows (sheetSpecs).
    talentPickerCareerIds = CharResolve.careerSkillIds(CharResolve.careerSkillStatus(sheet, data,
      { specIds: sheetSpecs.map(function(sp) { return sp && sp.id; }).filter(Boolean) }));
  }
  // Shared modals (both platforms): the Dedication characteristic picker + the force-power tree-buy modal.
  var modalsBlock = h(React.Fragment, null,
    charPick && data ? h(M7CharPickSheet, { pick: charPick, charbuild: data, characteristics: sheet.characteristics, sheet: sheet, onClose: closeCharPick,
      onStep: function(step) { setCharPick(Object.assign({}, charPick, { step: step })); },
      onChoose: function(c) {
        var cp = charPick; setCharPick(null);
        if (cp.mode === 'm8') {   // F-100 M8 step 3: the one writer (CharResolve.pickWrite) — a skill to talentChoices, an item as its mark
          var pos8 = cp.row + '-' + cp.col, optStep = !!cp.optionId && (cp.step || (CharResolve.pickOf(sheet, cp.specKey, pos8, cp.pickId, { talent: cp.talentKey }) ? 'option' : 'item')) === 'option';
          var was = cp.optionId && !optStep ? CharResolve.pickOf(sheet, cp.specKey, pos8, cp.pickId, { talent: cp.talentKey }) : null;
          var w8 = CharResolve.pickWrite(sheet, data, cp.talentKey, cp.specKey, pos8, optStep ? cp.optionId : cp.pickId, c);
          if (w8 && !w8.refused && onSheetChange) onSheetChange(w8);
          // Jury Rigged (frames J1 → J2): the item chosen, the same sheet asks the improvement; a changed item names the old one (J3)
          if (cp.optionId && !optStep && w8 && !w8.refused) {
            var wasShown = was && JSON.stringify(was) !== JSON.stringify(c) ? (function () { var e = resolveSheetEquipment(sheet.equipment || {}, sheet) || {};
              var r = was.section === 'armor' ? e.armor : (e[was.section] || [])[was.index]; return r && r.name; })() : null;
            setCharPick(Object.assign({}, cp, { step: 'option', prevItem: wasShown || null }));
          }
          return;
        }
        if (cp.mode === 'place') chooseTalentCharacteristic(cp.specKey, cp.row + '-' + cp.col, c);
        else buyTalent(cp.talentKey, cp.specKey, cp.row, cp.col, cp.cost, c);
      } }) : null,
    talentPicker && data ? h(TalentSkillChoicePicker, {
      talentName: talentPicker.talentName, grants: talentPicker.grants, charbuild: data,
      careerStatusIds: talentPickerCareerIds, cost: talentPicker.cost, mode: talentPicker.mode,
      initialPicks: talentPicker.initialPicks, accent: accent,
      onConfirm: function(skills) {
        if (talentPicker.mode === 'buy') { buyTalent(talentPicker.talentKey, talentPicker.specKey, talentPicker.row, talentPicker.col, talentPicker.cost, { skills: skills }); }
        else { chooseTalentSkills(talentPicker.specKey, talentPicker.row + '-' + talentPicker.col, skills); }
        setTalentPicker(null);
      },
      onCancel: function() { setTalentPicker(null); },
    }) : null,
    treeBuy ? (function() {
      // Mobile Force-upgrade buy: a full-screen modal wrapping DirectionalTree (no onInspect → its own
      // unified bottom sheet handles per-cell teach+buy). Replaces ForcePowerTreeView's modal chrome.
      var power = resolveFpPower(treeBuy);
      // R1 (lap paper-npost-sync): the unresolved-power stub's style, in scope. It was the talentBlock IIFE's local,
      // out of reach here since 20d20911, so this branch threw `ReferenceError: stubStyle`. Same values as that local.
      var stubStyle = { padding: '14px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'rgba(255,255,255,0.6)' };
      return h('div', { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Force power tree: ' + (treeBuy.name || ''), style: { position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(2,1,10,0.94)', display: 'flex', flexDirection: 'column', padding: '16px' } },
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flex: '0 0 auto' } },
          h('div', null,
            h('div', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.2em', color: accent, opacity: 0.85, marginBottom: '2px' } }, 'FORCE POWER TREE'),
            h('span', { style: { fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: '#fff' } }, treeBuy.name)),
          h('button', { type: 'button', onClick: function() { setTreeBuy(null); }, 'aria-label': 'Close tree view', style: { background: 'none', border: '1px solid ' + accent + '44', borderRadius: '6px', color: 'var(--text-muted)', minHeight: '44px', padding: '0 14px', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.12em', cursor: 'pointer' } }, '× CLOSE')),
        power ? h('div', { style: { flex: '1 1 auto', minHeight: 0 } }, h(TreeFitBox, null, h(DirectionalTree, {
          model: forcePowerToModel({ power: power, ownedUpgradeNames: treeBuy.ownedUpgradeNames, ownedCells: treeBuy.ownedCells, upgradeCost: fpUpgradeCost(power),
            onBuy: function(abilityKey, abilityName, cost, cell) {
              buyForceUpgrade(treeBuy.powerIndex, abilityKey, abilityName, cost, cell);
              setTreeBuy(function(prev) { if (!prev) return prev; return Object.assign({}, prev, fpOwnedAfterBuy(prev, abilityKey, cell)); });
            } }),
          accent: accent,
        }))) : h('div', { style: stubStyle }, 'Force power data unavailable.'));
    })() : null
  );

  // The New-Spec SELECTOR on the center stage (ruling advancement-modular-center). Reuses NewSpecJourney's
  // locked craft verbatim (center shell). onBuy → buy + show the new tree; landing CLOSE / confirm → back
  // to tree mode (the arrival beat: the just-added tree materializes where you were browsing). RAW spine
  // (costForNewSpec + buyNewSpec patch) byte-identical.
  // B-657: `paper` rides the SAME mount — the paper WALK and the dark journey are one component with
  // one set of props, one source gate and one cost function; the paper stage below renders this very
  // block, so there is no second wiring to drift.
  var selectorBlock = data ? h(NewSpecJourney, {
    center: true, tab: true, data: data, sheet: sheet, sheetSpecs: sheetSpecs, accent: accent,
    availableXp: availableXp, career: career, skillNames: data.skillNames || {},
    costFor: function(id) { return costForNewSpec(data.specializations[id], sheetSpecs.length, career); },
    onBuy: function(specId) { buyNewSpec(specId); setActiveSpecId(specId); setCenterMode('tree'); },
    onClose: function() { setCenterMode('tree'); },
  }) : null;


  // ── DESKTOP growth workshop (clearance §4.2): XP hero strip over a 3-column no-scroll grid —
  //    SKILLS+FORCE (left) · TALENT trees (center) · DETAILS + SPECIALIZATIONS (right). Zones scroll
  //    internally (.lo-zone/.lo-scroll), the page never scrolls (§4.6). ──
  // ═══ B-615 · THE GROWTH ROOM, ON PAPER (ruling sheet-paper-concept R-PSC-6; lab dffcd72b growth_room) ═══
  // ADVANCEMENT keeps its page whole — and it is a board: the spine of what puts a tree on the stage
  // (SKILLS as buy rows, two-up at wide; SPECIALIZATIONS; FORCE POWERS — one open), and the stage, which
  // the tree fills: the specialization's talent tree with the CREATOR's own skin, buying in the cell
  // (`tree-cell-buy-in-the-cell-gate`), or a Force power's upgrade tree with the purse, or the new-
  // specialization journey. No DETAILS pane: the cell reads and buys, and a press on a cell opens the
  // reading sheet the creator's phone uses. The same spines as the tab — buySkillRank with its two-tap
  // arm, buyTalent, buyForceUpgrade, buyBasicPower, buyNewSpec — and the same modals.
  var boxesOf = function (rank) { var out = []; for (var bi = 0; bi < 5; bi++) out.push(<span className="hp-box" key={bi} data-on={bi < rank ? '1' : '0'} />); return out; };
  var skillsPaper = SKILL_KEYS_DISPLAY.length === 0 ? <div className="hp-none">Loading skill data…</div> : (
    <div className="sb-buy2" data-testid="growth-skills">
      {SKILL_KEYS_DISPLAY.map(function(s) {
        var rank = skills[s.key] || skills[s.name] || 0;
        var isCareer = careerSkills && careerSkills.indexOf(s.key) !== -1;
        var atCap = rank >= 5;
        var cost = nextSkillRankCost(s.key, rank);
        var canAfford = cost != null && availableXp >= cost;
        var skillArmed = armedBuyKey === 'skill:' + s.key;
        var doBuy = function(e) { e.stopPropagation(); if (skillArmed) { buySkillRank(s.key, rank, cost); setArmedBuyKey(null); } else setArmedBuyKey('skill:' + s.key); };
        var buyAria = skillArmed ? ('Tap again to confirm: buy ' + s.name + ' rank ' + (rank + 1) + ' for ' + cost + ' XP') : ('Buy ' + s.name + ' rank ' + (rank + 1) + ' for ' + cost + ' XP');
        // B-855 · held (frame skillHeld): no price, no career line — the rank, the boxes, the refusal
        // word, and the buy disabled. The reason line above says why (R-B854-1).
        if (hold) return (
          <div className="sb-buy" key={s.key} data-testid={'adv-skill-' + s.key} data-refused="1">
            <div className="sb-sk-n">
              <div className="sb-sk-name">{s.name}</div>
              <div className="sb-sk-char">{'Rank ' + rank}</div>
            </div>
            <div className="sb-sk-boxes">{boxesOf(rank)}</div>
            <span className="sb-refuse">Held</span>
            <button type="button" className="sb-sk-buy" data-testid="skill-rank-buy" data-skill-key={s.key} data-armed="false" data-held="1" disabled aria-label={'Buy ' + s.name + ' rank ' + (rank + 1) + ' — held, see the note above'}>+</button>
          </div>);
        return (
          <div className="sb-buy" key={s.key} data-testid={'adv-skill-' + s.key} data-armed={skillArmed ? '1' : undefined} data-refused={atCap ? '1' : undefined}>
            <div className="sb-sk-n">
              <div className="sb-sk-name">{s.name}</div>
              <div className="sb-sk-char">{skillArmed ? 'Rank ' + rank + ' → ' + (rank + 1) : 'Rank ' + rank + (atCap ? ' (max)' : '') + (isCareer ? ' · Career' : ' · Non-career')}
                {!skillArmed && isCareer && m7CareerSuffix(careerStatus[s.key], data)
                  ? <span className="m7-because" data-testid="adv-skill-because" style={{ color: 'var(--doc-ink-muted)', textTransform: 'none', letterSpacing: '.02em' }}>{m7CareerSuffix(careerStatus[s.key], data)}</span> : null}</div>
            </div>
            {skillArmed
              ? <React.Fragment><span className="sb-sk-cost">{cost + ' XP'}</span>
                  <button type="button" className="sb-confirm" data-testid="skill-rank-buy" data-skill-key={s.key} data-armed="true" onClick={doBuy} aria-label={buyAria}>Tap again to confirm</button></React.Fragment>
              : <React.Fragment><div className="sb-sk-boxes">{boxesOf(rank)}</div>
                  {atCap ? <span className="sb-refuse">Capped</span> : <span className="sb-sk-cost">{cost + ' XP'}</span>}
                  <button type="button" className="sb-sk-buy" data-testid="skill-rank-buy" data-skill-key={s.key} data-armed="false" disabled={!canAfford || atCap} onClick={doBuy} aria-label={buyAria}>+</button></React.Fragment>}
          </div>);
      })}
    </div>);
  var specsPaper = (
    <React.Fragment>
      {sheetSpecs.map(function(spec, si) {
        var rec = data && data.specializations ? data.specializations[spec.id] : null;
        var unresolved = !rec; var nm = unresolved ? 'Specialization' : (rec.name || spec.id);
        var isActive = si === 0, isShown = spec.id === currentSpecId && centerMode === 'tree';
        return (
          <div className="sb-e" key={spec.id} data-testid={'adv-spec-' + spec.id} data-refused={unresolved ? '1' : undefined} role="button" tabIndex={0}
            onClick={function() { if (!unresolved) { setActiveSpecId(spec.id); setCenterMode('tree'); } }} onKeyDown={function(e) { if ((e.key === 'Enter' || e.key === ' ') && !unresolved) { e.preventDefault(); setActiveSpecId(spec.id); setCenterMode('tree'); } }}>
            <div className="sb-e-h"><span className="sb-e-n">{nm}</span>
              {unresolved ? <span className="sb-refuse">Unavailable</span> : <span className="sb-e-m">{(isActive ? 'Active' : 'Owned') + (isShown ? ' · on the stage' : '')}</span>}</div>
          </div>);
      })}
      {/* B-855 · the new-spec buy re-projects, so the door is held (frame specsBody; R-B854-1). The
          false "did not load … Reload" paragraph above is deleted on both skins (R-B854-4): the
          reason line carries the true words now. */}
      {hold
        ? <div className="sb-e"><div className="sb-hold-door"><button type="button" className="hp-commit" data-testid="growth-pick-spec" data-held="1" aria-disabled="true"
            onClick={function() { var el = document.querySelector('[data-testid="growth-hold"]'); if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); } }}><span>{specLess ? 'Choose your first specialization' : 'Pick new specialization'}</span></button><span className="sb-refuse">Held</span></div></div>
        : <div className="sb-e"><button type="button" className="hp-commit" data-testid="growth-pick-spec" onClick={function() { setCenterMode('selector'); }}>{specLess ? 'Choose your first specialization' : 'Pick new specialization'}</button></div>}
    </React.Fragment>);
  var forcePaper = forceHasContent ? (
    <React.Fragment>
      {powersDisplay.length ? powersDisplay.map(function(fp, i) {
        var fpName = fp.name || 'Unknown Power'; var fpUpgrades = fp.upgrades || [];
        var ownedNames = (fp.upgradeKeys && fp.upgradeKeys.length) ? fp.upgradeKeys : (fp.upgradeNames || []);
        var on = centerMode === 'fptree' && fpSel && fpSel.powerIndex === i;
        // R7 B-1536 (ADDENDUM 271): the saved entry's bought cells ride beside the names, so the tree badges each at its cell
        var ownedCells = (powers[i] && Array.isArray(powers[i].owned)) ? powers[i].owned.slice() : null;
        var pick = function() { setFpSel({ name: fpName, powerId: fp.id || null, ownedUpgradeNames: ownedNames, ownedCells: ownedCells, powerIndex: i }); setCenterMode('fptree'); };
        return (
          <div className="sb-pk-r" key={i} role="button" tabIndex={0} data-selected={on ? 'true' : 'false'} data-testid={'growth-power-' + i}
            onClick={pick} onKeyDown={function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}>
            <span className="sb-pk-c">{'›'}</span><span className="sb-pk-n">{fpName}</span>
            <span className="sb-pk-t">{fpUpgrades.length === 0 ? 'Basic power only' : fpUpgrades.length + ' upgrade' + (fpUpgrades.length === 1 ? '' : 's') + ' owned'}</span>
          </div>);
      }) : <div className="hp-none">No powers yet.</div>}
      {forceRating >= 1 ? <React.Fragment>
        <div className="sb-grp" style={{ marginTop: 10 }}>Learn a power</div>
        <div data-testid="growth-power-buy">{h(ForcePowerBuyList, {
  powers: (data && data.forcePowers) ? Object.values(data.forcePowers)
    : ((window._canonicalCharbuild && window._canonicalCharbuild.forcePowers) ? Object.values(window._canonicalCharbuild.forcePowers) : []),
  isOwned: function(p) { return powers.some(function(fp) { return fp.id === p.id; }); },
  xpRemaining: availableXp,
  accent: accent,
  gameLine: sheet.gameLine,
  onBuy: buyBasicPower,
  priceCtx: CharResolve.careerPowerPriceCtx(sheet, data),   // F-100 M12 Career Powers (the stamp view): null with the key OFF
  forceRating: frResolved,   // B-41
  held: !!hold,              // B-855
  hideOwned: true,
  // Desktop: addable powers are compact rows that teach + buy in DETAILS (Jason 2026-07-21). Mobile
  // keeps the full inline cards (no DETAILS pane in the stack). The buy spine (buyBasicPower) is unchanged.
  onInspect: advDesktop ? function(a) {
    setSel({ key: 'basicfp:' + (a.power.id || a.power.name), kind: 'FORCE POWER', name: a.power.name,
      meta: a.owned ? 'OWNED' : (a.cost != null ? a.cost + ' XP TO LEARN' : 'COST ?'),
      desc: a.desc,
      // B-41: an unmet Force-rating prerequisite locks the buy WITH its reason — the DETAILS
      // pane is where a growable thing's prose and its purchase share one surface, so the
      // refusal has to be legible right here rather than as a disabled control elsewhere.
      buy: a.owned ? { state: 'owned' }
        : hold ? { state: 'locked', reason: holdText }   // B-855
        : (!a.frMet ? { state: 'locked', reason: 'Requires Force rating ' + a.frNeed + ' — you have ' + a.frHave + '. Raise it with the Force Rating talent in a specialization tree.' }
        : (a.cost != null ? { state: 'buyable', cost: a.cost, label: 'LEARN POWER', doBuy: function() { a.doBuy(); setSel(null); } } : { state: 'locked' })) });
  } : undefined,
  paper: true, onInspect: undefined })}</div>   // B-625: on paper the row buys; no DETAILS pane to inspect into
      </React.Fragment> : null}
    </React.Fragment>) : null;
  // ── F-100 R-SCC-1 (ADDENDUM 11; ADDENDUM 18 Q1 (a) i): THE SIGNATURE UNIT — only with M12's key ON (its activation reader
  // is dark otherwise). The owned abilities in the Force row's idiom; the door opens the walk (SigAbilityJourney); a row
  // puts the ability's tree on the stage (`sigtree`), its boxes bought through buySigAbility.
  var sigOn = !!(window.SWRPGEngineRules && window.SWRPGEngineRules.on && window.SWRPGEngineRules.on());
  var sigReg = (data && data.signatureAbilities) || {};
  var sigEntries = sigOn && Array.isArray(sheet.signatureAbilities) ? sheet.signatureAbilities.filter(function(e) { return e && e.id; }) : [];
  var sigPaper = sigOn ? (
    <React.Fragment>
      {sigEntries.length ? sigEntries.map(function(e, i) {
        var rec = sigReg[e.id], nm = (rec && rec.name) || e.id, ups = (e.owned || []).filter(function(p) { return String(p) !== '0-0'; }).length;
        var on = centerMode === 'sigtree' && sigSel && sigSel.id === e.id;
        var pick = function() { if (!rec) return; setSigSel({ id: e.id, tree: e.tree }); setCenterMode('sigtree'); };
        return (
          <div className="sb-pk-r" key={e.id + '-' + i} role="button" tabIndex={0} data-selected={on ? 'true' : 'false'} data-testid={'growth-sig-' + e.id}
            onClick={pick} onKeyDown={function(ev) { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pick(); } }}>
            <span className="sb-pk-c">{'›'}</span><span className="sb-pk-n">{nm}</span>
            <span className="sb-pk-t">{ups === 0 ? 'Base ability only' : ups + ' upgrade' + (ups === 1 ? '' : 's') + ' owned'}</span>
          </div>);
      }) : <div className="hp-none">No signature abilities yet.</div>}
      {hold
        ? <div className="sb-e"><div className="sb-hold-door"><button type="button" className="hp-commit" data-testid="growth-pick-sig" data-held="1" aria-disabled="true"
            onClick={function() { var el = document.querySelector('[data-testid="growth-hold"]'); if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); } }}><span>Pick a signature ability</span></button><span className="sb-refuse">Held</span></div></div>
        : <div className="sb-e"><button type="button" className="hp-commit" data-testid="growth-pick-sig" onClick={function() { setCenterMode('sigwalk'); }}>Pick a signature ability</button></div>}
    </React.Fragment>) : null;
  var stagePaper, stageHead;
  if (centerMode === 'sigwalk' && sigOn) {
    stageHead = ['Signature ability', 'Journey'];
    stagePaper = data ? h(SigAbilityJourney, { data: data, sheet: sheet, sheetSpecs: sheetSpecs, career: career, accent: accent, availableXp: availableXp,
      onBuy: function(id, tree) { buySigAbility(id, tree, '0-0'); },
      onClose: function(id, tree) { if (id) { setSigSel({ id: id, tree: tree }); setGrowthUnit('sig'); setCenterMode('sigtree'); } else setCenterMode('tree'); } })
      : <div className="hp-none">Loading signature abilities…</div>;
  } else if (centerMode === 'sigtree' && sigOn && sigSel && sigReg[sigSel.id]) {
    var sigRec = sigReg[sigSel.id];
    var sigEntry = sigEntries.filter(function(e) { return e.id === sigSel.id; })[0] || { owned: [] };
    var sigOwned = (sigEntry.owned || []).map(String);
    stageHead = [sigRec.name || sigSel.id, 'Signature ability'];
    stagePaper = (
      <div className="hp-treescroll" data-testid="growth-sig-tree">
        <DirectionalTree accent={accent} headPress={!advDesktop} headPressWhereNoBadge model={sigTreeModel(sigRec, { mode: 'stage',
          cellState: function(pos) {
            if (sigOwned.indexOf(pos) !== -1) return { state: 'owned' };
            if (hold) return { state: 'locked', reason: holdText };   // B-855: nothing live-looking while held
            var plan = CharResolve.sigBuyPlan(sheet, data, sigSel.id, sigSel.tree, pos);
            if (plan.held) return { state: 'held', reason: plan.held.words };
            if (!plan.ok) return { state: 'locked', reason: plan.refusal && plan.refusal.words };
            return { state: plan.cost > availableXp ? 'needxp' : 'buyable' };
          },
          onBuy: function(pos) { buySigAbility(sigSel.id, sigSel.tree, pos); } })} />
      </div>);
  } else if (growthUnit === 'contacts' && centerMode !== 'selector') {
    // R6 S3 (S3-growth): CONTACTS open puts the networks on the stage, as the frame draws it — ROLL included (B-1662):
    // the host's `openHubGiven`, the same contract ALLIES' card uses, so the stage's card opens the hub on the network's check
    stageHead = ['Contacts', ''];
    stagePaper = <ContactStage sheet={sheet} openHubGiven={openHubGiven} />;
  } else if (centerMode === 'reward' && rwSel) {
    // R6 S2 (Q1 (a)): the pressed REWARDS type's tiers — the registry's name and cite on the head
    var rwType = rewardTypeOf(rwReg, rwSel);
    stageHead = [rwType ? <React.Fragment>{rwType.name}<RewardMaxLine type={rwType} sheet={sheet} /></React.Fragment> : '', rwType ? rwType.cite : ''];
    stagePaper = <RewardStage reg={rwReg} sel={rwSel} />;
  } else if (centerMode === 'selector') {
    // B-657: THE WALK, not an island. `selectorBlock` is the same mount the dark ADVANCEMENT uses and
    // it carries `paper` now, so the component draws the frame's own markup whole — no `.sb-legacy`
    // wrapper, no `data-sheet-interim`, no dark ground inside the paper room.
    stageHead = ['New specialization', 'Journey'];
    stagePaper = selectorBlock || <div className="hp-none">Loading specialization data…</div>;
  } else if (centerMode === 'fptree' && fpSel) {
    var fpPower = resolveFpPower(fpSel);
    stageHead = [fpSel.name, 'Force power'];
    var appendOwnedP = function(abilityKey, cell) { setFpSel(function(prev) { if (!prev) return prev; return Object.assign({}, prev, fpOwnedAfterBuy(prev, abilityKey, cell)); }); };
    stagePaper = fpPower ? (
      <div className="hp-treescroll" data-testid="growth-force-tree">
        {/* B-855: readOnly while held — the tree draws no live-looking buy (R-B854-1). */}
        <DirectionalTree accent={accent} headPress={!advDesktop} headPressWhereNoBadge model={forcePowerToModel({ power: fpPower, ownedUpgradeNames: fpSel.ownedUpgradeNames, upgradeCost: fpUpgradeCost(fpPower), availableXp: availableXp, readOnly: !!hold, skin: 'board', interactive: true,
          ownedCells: fpSel.ownedCells,
          onBuy: function(abilityKey, abilityName, cost, cell) { buyForceUpgrade(fpSel.powerIndex, abilityKey, abilityName, cost, cell); appendOwnedP(abilityKey, cell); } })} />
      </div>) : <div className="hp-none">Force power data unavailable.</div>;
  } else {
    var curSpec = null;
    for (var ci = 0; ci < sheetSpecs.length; ci++) { if (sheetSpecs[ci].id === currentSpecId) { curSpec = sheetSpecs[ci]; break; } }
    var recCurP = curSpec && data && data.specializations ? data.specializations[curSpec.id] : null;
    stageHead = [recCurP ? (recCurP.name || '') : (specLess && !specLegacy ? 'No specialization yet' : 'Specialization'), 'Talent tree'];
    stagePaper = !data ? <div className="hp-none">Loading talent data…</div>
      // B-855 R-B855-1: the specless stage's door into the walk is held like the spine's (the drawn
      // .sb-hold-door + chip); a press points at the reason line above the board. The reason line
      // itself renders for every held growth room, this composition included.
      : specLess ? <div className="sb-e"><p className="sb-e-t" data-spec-legacy={specLegacy ? '1' : undefined}>{specLegacy ? SPEC_LEGACY_COPY : 'First specialization from your career is free; others cost XP.'}</p>
          {hold
            ? <div className="sb-hold-door"><button type="button" className="hp-commit" data-testid="growth-stage-first-spec" data-held="1" aria-disabled="true"
                onClick={function() { var el = document.querySelector('[data-testid="growth-hold"]'); if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); } }}><span>Choose your first specialization</span></button><span className="sb-refuse">Held</span></div>
            : <button type="button" className="hp-commit" data-testid="growth-stage-first-spec" onClick={function() { setCenterMode('selector'); }}>Choose your first specialization</button>}</div>
      : curSpec ? <div className="hp-treescroll" data-testid="growth-talent-tree">{_makeTree(curSpec, { headPress: !advDesktop, headPressWhereNoBadge: true })}</div>
      : <div className="hp-none">No talent tree for this specialization.</div>;
  }
  var unitP = function(key, label, count, body) {
    var on = growthUnit === key;
    return (
      <section className="sb-unit" data-open={on ? '1' : '0'} key={key}>
        <button type="button" className="hp-head" data-state={on ? 'active' : ''} aria-expanded={on ? 'true' : 'false'} data-testid={'growth-unit-' + key} onClick={function() { setGrowthUnit(key); }}>
          <span className="hp-name">{label}</span><span className="hp-count">{count}</span></button>
        <div className="sb-unit-body" data-scroll="1" hidden={!on}>{on ? body : null}</div>
      </section>);
  };
  return (
    <div className="sb-growth-room" data-testid="sheet-growth" data-stage={centerMode}>
      {_modsNotice ? h(ModsLoadNotice, { onRetry: _retryMods }) : null}
      {_xpRepaired ? h(XpRepairedNotice, { onDismiss: _dismissXpRepaired }) : null}
      <div className="sb-xp" data-testid="growth-xp">
        <span>Total earned <b>{totalXp}</b></span><span>Spent <b>{spentXp}</b></span>
        <span className="sb-xp-hero">Available <b data-testid="growth-xp-left">{availableXp}</b></span>
        {xpEditing
          ? <input className="sb-filter" type="number" autoFocus defaultValue={availableXp} min={0} aria-label="Set available XP" style={{ width: 120, margin: 0 }}
              onBlur={function(e) { setAvailableXp(e.target.value); setXpEditing(false); }}
              onKeyDown={function(e) { if (e.key === 'Enter') { setAvailableXp(e.target.value); setXpEditing(false); } if (e.key === 'Escape') setXpEditing(false); }} />
          : <button type="button" className="hp-quiet" data-testid="growth-xp-edit" aria-label={'Edit available XP, currently ' + availableXp} onClick={function() { setXpEditing(true); }}>Edit</button>}
      </div>
      {/* B-855 · THE REASON LINE (frames H1/H2, R-B854-5): the frame's words exactly, in the sheet's
          existing notice treatment. A tap or hover on any held control points here; no dialog opens. */}
      {hold ? <div className="sb-notice" role="status" data-hold={hold.variant} data-testid="growth-hold" tabIndex={-1}><span className="sb-notice-t">{holdText}</span></div> : null}
      {/* F-100 M5 · the house-rules notices (frames M2a/U8/U9), in the same reason-line slot */}
      {h(HouseRuleNotices, { telling: telling })}
      {/* HOMEBREW H1 · the Homebrew line, F3's after-line and F4's offer (frames F1–F4, R-H1F-1), in the same slot */}
      {h(HomebrewLines, { sheet: sheet, onSheetChange: onSheetChange, hold: hold })}
      {/* B-657 · THE WALK TAKES THE ROOM'S WIDTH. On the journey the board is ONE COLUMN and the
          spine's three units step aside, because the frame's PREVIEW is itself a two-zone
          `.sb-board.sb-growth.sb-jprev` (the reading 680 + the tree ≥ 920) and the stage column at
          1440 measures ~700px — a grid inside a grid there loses the frame's geometry outright. The
          lab composes the walk the same way: `takeover("New specialization", spec_journey(), "Back
          to growth")` is its OWN takeover, holding the walk and nothing else. The room stays: the
          takeover's head, the XP strip, and the stage's own `New specialization · Journey` head.
          The walk carries its own paper (the shelf plate, the preview's two plates), so the stage's
          data plate steps aside with the spine — a plate inside a plate is double stock. */}
      <div className="sb-board sb-growth" data-journey={(centerMode === 'selector' || centerMode === 'sigwalk') ? '1' : undefined}>
        {(centerMode === 'selector' || centerMode === 'sigwalk') ? null : (
        <div className="sb-spine">
          {unitP('skills', 'Skills', SKILL_KEYS_DISPLAY.length || '—', skillsPaper)}
          {unitP('specs', 'Specializations', sheetSpecs.length || '—', specsPaper)}
          {sigOn ? unitP('sig', 'Signature abilities', sigEntries.length || '—', sigPaper) : null}
          {forceHasContent ? unitP('force', 'Force Powers', powersDisplay.length || '—', forcePaper) : null}
          {/* R6 S2 (U12): REWARDS after Force Powers */}
          {rwReg && window.SWRPGRewards ? unitP('rewards', 'Rewards', rewardRowsOf(sheet).filter(function (r) { return REWARD_BUY_TYPES.indexOf(r.key) >= 0; }).length || '—',
            <GrowthRewards reg={rwReg} sheet={sheet} sel={centerMode === 'reward' ? rwSel : null} left={availableXp} held={!!hold} onSheetChange={onSheetChange}
              onSel={function (k) { setRwSel(k); setCenterMode('reward'); }} />) : null}
          {/* R6 S3 (S3-growth): CONTACTS after REWARDS */}
          {rwReg && window.SWRPGRewards ? unitP('contacts', 'Contacts', contactRowsOf(sheet).length || '—',
            <GrowthContacts sheet={sheet} left={availableXp} held={!!hold} onSheetChange={onSheetChange} />) : null}
        </div>)}
        <div className="sb-stage">{(centerMode === 'selector' || centerMode === 'sigwalk') ? (
          <React.Fragment>
            <div className="hp-sechead" style={{ margin: '0 0 4px' }}><h2>{stageHead[0]}</h2><span className="hp-cite">{stageHead[1]}</span></div>
            {stagePaper}
          </React.Fragment>
        ) : (
        <div className="hp-plate" data-pad="data" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div className="hp-sechead" style={{ margin: '0 0 12px' }}><h2>{stageHead[0]}</h2><span className="hp-cite">{stageHead[1]}</span></div>
          {stagePaper}
        </div>)}</div>
      </div>
      {modalsBlock}
    </div>
  );
}

// ── CharacterSheet wrapper with page swipe ──
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// SHIP YARD S4 · THE SHEET'S VESSELS FACE (V1–V4), the PAPER sheet only (ruling `shipyard-s4-ground` §6 JOB 2
// chunk B; switch map P8/P9; R-S4G-14: a V face mounts only inside the paper sheet). The only VESSELS face since the S5
// flip (R-S5R-18). Built to the APPROVED frames `Docs/mockups/shipyard-sheet-vessels.html`
// (source `Docs/mockups/assets/shipyard/frames_vessels.cjs`). The drawing of a ship's condition, hull line, last
// log line and art is the floor's own (`SWRPGVesselView.YardParts`); every number comes from the ONE resolver;
// every pool from the sheet's ONE pool core (`sheetPoolFor`, the hub's pool with the item and talent dice) with
// the seat's upgrade from `seatPool` / `skillPool`. MARK (F-2) writes `sheetData.myShipId` through
// `SWRPGVesselResolve.markMyShip` and the sheet's own save path, and nothing else; `markRefusal` (R-S4G-6)
// decides the refusal, whose face (U19) waits on Jason's word — until then a refused press writes nothing.
var SEAT_OPENS = { pilot: 1 };   // the seat card the frames draw open (V2: SEAT · PILOT); others stay closed
function vesselSkillLabel(reg, skill) { var s = reg && reg.skills && reg.skills[skill]; return s ? s.label : skill; }
// ══ SHIP CARGO on the sheet (card `shipyard-cargo-build` F-2, F-3; the approved frames v3 @ ui-lab 318935e2e, D-265) ══
// The sheet's ONE resolver context (B-1067 F2, R-S5R-7): the character on the sheet joins the device's roster in the one builder.
// The VESSELS card and the GEAR row's Load onto ‹ship› both read through it, so a ship prints one number on both.
function sheetResolveCtx(me) { return yardResolveCtx(me ? [me] : []) || { characters: [] }; }
// The ships a character may load onto (R-SCG-4, the player host): one they own, and the party's; MY SHIP first.
function sheetCargoShips(charId, sheet, vessels) {
  var mine = (vessels || []).filter(function (r) { return r && r.owner === 'char:' + charId; }), party = (vessels || []).filter(function (r) { return r && r.owner === 'party'; });
  var bond = sheet && sheet.myShipId;
  return mine.filter(function (r) { return r.id === bond; }).concat(mine.filter(function (r) { return r.id !== bond; }), party.filter(function (r) { return r.id === bond; }), party.filter(function (r) { return r.id !== bond; }));
}
function sheetCargoShip(rec, ctrl, rctx) {
  var VS = window.SWRPGVessels, R = window.SWRPGVesselResolve, VV = window.SWRPGVesselView;
  var vrow = VS.vehicleFor(rec, ctrl.getVehicleByKey), res = vrow ? R.resolve(rec, vrow, rctx) : null;
  return { rec: rec, vrow: vrow, res: res, name: VV.YardParts.shipName(rec, vrow) };
}
// F-2 · the GEAR unit's row: Load onto ‹ship› on a registry entry; an inline copy says why it is HELD (R-SCG-6); with no ship
// in scope the act is ABSENT (nothing is drawn). Open, it takes the count (the sheet's own − n / N + idiom) and the preview.
// The act is the Yard's (SWRPGVesselView.cargoLoad) over the host's ONE character write (R-SCB-2): `write` is the function the
// store's FINALIZE persists through, handed down from the app.
function SheetCargoAct(props) {
  var VS = window.SWRPGVessels, VV = window.SWRPGVesselView, C = VV && VV.Cargo;
  var ctrl = useVehicleLibraryController();
  var state = VS ? VS.useVessels() : { vessels: [] };
  var gear = C ? C.useGear() : null;
  var YP = VV && VV.YardParts, attLoad = YP && YP.useAttachmentsLoad ? YP.useAttachmentsLoad() : { by: null }, wpById = YP && YP.useWeapons ? YP.useWeapons() : null;
  var entry = props.entry || {}, ref = entry._inline ? null : entry.id;
  var _o = React.useState(false), open = _o[0], setOpen = _o[1];
  var _q = React.useState(1), qty = _q[0], setQty = _q[1];
  var ships = sheetCargoShips(props.charId, props.sheet, state.vessels);
  var _s = React.useState((ships[0] && ships[0].id) || null), shipId = _s[0], setShip = _s[1];
  if (!C) return null;
  if (entry._inline) return <p className="sb-e-t sb-sc-read" data-testid="gear-held">{C.held}</p>;
  if (C.marked && C.marked(entry)) return null;   // R-SCG0-3: a talent-marked copy never loads (absent, not greyed; no string)
  if (!ships.length || !ref || !gear || !gear[ref] || typeof props.write !== 'function') return null;     // no ship on this device: ABSENT, not greyed
  var rctx = Object.assign({}, sheetResolveCtx({ id: props.charId, sheetData: props.sheet }), { attachments: attLoad.by || null, weapons: wpById });
  var ship = ships.filter(function (r) { return r.id === shipId; })[0] || ships[0], v = sheetCargoShip(ship, ctrl, rctx);
  var held = Math.max(1, Number(entry.qty) || 1), e = gear[ref].encumbrance, add = typeof e === 'number' ? e * qty : 0;
  var hold = C.holdOf(ship, v.res, gear);
  function commit() {
    var got = VV.cargoLoad({ host: 'player', write: props.write, roster: [{ id: props.charId, sheetData: props.sheet }], gearRow: function (k) { return gear[k] || null; },
      now: function () { try { return new Date().toISOString().slice(0, 10); } catch (x) { return null; } }, shipName: function (r) { return sheetCargoShip(r, ctrl, rctx).name; } },
      props.charId, props.gi, ship.id, Math.min(qty, held), false);
    if (got.ok) { setOpen(false); setQty(1); }
  }
  if (!open) return <div className="sb-sc-act"><button type="button" className="hp-quiet sb-sc-btn" data-testid="gear-load" onClick={function () { setOpen(true); }}>{'Load onto ' + v.name}</button></div>;
  return <div className="sb-sc-open" data-testid="gear-load-open">
    {ships.length > 1 ? <div className="sb-sc-onto"><span className="sb-e-m">Onto</span>{ships.map(function (r) { var on = r.id === ship.id;
      return <button type="button" key={r.id} className="hp-quiet sb-sc-btn" data-on={on ? '1' : '0'} aria-pressed={on ? 'true' : 'false'} data-testid="gear-load-ship" onClick={function () { setShip(r.id); }}>{sheetCargoShip(r, ctrl, rctx).name}</button>; })}</div> : null}
    <div className="sb-trk sb-sc-trk" data-testid="gear-load-qty">
      <button type="button" className="sb-trk-btn" aria-label="One less" disabled={qty <= 1} onClick={function () { setQty(Math.max(1, qty - 1)); }}>{'−'}</button>
      <span className="sb-vitval sb-trk-val">{qty}<span className="sb-of"> / {held}</span></span>
      <button type="button" className="sb-trk-btn" aria-label="One more" disabled={qty >= held} onClick={function () { setQty(Math.min(held, qty + 1)); }}>+</button>
    </div>
    <p className="sb-e-t sb-sc-read" data-testid="gear-load-hold">{'Encumbrance ' + add + ' · ' + C.tradeHoldTo(hold, add, false)}</p>
    {hold.openCap != null && hold.open + add > hold.openCap ? <p className="sb-sc-overp">{C.over(hold.open + add, hold.openCap)}</p> : null}
    <div className="sb-sc-verbs"><button type="button" className="hp-commit" data-testid="gear-load-commit" onClick={commit}>{'Load onto ' + v.name}</button>
      <button type="button" className="hp-quiet" data-testid="gear-load-cancel" onClick={function () { setOpen(false); }}>Cancel</button></div>
  </div>;
}
// F-3 · the VESSELS card: one line, the SHIP's total in TRADE's own words, ABOARD n · CAPACITY N (holdOf's open + hidden over
// resolve()'s encumbrance, as TRADE's trade-aboard line prints it), and a door to that ship's TRADE. Over capacity counts
// against that total (shipyard-cargo-frames-card ADDENDUM 1, F4).
function SheetHoldDoor(props) {
  var VV = window.SWRPGVesselView, C = VV && VV.Cargo, gear = C ? C.useGear() : null;
  if (!C || !gear || !props.res) return null;
  var h = C.holdOf(props.rec, props.res, gear), on = h.open + h.hidden, cap = props.res.encumbrance;
  return <React.Fragment>
    <button type="button" className="y-vseat y-sc-hold" data-testid="vessel-hold" onClick={props.onOpen}>
      <span className="y-seat">ABOARD <b>{on}</b> · CAPACITY <b>{cap == null ? '—' : cap}</b></span><span className="y-vseat-p"><span className="y-mono">TRADE</span><span className="y-chev">›</span></span></button>
    {cap != null && on > cap ? <p className="y-sc-overp" data-testid="vessel-hold-over">{C.over(on, cap)}</p> : null}
  </React.Fragment>;
}
// THE SHEET'S VESSELS GUN ROW, its lines (THE SURFACING MISSION, card `f100-surfacing-yard-concept` R-SYC-2; ADDENDUM 2 Q4 (b),
// Q5; ADDENDUM 6 ACCEPTANCE (6)): over the gun's own spec (`weaponPoolOf`'s vehicle arm), the APPLIED changes, untaken, whose
// source is an owned talent's (`poolMods` entries carrying `talentId`; a fitted part's granted talent, `spec.grantMods`, R-SYC-5)
// or THIS weapon's quality (`spec.qualityMods`) — carried
// gear's dice stay in the pool and off the row (the seat's own pool credits them); the TOLD lines are the weapon's modes
// (Auto-fire) in the hub's words (`offerEffect`), never ticked. A held spec has neither. → { lines: [change], told: [{name, words}] }.
function sheetVesselGunLines(spec, poolMods) {
  if (!spec || spec.held) return { lines: [], told: [] };
  var UI = window.SWRPGUI || {}, src = {};
  (poolMods || []).forEach(function (m) { if (m && m.talentId) src[m.source] = true; });
  (spec.qualityMods || []).concat(spec.grantMods || []).forEach(function (m) { if (m && m.source) src[m.source] = true; });
  return { lines: (spec._poolModChanges || []).filter(function (c) { return !c.taken && src[c.source]; }),
    told: (spec.modes || []).map(function (m) {
      var words = (m.poolRows || []).map(function (r) { return UI.offerEffect ? UI.offerEffect(r) : ''; }).filter(Boolean).join('; ');
      return { name: m.source, words: words || m.words || '' };
    }) };
}

// The V face's lists, ONE reading for its body and its head's count (B-945): the character's active ships, and the party's
// active ships that no party unit holds (a unit's ships are the unit's page).
function sheetVesselLists(VS, P, state, who) {
  var own = who ? VS.forOwner(state, who).filter(P.isActive) : [];
  var units = (function () { try { var u = JSON.parse(localStorage.getItem('swrpg_fleet_units_v1') || 'null'); return u && Array.isArray(u.units) ? u.units : []; } catch (e) { return []; } })();
  var inPartyUnit = {};
  units.forEach(function (u) { if (u && u.owner === 'party') { (u.members || []).forEach(function (id) { inPartyUnit[id] = true; }); if (u.flagship) inPartyUnit[u.flagship] = true; } });
  var partyAll = VS.forOwner(state, 'party');
  return { own: own, partyAll: partyAll, party: partyAll.filter(function (r) { return P.isActive(r) && !inPartyUnit[r.id]; }) };
}
// B-945: the VESSELS head counts the ships its body lists, as the approved sheet board draws it (lab pin `83bf1f2d`
// `stack_full`: "1 if ship", "—" when none) — the character's, the party's, and the ship marked as theirs. Its own
// component, so the head counts with the unit closed and follows the store.
function SheetVesselsCount({ char, sheet }) {
  var VS = window.SWRPGVessels, VV = window.SWRPGVesselView, P = VV && VV.YardParts, R = window.SWRPGVesselResolve;
  var state = VS ? VS.useVessels() : { vessels: [] };
  if (!VS || !P) return '—';
  var charId = char && char.id != null ? String(char.id) : null;
  var lists = sheetVesselLists(VS, P, state, charId ? 'char:' + charId : null), ids = {};
  var bond = charId && R ? R.resolveBond({ id: charId, sheetData: sheet || {} }, state) : null;
  lists.own.concat(lists.party, bond ? [bond] : []).forEach(function (r) { ids[r.id] = true; });
  var n = Object.keys(ids).length;
  return n ? String(n) : '—';
}
function SheetVesselsS4({ char, sheet, equipment, accent, onSheetChange, onOpenYard, committed }) {
  var ctrl = useVehicleLibraryController();
  var VV = window.SWRPGVesselView, VS = window.SWRPGVessels, R = window.SWRPGVesselResolve;
  var P = VV && VV.YardParts;
  var reg = P ? P.useRegistries() : null;
  // B-1047 (D-27's drawing B1047-VESSELS, `Docs/mockups/shipyard-s4-parts-missing.html`): while the parts list is missing
  // the resolver is handed `attachments: null` — its own HELD signal (vesselResolve `resolve`), as every Yard face does
  // (R-S4CC-6): a ship with parts prints "—" where a part could move a number, and its seat "UPGRADE —"; a ship with none
  // prints its numbers. WB5's words (LOADING… / the failure plate) print once, above the face, while any ship is held.
  var attLoad = P && P.useAttachmentsLoad ? P.useAttachmentsLoad() : { by: null, err: null };
  var wpById = P && P.useWeapons ? P.useWeapons() : null;   // S8 (card ADDENDUM 24 H-9 (a), R-S8B-10): a conversion's guns reach the pilot card
  var attById = attLoad.by;
  var state = VS ? VS.useVessels() : { vessels: [] };
  var _open = useState(null), openSeat = _open[0], setOpenSeat = _open[1];
  var canonReady = useCanonicalReady();
  var _tvEpoch = window.SWRPGTalentView.useTalentViewEpoch();   // A3-2 / B-844: a talent's dice resolve through the stamp view — the memo keys on its epoch
  var poolMods = React.useMemo(function () { return collectPoolMods(equipment); }, [equipment, canonReady, _tvEpoch]);
  if (!VV || !P || !VS || !R) return null;
  sheet = sheet || {};
  var cb = window._canonicalCharbuild || null;
  var charId = char && char.id != null ? String(char.id) : null;   // rider 22: no id → owns nothing (never 'char:unknown')
  var me = charId ? { id: charId, sheetData: sheet } : null;
  var ctxChars = sheetResolveCtx(me);   // F2: the device's roster joins in the one builder (sheetResolveCtx, shared with the GEAR row's act)
  var meCtx = me ? ctxChars.characters.filter(function (c) { return String(c.id) === charId; })[0] : null;
  var rosterCtx = ctxChars.characters;
  var rctx = Object.assign({}, ctxChars, { attachments: attById || null, weapons: wpById });
  var who = charId ? 'char:' + charId : null;
  var bond = me ? R.resolveBond(me, state) : null;
  function view(rec) {
    var vrow = VS.vehicleFor(rec, ctrl.getVehicleByKey);   // S8 (R-S8B-4): the ONE lookup — a built ship's frame, else the library row
    return { rec: rec, vrow: vrow, res: vrow ? R.resolve(rec, vrow, rctx) : null };
  }
  function sheetKey(skill) { return (cb && cb.skillSheetKeys && cb.skillSheetKeys[skill]) || null; }
  // THE YARD'S POOL DRAWING (B-1600; ruling release-r7-bug-burndown ADDENDA 251, 276): the ONE pool core (`sheetPoolOf`,
  // as `sheetPoolFor` reads it) drawn with `cssSized`, so yard.css's `.yd .hp-sym` 19px (the hangar mockup's) sizes the
  // glyphs; `sheetPoolFor` writes an inline 16px that beat it. The sheet's own pools keep `sheetPoolFor`.
  function yardPoolFor(sheet, key, mods, extra) {
    var Dice = window.SWRPGDice; if (!Dice || !Dice.renderPool) return null;
    return Dice.renderPool(sheetPoolOf(sheet, key, mods, extra), { size: 1, cssSized: true });
  }
  function seatPoolEl(v, role, skillOverride) {
    var SPg = window.SWRPGSheetPool, skill = skillOverride || R.seatSkill(role, v.vrow), key = sheetKey(skill);
    if (!key || !v.vrow) return null;
    var up = skillOverride ? R.skillPool(v.rec, v.vrow, Object.assign({}, rctx, { character: who }), skill) : R.seatPool(v.rec, v.vrow, Object.assign({}, rctx, { character: who }), role);
    // R-SYC-5: a fitted part's granted talents are the sitter's for this seat's roll (the one path's seatGrantMods)
    var mods = SPg && SPg.seatGrantMods && v.res ? poolMods.concat(SPg.seatGrantMods(sheet, v.res.seat_talents)) : poolMods;
    return { el: yardPoolFor(sheet, key, mods, { upgradeAbility: up ? up.upgradeAbility : 0 }), label: vesselSkillLabel(reg, skill), skill: skill, key: key,
      up: up ? up.upgradeAbility : 0, because: up ? up.because : [] };
  }
  function openShip(id) { if (onOpenYard) onOpenYard({ room: 'ship', id: id }); }
  var lists = sheetVesselLists(VS, P, state, who), own = lists.own, partyAll = lists.partyAll, party = lists.party;
  // B2 (D-27, R-S4CAT-3): the party's ship shows where its record is on this device, and one honest line (R-S3K-8)
  // elsewhere — only on a list that is otherwise non-empty; with nothing here V3 stands, as approved (ruling
  // `shipyard-s5-ready` R-S5R-1).
  var partyLine = !partyAll.length && own.length > 0;
  var yardDoor = <button type="button" className="y-door y-vdoor" data-testid="vessel-yard-door" onClick={function () { if (onOpenYard) onOpenYard(); }}>
    <span><span className="y-door-n">THE SHIPYARD</span><br /><span className="y-door-m">THE HANGAR AND THE CATALOGUE</span></span><span className="y-chev">›</span></button>;
  var group = function (hd) { return <div className="y-vgroup" key={'g-' + hd}><span>{hd}</span></div>; };
  // U4 (`shipyard-s4-approvals` R-S4A-1): S3's hold, inside the VESSELS unit only — the shared paper banner above
  // (THIS TAB IS BEHIND, REFRESH live), every control below receded and inert; the values stay readable.
  var stale = !!(VS.isStale && VS.isStale());
  function hold(kids) {
    var UI = window.SWRPGUI;
    if (!stale || !UI || !UI.StaleHold) return kids;
    return [<div key="stale-banner" style={{ marginBottom: 12 }}>{h(UI.StaleTabBanner, { paper: true })}</div>,
      <div key="stale-held" className="y-held">{h(UI.StaleHold, { held: true }, kids)}</div>];
  }

  // V3 · EMPTY — the shipped face's own sentence and its door; nothing else
  if (!own.length && !party.length) {
    return <div className="yd yd-in" data-testid="sheet-vessels-s4" data-face="V3" data-stale={stale ? '1' : undefined}>
      {hold([<p className="y-vnone" key="none" data-testid="vessel-empty">No vessels yet. Open the Shipyard to browse and add one.</p>, <React.Fragment key="door">{yardDoor}</React.Fragment>])}</div>;
  }

  function seatOf(rec) { var s = who && (rec.crew || []).filter(function (c) { return c.who === who; })[0]; return s ? s.role : null; }
  function SeatCard(v, role) {
    var ma = (reg && reg.starship) || { rows: [], cite: '' };
    var byName = function (n) { return ma.rows.filter(function (r) { return r.name === n; })[0]; };
    var pilotOnly = function (r) { return /Pilot Only/i.test(r.restrictions || ''); };
    var pilotM = ma.rows.filter(function (r) { return r.type === 'Maneuver' && pilotOnly(r); });
    var anyoneM = ma.rows.filter(function (r) { return r.type === 'Maneuver' && !pilotOnly(r); });
    var pilotA = ma.rows.filter(function (r) { return r.type === 'Action' && pilotOnly(r); });
    var combat = byName('Perform a Combat Check (vehicle weapons)');
    var sp = seatPoolEl(v, role), gun = seatPoolEl(v, 'gunner');
    var skillRec = reg && reg.skills && reg.skills[sp && sp.skill];
    var charName = skillRec ? skillRec.characteristic : '';
    var charVal = Number((sheet.characteristics || {})[charName]) || 0;
    // the talents that touch this roll, credited by name (the owned-talent arm of the ONE assembler's data)
    var credits = [];
    try {
      var PM = window.SWRPGPoolMods, idx = poolModSkillIndex();
      var mctx = PM && idx && sp ? PM.contextFor(sp.key, idx, (sheetSkillCharacteristic(sheet, sp.key) || {}).characteristic) : null;   // B-1065: the pool beside it rolls the best-of
      if (mctx && cb && cb.talents) CharResolve.ownedTalentPoolSources(sheet, cb).forEach(function (t) {
        (t.pool_mods || []).forEach(function (m) {
          var mod = { source: t.name, die: m.die, count: m.count, direction: m.direction === undefined ? null : m.direction, condition: m.condition === undefined ? null : m.condition, appliesTo: m.applies_to || null };
          if (!PM.appliesNow(mod) || !PM.matches(mod, mctx)) return;
          var times = (!t.ranked || m.per_rank === false) ? 1 : Math.max(1, Number(t.ranks) || 1);
          // F-100 M9 (R-M9S-6): `count: 'all'` never reaches arithmetic — it is credited as ALL, not multiplied
          credits.push({ name: t.name, die: m.die, n: m.count === 'all' ? 'all' : m.count * times, direction: m.direction, rank: t.ranked ? Math.max(1, Number(t.ranks) || 1) : null });
        });
      });
    } catch (e) { credits = []; }
    var checkWord = String((sp && sp.label) || '').replace(/\s*\(.*\)\s*$/, '').toUpperCase();
    // S7 (`shipyard-s7-build-card` R-S7B-7; D-220, board v3 frame A2): the combat act draws ONE row per resolved weapon — THE
    // SHIP's own row (`YardParts.WeaponRow`, never a copy; the VT-49's two quad lasers are two rows, D-220 Q2), with no TO HIT
    // (D-220 Q3). Under each row's qualities: the gunner pool (R-S7P-1), then its "because" lines in the hub's one applied
    // sentence (`appliedLineText`, R-S7P-3/-4), none drawn when nothing contributes. S7's source is TODAY's only: the sitter's
    // talents, as the hub's change records for the gun's own check (`yardSeatLines`, the one lines path). The credit line
    // above stays: it credits the SEAT's check (the pilot's Piloting, SEAT_OPENS), which never touches a gun's roll. No
    // weapons: today's single act pool line, no slot (R-S7P-2). A held number prints "—" in the row (B-1175 finding 5).
    var ws = (v.res && v.res.weapons) || [];
    var sth = v.res ? v.res.strain_threshold : null, down = sth != null && (v.rec.strain_current || 0) > sth;
    // THE SURFACING MISSION (R-SYC-2; ADDENDUM 2 Q1 (a), Q4, Q5): each row rolls ITS OWN gun — `weaponPoolOf`'s vehicle arm over
    // the resolver's row with the gunner seat's upgrade, so a Quad Laser's Accurate is a boost on its row and nowhere else; the
    // slot draws that spec's pool ("—" for a held rating), the lines are `sheetVesselGunLines`', the told lines the hub's look.
    var Row = P.WeaponRow, UI = window.SWRPGUI || {}, SP = window.SWRPGSheetPool, Dice = window.SWRPGDice;
    var gunRows = Row && ws.length ? ws.map(function (w, i) {
      var spec = gun && SP && SP.weaponPoolOf ? SP.weaponPoolOf(sheet, w, { vehicle: { upgradeAbility: gun.up, because: gun.because, index: i,
        weapons: wpById, grants: v.res.seat_talents }, forceCommitted: committed }) : null;   // B-1465: the session's committed set
      var gl = sheetVesselGunLines(spec, poolMods), pl = spec && !spec.held && Dice && Dice.buildPool ? Dice.buildPool(spec) : null;
      var pool = spec && Dice && Dice.renderPool ? <div data-testid="vessel-seat-weapon-pool">
        <div className="y-row" style={{ marginTop: 6 }}><span className="y-pool">{pl ? Dice.renderPool({ proficiency: pl.proficiency || 0, ability: pl.ability || 0,
          boost: pl.boost || 0, setback: pl.setback || 0 }, { size: 1, cssSized: true }) : '—'}</span><span className="y-mono">{gun.label}</span></div>
        {UI.AppliedLine && UI.appliedLineText ? gl.lines.map(function (c, k) { return <UI.AppliedLine key={k}>{UI.appliedLineText(c, gun.label)}</UI.AppliedLine>; }) : null}
        {UI.ToldLine ? gl.told.map(function (t, k) { return h(UI.ToldLine, { key: 't' + k, name: t.name, words: t.words }); }) : null}</div> : null;
      return <Row key={i} p={{ reg: reg }} res={v.res} w={w} i={i} down={down} sil={v.res.silhouette} noTohit={true} pool={pool} />;
    }) : null;
    var act = function (r, poolEl, body) {
      if (!r) return null;
      return <div className="y-act" key={r.name}><div className="y-e-h"><span className="y-e-n">{r.name}</span><span className="y-e-m">{r.type}</span></div>
        <div className="y-mono" style={{ marginTop: 3 }}>{r.restrictions}</div>
        {poolEl ? <div className="y-row" style={{ marginTop: 6 }}><span className="y-pool">{poolEl.el}</span><span className="y-mono">{poolEl.label}</span></div> : null}
        {body || null}</div>;
    };
    var glyph = (window.SWRPGUI || {}).glyphText || function (t) { return t; };
    return <div className="y-seatopen" data-testid="vessel-seat-open">
      <div className="y-e-h"><span className="y-e-n">{'SEAT · ' + P.roleWord(role)}</span><span className="y-e-m">{ma.cite}</span></div>
      {sp ? <div className="y-bigpool"><span className="y-mono">{sp.label} · <span style={{ textTransform: 'uppercase' }}>{charName}</span> {charVal} · RANK {sheetSkillRank(sheet.skills, sp.key)}</span><span className="y-pool">{sp.el}</span></div> : null}
      {credits.map(function (c, i) {
        return <p className="y-attr" key={i} style={{ textAlign: 'left', marginTop: 6 }} data-testid="vessel-seat-credit">
          {(c.direction === 'remove' ? 'REMOVE ' : 'ADD ')}
          {c.n === 'all' ? 'ALL ' : null}
          {Array.apply(null, Array(c.n === 'all' ? 1 : Math.max(0, c.n))).map(function (_, j) { return window.SWRPGDice && window.SWRPGDice.symbolSpan ? window.SWRPGDice.symbolSpan(c.die, 'cr' + i + '-' + j, 1) : null; })}
          {' ON ' + checkWord + ' CHECKS · ' + c.name + (c.rank != null ? ' · RANK ' + c.rank : '')}</p>;
      })}
      <div className="y-sub">PILOT ONLY · MANEUVERS · NO CHECK</div>
      <div className="y-mnvl">{pilotM.map(function (r) { return <div key={r.name}><span>{r.name}</span><i>{r.restrictions}</i></div>; })}</div>
      {v.res ? <p className="y-small y-muted" style={{ marginTop: 4 }}>{'This ship flies at speed ' + v.res.speed + ', silhouette ' + v.res.silhouette + '.'}</p> : null}
      {pilotA.map(function (r) { return act(r, sp, <p className="y-e-t y-small" style={{ marginTop: 6 }}>{glyph(r.effect)}</p>); })}
      {gunRows ? act(combat, null, <div data-testid="vessel-seat-weapons">{gunRows}</div>) : act(combat, gun, null)}
      <div className="y-sub">THE SHIP’S MANEUVERS · ANYONE ABOARD</div>
      {anyoneM.map(function (r) { return act(r, null, null); })}</div>;
  }
  function SigBlock(v) {
    var tal = (cb && cb.talents) || {};
    var S = window.SWRPGSources;
    var src = function (t) { var s0 = t && t.sources && t.sources[0]; return s0 && S && S.sourceLabel ? S.sourceLabel(window._swrpgSourcesIndex || null, s0) : s0; };
    var glyph = (window.SWRPGUI || {}).glyphText || function (t) { return t; };
    var cl = ((v.res && v.res.because && v.res.because.hard_points) || []).filter(function (b) { return b.source === 'Custom Loadout'; })[0];
    var mechKey = sheetKey('mechanics');
    var up = R.skillPool(v.rec, v.vrow, Object.assign({}, rctx, { character: who }), 'mechanics');   // the HOLDER's pool (R-S4G-7), never out.pools alone
    var clT = tal.custom_loadout, svT = tal.signature_vehicle;
    return <React.Fragment>
      <div className="y-vbond"><div className="y-row" style={{ justifyContent: 'space-between' }}><span className="y-seat">YOUR BOND · <b>MY SHIP</b></span>
        <button type="button" className="y-link" data-testid="vessel-unmark" onClick={function () { mark(null); }}>UNMARK</button></div></div>
      <div className="y-vsig" data-testid="vessel-sig">
        <div className="y-sub" style={{ marginTop: 0 }}>WHAT YOUR TALENTS DO ON IT</div>
        {cl && clT ? <div className="y-act" data-testid="vessel-sig-hp"><div className="y-e-h"><span className="y-e-n">{'HARD POINTS ' + (v.res.hard_points - cl.amount) + ' → ' + v.res.hard_points}</span><span className="y-e-m">{'+' + cl.amount}</span></div>
          <span className="y-attr" style={{ textAlign: 'left' }}>{clT.name + ' · ' + src(clT)}</span>
          <p className="y-e-t y-small" style={{ marginTop: 4 }}>{glyph(clT.description)}</p></div> : null}
        {svT && mechKey ? <div className="y-act" data-testid="vessel-sig-mech"><div className="y-e-h"><span className="y-e-n">MECHANICS UPGRADED ON YOUR SIGNATURE VEHICLE</span></div>
          <div className="y-row" style={{ marginTop: 6 }}><span className="y-pool" data-pool="mech-before">{yardPoolFor(sheet, mechKey, poolMods)}</span><span className="y-mono">→</span>
            <span className="y-pool" data-pool="mech-after">{yardPoolFor(sheet, mechKey, poolMods, { upgradeAbility: up.upgradeAbility })}</span><span className="y-mono">{vesselSkillLabel(reg, 'mechanics')}</span></div>
          <span className="y-attr" style={{ textAlign: 'left' }}>{svT.name + ' · ' + src(svT)}</span>
          <p className="y-e-t y-small" style={{ marginTop: 4 }}>{glyph(svT.description)}</p></div> : null}
        <p className="y-mono" style={{ marginTop: 8 }}>{'SILHOUETTE ' + (v.res ? v.res.silhouette : '—') + ' · A SIGNATURE VEHICLE CAN BE SILHOUETTE ' + R.signatureLimit(meCtx && meCtx.talents) + ' OR LESS '}
          <span className="y-cite">{R.SIGNATURE_VEHICLE.cite}</span></p></div>
    </React.Fragment>;
  }
  // U19 (`shipyard-s4-approvals` R-S4A-1): MARK's refusals — the button is disabled where the book says no, and its
  // reason is its caption, in the approved page's words (frames_undrawn.cjs U19). The kinds are the resolver's
  // (markRefusal: held · too_large; signatureOf's `second`: legacy data with two holders).
  function holderName(id) { var c = VV.readDeviceRoster().filter(function (x) { return String(x.id) === String(id); })[0]; return (c && c.name) || String(id); }
  function refusalWords(r) {
    if (!r) return null;
    var one = R.SIGNATURE_VEHICLE.one_holder_cite;
    if (r.kind === 'held') return 'Already ' + holderName(r.holder) + '’s Signature Vehicle. Only one character can hold a ship as Signature Vehicle (' + one + ').';
    if (r.kind === 'too_large') {
      var t = (meCtx && meCtx.talents) || {};
      return 'Too big to be a Signature Vehicle: silhouette ' + (r.silhouette != null ? r.silhouette : '—') + '. Yours can be silhouette ' + r.limit + ' or less'
        + (Number(t.larger_project) ? '.' : ', ' + R.signatureLimit(Object.assign({}, t, { larger_project: 1 })) + ' with one rank of Larger Project.');
    }
    if (r.kind === 'two') return 'Two characters hold this ship as Signature Vehicle, and only one can (' + one + '). Counting ' + holderName(r.holder) + '’s for now. Unmark it on the other.';
    return null;
  }
  // MARK (F-2): the bond's one writer. A refusal (R-S4G-6) writes nothing; its face is U19.
  function mark(vesselId) {
    if (!me || !onSheetChange) return;
    if (vesselId != null) {
      var rec = state.vessels.filter(function (r) { return r.id === vesselId; })[0];
      var refusal = R.markRefusal(meCtx, vesselId, state, rec ? VS.vehicleFor(rec, ctrl.getVehicleByKey) : null, rosterCtx);
      if (refusal) return;
    }
    var next = R.markMyShip(me, vesselId, state);
    onSheetChange({ myShipId: next.sheetData.myShipId });
  }
  function ShipCard(v, o) {
    var P2 = P, rec = v.rec, role = o.party ? seatOf(rec) : seatOf(rec);
    var seat = role ? seatPoolEl(v, role) : null;
    var isOpen = openSeat === rec.id && !!SEAT_OPENS[role];
    var refusal = o.two ? { kind: 'two', holder: o.two } : o.markable ? R.markRefusal(meCtx, rec.id, state, v.vrow, rosterCtx) : null;
    var why = refusalWords(refusal);
    return <article className="y-vship" key={rec.id} data-testid="vessel-ship" data-vessel={rec.id}>
      <button type="button" aria-label={'Open ' + P2.shipName(rec, v.vrow)} data-testid="vessel-open" onClick={function () { openShip(rec.id); }} style={{ display: 'contents' }}>
        <P2.Art rec={rec} vrow={v.vrow} host="sheet-vessel" schem={32} cls="y-vship-art" alt={P2.shipName(rec, v.vrow)} /></button>
      <div className="y-vship-b">
        <div className="y-row"><button type="button" onClick={function () { openShip(rec.id); }} style={{ textAlign: 'left' }}><h3 className="y-ship-n">{String(P2.shipName(rec, v.vrow)).toUpperCase()}</h3></button>
          {o.bonded ? <P2.Stamp k="bond">MY SHIP</P2.Stamp> : null}{o.holder ? <P2.Stamp k="flag">SIGNATURE VEHICLE</P2.Stamp> : null}{o.party ? <P2.Stamp k="party">THE PARTY’S SHIP</P2.Stamp> : null}</div>
        <P2.HullType rec={rec} vrow={v.vrow} res={v.res} />
        <P2.Cond rec={rec} res={v.res} />
        <SheetHoldDoor rec={rec} res={v.res} onOpen={function () { if (onOpenYard) onOpenYard({ room: 'ship', id: rec.id, tab: 'trade' }); }} />
        {o.party ? <p className="y-mono" style={{ margin: 0 }}>KEPT BY THE GM · YOU READ IT AND USE YOUR SEAT</p> : (o.markable ? null : <P2.LastCrit rec={rec} reg={reg} />)}
      </div>
      {o.markable ? <div className="y-vbond"><button type="button" className="y-quiet" data-testid="vessel-mark" data-refusal={refusal ? refusal.kind : undefined}
          disabled={!!why} aria-disabled={why ? 'true' : undefined} onClick={function () { if (!why) mark(rec.id); }}>MARK AS MY SHIP</button>
        {/* D-259 (card f100-surfacing-yard-concept ADDENDUM 8 Q6 (b)): the button says it all — the approved record (Y1–Y3, HELD)
            draws no caption under it; the refusal stays, a rule sentence */}
        {why ? <p className="y-small y-refuse" data-testid="vessel-mark-refusal">{why}</p> : null}</div> : null}
      {o.holder ? SigBlock(v) : null}
      {seat ? <button type="button" className="y-vseat" data-open={isOpen ? '1' : undefined} aria-expanded={isOpen ? 'true' : 'false'} data-testid="vessel-seat" data-role={role}
          data-undrawn={SEAT_OPENS[role] ? undefined : '1'}
          onClick={function () { if (SEAT_OPENS[role]) setOpenSeat(isOpen ? null : rec.id); }}>
        <span className="y-seat">YOUR SEAT · <b>{P2.roleWord(role)}</b></span><span className="y-vseat-p"><span className="y-pool">{seat.el}</span>{v.res && v.res.held && P2.UpHeld ? <P2.UpHeld /> : null}<span className="y-mono">{seat.label}</span></span><span className="y-chev">›</span></button> : null}
      {isOpen ? SeatCard(v, role) : null}
    </article>;
  }
  var hasSig = !!(meCtx && meCtx.talents && meCtx.talents.signature_vehicle);
  var bondView = bond ? view(bond) : null;
  var bondSig = bondView && hasSig ? R.signatureOf(bond, bondView.vrow, { characters: rosterCtx }) : null;
  var second = !!(bondSig && bondSig.holder && bondSig.second.some(function (s) { return String(s.character) === charId; }));
  var bonded = second ? null : bondView;   // U19: a second holder's ship is not drawn as YOUR SHIP
  var holder = bonded && bonded.res && (bonded.res.reminders || []).some(function (r) { return r.source === 'Signature Vehicle' && String(r.character) === charId; });
  var rest = own.filter(function (r) { return !bonded || r.id !== bonded.rec.id; }).map(function (r) {
    var v = view(r); v.two = second && bond && r.id === bond.id ? bondSig.holder.character : null; return v; });
  var partyTwo = second && bond && bond.owner === 'party' ? bondSig.holder.character : null;
  var partyViews = party.map(view);
  var anyHeld = [bonded].concat(rest, partyViews).some(function (v) { return !!(v && v.res && v.res.held); });
  return <div className="yd yd-in" data-testid="sheet-vessels-s4" data-face={bonded ? 'V1' : 'V4-mark'} data-stale={stale ? '1' : undefined}>{hold([
    <React.Fragment key="face">
    {anyHeld && P.PartsWords ? <P.PartsWords err={attLoad.err} /> : null}
    {bonded ? [group('YOUR SHIP'), ShipCard(bonded, { bonded: true, holder: holder })] : null}
    {rest.length ? [group('YOUR SHIPS'), rest.map(function (v) { return ShipCard(v, { markable: true, two: v.two }); })] : null}
    {party.length ? [group('THE PARTY’S SHIP'), partyViews.map(function (v) { var r = v.rec; return ShipCard(v, { party: true, markable: hasSig && !(bonded && r.id === bonded.rec.id), two: partyTwo && r.id === bond.id ? partyTwo : null }); })]
      : (partyLine ? [group('THE PARTY’S SHIP'), <p className="y-logline" key="pl" data-testid="vessel-party-line"><i>THE PARTY’S SHIP</i> · The GM keeps it, and it isn’t on this device.</p>] : null)}
    {yardDoor}
    </React.Fragment>])}
  </div>;
}

// C-YE1: the Yard's VESSELS view is a jump HOME to the sheet's VESSELS tab. This transient intent is
// read once by CharacterSheet's pageState initializer at mount (self-clearing) — no state threading.
var _pendingSheetPage = null;


// ═══════════════════════════════════════════════════════════════════════════════════════════════
// SHIP YARD S4 · THE YARD'S HOST, PLAYER SIDE (ruling `shipyard-s4-ground` §6 JOB 2, chunk A; switch map P1–P5, P7,
// P13). The rooms are K6's (R-S4G-3): HANGAR is the shared `YardHangar` (lib/shared/vesselView.jsx, frames H1 H3 H5,
// and U1 behind the fleet door); CATALOGUE and the ENTRY are `YardCatalogue` (JOB 4), and SHIP is `YardShip`. The bars
// are the app's own (SuiteBar/BottomNav, paper face, in PlayerApp). It is the only Yard since the S5 flip (R-S5R-18).
// Rider 22 on this face: the owner is the character `swrpg_last_sheet_id` names ONLY when that character is
// on this device; the `char:player` fallback is retired here. With no such character the catalogue offers no
// add (it never saves to nobody) and the hangar shows its empty floor — the U6/U7 faces wait for Jason's word.
// SHIP YARD S4 · G-7 — THE STORE MOUNT WITH THE YARD'S SEAM (ruling `shipyard-s4-workbench-contracts` R-S4WC-1 A2–A4, B8;
// K2 and WB2 on Jason's approved pages). Mounted in place of the plain Store (K9's OFF branch, the plain Store with no seam,
// was retired at the S5 flip, R-S5R-18). The seam
// (`SWRPGVesselView.useCounterSeam`) plans the counter's ship lines from the registries FIT reads and this device's vehicle
// library. FINALIZE delivers the charged ones BEFORE the sheet's write: a delivery the fold refuses writes nothing and
// charges nothing (`false`); a sheet write that fails puts the ships back (`undo`, the same act).
function PlayerStoreYard({ storeProps }) {
  var ctrl = useVehicleLibraryController();
  var seam = window.SWRPGVesselView.useCounterSeam(true, {
    getVehicle: function (k) { return ctrl.getVehicleByKey(k); }, loading: !!ctrl.loading,
    now: function () { try { return new Date().toISOString().slice(0, 10); } catch (e) { return null; } },
  });
  var onFinalize = window.SWRPGVesselView.finalizeThrough(seam, storeProps.onFinalize);
  return React.createElement(window.SWRPGStore.Store, Object.assign({}, storeProps, { vessel: seam, onFinalize: onFinalize }));
}

function PlayerYardS4({ route, yardChar, savedChars, go, catalogueView, pickerOpen, onClosePicker, onPickScope, toCounter, writeCharacter, onNewCharacter, onPlanExit }) {
  var ctrl = useVehicleLibraryController();
  // the character vocabulary and registries (loaded here if this is the first surface): a PC's seat pool is the
  // sheet's (`yardCharPool`) and its talents are the resolver's context, and both read them
  useCanonicalReady();
  var VV = window.SWRPGVesselView, VS = window.SWRPGVessels, Y = window.SWRPGYard;
  var accent = YARD_CARD.accent;   // the player's Yard accent, from its one home (the landing card)
  var vesselState = VS ? VS.useVessels() : { v: 1, vessels: [] };
  var vesselsStale = !!(VS && VS.isStale());
  var staleBanner = vesselsStale ? h(window.SWRPGUI.StaleTabBanner, { accent: accent }) : null;
  var owner = yardChar ? 'char:' + yardChar.id : null;
  function applyVessels(fn) { if (VS) VS.apply(fn); }
  function nowStamp() { try { return new Date().toISOString().slice(0, 10); } catch (e) { return null; } }
  if (!VV || !VV.YardHangar || !VS) return null;
  var room = route.room;
  // U7: the scope picker, over whichever room the chip sits above (never over the ship's, whose chip is static)
  var layer = pickerOpen && VV.PickerLayer ? h(VV.PickerLayer, { roster: savedChars, owner: owner, portraitFor: window.SWRPGPortraits.charPortrait,
    title: 'WHOSE HANGAR', onPick: onPickScope, onClose: onClosePicker }) : null;
  if (room === 'hangar' || room === 'unit') {
    return h(React.Fragment, null, h(VV.YardHangar, {
      host: 'player', route: route, owner: owner, character: yardChar, roster: savedChars,
      ctx: yardResolveCtx(yardChar ? [yardChar] : []),
      getVehicle: function (k) { return ctrl.getVehicleByKey(k); },
      portraitFor: window.SWRPGPortraits.charPortrait,   // R-S4CO-7: the one character-portrait lookup both apps load (lib/shared/portraits.js)
      nav: go,
      charPool: yardCharPool,   // a PC's seat pool = the sheet's own (one path, PRAXIS §5)
    }), layer);
  }
  if (room === 'build') {
    // SHIP YARD S8 · BUILD A SHIP (card `shipyard-s8-build` R-S8B-9, ADDENDUM 12 Q1): the shared room (`YardBuildRoom`), the same
    // one the GM mounts; its add reads the owner as the catalogue entry's does (U6/U7)
    return h(React.Fragment, null, h(VV.YardBuildRoom, {
      host: 'player', route: route, nav: go,
      character: yardChar, roster: savedChars, portraitFor: window.SWRPGPortraits.charPortrait, onNewCharacter: onNewCharacter,
    }), layer);
  }
  if (room === 'catalogue' || room === 'entry') {
    // THE CATALOGUE (JOB 4, ruling `shipyard-s4-catalogue-card` order 4): the shared room, C1–C5 and E1–E3 (lib/shared/
    // vesselView.jsx `YardCatalogue`), the same component the GM mounts. U6: with no character on this device the add
    // door is MAKE A CHARACTER ›; with characters but none chosen it asks "Whose ship?" (U7's picker).
    return h(React.Fragment, null, h(VV.YardCatalogue, {
      host: 'player', route: route, ctrl: ctrl, urlBase: '/player/yard/catalogue', go: go,
      character: yardChar, roster: savedChars, portraitFor: window.SWRPGPortraits.charPortrait, onNewCharacter: onNewCharacter,
    }), layer);
  }
  if (room === 'plan') {
    // P1 · PLAN (ruling `shipyard-s4-catalogue-card` R-S4CC-2/3): FIT's window on a catalogue hull, writing nothing until
    // ACQUIRE WITH THIS FIT; its add is the entry's (U6/U7). The shared room (`YardPlan`), the same one the GM mounts.
    return h(React.Fragment, null, h(VV.YardPlan, {
      host: 'player', route: route, ctrl: ctrl, go: go, onExit: onPlanExit,
      ctx: yardResolveCtx(yardChar ? [yardChar] : []), charPool: yardCharPool,
      character: yardChar, roster: savedChars, portraitFor: window.SWRPGPortraits.charPortrait, onNewCharacter: onNewCharacter,
    }), layer);
  }
  if (room === 'ship') {
    // THE SHIP (JOB 3a, ruling `shipyard-s4-collection` §7 order 2): the shared room — the SHEET, its DECK room and the
    // WORKBENCH (lib/shared/vesselView.jsx `YardShip`), the same component the GM mounts; the owner is this character, so
    // U1's picture line shows on its own ship (U15). The interim sheet is retired here.
    return h(VV.YardShip, {
      host: 'player', route: route, owner: owner, roster: savedChars, loading: ctrl.loading,
      ctx: yardResolveCtx(yardChar ? [yardChar] : []),
      getVehicle: function (k) { return ctrl.getVehicleByKey(k); },
      portraitFor: window.SWRPGPortraits.charPortrait,
      nav: go,
      charPool: yardCharPool,
      toCounter: toCounter,   // G-7 · R-S4WC-2: FIT's TO THE COUNTER (the GM host hands none: its door waits on Jason's pick)
      // SHIP CARGO (card `shipyard-cargo-build` R-SCB-2): TRADE's LOAD and UNLOAD write the character through the SAME function the
      // store's FINALIZE persists through — one character write; the GM host hands none yet (R-SCW-3, its seam on paper first)
      writeCharacter: writeCharacter,
    });
  }
  return h(VV.YardNotFoundRoom, { what: 'page' });
}
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// THE PAPER SHEET — surface two on the document ground, DARK behind `swrpg_sheet_transplant`.
// Ruling `sheet-paper-concept` R-PSC-1..7; mockup `lab/holocron/sheet.html` @ lab `dffcd72b`
// (Jason 2026-09-15: "Approved"). Built in R-PSC-5's order, one board row per zone:
//   B-609 the ground, the switch, the shell   ← THIS ROW
//   B-610 the spine · B-611 the stage · B-612 the stack · B-613 THE PACK · B-614 ABILITIES ·
//   B-615 ADVANCEMENT + CRAFT · B-616 the phone · B-617 the walk · B-618 the flip
//
// ROW 1's SHAPE. The running head (the board's `sb-runhead`: the title, the name, the between-
// session verbs as quiet buttons, DICE as the commit, TABLE MODE as the toggle), the three zones —
// spine · stage · stack — and the stack's units with their heads always visible and ONE open. The
// zones MOUNT THE EXISTING COMPONENTS until their own row lands: (B-610 — the spine is PAPER:
// SheetSpine; B-611 — the stage is PAPER: SheetStage; B-612 — the stack is PAPER: SheetStack, with
// PROFILE and VESSELS as the last islands and the three rooms' doors opening the legacy faces).
// One component, arranged (AS-1) — so the dark sheet is WHOLE from the first row and every later
// row is a swap. The interim bodies sit on `.sb-legacy` islands (sheet.css) and carry
// `data-sheet-interim`, so what is left to transplant is a query.
//
// WHAT DOES NOT CHANGE. Every write still goes through `onSheetChange` (SHEET_SHAPE §7, the plumbing
// fence); the open unit is the same by-LABEL page state; the dice hub is the same overlay state.
// (N-post, lap paper-sheet-delete: the old face, its `pages` renderers and its islands are deleted.)
// ── CAREER-SKILL STATUS for a sheet (R-CSK1) — ONE function, hoisted from ADVANCEMENT's memo so the
// stage's CAREER mark and ADVANCEMENT's pricing read the same union: the career's skills ∪ every owned
// specialization's ∪ the player-chosen spec skills, as SHEET keys. `data` is the canonical charbuild.
// Returns null while the registries are loading, [] for a career the registry does not know.
function sheetCareerSkillKeys(sheet, data) {
  if (!data || (!sheet.careerId && !sheet.career)) return null;
  var career = data.careers[CharResolve.sheetCareerId(sheet, data.careers)] || null;   // B-659: the one resolver
  if (!career) return [];
  // R-CSK1 → F-100 M7 C2 (R-M7K-7): the status is THE shared answer, CharResolve.careerSkillStatus — the career's
  // skills ∪ every owned specialization's (B-29's healed specs included) ∪ their chosen skills (B-40) ∪ every owned
  // talent's choice picks (B-181) and fixed grants (C1) ∪ the species' `as_career` (B-1034). STATUS only — no free
  // ranks (eote p.93). This wrapper keeps the sheet's loading (null) and unknown-career ([]) answers.
  return Object.keys(CharResolve.careerSkillStatus(sheet, data));
}

// The CREATOR's career-status ids ({ canonicalId: true }) from its in-progress state — the same one answer
// (CharResolve.careerSkillStatus, R-M7K-7) asked about a sheet-shaped view of what the creator holds. `specId` is the
// specialization whose OWN skills count (the creation rule: the chosen one); talents count from every tree they sit in.
function creatorCareerStatusIds(cb, careerData, specId, st) {
  if (!cb) return {};
  st = st || {};
  var view = {
    careerId: careerData ? careerData.id : null,
    speciesId: st.speciesId || null, speciesOptionPicks: st.speciesOptionPicks || {},
    specializations: CharResolve.specsFromCreatorState(st.talents || {}, specId ? [specId] : []),
    specSkillChoices: st.specSkillChoices || {}, talentSkillChoices: st.talentSkillChoices || {},
    // the species' career pick (the Cerean's Intellectuals, R-CI-3): the committed map, so a rank bought later prices as career
    speciesSkillChoices: st.speciesSkillChoices || {},
    // R6 lane D (ruling release-r6-bases-registry Q6 (c)): the draft's own id — a re-entered member reads its group's
    // career skills; a new character's draft id is no group's member, so it reads none
    id: st.id || null,
  };
  return CharResolve.careerSkillIds(CharResolve.careerSkillStatus(view, cb, { specIds: specId ? [specId] : [] }));
}

// R-SSC-1 (ruling f100-surfacing-sheet-concept; D-230 Q1 (a)) · THE ONE FORMATTER of a CharResolve.derivedBecause row, for the
// spine, the trackers and the PDF: "<from> → <to>[ <condition>] — <source>" ("5 → 6 — Enduring", "6 → 7 while wearing armour —
// Armor Master"); a floor row (source null) reads "<from> → <to>" (its tail is cut, copy-voice-sweep player/app.jsx:29714:27, D-289). `arrow` is
// "→" on screen and "to" in the PDF, whose font has no arrow (D-SSF2).
function becauseNoteText(row, arrow) {
  if (!row) return '';
  return row.from + ' ' + (arrow || '→') + ' ' + row.to + (row.condition ? ' ' + row.condition : '') +
    (row.source == null ? '' : ' — ' + row.source);
}

// ─── B-610 · THE SPINE — the instrument (SHEET_SHAPE §3.2), on paper ─────────────────────────────
// Top to bottom by at-table frequency, exactly as the board draws it: the identity plate (portrait on
// the mat, the designation with its rule, species · career · specialization · line), the six-capsule
// rail (the creator's own `.hp-rail` kit — full words, ban item 8), the vitals ledger — the two live
// trackers first, then soak, both defences, Force rating when it applies, and ENCUMBRANCE AS A VITAL
// with production's own OVER BY sentence past the line — and the critical-injuries plate. Every number
// is the shared core's (`computeDerived`, `computeEncumbrance`), never re-derived here. The instrument
// never shrinks (sheet.css: `.sb-instr{flex:none}`) and never scrolls; below wide it pins.
function SheetSpine(p) {
  var sheet = p.sheet || {}, accent = p.accent, derived = p.derived || {}, equipment = p.equipment || {};
  var chars = sheet.characteristics || {};
  var enc = CharResolve.computeEncumbrance(equipment, chars, CharResolve.speciesEncumbranceDelta(sheet)) || { carried: 0, limit: 0, over: 0, setbacks: 0 };
  // THE FORCE SWITCH (ruling `f100-surfacing-force-flip-card` ADDENDUM 9 E-FFL-4; ADDENDUM 8 F7 C-MU-3, a number in an existing
  // cell): the rail's printed bead reads the characteristic WITH the session's committed Controls (`p.committed`, never saved)
  // through CharResolve's one reader. The encumbrance and the vitals stay on the saved map (ADDENDUM 12 F2 (ii): a board row).
  var beadChars = p.committed != null ? CharResolve.characteristicsWithCommits(sheet, CharResolve.talentViewFor(sheet) || window._canonicalCharbuild, p.committed) : chars;
  var curWounds = sheet.currentWounds != null ? sheet.currentWounds : 0;
  var curStrain = sheet.currentStrain != null ? sheet.currentStrain : 0;
  var woundMax = derived.woundThreshold || 0, strainMax = derived.strainThreshold || 0;
  var woundsOver = woundMax > 0 && (Number(sheet.currentWounds) || 0) > woundMax;
  var crits = Array.isArray(sheet.criticalInjuries) ? sheet.criticalInjuries : [];
  function patch(u) { if (p.onSheetChange) p.onSheetChange(u); }
  var book = sheet.book ? (BOOK_LABELS[sheet.book] || sheet.book) : '';
  // F-100 M8 · Jury Rigged on the vitals (frame J6): a talent's change to the worn armour's defence, named in the defence's
  // why-sheet before the rows it chains into (m8ItemChanges reads the display's own `_talentEffects`; an unworn suit carries none)
  var defNotes = function (side) { return m8ItemChanges(equipment.armor, ['defense_delta']).filter(function (f) { return f.target === side; })
    .map(function (f) { return m8Cap(side) + ' ' + f.from + ' → ' + f.to + ' — ' + f.source; }); };
  // R-SSC-1′ (D-248, D-257; ADDENDUM 24 (a)–(c), (f)): every derived number ALONE, with ONE mark when the rules moved it, and
  // the row is the press — the reasons (CharResolve.derivedBecause: the base's parts and the rows of the one walk that printed
  // the number; never saved) open in the key's why-sheet. Each row shows when its reader answers (Q1 (a)): the engine-dark rows
  // (MV3, `when` thresholds, career) are absent with the key OFF, and so is their mark.
  var because = (CharResolve.derivedBecause && sheet.characteristics) ? CharResolve.derivedBecause(sheet, equipment, window._canonicalCharbuild) : {};
  // B-1395: an `item` row (Jury Rigged's step on the suit) is told by defNotes above in J6's words, so it is not told twice
  var becauseOf = function (k) { return (because[k] || []).filter(function (r) { return !r.item; }).map(function (r) { return becauseNoteText(r, '→'); }); };
  var baseOf = because._base || {}, partsOf = because._baseParts || {};
  var _why = React.useState(null), why = _why[0], setWhy = _why[1];   // { track, el } — the key whose why-sheet is open
  var closeWhy = React.useCallback(function () { setWhy(null); }, []);
  var openWhy = function (track) { return function (el) { setWhy({ track: track, el: el }); }; };
  var over = Number(enc.over) || 0;
  var handheld = p.band === 'mobile';
  var dirOf = function (key, total) { return sheetWhyDir(total, baseOf[key]); };
  // one key row: `keyEl` its key column, `v` its value, `total` the number the mark compares with the base, `label` the press's
  var row = function (track, key, total, keyEl, v, label, state) {
    var dir = dirOf(key, total);
    return (
      <div className="sb-vitrow" data-track={track} data-state={state || undefined} {...sheetWhyPress(sheetWhyLabel(label[0], label[1], dir), openWhy(track), !!why && why.track === track)} style={SHEET_WHY_PRESS_STYLE}>
        {keyEl}
        <div className="sb-vitval">{v}<SheetWhyMark dir={dir} /></div>
      </div>);
  };
  var keyRow = function (track, name, key) { var n = derived[key] || 0; return row(track, key, n, <div className="sb-vitkey">{name}</div>, n, [name, n]); };
  var derivedRows = (
    <React.Fragment>
            {keyRow('soak', 'Soak', 'soak')}
            {keyRow('ranged-defence', 'Ranged Defence', 'rangedDefense')}
            {keyRow('melee-defence', 'Melee Defence', 'meleeDefense')}
            {(derived.forceRating || 0) > 0 ? keyRow('force-rating', 'Force Rating', 'forceRating') : null}
            {row('encumbrance', 'encumbranceThreshold', enc.limit,
              over > 0
                ? <div><div className="sb-vitkey">Encumbrance</div>
                    <div className="sb-vitover">{'OVER BY ' + over + ' — add ' + (enc.setbacks || over) + ' setback ' + ((enc.setbacks || over) === 1 ? 'die' : 'dice') + ' to all Brawn and Agility checks'}   {/* production's own sentence (:26896) */}</div></div>
                : <div className="sb-vitkey">Encumbrance</div>,
              <React.Fragment>{enc.carried}<span className="sb-of"> / {enc.limit}</span></React.Fragment>, ['Encumbrance', enc.carried + ' / ' + enc.limit], over > 0 ? 'over' : null)}
    </React.Fragment>
  );
  // the key's why-sheet (the Encumbrance key opens the ONE Encumbrance sheet GEAR's entry opens, Q-D (a))
  var SPINE_WHY = { soak: ['Soak', 'soak', 'sheet-why-key'], 'ranged-defence': ['Ranged Defence', 'rangedDefense', 'sheet-why-key'],
    'melee-defence': ['Melee Defence', 'meleeDefense', 'sheet-why-key'], 'force-rating': ['Force Rating', 'forceRating', 'sheet-why-key'],
    wounds: ['Wound threshold', 'woundThreshold', 'sheet-why-threshold'], strain: ['Strain threshold', 'strainThreshold', 'sheet-why-threshold'] };
  var whySheet = !why ? null : why.track === 'encumbrance'
    ? <SheetEncumbranceWhy sheet={sheet} equipment={equipment} enc={enc} anchor={why.el} onClose={closeWhy} />
    : (function () {
      var w = SPINE_WHY[why.track]; if (!w) return null;
      var rows = (why.track === 'ranged-defence' ? defNotes('ranged') : why.track === 'melee-defence' ? defNotes('melee') : []).concat(becauseOf(w[1]));
      var total = w[1] === 'woundThreshold' ? woundMax : w[1] === 'strainThreshold' ? strainMax : (derived[w[1]] || 0);
      return (
        <M7PaperSheet anchor={why.el} title={w[0]} meta={sheet.name || ''} onClose={closeWhy} kind="why" testid={w[2]}>
          <SheetWhyBody whyKey={why.track} parts={partsOf[w[1]]} rows={rows} total={'Total ' + total} />
        </M7PaperSheet>);
    })();
  return (
    <React.Fragment>
      <div className="sb-instr">
        <div className="sb-ident">
          <div className="hp-artplate">
            {p.portraitSrc
              ? <div className="hp-artplate-in" style={{ backgroundImage: "url('" + p.portraitSrc + "')", backgroundSize: 'cover', backgroundPosition: 'center top', aspectRatio: '3 / 4' }} />
              : <div className="hp-artplate-in" data-empty="1" style={{ aspectRatio: '3 / 4' }} />}
          </div>
          <div className="sb-ident-b">
            <h2 className="sb-desig" data-testid="sheet-desig">{sheet.name || ''}</h2>
            <div className="sb-sub"><b>{sheet.species || ''}</b><br />{sheet.career || ''}{sheet.specialization ? <React.Fragment> · <b>{sheet.specialization}</b></React.Fragment> : null}
              {book ? <React.Fragment><br /><span className="sb-line">{book}</span></React.Fragment> : null}</div>
          </div>
        </div>
        {/* B-656 · THE SPECIES ASK, WITH THE IDENTITY (lab frame `species_ask()` composed in `spine()`):
            an unanswered species option is a fact about WHO THE CHARACTER IS, so the notice goes where its
            consequence lands — under the plate that names the species, before the instrument's numbers. */}
        <SpeciesAskSurface char={p.char} sheet={sheet} onSheetChange={p.onSheetChange} onEditInCreator={p.onEditInCreator} />
        <div className="hp-railwrap"><div className="hp-plate" data-pad="snug">
          <div className="hp-rail" data-testid="sheet-rail">
            {FC_CHAR_ORDER.map(function (a) {
              return (
                <div className="hp-char" key={a} data-char={a}>
                  <span className="hp-char-val"><span className="hp-char-bead">{beadChars[a] || 0}</span></span>
                  <i className="hp-char-lab">{FC_CHAR_FULL[a]}</i>
                </div>);
            })}
          </div>
        </div></div>
        <div className="hp-plate" data-pad="data">
          <div className="sb-vit" data-testid="sheet-vitals">
            <PlayerTrackerBox label="WOUNDS" threshold={woundMax} current={curWounds} accent={accent} whyDir={dirOf('woundThreshold', woundMax)} onWhy={openWhy('wounds')} whyOpen={!!why && why.track === 'wounds'} onChange={function (v) { patch({ currentWounds: v }); }} />
            <PlayerTrackerBox label="STRAIN" threshold={strainMax} current={curStrain} accent={accent} whyDir={dirOf('strainThreshold', strainMax)} onWhy={openWhy('strain')} whyOpen={!!why && why.track === 'strain'} onChange={function (v) { patch({ currentStrain: v }); }} />
            {handheld ? null : derivedRows}
          </div>
        </div>
      </div>
      {/* B-616 (Jason 2026-09-16, "as recommended"): on a HANDHELD the pin is the four things the two-second
          test demands — identity, the rail, wounds, strain — and the derived rows are a second plate that scrolls
          with the page. Above handheld the one plate the mockup draws. Same rows, one definition (`derivedRows`). */}
      {handheld ? <div className="hp-plate" data-pad="data"><div className="sb-vit" data-testid="sheet-vitals-derived">{derivedRows}</div></div> : null}
      <div className="hp-plate" data-pad="bleed">
        <div className="hp-head" data-static="1"><span className="hp-name">Critical Injuries</span><span className="hp-count">{crits.length}</span></div>
        <SheetCrits sheet={sheet} accent={accent} onSheetChange={p.onSheetChange} woundsOver={woundsOver} equipment={equipment} openHub={p.openHub} />
      </div>
      {whySheet}
    </React.Fragment>
  );
}


// ─── B-611 · THE STAGE — the skills (SHEET_SHAPE §3.3, amended 2026-09-15; R-PSC-2, R-PSC-6) ───────
// EVERY SKILL, NO TABS: the four registry groups — general · social · combat · knowledge, read from the
// canonical charbuild's `skillGroups` through the id → sheet-key bridge (`skillSheetKeys`), the same
// vocabulary the creator's pickers and the core's pool enums use — under sticky heads, a filter where
// the segmented control stood, ONE row per skill on one line: the name, its characteristic (full word,
// ban item 8) and CAREER when the status union says so (`sheetCareerSkillKeys`), five rank boxes, the
// assembled pool (the dice module's own `renderPool`, the creator's `span.hp-sym` per die — proficiency
// and ability over the live characteristic and rank; gear's boost and setback ride the hub's strip, as
// the board drew the row), and ROLL, which opens the DICE HUB on that skill (R-PSC-2: no in-place roll).
// Two-up when the STAGE is at least 800px wide — a container query on the stage in sheet.css, so the
// pane decides, not the window. Rows never stretch; content top-aligns.
// ─── F-100 M7 · W1/W2 — THE WORDS FOR "WHY CAREER" AND "WHAT IT ROLLS" (ruling f100-m7-build R-M7G-18..20, -24, -35)──────
// One reading of the two shared answers (CharResolve.careerSkillStatus, sheetSkillCharacteristic) into the approved
// frames' words (Docs/mockups/f100-m7-correct.html: W1/W2 @ 0e35174f, D-10; the added states @ ed98f936, D-13). Pure.
function m7CapChar(c) { return c ? c.charAt(0).toUpperCase() + c.slice(1) : ''; }
function m7TalentName(cb, because) {
  // F-100 M9 (R-M9S-1): a species ability's swap names its OWNER, 'species:<id>:<slug>' — DARK (only a key-ON read makes one)
  if (/^species:/.test(String(because || ''))) { var so = CharResolve.speciesAbilityOwner(because, cb); return (so && so.name) || ''; }
  var id = String(because || '').split(':')[1] || '';
  var t = cb && cb.talents ? cb.talents[id] : null;
  return (t && t.name) || id;
}
// W2 (R-M7G-20): " (Well Rounded)" — only when EVERY source of the career status is a talent (a fixed grant or a pick),
// several joined ", "; '' otherwise (the row reads "Rank N · Career" exactly as today).
function m7CareerSuffix(entry, cb) {
  var because = (entry && entry.because) || [];
  if (!because.length || because.some(function (b) { return b.indexOf('talent:') !== 0; })) return '';
  return ' (' + because.map(function (b) { return m7TalentName(cb, b); }).join(', ') + ')';
}
// W1's career line (R-M7G-18, approved by D-13 in frames v5 @ ed98f936): "Career · from X", the sources listed career →
// specializations → talents → species and joined "A and B" / "A, B and C"; a specialization's chosen skill, a talent
// with choice grants and the species' career pick (D-CI1 (a)) carry "(your pick)". Not career: "Not a career skill".
var M7_SOURCE_RANK = { career: 0, spec: 1, 'spec-choice': 1, talent: 2, species: 3, 'species-choice': 3 };
function m7JoinAnd(a) { return a.length < 2 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]; }
function m7CareerLine(entry, cb, sheet) {
  if (!entry || !entry.career) return 'Not a career skill';
  var kind = function (b) { var i = b.indexOf(':'); return i < 0 ? b : b.slice(0, i); };
  var parts = (entry.because || []).map(function (b, i) { return { b: b, i: i }; })
    .sort(function (x, y) { return ((M7_SOURCE_RANK[kind(x.b)] != null ? M7_SOURCE_RANK[kind(x.b)] : 9) - (M7_SOURCE_RANK[kind(y.b)] != null ? M7_SOURCE_RANK[kind(y.b)] : 9)) || x.i - y.i; })
    .map(function (x) {
      var k = kind(x.b), id = x.b.slice(k.length + 1);
      if (k === 'career') {
        var cid = window.CharResolve && cb && cb.careers ? CharResolve.sheetCareerId(sheet || {}, cb.careers) : null;
        return (cid && cb.careers[cid] && cb.careers[cid].name) || (sheet && sheet.career) || 'your career';
      }
      if (k === 'spec' || k === 'spec-choice') {
        var sp = cb && cb.specializations ? cb.specializations[id] : null;
        return ((sp && sp.name) || id) + (k === 'spec-choice' ? ' (your pick)' : '');
      }
      if (k === 'talent') {
        var t = cb && cb.talents ? cb.talents[id] : null;
        return ((t && t.name) || id) + ((t && window.CharResolve && (CharResolve.talentChoiceGrantsOf(t) || []).length) ? ' (your pick)' : '');
      }
      if (k === 'species') { var sx = cb && cb.species ? cb.species[id] : null; return (sx && sx.name) || id; }
      // the species' career pick (D-CI1 (a)): the group as the creator names it (the re-stamped ability) + "(your pick)"
      if (k === 'species-choice') {
        var sc = cb && cb.species ? cb.species[id] : null;
        var scg = (sc && window.CharResolve && CharResolve.speciesCareerPickGroups) ? CharResolve.speciesCareerPickGroups(sc, cb) : [];
        return ((scg[0] && scg[0].name) || (sc && sc.name) || id) + ' (your pick)';
      }
      // B-1039 (ruling release-r7-bug-burndown ADDENDUM 314 C7): an owned item's grant names the item, its registry name
      if (k === 'item' || k === 'group-item') {
        var io = window.CharResolve && CharResolve.itemCareerSkillOwner ? CharResolve.itemCareerSkillOwner(x.b, sheet, cb) : null;
        return (io && io.name) || id;
      }
      return id || k;
    });
  return 'Career · from ' + m7JoinAnd(parts);
}
// W1's characteristic line, from skillCharacteristic's answer: "Rolls with Brawn" · "Rolls with Agility · Renegade Form,
// instead of Brawn".
function m7RollsLine(sc, cb) {
  if (!sc) return '';
  // R-M7G-24 (D-13): an owned swap with no pick rolls the base, and says so
  if ((sc.unpicked || []).length && sc.characteristic === sc.base)
    return 'Rolls with ' + m7CapChar(sc.base) + ' · ' + m7TalentName(cb, sc.unpicked[0].because) + ' has no characteristic chosen yet';
  if (sc.because === 'base' || !sc.because) return 'Rolls with ' + m7CapChar(sc.base);
  return 'Rolls with ' + m7CapChar(sc.characteristic) + ' · ' + m7TalentName(cb, sc.because) + ', instead of ' + m7CapChar(sc.base);
}
// R-SSC-2 (ruling f100-surfacing-sheet-concept; frames S2; D-SSF1) · a skill's why-sheet beyond its two lines: what the engine
// APPLIES on a check of this skill, one sentence each, and what it OFFERS (a row with a condition, a cost or a limit) in
// offerOf's words — with NO control (D-SSF1: the tick lives on the hub). Read from the cores the hub reads, never re-derived:
// the pool path's change records (collectPoolMods → withPoolMods → appliedLineText), the owner's damage roll numbers at the
// check (rollNumbersAt; a species owner named with its species, M9 F4 — diceHubRollOwnerName), MV3's in-use rank
// (skillRankInUse, F4's "rolls as rank 3 (printed 2) — Repulsor-Assisted Lifting") and the pool path's reminders.
// → { applied: [sentence], offered: [{ name, text, tags }] }. Each reader answers only with its key ON (Q1 (a)).
function whySheetLines(sheet, key, cb) {
  var P = window.SWRPGPoolMods, UI = window.SWRPGUI || {}, idx = poolModSkillIndex(), out = { applied: [], offered: [], appliedInfo: [] };
  if (!P || !idx || !key || !sheet) return out;
  // B-1451 (D-406 item 14): a line's (i) — `appliedInfo[i]` beside `applied[i]` (the lines stay strings), `info` on an offered one
  var infoOf = function (tid, rid, t, item) { return UI.offerInfoOf ? UI.offerInfoOf(tid, rid, item, t) : null; };   // B-1590: an item's too
  var sc = sheetSkillCharacteristic(sheet, key), prop = (sc && sc.characteristic) || undefined;
  var ctx = P.contextFor(key, idx, prop);
  if (!ctx) return out;
  var eq = resolveSheetEquipment(sheet.equipment, sheet), mods = collectPoolMods(eq), rank = sheetSkillRank(sheet.skills, key);
  // the RANK this check rolls (MV3, R-M12B-60): an in-use item's skill_rank_delta, named by its attachment
  var inUse = CharResolve.skillRankInUse ? CharResolve.skillRankInUse(eq, cb, key, rank) : rank;
  if (inUse > rank) {
    var keys = (cb && cb.skillSheetKeys) || {};
    var who = CharResolve.itemCharacterEffects(eq).filter(function (x) { return x.effect.op === 'skill_rank_delta' && (x.effect.key === key || keys[x.effect.key] === key); })
      .map(function (x) { return x.owner.source || x.owner.name; }).filter(function (n, i, a) { return n && a.indexOf(n) === i; });
    out.applied.push('rolls as rank ' + inUse + ' (printed ' + rank + ') — ' + who.join(', '));
  }
  // the pool path's applied dice (an Average check, the hub's skill-row default, so a difficulty change has a die to move)
  var spec = withPoolMods({ characteristic: Number((sheet.characteristics || {})[prop]) || 0, skillRank: rank, difficulty: 2 }, mods, key, prop);
  var talA = UI.offerTalentsFor ? UI.offerTalentsFor(sheet, (spec && spec._poolModChanges) || []) : null;
  ((spec && spec._poolModChanges) || []).forEach(function (c) {
    if (UI.appliedLineText) { out.applied.push(UI.appliedLineText(c)); out.appliedInfo[out.applied.length - 1] = infoOf(c.talentId, c.rowId, talA, c.item); } });
  // the owner's damage rows at this check (a weapon of this skill adds them to one hit; the WEAPONS card names its own)
  if (P.rollNumbersAt) {
    var pickFor = function (tid, pid) { var v = CharResolve.pickOf ? CharResolve.pickOf(sheet, '_talent', tid, pid) : undefined; return v == null ? null : v; };
    var dAp = P.rollNumbersAt(collectRollNumbers(sheet), 'damage_delta', { ctx: ctx, band: '', sheet: sheet, pickFor: pickFor }).applied;
    var talD = UI.offerTalentsFor ? UI.offerTalentsFor(sheet, dAp) : talA;   // B-1451: a species owner's (i) resolves through its own view
    dAp.forEach(function (l) {
      if (l.amount) { out.applied.push(diceHubDamageLineText(Object.assign({}, l, { source: diceHubRollOwnerName(l, cb) }), null)); out.appliedInfo[out.applied.length - 1] = infoOf(l.talentId, l.rowId, talD); }
    });
  }
  var rem = P.reminders(mods, ctx), tal = UI.offerTalentsFor ? UI.offerTalentsFor(sheet, rem) : null;   // the ONE owner's lookup (R-SYC-3): one sentence per offer
  if (UI.offerOf) rem.forEach(function (m) { out.offered.push(Object.assign(UI.offerOf(m, tal), { info: infoOf(m.talentId, m.id, tal, m.item) })); });
  // B-1355 (D-406 item 16): the offered ROLL NUMBERS the hub lists as offer rows at this check (a damage row, a species crit
  // SET), word for word as diceHubOfferEls writes them — the owner's name, offerOf's words with the condition, the cost tags
  if (P.rollNumbersAt && UI.offerOf) {
    var pickR = function (tid, pid) { var v = CharResolve.pickOf ? CharResolve.pickOf(sheet, '_talent', tid, pid) : undefined; return v == null ? null : v; };
    var ownedR = collectRollNumbers(sheet), rOff = [], seenR = {};
    // the check itself, then each hub weapon row that rolls this skill (its record: the claws' SET is anchored to `unarmed`)
    var ctxs = [ctx].concat(diceHubWeaponsWithUnarmed(eq.weapons || [], sheet).filter(function (w) {
      return w && w.regId && resolveWeaponSkillKey(w.skillKey || w.skill || '') === key; }).map(function (w) { return Object.assign({}, ctx, { weapon: w.regId }); }));
    ctxs.forEach(function (c) {
      var ropt = { ctx: c, band: '', sheet: sheet, pickFor: pickR };
      P.rollNumbersAt(ownedR, 'damage_delta', ropt).offered.concat(P.rollNumbersAt(ownedR, 'crit_rating_set', ropt).offered).forEach(function (l) {
        var k = l.talentId + ':' + l.rowId; if (seenR[k]) return; seenR[k] = true; rOff.push(l); });
    });
    var talR = UI.offerTalentsFor ? UI.offerTalentsFor(sheet, rOff) : tal;
    rOff.forEach(function (l) {
      if (l.tickedBy) return;   // a number riding a target tick is that tick's, as on the hub
      out.offered.push({ name: diceHubRollOwnerName(l, cb), tags: UI.offerTags ? UI.offerTags(l.row || {}) : [], info: infoOf(l.talentId, l.rowId, talR),
        text: UI.offerOf({ op: l.op, amount: l.amount, talentId: l.talentId, condition: (l.row || {}).condition, source: l.source }, talR).text });
    });
  }
  return out;
}
// W1's meta line: "Combat skill · rank 1" — the registry's group for the skill.
function m7SkillMeta(key, rank, cb) {
  var id = null, keys = (cb && cb.skillSheetKeys) || {};
  for (var k in keys) if (keys[k] === key) { id = k; break; }
  var g = (cb && cb.skillGroups && id && cb.skillGroups[id]) || skillCategoryOf(key).toLowerCase();
  return m7CapChar(g) + ' skill · rank ' + rank;
}

// ─── F-100 M7 · THE PAPER TAP SHEET (ruling f100-m7-build R-M7G-27: one local popover, the frames' geometry) ────────
// The maker's `M7.sheet` (scripts/f100_m7_frames.py KIT), built: below 1024 px the tree's paper bottom sheet (a scrim,
// the raised paper rising from the foot, the action rule along its top); at 1024 and up a 420 px panel beside its
// anchor — below it, so the thing it explains stays in view, above it when it would not fit below. ✕ and Escape close it
// (capture phase, one press one layer — B-544's rule), the scrim too below 1024; focus goes to ✕ on open and back to
// the anchor on close (R-M7G-19). Portalled to document.body, as the tree's sheet is (B-557).
// ruling release-r6-companions-lifts Q2 (a): the sheet, its two styles and the why-sheet below live in lib/shared/ui.jsx; these
// are its names (R-M7G-27's "not a shared module" line retired there).
var M7_LINE_STYLE = window.SWRPGUI.M7_LINE_STYLE, M7_META_STYLE = window.SWRPGUI.M7_META_STYLE;
// R-M8B-17 (card #31, D-47): the PICKER's meta line and its note at the 11 px floor, in every picker mode (M7's and M8's) —
// the approved drawing lifts only what reads under 11 px, so a wider --fs-2xs stands. The shared token and every other
// M7PaperSheet (the skill tap sheet) keep M7_META_STYLE.
var M7_PICK_META_STYLE = Object.assign({}, M7_META_STYLE, { fontSize: 'max(11px, var(--fs-2xs))' });
var M7PaperSheet = window.SWRPGUI.M7PaperSheet;

// ─── THE ONE-NUMBER SHEET (D-248, D-257 · ruling f100-surfacing-sheet-concept ADDENDUM 24/27, R-SSC-1′ (a)–(f); the record
// Docs/mockups/f100-surfacing-sheet-redraw.html @ 6e95cbfd2) ────────────────────────────────────────────────────────────────
// Every derived number stands alone; when the rules moved it, ONE mark after it, ↑ raised / ↓ lowered (D-256 Q1 (a)) — the sign
// of the printed number less its base (Q-B3 (a): rows that net to 0, or a capped 4 → 4, mark nothing). Pressing the number opens
// the skill names' own sheet (M7PaperSheet) with what the number is made of (derivedBecause's `_baseParts`), "What changes it"
// and its rows in becauseNoteText's words, and the total; no rows → parts and total; no parts → the total alone (D-256).
// ruling release-r6-companions-lifts Q2 (a): the mark, the press and the body live in lib/shared/ui.jsx (one definition each,
// both hosts); these are its names.
var sheetWhyDir = window.SWRPGUI.sheetWhyDir, SheetWhyMark = window.SWRPGUI.SheetWhyMark, sheetWhyNet = window.SWRPGUI.sheetWhyNet,
    sheetWhyLabel = window.SWRPGUI.sheetWhyLabel, sheetWhyPress = window.SWRPGUI.sheetWhyPress,
    SHEET_WHY_PRESS_STYLE = window.SWRPGUI.SHEET_WHY_PRESS_STYLE, SheetWhyBody = window.SWRPGUI.SheetWhyBody;
// THE ENCUMBRANCE SHEET — ONE sheet, opened from the spine's Encumbrance key and from GEAR's one entry (Q-D (a)): what is carried
// (computeEncumbrance's own count, the Container row), the limit's parts, its rows (derivedBecause) and the items' changes (Jury
// Rigged's line, worn armour counting 3 less), "Total <limit>". The "Over by" sentence stays inline where it was (R-SSC-1′ (e)).
// `enc` is the CALLER's own computeEncumbrance answer (the spine's, the unit's) — the sheet never counts a second time.
function SheetEncumbranceWhy(p) {
  var sheet = p.sheet || {}, equipment = p.equipment || {}, enc = p.enc || {};
  var because = (CharResolve.derivedBecause && sheet.characteristics) ? CharResolve.derivedBecause(sheet, equipment, window._canonicalCharbuild) : {};
  var armor = equipment.armor || null;
  var carriedW = (equipment.weapons || []).filter(function (w) { return w && w.state !== 'stowed'; });
  var carriedG = (equipment.gear || []).filter(function (g) { return g && g.state !== 'stowed'; });
  var lines = [];
  // D-406 item 6 (B-1396, B-706 (1); Docs/mockups/release-r7-small-clusters-look.html L1 @ 4ec53a8b3): the Carried lines add up
  // to "carrying N" — each line's number is what computeEncumbrance charges for it (worn armour named "worn, n − 3 = c", a
  // stack its qty × each), stowed armour charges nothing and has no line, and the container's +N is told once, under the Limit.
  carriedW.forEach(function (w) { lines.push('Weapon · ' + w.name + ' ' + (w.encumbrance || 0)); });
  if (armor && armor.state !== 'stowed') {
    var aEnc = Number(armor.encumbrance) || 0;
    lines.push('Armour · ' + armor.name + ' ' + (armor.state === 'worn' ? aEnc + ' — worn, ' + aEnc + ' − 3 = ' + Math.max(0, aEnc - 3) : aEnc));
  }
  carriedG.forEach(function (g) {
    var qty = Number(g.qty) || 1;
    lines.push('Gear · ' + g.name + (qty > 1 ? ' ×' + qty : '') + ' ' + (Number(g.encumbrance) || 0) * qty); });
  var itemRows = [].concat(carriedW, armor ? [armor] : [], carriedG).reduce(function (out, it) {
    return out.concat(m8ItemChanges(it, ['encumbrance_delta']).map(function (f) {
      var worn = it === armor && armor.state === 'worn';
      return it.name + ' ' + f.from + ' → ' + f.to + ' — ' + f.source + (worn ? ' (worn, it counts ' + Math.max(0, f.to - 3) + ', not ' + Math.max(0, f.from - 3) + ')' : ''); })); }, []);
  var rows = (because.encumbranceThreshold || []).map(function (r) { return becauseNoteText(r, '→'); }).concat(itemRows);
  return (
    <M7PaperSheet anchor={p.anchor} title="Encumbrance" meta={(sheet.name || '') + ' · carrying ' + (enc.carried || 0) + ' of ' + (enc.limit || 0)} onClose={p.onClose} kind="why" testid="sheet-why-encumbrance">
      <SheetWhyBody whyKey="encumbrance" before={[{ head: 'Carried', key: 'carried', lines: lines.length ? lines : ['Nothing carried.'] }]} partsHead="Limit"
        parts={(because._baseParts || {}).encumbranceThreshold} rows={rows} total={'Total ' + (enc.limit || 0)} />
    </M7PaperSheet>);
}

// ─── F-100 M7 · W4 — THE ONE CHARACTERISTIC PICKER (ruling f100-m7-contracts R-M7K-37; f100-m7-build R-M7G-22..24;
// W4 @ 0e35174f, D-10; the added states @ ed98f936, D-13) ─────────────────────────────────────────────────────────────
// `pick` = { mode: 'buy' | 'place' | 'chosen', talentKey, specKey, row, col, cost, anchor, receipt }.
//   'buy'    — a `to: chosen` swap (Renegade Form): the six in two columns, the swap's own `from` disabled with its
//              reason, and Cancel (nothing is bought until one is chosen). Dedication: "Dedication · raise one
//              characteristic by 1", each button "Agility 3 → 4", one at 6 disabled with "Brawn is at 6, the most it
//              can be" (R-M7G-22). Its old dark dialog is retired.
//   'place'  — an owned swap with no pick, chosen in place with the same words and no XP (R-M7G-24).
//   'chosen' — the owned cell's receipt beside the cell (1440; the phone's tree sheet carries it, R-M7G-23).
var M7_CHAR_ORDER = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
function m7ChosenSwapOf(trec) {
  return ((trec && trec.effects) || []).filter(function (e) { return e && e.op === 'skill_characteristic' && e.to === 'chosen'; })[0] || null;
}
function m7TalentCellMeta(trec, row, cost, owned) {
  return ((trec && trec.force_talent) ? 'Force talent · ' : 'Talent · ') + (((window.SWRPGUI && window.SWRPGUI.formatActivation) ? window.SWRPGUI.formatActivation((trec && trec.activation) || 'passive') : '') || 'Passive').toLowerCase()
    + ' · row ' + (row + 1) + ' · ' + cost + ' XP' + (owned ? ' · owned' : '');
}
function M7CharPickSheet(p) {
  var pick = p.pick, cb = p.charbuild || {}, trec = cb.talents ? cb.talents[pick.talentKey] : null;
  if (!trec) return null;
  var title = trec.name || pick.talentKey;
  if (pick.mode === 'chosen') {
    return (
      <M7PaperSheet anchor={pick.anchor} title={title} meta={m7TalentCellMeta(trec, pick.row, pick.cost, true)} metaStyle={M7_PICK_META_STYLE} onClose={p.onClose} kind="chosen" testid="m7-char-chosen">
        <p style={M7_LINE_STYLE} data-m7-line="chosen">{pick.receipt}</p>
      </M7PaperSheet>);
  }
  var head, notes = [], buttons;
  if (pick.mode === 'm8') return h(M8PickBody, { pick: pick, trec: trec, title: title, charbuild: cb, sheet: p.sheet || {}, onClose: p.onClose, onChoose: p.onChoose, onStep: p.onStep });
  if (pick.talentKey === 'dedication') {
    var chars = p.characteristics || {}, cap = (window.CharResolve && CharResolve.CHARACTERISTIC_CAP) || 6;
    head = 'Dedication · raise one characteristic by 1';
    buttons = M7_CHAR_ORDER.map(function (c) {
      var cur = Number(chars[c]) || 0, off = cur >= cap;
      if (off) notes.push(m7CapChar(c) + ' is at ' + cap + ', the most it can be');
      return { c: c, off: off, label: off ? m7CapChar(c) : m7CapChar(c) + ' ' + cur + ' → ' + (cur + 1),
               aria: off ? m7CapChar(c) + ' is at ' + cap + ', the most it can be' : 'Raise ' + m7CapChar(c) + ' to ' + (cur + 1) };
    });
  } else {
    var swap = m7ChosenSwapOf(trec);
    if (!swap) return null;
    var skill = m7JoinAnd((Array.isArray(swap.skills) ? swap.skills : []).map(function (id) { return (cb.skillNames && cb.skillNames[id]) || id; }));
    head = title + ' · choose the characteristic you\'ll use for ' + skill + ' checks';
    if (swap.from) notes.push(m7CapChar(swap.from) + ': ' + skill + ' already uses ' + m7CapChar(swap.from));
    buttons = M7_CHAR_ORDER.map(function (c) {
      var off = c === swap.from;
      return { c: c, off: off, label: m7CapChar(c), aria: off ? m7CapChar(c) + ': ' + skill + ' already uses ' + m7CapChar(c) : 'Use ' + m7CapChar(c) + ' for ' + skill };
    });
  }
  return (
    <M7PaperSheet anchor={pick.anchor} title={title} meta={m7TalentCellMeta(trec, pick.row, pick.cost, pick.mode === 'place')} metaStyle={M7_PICK_META_STYLE} onClose={p.onClose} kind={pick.mode} testid="m7-char-pick">
      <p style={M7_LINE_STYLE}>{head}</p>
      <div role="group" aria-label={head} style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8 }}>
        {buttons.map(function (b) {
          return <button type="button" key={b.c} className="hp-quiet" disabled={b.off} data-m7-char={b.c} style={b.off ? { opacity: .45 } : undefined}
            aria-label={b.aria} onClick={b.off ? undefined : function () { p.onChoose(b.c); }}>{b.label}</button>;
        })}
      </div>
      {notes.map(function (n) { return <div key={n} style={Object.assign({}, M7_PICK_META_STYLE, { margin: '8px 0 0' })} data-m7-note="1">{n}</div>; })}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
        <button type="button" className="hp-quiet" data-testid="m7-char-pick-cancel" onClick={p.onClose}>Cancel</button>
      </div>
    </M7PaperSheet>);
}

// F-100 M8 step 3 (frame R6; f100-m8-contracts R-M8K-27 R6; R-M8K-6): M7's ONE picker, for a per-rank pick of a SKILL
// (Deadly Accuracy: "choose the combat skill for this rank", another rank's skill closed with its reason — `distinct`) or
// an ITEM the character owns (Tinkerer: "Name · 4 → 5", an item another rank already marked closed — "each item may only
// be modified once"; an entry with no stat block, the sheet's Unarmed, never listed — R-M8G-28). The current pick reads
// pressed; choosing again moves it. The words come from the talent's data; the write is CharResolve.pickWrite's (the one
// writer). `pick` = { mode: 'm8', pickId, kind, talentKey, specKey, row, col }.
function m8PickRank(sheet, cb, pick) {
  var pos = pick.row + '-' + pick.col, e = (CharResolve.picksOf(sheet, cb, pick.talentKey) || []).filter(function (x) { return x.pick.id === pick.pickId; })[0];
  if (!e) return { entry: null, index: -1 };
  var i = -1;
  e.instances.forEach(function (inst, k) { if (inst.specId === pick.specKey && inst.pos === pos) i = k; });
  return { entry: e, index: i };
}
// F-100 M8 · Jury Rigged's two questions on one owned cell (frames J1–J3 @ d58a3bae, D-59; f100-m8-contracts A3.2 (1),
// R-M8K-35..38): the ITEM first (a weapon or armor the character owns), then the IMPROVEMENT — the option pick's values
// whose `categories` fit the marked item, each labelled by its own words with the number it changes. A value that no
// longer fits the item (the item re-chosen) reads unpicked, says so, and asks again (CharResolve.optionValueAt, the
// engine's one reader). `pick` = { mode: 'm8', pickId (the item pick), kind: 'item', optionId, step?, prevItem? }.
var M8_ROW_STYLE = { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', padding: '9px 0', marginBottom: 10,
  borderBottom: '1px solid var(--doc-rule, rgba(0,0,0,.12))' };
var M8_REFUSE_STYLE = { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--doc-ink)', padding: '9px 12px', marginTop: 8,
  background: 'var(--doc-paper, rgba(0,0,0,.03))', borderLeft: '3px solid var(--doc-action)' };
var M8_OPT_CAT = { weapons: 'weapon', armor: 'armor', gear: 'gear' };
function m8Cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
// the number on the item before any talent changed it: the first change's `from` on the display, else the display's own
function m8PrintedOf(r, key) {
  var f = ((r && r._talentEffects) || []).filter(function (x) { return x && (x.op === key + '_delta' || (key.indexOf('defense_') === 0 && x.op === 'defense_delta' && 'defense_' + x.target === key)); })[0];
  if (f) return f.from;
  if (key.indexOf('defense_') === 0) return r && r[key] != null ? r[key] : (r && r.defense) || 0;
  return r ? r[key] : null;
}
function m8OptionNumbers(sheet, row, r) {
  var f = row.floor != null ? Number(row.floor) : null, a = Number(row.amount) || 0;
  var floored = function (v) { return f != null ? (v > f ? Math.max(f, v + a) : v) : v + a; };
  var atMin = function (v, w) { return w + ' ' + v + ' → ' + v + ' (already the minimum)'; };
  if (row.op === 'damage_delta') {
    var raw = m8PrintedOf(r, 'damage'), n = diceHubWeaponDamage(sheet, { damage: raw });
    return n == null ? 'Damage —' : 'Damage ' + n + ' → ' + (n + a);
  }
  if (row.op === 'crit_delta') {
    var c = Number(m8PrintedOf(r, 'crit'));
    if (!(c > 0)) return 'Crit — (none printed)';
    return f != null && c <= f ? atMin(c, 'Crit') : 'Crit ' + c + ' → ' + floored(c);
  }
  if (row.op === 'encumbrance_delta') {
    var e = Number(m8PrintedOf(r, 'encumbrance')) || 0;
    return f != null && e <= f ? atMin(e, 'Encumbrance') : 'Encumbrance ' + e + ' → ' + floored(e);
  }
  if (row.op === 'defense_delta') {
    var d = Number(m8PrintedOf(r, 'defense_' + row.target)) || 0;
    return m8Cap(row.target) + ' defense ' + d + ' → ' + (d + a);
  }
  return null;
}
var M8_OPT_SHORT = { damage_delta: 'its damage', crit_delta: 'its Critical', encumbrance_delta: 'its encumbrance' };
function M8OptionPickBody(p) {
  var pick = p.pick, cb = p.charbuild, sheet = p.sheet, trec = p.trec, title = p.title;
  var pos = pick.row + '-' + pick.col, desc = trec.description || '';
  var all = CharResolve.picksOf(sheet, cb, pick.talentKey) || [];
  var itemE = all.filter(function (x) { return x.pick.id === pick.pickId; })[0], optE = all.filter(function (x) { return x.pick.id === pick.optionId; })[0];
  var decl = (itemE && itemE.pick) || {}, odecl = (optE && optE.pick) || {};
  var idx = itemE ? itemE.instances.map(function (i) { return i.specId === pick.specKey && i.pos === pos; }).indexOf(true) : -1;
  var marked = idx > -1 ? itemE.instances[idx].value : null, rank = idx + 1;
  var oInst = optE ? optE.instances.filter(function (i) { return i.specId === pick.specKey && i.pos === pos; })[0] : null;
  var stored = oInst ? oInst.value : null;
  var raw = sheet.equipment || {}, shown = resolveSheetEquipment(raw, sheet) || {};
  var shownAt = function (v) { return !v ? null : v.section === 'armor' ? shown.armor : (shown[v.section] || [])[v.index]; };
  var rows = (trec.item_effects || []).filter(function (r) { return r && r.option && r.option.chosen === pick.optionId; });
  var inUse = rows.some(function (r) { return r.in_use === true; });
  var USING = 'Counts only while the item is in use: a weapon not stowed, armor worn.';
  var step = pick.step || (marked ? 'option' : 'item');
  var meta = m7TalentCellMeta(trec, pick.row, pick.cost, true) + (rank > 0 && itemE.instances.length > 1 ? ' · rank ' + rank : '');
  var head, buttons = [], notes = [], itemRow = null, refuse = null, oneCol = false;
  if (step === 'item') {
    var cats = decl.pool && Array.isArray(decl.pool.categories) ? decl.pool.categories : ['weapon', 'armor', 'gear'];
    var nouns = cats.map(function (c) { return c === 'weapon' ? 'a weapon' : c; });
    head = title + ' · choose ' + (nouns.length > 1 ? nouns.slice(0, -1).join(', ') + ' or ' + nouns[nouns.length - 1] : nouns[0]) + ' you own';
    var add = function (section, index, ent, r) {
      if (CharResolve.itemMarkRefusal(sheet, cb, section, ent)) return;   // R-M8G-28: Unarmed is never offered
      buttons.push({ v: { section: section, index: index }, key: section + index, on: !!(marked && marked.section === section && marked.index === index),
        label: ((r && r.name) || (ent && ent.id) || '?') + ' · ' + M8_OPT_CAT[section], aria: 'Choose ' + ((r && r.name) || '') });
    };
    if (cats.indexOf('weapon') !== -1) (raw.weapons || []).forEach(function (ent, i) { add('weapons', i, ent, (shown.weapons || [])[i]); });
    if (cats.indexOf('armor') !== -1 && raw.armor) add('armor', 0, raw.armor, shown.armor);
    if (cats.indexOf('gear') !== -1) (raw.gear || []).forEach(function (ent, i) { add('gear', i, ent, (shown.gear || [])[i]); });
    notes.push(decl.distinct ? 'Each item takes ' + title + ' once.' : 'Each rank improves one item. Two ranks may improve the same one.');
    if (inUse) notes.push(USING);
    if (!buttons.length) notes.push('No item you own can take ' + title + ' yet.');
  } else {
    var r = shownAt(marked), cat = marked ? M8_OPT_CAT[marked.section] : null, name = (r && r.name) || '?';
    head = title + ' · choose the improvement for your ' + name;
    itemRow = { name: name, kind: m8Cap(cat || '') + ' · ' + ((r && r.state) || (cat === 'armor' ? 'worn' : 'carried')) };
    var opts = Array.isArray(odecl.options) ? odecl.options : [];
    var wordsOf = function (o) { return o && o.words ? desc.slice(o.words.start, o.words.end) : (o && o.id) || ''; };
    opts.forEach(function (o, k) {
      if (Array.isArray(o.categories) && o.categories.indexOf(cat) === -1) return;
      var row = rows.filter(function (x) { return x.option.is === o.id; })[0];
      var prev = opts[k - 1], label = wordsOf(o);
      // a value whose words continue the previous value's clause (", or of any single other effect") reads with its verb:
      // the previous words up to the continuation's first word ("decrease the Advantage cost … of any single other effect",
      // frame J2). Only an ", or " between the two spans continues; a new clause ("; decrease …") or a shared span does not.
      var gap = prev && prev.words && o.words && o.words.start >= prev.words.end ? desc.slice(prev.words.end, o.words.start) : null;
      if (gap != null && /^,?\s*or\s*$/.test(gap)) {
        var pw = wordsOf(prev), cut = pw.lastIndexOf(' ' + label.split(' ')[0] + ' ');
        label = (cut > 0 ? pw.slice(0, cut) : pw) + ' … ' + label;
      }
      buttons.push({ v: o.id, key: o.id, on: stored === o.id, label: m8Cap(label),
        sub: row ? m8OptionNumbers(sheet, row, r) : null, tag: row ? null : 'not applied', aria: 'Choose ' + m8Cap(label) });
    });
    oneCol = true;
    if (stored != null && marked && CharResolve.optionValueAt(optE, pick.specKey, pos, marked.section) == null) {
      var srow = rows.filter(function (x) { return x.option.is === stored; })[0];
      var short = !srow ? 'another effect' : srow.op === 'defense_delta' ? 'its ' + srow.target + ' defense' : (M8_OPT_SHORT[srow.op] || 'its improvement');
      refuse = (pick.prevItem ? 'The ' + pick.prevItem + '’s improvement (' : 'This rank’s improvement (') + short + ') does not fit ' + cat
        + (pick.prevItem ? ', so this rank gives nothing until you choose again.' : ', so it gives nothing until you choose again.');
    }
    notes.push(cat === 'weapon' ? 'A weapon’s improvements. Armor’s defense is not offered for a weapon.' : USING);
  }
  var stepLine = 'Step ' + (step === 'item' ? 1 : 2) + ' of 2 · ' + (step === 'item' ? 'the item' : 'the improvement');
  var pressed = { background: 'var(--doc-action)', color: 'var(--doc-paper-raised)', borderColor: 'var(--doc-action)' };
  return (
    <M7PaperSheet anchor={pick.anchor} title={title} meta={meta} metaStyle={M7_PICK_META_STYLE} onClose={p.onClose} kind="m8" testid="m8-pick">
      <div style={Object.assign({}, M7_PICK_META_STYLE, { margin: '0 0 6px' })} data-m8-step={step}>{stepLine}</div>
      {refuse ? <div style={M8_REFUSE_STYLE} data-m8-refuse="1">{refuse}</div> : null}
      {itemRow ? <div style={M8_ROW_STYLE} data-m8-item="1">
        <div><b style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 16, color: 'var(--doc-ink)' }}>{itemRow.name}</b>
          <i style={{ fontFamily: 'var(--font-body)', fontStyle: 'normal', fontSize: 14, color: 'var(--doc-ink-muted)', display: 'block' }}>{itemRow.kind}</i></div>
        <button type="button" className="hp-quiet" data-testid="m8-change-item" onClick={function () { p.onStep && p.onStep('item'); }}>Change item</button>
      </div> : null}
      <p style={M7_LINE_STYLE}>{head}</p>
      <div role="group" aria-label={head} style={{ display: 'grid', gridTemplateColumns: oneCol ? '1fr' : 'repeat(2,minmax(0,1fr))', gap: 8 }}>
        {buttons.map(function (b) {
          return <button type="button" key={b.key} className="hp-quiet" data-m8-pick={b.key} aria-pressed={b.on ? 'true' : 'false'} aria-label={b.aria}
            style={Object.assign(oneCol ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'left', height: 'auto', padding: '9px 12px' } : {}, b.on ? pressed : {})}
            onClick={function () { p.onChoose(b.v); }}>
            {oneCol ? <span style={{ fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.35, letterSpacing: 0, textTransform: 'none' }}>{glyphs(b.label)}</span> : b.label}
            {oneCol && b.sub ? <span style={{ fontWeight: 600 }}>{b.sub}</span> : null}
            {b.tag ? <span className="m8-tag" style={Object.assign({}, (window.SWRPGUI || {}).M8_TAG, b.on ? { color: 'inherit', borderColor: 'currentColor' } : {})}>{b.tag}</span> : null}
          </button>;
        })}
      </div>
      {notes.map(function (n) { return <div key={n} style={Object.assign({}, M7_PICK_META_STYLE, { margin: '8px 0 0', textTransform: 'none', letterSpacing: '.02em', fontSize: 12 })} data-m7-note="1">{n}</div>; })}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
        <button type="button" className="hp-quiet" data-testid="m7-char-pick-cancel" onClick={p.onClose}>Cancel</button>
      </div>
    </M7PaperSheet>);
}
function M8PickBody(p) {
  if (p.pick && p.pick.optionId) return h(M8OptionPickBody, p);
  var pick = p.pick, cb = p.charbuild, sheet = p.sheet, trec = p.trec, title = p.title;
  var at = m8PickRank(sheet, cb, pick), e = at.entry, me = e && at.index > -1 ? e.instances[at.index] : null;
  var decl = (e && e.pick) || {}, mine = me ? me.value : null, rank = at.index + 1;
  var head, buttons = [], notes = [];
  if (pick.kind === 'skill') {
    var groups = cb.skillGroups || {}, names = cb.skillNames || {}, pool = decl.pool;
    var ids = Object.keys(names).filter(function (id) { return !pool || pool === 'any' ? true : Array.isArray(pool) ? pool.indexOf(id) !== -1 : groups[id] === pool; });
    head = title + ' · choose the ' + (typeof pool === 'string' && pool !== 'any' ? pool + ' ' : '') + 'skill for this rank';
    buttons = ids.map(function (id) {
      var other = decl.distinct ? (e ? e.instances : []).map(function (x, k) { return { x: x, k: k }; }).filter(function (o) { return o.k !== at.index && o.x.value === id; })[0] : null;
      if (other) notes.push((names[id] || id) + ': rank ' + (other.k + 1) + '’s pick. Each rank takes a different skill.');
      return { v: id, key: id, off: !!other, on: mine === id, label: names[id] || id, aria: other ? (names[id] || id) + ', rank ' + (other.k + 1) + '’s pick' : 'Choose ' + (names[id] || id) };
    });
  } else {
    var eff = (trec.item_effects || []).filter(function (r) { return r && r.op === 'hard_points_delta'; })[0];
    var amt = eff && typeof eff.amount === 'number' ? eff.amount : 1;
    head = title + ' · choose an item you own' + (eff ? ' to gain ' + amt + ' hard point' + (amt === 1 ? '' : 's') : '');
    var cats = decl.pool && Array.isArray(decl.pool.categories) ? decl.pool.categories : ['weapon', 'armor', 'gear'];
    var raw = sheet.equipment || {}, shown = resolveSheetEquipment(raw, sheet) || {};
    var baseHp = function (r) { return (Number(r && r.hp) || 0) - ((r && r._talentEffects) || []).reduce(function (n, f) { return n + (f && f.op === 'hard_points_delta' ? (Number(f.amount) || 0) : 0); }, 0); };
    var markedByOther = function (ent) { return (ent && Array.isArray(ent.talentMarks) ? ent.talentMarks : []).some(function (m) { return m && m.talent === pick.talentKey && !(m.spec === pick.specKey && m.cell === pick.row + '-' + pick.col); }); };
    var add = function (section, index, ent, r) {
      if (CharResolve.itemMarkRefusal(sheet, cb, section, ent)) return;   // R-M8G-28: the one mark rule's stat-block leg — Unarmed is never offered
      var hp = baseHp(r), other = decl.distinct && markedByOther(ent);
      buttons.push({ v: { section: section, index: index }, key: section + index, off: other, on: !!(mine && mine.section === section && mine.index === index),
        label: ((r && r.name) || (ent && ent.id) || '?') + ' · ' + hp + ' → ' + (hp + (eff ? amt : 0)),
        aria: other ? ((r && r.name) || '') + ' already took ' + title : 'Choose ' + ((r && r.name) || '') });
    };
    if (cats.indexOf('weapon') !== -1) (raw.weapons || []).forEach(function (ent, i) { add('weapons', i, ent, (shown.weapons || [])[i]); });
    if (cats.indexOf('armor') !== -1 && raw.armor) add('armor', 0, raw.armor, shown.armor);
    if (cats.indexOf('gear') !== -1) (raw.gear || []).forEach(function (ent, i) { add('gear', i, ent, (shown.gear || [])[i]); });
    if (decl.distinct) notes.push('Each item takes ' + title + ' once.');
    if (!buttons.length) notes.push('No item you own can take ' + title + ' yet.');
  }
  var meta = m7TalentCellMeta(trec, pick.row, pick.cost, true) + (rank > 0 && e && e.instances.length > 1 ? ' · rank ' + rank : '');
  return (
    <M7PaperSheet anchor={pick.anchor} title={title} meta={meta} metaStyle={M7_PICK_META_STYLE} onClose={p.onClose} kind="m8" testid="m8-pick">
      <p style={M7_LINE_STYLE}>{head}</p>
      <div role="group" aria-label={head} style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8 }}>
        {buttons.map(function (b) {
          return <button type="button" key={b.key} className="hp-quiet" disabled={b.off} data-m8-pick={b.key} aria-pressed={b.on ? 'true' : 'false'}
            style={b.off ? { opacity: .45 } : b.on ? { background: 'var(--doc-action)', color: 'var(--doc-paper-raised)', borderColor: 'var(--doc-action)' } : undefined}
            aria-label={b.aria} onClick={b.off ? undefined : function () { p.onChoose(b.v); }}>{b.label}</button>;
        })}
      </div>
      {notes.map(function (n) { return <div key={n} style={Object.assign({}, M7_PICK_META_STYLE, { margin: '8px 0 0' })} data-m7-note="1">{n}</div>; })}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
        <button type="button" className="hp-quiet" data-testid="m7-char-pick-cancel" onClick={p.onClose}>Cancel</button>
      </div>
    </M7PaperSheet>);
}

var SHEET_STAGE_GROUPS = [['general', 'General'], ['social', 'Social'], ['combat', 'Combat'], ['knowledge', 'Knowledge']];
function SheetStage(p) {
  var sheet = p.sheet || {}, accent = p.accent;
  var chars = sheet.characteristics || {}, skills = sheet.skills || {};
  var canonReady = useCanonicalReady();
  var cb = window._canonicalCharbuild || null;
  var _q = React.useState(''), q = _q[0], setQ = _q[1];
  var _tap = React.useState(null), tap = _tap[0], setTap = _tap[1];   // F-100 M7 W1: { key, el } — the row whose tap sheet is open
  var closeTap = React.useCallback(function () { setTap(null); }, []);
  // B-628 (Jason 2026-09-16: "Can the skill sections be collapsible? Keeping the search"): a group's head folds
  // its rows; the folds are this viewer's convenience (localStorage, never the record); a FILTER shows its
  // matches through a closed group — search always wins over a fold.
  var _folds = React.useState(function () { try { var v = JSON.parse(localStorage.getItem('swrpg_sheet_stage_folds') || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; } }), folds = _folds[0], setFolds = _folds[1];
  var toggleFold = function (key) {
    setFolds(function (prev) { var next = prev.indexOf(key) === -1 ? prev.concat([key]) : prev.filter(function (k) { return k !== key; }); try { localStorage.setItem('swrpg_sheet_stage_folds', JSON.stringify(next)); } catch (e) {} return next; });
  };
  var Dice = window.SWRPGDice;
  // the group per SHEET key: invert the bridge once; before the registries land, the cockpit's own
  // three-way split stands in (KNOW* → knowledge, the six attack skills → combat, the rest general)
  var groupOf = React.useMemo(function () {
    var m = {};
    if (cb && cb.skillSheetKeys && cb.skillGroups) {
      Object.keys(cb.skillSheetKeys).forEach(function (id) { m[cb.skillSheetKeys[id]] = cb.skillGroups[id] || 'general'; });
    }
    return function (key) { return m[key] || skillCategoryOf(key).toLowerCase(); };
  }, [cb, canonReady]);
  var career = React.useMemo(function () { return sheetCareerSkillKeys(sheet, cb) || []; }, [sheet, cb, canonReady]);
  // THE FORCE SWITCH (ruling `f100-surfacing-force-flip-card` ADDENDUM 9 E-FFL-4, §2.6 of its brief): `p.committed` = the
  // session's committed Force set (never saved). Each row's `cv` and `rank` (the five boxes, `data-rank`) read CharResolve's two
  // committed readers, and its pool is the ONE pool reader handed the set. Absent, or the key OFF, the sheet's own numbers.
  var committed = p.committed, cbv = CharResolve.talentViewFor(sheet) || cb;
  if (committed != null) chars = CharResolve.characteristicsWithCommits(sheet, cbv, committed);
  var rows = Object.keys(SKILL_NAMES).map(function (key) {
    var _sc = CharResolve.skillCharacteristic(sheet, key, cb), prop = (_sc && _sc.characteristic) || '';   // F-100 M7 C2 (R-M7K-9/10)
    var saved = sheetSkillRank(skills, key);
    return { key: key, name: SKILL_NAMES[key], charName: FC_CHAR_FULL[prop] || prop, cv: Number(chars[prop]) || 0,
             rank: committed != null ? CharResolve.skillRankWithCommits(sheet, cbv, committed, key, saved) : saved,
             group: groupOf(key), career: career.indexOf(key) !== -1 };
  }).sort(function (a, b) { return a.name.localeCompare(b.name); });
  var needle = q.trim().toLowerCase();
  var shown = needle ? rows.filter(function (r) { return r.name.toLowerCase().indexOf(needle) !== -1; }) : rows;
  var total = rows.length;
  var boxes = function (rank) {
    var out = [];
    for (var i = 0; i < 5; i++) out.push(<span className="hp-box" key={i} data-on={i < rank ? '1' : '0'} />);
    return out;
  };
  var _tvEpoch = window.SWRPGTalentView.useTalentViewEpoch();   // A3-2 / B-844: a talent's dice resolve through the stamp view — the memo keys on its epoch
  var poolMods = React.useMemo(function () { return collectPoolMods(p.equipment); }, [p.equipment, _tvEpoch]);
  var pool = function (r) { return sheetPoolFor(sheet, r.key, poolMods, committed != null ? { forceCommitted: committed } : undefined); };   // ONE core with the WEAPONS unit and the hub
  return (
    <React.Fragment>
      <input className="sb-filter" type="search" value={q} data-testid="stage-filter" aria-label={'Filter ' + total + ' skills'} placeholder={'Filter ' + total + ' skills'}
        onChange={function (e) { setQ(e.target.value); }} />
      <div className="hp-plate" data-pad="data" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div className="sb-stage2" data-testid="stage-skills" data-shown={shown.length}>
          {SHEET_STAGE_GROUPS.map(function (g) {
            var inGroup = shown.filter(function (r) { return r.group === g[0]; });
            if (!inGroup.length) return null;
            var closed = !q && folds.indexOf(g[0]) !== -1;
            return (
              <React.Fragment key={g[0]}>
                <button type="button" className="sb-grp" data-group={g[0]} data-open={closed ? '0' : '1'} aria-expanded={closed ? 'false' : 'true'} data-testid={'stage-group-' + g[0]}
                  aria-label={(closed ? 'Show ' : 'Hide ') + g[1] + ' skills'} onClick={function () { toggleFold(g[0]); }}>
                  {g[1]}<span>{inGroup.length}</span><i className="sb-grp-glyph" aria-hidden="true">▾</i></button>
                {closed ? null : inGroup.map(function (r) {
                  return (
                    <div className="sb-sk" data-compact="1" data-skill={r.key} data-rank={r.rank} data-career={r.career ? '1' : '0'} key={r.key}>
                      {/* F-100 M7 W1 (R-M7G-19): the name block is the button that opens the row's tap sheet; ROLL is unchanged */}
                      <button type="button" className="sb-sk-n" data-testid={'stage-why-' + r.key.toLowerCase()} aria-haspopup="dialog" aria-expanded={tap && tap.key === r.key ? 'true' : 'false'}
                        aria-label={r.name + ': why it is career, what it rolls'} onClick={function (e) { setTap({ key: r.key, el: e.currentTarget }); }}
                        style={{ background: 'none', border: 0, padding: 0, margin: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer', minHeight: 44, alignSelf: 'stretch' }}>
                        <div className="sb-sk-name">{r.name}</div>
                        <div className="sb-sk-char">{r.charName}{r.career ? ' · Career' : ''}</div>
                      </button>
                      <div className="sb-sk-boxes">{boxes(r.rank)}</div>
                      <div className="sb-pool">{pool(r)}</div>
                      <button type="button" className="sb-roll" aria-label={'Roll ' + r.name + ', rank ' + r.rank + ' — opens the dice hub'} data-testid={'roll-' + r.key.toLowerCase()}
                        onClick={function () { p.onRoll(r.key); }}>Roll</button>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
          {!shown.length ? <div className="hp-none" style={{ gridColumn: '1 / -1' }}>{'No skill matches "' + q.trim() + '".'}</div> : null}
        </div>
      </div>
      {tap ? (function () {
        // W1's lines: the career line (R-M7G-18) and the characteristic line, from the two shared answers
        var r = rows.filter(function (x) { return x.key === tap.key; })[0];
        if (!r) return null;
        var entry = r.career ? (CharResolve.careerSkillStatus(sheet, cb)[r.key] || null) : null;
        var cl = m7CareerLine(entry, cb, sheet), rl = m7RollsLine(CharResolve.skillCharacteristic(sheet, r.key, cb), cb);
        return (
          <M7PaperSheet anchor={tap.el} title={r.name} meta={m7SkillMeta(r.key, r.rank, cb)} onClose={closeTap} kind="skill" testid="stage-why-sheet">
            {cl ? <p style={M7_LINE_STYLE} data-m7-line="career">{cl}</p> : null}
            {rl ? <p style={M7_LINE_STYLE} data-m7-line="rolls">{rl}</p> : null}
            {(function () {
              // R-SSC-2 / D-SSF1: the two heads in the sheet's meta style; applied lines in the one applied look; offered lines
              // in offerOf's words with NO tick and no button (the hub owns the control)
              var ws = whySheetLines(sheet, r.key, cb), U = window.SWRPGUI || {}, head = Object.assign({}, M7_META_STYLE, { marginTop: 14 });
              return <React.Fragment>
                {ws.applied.length ? <div style={head} data-why-head="applied">Applied on this check</div> : null}
                {ws.applied.map(function (t, i) { return U.AppliedLine ? <U.AppliedLine key={'a' + i} info={(ws.appliedInfo || [])[i] || null}>{t}</U.AppliedLine> : null; })}
                {ws.offered.length ? <div style={head} data-why-head="offered">Offered on this check</div> : null}
                {ws.offered.map(function (o, i) { return (
                  <div key={'o' + i} className="m8-offer" data-m8="offer-line" style={{ fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.4, color: 'var(--doc-ink)', marginTop: 8 }}>
                    <div style={o.info && U.OfferInfo ? { display: 'flex', alignItems: 'center' } : undefined}><span><b style={{ fontWeight: 600 }}>{o.name}</b>{' — '}{U.glyphText ? U.glyphText(o.text) : o.text}</span>
                    {o.info && U.OfferInfo ? <span style={U.InfoSlot}><U.OfferInfo info={o.info} /></span> : null}</div>
                    {o.tags && o.tags.length ? <div className="m8-tags" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>{o.tags.map(function (t, k) { return <span key={k} className="m8-tag" style={U.M8_TAG}>{t}</span>; })}</div> : null}
                  </div>); })}
              </React.Fragment>;
            })()}
          </M7PaperSheet>);
      })() : null}
    </React.Fragment>
  );
}


// ─── B-612 · THE STACK — the reading units (SHEET_SHAPE §3.4), on paper ───────────────────────────
// Units in at-table frequency order, every head visible, ONE open, the body scrolling inside itself:
// WEAPONS · ARMOUR · ABILITIES · FORCE POWERS (only at Force rating ≥ 1) · GEAR · FORTIFICATIONS (only when built) · EXPERIENCE · PROFILE ·
// VESSELS. A body is the board's `.sb-e` entries over the resolved record — `resolveSheetEquipment`
// for the pack, the display helpers for talents, species abilities and powers, `computeEncumbrance`
// for the arithmetic — and every ROLL opens the DICE HUB on that strip (R-PSC-2). The three ROOMS are
// doors here — Open the pack · Open abilities · Open advancement — and until their own rows land
// (B-613 · B-614 · B-615) each door opens a takeover holding that page's legacy face on an island.
// PROFILE and VESSELS keep their legacy renderers as islands inside their units (their paper bodies
// are rows of their own, B-620 · B-621). `data-sheet-interim` still counts what is left.
// ── THE SIGNATURE GROUP (ruling f100-surfacing-sheet-concept R-SSC-3; frames S5/S6 of the approved record
// Docs/mockups/f100-surfacing-sheet.html @ 939c683d8; ADDENDUM 10 C1, Q1 (a)) — each owned signature ability through the ONE
// legality function (CharResolve.sigAbilityChecks), its XP SHOWN (sigAbilitiesXp; the charge is the creator's), its activation
// terms (sigActivation, a base-unstated term says so) and the prose check until B-1310's field. A refused entry carries its
// reasons in its meta. "XP cost not yet checked against the book" prints ONLY for an entry with an `inferred` cell (C1: none
// today, D-229's read; the rule stands, pinned on a synthetic cell). DARK: [] unless M12's engine key is ON (Q1 (a)).
var SIG_CHECK_RE = /(Simple|Easy|Average|Hard|Daunting|Formidable) \((?:\[[A-Z]{2}\])+\) ((?:[A-Z][a-z]+)(?: \([A-Z][a-z]+\))?(?: or [A-Z][a-z]+(?: \([A-Z][a-z]+\))?)?) check/;
var SIG_ONCE = { 1: 'once per session', 2: 'twice per session' };
function sheetSignatureEntries(sheet, cb) {
  if (!(window.SWRPGEngineRules && window.SWRPGEngineRules.on()) || !CharResolve.sigAbilityChecks) return [];
  cb = cb || (CharResolve.talentViewFor ? CharResolve.talentViewFor(sheet) : null) || window._canonicalCharbuild;
  var xp = CharResolve.sigAbilitiesXp ? CharResolve.sigAbilitiesXp(sheet, cb) || [] : [];
  return (CharResolve.sigAbilityChecks(sheet, cb) || []).map(function (c, i) {
    var rec = c.rec || {}, nodes = rec.abilities || {};
    if (!c.ok) return { id: c.id, name: c.name, ok: false, meta: 'not standing: ' + (c.problems || []).map(function (q) { return q.words; }).join(' '), sources: rec.sources };
    var base = nodes[c.cells[0].ability] || {}, act = (CharResolve.sigActivation ? CharResolve.sigActivation(sheet, cb, c.id) : null) || {}, terms = [];
    var sg = function (n) { return (n > 0 ? '+' : '') + n; };
    if (act.destiny) terms.push(act.destiny.value == null ? sg(act.destiny.delta) + ' Destiny (base not stated)' : act.destiny.value + ' Destiny');
    if (act.uses_per_session) terms.push(act.uses_per_session.value == null ? '+' + act.uses_per_session.delta + ' use per session (base not stated)' : (SIG_ONCE[act.uses_per_session.value] || act.uses_per_session.value + ' times per session'));
    if (act.range) terms.push(act.range.value == null ? sg(act.range.delta) + ' range band (base not stated)' : act.range.value + ' range');
    if (act.duration_rounds) terms.push(act.duration_rounds.value == null ? '+' + act.duration_rounds.delta + ' round' + (act.duration_rounds.delta === 1 ? '' : 's') + ' (base not stated)' : act.duration_rounds.value + ' rounds');
    if (act.strain) terms.push(sg(act.strain.delta) + ' strain (base not stated)');
    var m = SIG_CHECK_RE.exec(base.description || '');
    if (m) terms.push(m[1] + ' ' + m[2] + ' check');
    var ups = c.cells.slice(1).map(function (cell) { var n = nodes[cell.ability] || {};
      return { name: n.name || cell.ability, cost: cell.cost, inferred: cell.cost_status === 'inferred', words: n.description || '' }; });
    var x = xp[i] || {};
    return { id: c.id, name: c.name, ok: true, upgrades: ups.length, meta: ups.length + ' upgrade' + (ups.length === 1 ? '' : 's') + ' · ' + x.xp + ' XP',
      inferred: !!x.inferred, words: base.description || '', activation: terms.join(' · '), told: 'Activated at the table', upgradeCells: ups, sources: rec.sources };
  });
}
// R7 B-1457 (D-RML1 (a), ruling f100-surfacing-roll-modifier-legibility-audit ADDENDUM 2): the abilities room's talent and
// species entries print their told rows as told lines, as the signature entries do. The hub prints only check-anchored
// told rows (diceHubToldFor), so a told row with no anchor (Gearhead's half_mod_cost, the Ewok's Low-Tech Users) had no
// line anywhere. Same reader as the hub's (CharResolve.ownedTalentToldSources), same words (the row's span of the text).
// → { talent: { <id>: [words] }, species: { <ability name>: [words] } }
function sheetAbilityToldWords(sheet) {
  var out = { talent: {}, species: {} }, CR = window.CharResolve;
  var cb = (CR && CR.talentViewFor && CR.talentViewFor(sheet)) || window._canonicalCharbuild;
  if (!CR || !CR.ownedTalentToldSources || !cb || !cb.talents) return out;
  CR.ownedTalentToldSources(sheet, cb).forEach(function (t) {
    if (t.power || t.signature) return;
    var into = t.species ? out.species : out.talent, k = t.species ? t.name : t.id;
    t.told.forEach(function (row) {
      var w = (row && row.words) || {}, words = String(t.description || '').slice(w.start, w.end).replace(/\s+/g, ' ').trim();
      if (words && (into[k] = into[k] || []).indexOf(words) === -1) into[k].push(words);
    });
  });
  return out;
}
// a told line in DiceHubTold's look (▫, the muted ink) without its "not applied" tag: a rule the sheet shows, the table runs
function sheetToldLine(text, key) {
  return <div className="dh-rem" data-m8="told" key={key} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 6, fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.4, color: 'var(--doc-ink-muted)' }}>
    <span aria-hidden="true">▫</span><span>{text}</span></div>;
}
// card release-r6-ui §2.2: ALLIES after VESSELS (D-388: no GROUP). ONE line: two flip fixtures regex it (ruling release-r6-ui-c0 L1)
var SHEET_STACK_ORDER = ['WEAPONS', 'ARMOUR', 'ABILITIES', 'FORCE POWERS', 'GEAR', 'FORTIFICATIONS', 'EXPERIENCE', 'PROFILE', 'VESSELS', 'ALLIES'];
// D6 / B (D-291 D6 (a), D-367): FORTIFICATIONS — the character's built fortifications, a unit after GEAR, drawn only when one
// exists (empty = no unit). Each row is its name and "Ranged defence N · Occupancy N"; open, the card reads the template's name
// and cite, its numbers (Craft.fortificationOf at render, from the template and `built_with` — no number is stored) with no
// Silhouette (D-367), and BUILT WITH: each spend row's name, cite and effect.
function SheetFortifications(p) {
  var Craft = window.SWRPGCraft;
  var forts = (p.sheet && p.sheet.fortifications) || [];
  var _r = React.useState(_craftRegs), regs = _r[0], setRegs = _r[1];
  React.useEffect(function () { if (!regs) loadCraftingRegistries().then(setRegs); }, []);
  var _o = React.useState(null), open = _o[0], setOpen = _o[1];
  if (!forts.length || !regs || !Craft) return null;
  var TPL = regs.tpl.templates, idx = Craft.indexSpend(regs.spend, 'fortification');
  return <div data-testid="sheet-forts">{forts.map(function (f) {
    var st = Craft.fortificationOf(f, TPL, idx), t = TPL[f.template_id] || {}, isOpen = open === f.id;
    var cell = function (lab, key, v) { return <div key={key} data-testid={'fort-' + key}>{lab} <b>{v == null ? '—' : String(v)}</b></div>; };
    return <div className="sb-e" key={f.id}>
      <button type="button" className="sb-pk-r" data-selected={isOpen ? 'true' : 'false'} aria-expanded={isOpen ? 'true' : 'false'} data-testid={'sheet-fort-' + f.id}
        onClick={function () { setOpen(isOpen ? null : f.id); }}>
        <span className="sb-pk-c">›</span><span className="sb-pk-n">{f.name || t.name}</span>
        <span className="sb-pk-t" data-testid="fort-row">{'Ranged defence ' + (st.defense_ranged == null ? '—' : st.defense_ranged) + ' · Occupancy ' + (st.passengers == null ? '—' : st.passengers)}</span>
      </button>
      {isOpen ? <div data-testid="sheet-fort-card">
        <div className="sb-e-h"><span className="sb-e-n">{t.name}</span><span className="sb-e-m">{t.cite}</span></div>
        <div className="sb-e-l">{cell('Ranged defence', 'defense_ranged', st.defense_ranged)}{cell('Occupancy', 'passengers', st.passengers)}{cell('Armour', 'armor', st.armor)}</div>
        {(f.built_with || []).length ? <div className="sb-e">
          <div className="sb-e-h"><span className="sb-e-n">Built with</span></div>
          {f.built_with.map(function (id, i) { var e = idx[id], row = e && (e.row || e); return row ? <div className="sb-att" key={id + i}>
            <div className="sb-e-h"><span className="sb-e-n">{row.name}</span><span className="sb-e-m">{row.cite}</span></div>
            <p className="sb-e-t">{glyphs(row.effect)}</p></div> : null; })}
        </div> : null}
      </div> : null}
    </div>;
  })}</div>;
}
// ═══ R6 · ALLIES (card release-r6-ui; D-278 S1, D-389 S5; ruling release-r6-ui-c0 U1, L4, U10) ═══════════════════════════════
// The unit after VESSELS: COMPANIONS (each card resolved at render from its `ref` or `own`, SWRPGCompanions.profileOf — R-R6CC-4;
// no display shape is stored), then the direction rule (W2) and ＋ COMPANION, then FOLLOWERS and CONTACTS. Always drawn (U1, the
// VESSELS idiom): an empty unit's head reads "—" and the door sits inside it.
var _cmpNpcs = null, _cmpNpcsP = null;   // npcs.json (4 MB): fetched once, only when a sheet holds a library companion
function loadCompanionNpcs() {
  if (_cmpNpcs) return Promise.resolve(_cmpNpcs);
  if (_cmpNpcsP) return _cmpNpcsP;
  _cmpNpcsP = fetch('public/data/canonical/npcs.json').then(function (r) { return r.json(); })
    .then(function (j) { _cmpNpcs = (j && j.npcs) || {}; return _cmpNpcs; }).catch(function () { _cmpNpcsP = null; return null; });
  return _cmpNpcsP;
}
// the registries a sheet's companions resolve against: the library's creatures (a `ref`) and the crafting registries (a crafted
// droid's recipe, droidOf), each loaded only when a row needs it
function useAlliesRegs(sheet) {
  var rows = (window.SWRPGCompanions ? window.SWRPGCompanions.normalizeCompanions(sheet && sheet.companions) : []);
  var needN = rows.some(function (c) { return !!c.ref; }), needC = rows.some(function (c) { return c.own && c.own._provenance === 'crafted'; });
  var _n = React.useState(_cmpNpcs), npcs = _n[0], setNpcs = _n[1];
  var _c = React.useState(_craftRegs), craft = _c[0], setCraft = _c[1];
  React.useEffect(function () { if (needN && !npcs) loadCompanionNpcs().then(setNpcs); }, [needN]);
  React.useEffect(function () { if (needC && !craft) loadCraftingRegistries().then(setCraft); }, [needC]);
  return { rows: rows, npcs: npcs, craft: craft, ready: (!needN || !!npcs) && (!needC || !!craft) };
}
function companionProfile(c, regs, sheet) {
  var C = window.SWRPGCompanions;
  var droid = regs.craft ? { templates: regs.craft.tpl.templates, spend: regs.craft.spend,
    cybernetics: C.cyberneticsOf((sheetItemRegistries(sheet) || {}).gear) } : null;
  return C.profileOf(c, regs.npcs, { droid: droid });
}
var ALLIES_PEOPLE = { ALLY: 1, APPRENTICE: 1, FOLLOWER: 1, SCHOOL: 1 };   // the reward rows that are people (FOLLOWERS' cards)
function alliesPeople(sheet) { return (Array.isArray(sheet && sheet.rewards) ? sheet.rewards : []).filter(function (r) { return r && ALLIES_PEOPLE[r.key]; }); }
function alliesContacts(sheet) { return Array.isArray(sheet && sheet.contacts) ? sheet.contacts.filter(Boolean) : []; }
// L4 (D-278 Q2): the closed head — one card reads its name; otherwise the count of every card in the unit; none reads "—" (U1)
function alliesHead(sheet, regs) {
  var n = regs.rows.length + alliesPeople(sheet).length + alliesContacts(sheet).length;
  if (n === 0) return '—';
  if (n === 1 && regs.rows.length === 1) {
    var c = regs.rows[0], prof = regs.ready ? companionProfile(c, regs, sheet) : null;
    return c.name || (prof && prof.name) || '1';
  }
  return String(n);
}
var ALLIES_CHARS = [['brawn', 'Br'], ['agility', 'Ag'], ['intellect', 'Int'], ['cunning', 'Cun'], ['willpower', 'Will'], ['presence', 'Pr']];
function alliesCap(s) { s = String(s || ''); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
function SheetCompanionCard(p) {
  var c = p.c, prof = p.prof, L = p.lib && p.lib.NPC;
  var _s = React.useState(false), sbOpen = _s[0], setSb = _s[1];
  var _w = React.useState(null), why = _w[0], setWhy = _w[1];
  if (!prof || prof.missing) {
    return <div className="sb-e" data-testid="ally-companion" data-cmp={c.id}>
      <div className="sb-e-h"><span className="sb-e-n">{(prof && prof.name) || c.name || c.ref || ''}</span></div>
      <p className="sb-e-t"><span className="sb-refuse">Not in the library</span></p></div>;
  }
  var rec = prof.record || {}, d = rec.derived || {}, ch = rec.characteristics || {};
  var crafted = !!(c.own && c.own._provenance === 'crafted');
  var wFrom = (prof.because || []).filter(function (b) { return b.field === 'wounds' && b.source === 'young'; })[0] || null;
  var wounds = d.wounds || 0;
  var mount = c.mount ? window.SWRPGCompanions.mountOf(rec, c.mount.trained) : null;
  var cell = function (label, v, key) { return <div key={key}>{label + ' '}<b data-testid={'ally-' + key}>{String(v)}</b></div>; };
  var def = (d.ranged_defense || 0) + ' / ' + (d.melee_defense || 0);
  var woundsCell = wFrom
    ? <div key="w" {...sheetWhyPress(sheetWhyLabel('Wounds', wounds, 'down'), function (el) { setWhy(el); }, !!why)} style={SHEET_WHY_PRESS_STYLE} className="sb-al-whyv">
        {'Wounds '}<b data-testid="ally-wounds">{String(wounds)}</b><SheetWhyMark dir="down" /></div>
    : cell('Wounds', wounds, 'wounds');
  var dirName = crafted && c.own.directive_id && p.craft ? ((p.craft.tpl.templates[c.own.directive_id] || {}).name || c.own.directive_id) : null;
  var setWounds = function (v) { p.onChange({ wounds: Math.max(0, v) }); };
  var rend = sbOpen && !crafted && typeof npcFromLibrary === 'function' ? npcFromLibrary(rec) : null;
  return (
    <div className="sb-e sb-al-cmp" data-testid="ally-companion" data-cmp={c.id} data-young={c.young ? '1' : '0'}>
      <div className="sb-e-h"><span className="sb-e-n">{prof.name}</span>
        <span className="sb-e-m">{(prof.creature || '') + (prof.tier ? ' · ' + alliesCap(prof.tier) : '')}</span>
        {c.young ? <span className="sb-al-mark" data-testid="ally-young">Young</span> : null}</div>
      <div className="sb-e-l">{cell('Soak', d.soak || 0, 'soak')}{woundsCell}{cell('Defence', def, 'def')}</div>
      {mount ? <div className="sb-e-l sb-al-mount" data-testid="ally-mount"><div>Mount</div>{cell('Silhouette', mount.silhouette, 'sil')}{cell('Speed', mount.speed, 'speed')}
        {cell('Handling', mount.handling, 'handling')}{cell('Trained', mount.trained, 'trained')}</div> : null}
      {crafted ? <p className="sb-e-t" data-testid="ally-programmed">{'Programmed '}<b>{dirName || 'Not yet'}</b></p> : null}
      {!crafted && L && L.NpcPaperTrack ? <div className="sb-vit sb-al-trk"><L.NpcPaperTrack label="Wounds" track="wounds" cur={c.wounds || 0} max={wounds}
        onDec={function () { setWounds((c.wounds || 0) - 1); }} onInc={function () { setWounds((c.wounds || 0) + 1); }}
        decLabel={'Remove one wound from ' + prof.name} incLabel={'Add one wound to ' + prof.name} /></div> : null}
      {crafted
        ? <p className="sb-e-t" data-testid="ally-chars">{ALLIES_CHARS.map(function (k, i) { return <span key={k[0]}>{(i ? ' · ' : '') + k[1] + ' '}<b>{String(ch[k[0]] || 0)}</b></span>; })}</p>
        : <button type="button" className="sb-al-strip" aria-expanded={sbOpen ? 'true' : 'false'} data-testid="ally-strip"
            aria-label={(sbOpen ? 'Close ' : 'Open ') + prof.name + '’s stat block'} onClick={function () { setSb(!sbOpen); }}>
            <span data-testid="ally-chars">{ALLIES_CHARS.map(function (k) { return k[1] + ' ' + (ch[k[0]] || 0); }).join(' · ')}</span>
            <i aria-hidden="true" className="sb-al-chev">{sbOpen ? '▴' : '▾'}</i></button>}
      {rend && L && L.NpcPaperStatBlock && p.refData ? <div className="sb-al-sb" data-testid="ally-statblock"><L.NpcPaperStatBlock rend={rend} tier={rec.tier} refData={p.refData}
        name={prof.name} arrange={p.wide ? 'panels' : 'stack'} ctx="flip" /></div> : null}
      {why ? <M7PaperSheet anchor={why} title="Wounds" meta={prof.name + ' · ' + prof.creature} onClose={function () { setWhy(null); }} kind="why" testid="ally-why-young">
        <SheetWhyBody whyKey="young" parts={[{ text: prof.creature + ' ' + wFrom.from }, { text: 'Young ½' }]} total={'Total ' + wounds} /></M7PaperSheet> : null}
      {p.onRemove ? <SheetCompanionEdit c={c} name={prof.name} onChange={p.onChange} onRemove={p.onRemove} /> : null}
    </div>
  );
}
// D1 (card release-r6-ui ADDENDUM 7; D-395): the card's own controls behind EDIT — Name, Young Off/On, Mount Off/On (Trained 0–2
// when on), Remove asking once ("Remove <name>?" Remove · Keep). Writers: SWRPGCompanions.updateCompanion / removeCompanion.
function SheetCompanionEdit(p) {
  var c = p.c;
  var _o = React.useState(false), open = _o[0], setOpen = _o[1], _r = React.useState(false), rm = _r[0], setRm = _r[1];
  var _n = React.useState(c.name || ''), nm = _n[0], setNm = _n[1];
  var row = function (label, kid) { return <div className="sb-rw-row"><span className="sb-rw-k">{label}</span>{kid}</div>; };
  return (
    <div className="sb-al-edit" data-testid="ally-edit-box">
      <button type="button" className="hp-quiet" data-testid="ally-edit" aria-expanded={open ? 'true' : 'false'} aria-label={'Edit ' + p.name}
        onClick={function () { setOpen(!open); setRm(false); }}>Edit</button>
      {open ? <div className="sb-rw-form" data-testid="ally-edit-panel">
        <label className="sb-fld"><span>Name</span><input className="sb-filter" type="text" aria-label="Name" data-testid="ally-edit-name" value={nm}
          onChange={function (e) { setNm(e.target.value); }} onBlur={function () { if (nm !== (c.name || '')) p.onChange({ name: nm }); }}
          onKeyDown={function (e) { if (e.key === 'Enter') e.target.blur(); }} /></label>
        {row('Young', <GrowthSeg aria="Young" testid="ally-edit-young" opts={[[false, 'Off'], [true, 'On']]} cur={!!c.young} onPick={function (v) { p.onChange({ young: v }); }} />)}
        {row('Mount', <GrowthSeg aria="Mount" testid="ally-edit-mount" opts={[[false, 'Off'], [true, 'On']]} cur={!!c.mount} onPick={function (v) { p.onChange({ mount: v ? { trained: 0 } : null }); }} />)}
        {c.mount ? row('Trained', <GrowthSeg aria="Trained" testid="ally-edit-trained" opts={[[0, '0'], [1, '1'], [2, '2']]} cur={c.mount.trained || 0} onPick={function (v) { p.onChange({ mount: { trained: v } }); }} />) : null}
        {rm ? <div className="sb-rw-row" data-testid="ally-edit-confirm"><span className="sb-rw-k">{'Remove ' + p.name + '?'}</span>
            <button type="button" className="hp-commit" data-testid="ally-edit-remove-yes" onClick={p.onRemove}>Remove</button>
            <button type="button" className="hp-quiet" data-testid="ally-edit-keep" onClick={function () { setRm(false); }}>Keep</button></div>
          : <div className="sb-rw-row"><button type="button" className="hp-quiet" data-testid="ally-edit-remove" onClick={function () { setRm(true); }}>Remove</button></div>}
      </div> : null}
    </div>);
}
function SheetAllies(p) {
  var sheet = p.sheet || {}, regs = p.regs, C = window.SWRPGCompanions;
  var _l = React.useState(window.SWRPGLibrary || null), lib = _l[0], setLib = _l[1];
  var _rd = React.useState(null), refData = _rd[0], setRefData = _rd[1];
  var wide = useLayoutMode() === 'desktop';   // the app's 1024 seam: the stat block's panels from 1024, as the library draws it
  React.useEffect(function () {   // the card's wounds track and stat block are the library's own (both hosts, one module)
    if (!regs.rows.length) return;
    if (!lib) ensureNpcLibrary().then(setLib).catch(function () {});
    if (!refData && window.SWRPGNpcRefData) window.SWRPGNpcRefData.load().then(setRefData).catch(function () {});
  }, [regs.rows.length]);
  var write = function (id, fields) { p.onSheetChange({ companions: C.updateCompanion(regs.rows, id, fields) }); };
  return (
    <div data-testid="stack-allies">
      <div className="sb-grp" data-testid="allies-band-companions">Companions<span>{regs.rows.length || '—'}</span></div>
      {regs.ready ? regs.rows.map(function (c) {
        return <SheetCompanionCard key={c.id} c={c} prof={companionProfile(c, regs, sheet)} craft={regs.craft} lib={lib} refData={refData} wide={wide}
          onChange={function (f) { write(c.id, f); }} onRemove={function () { p.onSheetChange({ companions: C.removeCompanion(regs.rows, c.id) }); }} />;
      }) : (regs.rows.length ? <p className="hp-none">…</p> : null)}
      {regs.rows.length ? <div className="sb-e"><p className="sb-e-t sb-al-rule" data-testid="allies-direction">Direct it with one maneuver: it takes an action and a maneuver.</p></div> : null}
      <div className="sb-e"><button type="button" className="hp-commit" data-testid="door-companion" onClick={function () { p.openRoom('companion'); }}>＋ Companion</button></div>
      <SheetFollowers sheet={sheet} openHubGiven={p.openHubGiven} />
      <SheetContacts sheet={sheet} openHubGiven={p.openHubGiven} openRoom={p.openRoom} />
    </div>
  );
}
// R6 S2-followers: the people a character's Rewards bought (ALLY, APPRENTICE, FOLLOWER, SCHOOL) — each its name, "<tier> <type>",
// the registry's text (Q1 (a)) and, where its tier prints a pool, the pool and ROLL: the hub on that check (U6, rewardSpec only)
function SheetFollowers(p) {
  var sheet = p.sheet, R = window.SWRPGRewards, people = alliesPeople(sheet);
  var reg = useRewardsReg(people.length > 0);
  if (!people.length || !reg || !R) return null;
  return (
    <React.Fragment>
      <div className="sb-grp" data-testid="allies-band-followers">Followers<span>{people.length}</span></div>
      {people.map(function (r) {
        var type = rewardTypeOf(reg, r.key), tier = rewardTierOf(type, r.tier), name = r.name || (type ? type.name : r.key);
        var rs = R.rewardSpec(reg, sheet, r.id), rp = rs ? R.rewardPool(reg, sheet, r.id) : null, Dc = window.SWRPGDice;
        var sym = rs && (rs.symbols.success || rs.symbols.advantage) ? rs.symbols : null;
        var spec = rs ? (sym ? Object.assign({}, rs.spec, { _autoSymbols: sym }) : rs.spec) : null;
        return (
          <div className="sb-e" key={r.id} data-testid="ally-follower" data-rwd={r.id}>
            <div className="sb-e-h"><span className="sb-e-n">{name}</span><span className="sb-e-m">{[tier ? tier.name : '', type ? type.name : ''].filter(Boolean).join(' ')}</span></div>
            {tier && tier.text ? <p className="sb-e-t">{tier.text}</p> : null}
            {rp && Dc ? <div className="sb-rw-rollrow">
              <span className="sb-pool" data-testid="ally-follower-pool" data-pool={JSON.stringify(rp.pool)}>{Dc.renderPool(rp.pool, { size: 1 })}{sym ? Dc.renderResults(sym, { size: 1 }) : null}</span>
              <button type="button" className="sb-roll" data-testid="ally-follower-roll" aria-label={'Roll ' + name + '’s check'}
                onClick={function () { if (p.openHubGiven) p.openHubGiven({ spec: spec, label: name }); }}>Roll</button></div> : null}
          </div>);
      })}
    </React.Fragment>);
}
// ═══ R6 · CONTACT NETWORKS (card release-r6-ui §2.5; D-278 S3-contacts, S3-growth, S3-roll; ADDENDA 6, 7 D3) ═══════════════
// A network's card: its name, its Knowledge skill, Scope n · Expertise n, the pool from SWRPGRewards.contactPool only, and ROLL —
// the hub on that check, `contactSpec` its spec (U6), `kind: 'contact'` so the result offers Table 3-8 (ADDENDUM 6). The same
// card on ALLIES and on the growth room's stage (D-278 draws one). An unnamed network reads its skill as its name.
function contactRowsOf(sheet) { return window.SWRPGRewards ? window.SWRPGRewards.normalizeContacts(sheet && sheet.contacts) : []; }
function SheetContactCard(p) {
  var c = p.c, R = window.SWRPGRewards, Dc = window.SWRPGDice, sk = rewardSkillLabel(c.skill), name = c.name || sk;
  var pl = R.contactPool(c), spec = R.contactSpec(c);
  return (
    <div className="sb-e" data-testid="contact-card" data-cnt={c.id}>
      <div className="sb-e-h"><span className="sb-e-n">{name}</span>{c.name ? <span className="sb-e-m">{sk}</span> : null}</div>
      <div className="sb-e-l" data-testid="contact-ratings"><div>Scope <b>{c.scope}</b></div><div>Expertise <b>{c.expertise}</b></div></div>
      {pl && Dc ? <div className="sb-rw-rollrow">
        <span className="sb-pool" data-testid="contact-pool" data-pool={JSON.stringify(pl)}>{Dc.renderPool(pl, { size: 1 })}</span>
        {p.openHubGiven ? <button type="button" className="sb-roll" data-testid="contact-roll" aria-label={'Roll ' + name}
          onClick={function () { p.openHubGiven({ spec: spec, label: name, kind: 'contact' }); }}>Roll</button> : null}</div> : null}
    </div>);
}
// S3-contacts: ALLIES' CONTACTS band, after FOLLOWERS; always drawn — none reads 0 with its door (B-1646, D-419)
// B-1646 (D-419 item 3; ruling release-r7-bug-burndown ADDENDUM 375/381): the band is always drawn — with no network it reads
// "Contacts 0" and holds one door, ＋ ADD CONTACTS, that opens the growth room at its CONTACTS unit (where a network is bought)
function SheetContacts(p) {
  var rows = contactRowsOf(p.sheet), reg = useRewardsReg(rows.length > 0);
  if (rows.length && (!reg || !window.SWRPGRewards)) return null;
  return (
    <React.Fragment>
      <div className="sb-grp" data-testid="allies-band-contacts">Contacts<span>{rows.length}</span></div>
      {rows.length ? rows.map(function (c) { return <SheetContactCard key={c.id} c={c} openHubGiven={p.openHubGiven} />; })
        : <div className="sb-e"><button type="button" className="hp-commit" data-testid="door-contacts" onClick={function () { p.openRoom('growth', { unit: 'contacts' }); }}>＋ Add contacts</button></div>}
    </React.Fragment>);
}
// the Knowledge skills a network ties to (Endless Vigil p.76), from the skill vocabulary, named without "Knowledge ( )" as D3 draws
function contactSkillChoices() {
  return _rwdSkillEntries.filter(function (e) { return e.group === 'knowledge'; })
    .map(function (e) { return [e.id, String(e.label).replace(/^Knowledge \((.*)\)$/, '$1')]; });
}
function contactCharbuild(sheet) {
  var CR = window.CharResolve;
  return (CR && CR.talentViewFor && CR.talentViewFor(sheet)) || window._canonicalCharbuild || {};
}
// a row of today's chips (sb-critchips), one pressed — D3's skill pick
function GrowthChips(p) {
  return (
    <div className="sb-critchips sb-rw-chips" role="group" aria-label={p.aria} data-testid={p.testid}>
      {p.opts.map(function (o) { var sel = o[0] === p.cur;
        return <button key={String(o[0])} type="button" className={sel ? 'hp-commit' : 'hp-quiet'} aria-pressed={sel ? 'true' : 'false'}
          data-testid={p.testid + '-' + o[0]} onClick={function () { p.onPick(o[0]); }}>{o[1]}</button>; })}
    </div>);
}
// the roll sheet's segmented row (r4-seg), one pressed — D1's and D3's Off/On and 1–5 steps
function GrowthSeg(p) {
  return (
    <div className="sb-rw-seg" role="group" aria-label={p.aria} data-testid={p.testid}>
      {p.opts.map(function (o) { var sel = o[0] === p.cur;
        return <button key={String(o[0])} type="button" className="hp-quiet" data-on={sel ? '1' : '0'} aria-pressed={sel ? 'true' : 'false'}
          data-testid={p.testid + '-' + o[0]} onClick={function () { p.onPick(o[0]); }}>{o[1]}</button>; })}
    </div>);
}
// S3-growth: CONTACTS in the growth room — New network, then each network's Scope and Expertise raises, each BUY its price
// from SWRPGRewards.contactPrice (+5 off-career inside it, W1); ＋ GIVEN BY THE GM. D3: New network's BUY asks the skill and
// an optional name; GIVEN asks name, skill, and Scope/Expertise or Prominent in the field (prominentRatings).
function GrowthContacts(p) {
  var R = window.SWRPGRewards, sheet = p.sheet, cs = contactRowsOf(sheet), cb = contactCharbuild(sheet);
  var _n = React.useState(false), net = _n[0], setNet = _n[1], _g = React.useState(false), give = _g[0], setGive = _g[1];
  var raise = function (c, which, label, from) {
    var cost = R.contactPrice(sheet, cb, c.skill, from + 1), extra = cost > 5 * (from + 1), top = from >= 5;
    return (
      <div className="sb-buy sb-rw-buy" key={c.id + which} data-testid={'contact-raise-' + which} data-cnt={c.id}>
        <div className="sb-sk-n"><div className="sb-sk-name">{label}</div><div className="sb-sk-char">{top ? String(from) : from + ' → ' + (from + 1)}</div></div>
        <span className="sb-sk-cost" data-testid="contact-raise-cost">{top ? '' : cost + ' XP'}{!top && extra ? <span className="sb-rw-extra"> incl. +5, not a career skill</span> : null}</span>
        {top ? null : <button type="button" className="sb-sk-buy sb-rw-buybtn" data-testid="contact-raise-buy" disabled={!!p.held || p.left < cost}
          aria-label={'Raise ' + (c.name || rewardSkillLabel(c.skill)) + ' ' + label + ' to ' + (from + 1) + ', ' + cost + ' XP'}
          onClick={function () { var r = R.raiseContact(sheet, cb, c.id, which); if (r.ok) p.onSheetChange(r.patch); }}>Buy</button>}
      </div>);
  };
  return (
    <div data-testid="growth-contacts">
      <div className="sb-buy sb-rw-buy" data-testid="contact-new">
        <div className="sb-sk-n"><div className="sb-sk-name">New network</div></div>
        <span className="sb-sk-cost">10 XP</span>
        <button type="button" className="sb-sk-buy sb-rw-buybtn" data-testid="contact-new-open" disabled={!!p.held} aria-expanded={net ? 'true' : 'false'}
          aria-label="Buy a new network, 10 XP, +5 on a skill that is not a career skill" onClick={function () { setNet(!net); }}>Buy</button>
      </div>
      {net ? <ContactNetworkBuy sheet={sheet} cb={cb} left={p.left} onSheetChange={p.onSheetChange} onDone={function () { setNet(false); }} /> : null}
      {cs.map(function (c) {
        return (
          <React.Fragment key={c.id}>
            <div className="sb-grp" data-testid="contact-growth-head">{c.name || rewardSkillLabel(c.skill)}<span>{c.name ? rewardSkillLabel(c.skill) : ''}</span></div>
            {raise(c, 'scope', 'Scope', c.scope)}
            {raise(c, 'expertise', 'Expertise', c.expertise)}
          </React.Fragment>);
      })}
      <div className="sb-e"><button type="button" className="hp-quiet" data-testid="contact-give-open" aria-expanded={give ? 'true' : 'false'} onClick={function () { setGive(!give); }}>＋ Given by the GM</button></div>
      {give ? <ContactGiveForm sheet={sheet} onSheetChange={p.onSheetChange} onDone={function () { setGive(false); }} /> : null}
    </div>);
}
// D3 · New network's BUY: its Knowledge skill and optional name, then BUY at its price (SWRPGRewards.buyContact)
function ContactNetworkBuy(p) {
  var R = window.SWRPGRewards;
  var _s = React.useState(null), sk = _s[0], setSk = _s[1], _n = React.useState(''), nm = _n[0], setNm = _n[1];
  var price = sk ? R.contactPrice(p.sheet, p.cb, sk, null) : null, extra = price != null && price > 10;
  var buy = function () { var r = R.buyContact(p.sheet, p.cb, { skill: sk, name: nm }); if (r.ok) { p.onSheetChange(r.patch); p.onDone(); } };
  return (
    <div className="sb-e sb-rw-form" data-testid="contact-net-buy">
      <div className="sb-fld"><span>Skill</span><GrowthChips aria="Skill" testid="contact-net-skill" opts={contactSkillChoices()} cur={sk} onPick={setSk} /></div>
      <label className="sb-fld"><span>Name <span className="sb-e-m">Optional</span></span>
        <input className="sb-filter" type="text" aria-label="Name" data-testid="contact-net-name" value={nm} onChange={function (e) { setNm(e.target.value); }} /></label>
      <div className="sb-pk-verbs"><button type="button" className="hp-commit" data-testid="contact-net-go" disabled={!sk || p.left < price} onClick={buy}
        aria-label={'Buy the network' + (price != null ? ', ' + price + ' XP' : '')}>{'Buy · ' + (price != null ? price : '—') + ' XP'}</button></div>
      {extra ? <p className="sb-e-t sb-rw-extra" data-testid="contact-net-extra">incl. +5, not a career skill</p> : null}
    </div>);
}
// D3 · CONTACTS' ＋ GIVEN BY THE GM: name, skill, then Scope and Expertise — or Prominent in the field, the ratings from the
// skill ranks (SWRPGRewards.prominentRatings; Endless Vigil p.78). No XP (giveContact).
function ContactGiveForm(p) {
  var reveal = useRevealOnOpen();
  var R = window.SWRPGRewards, S5 = [[1, '1'], [2, '2'], [3, '3'], [4, '4'], [5, '5']];
  var _nm = React.useState(''), nm = _nm[0], setNm = _nm[1], _s = React.useState(null), sk = _s[0], setSk = _s[1];
  var _pr = React.useState(false), prom = _pr[0], setProm = _pr[1], _rk = React.useState(3), rk = _rk[0], setRk = _rk[1];
  var _hi = React.useState('scope'), hi = _hi[0], setHi = _hi[1], _sc = React.useState(1), sc = _sc[0], setSc = _sc[1], _ex = React.useState(1), ex = _ex[0], setEx = _ex[1];
  var pr = prom ? R.prominentRatings(rk, hi === 'scope') : null;
  var add = function () { var r = R.giveContact(p.sheet, { name: nm, skill: sk, scope: pr ? pr.scope : sc, expertise: pr ? pr.expertise : ex }); if (r.ok) { p.onSheetChange(r.patch); p.onDone(); } };
  var row = function (label, kid) { return <div className="sb-rw-row"><span className="sb-rw-k">{label}</span>{kid}</div>; };
  return (
    <div className="sb-e sb-rw-form" data-testid="contact-give" ref={reveal}>
      <label className="sb-fld"><span>Name</span><input className="sb-filter" type="text" aria-label="Name" data-testid="contact-give-name" value={nm} onChange={function (e) { setNm(e.target.value); }} /></label>
      <div className="sb-fld"><span>Skill</span><GrowthChips aria="Skill" testid="contact-give-skill" opts={contactSkillChoices()} cur={sk} onPick={setSk} /></div>
      {row('Prominent in the field', <GrowthSeg aria="Prominent in the field" testid="contact-give-prom" opts={[[false, 'Off'], [true, 'On']]} cur={prom} onPick={setProm} />)}
      {prom ? <React.Fragment>
          {row('Skill ranks', <GrowthSeg aria="Skill ranks" testid="contact-give-ranks" opts={S5} cur={rk} onPick={setRk} />)}
          {row('Higher', <GrowthSeg aria="Higher" testid="contact-give-hi" opts={[['scope', 'Scope'], ['expertise', 'Expertise']]} cur={hi} onPick={setHi} />)}
          <div className="sb-e-l" data-testid="contact-give-prominent"><div>Scope <b>{pr.scope}</b></div><div>Expertise <b>{pr.expertise}</b></div></div>
        </React.Fragment>
        : <React.Fragment>
          {row('Scope', <GrowthSeg aria="Scope" testid="contact-give-scope" opts={S5} cur={sc} onPick={setSc} />)}
          {row('Expertise', <GrowthSeg aria="Expertise" testid="contact-give-exp" opts={S5} cur={ex} onPick={setEx} />)}
        </React.Fragment>}
      <div className="sb-pk-verbs"><button type="button" className="hp-commit" data-testid="contact-give-add" disabled={!sk} onClick={add}>Add</button></div>
    </div>);
}
// the stage beside CONTACTS: each network as ALLIES draws it (S3-growth; W1: no price sentence)
function ContactStage(p) {
  var cs = contactRowsOf(p.sheet);
  return <div data-testid="contact-stage">{cs.length ? cs.map(function (c) { return <SheetContactCard key={c.id} c={c} openHubGiven={p.openHubGiven} />; }) : <p className="hp-none">—</p>}</div>;
}
// U10: the library in PICK mode, mounted whole in the room (`capabilities.pick`): PICK writes one `ref` row, MAKE YOUR OWN one `own`
// row (a copy of the shown record, D-278 S1-pick); the Animal Bond head shows the silhouette limit, never enforced (R-R4S-1).
function SheetCompanionPick(p) {
  var sheet = p.sheet || {}, C = window.SWRPGCompanions;
  var _L = React.useState(function () { return window.SWRPGLibrary ? window.SWRPGLibrary.NPC.Library : null; }), Lib = _L[0], setLib = _L[1];
  var _loc = React.useState('wildlife_creatures/wildlife_creatures'), loc = _loc[0], setLoc = _loc[1];
  var _ow = React.useState(null), ownRec = _ow[0], setOwnRec = _ow[1];   // D1: the record MAKE YOUR OWN opened its form on
  React.useEffect(function () { if (!Lib) ensureNpcLibrary().then(function (m) { setLib(function () { return m.NPC.Library; }); }).catch(function () {}); }, []);
  if (!Lib) return <p className="hp-none">…</p>;
  var owns = (sheet.talents || []).some(function (t) { return (t && (t.id || t)) === 'animal_bond'; });
  var head = owns ? <span data-testid="pick-head"><b>Animal Bond</b>{' · Silhouette ' + Math.floor(((p.derived && p.derived.forceRating) || 0) / 2)}</span> : null;
  var add = function (fields) { p.onSheetChange({ companions: C.addCompanion(sheet.companions, fields) }); p.onClose(); };
  return (
    <div className="sb-al-pick" data-testid="companion-pick">
      {ownRec ? <SheetCompanionOwn rec={ownRec} onCancel={function () { setOwnRec(null); }}
        onAdd={function (own, name) { add({ own: own, name: name }); }} /> : null}
      <Lib accent={NPC_LIB_ACCENT} actions={null} location={loc} onLocation={setLoc} onBack={p.onClose}
        capabilities={{ pick: { head: head,
          onPick: function (rec) { add({ ref: rec.id }); },
          onOwn: function (rec) { setOwnRec(rec); } } }} />
    </div>
  );
}
// D1 · MAKE YOUR OWN's form (D-278 S1-pick's foot; ADDENDUM 7): Name, the six characteristics, Soak, Wounds and the two
// defences, from the shown record and editable; Add writes a copy of it as `own` (`_provenance: 'custom'`, no library key)
var COMPANION_OWN_CHARS = [['brawn', 'Brawn'], ['agility', 'Agility'], ['intellect', 'Intellect'], ['cunning', 'Cunning'], ['willpower', 'Willpower'], ['presence', 'Presence']];
var COMPANION_OWN_DER = [['soak', 'Soak'], ['wounds', 'Wounds'], ['ranged_defense', 'Ranged def'], ['melee_defense', 'Melee def']];
function SheetCompanionOwn(p) {
  var rec = p.rec;
  var _v = React.useState(function () { return { name: '', ch: Object.assign({}, rec.characteristics), d: Object.assign({}, rec.derived) }; }), v = _v[0], setV = _v[1];
  var num = function (key, label, val, set) {
    return <label className="sb-fld" key={key}><span>{label}</span><input className="sb-filter" type="number" min={0} aria-label={label} data-testid={'ally-own-' + key}
      value={val == null ? 0 : val} onChange={function (e) { set(+e.target.value || 0); }} /></label>;
  };
  var add = function () {
    var own = Object.assign(JSON.parse(JSON.stringify(rec)), { characteristics: v.ch, derived: Object.assign({}, rec.derived, v.d), _provenance: 'custom' });
    delete own._source_key;
    p.onAdd(own, v.name);
  };
  return (
    <div className="sb-e sb-rw-form sb-al-own" data-testid="ally-own">
      <div className="sb-e-h"><span className="sb-e-n">Make your own</span><span className="sb-e-m">{rec.name}</span></div>
      <label className="sb-fld"><span>Name</span><input className="sb-filter" type="text" aria-label="Name" data-testid="ally-own-name" value={v.name}
        onChange={function (e) { setV(Object.assign({}, v, { name: e.target.value })); }} /></label>
      <div className="sb-al-owngrid">{COMPANION_OWN_CHARS.map(function (c) { return num(c[0], c[1], v.ch[c[0]], function (n) { var ch = Object.assign({}, v.ch); ch[c[0]] = n; setV(Object.assign({}, v, { ch: ch })); }); })}</div>
      <div className="sb-al-owngrid">{COMPANION_OWN_DER.map(function (c) { return num(c[0], c[1], v.d[c[0]], function (n) { var d = Object.assign({}, v.d); d[c[0]] = n; setV(Object.assign({}, v, { d: d })); }); })}</div>
      <div className="sb-pk-verbs"><button type="button" className="hp-quiet" data-testid="ally-own-cancel" onClick={p.onCancel}>Cancel</button>
        <button type="button" className="hp-commit" data-testid="ally-own-add" onClick={add}>Add</button></div>
    </div>);
}
// ═══ R6 · REWARDS in the growth room (card release-r6-ui §2.4; D-278 S2-growth-none/-owned/-give; ruling release-r6-ui-c0
// Q1 (a), U12; card ADDENDUM 4.2) ═══════════════════════════════════════════════════════════════════════════════════════════
// The unit after Force Powers (U12): one row per bought type, the row pressed puts the type's tiers on the stage. Every word,
// price and die resolves from rewards.json at render (Q1 (a): the registry's `name`, `text` and `cite`); the only writer is
// SWRPGRewards (buyRewardTier / giveReward). HONTITLE's BUY waits for D2's skill pick (ADDENDUM 4.2): its row draws no BUY.
var _rwdReg = null, _rwdRegP = null, _rwdSkills = {}, _rwdSkillEntries = [];   // rewards.json (+ the skill vocabulary's labels): fetched once, when a face first needs it
function loadRewardsReg() {
  if (_rwdReg) return Promise.resolve(_rwdReg);
  if (_rwdRegP) return _rwdRegP;
  _rwdRegP = Promise.all([fetch('public/data/canonical/rewards.json').then(function (r) { return r.json(); }),
    fetch('public/data/canonical/skills.vocab.json').then(function (r) { return r.json(); })])
    .then(function (a) { _rwdSkillEntries = (a[1] && a[1].entries) || []; _rwdSkillEntries.forEach(function (e) { _rwdSkills[e.id] = e.label; }); _rwdReg = a[0] || null; return _rwdReg; })
    .catch(function () { _rwdRegP = null; return null; });
  return _rwdRegP;
}
// `need` false: no fetch (a face with no reward row on it never loads the registry)
function useRewardsReg(need) {
  var _r = React.useState(_rwdReg), reg = _r[0], setReg = _r[1];
  React.useEffect(function () { if (!reg && need !== false) loadRewardsReg().then(setReg); }, [need !== false]);
  return reg;
}
function rewardSkillLabel(id) { return _rwdSkills[id] || id; }
// the crafting registries, fetched only when a face needs them (D6's Maker's Mark improvement pick)
function useCraftRegsFor(need) {
  var _c = React.useState(_craftRegs), craft = _c[0], setCraft = _c[1];
  React.useEffect(function () { if (need && !craft) loadCraftingRegistries().then(setCraft); }, [need]);
  return craft;
}
var REWARD_BUY_TYPES = ['ALLY', 'APPRENTICE', 'FOLLOWER', 'SCHOOL', 'HONTITLE'];   // the bought types, in the frame's order
var REWARD_GIVE_TYPES = ['ALLY', 'APPRENTICE', 'FOLLOWER', 'SCHOOL', 'HONTITLE', 'REP', 'MEDALS', 'MEDALSCAMP', 'DECORATIONS', 'MAKERSMARK', 'PROVENEXP',
  'BS', 'ACE', 'EXPLOIT', 'TROPHY'];   // S2-give's 11, then D6's four (ADDENDUM 7)
function rewardRowsOf(sheet) { return window.SWRPGRewards ? window.SWRPGRewards.normalizeRewards(sheet && sheet.rewards) : []; }
function rewardTypeOf(reg, key) { return (reg && reg.types && reg.types[key]) || null; }
function rewardTierOf(type, tierKey) { return ((type && type.tiers) || []).filter(function (t) { return t.key === tierKey; })[0] || null; }
// a tier's drawn dice: its pool, and the dice and symbols its mods ADD (an upgrade draws none, as the frame draws it)
function rewardTierDice(t) {
  var e = (t && t.effect) || {}, dice = {}, sym = null;
  Object.keys(e.pool || {}).forEach(function (k) { if (e.pool[k]) dice[k] = (dice[k] || 0) + e.pool[k]; });
  (e.mods || []).forEach(function (m) {
    if (m.direction !== 'add') return;
    if (m.die) dice[m.die] = (dice[m.die] || 0) + (m.count || 0);
    else if (m.symbol) { sym = sym || { success: 0, advantage: 0 }; sym[m.symbol] = (sym[m.symbol] || 0) + (m.count || 0); }
  });
  return { dice: dice, sym: sym };
}
function RewardDice(p) {
  var Dc = window.SWRPGDice, d = rewardTierDice(p.tier);
  var any = Object.keys(d.dice).some(function (k) { return d.dice[k]; });
  if (!Dc || (!any && !d.sym)) return null;
  return <span className="sb-pool sb-rw-inlpool" data-testid="reward-dice">{any ? Dc.renderPool(d.dice, { size: 1 }) : null}{d.sym ? Dc.renderResults(d.sym, { size: 1 }) : null}</span>;
}
// B-1625 (frame S2-give, D-278): a form opened inside a growth unit's body scrolls into that pane, so its last control (ADD)
// is in view on open, as drawn. The body is its own scroll pane (sheet.css `.sb-unit-body`), and the form used to open below it.
function useRevealOnOpen() {
  var ref = React.useRef(null);
  React.useEffect(function () { var el = ref.current; if (el && el.scrollIntoView) { try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { el.scrollIntoView(false); } } }, []);
  return ref;
}
function GrowthRewards(p) {
  var reg = p.reg, R = window.SWRPGRewards, rows = rewardRowsOf(p.sheet);
  var _g = React.useState(false), give = _g[0], setGive = _g[1];
  var _b = React.useState(null), buyOpen = _b[0], setBuyOpen = _b[1];   // D2: the one row whose BUY form is open
  return (
    <div data-testid="growth-rewards">
      {REWARD_BUY_TYPES.map(function (k) {
        var type = rewardTypeOf(reg, k); if (!type) return null;
        var mine = rows.filter(function (r) { return r.key === k; })[0] || null;
        var cur = mine ? rewardTierOf(type, mine.tier) : null, next = R.nextTier(reg, mine, k);
        return (
          <React.Fragment key={k}>
          <div className="sb-buy sb-rw-buy" data-testid={'reward-row-' + k} data-selected={p.sel === k ? 'true' : 'false'}>
            <button type="button" className="sb-sk-n sb-rw-press" aria-label={'Show ' + type.name} onClick={function () { p.onSel(k); }}>
              <div className="sb-sk-name">{type.name}</div>
              {mine ? <div className="sb-sk-char">{(mine.name ? mine.name + ' · ' : '') + (cur ? cur.name : '')}</div> : null}</button>
            <span className="sb-sk-cost" data-testid="reward-row-cost">{next ? next.name + ' · ' + next.xp + ' XP' : 'All three owned'}</span>
            {next ? <button type="button" className="sb-sk-buy sb-rw-buybtn" data-testid="reward-buy" disabled={!!p.held} aria-expanded={buyOpen === k ? 'true' : 'false'}
              aria-label={'Buy ' + type.name + ', ' + next.name + ', ' + next.xp + ' XP'} onClick={function () { setBuyOpen(buyOpen === k ? null : k); }}>Buy</button> : null}
          </div>
          {next && buyOpen === k ? <RewardBuyForm reg={reg} sheet={p.sheet} k={k} mine={mine} next={next} left={p.left}
            onSheetChange={p.onSheetChange} onDone={function () { setBuyOpen(null); }} /> : null}
          </React.Fragment>);
      })}
      <RewardScarRow reg={reg} sheet={p.sheet} left={p.left} held={p.held} onSheetChange={p.onSheetChange} />
      <div className="sb-e"><button type="button" className="hp-quiet" data-testid="reward-give-open" aria-expanded={give ? 'true' : 'false'} onClick={function () { setGive(!give); }}>＋ Given by the GM</button></div>
      {give ? <RewardGiveForm reg={reg} sheet={p.sheet} onSheetChange={p.onSheetChange} onDone={function () { setGive(false); }} /> : null}
    </div>);
}
// D2 (ADDENDUM 7; D-395): the BUY's form — a first Honored Title asks its kind (the two skills, rewards.json's kinds by key);
// a type not yet owned asks an optional name; then "Buy <tier> · n XP" (SWRPGRewards.buyRewardTier). An owned type asks nothing.
function RewardBuyForm(p) {
  var R = window.SWRPGRewards, k = p.k, mine = p.mine, next = p.next, type = rewardTypeOf(p.reg, k);
  var _k = React.useState(null), kind = _k[0], setKind = _k[1], _n = React.useState(''), nm = _n[0], setNm = _n[1];
  var kinds = k === 'HONTITLE' && !mine ? (type.kinds || []) : null, kd = kinds ? kinds.filter(function (x) { return x.key === kind; })[0] : null;
  var buy = function () {
    var r = R.buyRewardTier(p.sheet, p.reg, mine ? { rowId: mine.id } : { key: k, name: nm, skills: kd ? kd.skills : [] });
    if (r.ok) { p.onSheetChange(r.patch); p.onDone(); }
  };
  return (
    <div className="sb-e sb-rw-form" data-testid={'reward-buy-form-' + k}>
      {kinds ? <div className="sb-fld"><span>Title</span><GrowthChips aria="Title" testid="reward-buy-kind" cur={kind} onPick={setKind}
        opts={kinds.map(function (x) { return [x.key, x.name + ' · ' + x.skills.map(rewardSkillLabel).join(', ')]; })} /></div> : null}
      {!mine ? <label className="sb-fld"><span>Name <span className="sb-e-m">Optional</span></span>
        <input className="sb-filter" type="text" aria-label="Name" data-testid="reward-buy-name" value={nm} onChange={function (e) { setNm(e.target.value); }} /></label> : null}
      <div className="sb-pk-verbs"><button type="button" className="hp-commit" data-testid="reward-buy-go" disabled={(kinds && !kd) || p.left < next.xp} onClick={buy}
        aria-label={'Buy ' + type.name + ', ' + next.name + ', ' + next.xp + ' XP'}>{'Buy ' + next.name + ' · ' + next.xp + ' XP'}</button></div>
    </div>);
}
// D6 (ADDENDUM 7): the Battle Scar's row after the bought types — "n of 3" shown, never enforced; its BUY opens severity (each
// with its price), the talent (the book's suggestions), the severity's rule and cite, then "Buy · n XP" (buyBattleScar)
function RewardScarRow(p) {
  var R = window.SWRPGRewards, BS = rewardTypeOf(p.reg, 'BS');
  var _o = React.useState(false), open = _o[0], setOpen = _o[1], _t = React.useState(null), tier = _t[0], setTier = _t[1], _l = React.useState(null), tal = _l[0], setTal = _l[1];
  if (!BS) return null;
  var have = rewardRowsOf(p.sheet).filter(function (r) { return r.key === 'BS'; }).length, tr = rewardTierOf(BS, tier);
  var buy = function () { var r = R.buyBattleScar(p.sheet, p.reg, { tier: tier, talent: tal }); if (r.ok) { p.onSheetChange(r.patch); setOpen(false); setTier(null); setTal(null); } };
  return (
    <React.Fragment>
      <div className="sb-buy sb-rw-buy" data-testid="reward-row-BS">
        <div className="sb-sk-n"><div className="sb-sk-name">{BS.name}</div><div className="sb-sk-char" data-testid="reward-scar-max">{have + ' of ' + BS.max_shown}</div></div>
        <span className="sb-sk-cost">By severity</span>
        <button type="button" className="sb-sk-buy sb-rw-buybtn" data-testid="reward-buy" disabled={!!p.held} aria-expanded={open ? 'true' : 'false'}
          aria-label="Buy a Battle Scar" onClick={function () { setOpen(!open); }}>Buy</button>
      </div>
      {open ? <div className="sb-e sb-rw-form" data-testid="reward-buy-form-BS">
        <div className="sb-fld"><span>Severity</span><GrowthSeg aria="Severity" testid="reward-scar-tier" cur={tier} onPick={setTier}
          opts={BS.tiers.map(function (x) { return [x.key, x.name + ' ' + x.xp]; })} /></div>
        <div className="sb-fld"><span>Talent</span><GrowthChips aria="Talent" testid="reward-scar-talent" cur={tal} onPick={setTal}
          opts={BS.options.filter(function (o) { return o.suggested; }).map(function (o) { return [o.talent, o.name]; })} /></div>
        {tr ? <p className="sb-e-t sb-rw-rule" data-testid="reward-scar-rule">{tr.text}<span className="sb-e-m">{' ' + BS.cite}</span></p> : null}
        <div className="sb-pk-verbs"><button type="button" className="hp-commit" data-testid="reward-buy-go" disabled={!(tr && tal) || p.left < (tr ? tr.xp : 0)} onClick={buy}
          aria-label={'Buy the Battle Scar' + (tr ? ', ' + tr.xp + ' XP' : '')}>{'Buy · ' + (tr ? tr.xp : '—') + ' XP'}</button></div>
      </div> : null}
    </React.Fragment>);
}
// S2-give: the reward type; a Reputation takes its sphere (the row's name) and level, every other type its name. No XP.
function RewardGiveForm(p) {
  var reveal = useRevealOnOpen();
  var reg = p.reg, R = window.SWRPGRewards;
  var _k = React.useState('REP'), k = _k[0], setK = _k[1];
  var _n = React.useState(''), name = _n[0], setName = _n[1];
  var _l = React.useState('1'), level = _l[0], setLevel = _l[1];
  var _x = React.useState({}), extra = _x[0], setExtra = _x[1];   // D6: severity, talent, exploit, improvement
  var rep = rewardTypeOf(reg, 'REP'), T = rewardTypeOf(reg, k);
  var craft = useCraftRegsFor(k === 'MAKERSMARK');
  var pick = function (f, v) { var n = Object.assign({}, extra); n[f] = v; setExtra(n); };
  var first = function (list) { return list.length ? list[0][0] : ''; };
  var bsTiers = k === 'BS' && T ? T.tiers.map(function (x) { return [x.key, x.name]; }) : [];
  var bsTalents = k === 'BS' && T ? T.options.map(function (o) { return [o.talent, o.name]; }) : [];
  var exploits = k === 'EXPLOIT' && T ? T.options.map(function (o) { return [o.name, o.name]; }) : [];
  var improvements = k === 'MAKERSMARK' && craft ? [].concat.apply([], Object.keys(craft.spend.spend || {}).map(function (c) { return ((craft.spend.spend[c] || {}).improvements || []).map(function (i) { return [i.id, i.name]; }); })) : [];
  var val = function (f, list) { return extra[f] != null ? extra[f] : first(list); };
  var add = function () {
    var o = k === 'REP' ? { key: k, name: name, level: Number(level) }
      : k === 'BS' ? { key: k, name: name, tier: val('tier', bsTiers), talent: val('talent', bsTalents) }
      : k === 'EXPLOIT' ? { key: k, name: val('exploit', exploits) }
      : k === 'MAKERSMARK' ? { key: k, name: name, improvement: val('improvement', improvements) || null }
      : { key: k, name: name };
    var r = R.giveReward(p.sheet, reg, o); if (r.ok) { p.onSheetChange(r.patch); p.onDone(); }
  };
  var sel = function (f, label, list) {
    return <label className="sb-fld"><span>{label}</span><select className="sb-filter" aria-label={label} data-testid={'reward-give-' + f} value={val(f, list)}
      onChange={function (e) { pick(f, e.target.value); }}>{list.map(function (o) { return <option key={o[0]} value={o[0]}>{o[1]}</option>; })}</select></label>;
  };
  return (
    <div className="sb-e sb-rw-give" data-testid="reward-give" ref={reveal}>
      <label className="sb-fld"><span>Reward</span><select className="sb-filter" value={k} aria-label="Reward" onChange={function (e) { setK(e.target.value); }}>
        {REWARD_GIVE_TYPES.map(function (t) { var T = rewardTypeOf(reg, t); return T ? <option key={t} value={t}>{T.name}</option> : null; })}</select></label>
      {k === 'REP'
        ? <React.Fragment>
            <label className="sb-fld"><span>Sphere</span><input className="sb-filter" type="text" placeholder="Mos Eisley" aria-label="Sphere" value={name} onChange={function (e) { setName(e.target.value); }} /></label>
            <label className="sb-fld"><span>Level</span><select className="sb-filter" value={level} aria-label="Level" onChange={function (e) { setLevel(e.target.value); }}>
              {[3, 2, 1, 0, -1, -2, -3].map(function (n) { var o = ((rep && rep.options) || []).filter(function (x) { return x.level === n; })[0];
                return <option key={n} value={String(n)}>{(n > 0 ? '+' + n : String(n)) + ' · ' + (o ? o.name : '')}</option>; })}</select></label>
          </React.Fragment>
        : <React.Fragment>
            {k !== 'EXPLOIT' ? <label className="sb-fld"><span>Name</span><input className="sb-filter" type="text" aria-label="Name" value={name} onChange={function (e) { setName(e.target.value); }} /></label> : null}
            {k === 'BS' ? <React.Fragment>{sel('tier', 'Severity', bsTiers)}{sel('talent', 'Talent', bsTalents)}</React.Fragment> : null}
            {k === 'EXPLOIT' ? sel('exploit', 'Exploit', exploits) : null}
            {k === 'MAKERSMARK' ? sel('improvement', 'Improvement', improvements) : null}
            {['BS', 'ACE', 'EXPLOIT', 'TROPHY', 'MAKERSMARK'].indexOf(k) >= 0 && T && T.text ? <p className="sb-e-t sb-rw-rule" data-testid="reward-give-rule">{T.text}<span className="sb-e-m">{' ' + T.cite}</span></p> : null}
          </React.Fragment>}
      <div className="sb-pk-verbs"><button type="button" className="hp-commit" data-testid="reward-give-add" onClick={add}>Add</button></div>
    </div>);
}
// ═══ R6 · HONOURS in PROFILE (D-278 S2-honours; the band only, R-R4D-5) ══════════════════════════════════════════════════
// Every reward row that is not a person (ALLIES' FOLLOWERS) and not a Battle Scar (ABILITIES). An Honored Title reads its kind
// (SWRPGRewards.titleKindOf, from the row's two skills) and tier, then its dice and skills; a Reputation reads its level's
// option name, then its dice by skill, grouped as the registry's mods give them. Other types read their registry name.
// D7 (the band's place against Character and Duty/Obligation; each type's text line; ±1 on a sphere) waits for Jason.
var REWARD_PEOPLE = { ALLY: 1, APPRENTICE: 1, FOLLOWER: 1, SCHOOL: 1 };
function honourRowsOf(sheet) { return rewardRowsOf(sheet).filter(function (r) { return !REWARD_PEOPLE[r.key] && r.key !== 'BS'; }); }
function rewardDiceGlyphs(dice) { var Dc = window.SWRPGDice; return Dc ? <span className="sb-pool sb-rw-inlpool">{Dc.renderPool(dice, { size: 1 })}</span> : null; }
// D7 (ADDENDUM 7): under each honour, its type's line with the cite (a Reputation's level line where the book prints one), and a
// Reputation steps −/+ one through SWRPGRewards.setReputation (the book's step at an arc's end, Keeping the Peace p.92)
function HonourText(p) {
  var t = p.type, r = p.r, line = null;
  if (!t) return null;
  if (r.key === 'REP') { var o = (t.options || []).filter(function (x) { return x.level === r.level; })[0]; line = o && o.effect && o.effect.told; }
  else line = t.text || (t.effect && !Array.isArray(t.effect) && t.effect.told) || null;
  return line ? <p className="sb-e-t sb-rw-rule" data-testid="honour-text">{line}<span className="sb-e-m">{' ' + t.cite}</span></p> : null;
}
function HonourRepStep(p) {
  var R = window.SWRPGRewards, r = p.r, lv = r.level || 0;
  var set = function (n) { var o = R.setReputation(p.sheet, p.reg, r.id, n); if (o.ok) p.onSheetChange(o.patch); };
  return (
    <div className="sb-rw-row" data-testid="honour-rep-step">
      <button type="button" className="hp-quiet sb-rw-step" data-testid="honour-rep-down" disabled={lv <= -3} aria-label={'Lower ' + (r.name || 'the Reputation') + ' one step'} onClick={function () { set(lv - 1); }}>−</button>
      <b data-testid="honour-rep-level">{(lv > 0 ? '+' : '') + lv}</b>
      <button type="button" className="hp-quiet sb-rw-step" data-testid="honour-rep-up" disabled={lv >= 3} aria-label={'Raise ' + (r.name || 'the Reputation') + ' one step'} onClick={function () { set(lv + 1); }}>+</button>
    </div>);
}
function SheetHonours(p) {
  var reg = p.reg, R = window.SWRPGRewards, rows = honourRowsOf(p.sheet);
  if (!reg || !R || !rows.length) return null;
  return (
    <div className="sb-e" data-testid="prof-honours">
      <div className="sb-e-h"><span className="sb-e-n">Honours</span><span className="sb-e-m">{String(rows.length)}</span></div>
      {rows.map(function (r) {
        var type = rewardTypeOf(reg, r.key);
        if (r.key === 'HONTITLE') {
          var kind = R.titleKindOf(reg, r), tier = rewardTierOf(type, r.tier), fx = (tier && tier.effect) || {};
          var d = rewardTierDice(tier).dice, any = Object.keys(d).some(function (k) { return d[k]; });
          return (
            <div className="sb-rw-hon" key={r.id} data-testid="honour" data-rwd={r.id}>
              <div className="sb-rw-hon-h"><b>{r.name || type.name}</b><span>{[kind ? kind.name : type.name, tier ? tier.name : ''].filter(Boolean).join(', ')}</span></div>
              {tier ? <p className="sb-e-t sb-rw-rule">{fx.per === 'encounter' ? 'Once an encounter: ' : fx.per === 'session' ? 'Once a session: ' : ''}
                {any ? rewardDiceGlyphs(d) : null}{' on ' + (r.skills || []).map(rewardSkillLabel).join(' or ')}</p> : null}
              <HonourText r={r} type={type} />
            </div>);
        }
        if (r.key === 'REP') {
          var opt = ((type && type.options) || []).filter(function (o) { return o.key === r.tier; })[0] || null;
          var parts = [];   // one part per (die, count): its dice, then the skills it applies to — in the registry's order
          ((opt && opt.effect && opt.effect.mods) || []).forEach(function (m) {
            if (!m.die || m.direction !== 'add') return;
            var at = parts.filter(function (x) { return x.die === m.die && x.count === m.count; })[0];
            if (!at) { at = { die: m.die, count: m.count, skills: [] }; parts.push(at); }
            (m.applies_to || []).forEach(function (a) { if (a.scope === 'skill') at.skills.push(rewardSkillLabel(a.value)); });
          });
          return (
            <div className="sb-rw-hon" key={r.id} data-testid="honour" data-rwd={r.id}>
              <div className="sb-rw-hon-h"><b>{r.name || type.name}</b><span>{opt ? opt.name : ''}</span></div>
              {parts.length ? <p className="sb-e-t sb-rw-rule">{parts.map(function (x, i) { var dd = {}; dd[x.die] = x.count;
                return <React.Fragment key={i}>{i ? ' · ' : null}{rewardDiceGlyphs(dd)}{' ' + x.skills.join(', ')}</React.Fragment>; })}</p> : null}
              <HonourText r={r} type={type} />
              {p.onSheetChange ? <HonourRepStep r={r} reg={reg} sheet={p.sheet} onSheetChange={p.onSheetChange} /> : null}
            </div>);
        }
        return (
          <div className="sb-rw-hon" key={r.id} data-testid="honour" data-rwd={r.id}>
            <div className="sb-rw-hon-h"><b>{r.name || (type ? type.name : r.key)}</b><span>{type ? type.name : r.key}</span></div>
            <HonourText r={r} type={type} />
          </div>);
      })}
    </div>);
}
// ═══ R6 · a Battle Scar's talent in ABILITIES (D-278 S2-scar; ruling release-r6-ui-c0 L13, L14, U12) ═════════════════════
// Read ONLY through CharResolve.grantedTalentsAtRender (R-R4D-4's one core), the scar's own grants; resolved as the species'
// talents are, its meta "Battle Scar · <severity>" (the row's tier, by the registry's name). No second talent list is stored.
function sheetScarTalentsForDisplay(sheet, reg) {
  var CR = window.CharResolve;
  var cb = (CR && CR.talentViewFor && CR.talentViewFor(sheet)) || window._canonicalCharbuild || window._canonicalCharbuildBase;
  if (!cb || !CR || !CR.grantedTalentsAtRender) return [];
  var rows = rewardRowsOf(sheet), bs = rewardTypeOf(reg, 'BS');
  return CR.grantedTalentsAtRender(sheet, cb).filter(function (g) { return g.origin === 'battle_scar'; }).map(function (g) {
    var t = CR.resolveTalentEntry({ id: g.id, rank: g.rank }, cb.talents || {}, cleanMarkup);
    var row = rows.filter(function (r) { return r.id === g.reward; })[0], tier = row ? rewardTierOf(bs, row.tier) : null;
    return Object.assign({}, t, { _scarMeta: 'Battle Scar' + (tier ? ' · ' + tier.name : '') });
  });
}
// the stage beside REWARDS: the pressed type's tiers, each its registry name, price, dice and text, then the type's cite
function RewardStage(p) {
  var type = rewardTypeOf(p.reg, p.sel);
  if (!type) return <div className="hp-none">…</div>;
  return (
    <div data-testid="reward-stage" data-reward={p.sel}>
      {type.tiers.map(function (t) {
        return (
          <div className="sb-e" key={t.key} data-testid="reward-tier">
            <div className="sb-e-h"><span className="sb-e-n">{t.name}</span><span className="sb-e-m" data-testid="reward-tier-xp">{t.xp + ' XP'}</span></div>
            <p className="sb-e-t sb-rw-rule"><RewardDice tier={t} />{t.text}</p>
            <RewardTierArms tier={t} type={type} />
          </div>);
      })}
    </div>);
}
// D5 (ADDENDUM 7): a type's max SHOWN beside its name, "n of max", never enforced (R-R6RC-1)
function RewardMaxLine(p) {
  if (!p.type || p.type.max_shown == null) return null;
  var n = rewardRowsOf(p.sheet).filter(function (r) { return r.key === p.type.key; }).length;
  return <span className="sb-e-m sb-rw-max" data-testid="reward-max">{n + ' of ' + p.type.max_shown}</span>;
}
// D5: a tier's any_of as its options, "or" between — a mods arm by the dice core's glyphs and the checks it applies to; the TOLD
// arm as its line with the type's cite (Knights of Fate Q1b)
function RewardTierArms(p) {
  var fx = p.tier && p.tier.effect, arms = fx && !Array.isArray(fx) && Array.isArray(fx.any_of) ? fx.any_of : null, Dc = window.SWRPGDice;
  if (!arms) return null;
  var on = function (m) { var v = ((m.applies_to || [])[0] || {}).value || ''; return v ? (_rwdSkills[v] || (v.charAt(0).toUpperCase() + v.slice(1))) + ' checks' : ''; };
  return (
    <div className="sb-rw-arms" data-testid="reward-arms">
      {arms.map(function (arm, i) {
        var sym = {}, dice = {};
        (arm.mods || []).forEach(function (x) { if (x.symbol) sym[x.symbol] = (sym[x.symbol] || 0) + x.count; else if (x.die) dice[x.die] = (dice[x.die] || 0) + x.count; });
        return (
          <div className="sb-rw-arm" key={i} data-testid="reward-arm">{i ? <span className="sb-rw-or">or </span> : null}
            {arm.told ? <span className="sb-rw-rule">{arm.told}<span className="sb-e-m">{' ' + p.type.cite}</span></span>
              : <span className="sb-rw-inlpool">{Dc && Object.keys(sym).length ? Dc.renderResults(sym, { size: 1 }) : null}{Dc && Object.keys(dice).length ? Dc.renderPool(dice, { size: 1 }) : null}
                <span className="sb-rw-rule">{' ' + on((arm.mods || [])[0] || {})}</span></span>}
          </div>);
      })}
    </div>);
}
var SHEET_ABILITIES_PROSE_MAX = 11;   // the board's threshold: prose to eleven, the index from twelve (SHEET_SHAPE §3.4)
function sheetEntry(name, meta, children, extra) {
  return (
    <div className="sb-e" {...(extra || {})}>
      <div className="sb-e-h"><span className="sb-e-n">{name}</span>{meta != null ? <span className="sb-e-m">{meta}</span> : null}</div>
      {children}
    </div>
  );
}
// the WEAPONS card's own dice lines (R-SSC-4, frame S3, ADDENDUM 2 F6): the pool's applied changes — weaponPoolOf's change
// records, the hub's one sentence (SWRPGUI.appliedLineText) — a deficit quality named with the characteristic it is measured
// against, as the pool read it (the sheet's): "+1 difficulty — Cumbersome 4 (Brawn 3)"; "+1 boost — Accurate 1".
function sheetWeaponPoolLines(sheet, spec) {
  var words = (window.SWRPGUI || {}).appliedLineText, db = window._qualitiesDb || {}, chars = (sheet && sheet.characteristics) || {};
  if (!words || !spec) return [];
  var qm = spec.qualityMods || [];
  return (spec._poolModChanges || []).filter(function (c) { return !c.taken; }).map(function (c) {
    var q = qm.filter(function (m) { return m && m.source === c.source; })[0], src = c.source;
    var e = q ? Object.keys(db).map(function (k) { return db[k]; }).filter(function (x) { return x && x.id === q.quality; })[0] : null;
    var row = e ? (e.pool_mods || []).filter(function (r) { return r.id === q.rowId; })[0] : null;
    if (row && row.count_of && row.count_of.of === 'rating_deficit') src += ' (' + m8Cap(row.count_of.key) + ' ' + (Number(chars[row.count_of.key]) || 0) + ')';
    return words(Object.assign({}, c, { source: src }));
  });
}
function SheetStack(p) {
  var sheet = p.sheet || {}, accent = p.accent, equipment = p.equipment || {}, derived = p.derived || {};
  var cb = window._canonicalCharbuild || null;
  var Dice = window.SWRPGDice;
  var weaponsAll = equipment.weapons || [];
  var _tvEpoch = window.SWRPGTalentView.useTalentViewEpoch();   // A3-2 / B-844: a talent's dice resolve through the stamp view — a new epoch re-renders the cards' pools (weaponPoolOf, read at render)
  var rwRegS = useRewardsReg(rewardRowsOf(sheet).some(function (r) { return r.key === 'BS'; }));   // R6 S2-scar: a scar's severity word
  var alliesRegs = useAlliesRegs(sheet);   // R6: ALLIES' rows and the registries they resolve against (the head reads them too)
  var stackMode = useLayoutMode();   // THE FORCE SWITCH (frames T-a): the commit ticks take their own row at the phone, the pool line's wrap at 1024
  // the WEAPONS unit's preview is the WEAPON's own pool at its printed range — SWRPGSheetPool.weaponPoolOf, the hub row's one
  // builder (ruling f100-surfacing-sheet-concept R-SSC-4, ADDENDUM 10 C3 (a); B-1204's preview half; f100-b219-card
  // R-B219C-11 answered): range difficulty, encumbrance setback, the weapon's quality dice and the owner's, so the card's dice
  // equal the hub row's at its default band. Its ROLL opens the hub, which rolls that pool.
  var carriedW = weaponsAll.filter(function (w) { return w.state !== 'stowed'; });
  var stowedW = weaponsAll.length - carriedW.length;
  var armor = equipment.armor || null;
  var gearAll = equipment.gear || [];
  var carriedG = gearAll.filter(function (g) { return g.state !== 'stowed'; });
  var stowedG = gearAll.length - carriedG.length;
  var enc = CharResolve.computeEncumbrance(equipment, sheet.characteristics || {}, CharResolve.speciesEncumbranceDelta(sheet)) || {};
  var fr = derived.forceRating || 0;
  var credits = equipment.credits != null ? equipment.credits : ((sheet.equipment || {}).credits != null ? sheet.equipment.credits : 0);
  var xp = sheet.xp || {};
  var stat = function (label, val, after, mark) { return <div key={label}>{label} <b>{val == null || val === '' ? '—' : String(val)}{mark || null}</b>{after || null}</div>; };
  // R-SSC-1′ (d) (D-257): a card's reasons sit behind ONE "Why these numbers" press in the card's own why-sheet
  var _sw = React.useState(null), stackWhy = _sw[0], setStackWhy = _sw[1];   // { kind: 'weapon' | 'armour' | 'enc', idx, el }
  var closeStackWhy = React.useCallback(function () { setStackWhy(null); }, []);
  var whyOpenFor = function (kind, idx) { return !!stackWhy && stackWhy.kind === kind && stackWhy.idx === idx; };
  var whyButton = function (kind, idx, testid) {
    return <button type="button" className="hp-quiet" data-testid={testid} aria-haspopup="dialog" aria-expanded={whyOpenFor(kind, idx) ? 'true' : 'false'} style={{ minHeight: 44 }}
      onClick={function (e) { setStackWhy({ kind: kind, idx: idx, el: e.currentTarget }); }}>Why these numbers</button>;
  };
  // ── WEAPONS — the at-table read: what you hit with, its pool, ROLL to the hub's strip ──────────
  var weaponsBody = carriedW.length ? carriedW.map(function (w) {
    var idx = weaponsAll.indexOf(w);
    var quals = Array.isArray(w.qualities) ? w.qualities : [];
    var wSpec = Dice && Dice.buildPool ? window.SWRPGSheetPool.weaponPoolOf(sheet, w, { skillKey: resolveWeaponSkillKey(w.skillKey || w.skill || ''), equipment: equipment, accent: accent, forceCommitted: p.committed }) : null;
    var wDice = wSpec && !wSpec.barred ? Dice.buildPool(wSpec) : null;
    var td = sheetWeaponTalentDamage(sheet, w);   // F-100 M8 step 3 (R1): the hub's talent damage, on the card
    var tdTal = td && (window.SWRPGUI || {}).offerTalentsFor ? window.SWRPGUI.offerTalentsFor(sheet, td.applied) : null;   // B-1451: the line's owner's view (a species owner too)
    // B-1316 (rider R4): the Critical is the hub's figure — critRatingWith over the applied deltas AND sets, read off the one
    // pool (wSpec; sheetWeaponTalentDamage's when the dice module is absent) — and its line names an applied set with M9 F4's
    // label; a species owner's damage line gains its species, "(Trandoshan)", as the why-sheet's does
    var crS = wSpec && wSpec.critPrinted !== undefined ? wSpec : td;
    var critNow = crS && crS.critThreshold != null ? crS.critThreshold : null, critLine = crS ? diceHubCritLine(crS, sheet, true) : null;
    var cbv = (CharResolve.talentViewFor ? CharResolve.talentViewFor(sheet) : null) || cb;
    var owned = function (l) { return Object.assign({}, l, { source: diceHubRollOwnerName(l, cbv) }); };
    // the card's reasons (L12–L17, frame R4), the SAME expressions in the same order, now behind its one press: the talent
    // damage lines, the banded lines, the crit line, the item's own changes (Jury Rigged), the pool's dice lines
    var itemCh = m8ItemChanges(w, ['crit_delta', 'damage_delta']);
    var whyLines = [].concat(
      td ? td.applied.filter(function (l) { return l.amount; }).map(function (l) { return diceHubDamageLineText(owned(l), null); }) : [],
      td ? td.banded.map(function (b) { return 'At ' + b.bands.join(' or ') + ': ' + diceHubDamageLineText(owned(b.line), null); }) : [],
      critLine ? [critLine] : [],
      itemCh.map(function (f) { return m8WeaponChangeText(sheet, f, 'card'); }).filter(Boolean),
      sheetWeaponPoolLines(sheet, wSpec));
    // Damage's and Critical's marks: the net of the lines the sheet lists for each (Q-C (a), Q-B6 (a)) — the "+N on one hit"
    // raises Damage; a crit delta or SET that lowers the rating prints ↓; an item's own change counts with them
    var dmgDir = sheetWhyDir(sheetWhyNet(td ? td.total : 0, itemCh.filter(function (f) { return f.op === 'damage_delta'; })), 0);
    var critDir = sheetWhyDir(sheetWhyNet(crS && crS.critThreshold != null && crS.critPrinted != null ? crS.critThreshold - crS.critPrinted : 0,
      itemCh.filter(function (f) { return f.op === 'crit_delta'; })), 0);
    var wName = w.nickname ? '“' + w.nickname + '” · ' + w.name : w.name, critShown = critText(critNow != null ? critNow : w.crit);
    // L5 (release-r5-f1-c0, D-377): a crafted hilt with no crystal reads Crystal Needed where Damage and Critical sit
    var pw = ((sheet.equipment || {}).weapons || [])[idx];
    var hiltW = !!pw && hiltNeedsCrystal(pw._inline, pw.attachments, (sheetItemRegistries(sheet) || {}).attachments);
    return (
      <div className="sb-e" key={w.key || idx} data-testid="stack-weapon">
        <div className="sb-e-h"><span className="sb-e-n">{wName}</span><span className="sb-e-m">{w.skill || ''}</span></div>
        {/* B-1656 (D-427 item 2; page @ 8a97d5207): the fitted parts named under the head, the pack's word and names */}
        {pw && Array.isArray(pw.attachments) && pw.attachments.length ? <p className="sb-e-t" data-testid="stack-weapon-attachments" style={{ margin: '2px 0 4px' }}>
          <span className="sb-e-m">{'Attachments · '}</span>{pw.attachments.map(function (a) { return pcAttachmentName(a, (sheetItemRegistries(sheet) || {}).attachments); }).join(', ')}</p> : null}
        <div className="sb-e-l">{hiltW ? <div data-testid="hilt-needed"><b>{HILT_NEEDED}</b></div> : [<React.Fragment key="d">{stat('Damage', w.damage, null, <SheetWhyMark dir={dmgDir} />)}</React.Fragment>, <React.Fragment key="c">{stat('Critical', critShown, null, <SheetWhyMark dir={critDir} />)}</React.Fragment>]}{stat('Range', w.range)}{stat('Encumbrance', w.encumbrance)}</div>
        {/* B-1458 (D-406 item 15): the roller's own applied damage line under the card's numbers, word for word as the hub prints it.
            B-1592 (D-411 item 3, R-MIL-2 on his word): in the sheet's paper ink, --doc-ink-muted (9.56:1), not the approved blue (1.65:1) */}
        {!hiltW && td ? td.applied.filter(function (l) { return l.amount; }).map(function (l, k) {
          return <DiceHubStatusLine key={'dl' + k} m8="applied" tone="var(--doc-ink-muted)" info={diceHubLineInfo(l.talentId, l.rowId, tdTal)} infoKey={l.talentId + ':' + l.rowId}>{diceHubDamageLineText(l, null)}</DiceHubStatusLine>; }) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9, flexWrap: 'wrap' }}>
          <div className="sb-pool" data-testid="stack-weapon-pool" data-pool={wDice ? JSON.stringify(wDice) : ''}>{wDice && Dice.renderPool ? Dice.renderPool(wDice, { size: 1 }) : null}</div>
          <button type="button" className="sb-roll" data-testid={'roll-weapon-' + idx} aria-label={'Roll ' + w.name + ' — opens the dice hub'} onClick={function () { p.openHub(idx); }}>Roll</button>
          {whyLines.length ? whyButton('weapon', idx, 'why-weapon-' + idx) : null}
        </div>
        {quals.length ? <div style={{ marginTop: 7 }}>{quals.map(function (q, i) { return <span className="sb-qual" key={i}>{typeof q === 'string' ? q : (q.name + (q.rank ? ' ' + q.rank : ''))}</span>; })}</div> : null}
        {whyOpenFor('weapon', idx) ? <M7PaperSheet anchor={stackWhy.el} title={wName} meta={(w.skill || '') + (hiltW ? ' · ' + HILT_NEEDED : ' · Damage ' + w.damage + ' · Critical ' + critShown)} onClose={closeStackWhy} kind="why" testid="sheet-why-weapon">
          <SheetWhyBody whyKey={'weapon-' + idx} rows={whyLines} />
        </M7PaperSheet> : null}
      </div>
    );
  }) : <div className="hp-none">No weapons equipped.</div>;
  // ── ARMOUR — the worn suit with its real stats, and soak resolved ─────────────────────────────
  // F-100 M8 · Jury Rigged (frame J6; census F2): the suit's defence splits into Ranged / Melee ONLY when a talent made the
  // two sides differ (computeDerived's per-side read, `defense_<side> ?? defense`); each change is named under the stats
  var armDef = function (side) { return armor && armor['defense_' + side] != null ? armor['defense_' + side] : ((armor && armor.defense) || 0); };
  var armSplit = !!armor && armDef('ranged') !== armDef('melee');
  // R-SSC-1′ (d) (Q-E, Q-B4 (a)): the suit's changes behind the card's one press, drawn ONLY where a line exists; the stat a line
  // moved carries its mark. The old resolved-soak entry is gone — its cells are Soak's parts, in Soak's why-sheet on the spine.
  var armCh = armor ? m8ItemChanges(armor, ['defense_delta', 'encumbrance_delta']) : [];
  var armLines = armCh.map(function (f) { return (f.op === 'defense_delta' ? m8Cap(f.target) + ' defence ' : 'Encumbrance ') + f.from + ' → ' + f.to + ' — ' + f.source; });
  var armMark = function (op, side) { return <SheetWhyMark dir={sheetWhyDir(sheetWhyNet(0, armCh.filter(function (f) { return f.op === op && (!side || f.target === side); })), 0)} />; };
  var armourBody = armor ? (
    <React.Fragment>
      {sheetEntry(armor.name, 'Worn', <React.Fragment><div className="sb-e-l">{stat('Soak', '+' + (armor.soak || 0))}{armSplit ? stat('Ranged defence', armDef('ranged'), null, armMark('defense_delta', 'ranged')) : null}{armSplit ? stat('Melee defence', armDef('melee'), null, armMark('defense_delta', 'melee')) : stat('Defence', armDef('ranged'), null, armMark('defense_delta'))}{stat('Encumbrance', armor.encumbrance, null, armMark('encumbrance_delta'))}{stat('Hard points', armor.hp)}</div>
        {armLines.length ? <div style={{ marginTop: 9 }}>{whyButton('armour', 0, 'why-armour')}</div> : null}
        {whyOpenFor('armour', 0) ? <M7PaperSheet anchor={stackWhy.el} title={armor.name} meta="Worn" onClose={closeStackWhy} kind="why" testid="sheet-why-armour">
          <SheetWhyBody whyKey="armour" rows={armLines} />
        </M7PaperSheet> : null}</React.Fragment>, { 'data-testid': 'stack-armour' })}
    </React.Fragment>
  ) : <div className="hp-none">No armor equipped.</div>;
  // ── ABILITIES — prose to eleven, the index from twelve; the door at every count ───────────────
  var talents = (sheetTalentsForDisplay(sheet) || []).concat(sheetScarTalentsForDisplay(sheet, rwRegS));   // R6 S2-scar (L13): a scar's talent joins Talents
  var spAb = (speciesAbilitiesForDisplay(sheet) || []).concat(speciesTalentsForDisplay(sheet) || []);
  var sigs = sheetSignatureEntries(sheet);   // R-SSC-3: the Signature group counts toward the prose/index threshold
  var abilCount = talents.length + spAb.length + sigs.length;
  var actLabel = function (t) { return t._scarMeta ? t._scarMeta : t.activation ? activationLabelSheet(t.activation) + (t.ranked ? ' · Ranked' + (t.rank > 1 ? ' ' + t.rank : '') : '') : (t._ability ? 'Species' : ''); };
  var abilDoor = <div className="sb-e" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}><button type="button" className="hp-commit" data-testid="door-abilities" onClick={function () { p.openRoom('abilities'); }}>Open abilities</button></div>;
  var abilitiesBody = !abilCount ? (
    <React.Fragment><div className="hp-none">No species ability or talents on this character yet.</div>{abilDoor}</React.Fragment>
  ) : abilCount > SHEET_ABILITIES_PROSE_MAX ? (
    <React.Fragment>{abilDoor}
      <div className="sb-grp">Species<span>{spAb.length || '—'}</span></div>
      {spAb.map(function (a, i) { return <div className="sb-ax" key={'s' + i}><span className="sb-ax-n">{a.name}</span><span className="sb-ax-m">{actLabel(a)}</span></div>; })}
      <div className="sb-grp">Talents<span>{talents.length || '—'}</span></div>
      {talents.map(function (t, i) { return <div className="sb-ax" key={'t' + i}><span className="sb-ax-n">{t.name}</span><span className="sb-ax-m">{actLabel(t)}</span></div>; })}
      {sigs.length ? <div className="sb-grp" data-group="signature">Signature<span>{sigs.length}</span></div> : null}
      {sigs.map(function (g, i) { return <div className="sb-ax" key={'g' + i}><span className="sb-ax-n">{g.name}</span><span className="sb-ax-m">Signature</span></div>; })}
    </React.Fragment>
  ) : (
    <React.Fragment>
      <div className="sb-grp">Species<span>{spAb.length || '—'}</span></div>
      {spAb.length ? spAb.map(function (a, i) { return sheetEntry(a.name, sheet.species || 'Species', <p className="sb-e-t">{glyphs(cleanMarkup(a.description || ''))}</p>, { key: 's' + i, 'data-testid': 'stack-ability' }); }) : <div className="hp-none">None recorded</div>}
      <div className="sb-grp">Talents<span>{talents.length || '—'}</span></div>
      {talents.length ? talents.map(function (t, i) { return sheetEntry(t.name, actLabel(t), <p className="sb-e-t">{glyphs(cleanMarkup(t.descriptionRaw || t.description || ''))}</p>, { key: 't' + i, 'data-testid': 'stack-talent' }); }) : <div className="hp-none">None owned</div>}
      {sigs.length ? <div className="sb-grp" data-group="signature">Signature<span>{sigs.length}</span></div> : null}
      {sigs.map(function (g, i) {
        return sheetEntry(g.name, g.meta, g.ok ? <React.Fragment>
          <p className="sb-e-t">{glyphs(cleanMarkup(g.words))}</p>
          {g.activation ? <DiceHubStatusLine m8="applied" tone="var(--doc-ink-muted)">{g.activation}</DiceHubStatusLine> : null}
          {sheetToldLine(g.told, 'told')}
          {g.inferred ? sheetToldLine('XP cost not yet checked against the book', 'inf') : null}
        </React.Fragment> : null, { key: 'g' + i, 'data-testid': 'stack-signature' });
      })}
      {abilDoor}
    </React.Fragment>
  );
  // ── FORCE POWERS — only at Force rating ≥ 1; the pips are the rating; ROLL to the hub's strip ──
  var powers = fr >= 1 ? (sheetForcePowersForDisplay(sheet) || []) : [];
  // B-622 · FORCE COMMITMENT lives on the UNIT (the cockpit's zone retires with the flip): a power with an
  // ongoing effect holds a die for the session — COMMIT takes one of the rating's pips, RELEASE gives it
  // back; the pool draws the AVAILABLE dice and the hub rolls with them (`committed` reaches DiceHub).
  // Session-only, held by the shell (SheetPaper), never the record — RAW: a committed die stays committed
  // until the character chooses to release it, and rests reset nothing about it (F&D p.281).
  // F-100 M10 (R-M10B-6b): the count and the dice left read the hub's ONE function (CharResolve.forceDiceAvailable) — a
  // second FR−committed path is a defect (PRAXIS §5); the same numbers the inline arithmetic gave.
  var committedMap = p.committed || {}, committedCount = CharResolve.committedForceDice(committedMap);
  var availableFr = CharResolve.forceDiceAvailable(fr, committedMap);
  // THE FORCE SWITCH — THE COMMIT TICK (ruling `f100-surfacing-force-flip-card` ADDENDA 6, 9, 10, 12; frames T-a, D-300 (1)):
  // behind the Force key, one row per owned upgrade with an `effects` row costed `commit_force` — the hub's ONE list
  // (diceHubDurationCommits, widened) less its Duration rows — "Commit · <upgrade>" / "Release · <upgrade>" through the sheet's
  // ONE writer (onCommit → commitSetNext). Disabled with the basic button's words when no die is free, or "<name> at its
  // maximum" when one more commit would move nothing (F5, commitAtCap). A STACKING key (Farsight's Control: Perception) held
  // reads SWRPGUI.CountStep, D1 literal (F3 (b)): the label, [−] ×n [+]. Farsight's ONE tag (ADDENDUM 8 F6). The key OFF: none.
  var forceOn = !!(window.SWRPGForceRules && SWRPGForceRules.on && SWRPGForceRules.on());
  var tickCb = CharResolve.talentViewFor(sheet) || window._canonicalCharbuild;
  var forceTick = function (fp, i, nm, u) {
    var on = !!committedMap[u.key], free = availableFr >= u.dice, U = window.SWRPGUI || {};
    var capName = CharResolve.commitAtCap ? CharResolve.commitAtCap(sheet, tickCb, committedMap, u.key) : null;
    var tid = 'commit-force-' + i + '-' + u.key.split('.').pop(), who = nm + ' (' + u.label + ')';
    var tag = u.tag ? <span className="m8-tag" style={U.M8_TAG}>{u.tag}</span> : null;
    if (u.stacks && on && U.CountStep) {
      var reason = capName ? capName + ' at its maximum' : (!free ? 'No Force die free to commit' : null);
      return (
        <div key={u.key} data-tick={u.key} style={{ width: '100%' }}>
          <U.CountStep label={u.label} count={Math.floor((Number(committedMap[u.key]) || 0) / (u.dice || 1))} testid={tid} reason={reason} plusHeld={!!reason}
            onMinus={function () { p.onCommit(u.key, false); }} onPlus={function () { p.onCommit(u.key, true); }}
            minusLabel={'Release one die committed to ' + who} plusLabel={reason || ('Commit one more Force die to ' + who)} />
          {tag ? <div style={{ marginTop: 4 }}>{tag}</div> : null}
        </div>);
    }
    var dis = !on && (!free || !!capName);
    var btn = (
      <button type="button" className="hp-quiet" data-testid={tid} data-on={on ? '1' : '0'} aria-pressed={on ? 'true' : 'false'} disabled={dis}
        aria-label={on ? 'Release the die committed to ' + who : (capName ? capName + ' at its maximum' : (!free ? 'No Force die free to commit' : 'Commit a Force die to ' + who))}
        onClick={function () { p.onCommit(u.key, !on); }}>{(on ? 'Release · ' : 'Commit · ') + u.label}</button>);
    return tag ? <span key={u.key} data-tick={u.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{btn}{tag}</span>
      : React.cloneElement(btn, { key: u.key, 'data-tick': u.key });
  };
  var forceBody = powers.length ? (
    <React.Fragment>
      {fr >= 1 ? <div className="sb-grp" style={{ paddingTop: 0 }} data-testid="force-meta">{'Force rating ' + fr}<span data-testid="force-available">{committedCount ? committedCount + ' committed · ' + availableFr + ' available' : fr + ' available'}</span></div> : null}
      {powers.map(function (fp, i) {
        var nm = typeof fp === 'string' ? fp : (fp.name || 'Power');
        var ups = (fp.upgrades || []).length;
        var ck = forceCommitKey(fp), isCommitted = !!committedMap[ck], atCap = !isCommitted && committedCount >= fr;   // R-SHC-5: keyed by power
        var basicCommits = sheetPlainCommitShown(fp, tickCb, committedMap);   // B-1505 (D-402 = A)
        var ticks = forceOn && typeof fp !== 'string' && fp.id ? diceHubDurationCommits(fp, tickCb).filter(function (u) { return u.label !== 'Duration'; }) : [];
        // the meta counts every die the power's keys hold (the basic commit, its Duration, its Controls) with the key ON
        var heldN = forceOn ? Object.keys(committedMap).reduce(function (t, k) { var v = committedMap[k];
          return (k === ck || k.indexOf(ck + '.') === 0) ? t + (v === true ? 1 : (Number(v) || 0)) : t; }, 0) : 0;
        var tickEls = ticks.map(function (u) { return forceTick(fp, i, nm, u); });
        return (
          <div className="sb-e" key={'f' + i} data-testid="stack-power" data-committed={isCommitted ? '1' : '0'}>
            <div className="sb-e-h"><span className="sb-e-n">{nm}</span><span className="sb-e-m">{(ups ? ups + ' upgrade' + (ups === 1 ? '' : 's') + ' owned' : 'Basic power only') + (forceOn
              ? (heldN === 1 ? ' · a die committed' : (heldN > 1 ? ' · ' + heldN + ' dice committed' : '')) : (isCommitted ? ' · a die committed' : ''))}</span></div>
            {fp.description ? <p className="sb-e-t">{glyphs(cleanMarkup(fp.description))}</p> : null}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9, flexWrap: 'wrap' }}>
              <div className="sb-pool" data-testid={'force-pool-' + i}>{Dice && Dice.renderPool ? Dice.renderPool({ force: availableFr }, { size: 1 }) : null}{committedCount ? <span className="sb-pool-held" aria-label={committedCount + ' committed'}>{'+' + committedCount + ' held'}</span> : null}</div>
              <button type="button" className="sb-roll" data-testid={'roll-force-' + i} aria-label={'Roll ' + nm + ' with ' + availableFr + ' Force ' + (availableFr === 1 ? 'die' : 'dice') + ' — opens the dice hub'} onClick={function () { p.openHub('f' + i); }}>Roll</button>
              {p.onCommit && basicCommits ? <button type="button" className="hp-quiet" data-testid={'commit-force-' + i} data-on={isCommitted ? '1' : '0'} aria-pressed={isCommitted ? 'true' : 'false'} disabled={atCap}
                aria-label={isCommitted ? 'Release the die committed to ' + nm : (atCap ? 'No Force die free to commit' : 'Commit a Force die to ' + nm)} onClick={function () { p.onCommit(ck, !isCommitted); }}>{isCommitted ? 'Release' : 'Commit'}</button> : null}
              {p.onCommit && tickEls.length && stackMode !== 'mobile' ? tickEls : null}
            </div>
            {p.onCommit && tickEls.length && stackMode === 'mobile' ? <div data-testid={'force-ticks-' + i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9, flexWrap: 'wrap' }}>{tickEls}</div> : null}
          </div>
        );
      })}
    </React.Fragment>
  ) : <div className="hp-none">No powers yet — learn them in Advancement.</div>;
  // ── GEAR — credits, ONE pressable "Encumbrance C / L" entry, what is CARRIED, and the two doors ────────────
  // R-SSC-1′ (d)/(e), Q-D (a) (D-257): the carried count, the Container row and the items' encumbrance changes (Jury Rigged,
  // worn armour counting 3 less) open in the ONE Encumbrance sheet the spine's key opens; the "Over by" sentence stays here.
  // The limit's mark is the limit's (Q-B5 (a)): the printed limit against derivedBecause's base for it.
  var encBase = (((CharResolve.derivedBecause && sheet.characteristics) ? CharResolve.derivedBecause(sheet, equipment, cb) : {})._base || {}).encumbranceThreshold;
  var encDir = sheetWhyDir(enc.limit || 0, encBase != null ? encBase : (enc.limit || 0));
  var encShown = (enc.carried || 0) + ' / ' + (enc.limit || 0);
  var gearBody = (
    <React.Fragment>
      {sheetEntry('Credits', 'On hand', <div className="sb-e-l">{stat('Balance', credits)}</div>, { 'data-testid': 'stack-credits' })}
      <div className="sb-e" data-testid="stack-enc">
        <div className="sb-e-h" {...sheetWhyPress(sheetWhyLabel('Encumbrance', encShown, encDir), function (el) { setStackWhy({ kind: 'enc', idx: 0, el: el }); }, whyOpenFor('enc', 0))}
          style={Object.assign({ alignItems: 'center' }, SHEET_WHY_PRESS_STYLE)}><span className="sb-e-n">Encumbrance</span><span className="sb-e-m">{encShown}<SheetWhyMark dir={encDir} /></span></div>
        {enc.over > 0 ? <p className="sb-e-t"><b>{'Over by ' + enc.over}</b>{' — add ' + (enc.setbacks || enc.over) + ' setback ' + ((enc.setbacks || enc.over) === 1 ? 'die' : 'dice') + ' to all Brawn and Agility checks.'}</p> : null}
        {whyOpenFor('enc', 0) ? <SheetEncumbranceWhy sheet={sheet} equipment={equipment} enc={enc} anchor={stackWhy.el} onClose={closeStackWhy} /> : null}
      </div>
      {sheetEntry('Carried', carriedG.length + ' · ' + stowedG + ' stowed', null)}
      {/* F-100 surfacing K7 (R-SCC-3 / R6): a granted starting entitlement, one line; nothing when declined. */}
      {fcEntitlementLines(cb && cb.careers && cb.careers[CharResolve.sheetCareerId(sheet, cb.careers)], sheet.entitlements).filter(function (l) { return !l.declined; }).map(function (l) {
        return <div className="sb-e" key={'ent' + l.id} data-sheet-entitlement={l.id}><div className="sb-e-h"><span className="sb-e-n">{l.name + ' (starting entitlement)'}</span></div></div>; })}
      {carriedG.map(function (g, i) { var gi = gearAll.indexOf(g), pe = ((sheet.equipment || {}).gear || [])[gi];   // SHIP CARGO F-2: the persisted entry behind the row
        return <div className="sb-e" style={{ padding: '8px 0' }} key={'g' + i} data-testid="stack-gear" data-ref={pe && (pe.id || (pe._inline && pe._inline.id))}><div className="sb-e-h"><span className="sb-e-n" style={{ fontSize: 12 }}>{g.name}{(Number(g.qty) || 1) > 1 ? ' ×' + g.qty : ''}</span><span className="sb-e-m">{(g.type || 'Gear') + ' · enc ' + (g.encumbrance || 0)}</span></div>
          {pe && p.char ? <SheetCargoAct entry={pe} gi={gi} charId={String(p.char.id)} sheet={sheet} write={p.writeCharacter} /> : null}</div>; })}
      <div className="sb-e" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {/* THREE SIBLING DOORS, depth one (SHEET_SHAPE §3.4d): crafting a new item needs no selected item, so
            the workshop is a door on the unit beside the pack and the store — not a room inside the pack. */}
        <button type="button" className="hp-commit" data-testid="door-pack" onClick={function () { p.openRoom('pack'); }}>Open the pack</button>
        <button type="button" className="hp-quiet" data-testid="door-workshop" onClick={function () { p.openRoom('craft'); }}>Craft</button>
        {p.onOpenStore ? <button type="button" className="hp-quiet" data-testid="door-store" onClick={function () { p.onOpenStore(); }}>Add items from store</button> : null}
      </div>
    </React.Fragment>
  );
  // ── EXPERIENCE — the read and the door; the spending is the growth room's ─────────────────────
  var left = xp.remaining != null ? Number(xp.remaining) : Math.max(0, (Number(xp.total) || 0) - (Number(xp.spent) || 0));
  var xpBody = (
    <React.Fragment>
      <div className="sb-e" data-testid="stack-xp"><div className="sb-e-l">{stat('Total earned', xp.total || 0)}{stat('Spent', xp.spent || 0)}{stat('Available', left)}</div>
        {left < 0 ? <p className="hp-none" style={{ margin: '0 0 10px' }}>Overspent. Nothing may be bought until the ledger balances.</p> : null}
        <div style={{ marginTop: 11, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="hp-commit" data-testid="door-growth" onClick={function () { p.openRoom('growth'); }}>Open advancement</button>
        </div>
      </div>
    </React.Fragment>
  );
  var units = [
    { label: 'WEAPONS', count: weaponsAll.length ? String(carriedW.length) + (stowedW ? ' · ' + stowedW + ' stowed' : '') : '—', body: weaponsBody },
    { label: 'ARMOUR', count: armor ? '1' : '—', body: armourBody },
    { label: 'ABILITIES', count: abilCount || '—', body: abilitiesBody },
  ];
  if (fr >= 1) units.push({ label: 'FORCE POWERS', count: powers.length || '—', body: forceBody });
  units.push({ label: 'GEAR', count: gearAll.length ? String(carriedG.length) + (stowedG ? ' · ' + stowedG + ' stowed' : '') : '—', body: gearBody });
  // D6 / B (D-291 D6 (a)): FORTIFICATIONS after GEAR — empty = no unit
  var forts = sheet.fortifications || [];
  if (forts.length) units.push({ label: 'FORTIFICATIONS', count: String(forts.length), body: <SheetFortifications sheet={sheet} /> });
  units.push({ label: 'EXPERIENCE', count: xp.total != null ? String(xp.total) : '—', body: xpBody });
  // B-627: PROFILE and VESSELS are DOORS to rooms until their paper bodies land (B-620, B-621). Inline, the
  // legacy pages sat inside a unit body that is ~300px wide from 1024 up — the eyes read 'BACKSTORY' 134px
  // past its parent and three textareas below the reachable edge. A room gives the island the pane.
  // B-620 / B-621: PROFILE and VESSELS have their paper bodies — the lab's liturgy and the recognition rows —
  // in the unit, as the mockup draws them (B-627's doors were the interim while the legacy pages broke in a
  // 300px column). A vessel's own sheet opens as a room.
  units.push({ label: 'PROFILE', count: '—',
    body: <SheetProfile char={p.char} sheet={sheet} accent={accent} onPortrait={p.onPortrait} portraitSrc={p.portraitSrc} onSheetChange={p.onSheetChange} onEditInCreator={p.onEditInCreator} /> });
  units.push({ label: 'VESSELS', count: <SheetVesselsCount char={p.char} sheet={sheet} />,
    // SHIP YARD S4 (P8): the unit's body is the V face (V1–V4), whose ships open the Yard's SHIP room. The paper sheet is
    // the V faces' only host (R-S4G-14); the old paper face and its 'vessel' room were deleted at the S5 flip (R-S5R-18).
    body: <SheetVesselsS4 char={p.char} sheet={sheet} equipment={equipment} accent={accent} onSheetChange={p.onSheetChange} onOpenYard={p.onOpenYard} committed={p.committed} /> });
  // R6 (card release-r6-ui §2.2; ruling release-r6-ui-c0 U1, L4): ALLIES after VESSELS, always drawn — "—" when empty
  units.push({ label: 'ALLIES', count: alliesHead(sheet, alliesRegs),
    body: <SheetAllies sheet={sheet} regs={alliesRegs} onSheetChange={p.onSheetChange} openRoom={p.openRoom} openHubGiven={p.openHubGiven} /> });
  var openLabel = units.some(function (u) { return u.label === p.openLabel; }) ? p.openLabel : 'WEAPONS';
  return (
    <React.Fragment>
      {units.map(function (u) {
        var on = u.label === openLabel;
        return (
          <section key={u.label} className="sb-unit" data-open={on ? '1' : '0'}>
            <button type="button" className="hp-head" data-state={on ? 'active' : ''} aria-expanded={on ? 'true' : 'false'}
              data-testid={'sheet-unit-' + u.label.toLowerCase().replace(/ /g, '-')} onClick={function () { p.onOpen(u.label); }}>
              <span className="hp-name">{u.label}</span><span className="hp-count">{u.count}</span>
            </button>
            <div className="sb-unit-body" data-scroll="1" hidden={!on}>
              {on ? u.body : null}
            </div>
          </section>
        );
      })}
    </React.Fragment>
  );
}
function activationLabelSheet(a) {
  return { passive: 'Passive', action: 'Action', maneuver: 'Maneuver', incidental: 'Incidental',
           incidental_out_of_turn: 'Incidental (Out of Turn)', action_out_of_turn: 'Action (Out of Turn)',
           active: 'Active' }[String(a || '').toLowerCase()]   // `active`: F-100 M8 M3 (R-M8BG-225), the print's bare Active
         || String(a || '').replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
}
var SHEET_ROOM_TITLE = { pack: 'The pack', abilities: 'Abilities', growth: 'Growth', craft: 'Gear › Craft', modify: 'Gear › Modify', profile: 'Profile', vessels: 'Vessels', companion: 'Allies › Companion' };   // the walk's furniture says where it is, one level deep


// ─── B-613 · THE PACK — GEAR's managing room (SHEET_SHAPE §3.4b; ruling armor-save-shape R-ASS-1) ──
// The board's room, whole: the list left — two-up at wide, WEAPONS · PROTECTION · GEAR under heads that
// count carried and stowed, a row per item (chevron · name · state), the selected row in the selection
// ink — and the panel right: the item's name and kind, then production's OWN item panel
// (`LoadoutItemDetail`: nickname and story, attachments, MODIFY, SET STATE, SELL, DROP — the verbs Jason
// approved with SET STATE as drawn), mounted as it is. A takeover MOUNTS shared components; it never
// redraws them (§3.5) — the panel keeps its dark styling until its own paper skin (B-623), and says so
// (`data-sheet-interim="pack-panel"`). WEAR is the one verb the panel never had: a SPARE suit in the
// inventory (R-ASS-1) is listed under PROTECTION and WEAR swaps it with the worn one — worn ↔ inventory,
// no migration, the F-59 act. MODIFY and CRAFT are rooms of their own (the shell's `openRoom`).
function SheetPack(p) {
  var sheet = p.sheet || {}, equipment = p.equipment || {}, accent = p.accent;
  var persistedEq = sheet.equipment || {};
  var weapons = equipment.weapons || [], armor = equipment.armor || null, gear = equipment.gear || [];
  var rows = [];
  weapons.forEach(function (w, i) { rows.push({ kind: 'weapon', idx: i, group: 'Weapons', name: w.name, nick: (persistedEq.weapons || [])[i] && (persistedEq.weapons || [])[i].nickname, tag: w.state === 'stowed' ? 'Stowed' : 'Carried', stowed: w.state === 'stowed' }); });
  if (armor) rows.push({ kind: 'armor', idx: 0, group: 'Protection', name: armor.name, nick: persistedEq.armor && persistedEq.armor.nickname, tag: armor.state === 'worn' ? 'Worn' : (armor.state === 'stowed' ? 'Stowed' : 'Carried'), stowed: armor.state === 'stowed' });
  gear.forEach(function (g, i) {
    var spare = !!g._armorSpare;
    rows.push({ kind: 'gear', idx: i, group: spare ? 'Protection' : 'Gear', spare: spare, name: g.name, nick: (persistedEq.gear || [])[i] && (persistedEq.gear || [])[i].nickname, qty: g.qty,
                tag: (spare ? 'Spare · ' : '') + (g.state === 'stowed' ? 'Stowed' : 'Carried'), stowed: g.state === 'stowed' });
  });
  var first = rows[0] ? { kind: rows[0].kind, idx: rows[0].idx } : null;
  var _sel = React.useState(p.initialSelected || first), selected = _sel[0], setSelected = _sel[1];   // B-631: back from MODIFY lands on the item
  var _sellT = React.useState(null), sellTarget = _sellT[0], setSellTarget = _sellT[1];
  function patch(u) { if (p.onSheetChange) p.onSheetChange(u); }
  var selRow = selected ? rows.filter(function (r) { return r.kind === selected.kind && r.idx === selected.idx; })[0] : null;
  var selItem = !selected ? null : selected.kind === 'weapon' ? weapons[selected.idx] : selected.kind === 'armor' ? armor : gear[selected.idx];
  // F-100 M8 · Jury Rigged on an item NOT IN USE (frame J7): the bonus is off and the choice is kept (R-M8K-38). The display
  // carries no change for it, so each owned in_use row aimed at this entry is projected once onto a copy of it, as if in use,
  // through the one projector (ItemResolve.applyCharacterEffects), to name what returns. Never written.
  var m8Off = (function () {
    if (!selItem || !selected || !window.CharResolve || !CharResolve.ownedItemEffects || !window.ItemResolve) return [];
    var sec = selected.kind === 'weapon' ? 'weapons' : selected.kind === 'armor' ? 'armor' : 'gear', at = sec === 'armor' ? 0 : selected.idx;
    var off = sec === 'armor' ? selItem.state !== 'worn' : selItem.state === 'stowed';
    if (!off || selItem._armorSpare) return [];
    return (CharResolve.ownedItemEffects(sheet, window._canonicalCharbuild) || []).filter(function (fx) { return fx.section === sec && fx.index === at && fx.in_use === true; })
      .map(function (fx) {
        var d = { weapons: [], armor: null, gear: [] }, probe = Object.assign({}, selItem, { state: sec === 'armor' ? 'worn' : 'carried', _talentEffects: [] });
        if (sec === 'armor') d.armor = probe; else d[sec][at] = probe;
        ItemResolve.applyCharacterEffects(d, [fx]);
        var f = ((sec === 'armor' ? d.armor : d[sec][at])._talentEffects || [])[0];
        if (!f) return null;
        var what = f.op === 'crit_delta' ? 'Crit ' : f.op === 'damage_delta' ? 'Damage ' : f.op === 'encumbrance_delta' ? 'Encumbrance ' : f.op === 'defense_delta' ? m8Cap(f.target) + ' defence ' : '';
        return fx.source + ' (rank ' + fx.rank + '): ' + what + f.from + ' → ' + f.to + ' — no bonus while ' + (sec === 'armor' ? 'not worn' : 'stowed');
      }).filter(Boolean);
  })();
  function wear(spareIdx) {
    var nextGear = (persistedEq.gear || []).slice();
    var spare = nextGear[spareIdx]; if (!spare) return;
    nextGear.splice(spareIdx, 1);
    if (persistedEq.armor) {
      var off = Object.assign({}, persistedEq.armor, { state: 'carried' }); delete off.worn;
      nextGear.push(off);
    }
    patch({ equipment: Object.assign({}, persistedEq, { armor: Object.assign({}, spare, { state: 'worn' }), gear: nextGear }) });
    setSelected({ kind: 'armor', idx: 0 });
  }
  var groups = ['Weapons', 'Protection', 'Gear'];
  var counts = function (g) {
    var rs = rows.filter(function (r) { return r.group === g; });
    if (g === 'Protection') { var spares = rs.filter(function (r) { return r.spare; }).length; return (armor ? (armor.state === 'worn' ? '1 worn' : '1 carried') : '—') + (spares ? ' · ' + spares + ' spare' : ''); }
    var st = rs.filter(function (r) { return r.stowed; }).length; return (rs.length - st) + ' carried' + (st ? ' · ' + st + ' stowed' : '');
  };
  var list = groups.map(function (g) {
    var rs = rows.filter(function (r) { return r.group === g; });
    return (
      <React.Fragment key={g}>
        <div className="sb-pk-h">{g}<span>{counts(g)}</span></div>
        {rs.length ? rs.map(function (r) {
          var on = !!(selected && selected.kind === r.kind && selected.idx === r.idx);
          return (
            <div className="sb-pk-r" key={r.kind + r.idx} role="button" tabIndex={0} data-selected={on ? 'true' : 'false'} data-testid={'pack-row-' + r.kind + '-' + r.idx}
              onClick={function () { setSelected({ kind: r.kind, idx: r.idx }); }} onKeyDown={function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected({ kind: r.kind, idx: r.idx }); } }}>
              <span className="sb-pk-c">{'›'}</span>
              <span className="sb-pk-n">{r.nick ? '“' + r.nick + '” · ' + r.name : r.name}{r.qty > 1 ? ' ×' + r.qty : ''}</span>
              <span className="sb-pk-t" data-testid="pack-row-tag">{r.tag}</span>
            </div>
          );
        }) : <div className="hp-none" style={{ padding: '8px 6px' }}>{g === 'Weapons' ? 'Unarmed.' : g === 'Protection' ? 'Nothing worn.' : 'Empty pack.'}</div>}
      </React.Fragment>
    );
  });
  var credits = equipment.credits != null ? equipment.credits : (persistedEq.credits || 0);
  var panel = selItem ? (
    <React.Fragment>
      <div className="sb-e-h"><span className="sb-e-n" style={{ fontSize: 15 }} data-testid="pack-panel-name">{selItem.name}</span><span className="sb-e-m">{selItem.type || (selected.kind === 'weapon' ? 'Weapon' : selected.kind === 'armor' ? 'Armour' : 'Gear')}</span></div>
      {m8Off.length ? <div data-m8="stowed" style={{ marginTop: 8 }}>
        {m8Off.map(function (t, k) { return <DiceHubStatusLine key={k} m8="applied" glyph="▫" tone="var(--doc-ink-muted)">{t}</DiceHubStatusLine>; })}
      </div> : null}
      {selRow && selRow.spare ? (
        <div className="sb-pk-verbs" style={{ marginTop: 10 }}>
          <button type="button" className="hp-commit" data-testid="pack-wear" onClick={function () { wear(selected.idx); }}>{armor ? 'Wear — swap with ' + armor.name : 'Wear'}</button>
        </div>) : null}
      {/* B-623: the panel on paper — production's own component with `paper`; B-635: the owner block folds too. */}
      <div style={{ marginTop: 10 }}>
        <LoadoutItemDetail key={selected.kind + ':' + selected.idx} sheet={sheet} equipment={equipment} accent={accent} target={selected}
          onSheetChange={p.onSheetChange} qualitiesDb={p.qualitiesDb} onSell={function (t) { setSellTarget(t); }} onClose={function () { setSelected(null); }} onModify={function (t) { p.onOpenModify(t); }} />
      </div>
    </React.Fragment>
  ) : selected ? <div className="hp-none" data-testid="pack-not-found">{'Item not found.'}</div> : null;
  var SellSheet = window.SWRPGStore && window.SWRPGStore.SellSheet;
  var overlays = (sellTarget && SellSheet)
    ? <SellSheet item={sellTarget.item}
        unitNote={sellTarget.kind === 'gear' && (Number(sellTarget.item.qty) || 1) > 1 ? 'Sells one — ×' + sellTarget.item.qty + ' carried' : null}
        onSell={function (amount) { patch({ equipment: loadoutSellUpdate(persistedEq, sellTarget, amount) }); setSellTarget(null); setSelected(null); }}
        onClose={function () { setSellTarget(null); }} />
    : null;
  return (
    <React.Fragment>
      <div className="sb-pack" data-room="pack" data-testid="sheet-pack">
        <div className="sb-pk-list"><div className="hp-plate" data-pad="data" style={{ height: '100%', overflow: 'auto' }}>
          {list}
          <div className="sb-pk-h">Credits<span>{credits}</span></div>
          {/* B-630: the purse on paper — production's CreditsControl with `paper`; the pack has no island at rest */}
          <div className="sb-pk-purse" style={{ margin: '6px 0' }}>
            <CreditsControl value={credits} accent={accent} onChange={function (v) {
              if (typeof v === 'function') { if (p.onSheetChange) p.onSheetChange(function (prev) { var pe = (prev && prev.equipment) || {}; return Object.assign({}, prev, { equipment: Object.assign({}, pe, { credits: v(pe.credits || 0) }) }); }); }
              else patch({ equipment: Object.assign({}, persistedEq, { credits: v }) });
            }} />
          </div>
          <div className="sb-pk-verbs">
            {p.onOpenStore ? <button type="button" className="hp-quiet" data-testid="pack-store" onClick={function () { p.onOpenStore(); }}>Add items from store</button> : null}
            <button type="button" className="hp-quiet" data-testid="pack-craft" onClick={function () { p.onOpenCraft(); }}>Craft</button>
          </div>
        </div></div>
        <div className="sb-pk-panel"><div className="hp-plate" data-pad="read" style={{ height: '100%', overflow: 'auto' }}>{panel}{overlays}</div></div>
      </div>
    </React.Fragment>
  );
}


// ─── B-614 · ABILITIES — the room (R-PSC-3 as amended by Jason's approval: the GROUP READ) ─────────
// THE PACK's pattern, mounted rather than reinvented: the index on the left — TALENTS with the tier
// they sit on, SPECIES (the species' abilities and its granted talents), FORCE POWERS — as jump rows,
// two-up at wide, under a filter; and on the right the whole selected GROUP read at once: each entry
// with its activation in the app's own words, ranked or not (and the rank held), the row it sits on,
// the books that print it, and the text. The selected group's head takes the selection ink; a row
// press selects its group and scrolls its entry into view. Production's ABILITIES tab (index ‖ reading
// with a filter) is what this room IS — the same three derivations, the board's arrangement.
function sheetTalentTiers(sheet) {
  // talent id → the 1-based row it sits on in an OWNED specialization tree (the first owned spec that
  // holds it, by the positions the record keeps); a talent owned outside any tree carries no tier.
  // F-100 M5 C4.1: through the sheet's STAMP view (SWRPGTalentView.sheetTalentView); discovery only in C4.4's fallback.
  var cb = window.SWRPGTalentView.sheetTalentView(sheet) || window._canonicalCharbuild || null; var out = {};
  if (!cb || !cb.specializations) return out;
  (sheet.specializations || []).forEach(function (sp) {
    var rec = sp && sp.id ? cb.specializations[sp.id] : null; if (!rec || !Array.isArray(rec.talent_tree)) return;
    (sp.owned || []).forEach(function (pos) {
      var m = /^(\d+)-(\d+)$/.exec(String(pos)); if (!m) return;
      var row = rec.talent_tree[Number(m[1])]; var cell = row && row.talents ? row.talents[Number(m[2])] : null;
      if (cell && cell.talent && out[cell.talent] == null) out[cell.talent] = Number(m[1]) + 1;
    });
  });
  return out;
}
function SheetAbilitiesRoom(p) {
  var sheet = p.sheet || {}, accent = p.accent;
  var talents = sheetTalentsForDisplay(sheet) || [];
  var spAb = (speciesAbilitiesForDisplay(sheet) || []).concat(speciesTalentsForDisplay(sheet) || []);
  var powers = (derivedForceRatingOf(sheet) >= 1 ? (sheetForcePowersForDisplay(sheet) || []) : []);
  var _tvEpoch = window.SWRPGTalentView.useTalentViewEpoch();   // F-100 M5 C4.6
  var tiers = React.useMemo(function () { return sheetTalentTiers(sheet); }, [sheet.specializations, sheet.mods, _tvEpoch]);
  // B-986: every book prints through the ONE shared resolver (`SWRPGSources.sourceLabel`, by way of `gameLineTitle`,
  // which strips only the core books' " Core Rulebook" suffix) — the approved board's "Force and Destiny", "Special
  // Modifications". `BOOK_LABELS` knew only the three cores, so any other book printed its raw key.
  var bookOf = function (srcs) { return Array.isArray(srcs) && srcs.length ? srcs.map(gameLineTitle).join(', ') : (sheet.book ? gameLineTitle(sheet.book) : ''); };
  var groups = [];
  var toldOf = sheetAbilityToldWords(sheet);   // R7 B-1457: each entry's told rows, as the signature entries print theirs
  if (talents.length) groups.push({ key: 'talents', label: 'Talents', items: talents.map(function (t, i) {
    var tier = tiers[t.id]; return { id: 't' + i, name: t.name, tag: tier ? 'Tier ' + tier : 'Talent', desc: t.descriptionRaw || t.description || '',   // B-1033 (1): a legacy save keeps its dice tokens only in descriptionRaw
      meta: [activationLabelSheet(t.activation || 'passive'), t.ranked ? ('Ranked' + (t.rank > 1 ? ' · rank ' + t.rank : '')) : 'Not ranked', tier ? 'Row ' + tier : null].filter(Boolean).join(' · '), src: bookOf(t.sources),
      told: toldOf.talent[t.id] }; }) });
  if (spAb.length) groups.push({ key: 'species', label: 'Species', items: spAb.map(function (a, i) {
    return { id: 's' + i, name: a.name, tag: a._ability ? 'Ability' : 'Talent', desc: a.description || '',
      meta: a._ability ? (sheet.species || 'Species') + ' ability' : [activationLabelSheet(a.activation || 'passive'), a.ranked ? 'Ranked' : 'Not ranked', (sheet.species || 'Species') + ' talent'].join(' · '), src: bookOf(a.sources),
      told: a._ability ? toldOf.species[a.name] : toldOf.talent[a.id] }; }) });
  if (powers.length) groups.push({ key: 'force', label: 'Force powers', items: powers.map(function (fp, i) {
    var ups = (fp.upgrades || []).length; return { id: 'f' + i, name: typeof fp === 'string' ? fp : fp.name, tag: ups ? ups + ' upgrade' + (ups === 1 ? '' : 's') : 'Basic power', desc: fp.description || '',
      meta: ups ? 'Basic power · ' + (fp.upgrades || []).join(', ') : 'Basic power only', src: bookOf(fp.sources) }; }) });
  // R-SSC-3 (frame S6): the Signature group — one row per owned ability ("<n> upgrades"), the reading panel its base entry
  // (meta "Base ability · <upgrade names> · <book>", the words, the activation, the told line) and one entry per owned
  // upgrade cell ("Upgrade · <cost> XP", "(not yet checked against the book)" only for an inferred cell). DARK as the stack's.
  var sigs = sheetSignatureEntries(sheet);
  if (sigs.length) groups.push({ key: 'signature', label: 'Signature', items: sigs.map(function (g, i) {
    var src = bookOf(g.sources);
    return g.ok ? { id: 'g' + i, name: g.name, tag: g.upgrades + ' upgrade' + (g.upgrades === 1 ? '' : 's'), desc: g.words,
      meta: ['Base ability', g.upgradeCells.map(function (u) { return u.name; }).join(', ')].filter(Boolean).join(' · '), src: src,
      lines: g.activation ? [g.activation] : [], told: [g.told].concat(g.inferred ? ['XP cost not yet checked against the book'] : []),
      subs: g.upgradeCells.map(function (u) { return { name: u.name, meta: 'Upgrade · ' + u.cost + ' XP' + (u.inferred ? ' (not yet checked against the book)' : ''), desc: u.words }; }) }
      : { id: 'g' + i, name: g.name, tag: 'Not standing', desc: '', meta: g.meta, src: src }; }) });
  var _g = React.useState(0), gi = _g[0], setGi = _g[1];
  var _q = React.useState(''), q = _q[0], setQ = _q[1];
  var needle = q.trim().toLowerCase();
  var total = groups.reduce(function (n, g) { return n + g.items.length; }, 0);
  var listRef = React.useRef(null), readRef = React.useRef(null);
  function jump(gIdx, id) {
    setGi(gIdx);
    window.setTimeout(function () { var el = readRef.current && readRef.current.querySelector('[data-entry="' + id + '"]'); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' }); }, 0);
  }
  if (!total) {
    return (
      <div className="sb-pack" data-room="abilities" data-testid="sheet-abilities-room">
        <div className="sb-pk-list"><div className="hp-plate" data-pad="data" style={{ height: '100%', overflow: 'auto' }}>
          <div className="hp-none">No species ability or talents on this character yet.</div></div></div>
        <div className="sb-pk-panel"><div className="hp-plate" data-pad="read" style={{ height: '100%' }}><div className="hp-none">Nothing to read.</div></div></div>
      </div>);
  }
  var g = groups[Math.min(gi, groups.length - 1)];
  return (
    <div className="sb-pack" data-room="abilities" data-reading="group" data-testid="sheet-abilities-room">
      <div className="sb-pk-list"><div className="hp-plate" data-pad="data" style={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }} ref={listRef}>
        <input className="sb-filter" type="search" data-testid="abilities-filter" value={q} aria-label={'Filter ' + total + ' abilities'} placeholder={'Filter ' + total + ' abilities'} onChange={function (e) { setQ(e.target.value); }} />
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'grid', gridTemplateColumns: 'inherit' }} className="sb-ab-index">
          {groups.map(function (grp, idx) {
            var items = needle ? grp.items.filter(function (it) { return it.name.toLowerCase().indexOf(needle) !== -1; }) : grp.items;
            if (!items.length) return null;
            var on = idx === Math.min(gi, groups.length - 1);
            return (
              <React.Fragment key={grp.key}>
                <div className="sb-pk-h" role="button" tabIndex={0} data-selected={on ? 'true' : 'false'} data-group={grp.key} data-testid={'abilities-group-' + grp.key}
                  onClick={function () { setGi(idx); }} onKeyDown={function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGi(idx); } }}>{grp.label}<span>{items.length}</span></div>
                {items.map(function (it) {
                  return (
                    <div className="sb-pk-r" key={it.id} role="button" tabIndex={0} data-testid={'abilities-row-' + it.id}
                      onClick={function () { jump(idx, it.id); }} onKeyDown={function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); jump(idx, it.id); } }}>
                      <span className="sb-pk-c">{'›'}</span><span className="sb-pk-n">{it.name}</span><span className="sb-pk-t">{it.tag}</span>
                    </div>);
                })}
              </React.Fragment>);
          })}
          {needle && !groups.some(function (grp) { return grp.items.some(function (it) { return it.name.toLowerCase().indexOf(needle) !== -1; }); })
            ? <div className="hp-none" style={{ gridColumn: '1 / -1' }}>{'No ability matches "' + q.trim() + '".'}</div> : null}
        </div>
      </div></div>
      <div className="sb-pk-panel"><div className="hp-plate" data-pad="read" style={{ height: '100%', overflow: 'auto' }} ref={readRef} data-testid="abilities-reading">
        <div className="sb-grp">{g.label}<span>{g.items.length}</span></div>
        {g.items.reduce(function (acc, it) {
          // a signature item's owned upgrade cells read as their own entries, right after its base entry (frame S6)
          return acc.concat([
            <div className="sb-e" key={it.id} data-entry={it.id} data-testid="abilities-entry">
              <div className="sb-e-h"><span className="sb-e-n">{it.name}</span><span className="sb-e-m">{it.meta}{it.src ? ' · ' + it.src : ''}</span></div>
              {it.desc ? <p className="sb-e-t">{glyphs(cleanMarkup(it.desc))}</p> : <p className="hp-none">No text on file.</p>}
              {(it.lines || []).map(function (t, k) { return <DiceHubStatusLine key={'l' + k} m8="applied" tone="var(--doc-ink-muted)">{t}</DiceHubStatusLine>; })}
              {(it.told || []).map(function (t, k) { return sheetToldLine(t, 'k' + k); })}
            </div>], (it.subs || []).map(function (u, k) {
            return (
              <div className="sb-e" key={it.id + 'u' + k} data-entry={it.id + 'u' + k} data-testid="abilities-entry">
                <div className="sb-e-h"><span className="sb-e-n">{u.name}</span><span className="sb-e-m">{u.meta}</span></div>
                {u.desc ? <p className="sb-e-t">{glyphs(cleanMarkup(u.desc))}</p> : null}
              </div>); }));
        }, [])}
      </div></div>
    </div>
  );
}
// R-SHC-5 (ruling f100-surfacing-hub-concept, as errata'd by ADDENDUM 7 C2) — THE COMMITTED SET, keyed by POWER, not by
// the stack's index: '<power id>' = the sheet's basic commit (one die), '<power id>.<ability key>' = a Duration commit (n
// dice, CharResolve.commitValueOf → durationCommitDice). A legacy power with no id keys by its name.
// B-1505 (D-402 = A; ruling `release-r7-bug-burndown` ADDENDUM 111) — THE SHEET'S PLAIN COMMIT SHOWS ONLY WHERE THE BASIC POWER'S
// OWN WORDS COMMIT (CharResolve.basicPowerCommits over the record the sheet resolves through: Endure today, Knights of Fate p.36);
// a die already held under the power's key keeps its Release. A bought upgrade's commit is the tick's (diceHubDurationCommits),
// untouched. Pure, so test/b1505_basic_commit.cjs executes it over every shipped power.
function sheetPlainCommitShown(fp, cb, committed) {
  if (committed && committed[forceCommitKey(fp)]) return true;
  var rec = fp && typeof fp !== 'string' && fp.id && cb && cb.forcePowers ? cb.forcePowers[fp.id] : null;
  return !!(rec && window.CharResolve && CharResolve.basicPowerCommits && CharResolve.basicPowerCommits(rec));
}
function forceCommitKey(fp) {
  return typeof fp === 'string' ? fp : String((fp && (fp.id || fp.name)) || '');
}
// THE ONE WRITER's step (pure, so a guard executes it): the next committed set after `on`/off at `key`. A commit the free
// dice cannot hold (forceDiceAvailable < its dice) or whose value is unknown is REFUSED — `prev` comes back unchanged
// (the barred state, R-SHC-9). Session only, never the record (B-622).
// THE STACKING COMMIT AND THE CAP (ruling `f100-surfacing-force-flip-card` ADDENDA 10 and 12 F5; D-300 (3)) — the table:
//   on,  not held, any key          → refused when no die is free, or when commitAtCap (one more would move nothing: F5, EVERY
//                                     commit-costed key); else the key holds ONE commit's dice (commitValueOf)
//   on,  held,     not stacking     → refused (a key commits once)
//   on,  held,     stacking         → refused when no die is free or at the cap; else the value grows by ONE commit's dice
//   off, not held                   → nothing
//   off, held,     stacking, > one  → the value shrinks by one commit's dice (release one die)
//   off, held,     otherwise        → the key is deleted (as before)
function commitSetNext(prev, key, on, sheet, forceRating) {
  prev = prev || {};
  if (!key) return prev;
  var next = Object.assign({}, prev), held = Object.prototype.hasOwnProperty.call(prev, key);
  var cb = CharResolve.talentViewFor(sheet) || window._canonicalCharbuild;
  var v = CharResolve.commitValueOf(key, cb), dice = v === true ? 1 : v;
  var stacks = !!(CharResolve.commitStacks && CharResolve.commitStacks(key, cb));
  if (!on) {
    if (!held) return prev;
    if (stacks && typeof prev[key] === 'number' && typeof dice === 'number' && prev[key] > dice) { next[key] = prev[key] - dice; return next; }
    delete next[key]; return next;
  }
  if (held && !stacks) return prev;
  if (v == null || CharResolve.forceDiceAvailable(forceRating, prev) < dice) return prev;
  if (CharResolve.commitAtCap && CharResolve.commitAtCap(sheet, cb, prev, key)) return prev;
  next[key] = stacks ? (Number(prev[key]) || 0) + dice : v;
  return next;
}
function derivedForceRatingOf(sheet) {
  try { return CharResolve.computeDerived(sheet, null, window._canonicalCharbuild).forceRating || 0; } catch (e) { return 0; }
}

// THE SPECIES SWITCH'S ONE-TIME LINE (ruling `f100-surfacing-species-flip-card` R-SFL-3, ADDENDA 4 F5 and 14; D-229 N1 (b) ·
// W1 (a); the frames' N1-b in Docs/mockups/f100-surfacing-species-flip.html). The B-1190 notice idiom as HouseRuleNotices
// draws it (.sb-notice, "Got it"), mounted at the sheet's head. Every word and the predicate are CharResolve's
// (speciesFlipNotice); "Got it" is the seen-stamp's ONE writer (speciesFlipSeenPatch → `sheet.rules_seen.species`), through
// the sheet's own write-through. A sheet with no writer still hides the line on the tap, for this mount only.
function SpeciesFlipNotice({ sheet, onSheetChange }) {
  var _gone = React.useState(false), gone = _gone[0], setGone = _gone[1];
  var CR = window.CharResolve;
  var n = (!gone && CR && CR.speciesFlipNotice) ? CR.speciesFlipNotice(sheet, window._canonicalCharbuild) : null;
  if (!n) return null;
  return h('div', { className: 'sb-notice', role: 'status', 'data-testid': 'species-flip-notice', 'data-lines': n.lines },
    h('span', { className: 'sb-notice-t' }, n.words),
    h('span', { className: 'sb-notice-a' }, h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'species-flip-notice-ok',
      onClick: function () { setGone(true); if (onSheetChange) onSheetChange(CR.speciesFlipSeenPatch(sheet)); } }, 'Got it')));
}
// THE ENGINE SWITCH'S ONE-TIME LINE (ruling `f100-surfacing-engine-flip-card` R-EFL-4 as ruled at ADDENDA 9 and 12; D-284 (1)
// the words, F10 under the species line). The same idiom as SpeciesFlipNotice, its own key, line and stamp: the predicate and
// every word are CharResolve's (engineFlipNotice, handed the paper's resolved equipment, the sheet's own items view and the
// qualities vocabulary); "Got it" is the stamp's ONE writer (engineFlipSeenPatch → `sheet.rules_seen.engine`).
function EngineFlipNotice({ sheet, onSheetChange, equipment }) {
  var _gone = React.useState(false), gone = _gone[0], setGone = _gone[1];
  var CR = window.CharResolve;
  var regs = sheetItemRegistries(sheet);   // B-852: the view THIS SHEET owns, never discovery
  var n = (!gone && CR && CR.engineFlipNotice) ? CR.engineFlipNotice(sheet, window._canonicalCharbuild, { equipment: equipment,
    registries: regs, qualities: window._qualitiesDb }) : null;
  if (!n) return null;
  return h('div', { className: 'sb-notice', role: 'status', 'data-testid': 'engine-flip-notice', 'data-lines': n.lines },
    h('span', { className: 'sb-notice-t' }, n.words),
    h('span', { className: 'sb-notice-a' }, h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'engine-flip-notice-ok',
      onClick: function () { setGone(true); if (onSheetChange) onSheetChange(CR.engineFlipSeenPatch(sheet)); } }, 'Got it')));
}
// THE FORCE SWITCH'S ONE-TIME LINE (ruling `f100-surfacing-force-flip-card` R-FFL-4 as amended at ADDENDA 4 and 6, ruled at
// ADDENDUM 12; D-292 (1) the words), UNDER the engine line — its own key, line and stamp. The predicate and every word are
// CharResolve's (forceFlipNotice, handed the sheet's pool index for the dials' context); "Got it" is the stamp's ONE writer
// (forceFlipSeenPatch → `sheet.rules_seen.force`).
function ForceFlipNotice({ sheet, onSheetChange }) {
  var _gone = React.useState(false), gone = _gone[0], setGone = _gone[1];
  var CR = window.CharResolve;
  var n = (!gone && CR && CR.forceFlipNotice) ? CR.forceFlipNotice(sheet, window._canonicalCharbuild, { poolIndex: poolModSkillIndex() }) : null;
  if (!n) return null;
  return h('div', { className: 'sb-notice', role: 'status', 'data-testid': 'force-flip-notice', 'data-lines': n.lines },
    h('span', { className: 'sb-notice-t' }, n.words),
    h('span', { className: 'sb-notice-a' }, h('button', { type: 'button', className: 'hp-quiet', 'data-testid': 'force-flip-notice-ok',
      onClick: function () { setGone(true); if (onSheetChange) onSheetChange(CR.forceFlipSeenPatch(sheet)); } }, 'Got it')));
}
function SheetPaper(p) {
  var sheet = p.sheet, char = p.char, accent = p.accent;
  var mode = useLayoutMode();                    // the sheet's two modes; the board's bands are container queries in sheet.css
  var canonReady = useCanonicalReady();          // the same three lines SheetCombat derives from — one core, two call sites
  var equipment = React.useMemo(function () { return resolveSheetEquipment(sheet.equipment, sheet); }, [sheet, canonReady]);
  var _tvEpoch = window.SWRPGTalentView.useTalentViewEpoch();   // F-100 M5 C4.6 / B-844: the memo keys on the stamp view's epoch
  var derived = React.useMemo(function() { return CharResolve.computeDerived(sheet, equipment, window._canonicalCharbuild); }, [sheet, equipment, _tvEpoch]);
  // (the spine's figures live in SheetSpine — B-610; the stage's in SheetStage — B-611)
  // THE OPEN UNIT rides CharacterSheet's page state (by LABEL — the vocabulary `_yard_jump_page` speaks):
  // GEAR · ABILITIES · VESSELS · PROFILE name their units; ADVANCEMENT opens the growth room;
  // OVERVIEW (the landing label) means the first unit, WEAPONS. The ROOMS are a state of this shell.
  var _room = React.useState(null), room = _room[0], setRoom = _room[1];
  var _roomArg = React.useState(null), roomArg = _roomArg[0], setRoomArg = _roomArg[1];   // B-621: which vessel
  var _cm = React.useState({}), committed = _cm[0], setCommitted = _cm[1];              // B-622: the session's committed Force dice, by power (R-SHC-5) — never the record
  // R-SHC-5: the ONE writer, handed to the stack's Commit/Release AND the hub (its Duration spend row) — one key shape
  var onCommit = function (key, on) { setCommitted(function (prev) { return commitSetNext(prev, key, on, sheet, derived.forceRating || 0); }); };
  React.useEffect(function () {
    if (p.openLabel === 'ADVANCEMENT') { setRoom('growth'); p.onOpen('EXPERIENCE'); }
  }, [p.openLabel]);
  // B-616: A ROOM OPENS AT ITS HEAD. Below wide the wrap is the page's scroller, and a door pressed 800px
  // down left the room's head off-screen. The wrap goes to the top when a room opens and comes back to
  // where the door was when it closes, so Back to the sheet lands on the unit that was open.
  var wrapRef = React.useRef(null), wrapScrollRef = React.useRef(0);
  React.useEffect(function () {
    var w = wrapRef.current; if (!w) return;
    if (room) w.scrollTop = 0;                 // the door saved the offset before the room's commit
    else w.scrollTop = wrapScrollRef.current;
  }, [room]);
  // the door saves the offset SYNCHRONOUSLY — an effect after the takeover's commit read a wrap that the
  // browser had already re-laid (it read 0 every time)
  var openRoom = function (r, arg) { if (wrapRef.current) wrapScrollRef.current = wrapRef.current.scrollTop; setRoomArg(arg == null ? null : arg); setRoom(r); };
  React.useEffect(function () { fcUpdateScrollCues(); });   // B-617: the cue after every render of the sheet, as the creator does
  var openLabel = (p.openLabel === 'OVERVIEW' || !p.openLabel) ? 'WEAPONS' : p.openLabel;
  var name = String((sheet ? sheet.name : char.name) || '');
  var quiet = function (label, onClick, extra) {
    return React.createElement('button', Object.assign({ type: 'button', className: 'hp-quiet', onClick: onClick }, extra || {}), label);
  };
  // B-1594 (D-411 items 17–18; 255's rider): the PDF's failure line, under the running head. PDF pressed again and RETRY
  // clear it first, so a RETRY that succeeds leaves it gone; it is this shell's state, so leaving the sheet clears it.
  var _pdfFail = React.useState(null), pdfFail = _pdfFail[0], setPdfFail = _pdfFail[1];
  var liveRef = React.useRef(true);
  React.useEffect(function () { liveRef.current = true; return function () { liveRef.current = false; }; }, []);
  var makePdf = function () {
    if (!sheet) return;
    setPdfFail(null);
    downloadCharacterPDF(sheet).then(function (cause) { if (liveRef.current) setPdfFail(cause); });
  };
  return (
    <div className="hp-sheet sb-shell" data-doc-ground="creator" data-sheet-shell="1" data-band={mode}>
      <div className="sb-surface"><div className="sbwrap" ref={wrapRef}>
        <div className="sb-runhead">
          {/* B-629: the CONTEXTUAL BUTTON that calls the app's bars (the edge of the screen calls them too). It
              replaced the TABLE MODE toggle: the table is the default state now. MENU, first on the left, before
              BACK (Jason 2026-09-16: "change 'Nav' to 'Menu' and put it in front of the 'Back' on the left side"). */}
          {p.onSummonChrome ? quiet('Menu', p.onSummonChrome, { 'data-testid': 'chrome-summon', 'data-chrome-summon': '1', 'aria-pressed': p.chromePinned ? 'true' : 'false',
            'aria-label': p.chromePinned ? 'Hide the app menu bars' : 'Show the app menu bars (or move the pointer to the top or bottom edge of the screen)' }) : null}
          {p.onBack ? quiet('Back', p.onBack, { 'data-testid': 'sheet-back', 'aria-label': 'Back' }) : null}
          <span className="sb-runhead-t">Character Sheet</span>
          <span className="sb-runhead-t sb-runhead-name" data-testid="sheet-runhead-name">{name}</span>
          <span className="sb-runhead-r">
            {p.onOpenStore ? quiet('Store', function () { p.onOpenStore(); }, { 'aria-label': 'Open Session Store' }) : null}
            {p.onSave ? quiet(p.isSaved ? 'Saved' : 'Save', function () { p.onSave(); }, { 'data-testid': 'sheet-save', 'data-tour-id': 'p-sheet-save',
              'aria-label': p.isSaved ? undefined : 'Save to roster' }) : null}
            {quiet('PDF', makePdf, { 'aria-label': 'Download character sheet PDF' })}
            <button type="button" className="hp-commit" data-testid="dice-hub-open" aria-haspopup="dialog" aria-expanded={p.hubOpen ? 'true' : 'false'}
              aria-label="Open the dice hub — every pool on this character, and why each is what it is" onClick={function () { p.setHubOpen(true); }}>Dice</button>
          </span>
        </div>
        {/* B-1594: frame D1's notice, directly after the running head, as the 255 page drew it. Only a failed LOAD offers RETRY. */}
        {pdfFail ? (
          <div className="sb-notice" role="status" data-testid="pdf-notice" data-pdf-fail={pdfFail}>
            <span className="sb-notice-t">{pdfFail === 'load' ? PDF_LOAD_LINE : PDF_MAKE_LINE}</span>
            {pdfFail === 'load' ? <span className="sb-notice-a"><button type="button" className="hp-quiet" data-testid="pdf-notice-retry" onClick={makePdf}>Retry</button></span> : null}
          </div>
        ) : null}
        {/* R-SFL-3 (D-229 N1 (b)): the species switch's one-time line, directly after the running head — the sheet's first view */}
        <SpeciesFlipNotice sheet={sheet} onSheetChange={p.onSheetChange} />
        {/* R-EFL-4 (D-284 (1), F10): the engine switch's one-time line, UNDER the species line — its own key, line and stamp */}
        <EngineFlipNotice sheet={sheet} onSheetChange={p.onSheetChange} equipment={equipment} />
        {/* R-FFL-4 (D-292 (1)): the Force switch's one-time line, UNDER the engine line — its own key, line and stamp */}
        <ForceFlipNotice sheet={sheet} onSheetChange={p.onSheetChange} />
        {/* B-656: the ask MOVED into the spine — it sits WITH THE IDENTITY now (SheetSpine), not in the
            seam under the running head. The frame: `.sb-instr` > `.sb-ident` > the ask > the rail. */}
        {room ? (
          <div className="sb-over" data-fill="1" data-sheet-room={room}>
            <div className="sb-over-h"><span className="sb-over-t">{SHEET_ROOM_TITLE[room]}</span>
              <span className="sb-over-r"><button type="button" className="hp-quiet" data-testid="room-back" onClick={function () { setRoom(null); }}>Back to the sheet</button></span></div>
            {room === 'pack'
              ? <SheetPack sheet={sheet} equipment={equipment} accent={accent} onSheetChange={p.onSheetChange} qualitiesDb={p.qualitiesDb} onOpenStore={p.onOpenStore} onOpenCraft={function () { openRoom('craft'); }} onOpenModify={function (t) { openRoom('modify', t); }} initialSelected={roomArg} />
              : room === 'modify'
              /* B-631: MODIFY is a room off the pack — GearModifyStage with `paper`; DONE returns to the pack with the item still selected */
              ? <GearModifyStage sheet={sheet} equipment={equipment} persistedEq={sheet.equipment || {}} accent={accent} target={roomArg} onSheetChange={p.onSheetChange} onDone={function () { openRoom('pack', roomArg); }} />
              : room === 'abilities'
              ? <SheetAbilitiesRoom sheet={sheet} accent={accent} />
              : room === 'growth'
              ? <SheetAdvancement sheet={sheet} accent={accent} onSheetChange={p.onSheetChange} initialPick={roomArg && roomArg.pick ? roomArg.pick : null} initialUnit={roomArg && roomArg.unit ? roomArg.unit : null}
                  openHubGiven={function (g) { p.setHubOpen({ given: g }); }} /* B-1662: the CONTACTS stage's ROLL — the stack's own contract */ />
              : room === 'craft'
              /* THE CRAFT WALK (B-615, second half): the room is a WALK — TEMPLATE · MATERIALS · CONSTRUCTION, one
                 decision per stage — so it keeps the creator's anatomy, not the board's. Production's own workshop
                 (CraftWorkshop: the project list, the three stages, the reveal) is mounted whole on an island; its
                 paper body — the step rail, the plate, the walk foot, on the workshop's OWN stage — is B-624. */
              ? <div className="sb-room-walk" data-testid="sheet-craft"><CraftWorkshop sheet={sheet} accent={accent} onSheetChange={p.onSheetChange} /></div>
              : room === 'companion'
              /* R6 S1-pick (ruling release-r6-ui-c0 U10): ＋ COMPANION's room — the shared library, PICK mode */
              ? <SheetCompanionPick sheet={sheet} derived={derived} onSheetChange={p.onSheetChange} onClose={function () { setRoom(null); }} />
              : null}
          </div>
        ) : (
        <div className="sb-board">
          <div className="sb-spine" data-sheet-zone="spine">
            <SheetSpine sheet={sheet} accent={accent} portraitSrc={p.portraitSrc} equipment={equipment} derived={derived} onSheetChange={p.onSheetChange} band={mode} committed={committed}
              openHub={function (o) { p.setHubOpen(o); }} /* B-1209 retire: the crit plate's Medicine opens the hub on that injury, { treat } */
              char={char} onEditInCreator={p.onEditInCreator} /* B-656: the species ask rides the spine now, and it routes to the creator */ />
          </div>
          <div className="sb-stage" data-sheet-zone="stage">
            <SheetStage sheet={sheet} accent={accent} equipment={equipment} committed={committed} onRoll={function (key) { p.setHubOpen({ strip: key }); }} />
          </div>
          <div className="sb-stack" data-sheet-zone="stack">
            <SheetStack sheet={sheet} accent={accent} onPortrait={p.onPortrait} portraitSrc={p.portraitSrc} equipment={equipment} derived={derived} openLabel={openLabel} onOpen={p.onOpen} char={char} onEditInCreator={p.onEditInCreator} onOpenYard={p.onOpenYard}
              onSheetChange={p.onSheetChange} writeCharacter={p.writeCharacter} /* SHIP CARGO R-SCB-2: the host's one character write */ /* B-639: the stack never carried the writer — every PROFILE edit on paper (B-620) stopped at local state; the pads made it visible */
              committed={committed} onCommit={onCommit}
              openHub={function (strip) { p.setHubOpen({ strip: strip }); }} openHubGiven={function (g) { p.setHubOpen({ given: g }); }} openRoom={openRoom} onOpenStore={p.onOpenStore} />
          </div>
        </div>
        )}
      </div></div>
      {p.hubOpen ? <DiceHub sheet={sheet} accent={accent} committed={committed} onCommit={onCommit} /* F-100 M10 (R-M10B-6c): the session's committed SET, not its count — the hub counts it through the one function and hands it to collectPoolMods */ initialStrip={p.hubOpen && p.hubOpen.strip != null ? p.hubOpen.strip : null} onClose={function () { p.setHubOpen(false); }}
        given={p.hubOpen && p.hubOpen.given ? p.hubOpen.given : null} /* R6 U6: opened on a Follower's or a network's check, { spec, label } */
        treat={p.hubOpen && p.hubOpen.treat ? p.hubOpen.treat : null} /* B-1209 retire: opened from the crit plate's Medicine, on that injury */
        onTreated={function (id) { if (p.onSheetChange) p.onSheetChange({ criticalInjuries: (sheet.criticalInjuries || []).filter(function (x) { return x.id !== id; }) }); }}
        onChoosePick={function (q) { p.setHubOpen(false); openRoom('growth', { pick: q }); }} /> : null}
    </div>
  );
}

function CharacterSheet({ char, sheet, onBack, onSave, isSaved, onOpenStore, onOpenYard, onSheetChange, writeCharacter, onEditInCreator, qualitiesDb, chromePinned, onSummonChrome }) {
  var accent = char.accent || '#F5C842';
  // Portrait state — the paper PROFILE unit sets it (onPortrait); the spine and the unit read portraitSrc.
  // R-B1: hydrate from a PERSISTED uploaded portrait (a data: URL on the record) so a
  // custom portrait survives reload — incl. COMMERCIAL_MODE, where portraitSrc shows only
  // customPortrait. Pregen asset-path portraits stay null here (commercial hides them).
  // B-931 (ruling `paper-b931` R-PB9-4): read through the ONE fold, over the pair the sheet was handed — a `data:`
  // URI first, then any value; `selectedChar` before the sheet. The openers fold the record into the sheet, so on
  // any opened record this is `sheet.portrait`; the order only matters for a pair that did not come through one.
  var _storedPortrait = window.SWRPGRecordPortrait.recordPortraitOf({ selectedChar: char, sheetData: sheet });
  function _persistedPortrait() {
    return _storedPortrait.indexOf('data:') === 0 ? _storedPortrait : null;
  }
  var _cp = useState(_persistedPortrait()), customPortrait = _cp[0], setCustomPortrait = _cp[1];
  // R-B1: route portrait changes through the saved record (write-through), not just local state.
  function persistPortrait(v) { setCustomPortrait(v); if (onSheetChange) onSheetChange({ portrait: v || '' }); }
  var portraitSrc = customPortrait || (COMMERCIAL_MODE ? '' : _storedPortrait);

  // F-87 — the DICE HUB's open state. Deliberately CharacterSheet-level and nothing more: the hub
  // is an overlay STATE of this screen, never a route (D-DH-6), so it needs no URL, no history
  // entry and no persistence. Closing unmounts it, which is also what makes the band chip's
  // ephemerality structural rather than a promise (R-DRB-2).
  var _hub = React.useState(false), hubOpen = _hub[0], setHubOpen = _hub[1];

  // The open unit, by LABEL: 'OVERVIEW' (the default, the first unit) or a label the yard's jump names
  // ('VESSELS'). SheetPaper maps a label to its unit — the one home of that vocabulary.
  var pageState = useState(function() { var p = null; try { p = sessionStorage.getItem('_yard_jump_page'); } catch(e){} return p || 'OVERVIEW'; });
  useEffect(function() { try { sessionStorage.removeItem('_yard_jump_page'); } catch(e){} }, []);

  // THE PAPER SHEET — the one face since N-post (lap paper-sheet-delete): the old face, its switch and
  // its TABLE MODE are deleted.
  return h(SheetPaper, {
    sheet: sheet, char: char, accent: accent, portraitSrc: portraitSrc, onPortrait: persistPortrait,
    openLabel: pageState[0], onOpen: function (label) { pageState[1](label); },
    hubOpen: hubOpen, setHubOpen: setHubOpen, chromePinned: chromePinned, onSummonChrome: onSummonChrome,   // B-629: the paper sheet IS the table; no toggle
    onBack: onBack, onSave: onSave, isSaved: isSaved, onOpenStore: onOpenStore, onSheetChange: onSheetChange, onEditInCreator: onEditInCreator,
    qualitiesDb: qualitiesDb, onOpenYard: onOpenYard, writeCharacter: writeCharacter,
  });
}

/* ══════════════════════════════════════════════════════
   QUICKBUILD — "Build Your Legend"
   Guided 5-step character creator for Star Wars fans
══════════════════════════════════════════════════════ */

/* ── Role mappings: friendly labels → FFG careers ─────
   Each role maps to a career key in oggdude data.
   Reference archetypes help players connect to the IP.
*/
// GB-2 (ruling creator-raw-defects §3b): each role maps to a fixed career + specialization (the auto-build
// assigns them — see QB_AUTO_BUILD, keyed by career). The role step was DISHONESTLY SILENT about it; `spec`
// is the display name of QB_AUTO_BUILD[career].talent.specKey so the card can disclose "Career · Spec"
// (registry-verified names; a render-time specKey→name lookup isn't clean — OggDude keys ≠ canonical keys).
const QB_ROLES = {
  eote: [
    { id: 'outlaw',    label: 'The Outlaw',    career: 'Smuggler',      spec: 'Charmer',       ref: 'Think Han Solo',         desc: 'A fast-talking scoundrel who lives by their wits, flies by the seat of their pants, and always has an exit planned.' },
    { id: 'hunter',    label: 'The Hunter',     career: 'Bounty Hunter', spec: 'Assassin',      ref: 'Think Boba Fett',        desc: 'A relentless tracker who brings in targets for credits. Methodical, dangerous, and always working an angle.' },
    { id: 'survivor',  label: 'The Survivor',   career: 'Hired Gun',     spec: 'Bodyguard',     ref: 'Think Chewbacca',        desc: 'A tough-as-nails fighter who has seen the worst the galaxy can offer and is still standing.' },
    { id: 'operator',  label: 'The Operator',   career: 'Technician',    spec: 'Cyber Tech',    ref: 'Think Babu Frik',        desc: 'A mechanical genius who can fix anything, slice any system, and build weapons from scrap parts.' },
  ],
  aor: [
    { id: 'pilot',     label: 'The Pilot',      career: 'Ace',           spec: 'Beast Rider',   ref: 'Think Poe Dameron',      desc: 'Born to fly. Whether it\'s an X-wing or a stolen shuttle, the cockpit is home and the sky is freedom.' },
    { id: 'leader',    label: 'The Leader',      career: 'Commander',     spec: 'Commodore',     ref: 'Think Princess Leia',    desc: 'A natural commander who inspires loyalty, reads a battlefield, and makes the calls that bring people home.' },
    { id: 'trooper',   label: 'The Soldier',     career: 'Soldier',       spec: 'Commando',      ref: 'Think Captain Rex',      desc: 'A disciplined warrior who holds the line when everything falls apart. Steady, reliable, and deadly.' },
    { id: 'shadow',    label: 'The Shadow',      career: 'Spy',           spec: 'Courier',       ref: 'Think Cassian Andor',    desc: 'An intelligence operative who moves unseen, gathers secrets, and does what must be done for the cause.' },
  ],
  fad: [
    { id: 'guardian',  label: 'The Protector',   career: 'Guardian',      spec: 'Armorer',       ref: 'Think Kanan Jarrus',     desc: 'A warrior of the light who carries a lightsaber to defend the innocent. Duty before self, always.' },
    { id: 'seer',      label: 'The Mystic',      career: 'Mystic',        spec: 'Advisor',       ref: 'Think Chirrut Îmwe',     desc: 'A contemplative who channels the Force through intuition and vision. Sees truths others cannot.' },
    { id: 'wanderer',  label: 'The Seeker',      career: 'Seeker',        spec: 'Ataru Striker', ref: 'Think Ahsoka Tano',      desc: 'A nomad who follows the Force into the wild places. Part survivalist, part prophet, always searching.' },
    { id: 'blade',     label: 'The Sentinel',    career: 'Sentinel',      spec: 'Artisan',       ref: 'Think Cal Kestis',       desc: 'A Force-user who hunts the enemies of the light with precision, patience, and a self-built lightsaber.' },
  ],
};

const QB_SPECIES = [
  'Human', "Twi'lek", 'Wookiee', 'Rodian', 'Zabrak', 'Togruta',
  'Bothan', 'Mon Calamari', 'Mirialan', 'Nautolan', 'Duros', 'Chiss', 'Kel Dor',
];

// UXQP-15: one-line experience descriptor beside each species' stat grid.
const QB_SPECIES_DESC = {
  'Human': 'Versatile all-rounder — a little good at everything, plus an extra skill',
  "Twi'lek": 'Charismatic and persuasive',
  'Wookiee': 'Immense strength — a melee powerhouse',
  'Rodian': 'Quick and deadly — a natural hunter',
  'Zabrak': 'Iron-willed and relentless',
  'Togruta': 'Sharp instincts, hard to fool',
  'Bothan': 'Cunning operator, built for intrigue',
  'Mon Calamari': 'Brilliant and technical',
  'Mirialan': 'Graceful and precise',
  'Nautolan': 'Powerful and hardy',
  'Duros': 'Clever navigator, sharp-minded',
  'Chiss': 'Coldly brilliant tactician',
  'Kel Dor': 'Disciplined and strong-willed',
};

// QUICKBUILD PORTRAITS RETIRED (Nunes retirement, Addendum 1): the species portrait set is
// deleted; the two consumers already fall back with `QB_PORTRAITS[x] || ''`, and an empty
// portrait renders the shipped Monogram seam.
const QB_PORTRAITS = {};

const QB_ACCENT = { eote: '#F5C842', aor: '#00C8FF', fad: '#B080FF' };
const QB_GRAD = {
  eote: 'linear-gradient(160deg, #1a1000 0%, #0d0800 50%, #080400 100%)',
  aor:  'linear-gradient(160deg, #001020 0%, #000810 50%, #000408 100%)',
  fad:  'linear-gradient(160deg, #100820 0%, #080410 50%, #040208 100%)',
};

/* ── Auto-assignment: default free skills per career ──
   B-1504 (release-r7-bug-burndown ADDENDUM 76): each list is LEGAL — the career's own career skills, as many as the
   career's `free_ranks` (4 in EotE/AoR, EotE p.80 · AoR p.88; 3 in F&D, FaD p.43). A Mystic was handed Cool, which is
   no Mystic or Advisor skill (FaD p.81); eleven of twelve careers held an off-list or surplus rank here or in
   `QB_AUTO_BUILD.specSkills` (the spec's own four, EotE p.35). Guarded by `test/b1504_qb_default_skills.cjs`. */
const QB_DEFAULT_SKILLS = {
  'Smuggler':       ['DECEP', 'PILSP', 'SKULL', 'STREET'],
  'Bounty Hunter':  ['ATHL', 'BRAWL', 'PERCEP', 'RANGHVY'],
  'Hired Gun':      ['ATHL', 'BRAWL', 'DISC', 'RANGLT'],
  'Technician':     ['ASTRO', 'COMP', 'MECH', 'PERCEP'],
  'Ace':            ['ASTRO', 'COOL', 'GUNN', 'PILSP'],
  'Commander':      ['COOL', 'DISC', 'LEAD', 'VIGIL'],
  'Soldier':        ['ATHL', 'BRAWL', 'RANGHVY', 'RANGLT'],
  'Spy':            ['COMP', 'DECEP', 'PERCEP', 'STEAL'],
  'Guardian':       ['BRAWL', 'COOL', 'DISC'],
  'Mystic':         ['CHARM', 'KNOWLORE', 'PERCEP'],
  'Seeker':         ['PILPLAN', 'SURV', 'VIGIL'],
  'Sentinel':       ['DECEP', 'PERCEP', 'STEAL'],
};

/* ── Auto-assignment: starting gear per career ── */
// Auto-build data: spec skills, characteristic boosts, and starting talent per career
// Characteristic boosts: array of [attr, targetValue] pairs. Cost = sum of (rating * 10) for each step above species base.
// These are designed for a typical 100-110 XP species. Leftover XP remains unspent (shown on character sheet).
const QB_AUTO_BUILD = {
  'Smuggler':       { specSkills: ['CHARM', 'COOL'],     charBoosts: [['cunning', 3], ['agility', 3]],   talent: { key: 'SMOOTHTALK', specKey: 'CHARMER', row: 0, col: 0 } },
  'Bounty Hunter':  { specSkills: ['RANGHVY', 'STEAL'],   charBoosts: [['agility', 3], ['cunning', 3]],   talent: { key: 'GRIT', specKey: 'ASSAS', row: 0, col: 0 } },
  'Hired Gun':      { specSkills: ['PERCEP', 'RANGHVY'], charBoosts: [['brawn', 3], ['agility', 3]],     talent: { key: 'TOUGH', specKey: 'BODYGUARD', row: 0, col: 0 } },
  'Technician':     { specSkills: ['MECH', 'MED'],      charBoosts: [['intellect', 3], ['cunning', 3]], talent: { key: 'TOUGH', specKey: 'CYBERTECH', row: 0, col: 3 } },
  'Ace':            { specSkills: ['PERCEP', 'SURV'],     charBoosts: [['agility', 3], ['cunning', 3]],   talent: { key: 'TOUGH', specKey: 'BEASTRIDER', row: 0, col: 1 } },
  'Commander':      { specSkills: ['ASTRO', 'COMP'],     charBoosts: [['presence', 3], ['willpower', 3]],talent: { key: 'COM', specKey: 'COMMODORE', row: 0, col: 1 } },
  'Soldier':        { specSkills: ['RESIL', 'SURV'],      charBoosts: [['brawn', 3], ['agility', 3]],     talent: { key: 'GRIT', specKey: 'COMMANDO', row: 0, col: 1 } },
  'Spy':            { specSkills: ['DECEP', 'STREET'],    charBoosts: [['cunning', 3], ['agility', 3]],   talent: { key: 'INDIS', specKey: 'COURIER', row: 0, col: 0 } },
  'Guardian':       { specSkills: ['LTSAB', 'RESIL'],     charBoosts: [['brawn', 3], ['willpower', 3]],   talent: { key: 'GRIT', specKey: 'ARMORER', row: 0, col: 0 } },
  'Mystic':         { specSkills: ['CHARM', 'NEGOT'],     charBoosts: [['willpower', 3], ['presence', 3]],talent: { key: 'GRIT', specKey: 'ADVISOR', row: 0, col: 2 } },
  'Seeker':         { specSkills: ['COORD', 'PERCEP'],    charBoosts: [['agility', 3], ['cunning', 3]],   talent: { key: 'PARRY', specKey: 'ATARU', row: 0, col: 1 } },
  'Sentinel':       { specSkills: ['COMP', 'MECH'],     charBoosts: [['cunning', 3], ['willpower', 3]], talent: { key: 'MENTTOOLS', specKey: 'ARTISAN', row: 0, col: 2 } },
};

// QB_DEFAULT_GEAR DELETED (F-60, the subtraction lap; ruling `creator-plumbing-census` D-R0-3).
// 12 careers x {weapon, armor, gear[]} keyed by DISPLAY NAME, superseded by QB_LOADOUT_OPTIONS below
// and never removed. The R0 census measured it at exactly ONE reference in the repo — its own
// definition — and the architect re-verified that independently before ruling D-R0-3.

// Curated loadout packages — 3 per career, real oggdude keys, within 500cr budget
/* ── Guided's starting Force power — EDITORIAL, and PRICED ──────────────────────────────────
   Which power a Guided Force character starts with is OUR editorial choice, exactly like the curated
   loadouts below: Guided's promise is that it elects for you.

   IT IS NOT A RAW GRANT, and the comment that used to sit on this map said it was. Corrected with both
   cites (R-R1-2):
     · an F&D CAREER confers a Force RATING of 1 at creation
       (source: Force and Destiny Core Rulebook p.282)
     · powers are a PURCHASE, never a grant — "Characters do not begin with any Force powers, but they
       may purchase them using experience points during character creation"
       (source: Force and Destiny Core Rulebook p.103; the purchase sits in the starting-XP table p.102)

   So R1 folds it in as a PRICED AUTOMATED PURCHASE (ruling `r1-assembly-core-blockers` Q1, option (e)):
   Guided seeds the pick into the working character with its PRINTED row-0 cost, and the one shared
   assembly prices it in the XP pool beside the auto-bought characteristics and talent. Before R1 the
   power skipped the register entirely, so a Guided Mystic finished 5-20 XP richer than a Custom one
   built identically. New Guided Force characters now finish with the honest remainder. */
const QB_FORCE_POWER_PICKS = {
  'Mystic': 'heal_harm', 'Guardian': 'enhance', 'Seeker': 'foresee', 'Sentinel': 'sense',
  // Consular + Warrior have no Guided ROLE offering them, so these two are unreachable today. Kept as
  // future-role seeds rather than deleted (ruling `r1-guided-xp-solvency` §2) and NOTED for the
  // subtraction ledger's next pass, so a later lap decides deliberately instead of finding them cold.
  'Consular': 'heal_harm', 'Warrior': 'enhance',
};

/* PER-SPECIES OVERRIDES — P1 (Jason, 2026-07-30; ruling `r1-guided-xp-solvency`).
   Pricing the pick (ADR 0022) overdrew the two combos whose species starts from the smallest pool:
   a Wookiee begins with 90 XP, and Guided's auto-buys leave exactly 5 — where Heal/Harm costs 15 and
   Sense costs 10. Farsight is the game's ONE 5-XP basic power, so it is the only pick that fits, and
   both tight combos take it. Every other combo keeps its base pick unchanged.
   Editorial, like the base table: a seer's power suits a Mystic, and it keeps both paths complete
   rather than shipping a powerless or insolvent character.
   NOTE for whoever fixes B-59 (Guided ignores ACTIVE SOURCES): farsight is sourced from Savage
   Spirits, not a core book. Guided does not filter by source today, so this is inert — but a GM who
   deactivates that book once Guided DOES filter would strand these two combos. */
const QB_FORCE_POWER_PICKS_BY_SPECIES = {
  'Wookiee': { 'Mystic': 'farsight', 'Sentinel': 'farsight' },
};

// Seed Guided's editorial pick into the WORKING character as a priced purchase, so the shared assembly
// both persists it (powersFromCreatorState) and charges for it (xpSpentOnForcePowers). Returns the
// character unchanged when the career grants nothing or the registry cannot price the power — a missing
// row-0 cost is a registry bug that basicForcePowerCost already reports loudly, and a silent free
// power is the exact defect this replaces.
function seedGuidedForcePower(character, gameLine, gameData) {
  if (gameLine !== 'fad' || !gameData || !gameData.charbuild) return character;
  var careerName = character.career ? character.career.name : '';
  var speciesName = character.species ? character.species.name : '';
  var bySpecies = QB_FORCE_POWER_PICKS_BY_SPECIES[speciesName];
  // C-SOLV-3: the override is EDITORIAL DATA, resolved here. There is deliberately NO runtime
  // "skip the power if it will not fit" branch — that would be a silent editorial yield, which is the
  // exact failure C-R1-1 exists to catch. Solvency is guaranteed at BUILD time by
  // test/r1_guided_xp_solvency.cjs, which reddens on any future insolvent combo and returns as a brief.
  var pid = (bySpecies && bySpecies[careerName]) || QB_FORCE_POWER_PICKS[careerName];
  if (!pid) return character;
  var rec = gameData.charbuild.forcePowers ? gameData.charbuild.forcePowers[pid] : null;
  if (!rec) return character;
  var cost = basicForcePowerCost(rec);
  if (cost == null) return character;          // registry bug, already logged — never a free power
  if (character.forcePowers && character.forcePowers[pid]) return character;   // idempotent
  character.forcePowers = Object.assign({}, character.forcePowers || {});
  character.forcePowers[pid] = { cost: cost, basic: true, upgrades: [] };
  return character;
}

/* B-738 — GUIDED'S ONE ASSEMBLY, SHARED BY ITS REVIEW AND ITS FINALIZE.
   `QBReviewStep` rendered from raw reducer state while `handleQuickBuildComplete` persisted the
   output of `normalizeCreated`, so the review and the character could not be made to agree: every
   trained skill printed a LITERAL "Rank 1" (understating nine of twelve careers, whose career and
   specialization grant tables overlap — B-727), XP was absent entirely, and credits were
   hand-rolled from `500 + pocketMoney − Σ(price × qty)`, dropping `mechanic.creditAdjustment`
   (`charResolve.js:1480`).
   `FCReviewStep` had the answer one door over (`:11201` builds its `displayModel` through
   `normalizeCreated`). This is that derivation, lifted to ONE function both Guided doors call, so
   the review is a projection of the record rather than a second account of it — the same
   one-shared-core rule the resolver lives under. NOT a second sum: nothing here adds anything.
   `character` is passed THROUGH to `seedGuidedForcePower`, which is what the finalize did; the
   review hands it a shallow clone so a render cannot write reducer state. */
function guidedAssembledModel(character, gameLine, gameData, speciesRec) {
  // The species record, passed from the controller. Guided used to re-find it BY NAME even though
  // the species step already had the record in hand — a name round-trip that silently defaulted
  // wound/strain to 10 and the XP pool to 100 on a miss.
  var speciesData = speciesRec || (gameData
    ? Object.values(gameData.charbuild.species).find(function(sp) { return sp.name === (character.species ? character.species.name : ''); })
    : null);
  // The chosen specialization's record — Guided assigns it at the role step, so the id is on the
  // working character. normalizeCreated needs it for the T2-D Force-granting-spec branch.
  var specKey = character.specializations && character.specializations[0] ? character.specializations[0].key : null;
  var specData = (specKey && gameData && gameData.charbuild.specializations)
    ? (gameData.charbuild.specializations[specKey] || null) : null;
  // Guided's editorial Force pick, priced from the printed row-0 cost, BEFORE the assembly runs so
  // the one shared XP derivation charges for it (ruling `r1-assembly-core-blockers` Q1 option (e)).
  var built = seedGuidedForcePower(character, gameLine, gameData);
  return { built: built, sheet: normalizeCreated(built, speciesData, specData, gameData, 'QuickBuild') };
}

const QB_LOADOUT_OPTIONS = {
  'Smuggler': [
    { id: 'smuggler_fast', name: 'The Fast Talker', desc: 'Travel light, talk your way out of trouble, and always have an exit planned.',
      items: [{ key: 'BLASTLT', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'UTILBELT', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 }] },
    { id: 'smuggler_gun', name: 'The Gunslinger', desc: 'A reliable blaster and the clothes on your back. Sometimes that\'s all you need.',
      items: [{ key: 'BLASTPIS', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'smuggler_surv', name: 'The Survivor', desc: 'A concealable weapon, robes that blend in, and enough medical supplies to patch yourself up.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 }] },
  ],
  'Bounty Hunter': [
    { id: 'bh_tracker', name: 'The Tracker', desc: 'A good blaster, binders for the target, and stimpacks for when the target fights back.',
      items: [{ key: 'BLASTPIS', qty: 1 },{ key: 'BIND', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 }] },
    { id: 'bh_brawler', name: 'The Brawler', desc: 'Get in close, hit hard, and bring them in alive. The vibroknife is for when alive isn\'t an option.',
      items: [{ key: 'VIBKN', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'BIND', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 }] },
    { id: 'bh_pro', name: 'The Professional', desc: 'A blaster for range, a blade for close work, and optics for finding the target first.',
      items: [{ key: 'BLASTLT', qty: 1 },{ key: 'KNIFE', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'BIND', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
  ],
  'Hired Gun': [
    { id: 'hg_muscle', name: 'The Muscle', desc: 'A solid blaster and the grit to use it. You\'re the one they call when talking stops working.',
      items: [{ key: 'BLASTPIS', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'hg_bruiser', name: 'The Bruiser', desc: 'A vibroknife, a tough coat, and a medpac. You take the hits so others don\'t have to.',
      items: [{ key: 'VIBKN', qty: 1 },{ key: 'NOMADCOAT', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'RATION', qty: 2 }] },
    { id: 'hg_heavy', name: 'The Heavy', desc: 'A rifle for suppression, a pack for carrying extra gear, and enough supplies to outlast the enemy.',
      items: [{ key: 'SLUGRIF', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'BACKPACK', qty: 1 },{ key: 'MBIN', qty: 1 }] },
  ],
  'Technician': [
    { id: 'tech_mech', name: 'The Mechanic', desc: 'A holdout blaster for emergencies, a scanner for diagnostics, and enough tools to improvise the rest.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'HSCAN', qty: 1 },{ key: 'UTILBELT', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 }] },
    { id: 'tech_slicer', name: 'The Slicer', desc: 'Scanner goggles, a blaster for self-defense, and the tools to crack any system you find.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'SCANG', qty: 1 },{ key: 'UTILBELT', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 }] },
    { id: 'tech_tinker', name: 'The Tinkerer', desc: 'You fix problems, not shoot them. A knife, a scanner, a medpac, and everything you need to build from scrap.',
      items: [{ key: 'KNIFE', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'HSCAN', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'BACKPACK', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 1 }] },
  ],
  'Ace': [
    { id: 'ace_dog', name: 'The Dogfighter', desc: 'A reliable sidearm for when you\'re not in the cockpit. Which isn\'t often.',
      items: [{ key: 'BLASTPIS', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 1 }] },
    { id: 'ace_hot', name: 'The Hotshot', desc: 'A lighter blaster, more supplies, and the mindset that the ship is the real weapon.',
      items: [{ key: 'BLASTLT', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'UTILBELT', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 1 }] },
    { id: 'ace_escort', name: 'The Escort', desc: 'A concealed weapon, protective robes, and a medpac. You get your passengers where they\'re going.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
  ],
  'Commander': [
    { id: 'cmd_tact', name: 'The Tactician', desc: 'A standard-issue blaster and the confidence to lead from the front.',
      items: [{ key: 'BLASTPIS', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 1 }] },
    { id: 'cmd_field', name: 'The Field Commander', desc: 'Optics for reading the battlefield, a lighter blaster, and supplies to keep your people moving.',
      items: [{ key: 'BLASTLT', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 1 }] },
    { id: 'cmd_dipl', name: 'The Diplomat', desc: 'Robes that command respect, a concealed weapon for when respect fails, and a medpac for the aftermath.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 }] },
  ],
  'Soldier': [
    { id: 'sol_rifle', name: 'The Rifleman', desc: 'A rifle, a backup blade, optics, and enough stimpacks to keep fighting. Standard infantry loadout.',
      items: [{ key: 'SLUGRIF', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'KNIFE', qty: 1 },{ key: 'UTILBELT', qty: 1 },{ key: 'MBIN', qty: 1 }] },
    { id: 'sol_point', name: 'The Point Man', desc: 'A solid blaster pistol and light kit. You move fast and hit first.',
      items: [{ key: 'BLASTPIS', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'sol_medic', name: 'The Medic', desc: 'A holdout blaster for self-defense and a pack full of medical supplies. You keep the squad alive.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 3 },{ key: 'COMLINKH', qty: 1 },{ key: 'UTILBELT', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 1 }] },
  ],
  'Spy': [
    { id: 'spy_infil', name: 'The Infiltrator', desc: 'Concealed weapon, robes that blend into any crowd, and the supplies to stay hidden.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'UTILBELT', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 1 }] },
    { id: 'spy_op', name: 'The Operative', desc: 'A light blaster, macrobinoculars for surveillance, and enough stimpacks to handle complications.',
      items: [{ key: 'BLASTLT', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 }] },
    { id: 'spy_ghost', name: 'The Ghost', desc: 'You move unseen: a blade, a scanner, optics, and everything you need to disappear.',
      items: [{ key: 'KNIFE', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'HSCAN', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'BACKPACK', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 }] },
  ],
  'Guardian': [
    { id: 'guard_def', name: 'The Defender', desc: 'A training lightsaber to begin your path, basic protection, and the essentials to keep going.',
      items: [{ key: 'LTSABERTRAIN', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'guard_monk', name: 'The Warrior Monk', desc: 'A vibroknife, concealing robes, and supplies for the road. The Force is your real weapon.',
      items: [{ key: 'VIBKN', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 1 }] },
    { id: 'guard_shield', name: 'The Shield', desc: 'Your role is to protect and heal, not fight. A full medical kit, supplies, and the will to keep others alive.',
      items: [{ key: 'KNIFE', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 3 },{ key: 'COMLINKH', qty: 1 },{ key: 'BACKPACK', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'UTILBELT', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 2 }] },
  ],
  'Mystic': [
    { id: 'mys_seer', name: 'The Seer', desc: 'A training lightsaber and the bare essentials. Your connection to the Force is all the equipment you need.',
      items: [{ key: 'LTSABERTRAIN', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'mys_wander', name: 'The Wanderer', desc: 'Robes, a blade, a medpac, and supplies for the long road. You go where the Force leads.',
      items: [{ key: 'KNIFE', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 2 }] },
    { id: 'mys_healer', name: 'The Healer', desc: 'A concealed blaster, protective robes, and medical supplies. You mend what the galaxy breaks.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
  ],
  'Seeker': [
    { id: 'seek_path', name: 'The Pathfinder', desc: 'A training lightsaber and minimal gear. The wilds are your home, so you travel light.',
      items: [{ key: 'LTSABERTRAIN', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'seek_scout', name: 'The Scout', desc: 'No lightsaber yet, just a knife, optics, a medpac, and a full pack. You survive by knowing the terrain.',
      items: [{ key: 'KNIFE', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'BACKPACK', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 }] },
    { id: 'seek_hermit', name: 'The Hermit', desc: 'A slugthrower, a weathered coat, and enough supplies to disappear into the wilderness indefinitely.',
      items: [{ key: 'SLUGPIS', qty: 1 },{ key: 'NOMADCOAT', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'MBIN', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'BACKPACK', qty: 1 }] },
  ],
  'Sentinel': [
    { id: 'sent_shadow', name: 'The Shadow Blade', desc: 'A training lightsaber and the basics. You hunt the darkness. The blade is just a tool.',
      items: [{ key: 'LTSABERTRAIN', qty: 1 },{ key: 'HC', qty: 1 },{ key: 'STIMPACK', qty: 1 },{ key: 'COMLINKH', qty: 1 }] },
    { id: 'sent_invest', name: 'The Investigator', desc: 'A concealed blaster, robes for blending in, and a scanner for finding what others hide.',
      items: [{ key: 'BLASTHOLD', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'HSCAN', qty: 1 },{ key: 'COMLINKH', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 }] },
    { id: 'sent_exile', name: 'The Exile', desc: 'A knife, robes, a full pack, and enough supplies to stay off the grid. You\'re still running from the Empire.',
      items: [{ key: 'KNIFE', qty: 1 },{ key: 'CONROBE', qty: 1 },{ key: 'EMP', qty: 1 },{ key: 'STIMPACK', qty: 2 },{ key: 'COMLINKH', qty: 1 },{ key: 'BACKPACK', qty: 1 },{ key: 'MBIN', qty: 1 },{ key: 'SYNTHEROPE', qty: 1 },{ key: 'GLOWROD', qty: 1 },{ key: 'RATION', qty: 1 }] },
  ],
};

/* ── StepHeader — reusable across QuickBuild + FullCreator ── */
// R-CDW-3: the guided door's column cap, declared ONCE so its header and its body read the same
// value. The custom door's 125px offset existed because two places each defaulted independently.
var QB_STEP_COLUMN_MAX = 'var(--desk-read-max)';

function QBStepHeader({ title, subtitle, step, totalSteps, onBack, accent, contentMax, railWidth }) {
  // B-702's OTHER SITE, and the one a player can still reach: the legacy shell's counter is not a
  // folio, so `fcFolio`'s null fallback does not cover it. `step` is the controller's
  // `stepDisplayIndex`, which is NULL for a step the reachable list does not hold, and `null + 1`
  // is 1 — so the clamp's "1 / 11" came straight back here. No position, no progress fill, when
  // there is no position to print. QuickBuild's own index is never null, so its reading is
  // untouched.
  const _pos = (step == null) ? null : (step + 1);
  const pct = (_pos == null) ? 0 : (_pos / totalSteps) * 100;
  const _lm = useLayoutMode();  // R-D1-1: on desktop the title block caps to the content column
  // D2 — THE HEADING SITS OVER ITS OWN BODY. Ruled at C10, assigned to LAP 4, shipped unfixed, and
  // found by Jason on a monitor (the architect's ledger #32).
  //
  // THE DEFECT WAS STRUCTURAL, not a rounding error: this block centred across the WHOLE window
  // while the step body centred across the window MINUS the build rail — so the title sat exactly
  // half a rail-width left of its own content, on every step, at every width where the rail shows.
  // D1 measured it: 125px at 1920 with a 250px rail, and 0 at 1366 where no rail renders, which is
  // what proved the rail was the cause rather than the cap.
  //
  // TWO PARTS, because the offset had two causes:
  //   · `railWidth` reserves the rail's gutter on THIS block only — the progress bar above still
  //     spans the shell, because it is chrome and belongs to the whole screen.
  //   · `contentMax` takes the STEP's own effective cap instead of a hardcoded reading width, so a
  //     step that composes wider keeps its heading over its body rather than over where the body
  //     used to be. The default preserves today's behaviour for any caller that passes nothing.
  // Both creator doors share this component (R-CDW-3), so the guided flow is fixed by the same edit.
  const _titleCap = _lm === 'desktop'
    ? { maxWidth: contentMax || 'var(--desk-read-max)', margin: '0 auto', width: '100%', boxSizing: 'border-box' }
    : null;
  const _titleGutter = (_lm === 'desktop' && railWidth) ? { paddingRight: railWidth } : null;
  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 12px 0' }}>
        {/* The control that RETURNS a step. `QBStepHeader` is shared by the full creator and
           quickbuild, so one hook answers for both idioms — by role, not by label. */}
        <button onClick={onBack} data-creator-back="1" style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: accent, display: 'flex', alignItems: 'center',
          gap: '6px', minWidth: '48px', minHeight: '48px', padding: '10px',
        }}>
          <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
            <polyline points="13,4 7,10 13,16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.18em', color: accent, opacity: 0.7 }}>BACK</span>
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.35)' }}>
          {_pos == null ? '' : _pos + ' / ' + totalSteps}
        </span>
      </div>
      <div style={{ height: '2px', background: 'rgba(255,255,255,0.06)', margin: '0 16px' }}>
        <div style={{
          height: '100%', width: `${pct}%`, background: accent,
          transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          boxShadow: `0 0 8px ${accent}60`,
        }} />
      </div>
      {/* D1 measurement hook (ruling `creator-desktop-wide-and-fit-concept`). A `data-` attribute
          only — it changes no geometry and is NOT the D2 fix. It exists so the alignment assert can
          address this box BY ID rather than by matching its text, which would break the moment a
          step title changed. */}
      <div style={Object.assign({}, _titleGutter)}>
      <div data-step-title="1" style={Object.assign({ padding: '20px 24px 8px' }, _titleCap)}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', letterSpacing: '0.06em', color: '#fff', marginBottom: subtitle ? '8px' : 0 }}>{title}</div>
        {subtitle && <div style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'var(--fs-lg)', lineHeight: 1.5, color: 'rgba(255,255,255,0.5)' }} dangerouslySetInnerHTML={{ __html: subtitle }} />}
      </div>
      </div>
    </div>
  );
}

/* ── Step 0: Game Line ─────────────────────────────── */
// The collapsed height of a game-line card. Named once and read by BOTH the card (via `collapsedH`)
// and the step's own minHeight floor, so the floor cannot drift from the thing it is a floor for —
// R-D2B-1's second half, that a layout property is not a promise, applied before it could bite.
var QB_GL_COLLAPSED_H = 96;

/* ══ R-RG-4's SINGLE-LINE NOTE — ONE HOME, TWO FACES (B-752; frames AF and AG of lab `18301f1f` §D,
   Jason-approved 2026-09-21) ══════════════════════════════════════════════════════════════════════
   When exactly one game line survives the player's active sources the step STILL RENDERS — it does not
   auto-advance past a one-option step, "because auto-advancing hides why the others are gone and
   produces a 'where did they go' moment with no answer available" — so it names what is off and how to
   bring it back. Mockup `Docs/mockups/b184-guided-build-single-line.html` Frame B, Jason-approved
   2026-08-17, shipped dark ever since.

   NOT ONE SENTENCE IS AUTHORED HERE. Every word was already in the dark step and is LIFTED VERBATIM
   out of it — the heading, the body's first sentence, the joiner between the absent lines (`, ` up to
   the last pair, ` and ` before it, nothing after) and the verb that agrees with how many there are.
   The paper face reads the same words through the same assembly, which is the `paperPocketBeat` move of
   one row ago (B-723): a note that exists twice is a note that will eventually say two things.

   THE VESSEL IS THE ONLY THING THAT FORKS, and the kit answers it: the paper side is the printed
   SIDEBAR (`hp-note`, one cut corner on the passive indigo fill, title set right) — a sidebar states
   something true and is never red, which is exactly what this note is. So the dark note's info-blue
   needs NO paper equivalent: no new token, no new colour, and the only paper rule the port adds is a
   width cap (`gb-note`, `qsg.css`, and its own comment says why).

   WHAT IS NOT BUILT, and it is the approval's limit rather than an omission: the older approved B-184
   mockup carries a "⚙ Manage in Active Sources" affordance whose string exists NOWHERE in this file,
   and an OPENED card with a THE FORCE row no §D frame draws. Jason approved the §D frames AS DRAWN and
   those frames draw the SHIPPED behaviour, so neither extra is here (LAB_NOTES §199; board B-756 (2)
   holds them). Adding a door the product does not have would be the opposite of mockup-is-law. */
function qbSingleLineNote(soleLine, paper) {
  var head = (
    <React.Fragment>
      Only {gameLineTitle(soleLine.id)} is active
    </React.Fragment>
  );
  // THE PAPER SIDEBAR (frames AF · AG). The bold names take the paper's own ink by inheritance rather
  // than a colour of their own — `#fff` on paper would be invisible, and the kit already sets `b`.
  if (paper) {
    return (
      <div className="hp-note gb-note" data-qb-single-line={soleLine.id}>
        <div className="hp-note-title">{head}</div>
      </div>
    );
  }
  // THE DARK NOTE, byte-for-byte the block that has shipped since B-184 — every style, in order.
  return (
    <div data-qb-single-line={soleLine.id} style={{
      border: '1px solid rgba(126,196,255,0.28)', borderRadius: '11px', padding: '13px 15px',
      background: 'linear-gradient(160deg, rgba(126,196,255,0.08), rgba(126,196,255,0.02))',
    }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--text-info)' }}>
        {head}
      </div>
    </div>
  );
}

function QBGameLineStep({ onSelect }) {
  /* B-108 (lap C6): the three lines come from FC_GAME_LINES — ONE home — and the title from the
     books vocabulary, so the name a player reads is registry-driven like every other creator
     subject. The dead `banner: null` slot is gone.

     B-107's shape, ratified: THE CARD SAYS WHAT THE CHOICE DECIDES, not just how it feels. The
     shipped card gave mood — "crime, survival, and the criminal underworld" — and never mentioned
     that this pick sets your careers, hands you a campaign mechanic that becomes a whole step three
     screens later, and decides whether the Force is on your sheet. A front door says what walking
     through it does (ruling `creator-gameline-drawing`: four-gate TEST, mechanics voice, the
     just-looking moment). */
  var careerCount = (function() {
    try {
      var cb = window._canonicalCharbuild;
      var careers = (cb && cb.careers) || {};
      var out = {};
      Object.keys(careers).forEach(function(id) {
        ((careers[id] && careers[id].sources) || []).forEach(function(src) {
          out[src] = (out[src] || 0) + 1;
        });
      });
      return out;
    } catch (e) { return {}; }
  })();
  var MECH_LABEL = { obligation: 'Obligation', duty: 'Duty', morality: 'Morality' };

  // D3 — STEP 1 STOPS SCROLLING TO CHOOSE (ruled `creator-desktop-wide-and-fit-concept` §4 D3).
  //
  // The three lines were three tall cards stacked in a growing column: every consequence block open
  // at once, so the FIRST screen of the creator asked the player to scroll before they could see
  // their three options. They are now `HubCard` (window.SWRPGUI, destructured at the top of this file) in its EXPANDABLE variant — the
  // shared component and its first-tap-expands / second-tap-commits gesture. A bespoke accordion is a
  // REJECTED SHAPE, not a build detail, so this adopts the shipped primitive (AS-1, R-CUI-2).
  //
  // THE EXTRA TAP IS RATIFIED (Jason, 2026-08-04) and the one-tap-select alternative is WITHDRAWN.
  // The builder does not get to re-open it to save a tap — noted here because saving a tap is
  // exactly the argument a later reader will be tempted by.
  var _open = useState(null), openLine = _open[0], setOpenLine = _open[1];

  // THE TEASER IS DERIVED FROM THE COPY THAT ALREADY SHIPS — no new content (R-CDW-4). Each `desc`
  // is written as a short thematic clause followed by a longer one ("Crime, survival, and the
  // criminal underworld. You live on the fringes…"), so the first sentence IS the teaser and the
  // whole string stays the open card's description. Falls back to the full string if a future
  // record has no sentence break, so this degrades to "teaser == description" rather than to empty.
  function teaserOf(text) {
    var t = String(text || '');
    var i = t.indexOf('. ');
    return i > 0 ? t.slice(0, i + 1) : t;
  }

  /* B-184 (ruling `b183-b184-ready-gap-recon` R-RG-2/3): THE GAME-LINE STEP HONOURS ACTIVE SOURCES.
     This is the filter's PRIMARY bite point — the user's declaration first applies here, and gating
     here makes every downstream step correct without further filtering (a line that is off is not
     offered, so its four roles are never reachable). One predicate, the SAME `fcFilterActive` the
     other two doors use, reading the `sources` each line now carries.

     FAIL-SAFE, NOT FAIL-EMPTY: `setBook` refuses to disable the last active core (sources.js
     "an empty creator is a broken app, not a choice"), so ≥1 line always survives and the empty
     branch is structurally unreachable (ruling §2.1; pinned by test/qb_source_gate.cjs leg 5). If it
     ever DID empty — a relaxed guard, a corrupt override — falling back to the full list beats
     rendering a dead-end step. The fallback is a backstop for a broken invariant, never a path. */
  var activeLines = fcFilterActive(FC_GAME_LINES);
  if (!activeLines.length) activeLines = FC_GAME_LINES;
  var soleLine = activeLines.length === 1 ? activeLines[0] : null;

  /* R-RG-4: WHEN ONLY ONE LINE SURVIVES THE STEP STILL RENDERS — it does not auto-advance past a
     one-option step, because "auto-advancing hides why the others are gone and produces a 'where did
     they go' moment with no answer available." So the step names what is off and how to bring it
     back. Mockup Docs/mockups/b184-guided-build-single-line.html Frame B, Jason-approved 2026-08-17.
     The sole card also opens by default: with nothing to compare against, the space the other two
     would have filled teaches the one that is left (approved with the frame). */
  // B-752: `offLines` LEFT THIS SCOPE with the note — `qbSingleLineNote` derives it from
  // `FC_GAME_LINES` itself, so the list and the sentence that names it cannot come apart.
  var effectiveOpen = soleLine ? soleLine.id : openLine;

  return (
    // FULL HEIGHT, NOT CONTENT HEIGHT — this is what stops the scroll. The cards are flex children
    // of a height-constrained column, so three of them fit BY CONSTRUCTION at any viewport tall
    // enough to hold them, rather than by a value tuned to one screen.
    // `minHeight` is the honest floor: below it the step scrolls rather than squashing the cards
    // under AS-6's 44px tap floor. Fitting by crushing the target is not fitting (AS-5).
    // B-184: the floor follows the number of cards ACTUALLY offered — reserving three cards' height
    // for one card would leave the single-line step stranded in dead space.
    <div data-gameline-accordion="1" data-lines-offered={activeLines.length} style={{
      display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 16px 24px',
      height: '100%', minHeight: (activeLines.length * QB_GL_COLLAPSED_H + 24 + 32) + 'px', boxSizing: 'border-box',
      animation: 'slideInRight 0.35s ease-out',
    }}>
      {/* THE SINGLE-LINE NOTE — the answer to "where did the other lines go?" (R-RG-4). Present ONLY
          when exactly one line survives; with two or three, their absence is self-evident from the
          user's own toggles and the cards speak for themselves (C-MU-3 same-kind states). */}
      {/* B-752: the note itself is `qbSingleLineNote`, which both faces call — the dark vessel here,
          the kit's printed sidebar on the paper game-line page. Not one word moved in the lift. */}
      {soleLine && qbSingleLineNote(soleLine, false)}
      {activeLines.map(function(line, i) {
        var n = careerCount[line.id];
        // B-184: `effectiveOpen` is the sole line when only one survives (open by default, R-RG-4),
        // and the player's own expansion otherwise — the multi-line gesture is untouched.
        var isOpen = effectiveOpen === line.id;
        return (
          <HubCard
            key={line.id}
            expandable
            tourId={i === 0 ? 'p-custom-gameline-card' : undefined}
            collapsedH={QB_GL_COLLAPSED_H}
            eyebrow={MECH_LABEL[line.mechanic]}
            label={gameLineTitle(line.id)}
            sub={teaserOf(line.desc)}
            description={line.desc}
            accent={line.accent}
            isOpen={isOpen}
            anyOpen={effectiveOpen !== null}
            onExpand={function() { setOpenLine(line.id); }}
            onClick={function() { onSelect(line.id); }}
            tapCue={false}
            detail={
              /* WHAT IT DECIDES — the three consequences, measured where they are measurable.
                 B-107's ratified shape, MOVED into the open card rather than rewritten: a front
                 door says what walking through it does. */
              <div data-line={line.id} style={{ paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.10)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {n > 0 && (
                  <div data-decides="careers">
                    <div style={fcDecidesKeyStyle}>CAREERS</div>
                    <div style={fcDecidesValStyle}>{n} to choose from</div>
                  </div>
                )}
                <div data-decides="mechanic" data-mech={line.mechanic}>
                  <div style={fcDecidesKeyStyle}>YOUR CAMPAIGN MECHANIC</div>
                  <div style={fcDecidesValStyle}><b style={{ color: 'rgba(255,255,255,0.92)' }}>{MECH_LABEL[line.mechanic]}</b></div>
                </div>
                <div data-decides="force">
                  <div style={fcDecidesKeyStyle}>THE FORCE</div>
                  <div style={fcDecidesValStyle}>{line.id === 'fad' ? 'A step in your build' : 'Only through a Force-sensitive specialization'}</div>
                </div>
              </div>
            }
          />
        );
      })}
    </div>
  );
}

var fcDecidesKeyStyle = { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.16em', color: 'rgba(255,255,255,0.42)', marginBottom: '2px' };
var fcDecidesValStyle = { fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'rgba(255,255,255,0.8)', maxWidth: 'var(--desk-prose-max)' };


/* ── Step 1: Role ──────────────────────────────────── */
function QBRoleStep({ gameLine, roles, selected, onSelect, accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '8px 16px 24px', animation: 'slideInRight 0.35s ease-out' }}>
      {roles.map(role => {
        // Highlight the previously-selected role on back-nav so a player who
        // backed out from species → role sees what they had picked. Vet-r2 P1.
        var isSelected = !!(selected && (selected.id === role.id || selected.career === role.career));
        return (
        <button key={role.id} onClick={() => onSelect(role)} style={{
          background: isSelected ? accent + '14' : 'rgba(255,255,255,0.02)',
          border: '1px solid ' + (isSelected ? accent + '70' : 'rgba(255,255,255,0.06)'),
          borderRadius: '10px',
          padding: '18px 18px', cursor: 'pointer', textAlign: 'left',
          boxShadow: isSelected ? '0 0 14px ' + accent + '22' : 'none',
          transition: 'border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = accent + '35'; e.currentTarget.style.background = accent + '06'; } }}
        onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; } }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.04em', color: isSelected ? accent : '#fff' }}>{role.label}</div>
            {role.ref && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: accent, marginTop: '3px' }}>{role.ref.toUpperCase()}</div>}
          </div>
          {/* GB-2: disclose the fixed career + specialization this role assigns (was silent). Mechanics voice. */}
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.42)', marginBottom: '7px' }}>
            {role.career}{role.spec ? ' · ' + role.spec : ''}
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'rgba(255,255,255,0.5)' }}>{role.desc}</div>
        </button>
      ); })}
    </div>
  );
}

/* ── Step 2: Species ───────────────────────────────── */
// GUIDED'S SPECIES STEP WAS STILL READING THE RETIRED OGGDUDE RECORD SHAPE. `normalizeOggdude` was
// deleted in charbuild 3a and the list now comes from the canonical registry, but this component was
// never repointed — so it read three fields that no longer exist, and each one failed SILENTLY:
//
//   sp.img            -> undefined  =>  every card rendered an empty 110px black slab
//   sp[<characteristic>] -> undefined  =>  every card printed BR AG IN CU WI PR over six blanks
//   sp.key            -> undefined  =>  `selected.key === sp.key` is undefined===undefined, so once
//                                       any species was picked EVERY card drew as selected
//
// Nothing threw and no smoke covered it, which is how a whole step shipped with no art and no
// numbers. The canonical shape is `{id, characteristics:{brawn…}}` and the portrait comes from
// `speciesImgPath` — the same one home the custom browser and the sheet already use, rather than a
// field baked into the record.
function QBSpeciesStep({ speciesList, selected, onSelect, accent }) {
  const CHAR_LABELS = ['BR', 'AG', 'IN', 'CU', 'WI', 'PR'];
  const CHAR_KEYS = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '8px 16px 24px', animation: 'slideInRight 0.35s ease-out' }}>
      {speciesList.map(sp => {
        const isSelected = selected && selected.id === sp.id;
        const img = speciesImgPath(sp);
        const chars = sp.characteristics || {};
        return (
          <button key={sp.id || sp.name} onClick={() => onSelect(sp)} style={{
            background: isSelected ? accent + '0C' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${isSelected ? accent + '40' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: '10px', padding: '0', cursor: 'pointer', textAlign: 'center', overflow: 'hidden',
            transition: 'border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
            boxShadow: isSelected ? `0 0 20px ${accent}15` : 'none',
          }}>
            {!COMMERCIAL_MODE && <div style={{
              width: '100%', height: '110px', position: 'relative',
              backgroundImage: img ? `url('${img}')` : 'none', backgroundSize: 'contain',
              backgroundPosition: 'center bottom', backgroundRepeat: 'no-repeat', backgroundColor: '#0a0a0a',
              display: img ? undefined : 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {/* Same fallback the custom browser uses: a portrait-less species gets the designed
                  Monogram, not a black rectangle. Human's portrait is deliberately absent (proven
                  book art, deleted under the A1 treatment), so this fires on the very first card. */}
              {!img && window.SWRPGUI.Monogram({ name: sp.name, accent: accent, size: 56 })}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.8) 100%)' }} />
            </div>}
            <div style={{ padding: '8px 10px 10px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xs)', letterSpacing: '0.06em', color: isSelected ? accent : '#fff', marginBottom: '4px' }}>{sp.name}</div>
              {/* UXQP-15: one-line species descriptor */}
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', lineHeight: 1.3, color: 'rgba(255,255,255,0.55)', marginBottom: '6px', minHeight: '13px' }}>{QB_SPECIES_DESC[sp.name] || ''}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                {CHAR_KEYS.map((k, ci) => (
                  <div key={k} style={{ textAlign: 'center', minWidth: '22px' }}>
                    <div style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>{CHAR_LABELS[ci]}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: chars[k] > 2 ? accent : chars[k] < 2 ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.75)', fontWeight: chars[k] > 2 ? 700 : 400 }}>{chars[k]}</div>
                  </div>
                ))}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ── Step 3: Background ────────────────────────────── */
// UXQP-17: ONE sentence per campaign mechanic saying what the background step's choice DOES, in one home, read by
// the dark step and by the paper step's lead (Jason's D-PV2 (a), ruling `paper-verify`: kept on the paper face).
const QB_MECH_FRAMING = {
  obligation: "Taking on more Obligation grants extra starting XP or credits.",
  duty: "Greater Duty earns greater recognition from the Alliance.",
  morality: "Conflict moves Morality each session: high is paragon, low falls dark.",
};
function QBBackgroundStep({ gameLine, mechData, selected, onSelect, accent }) {
  const mechType = mechanicForLine(gameLine);                        // B-107: one home
  const mechLabel = mechType.charAt(0).toUpperCase() + mechType.slice(1);
  let items = [];
  if (mechType === 'obligation') items = (mechData.oblTypes.items || []).slice(0, 12);
  else if (mechType === 'duty') items = (mechData.dutyTypes.items || []).slice(0, 12);
  else {
    // B-117: paired BY `weakKey`, not by list position. This read `weaknesses[i]`, and the two
    // lists are both alphabetical but hold DIFFERENT vocabularies at DIFFERENT lengths (39
    // strengths, 48 weaknesses) — so index i pointed at an unrelated weakness for **39 of 39**
    // strengths. Every Quick Build character was shown a wrong pair: Bravery shadowed by
    // "Antipathy" instead of Anger, Compassion by "Condescension" instead of Hatred. The registry
    // has carried the correct mapping on each strength all along, and the full creator's DETAIL
    // already used it — only this path guessed.
    var _weakByKey = {};
    (mechData.morPairs.weaknesses || []).forEach(function(w) { _weakByKey[w.key] = w; });
    items = (mechData.morPairs.strengths || []).slice(0, 12).map((s) => ({
      key: s.key || s.name, name: s.name,
      description: s.description || '',
      weakness: (_weakByKey[s.weakKey] || {}).name || '',
    }));
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 16px 24px', animation: 'slideInRight 0.35s ease-out' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.18em', color: accent, marginBottom: '4px', textAlign: 'center' }}>
        CHOOSE YOUR {mechLabel.toUpperCase()}
      </div>
      {/* UXQP-17: one-sentence mechanic framing, keyed by the game line's mechanic */}
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.62)', lineHeight: 1.5, textAlign: 'center', marginBottom: '6px' }}>
        {QB_MECH_FRAMING[mechType]}
      </div>
      {items.map(item => {
        const isSelected = selected === (item.key || item.name);
        // B-751: `description` in all three branches, the registry's key (the paper step reads the same); the stale
        // `descriptionRaw` (C-C9-1) left Obligation and Duty blank on this, the rollback (`?qsg=0`) face.
        const desc = (item.description || '').replace(/\[H\d\][^\[]*\[h\d\]\s*/gi, '').replace(/\[p\]/gi, ' ').replace(/\[b\]/gi, '').replace(/\[B\]/gi, '').trim();
        const shortDesc = desc.length > 120 ? desc.slice(0, 120).replace(/\s+\S*$/, '') + '\u2026' : desc;
        return (
          <button key={item.key || item.name} onClick={() => onSelect(item.key || item.name)} style={{
            background: isSelected ? accent + '0C' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${isSelected ? accent + '35' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: '8px', padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
            transition: 'border-color 0.2s ease, background 0.2s ease',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-xs)', color: isSelected ? accent : '#fff', letterSpacing: '0.04em' }}>{item.name}</div>
              {mechType === 'morality' && item.weakness && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,200,100,0.5)', letterSpacing: '0.1em' }}>{'\u2194'} {item.weakness}</div>
              )}
            </div>
            {shortDesc && <div style={{ fontFamily: 'var(--font-body)', fontWeight: 300, fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>{shortDesc}</div>}
          </button>
        );
      })}
    </div>
  );
}

/* ── Step 4: Review ────────────────────────────────── */
function QBReviewStep({ character, gameLine, gameData, role, speciesData, bgKey, accent, onNameChange, onConfirm, onSetPocketMoney }) {
  const careerName = character.career ? character.career.name : (role ? role.career : '\u2014');
  const specName = character.species ? character.species.name : '\u2014';
  const specImg = speciesData ? speciesImgPath(speciesData) : '';   // was speciesData.img — a field the canonical record does not have
  const specKeys = ['brawn', 'agility', 'intellect', 'cunning', 'willpower', 'presence'];
  const specLabels = ['Brawn', 'Agility', 'Intellect', 'Cunning', 'Willpower', 'Presence'];
  /* B-738 - THE REVIEW READS THE RECORD IT IS ABOUT TO WRITE. `guidedAssembledModel` is the
     finalize's own derivation (see its comment), so `displayModel` IS the sheet Guided persists for
     this build, and every number below is read off it instead of recomputed.
     The shallow clone is the render's guard: `seedGuidedForcePower` replaces `forcePowers` on the
     object it is handed, and a render must not write reducer state. */
  const displayModel = React.useMemo(function() {
    if (!gameData || !gameData.charbuild) return null;
    try {
      return guidedAssembledModel(Object.assign({}, character), gameLine, gameData, speciesData).sheet;
    } catch (e) {
      // A review that cannot assemble still draws its identity, characteristics and equipment from
      // the reducer, and the derived panels fall back to what they read before. Never a blank step.
      if (window.console) console.warn('[guided review] assembly failed: ' + e.message);
      return null;
    }
  }, [character, gameLine, gameData, speciesData]);
  /* B-727 - THE RANK IS RESOLVED, NEVER TYPED. This list filtered `character.skills` for truthiness
     and the chip below printed the LITERAL string "Rank 1", so the nine careers whose career and
     specialization grant tables overlap were told rank 1 over a record that persists rank 2
     (`skillRankOf`, `charResolve.js:992`, SUMS the source flags - ruled legal and capped at 2 by
     `f45-disjoint-picks-correction` Q1). B-727 item (2) is the same fix: the chip COUNT is the
     persisted map's own, so an overlapping career shows the skills it actually trained.
     `normalizeCreated` keeps a key only at rank > 0 (`charResolve.js:1452-1457`) - exactly the
     filter this list was approximating by hand. */
  const skillRanks = displayModel ? displayModel.skills : null;
  const skillNames = skillRanks ? Object.keys(skillRanks).sort() : Object.keys(character.skills).filter(k => {
    const s = character.skills[k]; return s && (s.freeCareer || s.freeSpec || s.freeSpecies || s.purchased > 0 || (s.bought && s.bought.length > 0));
  });
  /* B-738 (c) - CREDITS COME OFF THE RECORD. The number printed below was hand-rolled as
     `500 + pocketMoney - SUM(price x qty)`, dropping the `mechanic.creditAdjustment` term
     `charResolve.js:1480` carries. `character.gearLedger.credits` - assigned on the line this
     replaces and never read - is not the remaining figure either: `SET_CREDITS` writes
     `500 + creditAdjustment` (`player/index.html:559-560`) and never subtracts the inventory, so it
     is the GROSS BUDGET. `displayModel.equipment.credits` is `creditTotal - creditSpent`
     (`charResolve.js:1638`): the adjustment, the pocket money and the gear spend, all three.
     CAN THE TWO DIFFER TODAY? No. Guided builds its mechanic with an EMPTY trade list, so
     `computeMechanic` returns `creditAdjustment: 0` on every Guided build and the dropped term is
     worth nothing - which is why this was latent. They diverge the moment a Guided build acquires a
     non-zero adjustment. */
  const credits = displayModel
    ? displayModel.equipment.credits
    : ((function(sc) { return sc.purse + sc.after; })(CharResolve.startingCredits(0, speciesData, character && character.speciesOptionPicks, character && character.creationBuyouts)) + (character.pocketMoney ? (character.pocketMoney.value || 0) : 0)
       - character.gearLedger.inventory.reduce((s, i) => s + (i.price || 0) * (i.qty || 1), 0));
  const xp = displayModel ? displayModel.xp : null;
  return (
    <div style={{ padding: '8px 20px 32px', animation: 'slideInRight 0.35s ease-out', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Name */}
      <div>
        <div style={fcSectionHeaderStyle(accent)}>CHARACTER NAME</div>
        <input type="text" value={character.identity.name} onChange={e => onNameChange(e.target.value)} placeholder="Enter a name..."
          style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: `1px solid ${accent}40`,
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-xl)', letterSpacing: '0.04em', color: '#fff', padding: '8px 0', outline: 'none', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderBottomColor = accent} onBlur={e => e.target.style.borderBottomColor = accent + '40'} />
      </div>
      {/* Identity card */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
        {specImg && <div style={{ width: '64px', height: '64px', borderRadius: '8px', backgroundImage: `url('${specImg}')`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundColor: '#0a0a0a', border: `1px solid ${accent}20`, flexShrink: 0 }} />}
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-sm)', color: '#fff', marginBottom: '4px' }}>{specName}</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: accent, opacity: 0.8 }}>{careerName}</div>
          {character.specializations[0] && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{character.specializations[0].name}</div>}
        </div>
      </div>
      {/* Characteristics */}
      <div>
        <div style={fcSectionHeaderStyle(accent)}>CHARACTERISTICS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {specKeys.map((k, i) => (
            <div key={k} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.04em', marginBottom: '4px', textTransform: 'uppercase' }}>{specLabels[i]}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xl)', color: character.characteristics[k] > 2 ? accent : 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{character.characteristics[k]}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Skills */}
      {skillNames.length > 0 && <div>
        <div style={fcSectionHeaderStyle(accent)}>TRAINED SKILLS</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {skillNames.map(k => {
            var name = SKILL_NAMES[k] || k;
            // B-727: the RESOLVED rank, the way the custom review renders it (`:11335`,
            // `{displayModel.skills[k]}`). The fallback is the pre-B-727 literal and is reached only
            // when the assembly could not run at all, where a wrong 1 beats a blank chip.
            var rank = skillRanks ? skillRanks[k] : 1;
            return <div key={k} data-testid="qb-skill-chip" data-skill={k} data-rank={rank} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.6)', padding: '4px 10px', background: accent + '0A', borderRadius: '4px', border: `1px solid ${accent}15` }}>{name} {'\u2022'} Rank {rank}</div>;
          })}
        </div>
      </div>}
      {/* Equipment */}
      {character.gearLedger.inventory.length > 0 && <div>
        <div style={fcSectionHeaderStyle(accent)}>EQUIPMENT</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {character.gearLedger.inventory.map((item, i) => (
            <div key={item.key + '-' + i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)' }}>{item.name}{item.qty > 1 ? ' \u00d7' + item.qty : ''}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.3)' }}>{(item.price || 0) * (item.qty || 1)} credits</span>
            </div>
          ))}
        </div>
      </div>}
      {/* B-738 (b) - XP, WHICH THIS REVIEW NEVER MENTIONED. A player finished Guided holding
          unspent XP and was never told it existed: a rendered-body census of this step read XP
          False, EXPERIENCE False. The maths was correct and persisted the whole time
          (`charResolve.js:1622`); only the telling was missing, and the custom review has said it
          since B-55 - XP LEFT at `:11439` and the carry-over sentence at `:11459`.
          THE SAME TWO THINGS, in the same words and the same idiom, read off the same record.
          `data-testid` mirrors the custom door's so one census finds both. */}
      {xp && <div>
        <div style={fcSectionHeaderStyle(accent)}>EXPERIENCE</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[['TOTAL XP', xp.total], ['SPENT', xp.spent], ['XP LEFT', xp.remaining]].map(function(pair, i) {
            return (
              <div key={pair[0]} data-testid={'qb-xp-' + ['total', 'spent', 'left'][i]}
                   style={{ flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)',
                              color: pair[1] < 0 ? 'var(--doc-action)' : (i === 2 && pair[1] > 0 ? accent : 'rgba(255,255,255,0.85)') }}>{pair[1]}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', marginTop: '2px' }}>{pair[0]}</div>
              </div>);
          })}
        </div>
      </div>}
      {xp && xp.remaining > 0 && (
        <div data-testid="qb-xp-signpost" style={{
          background: 'rgba(78,170,255,0.06)', border: '1px solid rgba(78,170,255,0.28)',
          borderRadius: '8px', padding: '10px 12px',
        }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.45, color: 'rgba(255,255,255,0.78)' }}>
            Your <strong style={{ color: '#fff' }}>{xp.remaining} unspent XP</strong> carries over. Skill ranks,
            talents and <strong style={{ color: '#fff' }}>Force-power upgrades</strong> are all bought on the
            sheet's <strong style={{ color: '#fff' }}>ADVANCEMENT</strong> tab.
          </div>
        </div>
      )}
      {/* Credits + Mechanic */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <div data-testid="qb-credits-left">
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.2em', color: accent, opacity: 0.5 }}>CREDITS LEFT</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xl)', color: accent }}>
            {credits}
          </div>
        </div>
        {character.mechanic && <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.2em', color: accent, opacity: 0.5 }}>
            {character.mechanic.type === 'obligation' ? 'OBLIGATION' : character.mechanic.type === 'duty' ? 'DUTY' : 'MORALITY'}
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)' }}>
            {character.mechanic.descriptor || '\u2014'} ({character.mechanic.finalValue})
          </div>
        </div>}
      </div>
      {/* F-35: the pocket-money beat, under the credits summary (guided-build mount). */}
      <PocketMoneyBeat value={character.pocketMoney ? character.pocketMoney.value : null} gameLine={gameLine} accent={accent} onSet={onSetPocketMoney} />
      {/* CTA */}
      <button onClick={onConfirm} style={{
        width: '100%', padding: '16px 0', marginTop: '8px',
        background: accent + '18', border: `1px solid ${accent}40`, borderRadius: '8px', cursor: 'pointer',
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.14em', color: accent,
        transition: 'background 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = accent + '28'; e.currentTarget.style.boxShadow = `0 0 24px ${accent}20`; }}
      onMouseLeave={e => { e.currentTarget.style.background = accent + '18'; e.currentTarget.style.boxShadow = 'none'; }}>
        BEGIN YOUR STORY
      </button>
    </div>
  );
}

/* ── QB Loadout Step ──────────────────────────────── */
function QBLoadoutStep({ career, gameData, accent, onSelect }) {
  var _exp = useState(null), expandedId = _exp[0], setExpandedId = _exp[1];
  var loadouts = QB_LOADOUT_OPTIONS[career] || [];

  // Resolve item details from the CANONICAL item registries (loadouts key by OggDude
  // source key; canonicalRefForSourceKey bridges to id-refs — the legacy oggdude item
  // index this read is gone with normalizeOggdude).
  // B-721 — THE ONE-LINE STAT MOVED OUT to `qsgItemStat` and this calls it. Quick Start's opened
  // card draws the same sentence over the same three registries, and two copies of "how a weapon
  // reads on one line" are two places for it to drift (PRAXIS §5). The function IS this block's own
  // three branches, moved — `rangeLabels` with them, and the `crit != null` read that keeps a crit
  // of **0** (the training lightsaber's) from printing as `-`. No rendered string changes.
  // B-722 — AND THE REST OF THE ROW WENT WITH IT: `resolveItem` is now `qsgLoadoutItem` at file
  // scope, because Guided's PAPER loadout page prices the same three bundles off the same registries.
  // One home for "a curated key becomes a priced row"; the body moved unchanged and no rendered
  // string moves with it. `smoke_gb_finalize` drives this step end to end and stays green.

  function handleTap(loadout) {
    if (expandedId === loadout.id) {
      onSelect(loadout);
    } else {
      setExpandedId(loadout.id);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 16px 24px', animation: 'slideInRight 0.35s ease-out' }}>
      {loadouts.map(function(loadout) {
        var isExp = expandedId === loadout.id;
        var resolvedItems = loadout.items.map(function(li) { var r = qsgLoadoutItem(li.key); return { key: li.key, qty: li.qty, name: r.name, price: r.price, enc: r.enc, section: r.section, stat: r.stat }; });
        var totalCost = resolvedItems.reduce(function(s, ri) { return s + ri.price * ri.qty; }, 0);
        var remaining = 500 - totalCost;

        return (
          <button key={loadout.id} onClick={function() { handleTap(loadout); }} style={{
            width: '100%', textAlign: 'left',
            padding: isExp ? '16px' : '14px 16px',
            background: isExp ? accent + '0A' : 'rgba(255,255,255,0.03)',
            border: '1px solid ' + (isExp ? accent + '40' : 'rgba(255,255,255,0.06)'),
            borderRadius: '10px', cursor: 'pointer',
            transition: 'border-color 0.25s, background 0.2s, box-shadow 0.25s',
            boxShadow: isExp ? '0 0 16px ' + accent + '12' : 'none',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isExp ? '6px' : '4px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: isExp ? '15px' : '14px', fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>{loadout.name}</div>
              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-sm)', fontWeight: 700, color: accent }}>{totalCost}cr</div>
                {remaining > 0 && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.3)' }}>{remaining}cr left</div>}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'rgba(255,255,255,0.45)', marginBottom: isExp ? '12px' : '0' }}>{loadout.desc}</div>

            {/* Expanded item list */}
            {isExp && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {resolvedItems.map(function(ri, i) {
                  return (
                    <div key={ri.key + '-' + i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                      padding: '8px 10px', background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xs)', fontWeight: 700, color: '#fff' }}>
                          {ri.name}{ri.qty > 1 ? ' \u00d7' + ri.qty : ''}
                        </div>
                        {ri.stat && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{ri.stat}</div>}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: accent, flexShrink: 0, marginLeft: '8px' }}>
                        {ri.price * ri.qty}cr
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ══ GUIDED'S STEP PAGES ON PAPER (B-722; R-QSG-6 row 4 — frames P, Q, R, S, T, U, X and Y at lab
   `284f9929`, fork (b), Jason's "B" 2026-09-19) ═══════════════════════════════════════════════════

   FIVE BODIES, drawn class for class off the pin's own step bodies — `body_gameline` · `body_role` ·
   `body_species` · `body_background` · `body_loadout`, which BOTH forks shared, so what fork (b)
   decided is the page around them and not these objects. The sixth (`review`) is row 5 / B-723 and
   still stands on its interim island; the head above it is this row's, because the pips and the six
   designations are B-722's for all six steps and the island is a BODY swap.

   NO COPY IS OWED, WHICH IS WHAT JASON'S "B" BOUGHT (R-QSG-2). Every designation and every lead is
   Guided's own `stepTitles` — twelve strings already authored and shipping, including the background
   step's per-line ternary — and every figure below is a registry's or a curated table's: `QB_ROLES`,
   `QB_SPECIES_DESC`, the species registry's characteristics/XP/bases, `obligation-types.json` /
   `duty-types.json` / `morality-pairs.json`, `QB_LOADOUT_OPTIONS` priced through the three item
   registries. Nothing here is typed.

   NO ACT BAR ON STEPS 1–5, and the pin says why in as many words: "NO ACT ROW AT ALL, not an empty
   one holding space… the app advances on the card's own press and has no button under it." So these
   pages render no `hp-onecol-act` and no `hp-phoneact`, the card's press IS the act, and the loadout
   asks for a second tap (its cue is cut, copy-voice-sweep player/app.jsx:32414:20, D-289). ONE EXCEPTION ARRIVED WITH B-750 and
   it is the species step's SUB-VIEW rather than a step: the grant pick's confirm stands in the band's
   own act row once the pick is complete, which is frame AE and which is what the app does (the dark
   bar is `revealed={count === max}` too). The five STEP pages are unchanged. The two-tap is drawn as it is rather
   than flattered — the pin's frame U calls it the one affordance that reads weaker at the desk than at
   the phone, and this build moves no pixel to hide that.

   THE DISPLAY LAW (B-746, Jason 2026-09-20) reaches ONE number on these five pages and is exempt
   there: the species card's `W 10 · S 10` is a species BASE with no character and no ceiling — the
   very case B-746's own sweep names as found-and-deliberately-not-changed (the species dossier's
   WOUND/STRAIN tiles). The loadout's credits are not one of the app's three thresholds, and that line
   already prints both numbers. Marked at the site, and the smoke asserts the absence of a slash as a
   POSITIVE so neither can rot into an unnoticed oversight. */

// THE WAVE AS PIPS, and the pips are the pin's REFUSAL rather than a simplification: the creator's
// paper step carries a NAMED wave (`hp-wave`, one control per step with the step's own word on it) and
// GUIDED HAS NO SUCH WORDS ANYWHERE IN THE PRODUCT — its six steps are the ids in `STEPS` and its
// header renders only "Step N of 6". Six display names would be six words this build invented, so
// both forks drew `hp-steps`, the kit's own wordless form (LAB_NOTES §194), and `paper.css` already
// carries every rule it needs (this is that object's first app consumer).
// `aria-hidden`, which the frame's static markup has no reason to say: the folio beside it already
// reads "Step N of 6" in words, so the pips are the same fact drawn — six empty spans announced to a
// screen reader would be noise over a sentence that is already there (the a11y floor, C-MU-4).
function GBSteps(p) {
  var cells = [];
  for (var i = 1; i <= p.total; i++) {
    cells.push(<span className="hp-step" key={i}
      data-now={i === p.now ? '1' : undefined}
      data-done={i < p.now ? '1' : undefined} />);
  }
  return <div className="hp-steps" aria-hidden="true">{cells}</div>;
}

// THE THREE LINES AS GUIDED'S PAGE DRAWS THEM, every field a READ. The name is `gameLineTitle`'s (the
// books vocabulary's label with " Core Rulebook" off); the cite is that label WHOLE; the mechanic's
// word is the mechanic itself capitalised, which is the pin's own method (`l["mechanic"].title()`) and
// the app's own at the background step (`mechLabel`) — no fourth copy of the obligation/duty/morality
// table, of which this file already holds three (:12089, :21844, :34325 — reported, not touched); the
// sentence is `FC_GAME_LINES`'s own `desc` (its `mechanicLine` is cut, D-289); and the careers figure is COUNTED
// the way `QBGameLineStep` counts it — a career is offered by a line when the line's id is in the
// career record's own `sources`.
function gbGameLineRows(lines) {
  var careers = ((typeof window !== 'undefined') && window._canonicalCharbuild
                 && window._canonicalCharbuild.careers) || {};
  var per = {};
  Object.keys(careers).forEach(function (id) {
    ((careers[id] && careers[id].sources) || []).forEach(function (src) {
      per[src] = (per[src] || 0) + 1;
    });
  });
  return (lines || []).map(function (l) {
    var m = String(l.mechanic || '');
    return {
      id: l.id, grad: l.grad, accent: l.accent,
      title: gameLineTitle(l.id) || l.fallbackTitle,
      mechanicName: m ? (m.charAt(0).toUpperCase() + m.slice(1)) : m,
      mechanicSub: null,          // the pin draws the mechanic's WORD alone in this slot …
      desc: l.desc,
      careerCount: per[l.id] || 0,
      cite: gameLineBookLabel(l.id),
    };
  });
}

/* STEP 1 · GAME LINE (frames P and Q) — the creator's own `hp-line` plates, drawn by the creator's own
   `fcGameLinePlates`, which is why row 1's port carried no `hp-line` rule of its own: the object
   already ships and `paper.css` owns it. The SELECTION state is reachable and real rather than a
   specimen's: a player who backs out of the role step returns with their line still in the draft, and
   `data-selected` marks it — the same back-nav courtesy `QBRoleStep` has carried since vet-r2 P1.
   `fcFilterActive` is the app's own source filter, so a line whose book is off is not offered (B-184 /
   R-RG-2) — and R-RG-4's SINGLE-LINE NOTE, which the dark step draws when exactly one line survives,
   IS HERE AS OF B-752 (frames AF and AG at lab `18301f1f`, Jason-approved 2026-09-21). B-722 left it
   out because no frame drew it and inventing a paper shape for an approved dark state is the thing
   `mockup-is-law` forbids; §D drew it, so the note is built and reads its words off the shipped
   block through `qbSingleLineNote`. */
function GBGameLineStep(p) {
  var lines = fcFilterActive(FC_GAME_LINES);
  if (!lines.length) lines = FC_GAME_LINES;
  /* B-752 — R-RG-4's NOTE IS HERE NOW (frames AF and AG at lab `18301f1f`, Jason-approved 2026-09-21).
     B-722 built this page with the FILTER honoured and no paper shape for the one-survivor case, because
     no frame drew it; §D drew it and the halt is lifted. The predicate is the DARK step's own — exactly
     one survivor, never a flag a caller passes — so the card list and the note cannot disagree about
     which line is left. THE NOTE SITS WHERE THE COMPONENT PUTS IT, the step's first child, above the
     cards and inside the same scroller (the shell's — `hp-phonebody` at the phone, `gb-scroll` at the
     desk), so it scrolls with the thing it explains rather than pinning over it.
     AND NOTHING RESERVES THE MISSING CARDS' ROOM: the dark step floors its height on the number of
     lines ACTUALLY offered, and the paper card list reserves no height at all, so a single card is not
     stranded in three cards' worth of space here by construction rather than by a floor. */
  var sole = lines.length === 1 ? lines[0] : null;
  /* NO `hp-linescroll` WRAPPER, at EITHER band, and this is the one place the build departs from the
     pin's markup — reported both ways. The pin's `body_gameline` wraps the plates in that scroller at
     both widths because it is shared with fork (a)'s creator page, where the scroller IS the zone. In
     this app the zone is already the shell's: `hp-phonebody` at the phone and `gb-scroll` at the desk,
     both declared scrollers in `qsg.css`, so a second one inside them is the nested-scroller trap the
     app already settled one step over — `FCGameLineSheet`'s phone band drops the same wrapper for the
     same reason (B-509), and AS-4 is law where a frame is a proposal (C-MU-4). `hp-lines` keeps its
     own grid either way, so nothing about the three plates moves. */
  return (
    <React.Fragment>
      {sole ? qbSingleLineNote(sole, true) : null}
      <div className="hp-lines" data-gb-lines={lines.length}>
        {fcGameLinePlates({ lines: gbGameLineRows(lines), selected: p.selected, onPick: p.onSelect })}
      </div>
    </React.Fragment>
  );
}

/* STEP 2 · ROLE (frames R and S) — the step with no creator twin, which is the pin's strongest single
   argument for the fork it was given. Four cards, each disclosing the CAREER and SPECIALIZATION the
   role assigns (GB-2's fix, in paper), the reference actor as the cite, and the selected role marked
   by the dark step's own predicate — id OR career, so a back-nav from species shows what was picked. */
function GBRoleStep(p) {
  return (
    <div className="gb-cards">
      {(p.roles || []).map(function (r) {
        var on = !!(p.selected && (p.selected.id === r.id || p.selected.career === r.career));
        return (
          <button type="button" className="hp-plate gb-card" data-pad="data" key={r.id}
            data-gb-role={r.id} data-now={on ? '1' : undefined} data-accent={on ? '1' : undefined}
            aria-pressed={on ? 'true' : 'false'} onClick={function () { p.onSelect(r); }}>
            <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
              <span className="gb-t">{r.label}</span>
              <span className="hp-cite">{r.ref}</span>
            </span>
            <span className="hp-run">{r.career}{' · '}<b>{r.spec}</b></span>
            <span className="gb-d">{r.desc}</span>
          </button>);
      })}
    </div>
  );
}

/* STEP 3 · SPECIES (frame X, drawn at 390 ONLY) — the thirteen the player is offered, in the order
   `QB_SPECIES` names them and resolved by the HOST (`speciesList`: filter to active, then the
   precedence-aware `dedupeByName`, which is why twelve of the thirteen do not silently become their
   Complete Species Guide twin). This page draws no portrait, because the frame draws none.

   THE BONUS-SKILL LINE DERIVES AND IS NOT WRITTEN: it appears under a species whose record carries
   `skill_choice_grants` and under no other — one of the thirteen today (Human) — because a promise of
   extra skills over a species that has none is the exile frame's defect. Pressing that card opens the
   GRANT PICK, which §D drew and Jason approved 2026-09-21 (frames AC/AD/AE at lab `18301f1f`): as of
   B-750 the host swaps in `FCSkillPicker` with `paper` — the SAME component the creator's three mounts
   use — and the interim island this step used to raise is GONE, which took `QSG_INTERIM_ZONES` to
   EMPTY. */
function GBSpeciesStep(p) {
  return (
    <div className="gb-grid">
      {(p.speciesList || []).map(function (s) {
        var on = !!(p.selected && p.selected.id === s.id);
        var g = ((s.skill_choice_grants || [])[0]) || null;
        return (
          <button type="button" className="hp-plate gb-sp" data-pad="snug" key={s.id || s.name}
            data-gb-species={s.id || s.name} data-now={on ? '1' : undefined}
            data-accent={on ? '1' : undefined} aria-pressed={on ? 'true' : 'false'}
            onClick={function () { p.onSelect(s); }}>
            <span className="gb-spn">{s.name}</span>
            <span className="hp-run" style={{ whiteSpace: 'normal', textAlign: 'center' }}>
              {QB_SPECIES_DESC[s.name] || ''}</span>
            <QSRail chars={s.characteristics || {}} short />
            {/* EXEMPT FROM THE n/threshold DISPLAY LAW (B-746, Jason 2026-09-20) — and it is the
                exemption that ruling's OWN sweep already named rather than a new one: "the species
                dossier's WOUND/STRAIN tiles are a species BASE with no character and no ceiling". So
                is this. `wound_threshold_base` is the left-hand side of the rule (`10 + Brawn`), not a
                position inside a ceiling; there is no `n` on a species, because there is no character
                yet. Do not "fix" it to `0/10`. The smoke asserts no `/` reaches this strip. */}
            <span className="hp-none" data-gb-base="1">
              {s.starting_xp + ' XP · W ' + s.wound_threshold_base + ' · S ' + s.strain_threshold_base}</span>
            {g ? (
              <span className="hp-none" data-gb-grant="1" style={{ display: 'block' }}>
                {'+' + g.count + ' ' + String(g.pool || '').replace(/_/g, '-') + ' skill'
                 + (g.count === 1 ? '' : 's') + ', your choice'}</span>) : null}
          </button>);
      })}
    </div>
  );
}

/* STEP 4's ROWS (frame Y) — the campaign mechanic's own vocabulary, twelve entries as the step slices
   it, PAIRED BY `weakKey` for Force and Destiny and never by list position (B-117's bug was index
   pairing over 39 strengths against 48 weaknesses, wrong for 39 of 39).

   IT READS `description` WHERE THE DARK STEP READS `descriptionRaw`, and that is a finding rather than
   a choice: `obligation-types.json` and `duty-types.json` carry `description` and no `descriptionRaw`
   at all, so the shipped dark rows print no sentence for either of those two mechanics — only the
   morality branch, which builds `descriptionRaw` itself, ever shows one. The paper rows print the
   registry's own sentence for all three. The dark step is NOT touched here (its rendered strings are
   not this row's to move); B-722's report carries the row. */
function qbBackgroundRows(gameLine, mechData, limit) {
  var mech = mechanicForLine(gameLine);
  var n = limit || 12;
  var md = mechData || {};
  if (mech === 'obligation' || mech === 'duty') {
    var src = (mech === 'obligation' ? md.oblTypes : md.dutyTypes) || {};
    return (src.items || []).slice(0, n).map(function (it) {
      return { key: it.key || it.name, name: it.name, description: it.description || '', pair: null };
    });
  }
  var weak = {};
  (((md.morPairs || {}).weaknesses) || []).forEach(function (w) { weak[w.key] = w; });
  return (((md.morPairs || {}).strengths) || []).slice(0, n).map(function (s) {
    return { key: s.key || s.name, name: s.name, description: s.description || '',
             pair: (weak[s.weakKey] || {}).name || '' };
  });
}

/* STEP 4 · BACKGROUND (frame Y, drawn at 390 ONLY) — an index of doors in the kit's own `hp-row` form.
   `data-tabwide="1"` is the frame's attribute and it is INERT in this app, carried so the markup is
   the frame's: `holocron.css` uses it to widen a fixed-width tab, where `paper.css` already sizes a
   `[data-noplate]` row's tab to its content (:925) — the lab's finding, closed in the app's kit. */
function GBBackgroundStep(p) {
  var rows = qbBackgroundRows(p.gameLine, p.mechData, 12);
  return (
    <div className="hp-plate" data-pad="bleed" style={{ marginTop: '11px' }}>
      <div className="hp-index">
        {rows.map(function (r) {
          var on = p.selected === r.key;
          return (
            <button type="button" className="hp-row" data-noplate="1" key={r.key}
              data-gb-bg={r.key} data-selected={on ? '1' : undefined}
              aria-pressed={on ? 'true' : 'false'} onClick={function () { p.onSelect(r.key); }}>
              <span className="hp-row-body">
                <span className="hp-row-desig">{r.name}</span>
                <span className="hp-run" style={{ whiteSpace: 'normal' }}>{r.description}</span>
              </span>
              <span className="hp-row-tab" data-tabwide="1">
                {r.pair ? <span className="hp-cite">{'↔ ' + r.pair}</span> : '›'}
              </span>
            </button>);
        })}
      </div>
    </div>
  );
}


// THE PURSE, which the pin calls "the app's own starting figure" and the app types in three places:
// `QBLoadoutStep`'s own `500 - totalCost`, `handleLoadout`'s `SET_CREDITS` (plus the mechanic's
// adjustment), and the loadout step's authored subtitle ("You have 500 credits to spend."). Named once
// for the paper page rather than typed a fourth time; the smoke reads the dark step's literal out of
// the source and asserts the two agree, so the pair cannot drift in silence.
var QB_PURSE = 500;

/* STEP 5 · LOADOUT (frames T and U) — the three curated bundles, PRICED AT RENDER from the three item
   registries (the store's own rule: a price is read, never stored), each row carrying the kernel's own
   one-line stat through `qsgLoadoutItem`. The open bundle lists its items and asks for the second tap
   in the app's own words; the closed ones show the count and the purse.
   A `<table>` INSIDE A `<button>` is the frame's own composition and the kit's ledger classes are
   table classes (`.hp-ledger tr` / `td` in `paper.css`), so this is the one plate where the card's
   content is not all phrasing content — reported both ways, unlike the shelf card, where spans could
   carry the same classes. No interactive element sits inside the table. */
var GB_TAKE_LOADOUT = 'Take this loadout';   // B-1661: D-433 item 2's words, the open card's one way on
function GBLoadoutStep(p) {
  var _open = useState(null), openId = _open[0], setOpenId = _open[1];
  var bundles = QB_LOADOUT_OPTIONS[p.career] || [];
  return (
    <div className="gb-cards">
      {bundles.map(function (b) {
        var open = openId === b.id;
        var rows = b.items.map(function (li) {
          var r = qsgLoadoutItem(li.key);
          return { key: li.key, qty: li.qty, name: r.name, stat: r.stat, price: r.price * li.qty };
        });
        var total = rows.reduce(function (s, r) { return s + r.price; }, 0);
        // B-1661 (D-433 item 2, D-434; the page release-r7-candidate3-fixes-look.html @ e71c8a582): the card is no
        // longer one big button. Its head opens or closes it; the open card ends with the one way on, Take this loadout.
        return (
          <div className="hp-plate gb-card" data-pad="data" key={b.id} data-gb-bundle={b.id} data-now={open ? '1' : undefined}
            data-accent={open ? '1' : undefined}>
            <button type="button" className="gb-card-head" aria-expanded={open ? 'true' : 'false'}
              onClick={function () { setOpenId(open ? null : b.id); }}>
              <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
                <span className="gb-t">{b.name}</span>
                <span className="hp-cite">{qsCredits(total) + ' of ' + qsCredits(QB_PURSE) + ' cr'}</span>
              </span>
              <span className="gb-d">{b.desc}</span>
            </button>
            {open ? (
              <span className="hp-plate" data-pad="snug" data-flat="1"
                style={{ display: 'block', marginTop: '9px' }}>
                <table className="hp-ledger"><tbody>
                  {rows.map(function (r, i) {
                    return (
                      <tr key={r.key + '-' + i} data-gb-kit="row">
                        <td className="hp-lkey" style={{ width: 'auto', whiteSpace: 'normal' }}>
                          {r.name}{r.qty > 1 ? ' ×' + r.qty : ''}</td>
                        <td className="hp-lval" style={{ textAlign: 'left', fontWeight: 400 }}>{r.stat}</td>
                        <td className="hp-lval">{qsCredits(r.price)}</td>
                      </tr>);
                  })}
                </tbody></table>
              </span>) : null}
            <span className="gb-money">
              <span className="hp-none">{rows.length + ' things'}</span>
              <span className="hp-none">{qsCredits(QB_PURSE - total) + ' cr left'}</span>
            </span>
            {open ? <button type="button" className="hp-commit" data-gb-take={b.id}
              onClick={function () { p.onSelect(b); }}>{GB_TAKE_LOADOUT} <span>{'›'}</span></button> : null}
          </div>);
      })}
    </div>
  );
}


/* ══ STEP 6 · THE REVIEW ON PAPER (B-723; R-QSG-6 row 5 — frames V and W at lab `284f9929`, fork
   (b), Jason's "B") ════════════════════════════════════════════════════════════════════════════

   THE LAST INTERIM ISLAND GUIDED HAS. `QSG_INTERIM_ZONES` goes 2 → 1 with this row; what remains is
   `species-pick`, which is B-750's and which NO FRAME DRAWS on either fork (LAB_NOTES §194), so it
   is deliberately left standing rather than invented here.

   TWO COLUMNS AT THE DESK AND ONE AT THE PHONE, which is `gb-cols`' own container query and not a
   second arrangement — frames V and W draw BOTH bands, so nothing about this page's shape is
   derived from a pattern; it is the pin's `body_review` class for class.

   EVERY FIGURE IS READ OFF `guidedAssembledModel` (B-738), which is the SAME function
   `handleQuickBuildComplete` persists through — so the review cannot disagree with the record by
   construction. Nothing below re-derives a number beside it: the characteristics, the skills and
   their ranks, the XP pool and the credits are `sheet`'s; the thresholds come from the shared
   `computeDerived`, which since B-736 reads `characteristicsAtCreation` rather than the live block;
   and the ledger's LINES are the same five terms `charResolve.js:1549-1577` sums, in the same order,
   so their total is that sum rather than a second opinion of it (`gbXpLines`).

   THE ONE THING THE PAPER FACE ADDS THAT THE DARK FACE NEVER HAD is that ledger (R-QSG-5 (3)): the
   shipped review shows a TOTAL · SPENT · XP LEFT block (B-738 (b)) and no account of where the XP
   went. "A receipt with the total torn off is not a receipt" — so the lines are drawn, each priced
   by the published rule (each step to a new rating costs ten times that rating; a talent at its
   tree row's own cost; a Force power at its printed basic cost). It is called out here rather than
   slipped in. */

// THE XP LEDGER'S LINES — THE SAME FIVE TERMS THE SHARED CORE SUMS, ITEMISED.
// `charResolve.js:1549-1577` adds characteristics + talents + Force powers + skill purchases +
// species-penalty buyouts and prints only the sum. This walks the SAME five over the SAME character
// object the assembly was handed, so `Σ lines === sheet.xp.spent` is an ARITHMETIC IDENTITY and not
// a coincidence — the smoke asserts it, and the component publishes both figures so the assert can
// discriminate. A sixth term added upstream would break that equality and redden the leg rather
// than quietly vanishing from the receipt, which is the whole reason the sum is republished here.
function gbXpLines(built, sheet, speciesData) {
  var cb = (window._canonicalCharbuild || {});
  var lines = [];
  // 1 — THE CHARACTERISTIC STEPS, priced by the published rule. The base is the RESOLVED species
  // (a variant option's own characteristics, not the record's root — `speciesEff`, C-OCS-7), which
  // is the term the core uses; the current value is the sheet's.
  var eff = speciesEff(speciesData, built.speciesOptionPicks);
  if (speciesData) {
    FC_CHAR_ORDER.forEach(function (a) {
      var base = (eff.characteristics || {})[a] || 2;
      var cur = (sheet.characteristics || {})[a] || 2;
      for (var r = base + 1; r <= cur; r++) {
        lines.push({ k: 'char-' + a + '-' + r, kind: 'char',
                     label: FC_CHAR_FULL[a] + ' to ' + r, cost: r * 10 });
      }
    });
  }
  // 2 — THE TALENT(S), at the price the specialization tree's own row carries (`ADD_TALENT` stores
  // `(row + 1) * 5`, which IS that row's cost). Named through `talents.json`; a talent whose id the
  // registry cannot answer prints its id rather than a blank, so a data gap is visible.
  Object.keys(built.talents || {}).forEach(function (posKey) {
    var e = built.talents[posKey] || {};
    var rec = cb.talents ? cb.talents[e.talentKey] : null;
    lines.push({ k: 'talent-' + posKey, kind: 'talent',
                 label: (rec && rec.name) || e.talentKey || 'Talent', cost: e.cost || 0 });
  });
  // 3 — THE FORCE POWER Guided seeds on a Force build, at its PRINTED basic cost (fad p.281), which
  // `seedGuidedForcePower` priced before the assembly ran. Absent on every non-Force build, which is
  // why the specimen walk alone would never have reached this line.
  Object.keys(built.forcePowers || {}).forEach(function (pid) {
    var e = built.forcePowers[pid] || {};
    var rec = cb.forcePowers ? cb.forcePowers[pid] : null;
    lines.push({ k: 'power-' + pid, kind: 'power',
                 label: (rec && rec.name) || pid, cost: e.cost || 0 });
  });
  // 4 — SKILL PURCHASES. Guided buys none today (its six ranks are all GRANTS), so this loop emits
  // nothing — it is here because the core counts the term and a receipt that silently drops a term
  // it cannot see is the defect this whole ledger exists to prevent.
  Object.keys(built.skills || {}).forEach(function (k) {
    var b = built.skills[k] && built.skills[k].bought;
    var c = b ? b.reduce(function (s, x) { return s + (x || 0); }, 0) : 0;
    if (c) lines.push({ k: 'skill-' + k, kind: 'skill',
                        label: (SKILL_NAMES[k] || k) + ' rank', cost: c });
  });
  // 5 — THE SPECIES-PENALTY BUYOUTS (R-SFC-2), summed by the core's own function so the two cannot
  // disagree. Zero on every Guided build today, for the same reason as (4).
  var bo = (window.CharResolve && window.CharResolve.creationBuyoutSpend)
    ? window.CharResolve.creationBuyoutSpend(built.creationBuyouts) : 0;
  if (bo) lines.push({ k: 'buyout', kind: 'buyout', label: 'Species penalty bought out', cost: bo });
  return lines;
}

// ONE SKILL AND THE POOL IT THROWS, on Guided's review. The kit's `hp-skill qs-sk` two-column form
// (row 2's measured fix, already in `qsg.css`), with the frame's own annotation — the characteristic
// and its value, then EVERY GRANT THAT REACHED THIS SKILL.
// THE RANK IS RESOLVED AND IT IS PRINTED (B-727, and B-718's ruled overlap is why). The frame's own
// `pool_glyphs(c, 1)` passes a LITERAL 1, which was the board's premise before B-718 measured it
// false: nine of twelve careers name the same skill in BOTH curated tables and `skillRankOf` SUMS
// the source flags, so the character legitimately holds it at RANK 2 (ruled legal and capped at 2 by
// `f45-disjoint-picks-correction` Q1; RAW: FaD p.43-44, AoR p.102). A rank-1 pool over a rank-2
// skill draws the wrong DICE, not just the wrong word — 1 proficiency + 2 ability where the
// character throws 2 proficiency + 1 ability. So the rank is `sheet.skills[k]` and the pool is built
// from it, and the clash with the frame is reported both ways rather than corrected in silence.
function GBSkillRow(p) {
  var D = window.SWRPGDice;
  var pool = (D && D.buildPool) ? D.buildPool({ characteristic: p.charVal, skillRank: p.rank })
                                : { proficiency: 0, ability: 0 };
  return (
    <div className="hp-skill qs-sk" data-gb-skill={p.skillKey} data-rank={p.rank}>
      <span className="qs-skn">
        <span className="hp-skill-name">{p.label}</span>
        <span className="hp-skill-char">
          {p.charKey.toUpperCase() + ' ' + p.charVal + ' · Rank ' + p.rank + ' · ' + p.why}</span>
      </span>
      <span className="hp-pool" data-pool={(pool.proficiency || 0) + 'p' + (pool.ability || 0) + 'a'}>
        {D && D.renderPool ? D.renderPool(pool, { size: 1, cssSized: true }) : null}
      </span>
    </div>
  );
}

// THE POCKET-MONEY BEAT, ONE HOME FOR BOTH PAPER REVIEWS (F-35; R-RVC-5a). This is the creator paper
// review's own trio — roll · edit · set — lifted VERBATIM out of `fcReviewCreditsPlate`, which now
// calls it, because Guided's review carries the same F-35 beat and a second copy of "the book's one
// d100 after gear" is the class PRAXIS §5 names. NO FRAME DRAWS IT on either door: the creator's was
// declared UNDRAWN under R-RVC-5a and carried in the kit's idiom, and this row does the same thing
// rather than inventing a second shape or dropping a shipped affordance on the way to paper.
// Not one rendered string, class or `data-` attribute changes; `smoke_c13_review_mechanic` drives
// the creator side and stays green, which is what makes the lift checkable rather than asserted.
function paperPocketBeat(a) {
  return (
    <React.Fragment>
      {a.pocket == null ? (
        <div className="hp-buy" data-review-pocket-roll="1">
          <span className="hp-buy-name">{'Pocket money · roll d100 once, after gear'}</span>
          <button type="button" className="hp-buy-btn" data-review-pocket-btn="roll"
            onClick={function() { a.onSetPocketMoney({ value: Math.floor(Math.random() * 100) + 1 }); }}>Roll</button>
        </div>
      ) : a.pocketEditing ? (
        <div className="hp-buy" data-review-pocket-edit="1">
          <span className="hp-buy-name">{'Pocket money · 1–100'}</span>
          <input type="number" min="1" max="100" value={a.pocketDraft} data-review-pocket-input="1" aria-label="Pocket money"
            onChange={function(e) { a.setPocketDraft(e.target.value); }}
            style={{ width: '5em', fontFamily: 'var(--font-body)', fontSize: '16px', padding: '6px 8px', border: '1px solid var(--doc-rule)', background: 'var(--doc-paper)', color: 'var(--doc-ink)' }} />
          <button type="button" className="hp-buy-btn" data-review-pocket-btn="set"
            onClick={function() { a.setPocketEditing(false); if (a.pocketDraft !== '' && !isNaN(Number(a.pocketDraft))) a.onSetPocketMoney({ value: a.clampPocket(a.pocketDraft) }); }}>Set</button>
        </div>
      ) : (
        <div className="hp-buy" data-review-pocket-rolled="1">
          <span className="hp-buy-name">{'Pocket money · rolled'}</span>
          <button type="button" className="hp-buy-btn" data-review-pocket-btn="edit"
            onClick={function() { a.setPocketDraft(String(a.pocket)); a.setPocketEditing(true); }}>Edit</button>
        </div>
      )}
      <div style={{ marginTop: '8px' }}><span className="hp-cite" data-review-pocket-cite="1">{a.pocketCite}</span></div>
    </React.Fragment>
  );
}

function GBReviewStep(p) {
  var character = p.character, gameData = p.gameData, cb = (gameData && gameData.charbuild) || {};
  /* THE ONE ASSEMBLY, and it is the FINALIZE'S (B-738). `guidedAssembledModel` returns both halves —
     `built`, the working character after the Force seed, and `sheet`, what `normalizeCreated` will
     persist — and this page reads BOTH: the sheet for every displayed figure, the built character
     for the ledger's five terms, which are the core's own inputs. The shallow clone is the render's
     guard: `seedGuidedForcePower` replaces `forcePowers` on the object it is handed. */
  var model = React.useMemo(function () {
    if (!gameData || !gameData.charbuild) return null;
    try {
      return guidedAssembledModel(Object.assign({}, character), p.gameLine, gameData, p.speciesData);
    } catch (e) {
      if (window.console) console.warn('[guided review · paper] assembly failed: ' + e.message);
      return null;
    }
  }, [character, p.gameLine, gameData, p.speciesData]);
  var sheet = model ? model.sheet : null;
  var built = model ? model.built : character;

  // POCKET MONEY (F-35) — the creator paper review's own state, beside its own lifted markup.
  var _pe = useState(false), pocketEditing = _pe[0], setPocketEditing = _pe[1];
  var _pd = useState(''), pocketDraft = _pd[0], setPocketDraft = _pd[1];
  function clampPocket(n) { n = Math.round(Number(n) || 0); return Math.max(1, Math.min(100, n)); }

  if (!sheet) return <div className="hp-plate" data-pad="read"><span className="hp-none">This review could not be assembled.</span></div>;

  var chars = sheet.characteristics || {};
  var eff = speciesEff(p.speciesData, built.speciesOptionPicks);
  // THE GOLD BEAD'S PREDICATE, the shelf's own (`qsRaisedOf`): a value ABOVE the resolved species
  // base. Here the thing that earned it is the XP the ledger below itemises, which is why the two
  // agree by construction — the bead marks exactly the characteristics the ledger charges for.
  var raised = {};
  FC_CHAR_ORDER.forEach(function (a) {
    if ((chars[a] || 0) > ((eff.characteristics || {})[a] || 0)) raised[a] = true;
  });
  var nRaised = Object.keys(raised).length;

  // THE THRESHOLDS, THROUGH THE SHARED CORE. `computeDerived` is the one home and since B-736 it
  // reads `characteristicsAtCreation` with a per-key fallback to live — so a review that recomputed
  // `wound_threshold_base + chars.brawn` here, which is what the frame's generator does, would be
  // reading a term the app has deliberately stopped reading. The equipment is resolved the way the
  // creator's review resolves it, so the armour in the pack below is where the soak comes from.
  var equip = resolveSheetEquipment(sheet.equipment, sheet);
  var derived = (window.CharResolve && window.CharResolve.computeDerived)
    ? window.CharResolve.computeDerived(sheet, equip, window._canonicalCharbuild) : {};

  // EVERY TRAINED SKILL, AT ITS RESOLVED RANK, IN THE SKILL'S OWN LABEL ORDER — the paper surface's
  // rule one door over (`qsSkillRowsOf`), not the key order the dark chip list happens to sort by.
  // The LIST is `sheet.skills`' own keys, which is B-727 item (2): `normalizeCreated` keeps a key
  // only above rank 0, so an overlapping career shows the skills it actually trained and the cite
  // below counts them rather than counting grants.
  var skillIdx = qsSkillIndex(cb);
  var WHY = { freeCareer: 'Career', freeSpec: 'Specialization', freeSpecies: 'Species' };
  var skillRows = Object.keys(sheet.skills || {}).map(function (k) {
    var s = skillIdx[k] || { label: SKILL_NAMES[k] || k, characteristic: null };
    var src = (built.skills && built.skills[k]) || {};
    var why = ['freeCareer', 'freeSpec', 'freeSpecies'].filter(function (f) { return !!src[f]; })
      .map(function (f) { return WHY[f]; });
    if (src.purchased > 0 || (src.bought && src.bought.length)) why.push('Bought');
    return { key: k, label: s.label, charKey: s.characteristic || '',
             charVal: s.characteristic ? (chars[s.characteristic] || 0) : 0,
             rank: sheet.skills[k], why: why.join(' + ') || 'Granted' };
  });
  skillRows.sort(function (a, b) { return a.label < b.label ? -1 : (a.label > b.label ? 1 : 0); });

  // THE TALENT, NAMED AND PRICED THROUGH THE REGISTRY. Guided buys exactly one today; the page draws
  // whatever the character holds, so a curated table that grew a second would print two.
  var talents = Object.keys(built.talents || {}).map(function (posKey) {
    var e = built.talents[posKey] || {};
    var rec = cb.talents ? cb.talents[e.talentKey] : null;
    return { k: posKey, name: (rec && rec.name) || e.talentKey,
             desc: (rec && rec.description) || '', cost: e.cost || 0 };
  });
  var talentCost = talents.reduce(function (s, t) { return s + t.cost; }, 0);

  // THE CAMPAIGN MECHANIC, through the background step's OWN row builder — one home for "what does
  // this registry entry say", so the review and the step it reviews cannot print two sentences.
  var mechType = mechanicForLine(p.gameLine);
  var mech = character.mechanic || null;
  var mechKey = mech ? mech.descriptor : p.bgKey;
  var mechRow = null;
  if (gameData && gameData.narrativeMechanics) {
    mechRow = qbBackgroundRows(p.gameLine, gameData.narrativeMechanics, 9999)
      .find(function (r) { return r.key === mechKey; }) || null;
  }

  // THE PACK, PRICED AT RENDER from the three item registries — the store's own rule, a price is
  // READ and never stored. The inventory carries CANONICAL id-refs plus their section (ADR 0003), so
  // this is a direct registry read rather than `qsgLoadoutItem`'s source-key bridge, which the
  // loadout STEP needs only because the curated bundles are keyed the OggDude way and the ledger is
  // not. `qsgItemStat` is the same one-line sentence the loadout page and the opened Quick Start card
  // print (B-721's one home). The reducer merges by key, so a row's `qty` is the count and the
  // quantity multiplies the PRICE alone.
  var regs = window._canonicalRegistries || {};
  var pack = ((character.gearLedger && character.gearLedger.inventory) || []).map(function (it, i) {
    var rec = (regs[it.section] && regs[it.section][it.key]) || null;
    return { k: it.key + '-' + i, name: rec ? rec.name : it.name, qty: it.qty || 1,
             stat: rec ? qsgItemStat(it.section, rec) : '',
             price: (rec ? (rec.price || 0) : (it.price || 0)) * (it.qty || 1) };
  });
  var packTotal = pack.reduce(function (s, r) { return s + r.price; }, 0);
  // CREDITS COME OFF THE RECORD (B-738 (c)): `equipment.credits` is `creditTotal - creditSpent`,
  // which carries the mechanic's adjustment and the pocket money that `gearLedger.credits` (a GROSS
  // budget) does not. The pack's rows are what was SPENT and they are drawn beside it, so the two
  // can be read against each other rather than believed.
  var credits = sheet.equipment ? sheet.equipment.credits : 0;

  var xp = sheet.xp || { total: 0, spent: 0, remaining: 0 };
  var xpLines = gbXpLines(built, sheet, p.speciesData);
  var xpLineSum = xpLines.reduce(function (s, l) { return s + l.cost; }, 0);
  var pocket = character.pocketMoney && character.pocketMoney.value != null
    ? Number(character.pocketMoney.value) : null;
  var pocketCite = POCKET_MONEY_CITE[p.gameLine] || POCKET_MONEY_CITE.eote;

  return (
    <div className="gb-cols" style={{ marginTop: '12px' }} data-gb-review="1">
      <div>
        <div className="hp-plate" data-pad="read">
          {/* THE NAME, WHICH IS THE ONE INPUT ON EITHER SURFACE. The frame draws the plate with the
              app's own placeholder inside it; here it is a real field with that placeholder, and
              `onNameChange` is the shipped handler. The plate carries the kit's `data-input="1"`
              inset, so the paper draws the well and the input draws nothing.

              B-724 — AND THE FIELD IS THE THING A FINGER HITS, NOT THE WELL AROUND IT (AS-6).
              B-723 built the well 44px and centred a bare input in it, so the PLATE measured 44 and
              the FIELD measured 18px at 390 / 21px at the three desk bands — an 11px dead band above
              and below a control a player must tap to name their character. AS-6 is a floor for what
              a finger must hit and C-MU-4 puts the standard above the drawing, which is not a reading
              invented here: `paper.css` :1478 carries that exact sentence over this exact object, and
              its measurement ("1366 / 1920 -> 43px, UNDER the floor") fixed the creator's own review
              field for the same reason.
              THE SHAPE IS THE APP'S OWN, LIFTED FROM `fcReviewNamePlate` (:12073): the inset lives on
              the INPUT and the plate carries no `data-pad`, so the input fills the well instead of
              floating in it. NO DRAWN PIXEL MOVES — the plate still measures 44px (11 + 22 + 11) and
              the text still sits on the same baseline; what changes is which box the press lands on.
              No `aria-required` is added: that is a semantic claim about whether Guided may finish
              without a name, and it would light `paper.css`'s ask-ring — a state no frame draws. */}
          <div className="hp-plate" data-input="1" data-flat="1"
            style={{ minHeight: '44px', display: 'flex', alignItems: 'stretch' }}>
            <input type="text" value={character.identity.name} data-gb-name="1"
              aria-label="Character name" placeholder="Enter a name..."
              onChange={function (e) { p.onNameChange(e.target.value); }}
              style={{ width: '100%', boxSizing: 'border-box', minHeight: '44px',
                       background: 'transparent', border: 0, outline: 'none',
                       font: 'inherit', color: 'var(--doc-ink)', padding: '11px 12px' }} />
          </div>
          <p className="hp-run" style={{ marginTop: '11px', whiteSpace: 'normal' }} data-gb-ident="1">
            <b>{sheet.species}</b>{' · ' + sheet.career + ' · '}<b>{sheet.specialization}</b>
            {' · ' + (gameLineTitle(p.gameLine) || p.gameLine)}</p>

          <div className="hp-sechead"><h2>Characteristics</h2>
            <span className="hp-cite" data-gb-raised={nRaised}>{nRaised + ' raised'}</span></div>
          <QSRail chars={chars} raised={raised} />
          {/* THE DISPLAY LAW, AND THIS PAGE TAKES THE PAIR (B-746, Jason 2026-09-20: "Whenever a
              threshold is displayed, it should show n/threshold"). The approved frame draws a BARE
              ceiling here and the law is LATER than the frame, so the clash is reported both ways in
              B-723's report and built the law's way — because the nearest ruled twin is Jason's own
              change: `FCReviewStep`'s creation tiles print `0/13` for exactly this object, a
              character being created whose two tracks are undamaged. B-747 (a)'s exemption does not
              reach it: this is neither a DERIVATION (the encumbrance total) nor a DELTA (the dark
              review's `17 → 18`) nor a species BASE with no character (the species card one step
              back) — it is a state readout on a real character, which is the case the law is about.
              `n` IS A LITERAL 0 and the reasoning is `FCReviewStep`'s own: nothing writes damage
              during creation and `normalizeCreated` never emits the two current keys, so a
              `!= null` read would be a fallback that can never fire. SOAK KEEPS ITS BARE NUMBER —
              it is a modifier on a roll with no track counting up to it, which is the same line
              B-746 drew. */}
          <div className="hp-status" data-gb-vitals="1">
            <span><span className="hp-status-k">Wounds</span>
              <span className="hp-status-v" data-gb-wound={derived.woundThreshold || 0}>
                {'0/' + (derived.woundThreshold || 0)}</span></span>
            <span><span className="hp-status-k">Strain</span>
              <span className="hp-status-v" data-gb-strain={derived.strainThreshold || 0}>
                {'0/' + (derived.strainThreshold || 0)}</span></span>
            <span><span className="hp-status-k">Soak</span>
              <span className="hp-status-v" data-gb-soak={derived.soak || 0}>{derived.soak || 0}</span></span>
            {derived.forceRating ? (
              <span><span className="hp-status-k">Force rating</span>
                <span className="hp-status-v" data-gb-fr={derived.forceRating}>{derived.forceRating}</span></span>
            ) : null}
          </div>

          <div className="hp-sechead"><h2>What you will roll</h2>
            <span className="hp-cite" data-gb-trained={skillRows.length}>{skillRows.length + ' trained'}</span></div>
          {skillRows.map(function (r) {
            return <GBSkillRow key={r.key} skillKey={r.key} label={r.label} charKey={r.charKey}
              charVal={r.charVal} rank={r.rank} why={r.why} />;
          })}

          {talents.length ? (
            <React.Fragment>
              <div className="hp-sechead"><h2>{talents.length > 1 ? 'Talents' : 'Talent'}</h2>
                <span className="hp-cite" data-gb-talent-xp={talentCost}>{talentCost + ' XP'}</span></div>
              {talents.map(function (t) {
                return (
                  <p className="hp-prose" style={{ margin: 0 }} key={t.k} data-gb-talent={t.name}>
                    <b>{t.name}</b>{t.desc ? ' — ' + t.desc : ''}</p>);
              })}
            </React.Fragment>
          ) : null}
        </div>
      </div>

      <div>
        {mech ? (
          <div className="hp-plate" data-pad="read" data-gb-mech={mechType}>
            <div className="hp-runhead" style={{ marginBottom: '9px' }}>
              <span>{mechType}</span>
              <span className="hp-folio" data-gb-mech-value={mech.finalValue}>{mech.finalValue}</span></div>
            <div className="hp-track-name">{mechRow ? mechRow.name : mechKey}</div>
            {mechRow && mechRow.description ? (
              <p className="hp-prose" style={{ margin: '7px 0 0' }}>{mechRow.description}</p>) : null}
            {mechRow && mechRow.pair ? (
              <span className="hp-cite" style={{ display: 'block', marginTop: '7px' }}
                data-gb-mech-pair="1">{'↔ ' + mechRow.pair}</span>) : null}
          </div>
        ) : null}

        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}
          data-gb-pack="1" data-gb-pack-total={packTotal}>
          <div className="hp-runhead" style={{ marginBottom: '9px' }}><span>The pack</span>
            <span className="hp-folio">{pack.length + ' things'}</span></div>
          {pack.length ? (
            <table className="hp-ledger"><tbody>
              {pack.map(function (r) {
                return (
                  <tr key={r.k} data-gb-pack-row="1">
                    <td className="hp-lkey" style={{ width: 'auto', whiteSpace: 'normal' }}>
                      {r.name}{r.qty > 1 ? ' ×' + r.qty : ''}</td>
                    <td className="hp-lval" style={{ textAlign: 'left', fontWeight: 400 }}>{r.stat}</td>
                    <td className="hp-lval">{qsCredits(r.price)}</td>
                  </tr>);
              })}
            </tbody></table>
          ) : <span className="hp-none">Nothing is carried yet.</span>}
          <dl style={{ margin: 0 }}>
            <div className="hp-xp" data-total="1">
              <dt>Credits left</dt>
              <dd data-gb-credits={credits}>{qsCredits(credits)}</dd></div>
          </dl>
          {/* UNDRAWN (R-RVC-5a), and carried rather than dropped: F-35's pocket money is a shipped
              affordance of BOTH reviews and no frame draws it on either. The creator's paper review
              answered that by carrying it in the kit's own idiom; this is that same markup, from the
              same function. Reported in B-723's list of what the frames did not draw. */}
          {paperPocketBeat({ pocket: pocket, pocketEditing: pocketEditing,
            setPocketEditing: setPocketEditing, pocketDraft: pocketDraft,
            setPocketDraft: setPocketDraft, clampPocket: clampPocket, pocketCite: pocketCite,
            onSetPocketMoney: p.onSetPocketMoney })}
        </div>

        {/* THE XP LEDGER — R-QSG-5 (3), the one number the paper face adds. */}
        <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}
          data-gb-xp="1" data-gb-xp-sum={xpLineSum} data-gb-xp-spent={xp.spent}>
          <div className="hp-runhead" style={{ marginBottom: '9px' }}><span>Experience</span>
            <span className="hp-folio" data-gb-xp-total={xp.total}>
              {sheet.species + ' · ' + xp.total + ' to start'}</span></div>
          <dl style={{ margin: 0 }}>
            {xpLines.map(function (l) {
              return (
                <div className="hp-xp" key={l.k} data-gb-xp-line={l.kind}>
                  <dt>{l.label}</dt><dd data-gb-xp-cost={l.cost}>{l.cost}</dd></div>);
            })}
            <div className="hp-xp" data-total="1" data-over={xp.remaining < 0 ? '1' : undefined}>
              <dt>Left to spend</dt>
              <dd data-gb-xp-left={xp.remaining}>{xp.remaining}</dd></div>
          </dl>
          {/* UNDRAWN, and the words are the product's own — B-738 (b) lifted this sentence out of
              `FCReviewStep` an hour before this row opened, and a ledger that ends at "Left to
              spend" without saying where it is spent is the census's §4 finding half-answered. Not
              re-authored: the same three emphases, the same ADVANCEMENT tab. */}
          {xp.remaining > 0 ? (
            <p className="hp-run" data-gb-xp-signpost="1"
              style={{ marginTop: '10px', whiteSpace: 'normal' }}>
              {'Your ' + xp.remaining + ' unspent XP carries over. Skill ranks, talents and '
               + 'Force-power upgrades are all bought on the sheet’s ADVANCEMENT tab.'}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// THE WORD FOR THE ACT THAT TAKES A GUIDED BUILD, and the shipped literal is DELIBERATELY NOT
// replaced with this name — the same decision `QS_SELECT_LABEL` records one door over (B-721 (2),
// ruled B-748). The approved lab board resolves this button's word by scanning the app's lines for
// `BEGIN YOUR STORY` (`qsg_build.py`, `BEGIN_LABEL`), so folding the dark step's JSX text into
// `{QB_BEGIN_LABEL}` would leave only a declaration for that parse to find and stop the approved
// board's next rebuild against a tree this row may not edit. `.hp-commit` is
// `text-transform:uppercase` in paper.css, so the frame's title-cased `Begin Your Story` and the
// shipped SHOUT are the same pixels. The duplication is GUARDED rather than removed:
// `smoke_qsg_transplant` parses the dark step's own line out of the source and asserts the paper
// button prints exactly it. B-726 makes the copy single by subtraction.
var QB_BEGIN_LABEL = 'BEGIN YOUR STORY';

/* ── QuickBuild Controller Hook ────────────────────── */
function useQuickBuildController(resumeDraft) {
  // UXQP-6: same draft hydrate/persist contract as useFullCreatorController —
  // all state here is plain JSON (QB role/species records + primitives).
  const _draft = (resumeDraft && resumeDraft.flow === 'quickbuild' && resumeDraft.character) ? resumeDraft : null;
  const [gameLine, setGameLine] = useState(_draft ? _draft.gameLine : null);
  const [role, setRole] = useState(_draft ? _draft.role : null);
  const [selectedSpecies, setSelectedSpecies] = useState(_draft ? _draft.species : null);
  const [bgKey, setBgKey] = useState(_draft ? _draft.bgKey : null);
  const [character, dispatch] = React.useReducer(charReducer, null, () => _draft ? _draft.character : createEmptyCharacter('eote'));
  const [step, setStep] = useState(_draft ? _draft.step : 0);
  const STEPS = ['gameline', 'role', 'species', 'background', 'loadout', 'review'];
  const updateCharacter = useCallback((field, value) => dispatchBinding(field, value, dispatch), []);
  useEffect(() => {
    if (!gameLine) return;
    saveCreationDraft({
      // `step` here indexes QuickBuild's OWN `STEPS` array, not FC_STEPS — a separate space, so
      // B-72's by-name contract does not apply and `v: 1` is correct for it. `loadCreationDraft`
      // only resolves a stepName for flow==='custom', which is what keeps the two apart.
      v: 1, flow: 'quickbuild', gameLine, step, role, species: selectedSpecies,
      bgKey, character, savedAt: Date.now(),
    });
  }, [gameLine, step, role, selectedSpecies, bgKey, character]);
  const nextStep = useCallback(() => setStep(s => Math.min(STEPS.length - 1, s + 1)), []);
  const prevStep = useCallback(() => setStep(s => Math.max(0, s - 1)), []);
  return { character, dispatch, updateCharacter, step, currentStep: STEPS[step], totalSteps: STEPS.length, gameLine, setGameLine, role, setRole, selectedSpecies, setSelectedSpecies, bgKey, setBgKey, nextStep, prevStep, setStep };
}

/* ── QuickBuild Shell ──────────────────────────────── */
// `paper` (B-719, R-QSG-3) — the SAME component takes the ground as a prop; the host passes
// `qsgTransplantSwitch()`. Absent or false is today's six dark steps, unchanged, on the same
// `STEPS` vocabulary and the same `swrpg_creation_draft` (R-QSG-4: neither moves).
function QuickBuild({ onBack, onComplete, resumeDraft, paper }) {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const ctrl = useQuickBuildController(resumeDraft);
  const mode = useLayoutMode();  // F-46 D1 adoption 2 (guided creator)

  // B-14: the species bonus-skill pick — RAW-required for Human (2 non-career skills "of their
  // choice", EotE p.48) and any future grant species. Guided surfaces it as one compact sub-view on
  // the species step (concept guided-parity-concept §3), reusing the custom creator's picker + the
  // shared pool helper (one home). `speciesPick` holds {grant, pool} while the player chooses; null =
  // no pick in flight (the 12 non-grant guided species never see it). The picks land as freeSpecies
  // ranks via the shared reducer (SET_FREE_SPECIES_SKILLS), preserved through loadout/review.
  const [speciesPick, setSpeciesPick] = useState(null);
  const [speciesPickSel, setSpeciesPickSel] = useState([]);
  // B-55 (second route, found by censusing the picker's mounts rather than trusting the report):
  // the guided path AUTO-dispatches SET_FREE_CAREER_SKILLS + SET_FREE_SPEC_SKILLS, so a guided
  // character reaches the species bonus-pick with ranks already committed. That picker passed
  // `rankCap` but no `currentRanks`, which makes curRank always 0 and the cap inert — a nominally
  // armed check that could never fire. Same arithmetic as FullCreator's fcCommittedRanks.
  const qbCommittedRanks = useMemo(function() {
    var out = {};
    var chSkills = (ctrl.character && ctrl.character.skills) || {};
    Object.keys(chSkills).forEach(function(k) {
      var sk = chSkills[k];
      var r = (sk.freeCareer ? 1 : 0) + (sk.freeSpec ? 1 : 0) + (sk.purchased || 0);
      if (r > 0) out[k] = r;
    });
    return out;
  }, [ctrl.character]);

  // B-27: same retryable boot as FullCreator — catch rejections AND null-resolving loaders.
  // B-184: gate on SWRPGSources.ready() too, exactly as FullCreator does. `fcFilterActive` reads the
  // index SYNCHRONOUSLY and FAILS OPEN when it is null, so without this await the first render of a
  // filtered step would show the unfiltered list and then silently correct itself — the precise
  // ready()-gap B-183 censused (this was one of its five unawaited load paths, correct-by-
  // construction only for as long as QuickBuild filtered nothing; it filters now).
  function bootLoad() {
    setLoadError(false); setLoading(true);
    var sourcesReady = (window.SWRPGSources && window.SWRPGSources.ready) ? window.SWRPGSources.ready() : Promise.resolve();
    Promise.all([loadQuickBuildData(), loadCanonicalRegistries(), loadCanonicalCharacterBuild(), sourcesReady]).then(res => {
      if (!res[1] || !res[2]) { setLoadError(true); setLoading(false); return; }
      setGameData({ charbuild: res[2], narrativeMechanics: res[0].narrativeMechanics, canonical: res[1] });
      setLoading(false);
    }).catch(err => {
      console.warn('[quickbuild] boot load failed:', err);
      setLoadError(true); setLoading(false);
    });
  }
  useEffect(() => { bootLoad(); }, []);

  // B-184: re-render when the ACTIVE SOURCES set changes, so the line + species lists re-filter live
  // if a GM toggles a book in ⚙ mid-build. Same subscription FullCreator carries.
  const [srcTick, setSrcTick] = useState(0);
  useEffect(() => {
    const on = () => setSrcTick(t => t + 1);
    window.addEventListener('swrpg-sources-changed', on);
    return () => window.removeEventListener('swrpg-sources-changed', on);
  }, []);

  const accent = ctrl.gameLine ? QB_ACCENT[ctrl.gameLine] : '#00C8FF';
  const bg = ctrl.gameLine ? QB_GRAD[ctrl.gameLine] : 'linear-gradient(160deg, #0a0a0a 0%, #000 100%)';

  const handleGameLine = useCallback(gl => {
    ctrl.setGameLine(gl);
    ctrl.dispatch({ type: 'HYDRATE', value: createEmptyCharacter(gl) });
    ctrl.nextStep();
  }, [ctrl]);

  const handleRole = useCallback(role => {
    if (!gameData) return;
    ctrl.setRole(role);
    const career = Object.values(gameData.charbuild.careers).find(c => c.name === role.career);
    if (career) {
      ctrl.updateCharacter('career', { key: career.id, name: career.name });
      if (career.specializations.length > 0) {
        const spec = gameData.charbuild.specializations[career.specializations[0]];
        if (spec) ctrl.dispatch({ type: 'SET_SPEC', value: { key: spec.id, name: spec.name } });
      }
    }
    ctrl.nextStep();
  }, [gameData, ctrl]);

  const handleSpecies = useCallback(sp => {
    ctrl.setSelectedSpecies(sp);
    ctrl.updateCharacter('species', { key: sp.id, name: sp.name });
    // C-OCS-7 (ruling `csg-option-contract-inert`): the comment below asserted "guided species carry
    // no pick-one option" (B-14). Fork B falsifies that for the variant case — a variant group is a
    // pick-one and its `preselect` makes it resolvable with no player input — so the guided seed
    // goes through the same resolution as the custom one. With no picks recorded, a preselect group
    // resolves to its first option and everything else to the base, which is exactly what B-14
    // described for the species Guided Build can reach today.
    // B-418: the Guided seed RECORDS what the resolution resolves — a preselected group's first option
    // is written into the map (and banked as `speciesOption.picks`, the field the assembly hands the
    // sheet as `speciesOptionPicks`), so its `skill_grants` reach the sheet through the one path and
    // the sheet's ask mount does not re-ask it. The XP delta rides with it (B-22's call-site rule,
    // the one shared reduce). Guided draws no second question, so a preselected option's own
    // `skill_choice_grants` / `starting_talents` are not banked here (B-14's contract; zero today).
    var gPicks = fcRecordedPicks(sp, ctrl.character && ctrl.character.speciesOptionPicks);
    ctrl.updateCharacter('speciesOptionPicks', gPicks);
    ctrl.updateCharacter('speciesOption', {
      xp: (window.CharResolve && window.CharResolve.speciesOptionXpDelta) ? window.CharResolve.speciesOptionXpDelta(sp, gPicks) : 0,
      skillChoiceGrants: [], startingTalents: [], picks: gPicks,
    });
    var gEff = speciesEff(sp, gPicks);
    ctrl.dispatch({ type: 'SET_FIELD', field: 'characteristics', value: {
      brawn: gEff.characteristics.brawn, agility: gEff.characteristics.agility, intellect: gEff.characteristics.intellect,
      cunning: gEff.characteristics.cunning, willpower: gEff.characteristics.willpower, presence: gEff.characteristics.presence,
    }});
    // B-14: the guided grant (if any) is the species root's; a variant group grants no skill.
    var grant = speciesChoiceGrantOf(sp, null);
    if (grant && gameData) {
      var cb = gameData.charbuild;
      var careerData = ctrl.role ? Object.values(cb.careers).find(function(c) { return c.name === ctrl.role.career; }) : null;
      var specData = careerData && careerData.specializations.length ? cb.specializations[careerData.specializations[0]] : null;
      var careerStatusIds = creatorCareerStatusIds(cb, careerData, specData && specData.id, { speciesId: sp && sp.id,
        speciesSkillChoices: ctrl.character && ctrl.character.speciesSkillChoices });   // F-100 M7 C2
      // B-40 LIMITATION, deliberate (ruling `f-eds-6-specializations-strip` §3.2): this is the THIRD
      // career-status derive site and the only one that does NOT add a spec's player-chosen career
      // skill (CharResolve.specChoiceSkillsFor). It is unreachable today — the guided path always
      // resolves `career.specializations[0]`, a career-OWNED spec, and only universal specs carry
      // `career_skill_choice_grants`. If QuickBuild is ever redesigned to offer universal specs, wire
      // the choice in here too; that can only happen by deliberate redesign, never by data growth.
      var pool = speciesChoicePoolOf(grant, cb, careerStatusIds);
      // Pre-select role-recommended skills that fall IN the pool — visible + changeable, never a
      // hidden auto-pick (concept §2). For Human's non-career pool the role plan rarely overlaps,
      // so the player consciously picks; the CONFIRM gate holds at exactly grant.count either way.
      var plan = QB_DEFAULT_SKILLS[ctrl.role ? ctrl.role.career : ''] || [];
      var planIds = {};
      Object.keys(cb.skillSheetKeys || {}).forEach(function(id) { if (plan.indexOf(cb.skillSheetKeys[id]) >= 0) planIds[id] = true; });
      setSpeciesPickSel(pool.filter(function(id) { return planIds[id]; }).slice(0, grant.count));
      setSpeciesPick({ grant: grant, pool: pool });
      return;  // stay on the species step; the picker renders in place of the species list
    }
    ctrl.nextStep();
  }, [ctrl, gameData]);

  const handleSpeciesPickToggle = useCallback(function(sk) {
    setSpeciesPickSel(function(prev) { return prev.indexOf(sk) >= 0 ? prev.filter(function(k) { return k !== sk; }) : prev.concat([sk]); });
  }, []);
  const handleSpeciesPickConfirm = useCallback(function() {
    var keys = (gameData && gameData.charbuild.skillSheetKeys) || {};
    ctrl.dispatch({ type: 'SET_FREE_SPECIES_SKILLS', value: speciesPickSel.map(function(id) { return keys[id] || id; }) });
    setSpeciesPick(null); setSpeciesPickSel([]);
    ctrl.nextStep();
  }, [ctrl, gameData, speciesPickSel]);

  const handleBackground = useCallback(key => { ctrl.setBgKey(key); ctrl.nextStep(); }, [ctrl]);

  // Loadout selection — resolves items from database, assigns inventory with real prices
  const handleLoadout = useCallback(loadout => {
    if (!gameData || !ctrl.role) return;
    var careerName = ctrl.role.career;
    var autoBuild = QB_AUTO_BUILD[careerName] || {};
    // QB_AUTO_BUILD keys talents/specs by OggDude source key; bridge to canonical ids.
    var cb = gameData.charbuild;
    function talentIdForSourceKey(k) {
      var hit = Object.keys(cb.talents).find(function(id) { return cb.talents[id]._source_key === k; });
      return hit || k;
    }
    function specIdForSourceKey(k) {
      var hit = Object.keys(cb.specializations).find(function(id) { return cb.specializations[id]._source_key === k; });
      return hit || k;
    }

    // 1. Set free career skills (4)
    ctrl.dispatch({ type: 'SET_FREE_CAREER_SKILLS', value: QB_DEFAULT_SKILLS[careerName] || [] });

    // 2. Set free spec skills (2) — use the dedicated bulk action to avoid
    // stale-closure overwrites when dispatching multiple SET_FIELD calls.
    if (autoBuild.specSkills) {
      ctrl.dispatch({ type: 'SET_FREE_SPEC_SKILLS', value: autoBuild.specSkills });
    }

    // 3. Boost characteristics (spend XP on 2 attributes, raising from species base to target)
    if (autoBuild.charBoosts && ctrl.selectedSpecies) {
      var newChars = Object.assign({}, ctrl.character.characteristics);
      autoBuild.charBoosts.forEach(function(boost) {
        var attr = boost[0];
        var target = boost[1];
        // C-OCS-7: the guided auto-build prices its boosts off the resolved variant, not the record.
        var base = (speciesEff(ctrl.selectedSpecies, ctrl.character && ctrl.character.speciesOptionPicks).characteristics || {})[attr] || 2;
        if (target > base && target <= 5) {
          newChars[attr] = target;
        }
      });
      ctrl.dispatch({ type: 'SET_FIELD', field: 'characteristics', value: newChars });
    }

    // 4. Assign starting talent (1 row-0 talent, costs 5 XP)
    if (autoBuild.talent) {
      var t = autoBuild.talent;
      ctrl.dispatch({ type: 'ADD_TALENT', specKey: specIdForSourceKey(t.specKey), row: t.row, col: t.col, talentKey: talentIdForSourceKey(t.key), cost: (t.row + 1) * 5 });
    }

    // 5. Set mechanic — Obligation/Duty start at 10; Morality starts at 50 per FFG rules
    var mechType = mechanicForLine(ctrl.gameLine);                   // B-107: one home
    var mechBase = mechType === 'morality' ? 50 : 10;
    var mech = computeMechanic(mechType, mechBase, ctrl.bgKey, [], gameData.narrativeMechanics.config);
    if (mech) { ctrl.updateCharacter('mechanic', mech); ctrl.dispatch({ type: 'SET_CREDITS', value: CharResolve.startingCredits(mech.creditAdjustment, ctrl.selectedSpeciesData, ctrl.character && ctrl.character.speciesOptionPicks, ctrl.character && ctrl.character.creationBuyouts).purse }); }

    // 6. Clear and add inventory as canonical id-refs (ADR 0003). Loadouts are keyed by OggDude
    //    Key -> bridge to the canonical id + section; an item absent from the canonical catalog is
    //    skipped (graceful).
    ctrl.dispatch({ type: 'RESET_INVENTORY' });
    loadout.items.forEach(function(li) {
      var ref = canonicalRefForSourceKey(li.key);
      if (!ref) return;
      var rec = window._canonicalRegistries[ref.section][ref.id];
      var name = rec.name;
      var price = rec.price || 0;
      var enc = rec.encumbrance || 0;
      for (var q = 0; q < li.qty; q++) {
        ctrl.dispatch({ type: 'ADD_ITEM', item: { key: ref.id, name: name, price: price, enc: enc, section: ref.section, qty: 1 } });
      }
    });
    ctrl.nextStep();
  }, [gameData, ctrl]);

  // Auto-assign skills + mechanic on entering review (if loadout was already handled)
  useEffect(() => {
    if (ctrl.currentStep !== 'review' || !ctrl.role || !gameData) return;
    // Skills are already set in handleLoadout; this is a safety re-apply
    const careerName = ctrl.role.career;
    ctrl.dispatch({ type: 'SET_FREE_CAREER_SKILLS', value: QB_DEFAULT_SKILLS[careerName] || [] });
  }, [ctrl.currentStep, ctrl.role, gameData]);

  const handleBack = useCallback(() => {
    // B-14: back out of the species skill pick returns to the species list, not the prior step.
    if (speciesPick) { setSpeciesPick(null); setSpeciesPickSel([]); return; }
    ctrl.step === 0 ? onBack() : ctrl.prevStep();
  }, [ctrl.step, onBack, ctrl, speciesPick]);
  // R1: hand over the species RECORD the species step already resolved, instead of making the
  // finalize re-find it by name.
  const handleConfirm = useCallback(() => { onComplete(ctrl.character, ctrl.gameLine, gameData, ctrl.selectedSpecies); }, [ctrl.character, ctrl.gameLine, onComplete, gameData, ctrl.selectedSpecies]);

  /* B-184 (R-RG-3) + RIDER R-RG-6 — the guided species shelf, filtered and resolved the way the rest
     of the app resolves a species by name.

     WAS: `QB_SPECIES.map(name => Object.values(species).find(s => s.name === name))` — no source
     filter (so a species from a book you switched off was still offered), and a RAW `.find` that
     takes whichever record sits first in the registry. 12 of the 13 curated names carry a
     `csg_*` twin from The Complete Species Guide, so that lookup was correct ONLY by registry
     ordering — which is not a contract (ruling §2.2; `csg-v7-xlsx-reimport` is a live dormant
     handoff that would reorder these keys). Pinned by test/qb_source_gate.cjs, whose reversed-order
     leg asserts the official record still wins and whose positive control shows the retired `.find`
     flipping 12 of 13 under that same order.

     NOW, in order: filter to ACTIVE records with the shared predicate, then collapse name collisions
     with the shared `dedupeByName` — the precedence-aware path (official ranks above community),
     which by construction only ever sees active records. Then take the curated 13 by name from the
     survivors. A curated name whose book is off simply is not there (R-RG-5: Chiss is a Supplement
     species and correctly drops with `Enter the Unknown`) — and the list cannot empty: the worst
     legal configuration (AoR alone) still leaves four (ruling §2.1).

     ABOVE THE EARLY RETURNS ON PURPOSE: `loadError`/`loading` return before the render body, so a
     hook placed after them runs on some renders and not others — React error #310, which the browser
     smoke caught as a blank step (and three of its legs pass VACUOUSLY when the step never renders,
     which is what the positive controls are for). */
  const speciesList = useMemo(function() {
    if (!gameData) return [];
    var S = (typeof window !== 'undefined') && window.SWRPGSources;
    var all = Object.values(gameData.charbuild.species);
    var active = fcFilterActive(all);
    var cur = S && S.current();
    var resolved = (S && S.dedupeByName && cur && cur.index) ? S.dedupeByName(active, cur.index) : active;
    var byName = {};
    resolved.forEach(function(s) {
      var k = (S && S.dedupeName) ? S.dedupeName(s && s.name) : String((s && s.name) || '').trim().toLowerCase();
      if (k && !byName[k]) byName[k] = s;
    });
    return QB_SPECIES.map(function(name) {
      var k = (S && S.dedupeName) ? S.dedupeName(name) : String(name).trim().toLowerCase();
      return byName[k];
    }).filter(Boolean);
    // srcTick: re-resolve when the active set changes mid-build (the ⚙ toggle).
  }, [gameData, srcTick]);

  if (loadError) return <DataLoadErrorScreen onRetry={() => { clearCanonicalLoadCaches(); bootLoad(); }} />;
  if (loading) return (
    <div style={{ position: 'absolute', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* B-437, and this site needed TWO fixes rather than one. The pulse becomes the guardable
          class as above — but "floor-safe" was unreachable here by any range, because the INK was
          already under the floor standing still: `rgba(255,255,255,0.3)` on #000 measures
          **2.46:1**, and the old .55 trough took it to 1.46:1. At .6 it rests at 7.37:1 and
          troughs at 5.47:1. The ruling asked for a floor-safe loader; on this one that is the ink,
          not the range. Disclosed in the gate — no mockup covers a loading screen. */}
      <div className="breathing-text" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.6)' }}>LOADING GALACTIC DATA...</div>
    </div>
  );

  const stepTitles = {
    gameline:   { title: 'What kind of Star Wars story?' },
    role:       { title: 'What\u2019s your place in the galaxy?' },
    species:    { title: 'Who are you?' },
    background: { title: 'What drives you?' },
    loadout:    { title: 'What do you carry?', subtitle: 'Choose a starting loadout. You have 500 credits to spend.' },
    review:     { title: 'Your story begins here.' },
  };
  // B-14: the species step wears the skill-pick title while a grant pick is in flight.
  const _speciesPickTitle = speciesPick && speciesPick.grant
    ? { title: 'Pick your bonus skills', subtitle: (ctrl.selectedSpecies ? ctrl.selectedSpecies.name : 'Your species') + ' trains ' + speciesPick.grant.count + ' extra skill' + (speciesPick.grant.count === 1 ? '' : 's') + ' — your choice.' }
    : null;
  const st = _speciesPickTitle || stepTitles[ctrl.currentStep] || {};

  // B-719 (R-QSG-3) — THE SHIPPED BODY, NAMED SO THE PAPER FACE CAN STAND OVER IT. Not one line of
  // it changed: `QBStepHeader` with its `data-tour-id` anchor, `CenterColumn`, the scroller and all
  // six step components with their props. With the switch OFF it is still what this component
  // returns; with it ON it mounts inside the step's interim island until B-722 and B-723 take it.
  var legacyBody = (
    <div style={{ position: 'absolute', inset: 0, background: bg, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'background 0.5s ease' }}>
      {/* R-CDW-3: BOTH DOORS. The guided flow shares `QBStepHeader`, and although it renders no
          build rail today — so its heading and body already agree — the cap is passed from ONE
          place rather than defaulted in two. That is the actual fix: the pair cannot drift if a
          later lap widens this column, which is exactly how the custom door's offset was born. */}
      <div data-tour-id="p-quickbuild-header"><QBStepHeader title={st.title} subtitle={st.subtitle} step={ctrl.step} totalSteps={ctrl.totalSteps} onBack={handleBack} accent={accent}
        contentMax={QB_STEP_COLUMN_MAX} /></div>
      {/* F-46 D1: guided step content centers at the reading-column cap on desktop (CenterColumn
          always mounted, A4). Header spans the shell; only the content column narrows.
          B-14: the key includes speciesPick so the species bonus-skill sub-view swaps in cleanly. */}
      <CenterColumn mode={mode} max={QB_STEP_COLUMN_MAX}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }} key={speciesPick ? 'speciesPick' : ctrl.currentStep}>
        {ctrl.currentStep === 'gameline' && <QBGameLineStep onSelect={handleGameLine} />}
        {ctrl.currentStep === 'role' && <QBRoleStep gameLine={ctrl.gameLine} roles={QB_ROLES[ctrl.gameLine] || []} selected={ctrl.role} onSelect={handleRole} accent={accent} />}
        {ctrl.currentStep === 'species' && !speciesPick && <QBSpeciesStep speciesList={speciesList} selected={ctrl.selectedSpecies} onSelect={handleSpecies} accent={accent} />}
        {ctrl.currentStep === 'species' && speciesPick && (
          <FCSkillPicker skills={speciesPick.pool} skillNames={gameData ? gameData.charbuild.skillNames : {}} skillSheetKeys={gameData ? gameData.charbuild.skillSheetKeys : {}} selected={speciesPickSel} max={speciesPick.grant.count} accent={accent} otherSelected={[]} currentRanks={qbCommittedRanks} rankCap={speciesPick.grant.rank_cap != null ? speciesPick.grant.rank_cap : 2} skillCaps={(ctrl.character && ctrl.character.speciesSkillCaps) || {}} onToggle={handleSpeciesPickToggle} onConfirm={handleSpeciesPickConfirm} />
        )}
        {ctrl.currentStep === 'background' && <QBBackgroundStep gameLine={ctrl.gameLine} mechData={gameData.narrativeMechanics} selected={ctrl.bgKey} onSelect={handleBackground} accent={accent} />}
        {ctrl.currentStep === 'loadout' && ctrl.role && <QBLoadoutStep career={ctrl.role.career} gameData={gameData} accent={accent} onSelect={handleLoadout} />}
        {ctrl.currentStep === 'review' && <QBReviewStep character={ctrl.character} gameLine={ctrl.gameLine} gameData={gameData} role={ctrl.role} speciesData={ctrl.selectedSpecies} bgKey={ctrl.bgKey} accent={accent} onNameChange={v => ctrl.updateCharacter('name', v)} onConfirm={handleConfirm} onSetPocketMoney={v => ctrl.updateCharacter('pocketMoney', v)} />}
      </div>
      </CenterColumn>
    </div>
  );

  if (!paper) return legacyBody;

  /* ── GUIDED ON PAPER: THE GROUND, THE SHELL AND THE RUNHEAD (B-719; frames P–Y of lab `284f9929`
     — fork (b), Jason's "B") ──────────────────────────────────────────────────────────────────────
     TWO SHELLS, and which one stands is the app's 1024 seam and nothing else (`useLayoutMode()`, the
     same answer the sheet and the landing take): the creator's one-column PAGE at the desk
     (`hpw-shell` > `hp-sheet hp-wide` > `hpw-bar`) and the board's phone sheet below it
     (`hp-sheet hp-phonesheet gb`). Both are the kit's own shells — `paper.css` carries every rule
     but the three the port adds for the phone's scroller, which is the defect `qsg_fit.py` caught
     (1,437px of the review eaten with no trace in the picture; qsg.css says it in full).

     THE GROUND IS DECLARED TWICE for the reason the landing states and the shelf above repeats.
     THE RUNHEAD'S LEFT CELL IS A BUTTON where the board draws a span — reported both ways, decided
     by the app's own precedent (`FCGearStep` :9616 + paper.css :491's 44px rule).

     WHAT ROW 1 DID NOT BUILD AND B-722 NOW DOES: the pips, the six designations and FIVE of the six
     step bodies. The head below — runhead · pips · rule · slash and door word · designation · lead —
     is drawn for ALL SIX steps, because the pips and the twelve strings are row 4's for every step;
     what is still row 5's (B-723) is the review's BODY, which stands on its interim island under that
     head. That is the island device working as `qsg.css` describes it: each island is the flex child
     its paper zone will be, so its row is a SWAP and not a rebuild.
     The two early returns above — `DataLoadErrorScreen` and the loader — are still NOT wrapped:
     neither is drawn on any frame, and putting a paper shell around a dark error screen would be a
     shape nobody approved. Reported at row 1, unchanged here. */
  // WHICH OF THE SIX STAND ON PAPER, DECLARED — not inferred from whether a component happens to
  // exist, so the island's presence is a READ of this list and `QSG_INTERIM_ZONES` is its complement.
  var _gbStep = ctrl.currentStep;
  // B-750 — `&& !speciesPick` IS GONE. The grant pick is a SUB-VIEW of a step that is on paper, and
  // now the sub-view is too (frames AC/AD/AE), so it no longer falls to an island: `GB_PAPER_STEPS`
  // alone decides, which is what "the island's presence is a READ of a list" was always meant to be.
  var _gbOnPaper = !!GB_PAPER_STEPS[_gbStep];
  // THE ZONE AN ISLAND WOULD CARRY — and after B-750 there is no state of this component that raises
  // one. `species-pick` LEFT THIS EXPRESSION with its drawing (frames AC/AD/AE at lab `18301f1f`,
  // Jason-approved 2026-09-21): the grant picker is `FCSkillPicker` with `paper`, so the last place a
  // dark tree stood inside a lit paper page is gone and `QSG_INTERIM_ZONES` is EMPTY.
  // THE `step-` FALLBACK STAYS, unreachable today and deliberately so: it is what a SEVENTH step would
  // land on. R-QSG-4 forbids one, but a vocabulary that cannot answer for the thing it forbids is the
  // shape that rots silently — and if a step ever arrives without a page, it raises an island whose
  // zone is NOT in the declared vocabulary and the smoke's enumeration says so on the spot. B-726
  // deletes this expression, the attribute and the list together with the flip.
  var _qsgZone = 'step-' + _gbStep;
  var _qsgRunhead = (
    <div className="hp-runhead">
      <button type="button" onClick={handleBack} data-creator-back="1"
        aria-label={ctrl.step === 0 ? ('Back to ' + PLAYER_BAR_COPY.app) : 'Back to the previous step'}
        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
        <span>{'‹  ' + LANDING_DOORS.quickbuild.title}</span>
      </button>
      {/* The folio is the board's, and every number in it is the controller's own: `step` and
          `totalSteps` come from `STEPS`, which R-QSG-4 fixes at six and this row does not touch. */}
      <span className="hp-folio">{'Step ' + (ctrl.step + 1) + ' of ' + ctrl.totalSteps}</span>
    </div>
  );
  var _qsgIsland = (
    <div className="qsg-legacy" data-qsg-interim={_qsgZone}>{legacyBody}</div>
  );
  // ALL SIX PAGES (B-722 the five steps, B-723 the review). Every handler is the SHIPPED one —
  // `handleGameLine`, `handleRole`, `handleSpecies`, `handleBackground`, `handleLoadout`,
  // `handleConfirm` — so the paper face advances the same controller, writes the same
  // `swrpg_creation_draft`, takes the same grant pick and FINALIZES through the same
  // `handleQuickBuildComplete` as the dark one; there is no second path and `STEPS` does not move
  // (R-QSG-4). The loadout guards on `ctrl.role` exactly as the dark body does, so a role-less
  // loadout step draws an empty page on both faces rather than raising an island the vocabulary no
  // longer carries.
  var _gbBody = !_gbOnPaper ? null
    : _gbStep === 'gameline' ? <GBGameLineStep selected={ctrl.gameLine} onSelect={handleGameLine} />
    : _gbStep === 'role' ? <GBRoleStep roles={QB_ROLES[ctrl.gameLine] || []} selected={ctrl.role} onSelect={handleRole} />
    // B-750: THE SPECIES STEP HAS TWO PAPER BODIES and the sub-view is the app's own swap, not a new
    // one — the same `speciesPick &&` condition the dark body has carried since B-14, so the two faces
    // branch on one value. The picker is the SHARED component with `paper`; `speciesPick.pool`,
    // `speciesPickSel`, `qbCommittedRanks` and both handlers are the shipped ones, unchanged, so the
    // grant lands through `SET_FREE_SPECIES_SKILLS` on either face and there is no second path.
    : _gbStep === 'species' ? (speciesPick
      ? <FCSkillPicker paper skills={speciesPick.pool} skillNames={gameData ? gameData.charbuild.skillNames : {}} skillSheetKeys={gameData ? gameData.charbuild.skillSheetKeys : {}} selected={speciesPickSel} max={speciesPick.grant.count} accent={accent} otherSelected={[]} currentRanks={qbCommittedRanks} rankCap={speciesPick.grant.rank_cap != null ? speciesPick.grant.rank_cap : 2} skillCaps={(ctrl.character && ctrl.character.speciesSkillCaps) || {}} onToggle={handleSpeciesPickToggle} onConfirm={handleSpeciesPickConfirm} />
      : <GBSpeciesStep speciesList={speciesList} selected={ctrl.selectedSpecies} onSelect={handleSpecies} />)
    : _gbStep === 'background' ? <GBBackgroundStep gameLine={ctrl.gameLine} mechData={gameData.narrativeMechanics} selected={ctrl.bgKey} onSelect={handleBackground} />
    : _gbStep === 'loadout' ? (ctrl.role ? <GBLoadoutStep career={ctrl.role.career} onSelect={handleLoadout} /> : null)
    : _gbStep === 'review' ? <GBReviewStep character={ctrl.character} gameLine={ctrl.gameLine} gameData={gameData}
        speciesData={ctrl.selectedSpecies} bgKey={ctrl.bgKey}
        onNameChange={function (v) { ctrl.updateCharacter('name', v); }}
        onSetPocketMoney={function (v) { ctrl.updateCharacter('pocketMoney', v); }} />
    : null;
  // THE ONE ACT, AND IT STANDS ON THE LAST STEP ALONE — `commit=None` on frames P through U and the
  // app's own `BEGIN YOUR STORY` on V and W. It is the shipped `handleConfirm`, which is
  // `onComplete(character, gameLine, gameData, selectedSpecies)`: the same finalize the dark review's
  // button calls, so there is one path out of Guided and not a second. `.hp-phoneact` is
  // `position:sticky;bottom:0`, which is how the phone frame's act holds the foot however long the
  // review runs; the desk's `.hp-onecol-act` sits under the column's own scroller.
  /* B-750 — AND THE GRANT PICK'S ACT, WHICH IS THE SECOND AND LAST ONE. Frame AE draws it in the
     page's own act row rather than inside the picker, so the HOST places it; the word is the picker's
     own (`FC_PICK_CONFIRM`, one home) and the handler is the shipped `handleSpeciesPickConfirm`.
     IT APPEARS ONLY WHEN THE PICK IS COMPLETE, because that is what the app does — `FCBottomAction`
     receives `revealed={count === max}` on the dark face, so 'SELECT n MORE' is authored and never
     read. The frames DRAW that (no act bar on AC/AD, the act on AE) and NAME the clash with
     `holocron.css` §14's refusal law; fixing it is B-756 item (1)'s, an APP question on its own row,
     and building the fix here would be shipping an unapproved behaviour.
     THE COUNT IS THE PICKER'S OWN B-18 COUNT — selections filtered to what is actually OFFERED — so a
     stale id from a prior subject cannot open this bar any more than it can open the dark one. */
  var _gbPickMax = speciesPick ? speciesPick.grant.count : 0;
  var _gbPickCount = speciesPick
    ? (speciesPickSel || []).filter(function (id) { return (speciesPick.pool || []).indexOf(id) >= 0; }).length
    : 0;
  var _gbAct = (_gbOnPaper && _gbStep === 'review') ? (
    <button type="button" className="hp-commit" data-gb-act="1" onClick={handleConfirm}>
      {QB_BEGIN_LABEL} <span>{'›'}</span></button>
  ) : (_gbOnPaper && speciesPick && _gbPickCount === _gbPickMax) ? (
    <button type="button" className="hp-commit" data-gb-act="1" data-gb-pick-act="1"
      onClick={handleSpeciesPickConfirm}>
      {FC_PICK_CONFIRM} <span>{'›'}</span></button>
  ) : null;
  var _gbPips = <GBSteps now={ctrl.step + 1} total={ctrl.totalSteps} />;
  // THE DESIGNATION AND THE LEAD ARE `stepTitles`, WHICH IS THE WHOLE OF WHAT FORK (b) COSTS IN COPY:
  // nothing. `st` is the same object the dark header prints — the twelve authored strings, including
  // the background step's per-line ternary and B-14's grant-pick title while a pick is in flight.
  var _gbDesig = (
    <React.Fragment>
      <h1 className="hp-desig" style={mode === 'desktop' ? undefined : { fontSize: '25px' }}>{st.title}</h1>
      <div className="hp-desig-rule" />
    </React.Fragment>
  );
  // UXQP-17 KEPT (Jason's D-PV2 (a)): on the background step the mechanic's framing sentence joins the lead, after the
  // line's own short subtitle. Not while the species grant pick holds the title (that is B-14's sub-view).
  var _gbFraming = (ctrl.currentStep === 'background' && !_speciesPickTitle) ? QB_MECH_FRAMING[mechanicForLine(ctrl.gameLine)] : null;
  var _gbLead = (st.subtitle || _gbFraming) ? (
    <p className="hp-lead" style={{ margin: 0 }}>{[st.subtitle, _gbFraming].filter(Boolean).join(' ')}</p>
  ) : null;
  return (
    // B-750: `data-qsg-view` CARRIES THE ONE NAME THE LEDGER IS LOSING. `data-qsg-step` stays
    // `species` while the grant pick is in flight, because `ctrl.currentStep` does — the pick is a
    // sub-view and the folio and pips do not move. The zone attribute used to be what named it; with
    // the island gone, this says it instead, in the spelling the lab addresses the drawing by.
    <div className="qsg-surface" data-doc-ground="creator" data-qsg-shell="1"
         data-band={mode} data-qsg-face="guided" data-qsg-step={_gbStep}
         data-qsg-view={speciesPick ? 'species-pick' : undefined}>
      {mode === 'desktop' ? (
        <div className="hpw-shell">
          <div className="hp-sheet hp-wide" data-doc-ground="creator">
            <div className="hpw-bar">{_qsgRunhead}</div>
            {/* THE DESK PAGE IS THE CREATOR'S ONE COLUMN, the pin's `gb_desk` class for class: the
                pips, then the head (slash · door word and folio · designation · rule) beside the lead
                plate, then the body as the column's own scroller. NO ACT ROW ON THE FIRST FIVE —
                `commit=None` on frames P through U, because the card's press IS the act — and ONE on
                the review, which is the app's own button (frame W). The lead plate's cap is the app's
                own token where the pin writes `--pp-lead-max`, which `qsg.css`'s header already says
                is the one lab name the port needs no copy of. */}
            <div className="hp-onecol">
              {_gbPips}
              <div className="hp-onecol-head">
                <div className="hp-onecol-title">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <span className="hp-slash"><i /><i /><i /></span>
                    <div className="hp-maker">{LANDING_DOORS.quickbuild.title
                      + ' · Step ' + (ctrl.step + 1) + ' of ' + ctrl.totalSteps}</div>
                  </div>
                  {_gbDesig}
                </div>
                {_gbLead ? <div className="hp-plate" data-pad="read"
                  style={{ maxWidth: 'calc(var(--doc-lead-max, 640px) + 38px)' }}>{_gbLead}</div> : null}
              </div>
              {_gbBody ? <div className="gb-scroll">{_gbBody}</div> : _qsgIsland}
              {_gbAct ? <div className="hp-onecol-act">{_gbAct}</div> : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="hp-sheet hp-phonesheet gb" data-doc-ground="creator">
          {/* THE PHONE PAGE, the pin's `gb_phone`: the head and the body are ONE scroller
              (`hp-phonebody`), which is what `qsg.css`'s `data-qsg-head` note predicted this row would
              undo — the mark comes off wherever the body is paper, and stays only over an island,
              where the thing that scrolls is the island and the head holds still. */}
          <div className="hp-phonebody" data-qsg-head={_gbBody ? undefined : '1'}>
            {_qsgRunhead}
            {_gbPips}
            <hr className="hp-hr" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
              <span className="hp-slash"><i /><i /><i /></span>
              <div className="hp-maker">{LANDING_DOORS.quickbuild.title}</div>
            </div>
            {_gbDesig}
            {_gbLead ? <div className="hp-plate" data-pad="read" style={{ marginTop: '12px' }}>{_gbLead}</div> : null}
            {_gbBody}
          </div>
          {_gbBody ? null : _qsgIsland}
          {_gbAct ? <div className="hp-phoneact">{_gbAct}</div> : null}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ROSTER PERSISTENCE (localStorage)
══════════════════════════════════════════════════════ */
// SHIP CARGO Part B (ruling `shipyard-cargo-build-gm-seam-census` R-SCGS-1): the roster's storage, envelope, F-54 hold,
// tombstones and record write live in lib/shared/roster.js (window.SWRPGRoster), lifted whole and verbatim so the GM
// writes a character through the same code. These are its names here; every call site below is unchanged.
const ROSTER_STORAGE_KEY = window.SWRPGRoster.ROSTER_STORAGE_KEY;
const loadSavedCharacters = window.SWRPGRoster.loadSavedCharacters;
const writeSavedCharacters = window.SWRPGRoster.writeSavedCharacters;
const upsertSavedCharacter = window.SWRPGRoster.upsertSavedCharacter;
const removeSavedCharacter = window.SWRPGRoster.removeSavedCharacter;
const openedPairOf = window.SWRPGRoster.openedPairOf;
const findCharById = window.SWRPGRoster.findCharById;
const loadDeletedIds = window.SWRPGRoster.loadDeletedIds;

// One-time pre-3b-migration snapshot of the roster (ruling charbuild-3b-recon D-3B4
// condition 2). Written once, before the first id-ref migration write-back; never updated.
const ROSTER_BACKUP_KEY = 'swrpg_saved_characters_pre3b';

/* THE ROSTER MIGRATION, named once (B-977, ruling `release-r7-bug-burndown` ADDENDUM 81, R-Y2-1 on paper there). charbuild 3b:
   once the charbuild registries land, legacy-shape saved characters become canonical id-refs (D-3B4: collision-aware name
   matching; ambiguous/unmatched entries stay as marked legacy display entries, never guessed, never dropped). Before the
   FIRST rewrite, the whole roster is snapshotted to a one-time backup key (ruling condition 2) so a wrong match is
   recoverable. Called at mount AND when a record lands (sync's adopted/rechecked/resolved, the roster `storage` event):
   it used to run at mount only, so another device's older-shape record stayed legacy until the next load, and its talents
   gave the pool no dice.
   · WARM OR WAIT: it runs on the charbuild's own promise, so a landing before the build is warm migrates when it warms,
     and an unavailable build leaves storage untouched. It never runs against an absent build.
   · A NO-OP WRITES NOTHING: when no record changed, nothing is written and no stamp moves. Otherwise one tab's write
     re-fires every other tab's landing, and two devices re-stamp each other forever.
   · No new field, key or stamp: `migrateRoster` stamps `updatedAt` on the records it changed (C-B-8.1), and the sweep
     pushes them as it pushes any write.
   Resolves to the written list, or null when nothing was written. */
function migrateStoredRoster() {
  return loadCanonicalCharacterBuild().then(function(cb) {
    if (!cb) return null;   // registries unavailable — leave saves untouched
    var res = CharResolve.migrateRoster(loadSavedCharacters(), cb);
    if (!res.changed) return null;
    try {
      if (!localStorage.getItem(ROSTER_BACKUP_KEY)) {
        var raw = localStorage.getItem(ROSTER_STORAGE_KEY);
        if (raw) localStorage.setItem(ROSTER_BACKUP_KEY, raw);
      }
    } catch (e) { return null; }   // can't back up -> don't rewrite
    writeSavedCharacters(res.value);
    return res.value;
  }, function() { return null; });
}

// ── Creation draft (UXQP-6) ──────────────────────────────────────────
// One in-progress build at a time. The creators write a draft on every state
// change once a game line is chosen; the landing offers CONTINUE WHERE YOU
// LEFT OFF while one exists. Cleared on completion or explicit discard —
// backing out of a creator deliberately KEEPS the draft (that's the resume
// case). Starting a fresh build overwrites it at the next game-line pick.
const CREATION_DRAFT_KEY = 'swrpg_creation_draft';

/* ── THE CREATION-DRAFT STEP CONTRACT (B-72, ruling `b72-draft-step-contract`) ──────────
   The draft persisted `step` as a RAW FC_STEPS INDEX and never read the `v` field it wrote itself,
   so ANY insertion or reorder silently resumed every in-flight draft on the wrong step. Inserting
   `specialization` at index 3 would have moved a draft saved on `skills` (5) to `characteristics`.
   No error, no signal, every player's localStorage.

   THE FIX IS BY NAME, not by a version bump that discards drafts (rejected outright): `v: 2` writes
   `step: 'skills'`, and a v1 draft migrates its integer through the FROZEN LITERAL below.

   ⚠ THE FROZEN LITERAL IS THE WHOLE POINT. It records the v1-era order as it was, forever. A
   migration that read the LIVE `FC_STEPS` would be this exact bug reborn inside its own fix — the
   array it consults is the array that changed. Never replace this with a reference to FC_STEPS. */
var FC_STEPS_V1 = ['gameline', 'species', 'career', 'mechanic', 'characteristics', 'skills',
                   'talents', 'forcepowers', 'motivation', 'gear', 'review'];

/* Resolve a persisted draft's step to a NAME, whatever version wrote it.
   · v2 → the name, verbatim.
   · v1 / missing → the integer, mapped through the frozen v1 order.
   · anything unresolvable → 'review' (F-45's landing pattern: the draft survives and the player
     lands somewhere true, never at a silently wrong position). */
function creationDraftStepName(d) {
  if (!d) return 'review';
  if (Number(d.v) >= 2) {
    return (typeof d.step === 'string' && d.step) ? d.step : 'review';
  }
  // `typeof` first, deliberately: `Number(null)` is 0, so a v1 draft with a null step would
  // otherwise resolve to FC_STEPS_V1[0] ('gameline') while `undefined` resolved to review — two
  // different answers for the same "no usable step". Caught by this contract's own guard.
  var i = d.step;
  return (typeof i === 'number' && Number.isInteger(i) && i >= 0 && i < FC_STEPS_V1.length)
    ? FC_STEPS_V1[i] : 'review';
}

/* A step NAME → the live index, for the controller. An unknown name (a step this build no longer
   has, or one a newer build added) lands on review rather than guessing a position. */
function creationDraftStepIndex(d, steps) {
  var name = creationDraftStepName(d);
  var i = steps.indexOf(name);
  return i >= 0 ? i : steps.indexOf('review');
}

function loadCreationDraft() {
  try {
    var raw = localStorage.getItem(CREATION_DRAFT_KEY);
    if (!raw) return null;
    var d = JSON.parse(raw);
    // Minimum viable draft: a known flow, a game line, and a character object.
    if (!d || (d.flow !== 'custom' && d.flow !== 'quickbuild')) return null;
    if (!d.gameLine || !d.character) return null;
    // B-72: READ THE VERSION FIELD. It was written from day one and never consulted — the guard
    // existed and nothing looked at it. Normalised here so every consumer sees a NAME.
    // The step VOCABULARY is the custom creator's. QuickBuild persists its own `step` over its own
    // STEPS array (:13057) — a different space entirely — so resolving it through FC_STEPS_V1 would
    // produce a confident, meaningless name. Only the custom flow gets a stepName.
    if (d.flow === 'custom') d.stepName = creationDraftStepName(d);
    // B-973: a finished build that came back is not a draft (see `creationIdTaken`) — never offered, never resumed.
    // Except the F-45 door's draft from before the fix, whose character id IS the character it re-enters.
    var _rx = d.reentry && d.reentry.fromCharacterId;
    if (!(_rx && _rx === d.character.id) && creationIdTaken(d.character.id)) return null;
    return d;
  } catch (e) { return null; }
}

function saveCreationDraft(draft) {
  try { localStorage.setItem(CREATION_DRAFT_KEY, JSON.stringify(draft)); } catch (e) {}
}

function clearCreationDraft() {
  try { localStorage.removeItem(CREATION_DRAFT_KEY); } catch (e) {}
}

/* ── B-973 · A FINISHED BUILD IS NOT A DRAFT, ON ANY DEVICE (card `data-loss-pair-card` C-1) ──────────────────────
   `clearCreationDraft` removes the draft only HERE. Sync never sends a removed key (sync.js `sweep`/`push` return on a
   null value), so the account keeps the last draft and the next adoption writes it back (syncMerge.js: a WHOLE key
   absent locally takes the account's). The landing then offered CONTINUE, and finishing it again replaced the
   character (Guided keeps the draft's id) or duplicated it (Custom took a new one).
   The cure is READ-SIDE, and rests on one premise: BOTH finishes save the character under the draft's own character
   id (Guided always has; Custom and the F-45 door since this fix). So a draft whose character id is already a
   character here — on the roster, or deleted from it (`swrpg_deleted_ids`) — is a finished build come back, never
   work in progress. Nothing about the draft, the record or what sync sends or merges changes (R-Y2-1).
   A draft written before this fix by the Custom creator or the F-45 door carries no link to the character it became
   (both finished under `Date.now()`), so it still reads as a draft, as it did. The door's old draft carried the
   SHEET's id (`sheetToCreatorState`), which on a pregen is the very record it re-enters: a draft whose id is its own
   `reentry.fromCharacterId` is that old form, and `loadCreationDraft` keeps offering it. */
function creationIdTaken(id) {
  if (!id) return false;
  if (loadSavedCharacters().some(function(c) { return c && c.id === id; })) return true;
  return loadDeletedIds().indexOf(id) >= 0;
}

function newCreationId() {   // `createEmptyCharacter`'s form (player/index.html)
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
}

// The finish's half: the id a finished build is saved under — the draft's own character id, unless that id is already
// a character here; then the build becomes a character of its own and the saved one is never replaced (a draft resumed
// on one device while another finished it, and adopted since). Remembered per draft id for this page, so a second
// press of the same finish writes the same record again rather than a copy.
var _creationFinishIds = {};
function creationFinishId(draftId) {
  if (draftId && _creationFinishIds[draftId]) return _creationFinishIds[draftId];
  var id = (draftId && !creationIdTaken(draftId)) ? draftId : newCreationId();
  if (draftId) _creationFinishIds[draftId] = id;
  return id;
}

/* NOTES — the per-character session pad (rail Addendum 1). At the table a note is written mid-round,
   so anything slower than "tap, type" fails the moment: no structure, no ceremony, no SAVE button.
   PERSISTENCE: typing reaches the saved character without an explicit save. That used to be a
   PRIVILEGE — `notes` was one of a handful of keys on a write-through whitelist — and since
   C-GFB-5 (autosave) it is simply how the sheet behaves, so there is no longer a class to join.
   The DEBOUNCE survives the whitelist's retirement and is unrelated to it: it exists so typing is
   not a storage write per keystroke. The local mirror keeps the caret steady while it is in flight.
   This is NOT the ephemeral class: a module switch or a table-mode change must never lose text.

   ONE COMPONENT, TWO DOORS (ruling table-feedback-crits-notes-mods §2 — Jason's table report). The
   rail is >=1367-only, and his report came FROM a table, on a phone. So this same pad also mounts
   as SESSION NOTES on the PROFILE tab, at every band. Same field, same write-through, one
   definition — never a second pad that could drift or double-write. `variant` only changes the
   chrome around it. */
function NotesPad({ notes, onNotesChange }) {
  const [draft, setDraft] = React.useState(notes || '');
  const timer = React.useRef(null);
  const lastPropped = React.useRef(notes || '');
  // The LATEST typed value, in a ref. The unmount flush must read the current text, and a plain
  // closure would capture whichever render it was created in.
  const draftRef = React.useRef(notes || '');
  const onChangeRef = React.useRef(onNotesChange);
  React.useEffect(() => { onChangeRef.current = onNotesChange; }, [onNotesChange]);

  // Adopt an EXTERNAL change (character switch, load) without clobbering in-flight typing.
  React.useEffect(() => {
    const incoming = notes || '';
    if (incoming !== lastPropped.current) {
      lastPropped.current = incoming;
      draftRef.current = incoming;
      setDraft(incoming);
    }
  }, [notes]);

  const onType = (v) => {
    setDraft(v);
    draftRef.current = v;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      lastPropped.current = v;
      onChangeRef.current(v);
    }, 400);
  };

  // Flush the pending edit. Reads the REFS, never the closure — an earlier version of this ran as a
  // `useEffect(() => flush, [draft])` cleanup, which fires on the NEXT change carrying the PREVIOUS
  // draft: every save landed one edit stale and overwrote the newer text. Caught by the
  // write-through assert; the bug is why the refs are here.
  const flush = React.useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (draftRef.current !== lastPropped.current) {
      lastPropped.current = draftRef.current;
      onChangeRef.current(draftRef.current);
    }
  }, []);

  // Unmount ONLY (empty deps) — a module switch mid-type must not drop the tail.
  React.useEffect(() => flush, [flush]);

  // B-639 · THE PAD ON PAPER — the same draft, debounce and flush; the kit's area field, no chrome (the fold's head is the label)
  return (
    <div data-testid="profile-notes" className="sb-notes">
      <textarea className="sb-filter sb-fld-area" data-testid="rail-notes-pad" rows={6} value={draft} onChange={(e) => onType(e.target.value)} onBlur={flush}
        placeholder="What happened this session…" aria-label="Session notes" />
    </div>
  );
}

/* JOURNAL — F-8 (ruling reddit-cluster-f7-f8-f9s3 §3). "We have a journal in the character sheet,
   would just like to be able to save multiple over time." (Jason.)

   THE PAD ABOVE IS NOT THIS. `sheet.notes` is one string, write-through, "what's happening now" —
   untouched by this component and by the field it writes. The journal is a SEPARATE field,
   `sheet.journal`, a dated append-only log. Nothing here reads or writes `notes`; the ruling chose
   a new field precisely so no bug in this feature can reach text a person already wrote.

   RULED SHAPE + BEHAVIOUR (all of it decided on paper, none of it invented here):
     · dated, untitled, append-only, newest-first — the date and the first line are the identity
     · DELETE yes, EDIT no — you may remove what you regret; you may not rewrite the past
     · SAVE IS EXPLICIT — the pad's debounce is right for scratch and wrong for a log, so an entry
       exists only after SAVE ENTRY. Nothing in this component writes on keystroke.
     · no sessions, no titles (both refused; sessions belong to the campaign container, W-E1)

   ONE COMPONENT, TWO DOORS — the same law NotesPad answers to (no-compromise-mirror, R-PL1, and
   the ruling binds it at the gate): this mounts on PROFILE at every band AND in the >=1367 rail's
   NOTES module. `variant` changes the chrome and the scroll owner, never the behaviour. A
   desktop-only or mobile-only journal would be exactly the compromise that law forbids.

   The transforms live in lib/shared/journal.js (window.SWRPGJournal) so they are node-testable
   (test/journal_store.cjs) and so append/delete/normalize have ONE definition rather than one per
   mount. If that script is absent the component renders nothing rather than throwing — a missing
   module must not take the whole sheet down with it; the smoke is what notices. */
function JournalLog({ journal, onJournalChange }) {
  var J = (typeof window !== 'undefined' && window.SWRPGJournal) || null;
  var _d = React.useState(''), draft = _d[0], setDraft = _d[1];
  // Two-tap delete confirm — the app's standing idiom (roster rows, sheet deletes). Delete is a
  // REAL removal (no tombstone the user cannot see), so it gets the same second beat everything
  // destructive gets here.
  var _a = React.useState(null), armedId = _a[0], setArmedId = _a[1];
  React.useEffect(function () {
    if (!armedId) return;
    var t = setTimeout(function () { setArmedId(null); }, 3000);
    return function () { clearTimeout(t); };
  }, [armedId]);

  if (!J) return null;
  var entries = J.normalize(journal);
  var canSave = !J.isBlank(draft);

  var save = function () {
    if (!canSave) return;
    var next = J.append(journal, draft);
    setDraft('');
    setArmedId(null);
    if (onJournalChange) onJournalChange(next);
  };
  var del = function (id) {
    if (armedId !== id) { setArmedId(id); return; }
    setArmedId(null);
    if (onJournalChange) onJournalChange(J.remove(journal, id));
  };

  // B-639 · THE JOURNAL ON PAPER — the same append-only log (SWRPGJournal), explicit SAVE, two-tap delete; entries as the kit's rows
  return (
    <div data-testid="profile-journal" className="sb-journal">
      <textarea className="sb-filter sb-fld-area sb-journal-draft" data-testid="journal-draft" rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write an entry…" aria-label="New journal entry" />
      <div className="sb-pk-verbs" style={{ marginTop: 8 }}><button type="button" className={canSave ? 'hp-commit' : 'hp-quiet'} data-testid="journal-save" disabled={!canSave} onClick={save}>Save entry</button></div>
      <div data-testid="journal-list" className="sb-journal-list">
        {entries.length === 0
          ? <p className="sb-e-t" data-testid="journal-empty">No saved entries yet. Write one above and press SAVE ENTRY.</p>
          : entries.map(function (e) {
              var armed = armedId === e.id;
              return (
                <div key={e.id} className="sb-att" data-testid="journal-entry" data-entry-id={e.id}>
                  <div className="sb-e-h"><span className="sb-e-m" data-testid="journal-entry-date">{J.formatDate(e.at)}</span>
                    <button type="button" className={armed ? 'sb-confirm' : 'hp-quiet'} data-testid="journal-delete" aria-label={armed ? 'Tap again to delete this entry' : 'Delete this entry'} onClick={function () { del(e.id); }}>{armed ? 'Tap again' : 'Delete'}</button></div>
                  <p className="sb-e-t" data-testid="journal-entry-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.text}</p>
                </div>);
            })}
      </div>
    </div>
  );
}

// B-405 — THE SHEET'S BOUNDARY (ruling `landing-front-matter-concept` R-LFM-4; `landing-revision-prep`
// card 3). A malformed save on the auto-route used to throw inside CharacterSheet with nothing to
// catch it, blanking the app on every visit until storage was cleared. The boundary catches the
// render, tells the shell (which falls back to the roster with one status line in mechanics voice),
// and resets when a different character is opened — its `key` is the character's id.
class SheetBoundary extends React.Component {
  constructor(p) { super(p); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) {
    try { console.error('[sheet boundary] the sheet could not render this record', err); } catch (e) {}
    if (this.props.onFail) this.props.onFail(err);
  }
  render() { return this.state.failed ? null : this.props.children; }
}

/* ══════════════════════════════════════════════════
   THE STORE (ITM-V1) — the SessionStore component was REPLACED by the shared
   store kernel (lib/store/view.jsx → window.SWRPGStore.Store), hosted in the
   PlayerApp render switch below. Deleted with it (ITM-A subtraction + ITM-B §4.3
   player-side channel retirement, discharged here because the surface that
   held the 3 read sites is gone):
     - the per-item instant-buy path + its credit BLOCK toast (cart replaces it;
       warn-never-block R-ITM1 lives at FINALIZE in the kernel)
     - SESSION_STORE_CONFIG_KEY / loadSessionStoreConfig / the storage-event
       listener — the GM→player same-device config channel (the GM-side WRITE
       stays; it feeds the GM's own screen)
══════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════
   TEST-BED GENERATOR SCREEN — dev-only, runs SWRPGTestBed
   and downloads 12 PC JSONs + a manifest.
   Accessed via /player?testbed (or legacy /player.html?testbed which 301s)
══════════════════════════════════════════════════════ */
function TestBedScreen() {
  const [gameData, setGameData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  useEffect(function() {
    Promise.all([loadQuickBuildData(), loadCanonicalRegistries(), loadCanonicalCharacterBuild()])
      .then(function(res) {
        if (!res[1] || !res[2]) { setError('Failed to load game data: canonical registries unavailable'); return; }
        setGameData({ charbuild: res[2], narrativeMechanics: res[0].narrativeMechanics, canonical: res[1] });
      })
      .catch(function(e) { setError('Failed to load game data: ' + e.message); });
  }, []);

  function triggerDownload(name, contents, mime) {
    const blob = new Blob([contents], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleGenerateAll() {
    if (!gameData) return;
    setStatus('generating');
    setError(null);
    try {
      const out = window.SWRPGTestBed.generateAll(gameData);
      const report = out.map(function(r) {
        if (r.error) return { id: r.spec.id, error: r.error };
        return {
          id: r.spec.id,
          name: r.result.sheet.name,
          species: r.result.sheet.species,
          career: r.result.sheet.career,
          tier: r.spec.tier,
          xp_total: r.result.sheet.xp.total,
          xp_remaining: r.result.sheet.xp.remaining,
        };
      });
      setResults(report);

      // Bundle everything into ONE JSON file to avoid browser multi-download
      // rate limits. Consumer scripts split this into individual PC files.
      const bundle = {
        schema: 'swrpg-test-bed-bundle',
        schema_version: 1,
        generated_at: new Date().toISOString(),
        count: out.filter(function(r) { return !r.error; }).length,
        errors: out.filter(function(r) { return r.error; }).map(function(r) { return { id: r.spec.id, error: r.error }; }),
        pcs: out.filter(function(r) { return !r.error; }).map(function(r) {
          return {
            id: r.spec.id,
            tier: r.spec.tier,
            name: r.result.sheet.name,
            species: r.result.sheet.species,
            career: r.result.sheet.career,
            file: r.spec.id + '_' + r.result.sheet.name.replace(/[^a-zA-Z0-9]/g, '_') + '.json',
            payload: {
              schema: 'swrpg-character-sheet',
              schema_version: 1,
              test_bed: { id: r.spec.id, tier: r.spec.tier },
              exported_at: new Date().toISOString(),
              sheet: r.result.sheet,
              character: r.result.character,
            },
          };
        }),
      };
      triggerDownload('swrpg_test_bed_bundle.json', JSON.stringify(bundle, null, 2), 'application/json');
      setStatus('done');
    } catch (e) {
      setError(String(e));
      setStatus('error');
    }
  }

  function handleDownloadSingle(specId) {
    if (!gameData) return;
    setError(null);
    try {
      const r = window.SWRPGTestBed.generateOne(specId, gameData);
      const baseName = specId + '_' + r.sheet.name.replace(/[^a-zA-Z0-9]/g, '_');
      const payload = {
        schema: 'swrpg-character-sheet',
        schema_version: 1,
        test_bed: { id: specId, tier: window.SWRPGTestBed.specs.find(function(s) { return s.id === specId; }).tier },
        exported_at: new Date().toISOString(),
        sheet: r.sheet,
        character: r.character,
      };
      triggerDownload(baseName + '.json', JSON.stringify(payload, null, 2), 'application/json');
    } catch (e) {
      // B-1594: the failure is drawn in this screen's own error line (GENERATE ALL's), never a browser dialog.
      setError('Generate failed: ' + e.message);
    }
  }

  const tierColors = { beginner: '#4ECB71', mid: '#FFC857', late: '#FF5555' };
  const specs = (window.SWRPGTestBed && window.SWRPGTestBed.specs) || [];

  return React.createElement('div', {
    style: {
      position: 'absolute', inset: 0, background: '#050c18', color: '#fff',
      fontFamily: 'var(--font-body)', overflow: 'auto', padding: '24px 20px',
    }
  },
    React.createElement('div', { style: { maxWidth: 720, margin: '0 auto' } },
      React.createElement('div', { style: { fontFamily: 'var(--font-display)', fontSize: 'var(--fs-lg)', letterSpacing: '0.2em', color: '#F5C842', marginBottom: 8 } }, 'TEST BED GENERATOR'),
      React.createElement('div', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', marginBottom: 24 } }, 'DEV-ONLY · /player?testbed'),
      error && React.createElement('div', { style: { padding: 12, background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.4)', borderRadius: 6, marginBottom: 16, fontSize: 'var(--fs-sm)', color: '#FF8A8A' } }, error),
      !gameData && !error && React.createElement('div', { style: { padding: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 6, fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.5)' } }, 'Loading game data…'),
      gameData && React.createElement('div', null,
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 24 } },
          React.createElement('button', {
            onClick: handleGenerateAll,
            disabled: status === 'generating',
            style: {
              flex: 1, padding: '14px', background: 'linear-gradient(135deg, #F5C84222, #F5C84208)',
              border: '1px solid #F5C84266', borderRadius: 8, cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-sm)', letterSpacing: '0.14em',
              color: '#F5C842', textShadow: '0 0 10px #F5C84288',
            }
          }, status === 'generating' ? 'GENERATING…' : 'GENERATE ALL 12 (BUNDLE) ▶'),
        ),
        React.createElement('div', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 } }, 'INDIVIDUAL PCS'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 24 } },
          specs.map(function(s) {
            return React.createElement('button', {
              key: s.id,
              onClick: function() { handleDownloadSingle(s.id); },
              style: {
                padding: '10px 12px', textAlign: 'left',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid ' + (tierColors[s.tier] || '#666') + '33',
                borderLeft: '3px solid ' + (tierColors[s.tier] || '#666'),
                borderRadius: 4, cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: '#fff',
              }
            },
              React.createElement('div', { style: { fontWeight: 700 } }, s.id + ' · ' + s.name),
              React.createElement('div', { style: { fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.5)', marginTop: 2 } }, s.tier + ' · ' + s.speciesKey + ' · ' + s.careerName),
            );
          })
        ),
        results.length > 0 && React.createElement('div', null,
          React.createElement('div', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', letterSpacing: '0.18em', color: 'var(--text-success)', marginBottom: 8 } }, '✓ LAST RUN (' + results.length + ' files)'),
          React.createElement('div', { style: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 } },
            results.map(function(r) {
              return React.createElement('div', { key: r.id },
                r.error ? (r.id + ': ERROR — ' + r.error) :
                (r.id + ' [' + r.tier + '] ' + r.name + ' · ' + r.species + ' ' + r.career + ' · ' + r.xp_remaining + '/' + r.xp_total + ' XP')
              );
            })
          )
        )
      )
    )
  );
}

/* ══════════════════════════════════════════════════════
   PROFILE FORK (Tutorial Layer 0)
   Single profile written on first launch, then a welcome
   fork (BEGIN TOUR / EXPLORE ON MY OWN). BEGIN TOUR opts into
   the in-context coach marks; the deep teaching is the Full
   Creator walkthrough (Docs/TUTORIAL_DIRECTION.md). The old
   standalone OVERVIEW slideshow was retired 2026-06-15.
══════════════════════════════════════════════════════ */
const PROFILE_STORAGE_KEY = 'swrpg_profile_v1';

function readProfile() {
  try {
    var raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return null;
    var p = JSON.parse(raw);
    return p;
  } catch (e) { return null; }
}
function writeProfile(p) {
  try { localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(p)); } catch (e) {}
}
// (WelcomeModal + TutorialHub REMOVED — nav Phase 4. The launchpad is the front door; the in-context
//  creation coach marks stay; the Ko-fi support link moved to the launchpad footer.)

function pathForScreen(screenId, opts) {
  var charId = opts && opts.charId;
  switch (screenId) {
    case 'landing':       return '/player';
    case 'pregen':        return '/player/pregen';
    case 'quickbuild':    return '/player/quickbuild';
    case 'custom':        return '/player/custom';
    case 'sheet':         return charId ? '/player/sheet/' + charId : '/player/sheet';
    case 'store':         return charId ? '/player/sheet/' + charId + '/store' : '/player/store';
    case 'yard':          return (opts && opts.vehicleKey) ? '/player/yard/' + opts.vehicleKey : '/player/yard';  // R-Y2-2 front door
    case 'npc':           return '/player/npc' + ((opts && opts.npcPath) ? '/' + opts.npcPath : '');   // B-863
    case 'import':        return '/player/import';   // OGG-1 import surface
    case 'backup':        return '/player/backup';   // RELEASE R3 (D-258): the whole-save backup
    case 'homebrew':      return '/player/homebrew';   // F-100 C2 (R-FIEC-5); reached only while swrpg_homebrew_editor is on
    default:              return '/player';
  }
}

// Parse a pathname into { screen, charId }. Returns { screen: null }
// for unknown paths so the caller can decide on a fallback. Both the
// cold-load initializer and the popstate handler use this.
function parsePathToScreen(path) {
  // Strip trailing slash for canonical comparison.
  var p = (path || '').replace(/\/$/, '');
  if (p === '' || p === '/' || p === '/player' || p === '/player.html') {
    return { screen: 'landing' };
  }
  // Match /player/sheet/[id]/store
  var storeMatch = p.match(/^\/player\/sheet\/([^/]+)\/store$/);
  if (storeMatch) return { screen: 'store', charId: storeMatch[1] };
  // Match /player/sheet/[id]
  var sheetMatch = p.match(/^\/player\/sheet\/([^/]+)$/);
  if (sheetMatch) return { screen: 'sheet', charId: sheetMatch[1] };
  // B-863 · R-NPC-4: /player/npc/<tail> — the library's position; the module's grammar reads the tail
  // (and REFUSES `encounters`/`custom` here: the player declares neither capability).
  var npcMatch = p.match(/^\/player\/npc\/(.+)$/);
  if (npcMatch) return { screen: 'npc', npcPath: npcMatch[1] };
  // Match /player/yard/[vehicleKey] (R-Y2-2 front door — the entry page)
  var yardMatch = p.match(/^\/player\/yard\/([^/]+)$/);
  if (yardMatch) return { screen: 'yard', vehicleKey: decodeURIComponent(yardMatch[1]) };
  // Single-segment routes
  switch (p) {
    // B-650 · R-LFM-6: the roster's old URL opens the landing on its INDEX (the roster). The URL is
    // not rewritten, so a bookmark on it stays valid and back/forward through it keep working.
    case '/player/roster':       return { screen: 'landing', zone: 'index' };
    case '/player/pregen':       return { screen: 'pregen' };
    case '/player/quickbuild':   return { screen: 'quickbuild' };
    case '/player/custom':       return { screen: 'custom' };
    case '/player/npc':          return { screen: 'npc' };
    case '/player/import':       return { screen: 'import' };  // OGG-1 import surface
    case '/player/backup':       return { screen: 'backup' };  // RELEASE R3 (D-258): the whole-save backup
    case '/player/store':        return { screen: 'store' };   // ITM-V1 top-level store
    case '/player/yard':         return { screen: 'yard' };    // R-Y2-2 the player Ship Yard (front door)
    // /player/dice-sandbox RETIRED (UXQP-10) — falls to the unknown-path
    // default; the dice surface lives at /dice.
    default:                     return { screen: null };
  }
}

/* F-100 C2 · THE HOMEBREW EDITOR'S ROUTES (ruling `f100-items-editor-contracts` R-FIEC-5). The parser above
   stays pure and answers `{screen: null}` for every /player/homebrew path, exactly as before. This wrapper is
   the one place the editor's switch bends a parse: ON, a /player/homebrew path is the `homebrew` screen (the
   editor reads the rest of the path itself, `SWRPGEditor.parse`); OFF, it answers nothing and today's
   unknown-path fallback (the landing) stands. */
function homebrewScreenFor(path) {
  return (window.SWRPGEditor && window.SWRPGEditor.screenFor(path)) ? 'homebrew' : null;
}
/* SHIP YARD S4 · K6'S ROUTES (R-S4G-3). The parser above stays pure. ANY /player/yard path is the `yard` screen and its
   tail is the Yard's to read (`SWRPGVesselView.yardRoute` — hangar · catalogue · catalogue/<key> · ship/<id>[/<tab>] ·
   unit/<id>, a lone <key> the ENTRY's alias, anything else not-found IN PLACE, never the landing). K9 bent this parse
   until the S5 flip retired it (R-S5R-18). */
function yardScreenFor(path) {
  var m = /^\/player\/yard(?:\/(.*))?$/.exec(String(path || '').replace(/\/$/, ''));
  return m ? (m[1] || '') : null;
}

/* ══════════════════════════════════════════════════════
   APP ROOT
══════════════════════════════════════════════════════ */
// Small localStorage-backed toggle for the suite ⚙ dice view-prefs (View Odds / Interpret Roll).
// Keys are shared with HoloDice; the sheet dice modal (diceUI.jsx) reads them on open. (Polish P1
// — player ⚙ parity; Sound is excluded, the flat modal plays no audio.)
function useLocalToggle(key) {
  var _s = useState(function() { try { return window.localStorage.getItem(key) === '1'; } catch (e) { return false; } });
  var on = _s[0], setOn = _s[1];
  var toggle = useCallback(function() {
    setOn(function(v) {
      var next = !v;
      try { window.localStorage.setItem(key, next ? '1' : '0'); } catch (e) {}
      return next;
    });
  }, [key]);
  return { enabled: on, toggle: toggle };
}

// ── Store-bar icon helpers (ITM-V1, ADR 0010) ─────────────────────
// The contextual store bar reuses the kernel's silhouette glyphs; navBadge wraps any bar icon
// with a count bubble (COUNTER(n) / HOLDS(n) / the global STORE tab while a cart is piled).
function storeGlyph(family) {
  var S = window.SWRPGStore;
  return S ? S.glyph(family, 20) : navIcon('shopping-cart');
}
function holdsGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7.5 9-7.5 9-7.5-9z" />
    </svg>
  );
}
// B-679 — `paper` returns the badge WITHOUT a positioned wrapper of its own, because the paper tab
// already draws one: `BottomNav`'s glyph span becomes the board's `.pb-mark{position:relative}`, and a
// second wrapper here would nest `.pb-mark` inside `.pb-mark` where the frame's markup has exactly
// one. The COUNT and its 99 cap are the app's and do not move — the holds badge is still the length of
// the holds list and the counter badge still the SUM of the slip's quantities (`cartCount`, never the
// number of lines). Only the ground and the ink move: `.pb-badge` is `--doc-mark` filled with ink on
// it, which is that token's own documented job.
function navBadge(icon, count, paper) {
  var badge = count > 0 ? (paper
    ? <span aria-hidden="true" className="pb-badge">{count > 99 ? '99' : count}</span>
    : <span aria-hidden="true" style={{
        position: 'absolute', top: -5, right: -9, minWidth: 14, height: 14,
        padding: '0 3px', borderRadius: 8, boxSizing: 'border-box',
        background: 'rgba(245,200,66,0.92)', color: '#1a1406',
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{count > 99 ? '99' : count}</span>) : null;
  if (paper) return <React.Fragment>{icon}{badge}</React.Fragment>;
  return (
    <span style={{ position: 'relative', display: 'flex', lineHeight: 0 }}>
      {icon}
      {badge}
    </span>
  );
}

// Reactive "wide desktop" gate for the master-detail RAIL (ruling sheet-immersive-table-mode-brief FORK 4):
// the character rail only earns its 320px on a genuine desktop (≥1367px). At tablet widths (1024–1366) the
// sheet goes full-width and roster⇄sheet switches like the phone. matchMedia so it reacts to rotation/resize.
function useWideDesktop() {
  var _s = React.useState(function () { try { return !!(window.matchMedia && window.matchMedia(window.SWRPGBands.MQ_WIDE).matches); } catch (e) { return false; } });
  var wide = _s[0], set = _s[1];
  React.useEffect(function () {
    try {
      var mq = window.matchMedia(window.SWRPGBands.MQ_WIDE);
      var on = function () { set(mq.matches); };
      mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
      return function () { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
    } catch (e) {}
  }, []);
  return wide;
}

// THE CREATOR'S BAND HOOK (lap C1, ruling `creator-c1-mechanical` — the fork ruled (b)).
//
// Three bands, ONE meaning each: mobile < 1024 · mid 1024–1366 · wide >= 1367 — the ladder's one
// home is lib/shared/bands.js (audit item B); the seams here are read from it, never typed.
//
// COMPOSED, NOT INVENTED. The concept ruling's other sentence ("`data-layout` grows a third
// value") was AMENDED away in favour of this: growing that enum would change a vocabulary three
// CSS selectors in three apps consume (`player/index.html`, `gm/index.html`, `dice/index.html`,
// all the same `.app-shell` cap release), and any selector that missed the new value would
// silently revert desktop to a phone column at >=1367 — the exact defect this programme exists to
// kill, delivered by its own plumbing. So: no new vocabulary, no migration, no R-SY4 surface.
//
// Both inputs already ship and are already reactive: `useLayoutMode` is F-46's 1024 seam (and
// honours the user's explicit ⚙ layout preference, so a forced-mobile pref correctly reads
// 'mobile' at any width), `useWideDesktop` is the sheet's own 1367 matchMedia — which gains its
// second consumer here, exactly as the concept wanted.
function useCreatorBand() {
  var mode = useLayoutMode();
  var wide = useWideDesktop();
  return mode !== 'desktop' ? 'mobile' : (wide ? 'wide' : 'mid');
}

function PlayerApp() {
  // Dev-only test-bed generator route: /player?testbed
  // Short-circuits before any pathname-based routing — testbed lives
  // outside the app's normal navigation graph.
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('testbed')) {
    return React.createElement(TestBedScreen);
  }
  var layoutMode = useLayoutMode();  // F-46 D2: drives the roster↔sheet master-detail composition
  var wideDesktop = useWideDesktop();  // ≥1367 → the rail earns its width; below → mid-tier full-width sheet
  // F-46 D4: the ⚙ GENERAL LAYOUT pref (auto/mobile/desktop). Tracked so the segment re-renders even
  // when the pref changes without flipping the resolved mode (e.g. auto→desktop on a wide screen).
  var _lp = useState(function () { return (window.SWRPGLayout && window.SWRPGLayout.pref()) || 'auto'; });
  var layoutPref = _lp[0], setLayoutPref = _lp[1];
  function chooseLayout(v) { if (window.SWRPGLayout) window.SWRPGLayout.setPref(v); setLayoutPref(v); }
  // Dice view-prefs for the suite ⚙ (shared keys with HoloDice; the sheet dice modal reads them on open).
  var diceOdds = useLocalToggle('swrpg_dice_odds');
  var diceInterpret = useLocalToggle('swrpg_dice_interpret');
  // Initial screen: cold-load URL parse first. If the visitor is
  // deep-linking to /player/sheet/[id], /player/quickbuild, etc. —
  // honor that path. Otherwise fall through to the landing.
  // See plan: Docs/PLAN_URL_ROUTING.md.
  const [screen, setScreen] = useState(function() {
    if (homebrewScreenFor(window.location.pathname)) return 'homebrew';   // F-100 C2 (null while the switch is off)
    if (yardScreenFor(window.location.pathname) != null) return 'yard';   // SHIP YARD S4 · K6
    var parsed = parsePathToScreen(window.location.pathname);
    if (parsed.screen) return parsed.screen;
    // Visitors land on the landing (the WelcomeModal was removed in nav Phase 4 — the launchpad is the
    // front door). A returning player is not routed past it (R-LFM-6): its stage offers OPEN › on their
    // last-opened character.
    return 'landing';
  });
  // (WelcomeModal + the `welcomed` gate REMOVED — nav Phase 4. New visitors land directly; the launchpad
  //  is the front door. Coach marks fire in-context without a welcome gate.)
  // SHIP YARD S4: the Yard's position is its URL tail (K6), read by `yardScreenFor`.
  const [yardTailPath, setYardTailPath] = useState(function() { return yardScreenFor(window.location.pathname) || ''; });
  // U3 (`shipyard-s4-approvals` R-S4A-1, with U15): where the open ship was opened FROM — { kind:'sheet', id } when the
  // sheet's VESSELS opened it, so its back reads ‹ <NAME>’S SHEET. Kept in the history entry, never in the URL (no new
  // address contract), so a reload of that entry keeps it; every other way in reads ‹ HANGAR.
  const [yardFrom, setYardFrom] = useState(function() { try { return (window.history.state && window.history.state.yardFrom) || null; } catch (e) { return null; } });
  // U7 (`shipyard-s4-approvals` R-S4A-1): the Yard's scope on the player's host — the character the scope picker chose,
  // for this visit; with none chosen here, the app's one "which character" (swrpg_last_sheet_id, rider 22) stands.
  const [yardPick, setYardPick] = useState(null);
  const [yardPickerOpen, setYardPickerOpen] = useState(false);
  // MATRIX has no route of its own (K6); the live browse's own `?view=` carries it, as it does today.
  const yardView = /[?&]view=matrix\b/.test(window.location.search) ? 'matrix' : 'index';
  // B-650 · R-LFM-6 — WHICH CHAPTER THE LANDING LANDS ON. The host states the intent; `LandingPaper`
  // does the scrolling and the focus (one effect, its own zone hooks). `/player/roster` asks for the
  // INDEX, the bar's CHARACTERS for the page's TOP, CREATE for the CREATE chapter. The `at` stamp is
  // what makes a SECOND press of the same word scroll again.
  const [landingFocus, setLandingFocus] = useState(function() {
    var z = parsePathToScreen(window.location.pathname).zone;
    return z ? { zone: z, at: 0 } : null;
  });
  const [wipeType, setWipeType] = useState('wipeHorizontal');
// B-863 · R-NPC-4 (ruling npc-library-paper-concept) — THE LIBRARY'S POSITION IS IN THE URL. This host
// owns the router: it parses `/player/npc/<tail>`, holds the tail, hands it to the shared module as
// `location`, and answers the module's `onLocation(tail, opts)` — a MOVE pushes one history entry, a
// correction (`replace`) replaces it silently, and a BACK whose target is the previous entry POPS to
// it, so the library's BACK and the browser's back walk one list. The grammar is `lib/library/npc/
// npcRoute.js`'s; this code never interprets a segment.
  const [npcPath, setNpcPath] = useState(function() {
    return parsePathToScreen(window.location.pathname).npcPath || '';
  });
  const [savedChars, setSavedChars] = useState(function() { return loadSavedCharacters(); });
  // One-shot toast shown after a character is created: teaches the unfamiliar
  // "bookmark = save" affordance so the player knows subsequent edits need a
  // manual tap (vet-GM fix 2026-04-23; auto-save only covers initial creation).
  const [savedToastVisible, setSavedToastVisible] = useState(false);
  // B-1211 (paper-n7-frontdoor-build; D-175 approved the paper "saved" message as drawn, lab 90e31f70c toast.*.paper.*):
  // the toast's place and its keyboard dismiss. It stands 12px clear of the bottom bar's height where the screen has one
  // (lab finding 2: today the woken bar, z 140, covered it at 24px) and 24px where there is none; Escape dismisses it
  // (R-FD-3 (3)). Read before paint, and again while it shows: the sheet's bar can mount AFTER the toast (a pick lands
  // on the sheet and raises the toast in one turn), so the read follows the bar's mount and size until the toast goes.
  const [savedToastLift, setSavedToastLift] = useState(24);
  useLayoutEffect(function() {
    if (!savedToastVisible || screen !== 'sheet') return undefined;
    var seen = null, ro = null;
    function measure() {
      var nav = document.querySelector('nav.bottom-nav');
      if (nav !== seen) {
        if (ro) ro.disconnect();
        seen = nav; ro = null;
        if (nav && window.ResizeObserver) { ro = new ResizeObserver(function() { measure(); }); ro.observe(nav); }
      }
      setSavedToastLift(nav && nav.offsetHeight ? nav.offsetHeight + 12 : 24);
    }
    measure();
    var mo = new MutationObserver(function() { measure(); });
    mo.observe(document.body, { childList: true, subtree: true });
    function onKey(e) { if (e.key === 'Escape') setSavedToastVisible(false); }
    document.addEventListener('keydown', onKey);
    return function() { mo.disconnect(); if (ro) ro.disconnect(); document.removeEventListener('keydown', onKey); };
  }, [savedToastVisible, screen]);

  // ── In-app navigation helper ────────────────────────────────────
  // navTo updates React state AND pushes a new URL to browser history.
  // ALL in-app navigation should go through navTo — never call setScreen
  // directly. The exception is COLD-LOAD: the useState initializer above
  // and the cold-load ID-resolution useEffect below use setScreen
  // directly because the URL is already correct on initial mount and
  // pushing it again would create a duplicate history entry.
  //
  // Cloudflare Web Analytics auto-tracks pushState navigations as
  // separate page views per their SPA docs. No manual beacon calls.
  // F-100 C2: the editor's own path (its levels live under /player/homebrew; the editor parses it). Moved by
  // navTo('homebrew'), by the editor's navigate below, and by popstate.
  const [homebrewPath, setHomebrewPath] = useState(function() { return window.location.pathname; });
  const navTo = useCallback(function(screenId, charId, opts) {
    setScreen(screenId);
    if (screenId === 'homebrew') setHomebrewPath(pathForScreen('homebrew'));
    // R-Y2-2: the yard carries an optional vehicleKey (its entry page) instead of a charId.
    var vk = (opts && opts.vehicleKey) || null;
    if (screenId === 'npc') setNpcPath((opts && opts.npcPath) || '');   // B-863: the NPCs door lands on the front
    var path = pathForScreen(screenId, { charId: charId, vehicleKey: vk, npcPath: opts && opts.npcPath });
    // SHIP YARD S4 · U9 (R-S1C-6): every door to the Yard opens the HANGAR floor, and a door that names a hull opens its
    // ENTRY. Every door comes through here (both bars' Yard item, the paper landing tile, the legacy card, the sheet's yard
    // door), so this is the one site.
    if (screenId === 'yard') {
      var ytail = vk ? 'catalogue/' + encodeURIComponent(vk) : 'hangar';
      setYardTailPath(ytail); setYardFrom(null);
      path = '/player/yard/' + ytail;
    }
    if (window.location.pathname !== path) {
      history.pushState({ screen: screenId, charId: charId || null, vehicleKey: vk }, '', path);
    }
  }, []);

  // B-863 · R-NPC-4 — the module reports its position; the host writes the history (the GM's twin).
  const handleNpcLocation = useCallback(function(tail, opts) {
    var path = '/player/npc' + (tail ? '/' + tail : '');
    var cur = window.location.pathname;
    var st = window.history.state || null;
    setNpcPath(tail);
    if (opts && opts.replace) {
      window.history.replaceState(Object.assign({}, st || {}, { screen: 'npc', npcPath: tail }), '', path);
      return;
    }
    if (opts && opts.back && st && st.screen === 'npc' && st.npcPrev === tail) { window.history.back(); return; }
    if (cur !== path) {
      var prev = cur.indexOf('/player/npc') === 0 ? cur.slice('/player/npc'.length).replace(/^\//, '') : null;
      window.history.pushState({ screen: 'npc', charId: null, npcPath: tail, npcPrev: prev }, '', path);
    }
  }, []);

  // B-650 — THE BAR'S TWO PLAYER WORDS (R-LFM-6). Both land on the landing and each says WHERE on it:
  // CHARACTERS the top (the stage, whose OPEN is the character you were in), CREATE the CREATE
  // chapter. The five words and their order are KEPT — a reorder would be the bar's own ruling.
  // F-100 C2: the editor walks its own levels through the host's history (a save's replaceState included).
  const homebrewNavigate = useCallback(function(path, o) {
    setScreen('homebrew'); setHomebrewPath(path);
    if (window.location.pathname === path) return;
    if (o && o.replace) history.replaceState({ screen: 'homebrew' }, '', path);
    else history.pushState({ screen: 'homebrew' }, '', path);
  }, []);
  // SHIP YARD S4 — a move between the Yard's rooms: one history entry per move.
  const yardGo = useCallback(function(route) {
    var VV = window.SWRPGVesselView;
    var tail = VV.yardTail(route);
    // U3; R-S4CC-2: PLAN from an owned ship's part row carries that ship ({ kind:'ship', id, name }) for its ‹ <SHIP NAME>
    var from = route && route.from ? { kind: route.from, id: route.fromId != null ? route.fromId : route.id, name: route.fromName || null }
      : (yardFrom && route && route.room === 'ship' && route.id === yardFrom.id ? yardFrom : null);   // U3
    setScreen('yard'); setYardTailPath(tail); setYardFrom(from);
    var path = '/player/yard/' + tail + VV.yardQuery(route);   // R-S4CC-2: P1's ?part=, the Yard's one URL key
    if (window.location.pathname + window.location.search !== path) history.pushState({ screen: 'yard', yardTail: tail, yardFrom: from }, '', path);
  }, [yardFrom]);
  // C-YE1 / U3: back to the active character's sheet, on its VESSELS tab. It must LOAD the character (selectedChar +
  // sheetData), like the last-opened-sheet auto-route — a bare navTo('sheet') leaves selectedChar null and the sheet
  // never mounts. The legacy Yard's VESSELS and the S4 ship's ‹ <NAME>’S SHEET both come here.
  function yardOpenSheetVessels() {
    var id = null; try { id = localStorage.getItem('swrpg_last_sheet_id'); } catch (e) {}
    var rec = id && savedChars.find(function(c) { return c.id === id; });
    if (rec && rec.sheetData) {
      try { sessionStorage.setItem('_yard_jump_page', 'VESSELS'); } catch (e) {}
      openSheetRecord(rec, { backTarget: 'landing' });   // B-407 (N-post: the roster screen is deleted; its home is the landing)
    } else { navTo(homeScreenFor(savedChars)); }   // B-953
  }
  const landTo = useCallback(function(zone) {
    setLandingFocus({ zone: zone, at: Date.now() });
    navTo('landing');
  }, [navTo]);

  // ── Popstate handling: refs-mirror pattern ──────────────────────
  // The popstate handler must read CURRENT state when invoked, not
  // stale closure values. Every piece of state the handler reads is
  // mirrored to a useRef via a one-liner useEffect; the handler reads
  // .current instead of the state variable.
  //
  // CONVENTION FOR FUTURE MODIFICATIONS:
  //   - The popstate useEffect dep array stays EMPTY — never add deps.
  //   - Any new state the handler needs to read: add a parallel
  //     useRef + mirror useEffect alongside the existing ones below.
  //   - Never call pushState from inside the handler — it would
  //     create an infinite loop. The handler ONLY reads URL → state.
  const savedCharsRef = useRef(savedChars);
  useEffect(function() { savedCharsRef.current = savedChars; }, [savedChars]);
  // R-UBC1: the popstate handler reads the CURRENT selected char (via selectedCharRef, declared
  // below with selectedChar) to tell a real character switch from a pop that lands on the char
  // already loaded. The handler closure resolves selectedCharRef at call time (post-mount), so its
  // declaration can live after this effect.

  useEffect(function() {
    function handlePopState(_event) {
      var path = window.location.pathname;
      var parsed = parsePathToScreen(path);
      if (homebrewScreenFor(path)) { setScreen('homebrew'); setHomebrewPath(path); return; }   // F-100 C2 (off: never)
      var ytp = yardScreenFor(path);   // SHIP YARD S4 · K6
      if (ytp != null) { setScreen('yard'); setYardTailPath(ytp); setYardFrom((_event && _event.state && _event.state.yardFrom) || null); return; }
      setScreen(parsed.screen || 'landing');
      if (parsed.zone) setLandingFocus({ zone: parsed.zone, at: Date.now() });   // B-650: /player/roster brings the index into view
      setNpcPath(parsed.npcPath || '');                 // B-863: back/forward inside the NPC library
      if (parsed.charId) {
        // R-UBC1 (data-loss class): re-hydrate sheetData from the SAVED record ONLY on a genuine
        // character switch. A pop that lands on the char ALREADY loaded is an overlay dismissal
        // (useBackToClose's history.back() — the ⚙ menu, app-switcher, item overlay, roster picker)
        // or a same-char screen change (sheet↔store) — NOT a switch. Re-hydrating there silently
        // discarded unsaved in-memory edits (confirmed: edit credits → open/close the ⚙ menu →
        // reverts to saved). Keep the live in-memory sheet; just sync the store chip context.
        var cur = selectedCharRef.current;
        if (cur && cur.id === parsed.charId) {
          // B-28: a /sheet/[id]/store URL owns its BACK — mirror the cold-load path
          // (13064), which the popstate path silently diverged from (BACK went roster).
          if (parsed.screen === 'store') { setShopForId(cur.id); setStoreBackTarget('sheet'); }
          return;
        }
        var char = findCharById(savedCharsRef.current, parsed.charId);
        if (char) {
          var opened = openedPairOf(char);   // B-931: the picture folded into the sheet
          setSelectedChar(opened.selectedChar);
          setSheetData(opened.sheetData);
          setSheetSource(char.source || 'unknown');
          if (parsed.screen === 'store') { setShopForId(char.id); setStoreBackTarget('sheet'); }   // ITM-V1 chip context + owned BACK
        }
        // If !char (deleted between bookmarking and now), the sheet
        // screen will render its missing-char state. User explicitly
        // hit back — don't navigate further on their behalf.
      }
    }
    window.addEventListener('popstate', handlePopState);
    return function() { window.removeEventListener('popstate', handlePopState); };
  }, []);  // EMPTY DEPS — handler reads via refs above. Do not add deps.

  // Immersive-hide (navigation Phase 2b): the creation wizards (pregen/quickbuild/custom) + the NPC
  // library are full-focus → both bars slide away (body.holo-immersive); each keeps its own back escape
  // (the wizard step-header back; the NPC library back).
  useEffect(function() {
    // IA lap: the NPC library is bars-resident + auto-hide now (not immersive) — the bars ride and fade
    // on idle (the shared useAutoHideChrome the library calls + the `autohide` prop on the bars below).
    // + TABLE MODE on the sheet: both global bars recede via the SAME holo-immersive idiom (display:none
    // frees their height to the sheet — architect-verified, ruling §2). Reuse, no parallel mechanism.
    // B-629: the PAPER sheet is the table by default and its bars are SUMMONED (useEdgeChrome below), never
    // display:none. The legacy sheet's TABLE MODE term went with the old face (lap paper-sheet-delete).
    var immersive = screen === 'pregen' || screen === 'quickbuild' || screen === 'custom' || screen === 'import' || screen === 'backup';
    document.body.classList.toggle('holo-immersive', immersive);
    return function() { document.body.classList.remove('holo-immersive'); };
  }, [screen]);
  // B-629 (Jason 2026-09-16): on the paper sheet the table IS the default state — no TABLE MODE button, no
  // rail, no bars — and the app's two bars are called from the screen's edge or the runhead's own button.
  var sheetPaperTable = screen === 'sheet';
  // B-652 / concept Addendum 17 (Jason 2026-09-17: "the top and bottom bar will follow sane change
  // made in the sheet"). THE SAME DEVICE, NOT A SECOND ONE: the front matter takes the screen exactly
  // as the paper sheet does — `body.sheet-table` slides both bars out of the flow, the pointer at the
  // screen's top or bottom edge summons them back, and the running head's MENU press pins them. One
  // `useEdgeChrome` for both surfaces, so the two can never drift apart; with the sheet's switch OFF,
  // the sheet never sets the class.
  var landingPaperTable = screen === 'landing';
  // F-100 C2 (R-FISF-1): the homebrew editor is a full page in the same paper family — one more term, false
  // on every screen while swrpg_homebrew_editor is off (the screen is unreachable then).
  var homebrewPaperTable = screen === 'homebrew';
  var edgeChrome = useEdgeChrome({ active: sheetPaperTable || landingPaperTable || homebrewPaperTable });

  // ── Cold-load ID resolution ─────────────────────────────────────
  // If the URL points to /player/sheet/[id] or /player/sheet/[id]/store,
  // resolve the ID against savedChars (loaded synchronously from
  // localStorage in the useState initializer above). On hit, set
  // selectedChar/sheetData. On miss, fall back via replaceState so
  // the browser back button doesn't get stuck on the broken URL.
  useEffect(function() {
    var parsed = parsePathToScreen(window.location.pathname);
    if (!parsed.charId) return;
    var char = findCharById(savedChars, parsed.charId);
    if (char) {
      var opened = openedPairOf(char);   // B-931: the picture folded into the sheet
      setSelectedChar(opened.selectedChar);
      setSheetData(opened.sheetData);
      setSheetBackTarget('landing');
      setSheetSource(char.source || 'unknown');
      // /player/sheet/[id]/store deep link — that character rides the SHOPPING-FOR chip.
      if (parsed.screen === 'store') { setShopForId(char.id); setStoreBackTarget('sheet'); }
    } else {
      // B-650: a dangling id lands on the LANDING — its index lists the roster, so there is one
      // fallback (R-LFM-4's dangling-id case, R-LFM-6's home).
      setScreen('landing');
      history.replaceState({}, '', pathForScreen('landing'));
    }
  }, []);  // run-once on mount


  // UXQP-6: mid-creation draft resume. The landing offers CONTINUE WHERE YOU
  // LEFT OFF while a draft exists. pendingResume is the one-shot hydration
  // payload — set ONLY by the continue affordance, so entering a creator via
  // the normal cards always starts fresh (the old draft survives until the
  // new build picks a game line and overwrites it).
  const [creationDraft, setCreationDraft] = useState(loadCreationDraft);
  const [pendingResume, setPendingResume] = useState(null);
  useEffect(() => {
    if (screen === 'landing') {
      setCreationDraft(loadCreationDraft());
      // Ruling ux-p1-batch C1: retire any consumed resume snapshot the moment
      // the landing shows — otherwise browser-forward could remount a creator
      // with a STALE draft that then overwrites (or resurrects) the stored one.
      setPendingResume(null);
    }
  }, [screen]);
  // C-CP-4 (ruling f34-conflict-prompt-build §5) — re-read the draft when SYNC delivers one.
  // The effect above keys on `screen`, which is set once at mount: on a COLD OPEN straight onto
  // the landing, a draft that arrives with the account pull is sitting in storage and goes
  // ungreeted until the person navigates or reloads. That is the exact promise of "your work
  // follows you" failing at the first moment it is asked to keep it — you continue a character on
  // another device, and the door does not know you have one. Listening to the seam closes it:
  // CONTINUE appears when the work does.
  // B-971 (ruling `pregen-hotfix-sweep` R-PHS-1) — the ROSTER is re-read on the same events. It was
  // read once at mount, so after the account pull the landing drew the pre-sync roster (a new device
  // showed none), and every opener took its record from that stale copy: the first edit then wrote
  // the stale copy plus the edit over the newer record sync had just stored.
  // B-975 (card `f100-fix-b975-sync-overwrite-card` ADDENDUM 1, F1 + F2 shape A) — the OPEN SHEET follows the same
  // events. Its `sheetData` is its own state, so after a newer copy landed, the next edit upserted the old copy over it
  // (measured on all three events at d605ed64d). Now, when the stored copy of the open character differs from the
  // sheet's, the sheet takes it through the openers' own pair, and the landing count remounts the sheet (the
  // `SheetBoundary` key) so every mirror seeded at mount starts from the newer copy. Nothing is asked here: autosave
  // has already stored the sheet's last edit, and the merge asked wherever it could not order the two copies.
  // SHIP CARGO Part B (ruling `shipyard-cargo-build-gm-seam-census` R-SCGS-2) — ANOTHER TAB'S WRITE is the same moment. A GM
  // tab's LOAD FROM, or a second player tab's store or cargo act, stores a newer copy of the character this tab has open,
  // and this tab's next edit wrote its old copy back over it (the goods came back). The re-adopt body is named once,
  // `readoptStored`, and runs on sync's three events AND on a `storage` event for the roster key, which every tab but the
  // writer hears. The residual (an edit typed in the instant before the event arrives) is B-1412.
  const openSheetRef = useRef(null);
  const [sheetLanding, setSheetLanding] = useState(0);
  useEffect(() => {
    function readoptStored() {
      setCreationDraft(loadCreationDraft());
      var list = loadSavedCharacters();
      setSavedChars(list);
      var open = openSheetRef.current;
      var hit = open && open.selectedChar && list.find(function(c) { return c && c.id === open.selectedChar.id; });
      if (!hit) return;   // no sheet open, or its character is gone: handleSheetChange's roster check refuses the write
      var pair = openedPairOf(hit);
      if (JSON.stringify([pair.selectedChar, pair.sheetData]) === JSON.stringify([open.selectedChar, open.sheetData])) return;
      setSelectedChar(pair.selectedChar);
      setSheetData(pair.sheetData);
      setSheetLanding(function(n) { return n + 1; });
    }
    // B-977 (ruling `release-r7-bug-burndown` ADDENDUM 81): a record that LANDS here (another device's older shape, by sync or
    // another tab) passes through the mount migration too, so it is never read in its legacy shape until the next load.
    // A migration that changed something re-adopts once more, so an open sheet takes the migrated copy and its next edit
    // cannot write the legacy one back. That second pass does not migrate again.
    var live = true;
    function onLanding() {
      readoptStored();
      migrateStoredRoster().then(function(list) { if (list && live) readoptStored(); });
    }
    function onStorage(e) { if (e && e.key === ROSTER_STORAGE_KEY) onLanding(); }
    window.addEventListener('storage', onStorage);
    const sync = typeof window !== 'undefined' && window.SWRPGSync;
    const unsubscribe = sync ? sync.subscribe((type) => {
      if (type === 'adopted' || type === 'rechecked' || type === 'resolved') onLanding();
      // B-1649 (ADDENDUM 387): a load's pull that changed local redraws at the write, before adopt()'s push loop
      if (type === 'adopt_written') onLanding();
    }) : null;
    return () => {
      live = false;
      window.removeEventListener('storage', onStorage);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);
  const handleResumeDraft = useCallback(() => {
    const d = loadCreationDraft();
    if (!d) { setCreationDraft(null); return; }
    setPendingResume(d);
    const wipe = WIPE_TYPES[Math.floor(Math.random() * WIPE_TYPES.length)];
    setWipeType(wipe);
    navTo(d.flow === 'quickbuild' ? 'quickbuild' : 'custom');
  }, [navTo]);
  // ── F-45: RE-ENTRY. "EDIT IN CREATOR" from the sheet (concept §3.4) ─────────────────────────
  // Deliberately NOT a second creator path: it converts the sheet back to creator state through the
  // shared inverse, writes it as the SHIPPED creation-draft contract, and resumes it exactly as the
  // landing's CONTINUE affordance does. Everything downstream — the controller's hydration, the
  // review's jump-to-step, B-18's picker invalidation on a changed subject — is inherited rather than
  // rebuilt, which is what makes re-entry a seam instead of a fork.
  //   Fork A (Jason): the FULL creator always, including for QuickBuild-born characters. Adjusting is
  //   a full-control moment; Guided is an onboarding funnel, not an editor.
  // Opens at REVIEW (FC_STEPS index 10) so the player lands on the summary and jumps to the step they
  // came to change.
  const handleEditInCreator = useCallback((charRec, sheet, opts) => {
    if (!sheet) return;
    // `opts.step` (card `f100-cerean-intellectuals` ADDENDUM 7): the species ask's career-pick route lands on the SPECIES
    // step, where the pick is made; every other door (PROFILE's, every other deferred class) passes none and keeps Review.
    var landOn = (opts && opts.step === 'species') ? 'species' : 'review';
    // B-744 / R-RTD-2 — THE TRADE TABLE IS THE CONVERSION'S THIRD ARGUMENT, and this door is the reason
    // it has to be reachable from here. Without it the trade's experience and credits come back as a
    // DECLARED LOSS rather than numbers (R-RTD-3), so the creator would open on the UNTRADED budget —
    // which is B-744 measured: Orin Vael read a 90-XP pool against 95 already spent and a 500-credit
    // purse against 1,125 of gear, a character the creator would refuse built from a record a guard
    // proves legal. The table is NOT in this component's state (`narrativeMechanics` belongs to the
    // creator screens), so it comes from the stash the creator's own loader fills, and on the cold path —
    // a player who opens a sheet and taps EDIT IN CREATOR without having visited the creator — it is
    // fetched first: one 6 KB request, on a deliberate two-tap action, rather than seven at every boot.
    // `loadMechConfig` resolves null instead of throwing and a null third argument is the declared-loss
    // path, so nothing here can block re-entry (Fork B: re-entry is never gated).
    var _mechReady = window._narrativeMechConfig ? Promise.resolve(window._narrativeMechConfig)
      : (typeof loadMechConfig === 'function' ? loadMechConfig() : Promise.resolve(null));
    // F-100 M6 R5 (ruling f100-m6-pack R-M6G-2, B-995/B-885): STAMP-SCOPED END TO END. The sheet's OWN packs — its
    // `mods` VERBATIM, variant and user: stamps alike; an unstamped sheet carries an explicit empty list — pick the
    // view the door projects and holds through, never this device's toggles. The list rides on the draft's `reentry`
    // marker, and the marker (never the toggle) tells the creator which view the draft resolves through and what
    // finalize stamps. Under the toggle view a re-entered character came back on the device's version, and could change
    // talents (R5a: Skilled Jockey → Gearhead) or be held silently for lacking a pack its own stamp names (R5c).
    var carried = Array.isArray(sheet.mods) ? sheet.mods.slice() : [];
    _mechReady.then(function(mechConfig) {
      return loadCanonicalCharacterBuild().then(function() {
        var base = window._canonicalCharbuildBase;
        return window.SWRPGMods ? window.SWRPGMods.resolveForStamp(carried, base) : base;
      }).then(function(cb) { return { mechConfig: mechConfig, cb: cb }; });
    }).then(function(r) {
      var mechConfig = r.mechConfig, cb = r.cb;
      if (!cb) return;
      // A carried SHIPPED pack that failed to load leaves the view without it, and projecting through that would fork
      // the character onto the base trees — so the door does not open (B-30's RETRY re-fetches).
      var _failedNow = (window.SWRPGMods && window.SWRPGMods.failed) ? window.SWRPGMods.failed() : [];
      if (carried.some(function(s) { return _failedNow.indexOf(s) >= 0; })) return;
      // B-855: the guard BEHIND the door (R-B854-1) — re-entry re-projects the sheet through THIS
      // view, so a held sheet never reaches saveCreationDraft, whatever still calls the handler.
      // (Fork B's "re-entry is never gated" predates the hold; f100-b854 holds it by ruling.)
      if (sheetHoldOf(sheet, cb) || deletedSpecHeldOf(sheet, cb)) return;   // + F-100 M5 U15: a deleted own specialization holds the door
      var out = window.CharResolve.sheetToCreatorState(sheet, cb, mechConfig || null);
      // B-973: the draft's character gets an id of its OWN — the finish saves under it (`creationFinishId`), so the
      // re-entered build still becomes a new character beside the one it came from, as it always has, and once
      // finished its draft reads as finished on every device. Load-bearing, not tidiness: `sheetToCreatorState` carries
      // `sheet.id`, which on a pregen is its static roster id ('smuggler'), so without this the re-entry draft of an owned
      // pregen would read as finished the moment it was written and CONTINUE would never offer it.
      var ch = Object.assign({}, out.character, { id: newCreationId() });
      var reg = (cb || {});
      saveCreationDraft({
        // C-GLB-1: v2, BY NAME. This wrote `v: 1` with a TWELVE-step index — a v1 label on v2
        // data. It landed on review only because 11 is out of the frozen order's range and the
        // unresolvable fallback catches it: the right destination reached through the FAILURE
        // path. Any other target, or a thirteenth step, and it lands silently wrong — the exact
        // bug B-72's contract lap existed to kill.
        v: 2, flow: 'custom', gameLine: ch.gameLine, step: landOn, partySize: 4,
        speciesData: (ch.species && reg.species) ? (reg.species[ch.species.key] || null) : null,
        careerData: (ch.career && reg.careers) ? (reg.careers[ch.career.key] || null) : null,
        specData: (ch.specializations[0] && reg.specializations) ? (reg.specializations[ch.specializations[0].key] || null) : null,
        character: ch,
        // F-45 §3.5: what the sheet could not give back, carried so the creator can say "set at
        // creation" rather than pretend the history exists. Never blocks re-entry.
        reentry: { fromCharacterId: charRec ? charRec.id : null, notes: out.notes, mods: carried },   // F-100 M6 R5
        savedAt: Date.now(),
      });
      setCreationDraft(loadCreationDraft());
      setPendingResume(loadCreationDraft());
      navTo('custom');
    });
  }, [navTo]);

  const handleDiscardDraft = useCallback(() => {
    clearCreationDraft();
    setCreationDraft(null);
    setPendingResume(null);   // ruling C1: a discarded draft must never resume
  }, []);

  const handleEnter = useCallback(sectionId => {
    const wipe = WIPE_TYPES[Math.floor(Math.random() * WIPE_TYPES.length)];
    setWipeType(wipe);
    setPendingResume(null);   // normal card entry = fresh build, never a resume
    navTo(sectionId);
  }, [navTo]);

  const handleBack = useCallback(() => {
    navTo('landing');
  }, [navTo]);

  const [selectedChar, setSelectedChar] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  // R-UBC1: mirror the selected char for the popstate handler's same-char guard (declared here,
  // after selectedChar, to avoid a temporal-dead-zone reference).
  const selectedCharRef = useRef(selectedChar);
  useEffect(function() { selectedCharRef.current = selectedChar; }, [selectedChar]);
  const [sheetBackTarget, setSheetBackTarget] = useState('pregen');
  const [sheetSource, setSheetSource] = useState('unknown');
  // B-975: the open sheet as the sync listener compares it (declared up with that listener, read only inside it).
  openSheetRef.current = { selectedChar: selectedChar, sheetData: sheetData };
  // B-405: the boundary's line for the roster; cleared by the next clean open (openSheetRecord).
  const [rosterStatus, setRosterStatus] = useState(null);

  // Weapon qualities database — loaded once on mount for the sheet's
  // tappable quality chips. Missing data just disables the expand panel;
  // non-blocking for the rest of the app.
  const [qualitiesDb, setQualitiesDb] = useState(null);
  // Populate window._canonicalRegistries at app init so the sheet viewer can resolve equipment
  // id-refs on a cold-load (/player/sheet/[id]). Sheet tabs re-resolve when it lands (ADR 0003 3b).
  // charbuild 3a: warm both registry sets at boot. charbuild 3b: once the charbuild
  // registries land, migrate any legacy-shape saved characters to canonical id-refs
  // (D-3B4: collision-aware name matching — ambiguous/unmatched entries stay as marked
  // legacy display entries, never guessed, never dropped). Before the FIRST rewrite, the
  // whole roster is snapshotted to a one-time backup key (ruling condition 2) so a wrong
  // match is recoverable. Runs ONLY with registries loaded; otherwise storage is untouched.
  useEffect(function() {
    loadCanonicalRegistries();
    migrateStoredRoster().then(function(list) { if (list) setSavedChars(list); });
  }, []);
  useEffect(function() {
    // Fetch with one-retry for transient boot-time races (QA C8).
    // Quality data is non-blocking (null → expand panels disabled, that's fine).
    function fetchQualities(attempt) {
      // blob-retirement-2: quality descriptions + the skill-name map come from the
      // canonical VOCABS — the player's last oggdude_merged.json fetch is RETIRED.
      return Promise.all([
        fetch('public/data/canonical/qualities.vocab.json').then(function(r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); }),
        fetch('public/data/canonical/skills.vocab.json').then(function(r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); }),
      ])
        .then(function(results) {
          var map = {};
          (results[0].entries || []).forEach(function(q) {
            map[q.label.toLowerCase()] = q;
            (q.aliases || []).forEach(function(a) { map[String(a).toLowerCase()] = q; });
          });
          setQualitiesDb(map);
          window._qualitiesDb = map;
          if (window.SWItems && window.SWItems.init) window.SWItems.init(results[1]);
        })
        .catch(function(err) {
          if (attempt < 2) {
            setTimeout(function() { fetchQualities(attempt + 1); }, 1000);
          } else {
            // Final failure is non-blocking; quiet the console so it doesn't
            // spam GMs who never open quality-expand drawers.
            console.info('[PlayerApp] Qualities DB unavailable (quality expand panels will stay collapsed).');
          }
        });
    }
    fetchQualities(0);
  }, []);


  // Active-session persistence (sheet-session F5 recovery). Helpers kept after the
  // encounter removal — the sheet restore/persist effects below still use them.
  const SESSION_KEY = 'swrpg_player_active_session';
  function writeActiveSession(payload) {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(payload)); } catch (e) {}
  }
  function readActiveSession() {
    try { var raw = localStorage.getItem(SESSION_KEY); return raw ? JSON.parse(raw) : null; }
    catch (e) { return null; }
  }
  function clearActiveSession() {
    try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  // Persist active session whenever the player is on the sheet screen. F5 then restores them into the
  // same character (see restore effect below).
  // B-650 · R-LRP-A2-2: "a returning player PASSING THROUGH THE LANDING must not lose F5 recovery." The
  // landing is the screen every return and every BACK lands on, so leaving the sheet does not clear the
  // key — clearing there would make the recovery unreachable. The key still ages out on its own
  // 30-minute clock in the restore effect below, so the survival is a SESSION's, not forever.
  useEffect(function() {
    if (screen === 'sheet' && selectedChar) {
      writeActiveSession({
        selectedCharId: selectedChar.id,
        screen: screen,
        savedAt: Date.now(),
      });
    }
  }, [screen, selectedChar]);

  // B-407 — ONE OPENER (ruling `landing-front-matter-concept` R-LFM-4; `landing-revision-prep` card 3).
  // Every path that puts a character's sheet on screen goes through here, so the two facts a return
  // visit reads — `swrpg_last_sheet_id` (UXQP-5: which character) and the record's `lastOpenedAt`
  // (UXQP-11b: when) — are stamped by every opener, not only the roster's row. Before this a
  // just-created, imported or pregen character carried neither until re-opened from the roster.
  // `rec` is a roster record or a record-shaped object {id, selectedChar?, sheetData, source, name…};
  // `opts.backTarget` is where BACK goes; `opts.source` overrides the record's own. The one
  // `navTo('sheet')` that stays outside: the store's EXIT back to the sheet already open — nothing is
  // opened there. `test/sheet_openers_routed.cjs` walks the openers.
  const openSheetRecord = useCallback(function(rec, opts) {
    opts = opts || {};
    var opened = openedPairOf(rec);   // B-931: the picture folded into the sheet (R-PB9-4)
    setSelectedChar(opened.selectedChar);
    setSheetData(opened.sheetData);
    setSheetBackTarget(opts.backTarget || 'landing');
    setSheetSource(opts.source || rec.source || 'unknown');
    setRosterStatus(null);
    try { localStorage.setItem('swrpg_last_sheet_id', rec.id); } catch (e) {}
    try {
      var list = loadSavedCharacters();
      var hit = list.find(function(c) { return c.id === rec.id; });
      if (hit) { hit.lastOpenedAt = Date.now(); writeSavedCharacters(list); setSavedChars(list); }
    } catch (e) {}
    navTo('sheet', rec.id);
  }, [navTo]);

  // B-405 — the boundary's fallback (R-LFM-4 as Addendum 18 amended it) is the LANDING: the line
  // stands at the index's head with the broken record listed beneath it — which is what the line's
  // own words promise. `landTo` brings the index into view and puts the focus there, so the line is
  // where the reader is placed rather than somewhere they have to go looking for it.
  const handleSheetFail = useCallback(function() {
    setRosterStatus('This character\u2019s save could not be read \u2014 it is listed below.');
    setSelectedChar(null);
    setSheetData(null);
    landTo('index');
  }, [landTo]);

  // Restore active session ONCE on mount: if the player was on a sheet when
  // they refreshed, drop them back where they were.
  // Skip if the saved session is older than 30 minutes (stale).
  // R-S4CO-3 (ruling `shipyard-s4-collection` ADDENDUM 1): ONE rule for every explicit path. The restore serves the
  // bare `/player` root — the phone app's relaunch — and nothing else: a cold load of any other path (a Ship Yard
  // room, the NPC library, a deep NPC path, the store, the roster, Homebrew, a sheet's own link) opens what it names,
  // and the session is kept for the next relaunch. The entry path is read ONCE, at mount, before any in-app move
  // rewrites the URL. Legs: scripts/smoke_explicit_path_wins.py (each red at f9de859c).
  const entryPathRef = useRef(window.location.pathname);
  const didRestoreSessionRef = useRef(false);
  // B-1585 — IT DECIDES ONCE, ON ITS FIRST RUN, THEN DISARMS. The roster is read synchronously at mount
  // (`loadSavedCharacters`), so the first run already holds every record a session from before this load can name.
  // Left armed, a first visit's empty roster kept it waiting, and the first character saved in THIS visit (a Quick
  // Start pick's autosave) fired it mid-session on the key the open sheet had just written — re-opening that sheet
  // with Back aimed at the landing instead of Quick Start. Guard: scripts/smoke_b1011_sheet_back_url.py leg B.
  useEffect(function() {
    if (didRestoreSessionRef.current) return;
    didRestoreSessionRef.current = true;
    if (!/^\/player(\/(index\.html)?)?$/.test(entryPathRef.current)) return;
    if (savedChars.length === 0) return;
    var s = readActiveSession();
    if (!s || !s.selectedCharId) return;
    if (Date.now() - (s.savedAt || 0) > 30 * 60 * 1000) { clearActiveSession(); return; }
    var rec = savedChars.find(function(c) { return c.id === s.selectedCharId; });
    if (!rec) return;
    openSheetRecord(rec, { backTarget: 'landing' });   // B-407
  }, [savedChars, openSheetRecord]);

  const handleSaveCharacter = useCallback(function() {
    if (!selectedChar) return;
    var list = upsertSavedCharacter(selectedChar, sheetData, sheetSource);
    setSavedChars(list);
    // Show the "saved" toast so the bookmark tap has visible feedback
    // (vet-r1 P1: server-party saves were silent — user assumed unsaved).
    setSavedToastVisible(true);
    setTimeout(function() { setSavedToastVisible(false); }, 2500);
  }, [selectedChar, sheetData, sheetSource]);

  const handleDeleteCharacter = useCallback(function(id) {
    var list = removeSavedCharacter(id);
    setSavedChars(list);
    if (list.length === 0) navTo('landing');
  }, [navTo]);

  const handleSelectSaved = useCallback(function(rec) {
    openSheetRecord(rec, { backTarget: 'landing' });   // B-407: UXQP-5's and UXQP-11b's stamps live in the opener
  }, [openSheetRecord]);

  // B-649 — THE ARRANGED ORDER'S ONE WRITER (R-LFM-12; the durable-contract change surfaced in
  // concept A4 before this code). The paper landing's ARRANGE hands up the ids in the order the
  // player left them and nothing else: the roster's storage stays where every other roster write
  // already is (R-LFM-6 — the landing owns none), and the list is re-read here so a move writes onto
  // the roster as it is on disk rather than onto a render-old copy.
  // EVERY RECORD GETS ONE, 0..n−1, and a record the caller did not name keeps its place at the end
  // (there is none today — the index draws the whole roster but the stage's character, which leads
  // the list the caller sends). A gap left by a later deletion is harmless: `ldOrdered` reads the
  // field's ORDER, never its arithmetic.
  const handleReorderRoster = useCallback(function(ids) {
    var pos = {};
    (ids || []).forEach(function(id, i) { pos[id] = i; });
    var list = loadSavedCharacters();
    var next = list.slice().sort(function(a, b) {
      var pa = pos[a.id] == null ? ids.length : pos[a.id];
      var pb = pos[b.id] == null ? ids.length : pos[b.id];
      return pa - pb;
    });
    next.forEach(function(r, i) { r.order = i; });
    writeSavedCharacters(next);
    setSavedChars(next);
  }, []);

  // ── THE STORE host state (ITM-V1) ────────────────────────────────
  // The kernel (lib/store/view.jsx) owns the shopping surface; the app owns the contextual
  // bars (ADR 0010), the SHOPPING-FOR context, and ALL persistence (finalize writes below).
  const [storeTab, setStoreTab] = useState('weapons');
  const [storeCounts, setStoreCounts] = useState(function() {
    var S = window.SWRPGStore;
    return { cart: S ? S.cartCount() : 0, holds: S ? S.holdsCount() : 0 };
  });
  // Chip default = the most-recently-opened character (items-program-scope §7c.2) — seeded at
  // mount so a cold-load of /player/store gets the same default as the bar entry; resolves
  // against the roster at render (findCharById → null when stale), so a dangling id is harmless.
  const [shopForId, setShopForId] = useState(function() {
    try { return localStorage.getItem('swrpg_last_sheet_id') || null; } catch (e) { return null; }
  });
  const [shopPickerOpen, setShopPickerOpen] = useState(false);
  const [artPanelOpen, setArtPanelOpen] = useState(false);   // BYOA — the ⚙ ITEM ARTWORK panel
  const [storeBackTarget, setStoreBackTarget] = useState('landing');
  // SHIP YARD S4 · G-7 (R-S4WC-2): the Yard route a TO THE COUNTER came from — ‹ BACK returns to it (`exitStore`)
  const [storeBackYard, setStoreBackYard] = useState(null);
  const shopForRec = shopForId ? findCharById(savedChars, shopForId) : null;
  // SHIP YARD S4: the Yard's route, and whose hangar it is — the character `swrpg_last_sheet_id` names
  // when that character is on this device (the app's one "which character", UXQP-5); none otherwise (rider 22).
  const yardS4 = screen === 'yard' && !!(window.SWRPGVesselView && window.SWRPGVesselView.yardRoute);
  const yardRouteNow = yardS4 ? window.SWRPGVesselView.yardRoute(yardTailPath, window.location.search) : null;
  const yardChar = yardS4 ? (function() { var id = yardPick; if (!id) { try { id = localStorage.getItem('swrpg_last_sheet_id'); } catch (e) {} } return id ? findCharById(savedChars, id) : null; })() : null;
  // U3: this ship was opened from the sheet's VESSELS (the entry says so, for this ship), and that sheet's character is here
  const yardShipFromSheet = !!(yardRouteNow && yardRouteNow.room === 'ship' && yardChar && yardFrom && yardFrom.kind === 'sheet' && yardFrom.id === yardRouteNow.id);
  // U15: the ship room's owner chip — YOURS for this character's own ship, else the owner's name (ownerLabel's words)
  // R-S4CC-2 · P1's back (U15's rule): to the owned ship's FIT tab it was opened from, ‹ <SHIP NAME>; else ‹ THE ENTRY.
  // EXIT PLAN is the same move.
  const yardPlanFromShip = !!(yardRouteNow && yardRouteNow.room === 'plan' && yardFrom && yardFrom.kind === 'ship');
  function yardPlanBack() {
    if (yardPlanFromShip) yardGo({ room: 'ship', id: yardFrom.id, tab: 'fit' });
    else yardGo({ room: 'entry', key: yardRouteNow.key });
  }
  function yardShipOwnerWord() {
    var VS = window.SWRPGVessels, VV = window.SWRPGVesselView;
    var r = VS && yardRouteNow ? (VS.getState().vessels || []).filter(function(v) { return v.id === yardRouteNow.id; })[0] : null;
    if (!r) return null;   // a ship this device does not hold: the room says not-found, the chip stays the scope's
    return yardChar && r.owner === 'char:' + yardChar.id ? 'YOURS' : VV.ownerLabel(r.owner, savedChars);
  }
  // SHIP YARD S4 · G-7 — FIT's TO THE COUNTER (ruling `shipyard-s4-workbench-contracts` R-S4WC-2): the line goes on through
  // the store's OWN writer (R-S4WC-16) and the shopper lands on the counter, K2's drawn landing; the Yard's character is
  // pre-selected, as the sheet's SHOP door pre-selects its own (R-ITM3). ‹ BACK returns to the FIT tab it came from.
  function yardToCounter(line) {
    var S = window.SWRPGStore;
    if (!S || typeof S.addToCounter !== 'function' || !S.addToCounter(line)) return;
    if (yardChar) setShopForId(yardChar.id);
    setStoreBackTarget('yard');
    setStoreBackYard(yardRouteNow ? Object.assign({}, yardRouteNow) : null);
    setStoreTab('counter');
    navTo('store');
  }

  /* B-671 — THE PURSE STRIP AT THE SHEET'S DOOR (lab frames O and P: `board(…, ctx=Ral Voth,
     purse=340)`). At THIS door the purse is THE CHARACTER'S OWN CREDITS — not a starting allowance —
     so it is read off the same roster record the kernel's counter reads its AFTER line from, and the
     smoke asserts the two numbers are EQUAL rather than trusting that they are. JUST BROWSING has no
     purse to read and the board drew no strip for that state, so this is null and nothing renders.

     NOT ON THE COUNTER TAB, and that is the board's own arrangement rather than a saving: the counter's
     summary already prints `<NAME> HAS N CR` beside `AFTER · N CR` (frame F), so a strip there would
     print the same number twice inside 100px. Frames O/P draw the strip on the SHELF; frame F and
     frame AD (the counter with both bars drawn) draw none.

     HOISTED OUT OF THE MOUNT ON PURPOSE: `scripts/store_map.mjs` reads the mount's object literal to
     list "props the host passes", and an inline nested object put this thing's own keys (`label`,
     `onAct`) in the map beside the real props. A named value keeps the generated map honest. */
  const storePurse = (shopForRec && storeTab !== 'counter'
                      && shopForRec.sheetData && shopForRec.sheetData.equipment
                      && shopForRec.sheetData.equipment.credits != null)
    ? { value: Number(shopForRec.sheetData.equipment.credits),
        label: 'COUNTER', onAct: function() { setStoreTab('counter'); } }
    : null;

  const handleStoreCounts = useCallback(function(c) { setStoreCounts(c); }, []);
  const handleStorePick = useCallback(function(id) { setShopForId(id); }, []);

  // Second door (R-ITM3): the sheet's SHOP button — same surface, that character pre-selected.
  const handleOpenStore = useCallback(function() {
    if (selectedChar) setShopForId(selectedChar.id);
    setStoreBackTarget('sheet');
    setStoreTab('weapons');
    navTo('store', selectedChar && selectedChar.id);
  }, [selectedChar, navTo]);

  // Front door: the bottom-bar STORE tab. SHOPPING-FOR defaults to the most-recently-opened
  // character (chip works with NONE selected — just browsing).
  const enterStore = useCallback(function() {
    setStoreBackTarget(screen === 'sheet' ? 'sheet' : (screen === 'npc' ? 'npc' : 'landing'));
    setShopForId(function(cur) {
      if (cur) return cur;
      try {
        var last = localStorage.getItem('swrpg_last_sheet_id');
        if (last && savedChars.some(function(c) { return c.id === last; })) return last;
      } catch (e) {}
      return null;
    });
    navTo('store');
  }, [screen, savedChars, navTo]);

  // THE STORE'S ONE BACK RESOLUTION (B-953's rule is its last branch, never a second). G-7 · R-S4WC-2: a shopper who came
  // through the Yard's TO THE COUNTER goes back to the FIT tab it came from (while the Yard is on; off, B-953 decides).
  const exitStore = useCallback(function() {
    if (storeBackTarget === 'sheet' && selectedChar) navTo('sheet', selectedChar.id);
    else if (storeBackTarget === 'npc') navTo('npc');
    else if (storeBackTarget === 'yard' && storeBackYard) yardGo(storeBackYard);
    else navTo(homeScreenFor(savedChars));   // B-953
  }, [storeBackTarget, storeBackYard, selectedChar, savedChars, navTo, yardGo]);

  // FINALIZE — the ONE equipment write (ITM-A contract). Persists straight to the roster
  // record (the market run must survive reload even when no sheet is open — R-ITM10);
  // if that character's sheet is live in state, it updates too.
  // SHIP CARGO Part B (R-SCGS-1): the write is `SWRPGRoster.writeEquipment`, the GM's too. Its base is the STORED
  // record, read at write time, not this render's `savedChars`, so another tab's write since is kept. The B-931 fold
  // (`openedPairOf`) runs inside it. A stale roster (F-54) writes nothing and answers false here.
  const handleStoreFinalize = useCallback(function(charId, nextEquipment, summary) {
    var list;
    try { list = window.SWRPGRoster.writeEquipment(charId, nextEquipment); } catch (e) { return false; }
    if (!list) return false;
    setSavedChars(list);
    var rec = findCharById(list, charId);
    if (selectedChar && selectedChar.id === charId) setSheetData(rec.sheetData);
    return true;
  }, [selectedChar]);

  const handleStoreOpenSheet = useCallback(function(id) {
    var rec = findCharById(savedChars, id);
    if (rec) handleSelectSaved(rec);
  }, [savedChars, handleSelectSaved]);

  const isSelectedSaved = selectedChar && savedChars.some(function(c) { return c.id === selectedChar.id; });

  // R1 (CREATOR_PLUMBING §5 item 1): Guided's 132-line parallel assembly is GONE. It ends in the same
  // `normalizeCreated` Custom does, so the nine drift points in §2.4 close by construction rather than
  // by being fixed twice. What Guided still owns is what makes it Guided: its editorial Force-power
  // pick (seeded PRICED, above) and its own `source` stamp.
  const handleQuickBuildComplete = useCallback((character, gameLine, gameData, speciesRec) => {
    // B-738: the species lookup, the spec lookup, the Force seed and the assembly moved VERBATIM
    // into `guidedAssembledModel`, which the REVIEW step now calls with the same inputs. The review
    // is therefore a projection of this record and cannot disagree with it. Nothing about what is
    // persisted changed: the same four steps, in the same order, on the same object.
    var assembled = guidedAssembledModel(character, gameLine, gameData, speciesRec);
    var built = assembled.built;
    var qbSheetData = assembled.sheet;

    var qbSelectedChar = {
      id: creationFinishId(built.id),   // B-973: never over a character this build already became
      name: qbSheetData.name,
      career: qbSheetData.career,
      species: qbSheetData.species,
      book: gameLine,
      accent: QB_ACCENT[gameLine] || '#00C8FF',
      // B-931: no `portrait` here — the picture is stored once, in the sheet (`qbSheetData.portrait`).
    };
    // UXQP-6: the build is complete — retire its draft so the landing stops
    // offering a resume into a character that now lives on the roster.
    clearCreationDraft();
    setCreationDraft(null);
    setPendingResume(null);
    // Autosave on creation (parallel to handleFullCreatorComplete). Subsequent
    // edits still require an explicit bookmark tap; this only persists the
    // freshly-built character so a closed tab does not lose them.
    try {
      var list = upsertSavedCharacter(qbSelectedChar, qbSheetData, 'quickbuild');
      setSavedChars(list);
      setSavedToastVisible(true);
      setTimeout(function() { setSavedToastVisible(false); }, 6000);
    } catch (e) { /* non-fatal — fall back to manual save */ }
    // B-407: opened AFTER the roster entry exists, so the opener's lastOpenedAt lands on it.
    openSheetRecord({ id: qbSelectedChar.id, selectedChar: qbSelectedChar, sheetData: qbSheetData, source: 'quickbuild' }, { backTarget: 'landing' });
  }, [openSheetRecord]);

  // R1 (CREATOR_PLUMBING §5 item 2): the finalize is no longer a RENDER. It used to receive
  // FCReviewStep's `useMemo` output — the review PREVIEW *was* the persisted sheet, so the write path
  // could not be versioned or tested apart from a component. It now receives the same INPUTS the
  // preview memo uses and runs the assembly itself. Preview and finalize share the function; the
  // finalize is not the preview.
  const handleFullCreatorComplete = useCallback((inputs, gameLine, speciesData) => {
    var displayModel = normalizeCreated(inputs.character, inputs.speciesData, inputs.specData, inputs.gameData, 'FullCreator');
    var accent = QB_ACCENT[gameLine] || '#F5A623';
    // Was `speciesData.img`, retired with normalizeOggdude — so every character finished in the full
    // creator was saved with `portrait: ''` and the roster card fell to the seam even for a species
    // whose art we ship. Same one home as every other portrait read.
    var portrait = (speciesData ? speciesImgPath(speciesData) : '') || '';
    // B-931 (ruling `paper-b931` R-PB9-1/6): the picture is stored once, in the sheet. This by-RECORD art used to
    // ride on `selectedChar` and won the display over the sheet's by-NAME art (W2); it now goes into the sheet with
    // that same precedence, so the 8 species whose two arts differ show exactly what they showed.
    if (portrait) displayModel = Object.assign({}, displayModel, { portrait: portrait });
    // B-1408 (D-271; card ADDENDUM 3 R-2/R-3): a re-entered build carries its source's own fields — the list
    // `CharResolve.SHEET_OWNED_FIELDS` — read from the SOURCE record, which is never written. No marker, or a source no
    // longer on the roster: nothing carried, nothing invented.
    var _fromId = inputs.reentry && inputs.reentry.fromCharacterId;
    var _source = _fromId ? loadSavedCharacters().find(function(c) { return c && c.id === _fromId; }) : null;
    if (_source && _source.sheetData) displayModel = CharResolve.carrySheetOwned(_source.sheetData, displayModel);
    var newChar = {
      // B-973: the draft's own character id (`creationFinishId`), so a finished Custom build is known as finished
      // wherever its draft comes back; was `Date.now().toString(36)`, which no draft could ever be matched to.
      id: creationFinishId(inputs.character && inputs.character.id),
      name: displayModel.name,
      career: displayModel.career,
      species: displayModel.species,
      book: gameLine,
      accent: accent,
    };
    // UXQP-6: completed build — retire the draft (see handleQuickBuildComplete).
    clearCreationDraft();
    setCreationDraft(null);
    setPendingResume(null);
    // First-save auto-persist per vet UX call 2026-04-22 — losing a freshly-created character to
    // a forgotten bookmark tap was the biggest first-run UX trap. C-GFB-5 (2026-08-23) generalised
    // it: SUBSEQUENT edits now write through too, so the "explicit save only" rule this comment
    // used to carve an exception out of no longer exists. This call survives unchanged because it
    // still does the one thing autosave deliberately will not — CREATE the roster entry.
    try {
      var list = upsertSavedCharacter(newChar, displayModel, 'custom');
      setSavedChars(list);
      setSavedToastVisible(true);
      setTimeout(function() { setSavedToastVisible(false); }, 6000);
    } catch (e) { /* non-fatal — fall back to manual save */ }
    openSheetRecord({ id: newChar.id, selectedChar: newChar, sheetData: displayModel, source: 'custom' }, { backTarget: 'landing' });   // B-407
  }, [openSheetRecord]);

  // OGG-1 import completion — the character is already assembled through the real core (ImportOgg ran
  // normalizeCreated + attached the re-encoded portrait + oggSource). Mirror the custom-creator handoff:
  // roster entry, load the sheet, auto-persist. The imported character behaves as a native character
  // (its provenance rides in sheet.oggSource, read by no resolver — R-SPP-12).
  const handleImportComplete = useCallback((sheet, meta) => {
    var gameLine = (meta && meta.gameLine) || sheet.book || 'eote';
    var accent = QB_ACCENT[gameLine] || '#F5A623';
    var newChar = {
      id: Date.now().toString(36), name: sheet.name, career: sheet.career, species: sheet.species,
      book: gameLine, accent: accent,   // B-931: no `portrait` — the re-encoded picture is stored once, in `sheet`
    };
    try {
      var list = upsertSavedCharacter(newChar, sheet, 'custom');
      setSavedChars(list);
      setSavedToastVisible(true);
      setTimeout(function() { setSavedToastVisible(false); }, 6000);
    } catch (e) { /* non-fatal — fall back to manual save */ }
    openSheetRecord({ id: newChar.id, selectedChar: newChar, sheetData: sheet, source: 'custom' }, { backTarget: 'landing' });   // B-407
  }, [openSheetRecord]);

  // Pregen IDs are static (e.g. 'smuggler'), so a pregen picked before is a character the player
  // OWNS, under that id. The FIRST pick creates its roster entry from the stock sheet (here). A
  // RE-pick never reaches here: `handleSelectCharacter` opens the player's own stored record through
  // the one opener, as the landing's stage and OPEN do (B-969). It used to open the stock sheet under
  // the owned id, and since C-GFB-5's autosave the first edit then wrote stock-plus-edit over the
  // progressed record. The id check below stays as the last line of defence against that overwrite.
  function autosavePregenIfNew(charRec, sheet) {
    if (!sheet) return; // no sheetData = nothing to persist
    var existing = loadSavedCharacters();
    if (existing.some(function(c) { return c.id === charRec.id; })) return;
    try {
      var list = upsertSavedCharacter(charRec, sheet, 'pregen');
      setSavedChars(list);
      setSavedToastVisible(true);
      setTimeout(function() { setSavedToastVisible(false); }, 6000);
    } catch (e) { /* non-fatal */ }
  }
  const handleSelectCharacter = useCallback((char) => {
    // B-969 — an OWNED pregen opens the player's own record, read from storage (not render state),
    // before any fetch: so neither the stock sheet nor a failed fetch's null ever opens under its id.
    var owned = loadSavedCharacters().find(function(c) { return c.id === char.id; });
    if (owned) { openSheetRecord(owned, { backTarget: 'pregen' }); return; }
    // Built-in pregens, first pick: load full sheet data from pregen_sheets.json
    fetch('public/data/pregen_sheets.json')
      .then(r => r.json())
      .then(data => {
        var sheet = (data.characters || []).find(c => c.id === char.id);
        autosavePregenIfNew(char, sheet);   // the first pick makes the roster entry the opener stamps
        openSheetRecord({ id: char.id, selectedChar: char, sheetData: sheet || null, source: 'pregen' }, { backTarget: 'pregen' });   // B-407
      })
      .catch(() => {
        openSheetRecord({ id: char.id, selectedChar: char, sheetData: null, source: 'pregen' }, { backTarget: 'pregen' });
      });
  }, [openSheetRecord]);

  // F-46 D2 — the sheet as MASTER-DETAIL on desktop: ONE SplitPane (rail = the chosen rail module, whose
  // CHARACTERS body is the roster list; detail = the selected character's sheet); selecting a roster row
  // still calls navTo('sheet', id), so the rail never leaves — the detail just updates. Mobile is
  // untouched: the full-screen sheet block below runs when not in MD mode. Defined once here so the
  // sheet's write-through closure isn't duplicated.
  // Mid-tier (ruling FORK 4): the SplitPane is a genuine-desktop wrapper (≥1367). The paper sheet draws no
  // rail at any width (NAVIGATION: "no rail"); its rail module, the CHARACTERS roster that filled it and the
  // TABLE MODE that collapsed it went with the old face (lap paper-sheet-delete, S1). The wrapper stays so the
  // sheet's tree position does not move.
  var mdRosterSheet = layoutMode === 'desktop' && wideDesktop && screen === 'sheet';
  // HOISTED so the sheet's every writer shares ONE write path (no-compromise-mirror: two
  // callers, one persistence rule — a second copy would be a second place for write-through to rot).
  var handleSheetChange = function(patchOrFn) {
        // ── C-GFB-5 — AUTOSAVE (Jason, 2026-08-23: "A. Autosave") ────────────────────────────
        // Every patch to a character ALREADY IN THE ROSTER writes through. The key-class whitelist
        // and its opt flags are gone: they were an ever-growing list of "things that count", and
        // every surface that forgot to join it leaked silently — a bought skill rank, an edited
        // obligation, an item nickname, all lost on reload with no error and no warning. The
        // sheet-plumbing census found the tail of them; the fix is to stop maintaining a list.
        //
        // The one gate that REMAINS is the one the whitelist always really encoded: a character
        // must be in the roster first. A brand-new sheet still enters via the explicit first SAVE,
        // so autosave never conjures roster entries out of a half-built draft.
        //
        // The SAVE bookmark stays, and is now honest: it takes an explicit snapshot, and its
        // filled state no longer implies "your edits are only safe because you pressed me".
        var isIdentityPatch = patchOrFn && typeof patchOrFn === 'object'
          && Object.keys(patchOrFn).length > 0
          && Object.keys(patchOrFn).every(function(k) { return k === 'name' || k === 'portrait'; });
        // identity also updates the ROSTER CARD, not just the sheet body — that is why it is still
        // asked about separately; it is not a persistence class.
        // B-931 (ruling `paper-b931` R-PB9-5): only the NAME goes into `selectedChar`. The picture lives in the
        // sheet alone, so a portrait patch lands in `sheetData.portrait` and nowhere else — it used to be copied
        // into `selectedChar` too, which is where the second stored copy came from.
        var isNamePatch = isIdentityPatch && Object.prototype.hasOwnProperty.call(patchOrFn, 'name');
        var scForUpsert = (isNamePatch && selectedChar) ? Object.assign({}, selectedChar, { name: patchOrFn.name }) : selectedChar;
        // Fold BEFORE the patch (R-PB9-5). The openers hand the sheet the one-copy shape already (`openedPairOf`), so
        // this runs only for a `selectedChar` that still carries a legacy copy: that copy moves into the sheet before
        // the patch (a removal included) is applied, and leaves state too, so no later edit can fold it back.
        var legacySc = (scForUpsert && sheetData && typeof sheetData === 'object'
          && Object.prototype.hasOwnProperty.call(scForUpsert, 'portrait')) ? scForUpsert : null;
        if (legacySc) { scForUpsert = Object.assign({}, legacySc); delete scForUpsert.portrait; }
        if ((isNamePatch || legacySc) && selectedChar) setSelectedChar(scForUpsert);
        setSheetData(function(prev) {
          if (legacySc && prev && typeof prev === 'object') {
            prev = window.SWRPGRecordPortrait.foldRecordPortrait({ selectedChar: legacySc, sheetData: prev }).sheetData;
          }
          var next = (typeof patchOrFn === 'function') ? patchOrFn(prev) : Object.assign({}, prev, patchOrFn);
          var inRoster = scForUpsert && savedChars.some(function(c) { return c.id === scForUpsert.id; });
          if (inRoster) {
            try {
              var list = upsertSavedCharacter(scForUpsert, next, sheetSource);
              setSavedChars(list);
            } catch (e) { /* write-through is best-effort; the explicit SAVE still works */ }
          }
          return next;
        });
      };

  var sheetNode = selectedChar ? (
    <SheetBoundary key={selectedChar.id + ':' + sheetLanding} onFail={handleSheetFail}>
    <CharacterSheet
      char={selectedChar}
      sheet={sheetData}
      onEditInCreator={handleEditInCreator}
      qualitiesDb={qualitiesDb}
      onSheetChange={handleSheetChange}
      writeCharacter={handleStoreFinalize}
      onBack={function() {
        // B-650 · R-LFM-6 — BACK FROM THE SHEET goes where its opener said: the landing (the front
        // matter IS the roster's home; its index is the roster), or Quick Start for a pregen pick.
        // B-1011 — through the one door, so the URL names the screen Back shows (`pathForScreen`), not the sheet.
        navTo(sheetBackTarget || 'pregen');
      }}
      onSave={handleSaveCharacter}
      isSaved={isSelectedSaved}
      onOpenStore={handleOpenStore}
      onOpenYard={function(route) {
        // SHIP YARD S4 (P9): a V face names the room it opens (a ship → /player/yard/ship/<id>); every other caller
        // passes nothing (or an event) and gets today's door, which U9 lands on the HANGAR.
        if (route && route.room) yardGo(route.room === 'ship' ? Object.assign({ from: 'sheet' }, route) : route); else navTo('yard');
      }}
      chromePinned={edgeChrome.pinned}
      onSummonChrome={edgeChrome.togglePinned}
    />
    </SheetBoundary>
  ) : null;

  // B-679 — THE STORE'S BARS TAKE THE PAPER (lab frames AA–AF @ `1e1e2f6a`; Jason 2026-09-18
  // "Approved", and before that "Keep them, but they need paper revision as well" — R-SPC-5 answered
  // KEEP). ONE flag for both bars, and it is `AND`ed with the SCREEN as well as the switch: the
  // app-wide chrome (the faces the paper sheet and landing summon at the edge) is B-689's row, not
  // this one, so nothing outside `/player/store` changes face here. The store's own bars are the
  // app's own components — no second bar is built; they take a ground and inks.
  const storePaper = screen === 'store' && storeTransplantSwitch();
  // B-689 · R-CGR-1 (ruling `chrome-ground-retirement`, Jason 2026-09-21: "We're doing a revision, the
  // black ground is the old screen, so the law is outdated and needs to be removed for the paper") —
  // THE CHROME MATCHES THE SURFACE. The store's `AND`ed flag above was the first instance of this rule,
  // not a special case, and this is the same expression once per host. It is deliberately NOT one
  // app-wide switch: the surfaces flip ONE AT A TIME behind their own keys, so an app-wide paper chrome
  // turned on once would paint paper bars around a screen that is still dark — the one outcome no frame
  // draws and neither law permits. A host whose switch is OFF contributes nothing and its face does not
  // move, so the bars change ground WITH the player as they cross, in one session and with no reload.
  //   · sheet    B-609 `swrpg_sheet_transplant`    — bars summoned at the edge (B-629)
  //   · landing  B-646, one face, no switch term   — bars summoned at the edge (B-652)
  //   · store    B-665 `swrpg_store_transplant`    — `storePaper` above, the face B-679 built
  //
  // THREE HOSTS AND NOT FIVE, and this was built the other way first and then measured back. The
  // ruling's own scope is "passed by the hosts under their switches (SHEET · LANDING · STORE)"
  // (chrome-ground-retirement §4; the B-689 row repeats it), and the creator (`custom`), Quick Start
  // (`pregen`) and Guided (`quickbuild`) are NOT in it. They are also the three screens that set
  // `body.holo-immersive`, which is `display:none !important` on BOTH bars (tokens.css:170) — so there
  // is no chrome on those surfaces for a ground to match, and adding them changed no pixel. What it did
  // change was two standing GREEN asserts in `smoke_store_transplant` ("the creator door is dark and
  // unchanged — 0 `pb-` classes") which count `pb-` and the chrome scope DOCUMENT-WIDE and so caught the
  // hidden bars; and B-697 item 2 has already CLOSED the creator door's bar as staying dark ("the
  // creator's own chrome is the creator programme's"). A build that needs a green guard re-pointed is a
  // build asking for a ruling, so the three named hosts are what lands and the other two are reported.
  // NOT INCLUDED for the same kind of reason: the creator's gear-step store door has its OWN BottomNav
  // (:23989), dark by B-697 item 2 and drawn by no frame; the GM Holocron and the Dice Tutor pass no
  // `paper` at all (R-SPC-2 — `gm/index.html` does not even link store.css).
  // B-862 — the NPC library joins the list (frames D4 · D5: PLAYER DATAPAD's bars, the approved paper
  // chrome, over the library); one face since B-871, so no switch term.
  const paperChrome = storePaper
    || screen === 'yard'   // SHIP YARD S4 · m1 (P3): the Yard declares the paper ground by name
    || screen === 'sheet'
    || screen === 'landing'
    || screen === 'homebrew'   // F-100 C2 (R-FISF-1): bars summoned over the editor wear its paper; off, unreachable
    || screen === 'npc';
  // B-896: the one screen whose paper bars AUTO-HIDE (`autohide={screen === 'npc'}` below) stands them on
  // the page, not the black — lib/shared/chrome.css's `[data-paper-ground]`; the inline black steps aside.
  // The other paper hosts' bars never fade, so their ground is never seen and stays as it was.
  const paperGround = screen === 'npc';

  return (
    <div className="app-shell" data-paper-ground={paperGround ? '1' : undefined} style={{
      width: '100vw', height: '100dvh',
      overflow: 'hidden', background: paperGround ? undefined : '#000',
      position: 'relative', display: 'flex', flexDirection: 'column',
    }}>
      {/* F-48 G13: honest update beacon — fixed toast, self-gating (renders null unless a newer deploy exists). */}
      <_VersionBeacon paper={paperChrome} />{/* B-1213 (R-B1213-12): the update bar follows the screen's bars */}
      {/* (WelcomeModal + Tutorial Hub renders REMOVED — nav Phase 4. The launchpad is the front door; the
          in-context creation coach marks stay. Ko-fi support relocated to the launchpad.) */}

      {/* Suite bar (navigation Phase 1) — persistent home + switcher + identity, above every screen.
          IA lap: the NPC library is bars-resident (the bars ride; no longer immersive). */}
      {/* ADR 0010: inside the store the top bar transforms — back-to-app + the SHOPPING-FOR chip. */}
      <SuiteBar app="player" accent="#4EAAFF" appName="PLAYER DATAPAD" autohide={screen === 'npc'}
        /* B-689 — `paperChrome`, not `storePaper`: the chrome matches whichever surface is under it. */
        paper={paperChrome}
        context={screen === 'store' ? {
          exitLabel: 'BACK',
          onExit: exitStore,
          chip: {
            label: shopForRec ? 'SHOPPING FOR · ' + (shopForRec.name || '').toUpperCase() : 'JUST BROWSING',
            active: !!shopForRec,
            ariaLabel: 'Change who you are shopping for',
            onClick: function() { setShopPickerOpen(true); },
          },
        } : yardS4 ? {
          // SHIP YARD S4 — the Yard's top bar (frames H1/H3/H5, U1): ‹ BACK and the scope chip. Rider 23: the
          // floor's and the catalogue's BACK go home through homeScreenFor; U1's and the ship's read ‹ HANGAR.
          // U3/U15: a ship opened from the sheet's VESSELS reads ‹ <NAME>’S SHEET and goes back there; the ship's owner
          // chip reads YOURS for the character's own ship (static: the owner picker is the GM's).
          // JOB 4 · E1–E3: an entry reads ‹ CATALOGUE and goes back to the index (the frames' back)
          exitLabel: yardShipFromSheet ? String(yardChar.name || '').toUpperCase() + '’S SHEET'
            : yardRouteNow.room === 'plan' ? (yardPlanFromShip ? String(yardFrom.name || 'THE SHIP').toUpperCase() : 'THE ENTRY')
            : yardRouteNow.room === 'build' ? 'HANGAR'   // S8 · BUILD A SHIP goes back to the HANGAR, as U1 and the ship do
            : (yardRouteNow.room === 'unit' || yardRouteNow.room === 'ship') ? 'HANGAR' : yardRouteNow.room === 'entry' ? 'CATALOGUE' : 'BACK',
          onExit: yardShipFromSheet ? yardOpenSheetVessels : yardRouteNow.room === 'plan' ? yardPlanBack
            : yardRouteNow.room === 'build' ? function() { yardGo({ room: 'hangar' }); }
            : (yardRouteNow.room === 'unit' || yardRouteNow.room === 'ship')
            ? function() { yardGo({ room: 'hangar' }); }
            : yardRouteNow.room === 'entry' ? function() { yardGo({ room: 'catalogue' }); }
            : function() { navTo(homeScreenFor(savedChars)); },
          // U6/U7: the scope chip. No character on this device: THE HANGAR / BROWSING, and nothing to pick. With characters:
          // the chosen one's name, or CHOOSE, with H4's caret — it opens U7's picker (this device's characters).
          chip: yardRouteNow.room === 'ship' && yardShipOwnerWord() ? { label: 'OWNER · ' + yardShipOwnerWord(), active: false } : (function() {
            var browse = yardRouteNow.room === 'catalogue' || yardRouteNow.room === 'entry' || yardRouteNow.room === 'plan';
            if (!savedChars.length) return { label: browse ? 'BROWSING' : 'THE HANGAR', active: false };
            return { label: (browse ? 'BROWSING AS · ' : 'THE HANGAR OF · ') + (yardChar ? String(yardChar.name || '').toUpperCase() : 'CHOOSE'),
              active: true, ariaLabel: browse ? 'Choose who is browsing' : 'Choose whose hangar to show', onClick: function() { setYardPickerOpen(!yardPickerOpen); } };
          })(),
        } : null}
        settings={(function() {
        // SCREEN-AWARE ⚙ (ruling suitebar-contextual-settings-concept §3): the roll toggles follow the
        // roll (the sheet), Item artwork lives on the store, Active sources is EVERYWHERE. The three
        // reference cards (Spend/Maneuvers/Healing) moved OUT of ⚙ into the bar's Help menu.
        var suite = [
          // RELEASE R3 (card release-r3-no-loss-card §4, D-258): the whole-save backup, FIRST in ⚙, above "Import or export…".
          { label: 'Back up everything…', onSelect: () => handleEnter('backup') },
          // OGG-1 (R-OGG1-2) — the return-trip entry to the OggDude importer. GENERAL group, reachable
          // from ANYWHERE (deliberately not the screen-aware group): the one ⚙ entry that opens a
          // doing-surface. A POINTER to /player/import via handleEnter — never a second implementation.
          { label: 'Import or export…', onSelect: () => handleEnter('import') },
          // BOOKS-1b — the ACTIVE SOURCES filter (which source books the app offers content from).
          // B-838 — `paper: paperChrome`, the SAME value this SuiteBar (and so its ModuleMenu) takes above:
          // the panel wears the face of the bar it was opened from, dark wherever the bar is dark.
          { label: 'Active sources', onSelect: () => window.SWRPGUI && window.SWRPGUI.openActiveSources && window.SWRPGUI.openActiveSources({ accent: '#4EAAFF', paper: paperChrome }) },
          // F-46 D4 — LAYOUT pref (AUTO·MOBILE·DESKTOP), G10 setPref. Desktop engages ≥1024 on AUTO.
          { label: 'LAYOUT', kind: 'segment', value: layoutPref, onSelect: chooseLayout,
            options: [{ value: 'auto', label: 'AUTO' }, { value: 'mobile', label: 'MOBILE' }, { value: 'desktop', label: 'DESKTOP' }] },
        ];
        // B-563 — THE WALKER'S LEVER, on LOCAL AND LAN BUILDS ONLY. An installed web app on iOS gets
        // its own storage and no address bar, so `?transplant=1` set in Safari never reaches it
        // (Jason 2026-09-13: "When I install to Home Screen it shows the old creator"). This toggle
        // writes the same key the URL flag writes and reloads. It is absent on holocron.work
        // (hostname test), so the dark period stayed dark for players; since the one-pass flip (R-PRS-8)
        // every switch reads ON by default and the toggle writes the '0' OFF override. It retires with the switch.
        if (/^(localhost|127\.|192\.168\.|10\.)/.test(window.location.hostname)) {
          var _tpOn = fcTransplantSwitch();
          suite.push({ label: 'Rebuilt creator (preview)', kind: 'toggle', value: _tpOn,
            onToggle: function() { try { window.localStorage.setItem(FC_TRANSPLANT_KEY, _tpOn ? '0' : '1'); } catch (e) {} window.location.reload(); } });
          // B-665 — the store's lever, the same shape, the same fence (local and LAN builds only).
          var _stOn = storeTransplantSwitch();
          suite.push({ label: 'Rebuilt store (preview)', kind: 'toggle', value: _stOn,
            onToggle: function() { try { window.localStorage.setItem(STORE_TRANSPLANT_KEY, _stOn ? '0' : '1'); } catch (e) {} window.location.reload(); } });
          // B-719 — the two CREATE on-ramps' lever, the same shape, the same fence (local and LAN
          // builds only). ONE toggle for both doors, because they take the ground on one key (R-QSG-1).
          var _qgOn = qsgTransplantSwitch();
          suite.push({ label: 'Rebuilt Quick Start + Guided (preview)', kind: 'toggle', value: _qgOn,
            onToggle: function() { try { window.localStorage.setItem(QSG_TRANSPLANT_KEY, _qgOn ? '0' : '1'); } catch (e) {} window.location.reload(); } });
          // B-770 — the character files receipt's lever, the same shape, the same fence (local and
          // LAN builds only). B-563's reason a sixth time: an installed web app has no address bar,
          // so `?files=1` set in Safari never reaches it.
          var _cfOn = filesTransplantSwitch();
          suite.push({ label: 'Rebuilt character files (preview)', kind: 'toggle', value: _cfOn,
            onToggle: function() { try { window.localStorage.setItem(FILES_TRANSPLANT_KEY, _cfOn ? '0' : '1'); } catch (e) {} window.location.reload(); } });
        }
        var scr = [], screenLabel = null;
        if (screen === 'sheet') {
          scr = [
            { label: 'View Odds', kind: 'toggle', value: diceOdds.enabled, onToggle: diceOdds.toggle },
            { label: 'Interpret Roll', kind: 'toggle', value: diceInterpret.enabled, onToggle: diceInterpret.toggle },
          ];
          screenLabel = 'CHARACTER SHEET';
        } else if (screen === 'store') {
          // BYOA — bring-your-own item artwork (import a folder of images; on-device only).
          scr = [ { label: 'Item artwork', onSelect: () => setArtPanelOpen(true) } ];
          screenLabel = 'STORE';
        }
        return { screen: scr, suite: suite, screenLabel: screenLabel };
      })()} />

      {/* Screen layer — flex:1 below the suite bar; the absolute-inset:0 screens resolve against this. */}
      <div id="suite-main" style={{ flex: 1, position: 'relative', minHeight: 0, zIndex: 1 }}>

      {/* Starfield — landing-only (B-143: was global, 13–53 MB texture animating on every screen).
          Conditionally rendered so it is ABSENT from the DOM on non-landing screens. */}
      {screen === 'landing' && <div className="starfield-drift" style={{
        position: 'fixed', inset: '-15%', pointerEvents: 'none', zIndex: 0,
        backgroundImage: [
          'radial-gradient(2px 2px at 33% 57%, rgba(255,255,255,0.95) 0%, transparent 100%)',
          'radial-gradient(2px 2px at 71% 51%, rgba(255,255,255,0.95) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 44% 37%, rgba(255,255,255,0.9) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 68% 18%, rgba(255,255,255,0.85) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 64% 42%, rgba(255,255,255,0.85) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 37%  4%, rgba(255,255,255,0.8) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 89% 74%, rgba(255,255,255,0.8) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  8% 14%, rgba(255,255,255,0.9) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 17% 87%, rgba(255,255,255,0.9) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 54% 11%, rgba(255,255,255,0.8) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 77% 81%, rgba(255,255,255,0.75) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 87% 27%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  4% 47%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 24% 64%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 91% 57%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 61% 71%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 13% 33%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 31% 22%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 82% 44%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 95% 12%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  6% 72%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 21% 51%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 34% 78%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 47% 58%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 58% 91%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 72% 63%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 97% 38%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  2% 27%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 15%  6%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 27% 93%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 41% 88%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 51% 24%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 74% 33%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 84% 61%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 93% 88%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 11% 58%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 29% 43%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 46% 71%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 56% 51%, rgba(255,255,255,0.5) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 66% 84%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 79%  9%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 88% 46%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  9% 94%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 19% 19%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 39% 63%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 63% 27%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 26% 12%, rgba(255,255,255,0.85) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 52% 79%, rgba(255,255,255,0.8) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 12% 45%, rgba(255,255,255,0.8) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  7% 34%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 43% 16%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 59% 34%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 69% 76%, rgba(255,255,255,0.7) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 81% 15%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 94% 68%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 36% 47%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 48% 33%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 23% 38%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 76% 48%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 86% 84%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 14% 76%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 32% 68%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 44% 96%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 62% 58%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 78% 71%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 96% 24%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at  3% 62%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 18% 28%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 38% 14%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 53% 44%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 67% 8%, rgba(255,255,255,0.65) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 73% 92%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 89% 36%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 24% 84%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 41% 52%, rgba(255,255,255,0.55) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 57% 66%, rgba(255,255,255,0.6) 0%, transparent 100%)',
          'radial-gradient(2px 2px at 16% 63%, rgba(255,255,255,0.9) 0%, transparent 100%)',
          'radial-gradient(2px 2px at 84% 22%, rgba(255,255,255,0.9) 0%, transparent 100%)',
        ].join(','),
      }} />}

      {/* The landing */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        visibility: screen === 'landing' ? 'visible' : 'hidden',
      }}>
        {/* B-646 — THE PAPER LANDING, HANDED the roster (R-LFM-6's props boundary) and
            `openSheetRecord` as its one opener (B-645). */}
        <LandingPaper
          savedChars={savedChars}
          activeId={selectedChar ? selectedChar.id : null}
          onSelect={handleSelectSaved}
          onDelete={handleDeleteCharacter}
          onOpenRecord={handleSelectSaved}
          onReorder={handleReorderRoster}
          focus={landingFocus}
          statusLine={rosterStatus}
          visible={screen === 'landing'}
          chromePinned={edgeChrome.pinned}
          onSummonChrome={edgeChrome.togglePinned}
          onEnter={handleEnter}
          draft={creationDraft}
          onResumeDraft={handleResumeDraft}
          onDiscardDraft={handleDiscardDraft}
        />
      </div>

      {/* Pre-Gen Carousel */}
      {screen === 'pregen' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          animation: `${wipeType} ${WIPE_DURATIONS[wipeType] ?? 1.3}s cubic-bezier(0.42,0,0.18,1) forwards`,
        }}>
          {/* B-719 (R-QSG-3) — the paper face is PER MOUNT, the store's own arrangement: the HOST
              reads the switch and the kernel just takes a prop, so nothing inside the component
              reaches localStorage and the two doors turn on together (R-QSG-1, one key). */}
          <PreGenCarousel onBack={handleBack} onSelect={handleSelectCharacter} paper={qsgTransplantSwitch()} />
        </div>
      )}

      {/* Character Sheet — the SplitPane on a genuine desktop (F-46 D2); a full screen below it. The paper
          sheet draws no rail, so the rail is collapsed at every width (lap paper-sheet-delete, S1). The node
          (sheetNode) is defined once above so the sheet's write-through closure isn't duplicated. */}
      {mdRosterSheet && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10, animation: 'fadeIn 0.3s ease' }}>
          <SplitPane
            mode={layoutMode}
            railWidth="0px"
            /* The cap follows WHAT IS IN THE PANE, not which route we arrived by. B-952 — THE PAPER SHEET IS
               UNCAPPED: its surface carries its own ladder (`--u` 1 → 1.3 at the `surface` container's 2300px →
               1.8 at 3000px, lib/shared/sheet.css), and the approved board draws it filling 1920, 2560 and 3840.
               'none' keeps the wrapper, so the sheet's tree position does not move. */
            detailMax={sheetNode ? "none" : "760px"}
            rail={null}
            detail={sheetNode || null}
          />
        </div>
      )}
      {!mdRosterSheet && screen === 'sheet' && sheetNode}

      {/* HOLOTABLE Stage B wiring — the player's join card + shared feed. Renders NOTHING until
          the launch flag flips; placement disclosed as the implementer's, movable by R-MPA-4. */}
      {!mdRosterSheet && screen === 'sheet' && (
        <HolotableJoinPanel name={selectedChar ? selectedChar.name : ''} />
      )}

      {/* THE STORE (ITM-V1) — shared kernel (lib/store/view.jsx), bars-resident top-level surface.
          Character context rides the SHOPPING-FOR chip (works with none — R-ITM10); persistence
          stays host-owned via handleStoreFinalize. Contextual bars per ADR 0010 render below. */}
      {screen === 'store' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column',
          animation: 'fadeIn 0.3s ease',
        }}>
          {window.SWRPGStore ? (function () {
            var storeProps = {
              accent: '#4EAAFF',
              // B-665 (R-SPC-2) — the paper face is PER MOUNT. This one mount serves two doors
              // (`/player/store` and `/player/sheet/{id}/store`), so both turn on together.
              paper: storeTransplantSwitch(),
              tab: storeTab,
              shopFor: shopForRec,
              purse: storePurse,               // B-671 — the door's own paper; see `storePurse` above
              roster: savedChars,
              qualitiesDb: qualitiesDb,
              onCounts: handleStoreCounts,
              onPickCharacter: handleStorePick,
              onFinalize: handleStoreFinalize,
              onOpenSheet: handleStoreOpenSheet,
              onBrowse: function() { setStoreTab('weapons'); },
            };
            // G-7: the counter's ship lines are the Yard's to plan and deliver (PlayerStoreYard)
            return window.SWRPGVesselView && window.SWRPGVesselView.useCounterSeam
              ? React.createElement(PlayerStoreYard, { storeProps: storeProps })
              : React.createElement(window.SWRPGStore.Store, storeProps);
          })() : null}
        </div>
      )}

      {yardS4 && (
        /* SHIP YARD S4: the Yard's host — its rooms by K6's route; the bars are the SuiteBar/BottomNav above/below */
        <div style={{ position: 'absolute', inset: 0, zIndex: 20 }} data-screen="yard-s4">
          {React.createElement(PlayerYardS4, { route: yardRouteNow, yardChar: yardChar, savedChars: savedChars,
            go: yardGo, catalogueView: yardView, toCounter: yardToCounter, writeCharacter: handleStoreFinalize, onNewCharacter: function() { navTo('landing'); }, onPlanExit: yardPlanBack,
            pickerOpen: yardPickerOpen && yardRouteNow.room !== 'ship', onClosePicker: function() { setYardPickerOpen(false); },
            onPickScope: function(key) { setYardPick(String(key).replace(/^char:/, '')); setYardPickerOpen(false); } })}
        </div>
      )}

      {/* SHOPPING-FOR picker (the top-bar chip opens it; the kernel opens its own for
          compare/finalize prompts) */}
      {screen === 'store' && shopPickerOpen && window.SWRPGStore ? React.createElement(window.SWRPGStore.RosterPicker, {
        roster: savedChars,
        currentId: shopForId,
        allowBrowsing: true,
        title: 'SHOPPING FOR',
        onPick: function(id) { setShopForId(id); setShopPickerOpen(false); },
        onClose: function() { setShopPickerOpen(false); },
      }) : null}

      {/* BYOA — the ⚙ ITEM ARTWORK panel (folder import + report + clear-all); kernel-owned */}
      {artPanelOpen && window.SWRPGStore && window.SWRPGStore.ArtworkPanel ? React.createElement(window.SWRPGStore.ArtworkPanel, {
        accent: '#4EAAFF', paper: storePaper,
        onClose: function() { setArtPanelOpen(false); },
      }) : null}

      {/* QuickBuild */}
      {screen === 'quickbuild' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          animation: `${wipeType} ${WIPE_DURATIONS[wipeType] ?? 1.3}s cubic-bezier(0.42,0,0.18,1) forwards`,
        }}>
          <QuickBuild
            onBack={handleBack}
            onComplete={handleQuickBuildComplete}
            resumeDraft={pendingResume}
            paper={qsgTransplantSwitch()}   /* B-719 — the same switch, the same mount-side read */
          />
        </div>
      )}

      {/* Full Creator */}
      {screen === 'custom' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          animation: `${wipeType} ${WIPE_DURATIONS[wipeType] ?? 1.3}s cubic-bezier(0.42,0,0.18,1) forwards`,
        }}>
          <FullCreator
            onBack={handleBack}
            onComplete={handleFullCreatorComplete}
            resumeDraft={pendingResume}
          />
        </div>
      )}

      {/* OggDude import (OGG-1) — reachable at /player/import; landing/settings doorways land in slice 6 */}
      {screen === 'import' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          animation: `${wipeType} ${WIPE_DURATIONS[wipeType] ?? 1.3}s cubic-bezier(0.42,0,0.18,1) forwards`,
        }}>
          {/* B-770 — `paper` is the room's own switch and nothing else moves: the route, both
              handlers and every anchor are unchanged, and OFF renders today's dark room. */}
          <ImportOgg onBack={handleBack} onComplete={handleImportComplete} onBackup={() => handleEnter('backup')}
            paper={filesTransplantSwitch()} />
        </div>
      )}

      {/* RELEASE R3 (D-258) — the whole-save backup, the ONE screen both apps draw (SWRPGUI.BackupScreen). */}
      {screen === 'backup' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          animation: `${wipeType} ${WIPE_DURATIONS[wipeType] ?? 1.3}s cubic-bezier(0.42,0,0.18,1) forwards`,
        }}>
          <PlayerBackup onBack={handleBack} />
        </div>
      )}

      {/* NPC Library (Step 2) — shared module, player chrome, browse-only */}
      {screen === 'npc' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column',
          animation: `${wipeType} ${WIPE_DURATIONS[wipeType] ?? 1.3}s cubic-bezier(0.42,0,0.18,1) forwards`,
        }}>
          <PlayerNpcLibrary onBack={handleBack} location={npcPath} onLocation={handleNpcLocation} />
        </div>
      )}

      {/* F-100 C2 · THE HOMEBREW EDITOR (lib/editor/, one module, two faces — R-FEDC-1). Reachable only while
          swrpg_homebrew_editor is on (homebrewScreenFor / the landing's Homebrew tile). */}
      {screen === 'homebrew' && window.SWRPGEditorView && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20 }} data-screen="homebrew">
          {React.createElement(window.SWRPGEditorView.Editor, {
            host: 'player', appName: 'Player Datapad', path: homebrewPath, roster: savedChars,
            navigate: homebrewNavigate, exit: handleBack })}
        </div>
      )}

      {/* Post-creation "save your next edit" toast. One-shot; 6s auto-dismiss.
         Tap to dismiss early. Names the paper sheet's own save control by its label (S11).
         B-1211 — the kit's NOTICE (`.sb-notice`, "the app speaking about itself"; the keyline in the state ink) on the
         paper sheet, at all five triggers (R-FD-6: the sheet has one face, so the green toast is retired). Tap anywhere
         still dismisses; the × is now a focusable button with the same look, held to 44px by padding its own margin
         cancels (R-FD-3 (3)). Its place is `savedToastLift` (above). */}
      {savedToastVisible && screen === 'sheet' && (
        <div role="status" aria-live="polite" data-role="toast" data-doc-ground="creator" className="sb-notice" data-kind="ok"
          onClick={() => setSavedToastVisible(false)}
          style={{ '--u': '1px', position: 'absolute', bottom: savedToastLift + 'px', left: '16px', right: '16px',
            maxWidth: 560, boxSizing: 'border-box', zIndex: 110, margin: '0 auto',
            flexWrap: 'nowrap', gap: 12, padding: '10px 14px', boxShadow: 'var(--doc-lift)', cursor: 'pointer',
            animation: 'slideUpBounce 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" style={{ color: 'var(--doc-state)', flexShrink: 0 }}>
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sb-e-n" style={{ fontSize: 13, letterSpacing: '.12em', color: 'var(--doc-state)', marginBottom: 2 }}>CHARACTER SAVED</div>
            <div className="sb-notice-t" style={{ minWidth: 0, lineHeight: 1.35 }}>Tap Save to save further changes.</div>
          </div>
          <button type="button" aria-label="Dismiss" data-role="dismiss" onClick={() => setSavedToastVisible(false)}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 18, color: 'var(--doc-ink-muted)', flexShrink: 0,
              background: 'none', border: 0, padding: '13px 18px', margin: '-13px -18px', cursor: 'pointer' }}>{'\u00D7'}</button>
        </div>
      )}


      {/* Other sections (placeholder) */}
      {screen !== 'landing' && screen !== 'pregen' && screen !== 'sheet' && screen !== 'quickbuild' && screen !== 'custom' && screen !== 'store' && screen !== 'npc' && screen !== 'yard' && screen !== 'import' && screen !== 'backup' && screen !== 'homebrew' && (
        <PlayerSectionShell
          sectionId={screen}
          wipeType={wipeType}
          onBack={handleBack}
        />
      )}

      </div>{/* /#suite-main */}

      {/* Bottom nav (navigation Phase 2c) — Player destinations. Sheet is a drill-down under Characters
          (stays active there); the creation wizards stay immersive; the NPC library is now bars-resident
          (IA lap: bars-resident). */}
      {screen === 'store' ? (
        /* ADR 0010: inside the store the bottom bar BECOMES the store's bar —
           WEAPONS · ARMOR · GEAR · ◈ HOLDS · COUNTER(n). Exiting restores the global bar. */
        <BottomNav app="player" label="Store" accent="#4EAAFF"
          /* B-679: the paper face. The five sections, their order, their words and their silhouettes
             are untouched — the board read them out of this very array (and the silhouettes out of the
             kernel's own `GLYPHS` table, which this host reuses by contract). */
          paper={storePaper}
          activeId={storeTab}
          items={[
            { id: 'weapons', label: 'Weapons', icon: storeGlyph('pistol'), onSelect: function() { setStoreTab('weapons'); } },
            { id: 'armor', label: 'Armor', icon: storeGlyph('armor'), onSelect: function() { setStoreTab('armor'); } },
            { id: 'gear', label: 'Gear', icon: storeGlyph('storage'), onSelect: function() { setStoreTab('gear'); } },
            /* B-1403 (D-279; ruling `b1403-store-attachments-owns` R-B1403-12) — PARTS, after GEAR as frame BAR-parts draws it;
               the tab id is the shelf's section, `attachments` */
            { id: 'attachments', label: 'Parts', icon: storeGlyph('toolkit'), onSelect: function() { setStoreTab('attachments'); } },
            { id: 'holds', label: 'Wish list', icon: navBadge(holdsGlyph(), storeCounts.holds, storePaper), onSelect: function() { setStoreTab('holds'); } },
            { id: 'counter', label: 'Counter', icon: navBadge(navIcon('shopping-cart'), storeCounts.cart, storePaper), onSelect: function() { setStoreTab('counter'); } },
          ]} />
      ) : yardS4 ? (
        /* SHIP YARD S4: the Yard's own bar — HANGAR · CATALOGUE on the player host, at every width
           (R-S2M-16.1), in the paper face (P2). */
        <BottomNav app="player" label="Shipyard" accent={YARD_CARD.accent} paper={true}
          activeId={(yardRouteNow.room === 'catalogue' || yardRouteNow.room === 'entry' || yardRouteNow.room === 'plan') ? 'catalogue' : 'hangar'}
          items={[
            { id: 'hangar', label: 'Hangar', icon: window.SWRPGVesselView.barIcon('hangar'), onSelect: function() { yardGo({ room: 'hangar' }); } },
            { id: 'catalogue', label: 'Catalogue', icon: window.SWRPGVesselView.barIcon('catalogue'), onSelect: function() { yardGo({ room: 'catalogue' }); } },
          ]} />
      ) : (
        <BottomNav app="player" label={PLAYER_BAR_COPY.app} accent="#4EAAFF" autohide={screen === 'npc'}
          /* B-689 · R-CGR-1 — THE GLOBAL BAR TAKES THE SURFACE'S GROUND. The five destinations, their
             order, their words and their glyphs are untouched; only the ground and the inks move, and
             only on a screen whose own switch is on. On the dark screens this is `false` and the bar
             is byte-identical to what it was. */
          paper={paperChrome}
          /* Addendum 1 (create-landing-flanks-concept): ONE VOCABULARY ACROSS THE TWO APPS.
             'Library' becomes 'NPCs' — the GM bar's own authored string for the same destination
             (gm/app.jsx:3635) — and YARD joins as a first-class door, as it already is on the GM
             bar (:3636). Ruled order: CHARACTERS · CREATE · NPCS · YARD · STORE — the player-only
             pair leads, then the three shared destinations in the GM bar's relative order, so a
             user moving between apps scans one vocabulary in one order.
             Display + destinations only: screens, routes and capability gates are untouched (the
             ENCOUNTERS-rename discipline). The item id renames library->npcs with its ONE consumer
             (the activeId map directly below) moved in the same edit.
             YARD needs no activeId row: inside /player/yard this generic bar is suppressed
             entirely (R-Y2-2 — the chapter bar owns that surface), so the item is the DOOR and
             never renders active. */
          /* B-650: both player words land on ONE screen, so which of them reads active there follows
             the CHAPTER the page was sent to — CHARACTERS for the index, CREATE for anything else. */
          activeId={(function() {
            var m = ({ sheet: 'characters',
                       pregen: 'create', quickbuild: 'create', custom: 'create',
                       npc: 'npcs' })[screen] || null;
            if (screen === 'landing') {
              var z = landingFocus && landingFocus.zone;
              return (z === 'index' || z === 'top') ? 'characters' : 'create';
            }
            return m;
          })()}
          items={[
            { id: 'characters', label: 'Characters', icon: navIcon('user-star'),
              onSelect: function() { landTo('top'); } },
            { id: 'create', label: PLAYER_BAR_COPY.create, icon: navIcon('user-plus'),
              onSelect: function() { landTo('create'); } },
            { id: 'npcs', label: 'NPCs', icon: navIcon('users'), onSelect: function() { navTo('npc'); } },
            { id: 'shipyard', label: 'Shipyard', icon: navIcon('rocket'), onSelect: function() { navTo('yard'); } },
            { id: 'store', label: 'Store', icon: navBadge(navIcon('shopping-cart'), storeCounts.cart), onSelect: enterStore },
          ]} />
      )}

    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<PlayerApp />);
